/* eslint-disable */
import { createClient } from '@supabase/supabase-js';
import { useState, useEffect, useRef, useMemo, useCallback, Fragment } from "react";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const supabase = createClient(
  'https://xstrugwgpozoipgjjspy.supabase.co',
  'sb_publishable_vSdEcWognHjuLuAnjJHq3A_C_zwP6W0'
);

// ── Auth automática hacia /api/*: toda llamada del frontend lleva el JWT de la sesión.
// Las APIs ahora EXIGEN autorización (candado de la auditoría) — este interceptor evita
// tocar los ~25 puntos de llamada uno por uno. getSession() lee de memoria (no hace red).
const _fetchOriginal = window.fetch.bind(window);
window.fetch = async (input, init) => {
  const u = typeof input === 'string' ? input : (input && input.url) || '';
  if (u.startsWith('/api/')) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        init = { ...(init || {}) };
        init.headers = { ...(init.headers || {}), Authorization: `Bearer ${session.access_token}` };
      }
    } catch (_) { /* sin sesión: la API responderá 401 */ }
  }
  return _fetchOriginal(input, init);
};

// ─── Generador de .xlsx (Excel real) sin dependencias ──────────────────────────
// Arma un ZIP "stored" (sin compresión) con CRC32 y el XML mínimo de OOXML.
const _xmlEsc = s => String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
const _colLetter = n => { let s=''; n++; while(n>0){ const m=(n-1)%26; s=String.fromCharCode(65+m)+s; n=Math.floor((n-1)/26);} return s; };
const _crcTable = (()=>{ const t=[]; for(let n=0;n<256;n++){ let c=n; for(let k=0;k<8;k++) c=(c&1)?(0xEDB88320^(c>>>1)):(c>>>1); t[n]=c>>>0;} return t; })();
const _crc32 = bytes => { let c=0xFFFFFFFF; for(let i=0;i<bytes.length;i++) c=_crcTable[(c^bytes[i])&0xFF]^(c>>>8); return (c^0xFFFFFFFF)>>>0; };
function buildXlsx(cols, rows, sheetName='Datos'){
  const enc = new TextEncoder();
  const sheetRows = [];
  // encabezado
  sheetRows.push('<row r="1">'+cols.map((c,i)=>`<c r="${_colLetter(i)}1" t="inlineStr"><is><t xml:space="preserve">${_xmlEsc(c)}</t></is></c>`).join('')+'</row>');
  rows.forEach((r,ri)=>{
    const cells = cols.map((c,ci)=>{
      let v = r[c];
      if (v===null||v===undefined||v==='') return '';
      const ref = `${_colLetter(ci)}${ri+2}`;
      if (typeof v==='number' && isFinite(v)) return `<c r="${ref}"><v>${v}</v></c>`;
      const s = (typeof v==='object') ? JSON.stringify(v) : String(v);
      return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${_xmlEsc(s)}</t></is></c>`;
    }).join('');
    sheetRows.push(`<row r="${ri+2}">${cells}</row>`);
  });
  const files = {
    '[Content_Types].xml':'<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>',
    '_rels/.rels':'<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>',
    'xl/workbook.xml':`<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${_xmlEsc(String(sheetName).slice(0,31))}" sheetId="1" r:id="rId1"/></sheets></workbook>`,
    'xl/_rels/workbook.xml.rels':'<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>',
    'xl/worksheets/sheet1.xml':'<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>'+sheetRows.join('')+'</sheetData></worksheet>',
  };
  return _zip(files);
}

// Multi-hoja: sheets = [{name, cols, rows}]. Reusa _zip/_xmlEsc/_colLetter.
function buildXlsxMulti(sheets){
  const sheetXml = (cols, rows) => {
    const sr = [];
    sr.push('<row r="1">'+cols.map((c,i)=>`<c r="${_colLetter(i)}1" t="inlineStr"><is><t xml:space="preserve">${_xmlEsc(c)}</t></is></c>`).join('')+'</row>');
    rows.forEach((r,ri)=>{
      const cells = cols.map((c,ci)=>{
        let v = r[c];
        if (v===null||v===undefined||v==='') return '';
        const ref = `${_colLetter(ci)}${ri+2}`;
        if (typeof v==='number' && isFinite(v)) return `<c r="${ref}"><v>${v}</v></c>`;
        const t = (typeof v==='object') ? JSON.stringify(v) : String(v);
        return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${_xmlEsc(t)}</t></is></c>`;
      }).join('');
      sr.push(`<row r="${ri+2}">${cells}</row>`);
    });
    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>'+sr.join('')+'</sheetData></worksheet>';
  };
  const files = {
    '_rels/.rels':'<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>',
  };
  const overrides = sheets.map((_,i)=>`<Override PartName="/xl/worksheets/sheet${i+1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('');
  files['[Content_Types].xml']='<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'+overrides+'</Types>';
  const sheetTags = sheets.map((sh,i)=>`<sheet name="${_xmlEsc(String(sh.name).slice(0,31))}" sheetId="${i+1}" r:id="rId${i+1}"/>`).join('');
  files['xl/workbook.xml']=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${sheetTags}</sheets></workbook>`;
  const rels = sheets.map((_,i)=>`<Relationship Id="rId${i+1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i+1}.xml"/>`).join('');
  files['xl/_rels/workbook.xml.rels']='<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'+rels+'</Relationships>';
  sheets.forEach((sh,i)=>{ files[`xl/worksheets/sheet${i+1}.xml`]=sheetXml(sh.cols, sh.rows); });
  return _zip(files);
}

// Empaqueta {nombre: contenido} en un ZIP "stored" (sin compresión) con CRC32.
function _zip(files){
  const enc = new TextEncoder();
  const localParts=[], centralParts=[]; let offset=0;
  const u16=n=>[n&0xFF,(n>>8)&0xFF]; const u32=n=>[n&0xFF,(n>>8)&0xFF,(n>>16)&0xFF,(n>>24)&0xFF];
  for (const name of Object.keys(files)){
    const nameB=enc.encode(name), data=enc.encode(files[name]); const crc=_crc32(data);
    const lh=[].concat(u32(0x04034b50),u16(20),u16(0),u16(0),u16(0),u16(0),u32(crc),u32(data.length),u32(data.length),u16(nameB.length),u16(0));
    localParts.push(new Uint8Array(lh), nameB, data);
    const ch=[].concat(u32(0x02014b50),u16(20),u16(20),u16(0),u16(0),u16(0),u16(0),u32(crc),u32(data.length),u32(data.length),u16(nameB.length),u16(0),u16(0),u16(0),u16(0),u32(0),u32(offset));
    centralParts.push(new Uint8Array(ch), nameB);
    offset += lh.length+nameB.length+data.length;
  }
  const centralSize=centralParts.reduce((a,b)=>a+b.length,0);
  const eocd=new Uint8Array([].concat(u32(0x06054b50),u16(0),u16(0),u16(Object.keys(files).length),u16(Object.keys(files).length),u32(centralSize),u32(offset),u16(0)));
  const all=[...localParts,...centralParts,eocd];
  const total=all.reduce((a,b)=>a+b.length,0); const out=new Uint8Array(total); let p=0;
  for(const part of all){ out.set(part,p); p+=part.length; }
  return out;
}

// ─── Excel CON ESTILOS: hoja Resumen + una pestaña por vendedor, etapas con color, filtros ──
const XL_ETAPAS = [
  ['prospecto','Prospecto','64748B'], ['contactado','Contactado','6366F1'],
  ['cotizacion','Cotización','F59E0B'], ['negociacion','Negociación','EC4899'],
  ['cerrado','Cerrado','0B7AB8'], ['facturado','Facturado','8B5CF6'],
  ['pagado','Pagado','06B6D4'], ['cerrado_ganado','Cerrado ganado','10B981'],
  ['cerrado_perdido','Cerrado perdido','EF4444'], ['prospecto_invalido','Prospecto inválido','94A3B8'],
  ['cliente_frio','Cliente frío','0EA5E9'],
];
function _stylesXml(){
  const fills = ['<fill><patternFill patternType="none"/></fill>','<fill><patternFill patternType="gray125"/></fill>',
    '<fill><patternFill patternType="solid"><fgColor rgb="FF4F46E5"/></patternFill></fill>'];
  XL_ETAPAS.forEach(e=>fills.push(`<fill><patternFill patternType="solid"><fgColor rgb="FF${e[2]}"/></patternFill></fill>`));
  const cellXfs = [
    '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>',                                            // 0 default
    '<xf fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>', // 1 header
    '<xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>',      // 2 money
    '<xf fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>', // 3 text
    '<xf fontId="2" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1"/>',                             // 4 bold text
  ];
  XL_ETAPAS.forEach((e,i)=>cellXfs.push(`<xf fontId="1" fillId="${3+i}" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>`)); // 5+i etapa
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'+
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'+
    '<numFmts count="1"><numFmt numFmtId="164" formatCode="&quot;$&quot;#,##0.00"/></numFmts>'+
    '<fonts count="3"><font><sz val="11"/><name val="Calibri"/></font>'+
      '<font><b/><color rgb="FFFFFFFF"/><sz val="11"/><name val="Calibri"/></font>'+
      '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'+
    `<fills count="${fills.length}">${fills.join('')}</fills>`+
    '<borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border>'+
      '<border><left style="thin"><color rgb="FFD5DBE6"/></left><right style="thin"><color rgb="FFD5DBE6"/></right><top style="thin"><color rgb="FFD5DBE6"/></top><bottom style="thin"><color rgb="FFD5DBE6"/></bottom><diagonal/></border></borders>'+
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'+
    `<cellXfs count="${cellXfs.length}">${cellXfs.join('')}</cellXfs>`+
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>';
}
const _cellTxt = (ref,s,v)=>`<c r="${ref}" s="${s}" t="inlineStr"><is><t xml:space="preserve">${_xmlEsc(v)}</t></is></c>`;
const _cellNum = (ref,s,v)=>`<c r="${ref}" s="${s}"><v>${v}</v></c>`;
function _sheetXml(cols, rows, first){
  // cols: [{w}] ; rows: [[{s,v,num}]] (fila 1 = encabezado)
  const colsXml = `<cols>${cols.map((c,i)=>`<col min="${i+1}" max="${i+1}" width="${c.w}" customWidth="1"/>`).join('')}</cols>`;
  const body = rows.map((cells,ri)=>{
    const cs = cells.map((cell,ci)=>{ const ref=`${_colLetter(ci)}${ri+1}`;
      if (cell.v===''||cell.v===null||cell.v===undefined) return `<c r="${ref}" s="${cell.s}"/>`;
      return cell.num ? _cellNum(ref,cell.s,cell.v) : _cellTxt(ref,cell.s,String(cell.v)); }).join('');
    return `<row r="${ri+1}">${cs}</row>`;
  }).join('');
  const ref = `A1:${_colLetter(cols.length-1)}${rows.length}`;
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'+
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'+
    `<sheetViews><sheetView ${first?'tabSelected="1" ':''}workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`+
    colsXml+`<sheetData>${body}</sheetData>`+
    `<autoFilter ref="${ref}"/></worksheet>`;
}
function _sanSheet(name, usados){
  let n = String(name||'Hoja').replace(/[\\/?*\[\]:]/g,' ').trim().slice(0,28) || 'Hoja';
  let base=n, k=2; while(usados.has(n.toLowerCase())){ n = `${base.slice(0,26)} ${k++}`; } usados.add(n.toLowerCase()); return n;
}
function buildXlsxReporte(all){
  const etIdx = {}; XL_ETAPAS.forEach((e,i)=>etIdx[e[0]]=i); const etLbl = k => (XL_ETAPAS[etIdx[k]]||[,'—'])[1] || (k||'—');
  const styleEtapa = k => etIdx[k]!==undefined ? 5+etIdx[k] : 3;
  // agrupar por vendedor
  const byVend = {}; for (const o of all){ const v=(o.vendedor||'Sin asignar').trim()||'Sin asignar'; (byVend[v]=byVend[v]||[]).push(o); }
  const vends = Object.keys(byVend).sort((a,b)=>byVend[b].length-byVend[a].length);
  const money = v => { const n=Number(v); return isFinite(n)?n:0; };
  const usados = new Set(); const sheetMeta = []; // {file, name}
  const files = {}; let sIdx=0;
  // Hoja 1: Resumen
  { const cols=[{w:26},{w:16},{w:20}];
    const rows=[[{s:1,v:'Vendedor'},{s:1,v:'Oportunidades'},{s:1,v:'Pipeline (USD)'}]];
    let tOpp=0,tVal=0;
    for(const v of vends){ const rs=byVend[v]; const val=rs.reduce((a,o)=>a+money(o.valor),0); tOpp+=rs.length; tVal+=val;
      rows.push([{s:4,v},{s:3,v:rs.length,num:true},{s:2,v:val,num:true}]); }
    rows.push([{s:4,v:'TOTAL'},{s:4,v:tOpp,num:true},{s:2,v:tVal,num:true}]);
    sIdx++; files[`xl/worksheets/sheet${sIdx}.xml`]=_sheetXml(cols,rows,true); sheetMeta.push({name:'Resumen',i:sIdx}); }
  // Una hoja por vendedor
  const COLS=[{h:'Etapa',w:16},{h:'Valor (USD)',w:14},{h:'Cliente',w:30},{h:'Teléfono',w:15},{h:'Ubicación',w:16},{h:'Línea',w:16},{h:'Fecha',w:12},{h:'Título',w:34},{h:'Nota',w:44}];
  for(const v of vends){
    const rs=byVend[v].slice().sort((a,b)=>(etIdx[a.etapa]??99)-(etIdx[b.etapa]??99));
    const rows=[COLS.map(c=>({s:1,v:c.h}))];
    for(const o of rs){ rows.push([
      {s:styleEtapa(o.etapa),v:etLbl(o.etapa)},
      {s:2,v:money(o.valor),num:true},
      {s:3,v:o.cliente||''},{s:3,v:o.cliente_telefono||''},{s:3,v:o.cliente_ciudad||''},
      {s:3,v:o.linea_negocio||''},{s:3,v:o.fecha_cierre||''},{s:3,v:o.titulo||''},{s:3,v:o.notas||''},
    ]); }
    sIdx++; files[`xl/worksheets/sheet${sIdx}.xml`]=_sheetXml(COLS.map(c=>({w:c.w})),rows,false);
    sheetMeta.push({name:_sanSheet(v,usados),i:sIdx});
  }
  // Ensamble workbook
  const stylesRid = sheetMeta.length+1;
  files['xl/styles.xml']=_stylesXml();
  files['[Content_Types].xml']='<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'+
    sheetMeta.map(s=>`<Override PartName="/xl/worksheets/sheet${s.i}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')+'</Types>';
  files['_rels/.rels']='<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>';
  files['xl/workbook.xml']='<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>'+
    sheetMeta.map((s,idx)=>`<sheet name="${_xmlEsc(s.name)}" sheetId="${idx+1}" r:id="rId${idx+1}"/>`).join('')+'</sheets></workbook>';
  files['xl/_rels/workbook.xml.rels']='<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'+
    sheetMeta.map((s,idx)=>`<Relationship Id="rId${idx+1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${s.i}.xml"/>`).join('')+
    `<Relationship Id="rId${stylesRid}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`;
  return _zip(files);
}

// ─── Excel de COMISIONES con estilos: hoja Resumen + una hoja por vendedor ─────
// data: { mes, lista:[calcComDe], adm, sinAsig:[{f,sub,...}] }
function buildXlsxComisiones(data){
  const { mes, lista, adm, sinAsig } = data;
  const money = v => Math.round((Number(v)||0)*100)/100;
  const usados = new Set(); const sheetMeta = []; const files = {}; let sIdx = 0;
  const addSheet = (name, cols, rows, first) => {
    sIdx++; files[`xl/worksheets/sheet${sIdx}.xml`] = _sheetXml(cols, rows, !!first);
    sheetMeta.push({ name:_sanSheet(name, usados), i:sIdx });
  };
  // ── Hoja 1: Resumen por vendedor ──
  { const cols=[{w:26},{w:11},{w:17},{w:17},{w:15},{w:10},{w:13},{w:15}];
    const rows=[[{s:1,v:'Vendedor'},{s:1,v:'Facturas'},{s:1,v:'Total ventas USD'},{s:1,v:'Subtotal sin ISV'},{s:1,v:'Comisión base'},{s:1,v:'Factor'},{s:1,v:'Bono KAM'},{s:1,v:'A PAGAR USD'}]];
    let tF=0,tT=0,tS=0,tB=0,tP=0;
    for (const c of lista){ tF+=c.filas.length; tT+=c.tot; tS+=c.sub; tB+=c.base; tP+=c.final;
      rows.push([{s:4,v:c.v+(c.kam?' (KAM)':'')},{s:3,v:c.filas.length,num:true},{s:2,v:money(c.tot),num:true},{s:2,v:money(c.sub),num:true},{s:2,v:money(c.base),num:true},
        {s:3,v:`${Math.round(c.factor*100)}%`},{s:3,v:c.bono?`L.${Math.round(c.bono).toLocaleString('es-HN')}`:''},{s:2,v:money(c.final),num:true}]); }
    if (adm && (adm.final>0 || adm.filas.length)){ tF+=adm.filas.length; tT+=adm.tot; tS+=adm.sub; tB+=adm.base; tP+=adm.final;
      rows.push([{s:4,v:'Administración (1% de ventas del mes)'},{s:3,v:adm.filas.length,num:true},{s:2,v:money(adm.tot),num:true},{s:2,v:money(adm.sub),num:true},{s:2,v:money(adm.base),num:true},{s:3,v:'100%'},{s:3,v:''},{s:2,v:money(adm.final),num:true}]); }
    if (sinAsig && sinAsig.length){
      rows.push([{s:4,v:'⚠ SIN ASIGNAR'},{s:3,v:sinAsig.length,num:true},{s:2,v:money(sinAsig.reduce((s,x)=>s+(Number(x.f.total_usd)||0),0)),num:true},{s:2,v:money(sinAsig.reduce((s,x)=>s+x.sub,0)),num:true},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''}]); }
    rows.push([{s:4,v:'TOTAL'},{s:4,v:tF,num:true},{s:2,v:money(tT),num:true},{s:2,v:money(tS),num:true},{s:2,v:money(tB),num:true},{s:3,v:''},{s:3,v:''},{s:2,v:money(tP),num:true}]);
    addSheet(`Resumen ${mes}`, cols, rows, true); }
  // ── Una hoja por vendedor (facturas + totales + a pagar) ──
  const COLS=[{h:'Factura',w:12},{h:'Empresa',w:11},{h:'Fecha',w:12},{h:'Cliente',w:34},{h:'Categoría',w:18},{h:'Total USD',w:13},{h:'ISV USD',w:11},{h:'Subtotal sin ISV',w:16},{h:'%',w:8},{h:'Comisión USD',w:14}];
  const hojaVendedor = (c, esAdmin) => {
    const rows=[COLS.map(x=>({s:1,v:x.h}))];
    for (const x of c.filas){
      rows.push([{s:3,v:x.f.doc_num},{s:3,v:String(x.f.empresa_id||'').toUpperCase()},{s:3,v:x.f.doc_date||''},{s:3,v:x.f.card_name||''},
        {s:3,v:esAdmin?'—':(x.cat||'sin categoría')},{s:2,v:money(x.f.total_usd),num:true},{s:2,v:money((x.f.total_usd||0)-x.sub),num:true},
        {s:2,v:money(x.sub),num:true},{s:3,v:x.pct?`${+(x.pct*100).toFixed(2)}%`:'—'},{s:2,v:money(x.com),num:true}]);
    }
    const fila = (lbl, val) => rows.push([{s:4,v:lbl},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:2,v:money(val),num:true}]);
    rows.push([{s:4,v:'TOTAL VENTAS'},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:2,v:money(c.tot),num:true},{s:2,v:money(c.tot-c.sub),num:true},{s:2,v:money(c.sub),num:true},{s:3,v:''},{s:2,v:money(c.filas.reduce((s,x)=>s+x.com,0)),num:true}]);
    if (esAdmin) fila('Comisión Administración: 1% de las ventas totales del mes', c.final);
    if (!esAdmin && !c.kam) fila(`Factor de cumplimiento: ${Math.round(c.factor*100)}%`, c.base*c.factor);
    if (c.bono) rows.push([{s:4,v:'Bono KAM'},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:''},{s:3,v:`L.${Math.round(c.bono).toLocaleString('es-HN')}`}]);
    fila('A PAGAR (USD)', c.final);
    return rows;
  };
  for (const c of lista) addSheet(c.v.split(' ').slice(0,2).join(' '), COLS.map(x=>({w:x.w})), hojaVendedor(c, false), false);
  if (adm && adm.filas.length) addSheet('Administración', COLS.map(x=>({w:x.w})), hojaVendedor(adm, true), false);
  if (sinAsig && sinAsig.length){
    const rows=[COLS.map(x=>({s:1,v:x.h}))];
    for (const x of sinAsig) rows.push([{s:3,v:x.f.doc_num},{s:3,v:String(x.f.empresa_id||'').toUpperCase()},{s:3,v:x.f.doc_date||''},{s:3,v:x.f.card_name||''},{s:3,v:x.cat||''},{s:2,v:money(x.f.total_usd),num:true},{s:2,v:money((x.f.total_usd||0)-x.sub),num:true},{s:2,v:money(x.sub),num:true},{s:3,v:'—'},{s:3,v:''}]);
    addSheet('Sin asignar', COLS.map(x=>({w:x.w})), rows, false);
  }
  // ── Ensamble del workbook (mismo esqueleto que el reporte de oportunidades) ──
  const stylesRid = sheetMeta.length+1;
  files['xl/styles.xml']=_stylesXml();
  files['[Content_Types].xml']='<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'+
    sheetMeta.map(s=>`<Override PartName="/xl/worksheets/sheet${s.i}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')+'</Types>';
  files['_rels/.rels']='<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>';
  files['xl/workbook.xml']='<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>'+
    sheetMeta.map((s,idx)=>`<sheet name="${_xmlEsc(s.name)}" sheetId="${idx+1}" r:id="rId${idx+1}"/>`).join('')+'</sheets></workbook>';
  files['xl/_rels/workbook.xml.rels']='<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'+
    sheetMeta.map((s,idx)=>`<Relationship Id="rId${idx+1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${s.i}.xml"/>`).join('')+
    `<Relationship Id="rId${stylesRid}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`;
  return _zip(files);
}

// ─── DESIGN TOKENS (DS v3 — ver DESIGN-SPEC.md, la fuente de verdad) ─────────
// Tema claro premium: fondo slate suave, superficies blancas, acento TEAL.
// bg0=página, bg1=paneles/inputs, bg2=tarjetas (blanco), bg3=hover/tracks.
// OJO: C.blue se llama "blue" por historia pero ES el primary teal #0F766E.
const C = {
  bg0:'#ECEFF4', bg1:'#F3F5F9', bg2:'#FFFFFF', bg3:'#E7ECF3',
  border:'#E4E9F0', border2:'#CBD5E1',
  t1:'#0B1220', t2:'#3B4657', t3:'#65748B', t4:'#98A2B3',
  blue:'#0F766E', green:'#16A34A', orange:'#D97706',
  red:'#DC2626', pink:'#DB2777', indigo:'#0D9488',
  // Primary teal — familia completa
  primary:'#0F766E',
  primaryHover:'#0D5F58', primaryActive:'#0B4F4A',
  primaryTint:'#F0FDFA', primaryBorder:'#99F6E4', ring:'rgba(15,118,110,.18)',
  // Semánticos — tripleta texto/tinte/borde (el pleno es green/orange/red arriba)
  greenText:'#15803D',  greenTint:'#F0FDF4',  greenBorder:'#BBF7D0',
  orangeText:'#B45309', orangeTint:'#FFFBEB', orangeBorder:'#FDE68A',
  redText:'#B91C1C',    redTint:'#FEF2F2',    redBorder:'#FECACA',
  info:'#2563EB', infoText:'#1D4ED8', infoTint:'#EFF6FF', infoBorder:'#BFDBFE',
  // Sidebar (verde profundo)
  sbBg:'#14201D', sbHover:'#1E2E2A', sbActive:'#0F766E',
  sbText:'#A3B5B0', sbTextActive:'#FFFFFF', sbMuted:'#7E938D', sbBorder:'#233530',
  // Identidad por línea de negocio (tags, canales, íconos)
  lob:{
    venta:    { text:'#0F766E', bg:'#F0FDFA', border:'#99F6E4' },
    renta:    { text:'#1D4ED8', bg:'#EFF6FF', border:'#BFDBFE' },
    servicio: { text:'#6D28D9', bg:'#F5F3FF', border:'#DDD6FE' },
    repuestos:{ text:'#C2410C', bg:'#FFF7ED', border:'#FED7AA' },
  },
};
// Escala de elevación (capas suaves, nunca pesadas) — DS v3
const SH = {
  xs:'0 1px 2px rgba(16,24,40,.04)',
  sm:'0 1px 2px rgba(16,24,40,.05), 0 1px 3px rgba(16,24,40,.06)',
  md:'0 2px 4px rgba(16,24,40,.05), 0 8px 20px rgba(16,24,40,.08)',
  lg:'0 4px 8px rgba(16,24,40,.06), 0 16px 36px rgba(16,24,40,.11)',
  hover:'0 2px 4px rgba(16,24,40,.05), 0 8px 20px rgba(16,24,40,.08)',
  primary:'0 1px 2px rgba(15,118,110,.32), 0 2px 6px rgba(15,118,110,.18)',
  primaryHover:'0 2px 4px rgba(15,118,110,.30), 0 6px 14px rgba(15,118,110,.24)',
};
// Radios DS v3: techo 14. sm=chips/tags, md=botones/inputs (8 según mockup), lg=cards/paneles.
const R = { sm:6, md:8, lg:14, pill:999 };
// Estilos globales una sola vez: transiciones suaves, números tabulares, scroll fino,
// y respeto de prefers-reduced-motion. Inyectado al cargar el módulo.
if (typeof document !== 'undefined' && !document.getElementById('radar-fx')) {
  const st = document.createElement('style'); st.id = 'radar-fx';
  st.textContent = `
    .radar-card{transition:box-shadow .16s ease, transform .16s ease;}
    .radar-card:hover{box-shadow:${SH.hover};transform:translateY(-1px);}
    .radar-btn{transition:filter .15s ease, transform .1s ease, box-shadow .15s ease;}
    .radar-btn:hover:not(:disabled){filter:brightness(1.07);transform:translateY(-1px);}
    .radar-btn:active:not(:disabled){transform:translateY(0);}
    .radar-num{font-variant-numeric:tabular-nums;}
    summary{list-style:none;} summary::-webkit-details-marker{display:none;}
    @keyframes radarShimmer{0%{background-position:-400px 0;}100%{background-position:400px 0;}}
    .radar-skel{background:linear-gradient(90deg,${C.bg3} 25%,${C.bg1} 37%,${C.bg3} 63%);background-size:800px 100%;animation:radarShimmer 1.3s infinite linear;border-radius:8px;}
    @keyframes radarPop{from{opacity:0;transform:translateY(8px) scale(.98);}to{opacity:1;transform:none;}}
    .radar-pop{animation:radarPop .18s ease-out;}
    @keyframes radarSlideIn{from{opacity:0;transform:translateX(24px);}to{opacity:1;transform:none;}}
    .radar-toast{animation:radarSlideIn .22s cubic-bezier(.2,.8,.3,1);}
    .radar-tbl thead th{position:sticky;top:0;z-index:2;background:${C.bg2};}
    .radar-tbl tbody tr{transition:background .12s ease;}
    .radar-tbl tbody tr:hover{background:${C.bg3};}
    /* DS v3 — KPI cards estilo Stripe y cards de kanban (hover lift) */
    .radar-kpi{transition:box-shadow .18s cubic-bezier(.4,0,.2,1),transform .18s cubic-bezier(.4,0,.2,1),border-color .18s ease;}
    .radar-kpi:hover{box-shadow:${SH.md};transform:translateY(-1px);border-color:${C.border2};}
    .radar-kcard{transition:box-shadow .18s cubic-bezier(.4,0,.2,1),transform .18s cubic-bezier(.4,0,.2,1);}
    .radar-kcard:hover{box-shadow:${SH.md};transform:translateY(-2px);}
    .radar-hrow{transition:background .13s ease;}
    .radar-hrow:hover{background:${C.bg1};}
    /* Chevron de fila (mockup: translateX(2px) + sube a t3 en hover) */
    .radar-chev{transition:transform .15s ease,color .15s ease;}
    .radar-hrow:hover .radar-chev{transform:translateX(2px);color:${C.t3};}
    /* Fila de bandeja/inbox (NEXA, WhatsApp): hover sutil salvo la seleccionada */
    .radar-row:hover{background:${C.bg3};}
    .radar-plus{transition:background .15s ease,color .15s ease,border-color .15s ease;}
    .radar-plus:hover{background:${C.primaryTint};color:${C.blue};border-color:${C.primaryBorder};}
    /* DS v3 — inputs/selects: hover borde fuerte + focus ring teal (receta 7.6) */
    .radar-input{transition:border-color .15s ease,box-shadow .15s ease;}
    .radar-input:hover{border-color:${C.border2};}
    .radar-input:focus{outline:none;border-color:${C.blue};box-shadow:0 0 0 3px ${C.ring};}
    /* Pill de toolbar/filtro (receta 7.4) */
    .radar-pill{transition:background .15s ease,border-color .15s ease,transform .12s ease,box-shadow .15s ease;}
    .radar-pill:hover{background:${C.bg3};border-color:${C.border2};transform:translateY(-1px);box-shadow:${SH.sm};}
    /* iOS Safari hace zoom al enfocar inputs con font-size<16px y descuadra los
       formularios en iPhone: forzamos 16px en móvil (auditoría, P3 mobile). */
    @media (max-width:768px){
      input:not([type=checkbox]):not([type=radio]), select, textarea{font-size:16px!important;}
      /* Las lineas del cotizador: en el telefono no caben cinco columnas, asi que
         la descripcion pasa a ocupar el ancho completo y debajo quedan cantidad,
         precio y descuento. Una grilla no hace wrap sola: hay que decirselo. */
      .cot-linea{grid-template-columns:1fr 1fr 1fr 22px!important;}
      .cot-linea > :first-child{grid-column:1 / -1;}
    }
    @media (prefers-reduced-motion: reduce){.radar-card,.radar-btn,.radar-pop,.radar-toast,.radar-skel,.radar-kpi,.radar-kcard{transition:none!important;transform:none!important;animation:none!important;}}
  `;
  (document.head||document.documentElement).appendChild(st);
}

// DS v3: cada estado lleva la tripleta pleno(color)/texto(text)/tinte(bg)/borde(border)
// (receta 7.5 del DESIGN-SPEC). color = pleno para dots; text sobre bg con border en chips.
const STATUS = {
  disponible:    { label:'Disponible',     color:'#16A34A', text:'#15803D', bg:'#F0FDF4', border:'#BBF7D0' },
  en_renta:      { label:'En Renta',       color:'#1D4ED8', text:'#1D4ED8', bg:'#EFF6FF', border:'#BFDBFE' },
  mantenimiento: { label:'Mantenimiento',  color:'#D97706', text:'#B45309', bg:'#FFFBEB', border:'#FDE68A' },
  reparacion:    { label:'Por Reparar',    color:'#DC2626', text:'#B91C1C', bg:'#FEF2F2', border:'#FECACA' },
  detenido:      { label:'Detenido',       color:'#DB2777', text:'#BE185D', bg:'#FDF2F8', border:'#FBCFE8' },
  inactivo:      { label:'Inactivo',       color:'#65748B', text:'#65748B', bg:'#F1F5F9', border:'#E4E9F0' },
  vendido:       { label:'Vendido',        color:'#56657F', text:'#56657F', bg:'#F1F5F9', border:'#E4E9F0' },
  desconocido:   { label:'Sin estado',     color:'#65748B', text:'#65748B', bg:'#F1F5F9', border:'#E4E9F0' },
};

// Íconos de tipo de equipo migrados a SVG (EQ_I / EquipIcon / tipoIconKey, definidos
// junto a los helpers de controles). Cero emojis en la UI de flota (DS v3).
const combColor = c => ({'Eléctrico':'#059669','Electrico':'#059669','Gas':'#D97706','Diésel':'#64748B','Diesel':'#64748B','Híbrido':'#7C3AED','Hibrido':'#7C3AED'})[c] || '#56657F';
// Title Case correcto (respeta acentos). NO usar \b: con acentos Unicode inserta
// limites de palabra falsos y rompe "EléCtrico"/"DiéSel". Capitaliza solo tras
// inicio, espacio, guion o slash. Global para reusar en Flota/Rentas/ficha.
const titleCase = s => String(s||'').toLowerCase().replace(/(^|[\s/-])(\p{L})/gu, (_,sep,ch)=>sep+ch.toUpperCase());
// Mini-badge reutilizable
const Pill = ({t,c='#56657F'}) => <span style={{fontSize:9,fontWeight:800,color:c,background:`${c}18`,border:`1px solid ${c}40`,borderRadius:5,padding:'1px 6px',whiteSpace:'nowrap',lineHeight:1.5}}>{t}</span>;
const capTxt = kg => kg ? `${(kg/1000).toFixed(kg%1000?1:0)}T` : null;
// ── Taxonomia canonica de tipo_equipo ────────────────────────────────────────
// Los tipos vienen de Firebase/SAP inconsistentes (MAYUS/minus, acentos, sinonimos).
// (a) fusiona sinonimos (Plataforma aerea == Elevadora); (b) agrupa torres de luz;
// (c) saca del filtro de TIPOS lo que no es maquina (Taller) o es otra dimension
// (Equipos de cliente, que pasa al filtro de Empresa).
const normTipo = s => String(s||'').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g,'');
// Clasificacion por CODIGO (autoritativa segun Fabrizio):
//  - Torre de Luz  = codigos que empiezan con "T" seguido de digito (T-02, T04). NO "ET".
//  - Electric Tugger = codigos que empiezan con "ET" (ET-15...). OJO: "Electro Tower"
//    en los datos NO es torre, es el Tugger.
const codeStr = e => String(e?.codigo||'').trim();
const esTugger = e => /^et-?\d/i.test(codeStr(e)) || /tugger|electro tower/.test(normTipo([e?.tipo_equipo,e?.modelo].filter(Boolean).join(' ')));
const esTorreLuz = e => !esTugger(e) && (/^t-?\d/i.test(codeStr(e)) || /torre de luz|light tower/.test(normTipo([e?.tipo_equipo,e?.modelo].filter(Boolean).join(' '))));
// Equipo de cliente: hoy solo se distingue por tipo_equipo (el sync no trae el flag).
const esEquipoCliente = e => e?.esEquipoCliente === true || ['equipos de cliente','equipo de cliente'].includes(normTipo(e?.tipo_equipo));
// tipo_equipo -> tipo canonico { canon, label } o null si NO va en el filtro de tipos.
const canonTipo = e => {
  if (esTugger(e))   return { canon:'tugger',    label:'Electric Tugger' };
  if (esTorreLuz(e)) return { canon:'torre_luz', label:'Torre de Luz' };
  const t = normTipo(e?.tipo_equipo);
  if (!t) return null;
  if (t === 'taller') return null;
  if (['equipos de cliente','equipo de cliente'].includes(t)) return null;
  if (t.includes('plataforma') || t.includes('elevad')) return { canon:'plataforma_aerea', label:'Plataforma Aérea' };
  if (t.includes('montacarga')) return { canon:'montacargas', label:'Montacargas' };
  if (t.includes('grua')) return { canon:'grua', label:'Grúa' };
  if (t === 'otro' || t === 'otros') return { canon:'otro', label:'Otro' };
  return { canon: t.replace(/\s+/g,'_'), label: titleCase(e.tipo_equipo) };
};
// Lo que NO se renta y por lo tanto no debe pesar en el reporte de flota: los
// vehiculos de la empresa (pick ups, paneles, camiones), los equipos del taller
// y los que son del cliente. Los nueve vehiculos estaban todos en "disponible",
// asi que inflaban la disponibilidad y hundian el porcentaje de la regla del 10%
// con equipos que nadie va a rentar nunca.
//
// Siguen visibles: si se filtra por Vehiculo, salen. Lo que cambia es que no
// entran en el conteo cuando no se pide ese tipo.
// El codigo NO identifica una maquina: hay un MT-23 en Monhaco y otro MT-23 en
// MT Rental, y son equipos distintos. Cuando Montasa cerro, sus equipos pasaron
// a Monhaco sin renumerar para no armar relajo en campo. Son 12 codigos que
// viven en las dos empresas.
//
// Lo que si identifica es la SERIE: se reviso y no hay una sola repetida entre
// empresas. Pero la serie no sirve para hablar ("traeme la B16090J00036"), asi
// que el codigo se muestra con la empresa pegada: MT-23-MC y MT-23-MTR.
const SUF_EMP = { mtrental:'MTR', honduras:'MTR', monhaco:'MC', monhagro:'MG', costarica:'CR' };
const codigoDe = e => {
  const c = String(e?.codigo || '').trim();
  const suf = SUF_EMP[e?.empresa_id];
  return c && suf ? `${c}-${suf}` : c;
};

const esVehiculo = e => /^veh[ií]culo/.test(normTipo(e?.tipo_equipo));
const esRentable = e => !esVehiculo(e) && !!canonTipo(e);

// Etiqueta canonica para MOSTRAR el tipo (torres salen 'Torre de Luz', no 'Otro').
const labelTipo = e => { const c = canonTipo(e); return c ? c.label : (esEquipoCliente(e) ? 'Equipos de Cliente' : titleCase(e?.tipo_equipo)); };

// ── Estilos compartidos de la ficha de oportunidad ───────────────────────────
// Rotulo de seccion: barra teal + texto oscuro (el gris claro no daba estructura).
// Etiqueta de dia para separar mensajes en los chats: 'Hoy', 'Ayer', '20 ago' o
// '20 ago 2025' si es de otro anio. Antes solo se mostraba la hora, asi que una
// conversacion de hace dias parecia de hoy.
const diaISO = (d) => { const x = new Date(d); return `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`; };
const etiquetaDia = (ts) => {
  if (!ts) return '';
  const d = new Date(ts), hoy = new Date();
  const ayer = new Date(hoy); ayer.setDate(hoy.getDate() - 1);
  if (diaISO(d) === diaISO(hoy)) return 'Hoy';
  if (diaISO(d) === diaISO(ayer)) return 'Ayer';
  const opts = { day:'numeric', month:'short' };
  if (d.getFullYear() !== hoy.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('es-HN', opts);
};
// Separador visual entre dias, con el mismo look de las burbujas de WhatsApp.
const SepDia = ({ ts }) => (
  <div style={{display:'flex',justifyContent:'center',margin:'14px 0 10px'}}>
    <span style={{background:'#FFFFFF',border:`1px solid ${C.border}`,color:C.t3,borderRadius:R.pill,
      padding:'3px 12px',fontSize:11,fontWeight:700,letterSpacing:'.02em',boxShadow:'0 1px 1.5px rgba(0,0,0,.06)',
      textTransform:'uppercase'}}>{etiquetaDia(ts)}</span>
  </div>
);
const SEC_LAB = { display:'flex', alignItems:'center', gap:9, fontSize:12, fontWeight:800,
                  color:C.t1, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:13 };
const SEC_BAR = { width:3, height:14, borderRadius:2, background:C.primary, flexShrink:0, display:'inline-block' };
const FLD_LAB = { fontSize:11, fontWeight:700, color:C.t2, marginBottom:5 };
const INP_ST  = { width:'100%', fontFamily:'inherit', fontSize:13, color:C.t1, background:C.bg2,
                  border:`1px solid ${C.border}`, borderRadius:R.md, padding:'8px 10px', outline:'none', cursor:'pointer' };
const INP_WARN= { borderColor:C.orangeBorder, background:C.orangeTint, color:C.orangeText, fontWeight:700 };

// Mapa IA aprobado: Dashboard + Comercial / Finanzas / Operaciones / Inventario /
// Análisis / Admin. Los módulos viejos viven ahora como tabs dentro de hubs
// (CRM, Mensajes, Cobros, Metas, Flota, Taller) — ids legacy redirigen al hub.
const NAV_GROUPS_ROL = {
  admin: [
    { id:'dashboard',     label:'Dashboard',   icon:'📊', single:true },
    { id:'g_comercial',   label:'Comercial',   icon:'💼', items:['plan','crm','agenda','mensajes','tickets'] },
    { id:'g_finanzas',    label:'Finanzas',    icon:'💳', items:['cobros'] },
    { id:'g_operaciones', label:'Operaciones', icon:'🔧', items:['flota','flota_cliente','taller','rentas','viaticos'] },
    { id:'g_inventario',  label:'Inventario',  icon:'📦', items:['inventario'] },
    { id:'g_analisis',    label:'Análisis',    icon:'📈', items:['metas','supervision','reportes'] },
    { id:'g_admin',       label:'Admin',       icon:'⚙️', items:['usuarios','sap','elsa'] },
  ],
  gerente_ventas: [
    { id:'dashboard',     label:'Dashboard',   icon:'📊', single:true },
    { id:'g_comercial',   label:'Comercial',   icon:'💼', items:['plan','crm','agenda','mensajes','tickets'] },
    { id:'g_finanzas',    label:'Finanzas',    icon:'💳', items:['cobros'] },
    { id:'g_operaciones', label:'Operaciones', icon:'🔧', items:['rentas','viaticos'] },
    { id:'g_analisis',    label:'Análisis',    icon:'📈', items:['metas','reportes'] },
    { id:'elsa',          label:'ELSA',        icon:'⚡', single:true },
  ],
  ventas: [
    { id:'dashboard',     label:'Dashboard',   icon:'📊', single:true },
    { id:'g_comercial',   label:'Comercial',   icon:'💼', items:['plan','crm','agenda','mensajes','tickets'] },
    { id:'g_analisis',    label:'Análisis',    icon:'📈', items:['metas'] },
    { id:'g_operaciones', label:'Operaciones', icon:'🔧', items:['flota','rentas','viaticos'] },
    { id:'elsa',          label:'ELSA',        icon:'⚡', single:true },
  ],
  taller: [
    { id:'dashboard',     label:'Dashboard',   icon:'📊', single:true },
    { id:'g_operaciones', label:'Operaciones', icon:'🔧', items:['flota','taller','tickets','viaticos'] },
  ],
  finanzas: [
    { id:'dashboard',     label:'Dashboard',   icon:'📊', single:true },
    { id:'g_finanzas',    label:'Finanzas',    icon:'💳', items:['cobros'] },
    { id:'g_operaciones', label:'Operaciones', icon:'🔧', items:['rentas'] },
    { id:'g_analisis',    label:'Análisis',    icon:'📈', items:['reportes'] },
    { id:'g_comercial',   label:'Comercial',   icon:'💼', items:['crm'] },
  ],
};
// Ids viejos -> hub donde viven hoy (permisos y deep-links viejos siguen valiendo).
const HUB_DE = {
  seguimiento:'agenda', actividades:'agenda', visitas:'agenda',
  whatsapp:'mensajes', sibila:'mensajes', campanias:'mensajes',
  clientes_inactivos:'crm', elevadoras:'crm',
  ingresos:'cobros', facturacion:'cobros', semana:'metas',
  forecast:'metas', mantenimiento:'flota', cotizaciones:'taller',
};
// Modulos que YA trae un rol por su nav base. Con esto no se ofrece como "adicional"
// algo que el rol ya incluye (ej. ventas ya tiene flota, rentas y viaticos).
const modsDeRol = rol => {
  const gs = NAV_GROUPS_ROL[rol] || NAV_GROUPS_ROL.ventas;
  const s = new Set();
  gs.forEach(g => { if (g.single) s.add(g.id); (g.items||[]).forEach(i=>s.add(i)); });
  return s;
};
const rolIncluye = (rol, modId) => modsDeRol(rol).has(HUB_DE[modId] || modId);
// ── Íconos SVG (estilo lucide, monocromo, heredan currentColor) ──────────────
// Reemplazan los emojis del nav — se ven idénticos en todo OS y suben la categoría
// del producto (hallazgo #1 de look del informe). Fallback al emoji si falta uno.
const ICON_PATHS = {
  dashboard:'M3 13h8V3H3zM13 21h8V3h-8zM3 21h8v-6H3z',
  flota:'M2 17h20M6 17V9l4-4h5l3 4v8M9 17a2 2 0 1 1-4 0M19 17a2 2 0 1 1-4 0',
  rentas:'M20 7H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1zM16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2',
  cotizaciones:'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 13h6M9 17h6',
  ingresos:'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  cobros:'M2 8h20a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM1 12h22M2 8l3-4h14l3 4',
  crm:'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  seguimiento:'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z',
  campanias:'M3 11l18-5v12L3 14v-3zM11.6 16.8a3 3 0 1 1-5.8-1.6',
  tickets:'M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2zM13 5v14',
  mantenimiento:'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.8 2.8-2-2z',
  reportes:'M3 3v18h18M7 16l4-6 4 3 5-7',
  whatsapp:'M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 21l2.1-5.4A8.5 8.5 0 1 1 21 11.5z',
  mensajes:'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  sibila:'M12 2l3 3-3 3-3-3zM12 16l3 3-3 3-3-3zM2 12l3-3 3 3-3 3zM16 12l3-3 3 3-3 3z',
  visitas:'M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0zM12 10a3 3 0 1 1 0-6 3 3 0 0 1 0 6z',
  waequipo:'M5 12.55a11 11 0 0 1 14 0M2 8.82a16 16 0 0 1 20 0M8.5 16.4a6 6 0 0 1 7 0M12 20h.01',
  elsa:'M13 2L3 14h9l-1 8 10-12h-9z',
  usuarios:'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  sap:'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5',
  inventario:'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.3 7L12 12l8.7-5M12 22V12',
  orgs:'M3 21h18M5 21V7l8-4v18M19 21V11l-6-3M9 9v.01M9 12v.01M9 15v.01M9 18v.01',
  taller:'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.8 2.8-2-2z',
  viaticos:'M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5a1 1 0 0 0-.9.55L2 12v4h2M7 16a2 2 0 1 0 4 0 2 2 0 0 0-4 0zM17 16a2 2 0 1 0 4 0 2 2 0 0 0-4 0z',
  forecast:'M12 2a10 10 0 1 0 10 10h-10z M12 2v10l7 3',
  metas:'M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22M18 2H6v7a6 6 0 0 0 12 0z',
  semana:'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  actividades:'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  agenda:'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  facturacion:'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  clientes_inactivos:'M18.36 6.64A9 9 0 1 1 5.64 6.64M12 2v10',
};
const Icon = ({ id, size=15, color='currentColor', style={} }) => {
  const d = ICON_PATHS[id];
  if (!d) { const e = NAV_META[id]?.icon || '•'; return <span style={{fontSize:size,...style}}>{e}</span>; }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,...style}} aria-hidden="true">
      {d.split('M').filter(Boolean).map((seg,i)=><path key={i} d={'M'+seg}/>)}
    </svg>
  );
};

// Equipo de ventas REAL (por primer nombre) — los demás usuarios (Omar, Fabrizio,
// Elia…) no son vendedores y ensuciaban los selectores. "Administracion" = venta de
// la casa. Mismo criterio que los reportes (VENDS_REPORTE en api/report.js).
const EQUIPO_VENTAS = ['Kevin', 'Audry', 'Daniel', 'Ricardo', 'Christian'];
const esVendedorReal = n => ORG.equipoVentas.includes(String(n || '').trim().split(' ')[0]);

const NAV_META = {
  dashboard:     { label:'Dashboard',     icon:'📊' },
  flota:         { label:'Flota',         icon:'🏗️' },
  flota_cliente: { label:'Flota Cliente', icon:'🏭' },
  rentas:        { label:'Rentas',        icon:'💼' },
  cotizaciones:  { label:'Órd. Trabajo',  icon:'🔧' },
  ingresos:      { label:'Ingresos',      icon:'💰' },
  cobros:        { label:'Finanzas', icon:'💳' },
  plan:          { label:'Plan Octubre', icon:'🎯' },
  crm:           { label:'CRM',           icon:'👥' },
  seguimiento:   { label:'Seguimiento',   icon:'📞' },
  campanias:     { label:'Campañas',       icon:'📣' },
  tickets:       { label:'Tickets',        icon:'🎫' },
  supervision:   { label:'Supervisión',    icon:'👁️' },
  mantenimiento: { label:'Mantenimiento', icon:'🔧' },
  reportes:      { label:'Reportes',      icon:'📈' },
  whatsapp:      { label:'WhatsApp',      icon:'💬' },
  mensajes:      { label:'Mensajes',      icon:'💬' },
  sibila:        { label:'NEXA (Redes)', icon:'🔷' },
  visitas:       { label:'Visitas',        icon:'📍' },
  waequipo:      { label:'WA Equipo',     icon:'📡' },
  elsa:          { label:'ELSA',          icon:'⚡' },
  usuarios:      { label:'Usuarios',      icon:'👤' },
  sap:           { label:'SAP B1',        icon:'🔗' },
  inventario:    { label:'Inventario',    icon:'📦' },
  taller:        { label:'Taller',        icon:'🔩' },
  viaticos:      { label:'Viáticos',      icon:'🚗' },
  metas:         { label:'Metas & Revisión', icon:'🏆' },
  semana:        { label:'Comité',        icon:'📅' },
  actividades:   { label:'Actividades',   icon:'📅' },
  agenda:        { label:'Agenda',        icon:'📅' },
  facturacion:   { label:'Facturación',   icon:'💰' },
  clientes_inactivos:{ label:'Sin Atender',   icon:'🔴' },
  orgs:              { label:'Organizaciones', icon:'🏢' },
};

const fmt  = n => `$${(n||0).toLocaleString('en-US')}`;
const dias = f => f ? Math.floor((Date.now()-new Date(f))/86400000) : 999;
const fecha= f => f ? new Date(f).toLocaleDateString('es-HN') : '—';

const ETAPAS_PROB = {prospecto:0.10,contactado:0.25,cotizacion:0.40,negociacion:0.60,cerrado:0.75,facturado:0.85,pagado:0.92,cerrado_ganado:1.00,cerrado_perdido:0.00,prospecto_invalido:0.00,cliente_frio:0.00};
// "Mantenimiento Flotas" y "Servicios Técnicos" eran lo mismo con dos nombres:
// se fusionaron en "Mano de Obra" (decisión del 3-ago-2026). El SQL de migración
// suma los montos históricos de ambas en la nueva y actualiza las llaves de
// comisión — sin eso, lo categorizado como Mano de Obra comisionaría 0%.
const LINEAS_NEGOCIO = ['Renta y Leasing Operativo','Renta por Hora','Mano de Obra','Venta de Equipo','Repuestos y Suministros','Fletes','Certificación de Operador','Revisiones IHTT'];
const SEGMENTOS = [{v:'A',l:'A — Estratégica (10+ unidades)'},{v:'B',l:'B — Clave (3-9 unidades)'},{v:'C',l:'C — Táctica (1-2 unidades)'},{v:'D',l:'D — Reposición'}];
const INICIATIVAS = ['AgroLift','InPark','ZOLI KAM','Flota Express','Hub Ventas','Expansión HN','Mayoreo'];

// ── Config de la organización (white-label multi-tenant) ──────────────────────
// ORG arranca con los valores de Montasa (org #1) como fallback. Al iniciar sesión
// se sobreescribe con la config de la org del usuario (tabla orgs.config). Así el
// app se comporta idéntico para Montasa y un cliente nuevo solo define lo suyo.
const MONTASA_ORG = '11111111-1111-4111-8111-111111111111'; // org del proveedor (crea clientes)
const ORG = {
  nombre: 'Montasa Handling Co.',
  marcaUpper: 'MONTASA HANDLING CO.',
  pais: 'Honduras', locale: 'es-HN',
  lineasNegocio: LINEAS_NEGOCIO, segmentos: SEGMENTOS, iniciativas: INICIATIVAS, equipoVentas: EQUIPO_VENTAS,
  // coti: membrete del PDF de cotización por empresa_id ({honduras:{...}, costarica:{...}}).
  // null = usa el membrete por defecto del backend (Montasa). Lo llena la config de la org.
  coti: null,
  // demo: true en orgs de demostracion. Los paneles con ventana de tiempo muestran
  // lo ultimo cargado en vez de filtrar por fecha, asi la demo nunca se ve vacia.
  demo: false,
  // ocultos: modulos que esta org NO muestra en el nav (config.modulos_ocultos).
  // En la org de demo se oculta 'reportes' por defecto: sale casi vacio y no aporta
  // a la demostracion. Se puede sobreescribir desde la config de la org.
  ocultos: [],
  // empresas: razones sociales / compañías SAP del tenant (pestañas HN/CR de Montasa).
  // Cada org la sobreescribe con las suyas desde config.empresas.
  empresas: [
    { id:'honduras',   label:'🏢 Monhaco Honduras',   color:'#0F766E' },
    { id:'costarica',  label:'🇨🇷 Montasa Costa Rica', color:'#059669' },
  ],
  // modulos: qué módulos tiene contratados la org. null = todos (Montasa).
  // Un cliente de CRM+inventario no debe ver Flota/Rentas/Taller ni los
  // equipos serializados: eso es del negocio de renta, no del suyo.
  modulos: null,
};
// ¿La org tiene este módulo? null = los tiene todos.
const orgTiene = (m) => !(ORG.ocultos||[]).includes(m) && (!ORG.modulos || ORG.modulos.includes(m));
// Linea de producto a partir del codigo. Monhaco codifica la categoria en el
// prefijo (R01 repuestos, L01 lubricantes, EPP proteccion personal...) y esa
// taxonomia es mas util para el vendedor que las "familias" que manda SAP.
// D3 (Sprint 1): disposiciones al completar una actividad de cliente.
// CONTACTO SIGNIFICATIVO = actividad completada + disposicion valida
// (cualquiera excepto SIN_RESPUESTA). La nota es control anti-vacio,
// no la definicion comercial.
const DISPOSICIONES = [
  ['NECESIDAD_IDENTIFICADA','Necesidad identificada'],
  ['SEGUIMIENTO_PROYECTO','Seguimiento de proyecto'],
  ['REVISION_OPERACIONAL','Revisión operacional'],
  ['REVISION_CONTRACTUAL','Revisión contractual'],
  ['COTIZACION_DISCUTIDA','Cotización discutida'],
  ['PROXIMA_REUNION_ACORDADA','Próxima reunión acordada'],
  ['RENOVACION','Renovación'],
  ['PROBLEMA_IDENTIFICADO','Problema identificado'],
  ['SIN_NECESIDAD_ACTUAL_CONTACTO_REALIZADO','Sin necesidad actual (contacto realizado)'],
  ['SIN_RESPUESTA','Sin respuesta (no cuenta como contacto)'],
];
const lineaDe = (codigo) => {
  const c = String(codigo || '').trim().toUpperCase();
  if (!c) return '';
  const pre = c.split('-')[0];
  return pre.length <= 5 ? pre : c.slice(0, 3);
};
// Snapshot de los defaults. ORG es un objeto global mutable: al cambiar de org
// hay que volver a los defaults ANTES de aplicar la config nueva, porque los
// campos que la org nueva no define (ej. modulos:null = los tiene todos) se
// quedarian con el valor de la org anterior. Asi le desaparecio Flota/Taller a
// Montasa despues de entrar a la org demo en la misma pestana.
const ORG_DEFAULTS = JSON.parse(JSON.stringify(ORG));
async function cargarOrgConfig(orgId) {
  if (!orgId) return;
  Object.assign(ORG, JSON.parse(JSON.stringify(ORG_DEFAULTS)));
  try {
    const { data } = await supabase.from('orgs').select('nombre,config').eq('id', orgId).maybeSingle();
    if (!data) return;
    const c = data.config || {};
    if (data.nombre) ORG.nombre = data.nombre;
    // Org de DEMO: los paneles con ventana de tiempo (actividad de hoy, leads de la
    // semana) ignoran la fecha y muestran lo ultimo cargado, para que la data
    // sembrada no se vea vacia al pasar los dias. Se activa por config.demo o por
    // el nombre de la org.
    ORG.demo = c.demo === true || /\bdemo\b/i.test(String(data.nombre||''));
    ORG.ocultos = Array.isArray(c.modulos_ocultos) ? c.modulos_ocultos : (ORG.demo ? ['reportes'] : []);
    if (c.marca_upper) ORG.marcaUpper = c.marca_upper;
    if (c.pais) ORG.pais = c.pais;
    if (c.locale) ORG.locale = c.locale;
    if (Array.isArray(c.lineas_negocio) && c.lineas_negocio.length) ORG.lineasNegocio = c.lineas_negocio;
    if (Array.isArray(c.segmentos) && c.segmentos.length) ORG.segmentos = c.segmentos;
    if (Array.isArray(c.iniciativas) && c.iniciativas.length) ORG.iniciativas = c.iniciativas;
    if (Array.isArray(c.equipo_ventas) && c.equipo_ventas.length) ORG.equipoVentas = c.equipo_ventas;
    if (c.coti_empresas && typeof c.coti_empresas === 'object') ORG.coti = c.coti_empresas;
    if (Array.isArray(c.empresas) && c.empresas.length) ORG.empresas = c.empresas;
    if (Array.isArray(c.modulos) && c.modulos.length) ORG.modulos = c.modulos;
  } catch (_) { /* sin config: se queda con los defaults */ }
}
// Etiqueta de una empresa. UNA sola fuente (ORG.empresas) para que la misma
// empresa no se llame distinto segun la pantalla: el panel SAP decia "Monhaco
// Honduras" y el modal de cotizacion "Montasa HN" para la MISMA base, y ahi el
// riesgo es emitir un documento fiscal con la empresa equivocada.
const nombreEmpresa = (eid) => ORG.empresas.find(e => e.id === eid)?.label || eid;

// ¿El nombre sirve para buscar por "contiene"? Sin esto, un cliente roto
// llamado "." mostraba en su ficha las facturas de MEDIA empresa (todo
// nombre con punto calzaba). Minimo 4 caracteres reales.
const nombreBuscable = (n) => String(n||'').replace(/[^a-zA-Z0-9\u00C0-\u024F]/g,'').length >= 4;
// Escapar comodines de PostgREST para que un nombre con % o _ no barra la tabla.
const escLike = (n) => String(n||'').replace(/[%_]/g, '\\$&');

// AVISARLE A ALGUIEN. Existe porque los seis inserts a notificaciones que habia
// repartidos por la app fallaban TODOS en silencio, cada uno por su cuenta:
//   - 'titulo' es NOT NULL y ninguno lo mandaba
//   - tres mandaban 'opp_id', que no es una columna de esa tabla
// y los seis cerraban con .then(()=>{}) sin mirar el error. Resultado: en toda
// la vida de RADAR hubo CERO notificaciones de asignacion. El vendedor se
// enteraba de que le pasaron un cliente cuando el cliente le llamaba.
// FALTABA org_id Y POR ESO SEGUIA SIN FUNCIONAR. La tabla tiene RLS con
// check (org_id = current_org_id()): una fila sin org_id no pasa, y el insert
// se rechazaba igual que antes, nada mas que ahora el error si se imprimia.
// Las 49 notificaciones que existen las escribieron los endpoints del servidor,
// que si lo mandan. Ninguna nacio dentro de la app.
async function notificar({ para, tipo, titulo, mensaje, modulo = null, refId = null, orgId = null }) {
  if (!para || !titulo) return false;
  const { error } = await supabase.from('notificaciones').insert({
    ...(orgId ? { org_id: orgId } : {}),
    destinatario: para, tipo, titulo, mensaje: mensaje || null,
    link_modulo: modulo, link_id: refId, leida: false,
  });
  if (error) { console.error('notificar:', error.message); return false; }
  return true;
}

// Empresas cuyos clientes NO vienen del sync de RADAR. RADAR sincroniza clientes
// de la base HN y de la de CR, asi que para esas dos ya conoce el CardCode. Para
// cualquier otra base (Monhagro, MONTASA SPS...) el codigo hay que ponerlo a mano,
// porque el que RADAR tiene es el de la base HN y en ESA base no existe.
const emisoraRequiereCardCode = (eid) => !!eid && eid !== 'honduras' && eid !== 'costarica';

// LAS COTIZACIONES SE LEEN DE LA BASE, NO DE SAP EN VIVO.
// Pedirlas a SAP en cada ficha tardaba segundos y solo alcanzaba los ultimos
// meses: el cliente de Fernando Sacasa tenia seis cotizaciones y la ficha decia
// "sin cotizaciones" porque cinco eran de enero de 2024. Ahora las baja el
// sync (api/sync-cotizaciones.js) y aca solo se leen.
//
// Devuelve la MISMA forma que devolvia /api/sap, para no tocar nada de lo que
// las pinta. Si el cliente tiene varios registros se pasan todos sus ids.
async function cotizacionesDe({ clienteIds = [], sapCode = null, empresaId = null }) {
  let q = supabase.from('cotizaciones_sap')
    .select('doc_num,doc_entry,doc_date,doc_due_date,moneda,doc_total,total_usd,estado,anulada,comentarios,empresa_id')
    .order('doc_date', { ascending: false }).limit(60);
  const ids = (clienteIds || []).filter(Boolean);
  if (ids.length) q = q.in('cliente_id', ids);
  else if (sapCode) {
    // Respaldo para el cliente que todavia no quedo enlazado en el sync.
    q = q.eq('card_code', String(sapCode).replace(/^[a-z]+_/, ''));
    if (empresaId) q = q.eq('empresa_id', empresaId);
  } else return { lista: [], error: null };

  const { data, error } = await q;
  if (error) return { lista: [], error: error.message };
  return {
    lista: (data || []).map(c => ({
      docEntry: c.doc_entry, docNum: c.doc_num,
      fecha: c.doc_date || '', vence: c.doc_due_date || '',
      moneda: c.moneda, total: c.doc_total, totalUsd: c.total_usd,
      estado: c.anulada ? 'anulada' : (c.estado === 'bost_Open' ? 'abierta' : 'cerrada'),
      comentarios: c.comentarios, empresa_id: c.empresa_id,
    })),
    error: null,
  };
}

const SEG_COLOR = {A:'#10B981',B:'#0B7AB8',C:'#F59E0B',D:'#64748B'};

function NavDropdown({ group, mod, setMod, badge, badges }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hasActive = group.items?.some(id => id === mod);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{position:'relative'}}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{height:54,padding:'0 11px',border:'none',cursor:'pointer',background:'transparent',
          color:hasActive?C.t1:C.t4,borderBottom:`2px solid ${hasActive?C.blue:'transparent'}`,
          fontSize:12,fontWeight:hasActive?700:400,display:'flex',alignItems:'center',gap:5,
          transition:'all .15s',whiteSpace:'nowrap',position:'relative'}}
      >
        <span style={{fontSize:14}}>{group.icon}</span>
        {group.label}
        <span style={{fontSize:9,marginLeft:2,opacity:0.6}}>{open?'▲':'▼'}</span>
        {badge>0&&<span style={{position:'absolute',top:8,right:2,background:C.red,color:'#fff',borderRadius:'50%',width:15,height:15,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:900}}>{badge>9?'9+':badge}</span>}
      </button>
      {open && (()=>{
        // position:fixed (no absolute): el nav tiene overflow para scrollear en móvil
        // y un menú absolute quedaría recortado/invisible dentro de él.
        const r = ref.current?.getBoundingClientRect();
        const left = Math.min(r?.left||0, (typeof window!=='undefined'?window.innerWidth:9999)-196);
        return (
        <div style={{position:'fixed',top:(r?.bottom||54)+2,left,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:10,padding:'6px',zIndex:1200,minWidth:180,maxHeight:'70vh',overflowY:'auto',boxShadow:'0 8px 32px rgba(0,0,0,0.4)'}}>
          {group.items.map(id => {
            const item = NAV_META[id];
            const act = mod === id;
            return (
              <button key={id} onClick={() => { setMod(id); setOpen(false); }}
                style={{width:'100%',display:'flex',alignItems:'center',gap:8,padding:'9px 12px',border:'none',cursor:'pointer',
                  background:act?`${C.blue}20`:'transparent',borderRadius:7,color:act?C.t1:C.t3,
                  fontSize:13,fontWeight:act?700:400,textAlign:'left',transition:'background .1s'}}
                onMouseEnter={e=>{ if(!act) e.currentTarget.style.background=C.bg2; }}
                onMouseLeave={e=>{ if(!act) e.currentTarget.style.background='transparent'; }}
              >
                <Icon id={id} size={16}/>{item.label}
                {badges?.[id]>0
                  ? <span style={{marginLeft:'auto',background:C.red,color:'#fff',borderRadius:10,minWidth:17,height:16,padding:'0 5px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:900,flexShrink:0}}>{badges[id]>99?'99+':badges[id]}</span>
                  : act && <span style={{marginLeft:'auto',width:6,height:6,borderRadius:'50%',background:C.blue,flexShrink:0}}/>}
              </button>
            );
          })}
        </div>
        );
      })()}
    </div>
  );
}

// ─── BASE UI ──────────────────────────────────────────────────────────────────
// TOASTS (reemplazo de alert(): aviso flotante moderno, no bloquea).
// toast('mensaje') desde cualquier componente. Rojo si parece error, oscuro si no.
const toast = (msg) => {
  try { window.dispatchEvent(new CustomEvent('radar-toast', { detail: { msg: String(msg) } })); }
  catch { console.error('toast:', msg); }
};
function Toasts() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const h = e => {
      const id = Math.random().toString(36).slice(2);
      const msg = e.detail?.msg || '';
      setItems(ts => [...ts.slice(-3), { id, msg }]);
      setTimeout(() => setItems(ts => ts.filter(t => t.id !== id)), 5500);
    };
    window.addEventListener('radar-toast', h);
    return () => window.removeEventListener('radar-toast', h);
  }, []);
  if (!items.length) return null;
  return (
    <div style={{position:'fixed',bottom:24,left:'50%',transform:'translateX(-50%)',zIndex:5000,display:'flex',flexDirection:'column',gap:8,alignItems:'center',pointerEvents:'none',maxWidth:'92vw'}}>
      {items.map(t => {
        // El aviso de error se detectaba solo por como EMPIEZA el mensaje, asi
        // que "SAP: SAP rechazo la orden de trabajo..." salia con un tilde verde
        // de exito. Un error disfrazado de confirmacion es peor que no avisar.
        const m = t.msg.trim();
        const esError = /^(no se pudo|no hay|no encuentro|error|⚠|❌|ese |fall)/i.test(m)
          || /(rechaz|obligatori|no se puede|invalid|no fue posible|ya existe|ya tiene)/i.test(m);
        return (
          <div key={t.id} className="radar-toast" onClick={()=>setItems(ts=>ts.filter(x=>x.id!==t.id))}
            style={{background:esError?'#DC2626':'#0F172A',color:'#fff',borderRadius:12,padding:'12px 18px',fontSize:13.5,fontWeight:600,
              boxShadow:'0 12px 32px rgba(0,0,0,.28)',maxWidth:480,pointerEvents:'auto',cursor:'pointer',lineHeight:1.45}}>
            {esError?'⚠️ ':'✓ '}{t.msg}
          </div>
        );
      })}
    </div>
  );
}

// CONFIRMACIONES (reemplazo de window.confirm(): modal propio, devuelve Promise<boolean>).
// await confirmar('¿Seguro?', { ok:'Eliminar', peligro:true })
let _confirmResolve = null;
let _setConfirmState = null;
const confirmar = (msg, { ok = 'Sí, continuar', cancel = 'Cancelar', peligro = false } = {}) => new Promise(res => {
  if (!_setConfirmState) { res(window.confirm(msg)); return; }  // fallback si aún no montó
  _confirmResolve = res;
  _setConfirmState({ msg, ok, cancel, peligro });
});
function Confirmes() {
  const [st, setSt] = useState(null);
  useEffect(() => { _setConfirmState = setSt; return () => { _setConfirmState = null; }; }, []);
  const responder = (v) => { setSt(null); const r = _confirmResolve; _confirmResolve = null; if (r) r(v); };
  useEffect(() => {
    if (!st) return;
    const h = e => { if (e.key === 'Escape') responder(false); if (e.key === 'Enter') responder(true); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });
  if (!st) return null;
  return (
    <div onClick={()=>responder(false)} style={{position:'fixed',inset:0,background:'rgba(15,23,42,.5)',zIndex:4000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:16,padding:'24px 26px',width:'min(400px,100%)',boxShadow:'0 24px 64px rgba(0,0,0,.3)',animation:'fadeUp .15s ease'}}>
        <div style={{fontSize:30,marginBottom:10}}>{st.peligro?'🗑️':'❓'}</div>
        <div style={{fontSize:14.5,fontWeight:700,color:C.t1,lineHeight:1.5,marginBottom:18}}>{st.msg}</div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={()=>responder(false)} style={{padding:'9px 18px',borderRadius:9,border:`1px solid ${C.border}`,background:C.bg2,color:C.t3,fontSize:13,fontWeight:700,cursor:'pointer'}}>{st.cancel}</button>
          <button autoFocus onClick={()=>responder(true)} style={{padding:'9px 18px',borderRadius:9,border:'none',background:st.peligro?C.red:C.blue,color:'#fff',fontSize:13,fontWeight:800,cursor:'pointer'}}>{st.ok}</button>
        </div>
      </div>
    </div>
  );
}

function Modal({ open, onClose, title, children, width=640 }) {
  useEffect(()=>{
    if (!open) return;
    const handler = e => { if (e.key==='Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  },[open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(15,23,42,.6)',backdropFilter:'blur(2px)',WebkitBackdropFilter:'blur(2px)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div className="radar-pop" onClick={e=>e.stopPropagation()} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,width:'100%',maxWidth:width,maxHeight:'90vh',overflowY:'auto',boxShadow:SH.lg}}>
        <div style={{padding:'18px 24px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center',position:'sticky',top:0,background:C.bg2,zIndex:1}}>
          <div style={{fontSize:17,fontWeight:800,color:C.t1}}>{title}</div>
          <button onClick={onClose} style={{background:'none',border:'none',color:C.t4,fontSize:22,cursor:'pointer',lineHeight:1}}>×</button>
        </div>
        <div style={{padding:24}}>{children}</div>
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, type='text', placeholder='', required, rows }) {
  const base = {width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px',color:C.t1,fontSize:14,outline:'none',fontFamily:'inherit'};
  return (
    <div style={{marginBottom:14}}>
      {label && <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:5}}>{label}{required?' *':''}</div>}
      {rows
        ? <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows} style={{...base,resize:'vertical'}}/>
        : <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={base}/>}
    </div>
  );
}

function Sel({ label, value, onChange, options, required }) {
  return (
    <div style={{marginBottom:14}}>
      {label && <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:5}}>{label}{required?' *':''}</div>}
      <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px',color:C.t1,fontSize:14,outline:'none'}}>
        <option value="">— Seleccionar —</option>
        {options.map(o=>typeof o==='string'
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function SearchSel({ label, value, onChange, options, required, placeholder='Buscar...' }) {
  const [busq, setBusq] = useState('');
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.v === value);
  // BUSCAR COMO ESCRIBE LA GENTE, NO COMO ESTA GUARDADO.
  // Comparaba texto literal, asi que "z60" no encontraba "Z-60/37 FE" y parecia
  // que la maquina no existia. Los modelos estan escritos de mil formas -Z33/18,
  // Z-60/37 FE, GS4069RT, KBG-50-, y nadie se acuerda de los guiones.
  // Se comparan ambos lados sin acentos, guiones, barras ni espacios, y cada
  // palabra por separado: "genie z60" y "mt 130" tambien encuentran.
  const norm = t => String(t||'').toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]/g,'');
  const terminos = busq.trim().split(/\s+/).filter(Boolean).map(norm).filter(Boolean);
  const filtrados = !terminos.length ? options
    : options.filter(o => { const l = norm(o.l); return terminos.every(t => l.includes(t)); });

  return (
    <div style={{marginBottom:14,position:'relative'}}>
      {label && <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:5}}>{label}{required?' *':''}</div>}
      <div onClick={()=>setOpen(v=>!v)} style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${open?C.blue:C.border}`,borderRadius:8,padding:'10px 12px',color:selected?C.t1:C.t4,fontSize:14,cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <span style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{selected ? selected.l : '— Seleccionar —'}</span>
        <span style={{fontSize:10,color:C.t4,marginLeft:8,flexShrink:0}}>{open?'▲':'▼'}</span>
      </div>
      {open && (
        <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:200,background:C.bg2,border:`1px solid ${C.blue}`,borderRadius:8,marginTop:4,overflow:'hidden',boxShadow:'0 8px 32px rgba(0,0,0,0.4)'}}>
          <div style={{padding:8,borderBottom:`1px solid ${C.border}`}}>
            <input autoFocus value={busq} onChange={e=>setBusq(e.target.value)} placeholder={placeholder}
              style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.t1,fontSize:13,outline:'none'}}/>
          </div>
          <div style={{maxHeight:220,overflowY:'auto'}}>
            {filtrados.length===0
              ? <div style={{padding:'12px 14px',color:C.t4,fontSize:13}}>Sin resultados</div>
              : filtrados.map(o=>(
                <div key={o.v} onClick={()=>{onChange(o.v);setOpen(false);setBusq('');}}
                  style={{padding:'10px 14px',fontSize:13,color:o.v===value?C.blue:C.t1,background:o.v===value?`${C.blue}15`:'transparent',cursor:'pointer',fontWeight:o.v===value?700:400}}
                  onMouseEnter={e=>e.currentTarget.style.background=`${C.blue}10`}
                  onMouseLeave={e=>e.currentTarget.style.background=o.v===value?`${C.blue}15`:'transparent'}>
                  {o.l}
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

function Btn({ children, onClick, variant='primary', size='md', disabled, full, style={} }) {
  // DS v3: primario teal sólido con sombra primaria (receta 7.4 del DESIGN-SPEC).
  const bg = disabled ? C.bg3
    : variant==='danger'    ? C.red
    : variant==='secondary' ? 'transparent'
    : variant==='warn'      ? C.orange
    : C.blue;
  const pad = size==='sm' ? '6px 13px' : size==='lg' ? '12px 24px' : '8px 16px';
  // color: los rellenos (primary/danger/warn) llevan texto BLANCO — antes usaban C.t1
  // (casi negro) con contraste ~2.5:1, ilegible (bug P1 auditoría).
  const fg = disabled ? C.t4 : variant==='secondary' ? C.t2 : '#fff';
  const shadow = variant==='secondary'||disabled ? 'none'
    : variant==='primary' ? `${SH.primary}, inset 0 1px 0 rgba(255,255,255,.12)`
    : `${SH.sm}, inset 0 1px 0 rgba(255,255,255,.12)`;
  return (
    <button className="radar-btn" onClick={onClick} disabled={disabled} style={{background:bg,color:fg,border:variant==='secondary'?`1px solid ${C.border}`:'none',borderRadius:R.md,padding:pad,fontSize:size==='sm'?12:13,fontWeight:600,letterSpacing:'.005em',cursor:disabled?'not-allowed':'pointer',opacity:disabled?.6:1,width:full?'100%':'auto',boxShadow:shadow,...style}}>
      {children}
    </button>
  );
}

function Badge({ estado }) {
  // DS v3 receta 7.5: pill tintado con dot 6px del pleno + texto oscuro sobre tinte + borde.
  const s = STATUS[estado] || STATUS.desconocido;
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:6,background:s.bg,color:s.text,border:`1px solid ${s.border}`,borderRadius:R.pill,padding:'2px 9px',fontSize:10.5,fontWeight:600,whiteSpace:'nowrap'}}>
      <span style={{width:6,height:6,borderRadius:R.pill,background:s.color,flexShrink:0}}/>{s.label}
    </span>
  );
}

// Skeletons de carga — reemplazan el texto "Cargando…" por barras con shimmer.
const Skel = ({ w='100%', h=14, r=8, style={} }) => <div className="radar-skel" style={{width:w,height:h,borderRadius:r,...style}}/>;
// Bloque de tarjetas KPI fantasma (para el primer render de un módulo).
const SkelCards = ({ n=4 }) => (
  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10,marginBottom:14}}>
    {Array.from({length:n}).map((_,i)=>(
      <div key={i} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'16px 18px'}}>
        <Skel w="52%" h={9}/><Skel w="70%" h={22} style={{marginTop:10}}/><Skel w="40%" h={9} style={{marginTop:8}}/>
      </div>
    ))}
  </div>
);
// Filas fantasma para tablas/listas.
const SkelRows = ({ n=6 }) => (
  <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:14}}>
    {Array.from({length:n}).map((_,i)=>(
      <div key={i} style={{display:'flex',gap:12,alignItems:'center',padding:'9px 4px',borderBottom:i<n-1?`1px solid ${C.border}55`:'none'}}>
        <Skel w={34} h={34} r={R.pill}/><Skel w="30%" h={12}/><Skel w="18%" h={12} style={{marginLeft:'auto'}}/><Skel w="12%" h={12}/>
      </div>
    ))}
  </div>
);

function Stat({ label, value, sub, color=C.blue }) {
  return (
    <div className="radar-card" style={{background:`linear-gradient(180deg,${C.bg2},${C.bg1})`,border:`1px solid ${C.border}`,borderTop:`2px solid ${color}`,borderRadius:R.lg,padding:'18px 22px',boxShadow:SH.md}}>
      <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>{label}</div>
      <div className="radar-num" style={{fontSize:34,fontWeight:900,color,lineHeight:1,marginBottom:4}}>{value}</div>
      {sub && <div style={{fontSize:12,color:C.t3}}>{sub}</div>}
    </div>
  );
}

// ─── DS v3: componentes visuales compartidos (KPI Stripe, tags LOB, avatares) ───
// Tripletas semánticas (texto / tinte / borde) — receta 1.3 del DESIGN-SPEC.
const TONES = {
  primary:{ text:C.blue,       tint:C.primaryTint, border:C.primaryBorder, solid:C.blue },
  green:  { text:C.greenText,  tint:C.greenTint,   border:C.greenBorder,   solid:C.green },
  orange: { text:C.orangeText, tint:C.orangeTint,  border:C.orangeBorder,  solid:C.orange },
  red:    { text:C.redText,    tint:C.redTint,     border:C.redBorder,     solid:C.red },
  info:   { text:C.infoText,   tint:C.infoTint,    border:C.infoBorder,    solid:C.info },
  neutral:{ text:C.t3,         tint:C.bg3,         border:C.border,        solid:C.t4 },
};
// Íconos stroke 1.5 (estilo feather) para las KPI cards.
const KI = {
  dollar: <><circle cx="12" cy="12" r="9"/><path d="M12 7v10M9.5 9.7h3.6a1.8 1.8 0 010 3.6h-2.2a1.8 1.8 0 000 3.6H15"/></>,
  funnel: <path d="M4 5h16l-6.2 7.2V18l-3.6 2v-7.8L4 5z"/>,
  clock:  <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  tool:   <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>,
  trend:  <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
  box:    <><path d="M21 8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.3 7 12 12 20.7 7"/><line x1="12" y1="22" x2="12" y2="12"/></>,
  phone:  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/>,
  check:  <><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.27"/></>,
  users:  <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>,
};
const KIcon = ({ d, tone }) => (
  <span style={{width:28,height:28,borderRadius:8,background:tone.tint,border:`1px solid ${tone.border}`,color:tone.text,display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
  </span>
);
// Mini sparkline SVG (polyline apagada + punto final) — receta 7.1.
const Spark = ({ points, color=C.blue }) => {
  if (!points || points.length < 2) return null;
  const w=64, h=26, max=Math.max(...points), min=Math.min(...points), rng=(max-min)||1;
  const xy = points.map((v,i)=>[ (i/(points.length-1))*(w-4)+2, h-4-((v-min)/rng)*(h-9) ]);
  return (
    <svg width={w} height={h} style={{flexShrink:0,overflow:'visible'}} aria-hidden="true">
      <polyline points={xy.map(p=>p.join(',')).join(' ')} fill="none" stroke={color} strokeWidth="1.5" opacity=".42" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={xy[xy.length-1][0]} cy={xy[xy.length-1][1]} r="2" fill={color}/>
    </svg>
  );
};
// KPI card estilo Stripe — receta 7.1 del DESIGN-SPEC.
function KpiCard({ label, value, sub, icon, tone='primary', spark, delta, deltaTone='neutral', onClick }) {
  const t  = TONES[tone] || TONES.primary;
  const dt = TONES[deltaTone] || TONES.neutral;
  return (
    <div className="radar-kpi" onClick={onClick}
      style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',boxShadow:SH.sm,cursor:onClick?'pointer':'default',minWidth:0,display:'flex',flexDirection:'column',gap:10}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
        <span style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{label}</span>
        {icon && <KIcon d={icon} tone={t}/>}
      </div>
      <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',gap:8}}>
        <div style={{minWidth:0}}>
          <div className="radar-num" style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',color:C.t1,lineHeight:1.05}}>{value}</div>
          {(delta || sub) && (
            <div style={{display:'flex',alignItems:'center',gap:6,marginTop:7,flexWrap:'wrap'}}>
              {delta && <span className="radar-num" style={{height:18,display:'inline-flex',alignItems:'center',padding:'0 7px',borderRadius:R.pill,fontSize:11,fontWeight:600,background:dt.tint,color:dt.text,border:`1px solid ${dt.border}`,whiteSpace:'nowrap'}}>{delta}</span>}
              {sub && <span style={{fontSize:11,color:C.t4}}>{sub}</span>}
            </div>
          )}
        </div>
        {spark}
      </div>
    </div>
  );
}
// Identidad por línea de negocio (C.lob) — keyword-match sobre el texto libre.
const lobOf = (l) => {
  const s = (l||'').toLowerCase();
  if (!s) return null;
  if (s.includes('rent')) return C.lob.renta;
  if (s.includes('serv') || s.includes('mant') || s.includes('taller')) return C.lob.servicio;
  if (s.includes('repuest') || s.includes('part') || s.includes('accesor')) return C.lob.repuestos;
  if (s.includes('vent') || s.includes('equipo')) return C.lob.venta;
  return null;
};
const LobTag = ({ l, max=110 }) => {
  if (!l) return null;
  const lob = lobOf(l) || { text:C.t3, bg:C.bg3, border:C.border };
  return <span title={l} style={{background:lob.bg,color:lob.text,border:`1px solid ${lob.border}`,borderRadius:R.sm,padding:'1px 7px',fontSize:10,fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:max,lineHeight:1.6}}>{l}</span>;
};
// Avatar chip con iniciales (color pleno estable por nombre).
const AV_COLORS = ['#0F766E','#1D4ED8','#6D28D9','#C2410C','#BE185D','#15803D','#B45309'];
// Chips de vendedor con avatar — reemplazo de los <select> "Todos los
// vendedores" (Fabrizio: "no somos excel"). value: ''/null = todos.
function VendChips({ vendedores = [], value, onChange, counts = null, todosLabel = 'Todos', extra = [] }) {
  // Multi-seleccion si value es array; single si es string (compat hacia atras).
  const multi = Array.isArray(value);
  const sel = multi ? value : (value ? [value] : []);
  const toggle = (v) => {
    if (!v) { onChange(multi ? [] : ''); return; }                 // "Todos" limpia
    if (multi) onChange(sel.includes(v) ? sel.filter(x => x !== v) : [...sel, v]);
    else onChange((value || '') === v ? '' : v);
  };
  const Chip = ({ v, label, avatar }) => {
    const on = v ? sel.includes(v) : sel.length === 0;             // "Todos" activo si no hay seleccion
    const n = counts ? counts(v) : null;
    return (
      <button onClick={() => toggle(v)}
        style={{display:'inline-flex',alignItems:'center',gap:6,height:32,padding:avatar?'0 12px 0 5px':'0 13px',borderRadius:999,
          fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',
          background:on?C.primaryTint:C.bg2,border:`1px solid ${on?C.primaryBorder:C.border}`,color:on?C.primary:C.t3,boxShadow:SH.xs}}>
        {avatar && <AvatarChip name={label} size={22}/>}
        {avatar ? label.split(' ')[0] : label}
        {n != null && <span className="radar-num" style={{fontSize:10.5,fontWeight:800,opacity:.65}}>{n}</span>}
      </button>
    );
  };
  return (
    <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
      <Chip v="" label={multi && sel.length ? `${todosLabel} (${sel.length})` : todosLabel}/>
      {vendedores.map(v => <Chip key={v} v={v} label={v} avatar/>)}
      {extra.map(([v,l]) => <Chip key={v} v={v} label={l}/>)}
    </div>
  );
}

// Dropdown de multi-seleccion reusable (reemplaza los <select> de FILTRO por
// uno donde se puede escoger uno o varios). value = array; [] = todos.
// options: array de strings o {value,label}. counts(v) opcional para el conteo.
function MultiSelect({ options = [], value = [], onChange, label = 'Filtrar', width = 210, counts = null }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, [open]);
  const opts = options.map(o => (typeof o === 'string' || typeof o === 'number') ? { value: o, label: String(o) } : o);
  const activo = value.length > 0;
  const allVals = opts.map(o => o.value);
  const allOn = allVals.length > 0 && value.length === allVals.length;  // todos marcados
  const toggle = v => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  const resumen = !activo ? label
    : value.length === 1 ? (opts.find(o => o.value === value[0])?.label || String(value[0]))
    : `${label}: ${value.length}`;
  return (
    <div ref={ref} style={{ position:'relative', display:'inline-block' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display:'inline-flex',alignItems:'center',gap:6,height:32,padding:'0 12px',borderRadius:R.md,
          background:activo?C.primaryTint:C.bg2,color:activo?C.primary:C.t2,border:`1px solid ${activo?C.primaryBorder:C.border}`,
          fontSize:12,fontWeight:activo?700:500,cursor:'pointer',whiteSpace:'nowrap',boxShadow:SH.xs }}>
        {resumen}<span style={{ opacity:.55,fontSize:9 }}>▾</span>
      </button>
      {open && (
        <div style={{ position:'absolute',top:36,left:0,zIndex:1200,minWidth:width,maxHeight:320,overflowY:'auto',
          background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,boxShadow:SH.lg,padding:6 }}>
          {/* "Todos" marca TODAS las opciones; despues destildas las que no querer.
              Si ya estan todas, vuelve a limpiar. (value=[] tambien = todos en el filtro.) */}
          <button onClick={() => onChange(allOn ? [] : allVals)}
            style={{ display:'flex',alignItems:'center',gap:8,width:'100%',textAlign:'left',padding:'7px 10px',borderRadius:6,border:'none',
              background:(allOn||!activo)?C.primaryTint:'transparent',color:(allOn||!activo)?C.primary:C.t2,fontSize:12,fontWeight:700,cursor:'pointer' }}>
            <span style={{ width:15,height:15,borderRadius:4,border:`1.5px solid ${(allOn||!activo)?C.primary:C.border}`,background:(allOn||!activo)?C.primary:'transparent',
              display:'inline-flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:10,flexShrink:0 }}>{(allOn||!activo)?'✓':''}</span>
            <span style={{ flex:1 }}>Todos{counts ? ` (${counts('')})` : ''}</span>
          </button>
          {opts.map(o => {
            const on = value.includes(o.value);
            const n = counts ? counts(o.value) : null;
            return (
              <button key={String(o.value)} onClick={() => toggle(o.value)}
                style={{ display:'flex',alignItems:'center',gap:8,width:'100%',textAlign:'left',padding:'7px 10px',borderRadius:6,border:'none',
                  background:on?C.primaryTint:'transparent',color:C.t1,fontSize:12,fontWeight:on?700:500,cursor:'pointer' }}>
                <span style={{ width:15,height:15,borderRadius:4,border:`1.5px solid ${on?C.primary:C.border}`,background:on?C.primary:'transparent',
                  display:'inline-flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:10,flexShrink:0 }}>{on?'✓':''}</span>
                <span style={{ flex:1 }}>{o.label}</span>
                {n != null && <span className="radar-num" style={{ fontSize:10.5,fontWeight:700,opacity:.6 }}>{n}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Pantalla in-app del Reporte Comercial Semanal (cierre semana anterior +
// apertura). Trae el mismo texto que sale por WhatsApp el lunes, via el preview
// read-only (no envia, no muta). Renderiza el markdown de WhatsApp (*negrita*).
function ReporteSemanal() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const cargar = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch('/api/report?tipo=revision_semanal&preview=1');
      const raw = await r.text();
      let j; try { j = JSON.parse(raw); } catch { j = null; }
      if (j && j.ok) setD(j); else setErr((j && j.error) || 'No se pudo generar el reporte.');
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };
  useEffect(() => { cargar(); }, []);
  const usd = n => `$${Math.round(Number(n)||0).toLocaleString('en-US')}`;
  const data = d?.data || {};
  const cierre = data.cierre || [], ct = data.cierreTotal, apertura = data.apertura || [], at = data.aperturaTotal;
  const primer = (n)=> String(n||'').split(' ')[0];
  const Th = ({children,r}) => <th style={{padding:'7px 10px',fontSize:10,fontWeight:800,color:C.t3,textTransform:'uppercase',letterSpacing:'.04em',textAlign:r?'right':'left',whiteSpace:'nowrap',borderBottom:`2px solid ${C.border}`}}>{children}</th>;
  const Td = ({children,r,b,c}) => <td className={r?'radar-num':''} style={{padding:'8px 10px',fontSize:12.5,fontWeight:b?800:500,color:c||C.t1,textAlign:r?'right':'left',whiteSpace:'nowrap'}}>{children}</td>;
  const Tabla = ({children}) => <div style={{overflowX:'auto',border:`1px solid ${C.border}`,borderRadius:10}}><table style={{width:'100%',borderCollapse:'collapse',minWidth:560}}>{children}</table></div>;
  const Stat = ({l,v,c,sub}) => (
    <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 12px',minWidth:0}}>
      <div style={{fontSize:10.5,color:C.t3,fontWeight:600,marginBottom:3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{l}</div>
      <div className="radar-num" style={{fontSize:17,fontWeight:900,color:c||C.t1}}>{v}</div>
      {sub && <div style={{fontSize:10,color:C.t4,marginTop:1}}>{sub}</div>}
    </div>
  );
  return (
    <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:14, padding:'16px 18px', marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom: abierto?14:0 }}>
        <div>
          <div style={{ fontSize:15, fontWeight:900, color:C.t1 }}>Reporte Comercial Semanal</div>
          {d && <div style={{fontSize:11.5,color:C.t3,marginTop:1}}>Cierre {d.semana_cerrada} a Apertura {d.semana_actual}</div>}
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => window.open('/api/report?tipo=revision_semanal&preview=1&pdf=1', '_blank')} style={{ height:32, padding:'0 12px', borderRadius:R.md, border:`1px solid ${C.border}`, background:C.bg1, color:C.t2, fontSize:12, fontWeight:700, cursor:'pointer' }}>PDF</button>
          <button onClick={cargar} title="Actualizar" style={{ height:32, padding:'0 12px', borderRadius:R.md, border:`1px solid ${C.border}`, background:C.bg1, color:C.t2, fontSize:12, fontWeight:700, cursor:'pointer' }}>Refrescar</button>
          <button onClick={() => setAbierto(a => !a)} style={{ height:32, padding:'0 14px', borderRadius:R.md, border:'none', background:C.primary, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>{abierto ? 'Ocultar' : 'Ver'}</button>
        </div>
      </div>
      {abierto && (loading ? <div style={{ color:C.t4, padding:'8px 0' }}>Generando...</div>
        : err ? <div style={{ color:C.redText, padding:'8px 0' }}>No se pudo cargar: {err}</div>
        : (
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10,marginBottom:16}}>
            <Stat l="Ingreso semana (pagos)" v={usd(ct?.ingreso)} c={C.green} sub={ct?`contado ${usd(ct.contado)} / credito ${usd(ct.credito)}`:''}/>
            <Stat l="Ventas: prometio a cerro" v={usd(ct?.cerrado)} c={C.blue} sub={ct?`de ${usd(ct.prometido)} prometido`:''}/>
            <Stat l="Pasa a esta semana" v={usd(ct?.arrastre)} c={ct?.arrastre?C.redText:C.t3}/>
            <Stat l="Proyeccion esta semana" v={usd(at?.proyeccion)} c={C.indigo} sub={`${apertura.length} vendedor(es)`}/>
          </div>
          <div style={{fontSize:12.5,fontWeight:800,color:C.t2,margin:'0 0 7px'}}>CIERRE DE VENTAS - semana {d.semana_cerrada}</div>
          {cierre.length ? (
            <Tabla>
              <thead><tr style={{background:C.bg1}}>
                <Th>Vendedor</Th><Th r>Contado</Th><Th r>Credito</Th><Th r>Ingreso</Th><Th r>Prometio</Th><Th r>Cerro</Th><Th r>%</Th><Th r>Actividad</Th>
              </tr></thead>
              <tbody>
                {cierre.map((v,i)=>(
                  <tr key={v.vendedor} style={{background:i%2?C.bg1:'transparent',borderTop:`1px solid ${C.bg3}`}}>
                    <Td b>{primer(v.vendedor)}</Td>
                    <Td r>{v.contado?usd(v.contado):'-'}</Td>
                    <Td r>{v.credito?usd(v.credito):'-'}</Td>
                    <Td r b c={C.green}>{usd(v.ingreso)}</Td>
                    <Td r c={C.t3}>{usd(v.prometido)}</Td>
                    <Td r b c={C.blue}>{usd(v.cerrado)}</Td>
                    <Td r c={v.pct==null?C.t4:v.pct>=80?C.greenText:v.pct>=50?C.orange:C.redText}>{v.pct==null?'-':v.pct+'%'}</Td>
                    <Td r c={C.t3}>{v.vis}v / {v.llam}ll</Td>
                  </tr>
                ))}
                {ct && <tr style={{background:C.bg1,borderTop:`2px solid ${C.border}`}}>
                  <Td b>TOTAL</Td><Td r b>{usd(ct.contado)}</Td><Td r b>{usd(ct.credito)}</Td><Td r b c={C.green}>{usd(ct.ingreso)}</Td>
                  <Td r b c={C.t3}>{usd(ct.prometido)}</Td><Td r b c={C.blue}>{usd(ct.cerrado)}</Td><Td>-</Td><Td>-</Td>
                </tr>}
              </tbody>
            </Tabla>
          ) : <div style={{fontSize:12,color:C.t4,padding:'6px 0'}}>Sin movimiento la semana pasada.</div>}
          <div style={{fontSize:12.5,fontWeight:800,color:C.t2,margin:'16px 0 7px'}}>APERTURA - esta semana {d.semana_actual}</div>
          {apertura.length ? (
            <Tabla>
              <thead><tr style={{background:C.bg1}}>
                <Th>Vendedor</Th><Th r>Proyeccion</Th><Th r>Arrastre</Th><Th r>Nuevo</Th><Th r>Cobros proy.</Th>
              </tr></thead>
              <tbody>
                {apertura.map((v,i)=>(
                  <tr key={v.vendedor} style={{background:i%2?C.bg1:'transparent',borderTop:`1px solid ${C.bg3}`}}>
                    <Td b>{primer(v.vendedor)}</Td>
                    <Td r b c={C.indigo}>{usd(v.proyeccion)}</Td>
                    <Td r c={v.arrastre?C.redText:C.t4}>{v.arrastre?usd(v.arrastre):'-'}</Td>
                    <Td r>{usd(v.nuevo)}</Td>
                    <Td r c={C.green}>{v.cobros?usd(v.cobros):'-'}</Td>
                  </tr>
                ))}
                {at && <tr style={{background:C.bg1,borderTop:`2px solid ${C.border}`}}>
                  <Td b>TOTAL</Td><Td r b c={C.indigo}>{usd(at.proyeccion)}</Td><Td>-</Td><Td>-</Td><Td>-</Td>
                </tr>}
              </tbody>
            </Tabla>
          ) : <div style={{fontSize:12,color:C.t4,padding:'6px 0'}}>Nadie presento proyeccion todavia.</div>}
          {(data.no_presentaron?.length>0 || data.arrastres_criticos?.length>0) && (
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:12}}>
              {data.no_presentaron?.length>0 && <div style={{background:C.redTint,color:C.redText,borderRadius:8,padding:'6px 10px',fontSize:11.5,fontWeight:600}}>No presentaron: {data.no_presentaron.map(primer).join(', ')}</div>}
              {data.arrastres_criticos?.length>0 && <div style={{background:'#FEF3C7',color:C.orange,borderRadius:8,padding:'6px 10px',fontSize:11.5,fontWeight:600}}>Arrastres criticos: {data.arrastres_criticos.map(x=>`${primer(x.vendedor)} (${x.semanas}sem)`).join(', ')}</div>}
            </div>
          )}
          {data.nexa && <div style={{marginTop:12,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 12px'}}>
            <div style={{fontSize:10.5,color:C.t3,fontWeight:700,marginBottom:3}}>NEXA - respuesta a leads (ultimos 7 dias)</div>
            <div style={{fontSize:12,color:C.t2,lineHeight:1.5}}>{data.nexa}</div>
          </div>}
          <div style={{ marginTop:12, fontSize:10.5, color:C.t4 }}>Vista previa - el reporte oficial se envia por WhatsApp el lunes.</div>
        </div>
      ))}
    </div>
  );
}

const AvatarChip = ({ name, size=20 }) => {
  const n = (name||'').trim();
  if (!n) return null;
  const ini = n.split(/\s+/).slice(0,2).map(w=>w[0]).join('').toUpperCase();
  let h = 0; for (let i=0;i<n.length;i++) h = (h*31 + n.charCodeAt(i)) >>> 0;
  return <span style={{width:size,height:size,borderRadius:'50%',background:AV_COLORS[h%AV_COLORS.length],color:'#fff',fontSize:Math.round(size*0.425),fontWeight:700,display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0,letterSpacing:'.02em'}}>{ini}</span>;
};
// Badge de etapa (pill tintado con dot) — receta 7.5, tinte derivado del color de etapa.
const StageChip = ({ label, color, fs=10.5 }) => (
  <span style={{display:'inline-flex',alignItems:'center',gap:5,background:`${color}12`,color,border:`1px solid ${color}33`,borderRadius:R.pill,padding:'2px 8px',fontSize:fs,fontWeight:600,whiteSpace:'nowrap',lineHeight:1.5}}>
    <span style={{width:6,height:6,borderRadius:'50%',background:color,flexShrink:0}}/>{label}
  </span>
);

// ── Íconos de CONTROLES (stroke 1.5, feather) — reemplazan emojis en tabs/botones (DS v3, cero emojis en UI).
const CI = {
  inbox:   <><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></>,
  chart:   <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
  bullhorn:<><path d="M3 11l18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></>,
  contacts:<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></>,
  phone:   <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>,
  calendar:<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
  target:  <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>,
  pin:     <><path d="M12 17v5"/><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"/></>,
  cash:    <><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></>,
  clipboard:<><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/></>,
  user:    <><circle cx="12" cy="8" r="4"/><path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/></>,
  users:   <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></>,
  download:<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
  refresh: <><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></>,
  play:    <polygon points="6 4 20 12 6 20 6 4"/>,
  send:    <><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>,
  search:  <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
  flag:    <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></>,
  checkGoal:<><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22M18 2H6v7a6 6 0 0 0 12 0z"/></>,
  broadcast:<><path d="M5 12.55a11 11 0 0 1 14 0M2 8.82a16 16 0 0 1 20 0M8.5 16.4a6 6 0 0 1 7 0M12 20h.01"/></>,
  zap:     <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>,
  clock:   <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  chat:    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>,
  alert:   <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
  wrench:  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>,
  help:    <><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
  ticket:  <><path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1a2 2 0 0 0 0 4v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a2 2 0 0 0 0-4z"/><line x1="9" y1="7" x2="9" y2="17"/></>,
  edit:    <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></>,
  save:    <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></>,
  printer: <><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></>,
  qr:      <><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h3v3M20 14v.01M14 20h.01M20 20v.01M17 17v3"/></>,
  copy:    <><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>,
  smile:   <><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></>,
  sun:     <><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></>,
  sunset:  <><path d="M17 18a5 5 0 0 0-10 0"/><line x1="12" y1="2" x2="12" y2="9"/><line x1="4.22" y1="10.22" x2="5.64" y2="11.64"/><line x1="1" y1="18" x2="3" y2="18"/><line x1="21" y1="18" x2="23" y2="18"/><line x1="18.36" y1="11.64" x2="19.78" y2="10.22"/><line x1="23" y1="22" x2="1" y2="22"/><polyline points="8 6 12 2 16 6"/></>,
  pin2:    <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>,
  plus:    <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
  check2:  <polyline points="20 6 9 17 4 12"/>,
  bulb:    <><path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V17h6v-.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z"/></>,
};
// Ícono inline para controles (hereda color del texto del botón). size por defecto 13.
const CIcon = ({ d, size=13, style={} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,...style}} aria-hidden="true">{d}</svg>
);
// Segmented control (receta 7.4): track bg3, botón h26, activo blanco + SH.sm.
function Seg({ items, value, onChange }) {
  return (
    <div style={{display:'inline-flex',gap:2,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2}}>
      {items.map(([v,label,icon])=>{
        const on = value===v;
        return (
          <button key={v} onClick={()=>onChange(v)}
            style={{height:26,display:'inline-flex',alignItems:'center',gap:6,padding:'0 12px',border:'none',borderRadius:R.sm,cursor:'pointer',fontSize:12,fontWeight:600,background:on?C.bg2:'transparent',color:on?C.t1:C.t3,boxShadow:on?SH.sm:'none',whiteSpace:'nowrap',transition:'background .15s ease,color .15s ease'}}>
            {icon && <CIcon d={icon}/>}{label}
          </button>
        );
      })}
    </div>
  );
}

// ── Íconos de EQUIPO (stroke 1.5) — reemplazan los emojis de tipo (montacargas,
// plataforma, grúa…). tipoIconKey mapea el texto libre del tipo a una llave EQ_I.
const EQ_I = {
  // Montacargas — carretilla elevadora con mástil
  montacargas: <><path d="M3 17h9M3 17V6h6v11M9 9h4l3 4v4h-4"/><path d="M16 6v11M18 4v8"/><circle cx="6.5" cy="19.5" r="1.6"/><circle cx="13.5" cy="19.5" r="1.6"/></>,
  // Plataforma / tijera elevadora
  plataforma: <><path d="M4 20h16M6 20v-2h12v2M7 18l4-4-4-4 4-4M17 18l-4-4 4-4-4-4"/></>,
  // Grúa
  grua: <><path d="M3 21h18M6 21V4h2l12 3M6 7h12M8 7v3M6 4l3 0"/><path d="M15 10v3a2 2 0 0 0 2 2"/></>,
  // Genérico (llave inglesa)
  generico: <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.8 2.8-2-2z"/>,
};
const tipoIconKey = e => {
  const t = (e?.tipo_equipo||'').toLowerCase();
  if (t.includes('montacarga')) return 'montacargas';
  if (t.includes('plataforma') || t.includes('elevad') || t.includes('tijera') || t.includes('articulad')) return 'plataforma';
  if (t.includes('grua') || t.includes('grúa')) return 'grua';
  return 'generico';
};
// Ícono de equipo en caja tintada teal (mismo lenguaje que los KIcon de KPI).
const EquipIcon = ({ e, size=34, icon=17 }) => (
  <span style={{width:size,height:size,borderRadius:R.md,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,color:C.blue,display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
    <CIcon d={EQ_I[tipoIconKey(e)]} size={icon}/>
  </span>
);

// ─── BÚSQUEDA GLOBAL ─────────────────────────────────────────────────────────
function GlobalSearch({ onClose, onNavigate, clientes, equipos }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState({ cl:[], eq:[], op:[] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (q.length < 2) { setRes({ cl:[], eq:[], op:[] }); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      const lq = q.toLowerCase();
      const b  = q.replace(/[%_]/g,'\\$&');
      let cl;
      if (q.length >= 3) {
        const { data } = await supabase.from('clientes').select('id,empresa,contacto,ciudad,vendedor').neq('estado','inactivo').or(`empresa.ilike.%${b}%,contacto.ilike.%${b}%`).order('empresa').limit(6);
        cl = data || [];
      } else {
        cl = clientes.filter(c => (c.empresa||'').toLowerCase().includes(lq)||(c.contacto||'').toLowerCase().includes(lq)).slice(0,6);
      }
      const eq = equipos.filter(e =>
        (e.codigo||'').toLowerCase().includes(lq)||(e.modelo||'').toLowerCase().includes(lq)
      ).slice(0,4);
      const {data:op} = await supabase.from('oportunidades')
        .select('id,titulo,cliente,etapa,valor,vendedor')
        .or(`titulo.ilike.%${q}%,cliente.ilike.%${q}%`)
        .not('etapa','in','(cerrado_ganado,cerrado_perdido,prospecto_invalido,cliente_frio)').limit(6);
      setRes({ cl, eq, op:op||[] });
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const total = res.cl.length+res.eq.length+res.op.length;
  const EC2={prospecto:'#64748B',contactado:'#0D9488',cotizacion:'#F59E0B',negociacion:'#EC4899',cerrado:'#0B7AB8',facturado:'#8B5CF6',pagado:'#06B6D4',cerrado_ganado:'#10B981',cerrado_perdido:'#EF4444',prospecto_invalido:'#94A3B8',cliente_frio:'#0EA5E9'};
  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.72)',zIndex:3000,display:'flex',alignItems:'flex-start',justifyContent:'center',paddingTop:'13vh'}}>
      <div onClick={e=>e.stopPropagation()} style={{width:'100%',maxWidth:600,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,overflow:'hidden',boxShadow:SH.lg}}>
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'13px 16px',borderBottom:`1px solid ${C.border}`}}>
          <span style={{fontSize:16,color:C.t4,flexShrink:0}}>🔍</span>
          <input ref={inputRef} value={q} onChange={e=>setQ(e.target.value)}
            placeholder="Buscar clientes, oportunidades, equipos..."
            style={{flex:1,background:'none',border:'none',color:C.t1,fontSize:15,outline:'none',fontFamily:'inherit'}}
            onKeyDown={e=>e.key==='Escape'&&onClose()}/>
          {loading&&<span style={{fontSize:12,color:C.t4}}>⏳</span>}
          <kbd style={{fontSize:10,color:C.t4,border:`1px solid ${C.border}`,borderRadius:4,padding:'2px 6px',flexShrink:0}}>ESC</kbd>
        </div>
        <div style={{maxHeight:440,overflowY:'auto'}}>
          {q.length<2
            ?<div style={{padding:32,textAlign:'center',color:C.t4,fontSize:13}}>
               <div style={{fontSize:28,marginBottom:8}}>⌘K</div>
               Escribe al menos 2 caracteres · clientes, pipeline y flota
             </div>
            :total===0&&!loading
              ?<div style={{padding:32,textAlign:'center',color:C.t4,fontSize:13}}>Sin resultados para "<strong style={{color:C.t2}}>{q}</strong>"</div>
              :<>
                {res.cl.length>0&&<>
                  <div style={{padding:'8px 16px 3px',fontSize:10,fontWeight:800,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',background:C.bg1}}>👥 Clientes</div>
                  {res.cl.map(c=>(
                    <div key={c.id} onClick={()=>{onNavigate('crm');onClose();}}
                      style={{padding:'11px 16px',cursor:'pointer',display:'flex',alignItems:'center',gap:12,borderBottom:`1px solid ${C.border}`}}
                      onMouseEnter={e=>e.currentTarget.style.background=C.bg3}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <div style={{width:34,height:34,borderRadius:'50%',background:`${C.blue}20`,color:C.blue,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:900,flexShrink:0}}>{(c.empresa||'?')[0].toUpperCase()}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:C.t1}}>{c.empresa}</div>
                        {c.contacto&&<div style={{fontSize:11,color:C.t3}}>{c.contacto}{c.vendedor?` · ${c.vendedor}`:''}</div>}
                      </div>
                      <span style={{fontSize:10,color:C.t4,background:C.bg3,borderRadius:6,padding:'2px 7px',flexShrink:0}}>{c.estado||'—'}</span>
                    </div>
                  ))}
                </>}
                {res.op.length>0&&<>
                  <div style={{padding:'8px 16px 3px',fontSize:10,fontWeight:800,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',background:C.bg1}}>💼 Oportunidades</div>
                  {res.op.map(o=>(
                    <div key={o.id} onClick={()=>{onNavigate('crm');onClose();}}
                      style={{padding:'11px 16px',cursor:'pointer',display:'flex',alignItems:'center',gap:12,borderBottom:`1px solid ${C.border}`}}
                      onMouseEnter={e=>e.currentTarget.style.background=C.bg3}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <div style={{width:34,height:34,borderRadius:'50%',background:`${EC2[o.etapa]||C.bg3}20`,color:EC2[o.etapa]||C.t4,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,flexShrink:0}}>💼</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{o.titulo}</div>
                        <div style={{fontSize:11,color:C.t3}}>{o.cliente}{o.vendedor?` · ${o.vendedor}`:''}</div>
                      </div>
                      <div style={{textAlign:'right',flexShrink:0}}>
                        {o.valor>0&&<div style={{fontSize:12,fontWeight:800,color:C.blue}}>${(o.valor).toLocaleString()}</div>}
                        <div style={{fontSize:10,color:EC2[o.etapa]||C.t4,fontWeight:700,textTransform:'uppercase'}}>{o.etapa}</div>
                      </div>
                    </div>
                  ))}
                </>}
                {res.eq.length>0&&<>
                  <div style={{padding:'8px 16px 3px',fontSize:10,fontWeight:800,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',background:C.bg1}}>🏗️ Equipos</div>
                  {res.eq.map(e=>(
                    <div key={e.id} onClick={()=>{onNavigate('flota');onClose();}}
                      style={{padding:'11px 16px',cursor:'pointer',display:'flex',alignItems:'center',gap:12,borderBottom:`1px solid ${C.border}`}}
                      onMouseEnter={ev=>ev.currentTarget.style.background=C.bg3}
                      onMouseLeave={ev=>ev.currentTarget.style.background='transparent'}>
                      <div style={{width:34,height:34,borderRadius:'50%',background:`${C.green}20`,color:C.green,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>🏗️</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:C.t1}}>{e.codigo}</div>
                        <div style={{fontSize:11,color:C.t3}}>{e.modelo}</div>
                      </div>
                      <span style={{fontSize:10,fontWeight:700,color:STATUS[e.estado]?.color||C.t4,background:`${STATUS[e.estado]?.color||C.t4}15`,borderRadius:6,padding:'2px 8px',flexShrink:0,whiteSpace:'nowrap'}}>{STATUS[e.estado]?.label||e.estado||'—'}</span>
                    </div>
                  ))}
                </>}
              </>
          }
        </div>
        <div style={{padding:'8px 16px',borderTop:`1px solid ${C.border}`,display:'flex',gap:16,fontSize:10,color:C.t4}}>
          <span>↵ Navegar</span><span>ESC Cerrar</span>
          {total>0&&<span style={{marginLeft:'auto'}}>{total} resultado{total!==1?'s':''}</span>}
        </div>
      </div>
    </div>
  );
}

// ─── ACTIVIDADES ──────────────────────────────────────────────────────────────
function Actividades({ usuario, clientes, empresaActiva }) {
  const [acts, setActs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modal, setModal]             = useState(false);
  const [saving, setSaving]           = useState(false);
  const [modalSeg, setModalSeg]       = useState(null); // actividad origen para seguimiento
  const [savingSeg, setSavingSeg]     = useState(false);
  const [fSeg, setFSeg]               = useState({titulo:'',fecha:'',notas:''});
  const [oppsLista, setOppsLista]     = useState([]);
  const [vendedoresList, setVendedoresList] = useState([]);
  const [busqCl, setBusqCl]           = useState('');
  const [busqClLive, setBusqClLive]   = useState(null);
  const [showClDrop, setShowClDrop]   = useState(false);
  const [crearOpp, setCrearOpp]       = useState(false);
  const [filtroVend, setFiltroVend]   = useState([]);   // multi-seleccion de vendedores ([] = todos)

  const hoy  = new Date().toISOString().split('T')[0];
  const ayerD = new Date(); ayerD.setDate(ayerD.getDate()-1);
  const ayer  = ayerD.toISOString().split('T')[0];
  const lunesD = new Date(); const dd=lunesD.getDay(); lunesD.setDate(lunesD.getDate()-dd+(dd===0?-6:1));
  const lunes  = lunesD.toISOString().split('T')[0];
  const inicioMes = hoy.slice(0,7)+'-01';
  const tresDias  = new Date(); tresDias.setDate(tresDias.getDate()+3);
  const tresDiasStr = tresDias.toISOString().split('T')[0];

  const [f, setF] = useState({titulo:'',tipo:'llamada',fecha:hoy,hora:'',vendedor:usuario.nombre||'',cliente_id:'',opp_id:'',notas:''});

  const TIPOS  = ['llamada','reunión','demo','seguimiento','tarea','visita'];
  // Íconos stroke 1.5 (kit feather) por tipo — cero emojis en la UI (DS v3).
  const TIPO_IC= {
    llamada:     <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/>,
    reunión:     <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>,
    demo:        <><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>,
    seguimiento: <><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></>,
    tarea:       <><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></>,
    visita:      <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>,
  };
  // Tono TONES por tipo (tripleta) — el chip usa tinte+texto+borde; el dot usa solid.
  const TIPO_T = {llamada:TONES.green,reunión:TONES.primary,demo:TONES.info,seguimiento:TONES.orange,tarea:TONES.neutral,visita:{text:C.lob.servicio.text,tint:C.lob.servicio.bg,border:C.lob.servicio.border,solid:C.lob.servicio.text}};
  const tipoTono = t => TIPO_T[t] || TONES.neutral;
  const capTipo = t => t.charAt(0).toUpperCase()+t.slice(1);

  const cargar = async (eid) => {
    setLoading(true);
    const filtroEid = eid || (usuario.rol!=='admin' ? usuario.empresa_id : null);
    let q = supabase.from('actividades_programadas')
      .select('*,clientes(empresa),oportunidades(titulo)')
      .order('fecha',{ascending:false}).order('hora',{ascending:false,nullsFirst:true}).limit(400);
    if (usuario.rol==='ventas') q = q.eq('vendedor',usuario.nombre);
    if (filtroEid) q = q.eq('empresa_id', filtroEid);
    const {data} = await q;
    setActs(data||[]); setLoading(false);
  };
  useEffect(()=>{ cargar(); },[empresaActiva]);
  useEffect(()=>{
    if(busqCl.length<3){setBusqClLive(null);return;}
    const b=busqCl.replace(/[%_]/g,'\\$&');
    const t=setTimeout(async()=>{
      let qr=supabase.from('clientes').select('id,empresa').neq('estado','inactivo').ilike('empresa',`%${b}%`).order('empresa').limit(12);
      if(empresaActiva) qr=qr.eq('empresa_id',empresaActiva);
      const{data}=await qr; setBusqClLive(data||[]);
    },300);
    return()=>clearTimeout(t);
  },[busqCl,empresaActiva]);
  useEffect(()=>{
    supabase.from('oportunidades').select('id,titulo,cliente').not('etapa','in','(cerrado_ganado,cerrado_perdido,prospecto_invalido,cliente_frio)').order('titulo').limit(200)
      .then(({data})=>setOppsLista(data||[]));
    supabase.from('radar_usuarios').select('nombre').in('rol',['ventas','gerente_ventas','admin']).eq('activo',true)
      .then(({data})=>setVendedoresList((data||[]).map(u=>u.nombre).sort()));
  },[]);

  const crear = async () => {
    if (!f.titulo||!f.fecha||!f.vendedor) return;
    setSaving(true);
    const eid = empresaActiva || usuario.empresa_id || 'honduras';
    const {error} = await supabase.from('actividades_programadas').insert({
      titulo:f.titulo,tipo:f.tipo,fecha:f.fecha,hora:f.hora||null,
      vendedor:f.vendedor,cliente_id:f.cliente_id||null,opp_id:f.opp_id||null,
      notas:f.notas,completada:false,empresa_id:eid,
    });
    if (error) { toast('Error al guardar: '+error.message); setSaving(false); return; }
    if (crearOpp) {
      const clienteNombre = clientes.find(c=>c.id===f.cliente_id)?.empresa||'';
      await supabase.from('oportunidades').insert({
        titulo:f.titulo,etapa:'prospecto',vendedor:f.vendedor,
        cliente:clienteNombre,cliente_id:f.cliente_id||null,
        notas:f.notas||null,ultimo_seguimiento:f.fecha,empresa_id:eid,
      });
    }
    await cargar(empresaActiva||usuario.empresa_id||null);
    setSaving(false); setModal(false);
    setF({titulo:'',tipo:'llamada',fecha:hoy,hora:'',vendedor:usuario.nombre||'',cliente_id:'',opp_id:'',notas:''});
    setBusqCl(''); setCrearOpp(false);
  };

  const crearSeguimiento = async () => {
    if (!fSeg.titulo||!fSeg.fecha||!modalSeg) return;
    setSavingSeg(true);
    const eid = empresaActiva || usuario.empresa_id || 'honduras';
    await supabase.from('actividades_programadas').insert({
      titulo: fSeg.titulo,
      tipo: 'seguimiento',
      fecha: fSeg.fecha,
      vendedor: modalSeg.vendedor || usuario.nombre,
      cliente_id: modalSeg.cliente_id||null,
      opp_id: modalSeg.opp_id||null,
      notas: fSeg.notas||null,
      completada: false,
      empresa_id: modalSeg.empresa_id||eid,
    });
    await cargar(empresaActiva||usuario.empresa_id||null);
    setSavingSeg(false); setModalSeg(null);
  };

  const [modalDisp, setModalDisp] = useState(null);   // D3: {act} pendiente de disposicion
  const [dispSel, setDispSel] = useState('');
  const marcarHecho = async (a) => {
    // D3: si la actividad es de cliente (visita/llamada/reunion con cliente u opp),
    // el cierre exige DISPOSICION — es lo que define el contacto significativo.
    const esDeCliente = (a.cliente_id || a.opp_id) && ['visita','llamada','reunion','seguimiento'].includes(a.tipo);
    if (esDeCliente && !a.disposicion) { setDispSel(''); setModalDisp(a); return; }
    await supabase.from('actividades_programadas').update({completada:true}).eq('id',a.id);
    setActs(prev=>prev.map(x=>x.id===a.id?{...x,completada:true}:x));
  };
  const confirmarDisposicion = async () => {
    if (!modalDisp || !dispSel) return;
    await supabase.from('actividades_programadas').update({completada:true, disposicion:dispSel}).eq('id',modalDisp.id);
    setActs(prev=>prev.map(x=>x.id===modalDisp.id?{...x,completada:true,disposicion:dispSel}:x));
    setModalDisp(null);
  };

  const eliminar = async (id) => {
    if (!(await confirmar('¿Eliminar esta actividad?', { ok:'Eliminar', peligro:true }))) return;
    await supabase.from('actividades_programadas').delete().eq('id',id);
    setActs(prev=>prev.filter(x=>x.id!==id));
  };

  const abrirSeguimiento = (a) => {
    setModalSeg(a);
    setFSeg({ titulo:`Seguimiento — ${a.clientes?.empresa||a.titulo}`, fecha: tresDiasStr, notas:'' });
  };

  // Filtro por vendedor (solo admin/gerente)
  const esAdmin = ['admin','gerente_ventas'].includes(usuario.rol);
  const actsVis = filtroVend.length ? acts.filter(a=>filtroVend.includes(a.vendedor)) : acts;

  // Grupos — cobertura exhaustiva: cada actividad cae en exactamente un cajón
  // (incluye un cajón "Anteriores" para que nada se pierda al pasar el mes).
  const mananaD = new Date(); mananaD.setDate(mananaD.getDate()+1);
  const manana = mananaD.toISOString().split('T')[0];
  const pendientes = actsVis.filter(a=>!a.completada&&a.fecha>hoy).sort((a,b)=>a.fecha.localeCompare(b.fecha));
  const futurasHechas = actsVis.filter(a=>a.completada&&a.fecha>hoy).sort((a,b)=>a.fecha.localeCompare(b.fecha));
  const deHoy      = actsVis.filter(a=>a.fecha===hoy).sort((a,b)=>(a.hora||'99').localeCompare(b.hora||'99'));
  // VENCIDAS: pasadas sin completar — el cajon que reclama (mockup). Antes se
  // perdian mezcladas entre "ayer/semana/mes" con las ya hechas.
  const vencidas   = actsVis.filter(a=>!a.completada&&a.fecha<hoy).sort((a,b)=>b.fecha.localeCompare(a.fecha));
  const deManana   = pendientes.filter(a=>a.fecha===manana);
  const compSemana = actsVis.filter(a=>a.completada&&a.fecha>=lunes&&a.fecha<=hoy).length;
  const deAyer     = actsVis.filter(a=>a.completada&&a.fecha===ayer);
  const deSemana   = actsVis.filter(a=>a.completada&&a.fecha>=lunes&&a.fecha<ayer);
  const delMes     = actsVis.filter(a=>a.completada&&a.fecha>=inicioMes&&a.fecha<lunes);
  const anteriores = actsVis.filter(a=>a.completada&&a.fecha<inicioMes).sort((a,b)=>b.fecha.localeCompare(a.fecha));

  const fmtFecha = f => new Date(f+'T12:00:00').toLocaleDateString('es-HN',{weekday:'short',day:'numeric',month:'short'});

  // Meta-línea con ícono SVG + texto (metadatos de la actividad)
  const Meta = ({d, children}) => (
    <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,color:C.t4,minWidth:0}}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,opacity:.85}}>{d}</svg>
      <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{children}</span>
    </span>
  );
  const ActCard = ({a, esPendiente=false}) => {
    const t = tipoTono(a.tipo);
    return (
      <div className="radar-card" style={{background:C.bg2,border:`1px solid ${esPendiente?C.primaryBorder:C.border}`,borderLeft:`3px solid ${t.solid}`,borderRadius:R.lg,padding:'11px 14px',display:'flex',gap:11,alignItems:'flex-start',opacity:a.completada?0.6:1,boxShadow:SH.sm}}>
        {/* Ícono del tipo en caja tintada (receta KIcon) */}
        <span style={{width:28,height:28,borderRadius:8,background:t.tint,border:`1px solid ${t.border}`,color:t.text,display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1}}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{TIPO_IC[a.tipo]||TIPO_IC.tarea}</svg>
        </span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:5,flexWrap:'wrap'}}>
            <span style={{fontSize:13,fontWeight:600,letterSpacing:'-0.005em',color:C.t1,textDecoration:a.completada?'line-through':'none'}}>{a.titulo}</span>
            <span style={{display:'inline-flex',alignItems:'center',gap:5,background:t.tint,color:t.text,border:`1px solid ${t.border}`,borderRadius:R.pill,padding:'2px 8px',fontSize:10.5,fontWeight:600}}><span style={{width:6,height:6,borderRadius:'50%',background:t.solid,flexShrink:0}}/>{capTipo(a.tipo)}</span>
            {a.completada&&<span style={{display:'inline-flex',alignItems:'center',gap:5,background:C.greenTint,color:C.greenText,border:`1px solid ${C.greenBorder}`,borderRadius:R.pill,padding:'2px 8px',fontSize:10.5,fontWeight:600}}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Realizada</span>}
            {esPendiente&&!a.completada&&<span style={{display:'inline-flex',alignItems:'center',gap:5,background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,borderRadius:R.pill,padding:'2px 8px',fontSize:10.5,fontWeight:600}}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>{fmtFecha(a.fecha)}</span>}
          </div>
          <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'center'}}>
            {a.hora&&!esPendiente&&<Meta d={<><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>}>{a.hora.slice(0,5)}</Meta>}
            <span style={{display:'inline-flex',alignItems:'center',gap:6,minWidth:0}}><AvatarChip name={a.vendedor} size={18}/><span style={{fontSize:11,fontWeight:500,color:C.t2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.vendedor}</span></span>
            {a.clientes?.empresa&&<Meta d={<><path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-3"/></>}>{a.clientes.empresa}</Meta>}
            {a.oportunidades?.titulo&&<span style={{maxWidth:200,display:'inline-flex'}}><Meta d={<><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></>}>{a.oportunidades.titulo}</Meta></span>}
          </div>
          {a.notas&&<div style={{fontSize:11,color:C.t3,marginTop:5,lineHeight:1.45}}>{a.notas}</div>}
        </div>
        <div style={{display:'flex',gap:6,flexShrink:0,alignItems:'flex-start'}}>
          {/* Seguimiento: disponible en todos */}
          <button className="radar-btn" onClick={()=>abrirSeguimiento(a)}
            title="Agendar seguimiento"
            style={{display:'inline-flex',alignItems:'center',gap:5,background:C.orangeTint,color:C.orangeText,border:`1px solid ${C.orangeBorder}`,borderRadius:R.md,padding:'5px 10px',fontSize:11,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="17" x2="12" y2="3"/><path d="M5 10l7-7 7 7"/><path d="M5 21h14"/></svg>
            Seguimiento
          </button>
          {/* Marcar como hecho: disponible en cualquier actividad no completada */}
          {!a.completada&&(
            <button className="radar-btn" onClick={()=>marcarHecho(a)} title="Marcar como realizado"
              style={{display:'inline-flex',alignItems:'center',gap:5,background:C.greenTint,color:C.greenText,border:`1px solid ${C.greenBorder}`,borderRadius:R.md,padding:'5px 10px',fontSize:11,fontWeight:600,cursor:'pointer'}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Hecho
            </button>
          )}
          <button onClick={()=>eliminar(a.id)} title="Eliminar"
            style={{background:'none',border:'none',color:C.t4,cursor:'pointer',padding:'4px',lineHeight:0,borderRadius:R.sm,display:'inline-flex'}}
            onMouseEnter={e=>e.currentTarget.style.color=C.red}
            onMouseLeave={e=>e.currentTarget.style.color=C.t4}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>
    );
  };

  const Seccion = ({titulo, items, esPendiente=false, emptyMsg}) => {
    const [open, setOpen] = useState(true);
    if (items.length===0) return emptyMsg?<div style={{fontSize:12,color:C.t4,padding:'8px 0'}}>{emptyMsg}</div>:null;
    return (
      <div style={{marginBottom:20}}>
        <button onClick={()=>setOpen(v=>!v)}
          style={{display:'flex',alignItems:'center',gap:8,background:'none',border:'none',cursor:'pointer',marginBottom:9,padding:0}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.t4} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{transform:open?'none':'rotate(-90deg)',transition:'transform .15s ease'}}><polyline points="6 9 12 15 18 9"/></svg>
          <span style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em'}}>{titulo}</span>
          <span className="radar-num" style={{background:esPendiente?C.primaryTint:C.bg3,color:esPendiente?C.blue:C.t3,border:`1px solid ${esPendiente?C.primaryBorder:C.border}`,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600}}>{items.length}</span>
        </button>
        {open&&<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(400px,1fr))',gap:8}}>{items.map(a=><ActCard key={a.id} a={a} esPendiente={esPendiente}/>)}</div>}
      </div>
    );
  };

  const total = vencidas.length+pendientes.length+futurasHechas.length+deHoy.length+deAyer.length+deSemana.length+delMes.length+anteriores.length;

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* Barra superior — flota sobre el gris, lenguaje del CRM (sin franja blanca innecesaria) */}
      <div style={{padding:'12px 24px',display:'flex',alignItems:'center',gap:10,flexShrink:0,flexWrap:'wrap'}}>
        {esAdmin&&(
          <VendChips vendedores={vendedoresList} value={filtroVend} onChange={setFiltroVend}
            counts={v => v ? acts.filter(a=>a.vendedor===v).length : acts.length}/>
        )}
        <div style={{display:'inline-flex',alignItems:'center',gap:8,marginLeft:esAdmin?0:'auto'}}>
          <span className="radar-num" style={{fontSize:12,color:C.t3}}>{total} actividades</span>
          {pendientes.length>0&&<span className="radar-num" style={{display:'inline-flex',alignItems:'center',gap:5,background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,borderRadius:R.pill,padding:'2px 9px',fontSize:11,fontWeight:600}}><span style={{width:6,height:6,borderRadius:'50%',background:C.blue,flexShrink:0}}/>{pendientes.length} pendiente{pendientes.length!==1?'s':''}</span>}
        </div>
        <div style={{marginLeft:'auto'}}><Btn size="sm" onClick={()=>setModal(true)}>+ Nueva actividad</Btn></div>
      </div>

      {/* Contenido */}
      <div style={{flex:1,overflowY:'auto',padding:'4px 24px 24px'}}>
        {loading ? <div style={{padding:'8px 0'}}><SkelRows n={6}/></div> : (
          <div style={{maxWidth:880}}>
            {total===0&&(
              <div style={{background:C.bg2,border:`1px dashed ${C.border2}`,borderRadius:R.lg,padding:'56px 20px',textAlign:'center'}}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={C.t4} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom:12}}><rect x="3" y="4" width="18" height="17" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="15" x2="16" y2="15"/></svg>
                <div style={{fontSize:14,fontWeight:600,color:C.t2,marginBottom:3}}>Sin actividades registradas</div>
                <div style={{fontSize:12,color:C.t4,marginBottom:14}}>Registrá llamadas, reuniones, demos y visitas para verlas en la línea de tiempo.</div>
                <Btn size="sm" onClick={()=>setModal(true)}>+ Registrar actividad</Btn>
              </div>
            )}
            {/* KPIs del dia (mockup): hoy / manana / vencidas / completadas */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:8,marginBottom:14}}>
              {[['Hoy',deHoy.length,`${deHoy.filter(a=>a.tipo==='visita').length} visitas · ${deHoy.filter(a=>a.tipo==='llamada').length} llamadas`,C.t1],
                ['Mañana',deManana.length,'programadas',C.t1],
                ['Vencidas sin cerrar',vencidas.length,'marcá hecho o reprogramá',vencidas.length>0?C.redText:C.t1],
                ['Completadas semana',compSemana,'de lo planificado',C.greenText]].map(([l,v,s2,cx])=>(
                <div key={l} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:11,boxShadow:SH.xs,padding:'9px 13px'}}>
                  <div style={{fontSize:9,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4}}>{l}</div>
                  <div className="radar-num" style={{fontSize:19,fontWeight:800,letterSpacing:'-0.02em',color:cx}}>{v}</div>
                  <div style={{fontSize:9.5,color:C.t4}}>{s2}</div>
                </div>
              ))}
            </div>
            <Seccion titulo="⚠ Vencidas — hecho o reprogramar, no limbo" items={vencidas} esPendiente={true}/>
            <Seccion titulo="Hoy" items={deHoy}/>
            <Seccion titulo="Seguimientos pendientes (próximos días)" items={pendientes} esPendiente={true}/>
            <Seccion titulo="Futuras realizadas" items={futurasHechas}/>
            <Seccion titulo="Completadas — ayer" items={deAyer}/>
            <Seccion titulo="Completadas — esta semana" items={deSemana}/>
            <Seccion titulo="Completadas — este mes" items={delMes}/>
            <Seccion titulo="Completadas — anteriores" items={anteriores}/>
          </div>
        )}
      </div>

      {/* Modal nueva actividad */}
      <Modal open={modal} onClose={()=>setModal(false)} title="Nueva Actividad" width={500}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <div style={{gridColumn:'1/-1'}}><Inp label="Título *" value={f.titulo} onChange={v=>setF(x=>({...x,titulo:v}))} placeholder="ej: Llamada con ABBOTT MEDICAL" required/></div>
          <Sel label="Tipo *" value={f.tipo} onChange={v=>setF(x=>({...x,tipo:v}))} options={TIPOS.map(t=>({v:t,l:capTipo(t)}))}/>
          <Inp label="Fecha *" type="date" value={f.fecha} onChange={v=>setF(x=>({...x,fecha:v}))} required/>
          <Inp label="Hora" type="time" value={f.hora} onChange={v=>setF(x=>({...x,hora:v}))}/>
          <Sel label="Vendedor *" value={f.vendedor} onChange={v=>setF(x=>({...x,vendedor:v}))} required options={vendedoresList.map(v=>({v,l:v}))}/>
          <div style={{position:'relative'}}>
            <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:5}}>Cliente (opcional)</div>
            <input type="text" placeholder="Buscar cliente..." value={busqCl}
              onChange={e=>{ setBusqCl(e.target.value); setF(x=>({...x,cliente_id:''})); setShowClDrop(true); }}
              onFocus={()=>setShowClDrop(true)}
              style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${f.cliente_id?C.blue:C.border}`,borderRadius:R.md,padding:'10px 12px',color:C.t1,fontSize:14,outline:'none'}}/>
            {showClDrop&&busqCl&&(
              <div style={{position:'absolute',zIndex:999,top:'100%',left:0,right:0,marginTop:4,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,maxHeight:160,overflowY:'auto',boxShadow:SH.lg}}>
                {(busqClLive!==null?busqClLive:clientes.filter(c=>c.empresa?.toLowerCase().includes(busqCl.toLowerCase())).slice(0,12)).map(c=>(
                  <div key={c.id} onMouseDown={()=>{ setF(x=>({...x,cliente_id:c.id})); setBusqCl(c.empresa); setShowClDrop(false); }}
                    style={{padding:'9px 12px',cursor:'pointer',fontSize:13,color:C.t1,borderBottom:`1px solid ${C.border}`}}
                    onMouseEnter={e=>e.currentTarget.style.background=C.bg3}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>{c.empresa}</div>
                ))}
                {(busqClLive!==null?busqClLive:clientes.filter(c=>c.empresa?.toLowerCase().includes(busqCl.toLowerCase()))).length===0&&<div style={{padding:'9px 12px',fontSize:12,color:C.t4}}>Sin resultados</div>}
              </div>
            )}
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:5}}>Oportunidad (opcional)</div>
            <select value={f.opp_id} onChange={e=>setF(x=>({...x,opp_id:e.target.value}))}
              style={{width:'100%',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'10px 12px',color:f.opp_id?C.t1:C.t4,fontSize:14,outline:'none',boxSizing:'border-box'}}>
              <option value="">— Sin oportunidad —</option>
              {oppsLista.map(o=><option key={o.id} value={o.id} style={{background:C.bg2,color:C.t1}}>{o.titulo}</option>)}
            </select>
          </div>
          <div style={{gridColumn:'1/-1'}}><Inp label="Notas" value={f.notas} onChange={v=>setF(x=>({...x,notas:v}))} rows={2} placeholder="Qué pasó, próximos pasos..."/></div>
          <div style={{gridColumn:'1/-1',display:'flex',alignItems:'center',gap:10,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:R.md,padding:'10px 14px',cursor:'pointer'}} onClick={()=>setCrearOpp(v=>!v)}>
            <div style={{width:18,height:18,borderRadius:R.sm,border:`2px solid ${crearOpp?C.blue:C.border2}`,background:crearOpp?C.blue:'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all .15s'}}>
              {crearOpp&&<span style={{color:'#fff',fontSize:11,fontWeight:900,lineHeight:1}}>✓</span>}
            </div>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:C.t1}}>Convertir a oportunidad en el pipeline</div>
              <div style={{fontSize:11,color:C.t4}}>Crea un prospecto con los datos de esta actividad</div>
            </div>
          </div>
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:8}}>
          <Btn variant="secondary" onClick={()=>setModal(false)}>Cancelar</Btn>
          <Btn onClick={crear} disabled={!f.titulo||!f.fecha||!f.vendedor||saving}>{saving?'Guardando...':`Crear actividad${crearOpp?' + oportunidad':''}`}</Btn>
        </div>
      </Modal>

      {/* Modal agendar seguimiento */}
      {/* D3 (Sprint 1): disposicion obligatoria al completar actividad de cliente */}
      <Modal open={!!modalDisp} onClose={()=>setModalDisp(null)} title="¿Cómo terminó el contacto?" width={440}>
        <div style={{fontSize:12,color:C.t3,marginBottom:12}}>
          {modalDisp?.titulo} — la disposición define si cuenta como <b>contacto significativo</b> (alimenta AAA y el reporte semanal).
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:'46vh',overflowY:'auto'}}>
          {DISPOSICIONES.map(([v,l])=>(
            <label key={v} style={{display:'flex',alignItems:'center',gap:9,padding:'8px 11px',border:`1px solid ${dispSel===v?C.primaryBorder:C.border}`,borderRadius:R.md,background:dispSel===v?C.primaryTint:C.bg1,cursor:'pointer',fontSize:13,color:v==='SIN_RESPUESTA'?C.t3:C.t1}}>
              <input type="radio" name="disp" checked={dispSel===v} onChange={()=>setDispSel(v)} style={{accentColor:C.primary}}/>
              {l}
            </label>
          ))}
        </div>
        <div style={{display:'flex',gap:8,marginTop:14}}>
          <Btn variant="ghost" onClick={()=>setModalDisp(null)} full>Cancelar</Btn>
          <Btn onClick={confirmarDisposicion} disabled={!dispSel} full>Completar actividad</Btn>
        </div>
      </Modal>

      <Modal open={!!modalSeg} onClose={()=>setModalSeg(null)} title="Agendar seguimiento" width={420}>
        {modalSeg&&<>
          <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'10px 12px',marginBottom:14,fontSize:12}}>
            <div style={{fontWeight:700,color:C.t1}}>{modalSeg.clientes?.empresa||modalSeg.titulo}</div>
            <div style={{color:C.t4,marginTop:2}}>Vendedor: {modalSeg.vendedor}</div>
          </div>
          <Inp label="¿Qué se va a hacer? *" value={fSeg.titulo} onChange={v=>setFSeg(x=>({...x,titulo:v}))} placeholder="ej: Llamar para confirmar disponibilidad"/>
          <Inp label="Fecha del seguimiento *" type="date" value={fSeg.fecha} onChange={v=>setFSeg(x=>({...x,fecha:v}))}/>
          <Inp label="Notas adicionales" value={fSeg.notas} onChange={v=>setFSeg(x=>({...x,notas:v}))} rows={2} placeholder="Contexto, qué preguntar..."/>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:8}}>
            <Btn variant="secondary" onClick={()=>setModalSeg(null)}>Cancelar</Btn>
            <Btn onClick={crearSeguimiento} disabled={!fSeg.titulo||!fSeg.fecha||savingSeg}>{savingSeg?'Guardando...':'Agendar'}</Btn>
          </div>
        </>}
      </Modal>
    </div>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [u,setU]=useState(''); const [p,setP]=useState('');
  const [err,setErr]=useState(''); const [loading,setLoading]=useState(false);
  const fallosRef = useRef(0);   // freno progresivo a fuerza bruta: 1s extra por intento fallido
  const go = async () => {
    if (!u||!p||loading) return;
    setLoading(true); setErr('');
    if (fallosRef.current > 0) await new Promise(r=>setTimeout(r, Math.min(fallosRef.current,5)*1000));
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: `${u.toLowerCase().trim()}@radar.montasa`,
        password: p,
      });
      if (error) { fallosRef.current++; setErr('Usuario o contraseña incorrectos'); setLoading(false); return; }
      // Si Auth acepto, la contrasena ES la correcta. Lo que falte de aca en
      // adelante es otra cosa, y decir "contraseña incorrecta" manda a la
      // persona a probar contrasenas que nunca van a funcionar.
      const {data} = await supabase.from('radar_usuarios').select('*').eq('usuario',u.toLowerCase().trim()).maybeSingle();
      if (data && data.activo !== false) { fallosRef.current = 0; onLogin(data); }
      else {
        await supabase.auth.signOut().catch(()=>{});
        setErr(data
          ? 'Tu usuario esta desactivado. Pedile a un administrador que lo active.'
          : 'Tu contraseña es correcta, pero tu usuario todavia no esta dado de alta en RADAR. Avisale a un administrador.');
        setLoading(false);
      }
    } catch { fallosRef.current++; setErr('Usuario o contraseña incorrectos'); setLoading(false); }
  };
  return (
    <div style={{fontFamily:'Inter,sans-serif',background:C.bg0,height:'100vh',maxHeight:'100dvh',display:'flex',alignItems:'center',justifyContent:'center',overflowY:'auto',padding:16,boxSizing:'border-box'}}>
      <div style={{width:352,padding:'40px 36px',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,boxShadow:SH.lg}}>
        <div style={{textAlign:'center',marginBottom:32}}>
          <div style={{width:56,height:56,borderRadius:14,background:C.blue,boxShadow:'inset 0 1px 0 rgba(255,255,255,.18), 0 1px 2px rgba(0,0,0,.20)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,margin:'0 auto 14px'}}>📡</div>
          <div style={{fontSize:26,fontWeight:900,letterSpacing:'.12em',color:C.blue}}>RADAR</div>
          <div style={{fontSize:11,fontWeight:600,color:C.t3,marginTop:4,letterSpacing:'.08em'}}>PLATAFORMA COMERCIAL</div>
        </div>
        {['Usuario','Contraseña'].map((lbl,i)=>(
          <div key={lbl} style={{marginBottom:i===0?12:20}}>
            <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:5}}>{lbl}</div>
            <input type={i===1?'password':'text'} value={i===0?u:p} onChange={e=>i===0?setU(e.target.value):setP(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&go()} placeholder={i===0?'tu usuario':'••••••••'}
              autoComplete={i===0?'username':'current-password'} autoCapitalize="none" autoCorrect="off"
              onFocus={e=>{e.currentTarget.style.borderColor=C.blue;e.currentTarget.style.boxShadow=`0 0 0 3px ${C.ring}`;}}
              onBlur={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.boxShadow='none';}}
              style={{width:'100%',boxSizing:'border-box',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'10px 12px',color:C.t1,fontSize:14,outline:'none',transition:'border-color .15s, box-shadow .15s'}}/>
          </div>
        ))}
        {err&&<div style={{background:C.redTint,border:`1px solid ${C.redBorder}`,borderRadius:R.md,padding:'8px 12px',marginBottom:14,fontSize:12,color:C.redText,textAlign:'center'}}>{err}</div>}
        <button onClick={go} disabled={!u||!p||loading} className="radar-btn" style={{width:'100%',padding:'11px 12px',borderRadius:R.md,border:'none',background:u&&p?C.blue:C.bg3,color:u&&p?'#fff':C.t4,fontSize:14,fontWeight:600,letterSpacing:'.005em',cursor:u&&p?'pointer':'default',boxShadow:u&&p?`${SH.primary}, inset 0 1px 0 rgba(255,255,255,.12)`:'none'}}>
          {loading?'Entrando...':'ENTRAR →'}
        </button>
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
const DASH_VIEWS = [
  { id:'total',      label:'🌐 Total',           empresa:false },
  { id:'monhaco',    label:'🏢 Monhaco',        empresa:true  },
  { id:'costarica',  label:'🇨🇷 Montasa CR',      empresa:true },
  { id:'monhagro',   label:'🌱 Monhagro',        empresa:true, empty:true },
  { id:'ventas',     label:'💼 Ventas',          empresa:false },
  { id:'taller',     label:'🔩 Taller',          empresa:false },
  { id:'finanzas',   label:'💰 Finanzas',        empresa:false },
];

function Dashboard({ usuario, equipos, onNavigate }) {
  const [dashView, setDashView] = useState(usuario?.empresa_id === 'costarica' ? 'costarica' : 'total');
  const [opps, setOpps]           = useState([]);
  const [rentasAct, setRentasAct] = useState([]);
  const [actHoy, setActHoy]       = useState([]);
  const [oppsGanadas, setOppsGanadas] = useState([]);
  const [mantsHoy, setMantsHoy]   = useState([]);
  const [ingresosMes, setIngresosMes] = useState([]);
  const [gastosMes, setGastosMes] = useState([]);
  const [ingSemPasada, setIngSemPasada] = useState([]);
  // Paneles nuevos del dashboard v3 (lecturas read-only, mismos patrones que Cobros/NEXA):
  const [facturasPend, setFacturasPend] = useState([]); // facturas_sap pagado=false → aging / deudores / alertas
  const [leadsNexa, setLeadsNexa]       = useState([]); // sibila_leads → panel NEXA
  const [invValor, setInvValor]         = useState(null); // valor de inventario SAP (real) — resto honesto-vacío
  const [forecastSem, setForecastSem]   = useState([]);   // forecast_semanal semana en curso → "prometido" por vendedor
  const [metasVend, setMetasVend]       = useState([]);   // metas_vendedor del mes → marca de meta en el grafico

  useEffect(()=>{
    const hoy = new Date().toISOString().split('T')[0];
    const mes  = new Date(Date.now()-30*86400000).toISOString().split('T')[0];
    const mesI = new Date().toISOString().slice(0,7)+'-01';
    const _hoy = new Date();
    const _dow  = _hoy.getDay();
    const _lunes = new Date(_hoy);
    _lunes.setDate(_hoy.getDate() - (_dow === 0 ? 6 : _dow - 1));
    const swStr  = _lunes.toISOString().split('T')[0];
    const slwStr = new Date(_lunes.getTime() - 7*86400000).toISOString().split('T')[0];
    const elwStr = new Date(_lunes.getTime() -   86400000).toISOString().split('T')[0];
    // Filter queries by empresa_id for non-admin users
    const eid = (usuario?.empresa_id && usuario.empresa_id !== 'all') ? usuario.empresa_id : null;
    const fe = q => eid ? q.eq('empresa_id', eid) : q;
    Promise.all([
      fe(supabase.from('oportunidades').select('*,empresa_id').not('etapa','in','(cerrado_ganado,cerrado_perdido,prospecto_invalido,cliente_frio)')),
      fe(supabase.from('rentas').select('valor_mensual,cliente_id,equipo_id,fecha_inicio,fecha_fin,empresa_id').eq('estado','activa')),
      // En DEMO no se filtra por fecha: se muestran las ultimas actividades cargadas,
      // asi el panel nunca aparece vacio aunque la data sembrada sea de otro dia.
      (ORG.demo
        ? supabase.from('actividades').select('*').order('created_at',{ascending:false}).limit(15)
        : supabase.from('actividades').select('*').gte('created_at',hoy+'T00:00:00').order('created_at',{ascending:false}).limit(15)),
      fe(supabase.from('oportunidades').select('vendedor,valor,fecha_cierre,empresa_id').eq('etapa','cerrado_ganado').gte('fecha_cierre',mes)),
      supabase.from('mantenimientos').select('*,equipos(codigo,modelo)').gte('fecha',mesI).order('fecha',{ascending:false}).limit(20),
      // Ingresos REALES: cobros aplicados en SAP (cobros_sap), no la tabla 'ingresos' (módulo retirado).
      fe(supabase.from('cobros_sap').select('total_usd,doc_total,vendedor,doc_date,card_name,empresa_id').gte('doc_date',mesI).gt('doc_total',0)),
      supabase.from('mantenimientos').select('total,tipo,tecnico').gte('fecha',mesI),
      fe(supabase.from('cobros_sap').select('total_usd,doc_total,empresa_id').gte('doc_date',slwStr).lte('doc_date',elwStr).gt('doc_total',0)),
    ]).then(([{data:o},{data:r},{data:a},{data:g},{data:mt},{data:ing},{data:gas},{data:isp}])=>{
      // Normalizar cobros SAP a la forma {monto} que usa todo el dashboard.
      const norm = rows => (rows||[]).map(c=>({ ...c, monto: c.total_usd || c.doc_total || 0, fecha: c.doc_date }));
      setOpps(o||[]); setRentasAct(r||[]); setActHoy(a||[]); setOppsGanadas(g||[]);
      setMantsHoy(mt||[]); setIngresosMes(norm(ing)); setGastosMes(gas||[]);
      setIngSemPasada(norm(isp));
    });
    // Facturas pendientes (aging / top deudores / alertas de cobro) — mismo patrón que CobrosFacturacion.
    fe(supabase.from('facturas_sap').select('doc_num,doc_date,doc_due_date,doc_total,total_usd,saldo_usd,card_name,card_code,empresa_id').eq('pagado',false).limit(2000))
      .then(({data})=>setFacturasPend((data||[]).filter(f=>saldoUSDde(f)>0.01&&!esInterco(f))));
    // Leads NEXA de la semana en curso (mismo patrón que Sibila). En DEMO no se
    // filtra por fecha: se muestran los ultimos leads sembrados.
    fe(ORG.demo
        ? supabase.from('sibila_leads').select('id,canal,red_origen,estado,created_at,ultimo_mensaje,leido_at,oportunidad_id,empresa_id').order('created_at',{ascending:false}).limit(300)
        : supabase.from('sibila_leads').select('id,canal,red_origen,estado,created_at,ultimo_mensaje,leido_at,oportunidad_id,empresa_id').gte('created_at',swStr).limit(2000))
      .then(async ({data})=>{
        if (data && data.length) { setLeadsNexa(data); return; }
        // Sin tabla de leads propia: se derivan de las conversaciones del bot
        // de la semana (un lead por numero que escribio).
        const { data: msgs } = await supabase.from('wa_mensajes')
          .select('wa_from,role,created_at').gte('created_at', ORG.demo?'1970-01-01':swStr).order('created_at').limit(2000);
        const porNum = {};
        (msgs||[]).forEach(m => { if (!porNum[m.wa_from]) porNum[m.wa_from] = m; });
        setLeadsNexa(Object.entries(porNum).map(([n,m],i)=>({
          id:`wa_${n}`, canal:'whatsapp', red_origen:'whatsapp', estado:'nuevo',
          created_at:m.created_at, ultimo_mensaje:null, leido_at:m.created_at, oportunidad_id:null, empresa_id:null })));
      });
    // Comercial por vendedor: "prometido" (forecast_semanal de la semana en curso)
    // + meta del mes (metas_vendedor). Se agrupan por vendedor para el grafico del
    // dashboard (prometido vs cerrado vs cobrado vs meta). RLS scope por org.
    supabase.from('forecast_semanal').select('vendedor,monto_estimado,tipo,semana_inicio')
      .eq('semana_inicio', swStr).limit(3000)
      .then(({data})=>setForecastSem(data||[]));
    supabase.from('metas_vendedor').select('vendedor,meta_dolares').eq('mes', new Date().toISOString().slice(0,7))
      .then(({data})=>setMetasVend(data||[]));
    // Valor de inventario: del CACHE inventario_sap (instantaneo y funciona en
    // toda org). La consulta viva a SAP tardaba y en orgs sin SAP quedaba en cero.
    fe(supabase.from('inventario_sap').select('nombre,stock,valor_total_usd,empresa_id')
      .gt('stock',0).order('valor_total_usd',{ascending:false,nullsFirst:false}).limit(3000))
      .then(({data})=>setInvValor(data||[]));
  },[]); // eslint-disable-line

  // Filter dashboard data by empresa when admin switches views — must come first
  const dashEmpresa   = {monhaco:'honduras', costarica:'costarica'}[dashView] || null;
  const dOpps         = dashEmpresa ? opps.filter(o=>o.empresa_id===dashEmpresa)         : opps;
  const dRentasAct    = dashEmpresa ? rentasAct.filter(r=>r.empresa_id===dashEmpresa)    : rentasAct;
  const dIngresosMes  = dashEmpresa ? ingresosMes.filter(i=>i.empresa_id===dashEmpresa)  : ingresosMes;
  const dGastosMes    = dashEmpresa ? gastosMes.filter(g=>g.empresa_id===dashEmpresa)    : gastosMes;
  const dOppsGanadas  = dashEmpresa ? oppsGanadas.filter(o=>o.empresa_id===dashEmpresa)  : oppsGanadas;
  const dIngSemPasada = dashEmpresa ? ingSemPasada.filter(i=>i.empresa_id===dashEmpresa) : ingSemPasada;
  const dEquipos      = dashEmpresa ? equipos.filter(e=>e.empresa_id===dashEmpresa)      : equipos;
  const dFacturas     = dashEmpresa ? facturasPend.filter(f=>f.empresa_id===dashEmpresa) : facturasPend;
  const dLeads        = dashEmpresa ? leadsNexa.filter(l=>l.empresa_id===dashEmpresa)    : leadsNexa;

  const enRenta    = dEquipos.filter(e=>e.estado==='en_renta').length;
  const disponibles= dEquipos.filter(e=>e.estado==='disponible').length;
  const enTaller   = dEquipos.filter(e=>['mantenimiento','reparacion'].includes(e.estado)).length;
  const mrr        = dRentasAct.reduce((s,r)=>s+(r.valor_mensual||0),0);
  const pipeline   = dOpps.reduce((s,o)=>s+(o.valor||0),0);
  const pipelinePond = dOpps.reduce((s,o)=>s+(o.valor||0)*(ETAPAS_PROB[o.etapa]||0),0);
  const sinSeg     = dOpps.filter(o=>dias(o.ultimo_seguimiento)>=3).length;
  const hace60     = new Date(Date.now()-60*86400000).toISOString().split('T')[0];
  const ganadasRec = dOppsGanadas.filter(o=>(o.fecha_cierre||'')>=hace60);
  const totalIngMes= dIngresosMes.reduce((s,i)=>s+(i.monto||0),0);
  const totalGasMes= dGastosMes.reduce((s,g)=>s+(g.total||0),0);
  const totalIngSemPasada = dIngSemPasada.reduce((s,i)=>s+(i.monto||0),0);
  const _now2 = new Date();
  const _dow2 = _now2.getDay();
  const _lun2 = new Date(_now2); _lun2.setDate(_now2.getDate()-(_dow2===0?6:_dow2-1));
  const swStr2  = _lun2.toISOString().split('T')[0];
  const etwStr2 = new Date(_lun2.getTime()+6*86400000).toISOString().split('T')[0];
  const mesI2   = new Date().toISOString().slice(0,7)+'-01';
  const mesF2   = new Date(new Date().getFullYear(), new Date().getMonth()+1, 0).toISOString().split('T')[0];
  const oppsSemana = dOpps.filter(o=>o.fecha_cierre>=swStr2&&o.fecha_cierre<=etwStr2);
  const oppsMes    = dOpps.filter(o=>o.fecha_cierre>=mesI2&&o.fecha_cierre<=mesF2);
  const proyeccionSemana = oppsSemana.reduce((s,o)=>s+(o.valor||0),0);
  const proyeccionMes    = oppsMes.reduce((s,o)=>s+(o.valor||0),0);
  // Serie acumulada de cobros del mes por día — solo presentación (sparkline KPI).
  const sparkIngresos = (()=>{ const porDia={}; dIngresosMes.forEach(i=>{ const k=(i.fecha||'').slice(0,10); if(k) porDia[k]=(porDia[k]||0)+(i.monto||0); }); const ks=Object.keys(porDia).sort(); let acc=0; return ks.map(k=>acc+=porDia[k]); })();

  const ETAPAS_D = ['prospecto','contactado','cotizacion','negociacion','cerrado','facturado','pagado'];
  const EC2 = {prospecto:'#64748B',contactado:'#0D9488',cotizacion:'#F59E0B',negociacion:'#EC4899',cerrado:'#0B7AB8',facturado:'#8B5CF6',pagado:'#06B6D4'};
  // Vendedores normalizados con trim: "Kevin " y "Kevin" son la misma persona.
  const vTrim = o => (o.vendedor||'').trim();
  const vendedores = [...new Set([...dOpps.map(vTrim),...dOppsGanadas.map(vTrim)].filter(Boolean))];
  const vendStats = vendedores.map(v=>({
    nombre:v,
    pipeline: dOpps.filter(o=>vTrim(o)===v).reduce((s,o)=>s+(o.valor||0),0),
    abiertas: dOpps.filter(o=>vTrim(o)===v).length,
    sinSeg:   dOpps.filter(o=>vTrim(o)===v&&dias(o.ultimo_seguimiento)>=3).length,
    ganadas:  dOppsGanadas.filter(o=>vTrim(o)===v).length,
    ingresos: dOppsGanadas.filter(o=>vTrim(o)===v).reduce((s,o)=>s+(o.valor||0),0),
  })).sort((a,b)=>b.pipeline-a.pipeline);

  // ── Comercial por vendedor: Prometido (forecast semana) vs Cerrado (opps
  //    ganadas mes) vs Cobrado (SAP mes) vs Meta (metas_vendedor mes). ──
  // Los nombres se NORMALIZAN (sin acentos, minusculas) para que "Audry Garcia"
  // (cobros) y "Audry García" (metas) sean la misma persona; si no, la meta salia $0.
  const _normV = s => String(s||'').normalize('NFD').replace(/[̀-ͯ]/g,'').trim().toLowerCase();
  const _disp = {}, _prom={}, _cob={}, _cerr={}, _meta={};
  const _add = (map,name,val) => { const n=_normV(name); if(!n) return; if(!_disp[n]) _disp[n]=String(name).trim(); map[n]=(map[n]||0)+val; };
  (forecastSem||[]).forEach(f=>_add(_prom,f.vendedor,Number(f.monto_estimado||0)));
  dIngresosMes.forEach(i=>_add(_cob,i.vendedor,i.monto||0));
  dOppsGanadas.forEach(o=>_add(_cerr,o.vendedor,Number(o.valor||0)));
  (metasVend||[]).forEach(m=>{ const n=_normV(m.vendedor); if(n){ if(!_disp[n])_disp[n]=String(m.vendedor).trim(); _meta[n]=Number(m.meta_dolares||0); } });
  // Solo vendedores REALES = los que tienen meta del mes. Asi Administracion/
  // Andrea/ex-vendedores (sin meta) NO ensucian el panel.
  const comercialData = Object.keys(_meta).filter(n=>_meta[n]>0)
    .map(n=>({ v:_disp[n], nk:n, prometido:_prom[n]||0, cerrado:_cerr[n]||0, cobrado:_cob[n]||0, meta:_meta[n]||0 }))
    .sort((a,b)=>b.cobrado-a.cobrado);

  // ── Cobros: aging del saldo vencido + top deudores (facturas_sap pendientes) ──
  const hoyStr_ = HOY_STR();
  const vencidas = dFacturas.filter(f => diasAtrasoDe(f, hoyStr_) > 0);
  const totalVencido = vencidas.reduce((s,f)=>s+saldoUSDde(f), 0);
  // Aging del vencido: 0-30 / 31-60 / +60 (barra apilada del KPI).
  const aging0_30 = vencidas.filter(f=>{const d=diasAtrasoDe(f,hoyStr_);return d>=1&&d<=30;}).reduce((s,f)=>s+saldoUSDde(f),0);
  const aging31_60= vencidas.filter(f=>{const d=diasAtrasoDe(f,hoyStr_);return d>=31&&d<=60;}).reduce((s,f)=>s+saldoUSDde(f),0);
  const aging60p  = vencidas.filter(f=>diasAtrasoDe(f,hoyStr_)>60).reduce((s,f)=>s+saldoUSDde(f),0);
  const vencidas30p = vencidas.filter(f=>diasAtrasoDe(f,hoyStr_)>30);
  const totalVenc30p = vencidas30p.reduce((s,f)=>s+saldoUSDde(f),0);
  // Top deudores: agrupar saldo vencido por cliente (card_name), tomar los 4 mayores.
  const deudores = Object.values(vencidas.reduce((m,f)=>{
    const k = f.card_name || f.card_code || '—';
    if (!m[k]) m[k] = { nombre:k, saldo:0, maxDias:0 };
    m[k].saldo += saldoUSDde(f);
    m[k].maxDias = Math.max(m[k].maxDias, diasAtrasoDe(f, hoyStr_));
    return m;
  },{})).sort((a,b)=>b.saldo-a.saldo);
  const topDeudores = deudores.slice(0,4);
  const totalTop4 = topDeudores.reduce((s,d)=>s+d.saldo,0);

  // ── Alertas accionables (datos reales del scope) ─────────────────────────────
  const oppsEstancadas = dOpps.filter(o=>dias(o.ultimo_seguimiento)>=60).length;
  const _hoyD = new Date();
  const contratosPorVencer = dRentasAct.filter(r=>{
    if (!r.fecha_fin) return false;
    const dd = Math.ceil((new Date(String(r.fecha_fin).slice(0,10)+'T00:00:00') - _hoyD)/86400000);
    return dd >= 0 && dd <= 15;
  }).length;

  // ── NEXA: leads de redes de la semana ────────────────────────────────────────
  const leadsSemana = dLeads.length;
  const leadsSinAtender = dLeads.filter(l => l.estado==='nuevo' || !l.leido_at).length;
  const leads24h = dLeads.filter(l => (l.estado==='nuevo'||!l.leido_at) && dias(l.created_at)>=1).length;
  const canalCount = { whatsapp:0, messenger:0, instagram:0 };
  dLeads.forEach(l=>{ if (canalCount[l.canal]!=null) canalCount[l.canal]++; });
  const maxCanal = Math.max(1, ...Object.values(canalCount));
  const leadsConvertidos = dLeads.filter(l => l.estado==='convertido' || l.oportunidad_id).length;
  const conversionNexa = leadsSemana>0 ? Math.round(leadsConvertidos/leadsSemana*100) : null;

  // ── Inventario: valor real desde SAP; resto sin fuente → honesto-vacío ────────
  const invItems = (()=>{
    if (!invValor) return null;
    return dashEmpresa ? invValor.filter(i => i.empresa_id === dashEmpresa) : invValor;
  })();
  const invValorTotal = invItems ? invItems.reduce((s,i)=>s+(i.valor_total_usd||0),0) : null;

  const vistasDash = DASH_VIEWS.filter(v => {
    if (v.empresa) return ORG.empresas.length > 1;   // vistas por empresa: solo multi-empresa
    if (v.id === 'taller')   return orgTiene('taller');
    if (v.id === 'finanzas') return orgTiene('cobros');
    return true;
  });
  const curView = DASH_VIEWS.find(v=>v.id===dashView)||DASH_VIEWS[0];

  // ── Dropdown selector ────────────────────────────────────────────────────────
  const Selector = () => (
    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:22}}>
      <span style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em'}}>Dashboard</span>
      <div style={{position:'relative',display:'inline-block'}}>
        <select value={dashView} onChange={e=>setDashView(e.target.value)}
          style={{appearance:'none',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 36px 8px 14px',
            color:C.t1,fontSize:14,fontWeight:700,letterSpacing:'-0.01em',cursor:'pointer',outline:'none',minWidth:200,boxShadow:SH.xs}}>
          {vistasDash.map(v=><option key={v.id} value={v.id} style={{background:C.bg2}}>{v.label}</option>)}
        </select>
        <span style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',color:C.t4,pointerEvents:'none',fontSize:11}}>▾</span>
      </div>
      <span style={{fontSize:12.5,color:C.t3}}>{new Date().toLocaleDateString('es-HN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</span>
    </div>
  );

  // ── Empty placeholder (Costa Rica, Monhagro) ─────────────────────────────────
  const EmptyDash = ({name}) => (
    <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:60,color:C.t4}}>
      <Selector/>
      <div style={{fontSize:48,marginBottom:16}}>🚧</div>
      <div style={{fontSize:20,fontWeight:800,color:C.t2,marginBottom:8}}>{name}</div>
      <div style={{fontSize:14,color:C.t4,textAlign:'center',maxWidth:360}}>
        Dashboard en configuración.<br/>Los datos de esta empresa se conectarán próximamente.
      </div>
    </div>
  );

  if (curView.empty) return <div style={{flex:1,overflowY:'auto',background:C.bg1}}><EmptyDash name={curView.label}/></div>;

  // ── Dashboard Ventas ─────────────────────────────────────────────────────────
  if (dashView==='ventas') return (
    <div style={{flex:1,overflowY:'auto',padding:'22px 28px',background:C.bg1}}>
      <Selector/>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(185px,1fr))',gap:14,marginBottom:22}}>
        <KpiCard label="Pipeline Total"   value={fmt(pipeline)}                 sub={`${dOpps.length} opps activas`}       icon={KI.funnel} tone="primary"/>
        <KpiCard label="Ponderado"        value={fmt(Math.round(pipelinePond))} sub="Probabilidad × valor"                 icon={KI.trend}  tone="green"/>
        <KpiCard label="Sin seguimiento"  value={sinSeg}                        sub="opps +3 días sin contacto"            icon={KI.phone}  tone={sinSeg>0?'red':'green'} delta={sinSeg>0?'⚠ atención':'✓ al día'} deltaTone={sinSeg>0?'red':'green'}/>
        <KpiCard label="Ganadas 60d"      value={ganadasRec.length}             sub={fmt(ganadasRec.reduce((s,o)=>s+(o.valor||0),0))} icon={KI.check} tone="green"/>
        <KpiCard label="Cobrado este mes" value={fmt(totalIngMes)}              sub="Pagos aplicados en SAP"               icon={KI.dollar} tone="info" spark={<Spark points={sparkIngresos} color={C.blue}/>}/>
      </div>
      {/* Pipeline funnel */}
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 18px',marginBottom:16,boxShadow:SH.sm}}>
        <div style={{fontSize:13,fontWeight:700,color:C.t1,letterSpacing:'-0.005em',marginBottom:12}}>Pipeline por etapa</div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {ETAPAS_D.map(e=>{
            const n=dOpps.filter(o=>o.etapa===e).length;
            const v=dOpps.filter(o=>o.etapa===e).reduce((s,o)=>s+(o.valor||0),0);
            return <div key={e} onClick={()=>onNavigate('crm')} className="radar-kpi"
              style={{flex:'1 1 92px',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 12px',cursor:'pointer',boxShadow:SH.xs}}>
              <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                <span style={{width:7,height:7,borderRadius:'50%',background:EC2[e],flexShrink:0}}/>
                <span style={{fontSize:10,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.05em'}}>{e}</span>
              </div>
              <div className="radar-num" style={{fontSize:22,fontWeight:700,letterSpacing:'-0.02em',color:C.t1,lineHeight:1.05}}>{n}</div>
              <div className="radar-num" style={{fontSize:11,color:C.t4,marginTop:3}}>{v>0?`$${Math.round(v/1000)}k`:'—'}</div>
            </div>;
          })}
        </div>
      </div>
      {/* Tabla vendedores */}
      {vendStats.length>0&&<div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 18px',boxShadow:SH.sm}}>
        <div style={{fontSize:13,fontWeight:700,color:C.t1,letterSpacing:'-0.005em',marginBottom:12}}>Rendimiento · Últimos 30 días</div>
        <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
            {['Vendedor','Pipeline','Abiertas','Sin seg.','Cerradas','Ingresos'].map((h,i)=>(
              <th key={h} style={{padding:'6px 12px',textAlign:i>=4?'right':'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',whiteSpace:'nowrap'}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{vendStats.map(v=>(
            <tr key={v.nombre} style={{borderBottom:`1px solid ${C.bg3}`}}>
              <td style={{padding:'9px 12px',fontWeight:600,color:C.t1}}>{v.nombre}</td>
              <td className="radar-num" style={{padding:'9px 12px',color:C.blue,fontWeight:700}}>${Math.round(v.pipeline/1000)}k</td>
              <td className="radar-num" style={{padding:'9px 12px',color:C.t2}}>{v.abiertas}</td>
              <td className="radar-num" style={{padding:'9px 12px'}}><span style={{color:v.sinSeg>0?C.redText:C.greenText,fontWeight:700}}>{v.sinSeg}</span></td>
              <td className="radar-num" style={{padding:'9px 12px',color:C.greenText,fontWeight:700,textAlign:'right'}}>{v.ganadas}</td>
              <td className="radar-num" style={{padding:'9px 12px',color:C.greenText,fontWeight:700,textAlign:'right'}}>{v.ingresos>0?fmt(v.ingresos):'—'}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>}
    </div>
  );

  // ── Dashboard Taller ─────────────────────────────────────────────────────────
  if (dashView==='taller') return (
    <div style={{flex:1,overflowY:'auto',padding:'22px 28px',background:C.bg1}}>
      <Selector/>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(185px,1fr))',gap:14,marginBottom:22}}>
        <KpiCard label="Equipos en renta"    value={enRenta}         sub={`${disponibles} disponibles`}   icon={KI.box}    tone="primary" onClick={()=>onNavigate('flota')}/>
        <KpiCard label="En mantenimiento"    value={enTaller}        sub="mantenimiento + reparación"     icon={KI.tool}   tone="orange"  onClick={()=>onNavigate('flota')}/>
        <KpiCard label="Gastos taller (mes)" value={fmt(totalGasMes)} sub={`${gastosMes.length} órdenes`} icon={KI.dollar} tone="red"     onClick={()=>onNavigate('flota')}/>
        <KpiCard label="Disponibles"         value={disponibles}     sub="listos para renta"              icon={KI.check}  tone="green"   onClick={()=>onNavigate('flota')}/>
        <KpiCard label="Total flota"         value={dEquipos.length} sub="equipos registrados"            icon={KI.funnel} tone="neutral" onClick={()=>onNavigate('flota')}/>
      </div>
      {/* Estado flota */}
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 18px',marginBottom:16,boxShadow:SH.sm}}>
        <div style={{fontSize:13,fontWeight:700,color:C.t1,letterSpacing:'-0.005em',marginBottom:12}}>Estado de flota</div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          {Object.entries(STATUS).map(([k,v])=>{ const n=dEquipos.filter(e=>e.estado===k).length; if(!n)return null;
            return <div key={k} className="radar-kpi" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 16px',minWidth:110,boxShadow:SH.xs}}>
              <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                <span style={{width:7,height:7,borderRadius:'50%',background:v.color,flexShrink:0}}/>
                <span style={{fontSize:10,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>{v.label}</span>
              </div>
              <div className="radar-num" style={{fontSize:24,fontWeight:700,letterSpacing:'-0.02em',color:C.t1,lineHeight:1.05}}>{n}</div>
            </div>;
          })}
        </div>
      </div>
      {/* Mantenimientos del mes */}
      {mantsHoy.length>0&&<div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 18px',boxShadow:SH.sm}}>
        <div style={{fontSize:13,fontWeight:700,color:C.t1,letterSpacing:'-0.005em',marginBottom:12}}>Mantenimientos este mes <span className="radar-num" style={{background:C.bg3,color:C.t3,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,marginLeft:4}}>{mantsHoy.length}</span></div>
        <div style={{overflowX:'auto'}}>
          <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
              {['Equipo','Tipo','Fecha','Total'].map((h,i)=><th key={h} style={{padding:'5px 10px',textAlign:i===3?'right':'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>)}
            </tr></thead>
            <tbody>{mantsHoy.map((m,i)=>(
              <tr key={i} style={{borderBottom:`1px solid ${C.bg3}`}}>
                <td style={{padding:'8px 10px',color:C.t1,fontWeight:600}}>{m.equipos?.codigo||'—'}</td>
                <td style={{padding:'8px 10px',color:C.t3}}>{m.tipo||'—'}</td>
                <td className="radar-num" style={{padding:'8px 10px',color:C.t4}}>{fecha(m.fecha)}</td>
                <td className="radar-num" style={{padding:'8px 10px',color:C.orangeText,fontWeight:700,textAlign:'right'}}>{m.total>0?fmt(m.total):'—'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>}
    </div>
  );

  // ── Dashboard Finanzas ───────────────────────────────────────────────────────
  if (dashView==='finanzas') return (
    <div style={{flex:1,overflowY:'auto',padding:'22px 28px',background:C.bg1}}>
      <Selector/>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(185px,1fr))',gap:14,marginBottom:22}}>
        <KpiCard label="MRR Rentas"      value={fmt(mrr)}         sub={`${dRentasAct.length} contratos activos`} icon={KI.box}    tone="green"/>
        <KpiCard label="Ingresos (mes)"  value={fmt(totalIngMes)} sub="Cerrado este mes"                         icon={KI.dollar} tone="primary" spark={<Spark points={sparkIngresos} color={C.blue}/>}/>
        <KpiCard label="Gastos (mes)"    value={fmt(totalGasMes)} sub="Mantenimientos y taller"                  icon={KI.tool}   tone="red"/>
        <KpiCard label="Utilidad aprox." value={fmt(mrr+totalIngMes-totalGasMes)} sub="MRR + Ingresos − Gastos"  icon={KI.trend}  tone={(mrr+totalIngMes-totalGasMes)>0?'green':'red'} delta={(mrr+totalIngMes-totalGasMes)>0?'↑ positiva':'↓ negativa'} deltaTone={(mrr+totalIngMes-totalGasMes)>0?'green':'red'}/>
        <KpiCard label="Pipeline pond."  value={fmt(Math.round(pipelinePond))} sub="Potencial ponderado CRM"     icon={KI.funnel} tone="info"/>
      </div>
      {/* Rentas activas */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 18px',boxShadow:SH.sm}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:12}}>
            <span style={{fontSize:13,fontWeight:700,color:C.t1,letterSpacing:'-0.005em'}}>Rentas activas</span>
            <span className="radar-num" style={{fontSize:11.5,color:C.t3}}>{dRentasAct.length} contratos</span>
          </div>
          {dRentasAct.slice(0,8).map((r,i)=>(
            <div key={i} className="radar-hrow" style={{display:'flex',justifyContent:'space-between',padding:'7px 4px',borderBottom:`1px solid ${C.bg3}`,borderRadius:6}}>
              <span style={{fontSize:12,color:C.t2}}>#{i+1} Contrato {r.equipo_id?.toString().slice(0,8)}</span>
              <span className="radar-num" style={{fontSize:12,fontWeight:700,color:C.greenText}}>{fmt(r.valor_mensual)}/mes</span>
            </div>
          ))}
          {dRentasAct.length>8&&<div className="radar-num" style={{fontSize:11,color:C.t4,marginTop:8,textAlign:'center'}}>+{dRentasAct.length-8} más...</div>}
        </div>
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 18px',boxShadow:SH.sm}}>
          <div style={{fontSize:13,fontWeight:700,color:C.t1,letterSpacing:'-0.005em',marginBottom:12}}>Ingresos por vendedor · Este mes</div>
          {[...new Set(dIngresosMes.map(i=>i.vendedor).filter(Boolean))].map(v=>{
            const total=dIngresosMes.filter(i=>i.vendedor===v).reduce((s,i)=>s+(i.monto||0),0);
            const pct=totalIngMes>0?Math.round(total/totalIngMes*100):0;
            return <div key={v} style={{marginBottom:12}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                <span style={{fontSize:12,fontWeight:600,color:C.t1}}>{v}</span>
                <span className="radar-num" style={{fontSize:12,color:C.greenText,fontWeight:700}}>{fmt(total)}</span>
              </div>
              <div style={{height:6,background:C.bg3,borderRadius:R.pill,overflow:'hidden'}}>
                <div style={{height:6,background:C.green,borderRadius:R.pill,width:`${pct}%`,transition:'width .4s cubic-bezier(.4,0,.2,1)'}}/>
              </div>
            </div>;
          })}
          {dIngresosMes.length===0&&<div style={{color:C.t4,fontSize:13,textAlign:'center',padding:'20px 0'}}>Sin cobros aplicados en SAP este mes</div>}
        </div>
      </div>
    </div>
  );

  // ── Dashboard Monhaco / Costa Rica / Total (default) — reconstruido al mockup v3
  // Compacto tipo mockup: $286.4K / $1.2M — con title del valor exacto.
  const fmtK = n => { const x=Math.abs(n||0); if(x>=1e6) return `$${(n/1e6).toFixed(1).replace(/\.0$/,'')}M`; if(x>=1e3) return `$${(n/1e3).toFixed(1).replace(/\.0$/,'')}K`; return `$${Math.round(n||0)}`; };
  // Etapas abiertas del pipeline con el color de dot del DS v3 (mismos del kanban).
  const DASH_STAGES = [
    {id:'prospecto',  label:'Prospecto',   color:'#94A3B8'},
    {id:'contactado', label:'Contactado',  color:'#0F766E'},
    {id:'cotizacion', label:'Cotización',  color:'#C4933F'},
    {id:'negociacion',label:'Negociación', color:'#0E7490'},
    {id:'cerrado',    label:'Cerrado',     color:'#0B7AB8'},
    {id:'facturado',  label:'Facturado',   color:'#8B5CF6'},
    {id:'pagado',     label:'Pagado',      color:'#06B6D4'},
  ];
  const stageData = DASH_STAGES.map(s=>({ ...s,
    n: dOpps.filter(o=>o.etapa===s.id).length,
    v: dOpps.filter(o=>o.etapa===s.id).reduce((a,o)=>a+(o.valor||0),0),
  })).filter(s=>s.n>0);
  const maxStageV = Math.max(1, ...stageData.map(s=>s.v));
  const ganadasMes = dOppsGanadas.filter(o=>(o.fecha_cierre||'')>=mesI2);
  const ventasMes  = ganadasMes.reduce((s,o)=>s+(o.valor||0),0);
  const ticketProm = ganadasMes.length ? ventasMes/ganadasMes.length : 0;
  const tasaCierre = (dOpps.length+ganadasMes.length)>0 ? Math.round(ganadasMes.length/(dOpps.length+ganadasMes.length)*100) : 0;
  // Meta de ventas del mes: si no hay una fuente de meta, proyección del mes como referencia honesta.
  const metaMes = proyeccionMes>0 ? proyeccionMes : (ventasMes>0?ventasMes:0);
  const pctMeta  = metaMes>0 ? Math.min(100, Math.round(ventasMes/metaMes*100)) : 0;
  // Donut de flota (utilización = en renta / (en renta + disponible)).
  const fleetCounts = [
    {k:'disponible',  label:'Disponible',  color:'#16A34A', n:disponibles},
    {k:'en_renta',    label:'En renta',    color:'#2563EB', n:enRenta},
    {k:'reparar',     label:'Por reparar', color:'#DC2626', n:enTaller},
    {k:'vendido',     label:'Vendido',     color:'#98A2B3', n:dEquipos.filter(e=>e.estado==='vendido').length},
  ];
  const fleetTotal = fleetCounts.reduce((s,f)=>s+f.n,0);
  const rentables  = enRenta + disponibles;
  const utilPct    = rentables>0 ? Math.round(enRenta/rentables*100) : 0;
  const CIRC = 2*Math.PI*45; // r=45
  let donutOff = 0;
  const donutSegs = fleetCounts.filter(f=>f.n>0).map(f=>{ const len=fleetTotal>0?(f.n/fleetTotal)*CIRC:0; const seg={color:f.color,len,off:-donutOff}; donutOff+=len; return seg; });
  // Rendimiento del equipo: cumplimiento REAL = cobrado (SAP) vs meta del mes.
  // Solo vendedores con meta (comercialData ya los filtra). Trae prometido/cerrado.
  const teamRows = comercialData.map(d=>{
    const st = vendStats.find(s=>_normV(s.nombre)===d.nk) || {};
    const pct = d.meta>0 ? Math.round(d.cobrado/d.meta*100) : 0;
    return { nombre:d.v, cobrado:d.cobrado, meta:d.meta, prometido:d.prometido, cerrado:d.cerrado,
             pct, ganadas:st.ganadas||0, abiertas:st.abiertas||0 };
  });
  const CHEV = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>;
  const PanelLink = ({children,to}) => (
    <button onClick={()=>to&&onNavigate(to)} className="radar-plus" style={{marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:4,fontSize:12,fontWeight:600,color:C.blue,background:'transparent',border:'none',cursor:'pointer',padding:'4px 6px',borderRadius:R.sm}}>{children}{CHEV}</button>
  );
  const panelSt   = {background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:SH.sm,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden'};
  const panelHead = {display:'flex',alignItems:'center',gap:8,padding:'13px 16px',borderBottom:`1px solid ${C.border}`,flexShrink:0};
  const panelTitleSt = {fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1};
  const panelMetaSt  = {fontSize:11.5,color:C.t3};
  const footSt   = {display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 16px',borderTop:`1px solid ${C.bg1}`,fontSize:11.5,color:C.t3};
  const b2 = {fontWeight:600,color:C.t2};

  return (
    <div style={{flex:1,overflowY:'auto',overflowX:'hidden',padding:'20px 24px',background:C.bg1}}>
      <Selector/>

      {/* ===== ROW 1 — HERO KPIs ===== */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))',gap:16,marginBottom:16}}>
        {/* Ventas del mes + barra de meta */}
        <div className="radar-kpi" onClick={()=>onNavigate('crm')} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',boxShadow:SH.sm,cursor:'pointer',display:'flex',flexDirection:'column',gap:10,minWidth:0}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
            <span style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',color:C.t3}}>Ventas del mes</span>
            <KIcon d={KI.trend} tone={TONES.primary}/>
          </div>
          <div style={{minWidth:0}}>
            <div className="radar-num" title={fmt(ventasMes)} style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',color:C.t1,lineHeight:1.05}}>{fmtK(ventasMes)}</div>
            {metaMes>0 ? (
              <div style={{display:'flex',flexDirection:'column',gap:5,marginTop:8}}>
                <div style={{height:6,borderRadius:R.pill,background:C.bg3,overflow:'hidden'}}><div style={{height:'100%',borderRadius:R.pill,background:C.blue,width:`${pctMeta}%`}}/></div>
                <div className="radar-num" style={{display:'flex',justifyContent:'space-between',fontSize:11,color:C.t3}}><span><b style={b2}>{pctMeta}%</b> de la meta</span><span>{fmtK(metaMes)}</span></div>
              </div>
            ) : <div style={{fontSize:11,color:C.t4,marginTop:8}}>Sin meta configurada</div>}
          </div>
        </div>

        {/* Cobrado este mes + sparkline */}
        <KpiCard label="Cobrado este mes" value={fmtK(totalIngMes)} sub={`${dIngresosMes.length} pagos en SAP`} icon={KI.check} tone="green" onClick={()=>onNavigate('cobros')} spark={<Spark points={sparkIngresos} color={C.green}/>}/>

        {/* Por cobrar vencido + aging apilado */}
        <div className="radar-kpi" onClick={()=>onNavigate('cobros')} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',boxShadow:SH.sm,cursor:'pointer',display:'flex',flexDirection:'column',gap:10,minWidth:0}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
            <span style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',color:C.t3}}>Por cobrar vencido</span>
            <KIcon d={KI.clock} tone={TONES.red}/>
          </div>
          <div style={{minWidth:0}}>
            <div className="radar-num" title={fmt(totalVencido)} style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',color:totalVencido>0?C.redText:C.t1,lineHeight:1.05}}>{fmtK(totalVencido)}</div>
            {totalVencido>0 ? (
              <div style={{display:'flex',flexDirection:'column',gap:5,marginTop:8}}>
                <div style={{display:'flex',height:6,borderRadius:R.pill,overflow:'hidden',background:C.bg3}}>
                  <i style={{display:'block',height:'100%',width:`${totalVencido>0?aging0_30/totalVencido*100:0}%`,background:C.orange}}/>
                  <i style={{display:'block',height:'100%',width:`${totalVencido>0?aging31_60/totalVencido*100:0}%`,background:'#EA580C'}}/>
                  <i style={{display:'block',height:'100%',width:`${totalVencido>0?aging60p/totalVencido*100:0}%`,background:C.red}}/>
                </div>
                <div className="radar-num" style={{display:'flex',gap:8,fontSize:10.5,color:C.t3,flexWrap:'wrap'}}>
                  <span><span style={{display:'inline-block',width:6,height:6,borderRadius:'50%',background:C.orange,marginRight:3,verticalAlign:1}}/>0-30 <b style={b2}>{fmtK(aging0_30)}</b></span>
                  <span><span style={{display:'inline-block',width:6,height:6,borderRadius:'50%',background:'#EA580C',marginRight:3,verticalAlign:1}}/>31-60 <b style={b2}>{fmtK(aging31_60)}</b></span>
                  <span><span style={{display:'inline-block',width:6,height:6,borderRadius:'50%',background:C.red,marginRight:3,verticalAlign:1}}/>+60 <b style={b2}>{fmtK(aging60p)}</b></span>
                </div>
              </div>
            ) : <div style={{fontSize:11,color:C.t4,marginTop:8}}>Sin saldo vencido</div>}
          </div>
        </div>

        {/* Pipeline ponderado + sparkline */}
        <KpiCard label="Pipeline ponderado" value={fmtK(Math.round(pipelinePond))} sub={`${dOpps.length} oportunidades`} icon={KI.funnel} tone="primary" onClick={()=>onNavigate('crm')} spark={<Spark points={stageData.map(s=>s.v)} color={C.blue}/>}/>

        {/* MRR rentas + sparkline */}
        {orgTiene('rentas') && <KpiCard label="MRR rentas" value={fmtK(mrr)} sub={`${dRentasAct.length} contratos activos`} icon={KI.dollar} tone="info" onClick={()=>onNavigate('rentas')} spark={<Spark points={dRentasAct.slice(0,8).map(r=>r.valor_mensual||0)} color={C.info}/>}/>}
      </div>

      {/* ===== ROW 2 — PIPELINE + ALERTAS ===== */}
      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:16,marginBottom:16}}>
        {/* Pipeline por etapa (barras horizontales) */}
        <div style={panelSt}>
          <div style={panelHead}>
            <span style={panelTitleSt}>Pipeline por etapa</span>
            <span className="radar-num" style={panelMetaSt}>· {fmtK(pipeline)} abiertos</span>
            <PanelLink to="crm">Ver pipeline</PanelLink>
          </div>
          <div style={{padding:16,flex:1}}>
            {stageData.length===0 ? <div style={{color:C.t4,fontSize:13,textAlign:'center',padding:'24px 0'}}>Sin oportunidades abiertas</div> : (
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              {stageData.map(s=>(
                <div key={s.id} onClick={()=>onNavigate('crm')} className="radar-hrow" style={{display:'grid',gridTemplateColumns:'110px 1fr 128px',alignItems:'center',gap:12,cursor:'pointer',borderRadius:R.md,padding:'4px 6px',margin:'-4px -6px'}}>
                  <span style={{display:'flex',alignItems:'center',gap:8,fontSize:12.5,fontWeight:600,color:C.t2,whiteSpace:'nowrap'}}>
                    <span style={{width:8,height:8,borderRadius:'50%',background:s.color,flexShrink:0,boxShadow:`0 0 0 3px ${s.color}22`}}/>{s.label}
                  </span>
                  <span style={{height:22,borderRadius:R.sm,background:C.bg1,position:'relative',overflow:'hidden',minWidth:0}}>
                    <span style={{position:'absolute',inset:'0 auto 0 0',borderRadius:R.sm,width:`${Math.max(4,s.v/maxStageV*100)}%`,background:`color-mix(in srgb, ${s.color} 70%, #FFFFFF)`}}/>
                  </span>
                  <span style={{display:'flex',alignItems:'baseline',justifyContent:'flex-end',gap:6,whiteSpace:'nowrap'}}>
                    <span className="radar-num" style={{fontSize:13.5,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>{fmtK(s.v)}</span>
                    <span className="radar-num" style={{fontSize:11,fontWeight:600,color:C.t3,background:C.bg3,padding:'1px 8px',borderRadius:R.pill}}>{s.n}</span>
                  </span>
                </div>
              ))}
            </div>)}
            <div className="radar-num" style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:14,paddingTop:12,borderTop:`1px solid ${C.bg1}`,fontSize:11.5,color:C.t3,flexWrap:'wrap',gap:10}}>
              <span>Ticket promedio: <b style={b2}>{ticketProm>0?fmtK(ticketProm):'—'}</b></span>
              <span>Tasa de cierre: <b style={b2}>{tasaCierre}%</b></span>
              <span>Ganadas (mes): <b style={b2}>{ganadasMes.length}</b></span>
            </div>
          </div>
        </div>

        {/* Alertas */}
        {(()=>{
          const alertas = [
            {sev:'red',   icon:<><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>, t:'Oportunidades estancadas', s:'Sin movimiento hace más de 60 días', n:oppsEstancadas, to:'seguimiento'},
            {sev:'red',   icon:<><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></>, t:'Facturas vencidas +30 días', s:`${fmtK(totalVenc30p)} por gestionar cobro`, n:vencidas30p.length, to:'cobros'},
            {sev:'amber', icon:<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>, t:'Sin seguimiento +3 días', s:'Requieren contacto esta semana', n:sinSeg, to:'seguimiento'},
            {sev:'amber', icon:<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>, t:'Contratos de renta por vencer', s:'Vencen en los próximos 15 días', n:contratosPorVencer, to:'rentas'},
          ].filter(a=>a.n>0);
          const nRed = alertas.filter(a=>a.sev==='red').reduce((s,a)=>s+1,0);
          const nAmber = alertas.filter(a=>a.sev==='amber').length;
          return (
          <div style={panelSt}>
            <div style={panelHead}>
              <span style={panelTitleSt}>Alertas</span>
              {nRed>0&&<span className="radar-num" style={{fontSize:11,fontWeight:700,padding:'1px 8px',borderRadius:R.pill,background:C.redTint,color:C.redText,border:`1px solid ${C.redBorder}`}}>{nRed}</span>}
              {nAmber>0&&<span className="radar-num" style={{fontSize:11,fontWeight:700,padding:'1px 8px',borderRadius:R.pill,background:C.orangeTint,color:C.orangeText,border:`1px solid ${C.orangeBorder}`}}>{nAmber}</span>}
              <PanelLink to="seguimiento">Ver todas</PanelLink>
            </div>
            <div style={{display:'flex',flexDirection:'column'}}>
              {alertas.length===0 ? <div style={{color:C.t4,fontSize:13,textAlign:'center',padding:'28px 0'}}>Sin alertas — todo al día ✓</div> :
              alertas.map((a,i)=>{ const tint=a.sev==='red'?TONES.red:TONES.orange;
                return (
                <div key={i} onClick={()=>onNavigate(a.to)} className="radar-hrow" style={{display:'flex',alignItems:'center',gap:10,padding:'11px 16px',borderBottom:i<alertas.length-1?`1px solid ${C.bg1}`:'none',cursor:'pointer'}}>
                  <span style={{width:28,height:28,borderRadius:R.md,display:'grid',placeItems:'center',flexShrink:0,background:tint.tint,color:tint.text,border:`1px solid ${tint.border}`}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{a.icon}</svg>
                  </span>
                  <span style={{flex:1,minWidth:0,lineHeight:1.35}}>
                    <span style={{display:'block',fontSize:12.5,fontWeight:600,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{a.t}</span>
                    <span className="radar-num" style={{display:'block',fontSize:11,color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{a.s}</span>
                  </span>
                  <span className="radar-num" style={{fontSize:11,fontWeight:700,padding:'1px 8px',borderRadius:R.pill,flexShrink:0,background:tint.tint,color:tint.text,border:`1px solid ${tint.border}`}}>{a.n}</span>
                  <span className="radar-chev" style={{color:C.t4,flexShrink:0}}>{CHEV}</span>
                </div>
              );})}
            </div>
          </div>
          );
        })()}
      </div>

      {/* ===== ROW 3 — ACTIVIDAD · FLOTA · DEUDORES ===== */}
      <div style={{display:'grid',gridTemplateColumns:orgTiene('flota')?'1fr 1fr 1fr':'1fr 1fr',gap:16,marginBottom:16}}>
        {/* Actividad de hoy */}
        <div style={panelSt}>
          <div style={panelHead}>
            <span style={panelTitleSt}>{ORG.demo?'Actividad reciente':'Actividad de hoy'}</span>
            <span className="radar-num" style={panelMetaSt}>· {actHoy.length} registrada{actHoy.length!==1?'s':''}</span>
            <PanelLink to="actividades">Agenda</PanelLink>
          </div>
          <div style={{display:'flex',flexDirection:'column',maxHeight:300,overflowY:'auto'}}>
            {actHoy.length===0 ? <div style={{color:C.t4,fontSize:13,textAlign:'center',padding:'28px 0'}}>{ORG.demo?'Sin actividades cargadas':'Sin actividades hoy'}</div> :
            actHoy.map((a,i)=>{ const tipo=(a.tipo||'').toLowerCase();
              const tk = tipo.includes('visit')?TONES.primary:tipo.includes('llam')||tipo.includes('call')?TONES.info:tipo==='entrega'?{tint:C.lob.repuestos.bg,text:C.lob.repuestos.text,border:C.lob.repuestos.border}:TONES.neutral;
              // Ícono SVG por tipo (mockup timelineItem): visita = pin, llamada = teléfono, entrega = camión, resto = nota.
              const tIcon = tipo.includes('visit') ? <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>
                : (tipo.includes('llam')||tipo.includes('call')) ? <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                : tipo==='entrega' ? <><rect x="1" y="3" width="15" height="13" rx="1"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></>
                : <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>;
              return (
              <div key={a.id||i} className="radar-hrow" style={{display:'flex',alignItems:'center',gap:10,padding:'9px 16px',borderBottom:i<actHoy.length-1?`1px solid ${C.bg1}`:'none'}}>
                <span className="radar-num" style={{fontSize:11.5,fontWeight:600,color:C.t3,width:44,flexShrink:0}}>{a.created_at?new Date(a.created_at).toLocaleTimeString('es-HN',{hour:'2-digit',minute:'2-digit'}):'—'}</span>
                <span style={{width:26,height:26,borderRadius:R.md,display:'grid',placeItems:'center',flexShrink:0,background:tk.tint,color:tk.text,border:`1px solid ${tk.border}`}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{tIcon}</svg>
                </span>
                <span style={{flex:1,minWidth:0,lineHeight:1.3}}>
                  <span style={{display:'block',fontSize:12.5,fontWeight:600,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{a.titulo||'Actividad'}</span>
                  {a.descripcion&&<span style={{display:'block',fontSize:11,color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{a.descripcion}</span>}
                </span>
              </div>
            );})}
          </div>
        </div>

        {/* Estado de flota — donut (solo orgs con modulo de flota) */}
        {orgTiene('flota') && (
        <div style={panelSt}>
          <div style={panelHead}>
            <span style={panelTitleSt}>Estado de flota</span>
            <span className="radar-num" style={panelMetaSt}>· {dEquipos.length} equipos</span>
            <PanelLink to="flota">Flota</PanelLink>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:18,padding:16}}>
            <div style={{position:'relative',width:124,height:124,flexShrink:0}}>
              <svg width="124" height="124" viewBox="0 0 120 120" role="img" aria-label="Estado de flota">
                <circle cx="60" cy="60" r="45" fill="none" stroke={C.bg3} strokeWidth="12"/>
                <g transform="rotate(-90 60 60)">
                  {donutSegs.map((seg,i)=>(
                    <circle key={i} cx="60" cy="60" r="45" fill="none" stroke={seg.color} strokeWidth="12" strokeDasharray={`${seg.len} ${CIRC}`} strokeDashoffset={seg.off} strokeLinecap="butt"/>
                  ))}
                </g>
              </svg>
              <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',lineHeight:1.1}}>
                <span className="radar-num" style={{fontSize:21,fontWeight:700,letterSpacing:'-0.02em',color:C.t1}}>{utilPct}%</span>
                <span style={{fontSize:10,fontWeight:600,letterSpacing:'.06em',textTransform:'uppercase',color:C.t3,marginTop:2}}>Utilización</span>
              </div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8,flex:1,minWidth:0}}>
              {fleetCounts.map(f=>(
                <div key={f.k} style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:C.t2,whiteSpace:'nowrap'}}>
                  <span style={{width:8,height:8,borderRadius:'50%',background:f.color,flexShrink:0}}/>
                  <span style={{overflow:'hidden',textOverflow:'ellipsis'}}>{f.label}</span>
                  <span className="radar-num" style={{marginLeft:'auto',fontWeight:700,color:C.t1}}>{f.n}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="radar-num" style={footSt}><span>Utilización de flota rentable: <b style={b2}>{utilPct}%</b></span><span>{enRenta} de {rentables}</span></div>
        </div>
        )}

        {/* Top deudores */}
        <div style={panelSt}>
          <div style={panelHead}>
            <span style={panelTitleSt}>Top deudores</span>
            <span style={panelMetaSt}>· saldo vencido</span>
            <PanelLink to="cobros">Cobros</PanelLink>
          </div>
          <div style={{display:'flex',flexDirection:'column'}}>
            {topDeudores.length===0 ? <div style={{color:C.t4,fontSize:13,textAlign:'center',padding:'28px 0'}}>Sin saldos vencidos</div> :
            topDeudores.map((d,i)=>{ const dcls=d.maxDias>30?TONES.red:d.maxDias>15?TONES.orange:TONES.neutral;
              return (
              <div key={i} onClick={()=>onNavigate('cobros')} className="radar-hrow" style={{display:'flex',alignItems:'center',gap:10,padding:'10px 16px',borderBottom:i<topDeudores.length-1?`1px solid ${C.bg1}`:'none',cursor:'pointer'}}>
                <span style={{flex:1,minWidth:0,fontSize:12.5,fontWeight:600,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={d.nombre}>{d.nombre}</span>
                <span className="radar-num" title={fmt(d.saldo)} style={{fontSize:13,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,whiteSpace:'nowrap'}}>{fmtK(d.saldo)}</span>
                <span className="radar-num" style={{display:'inline-flex',alignItems:'center',justifyContent:'center',minWidth:44,fontSize:11,fontWeight:600,padding:'1px 8px',borderRadius:R.pill,background:dcls.tint,color:dcls.text,border:`1px solid ${dcls.border}`}}>{d.maxDias}d</span>
              </div>
            );})}
          </div>
          {topDeudores.length>0&&<div className="radar-num" style={footSt}><span>Total {topDeudores.length} principales</span><b style={{fontWeight:600,color:C.redText}}>{fmtK(totalTop4)}</b></div>}
        </div>
      </div>

      {/* ===== ROW 3.5 — NEXA + INVENTARIO ===== */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
        {/* Leads NEXA */}
        <div style={panelSt}>
          <div style={panelHead}>
            <span style={panelTitleSt}>Leads de redes (NEXA)</span>
            <span style={panelMetaSt}>· {ORG.demo?'recientes':'esta semana'}</span>
            <PanelLink to="sibila">NEXA</PanelLink>
          </div>
          <div style={{display:'flex',alignItems:'stretch',gap:16,padding:'16px 16px 0'}}>
            <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',justifyContent:'center',gap:3}}>
              <span style={{fontSize:11,fontWeight:600,letterSpacing:'.07em',textTransform:'uppercase',color:C.t3}}>Leads nuevos</span>
              <span className="radar-num" style={{fontSize:26,fontWeight:700,lineHeight:1.05,letterSpacing:'-0.025em',color:C.t1}}>{leadsSemana}</span>
              <span style={{fontSize:11,color:C.t4,marginTop:3}}>{leadsConvertidos} convertido{leadsConvertidos!==1?'s':''}</span>
            </div>
            <div onClick={()=>onNavigate('sibila')} style={{flex:1,minWidth:0,borderRadius:R.md,padding:'10px 12px',display:'flex',flexDirection:'column',justifyContent:'center',gap:2,lineHeight:1.3,cursor:'pointer',
              background:leadsSinAtender>0?C.redTint:C.greenTint,border:`1px solid ${leadsSinAtender>0?C.redBorder:C.greenBorder}`}}>
              <span className="radar-num" style={{fontSize:15,fontWeight:700,letterSpacing:'-0.01em',color:leadsSinAtender>0?C.redText:C.greenText,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{leadsSinAtender} sin atender</span>
              <span className="radar-num" style={{fontSize:11,fontWeight:500,color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{leads24h>0?<><b style={{color:C.redText}}>{leads24h} con más de 24h</b> · ventana WhatsApp</>:'ventana de WhatsApp al día'}</span>
            </div>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:10,padding:'14px 16px 0'}}>
            {[
              {k:'whatsapp', label:'WhatsApp',  n:canalCount.whatsapp, color:C.green,  tint:C.greenTint,  bd:C.greenBorder,  txt:C.greenText, mix:65,
               icon:<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>},
              {k:'messenger',label:'Messenger', n:canalCount.messenger,color:C.info,   tint:C.infoTint,   bd:C.infoBorder,   txt:C.infoText, mix:60,
               icon:<><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><polyline points="8 11 11 13 16 9"/></>},
              {k:'instagram',label:'Instagram', n:canalCount.instagram,color:'#6D28D9',tint:'#F5F3FF',    bd:'#DDD6FE',      txt:'#6D28D9', mix:55,
               icon:<><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></>},
            ].map(c=>(
              <div key={c.k} style={{display:'grid',gridTemplateColumns:'24px 84px 1fr 22px',alignItems:'center',gap:10,minWidth:0}}>
                <span title={c.label} style={{width:24,height:24,borderRadius:R.md,display:'grid',placeItems:'center',flexShrink:0,background:c.tint,color:c.txt,border:`1px solid ${c.bd}`}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{c.icon}</svg>
                </span>
                <span style={{fontSize:12,fontWeight:600,color:C.t2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{c.label}</span>
                <span style={{height:8,borderRadius:R.pill,background:C.bg1,position:'relative',minWidth:0}}><span style={{position:'absolute',inset:'0 auto 0 0',borderRadius:R.pill,width:`${maxCanal>0?c.n/maxCanal*100:0}%`,background:`color-mix(in srgb, ${c.color} ${c.mix}%, #FFFFFF)`}}/></span>
                <span className="radar-num" style={{fontSize:12.5,fontWeight:700,color:C.t1,textAlign:'right'}}>{c.n}</span>
              </div>
            ))}
          </div>
          <div className="radar-num" style={{...footSt,marginTop:12}}>
            <span>Sin atender: <b style={b2}>{leadsSinAtender}</b></span>
            <span>Conversión lead→opp: <b style={b2}>{conversionNexa!=null?`${conversionNexa}%`:'—'}</b></span>
          </div>
        </div>

        {/* Inventario */}
        <div style={panelSt}>
          <div style={panelHead}>
            <span style={panelTitleSt}>Inventario</span>
            <span style={panelMetaSt}>· SAP B1</span>
            <PanelLink to="inventario">Inventario</PanelLink>
          </div>
          <div style={{display:'flex',alignItems:'stretch',gap:16,padding:'16px 16px 0'}}>
            <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',justifyContent:'center',gap:3}}>
              <span style={{fontSize:11,fontWeight:600,letterSpacing:'.07em',textTransform:'uppercase',color:C.t3}}>Valor de inventario</span>
              <span className="radar-num" title={invValorTotal!=null?fmt(invValorTotal):''} style={{fontSize:26,fontWeight:700,lineHeight:1.05,letterSpacing:'-0.025em',color:C.t1}}>{invValorTotal!=null?fmtK(invValorTotal):'—'}</span>
              <span style={{fontSize:11,color:C.t4,marginTop:3}}>{invItems!=null?`${invItems.length} ítems · repuestos + equipos`:'cargando SAP…'}</span>
            </div>
            <div style={{flex:1,minWidth:0,borderRadius:R.md,padding:'10px 12px',display:'flex',flexDirection:'column',justifyContent:'center',gap:2,lineHeight:1.3,background:C.bg3,border:`1px solid ${C.border}`}}>
              <span className="radar-num" style={{fontSize:15,fontWeight:700,letterSpacing:'-0.01em',color:C.t2}}>{invItems!=null?`${invItems.filter(i=>i.stock>0).length} con stock`:'—'}</span>
              <span style={{fontSize:11,fontWeight:500,color:C.t3}}>en existencia actual</span>
            </div>
          </div>
          <div style={{fontSize:11,fontWeight:600,letterSpacing:'.07em',textTransform:'uppercase',color:C.t3,padding:'14px 16px 6px'}}>Ítems con más valor</div>
          <div style={{display:'flex',flexDirection:'column'}}>
            {invItems==null ? <div style={{color:C.t4,fontSize:13,textAlign:'center',padding:'20px 0'}}>Cargando inventario de SAP…</div> :
             invItems.length===0 ? <div style={{color:C.t4,fontSize:13,textAlign:'center',padding:'20px 0'}}>Sin ítems de inventario</div> :
             [...invItems].sort((a,b)=>(b.valor_total_usd||0)-(a.valor_total_usd||0)).slice(0,4).map((it,i)=>(
              <div key={i} className="radar-hrow" style={{display:'flex',alignItems:'center',gap:10,padding:'8px 16px',borderBottom:i<3?`1px solid ${C.bg1}`:'none'}}>
                <span style={{width:26,height:26,borderRadius:R.md,display:'grid',placeItems:'center',flexShrink:0,background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                </span>
                <span style={{flex:1,minWidth:0,fontSize:12.5,fontWeight:600,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={it.nombre}>{it.nombre}</span>
                <span className="radar-num" style={{fontSize:11,color:C.t3,whiteSpace:'nowrap'}}>{it.stock} uds</span>
                <span className="radar-num" style={{fontSize:12.5,fontWeight:700,color:C.t1,whiteSpace:'nowrap'}}>{fmtK(it.valor_total_usd)}</span>
              </div>
            ))}
          </div>
          <div className="radar-num" style={{...footSt,marginTop:12}}>
            <span>Ítems totales: <b style={b2}>{invItems!=null?invItems.length:'—'}</b></span>
            <span>Con existencia: <b style={b2}>{invItems!=null?invItems.filter(i=>i.stock>0).length:'—'}</b></span>
          </div>
        </div>
      </div>

      {/* ===== ROW 4 — RENDIMIENTO DEL EQUIPO (bullet bars) ===== */}
      {(usuario.rol==='admin'||usuario.rol==='ventas'||usuario.rol==='gerente_ventas')&&teamRows.length>0&&(
        <div style={{...panelSt,marginBottom:16}}>
          <div style={panelHead}>
            <span style={panelTitleSt}>Rendimiento del equipo</span>
            <span style={panelMetaSt}>· cobrado vs meta · {new Date().toLocaleDateString('es-HN',{month:'long',year:'numeric'})}</span>
            <PanelLink to="metas">Metas &amp; Revisión</PanelLink>
          </div>
          {teamRows.length===0 ? <div style={{padding:'22px 16px',textAlign:'center',color:C.t4,fontSize:13}}>Sin vendedores con meta este mes.</div> : (
          <div style={{display:'flex',flexDirection:'column'}}>
            {teamRows.map((v,i)=>{ const cls=v.pct>=85?{fill:C.green,txt:C.greenText}:v.pct>=70?{fill:C.blue,txt:C.blue}:{fill:C.orange,txt:C.orangeText};
              return (
              <div key={v.nombre} onClick={()=>onNavigate('metas')} className="radar-hrow" style={{display:'grid',gridTemplateColumns:'190px 120px 1fr 150px',alignItems:'center',gap:16,padding:'12px 16px',borderBottom:i<teamRows.length-1?`1px solid ${C.bg1}`:'none',cursor:'pointer'}}>
                <div style={{display:'flex',alignItems:'center',gap:10,minWidth:0}}>
                  <AvatarChip name={v.nombre} size={28}/>
                  <div style={{lineHeight:1.3,minWidth:0}}>
                    <div style={{fontSize:12.5,fontWeight:600,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{v.nombre}</div>
                    <div className="radar-num" style={{fontSize:10.5,color:C.t3}}>prometido {fmtK(v.prometido)} · cerrado {fmtK(v.cerrado)}</div>
                  </div>
                </div>
                <div style={{textAlign:'right',lineHeight:1.25}}>
                  <div className="radar-num" title={fmt(v.cobrado)} style={{fontSize:15,fontWeight:700,letterSpacing:'-0.015em',color:C.greenText}}>{fmtK(v.cobrado)}</div>
                  <div className="radar-num" style={{fontSize:10.5,color:C.t4}}>de {fmtK(v.meta)}</div>
                </div>
                <div style={{position:'relative',height:8,borderRadius:R.pill,background:C.bg3,minWidth:0}}>
                  <span style={{position:'absolute',inset:'0 auto 0 0',borderRadius:R.pill,height:'100%',width:`${Math.min(100,v.pct)}%`,background:cls.fill}}/>
                  <span style={{position:'absolute',top:-3,bottom:-3,width:2,borderRadius:1,background:C.t2,right:0}}/>
                </div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'flex-end',gap:8,whiteSpace:'nowrap'}}>
                  <span className="radar-num" style={{fontSize:13,fontWeight:800,minWidth:38,textAlign:'right',color:cls.txt}}>{v.pct}%</span>
                  <span className="radar-num" style={{fontSize:11,fontWeight:600,color:C.t3,background:C.bg3,padding:'2px 9px',borderRadius:R.pill}}>{v.ganadas} ganadas</span>
                </div>
              </div>
            );})}
          </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── FLOTA ────────────────────────────────────────────────────────────────────
function FormRenta({ equipoFijo, clientes, equipos, empresaActiva, onSave, onClose, documentar }) {
  const [f,setF]=useState({ equipo_id:equipoFijo?.id||'', cliente_id:'', cliente_nombre:'', valor_mensual:'', fecha_inicio:new Date().toISOString().split('T')[0], fecha_fin:'', fecha_fin_factura:'', tipo_cobro:'mensual', notas:'', cotizacion_sap:'', factura_sap:'' });
  const [saving,setSaving]=useState(false);
  // Crear la orden de trabajo en SAP a partir de la cotizacion. Es lo que imprime
  // el taller y lo que firma el cliente al recibir la maquina.
  const [conOT,setConOT]=useState(true);
  const [cotizsCli,setCotizsCli]=useState([]);   // cotizaciones del cliente elegido (SAP)
  const [facturasCli,setFacturasCli]=useState([]);// facturas del cliente elegido (SAP)
  // fecha_fin es OBLIGATORIA. Es el unico dato que permite avisar antes de que
  // la renta se venza; sin el, la maquina se queda en la planta del cliente sin
  // que nadie se entere, que es lo que hoy pasa con MT-23 y MT-7.
  const ok = f.equipo_id&&f.cliente_id&&f.valor_mensual&&f.fecha_fin&&f.fecha_fin>=f.fecha_inicio;

  // Al elegir cliente: traer SUS cotizaciones y facturas de SAP para elegir de una
  // lista (en vez de teclear el numero a mano).
  useEffect(()=>{
    if (!f.cliente_id) { setCotizsCli([]); setFacturasCli([]); return; }
    let vivo=true;
    (async()=>{
      const [{data:cot},{data:fac}] = await Promise.all([
        supabase.from('oportunidades').select('sap_quotation_num,titulo').eq('cliente_id',f.cliente_id).not('sap_quotation_num','is',null).order('sap_quotation_num',{ascending:false}).limit(60),
        supabase.from('facturas_sap').select('doc_num,doc_date,total_usd,doc_total').ilike('card_name', f.cliente_nombre||'').order('doc_date',{ascending:false}).limit(60),
      ]);
      if(!vivo) return;
      const seen=new Set();
      setCotizsCli((cot||[]).filter(o=>{const n=String(o.sap_quotation_num); if(seen.has(n))return false; seen.add(n); return true;})
        .map(o=>({v:String(o.sap_quotation_num), l:`${o.sap_quotation_num}${o.titulo?` — ${String(o.titulo).slice(0,32)}`:''}`})));
      setFacturasCli((fac||[]).map(x=>({v:String(x.doc_num), l:`${x.doc_num} · ${x.doc_date?new Date(x.doc_date+'T12:00:00Z').toLocaleDateString('es-HN'):''} · $${Math.round(x.total_usd||x.doc_total||0).toLocaleString('en-US')}`})));
    })();
    return ()=>{vivo=false;};
  }, [f.cliente_id, f.cliente_nombre]);
  const guardar = async () => {
    if (!ok || saving) return; setSaving(true);
    // El equipo pudo rentarse desde otra sesión: verificar que siga disponible.
    // Al DOCUMENTAR una renta que ya existe (equipo ya marcado en_renta por Miguel)
    // no exigimos que este 'disponible'; solo bloqueamos si se renta un equipo libre
    // que otra sesion ya rento.
    const {data:eqNow} = await supabase.from('equipos').select('estado').eq('id',f.equipo_id).maybeSingle();
    if (!documentar && eqNow && eqNow.estado !== 'disponible') { toast('Ese equipo ya no está disponible (estado: '+eqNow.estado+'). Refrescá y elegí otro.'); setSaving(false); return; }
    // CON ORDEN DE TRABAJO: la orden se crea en SAP COPIANDO la cotizacion, y es
    // el servidor el que abre la renta y marca la maquina. Asi la orden queda
    // ligada a la cotizacion -SAP la imprime como "Basado en Ofertas de ventas
    // N"- y la cotizacion se cierra sola en vez de quedar abierta para siempre.
    if (conOT && f.cotizacion_sap.trim()) {
      const cli = clientes.find(c=>c.id===f.cliente_id);
      const empId = cli?.empresa_id || (empresaActiva && empresaActiva!=='all' ? empresaActiva : 'honduras');
      try {
        const r = await fetch('/api/sap',{method:'POST',headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ action:'crear_orden_trabajo', empresa_id: empId,
            docNum: f.cotizacion_sap.trim(), equipo_id: f.equipo_id,
            fecha_inicio: f.fecha_inicio, fecha_fin: f.fecha_fin,
            tipo_cobro: f.tipo_cobro, notas: f.notas || null,
            valor_mensual: parseFloat(f.valor_mensual) || null })});
        const d = await r.json();
        if (!d.ok) { toast('SAP: '+(d.error||'no se pudo crear la orden')); setSaving(false); return; }
        toast(`Orden de trabajo ${d.docNum} creada en SAP`);
        // EL PAPEL SALE DE RADAR. Si hay que entrar a SAP a imprimirlo, el
        // vendedor termina haciendo todo alla y la renta no queda registrada de
        // este lado: sin fechas, sin aviso de vencimiento, sin historial de la
        // maquina. Por eso el PDF se baja solo al crear la orden.
        if (d.pdf?.base64) {
          try {
            const a = document.createElement('a');
            a.href = `data:application/pdf;base64,${d.pdf.base64}`;
            a.download = `Orden-de-trabajo-${d.docNum}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
          } catch (e) { toast('La orden se creo, pero el PDF no se pudo descargar: '+e.message); }
        } else if (d.pdfError) { toast(d.pdfError); }
        if (d.avisos) d.avisos.forEach(a=>toast(a));
      } catch(e) { toast('No se pudo hablar con SAP: '+e.message); setSaving(false); return; }
      setSaving(false); onSave(f.equipo_id); return;
    }

    const {data,error} = await supabase.from('rentas').insert({ equipo_id:f.equipo_id, cliente_id:f.cliente_id, estado:'activa', valor_mensual:parseFloat(f.valor_mensual), valor:parseFloat(f.valor_mensual), fecha_inicio:f.fecha_inicio, fecha_fin:f.fecha_fin||null, fecha_fin_factura:f.fecha_fin_factura||null, tipo_cobro:f.tipo_cobro, notas:f.notas, cotizacion_sap:f.cotizacion_sap.trim()||null, factura_sap:f.factura_sap.trim()||null }).select().single();
    if (error) { toast('No se pudo crear la renta: '+error.message); setSaving(false); return; }
    if (data) {
      await supabase.from('equipos').update({ estado:'en_renta', asignado_a:f.cliente_id }).eq('id',f.equipo_id);
      // Verificar si hay cotización previa para este cliente+equipo
      const {data:cot} = await supabase.from('cotizaciones').select('id').eq('cliente_id',f.cliente_id).eq('equipo_id',f.equipo_id).in('estado',['aprobada','enviada']).limit(1);
      if (!cot || cot.length===0) {
        const cl = clientes.find(c=>c.id===f.cliente_id);
        const eq = equipos.find(e=>e.id===f.equipo_id);
        await supabase.from('actividades').insert({
          tipo:'alerta',
          titulo:'⚠️ Renta sin cotización previa',
          descripcion:`${eq?.codigo||'Equipo'} → ${cl?.empresa||'Cliente'} — Se creó una renta sin cotización aprobada. Pendiente documentar.`,
          fecha_actividad: new Date().toISOString(),
        });
      }
    }
    setSaving(false); onSave(f.equipo_id);
  };
  // EL SELECTOR DE EQUIPO, ORDENADO COMO PIENSA QUIEN RENTA.
  // Salia por codigo alfabetico, asi que arrancaba con "010352h5584" y
  // "B16006V03440": son EQUIPOS DE CLIENTE -maquinas ajenas que estan en el
  // taller- y no se pueden rentar. Se sacan de la lista, y el resto se agrupa por
  // tipo en el orden en que se piden: montacargas, luego plataformas, y al final
  // lo demas.
  const ORDEN_TIPO = ['montacargas','plataforma aerea','elevadora','electro tower','stacker','bar clamp','mulas','vehiculo'];
  const normTipo = t => String(t||'').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'').trim();
  const rankTipo = t => { const i = ORDEN_TIPO.indexOf(normTipo(t)); return i < 0 ? 99 : i; };
  const disponibles = equipos
    .filter(e=>e.estado==='disponible')
    .filter(e=>normTipo(e.tipo_equipo)!=='equipos de cliente' && normTipo(e.tipo_equipo)!=='taller')
    .sort((a,b)=> rankTipo(a.tipo_equipo)-rankTipo(b.tipo_equipo)
      || String(a.tipo_equipo||'').localeCompare(String(b.tipo_equipo||''))
      || (b.capacidad_kg||0)-(a.capacidad_kg||0)
      || String(a.codigo||'').localeCompare(String(b.codigo||'')));
  // Etiqueta legible: tipo, codigo, marca y modelo, y la capacidad si se sabe.
  const etiquetaEq = e => {
    const cap = e.capacidad_kg ? ` · ${Number(e.capacidad_kg).toLocaleString('en-US')} kg` : '';
    const mm = [e.marca, e.modelo].filter(Boolean).join(' ');
    return `${titleCase(e.tipo_equipo||'Sin tipo')} — ${e.codigo}${mm?` · ${mm}`:''}${cap}`;
  };
  // El nombre del campo del valor depende de como se cobra: decia "Valor
  // mensual" incluso cuando la renta era semanal.
  const LABEL_VALOR = { 'por hora':'Valor por hora ($)', diaria:'Valor diario ($)', semanal:'Valor semanal ($)',
    quincenal:'Valor quincenal ($)', mensual:'Valor mensual ($)', 'por proyecto':'Valor del proyecto ($)' };
  return (
    <div>
      {equipoFijo
        ? <div style={{background:C.bg3,borderRadius:R.md,padding:'10px 14px',marginBottom:14,fontSize:13,color:C.t2,display:'flex',alignItems:'center',gap:10}}><EquipIcon e={equipoFijo} size={30} icon={15}/>{equipoFijo.codigo} — {equipoFijo.modelo}</div>
        : disponibles.length===0
          ? <div style={{background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.md,padding:'12px 14px',marginBottom:14,fontSize:12.5,color:C.orangeText,fontWeight:600,display:'flex',alignItems:'center',gap:8}}><CIcon d={KI.tool} size={14}/>No hay equipos disponibles ahora mismo — todos están en renta, taller o inactivos. Liberá uno desde Flota para poder rentarlo.</div>
          : <SearchSel label="Equipo *" value={f.equipo_id} onChange={v=>setF(x=>({...x,equipo_id:v}))} required placeholder="Buscar equipo..."
            options={disponibles.map(e=>({v:e.id,l:etiquetaEq(e)}))}/>}
      <ClienteBuscador
        clientes={clientes}
        value={f.cliente_nombre}
        onSelect={c=>setF(x=>({...x,cliente_id:c.id,cliente_nombre:c.empresa}))}
        onClear={()=>setF(x=>({...x,cliente_id:'',cliente_nombre:''}))}
        empresaActiva={empresaActiva}
      />
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
        {/* El tipo de cobro va ANTES que el monto: es el que decide que
            significa el numero que se escribe abajo. */}
        <Sel label="Tipo cobro *" value={f.tipo_cobro} onChange={v=>setF(x=>({...x,tipo_cobro:v}))} options={[
          {v:'por hora',l:'POR HORA'},{v:'diaria',l:'DIARIA'},{v:'semanal',l:'SEMANAL'},
          {v:'quincenal',l:'QUINCENAL'},{v:'mensual',l:'MENSUAL'},{v:'por proyecto',l:'POR PROYECTO'}]}/>
        <Inp label={`${LABEL_VALOR[f.tipo_cobro]||'Valor ($)'} *`} type="number" value={f.valor_mensual} onChange={v=>setF(x=>({...x,valor_mensual:v}))} required/>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
        <Inp label="Fecha inicio" type="date" value={f.fecha_inicio} onChange={v=>setF(x=>({...x,fecha_inicio:v}))}/>
        {/* Sin fecha de fin no hay aviso posible y la maquina se queda olvidada
            en la planta del cliente. Por eso es obligatoria. */}
        <Inp label="Fin de la renta *" type="date" value={f.fecha_fin} onChange={v=>setF(x=>({...x,fecha_fin:v}))} required/>
      </div>
      {f.fecha_fin&&f.fecha_fin<f.fecha_inicio&&(
        <div style={{fontSize:11.5,color:C.redText,background:C.redTint,border:`1px solid ${C.redBorder}`,borderRadius:8,padding:'8px 10px',marginBottom:12}}>
          La fecha de fin no puede ser anterior a la de inicio.
        </div>
      )}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
        <Inp label="Fecha fin primera factura" type="date" value={f.fecha_fin_factura} onChange={v=>setF(x=>({...x,fecha_fin_factura:v}))}/>
        <div/>
      </div>
      {/* Documento de venta que respalda el contrato: se elige de las cotizaciones/
          facturas del cliente (SAP). Si no hay cargadas, se puede escribir el numero. */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
        {cotizsCli.length
          ? <SearchSel label="Cotización SAP #" value={f.cotizacion_sap} onChange={v=>setF(x=>({...x,cotizacion_sap:v}))} placeholder="Elegí una cotización..." options={cotizsCli}/>
          : <Inp label="Cotización SAP #" value={f.cotizacion_sap} onChange={v=>setF(x=>({...x,cotizacion_sap:v}))} placeholder={f.cliente_id?'sin cotizaciones — escribí el #':'elegí cliente primero'}/>}
        {facturasCli.length
          ? <SearchSel label="Factura SAP #" value={f.factura_sap} onChange={v=>setF(x=>({...x,factura_sap:v}))} placeholder="Elegí una factura..." options={facturasCli}/>
          : <Inp label="Factura SAP #" value={f.factura_sap} onChange={v=>setF(x=>({...x,factura_sap:v}))} placeholder={f.cliente_id?'sin facturas — escribí el #':'si ya se facturó'}/>}
      </div>
      {/* LA ORDEN DE TRABAJO. En SAP es una Orden de Venta copiada de la
          cotizacion: por eso se imprime con la leyenda "Basado en Ofertas de
          ventas N" y por eso la cotizacion queda cerrada en vez de abierta
          para siempre. Es el papel que firma el cliente al recibir la maquina. */}
      {f.cotizacion_sap.trim()&&(
        <label style={{display:'flex',gap:10,alignItems:'flex-start',cursor:'pointer',
          background:conOT?C.primaryTint:C.bg3,border:`1px solid ${conOT?C.primaryBorder:C.border}`,
          borderRadius:8,padding:'10px 12px',marginBottom:12}}>
          <input type="checkbox" checked={conOT} onChange={e=>setConOT(e.target.checked)}
            style={{width:16,height:16,flexShrink:0,marginTop:1,accentColor:C.blue}}/>
          <span style={{fontSize:12.5,color:C.t2,lineHeight:1.5}}>
            <b style={{color:C.t1}}>Crear la orden de trabajo en SAP</b> desde la cotización {f.cotizacion_sap.trim()}.
            {' '}Es la que imprime el taller para entregar la máquina.
            <span style={{display:'block',color:C.t4,fontSize:11.5,marginTop:2}}>
              Si la desmarcás, la renta queda registrada solo en RADAR y la orden hay que hacerla a mano en SAP.
            </span>
          </span>
        </label>
      )}
      {!f.cotizacion_sap.trim()&&!f.factura_sap.trim()&&(
        <div style={{fontSize:11.5,color:C.orange,background:'#F59E0B12',border:'1px solid #F59E0B40',borderRadius:8,padding:'8px 10px',marginBottom:12}}>
          ⚠️ Sin documento de venta adjunto — la renta quedará marcada como pendiente de documentar (cotización o factura SAP).
        </div>
      )}
      <Inp label="Notas" value={f.notas} onChange={v=>setF(x=>({...x,notas:v}))} placeholder="condiciones especiales, entrega..." rows={2}/>
      <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:4}}>
        <Btn variant="secondary" onClick={onClose}>Cancelar</Btn>
        <Btn onClick={guardar} disabled={!ok||saving}>{saving?'Guardando...':'✅ Iniciar Renta'}</Btn>
      </div>
    </div>
  );
}

function DetalleEquipo({ eq: eqInicial, clientes, onClose, onUpdate }) {
  const [eq, setEq] = useState(eqInicial);
  const [tab, setTab] = useState('info');
  const [gastos, setGastos] = useState([]);
  const [loadingG, setLoadingG] = useState(true);
  const [editEstado, setEditEstado] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState(eqInicial.estado);
  const [razon, setRazon] = useState('');
  const [rentaActiva, setRentaActiva] = useState(null);
  const [gasto, setGasto] = useState({ tipo:'', descripcion:'', mano_obra:'', costo_repuestos:'', tecnico:'', taller:'' });
  const [savingG, setSavingG] = useState(false);

  useEffect(()=>{
    supabase.from('mantenimientos').select('*').eq('equipo_id',eq.id).order('created_at',{ascending:false}).then(({data})=>{setGastos(data||[]);setLoadingG(false);});
    if (eq.estado==='en_renta') supabase.from('rentas').select('*,clientes(empresa)').eq('equipo_id',eq.id).eq('estado','activa').single().then(({data})=>setRentaActiva(data));
  },[eq.id]);

  const cambiarEstado = async () => {
    await supabase.from('equipos').update({estado:nuevoEstado,razon_detencion:razon||null}).eq('id',eq.id);
    const upd={...eq,estado:nuevoEstado,razon_detencion:razon||null};
    setEq(upd); onUpdate(upd); setEditEstado(false);
  };

  const agregarGasto = async () => {
    if (!gasto.tipo) return; setSavingG(true);
    const total=(parseFloat(gasto.mano_obra)||0)+(parseFloat(gasto.costo_repuestos)||0);
    const {data}=await supabase.from('mantenimientos').insert({equipo_id:eq.id,tipo:gasto.tipo,descripcion:gasto.descripcion,tecnico:gasto.tecnico,mano_obra:parseFloat(gasto.mano_obra)||0,costo_repuestos:parseFloat(gasto.costo_repuestos)||0,total,taller:gasto.taller,fecha:new Date().toISOString().split('T')[0]}).select().single();
    if (data) setGastos(g=>[data,...g]);
    setGasto({tipo:'',descripcion:'',mano_obra:'',costo_repuestos:'',tecnico:'',taller:''});
    setSavingG(false); setTab('gastos');
  };

  const totalGastos = gastos.reduce((s,g)=>s+(g.total||0),0);
  const tabs = [['info','Info',CI.clipboard],['gastos',`Gastos (${gastos.length})`,CI.cash],['nuevo','Agregar Gasto',CI.flag]];

  // ── Helpers de presentacion de la ficha (stateless) ──
  const vacio = v => v===null || v===undefined || String(v).trim()==='' || String(v)==='—';
  const split = campos => [campos.filter(c=>!vacio(c[1])), campos.filter(c=>vacio(c[1])).map(c=>c[0])];

  const Sec = ({ icon, title, right, children }) => (
    <section style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,boxShadow:SH.xs,marginBottom:12,overflow:'hidden'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 14px',background:C.bg1,borderBottom:`1px solid ${C.border}`}}>
        <span style={{color:C.t3,display:'inline-flex'}}><CIcon d={icon} size={13}/></span>
        <span style={{fontSize:10.5,fontWeight:700,color:C.t2,textTransform:'uppercase',letterSpacing:'.07em'}}>{title}</span>
        {right && <span style={{marginLeft:'auto'}}>{right}</span>}
      </div>
      <div style={{padding:'2px 14px 12px'}}>{children}</div>
    </section>
  );

  const DL = ({ children }) => (
    <dl style={{margin:0,display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(190px,100%),1fr))',gap:'0 20px'}}>{children}</dl>
  );

  const F = ({ l, v, o={} }) => (
    <div style={{padding:'9px 0',borderBottom:`1px solid ${C.border}`,minWidth:0}}>
      <dt style={{fontSize:10,color:C.t3,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>{l}</dt>
      <dd className={o.num?'radar-num':undefined} style={{margin:0,fontSize:13.5,fontWeight:600,color:o.color||C.t1,display:'flex',alignItems:'center',gap:6,overflowWrap:'anywhere'}}>
        {o.dot && <span style={{width:7,height:7,borderRadius:R.pill,background:o.dot,flexShrink:0}}/>}
        {v}
      </dd>
    </div>
  );

  const Faltan = ({ ls }) => !ls.length ? null : (
    <div style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:6,paddingTop:10}}>
      <span style={{fontSize:10,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>Sin dato</span>
      {ls.map(x=>(
        <span key={x} style={{fontSize:10.5,color:C.t3,background:C.bg1,border:`1px dashed ${C.border2}`,borderRadius:R.sm,padding:'1px 7px',lineHeight:1.7}}>{x}</span>
      ))}
    </div>
  );

  const Tarifa = ({ l, v }) => (
    <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 11px',minWidth:0}}>
      <div style={{fontSize:9.5,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>{l}</div>
      <div className="radar-num" style={{fontSize:15,fontWeight:700,letterSpacing:'-.015em',color:v>0?C.t1:C.t3,marginTop:1}}>{v>0?fmt(v):'—'}</div>
    </div>
  );

  // Derivados de la seccion Economico
  const gastosAcum   = eq.total_costos_mantenimiento || totalGastos || 0;
  const ingresosAcum = eq.total_ingresos_renta || 0;
  const balance      = ingresosAcum - gastosAcum;
  const recup        = eq.costo_adquisicion > 0 ? Math.min(100, Math.round((ingresosAcum / eq.costo_adquisicion) * 100)) : null;

  const [specsOk, specsNo] = split([
    ['Tipo', labelTipo(eq)],
    ['Subtipo', titleCase(eq.subtipo)],
    ['Marca', eq.marca],
    ['Modelo', eq.modelo],
    ['Capacidad', eq.capacidad_kg ? `${eq.capacidad_kg.toLocaleString('en-US')} kg` : null, { num:true }],
    ['Combustible', titleCase(eq.combustible), { dot: combColor(titleCase(eq.combustible)) }],
    ['Año', eq.año, { num:true }],
    ['Serie', eq.serie, { num:true }],
  ]);

  const [opOk, opNo] = split([
    ['Sucursal', eq.sucursal],
    ['Ubicación', eq.ubicacion],
    ['Cliente actual', rentaActiva?.clientes?.empresa || eq.cliente_actual],
    ['Horómetro', eq.horometro ? `${Number(eq.horometro).toLocaleString('en-US')} h` : null, { num:true }],
    ['Ingreso a cliente', fecha(eq.fecha_ingreso_cliente)],
    ['Estado técnico', titleCase(eq.estado_tecnico)],
  ]);

  return (
    <div>
      {/* ── HEADER ──────────────────────────────────────────────────────────── */}
      <div style={{display:'flex',flexWrap:'wrap',alignItems:'flex-start',gap:12,marginBottom:12}}>
        <EquipIcon e={eq} size={52} icon={26}/>
        <div style={{flex:'1 1 220px',minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:9,flexWrap:'wrap'}}>
            <span className="radar-num" style={{fontSize:24,fontWeight:800,letterSpacing:'-.025em',color:C.t1,lineHeight:1.1}}>{eq.codigo}</span>
            <Badge estado={eq.estado}/>
          </div>
          <div style={{fontSize:13.5,color:C.t2,fontWeight:600,marginTop:3,overflowWrap:'anywhere'}}>
            {[eq.marca, eq.modelo].filter(Boolean).join(' ') || 'Marca y modelo sin registrar'}
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:5,marginTop:7}}>
            <Pill t={labelTipo(eq)} c={C.blue}/>
            {eq.subtipo && <Pill t={titleCase(eq.subtipo)}/>}
            {capTxt(eq.capacidad_kg) && <Pill t={capTxt(eq.capacidad_kg)}/>}
            {eq.combustible && <Pill t={titleCase(eq.combustible)} c={combColor(titleCase(eq.combustible))}/>}
            {eq.año && <Pill t={String(eq.año)}/>}
          </div>
        </div>
        <Btn size="sm" variant="secondary" onClick={()=>setEditEstado(v=>!v)} style={{marginLeft:'auto',flexShrink:0}}>
          <span style={{display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={CI.refresh}/>Cambiar estado</span>
        </Btn>
      </div>

      {/* Tira de contexto: donde esta y con quien (solo si hay dato) */}
      {(rentaActiva?.clientes?.empresa || eq.cliente_actual || eq.ubicacion) && (
        <div style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:'4px 14px',padding:'7px 12px',marginBottom:14,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:R.md,fontSize:12,color:C.t2}}>
          {(rentaActiva?.clientes?.empresa || eq.cliente_actual) && (
            <span style={{display:'inline-flex',alignItems:'center',gap:6,minWidth:0}}>
              <span style={{color:C.blue,display:'inline-flex'}}><CIcon d={CI.user} size={12}/></span>
              <b style={{fontWeight:700,color:C.t1}}>{rentaActiva?.clientes?.empresa || eq.cliente_actual}</b>
            </span>
          )}
          {eq.ubicacion && (
            <span style={{display:'inline-flex',alignItems:'center',gap:6,minWidth:0}}>
              <span style={{color:C.blue,display:'inline-flex'}}><CIcon d={CI.pin2} size={12}/></span>{eq.ubicacion}
            </span>
          )}
          {eq.fecha_ingreso_cliente && (
            <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
              <span style={{color:C.blue,display:'inline-flex'}}><CIcon d={CI.calendar} size={12}/></span>desde {fecha(eq.fecha_ingreso_cliente)}
            </span>
          )}
        </div>
      )}

      {editEstado && (
        <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:16,marginBottom:16}}>
          <Sel label="Nuevo Estado" value={nuevoEstado} onChange={setNuevoEstado} options={Object.entries(STATUS).map(([k,v])=>({v:k,l:v.label}))}/>
          <Inp label="Razón (opcional)" value={razon} onChange={setRazon} placeholder="motivo del cambio..."/>
          <div style={{display:'flex',gap:8}}><Btn onClick={cambiarEstado}>Guardar</Btn><Btn variant="secondary" onClick={()=>setEditEstado(false)}>Cancelar</Btn></div>
        </div>
      )}

      {/* Tabs — segmented control (receta 7.4) */}
      <div style={{marginBottom:16}}>
        <Seg items={tabs} value={tab} onChange={setTab}/>
      </div>

      {tab==='info' && (
        <div>
          {/* 1 · COMERCIAL */}
          <Sec icon={CI.cash} title="Comercial · tarifas">
            <div style={{display:'flex',flexWrap:'wrap',gap:10,marginTop:12}}>
              <div className="radar-kpi" style={{flex:'2 1 200px',background:C.greenTint,border:`1px solid ${C.greenBorder}`,borderRadius:R.md,padding:'11px 14px',minWidth:0}}>
                <div style={{fontSize:10,fontWeight:700,color:C.greenText,textTransform:'uppercase',letterSpacing:'.07em'}}>Renta mensual</div>
                <div className="radar-num" style={{fontSize:26,fontWeight:800,letterSpacing:'-.03em',lineHeight:1.15,marginTop:2,color:eq.precio_renta_mes>0?C.greenText:C.t3}}>
                  {eq.precio_renta_mes>0 ? fmt(eq.precio_renta_mes) : 'Sin tarifa'}
                </div>
              </div>
              {/* SEMANA, DIA Y -solo si existe- HORA.
                  Antes decia "por dia" y "por hora" sobre los campos de dia y
                  hora, pero los numeros estaban corridos una casilla: lo que
                  se mostraba como dia era la semana y lo que se mostraba como
                  hora era el dia. Ya se corrigieron los 72 equipos.
                  Una elevadora no se renta por hora, asi que esa casilla solo
                  aparece cuando de verdad hay un precio cargado. */}
              <div style={{flex:'1 1 160px',display:'grid',
                gridTemplateColumns:eq.precio_renta_hora>0?'1fr 1fr 1fr':'1fr 1fr',gap:8,minWidth:0}}>
                <Tarifa l="Por semana" v={eq.precio_renta_semana}/>
                <Tarifa l="Por día" v={eq.precio_renta_dia}/>
                {eq.precio_renta_hora>0 && <Tarifa l="Por hora" v={eq.precio_renta_hora}/>}
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,marginTop:10,padding:'8px 12px',background:C.lob.venta.bg,border:`1px solid ${C.lob.venta.border}`,borderRadius:R.md}}>
              <span style={{fontSize:10.5,fontWeight:700,color:C.lob.venta.text,textTransform:'uppercase',letterSpacing:'.07em'}}>Precio de venta</span>
              <span className="radar-num" style={{fontSize:16,fontWeight:800,letterSpacing:'-.02em',color:eq.precio_venta>0?C.lob.venta.text:C.t3}}>
                {eq.precio_venta>0 ? fmt(eq.precio_venta) : 'No cotizado'}
              </span>
            </div>
          </Sec>

          {/* 2 · OPERACIÓN */}
          <Sec icon={CI.pin2} title="Operación">
            <DL>{opOk.map(([l,v,o])=><F key={l} l={l} v={v} o={o}/>)}</DL>
            <Faltan ls={opNo}/>
          </Sec>

          {/* 3 · ESPECIFICACIONES */}
          <Sec icon={KI.box} title="Especificaciones técnicas">
            <DL>{specsOk.map(([l,v,o])=><F key={l} l={l} v={v} o={o}/>)}</DL>
            <Faltan ls={specsNo}/>
          </Sec>

          {/* 4 · ECONÓMICO */}
          <Sec icon={CI.chart} title="Económico · acumulado">
            <DL>
              <F l="Costo de adquisición" v={eq.costo_adquisicion>0?fmt(eq.costo_adquisicion):'—'} o={{num:true,color:eq.costo_adquisicion>0?C.t1:C.t3}}/>
              <F l="Total gastos" v={gastosAcum>0?fmt(gastosAcum):'—'} o={{num:true,color:gastosAcum>0?C.redText:C.t3}}/>
              <F l="Total ingresos" v={ingresosAcum>0?fmt(ingresosAcum):'—'} o={{num:true,color:ingresosAcum>0?C.greenText:C.t3}}/>
              <F l="Balance" v={(ingresosAcum||gastosAcum)?`${balance<0?'-':''}${fmt(Math.abs(balance))}`:'—'} o={{num:true,color:!(ingresosAcum||gastosAcum)?C.t3:balance>=0?C.greenText:C.redText}}/>
            </DL>
            {recup!==null && (
              <div style={{marginTop:12}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:5}}>
                  <span style={{fontSize:10,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>Recuperación de la inversión</span>
                  <span className="radar-num" style={{fontSize:12,fontWeight:700,color:recup>=100?C.greenText:C.t2}}>{recup}%</span>
                </div>
                <div style={{height:6,borderRadius:R.pill,background:C.bg3,overflow:'hidden'}} role="progressbar" aria-valuenow={recup} aria-valuemin={0} aria-valuemax={100} aria-label="Recuperación de la inversión">
                  <div style={{height:'100%',width:`${recup}%`,borderRadius:R.pill,background:recup>=100?C.green:C.blue,transition:'width .3s cubic-bezier(.4,0,.2,1)'}}/>
                </div>
              </div>
            )}
          </Sec>

          {/* 5 · NOTAS Y ALERTAS */}
          {eq.razon_detencion && (
            <div style={{display:'flex',gap:9,alignItems:'flex-start',background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.md,padding:'10px 13px',marginBottom:12}}>
              <span style={{color:C.orangeText,display:'inline-flex',marginTop:1}}><CIcon d={CI.alert} size={14}/></span>
              <div style={{minWidth:0}}>
                <div style={{fontSize:10,fontWeight:700,color:C.orangeText,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>Razón del estado actual</div>
                <div style={{fontSize:13,color:C.t2,lineHeight:1.5,overflowWrap:'anywhere'}}>{eq.razon_detencion}</div>
              </div>
            </div>
          )}
          {eq.observaciones && (
            <Sec icon={CI.edit} title="Observaciones">
              <div style={{fontSize:13,color:C.t2,lineHeight:1.55,paddingTop:10,overflowWrap:'anywhere'}}>{eq.observaciones}</div>
            </Sec>
          )}
        </div>
      )}

      {tab==='gastos'&&(
        <div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:14}}>
            <div style={{fontSize:13,color:C.t3}}>{gastos.length} registros</div>
            <div style={{fontSize:16,fontWeight:800,color:C.red}}>Total: {fmt(totalGastos)}</div>
          </div>
          {loadingG?<div style={{textAlign:'center',color:C.t4,padding:30}}>Cargando...</div>
          :gastos.length===0?<div style={{textAlign:'center',color:C.t4,padding:40,fontSize:14}}>Sin registros de gastos</div>
          :gastos.map(g=>(
            <div key={g.id} style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:8}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:C.t1}}>{g.tipo}{g.descripcion?` — ${g.descripcion}`:''}</div>
                  <div style={{fontSize:12,color:C.t4,marginTop:2}}>{fecha(g.fecha)}{g.tecnico?` · ${g.tecnico}`:''}{g.taller?` · ${g.taller}`:''}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:16,fontWeight:800,color:C.red}}>{fmt(g.total||0)}</div>
                  {(g.mano_obra||g.costo_repuestos)&&<div style={{fontSize:11,color:C.t4}}>MO {fmt(g.mano_obra||0)} · Rep {fmt(g.costo_repuestos||0)}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab==='nuevo'&&(
        <div>
          <Sel label="Tipo *" value={gasto.tipo} onChange={v=>setGasto(g=>({...g,tipo:v}))} options={['Mantenimiento preventivo','Reparación','Repuesto','Revisión','Overhaul','Otro']}/>
          <Inp label="Descripción" value={gasto.descripcion} onChange={v=>setGasto(g=>({...g,descripcion:v}))} placeholder="qué se hizo..." rows={2}/>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
            <Inp label="Mano de obra ($)" type="number" value={gasto.mano_obra} onChange={v=>setGasto(g=>({...g,mano_obra:v}))} placeholder="0"/>
            <Inp label="Repuestos ($)" type="number" value={gasto.costo_repuestos} onChange={v=>setGasto(g=>({...g,costo_repuestos:v}))} placeholder="0"/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
            <Inp label="Técnico" value={gasto.tecnico} onChange={v=>setGasto(g=>({...g,tecnico:v}))} placeholder="nombre"/>
            <Inp label="Taller" value={gasto.taller} onChange={v=>setGasto(g=>({...g,taller:v}))} placeholder="Taller propio"/>
          </div>
          {(gasto.mano_obra||gasto.costo_repuestos)&&(
            <div style={{background:C.bg3,borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:15,fontWeight:800,color:C.red}}>
              Total: {fmt((parseFloat(gasto.mano_obra)||0)+(parseFloat(gasto.costo_repuestos)||0))}
            </div>
          )}
          <Btn full onClick={agregarGasto} disabled={!gasto.tipo||savingG}>{savingG?'Guardando…':'Guardar Gasto'}</Btn>
        </div>
      )}
    </div>
  );
}

function Flota({ clientes, equipos: equiposGlobal, onEquiposChange, empresaActiva }) {
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState([]);   // multi-select (vacio = todos)
  const [filtroTipo, setFiltroTipo] = useState([]);       // multi-select
  const [filtroEmpresa, setFiltroEmpresa] = useState([]); // multi-select por empresa
  const [busq, setBusq] = useState('');
  const [vista, setVista] = useState('kanban');
  const [detalle, setDetalle] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dropCol, setDropCol] = useState(null);
  const [modalRenta, setModalRenta] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const [rentasActivas, setRentasActivas] = useState([]);

  const cargar = async () => {
    // Flota NO se ata al HN/CR de arriba: carga TODAS las empresas y se filtra con
    // el multi-select de Empresa (MT Rental/Monhaco/Monhagro/CR) aca abajo.
    const qE = supabase.from('equipos').select('*').order('codigo');
    const qR = supabase.from('rentas').select('equipo_id,valor_mensual').eq('estado','activa');
    const [{data:eq},{data:rentas}] = await Promise.all([qE, qR]);
    setEquipos(eq||[]);
    setRentasActivas(rentas||[]);
    setLoading(false);
  };
  useEffect(()=>{ cargar(); },[]); // eslint-disable-line

  const sincronizar = async () => {
    setSyncing(true);
    try {
      // Con timeout: antes era un fetch pelado y si la funcion tardaba, el boton
      // se quedaba en "Actualizando..." sin fin y sin decir nada.
      // El endpoint ya no es publico: hay que mandarle la sesion (ver api/sync-flota.js).
      const { data:{ session } } = await supabase.auth.getSession();
      const r = await fetch('/api/sync-flota', {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
        signal: AbortSignal.timeout(75000) });
      // Si Vercel corta la funcion por tiempo, la respuesta NO es JSON y el
      // r.json() explotaba con un error de parseo que no explicaba nada.
      const txt = await r.text();
      let d; try { d = JSON.parse(txt); }
      catch {
        toast(r.status === 504
          ? 'La actualizacion se paso del tiempo limite. Volvé a intentar en un momento.'
          : `El servidor respondió algo raro (HTTP ${r.status}).`);
        setSyncing(false); return;
      }
      if (d.ok) { await cargar();
        const fu = d.fuentes || {};
        const tot = Object.values(fu).reduce((s,x)=>s+((x?.actualizados||0)+(x?.nuevos||0)),0);
        toast(`✓ Flota actualizada\nMT Rental ${fu.montasa?.equipos||0} · Monhaco ${fu.monhaco?.equipos||0} · Monhagro ${fu.monhagro?.equipos||0}\n${tot} cambios`); }
      else toast('No se pudo sincronizar: ' + (d.error||'error'));
    } catch (e) {
      toast(e.name === 'TimeoutError'
        ? 'Se pasó de un minuto y lo corté. La flota puede haberse actualizado a medias: volvé a intentar.'
        : 'Error: ' + e.message);
    }
    setSyncing(false);
  };

  // Se dice 'mtrental' y no 'honduras': esa palabra ya significa Monhaco del
  // lado de las facturas de SAP (ver sql/renombrar-mtrental.sql).
  const EMP_LBL = { mtrental:'MT Rental', monhaco:'Monhaco', monhagro:'Monhagro', costarica:'Costa Rica' };
  // Tipos canonicos: canonTipo fusiona plataforma aerea==elevadora, agrupa torres de
  // luz, y excluye Taller / Equipos de cliente (esos NO son tipo de maquina).
  const tipoMap = {};
  equipos.forEach(e=>{ const c=canonTipo(e); if(c && !tipoMap[c.canon]) tipoMap[c.canon]=c.label; });
  const tipos = Object.entries(tipoMap).sort((a,b)=>a[1].localeCompare(b[1])).map(([value,label])=>({ value, label }));
  const estados = Object.entries(STATUS).map(([k,v])=>({ value:k, label:v.label }));
  // Empresa: las 3 de Honduras siempre; CR si hay; y "Equipos de cliente" como una
  // categoria mas (value __cliente) que filtra por esEquipoCliente, no por empresa_id.
  // En equipos, 'honduras' ES MT Rental (viene del nodo 'montasa' de la app de
  // campo). Se normaliza aca para que el filtro funcione tanto antes como
  // despues de correr sql/renombrar-mtrental.sql.
  const empDe = e => e?.empresa_id === 'honduras' ? 'mtrental' : e?.empresa_id;
  const empIds = new Set(equipos.map(empDe).filter(Boolean));
  const hayCliente = equipos.some(esEquipoCliente);
  const empresas = [
    ...['mtrental','monhaco','monhagro','costarica'].filter(id=>empIds.has(id))
        .map(id=>({ value:id, label:EMP_LBL[id] || titleCase(id) })),
    ...(hayCliente ? [{ value:'__cliente', label:'Equipos de cliente' }] : []),
  ];
  const filtrados = equipos.filter(e=>{
    if (filtroEstado.length && !filtroEstado.includes(e.estado)) return false;
    if (filtroTipo.length) { const c=canonTipo(e); if(!c || !filtroTipo.includes(c.canon)) return false; }
    // Sin filtro de tipo, el reporte deja fuera lo que no se renta. Con filtro,
    // manda lo que se pidio: si se escoge Vehiculo, se ven los vehiculos.
    else if (!esRentable(e)) return false;
    if (filtroEmpresa.length) {
      const cli = esEquipoCliente(e);
      const wantsCli = filtroEmpresa.includes('__cliente');
      // Una empresa real NO incluye equipos de cliente; "Equipos de cliente" muestra
      // solo esos, sin importar su empresa_id.
      const matchEmp = !cli && filtroEmpresa.some(v=>v!=='__cliente' && v===empDe(e));
      if (!(matchEmp || (wantsCli && cli))) return false;
    }
    if (busq) { const b=busq.toLowerCase(); return codigoDe(e).toLowerCase().includes(b)||(e.serie||'').toLowerCase().includes(b)||(e.marca||'').toLowerCase().includes(b)||(e.modelo||'').toLowerCase().includes(b); }
    return true;
  });

  // DS v3: los colores de columna salen de STATUS (mismo lenguaje del CRM).
  const COLS = [
    {id:'disponible',    label:'Disponible',    color:STATUS.disponible.color},
    {id:'en_renta',      label:'En Renta',      color:STATUS.en_renta.color},
    {id:'mantenimiento', label:'Mantenimiento', color:STATUS.mantenimiento.color},
    {id:'reparacion',    label:'Por Reparar',   color:STATUS.reparacion.color},
    {id:'detenido',      label:'Detenido',      color:STATUS.detenido.color},
    {id:'inactivo',      label:'Inactivo',      color:STATUS.inactivo.color},
    {id:'vendido',       label:'Vendido',       color:STATUS.vendido.color},
  ];

  // Renta mensual que dejaria cada equipo si estuviera rentado: su precio de renta,
  // o el promedio de la flota cuando no tiene precio cargado.
  const avgRent = rentasActivas.length ? rentasActivas.reduce((s,r)=>s+(r.valor_mensual||0),0)/rentasActivas.length : 0;
  const rentaDe = e => Number(e.precio_renta_mes)||Number(e.valor_mensual)||avgRent;
  // Un solo contrato para TODOS los headers: siempre devuelve {label,val,tone} —
  // nunca null, para que las 7 columnas midan exactamente igual.
  const rentaMap = {};
  rentasActivas.forEach(r=>{ rentaMap[r.equipo_id] = (rentaMap[r.equipo_id]||0) + (r.valor_mensual||0); });
  const colVal = (col, eqs) => {
    if (col.id === 'vendido') return { label:'fuera de flota', val:'—', tone:'muted' };
    if (col.id === 'en_renta') {
      // Suma sobre eqs (no sobre rentasActivas) para que respete los filtros de arriba.
      const total = eqs.reduce((s,e)=>s+(rentaMap[e.id]||rentaDe(e)),0);
      return { label:'ingreso/mes', val:fmt(Math.round(total)), tone: total>0?'good':'muted' };
    }
    // Toda fase donde el equipo NO se renta = plata parada. Disponible es "potencial"
    // (se podria rentar); el resto es "perdido" (no se puede rentar).
    const pot = eqs.reduce((s,e)=>s+rentaDe(e),0);
    if (col.id === 'disponible') return { label:'potencial/mes', val:fmt(Math.round(pot)), tone: pot>0?'warn':'muted' };
    return { label:'perdido/mes', val:fmt(Math.round(pot)), tone: pot>0?'bad':'muted' };
  };
  const TONE = { good:C.greenText, bad:C.redText, warn:C.orangeText, muted:C.t3 };

  const handleDrop = async (nuevoEstado) => {
    if (!dragId) return;
    const eq = equipos.find(e=>e.id===dragId);
    if (!eq||eq.estado===nuevoEstado) { setDragId(null); setDropCol(null); return; }
    if (nuevoEstado==='en_renta') { setModalRenta(eq); setDragId(null); setDropCol(null); return; }
    await supabase.from('equipos').update({estado:nuevoEstado}).eq('id',dragId);
    setEquipos(es=>es.map(e=>e.id===dragId?{...e,estado:nuevoEstado}:e));
    setDragId(null); setDropCol(null);
  };

  const updateEquipo = (upd) => { setEquipos(es=>es.map(e=>e.id===upd.id?upd:e)); if(detalle?.id===upd.id) setDetalle(upd); };

  // Etiquetas cortas: el tipo nunca debe desbordar los ~174px utiles de la tarjeta.
  const TIPO_CORTO = {'Plataforma Aérea':'Plataforma','Torre de Luz':'Torre Luz','Electric Tugger':'Tugger','Equipos de Cliente':'De Cliente','Grúa':'Grúa'};
  const corto = (s,n=11) => { const t=String(s||''); return t.length>n ? t.slice(0,n-1)+'…' : t; };

  // Mini-ficha de flota — CONJUNTO FIJO de campos: SIEMPRE las mismas filas y la
  // misma altura (136px). Lo que falta degrada a "—"; ninguna fila se omite nunca.
  // Jerarquia: codigo > marca/modelo > serie > atributos > ubicacion/cliente > economico.
  // El "año" se saco a proposito: no cambia ninguna decision y venia inconsistente.
  const Card = ({eq}) => {
    const s = STATUS[eq.estado]||STATUS.desconocido;
    const marcaMod = [eq.marca,eq.modelo].filter(Boolean).join(' ');
    const tipoTxt  = eq.subtipo ? corto(titleCase(eq.subtipo)) : corto(TIPO_CORTO[labelTipo(eq)] || labelTipo(eq));
    const enRenta  = eq.estado==='en_renta';
    const ctxTxt   = (enRenta ? eq.cliente_actual : eq.ubicacion) || '—';
    return (
      <div className="radar-card" draggable onDragStart={()=>setDragId(eq.id)} onDragEnd={()=>{setDragId(null);setDropCol(null);}}
        onClick={()=>setDetalle(eq)} title={`${codigoDe(eq)||''}${marcaMod?' — '+marcaMod:''}`}
        style={{background:C.bg2,border:`1px solid ${C.border}`,borderLeft:`3px solid ${s.color}`,borderRadius:10,padding:'11px 12px 10px',cursor:dragId===eq.id?'grabbing':'grab',userSelect:'none',opacity:dragId===eq.id?.4:1,boxShadow:SH.xs,marginBottom:8,boxSizing:'border-box'}}>

        {/* 1+2 — identificador principal + marca/modelo. Alto fijo 30 = alto del icono. */}
        <div style={{display:'flex',alignItems:'center',gap:8,height:30}}>
          <EquipIcon e={eq} size={30} icon={15}/>
          <div style={{minWidth:0,flex:1}}>
            <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1,height:16,lineHeight:'16px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{codigoDe(eq)||'—'}</div>
            <div style={{fontSize:11,color:C.t3,height:14,lineHeight:'14px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{marcaMod||'—'}</div>
          </div>
        </div>

        {/* 3 — serie (trazabilidad). Siempre presente. */}
        <div style={{fontSize:9,color:C.t4,fontFamily:'monospace',height:12,lineHeight:'12px',marginTop:5,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>S/N {eq.serie||'—'}</div>

        {/* 4 — atributos: SIEMPRE 3 pills (tipo · capacidad · combustible). nowrap+overflow = altura blindada. */}
        <div style={{display:'flex',alignItems:'center',flexWrap:'nowrap',gap:4,height:18,marginTop:5,overflow:'hidden'}}>
          <Pill t={tipoTxt||'—'} c={C.indigo}/>
          <Pill t={capTxt(eq.capacidad_kg)||'—'} c={C.t3}/>
          <Pill t={eq.combustible?titleCase(eq.combustible):'—'} c={eq.combustible?combColor(eq.combustible):C.t4}/>
        </div>

        {/* 5 — donde esta / con quien. En Renta -> cliente; el resto -> ubicacion. */}
        <div style={{display:'flex',alignItems:'center',gap:5,height:14,marginTop:6,fontSize:10.5,color:C.t3}}>
          <CIcon d={enRenta?CI.user:CI.pin} size={11} style={{color:C.t4}}/>
          <span style={{flex:1,minWidth:0,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{ctxTxt}</span>
        </div>

        {/* 6 — economico: renta/mes + venta, siempre una linea. */}
        <div className="radar-num" style={{height:17,lineHeight:'17px',marginTop:6,fontSize:12.5,fontWeight:700,letterSpacing:'-0.015em',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',color:eq.precio_renta_mes>0?C.greenText:C.t4}}>
          {eq.precio_renta_mes>0
            ? <>{fmt(eq.precio_renta_mes)}<span style={{fontWeight:500,color:C.t4}}>/mes</span></>
            : <span style={{fontWeight:600}}>Sin renta</span>}
          <span style={{fontWeight:500,color:C.t4}}>{eq.precio_venta>0?` · venta ${fmt(eq.precio_venta)}`:' · venta —'}</span>
        </div>
      </div>
    );
  };

  if (loading) return <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:C.t4}}>Cargando flota...</div>;

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* Toolbar — flota sobre gris, sin franja blanca (receta 7.4/7.6) */}
      <div style={{padding:'12px 16px',display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <div style={{position:'relative'}}>
          <span style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:C.t4,pointerEvents:'none',display:'inline-flex'}}><CIcon d={CI.search} size={14}/></span>
          <input className="radar-input" value={busq} onChange={e=>setBusq(e.target.value)} placeholder="Buscar equipo…"
            style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'0 12px 0 32px',height:34,color:C.t1,fontSize:13,width:200,boxShadow:SH.xs}}/>
        </div>
        <MultiSelect label="Estados" value={filtroEstado} onChange={setFiltroEstado} options={estados} width={190}/>
        <MultiSelect label="Tipos" value={filtroTipo} onChange={setFiltroTipo} options={tipos} width={220}/>
        {empresas.length>1 && <MultiSelect label="Empresa" value={filtroEmpresa} onChange={setFiltroEmpresa} options={empresas} width={180}/>}
        <span style={{fontSize:12,color:C.t3,fontWeight:500}} className="radar-num">{filtrados.length} equipos</span>
        <div style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center'}}>
          <button className="radar-pill" onClick={sincronizar} disabled={syncing} title="Trae los cambios de la app de Miguel (conserva precios)"
            style={{display:'inline-flex',alignItems:'center',gap:6,background:syncing?C.bg3:C.greenTint,color:syncing?C.t4:C.greenText,border:`1px solid ${syncing?C.border:C.greenBorder}`,borderRadius:R.md,padding:'0 13px',height:34,fontSize:12.5,fontWeight:600,cursor:syncing?'default':'pointer'}}>
            <CIcon d={CI.refresh}/>{syncing?'Actualizando…':'Actualizar flota'}
          </button>
          <Seg items={[['kanban','Kanban',CI.chart],['lista','Lista',CI.inbox]]} value={vista} onChange={setVista}/>
        </div>
      </div>

      {/* KANBAN */}
      {vista==='kanban'&&(
        <div style={{flex:1,overflowX:'auto',overflowY:'hidden',display:'flex'}}>
          {COLS.map(col=>{
            const eqs = filtrados.filter(e=>e.estado===col.id);
            const isTarget = dropCol===col.id && dragId;
            return (
              <div key={col.id}
                onDragOver={e=>{e.preventDefault();setDropCol(col.id);}}
                onDragLeave={()=>setDropCol(null)}
                onDrop={()=>handleDrop(col.id)}
                style={{flex:'0 0 226px',borderRight:`1px solid ${C.border}`,display:'flex',flexDirection:'column',background:isTarget?C.primaryTint:'transparent',transition:'background .15s'}}>
                <div style={{padding:'10px 12px 8px',flexShrink:0}}>
                  {/* Header tarjeta blanca con dot+halo (receta 7.3) */}
                  <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',boxShadow:SH.sm}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,minHeight:20}}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:8,minWidth:0}}>
                        <span style={{width:8,height:8,borderRadius:R.pill,background:col.color,boxShadow:`0 0 0 3px ${col.color}24`,flexShrink:0}}/>
                        <span style={{fontSize:13,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{col.label}</span>
                      </span>
                      <span className="radar-num" style={{background:C.bg3,color:C.t3,borderRadius:R.pill,padding:'2px 8px',fontSize:11,fontWeight:700,lineHeight:'14px',flexShrink:0}}>{eqs.length}</span>
                    </div>
                    {/* Fila metrica: SIEMPRE se renderiza (aunque sea $0 o "—") -> todos los headers miden igual. */}
                    {(()=>{ const cv=colVal(col,eqs); return (
                      <div title={`${col.label}: ${cv.label} ${cv.val}`}
                        style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',gap:8,marginTop:7,paddingTop:6,borderTop:`1px solid ${C.border}`,minHeight:17}}>
                        <span style={{fontSize:10,fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{cv.label}</span>
                        <span className="radar-num" style={{fontSize:12.5,fontWeight:700,letterSpacing:'-0.01em',color:TONE[cv.tone]||C.t3,whiteSpace:'nowrap',flexShrink:0}}>{cv.val}</span>
                      </div>
                    ); })()}
                  </div>
                </div>
                <div style={{flex:1,overflowY:'auto',padding:'0 12px 12px'}}>
                  {isTarget&&<div style={{border:`2px dashed ${C.primaryBorder}`,borderRadius:10,padding:16,textAlign:'center',fontSize:12,color:C.blue,fontWeight:600,marginBottom:8}}>Soltar aquí</div>}
                  {eqs.map(eq=><Card key={eq.id} eq={eq}/>)}
                  {eqs.length===0&&!isTarget&&<div style={{textAlign:'center',color:C.t4,fontSize:12,marginTop:16,padding:'0 8px'}}>Arrastrá equipos aquí</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* LISTA */}
      {vista==='lista'&&(
        <div style={{flex:1,overflowY:'auto',padding:20}}>
          <div style={{display:'grid',gap:6}}>
            {filtrados.map(eq=>(
              <div key={eq.id} className="radar-card" onClick={()=>setDetalle(eq)}
                style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'11px 16px',cursor:'pointer',boxShadow:SH.xs,display:'grid',gridTemplateColumns:'36px 2.2fr 1.4fr 0.7fr 1fr 130px',alignItems:'center',gap:12}}>
                <EquipIcon e={eq} size={34} icon={17}/>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>{codigoDe(eq)} <span style={{fontWeight:500,color:C.t3,fontSize:12}}>{[eq.marca,eq.modelo].filter(Boolean).join(' ')}</span></div>
                  {eq.serie&&<div style={{fontSize:10,color:C.t4,fontFamily:'monospace'}}>S/N {eq.serie}</div>}
                </div>
                {/* Mismo set fijo de 3 pills que la tarjeta Kanban (tipo · capacidad · combustible). */}
                <div style={{display:'flex',alignItems:'center',flexWrap:'nowrap',gap:4,overflow:'hidden'}}>
                  <Pill t={eq.subtipo?titleCase(eq.subtipo):(labelTipo(eq)||'—')} c={C.indigo}/>
                  <Pill t={capTxt(eq.capacidad_kg)||'—'} c={C.t3}/>
                  <Pill t={eq.combustible?titleCase(eq.combustible):'—'} c={eq.combustible?combColor(eq.combustible):C.t4}/>
                </div>
                <div style={{fontSize:12,color:C.t3}}>{eq.ubicacion||'—'}</div>
                <div className="radar-num" style={{fontSize:13,fontWeight:700,letterSpacing:'-0.015em',color:C.greenText,textAlign:'right'}}>
                  {eq.precio_renta_mes>0?<>{fmt(eq.precio_renta_mes)}<span style={{color:C.t4,fontWeight:500}}>/mes</span></>:<span style={{color:C.t4,fontWeight:500}}>sin precio</span>}
                  {eq.precio_venta>0&&<div style={{fontSize:10,color:C.t4,fontWeight:600}}>venta {fmt(eq.precio_venta)}</div>}
                </div>
                <Badge estado={eq.estado}/>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal open={!!detalle} onClose={()=>setDetalle(null)} title="Ficha del equipo" width={680}>
        {detalle&&<DetalleEquipo eq={detalle} clientes={clientes} onClose={()=>setDetalle(null)} onUpdate={updateEquipo}/>}
      </Modal>
      <Modal open={!!modalRenta} onClose={()=>setModalRenta(null)} title="Nueva Renta" width={520}>
        {modalRenta&&<FormRenta equipoFijo={modalRenta} clientes={clientes} equipos={equipos} empresaActiva={empresaActiva} onSave={eqId=>{setEquipos(es=>es.map(e=>e.id===eqId?{...e,estado:'en_renta'}:e));setModalRenta(null);}} onClose={()=>setModalRenta(null)}/>}
      </Modal>
    </div>
  );
}

// ─── RENTAS ───────────────────────────────────────────────────────────────────
// ── FLOTA DE CLIENTES ────────────────────────────────────────────────────────
// Los montacargas que son del CLIENTE y a los que Montasa les vende
// mantenimiento. Es la base del negocio de mantenimientos: sin saber que tiene
// cada cliente y en que horometro va, no hay a quien venderle ni cuando.
//
// Vive aparte de `equipos` (la flota propia) a proposito: ver sql/equipos_cliente.sql.

// Los ciclos se anidan: cada 250 horas cae algo, y cada cuarto/octavo hito cae
// uno mas grande. Un equipo en 2000 horas lleva el de 2000, no el de 250.
const tipoMantPorHoras = (horas) => {
  const n = Math.floor((Number(horas) || 0) / 250);
  if (n <= 0) return null;
  if (n % 8 === 0) return 2000;
  if (n % 4 === 0) return 1000;
  if (n % 2 === 0) return 500;
  return 250;
};

// El ciclo MAS GRANDE que el equipo ya paso segun su horometro. A las 1,832
// horas paso el hito de 1,000 (cuarto hito de 250), asi que le tocaba el
// mantenimiento de 1,000 horas aunque su ultimo servicio haya sido un 250.
const cicloAlcanzado = (horas) => {
  const n = Math.floor((Number(horas) || 0) / 250);
  if (n >= 8) return 2000;
  if (n >= 4) return 1000;
  if (n >= 2) return 500;
  if (n >= 1) return 250;
  return null;
};

// Que le toca, cuando, y por que. Reglas, en este orden:
//
//   1. Si alguien puso el proximo a mano (el tecnico en su reporte o el
//      vendedor en RADAR), eso manda. Nadie le discute al que vio la maquina.
//   2. Se revisa si se SALTARON ciclos. Un equipo con 1,832 horas cuyo ultimo
//      servicio fue de 250 nunca recibio el de 1,000: eso no es "le toca el de
//      2,000 en 24 dias", es que arrastra uno vencido. Antes se ignoraba y por
//      eso salia un numero tranquilizador que no era cierto.
//   3. El mantenimiento cae cada 250 horas O cada 3 meses, lo que llegue
//      primero. Hay clientes que piden por horas y no las hacen en 3 meses;
//      esos igual tienen que entrar a revision.
//   4. Sin horometro confiable, todo se va por calendario.
const proximoMant = (eq) => {
  const h = Number(eq.horometro_horas);
  const ultH = Number(eq.ultimo_mant_horometro);
  const ultTipo = Number(eq.ultimo_mant_tipo) || 0;
  const funciona = eq.horometro_funciona !== false;
  const hoy = new Date();
  const masDias = (n) => { const f = new Date(); f.setDate(f.getDate() + n); return f.toISOString().slice(0,10); };

  // Cada cuanto toca por calendario: 3 meses desde el ultimo servicio.
  const fechaCalendario = eq.ultimo_mant_fecha
    ? (() => { const f = new Date(eq.ultimo_mant_fecha); f.setDate(f.getDate() + 90); return f.toISOString().slice(0,10); })()
    : null;
  const vencidoPorTiempo = fechaCalendario && new Date(fechaCalendario) <= hoy;

  // 1. Lo que dijo una persona gana sobre cualquier calculo.
  const manual = Number(eq.proximo_mant_tipo);
  if (manual && eq.proximo_mant_metodo === 'manual') {
    return { tipo: manual, metodo: 'manual', fecha: eq.proximo_mant_fecha_est || fechaCalendario,
             faltan: null, hito: eq.proximo_mant_horometro || null,
             nota: 'Lo definio quien atendio el equipo', vencidoPorTiempo };
  }

  // 4. Sin horometro: solo calendario.
  if (!funciona || !h) {
    if (!eq.ultimo_mant_fecha) return { tipo: 250, metodo: 'sin datos', fecha: null, faltan: null,
      nota: 'No hay horometro ni fecha del ultimo servicio' };
    return { tipo: 250, metodo: 'calendario', fecha: fechaCalendario, faltan: null,
             nota: 'El horometro no sirve, se programa por fecha', vencidoPorTiempo };
  }

  // 2. Ciclos saltados: si el equipo ya paso un ciclo mas grande del que
  //    recibio, ese es el que debe, y esta vencido desde hace rato.
  const debido = cicloAlcanzado(h);
  if (debido && ultTipo && ultTipo < debido) {
    return { tipo: debido, metodo: 'atrasado', fecha: null, faltan: 0,
             hito: Math.floor(h / debido) * debido,
             nota: `Ya paso las ${debido} horas y su ultimo servicio fue de ${ultTipo}. Se salto el de ${debido}.`,
             atrasado: true, vencidoPorTiempo };
  }

  // 3. Lo normal: el siguiente hito de 250 despues del ultimo servicio.
  const desde = Math.round(Number.isFinite(ultH) && ultH > 0 ? ultH : h);
  const hito = (Math.floor(desde / 250) + 1) * 250;
  const faltan = Math.max(0, hito - Math.round(h));

  const porDia = (Number(eq.horas_dia) || 8) * ((Number(eq.dias_semana) || 6) / 7);
  const dias = porDia > 0 ? Math.round(faltan / porDia) : null;
  const fechaHoras = dias !== null ? masDias(dias) : null;

  // Lo que llegue primero, horas o los 3 meses.
  const fecha = (fechaHoras && fechaCalendario)
    ? (fechaHoras < fechaCalendario ? fechaHoras : fechaCalendario)
    : (fechaHoras || fechaCalendario);
  const mandaTiempo = fecha && fechaCalendario && fecha === fechaCalendario && fechaHoras && fechaCalendario < fechaHoras;

  return {
    tipo: tipoMantPorHoras(hito) || 250,
    metodo: mandaTiempo ? 'calendario' : 'horometro',
    fecha, faltan: Math.round(faltan), hito, vencidoPorTiempo,
    nota: mandaTiempo
      ? `Le tocan 3 meses del ultimo servicio antes de llegar a las ${hito} horas`
      : `Faltan ${Math.round(faltan)} horas para las ${hito}, a ${porDia.toFixed(1)} horas por dia`,
    porDia,
  };
};

const URGENCIA = (p) => {
  // Un ciclo saltado no tiene fecha futura: ya se paso. Y los 3 meses vencidos
  // pesan igual aunque al horometro le falten horas.
  if (p.atrasado) return { txt: 'ATRASADO', color: C.redText, bg: C.redTint, dias: -1 };
  if (p.vencidoPorTiempo) return { txt: 'VENCIDO 3 MESES', color: C.redText, bg: C.redTint, dias: 0 };
  if (!p.fecha) return { txt: 'sin fecha', color: C.t4, bg: C.bg3 };
  const d = Math.round((new Date(p.fecha) - new Date()) / 86400000);
  if (d <= 0)  return { txt: 'VENCIDO', color: C.redText, bg: C.redTint, dias: d };
  if (d <= 15) return { txt: `en ${d}d`, color: C.orangeText, bg: C.orangeTint, dias: d };
  if (d <= 45) return { txt: `en ${d}d`, color: C.infoText, bg: C.azulF || C.bg3, dias: d };
  return { txt: `en ${d}d`, color: C.t3, bg: C.bg3, dias: d };
};

function FlotaCliente({ usuario, clientes = [], empresaActiva }) {
  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [busq, setBusq]     = useState('');
  const [fCliente, setFCli] = useState('');
  const [fUrg, setFUrg]     = useState('todos');
  const [sel, setSel]       = useState(null);
  const [alta, setAlta]     = useState(false);
  const [tab, setTab]       = useState('equipos');
  const [nPend, setNPend]   = useState(0);

  const contarPendientes = async () => {
    const { count } = await supabase.from('mantenimientos')
      .select('id', { count:'exact', head:true }).eq('bandeja_estado','pendiente');
    setNPend(count || 0);
  };
  useEffect(() => { contarPendientes(); }, []); // eslint-disable-line

  const cargar = async () => {
    setLoad(true);
    const { data } = await supabase.from('equipos_cliente')
      .select('*').order('cliente_nombre').limit(3000);
    setItems(data || []);
    setLoad(false);
  };
  useEffect(() => { cargar(); }, []); // eslint-disable-line

  const conMant = items.map(e => ({ ...e, _p: proximoMant(e) }));
  const filtrados = conMant.filter(e => {
    if (fCliente && e.cliente_nombre !== fCliente) return false;
    if (fUrg !== 'todos') {
      const u = URGENCIA(e._p);
      if (fUrg === 'vencidos' && u.txt !== 'VENCIDO') return false;
      if (fUrg === 'mes' && !(u.dias != null && u.dias > 0 && u.dias <= 30)) return false;
    }
    if (!busq) return true;
    const t = busq.toLowerCase();
    return [e.cliente_nombre, e.marca, e.modelo, e.serie, e.codigo_interno, e.sitio, e.ciudad]
      .some(x => String(x || '').toLowerCase().includes(t));
  });

  const clientesUnicos = [...new Set(items.map(e => e.cliente_nombre).filter(Boolean))].sort();
  const vencidos = conMant.filter(e => URGENCIA(e._p).txt === 'VENCIDO').length;
  const esteMes  = conMant.filter(e => { const u = URGENCIA(e._p); return u.dias != null && u.dias > 0 && u.dias <= 30; }).length;

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', minHeight:0 }}>
      <div style={{ padding:'18px 22px 0' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:4, flexWrap:'wrap' }}>
          <h1 style={{ margin:0, fontSize:19, fontWeight:700, letterSpacing:'-0.015em', color:C.t1 }}>Flota de clientes</h1>
          <button onClick={()=>setAlta(true)} style={{ marginLeft:'auto', ...BTN_PRIMARY }}>+ Agregar equipo</button>
        </div>
        <p style={{ margin:'0 0 14px', fontSize:12.5, color:C.t3 }}>
          Los equipos propios de cada cliente y cuando les toca mantenimiento
        </p>

        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
          <Tarjeta n={items.length} t="equipos mapeados" />
          <Tarjeta n={clientesUnicos.length} t="clientes" />
          <Tarjeta n={vencidos} t="con mantenimiento vencido" tono="rojo" />
          <Tarjeta n={esteMes} t="les toca este mes" tono="naranja" />
        </div>

        <div style={{ display:'flex', gap:6, marginBottom:14, borderBottom:`1px solid ${C.border}` }}>
          {[['equipos','Equipos'],['bandeja',`Pendientes de asignar${nPend?` (${nPend})`:''}`],['insumos','Insumos']].map(([v,l])=>(
            <button key={v} onClick={()=>setTab(v)} style={{
              fontFamily:'inherit', fontSize:12.5, fontWeight:700, padding:'8px 14px', cursor:'pointer',
              background:'none', border:'none', borderBottom:`2px solid ${tab===v?C.primary:'transparent'}`,
              color: tab===v ? C.primary : (v==='bandeja'&&nPend ? C.orangeText : C.t3) }}>{l}</button>
          ))}
        </div>

        {tab==='equipos' && <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
          <input value={busq} onChange={e=>setBusq(e.target.value)} placeholder="Buscar equipo, serie, cliente, sitio…"
            style={{ ...INP_ST, flex:'1 1 240px', cursor:'text' }} />
          <select value={fCliente} onChange={e=>setFCli(e.target.value)} style={{ ...INP_ST, width:'auto', minWidth:170 }}>
            <option value="">Todos los clientes</option>
            {clientesUnicos.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select value={fUrg} onChange={e=>setFUrg(e.target.value)} style={{ ...INP_ST, width:'auto' }}>
            <option value="todos">Todos</option>
            <option value="vencidos">Solo vencidos</option>
            <option value="mes">Les toca este mes</option>
          </select>
        </div>}
      </div>

      <div style={{ flex:1, minHeight:0, overflowY:'auto', padding:'0 22px 22px' }}>
        {tab==='insumos' ? (
          <HistorialInsumos clientes={clientes} />
        ) : tab==='bandeja' ? (
          <BandejaMantenimientos usuario={usuario} clientes={clientes}
            onAsignado={()=>{ cargar(); contarPendientes(); }} />
        ) : loading ? <p style={{ color:C.t3, fontSize:13 }}>Cargando…</p>
         : !filtrados.length ? (
          <div style={{ textAlign:'center', padding:'50px 20px', color:C.t3 }}>
            <p style={{ fontSize:14, margin:'0 0 6px' }}>
              {items.length ? 'Sin resultados para esos filtros.' : 'Todavia no hay ningun equipo de cliente mapeado.'}
            </p>
            {!items.length && <p style={{ fontSize:12.5, margin:0 }}>
              Comenzar por los clientes con equipo vendido, que son los que ya tienen flota identificada.
            </p>}
          </div>
        ) : (
          <div style={{ display:'grid', gap:8 }}>
            {/* Sin encabezados nadie sabia que era ese "2000 h" suelto. */}
            <div style={{ display:'flex', gap:14, alignItems:'center', padding:'0 14px',
              fontSize:10.5, fontWeight:800, letterSpacing:'.06em', textTransform:'uppercase', color:C.t4 }}>
              <div style={{ flex:'1 1 220px', minWidth:0 }}>Equipo y cliente</div>
              <div style={{ minWidth:96 }}>Horometro</div>
              <div style={{ minWidth:74 }}>Le toca</div>
              <div style={{ minWidth:96 }}>Cuando</div>
            </div>
            {filtrados.map(e => {
              const u = URGENCIA(e._p);
              return (
                <div key={e.id} onClick={()=>setSel(e)} style={{
                  background:C.bg2, border:`1px solid ${C.border}`, borderRadius:R.lg, padding:'12px 14px',
                  cursor:'pointer', display:'flex', gap:14, alignItems:'center', flexWrap:'wrap' }}>
                  <div style={{ flex:'1 1 220px', minWidth:0 }}>
                    <div style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>
                      {[e.marca, e.modelo].filter(Boolean).join(' ') || e.codigo_interno || 'Sin identificar'}
                    </div>
                    <div style={{ fontSize:12, color:C.t3, marginTop:2 }}>
                      {e.cliente_nombre}{e.sitio ? ` · ${e.sitio}` : ''}{e.serie ? ` · serie ${e.serie}` : ''}
                    </div>
                  </div>
                  <div style={{ fontSize:12, color:C.t2, minWidth:96 }}>
                    {e.horometro_horas ? `${Math.round(Number(e.horometro_horas)).toLocaleString()} h` : 'sin horometro'}
                  </div>
                  <div style={{ minWidth:74 }}>
                    <div style={{ fontSize:12.5, fontWeight:700, color:C.t1 }}>
                      {e._p.tipo ? `Mant. ${e._p.tipo} h` : '—'}
                    </div>
                    {e._p.metodo && e._p.metodo !== 'horometro' && (
                      <div style={{ fontSize:10.5, color:C.t4, marginTop:1 }}>por {e._p.metodo}</div>
                    )}
                  </div>
                  <div style={{ minWidth:96 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:u.color, background:u.bg,
                      border:`1px solid ${u.color}33`, borderRadius:R.pill, padding:'3px 10px', whiteSpace:'nowrap' }}>
                      {u.txt}
                    </span>
                    {e._p.faltan != null && !e._p.atrasado && (
                      <div style={{ fontSize:10.5, color:C.t4, marginTop:3 }}>faltan {e._p.faltan} h</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AltaEquipoCliente open={alta} onClose={()=>setAlta(false)} clientes={clientes}
        usuario={usuario} empresaActiva={empresaActiva} onGuardado={()=>{ setAlta(false); cargar(); }} />
      <FichaEquipoCliente usuario={usuario} eq={sel} onClose={()=>setSel(null)} onCambio={cargar} />
    </div>
  );
}

// ── BANDEJA: reportes de servicio sin asignar ────────────────────────────────
// Los tecnicos llenan el reporte en campo y el nombre del cliente casi nunca
// coincide con como esta en RADAR. En vez de pelear con eso, el reporte llega
// crudo y Kevin hace el empate contra la maquina que ya esta registrada.
//
// Al asignar pasan tres cosas de un golpe: el mantenimiento queda amarrado al
// equipo, el horometro del equipo se actualiza con la lectura del reporte, y se
// recalcula cuando le toca el proximo.
function BandejaMantenimientos({ usuario, clientes, onAsignado }) {
  const [pend, setPend]   = useState([]);
  const [loading, setL]   = useState(true);
  const [sel, setSel]     = useState(null);
  const [nuevo, setNuevo] = useState(false);
  const [trayendo, setT]  = useState(false);

  // Los mantenimientos de cliente ya los registra Miguel en su app; esto solo
  // los baja. Corre solo todos los dias, el boton es para no esperar.
  const traer = async () => {
    setT(true);
    try {
      // Mismo caso que el sync de flota: ahora pide sesion.
      const { data:{ session } } = await supabase.auth.getSession();
      const r = await fetch('/api/sync-mantenimientos', {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
        signal: AbortSignal.timeout(75000) });
      const txt = await r.text();
      let d; try { d = JSON.parse(txt); }
      catch { toast(r.status===504 ? 'Se paso del tiempo limite. Proba de nuevo.' : `Respuesta rara (HTTP ${r.status}).`); setT(false); return; }
      if (!d.ok) { toast('No se pudo traer: ' + (d.error||'error')); setT(false); return; }
      toast(d.nuevos ? `Llegaron ${d.nuevos} reportes nuevos` : 'No hay reportes nuevos');
      await cargar();
    } catch (e) {
      toast(e.name==='TimeoutError' ? 'Tardo demasiado y lo corte.' : 'Error: ' + e.message);
    }
    setT(false);
  };

  // La app de Miguel tiene registros de prueba ("235", "ds"). No se borran:
  // se marcan descartados y dejan de estorbar.
  const descartar = async (m) => {
    if (!window.confirm(`Descartar ${m.correlativo || 'este reporte'}? No se borra, solo deja de aparecer.`)) return;
    await supabase.from('mantenimientos').update({ bandeja_estado:'descartado' }).eq('id', m.id);
    cargar();
  };

  const cargar = async () => {
    setL(true);
    const { data } = await supabase.from('mantenimientos')
      .select('*').eq('bandeja_estado','pendiente').order('fecha', { ascending:false }).limit(300);
    setPend(data || []);
    setL(false);
  };
  useEffect(() => { cargar(); }, []); // eslint-disable-line

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, flexWrap:'wrap' }}>
        <p style={{ margin:0, fontSize:12.5, color:C.t3, flex:1 }}>
          Reportes de servicio cargados que todavia no estan amarrados a ninguna maquina
        </p>
        <button onClick={traer} disabled={trayendo}
          style={{ ...BTN_PRIMARY, background:C.bg3, color:C.t2, opacity: trayendo ? .6 : 1 }}>
          {trayendo ? 'Trayendo…' : 'Traer de la app de Miguel'}
        </button>
        <button onClick={()=>setNuevo(true)} style={BTN_PRIMARY}>+ Cargar a mano</button>
      </div>

      {loading ? <p style={{ color:C.t3, fontSize:13 }}>Cargando…</p>
       : !pend.length ? (
        <div style={{ textAlign:'center', padding:'44px 20px', color:C.t3 }}>
          <p style={{ fontSize:14, margin:'0 0 6px' }}>No hay nada pendiente de asignar.</p>
          <p style={{ fontSize:12.5, margin:0 }}>
            Se traen solos todos los dias de la app de Miguel. Si acaba de cerrar
            uno, toca "Traer de la app de Miguel".
          </p>
        </div>
      ) : (
        <div style={{ display:'grid', gap:8 }}>
          {pend.map(m => (
            <div key={m.id} onClick={()=>setSel(m)} style={{
              background:C.bg2, border:`1px solid ${C.orangeBorder}`, borderRadius:R.lg,
              padding:'12px 14px', cursor:'pointer', display:'flex', gap:14, alignItems:'center', flexWrap:'wrap' }}>
              <div style={{ flex:'1 1 200px', minWidth:0 }}>
                <div style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>
                  {m.correlativo || 'Sin correlativo'} · {m.equipo_texto || 'equipo sin identificar'}
                </div>
                <div style={{ fontSize:12, color:C.t3, marginTop:2 }}>
                  {m.cliente_texto || 'cliente sin anotar'}{m.fecha ? ` · ${m.fecha}` : ''}
                  {m.tecnico ? ` · ${m.tecnico}` : ''}
                </div>
              </div>
              <div style={{ fontSize:12, color:C.t2, minWidth:90 }}>{m.tipo || '—'}</div>
              <div style={{ fontSize:12, color:C.t2, minWidth:88 }}>
                {m.horas_equipo ? `${Number(m.horas_equipo).toLocaleString()} h` : 'sin horometro'}
              </div>
              <span style={{ fontSize:11, fontWeight:700, color:C.orangeText, background:C.orangeTint,
                border:`1px solid ${C.orangeBorder}`, borderRadius:R.pill, padding:'3px 10px' }}>Asignar</span>
              <button onClick={ev=>{ ev.stopPropagation(); descartar(m); }} title="Descartar (prueba o duplicado)"
                style={{ fontFamily:'inherit', fontSize:15, lineHeight:1, color:C.t4, background:'none',
                  border:'none', cursor:'pointer', padding:'2px 4px' }}>×</button>
            </div>
          ))}
        </div>
      )}

      <AsignarReporte m={sel} clientes={clientes} usuario={usuario}
        onClose={()=>setSel(null)} onListo={()=>{ setSel(null); cargar(); onAsignado && onAsignado(); }} />
      <CargarReporte open={nuevo} usuario={usuario}
        onClose={()=>setNuevo(false)} onListo={()=>{ setNuevo(false); cargar(); }} />
    </div>
  );
}

// Kevin asigna en tres pasos, en este orden y no en otro: primero el CLIENTE,
// despues la maquina de ese cliente, y al final la factura de ese cliente.
//
// Buscar la maquina primero no funcionaba: el reporte trae el cliente escrito
// como se le ocurrio al tecnico ("Pro consumo", "Proconsumo", "MT PLATINUM"),
// y eso no empata con nada. El cliente lo elige Kevin de la lista de RADAR, y
// de ahi para abajo todo queda filtrado por ese cliente.
function AsignarReporte({ m, clientes = [], usuario, onClose, onListo }) {
  const [cli, setCli]       = useState(null);     // cliente de RADAR
  const [busqCli, setBusqCli] = useState('');
  const [maquinas, setMaq]  = useState(null);     // las de ese cliente
  const [eq, setEq]         = useState(null);
  const [nuevaMaq, setNM]   = useState(false);
  const [facturas, setFac]  = useState(null);
  const [fac, setFacSel]    = useState(null);
  const [lineas, setLineas] = useState(null);     // las del PDF, desde SAP
  const [guardando, setG]   = useState(false);

  useEffect(() => {
    if (!m) return;
    setCli(null); setBusqCli(m.cliente_texto || ''); setMaq(null); setEq(null);
    setNM(false); setFac(null); setFacSel(null); setLineas(null);
  }, [m]);

  // Al elegir cliente se traen SUS maquinas y SUS facturas. Nada mas.
  useEffect(() => {
    if (!cli) { setMaq(null); setFac(null); return; }
    (async () => {
      const { data: mq } = await supabase.from('equipos_cliente')
        .select('*').eq('cliente_id', cli.id).order('marca');
      setMaq(mq || []);
      // Se liga por cliente_id, que es el vinculo resuelto (ver
      // sql/limpiar-clientes-y-facturas.sql). Buscar por nombre era un parche:
      // fallaba
      // cuando el cliente estaba escrito distinto en SAP que en RADAR.
      let q = supabase.from('facturas_sap')
        .select('doc_num,card_name,doc_date,total_usd,moneda,doc_total,empresa_id')
        .order('doc_date', { ascending: false }).limit(40);
      q = cli.id ? q.eq('cliente_id', cli.id)
                 : q.ilike('card_name', `%${(cli.empresa || '').slice(0, 18)}%`);
      const { data: fs } = await q;
      setFac(fs || []);
    })();
  }, [cli]);

  if (!m) return null;

  const t = busqCli.toLowerCase().trim();
  // Se excluyen los inactivos. La lista `clientes` los trae a proposito porque
  // el modulo Clientes tiene su pestana de Inactivos, pero aca no sirven: los
  // duplicados que se limpiaron quedaron marcados asi, y eran los que hacian
  // que buscando "proconsumo" salieran tres y solo uno tuviera facturas.
  //
  // Los de SAP van primero: son los unicos que traen facturas.
  const sugCli = cli || t.length < 2 ? [] :
    clientes.filter(c => c.estado !== 'inactivo' && String(c.empresa || '').toLowerCase().includes(t))
      .sort((a, b) => (b.sap_code ? 1 : 0) - (a.sap_code ? 1 : 0))
      .slice(0, 8);

  const verLineas = async (f) => {
    setFacSel(f); setLineas('cargando');
    try {
      const r = await fetch(`/api/factura-lineas?doc_num=${f.doc_num}&empresa=${f.empresa_id || 'honduras'}`);
      const d = await r.json();
      setLineas(d.ok ? d : { error: d.error || 'no se pudo leer' });
    } catch (e) { setLineas({ error: e.message }); }
  };

  const asignar = async () => {
    setG(true);
    let equipoId = eq?.id;

    // Si la maquina no estaba, se crea con lo que ya trae el reporte: marca,
    // modelo y serie salen del texto que registro el tecnico.
    if (nuevaMaq && !equipoId) {
      const txt = m.equipo_texto || '';
      const serie = /serie\s+([A-Za-z0-9-]+)/i.exec(txt)?.[1] || null;
      const sinSerie = txt.replace(/\s*\(serie[^)]*\)/i, '').trim();
      const [marca, ...resto] = sinSerie.split(/\s+/);
      const { data, error } = await supabase.from('equipos_cliente').insert([{
        cliente_id: cli.id, cliente_nombre: cli.empresa,
        marca: marca || 'Sin marca', modelo: resto.join(' ') || null, serie,
        empresa_id: m.empresa_id || null,
        levantado_por: usuario?.nombre || null, levantado_at: new Date().toISOString(),
        origen: 'desconocido',
      }]).select('*').single();
      if (error) { setG(false); toast('No pude crear la maquina: ' + error.message); return; }
      equipoId = data.id; setEq(data);
    }
    if (!equipoId) { setG(false); return; }

    const { error: e1 } = await supabase.from('mantenimientos').update({
      equipo_cliente_id: equipoId, bandeja_estado: 'asignado',
      asignado_por: usuario?.nombre || null, asignado_at: new Date().toISOString(),
      factura_num: fac?.doc_num ? String(fac.doc_num) : null,
      factura_usd: fac?.total_usd ?? null,
    }).eq('id', m.id);
    if (e1) { setG(false); toast('No se pudo asignar: ' + e1.message); return; }

    // El horometro del reporte es la unica lectura confiable que existe: con el
    // se recalcula cuando le toca el proximo servicio.
    if (m.horas_equipo) {
      const h = Number(m.horas_equipo);
      const tipoNum = Number(String(m.tipo || '').replace(/\D/g, '')) || null;
      const p = proximoMant({ horometro_horas: h, ultimo_mant_horometro: h });
      // Si el tecnico dijo que toca despues, eso manda sobre el calculo. La
      // formula se equivoca cuando se saltaron ciclos: un equipo con 1,832
      // horas cuyo unico servicio fue de 250 nunca recibio el de 1,000, y sola
      // proponia el de 2,000. El que vio la maquina sabe mas.
      const dijoElTecnico = Number(m.proximo_mant_tipo) || null;
      await supabase.from('equipos_cliente').update({
        horometro_horas: Math.round(h), horometro_fecha: m.fecha || new Date().toISOString().slice(0,10),
        ultimo_mant_fecha: m.fecha || null, ultimo_mant_tipo: tipoNum,
        ultimo_mant_horometro: Math.round(h), ultimo_mant_quien: 'montasa',
        proximo_mant_tipo: dijoElTecnico || p.tipo,
        proximo_mant_fecha_est: p.fecha,
        proximo_mant_metodo: dijoElTecnico ? 'manual' : p.metodo,
        proximo_mant_horometro: p.hito || null,
      }).eq('id', equipoId);
    }
    setG(false);
    toast('Asignado. El horometro y el proximo servicio quedaron al dia.');
    onListo();
  };

  const puede = cli && (eq || nuevaMaq);

  return (
    <Modal open={!!m} onClose={onClose} title={`Asignar ${m.correlativo || 'reporte'}`} width={660}>
      <div style={{ display:'grid', gap:15 }}>
        <div style={{ background:C.bg1, borderRadius:R.md, padding:'11px 13px' }}>
          <div style={{ fontSize:10.5, color:C.t4, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:5 }}>
            Lo que dice el reporte
          </div>
          <div style={{ fontSize:13, fontWeight:700, color:C.t1 }}>{m.equipo_texto || 'sin identificar'}</div>
          <div style={{ fontSize:12, color:C.t2, marginTop:3 }}>
            {m.cliente_texto || 'sin cliente'} · {m.tipo || '—'}
            {m.horas_equipo ? ` · ${Number(m.horas_equipo).toLocaleString()} h` : ''}
            {m.fecha ? ` · ${m.fecha}` : ''}{m.tecnico ? ` · ${m.tecnico}` : ''}
          </div>
        </div>

        {/* ── 1. CLIENTE ── */}
        <div>
          <div style={SEC_LAB}><span style={SEC_BAR}/>1 · De que cliente es</div>
          {cli ? (
            <div style={{ display:'flex', alignItems:'center', gap:10, background:C.greenTint,
              border:`1px solid ${C.greenBorder}`, borderRadius:R.md, padding:'9px 12px' }}>
              <b style={{ fontSize:13, color:C.t1, flex:1 }}>{cli.empresa}</b>
              <button onClick={()=>{ setCli(null); setEq(null); setNM(false); setFacSel(null); }}
                style={{ ...BTN_PRIMARY, background:'transparent', color:C.t3, padding:'4px 8px' }}>cambiar</button>
            </div>
          ) : (
            <>
              <input value={busqCli} onChange={e=>setBusqCli(e.target.value)} autoFocus
                placeholder="Escribi parte del nombre del cliente"
                style={{ ...INP_ST, cursor:'text' }} />
              {sugCli.length > 0 && (
                <div style={{ marginTop:6, border:`1px solid ${C.border}`, borderRadius:R.md, overflow:'hidden' }}>
                  {sugCli.map(c => (
                    <div key={c.id} onClick={()=>setCli(c)} style={{ padding:'9px 11px', fontSize:12.5,
                      cursor:'pointer', borderBottom:`1px solid ${C.border}`, background:C.bg2, color:C.t1,
                      display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ flex:1 }}>{c.empresa}</span>
                      {c.sap_code
                        ? <span style={{ fontSize:10, fontWeight:700, color:C.greenText, background:C.greenTint,
                            border:`1px solid ${C.greenBorder}`, borderRadius:R.pill, padding:'2px 7px' }}>SAP</span>
                        : <span style={{ fontSize:10, color:C.t4 }}>sin SAP</span>}
                    </div>
                  ))}
                </div>
              )}
              {busqCli.length >= 2 && !sugCli.length && (
                <p style={{ fontSize:12, color:C.orangeText, margin:'7px 0 0' }}>
                  Ningun cliente se llama asi en RADAR. Proba con menos letras.
                </p>
              )}
            </>
          )}
        </div>

        {/* ── 2. MAQUINA ── */}
        {cli && (
          <div>
            <div style={SEC_LAB}><span style={SEC_BAR}/>2 · A que maquina de {cli.empresa}</div>
            {maquinas === null ? <p style={{ fontSize:12, color:C.t3, margin:0 }}>Buscando…</p>
             : (
              <>
                {maquinas.length > 0 && (
                  <div style={{ border:`1px solid ${C.border}`, borderRadius:R.md, overflow:'hidden', marginBottom:8 }}>
                    {maquinas.map(q => (
                      <div key={q.id} onClick={()=>{ setEq(q); setNM(false); }} style={{
                        padding:'9px 11px', fontSize:12.5, cursor:'pointer', borderBottom:`1px solid ${C.border}`,
                        background: eq?.id===q.id ? C.primaryTint : C.bg2 }}>
                        <b style={{ color:C.t1 }}>{[q.marca, q.modelo].filter(Boolean).join(' ')}</b>
                        {q.serie && <span style={{ color:C.t3 }}> · serie {q.serie}</span>}
                        {q.horometro_horas && <span style={{ color:C.t3 }}> · {Number(q.horometro_horas).toLocaleString()} h</span>}
                      </div>
                    ))}
                  </div>
                )}
                <div onClick={()=>{ setNM(true); setEq(null); }} style={{
                  padding:'10px 12px', fontSize:12.5, cursor:'pointer', borderRadius:R.md,
                  border:`1px dashed ${nuevaMaq ? C.primary : C.border2}`,
                  background: nuevaMaq ? C.primaryTint : 'transparent', color: nuevaMaq ? C.primary : C.t2 }}>
                  + Registrar maquina nueva
                  {nuevaMaq && <div style={{ fontSize:11.5, color:C.t2, marginTop:4 }}>
                    Se va a registrar <b>{m.equipo_texto}</b> como equipo de {cli.empresa},
                    con la marca, el modelo y la serie que trae el reporte.
                  </div>}
                </div>
                {!maquinas.length && !nuevaMaq && (
                  <p style={{ fontSize:12, color:C.t3, margin:'7px 0 0' }}>
                    Este cliente no tiene ninguna maquina registrada todavia.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* ── 3. FACTURA ── */}
        {cli && (eq || nuevaMaq) && (
          <div>
            <div style={SEC_LAB}><span style={SEC_BAR}/>3 · Que factura lo cobra (se puede despues)</div>
            {facturas === null ? <p style={{ fontSize:12, color:C.t3, margin:0 }}>Buscando…</p>
             : !facturas.length ? (
              <p style={{ fontSize:12, color:C.t3, margin:0 }}>
                Este cliente no tiene facturas registradas en RADAR. Se puede asignar
                el mantenimiento y agregar la factura mas adelante.
              </p>
            ) : (
              <div style={{ border:`1px solid ${C.border}`, borderRadius:R.md, maxHeight:190, overflowY:'auto' }}>
                {facturas.map(f => (
                  <div key={f.doc_num} style={{ borderBottom:`1px solid ${C.border}`,
                    background: fac?.doc_num===f.doc_num ? C.primaryTint : C.bg2 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 11px' }}>
                      <div onClick={()=>setFacSel(fac?.doc_num===f.doc_num ? null : f)}
                        style={{ flex:1, cursor:'pointer', fontSize:12.5, minWidth:0 }}>
                        <b style={{ color:C.t1 }}>#{f.doc_num}</b>
                        <span style={{ color:C.t3 }}> · {f.doc_date} · {f.moneda} {Number(f.doc_total||0).toLocaleString()}</span>
                        {f.total_usd ? <span style={{ color:C.t3 }}> · USD {Number(f.total_usd).toLocaleString()}</span> : ''}
                      </div>
                      <button onClick={()=>verLineas(f)} style={{ ...BTN_PRIMARY, background:'transparent',
                        color:C.primary, padding:'3px 8px', fontSize:11.5 }}>ver lineas</button>
                    </div>
                    {fac?.doc_num===f.doc_num && lineas && (
                      <div style={{ padding:'0 11px 10px', fontSize:11.5, color:C.t2 }}>
                        {lineas === 'cargando' ? 'Leyendo de SAP…'
                         : lineas.error ? <span style={{ color:C.orangeText }}>{lineas.error}</span>
                         : (lineas.lineas || []).map((l, i) => (
                          <div key={i} style={{ display:'flex', gap:8, padding:'2px 0' }}>
                            <span style={{ flex:1 }}>{l.descripcion}</span>
                            <span style={{ color:C.t3 }}>{l.cantidad}</span>
                            <span style={{ minWidth:70, textAlign:'right' }}>{Number(l.total||0).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ ...BTN_PRIMARY, background:C.bg3, color:C.t2 }}>Cancelar</button>
          <button onClick={asignar} disabled={!puede || guardando}
            style={{ ...BTN_PRIMARY, opacity: !puede || guardando ? .5 : 1 }}>
            {guardando ? 'Asignando…' : nuevaMaq ? 'Crear maquina y asignar' : 'Asignar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Carga manual del reporte. Son los datos de la cabecera del reporte de Miguel:
// correlativo, equipo, servicio, tecnicos, horometro y fecha. El texto largo de
// "trabajo realizado" NO se pide: Kevin lo revisa en la foto y no aporta al dato.
// ─── INSUMOS DE UN MANTENIMIENTO ──────────────────────────────────────────────
// Una factura trae diez renglones y esos diez renglones pueden ser de tres
// maquinas distintas. Amarrar el mantenimiento a la factura entera no dice que
// llevo cada equipo, y sin eso no hay historial de insumos ni por maquina ni
// por cliente.
//
// Aca el vendedor reparte: de la factura 1234 agarra 2 de este codigo y 3 del
// otro para ESTE mantenimiento. Lo que no reparte queda flotando y se le puede
// asignar despues a otro mantenimiento del mismo cliente.
function InsumosDelMantenimiento({ mant, usuario, onCambio }) {
  const [lineas, setLineas] = useState(null);   // renglones de la factura, de SAP
  const [puestos, setPuestos] = useState([]);   // lo ya asignado, de RADAR
  const [cant, setCant] = useState({});         // lo que se esta por asignar
  const [guardando, setG] = useState(false);
  const [err, setErr] = useState('');

  const facturaNum = mant?.factura_num;
  const empresa = mant?.empresa_id || 'honduras';

  const cargar = useCallback(async () => {
    const { data } = await supabase.from('mantenimiento_insumos')
      .select('*').eq('mantenimiento_id', mant.id).order('created_at');
    setPuestos(data || []);
  }, [mant?.id]);

  useEffect(() => { if (mant?.id) cargar(); }, [mant?.id, cargar]);

  // Los renglones se leen de SAP en vivo: RADAR guarda solo la cabecera de la
  // factura, no el detalle.
  useEffect(() => {
    if (!facturaNum) { setLineas(null); return; }
    let vivo = true;
    setLineas('cargando'); setErr('');
    fetch(`/api/factura-lineas?doc_num=${encodeURIComponent(facturaNum)}&empresa=${encodeURIComponent(empresa)}`)
      .then(r => r.json())
      .then(d => { if (!vivo) return;
        if (d.ok) setLineas(d.lineas || []);
        else { setLineas([]); setErr(d.error || 'No se pudieron leer los renglones de la factura'); } })
      .catch(e => { if (vivo) { setLineas([]); setErr(e.message); } });
    return () => { vivo = false; };
  }, [facturaNum, empresa]);

  // Cuanto de cada renglon ya se repartio, contando TODOS los mantenimientos,
  // no solo este: si la mitad se uso en otra maquina, aca ya no esta disponible.
  const [usados, setUsados] = useState({});
  useEffect(() => {
    if (!facturaNum) return;
    supabase.from('mantenimiento_insumos')
      .select('linea_sap,item_code,cantidad')
      .eq('factura_num', String(facturaNum)).eq('empresa_id', empresa)
      .then(({ data }) => {
        const m = {};
        (data || []).forEach(x => {
          const k = x.linea_sap != null ? `L${x.linea_sap}` : `I${x.item_code || ''}`;
          m[k] = (m[k] || 0) + Number(x.cantidad || 0);
        });
        setUsados(m);
      });
  }, [facturaNum, empresa, puestos.length]);

  const llave = (l, i) => (l.linea_sap != null ? `L${l.linea_sap}` : (l.item ? `I${l.item}` : `X${i}`));
  const libre = (l, i) => Math.max(0, Number(l.cantidad || 0) - Number(usados[llave(l, i)] || 0));

  const asignar = async () => {
    const filas = [];
    (Array.isArray(lineas) ? lineas : []).forEach((l, i) => {
      const q = Number(cant[llave(l, i)] || 0);
      if (q <= 0) return;
      const unit = Number(l.precio) || null;
      filas.push({
        mantenimiento_id: mant.id,
        equipo_cliente_id: mant.equipo_cliente_id || null,
        cliente_id: mant.cliente_id || null,
        factura_num: String(facturaNum), empresa_id: empresa,
        linea_sap: l.linea_sap ?? i,
        item_code: l.item || null,
        descripcion: l.descripcion || l.item || 'Sin descripcion',
        cantidad: q,
        precio_unitario: unit,
        total_usd: unit != null ? Number((unit * q).toFixed(2)) : null,
        clase: /mano de obra|servicio t|hora/i.test(l.descripcion || '') ? 'mano_obra' : 'insumo',
        creado_por: usuario?.nombre || null,
      });
    });
    if (!filas.length) { setErr('No pusiste ninguna cantidad.'); return; }
    setG(true); setErr('');
    const { error } = await supabase.from('mantenimiento_insumos').insert(filas);
    setG(false);
    if (error) { setErr(error.message); return; }
    setCant({}); await cargar(); onCambio && onCambio();
    toast(`${filas.length} insumo${filas.length > 1 ? 's' : ''} asignado${filas.length > 1 ? 's' : ''}`);
  };

  const quitar = async (id) => {
    await supabase.from('mantenimiento_insumos').delete().eq('id', id);
    await cargar(); onCambio && onCambio();
  };

  const totalPuesto = puestos.reduce((a, x) => a + Number(x.total_usd || 0), 0);

  if (!facturaNum) return (
    <div style={{ background:C.bg1, borderRadius:R.md, padding:'12px 14px', fontSize:12.5, color:C.t3 }}>
      Este mantenimiento no tiene factura asignada. Asignale una y despues se pueden repartir sus renglones.
    </div>
  );

  return (
    <div style={{ display:'grid', gap:12 }}>
      <div>
        <div style={SEC_LAB}>Insumos aplicados a este equipo</div>
        {!puestos.length ? (
          <div style={{ fontSize:12.5, color:C.t3, padding:'6px 0' }}>Sin insumos asignados.</div>
        ) : (
          <div style={{ display:'grid', gap:5 }}>
            {puestos.map(x => (
              <div key={x.id} style={{ display:'flex', gap:10, alignItems:'center',
                background:C.bg1, borderRadius:R.md, padding:'8px 11px' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12.5, color:C.t1, fontWeight:600 }}>{x.descripcion}</div>
                  <div style={{ fontSize:11, color:C.t4 }}>
                    {x.item_code ? `${x.item_code} · ` : ''}{Number(x.cantidad)} u
                    {x.clase === 'mano_obra' ? ' · mano de obra' : ''}
                  </div>
                </div>
                <div style={{ fontSize:12.5, fontWeight:700, color:C.t1 }}>{dolar(x.total_usd || 0)}</div>
                <button onClick={()=>quitar(x.id)} title="Quitar"
                  style={{ border:'none', background:'transparent', cursor:'pointer', color:C.t4, fontSize:14 }}>×</button>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, paddingTop:4,
              fontSize:13, fontWeight:800, color:C.t1 }}>
              Total: {dolar(totalPuesto)}
            </div>
          </div>
        )}
      </div>

      <div>
        <div style={SEC_LAB}>Renglones de la factura {facturaNum}</div>
        {lineas === 'cargando' ? (
          <div style={{ fontSize:12.5, color:C.t3 }}>Leyendo la factura en SAP...</div>
        ) : !Array.isArray(lineas) || !lineas.length ? (
          <div style={{ fontSize:12.5, color:C.t3 }}>{err || 'La factura no trajo renglones.'}</div>
        ) : (
          <div style={{ display:'grid', gap:5 }}>
            {lineas.map((l, i) => {
              const k = llave(l, i), disp = libre(l, i);
              return (
                <div key={k} style={{ display:'flex', gap:10, alignItems:'center',
                  background: disp > 0 ? C.bg2 : C.bg1, border:`1px solid ${C.border}`,
                  borderRadius:R.md, padding:'8px 11px', opacity: disp > 0 ? 1 : .55 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12.5, color:C.t1 }}>{l.descripcion || l.item}</div>
                    <div style={{ fontSize:11, color:C.t4 }}>
                      {l.item ? `${l.item} · ` : ''}factura {Number(l.cantidad)} u
                      {disp > 0
                        ? ` · quedan ${disp} sin asignar`
                        : ' · todo repartido'}
                    </div>
                  </div>
                  <input type="number" min="0" max={disp} step="any" disabled={disp <= 0}
                    value={cant[k] ?? ''} placeholder="0"
                    onChange={ev => setCant(c => ({ ...c, [k]: ev.target.value }))}
                    style={{ ...INP_ST, width:74, textAlign:'right' }} />
                </div>
              );
            })}
            {err && <div style={{ fontSize:12, color:C.redText }}>{err}</div>}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, paddingTop:4 }}>
              <div style={{ fontSize:11.5, color:C.t3 }}>
                Las cantidades que no asignes quedan disponibles para otro mantenimiento del mismo cliente.
              </div>
              <Btn size="sm" onClick={asignar} disabled={guardando}>
                {guardando ? 'Guardando...' : 'Asignar al mantenimiento'}
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CargarReporte({ open, usuario, onClose, onListo }) {
  const [f, setF] = useState({});
  const [guardando, setG] = useState(false);
  useEffect(() => { if (open) setF({ fecha: new Date().toISOString().slice(0,10) }); }, [open]);
  const set = (k,v) => setF(p=>({ ...p, [k]:v }));
  const falta = !f.correlativo || !f.equipo_texto || !f.tipo;

  const guardar = async () => {
    setG(true);
    const { error } = await supabase.from('mantenimientos').insert([{
      correlativo: f.correlativo, equipo_texto: f.equipo_texto, cliente_texto: f.cliente_texto || null,
      tipo: f.tipo, tecnico: f.tecnico || null, fecha: f.fecha || null,
      horas_equipo: f.horas_equipo ? Number(f.horas_equipo) : null,
      descripcion: f.sugerencias || null, reporte_url: f.reporte_url || null,
      bandeja_estado: 'pendiente',
    }]);
    setG(false);
    if (error) {
      toast(/duplicate|unique/i.test(error.message)
        ? `El reporte ${f.correlativo} ya estaba cargado.`
        : 'No se pudo guardar: ' + error.message);
      return;
    }
    toast('Reporte en la bandeja, listo para asignar');
    onListo();
  };

  const campo = (k, et, extra={}) => (
    <div style={{ flex:'1 1 150px' }}>
      <div style={FLD_LAB}>{et}</div>
      <input value={f[k] ?? ''} onChange={e=>set(k, e.target.value)} {...extra}
        style={{ ...INP_ST, cursor:'text' }} />
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title="Cargar reporte de servicio" width={640}>
      <div style={{ display:'grid', gap:14 }}>
        <p style={{ margin:0, fontSize:12.5, color:C.t3 }}>
          Solo la cabecera del reporte. El detalle del trabajo queda en la foto.
        </p>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {campo('correlativo', 'Correlativo *', { placeholder:'MH-0120' })}
          {campo('fecha', 'Fecha', { type:'date' })}
          <div style={{ flex:'1 1 170px' }}>
            <div style={FLD_LAB}>Servicio *</div>
            <select value={f.tipo ?? ''} onChange={e=>set('tipo', e.target.value)} style={INP_ST}>
              <option value="">—</option>
              {[250,500,1000,2000].map(t => <option key={t} value={`Mantenimiento ${t} horas`}>Preventivo {t} horas</option>)}
              <option value="Correctivo">Correctivo (reparacion)</option>
            </select>
          </div>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {campo('equipo_texto', 'Equipo *', { placeholder:'Como lo escribio el tecnico' })}
          {campo('cliente_texto', 'Cliente', { placeholder:'Como lo escribio el tecnico' })}
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {campo('horas_equipo', 'Horometro', { type:'number', placeholder:'1007.4' })}
          {campo('tecnico', 'Tecnico(s)')}
        </div>
        {campo('reporte_url', 'Enlace al reporte', { placeholder:'Drive, WhatsApp, donde este la foto' })}

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ ...BTN_PRIMARY, background:C.bg3, color:C.t2 }}>Cancelar</button>
          <button onClick={guardar} disabled={falta || guardando}
            style={{ ...BTN_PRIMARY, opacity: falta || guardando ? .5 : 1 }}>
            {guardando ? 'Guardando…' : 'Guardar en la bandeja'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Las lineas del cotizador. Encabezado y filas usan ESTA grilla, para que las
// etiquetas caigan siempre sobre su campo. El ancho minimo de la descripcion es
// lo que decide cuando el modal empieza a apretar.
const GRID_COT = {
  display: 'grid',
  gridTemplateColumns: 'minmax(110px,1fr) 62px 100px 100px 22px',
  gap: 6,
  alignItems: 'center',
};
const INP_COT = {
  width: '100%', background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8,
  padding: '8px 9px', color: C.t1, fontSize: 12, outline: 'none', boxSizing: 'border-box',
};

const BTN_PRIMARY = { fontFamily:'inherit', fontSize:12.5, fontWeight:700, color:'#fff', background:C.primary,
  border:'none', borderRadius:R.md, padding:'8px 14px', cursor:'pointer' };

function Tarjeta({ n, t, tono }) {
  const col = tono==='rojo' ? C.redText : tono==='naranja' ? C.orangeText : C.t1;
  return (
    <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:R.lg, padding:'10px 14px', minWidth:118 }}>
      <div style={{ fontSize:20, fontWeight:800, color:col, lineHeight:1.1 }}>{n}</div>
      <div style={{ fontSize:11, color:C.t3, marginTop:2 }}>{t}</div>
    </div>
  );
}

// Alta desde el campo. El tecnico la llena parado frente al montacargas, asi que
// solo 5 campos son obligatorios: lo demas se completa despues desde el escritorio.
// El cliente se escribe libre y se concilia luego — obligar a elegir de una lista
// de 5,000 parado en una planta es garantia de que no lo llenen.
function AltaEquipoCliente({ open, onClose, clientes = [], usuario, empresaActiva, onGuardado }) {
  const [f, setF] = useState({});
  const [guardando, setG] = useState(false);
  const [abierto, setAbierto] = useState(false);
  useEffect(() => { if (open) { setF({ estado:'operando' }); setAbierto(false); } }, [open]);

  // Busca en cualquier parte del nombre, no solo al principio.
  const sugerencias = (() => {
    const t = (f.cliente_nombre || '').toLowerCase().trim();
    if (t.length < 2) return [];
    return clientes.filter(c => String(c.empresa||'').toLowerCase().includes(t)).slice(0, 8);
  })();

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  // Lo que pidio Miguel: marca, modelo, serie y combustible. Anio y capacidad
  // salen de la placa cuando alguien tenga tiempo, no frenan el registro.
  const falta = !f.cliente_nombre || !f.marca || !f.modelo || !f.serie || !f.combustible;

  const guardar = async () => {
    setG(true);
    // Si el nombre coincide exacto con un cliente, se amarra solo. Si no, queda
    // el texto y se concilia despues.
    const match = clientes.find(c => (c.empresa || '').toLowerCase() === String(f.cliente_nombre).toLowerCase().trim());
    const p = proximoMant(f);
    const { error } = await supabase.from('equipos_cliente').insert([{
      ...f,
      cliente_id: match?.id || null,
      empresa_id: empresaActiva || null,
      anio: f.anio ? Number(f.anio) : null,
      capacidad_kg: f.capacidad_kg ? Number(f.capacidad_kg) : null,
      // El horometro NO se pide aca: lo trae el reporte de Miguel cada vez que
      // hace un servicio, que es la unica lectura confiable que existe.
      ultimo_mant_tipo: f.ultimo_mant_tipo ? Number(f.ultimo_mant_tipo) || null : null,
      proximo_mant_tipo: p.tipo, proximo_mant_fecha_est: p.fecha, proximo_mant_metodo: p.metodo,
      proximo_mant_horometro: p.hito || null,
      levantado_por: usuario?.nombre || null, levantado_at: new Date().toISOString(),
    }]);
    setG(false);
    if (error) { toast('No se pudo guardar: ' + error.message); return; }
    toast('Equipo agregado a la flota del cliente');
    onGuardado();
  };

  const campo = (k, etiqueta, extra = {}) => (
    <div style={{ flex:'1 1 150px', minWidth:0 }}>
      <div style={FLD_LAB}>{etiqueta}</div>
      <input value={f[k] ?? ''} onChange={e=>set(k, e.target.value)} {...extra}
        style={{ ...INP_ST, cursor:'text' }} />
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title="Agregar equipo del cliente" width={680}>
      <div style={{ display:'grid', gap:16 }}>
        <div>
          <div style={SEC_LAB}><span style={SEC_BAR}/>De quien es</div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <div style={{ flex:'1 1 260px', position:'relative' }}>
              <div style={FLD_LAB}>Cliente *</div>
              <input value={f.cliente_nombre ?? ''}
                onChange={e=>{ set('cliente_nombre', e.target.value); set('cliente_id', null); setAbierto(true); }}
                onFocus={()=>setAbierto(true)}
                placeholder="Escribi cualquier parte del nombre"
                style={{ ...INP_ST, cursor:'text' }} />
              {abierto && (f.cliente_nombre||'').length >= 2 && sugerencias.length > 0 && (
                <div style={{ position:'absolute', zIndex:30, top:'100%', left:0, right:0, marginTop:4,
                  background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:R.md,
                  boxShadow:SH.lg, maxHeight:230, overflowY:'auto' }}>
                  {sugerencias.map(c => (
                    <div key={c.id} onMouseDown={()=>{ set('cliente_nombre', c.empresa); set('cliente_id', c.id); setAbierto(false); }}
                      style={{ padding:'8px 11px', fontSize:12.5, color:C.t1, cursor:'pointer',
                        borderBottom:`1px solid ${C.border}` }}>{c.empresa}</div>
                  ))}
                </div>
              )}
              {f.cliente_id && <div style={{ fontSize:10.5, color:C.greenText, marginTop:3 }}>Cliente de RADAR</div>}
            </div>
            {campo('sitio', 'Planta o sitio')}
            {campo('ciudad', 'Ciudad')}
          </div>
        </div>

        <div>
          <div style={SEC_LAB}><span style={SEC_BAR}/>El equipo</div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {campo('marca', 'Marca *', { placeholder:'Toyota, Hyster, Yale…' })}
            {campo('modelo', 'Modelo *')}
            {campo('serie', 'Serie *', { placeholder:'De la placa' })}
            {campo('codigo_interno', 'Codigo interno del cliente')}
            {campo('anio', 'Anio')}
            {campo('capacidad_kg', 'Capacidad (kg)')}
            <div style={{ flex:'1 1 150px' }}>
              <div style={FLD_LAB}>Combustible *</div>
              <select value={f.combustible ?? ''} onChange={e=>set('combustible', e.target.value)} style={INP_ST}>
                <option value="">—</option><option value="gas">Gas</option>
                <option value="diesel">Diesel</option><option value="electrico">Electrico</option>
                <option value="dual">Dual</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <div style={SEC_LAB}><span style={SEC_BAR}/>Ultimo servicio (si se sabe)</div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {campo('ultimo_mant_fecha', 'Cuando', { type:'date' })}
            <div style={{ flex:'1 1 150px' }}>
              <div style={FLD_LAB}>Que le hicieron</div>
              <select value={f.ultimo_mant_tipo ?? ''} onChange={e=>set('ultimo_mant_tipo', e.target.value)} style={INP_ST}>
                <option value="">No sabe</option>
                {[250,500,1000,2000].map(t => <option key={t} value={t}>Preventivo {t} horas</option>)}
                <option value="0">Correctivo (reparacion)</option>
              </select>
            </div>
            <div style={{ flex:'1 1 150px' }}>
              <div style={FLD_LAB}>Quien se lo hizo</div>
              <select value={f.ultimo_mant_quien ?? ''} onChange={e=>set('ultimo_mant_quien', e.target.value)} style={INP_ST}>
                <option value="">—</option><option value="montasa">Montasa</option>
                <option value="cliente">El cliente</option><option value="otro proveedor">Otro proveedor</option>
                <option value="ninguno">Nunca le hicieron</option><option value="no sabe">No sabe</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <div style={FLD_LAB}>Observaciones</div>
          <textarea value={f.observaciones ?? ''} onChange={e=>set('observaciones', e.target.value)} rows={2}
            placeholder="Fugas, llantas, lo que se vea mal…"
            style={{ ...INP_ST, cursor:'text', resize:'vertical' }} />
        </div>

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ ...BTN_PRIMARY, background:C.bg3, color:C.t2 }}>Cancelar</button>
          <button onClick={guardar} disabled={falta || guardando}
            style={{ ...BTN_PRIMARY, opacity: falta || guardando ? .5 : 1 }}>
            {guardando ? 'Guardando…' : 'Guardar equipo'}
          </button>
        </div>
        {falta && <p style={{ margin:0, fontSize:11.5, color:C.t3, textAlign:'right' }}>
          Faltan: {[!f.cliente_nombre&&'cliente', !f.marca&&'marca', !f.modelo&&'modelo',
                    !f.serie&&'serie', !f.combustible&&'combustible'].filter(Boolean).join(', ')}
        </p>}
      </div>
    </Modal>
  );
}

// Ficha del equipo. Lo que mas se usa es actualizar el horometro: cada vez que
// alguien pasa por la planta, ese numero cambia y con el cambia la programacion.
function FichaEquipoCliente({ eq, usuario, onClose, onCambio }) {
  const [h, setH] = useState('');
  const [guardando, setG] = useState(false);
  useEffect(() => { setH(eq?.horometro_horas ?? ''); }, [eq]);
  if (!eq) return null;

  const p = proximoMant({ ...eq, horometro_horas: h || eq.horometro_horas });
  const u = URGENCIA(p);

  const actualizar = async () => {
    setG(true);
    const pp = proximoMant({ ...eq, horometro_horas: Number(h) });
    const { error } = await supabase.from('equipos_cliente').update({
      horometro_horas: Number(h),
      horometro_fecha: new Date().toISOString().slice(0,10),
      proximo_mant_tipo: pp.tipo, proximo_mant_fecha_est: pp.fecha,
      proximo_mant_metodo: pp.metodo, proximo_mant_horometro: pp.hito || null,
    }).eq('id', eq.id);
    setG(false);
    if (error) { toast('No se pudo actualizar: ' + error.message); return; }
    toast('Horometro actualizado');
    onCambio(); onClose();
  };

  const dato = (et, v) => v ? (
    <div style={{ flex:'1 1 130px' }}>
      <div style={{ fontSize:10.5, color:C.t4, textTransform:'uppercase', letterSpacing:'.06em' }}>{et}</div>
      <div style={{ fontSize:13, color:C.t1, fontWeight:600, marginTop:2 }}>{v}</div>
    </div>
  ) : null;

  return (
    <Modal open={!!eq} onClose={onClose} title={[eq.marca, eq.modelo].filter(Boolean).join(' ') || 'Equipo'} width={640}>
      <div style={{ display:'grid', gap:16 }}>
        <div style={{ background:u.bg, border:`1px solid ${u.color}33`, borderRadius:R.lg, padding:'14px 16px' }}>
          <div style={{ fontSize:11, color:C.t3, textTransform:'uppercase', letterSpacing:'.06em' }}>Le toca</div>
          <div style={{ fontSize:24, fontWeight:800, color:u.color, lineHeight:1.2 }}>
            Mantenimiento de {p.tipo} horas
          </div>
          <div style={{ fontSize:12.5, color:C.t2, marginTop:4 }}>
            {p.atrasado
              ? <b>Deberia haberse hecho hace rato</b>
              : (p.fecha ? <>Alrededor del <b>{p.fecha}</b> · {u.txt}</> : 'Sin fecha: falta horometro o ultimo servicio')}
          </div>
          {/* De donde sale el numero. Antes decia "2000 horas en 24 dias" sin
              explicar nada, y ese 24 salia de suponer 8 horas al dia. */}
          {p.nota && (
            <div style={{ fontSize:11.5, color:C.t3, marginTop:6, paddingTop:6,
              borderTop:`1px solid ${u.color}22` }}>
              {p.nota}
            </div>
          )}
          {p.vencidoPorTiempo && !p.atrasado && (
            <div style={{ fontSize:11.5, fontWeight:700, color:C.redText, marginTop:4 }}>
              Ademas pasaron mas de 3 meses del ultimo servicio.
            </div>
          )}
        </div>

        <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
          {dato('Cliente', eq.cliente_nombre)}
          {dato('Sitio', eq.sitio)}
          {dato('Ciudad', eq.ciudad)}
          {dato('Serie', eq.serie)}
          {dato('Codigo del cliente', eq.codigo_interno)}
          {dato('Capacidad', eq.capacidad_kg ? `${Number(eq.capacidad_kg).toLocaleString()} kg` : null)}
          {dato('Combustible', eq.combustible)}
          {dato('Anio', eq.anio)}
          {dato('Uso', eq.horas_dia
            ? `${eq.horas_dia} h/dia, ${eq.dias_semana || 6} dias por semana`
            : 'sin medir: se asumen 8 h/dia, 6 dias')}
          {dato('Ultimo servicio', eq.ultimo_mant_fecha
            ? `${eq.ultimo_mant_fecha}${eq.ultimo_mant_tipo ? ` — mant. de ${eq.ultimo_mant_tipo} h` : ''}` : null)}
          {dato('A que horometro', eq.ultimo_mant_horometro
            ? `${Number(eq.ultimo_mant_horometro).toLocaleString()} h`
            : 'no quedo anotado')}
          {dato('Se lo hizo', eq.ultimo_mant_quien)}
          {dato('Levantado por', eq.levantado_por)}
        </div>

        {eq.observaciones && (
          <div style={{ background:C.bg1, borderRadius:R.md, padding:'10px 12px', fontSize:12.5, color:C.t2 }}>
            {eq.observaciones}
          </div>
        )}

        <SeguimientoMant eq={eq} usuario={usuario} onCambio={onCambio} />

        <div>
          <div style={SEC_LAB}><span style={SEC_BAR}/>Servicios e insumos</div>
          <MantenimientosDelEquipo eq={eq} usuario={usuario} />
        </div>

        <div style={{ background:C.bg1, borderRadius:R.md, padding:'12px 14px' }}>
          <div style={{ fontSize:12, color:C.t2 }}>
            El horometro y la fecha se actualizan solos cuando se carga el reporte
            de un servicio. No hay que anotarlo a mano.
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── LA LLAMADA DE LOS 3 MESES ────────────────────────────────────────────────
// El mantenimiento cae cada 250 horas O cada 3 meses, lo que llegue primero.
// Cuando pasan los 3 meses hay que llamar al cliente, y esta alerta NO se apaga
// sola: se apaga cuando el vendedor escribe que hablo y que le contestaron.
// Si el cliente dijo que no, eso tambien se registra — un "no" con razon vale
// mas que una alerta que alguien cerro sin llamar.
function SeguimientoMant({ eq, usuario, onCambio }) {
  const [caso, setCaso] = useState(null);       // la alerta abierta, si hay
  const [f, setF] = useState({ se_agendo:null, fecha_agendada:'', razon_no:'', comentario:'' });
  const [guardando, setG] = useState(false);
  const [err, setErr] = useState('');

  const cargar = useCallback(async () => {
    const { data } = await supabase.from('mantenimiento_seguimiento')
      .select('*').eq('equipo_cliente_id', eq.id).eq('estado','abierta').maybeSingle();
    setCaso(data || null);
  }, [eq?.id]);
  useEffect(() => { if (eq?.id) cargar(); }, [eq?.id, cargar]);

  const p = eq?._p || proximoMant(eq);
  const debeLlamarse = !!(p?.vencidoPorTiempo || p?.atrasado);

  const abrir = async () => {
    setG(true);
    const { error } = await supabase.from('mantenimiento_seguimiento').insert([{
      equipo_cliente_id: eq.id, cliente_id: eq.cliente_id || null,
      motivo: p?.atrasado ? 'ciclo_saltado' : 'tres_meses',
    }]);
    setG(false);
    if (error && !/duplicate|unique/i.test(error.message)) { setErr(error.message); return; }
    cargar();
  };

  const cerrar = async () => {
    setErr('');
    if (f.se_agendo === null) { setErr('Falta decir si se agendo o no.'); return; }
    if (f.se_agendo && !f.fecha_agendada) { setErr('Si se agendo, falta la fecha.'); return; }
    if (!f.se_agendo && !f.razon_no.trim()) { setErr('Si no se agendo, hay que decir por que.'); return; }
    if (!f.comentario.trim()) { setErr('Falta escribir que se hablo.'); return; }
    setG(true);
    const { error } = await supabase.from('mantenimiento_seguimiento').update({
      estado:'resuelta', atendio: usuario?.nombre || null,
      se_agendo: f.se_agendo, fecha_agendada: f.se_agendo ? f.fecha_agendada : null,
      razon_no: f.se_agendo ? null : f.razon_no.trim(), comentario: f.comentario.trim(),
    }).eq('id', caso.id);
    setG(false);
    if (error) { setErr(error.message); return; }
    setF({ se_agendo:null, fecha_agendada:'', razon_no:'', comentario:'' });
    await cargar(); onCambio && onCambio();
    toast('Seguimiento registrado');
  };

  if (!debeLlamarse && !caso) return null;

  if (!caso) return (
    <div style={{ background:C.orangeTint, border:`1px solid ${C.orangeText}33`,
      borderRadius:R.md, padding:'11px 13px', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
      <div style={{ flex:1, minWidth:180, fontSize:12.5, color:C.t1 }}>
        {p?.atrasado
          ? 'Este equipo arrastra un mantenimiento que nunca se hizo.'
          : 'Pasaron mas de 3 meses del ultimo servicio.'} Hay que llamar al cliente.
      </div>
      <Btn size="sm" onClick={abrir} disabled={guardando}>Abrir seguimiento</Btn>
    </div>
  );

  return (
    <div style={{ background:C.orangeTint, border:`1px solid ${C.orangeText}33`, borderRadius:R.md, padding:'12px 14px' }}>
      <div style={{ fontSize:12.5, fontWeight:700, color:C.t1, marginBottom:8 }}>
        Seguimiento abierto desde el {String(caso.abierta_at).slice(0,10)}
      </div>
      <div style={{ fontSize:11.5, color:C.t2, marginBottom:10 }}>
        Esta alerta no se quita hasta que quede escrito que se hablo con el cliente.
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:10 }}>
        {[[true,'Si se agendo'],[false,'No se agendo']].map(([v,l])=>(
          <button key={l} onClick={()=>setF(x=>({ ...x, se_agendo:v }))}
            style={{ ...BTN_PRIMARY, padding:'6px 12px', fontSize:12,
              background: f.se_agendo===v ? C.primary : C.bg3,
              color: f.se_agendo===v ? '#fff' : C.t2 }}>{l}</button>
        ))}
      </div>

      {f.se_agendo === true && (
        <div style={{ marginBottom:10 }}>
          <div style={FLD_LAB}>Para cuando</div>
          <input type="date" value={f.fecha_agendada}
            onChange={e=>setF(x=>({ ...x, fecha_agendada:e.target.value }))} style={INP_ST} />
        </div>
      )}
      {f.se_agendo === false && (
        <div style={{ marginBottom:10 }}>
          <div style={FLD_LAB}>Por que no</div>
          <input value={f.razon_no} onChange={e=>setF(x=>({ ...x, razon_no:e.target.value }))}
            placeholder="No hay presupuesto, lo hace otro proveedor, la maquina esta parada..."
            style={{ ...INP_ST, cursor:'text' }} />
        </div>
      )}

      <div style={{ marginBottom:10 }}>
        <div style={FLD_LAB}>Que se hablo</div>
        <textarea value={f.comentario} onChange={e=>setF(x=>({ ...x, comentario:e.target.value }))}
          rows={2} placeholder="¿Con quién se habló y qué dijo?"
          style={{ ...INP_ST, cursor:'text', resize:'vertical' }} />
      </div>

      {err && <div style={{ fontSize:12, color:C.redText, marginBottom:8 }}>{err}</div>}
      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <Btn size="sm" onClick={cerrar} disabled={guardando}>
          {guardando ? 'Guardando...' : 'Registrar y cerrar'}
        </Btn>
      </div>
    </div>
  );
}

// ─── HISTORIAL DE INSUMOS ─────────────────────────────────────────────────────
// Lo mismo visto al reves: en vez de "que llevo esta maquina", "que le hemos
// puesto a este cliente" y "que es lo que mas gastamos". Sale de la vista
// v_insumos_por_cliente (ver sql/insumos-y-seguimiento.sql).
function HistorialInsumos({ clientes = [] }) {
  const [filas, setFilas] = useState(null);
  const [cliente, setCliente] = useState('');
  const [busq, setBusq] = useState('');

  useEffect(() => {
    (async () => {
      let q = supabase.from('v_insumos_por_cliente').select('*').order('total_usd', { ascending:false }).limit(1000);
      if (cliente) q = q.eq('cliente_id', cliente);
      const { data } = await q;
      setFilas(data || []);
    })();
  }, [cliente]);

  const vista = (filas || []).filter(f => {
    if (!busq) return true;
    const b = busq.toLowerCase();
    return (f.descripcion||'').toLowerCase().includes(b)
        || (f.item_code||'').toLowerCase().includes(b)
        || (f.cliente||'').toLowerCase().includes(b);
  });
  const total = vista.reduce((a,f)=>a+Number(f.total_usd||0), 0);

  return (
    <div style={{ display:'grid', gap:12 }}>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
        <input value={busq} onChange={e=>setBusq(e.target.value)}
          placeholder="Buscar insumo, codigo o cliente..."
          style={{ ...INP_ST, flex:'1 1 260px', cursor:'text' }} />
        <select value={cliente} onChange={e=>setCliente(e.target.value)}
          style={{ ...INP_ST, flex:'0 1 260px' }}>
          <option value="">Todos los clientes</option>
          {clientes.filter(c=>c.estado!=='inactivo').map(c=>(
            <option key={c.id} value={c.id}>{c.empresa}</option>
          ))}
        </select>
      </div>

      {filas === null ? <div style={{ fontSize:12.5, color:C.t3 }}>Cargando...</div>
      : !vista.length ? (
        <div style={{ fontSize:12.5, color:C.t3, padding:'20px 0', textAlign:'center' }}>
          Aun no hay insumos asignados a mantenimientos.
          <div style={{ fontSize:11.5, color:C.t4, marginTop:4 }}>
            Se registran desde la ficha del equipo, distribuyendo los renglones de la factura.
          </div>
        </div>
      ) : (
        <div style={{ display:'grid', gap:4 }}>
          <div style={{ display:'flex', gap:10, padding:'0 12px', fontSize:10.5, fontWeight:800,
            letterSpacing:'.06em', textTransform:'uppercase', color:C.t4 }}>
            <div style={{ flex:'1 1 220px' }}>Insumo</div>
            <div style={{ minWidth:170 }}>Cliente</div>
            <div style={{ minWidth:60, textAlign:'right' }}>Veces</div>
            <div style={{ minWidth:70, textAlign:'right' }}>Cantidad</div>
            <div style={{ minWidth:90, textAlign:'right' }}>Total</div>
          </div>
          {vista.map((f,i)=>(
            <div key={i} style={{ display:'flex', gap:10, alignItems:'center', background:C.bg2,
              border:`1px solid ${C.border}`, borderRadius:R.md, padding:'8px 12px' }}>
              <div style={{ flex:'1 1 220px', minWidth:0 }}>
                <div style={{ fontSize:12.5, color:C.t1, fontWeight:600 }}>{f.descripcion}</div>
                <div style={{ fontSize:11, color:C.t4 }}>
                  {f.item_code || 'sin codigo'}
                  {f.clase === 'mano_obra' ? ' · mano de obra' : ''}
                  {f.ultima_vez ? ` · ultima vez ${f.ultima_vez}` : ''}
                </div>
              </div>
              <div style={{ minWidth:170, fontSize:12, color:C.t2 }}>{f.cliente || '—'}</div>
              <div style={{ minWidth:60, textAlign:'right', fontSize:12, color:C.t2 }}>{f.veces}</div>
              <div style={{ minWidth:70, textAlign:'right', fontSize:12, color:C.t2 }}>{Number(f.cantidad)}</div>
              <div style={{ minWidth:90, textAlign:'right', fontSize:12.5, fontWeight:700, color:C.t1 }}>
                {dolar(f.total_usd || 0)}
              </div>
            </div>
          ))}
          <div style={{ display:'flex', justifyContent:'flex-end', gap:8, paddingTop:6,
            fontSize:13, fontWeight:800, color:C.t1 }}>
            Total: {dolar(total)}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HISTORIAL DE UNA MAQUINA ─────────────────────────────────────────────────
// Todos los servicios que se le hicieron, y dentro de cada uno lo que llevo.
// Es lo que permite contestar "cuanto me cuesta mantener este montacargas".
function MantenimientosDelEquipo({ eq, usuario }) {
  const [mants, setMants] = useState(null);
  const [abierto, setAbierto] = useState(null);
  const [totales, setTotales] = useState({});

  const cargar = useCallback(async () => {
    const { data } = await supabase.from('mantenimientos')
      .select('*').eq('equipo_cliente_id', eq.id).order('fecha', { ascending:false }).limit(60);
    setMants(data || []);
    const ids = (data || []).map(x => x.id);
    if (!ids.length) { setTotales({}); return; }
    const { data: ins } = await supabase.from('mantenimiento_insumos')
      .select('mantenimiento_id,total_usd,cantidad').in('mantenimiento_id', ids);
    const t = {};
    (ins || []).forEach(x => {
      const k = x.mantenimiento_id;
      if (!t[k]) t[k] = { usd:0, n:0 };
      t[k].usd += Number(x.total_usd || 0); t[k].n += 1;
    });
    setTotales(t);
  }, [eq?.id]);
  useEffect(() => { if (eq?.id) cargar(); }, [eq?.id, cargar]);

  if (mants === null) return <div style={{ fontSize:12.5, color:C.t3 }}>Cargando historial...</div>;
  if (!mants.length) return (
    <div style={{ fontSize:12.5, color:C.t3 }}>
      Sin servicios registrados para este equipo.
    </div>
  );

  const granTotal = Object.values(totales).reduce((a, x) => a + x.usd, 0);

  return (
    <div style={{ display:'grid', gap:6 }}>
      {mants.map(m => {
        const t = totales[m.id] || { usd:0, n:0 };
        const ab = abierto === m.id;
        return (
          <div key={m.id} style={{ border:`1px solid ${C.border}`, borderRadius:R.md, overflow:'hidden' }}>
            <div onClick={()=>setAbierto(ab ? null : m.id)}
              style={{ display:'flex', gap:10, alignItems:'center', padding:'9px 12px',
                cursor:'pointer', background: ab ? C.bg1 : C.bg2 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12.5, fontWeight:600, color:C.t1 }}>
                  {m.tipo || 'Servicio'}{m.correlativo ? ` · ${m.correlativo}` : ''}
                </div>
                <div style={{ fontSize:11, color:C.t4 }}>
                  {m.fecha || 'sin fecha'}
                  {m.horas_equipo ? ` · ${Number(m.horas_equipo).toLocaleString()} h` : ''}
                  {m.factura_num ? ` · factura ${m.factura_num}` : ' · sin factura'}
                  {t.n ? ` · ${t.n} insumo${t.n>1?'s':''}` : ''}
                </div>
              </div>
              <div style={{ fontSize:12.5, fontWeight:700, color: t.usd ? C.t1 : C.t4 }}>
                {t.usd ? dolar(t.usd) : '—'}
              </div>
              <span style={{ fontSize:11, color:C.t4 }}>{ab ? '▲' : '▼'}</span>
            </div>
            {ab && (
              <div style={{ padding:'12px', background:C.bg1, borderTop:`1px solid ${C.border}` }}>
                <InsumosDelMantenimiento mant={m} usuario={usuario} onCambio={cargar} />
              </div>
            )}
          </div>
        );
      })}
      {granTotal > 0 && (
        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, fontSize:12.5,
          fontWeight:800, color:C.t1, paddingTop:4 }}>
          Costo acumulado del equipo: {dolar(granTotal)}
        </div>
      )}
    </div>
  );
}

function Rentas({ clientes, equipos, empresaActiva }) {
  const [rentas, setRentas]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filtro, setFiltro]       = useState('activa');
  const [filtroEmpresa, setFiltroEmpresa] = useState([]); // multi-select por empresa (desacoplado del HN/CR)
  const [modalNueva, setModalNueva] = useState(false);
  const [modalDoc, setModalDoc]   = useState(null);   // equipo en_renta sin registro → completar info
  const [docIds, setDocIds]       = useState(null);   // set de equipo_id con renta activa (para detectar sin documentar)
  const [sel, setSel]             = useState(null);
  const [confirmFin, setConfirmFin] = useState(null);
  const [bajandoOT, setBajandoOT] = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  // Se dice 'mtrental' y no 'honduras': esa palabra ya significa Monhaco del
  // lado de las facturas de SAP (ver sql/renombrar-mtrental.sql).
  const EMP_LBL = { mtrental:'MT Rental', monhaco:'Monhaco', monhagro:'Monhagro', costarica:'Costa Rica' };

  const cargar = async () => {
    setLoading(true);
    // Operaciones (Rentas) NO se ata al HN/CR de arriba: carga todo y se filtra por
    // empresa aca abajo (mismo criterio que Flota).
    let q = supabase.from('rentas').select('*,clientes(empresa,contacto,telefono,email),equipos(codigo,modelo,tipo_equipo,subtipo,combustible,capacidad_kg,empresa_id)').order('fecha_inicio',{ascending:false});
    if (filtro!=='todas') q=q.eq('estado',filtro);
    const qa = supabase.from('rentas').select('equipo_id').eq('estado','activa');
    const [{data},{data:act}] = await Promise.all([q, qa]);
    setRentas(data||[]); setDocIds(new Set((act||[]).map(r=>r.equipo_id))); setLoading(false);
  };
  useEffect(()=>{cargar();},[filtro]); // eslint-disable-line

  // Filtro por empresa (client-side sobre lo cargado).
  // La tabla rentas NO tiene empresa_id; la empresa se deriva del equipo (como en Flota).
  const rentasFiltradas = filtroEmpresa.length ? rentas.filter(r=>filtroEmpresa.includes(r.equipos?.empresa_id)) : rentas;
  const empIdsR = new Set((equipos||[]).map(e=>e.empresa_id).filter(Boolean));
  const empresasOpts = ['mtrental','monhaco','monhagro', ...(empIdsR.has('costarica')?['costarica']:[])].map(id=>({ value:id, label:EMP_LBL[id]||id }));

  // Equipos marcados "en renta" (por Miguel en Flota) que NO tienen registro de
  // renta todavia → el vendedor debe completar cliente/valor/fechas/docs.
  const eqEmpresa = filtroEmpresa.length ? (equipos||[]).filter(e=>filtroEmpresa.includes(e.empresa_id)) : (equipos||[]);
  const sinDocumentar = docIds ? eqEmpresa.filter(e=>e.estado==='en_renta' && !docIds.has(e.id)) : [];

  const eliminar = async (r) => {
    const { error } = await supabase.from('rentas').delete().eq('id', r.id);
    if (error) { toast('No se pudo eliminar la renta: '+error.message); return; }
    if (r.equipo_id && r.estado === 'activa') await supabase.from('equipos').update({estado:'disponible',asignado_a:null}).eq('id',r.equipo_id);
    setConfirmEliminar(null); setSel(null); cargar();
  };

  const terminar = async (r) => {
    const { error } = await supabase.from('rentas').update({estado:'finalizada',fecha_fin:new Date().toISOString().split('T')[0]}).eq('id',r.id);
    if (error) { toast('No se pudo finalizar la renta: '+error.message); return; }
    if (r.equipo_id) await supabase.from('equipos').update({estado:'disponible',asignado_a:null}).eq('id',r.equipo_id);
    await supabase.from('actividades').insert({tipo:'renta',titulo:'Renta finalizada',descripcion:`${r.equipos?.codigo} → ${r.clientes?.empresa}`,fecha_actividad:new Date().toISOString()});
    setConfirmFin(null); setSel(null); cargar();
  };

  // Fechas 'YYYY-MM-DD' parseadas como medianoche LOCAL (sin T00:00:00 serían UTC:
  // en Honduras eso corría todo un día y marcaba "Vencida" antes de tiempo).
  const _dLocal = s => new Date(String(s).slice(0,10)+'T00:00:00');
  // Nunca negativo: una renta que arranca hoy lleva 0 dias, no -1. Ese -1 hacia
  // que el ingreso estimado saliera en negativo.
  const diasActiva = r => r.fecha_inicio ? Math.max(0, Math.floor((Date.now()-_dLocal(r.fecha_inicio))/86400000)) : 0;
  // Cuantos dias cubre UN cobro. valor_mensual guarda el valor del periodo, que
  // no siempre es un mes: dividir todo entre 30 subestimaba una renta semanal a
  // la cuarta parte de lo que de verdad factura.
  const DIAS_PERIODO = { 'por hora':1, diaria:1, semanal:7, quincenal:15, mensual:30, 'por proyecto':30 };
  const diasDe = r => DIAS_PERIODO[String(r.tipo_cobro||'').toLowerCase()] || 30;
  const SUFIJO = { 'por hora':'/hora', diaria:'/día', semanal:'/semana', quincenal:'/quincena', mensual:'/mes', 'por proyecto':' proyecto' };
  const sufijoDe = r => SUFIJO[String(r.tipo_cobro||'').toLowerCase()] || '/mes';
  const ingresoAprox = r => (r.valor_mensual||r.valor||0) * (diasActiva(r)/diasDe(r));
  const diasParaVencer = r => r.fecha_fin ? Math.ceil((_dLocal(r.fecha_fin)-Date.now())/86400000) : null;

  const activas   = rentasFiltradas.filter(r=>r.estado==='activa');
  const ingresos  = activas.reduce((s,r)=>s+(r.valor_mensual||r.valor||0),0);
  const porVencer = activas.filter(r=>{ const d=diasParaVencer(r); return d!==null&&d<=30&&d>=0; });
  const diasParaVencerFactura = r => r.fecha_fin_factura ? Math.ceil((_dLocal(r.fecha_fin_factura)-Date.now())/86400000) : null;
  const proximasRefacturar = activas.filter(r=>{ const d=diasParaVencerFactura(r); return d!==null&&d>=0&&d<=5; });

  // Chip por-vencer con tripleta + dot (receta 7.5), sin emojis.
  const TagVencer = ({r}) => {
    const d = diasParaVencer(r);
    if (d===null) return null;
    const [txt,tone] = d<0 ? [`Vencida ${Math.abs(d)}d`,'red']
      : d<=7  ? [`Vence en ${d}d`,'red']
      : d<=30 ? [`Vence en ${d}d`,'orange']
      : [`${d}d restantes`,'green'];
    const t = TONES[tone];
    return <span style={{display:'inline-flex',alignItems:'center',gap:5,background:t.tint,color:t.text,border:`1px solid ${t.border}`,borderRadius:R.pill,padding:'2px 8px',fontSize:10.5,fontWeight:600,whiteSpace:'nowrap'}}><span style={{width:6,height:6,borderRadius:'50%',background:t.solid,flexShrink:0}}/>{txt}</span>;
  };

  return (
    <div style={{flex:1,display:'flex',overflow:'hidden',background:C.bg1}}>
      {/* Lista */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {/* Stats + toolbar — flotan sobre gris, sin franja blanca */}
        <div style={{padding:'14px 16px 4px',flexShrink:0}}>
          {filtro==='activa'&&activas.length>0&&(
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12,marginBottom:12}}>
              <KpiCard label="Rentas activas"  value={activas.length}  sub="contratos vigentes" icon={KI.box}    tone="info"/>
              <KpiCard label="Ingresos / mes"  value={fmt(ingresos)}   sub="MRR de rentas"      icon={KI.dollar} tone="green"/>
              {porVencer.length>0&&<KpiCard label="Por vencer (≤30d)" value={porVencer.length} sub="requieren renovación" icon={KI.clock} tone="orange"/>}
            </div>
          )}
          {proximasRefacturar.length>0&&(
            <div style={{marginBottom:12,padding:'11px 14px',background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.md}}>
              <div style={{fontSize:12,fontWeight:700,color:C.orangeText,marginBottom:6,display:'flex',alignItems:'center',gap:7}}><CIcon d={CI.clock} size={14}/>{proximasRefacturar.length} renta{proximasRefacturar.length>1?'s':''} por refacturar en ≤5 días</div>
              {proximasRefacturar.map(r=>{
                const d=diasParaVencerFactura(r);
                return (
                  <div key={r.id} style={{fontSize:11.5,color:C.t2,marginBottom:2,paddingLeft:21}}>
                    <strong>{r.clientes?.empresa}</strong> — {r.equipos?.codigo} — vence {d===0?'hoy':`en ${d}d`} ({fecha(r.fecha_fin_factura)})
                  </div>
                );
              })}
            </div>
          )}
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <Seg items={[['activa','Activas'],['finalizada','Finalizadas'],['todas','Todas']]} value={filtro} onChange={v=>{setFiltro(v);setSel(null);}}/>
            <MultiSelect label="Empresa" value={filtroEmpresa} onChange={setFiltroEmpresa} options={empresasOpts} width={180}/>
            <div style={{marginLeft:'auto'}}><Btn onClick={()=>setModalNueva(true)} style={{display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>}/>Nueva Renta</Btn></div>
          </div>
        </div>

        <div style={{flex:1,overflowY:'auto',padding:16}}>
          {filtro==='activa' && sinDocumentar.length>0 && (
            <div style={{marginBottom:14,background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.lg,overflow:'hidden'}}>
              <div style={{padding:'11px 14px',borderBottom:`1px solid ${C.orangeBorder}`,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                <CIcon d={KI.tool} size={14}/>
                <span style={{fontSize:13,fontWeight:800,color:C.orangeText}}>En renta sin documentar ({sinDocumentar.length})</span>
                <span style={{fontSize:11.5,color:C.orangeText}}>equipos marcados en renta en Flota — falta cliente, valor y documentos</span>
              </div>
              <div style={{display:'grid',gap:8,padding:12}}>
                {sinDocumentar.map(e=>(
                  <div key={e.id} style={{display:'flex',alignItems:'center',gap:12,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'10px 12px'}}>
                    <EquipIcon e={e} size={34} icon={17}/>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{fontSize:14,fontWeight:700,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{[e.codigo,e.modelo].filter(Boolean).join(' — ')}</div>
                      <div style={{fontSize:11.5,color:C.orangeText,fontWeight:600}}>Faltante de información</div>
                    </div>
                    <Btn onClick={()=>setModalDoc(e)} style={{whiteSpace:'nowrap'}}>Completar información</Btn>
                  </div>
                ))}
              </div>
            </div>
          )}
          {loading?<div style={{textAlign:'center',color:C.t4,padding:40}}>Cargando...</div>
          :rentasFiltradas.length===0?<div style={{textAlign:'center',color:C.t4,padding:60,fontSize:14}}>{sinDocumentar.length>0?'':'Sin rentas '+(filtro!=='todas'?filtro+'s':'')}</div>
          :<div style={{display:'grid',gap:8}}>
            {rentasFiltradas.map(r=>{
              const da=diasActiva(r);
              const isSelected=sel?.id===r.id;
              return (
                <div key={r.id} className="radar-card" onClick={()=>setSel(isSelected?null:r)}
                  style={{background:isSelected?C.primaryTint:C.bg2,border:`1px solid ${isSelected?C.primaryBorder:C.border}`,borderLeft:isSelected?`3px solid ${C.blue}`:`1px solid ${C.border}`,borderRadius:R.lg,padding:16,cursor:'pointer',boxShadow:SH.sm}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8,gap:12}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,minWidth:0,flex:1}}>
                      <EquipIcon e={r.equipos} size={36} icon={18}/>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:15,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,marginBottom:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{r.clientes?.empresa||'—'}</div>
                        <div style={{fontSize:12,color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{[r.equipos?.codigo,r.equipos?.modelo].filter(Boolean).join(' — ')}</div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:5}}>
                          {(r.equipos?.subtipo||r.equipos?.tipo_equipo)&&<Pill t={r.equipos?.subtipo?titleCase(r.equipos.subtipo):labelTipo(r.equipos)} c={C.indigo}/>}
                          {capTxt(r.equipos?.capacidad_kg)&&<Pill t={capTxt(r.equipos.capacidad_kg)} c={C.t3}/>}
                          {r.equipos?.combustible&&<Pill t={titleCase(r.equipos.combustible)} c={combColor(r.equipos.combustible)}/>}
                        </div>
                      </div>
                    </div>
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <div className="radar-num" style={{fontSize:18,fontWeight:700,letterSpacing:'-0.02em',color:C.greenText}}>{fmt(r.valor_mensual||r.valor||0)}<span style={{fontSize:11,color:C.t4,fontWeight:400}}>/mes</span></div>
                      <div style={{marginTop:4}}><TagVencer r={r}/></div>
                    </div>
                  </div>
                  <div style={{display:'flex',gap:14,fontSize:11,color:C.t4,flexWrap:'wrap',borderTop:`1px solid ${C.bg3}`,paddingTop:8}}>
                    <span style={{display:'inline-flex',alignItems:'center',gap:5}}><CIcon d={CI.calendar} size={12}/>Inicio {fecha(r.fecha_inicio)}</span>
                    {r.fecha_fin&&<span>Fin {fecha(r.fecha_fin)}</span>}
                    {r.tipo_cobro&&<span style={{display:'inline-flex',alignItems:'center',gap:5}}><CIcon d={CI.cash} size={12}/>{titleCase(r.tipo_cobro)}</span>}
                    {r.estado==='activa'&&<span style={{color:C.info,display:'inline-flex',alignItems:'center',gap:5}}><CIcon d={CI.clock} size={12}/>{da}d activa</span>}
                    {r.estado==='activa'&&<span className="radar-num" style={{color:C.greenText,fontWeight:700}}>{fmt((r.valor_mensual||0)*(da/30))} facturado aprox.</span>}
                  </div>
                  {r.notas&&<div style={{fontSize:11,color:C.t4,marginTop:6,fontStyle:'italic'}}>{r.notas}</div>}
                </div>
              );
            })}
          </div>}
        </div>
      </div>

      {/* Detail panel */}
      {sel&&(
        <div style={{width:320,borderLeft:`1px solid ${C.border}`,background:C.bg2,overflowY:'auto',padding:20,flexShrink:0}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div style={{fontSize:14,fontWeight:800,color:C.t1}}>Detalle de renta</div>
            <button onClick={()=>setSel(null)} style={{background:'none',border:'none',color:C.t4,fontSize:20,cursor:'pointer',lineHeight:1}}>×</button>
          </div>
          <div className="radar-num" style={{fontSize:24,fontWeight:700,letterSpacing:'-0.025em',color:C.greenText,marginBottom:4}}>{fmt(sel.valor_mensual||sel.valor||0)}<span style={{fontSize:12,color:C.t4,fontWeight:400}}>{sufijoDe(sel)}</span></div>
          <div style={{fontSize:13,fontWeight:700,color:C.t1,marginBottom:2}}>{sel.clientes?.empresa}</div>
          {sel.clientes?.contacto&&<div style={{fontSize:12,color:C.t3,marginBottom:2,display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={CI.user} size={12}/>{sel.clientes.contacto}</div>}
          {sel.clientes?.telefono&&<div style={{fontSize:12,color:C.t3,marginBottom:2,display:'flex',alignItems:'center',gap:6}}><CIcon d={CI.phone} size={12}/>{sel.clientes.telefono}</div>}
          {sel.clientes?.email&&<div style={{fontSize:12,color:C.t3,marginBottom:12,display:'flex',alignItems:'center',gap:6}}><CIcon d={CI.send} size={12}/>{sel.clientes.email}</div>}

          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginBottom:12}}>
            {[['Equipo',`${sel.equipos?.codigo} — ${sel.equipos?.modelo}`],['Inicio',fecha(sel.fecha_inicio)],['Fin pactado',sel.fecha_fin?fecha(sel.fecha_fin):'Sin fecha fin'],['Tipo cobro',sel.tipo_cobro?titleCase(sel.tipo_cobro):'—'],['Días activa',`${diasActiva(sel)}d`],['Ingreso aprox.',fmt(ingresoAprox(sel))]].map(([l,v])=>(
              <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:`1px solid ${C.border}`,fontSize:12}}>
                <span style={{color:C.t4}}>{l}</span>
                <span style={{color:C.t1,fontWeight:600}}>{v}</span>
              </div>
            ))}
          </div>

          {sel.notas&&<div style={{background:C.bg3,borderRadius:8,padding:10,marginBottom:12,fontSize:12,color:C.t2,lineHeight:1.5}}>{sel.notas}</div>}

          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {/* Reimprimir la OT. El papel se pierde o el cliente pide copia; si
                para eso hay que entrar a SAP, se pierde lo que se gano poniendo
                el PDF en RADAR. */}
            {sel.orden_sap&&(
              <Btn full variant="secondary" size="sm" disabled={bajandoOT}
                onClick={async()=>{
                  setBajandoOT(true);
                  try {
                    const r = await fetch('/api/sap',{method:'POST',headers:{'Content-Type':'application/json'},
                      body:JSON.stringify({action:'pdf_orden_trabajo',empresa_id:sel.empresa_id||'honduras',docNum:sel.orden_sap,rentaId:sel.id})});
                    const d = await r.json();
                    if (!d.ok || !d.pdf?.base64) { toast('No se pudo generar el PDF: '+(d.error||'sin detalle')); }
                    else {
                      const a=document.createElement('a');
                      a.href=`data:application/pdf;base64,${d.pdf.base64}`;
                      a.download=`Orden-de-trabajo-${sel.orden_sap}.pdf`;
                      document.body.appendChild(a); a.click(); a.remove();
                    }
                  } catch(e) { toast('No se pudo generar el PDF: '+e.message); }
                  setBajandoOT(false);
                }}
                style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,marginBottom:8}}>
                <CIcon d={CI.printer}/>{bajandoOT?'Generando…':`Orden de trabajo ${sel.orden_sap}`}</Btn>
            )}
            {sel.estado==='activa'&&<Btn full variant="warn" size="sm" onClick={()=>setConfirmFin(sel)} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6}}><CIcon d={<rect x="6" y="6" width="12" height="12" rx="1"/>}/>Finalizar esta renta</Btn>}
            <Btn full variant="danger" size="sm" onClick={()=>setConfirmEliminar(sel)} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6}}><CIcon d={<><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></>}/>Eliminar renta</Btn>
          </div>
        </div>
      )}

      {/* Confirm eliminar */}
      <Modal open={!!confirmEliminar} onClose={()=>setConfirmEliminar(null)} title="Eliminar Renta" width={400}>
        <div style={{fontSize:14,color:C.t2,marginBottom:20,lineHeight:1.6}}>
          ¿Confirmás que querés <strong style={{color:C.red}}>eliminar permanentemente</strong> la renta de <strong style={{color:C.t1}}>{confirmEliminar?.clientes?.empresa}</strong>?<br/>
          Esta acción no se puede deshacer.
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" onClick={()=>setConfirmEliminar(null)}>Cancelar</Btn>
          <Btn variant="danger" onClick={()=>eliminar(confirmEliminar)}>Sí, eliminar</Btn>
        </div>
      </Modal>

      {/* Confirm finalizar */}
      <Modal open={!!confirmFin} onClose={()=>setConfirmFin(null)} title="Finalizar Renta" width={400}>
        <div style={{fontSize:14,color:C.t2,marginBottom:20,lineHeight:1.6}}>
          ¿Confirmás que querés finalizar la renta de <strong style={{color:C.t1}}>{confirmFin?.clientes?.empresa}</strong>?<br/>
          El equipo <strong style={{color:C.t1}}>{confirmFin?.equipos?.codigo}</strong> volverá a estado Disponible.
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" onClick={()=>setConfirmFin(null)}>Cancelar</Btn>
          <Btn variant="danger" onClick={()=>terminar(confirmFin)}>Sí, finalizar</Btn>
        </div>
      </Modal>

      <Modal open={!!modalDoc} onClose={()=>setModalDoc(null)} title="Completar información de renta" width={520}>
        {modalDoc&&<FormRenta equipoFijo={modalDoc} documentar clientes={clientes} equipos={equipos} empresaActiva={empresaActiva} onSave={()=>{setModalDoc(null);cargar();}} onClose={()=>setModalDoc(null)}/>}
      </Modal>
      <Modal open={modalNueva} onClose={()=>setModalNueva(false)} title="Nueva Renta" width={520}>
        <FormRenta clientes={clientes} equipos={equipos} empresaActiva={empresaActiva} onSave={()=>{setModalNueva(false);cargar();}} onClose={()=>setModalNueva(false)}/>
      </Modal>
    </div>
  );
}

// ─── MODAL VISITA / LLAMADA ───────────────────────────────────────────────────
function ModalVisita({ open, onClose, usuario, clientes = [], prefill = null, empresaActiva = null }) {
  const [tab, setTab]               = useState('visita');
  const [saving, setSaving]         = useState(false);
  const [gps, setGps]               = useState(null);
  const [gpsStatus, setGpsStatus]   = useState('');
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteSearchLive, setClienteSearchLive] = useState(null);
  const [showList, setShowList]     = useState(false);
  const [f, setF] = useState({
    cliente_id:'', empresa:'', contacto:'', telefono:'',
    estado_visita:'', equipo_interes:'', valor_estimado:'', observaciones:'', proxima_accion:'',
  });

  useEffect(() => {
    if (open && prefill) {
      setF(x => ({ ...x, cliente_id: prefill.id||'', empresa: prefill.empresa||'', contacto: prefill.contacto||'', telefono: prefill.telefono||'' }));
      setClienteSearch(prefill.empresa||'');
    }
  }, [open, prefill]);

  useEffect(() => {
    if (clienteSearch.length < 3) { setClienteSearchLive(null); return; }
    const b = clienteSearch.replace(/[%_]/g, '\\$&');
    const t = setTimeout(async () => {
      let qr = supabase.from('clientes').select('id,empresa,contacto,ciudad,telefono,vendedor').neq('estado','inactivo')
        .or(`empresa.ilike.%${b}%,contacto.ilike.%${b}%`).order('empresa').limit(15);
      if (empresaActiva) qr = qr.eq('empresa_id', empresaActiva);
      const { data } = await qr;
      setClienteSearchLive(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [clienteSearch, empresaActiva]);

  const ESTADOS_VISITA  = ['Interesado','Cotización enviada','Contrato activo','Sin interés','No estaba'];
  const ESTADOS_LLAMADA = ['Interesado','Cotización enviada','Contrato activo','Sin interés','No contestó','Buzón de voz'];
  const ESTADOS = tab === 'visita' ? ESTADOS_VISITA : ESTADOS_LLAMADA;
  const ECOLOR  = { 'Interesado':C.green, 'Cotización enviada':C.orange, 'Contrato activo':C.blue, 'Sin interés':C.red, 'No estaba':C.t4, 'No contestó':C.t4, 'Buzón de voz':C.t4 };
  const PROXIMAS = ['Llamada de seguimiento','Enviar cotización','Visita de seguimiento','Coordinar demo de equipo','Ninguna por ahora'];

  const reset = () => {
    setF({ cliente_id:'', empresa:'', contacto:'', telefono:'', estado_visita:'', equipo_interes:'', valor_estimado:'', observaciones:'', proxima_accion:'' });
    setGps(null); setGpsStatus(''); setClienteSearch(''); setTab('visita');
  };

  const handleClose = () => { reset(); onClose(); };

  const capturarGps = () => {
    if (!navigator.geolocation) { setGpsStatus('GPS no disponible en este dispositivo'); return; }
    setGpsStatus('Obteniendo ubicación...');
    navigator.geolocation.getCurrentPosition(
      pos => {
        const lat = pos.coords.latitude.toFixed(6);
        const lng = pos.coords.longitude.toFixed(6);
        setGps({ lat, lng });
        setGpsStatus(`✅ ${lat}, ${lng}`);
      },
      err => {
        if (err.code === 1) setGpsStatus('⚠️ Permiso denegado — activá ubicación en Configuración del navegador');
        else if (err.code === 2) setGpsStatus('⚠️ Ubicación no disponible — intentá de nuevo');
        else setGpsStatus('⚠️ Tiempo agotado — intentá de nuevo');
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  };

  const clientesFiltrados = clienteSearchLive !== null
    ? clienteSearchLive
    : clientes.filter(c =>
        !clienteSearch ||
        (c.empresa||'').toLowerCase().includes(clienteSearch.toLowerCase()) ||
        (c.contacto||'').toLowerCase().includes(clienteSearch.toLowerCase())
      ).slice(0, 8);

  const selCliente = c => {
    setF(x => ({ ...x, cliente_id: c.id, empresa: c.empresa, contacto: c.contacto || x.contacto, telefono: c.telefono || x.telefono }));
    setClienteSearch(c.empresa);
    setShowList(false);
  };

  const guardar = async () => {
    if (!f.empresa || !f.estado_visita || !f.observaciones) return;
    setSaving(true);
    const hoy = new Date().toISOString();
    const hoyFecha = hoy.split('T')[0];

    // D6 (Sprint 1): la visita se inserta con .select() para conocer su id y
    // poder atribuirle la oportunidad que nazca de ella (source_visit_id).
    const { data: visitaRow } = await supabase.from('visitas').insert({
      tipo: tab,
      vendedor: usuario.nombre,
      cliente_id: f.cliente_id || null,
      empresa: f.empresa,
      contacto: f.contacto || null,
      telefono: f.telefono || null,
      estado_visita: f.estado_visita,
      equipo_interes: f.equipo_interes || null,
      valor_estimado: f.valor_estimado ? parseFloat(f.valor_estimado) : null,
      observaciones: f.observaciones,
      proxima_accion: f.proxima_accion || null,
      latitud:  gps ? parseFloat(gps.lat) : null,
      longitud: gps ? parseFloat(gps.lng) : null,
      maps_url: gps ? `https://maps.google.com/?q=${gps.lat},${gps.lng}` : null,
    }).select('id').single();

    await supabase.from('actividades').insert({
      tipo: tab,
      titulo: `${tab === 'visita' ? '📍 Visita' : '📞 Llamada'}: ${f.empresa}`,
      descripcion: `${f.estado_visita}${f.equipo_interes ? ` — ${f.equipo_interes}` : ''}${f.valor_estimado ? ` — $${f.valor_estimado}` : ''}. ${f.observaciones}`,
      fecha_actividad: hoy,
    });

    if (['Interesado','Cotización enviada'].includes(f.estado_visita)) {
      await supabase.from('oportunidades').insert({
        titulo: `${f.equipo_interes || 'Oportunidad'} — ${f.empresa}`,
        cliente: f.empresa,
        cliente_id: f.cliente_id || null,
        vendedor: usuario.nombre,
        valor: f.valor_estimado ? parseFloat(f.valor_estimado) : 0,
        etapa: f.estado_visita === 'Cotización enviada' ? 'cotizacion' : 'contactado',
        notas: f.observaciones,
        ultimo_seguimiento: hoyFecha,
        // D6: atribucion visita -> oportunidad. Con esto la cadena
        // VISITAS -> OPORTUNIDADES -> BOOKINGS se vuelve medible con datos.
        origen: 'visita',
        source_visit_id: visitaRow?.id || null,
      });
    }

    if (f.cliente_id) {
      const cambios = { ultima_actividad: hoy };
      // REGISTRAR UNA LLAMADA TAMBIEN ES TOMAR AL CLIENTE.
      // Antes esto solo tocaba ultima_actividad. Anthony llamo a AEROSOLES
      // FORMULACIONES desde el CRM, quedo registrado, y el cliente siguio a
      // nombre de una persona que ya no trabaja aca: en la lista lo seguia
      // viendo como ajeno y en el plan seguia saliendo como libre. El "Tomar y
      // registrar" del Plan de Octubre si lo hacia; esta pantalla no.
      //
      // Un cliente de alguien que renuncio cuenta como libre, igual que alla.
      const cli = (clientes || []).find(c => c.id === f.cliente_id);
      const duenio = cli?.vendedor || null;
      let libre = !duenio;
      if (duenio && duenio !== usuario.nombre) {
        const { data: act } = await supabase.from('radar_usuarios')
          .select('nombre').eq('nombre', duenio).eq('activo', true).maybeSingle();
        libre = !act;
      }
      if (libre && duenio !== usuario.nombre) {
        cambios.vendedor = usuario.nombre;
        cambios.vendedor_anterior = duenio;
        cambios.vendedor_manual_at = new Date().toISOString();
        cambios.vendedor_manual_por = usuario.nombre;
      }
      const { error: eCli } = await supabase.from('clientes').update(cambios).eq('id', f.cliente_id).select('id');
      if (eCli) toast('La visita quedo guardada, pero no se pudo actualizar el cliente: ' + eCli.message);
      else if (cambios.vendedor) toast(`"${cli?.empresa || 'El cliente'}" quedo a tu nombre.`);
    }

    setSaving(false);
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={tab === 'visita' ? '📍 Registrar Visita' : '📞 Registrar Llamada'} width={540}>
      {/* Tabs */}
      <div style={{display:'flex',gap:4,marginBottom:20,background:C.bg3,borderRadius:10,padding:4}}>
        {[['visita','📍 Visita'],['llamada','📞 Llamada']].map(([id,lbl])=>(
          <button key={id} onClick={()=>{setTab(id);setF(x=>({...x,estado_visita:''}));}}
            style={{flex:1,padding:'9px',background:tab===id?C.bg2:'transparent',color:tab===id?C.t1:C.t4,
              border:tab===id?`1px solid ${C.border}`:'none',borderRadius:7,fontSize:13,fontWeight:700,cursor:'pointer'}}>
            {lbl}
          </button>
        ))}
      </div>

      {/* Empresa / cliente search */}
      <div style={{marginBottom:14,position:'relative'}}>
        <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:5}}>Empresa *</div>
        <input
          value={clienteSearch || f.empresa}
          onChange={e=>{ const v=e.target.value; setClienteSearch(v); setF(x=>({...x,empresa:v,cliente_id:''})); setShowList(true); }}
          onFocus={()=>setShowList(true)}
          placeholder="Buscar cliente existente o escribir nuevo..."
          style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px',color:C.t1,fontSize:14,outline:'none'}}
        />
        {showList && clientesFiltrados.length > 0 && (
          <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:300,background:C.bg2,border:`1px solid ${C.blue}`,borderRadius:8,marginTop:4,boxShadow:'0 8px 32px rgba(0,0,0,.4)',maxHeight:200,overflowY:'auto'}}>
            {clientesFiltrados.map(c=>(
              <div key={c.id} onClick={()=>selCliente(c)}
                style={{padding:'10px 14px',cursor:'pointer',fontSize:13,color:C.t1,borderBottom:`1px solid ${C.border}`}}
                onMouseEnter={e=>e.currentTarget.style.background=C.bg3}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <span style={{fontWeight:700}}>{c.empresa}</span>
                {c.contacto&&<span style={{color:C.t4,marginLeft:8,fontSize:11}}>{c.contacto}</span>}
              </div>
            ))}
            <div onClick={()=>setShowList(false)}
              style={{padding:'8px 14px',cursor:'pointer',fontSize:12,color:C.t4,textAlign:'center',fontStyle:'italic'}}>
              ✏️ Usar "{clienteSearch||f.empresa}" como cliente nuevo
            </div>
          </div>
        )}
      </div>

      {/* Contacto + Teléfono */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
        <Inp label="Contacto que atendió" value={f.contacto} onChange={v=>setF(x=>({...x,contacto:v}))} placeholder="Nombre y cargo"/>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:5}}>Teléfono</div>
          <div style={{display:'flex',gap:6}}>
            <input value={f.telefono} onChange={e=>setF(x=>({...x,telefono:e.target.value}))} placeholder="9888-0000"
              style={{flex:1,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px',color:C.t1,fontSize:14,outline:'none'}}/>
            {tab==='llamada'&&f.telefono&&(
              <a href={`tel:${f.telefono.replace(/[^0-9+]/g,'')}`}
                style={{background:'linear-gradient(135deg,#0D5F58,#0F766E)',borderRadius:8,padding:'10px 14px',display:'flex',alignItems:'center',justifyContent:'center',textDecoration:'none',fontSize:18,flexShrink:0}}>
                📞
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Estado pills */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>Estado de la {tab} *</div>
        <div style={{display:'flex',flexWrap:'wrap',gap:7}}>
          {ESTADOS.map(e=>{
            const color = ECOLOR[e]||C.blue;
            const sel   = f.estado_visita===e;
            return (
              <button key={e} onClick={()=>setF(x=>({...x,estado_visita:e}))}
                style={{padding:'7px 14px',borderRadius:24,border:`1.5px solid ${sel?color:C.border}`,
                  background:sel?`${color}22`:'transparent',color:sel?color:C.t4,
                  fontSize:12,fontWeight:700,cursor:'pointer',transition:'all .15s'}}>
                {e}
              </button>
            );
          })}
        </div>
      </div>

      {/* Oportunidad */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
        <Inp label="Equipo / Oportunidad" value={f.equipo_interes} onChange={v=>setF(x=>({...x,equipo_interes:v}))} placeholder="ej: 2 tijeras GS-1930"/>
        <Inp label="Valor estimado (USD)" type="number" value={f.valor_estimado} onChange={v=>setF(x=>({...x,valor_estimado:v}))} placeholder="0"/>
      </div>

      <Inp label="Observaciones *" value={f.observaciones} onChange={v=>setF(x=>({...x,observaciones:v}))} placeholder="¿Qué se habló? ¿Qué queda pendiente?" rows={3} required/>

      <Sel label="Próxima acción" value={f.proxima_accion} onChange={v=>setF(x=>({...x,proxima_accion:v}))} options={PROXIMAS.map(p=>({v:p,l:p}))}/>

      {/* GPS — solo visita */}
      {tab==='visita'&&(
        <div style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:14,display:'flex',alignItems:'center',gap:12}}>
          <div style={{width:40,height:40,borderRadius:'50%',background:`${C.blue}20`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>📍</div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:C.t1}}>Ubicación GPS</div>
            <div style={{fontSize:11,marginTop:2,color:gps?C.green:gpsStatus.includes('No')?C.red:C.t4}}>
              {gps?`✓ ${gpsStatus}`:gpsStatus||'Sin capturar'}
            </div>
          </div>
          <button onClick={capturarGps}
            style={{background:gps?`${C.green}20`:C.bg2,color:gps?C.green:C.t2,border:`1px solid ${gps?C.green:C.border}`,borderRadius:8,padding:'8px 14px',fontSize:12,fontWeight:700,cursor:'pointer',flexShrink:0}}>
            {gps?'✓ OK':'Capturar'}
          </button>
        </div>
      )}

      <div style={{background:`${C.orange}15`,border:`1px solid ${C.orange}30`,borderRadius:8,padding:'8px 12px',marginBottom:14,fontSize:12,color:C.orange}}>
        {['Interesado','Cotización enviada'].includes(f.estado_visita)
          ? '✅ Se creará una oportunidad automáticamente en el pipeline'
          : '💡 Si marcás Interesado o Cotización enviada, se crea la oportunidad sola'}
      </div>

      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <Btn variant="secondary" onClick={handleClose}>Cancelar</Btn>
        <Btn onClick={guardar} disabled={!f.empresa||!f.estado_visita||!f.observaciones||saving}>
          {saving?'Guardando...':`✅ Guardar ${tab==='visita'?'Visita':'Llamada'}`}
        </Btn>
      </div>
    </Modal>
  );
}

function ClienteBuscador({ clientes, value, onSelect, onClear, onCreateNew, onChange, label, empresaActiva }) {
  const [q, setQ] = useState(value||'');
  const [open, setOpen] = useState(false);
  const [liveRes, setLiveRes] = useState(null);
  const ref = useRef(null);
  useEffect(()=>{ setQ(value||''); },[value]);
  useEffect(()=>{
    const h = e => { if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown',h); return ()=>document.removeEventListener('mousedown',h);
  },[]);
  useEffect(()=>{
    if(q.length<3){setLiveRes(null);return;}
    const b=q.replace(/[%_]/g,'\\$&');
    const t=setTimeout(async()=>{
      let qr=supabase.from('clientes').select('id,empresa,contacto,ciudad,vendedor,segmento').neq('estado','inactivo').or(`empresa.ilike.%${b}%,contacto.ilike.%${b}%`).order('empresa').limit(15);
      if(empresaActiva&&empresaActiva!=='all') qr=qr.eq('empresa_id',empresaActiva);
      const{data}=await qr; setLiveRes(data||[]);
    },300);
    return()=>clearTimeout(t);
  },[q,empresaActiva]);
  const filtered = liveRes!==null ? liveRes : (q.length>0 ? clientes.filter(c=>(c.empresa||'').toLowerCase().includes(q.toLowerCase())||(c.contacto||'').toLowerCase().includes(q.toLowerCase())).slice(0,8) : []);
  const selected = !!value;
  return (
    <div style={{marginBottom:12}} ref={ref}>
      <div style={{fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5}}>{label||'Cliente *'}</div>
      <div style={{position:'relative'}}>
        <input value={q} onChange={e=>{setQ(e.target.value);setOpen(true);if(onChange)onChange(e.target.value);if(!e.target.value&&onClear)onClear();}}
          onFocus={()=>setOpen(true)}
          placeholder="Buscar cliente por nombre..."
          style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${selected?C.blue:C.border}`,borderRadius:8,padding:'9px 36px 9px 12px',color:C.t1,fontSize:13,outline:'none'}}/>
        {selected&&<button onClick={()=>{setQ('');onClear();}} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:C.t4,cursor:'pointer',fontSize:16,lineHeight:1}}>×</button>}
        {open && (filtered.length>0 || (q.length>1 && onCreateNew)) && (
          <div style={{position:'absolute',top:'100%',left:0,right:0,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,zIndex:999,maxHeight:240,overflowY:'auto',boxShadow:'0 8px 24px rgba(0,0,0,.4)'}}>
            {filtered.map(c=>(
              <div key={c.id} onMouseDown={()=>{onSelect(c);setQ(c.empresa);setOpen(false);}}
                style={{padding:'9px 12px',cursor:'pointer',borderBottom:`1px solid ${C.border}`,fontSize:13}}
                onMouseEnter={e=>e.currentTarget.style.background=C.bg2}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{color:C.t1,fontWeight:600}}>{c.empresa}
                  {c.segmento&&<span style={{marginLeft:6,fontSize:10,fontWeight:800,color:SEG_COLOR[c.segmento]||C.t4}}>[{c.segmento}]</span>}
                </div>
                {(c.contacto||c.ciudad)&&<div style={{fontSize:11,color:C.t4}}>{[c.contacto,c.ciudad].filter(Boolean).join(' · ')}</div>}
              </div>
            ))}
            {q.length>1 && onCreateNew && (
              <div onMouseDown={()=>{ onCreateNew(q); setOpen(false); }}
                style={{padding:'9px 12px',cursor:'pointer',fontSize:13,display:'flex',alignItems:'center',gap:8,background:`${C.green}10`,borderTop:filtered.length>0?`1px solid ${C.border}`:'none'}}
                onMouseEnter={e=>e.currentTarget.style.background=`${C.green}20`}
                onMouseLeave={e=>e.currentTarget.style.background=`${C.green}10`}>
                <span style={{fontSize:15}}>✨</span>
                <div>
                  <div style={{color:C.green,fontWeight:700}}>Crear "{q}" como prospecto</div>
                  <div style={{fontSize:10,color:C.t4}}>Nuevo prospecto — no está en RADAR</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── CRM ──────────────────────────────────────────────────────────────────────
const CRM_TAB_LABELS = { pipeline:'Pipeline', clientes:'Clientes', inactivos:'Sin atender' };
// ─── BASES DE LLAMADAS ────────────────────────────────────────────────────────
// Listas para llamar, armadas por criterio: certificacion de operador,
// repuestos y llantas, revision fisicotecnica. Cada una junta dos origenes,
// porque el interesado llega por los dos lados: leads de NEXA (la mayoria
// todavia NO son clientes, y a esos hay que llamarlos igual) y clientes de
// cartera con la linea de negocio marcada o el nombre que calza.
//
// Por que se saca a alguien de una base. El motivo es obligatorio: sin el,
// dentro de seis meses nadie sabe si el contacto no servia o si el que llamo se
// equivoco de fila. La base de datos lo exige tambien, no solo la pantalla.
const MOTIVOS_QUITAR = {
  no_es_potencial:    'No califica para esta lista',
  numero_malo:        'Teléfono incorrecto',
  no_le_interesa:     'Sin interés',
  ya_lo_atiende_otro: 'Lo atiende otra área',
  duplicado:          'Registro duplicado',
  otro:               'Otro',
};

// La definicion de cada base es una fila en bases_llamadas, no codigo: agregar
// la cuarta es un insert, no un deploy (ver sql/bases-de-llamadas.sql).
function BasesLlamadas({ usuario }) {
  const [bases, setBases] = useState(null);
  const [base, setBase] = useState(null);
  const [filas, setFilas] = useState(null);
  const [busq, setBusq] = useState('');
  const [soloSinVendedor, setSSV] = useState(false);
  const [seg, setSeg] = useState(null);          // a quien se le esta poniendo seguimiento
  const [fSeg, setFSeg] = useState({ que:'', cuando:'' });
  const [ocupado, setOcupado] = useState('');
  const [quitar, setQuitar] = useState(null);    // a quien se esta sacando de la base
  const [fQuitar, setFQuitar] = useState({ motivo:'', detalle:'' });
  const [descartados, setDescartados] = useState([]);
  const [verDescartados, setVerDesc] = useState(false);
  const [abierto, setAbierto] = useState('');     // fila con el panel desplegado
  const [detalle, setDetalle] = useState({});     // lo ya traido, por fila
  const [cots, setCots]       = useState({});    // cotizaciones de SAP, por fila
  const [cotAbierta, setCotAbierta] = useState(null);   // docNum expandido
  const [cotLineas, setCotLineas]   = useState({});     // lineas por docNum
  const [bajando, setBajando]       = useState(null);   // docNum cuyo PDF se esta bajando
  const [vendedores, setVendedores] = useState([]);    // a quien se le puede pasar un cliente
  const [reasig, setReasig]         = useState(null);  // fila que se esta reasignando
  const [fReasig, setFReasig]       = useState({ vendedor:'', motivo:'' });

  useEffect(() => {
    supabase.from('radar_usuarios').select('nombre,rol')
      .in('rol', ['ventas','gerente_ventas','admin']).eq('activo', true)
      .then(({ data }) => {
        // El equipo comercial primero. La consulta trae tambien a los admin
        // -que a veces si cargan cartera- pero mezclados alfabeticamente el
        // desplegable queda con once nombres y es facil asignarle un cliente a
        // alguien de administracion por error.
        const ns = (data || []).map(u => u.nombre).filter(Boolean);
        const ventas = ns.filter(esVendedorReal).sort();
        const resto  = ns.filter(n => !esVendedorReal(n)).sort();
        setVendedores([...ventas, ...resto]);
      });
  }, []);

  // TOMAR: el vendedor se queda con el cliente. Se escribe vendedor_manual_at
  // para que el sync de SAP no se lo devuelva al vendedor viejo en dos horas.
  const tomar = async (f) => {
    if (!usuario?.nombre) return;
    setOcupado(f.origen + f.ref);
    const ahora = new Date().toISOString();
    const { error } = f.origen === 'cliente'
      ? await supabase.from('clientes').update({
          vendedor: usuario.nombre, editado_manual_at: ahora,
          vendedor_manual_at: ahora, vendedor_manual_por: usuario.nombre,
        }).eq('id', f.ref)
      : await supabase.from('sibila_leads').update({ vendedor: usuario.nombre }).eq('id', f.ref);
    setOcupado('');
    if (error) { toast('No se pudo tomar: ' + error.message); return; }
    setFilas(fs => fs.map(x => (x.origen===f.origen && x.ref===f.ref) ? { ...x, vendedor: usuario.nombre } : x));
    toast(`${f.nombre} quedó asignado a ${usuario.nombre}`);
  };

  // REASIGNAR: pasarle el cliente a otro vendedor. Hace falta porque la lista de
  // llamadas muestra clientes que YA tienen dueno -y a veces el dueno correcto
  // es otro: el cliente se movio de empresa, el vendedor se fue, o quien llamo
  // descubrio que lo esta atendiendo alguien mas-.
  //
  // Se guarda vendedor_anterior para que quede el rastro de quien lo tenia: sin
  // eso, al mes nadie se acuerda por que cambio de mano.
  const abrirReasignar = (f) => {
    setReasig(f);
    setFReasig({ vendedor:'', motivo:'' });
  };

  const reasignar = async () => {
    if (!reasig || !fReasig.vendedor || fReasig.vendedor === reasig.vendedor) return;
    setOcupado('reasig');
    const ahora = new Date().toISOString();
    const nuevo = fReasig.vendedor;
    const anterior = reasig.vendedor || null;

    const { error } = reasig.origen === 'cliente'
      ? await supabase.from('clientes').update({
          vendedor: nuevo,
          vendedor_anterior: anterior,
          // vendedor_manual_at es lo que impide que el sync de SAP le devuelva
          // el vendedor viejo en la proxima corrida.
          editado_manual_at: ahora, vendedor_manual_at: ahora,
          vendedor_manual_por: usuario?.nombre || null,
        }).eq('id', reasig.ref)
      : await supabase.from('sibila_leads').update({ vendedor: nuevo }).eq('id', reasig.ref);

    if (error) { setOcupado(''); toast('No se pudo reasignar: ' + error.message); return; }

    // Avisarle al que lo recibe. Si no, se entera cuando el cliente le llama.
    if (nuevo !== usuario?.nombre) {
      notificar({ para: nuevo, tipo: 'cliente_asignado',
        titulo: 'Te pasaron un cliente',
        mensaje: `${usuario?.nombre || 'Alguien'} te asigno "${reasig.nombre}"`
          + (anterior ? ` (lo tenia ${anterior})` : '')
          + (fReasig.motivo.trim() ? ` \u2014 ${fReasig.motivo.trim()}` : ''),
        modulo: 'crm', refId: reasig.origen === 'cliente' ? reasig.ref : null , orgId: usuario?.org_id || null });
    }

    // Y que quede en el historial del cliente, no solo en la columna.
    const cid = reasig.origen === 'cliente' ? reasig.ref : (reasig.cliente_id || null);
    if (cid) {
      supabase.from('actividades').insert([{
        cliente_id: cid, tipo: 'nota', completada: true,
        titulo: `Reasignado a ${nuevo}`,
        descripcion: (anterior ? `Lo tenia ${anterior}. ` : 'No tenia vendedor. ')
          + `Lo paso ${usuario?.nombre || 'alguien'}.`
          + (fReasig.motivo.trim() ? ` Motivo: ${fReasig.motivo.trim()}` : ''),
        fecha_actividad: ahora,
      }]).then(()=>{},()=>{});
    }

    setFilas(fs => fs.map(x => (x.origen===reasig.origen && x.ref===reasig.ref)
      ? { ...x, vendedor: nuevo } : x));
    setOcupado(''); setReasig(null);
    toast(`${reasig.nombre} ahora es de ${nuevo}.`);
  };

  // SEGUIMIENTO: queda la nota de lo que se hablo y, si el cliente no tenia
  // dueno, se lo queda quien llamo. Llamar sin quedarse el cliente es como no
  // haber llamado: nadie sabe despues quien lo tiene.
  const guardarSeguimiento = async () => {
    if (!seg || !fSeg.que.trim()) return;
    setOcupado('seg');
    const cid = seg.origen === 'cliente' ? seg.ref : (seg.cliente_id || null);
    const { error } = await supabase.from('actividades').insert([{
      cliente_id: cid,
      tipo: 'llamada',
      titulo: `Llamada de ${actual?.base || 'base'}`,
      descripcion: fSeg.que.trim(),
      fecha_actividad: new Date().toISOString(),
      requiere_seguimiento: !!fSeg.cuando,
      fecha_seguimiento: fSeg.cuando || null,
      completada: true,
    }]);
    if (error) { setOcupado(''); toast('No se pudo guardar: ' + error.message); return; }
    if (!seg.vendedor) await tomar(seg);
    setOcupado(''); setSeg(null); setFSeg({ que:'', cuando:'' });
    toast('Seguimiento guardado');
  };

  // EL PANEL DE LA FILA. Llamar sin saber que compro el cliente ni cuando fue la
  // ultima vez que alguien le hablo es llamar a ciegas. Se trae al abrir, no
  // antes: son cientos de filas y casi ninguna se abre.
  const abrirDetalle = async (f) => {
    const k = f.origen + f.ref;
    if (abierto === k) { setAbierto(''); return; }
    setAbierto(k);
    if (detalle[k]) return;
    setDetalle(d => ({ ...d, [k]: { cargando:true } }));

    const cid = f.origen === 'cliente' ? f.ref : f.cliente_id;
    const [cat, fact, act, leads, ficha] = await Promise.all([
      cid ? supabase.from('v_cliente_categorias').select('categoria,lineas,facturas,usd,ultima')
              .eq('cliente_id', cid).order('usd', { ascending:false }) : { data:[] },
      cid ? supabase.from('facturas_sap').select('doc_num,doc_date,total_usd,empresa_id')
              .eq('cliente_id', cid).order('doc_date', { ascending:false }).limit(3) : { data:[] },
      cid ? supabase.from('actividades').select('tipo,titulo,descripcion,fecha_actividad')
              .eq('cliente_id', cid).order('fecha_actividad', { ascending:false }).limit(3) : { data:[] },
      // El chat: si la fila ES un lead, es el suyo; si es un cliente, el lead
      // que quedo enlazado a su ficha.
      f.origen === 'lead'
        ? { data:[{ id: f.ref }] }
        : (cid ? supabase.from('sibila_leads').select('id').eq('cliente_id', cid).limit(1) : { data:[] }),
      // La vista de listas no trae el codigo de SAP ni la empresa, y sin eso no
      // se pueden pedir las cotizaciones.
      cid ? supabase.from('clientes').select('id,empresa,sap_code,empresa_id,rtn_sap,vendedor')
              .eq('id', cid).maybeSingle() : { data:null },
    ]);
    const cli = ficha?.data || null;

    // EL MISMO CLIENTE EN OTRA EMPRESA. Pasa seguido desde que la cartera de
    // Montasa Honduras se esta pasando a Monhaco: el de la lista es el registro
    // viejo -sin facturas nuevas- y el que de verdad se esta atendiendo es el
    // otro, con otro vendedor. Sin este aviso uno llama a un cliente que ya
    // tiene quien lo atienda.
    let gemelos = { data: [] };
    if (cli?.empresa) {
      const rtn = (cli.rtn_sap || '').replace(/[^0-9]/g, '');
      const raiz = String(cli.empresa).split(/[\/,]/)[0].trim().slice(0, 18);
      let qg = supabase.from('clientes').select('id,empresa,empresa_id,vendedor,sap_code')
        .neq('id', cid).limit(4);
      qg = rtn.length >= 8
        ? qg.or(`rtn_sap.eq.${cli.rtn_sap},empresa.ilike.%${escLike(raiz)}%`)
        : qg.ilike('empresa', `%${escLike(raiz)}%`);
      gemelos = await qg;
    }
    const leadId = leads?.data?.[0]?.id || null;
    const msgs = leadId
      ? await supabase.from('sibila_mensajes').select('direccion,texto,media_tipo,created_at')
          .eq('lead_id', leadId).order('created_at', { ascending:false }).limit(6)
      : { data:[] };

    setDetalle(d => ({ ...d, [k]: {
      categorias: cat?.data || [], facturas: fact?.data || [],
      actividades: act?.data || [], mensajes: (msgs?.data || []).reverse(), leadId,
      cliente_id: cid, sap_code: cli?.sap_code || null, empresa_id: cli?.empresa_id || null,
      gemelos: (gemelos?.data || []).filter(g => g.empresa_id !== cli?.empresa_id),
    }}));

    // Las cotizaciones van APARTE y despues: es una consulta en vivo a SAP y
    // tarda. Si se esperara a esto, el panel entero quedaria en blanco un
    // segundo largo cada vez que alguien abre una fila.
    if (cli?.sap_code) cargarCotizaciones(k, cli.sap_code, cli.empresa_id);
  };

  // COTIZACIONES. Es lo que hacia falta antes de levantar el telefono: no que
  // compro -eso son las facturas- sino que PIDIO y a que precio se lo pasamos.
  const cargarCotizaciones = async (k, sapCode, empresaId) => {
    setCots(c => ({ ...c, [k]: { cargando:true } }));
    try {
      const d = await cotizacionesDe({ sapCode, empresaId });
      setCots(c => ({ ...c, [k]: d }));
    } catch (e) {
      setCots(c => ({ ...c, [k]: { lista: [], error: 'No se pudieron leer las cotizaciones.' } }));
    }
  };

  // Ver QUE pidio: las lineas de esa cotizacion, sin salir de la ficha.
  const verLineasCot = async (cot) => {
    const llave = `${cot.empresa_id}:${cot.docNum}`;
    if (cotAbierta === llave) { setCotAbierta(null); return; }
    setCotAbierta(llave);
    if (cotLineas[llave]) return;
    setCotLineas(l => ({ ...l, [llave]: { cargando:true } }));
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'quotation_lines', docNum: cot.docNum, empresa_id: cot.empresa_id }) });
      const d = await r.json();
      setCotLineas(l => ({ ...l, [llave]: { lineas: d.lines || [], doc: d.doc || null, moneda: d.moneda, error: d.ok ? null : d.error } }));
    } catch (e) {
      setCotLineas(l => ({ ...l, [llave]: { lineas: [], error: 'No se pudieron traer las lineas.' } }));
    }
  };

  const bajarPdf = async (cot) => {
    setBajando(cot.docNum);
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'get_pdf', proforma:false, empresa_id: cot.empresa_id,
                               docNum: cot.docNum, emisor: ORG.coti?.[cot.empresa_id] || null }) });
      const d = await r.json();
      if (d.ok && d.base64) {
        const bin = atob(d.base64); const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const url = URL.createObjectURL(new Blob([bytes], { type:'application/pdf' }));
        const a = Object.assign(document.createElement('a'), { href:url, download:`Cotizacion-${cot.docNum}.pdf` });
        document.body.appendChild(a); a.click(); a.remove();
        window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else toast('No se pudo bajar el PDF: ' + (d.error || 'SAP no respondio'));
    } catch (e) { toast('No se pudo bajar el PDF.'); }
    setBajando(null);
  };

  // QUITAR DE LA BASE: el contacto deja de aparecer en ESTA lista de llamadas.
  // No se borra ni se desactiva el cliente: su ficha, su cartera y su historial
  // quedan igual. Se saca de una base sola porque el mismo contacto puede no
  // servir para certificacion y si servir para repuestos.
  const descartar = async () => {
    if (!quitar || !fQuitar.motivo) return;
    if (fQuitar.motivo === 'otro' && !fQuitar.detalle.trim()) return;
    setOcupado('quitar');
    const { error } = await supabase.from('bases_llamadas_descartes').insert([{
      base_id: base, origen: quitar.origen, ref: quitar.ref,
      motivo: fQuitar.motivo, detalle: fQuitar.detalle.trim() || null,
      quien: usuario?.nombre || null,
    }]);
    setOcupado('');
    if (error) { toast('No se pudo quitar: ' + error.message); return; }
    setFilas(fs => fs.filter(x => !(x.origen===quitar.origen && x.ref===quitar.ref)));
    setDescartados(d => [{ ...quitar, motivo:fQuitar.motivo, detalle:fQuitar.detalle.trim()||null,
                           quien: usuario?.nombre||null, created_at:new Date().toISOString() }, ...d]);
    toast(`${quitar.nombre} salió de la lista. Su ficha de cliente no se tocó.`);
    setQuitar(null); setFQuitar({ motivo:'', detalle:'' });
  };

  // Devolver a la base lo que se saco por error.
  const devolver = async (d) => {
    setOcupado('dev' + d.ref);
    const { error } = await supabase.from('bases_llamadas_descartes').delete()
      .eq('base_id', base).eq('origen', d.origen).eq('ref', d.ref);
    setOcupado('');
    if (error) { toast('No se pudo devolver: ' + error.message); return; }
    setDescartados(ds => ds.filter(x => !(x.origen===d.origen && x.ref===d.ref)));
    // Se recarga la base para que vuelva con sus datos frescos.
    supabase.from('v_bases_llamadas_llamables').select('*').eq('base_id', base)
      .order('desde', { ascending:false }).limit(4000)
      .then(({ data }) => setFilas(data || []));
    toast(`${d.nombre} volvió a la lista`);
  };

  useEffect(() => {
    supabase.from('v_bases_llamadas_resumen').select('*').order('orden')
      .then(({ data }) => {
        setBases(data || []);
        if ((data || []).length) setBase(data[0].base_id);
      });
  }, []);

  useEffect(() => {
    if (!base) return;
    setFilas(null);
    supabase.from('v_bases_llamadas_llamables').select('*').eq('base_id', base)
      .order('desde', { ascending:false }).limit(4000)
      .then(({ data }) => setFilas(data || []));
    supabase.from('v_bases_llamadas_descartados').select('*').eq('base_id', base)
      .order('created_at', { ascending:false }).limit(1000)
      .then(({ data }) => setDescartados(data || []));
    setVerDesc(false);
  }, [base]);

  const actual = (bases || []).find(b => b.base_id === base);
  const vista = (filas || []).filter(f => {
    if (soloSinVendedor && f.vendedor) return false;
    if (!busq) return true;
    const b = busq.toLowerCase();
    return (f.nombre||'').toLowerCase().includes(b)
        || (f.empresa||'').toLowerCase().includes(b)
        || (f.telefono||'').includes(busq);
  });
  const conTel = vista.filter(f => f.tel_digitos);

  const copiar = () => {
    const txt = conTel.map(f => [f.nombre, f.telefono, f.empresa || '', f.vendedor || ''].join('\t')).join('\n');
    navigator.clipboard?.writeText(txt)
      .then(()=>toast(`${conTel.length} contactos copiados. Se pegan en columnas en Excel.`))
      .catch(()=>toast('No se pudo copiar'));
  };

  if (bases === null) return <div style={{padding:20,color:C.t3,fontSize:13}}>Cargando listas...</div>;
  if (!bases.length) return (
    <div style={{padding:'30px 20px',textAlign:'center',color:C.t3,fontSize:13}}>
      No hay ninguna base configurada.
      <div style={{fontSize:11.5,color:C.t4,marginTop:6}}>Se definen en la tabla bases_llamadas.</div>
    </div>
  );

  return (
    <div style={{ padding:'14px 16px', display:'grid', gap:12 }}>
      {/* Un boton por base, con su conteo, para no tener que entrar a ver si hay algo */}
      {/* Las bases eran cinco tarjetas de dos renglones que se partian en dos
          filas y llenaban media pantalla antes de mostrar un solo contacto.
          Ahora son pastillas de una linea: el nombre y cuantos hay. El desglose
          (con telefono, sin vendedor) se muestra solo de la base abierta, que es
          la unica sobre la que se puede actuar. */}
      <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
        {bases.map(b => (
          <button key={b.base_id} onClick={()=>{ setBase(b.base_id); setBusq(''); setSSV(false); setAbierto(''); }}
            title={b.descripcion || ''}
            style={{ height:32, padding:'0 12px', borderRadius:999, cursor:'pointer',
              display:'inline-flex', alignItems:'center', gap:7, fontSize:12.5,
              fontWeight: base===b.base_id?700:600,
              border:`1px solid ${base===b.base_id?C.primaryBorder:C.border}`,
              background: base===b.base_id?C.primaryTint:C.bg2,
              color: base===b.base_id?C.primary:C.t2 }}>
            {b.base}
            <span style={{ fontSize:11, fontWeight:700, opacity:.7 }}>{b.total}</span>
          </button>
        ))}
      </div>

      {actual && (
        <div style={{ fontSize:12, color:C.t3 }}>
          {actual.descripcion}
          <span style={{ color:C.t4 }}>
            {actual.descripcion ? '  ·  ' : ''}{actual.con_telefono} con telefono
            {actual.sin_vendedor ? `  ·  ${actual.sin_vendedor} sin vendedor` : ''}
          </span>
        </div>
      )}

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
        <input value={busq} onChange={e=>setBusq(e.target.value)}
          placeholder="Buscar nombre, empresa o teléfono..."
          style={{ ...INP_ST, flex:'1 1 240px', cursor:'text' }} />
        <button onClick={()=>setSSV(v=>!v)}
          style={{ height:34, padding:'0 12px', borderRadius:R.md, fontSize:12, fontWeight:700, cursor:'pointer',
            border:`1px solid ${soloSinVendedor?C.primaryBorder:C.border}`,
            background: soloSinVendedor?C.primaryTint:C.bg2,
            color: soloSinVendedor?C.blue:C.t3 }}>
          Sin vendedor
        </button>
        <Btn size="sm" variant="secondary" onClick={copiar} disabled={!conTel.length}>
          Copiar {conTel.length ? `(${conTel.length})` : ''}
        </Btn>
        {!!descartados.length && (
          <button onClick={()=>setVerDesc(v=>!v)}
            style={{ height:34, padding:'0 12px', borderRadius:R.md, fontSize:12, fontWeight:700, cursor:'pointer',
              border:`1px solid ${verDescartados?C.primaryBorder:C.border}`,
              background: verDescartados?C.primaryTint:C.bg2,
              color: verDescartados?C.blue:C.t3 }}>
            Excluidos ({descartados.length})
          </button>
        )}
      </div>

      {verDescartados ? (
        <div style={{ display:'grid', gap:4 }}>
          <div style={{ fontSize:11.5, color:C.t3, marginBottom:2 }}>
            Estos salieron de la lista. Siguen siendo clientes: su ficha, su cartera y su
            historial no se tocaron. Se pueden devolver.
          </div>
          {descartados.map(d => (
            <div key={d.origen+d.ref} style={{ display:'flex', gap:10, alignItems:'center',
              background:C.bg2, border:`1px solid ${C.border}`, borderRadius:R.md, padding:'8px 12px' }}>
              <div style={{ flex:'1 1 200px', minWidth:0 }}>
                <div style={{ fontSize:12.5, fontWeight:600, color:C.t2 }}>{d.nombre}</div>
                {d.empresa && d.empresa !== d.nombre && (
                  <div style={{ fontSize:11, color:C.t4 }}>{d.empresa}</div>
                )}
              </div>
              <div className="radar-num" style={{ minWidth:120, fontSize:12, color:C.t4 }}>{d.telefono || 'sin teléfono'}</div>
              <div style={{ flex:'1 1 220px', fontSize:11.5, color:C.t3 }}>
                {MOTIVOS_QUITAR[d.motivo] || d.motivo}{d.detalle ? ` — ${d.detalle}` : ''}
                <div style={{ fontSize:10.5, color:C.t4 }}>
                  {d.quien ? `lo quito ${d.quien}` : ''}{d.created_at ? ` · ${String(d.created_at).slice(0,10)}` : ''}
                </div>
              </div>
              <button onClick={()=>devolver(d)} disabled={ocupado === 'dev'+d.ref}
                style={{ ...BTN_PRIMARY, padding:'4px 10px', fontSize:11.5, background:C.bg3, color:C.t1 }}>
                {ocupado === 'dev'+d.ref ? '...' : 'Devolver'}
              </button>
            </div>
          ))}
        </div>
      ) : filas === null ? <div style={{fontSize:12.5,color:C.t3}}>Cargando...</div>
      : !vista.length ? (
        <div style={{ textAlign:'center', padding:'40px 20px', color:C.t3, fontSize:13 }}>
          {filas.length ? 'Sin resultados para ese filtro.' : (<>
            Esta lista no tiene contactos.
            <div style={{ fontSize:11.5, color:C.t4, marginTop:6 }}>
              Los contactos entran al marcar la necesidad en NEXA o la línea de
              negocio en la ficha del cliente.
            </div>
          </>)}
        </div>
      ) : (
        <div style={{ display:'grid', gap:4 }}>
          <div style={{ display:'flex', gap:10, padding:'0 12px', fontSize:10, fontWeight:800,
            letterSpacing:'.06em', textTransform:'uppercase', color:C.t4 }}>
            <div style={{ flex:'1 1 220px' }}>Contacto</div>
            <div style={{ minWidth:120 }}>Teléfono</div>
            <div style={{ minWidth:150 }}>Vendedor</div>
            <div style={{ minWidth:190 }}></div>
          </div>
          {vista.map(f => (<Fragment key={f.origen+f.ref}>
            <div style={{ display:'flex', gap:10, alignItems:'center',
              background:C.bg2, border:`1px solid ${C.border}`,
              borderRadius: abierto===f.origen+f.ref ? `${R.md} ${R.md} 0 0` : R.md,
              borderBottom: abierto===f.origen+f.ref ? 'none' : `1px solid ${C.border}`,
              padding:'8px 12px' }}>
              {/* QUIEN. El origen dejo de ser una columna propia: es un punto de
                  color al lado del nombre. Ocupaba el ancho de una columna para
                  decir una palabra que casi siempre es la misma. */}
              <div onClick={()=>abrirDetalle(f)} style={{ flex:'1 1 220px', minWidth:0, cursor:'pointer' }}
                title="Ver historial de compra, último contacto y conversación">
                <div style={{ display:'flex', alignItems:'center', gap:7, minWidth:0 }}>
                  <span title={f.origen==='cliente' ? 'De la cartera' : `De ${f.canal || 'NEXA'}`}
                    style={{ width:6, height:6, borderRadius:'50%', flexShrink:0,
                      background: f.origen==='cliente' ? C.primary : C.infoText }} />
                  <div style={{ fontSize:12.5, fontWeight:600, color:C.t1, overflow:'hidden',
                    textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.nombre}</div>
                  <span style={{ fontSize:9, color:C.t4, flexShrink:0,
                    transform: abierto===f.origen+f.ref ? 'rotate(90deg)' : 'none' }}>&#9654;</span>
                </div>
                {f.empresa && f.empresa !== f.nombre && (
                  <div style={{ fontSize:11, color:C.t4, marginLeft:13, overflow:'hidden',
                    textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.empresa}</div>
                )}
              </div>

              <div className="radar-num" style={{ minWidth:120, fontSize:12, color: f.telefono?C.t2:C.t4 }}>
                {f.telefono || 'sin teléfono'}
              </div>

              {/* VENDEDOR. "Tomar" vive aca y no al final: la accion es sobre
                  esta columna, y asi deja de ser un boton mas en la fila. */}
              <div style={{ minWidth:150, fontSize:11.5 }}>
                {f.vendedor ? (
                  <span style={{ display:'inline-flex', alignItems:'baseline', gap:6, maxWidth:'100%' }}>
                    <span style={{ color:C.t2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {f.vendedor}
                    </span>
                    <button onClick={()=>abrirReasignar(f)}
                      title={`Pasarle "${f.nombre}" a otro vendedor`}
                      style={{ background:'none', border:'none', padding:0, cursor:'pointer', flexShrink:0,
                        fontSize:10.5, fontWeight:700, color:C.t4, textDecoration:'underline' }}>
                      cambiar
                    </button>
                  </span>
                ) : (
                  <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                    <span style={{ color:C.orangeText, fontWeight:700 }}>sin asignar</span>
                    <button onClick={()=>tomar(f)} disabled={ocupado === f.origen+f.ref}
                      title="Asignarte este cliente"
                      style={{ background:'none', border:'none', padding:0, cursor:'pointer',
                        fontSize:11.5, fontWeight:700, color:C.primary, textDecoration:'underline' }}>
                      {ocupado === f.origen+f.ref ? '...' : 'Asignarme'}
                    </button>
                    <button onClick={()=>abrirReasignar(f)} title="Asignarselo a otro vendedor"
                      style={{ background:'none', border:'none', padding:0, cursor:'pointer',
                        fontSize:10.5, fontWeight:700, color:C.t4, textDecoration:'underline' }}>
                      a otro
                    </button>
                  </span>
                )}
              </div>

              {/* ACCIONES. Llamar y WhatsApp pasaron a iconos: eran dos pastillas
                  de texto repetidas en cada una de las cientos de filas. Queda
                  una sola accion con palabras, que es la que cierra el ciclo. */}
              <div style={{ minWidth:190, display:'flex', gap:6, justifyContent:'flex-end', alignItems:'center' }}>
                {f.tel_digitos && (<>
                  <a href={`tel:${f.telefono.replace(/[^0-9+]/g,'')}`} title={`Llamar a ${f.telefono}`}
                    style={{ width:30, height:30, borderRadius:8, display:'inline-flex', alignItems:'center',
                      justifyContent:'center', background:C.bg1, border:`1px solid ${C.border}`, color:C.t2 }}>
                    <CIcon d={CI.phone} size={14} />
                  </a>
                  <a href={`https://wa.me/${f.tel_digitos}?text=${encodeURIComponent(`Hola ${f.nombre}, le contacto de ${ORG.nombre}.`)}`}
                    target="_blank" rel="noreferrer" title="Escribir por WhatsApp"
                    style={{ width:30, height:30, borderRadius:8, display:'inline-flex', alignItems:'center',
                      justifyContent:'center', background:'#25D3661a', border:'1px solid #25D36640', color:'#1EA952' }}>
                    <CIcon d={CI.chat} size={14} />
                  </a>
                </>)}
                <button onClick={()=>{ setSeg(f); setFSeg({ que:'', cuando:'' }); }}
                  style={{ ...BTN_PRIMARY, padding:'0 11px', height:30, fontSize:11.5,
                    background:C.bg3, color:C.t1 }}>Seguimiento</button>
                <button onClick={()=>{ setQuitar(f); setFQuitar({ motivo:'', detalle:'' }); }}
                  title="Quitar de esta lista. No borra al cliente."
                  style={{ width:26, height:30, borderRadius:8, border:'none', background:'transparent',
                    color:C.t4, fontSize:15, lineHeight:1, cursor:'pointer' }}>&times;</button>
              </div>
            </div>

            {/* EL PANEL. Lo que hace falta saber ANTES de marcar: que compro,
                cuanto, cuando fue la ultima vez, quien le hablo y que se dijo.
                Sin esto la llamada arranca preguntando lo que ya sabemos. */}
            {abierto===f.origen+f.ref && (()=>{ const d = detalle[f.origen+f.ref] || {}; return (
              <div style={{ background:C.bg1, border:`1px solid ${C.border}`, borderTop:'none',
                borderRadius:`0 0 ${R.md} ${R.md}`, padding:'12px 14px 14px', marginBottom:2 }}>
                {d.cargando ? <div style={{ fontSize:12, color:C.t4 }}>Cargando...</div> : (<>

                  {/* EL MISMO CLIENTE EN OTRA EMPRESA. Es lo que mas confunde
                      desde que la cartera de Montasa Honduras se pasa a
                      Monhaco: el registro de esta lista es el viejo, sin
                      facturas nuevas, y el que se esta atendiendo de verdad es
                      el otro. */}
                  {(d.gemelos||[]).length > 0 && (
                    <div style={{ background:C.orangeTint, border:`1px solid ${C.orangeBorder}`,
                      borderRadius:R.md, padding:'8px 11px', marginBottom:12, fontSize:11.5, color:C.orangeText }}>
                      <b>Este cliente tambien existe en otra empresa.</b>{' '}
                      {d.gemelos.map((g,i)=>(
                        <span key={g.id}>
                          {i>0 && ' · '}
                          {nombreEmpresa(g.empresa_id) || 'sin empresa'}
                          {g.vendedor ? ` — lo atiende ${g.vendedor}` : ' — sin vendedor'}
                        </span>
                      ))}
                      <div style={{ color:C.t3, marginTop:3 }}>
                        Antes de llamar, revisa cual de los dos esta activo: el historial de abajo es
                        solo el de <b>{nombreEmpresa(d.empresa_id) || 'esta empresa'}</b>.
                      </div>
                    </div>
                  )}

                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(230px,1fr))', gap:16 }}>

                    <div>
                      <div style={{ fontSize:10, fontWeight:800, letterSpacing:'.06em',
                        textTransform:'uppercase', color:C.t4, marginBottom:7 }}>Historial de compra</div>
                      {(d.categorias||[]).length ? (
                        <div style={{ display:'grid', gap:5 }}>
                          {d.categorias.slice(0,5).map(c=>(
                            <div key={c.categoria} style={{ display:'flex', justifyContent:'space-between', gap:8, fontSize:11.5 }}>
                              <span style={{ color:C.t2 }}>{c.categoria}</span>
                              <span className="radar-num" style={{ color:C.t3, whiteSpace:'nowrap' }}>
                                {c.usd!=null ? `$${Math.round(c.usd).toLocaleString()}` : ''} · {String(c.ultima||'').slice(0,7)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize:11.5, color:C.t4 }}>Sin compras registradas.</div>}
                    </div>

                    <div>
                      <div style={{ fontSize:10, fontWeight:800, letterSpacing:'.06em',
                        textTransform:'uppercase', color:C.t4, marginBottom:7 }}>Últimas facturas</div>
                      {(d.facturas||[]).length ? (
                        <div style={{ display:'grid', gap:5 }}>
                          {d.facturas.map(x=>(
                            <div key={x.doc_num+String(x.empresa_id)} style={{ display:'flex', justifyContent:'space-between', gap:8, fontSize:11.5 }}>
                              <span style={{ color:C.t3 }}>{String(x.doc_date||'').slice(0,10)}</span>
                              <span className="radar-num" style={{ color:C.t2, fontWeight:600 }}>
                                ${Math.round(x.total_usd||0).toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize:11.5, color:C.t4 }}>Sin facturas registradas.</div>}
                    </div>

                    <div>
                      <div style={{ fontSize:10, fontWeight:800, letterSpacing:'.06em',
                        textTransform:'uppercase', color:C.t4, marginBottom:7 }}>Último contacto</div>
                      {(d.actividades||[]).length ? (
                        <div style={{ display:'grid', gap:6 }}>
                          {d.actividades.map((a,i)=>(
                            <div key={i} style={{ fontSize:11.5 }}>
                              <div style={{ color:C.t3 }}>{String(a.fecha_actividad||'').slice(0,10)} · {a.tipo}</div>
                              {a.descripcion && <div style={{ color:C.t2 }}>{a.descripcion.slice(0,120)}</div>}
                            </div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize:11.5, color:C.t4 }}>Sin contactos registrados.</div>}
                    </div>

                    <div>
                      <div style={{ fontSize:10, fontWeight:800, letterSpacing:'.06em',
                        textTransform:'uppercase', color:C.t4, marginBottom:7 }}>Conversación en NEXA</div>
                      {(d.mensajes||[]).length ? (
                        <div style={{ display:'grid', gap:4 }}>
                          {d.mensajes.map((m,i)=>(
                            <div key={i} style={{ fontSize:11.5, display:'flex', gap:6 }}>
                              <span style={{ color: m.direccion==='out'?C.primary:C.t4, fontWeight:700, flexShrink:0 }}>
                                {m.direccion==='out' ? 'nos' : 'el'}
                              </span>
                              <span style={{ color:C.t2 }}>
                                {(m.texto||'').slice(0,90) || (m.media_tipo ? `[${m.media_tipo}]` : '')}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize:11.5, color:C.t4 }}>Sin conversación registrada.</div>}
                    </div>

                  </div>

                  {/* COTIZACIONES. Lo que PIDIO, que no es lo mismo que lo que
                      compro. Va en vivo contra SAP, abajo y a lo ancho, porque
                      es la seccion con la que de verdad se trabaja: se abre
                      para ver las lineas y se baja el PDF. */}
                  {(() => { const c = cots[f.origen+f.ref] || {}; return (
                    <div style={{ marginTop:14, borderTop:`1px solid ${C.border}`, paddingTop:12 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:7 }}>
                        <div style={{ fontSize:10, fontWeight:800, letterSpacing:'.06em',
                          textTransform:'uppercase', color:C.t4 }}>Cotizaciones</div>
                        {c.cargando && <span style={{ fontSize:10.5, color:C.t4 }}>consultando SAP...</span>}
                        {!c.cargando && (c.lista||[]).length > 0 &&
                          <span style={{ fontSize:10.5, color:C.t4 }}>{c.lista.length} en los ultimos 24 meses</span>}
                      </div>

                      {!d.sap_code ? (
                        <div style={{ fontSize:11.5, color:C.t4 }}>
                          Este cliente no tiene codigo de SAP, asi que no hay cotizaciones que buscar.
                        </div>
                      ) : c.error ? (
                        <div style={{ fontSize:11.5, color:C.orangeText }}>{c.error}</div>
                      ) : (c.lista||[]).length === 0 && !c.cargando ? (
                        <div style={{ fontSize:11.5, color:C.t4 }}>Sin cotizaciones en los ultimos 24 meses.</div>
                      ) : (
                        <div style={{ display:'grid', gap:4 }}>
                          {(c.lista||[]).map(q => { const llave = `${q.empresa_id}:${q.docNum}`;
                            const ln = cotLineas[llave] || {}; const abiertaEsta = cotAbierta === llave; return (
                            <div key={llave} style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:R.md }}>
                              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 10px', flexWrap:'wrap' }}>
                                <span className="radar-num" style={{ fontSize:11.5, fontWeight:700, color:C.t1, minWidth:58 }}>
                                  #{q.docNum}
                                </span>
                                <span style={{ fontSize:11.5, color:C.t3, minWidth:80 }}>{q.fecha}</span>
                                <span className="radar-num" style={{ fontSize:11.5, fontWeight:700, color:C.t1 }}>
                                  {q.moneda === 'USD' ? '$' : 'L'}{Math.round(q.total).toLocaleString()}
                                </span>
                                <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:999,
                                  background: q.estado==='abierta' ? C.greenTint : C.bg3,
                                  color: q.estado==='abierta' ? C.greenText : C.t4,
                                  border:`1px solid ${q.estado==='abierta' ? C.greenBorder : C.border}` }}>
                                  {q.estado}
                                </span>
                                <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
                                  <button onClick={()=>verLineasCot(q)}
                                    style={{ fontFamily:'inherit', fontSize:11, fontWeight:700, padding:'4px 9px',
                                      borderRadius:R.sm, border:`1px solid ${C.border}`, background:C.bg1,
                                      color:C.t2, cursor:'pointer' }}>
                                    {abiertaEsta ? 'Ocultar' : 'Que pidio'}
                                  </button>
                                  <button onClick={()=>bajarPdf(q)} disabled={bajando===q.docNum}
                                    style={{ fontFamily:'inherit', fontSize:11, fontWeight:700, padding:'4px 9px',
                                      borderRadius:R.sm, border:`1px solid ${C.primaryBorder}`, background:C.primaryTint,
                                      color:C.blue, cursor: bajando===q.docNum ? 'wait' : 'pointer' }}>
                                    {bajando===q.docNum ? 'Bajando...' : 'PDF'}
                                  </button>
                                </div>
                              </div>
                              {q.comentarios && (
                                <div style={{ fontSize:11, color:C.t3, fontStyle:'italic', padding:'0 10px 7px' }}>
                                  {String(q.comentarios).slice(0,160)}
                                </div>
                              )}
                              {abiertaEsta && (
                                <div style={{ borderTop:`1px solid ${C.border}`, padding:'8px 10px', background:C.bg1 }}>
                                  {ln.cargando ? <div style={{ fontSize:11.5, color:C.t4 }}>Trayendo las lineas...</div>
                                   : ln.error ? <div style={{ fontSize:11.5, color:C.redText }}>{ln.error}</div>
                                   : (ln.lineas||[]).length === 0 ? <div style={{ fontSize:11.5, color:C.t4 }}>La cotizacion no tiene lineas.</div>
                                   : (
                                    <div style={{ display:'grid', gap:3 }}>
                                      {ln.lineas.map((l,i)=>(
                                        <div key={i} style={{ display:'grid',
                                          gridTemplateColumns:'1fr 52px 90px', gap:8, fontSize:11.5, alignItems:'baseline' }}>
                                          <span style={{ color:C.t2 }}>{l.ItemDescription || l.ItemCode || 'sin descripcion'}</span>
                                          <span className="radar-num" style={{ color:C.t4, textAlign:'right' }}>{l.Quantity}</span>
                                          <span className="radar-num" style={{ color:C.t1, textAlign:'right', fontWeight:600 }}>
                                            {ln.moneda === 'USD' ? '$' : 'L'}{Number(l.lineTotal||0).toLocaleString(undefined,{maximumFractionDigits:0})}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ); })}
                        </div>
                      )}
                    </div>
                  ); })()}

                </>)}
              </div>
            ); })()}
          </Fragment>))}
        </div>
      )}

      {/* REASIGNAR. Separado de "Asignarme" a proposito: tomar un cliente libre
          es un clic, pero quitarselo a alguien es una decision, y conviene que
          se vea quien lo tenia y por que cambia de mano. */}
      <Modal open={!!reasig} onClose={()=>setReasig(null)}
        title={reasig ? `Reasignar \u2014 ${reasig.nombre}` : ''} width={440}>
        {reasig && (
          <div style={{ display:'grid', gap:12 }}>
            <div style={{ fontSize:12.5, color:C.t3 }}>
              {reasig.vendedor
                ? <>Hoy lo tiene <b style={{ color:C.t1 }}>{reasig.vendedor}</b>. Al cambiarlo queda
                    registrado que era suyo, y al que lo recibe le llega un aviso.</>
                : <>Este contacto no tiene vendedor. Se le asigna a quien elijas y le llega un aviso.</>}
            </div>

            <Sel label="Pasarselo a *" value={fReasig.vendedor}
              onChange={v=>setFReasig(x=>({ ...x, vendedor:v }))}
              options={vendedores.filter(v=>v!==reasig.vendedor).map(v=>({
                v, l: esVendedorReal(v) ? v : `${v}  (no es del equipo comercial)`,
              }))} />

            {usuario?.nombre && usuario.nombre !== reasig.vendedor
              && vendedores.includes(usuario.nombre) && fReasig.vendedor !== usuario.nombre && (
              <button onClick={()=>setFReasig(x=>({ ...x, vendedor: usuario.nombre }))}
                style={{ justifySelf:'start', background:'none', border:'none', padding:0,
                  fontSize:11.5, fontWeight:700, color:C.blue, textDecoration:'underline', cursor:'pointer' }}>
                Pasarmelo a mi
              </button>
            )}

            <Inp label="Por que (opcional)" value={fReasig.motivo}
              onChange={v=>setFReasig(x=>({ ...x, motivo:v }))} rows={2}
              placeholder="ej: ya lo atiende Monhaco, o el cliente pidio el cambio" />

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn variant="secondary" onClick={()=>setReasig(null)}>Cancelar</Btn>
              <Btn onClick={reasignar} disabled={!fReasig.vendedor || ocupado==='reasig'}>
                {ocupado==='reasig' ? 'Guardando...' : 'Reasignar'}
              </Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* QUITAR DE LA BASE. Sale de esta lista de llamadas y de ninguna otra
          parte: el cliente queda intacto. Por eso el texto lo dice, para que
          nadie crea que esta borrando un cliente. */}
      <Modal open={!!quitar} onClose={()=>setQuitar(null)}
        title={quitar ? `Quitar de la lista — ${quitar.nombre}` : ''} width={480}>
        {quitar && (
          <div style={{ display:'grid', gap:12 }}>
            <div style={{ fontSize:12.5, color:C.t3 }}>
              Sale de la lista <b>{actual?.base}</b>. El cliente no se borra
              ni se desactiva: su ficha, su cartera y su historial quedan igual, y sigue
              apareciendo en las otras listas donde aplique.
            </div>
            <div style={{ display:'grid', gap:7 }}>
              {Object.entries(MOTIVOS_QUITAR).map(([v,l]) => (
                <label key={v} style={{ display:'flex', alignItems:'center', gap:9, padding:'8px 11px',
                  border:`1px solid ${fQuitar.motivo===v?C.primaryBorder:C.border}`, borderRadius:R.md,
                  background: fQuitar.motivo===v?C.primaryTint:C.bg1, cursor:'pointer', fontSize:13, color:C.t1 }}>
                  <input type="radio" name="motivoquitar" checked={fQuitar.motivo===v}
                    onChange={()=>setFQuitar(f=>({...f, motivo:v}))} style={{ accentColor:C.primary }} />
                  {l}
                </label>
              ))}
              {fQuitar.motivo === 'otro' && (
                <textarea rows={2} placeholder="¿Cuál es el motivo? (obligatorio)"
                  value={fQuitar.detalle} onChange={e=>setFQuitar(f=>({...f, detalle:e.target.value}))}
                  style={{ ...INP_ST, resize:'vertical', height:'auto', padding:'9px 11px' }} />
              )}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <Btn variant="secondary" style={{ flex:1 }} onClick={()=>setQuitar(null)}>Cancelar</Btn>
              <Btn style={{ flex:1 }} onClick={descartar}
                disabled={ocupado==='quitar' || !fQuitar.motivo || (fQuitar.motivo==='otro' && !fQuitar.detalle.trim())}>
                {ocupado==='quitar' ? 'Quitando...' : 'Quitar de la lista'}
              </Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* SEGUIMIENTO. Queda la nota de lo que se hablo y, si el cliente no
          tenia dueno, se lo queda quien llamo en el mismo acto: llamar sin
          tomarlo es como no haber llamado, porque despues nadie sabe quien lo
          tiene. */}
      <Modal open={!!seg} onClose={()=>setSeg(null)} title={seg ? `Seguimiento — ${seg.nombre}` : ''} width={520}>
        {seg && (
          <div style={{ display:'grid', gap:12 }}>
            <div style={{ fontSize:12, color:C.t3 }}>
              {seg.empresa && seg.empresa !== seg.nombre ? `${seg.empresa} · ` : ''}
              {seg.telefono || 'sin teléfono'}
            </div>
            <div>
              <div style={FLD_LAB}>Que se hablo *</div>
              <textarea value={fSeg.que} onChange={e=>setFSeg(x=>({ ...x, que:e.target.value }))}
                rows={3} placeholder="¿Con quién se habló y qué dijo?"
                style={{ ...INP_ST, cursor:'text', resize:'vertical' }} />
            </div>
            <div>
              <div style={FLD_LAB}>Volver a llamar el</div>
              <input type="date" value={fSeg.cuando}
                onChange={e=>setFSeg(x=>({ ...x, cuando:e.target.value }))} style={INP_ST} />
            </div>
            {!seg.vendedor && (
              <div style={{ background:C.primaryTint, border:`1px solid ${C.primaryBorder}`,
                borderRadius:R.md, padding:'9px 11px', fontSize:12, color:C.t2 }}>
                Este contacto no tiene vendedor asignado. Al guardar queda a tu nombre.
              </div>
            )}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn size="sm" variant="secondary" onClick={()=>setSeg(null)}>Cancelar</Btn>
              <Btn size="sm" onClick={guardarSeguimiento} disabled={!fSeg.que.trim() || ocupado==='seg'}>
                {ocupado==='seg' ? 'Guardando...' : 'Guardar'}
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function CRM({ usuario, empresaActiva, initFiltro = {}, initTab }) {
  // QUIEN VE QUE. Un vendedor ve lo suyo y lo que no tiene dueno. Eso esta bien
  // en Honduras, donde son varios repartiendose la cartera, pero deja ciego al
  // que lleva un pais entero: en Costa Rica habia 70 oportunidades a nombre de
  // otras personas o de "Administracion" que el responsable del pais no podia
  // abrir.
  //
  // Se resuelve por usuario y por empresa, no cambiandole el rol: 'ver_todo:<id>'
  // en sus modulos_extra le da la cartera COMPLETA de esa empresa, sin tocar lo
  // que puede hacer en las demas ni darle permisos de gerente.
  const VER_TODO_EMP = (usuario?.modulos_extra || [])
    .filter(m => String(m).startsWith('ver_todo:'))
    .map(m => String(m).slice(9).trim())
    .filter(Boolean);

  // 'Sin atender' (ex clientes_inactivos) solo para los roles que ya lo tenían en el nav
  const conInactivos = ['admin','gerente_ventas','ventas'].includes(usuario?.rol) || (usuario?.modulos_extra||[]).includes('clientes_inactivos');
  // La pestana se recuerda. Si el modulo se remonta -o se vuelve al CRM desde
  // otro lado- se cae donde estabas, no en Pipeline.
  const [tab, setTab]           = useState(() => {
    const valida = t => t && CRM_TAB_LABELS[t] && (t !== 'inactivos' || conInactivos);
    if (valida(initTab)) return initTab;
    try { const g = localStorage.getItem('radar_crm_tab'); if (valida(g)) return g; } catch (_) {}
    return 'pipeline';
  });
  useEffect(() => { try { localStorage.setItem('radar_crm_tab', tab); } catch (_) {} }, [tab]);
  // Anuncia la sub-vista al topbar (breadcrumb CRM / Pipeline)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('radar-submod', { detail: CRM_TAB_LABELS[tab] || 'Pipeline' }));
    return () => window.dispatchEvent(new CustomEvent('radar-submod', { detail: '' }));
  }, [tab]);
  const [clientes, setClientes] = useState([]);
  const [opps, setOpps]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [sel, setSel]           = useState(null);
  const [oppTab, setOppTab]     = useState('resumen'); // pestana de la ficha de oportunidad
  const [busq, setBusq]         = useState('');
  const [clBusqResults, setClBusqResults] = useState(null);
  const [filtroEst, setFiltroEst] = useState('todos');
  // Rediseño Clientes (mockup aprobado): filtros por KPI + segmento, saldo
  // abierto por cliente, y stats de compras para la ficha.
  const [filtroSeg, setFiltroSeg] = useState([]);   // multi ([] = todos los segmentos)
  const [filtroKpi, setFiltroKpi] = useState('');
  const [salditos, setSalditos] = useState({});     // sap_code -> {saldo, maxDias}
  const [cliStats, setCliStats] = useState(null);   // compras 12m del cliente elegido
  useEffect(() => { (async () => {
    // Facturas ABIERTAS (acotado): con esto la tabla muestra el saldo de cada
    // cliente sin traer todo el historial.
    const { data } = await supabase.from('facturas_sap')
      .select('card_code,empresa_id,saldo_usd,doc_due_date').eq('pagado', false).gt('saldo_usd', 0.01).limit(3000);
    const m = {}; const hoyD = Date.now();
    (data||[]).forEach(f => {
      const k = `${f.empresa_id}_${f.card_code}`;
      if (!m[k]) m[k] = { saldo:0, maxDias:0 };
      m[k].saldo += Number(f.saldo_usd)||0;
      if (f.doc_due_date) m[k].maxDias = Math.max(m[k].maxDias, Math.floor((hoyD - new Date(f.doc_due_date+'T12:00:00').getTime())/86400000));
    });
    setSalditos(m);
  })(); }, []); // eslint-disable-line
  // Stats de la ficha: compras de 12 meses del cliente seleccionado (1 query).
  useEffect(() => { (async () => {
    setCliStats(null);
    if (!sel?.sap_code || tab!=='clientes') return;
    const i = String(sel.sap_code).indexOf('_');
    if (i < 1) return;
    const eid = sel.sap_code.slice(0,i), cc = sel.sap_code.slice(i+1);
    const desde = new Date(Date.now()-365*86400000).toISOString().slice(0,10);
    const { data } = await supabase.from('facturas_sap')
      .select('total_usd,doc_date').eq('empresa_id',eid).eq('card_code',cc).eq('pagado',true).gte('doc_date',desde).limit(500);
    const tot = (data||[]).reduce((x,f)=>x+(Number(f.total_usd)||0),0);
    const n = (data||[]).length;
    setCliStats({ compras12: tot, n, ticket: n?tot/n:0, freq: Math.round(n/12*10)/10 });
  })(); }, [sel?.id, tab]); // eslint-disable-line
  const saldoDe = (c) => salditos[c.sap_code]?.saldo || 0;
  // Datos rotos: sin nombre, solo un punto, o con comillas de la importacion vieja.
  const esRoto = (c) => { const n = String(c.empresa||'').trim(); return !n || n==='.' || /^["\u201C\u201D']/.test(n); };
  const limpiarComillas = async () => {
    const sucios = clientes.filter(c => /^["\u201C\u201D']|["\u201C\u201D']$/.test(String(c.empresa||'').trim()));
    if (!sucios.length) { toast('No hay nombres con comillas.'); return; }
    if (!(await confirmar(`Se van a limpiar las comillas de ${sucios.length} nombres. ¿Dale?`, { ok:'Limpiar' }))) return;
    for (const c of sucios) {
      const limpio = String(c.empresa).trim().replace(/^["\u201C\u201D']+|["\u201C\u201D']+$/g,'').trim();
      if (limpio) await supabase.from('clientes').update({ empresa: limpio }).eq('id', c.id);
    }
    setClientes(cs => cs.map(c => {
      const limpio = String(c.empresa||'').trim().replace(/^["\u201C\u201D']+|["\u201C\u201D']+$/g,'').trim();
      return limpio && limpio !== c.empresa ? { ...c, empresa: limpio } : c;
    }));
    toast(`${sucios.length} nombres limpiados.`);
  };
  const [nota, setNota]         = useState('');
  const [savingNota, setSavingNota] = useState(false);
  const [segHist, setSegHist]   = useState([]);   // historial de seguimientos de la opp abierta
  const [segExpand, setSegExpand] = useState(false);
  const [etapaHist, setEtapaHist] = useState([]); // historial de cambios de etapa (días por etapa)
  const [diasExpand, setDiasExpand] = useState(false);
  const [modalMover, setModalMover] = useState(null); // {opp, etapa} cambio de etapa que exige comentario
  const [notaMover, setNotaMover]   = useState('');
  const [savingMover, setSavingMover] = useState(false);
  const [selClienteTel, setSelClienteTel] = useState(''); // teléfono del cliente de la opp abierta (para alerta)
  const [telInput, setTelInput] = useState('');
  const [savingTel, setSavingTel] = useState(false);
  const [modalOpp, setModalOpp] = useState(false);
  const [modalVisita, setModalVisita] = useState(false);
  const [visitasPrefill, setVisitasPrefill] = useState(null);
  const [dragOpp, setDragOpp]   = useState(null);
  const [visitasCliente, setVisitasCliente] = useState([]);
  const [loadingVisitas, setLoadingVisitas] = useState(false);
  const [fOpp, setFOpp]         = useState({titulo:'',cliente:'',cliente_id:'',vendedor:'',valor:'',moneda:'USD',etapa:'prospecto',tipo:'',linea_negocio:'',proxima_accion:'',fecha_cierre:'',notas:'',equipo_id:''});
  const [savingOpp, setSavingOpp] = useState(false);
  const [equiposPrecio, setEquiposPrecio] = useState([]);   // equipos con precios para cotizar
  const [equipoBusq, setEquipoBusq] = useState('');
  const [flotaOpen, setFlotaOpen] = useState(false);       // panel "cotizar equipo de flota"
  const [flotaPeriodo, setFlotaPeriodo] = useState('mes');
  const [flotaQ, setFlotaQ] = useState('');
  const [modalEditOpp, setModalEditOpp] = useState(false);
  const [fEditOpp, setFEditOpp] = useState({});
  const [savingEditOpp, setSavingEditOpp] = useState(false);
  const [tasaHNL, setTasaHNL] = useState(26.74);
  const [tasaCRC, setTasaCRC] = useState(515.0);
  const [modalGanado, setModalGanado] = useState(null);
  const [fGanado, setFGanado] = useState({valor:'',notas:''});
  const [modalPerdido, setModalPerdido] = useState(null); // opp a cerrar como perdida
  const [motivoPerdido, setMotivoPerdido] = useState('');
  const [notasPerdido, setNotasPerdido] = useState('');
  const [savingPerdido, setSavingPerdido] = useState(false);
  const [modalInvalido, setModalInvalido] = useState(null);   // opp a marcar Prospecto Inválido
  const [notaInvalido, setNotaInvalido]   = useState('');
  const [savingInvalido, setSavingInvalido] = useState(false);
  const [modalCampania, setModalCampania] = useState(null);   // {opp, frio} para inscribir en campaña
  const [cadenciaCamp, setCadenciaCamp]   = useState('mensual');
  const [notaCamp, setNotaCamp]           = useState('');
  const [savingCamp, setSavingCamp]       = useState(false);
  const [facturaMatch, setFacturaMatch] = useState(null);
  const [facturaError, setFacturaError] = useState('');
  const [savingGanado, setSavingGanado] = useState(false);
  const [modalFactura, setModalFactura] = useState(null); // {opp, pendingEtapa}
  const [facturaTab, setFacturaTab] = useState('digitos'); // digitos | lista
  const [facturas4dig, setFacturas4dig] = useState('');
  const [facturasLista, setFacturasLista] = useState([]);
  const [facturaSelLista, setFacturaSelLista] = useState(null);
  const [facturaSearchMatch, setFacturaSearchMatch] = useState(null);
  const [facturaSearchErr, setFacturaSearchErr] = useState('');
  const [loadingFacturasLista, setLoadingFacturasLista] = useState(false);
  const [savingFactura, setSavingFactura] = useState(false);
  const [sapLines, setSapLines] = useState(null);
  const [loadingSapLines, setLoadingSapLines] = useState(false);
  const [sapPDF, setSapPDF] = useState(null);
  const [loadingPDF, setLoadingPDF] = useState(false);
  // Filtro de vendedor MULTI-SELECT: array de nombres (normalizados). Vacío = todos.
  // Sentinela '__sin__' = sin asignar.
  const [filtroVends, setFiltroVends] = useState([]);
  const [filtroPeriodo, setFiltroPeriodo] = useState('todo');
  const [filtroLinea, setFiltroLinea] = useState(initFiltro.linea ? [initFiltro.linea] : []);
  const [filtroEtapaK, setFiltroEtapaK] = useState(initFiltro.etapa || '');
  // Abrir una oportunidad puntual (ej. desde la notificación de "prospecto asignado" de NEXA).
  useEffect(()=>{ if (initFiltro.oppId && opps.length) { const o = opps.find(x=>String(x.id)===String(initFiltro.oppId)); if (o) { setTab('pipeline'); setSel(o); } } },[initFiltro.oppId, opps]); // eslint-disable-line
  // Abrir un CLIENTE puntual: es a donde apunta la notificacion de "entro un
  // cliente por la web", que antes no llevaba a ningun lado.
  useEffect(()=>{ if (initFiltro.clienteId && clientes.length) { const c = clientes.find(x=>String(x.id)===String(initFiltro.clienteId)); if (c) { setTab('clientes'); setSel(c); } } },[initFiltro.clienteId, clientes]); // eslint-disable-line
  const [dropLinea, setDropLinea] = useState(false);
  const lineaBtnRef = useRef(null);
  const lineaMenuRef = useRef(null);
  // Cerrar el dropdown de "Línea" al hacer clic en cualquier otro lado.
  useEffect(()=>{
    if (!dropLinea) return;
    const h = (e)=>{
      if (lineaBtnRef.current?.contains(e.target)) return;
      if (lineaMenuRef.current?.contains(e.target)) return;
      setDropLinea(false);
    };
    document.addEventListener('mousedown', h);
    return ()=>document.removeEventListener('mousedown', h);
  },[dropLinea]);
  const [dropVend, setDropVend] = useState(false);
  const vendBtnRef = useRef(null);
  const vendMenuRef = useRef(null);
  useEffect(()=>{
    if (!dropVend) return;
    const h = (e)=>{
      if (vendBtnRef.current?.contains(e.target)) return;
      if (vendMenuRef.current?.contains(e.target)) return;
      setDropVend(false);
    };
    document.addEventListener('mousedown', h);
    return ()=>document.removeEventListener('mousedown', h);
  },[dropVend]);
  const [busqOpp, setBusqOpp] = useState('');
  const [sortOpps, setSortOpps] = useState('none');
  const [modalCotizSAP, setModalCotizSAP] = useState(null);
  const [sapCotizInput, setSapCotizInput] = useState('');
  const [savingCotiz, setSavingCotiz] = useState(false);
  // Buscar en inventario sin salir de la oportunidad: lee el cache de inventario_sap.
  const [invQ, setInvQ] = useState('');
  const [invRes, setInvRes] = useState(null);
  const [invBusy, setInvBusy] = useState(false);
  const buscarInventario = async () => {
    const q = invQ.trim();
    if (q.length < 2) return;
    setInvBusy(true);
    try {
      const pat = `%${q.replace(/[%_]/g,'')}%`;
      const { data } = await supabase.from('inventario_sap')
        .select('codigo,nombre,num_parte,empresa_id,stock,disponible,precio_usd')
        .or(`codigo.ilike.${pat},nombre.ilike.${pat},num_parte.ilike.${pat}`)
        .order('disponible', { ascending:false, nullsFirst:false })
        .limit(8);
      setInvRes(data || []);
    } catch { setInvRes([]); }
    setInvBusy(false);
  };
  const [modalUnir, setModalUnir] = useState(null);   // oportunidad principal (target)
  const [unirSel, setUnirSel] = useState(null);       // oportunidad a anexar (source)
  const [unirBusq, setUnirBusq] = useState('');
  const [savingUnir, setSavingUnir] = useState(false);
  const [modalConvertir, setModalConvertir] = useState(null);
  const [fConvertir, setFConvertir] = useState({nombre:'',telefono:'',rtn:'',contacto:'',email:'',ciudad:'',industria:''});
  const [savingConvertir, setSavingConvertir] = useState(false);
  const [vendedoresList, setVendedoresList] = useState([]);
  const [modalCliente, setModalCliente] = useState(false);
  const [fCl, setFCl] = useState({empresa:'',rtn:'',contacto:'',cargo:'',telefono:'',email:'',ciudad:'',pais:'Honduras',industria:'',linea_negocio:'',vendedor:'',segmento:'',iniciativa:'',estado:'activo'});
  const [savingCl, setSavingCl] = useState(false);
  const [dupCliente, setDupCliente] = useState(null);
  const [modalEditCl, setModalEditCl] = useState(false);
  const [fEditCl, setFEditCl] = useState({});
  const [savingEditCl, setSavingEditCl] = useState(false);
  const [facturasCliente, setFacturasCliente] = useState([]);
  // Las cotizaciones del cliente. La ficha del CRM nunca las tuvo: se veia
  // que compro y que pago, pero no que PIDIO, que es lo que hace falta antes
  // de levantar el telefono.
  const [cotsCliente, setCotsCliente] = useState([]);
  const [cotAbiertaCl, setCotAbiertaCl] = useState(null);
  const [cotLineasCl, setCotLineasCl] = useState({});
  const [bajandoCot, setBajandoCot] = useState(null);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  // Chat de WhatsApp del vendedor con el cliente de la opp abierta (solo lectura)
  const [waChat, setWaChat]         = useState(null);
  const [waMsgs, setWaMsgs]         = useState([]);
  const [waCargando, setWaCargando] = useState(false);
  // Arranca ABIERTO: ahora el chat vive en su propia pestana de la ficha, si entraras
  // ahi y estuviera colapsado la pestana se veria vacia.
  const [waAbierto, setWaAbierto]   = useState(true);
  // Abrir una factura del cliente: líneas desde SAP + PDF (sin entrar a SAP).
  const [facAbierta, setFacAbierta] = useState(null);   // doc_num abierto
  const [facDet, setFacDet] = useState({});             // { [doc_num]: {lines, moneda, pdf, error} }
  const [facCargando, setFacCargando] = useState(null);
  const abrirFacturaDet = async (f) => {
    if (facAbierta === f.doc_num) { setFacAbierta(null); return; }
    setFacAbierta(f.doc_num);
    if (facDet[f.doc_num]) return;                      // cache
    setFacCargando(f.doc_num);
    try {
      const eid = f.empresa_id || empresaActiva || 'honduras';
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'factura_lines', docNum:f.doc_num, empresa_id:eid, conPdf:true, emisor: ORG.coti?.[eid] || null }) });
      const d = await r.json();
      setFacDet(x=>({ ...x, [f.doc_num]: d.ok ? d : { error: d.error || 'No se pudo leer la factura en SAP' } }));
    } catch (e) { setFacDet(x=>({ ...x, [f.doc_num]: { error: e.message } })); }
    setFacCargando(null);
  };
  const pdfFactura = (docNum, bajar) => {
    const b64 = facDet[docNum]?.pdf?.base64; if (!b64) return;
    if (bajar) { const a=document.createElement('a'); a.href=`data:application/pdf;base64,${b64}`; a.download=`Factura-${docNum}.pdf`; a.click(); return; }
    const bin=atob(b64), bytes=new Uint8Array(bin.length);
    for (let i=0;i<bin.length;i++) bytes[i]=bin.charCodeAt(i);
    const u=URL.createObjectURL(new Blob([bytes],{type:'application/pdf'}));
    window.open(u,'_blank'); setTimeout(()=>URL.revokeObjectURL(u),60000);
  };
  const [cobrosCliente, setCobrosCliente] = useState([]);
  const [selRtn, setSelRtn] = useState('');
  const [modalProspecto, setModalProspecto] = useState(false);
  const [fProspecto, setFProspecto] = useState({empresa:'',titulo:'',telefono:'',vendedor:''});
  const [savingProspecto, setSavingProspecto] = useState(false);
  const [errorProspecto, setErrorProspecto] = useState('');
  const [confirmEliminarOpp, setConfirmEliminarOpp] = useState(null);
  const [modalPagadoSAP, setModalPagadoSAP] = useState(null); // {opp, fac, tipo}
  const [sapPagoStatus, setSapPagoStatus] = useState({}); // {[doc_num]: {pagado, doc_date, total_usd}}
  // Cotizador SAP
  const [modalCotSAP, setModalCotSAP] = useState(false);
  const [cotEditar, setCotEditar] = useState(null);   // {docEntry, docNum} cuando se esta editando una existente
  const [numCargar, setNumCargar] = useState('');
  const [cargandoCot, setCargandoCot] = useState(false);
  const [fCotSAP, setFCotSAP] = useState({ cliente_id:'', cliente_nombre:'', moneda:'USD', isv:'con', validez:'', comentarios:'', tipoDoc:'cotizacion', sapEmp:'', cardManual:'', linea_negocio:'', lineas:[{descripcion:'',cantidad:'1',precio:'',itemCode:''}] });
  // Autocompletar lineas desde el catalogo COMPLETO de SAP: se escribe "cemento"
  // o "revision fisic" y salen codigo, disponible y precio.
  //
  // Miraba inventario_sap, que excluye los servicios a proposito -ese modulo es
  // de stock y margen-. Resultado: las once revisiones fisicotecnicas, que son
  // el frente IHTT entero, no aparecian al cotizar y habia que escribir la
  // descripcion a mano e inventar el precio. v_items_cotizables junta las dos
  // cosas: lo que hay en bodega y lo que se vende como servicio.
  const [invSug, setInvSug] = useState(null); // { i: indice de linea, rows }
  const invSugTimer = useRef(null);
  const buscarInvLinea = (i, q) => {
    clearTimeout(invSugTimer.current);
    if (!q || q.trim().length < 2) { setInvSug(null); return; }
    invSugTimer.current = setTimeout(async () => {
      const pat = `%${q.trim().replace(/[%_]/g,'')}%`;
      let qq = supabase.from('v_items_cotizables')
        .select('codigo,nombre,grupo,disponible,precio_local,precio_usd,moneda,es_servicio,empresa_id')
        // Se ordena por nombre y no por existencias: un servicio no tiene
        // existencias, y ordenando por disponible quedaban todos al final,
        // fuera del corte. Buscando "revision fisic" salen once y no cabian.
        .or(`codigo.ilike.${pat},nombre.ilike.${pat},num_parte.ilike.${pat}`)
        .order('nombre').limit(12);
      if (fCotSAP.sapEmp) qq = qq.eq('empresa_id', fCotSAP.sapEmp);
      const { data } = await qq;
      setInvSug({ i, rows: data || [] });
    }, 250);
  };
  const elegirInvLinea = (i, r) => {
    setFCotSAP(x => { const ls = [...x.lineas];
      const precio = x.moneda === 'USD' ? r.precio_usd : r.precio_local;
      ls[i] = { ...ls[i], descripcion: r.nombre, itemCode: r.codigo,
        precio: precio != null ? String(precio) : ls[i].precio, cantidad: ls[i].cantidad || '1' };
      return { ...x, lineas: ls };
    });
    setInvSug(null);
    // El precio del catalogo es el de la lista 1. El que SAP exige es el de la
    // lista de ESTE cliente, asi que se le pregunta y se corrige solo.
    buscarPrecio(i, r.codigo);
  };
  const [savingCotSAP, setSavingCotSAP] = useState(false);
  const [creandoCliSAP, setCreandoCliSAP] = useState(false);
  const [resCrearCliSAP, setResCrearCliSAP] = useState(null);

  // Crear el cliente en SAP sin salir del modal de cotización. Cierra el flujo
  // "creo el cliente y le cotizo rápido", que antes moría porque RADAR creaba el
  // cliente en su base pero no en SAP, y SAP es el que emite el documento.
  const crearClienteEnSAP = async () => {
    if (!fCotSAP.cliente_id) return;
    setCreandoCliSAP(true); setResCrearCliSAP(null);
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          action:'crear_cliente_sap',
          cliente_id: fCotSAP.cliente_id,
          empresa_id: fCotSAP.sapEmp || empresaActiva || 'honduras',
        }) });
      const d = await r.json();
      setResCrearCliSAP(d);
      if (d.ok) {
        // Refrescar el cliente en memoria: el modal deja de pedir CardCode y la
        // ficha abierta detras se actualiza sola. Antes solo se refrescaba la
        // lista, asi que la ficha seguia mostrando el cliente sin codigo hasta
        // que uno la cerraba y volvia a abrir.
        const { data } = await supabase.from('clientes').select('*').eq('id', fCotSAP.cliente_id).single();
        if (data) {
          setClientes(cs => cs.map(c => c.id === data.id ? data : c));
          setSel(s => (s && s.id === data.id) ? { ...s, ...data } : s);
        }
        setFCotSAP(x => ({ ...x, cardManual:'' }));
        setResCotSAP(null);
        toast(d.yaExistia ? `Enlazado a ${d.cardCode} (ya existía en SAP)` : `Cliente creado en SAP: ${d.cardCode}`);
      }
    } catch (e) { setResCrearCliSAP({ ok:false, error:e.message }); }
    setCreandoCliSAP(false);
  };
  const [resCotSAP, setResCotSAP] = useState(null); // {ok, docNum, docTotal, moneda, pdf, error}
  // EL PRECIO LO DICE SAP, NO LA MEMORIA DEL VENDEDOR.
  // SAP valida que el precio coincida con la lista asignada al cliente y
  // rebota la cotizacion entera si no: "(3052022) El precio del Codigo MO-019
  // no coincide con el de la lista de precio asignada al cliente". Anthony
  // escribio 1450 y la lista decia 1500. Aca se le pregunta a SAP al escribir
  // el codigo y se llena solo.
  const [precioSAP, setPrecioSAP] = useState({});   // por indice de linea
  const buscarPrecio = async (i, itemCode, monedaOverride) => {
    const item = String(itemCode || '').trim();
    if (!item) { setPrecioSAP(p => { const q = { ...p }; delete q[i]; return q; }); return; }
    const cli = fCotSAP.cliente_id ? clientes.find(c => c.id === fCotSAP.cliente_id) : null;
    const emp = fCotSAP.sapEmp || cli?.empresa_id || empresaActiva || 'honduras';
    setPrecioSAP(p => ({ ...p, [i]: { cargando: true } }));
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'precio_item', empresa_id: emp,
                               cardCode: fCotSAP.cardManual || cli?.sap_code || '', itemCode: item,
                               // Sin esto SAP devuelve el precio en la moneda de la lista
                               // y se escribia tal cual: CO-25 vale USD 123.90 y la linea
                               // en lempiras quedaba en L.123.90.
                               moneda: monedaOverride || fCotSAP.moneda || 'USD' }) });
      const d = await r.json();
      setPrecioSAP(p => ({ ...p, [i]: d }));
      // Se llena solo si el vendedor no habia puesto precio todavia. Si ya
      // puso uno, no se le pisa: se le avisa y el decide.
      if (d.ok && d.precio) {
        setFCotSAP(x => {
          const ls = [...x.lineas];
          if (!ls[i]) return x;
          const nuevo = { ...ls[i] };
          if (!Number(nuevo.precio)) nuevo.precio = String(d.precio);
          if (!nuevo.descripcion && d.descripcion) nuevo.descripcion = d.descripcion;
          ls[i] = nuevo;
          return { ...x, lineas: ls };
        });
      }
    } catch (e) { setPrecioSAP(p => ({ ...p, [i]: { ok:false, error:'No se pudo consultar SAP.' } })); }
  };

  // CAMBIAR LA MONEDA TIENE QUE MOVER LOS MONTOS, NO SOLO LA ETIQUETA.
  // La 4244 (DICAHSA) estaba en dolares, se paso a lempiras y salio emitida
  // con los mismos numeros: la certificacion quedo en L.123.90 donde iban
  // L.3,348.76. Se convierten las DOS columnas -precio de lista y precio con
  // descuento- de TODAS las lineas, tengan ItemCode o no, porque las de
  // servicio libre no las cubre ninguna consulta a SAP.
  const [cambiandoMoneda, setCambiandoMoneda] = useState(false);
  const cambiarMonedaCot = async (nueva) => {
    const vieja = fCotSAP.moneda;
    if (!nueva || nueva === vieja) return;
    const cli = fCotSAP.cliente_id ? clientes.find(c => c.id === fCotSAP.cliente_id) : null;
    const emp = fCotSAP.sapEmp || cli?.empresa_id || empresaActiva || 'honduras';
    const hayMontos = fCotSAP.lineas.some(l => Number(l.precio) > 0 || Number(l.precioDesc) > 0);
    // Entre dos monedas locales no hay tasa: SAP solo mantiene la del dolar.
    if (vieja !== 'USD' && nueva !== 'USD') {
      setFCotSAP(x => ({ ...x, moneda: nueva }));
      if (hayMontos) toast(`No hay tipo de cambio entre ${vieja} y ${nueva}. Revisá los montos a mano.`);
      return;
    }
    if (!hayMontos) { setFCotSAP(x => ({ ...x, moneda: nueva })); return; }
    setCambiandoMoneda(true);
    let tasa = null;
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'tasa_sap', empresa_id: emp }) });
      const d = await r.json();
      if (d.ok && Number(d.tasa) > 1) tasa = Number(d.tasa);
    } catch (_) {}
    if (!tasa) {
      setCambiandoMoneda(false);
      setFCotSAP(x => ({ ...x, moneda: nueva }));
      toast('No se pudo traer el tipo de cambio de SAP. La moneda cambió pero los montos quedaron igual: revisalos antes de generar.');
      return;
    }
    const conv = (n) => {
      const v = Number(n);
      if (!v) return n;
      return String(Math.round((nueva === 'USD' ? v / tasa : v * tasa) * 100) / 100);
    };
    setFCotSAP(x => ({ ...x, moneda: nueva,
      lineas: x.lineas.map(l => ({ ...l, precio: conv(l.precio), precioDesc: conv(l.precioDesc) })) }));
    setCambiandoMoneda(false);
    toast(`Montos convertidos a ${nueva} a la tasa de SAP (${tasa.toLocaleString('en-US',{minimumFractionDigits:4})}).`);
    // Y que SAP confirme el de lista de las lineas que tienen codigo. No pisa
    // lo convertido -solo rellena vacios-, pero avisa si no coincide.
    fCotSAP.lineas.forEach((l, j) => { if (l.itemCode) buscarPrecio(j, l.itemCode, nueva); });
  };

  const masDias = (n) => new Date(Date.now()+n*86400000).toISOString().split('T')[0];
  // LA MONEDA POR DEFECTO ES LA DEL PAIS, NO DOLARES.
  // Abria siempre en USD y aca se cotiza en lempiras, asi que bastaba con no
  // tocar el selector para que el documento naciera marcado en dolares. Cuando
  // eso pasa, SAP convierte: un precio de 11,200 lempiras quedaba guardado como
  // 11,200 dolares y salia impreso multiplicado por el tipo de cambio.
  const monedaDe = (emp) => (emp === 'costarica' ? 'CRC' : 'HNL');
  const abrirCotSAP = (cli) => {
    setResCotSAP(null);
    const emp = cli?.empresa_id || (empresaActiva && empresaActiva !== 'all' ? empresaActiva : null)
      || (usuario.empresa_id && usuario.empresa_id !== 'all' ? usuario.empresa_id : null) || 'honduras';
    setFCotSAP({ cliente_id: cli?.id || '', cliente_nombre: cli?.empresa || '', moneda: monedaDe(emp), isv:'con', validez: masDias(15), comentarios:'', tipoDoc:'cotizacion', sapEmp:'', cardManual:'',
      // Se arranca con la linea del cliente: casi siempre se le cotiza lo mismo,
      // y asi el vendedor confirma en vez de tener que elegir de cero.
      linea_negocio: cli?.linea_negocio || '',
      lineas:[{descripcion:'',cantidad:'1',precio:'',precioDesc:'',itemCode:''}] });
    setModalCotSAP(true);
  };
  // Editar la cotizacion desde la oportunidad, que es donde el numero esta a la
  // vista. Antes habia que abrir el cotizador y volver a teclear el numero que
  // ya estaba en pantalla.
  const editarCotSAP = (num, emp) => {
    setResCotSAP(null);
    setCotEditar(null);
    setNumCargar(String(num));
    setModalCotSAP(true);
    cargarCotSAP(num, emp);
  };

  // SAP guarda el descuento como PORCENTAJE. El vendedor escribe el precio final,
  // que es lo que negocia, y aca se traduce. Si no hay precio con descuento o es
  // mayor que el de lista, no hay descuento.
  const pctDescuento = (l) => {
    const p = Number(l.precio) || 0;
    const d = (l.precioDesc === '' || l.precioDesc == null) ? null : Number(l.precioDesc);
    if (d == null || !p || d > p || d < 0) return null;
    // SEIS decimales, no cuatro. SAP guarda el descuento como PORCENTAJE y
    // despues recalcula el neto multiplicando: con cuatro decimales, 48,000 con
    // 40,000 de precio final daba 16.6667% y SAP devolvia 39,999.98. Dos
    // centavos que el cliente ve impresos en su cotizacion.
    return Math.round((1 - d / p) * 100000000) / 1000000;
  };

  const generarCotSAP = async () => {
    const esOrden = fCotSAP.tipoDoc === 'orden';
    const cardManual = (fCotSAP.cardManual || '').trim();
    if (!fCotSAP.cliente_id && !cardManual) { setResCotSAP({ ok:false, error:'Seleccioná un cliente o escribí el CardCode SAP manual.' }); return; }
    // Resolver el cliente (la búsqueda en vivo no trae sap_code/empresa_id)
    let cli = null;
    if (fCotSAP.cliente_id) {
      cli = clientes.find(c => c.id === fCotSAP.cliente_id);
      if (!cli || cli.sap_code === undefined) {
        const { data } = await supabase.from('clientes').select('id,empresa,sap_code,empresa_id').eq('id', fCotSAP.cliente_id).single();
        if (data) cli = data;
      }
    }
    if (!cardManual) {
      if (!cli) { setResCotSAP({ ok:false, error:'No se pudo cargar el cliente.' }); return; }
      if (!cli.sap_code) { setResCotSAP({ ok:false, error:'Ese cliente no tiene código SAP (usá el CardCode manual).' }); return; }
    }
    const lineas = fCotSAP.lineas
      .filter(l => l.descripcion || l.itemCode)
      .map(l => ({ descripcion:l.descripcion||null, cantidad:Number(l.cantidad)||1, precioUnitario:Number(l.precio)||0,
                   itemCode:l.itemCode||null, descuento: pctDescuento(l) }));
    if (!lineas.length) { setResCotSAP({ ok:false, error:'Agregá al menos una línea con descripción y precio.' }); return; }
    // Empresa EMISORA: la elegida en el modal manda (ej. Monhagro); si no, la del cliente.
    const empId = fCotSAP.sapEmp || cli?.empresa_id || empresaActiva || usuario.empresa_id || 'honduras';

    // EL CODIGO TIENE QUE SER DE LA EMPRESA QUE EMITE. Cada empresa es una base
    // de SAP distinta y no se conocen entre si: mandarle a Costa Rica un
    // CardCode de Monhaco devuelve "Invalid BP code", que no le dice nada a
    // nadie. Mejor avisarlo aca, con el nombre de las dos empresas y que hacer.
    if (!cardManual && cli?.sap_code) {
      const empDelCodigo = String(cli.sap_code).includes('_')
        ? String(cli.sap_code).split('_')[0] : null;
      if (empDelCodigo && empDelCodigo !== empId) {
        setResCotSAP({ ok:false, error:
          `"${cli.empresa}" tiene codigo de ${nombreEmpresa(empDelCodigo)} (${String(cli.sap_code).split('_')[1]}), `
          + `y estas emitiendo desde ${nombreEmpresa(empId)}. Cada empresa es una base de SAP aparte y no se ven entre si.\n\n`
          + `Dos salidas: emiti desde ${nombreEmpresa(empDelCodigo)}, o dale de alta en ${nombreEmpresa(empId)} `
          + `con el boton de abajo y usa ese codigo nuevo.` });
        return;
      }
    }

    setSavingCotSAP(true); setResCotSAP(null);
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          action:'crear_cotizacion_sap',
          empresa_id: empId, tipoDoc: fCotSAP.tipoDoc || 'cotizacion',
          linea_negocio: fCotSAP.linea_negocio || null,
          cardCode: cardManual || cli.sap_code, vendedor: usuario.nombre,
          moneda: fCotSAP.moneda || null, validez: fCotSAP.validez || null,
          exento: fCotSAP.isv === 'exento',
          comentarios: fCotSAP.comentarios || null, lineas, returnPdf: !esOrden,
          emisor: ORG.coti?.[empId] || null,
        }) });
      // El servidor SIEMPRE deberia responder JSON; si truena feo (500 de Vercel),
      // devuelve texto plano y r.json() reventaria con "Unexpected token 'A'".
      const raw = await r.text();
      let data;
      try { data = JSON.parse(raw); }
      catch {
        // Respuesta NO-JSON: error de transporte. Avisar al equipo (WhatsApp) y
        // mostrar algo entendible en vez del error cru­do de parseo.
        fetch('/api/error-report', { method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ mensaje:`[COTIZADOR SAP] respuesta no-JSON (HTTP ${r.status}): ${String(raw).slice(0,200)}`,
            url: window.location.href, usuario: usuario.nombre }) }).catch(()=>{});
        data = { ok:false, error: r.status>=500 ? 'El servidor SAP tuvo un error inesperado. El equipo ya fue notificado — reintentá en un momento.' : 'Respuesta inesperada del servidor.' };
      }
      setResCotSAP(data.ok ? { ok:true, empresa_id:empId, esOrden, ...data } : { ok:false, error:data.error||`SAP rechazó la ${esOrden?'orden de venta':'cotización'}.` });
      // La cotizacion ya quedo en RADAR del lado del servidor; se recarga el
      // pipeline para que aparezca sin tener que refrescar la pagina ni correr
      // el sync de SAP.
      if (data.ok && !esOrden) cargar(empresaActiva);
    } catch (e) {
      // Error de red (fetch fallo). Reportar y mostrar mensaje claro.
      fetch('/api/error-report', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ mensaje:`[COTIZADOR SAP] fallo de red: ${String(e?.message||e).slice(0,200)}`,
          url: window.location.href, usuario: usuario.nombre }) }).catch(()=>{});
      setResCotSAP({ ok:false, error: e.message });
    }
    setSavingCotSAP(false);
  };
  // Traer una cotizacion que ya existe en SAP para editarla. Se busca por
  // NUMERO, que es lo que la gente tiene a mano, no por el id interno.
  const cargarCotSAP = async (numDirecto, empDirecta) => {
    const num = String(numDirecto ?? numCargar).trim();
    if (!num) return;
    const empId = empDirecta || fCotSAP.sapEmp || empresaActiva || usuario.empresa_id || 'honduras';
    setCargandoCot(true); setResCotSAP(null);
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'cargar_cotizacion_sap', empresa_id: empId, docNum: num }) });
      const d = await r.json().catch(()=>({}));
      if (!d.ok) { setResCotSAP({ ok:false, error:d.error||'No se pudo cargar.' }); return; }
      if (!d.abierta) { setResCotSAP({ ok:false, error:'Esa cotización ya está cerrada o convertida en orden. En SAP no se puede editar: hay que hacer una nueva.' }); return; }
      setCotEditar({ docEntry:d.docEntry, docNum:d.docNum, cardName:d.cardName, empresa_id:empId });
      setFCotSAP(x=>({ ...x, sapEmp:empId, cliente_nombre:d.cardName||'', cardManual:d.cardCode||'',
        moneda:d.moneda==='LPS'?'HNL':(d.moneda==='COL'?'CRC':d.moneda||'USD'),
        validez:d.validez||'', comentarios:d.comentarios||'',
        lineas:(d.lineas||[]).map(l=>({ itemCode:l.itemCode||'', descripcion:l.descripcion||'',
          cantidad:l.cantidad, precio:l.precio, taxCode:l.taxCode, warehouseCode:l.warehouseCode,
          // SAP devuelve el descuento en %, pero en pantalla se trabaja con el
          // precio final: se reconstruye para que se vea lo mismo que se escribio.
          // El descuento se acota: un documento viejo puede traer un porcentaje
          // absurdo guardado por SAP y no tiene por que ensuciar el formulario.
          precioDesc: l.descuento > 0 && l.descuento < 100
            ? Math.round(l.precio * (1 - Math.min(100, l.descuento)/100) * 100) / 100 : '' })) }));
    } catch(e){ setResCotSAP({ ok:false, error:String(e?.message||e) }); }
    finally { setCargandoCot(false); }
  };

  // CAMBIARLE LA MONEDA A LA COTIZACION QUE ESTA ABIERTA EN EL MODAL.
  // Lo hace SAP, no la pantalla: se le pide el cambio y despues se recarga el
  // documento para mostrar exactamente lo que quedo guardado. Antes esto no
  // existia -se creia que SAP no lo permitia- y el camino era rehacerla como
  // nueva, que es lo que descuadro la 4244.
  const cambiarMonedaEnSAP = async (nueva) => {
    if (!cotEditar || !nueva || nueva === fCotSAP.moneda) return;
    const anterior = fCotSAP.moneda;
    setCambiandoMoneda(true); setResCotSAP(null);
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'cambiar_moneda_cotizacion', empresa_id: cotEditar.empresa_id,
                               docEntry: cotEditar.docEntry, moneda: nueva }) });
      const d = await r.json().catch(()=>({}));
      if (!d.ok) {
        setResCotSAP({ ok:false, error: d.error || 'SAP no dejó cambiar la moneda.' });
        return;   // la pantalla no se toca: sigue mostrando la moneda real
      }
      toast(`La ${d.docNum} quedó en ${nueva} (tasa ${Number(d.tasa).toLocaleString('en-US',{minimumFractionDigits:4})}).`);
      await cargarCotSAP(cotEditar.docNum, cotEditar.empresa_id);
    } catch (e) {
      setResCotSAP({ ok:false, error:`No se pudo cambiar la moneda: ${String(e?.message||e)}. Sigue en ${anterior}.` });
    } finally { setCambiandoMoneda(false); }
  };

  const guardarCotSAP = async () => {
    if (!cotEditar) return;
    const lineas = fCotSAP.lineas
      .filter(l => l.descripcion || l.itemCode)
      .map(l => ({ descripcion:l.descripcion||null, cantidad:Number(l.cantidad)||1,
        precio:Number(l.precio)||0, itemCode:l.itemCode||null, descuento: pctDescuento(l),
        taxCode:l.taxCode||null, warehouseCode:l.warehouseCode||null }));
    if (!lineas.length) { setResCotSAP({ ok:false, error:'Tiene que quedar al menos una línea.' }); return; }
    setSavingCotSAP(true); setResCotSAP(null);
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'editar_cotizacion_sap', empresa_id: cotEditar.empresa_id,
          docEntry: cotEditar.docEntry, lineas, comentarios: fCotSAP.comentarios || '',
          validez: fCotSAP.validez || null, exento: fCotSAP.isv === 'exento' }) });
      const d = await r.json().catch(()=>({}));
      setResCotSAP(d.ok ? { ok:true, editada:true, empresa_id:cotEditar.empresa_id, ...d }
                        : { ok:false, error:d.error||'SAP rechazó el cambio.' });
    } catch(e){ setResCotSAP({ ok:false, error:String(e?.message||e) }); }
    setSavingCotSAP(false);
  };

  const verPdfCotSAP = () => {
    if (!resCotSAP?.pdf?.base64) return;
    // Abrir el PDF en una pestaña nueva para verlo de inmediato
    const bin = atob(resCotSAP.pdf.base64);
    const bytes = new Uint8Array(bin.length);
    for (let i=0;i<bin.length;i++) bytes[i]=bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes],{type:'application/pdf'}));
    window.open(url,'_blank');
    setTimeout(()=>URL.revokeObjectURL(url),60000);
  };
  const descargarPdfCotSAP = () => {
    if (!resCotSAP?.pdf?.base64) return;
    const a = document.createElement('a');
    a.href = `data:application/pdf;base64,${resCotSAP.pdf.base64}`;
    a.download = `${resCotSAP.esFactura?'Factura':'Cotizacion'}-${resCotSAP.docNum}.pdf`;
    a.click();
  };
  // Paso 2 del flujo: facturar la orden de venta recién creada (SAP copia la orden
  // a factura y le pone su correlativo fiscal). RADAR devuelve el PDF descargable.
  const [facturando, setFacturando] = useState(false);
  const facturarOrdenSAP = async () => {
    if (!resCotSAP?.docEntry) return;
    if (!(await confirmar(`¿Facturar la orden #${resCotSAP.docNum}? SAP emitirá la factura fiscal (no se puede deshacer).`, { ok:'Sí, facturar' }))) return;
    setFacturando(true);
    try {
      const empId = resCotSAP.empresa_id || 'honduras';
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'facturar_orden_sap', empresa_id: empId, docEntry: resCotSAP.docEntry, emisor: ORG.coti?.[empId] || null }) });
      const d = await r.json();
      if (d.ok) { setResCotSAP({ ok:true, empresa_id:empId, esFactura:true, ...d }); toast(`Factura #${d.docNum} emitida en SAP.`); }
      else toast('No se pudo facturar: '+(d.error||'error de SAP'));
    } catch (e) { toast('No se pudo facturar: '+e.message); }
    setFacturando(false);
  };
  const [reintentandoPdf, setReintentandoPdf] = useState(false);
  const reintentarPdfCotSAP = async () => {
    if (!resCotSAP?.docNum && !resCotSAP?.docEntry) return;
    setReintentandoPdf(true);
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'get_pdf', empresa_id: resCotSAP.empresa_id || empresaActiva || 'honduras', docNum: resCotSAP.docNum, docEntry: resCotSAP.docEntry }) });
      const data = await r.json();
      if (data.ok && data.base64) setResCotSAP(x=>({...x, pdf:data}));
      else setResCotSAP(x=>({...x, pdf:{ok:false,error:data.error||'No se pudo generar el PDF'}}));
    } catch (e) { setResCotSAP(x=>({...x, pdf:{ok:false,error:e.message}})); }
    setReintentandoPdf(false);
  };

  const normRtn = s => (s||'').replace(/[^0-9a-zA-Z]/g,'').toLowerCase();
  // A que empresa pertenece una ficha de cliente. Solo se ofrecen las empresas
  // vivas (ORG.empresas): Montasa Honduras se cerro y no debe recibir altas
  // nuevas, aunque sus 3,314 clientes historicos sigan existiendo.
  const EMPRESAS_ALTA = ORG.empresas.map(e => e.id);
  const empPorDefecto = EMPRESAS_ALTA.includes(empresaActiva) ? empresaActiva : (EMPRESAS_ALTA[0] || 'honduras');
  const empDe = c => c?.empresa_id || empPorDefecto;

  const crearCliente = async () => {
    if (!fCl.empresa) return; setSavingCl(true); setDupCliente(null);
    // LA EMPRESA DECIDE SI ES DUPLICADO. Cada empresa_id es una base de SAP
    // distinta -Monhaco, Montasa Honduras, Costa Rica, Monhagro- y el mismo
    // cliente puede existir en varias, porque facturan por separado. Antes la
    // revision barria toda la cartera sin mirar empresa, asi que dar de alta en
    // Monhaco a alguien que ya estaba en Montasa Honduras era imposible: justo
    // lo que hay que hacer para pasar esa cartera.
    // Dentro de UNA empresa el candado sigue igual de apretado: de ahi salieron
    // los 201 duplicados que hubo que limpiar en agosto.
    const emp = empDe(fCl);
    const mismaEmp = c => empDe(c) === emp;
    const rtnN = normRtn(fCl.rtn);
    if (rtnN) {
      const dup = clientes.find(c => normRtn(c.rtn) === rtnN && mismaEmp(c));
      if (dup) { setDupCliente({ ...dup, motivo:'RTN' }); setSavingCl(false); return; }
    }
    const nomN = String(fCl.empresa).trim().toUpperCase();
    const dupNom = clientes.find(c => String(c.empresa||'').trim().toUpperCase() === nomN && c.estado !== 'inactivo' && mismaEmp(c));
    if (dupNom) { setDupCliente({ ...dupNom, motivo:'nombre' }); setSavingCl(false); return; }

    const {data,error} = await supabase.from('clientes').insert({...fCl, empresa_id: emp}).select().single();
    if (error) {
      // 23505 lo levantan tanto los indices unicos como el trigger
      // trg_clientes_no_duplicar, que es la ultima red por si la lista en
      // memoria estaba desactualizada.
      if (error.code === '23505') {
        const dup = clientes.find(c => normRtn(c.rtn) === normRtn(fCl.rtn) && normRtn(fCl.rtn) && empDe(c) === emp)
          || clientes.find(c => String(c.empresa||'').trim().toUpperCase() === nomN && empDe(c) === emp)
          || { empresa: fCl.empresa, id:null };
        setDupCliente({ ...dup, motivo: 'nombre', detalle: error.message });
      }
      setSavingCl(false); return;
    }
    if (data) { setClientes(cs=>[...cs,data].sort((a,b)=>String(a.empresa||'').localeCompare(String(b.empresa||'')))); setSel(data); setTab('clientes'); }
    setSavingCl(false); setModalCliente(false);
    setFCl({empresa:'',rtn:'',contacto:'',cargo:'',telefono:'',email:'',ciudad:'',pais:'Honduras',industria:'',vendedor:'',segmento:'',iniciativa:'',estado:'activo',empresa_id:emp});
  };

  const crearProspecto = async () => {
    if (!fProspecto.empresa) return;
    setSavingProspecto(true); setErrorProspecto('');
    const { data, error } = await supabase.from('oportunidades').insert({
      titulo: fProspecto.titulo || fProspecto.empresa,
      cliente: fProspecto.empresa,
      vendedor: fProspecto.vendedor || usuario.nombre,
      etapa: 'prospecto',
      valor: 0,
      notas: fProspecto.telefono ? `Tel: ${fProspecto.telefono}` : null,
      ultimo_seguimiento: new Date().toISOString().split('T')[0],
      empresa_id: empresaActiva || 'honduras',
    }).select().single();
    if (error) { setErrorProspecto(error.message); setSavingProspecto(false); return; }
    if (data) {
      setOpps(os => [...os, data]);
      if (fProspecto.vendedor && fProspecto.vendedor !== usuario.nombre) {
        notificar({ para: fProspecto.vendedor, tipo: 'prospecto_asignado',
          titulo: 'Te asignaron un prospecto',
          mensaje: `Nuevo prospecto asignado por ${usuario.nombre}: "${data.titulo}" — ${data.cliente}`,
          modulo: 'crm', refId: data.id , orgId: usuario?.org_id || null }).then(()=>{});
      }
    }
    setSavingProspecto(false); setModalProspecto(false);
    setFProspecto({empresa:'',titulo:'',telefono:'',vendedor:''});
  };

  const eliminarOpp = async (opp) => {
    const { error } = await supabase.from('oportunidades').delete().eq('id', opp.id);
    if (error) { toast('No se pudo eliminar: '+error.message); setConfirmEliminarOpp(null); return; }
    setOpps(os => os.filter(o => o.id !== opp.id));
    if (sel?.id === opp.id) setSel(null);
    setConfirmEliminarOpp(null);
  };

  const guardarEditCliente = async () => {
    if (!fEditCl.empresa||!fEditCl.id) return; setSavingEditCl(true);
    const {id, ...upd} = fEditCl;
    upd.editado_manual_at = new Date().toISOString();   // S5: el sync no pisa esta edicion
    const previo = clientes.find(c=>c.id===id)?.vendedor || null;
    const { error } = await supabase.from('clientes').update(upd).eq('id', id);
    if (error) { toast('No se pudo guardar el cliente: '+error.message); setSavingEditCl(false); return; }
    // El vendedor se entera al instante: campanita con "te asignaron cliente".
    if (upd.vendedor && upd.vendedor !== previo && upd.vendedor !== usuario.nombre) {
      notificar({ para: upd.vendedor, tipo: 'cliente_asignado',
        titulo: 'Te asignaron un cliente',
        mensaje: `${usuario.nombre} te asignó el cliente "${upd.empresa}"`,
        modulo: 'crm', refId: id , orgId: usuario?.org_id || null });
    }
    setClientes(cs=>cs.map(c=>c.id===id?{...c,...upd}:c));
    setSel(s=>({...s,...upd}));
    setSavingEditCl(false); setModalEditCl(false);
  };
  // Asignación rápida desde la tabla (el flujo de Claudia): elegir vendedor
  // en la fila, guardar y notificar, sin abrir la ficha.
  // Las lineas de una cotizacion, sin salir de la ficha. Salen de la tabla, no
  // de SAP: antes esto era un viaje por cada cotizacion que uno abriera.
  const verLineasCliente = async (cot) => {
    const llave = `${cot.empresa_id}:${cot.docNum}`;
    if (cotAbiertaCl === llave) { setCotAbiertaCl(null); return; }
    setCotAbiertaCl(llave);
    if (cotLineasCl[llave]) return;
    const { data, error } = await supabase.from('cotizacion_lineas_sap')
      .select('linea_num,item_code,descripcion,cantidad,precio,total_linea,moneda')
      .eq('empresa_id', cot.empresa_id).eq('doc_num', cot.docNum)
      .order('linea_num');
    setCotLineasCl(l => ({ ...l, [llave]: { lineas: data || [], error: error?.message || null } }));
  };

  // El PDF de la cotizacion. La tabla guarda el contenido, pero el documento
  // impreso lo arma SAP con su formato: para eso se le pide en el momento.
  const bajarPdfCot = async (cot) => {
    setBajandoCot(cot.docNum);
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'get_pdf', proforma:false, empresa_id: cot.empresa_id,
                               docNum: cot.docNum, docEntry: cot.docEntry || undefined,
                               emisor: ORG.coti?.[cot.empresa_id] || null }) });
      const d = await r.json();
      if (d.ok && d.base64) {
        const bin = atob(d.base64); const by = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) by[i] = bin.charCodeAt(i);
        const url = URL.createObjectURL(new Blob([by], { type:'application/pdf' }));
        const a2 = Object.assign(document.createElement('a'), { href:url, download:`Cotizacion-${cot.docNum}.pdf` });
        document.body.appendChild(a2); a2.click(); a2.remove();
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else toast('No se pudo bajar el PDF: ' + (d.error || 'SAP no respondio'));
    } catch (e) { toast('No se pudo bajar el PDF: ' + (e?.message || 'sin respuesta')); }
    setBajandoCot(null);
  };

  const asignarClienteRapido = async (c, vendedor) => {
    // vendedor_manual_at es lo que impide que el sync de SAP le devuelva el
    // vendedor viejo en la proxima corrida (ver sql/vendedor-manual.sql).
    const ahora = new Date().toISOString();

    // ESTE BOTON NO PUEDE FALLAR EN SILENCIO, Y FALLABA DE DOS FORMAS.
    // 1. Un update que no toca ninguna fila -porque RLS no deja- NO devuelve
    //    error: devuelve error null y cero filas. La pantalla pintaba al
    //    vendedor, la base seguia igual, y al recargar volvia a estar libre.
    // 2. Si el request se cae antes de responder, la promesa se rechaza y aqui
    //    no habia try: no salia ni el toast de error. "Le puse asignar tres
    //    veces y no pasa nada" era exactamente esto.
    // Por eso ahora se pide .select(): si no vuelve la fila, no se asigno.
    let filas = null;
    try {
      const { data, error } = await supabase.from('clientes').update({ vendedor,
        editado_manual_at: ahora,
        vendedor_manual_at: ahora,
        vendedor_manual_por: usuario?.nombre || null,
      }).eq('id', c.id).select('id,vendedor');
      if (error) { toast('No se pudo asignar: ' + error.message); return; }
      filas = data;
    } catch (e) {
      toast('No se pudo asignar: ' + (e?.message || 'no hubo respuesta de la base'));
      return;
    }
    if (!filas || !filas.length) {
      toast(`No se pudo asignar "${c.empresa}": la base rechazo el cambio sin dar razon.`
          + ' Suele ser permisos. Avisale a soporte y no lo intentes de nuevo.');
      return;
    }
    if (vendedor && vendedor !== usuario.nombre) {
      notificar({ para: vendedor, tipo: 'cliente_asignado',
        titulo: 'Te asignaron un cliente',
        mensaje: `${usuario.nombre} te asignó el cliente "${c.empresa}"`,
        modulo: 'crm', refId: c.id , orgId: usuario?.org_id || null });
    }
    // Hay TRES lugares donde puede estar ese cliente en pantalla y hay que
    // refrescar los tres. Solo se refrescaba la lista general: si uno venia de
    // buscar (que es lo normal para asignar), la tabla mostrada era la de
    // resultados de busqueda y seguia pintando el cliente sin vendedor. La base
    // guardaba bien y la pantalla decia que no.
    const ponerVend = x => x.id===c.id ? {...x, vendedor, vendedor_manual_at: ahora, vendedor_manual_por: usuario?.nombre || null} : x;
    setClientes(cs => cs.map(ponerVend));
    setClBusqResults(r => r ? r.map(ponerVend) : r);
    setSel(x => (x && x.id===c.id) ? ponerVend(x) : x);
    toast(`"${c.empresa}" asignado a ${vendedor}.`);
  };

  const cargar = async (eid) => {
    // bloqueo/bloqueo_desde/pago_anticipado_sap se LEEN en la ficha pero no venian en
    // el select: el bloqueo interno salia siempre vacio y el aviso de pago anticipado
    // nunca aparecia.
    const OPP_COLS = 'id,etapa,titulo,cliente,cliente_id,vendedor,valor,fecha_cierre,ultimo_seguimiento,notas,tipo,linea_negocio,proxima_accion,sap_quotation_num,factura_sap_num,created_at,empresa_id,descartado_por,bloqueo,bloqueo_desde,pago_anticipado_sap';
    const esVentas = usuario.rol === 'ventas';
    // Supabase corta cada request a 1000 filas (db-max-rows). Con >1000 oportunidades,
    // un solo request truncaba de forma arbitraria => cada filtro de país mostraba un
    // subconjunto distinto e incompleto. Paginamos por id (PK indexada, no truena) hasta
    // traer TODO, y ordenamos/filtramos en JS. Ordenar por ultimo_seguimiento en la DB
    // sobre toda la tabla (columna sin índice) excedía el statement_timeout: por eso el id.
    // Supabase corta cada request a 1000 filas, asi que hay que paginar. Antes se
    // pedia una pagina, se esperaba la respuesta, se pedia la siguiente: con 6,600
    // clientes son SIETE viajes en fila india y el usuario espera la suma de los
    // siete. Ahora la primera pagina viene con el total (count), y con ese numero
    // se piden todas las demas a la vez: se espera el viaje mas lento, no la suma.
    const fetchPaged = async (build) => {
      const PAGE = 1000;
      const primera = await build({ count: 'exact' }).order('id',{ascending:true}).range(0, PAGE-1);
      if (primera.error) { console.error('cargar() fetchPaged', primera.error); return []; }
      const all = [...(primera.data || [])];
      const total = primera.count ?? all.length;
      if (total > PAGE) {
        const desdes = [];
        for (let from = PAGE; from < total; from += PAGE) desdes.push(from);
        const paginas = await Promise.all(
          desdes.map(f => build().order('id',{ascending:true}).range(f, f+PAGE-1)));
        for (const p of paginas) {
          if (p.error) { console.error('cargar() fetchPaged', p.error); continue; }
          if (p.data) all.push(...p.data);
        }
      }
      return all;
    };
    const buildOp = (opt) => {
      let q = supabase.from('oportunidades').select(OPP_COLS, opt).not('etapa','eq','prospecto');
      if (esVentas) {
        const extra = VER_TODO_EMP.length ? `,empresa_id.in.(${VER_TODO_EMP.join(',')})` : '';
        q = q.or(`vendedor.eq."${usuario.nombre}",vendedor.is.null${extra}`);
      }
      // eid==='all' (país "Todo") = sin filtro de empresa: traer TODO. Con país específico,
      // incluir igual las opps "all"/sin empresa (agnósticas) para no esconderlas.
      if (eid && eid !== 'all') q = q.or(`empresa_id.eq.${eid},empresa_id.eq.all,empresa_id.is.null`);
      return q;
    };
    const buildProsp = (opt) => supabase.from('oportunidades').select(OPP_COLS, opt).eq('etapa','prospecto');
    const buildCl = (opt) => {
      let q = supabase.from('clientes').select('*', opt).neq('estado','inactivo');
      if (eid && eid !== 'all') q = q.eq('empresa_id',eid);
      return q;
    };

    const [cl, op, prospAll] = await Promise.all([ fetchPaged(buildCl), fetchPaged(buildOp), fetchPaged(buildProsp) ]);
    const nombre = (usuario.nombre||'').trim().toLowerCase();
    const prosp = (esVentas
      ? prospAll.filter(o => !o.vendedor || (o.vendedor||'').trim().toLowerCase() === nombre
                             || VER_TODO_EMP.includes(o.empresa_id))
      : prospAll
    ).sort((a,b)=>String(b.created_at||'').localeCompare(String(a.created_at||''))); // prospectos por created_at desc

    const prospIds = new Set(prosp.map(o=>o.id));
    // no-prospectos por ultimo_seguimiento asc (más urgente arriba)
    const opSorted = op.slice().sort((a,b)=>
      String(a.ultimo_seguimiento||'9999-12-31').localeCompare(String(b.ultimo_seguimiento||'9999-12-31')));
    const merged = [...opSorted.filter(o=>!prospIds.has(o.id)), ...prosp];
    cl.sort((a,b)=>String(a.empresa||'').localeCompare(String(b.empresa||''))); // clientes por nombre
    setClientes(cl); setOpps(merged); setLoading(false);
  };
  useEffect(()=>{ cargar(); },[empresaActiva]);

  // ── Exportar TODAS las oportunidades — el usuario elige CSV o Excel (.xlsx) ──
  const [exportando, setExportando] = useState(false);
  const [menuAcciones, setMenuAcciones] = useState(false);
  const descargar = (bytes, mime, nombre) => {
    const a = Object.assign(document.createElement('a'),{
      href: URL.createObjectURL(new Blob([bytes],{type:mime})), download:nombre
    });
    document.body.appendChild(a); a.click(); a.remove();
  };
  const exportarOpps = async (fmt) => {
    setMenuExport(false); setExportando(true);
    try {
      // Jala la tabla completa con todas las columnas, paginando por id (sin filtros).
      const PAGE = 1000; const all = []; let from = 0;
      for (;;) {
        const { data, error } = await supabase.from('oportunidades').select('*').order('id',{ascending:true}).range(from, from+PAGE-1);
        if (error) { toast('Error al exportar: '+error.message); setExportando(false); return; }
        if (!data || !data.length) break;
        all.push(...data);
        if (data.length < PAGE) break;
        from += PAGE;
      }
      if (!all.length) { toast('No hay oportunidades para exportar.'); setExportando(false); return; }
      // Cruzar el contacto REAL del cliente (el teléfono vive en clientes, no en la oportunidad).
      const cmap = {};
      { let cf = 0;
        for (;;) {
          const { data, error } = await supabase.from('clientes').select('id,empresa,contacto,telefono,email,ciudad').order('id',{ascending:true}).range(cf, cf+PAGE-1);
          if (error) break;
          if (!data || !data.length) break;
          data.forEach(c=>{ cmap[c.id]=c; });
          if (data.length < PAGE) break;
          cf += PAGE;
        }
      }
      all.forEach(o=>{
        const c = o.cliente_id!=null ? cmap[o.cliente_id] : null;
        o.cliente_telefono = c?.telefono || '';
        o.cliente_contacto = c?.contacto || '';
        o.cliente_email    = c?.email || '';
        o.cliente_ciudad   = c?.ciudad || '';
      });
      // Campos fijos por ahora (el selector de campos vendrá en el módulo "Exportar").
      const SPEC = [
        ['Vendedor','vendedor'], ['Etapa','etapa'], ['Valor','valor'],
        ['Nombre del Cliente','cliente'], ['Número de teléfono','cliente_telefono'],
        ['Ubicación','cliente_ciudad'], ['Línea de Negocio','linea_negocio'],
        ['Fecha','fecha_cierre'], ['Nota','notas'], ['Título','titulo'],
      ];
      const cols = SPEC.map(s=>s[0]);
      const outRows = all.map(o => { const r={}; SPEC.forEach(([h,k])=>{ r[h]=o[k]; }); return r; });
      const fecha = new Date().toISOString().split('T')[0];
      if (fmt==='xlsx') {
        descargar(buildXlsxReporte(all), 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', `oportunidades_RADAR_${fecha}.xlsx`);
      } else {
        const esc = v => {
          if (v===null||v===undefined) return '';
          let s = (typeof v==='object') ? JSON.stringify(v) : String(v);
          if (/[",\n;]/.test(s)) s = '"'+s.replace(/"/g,'""')+'"';
          return s;
        };
        const csv = [cols.join(','), ...outRows.map(r=>cols.map(c=>esc(r[c])).join(','))].join('\r\n');
        descargar('﻿'+csv, 'text/csv;charset=utf-8;', `oportunidades_RADAR_${fecha}.csv`);
      }
    } catch(e){ toast('Error al exportar: '+(e?.message||e)); }
    setExportando(false);
  };
  // Limpiar el cuadro de nota al cambiar de oportunidad/cliente: evita que el
  // comentario escrito para uno se arrastre y se guarde en otro de la misma etapa.
  // Además carga el historial de seguimientos de la oportunidad abierta.
  useEffect(()=>{
    setNota(''); setSegExpand(false); setSegHist([]); setEtapaHist([]); setDiasExpand(false);
    if (tab==='pipeline' && sel?.id) {
      supabase.from('opp_seguimientos').select('*').eq('opp_id',sel.id).order('created_at',{ascending:false})
        .then(({data})=>setSegHist(data||[]));
      supabase.from('opp_etapa_historial').select('etapa,created_at').eq('opp_id',sel.id).order('created_at',{ascending:true})
        .then(({data})=>setEtapaHist(data||[]));
      setTelInput('');
      // Teléfono del cliente: por id si la opp está enlazada; si NO lo está (típico de
      // opps importadas de SAP sin match de CardCode), se busca por NOMBRE para no
      // perder el contacto. Así el botón de WhatsApp aparece igual.
      (async () => {
        let tel = '';
        if (sel.cliente_id) {
          const { data } = await supabase.from('clientes').select('telefono').eq('id',sel.cliente_id).maybeSingle();
          tel = data?.telefono || '';
        }
        if (!tel && (sel.cliente||'').trim().length >= 3) {
          const nom = sel.cliente.trim().replace(/[%_]/g,'\\$&');
          let q = supabase.from('clientes').select('id,telefono').not('telefono','is',null).neq('telefono','')
            .ilike('empresa', nom).limit(1);
          let { data } = await q;
          if (!data?.length) { const r = await supabase.from('clientes').select('id,telefono').not('telefono','is',null).neq('telefono','').ilike('empresa', `%${nom}%`).limit(1); data = r.data; }
          if (data?.length) tel = data[0].telefono || '';
        }
        setSelClienteTel(tel);
      })();
    }
  },[sel?.id, tab]);
  // ── Chat de WhatsApp del vendedor con este cliente (espejo de WA Equipo) ──
  // Cruza wa_equipo_chats.cliente_numero con el teléfono del cliente (o el de la
  // nota) por los últimos 8 dígitos, así matchea con o sin +504. Solo lectura:
  // el vendedor sigue usando su WhatsApp normal. RLS protege la bandeja privada.
  useEffect(() => {
    if (tab!=='pipeline' || !sel?.id) { setWaChat(null); setWaMsgs([]); return; }
    const telNota = (sel.notas||'').match(/(?:Tel|Teléfono|Telefono|Cel|Celular|WhatsApp|Wsp)[:\s]*(\+?[\d\s\-().]{7,})/i);
    const dig = s => String(s||'').replace(/\D/g,'');
    const base = dig(selClienteTel) || (telNota ? dig(telNota[1]) : '');
    if (base.length < 8) { setWaChat(null); setWaMsgs([]); return; }
    const ult8 = base.slice(-8);
    let vivo = true;
    (async () => {
      setWaCargando(true);
      const { data: chats } = await supabase.from('wa_equipo_chats')
        .select('id,vendedor,chat_jid,cliente_nombre,cliente_numero,mensajes_total,ultima_actividad,categoria,resumen')
        .ilike('cliente_numero', `%${ult8}%`).order('ultima_actividad',{ascending:false}).limit(5);
      if (!vivo) return;
      // Preferir el chat del vendedor dueño de la oportunidad
      const mio = (chats||[]).find(c => c.vendedor === sel.vendedor) || (chats||[])[0] || null;
      setWaChat(mio);
      if (mio) {
        const { data: msgs } = await supabase.from('wa_equipo_mensajes')
          .select('direccion,contenido,tipo,ts').eq('vendedor',mio.vendedor).eq('chat_jid',mio.chat_jid)
          .order('ts',{ascending:false}).limit(40);
        if (vivo) setWaMsgs((msgs||[]).reverse());
      } else setWaMsgs([]);
      if (vivo) setWaCargando(false);
    })();
    return () => { vivo = false; };
  },[sel?.id, sel?.vendedor, selClienteTel, tab]); // eslint-disable-line
  useEffect(()=>{
    if (busq.length < 3) { setClBusqResults(null); return; }
    const b = busq.replace(/[%_]/g,'\\$&');
    const timer = setTimeout(async () => {
      // Incluye RTN, teléfono y código SAP. El RTN puede venir con guiones en la BD,
      // así que se busca el texto tal cual Y sin separadores.
      const bNum = busq.replace(/[^0-9a-zA-Z]/g,'');
      const ors = [`empresa.ilike.%${b}%`, `contacto.ilike.%${b}%`, `rtn.ilike.%${b}%`, `telefono.ilike.%${b}%`, `sap_code.ilike.%${b}%`];
      if (bNum.length >= 4 && bNum !== b) ors.push(`rtn.ilike.%${bNum}%`, `telefono.ilike.%${bNum}%`);
      let q = supabase.from('clientes').select('*').neq('estado','inactivo')
        .or(ors.join(','))
        .order('empresa').limit(50);
      if (empresaActiva) q = q.eq('empresa_id', empresaActiva);
      const { data } = await q;
      setClBusqResults(data || []);
    }, 300);
    return () => clearTimeout(timer);
  }, [busq, empresaActiva]);
  // Cual de las cotizaciones del cliente se esta mirando. Antes solo se podia
  // trabajar con la de ESTA oportunidad, asi que para ver una vieja habia que
  // salir a buscarla en SAP.
  const [cotSel, setCotSel] = useState(null);
  useEffect(()=>{ setSapLines(null); setSapPDF(null); setCotSel(null); },[sel?.id]);
  // Equipos con precios (para cotizar desde la lista de precios)
  useEffect(()=>{
    // Sin filtrar por empresaActiva A PROPOSITO: equipos.empresa_id guarda
    // monhaco/mtrental y clientes.empresa_id guarda montasa/honduras/costarica.
    // Son dos vocabularios distintos, asi que el filtro dejaba la lista vacia
    // para quien estuviera parado en montasa o costarica. La flota es una sola.
    supabase.from('equipos')
      .select('id,codigo,marca,modelo,tipo_equipo,estado,empresa_id,precio_renta_mes,precio_renta_semana,precio_renta_dia,precio_renta_hora,precio_venta')
      .order('codigo').limit(2000)
      .then(({data})=>setEquiposPrecio(data||[]));
  },[]);
  useEffect(()=>{
    supabase.from('radar_usuarios').select('nombre').in('rol',['ventas','gerente_ventas','admin']).eq('activo',true)
      // Solo el equipo de ventas real + "Administracion" (venta de la casa). Antes
      // salían todos los usuarios (Omar, Fabrizio, Elia…) que no son vendedores.
      .then(({data})=>setVendedoresList([...(data||[]).map(u=>u.nombre).filter(esVendedorReal).sort(), 'Administracion']));
    // FILTRAR POR ORG. Sin esto la consulta traia tambien la tasa de la org DEMO
    // -congelada en agosto- y ganaba la que Postgres devolviera de ultimo, asi
    // que la conversion HNL/USD de todo el modulo bailaba entre 26.86 y 26.78
    // segun el orden de las filas.
    (usuario?.org_id
      ? supabase.from('radar_config').select('key,value').eq('org_id', usuario.org_id).in('key',['tasa_hnl_usd','tasa_crc_usd'])
      : supabase.from('radar_config').select('key,value').in('key',['tasa_hnl_usd','tasa_crc_usd']))
      .then(({data})=>{
        (data||[]).forEach(r=>{
          if (r.key==='tasa_hnl_usd'&&r.value?.rate) setTasaHNL(r.value.rate);
          if (r.key==='tasa_crc_usd'&&r.value?.rate) setTasaCRC(r.value.rate);
        });
      });
  },[usuario?.org_id]);

  // ── COTIZAR UN EQUIPO DE LA FLOTA ────────────────────────────────────────
  // Los precios de equipos estan en USD; la cotizacion puede ir en lempiras o
  // colones, asi que se convierten al vuelo con la misma tasa del modulo.
  // La SEMANA no tiene tarifa propia en la flota. No se inventa multiplicando
  // el precio por dia (que es tarifa de alquiler corto y saldria mas caro que el
  // mes): se saca de la mensual entre 4. Es un punto de partida editable.
  // LA SEMANA ES UN PRECIO PROPIO, NO EL MES ENTRE CUATRO.
  // Se calculaba dividiendo la mensual entre 4, y daba de mas: el Z33/18 vale
  // 1,740 al mes y 522 a la semana, no 435. La columna precio_renta_semana ya
  // existe y ahora esta cargada en los 72 equipos que la tenian vacia. Si
  // algun equipo todavia no la tiene, se cae al calculo viejo.
  const SEMANAS_POR_MES = 4;
  const PERIODOS_FLOTA = [
    { k:'hora',   l:'Por hora',   campo:'precio_renta_hora',   texto:'Renta por hora' },
    { k:'dia',    l:'Por d\u00eda',    campo:'precio_renta_dia',    texto:'Renta por d\u00eda' },
    { k:'semana', l:'Por semana', campo:'precio_renta_semana', texto:'Renta semanal' },
    { k:'mes',    l:'Por mes',    campo:'precio_renta_mes',    texto:'Renta mensual' },
    { k:'venta',  l:'Venta',      campo:'precio_venta',        texto:'Venta de equipo' },
  ];
  const precioFlotaUSD = (e, k) => {
    if (k === 'semana') {
      if (e?.precio_renta_semana != null && Number(e.precio_renta_semana) > 0) return Number(e.precio_renta_semana);
      return e?.precio_renta_mes != null ? Number(e.precio_renta_mes)/SEMANAS_POR_MES : null;
    }
    const c = PERIODOS_FLOTA.find(p=>p.k===k)?.campo;
    return (c && e?.[c]!=null) ? Number(e[c]) : null;
  };
  const desdeUSD = (n, moneda) => moneda==='HNL' ? n*tasaHNL : moneda==='CRC' ? n*tasaCRC : n;
  const agregarEquipoFlota = (eq) => {
    const per = PERIODOS_FLOTA.find(p=>p.k===flotaPeriodo) || PERIODOS_FLOTA[3];
    const usd = precioFlotaUSD(eq, flotaPeriodo);
    const precio = usd==null ? '' : String(Math.round(desdeUSD(usd, fCotSAP.moneda)*100)/100);
    const desc = `${per.texto} - ${[eq.marca,eq.modelo].filter(Boolean).join(' ')} (${eq.codigo})`.slice(0,200);
    setFCotSAP(x=>{
      const ls=[...x.lineas];
      const nueva={descripcion:desc,cantidad:'1',precio,precioDesc:'',itemCode:''};
      // Si la primera linea todavia esta en blanco se usa esa, para que el
      // vendedor no tenga que borrarla despues.
      const vacia = ls.findIndex(l=>!String(l.descripcion||'').trim() && !String(l.precio||'').trim());
      if (vacia>=0) ls[vacia]=nueva; else ls.push(nueva);
      return {...x, lineas:ls};
    });
    setFlotaOpen(false); setFlotaQ('');
  };

  const convertirAUSD = (val, moneda) => {
    const n = parseFloat(val) || 0;
    if (moneda === 'HNL') return Math.round(n / tasaHNL * 100) / 100;
    if (moneda === 'CRC') return Math.round(n / tasaCRC * 100) / 100;
    return n;
  };

  const cargarVisitas = async (cliente) => {
    setLoadingVisitas(true);
    const filters = [];
    if (cliente.id) filters.push(`cliente_id=eq.${cliente.id}`);
    const { data: porId } = filters.length
      ? await supabase.from('visitas').select('*').eq('cliente_id', cliente.id).order('created_at', { ascending: false }).limit(30)
      : { data: [] };
    const { data: porNombre } = await supabase.from('visitas').select('*')
      .ilike('empresa', `%${cliente.empresa}%`).order('created_at', { ascending: false }).limit(30);
    const merged = [...(porId||[]), ...(porNombre||[])];
    const unique = merged.filter((v,i,a) => a.findIndex(x=>x.id===v.id)===i);
    setVisitasCliente(unique);
    setLoadingVisitas(false);
  };

  useEffect(() => {
    if (sel && tab === 'clientes') cargarVisitas(sel);
    else setVisitasCliente([]);
  }, [sel, tab]);

  useEffect(() => {
    if (!sel || tab !== 'clientes') { setFacturasCliente([]); setCobrosCliente([]); setCotsCliente([]); setSelRtn(''); return; }
    // El cliente guarda sap_code con prefijo de empresa (ej. "honduras_CE11123").
    const cc = (sel.sap_code || sel.card_code || '').replace(/^(honduras|costarica)_/, '');
    // RTN: se lee on-demand de SAP (no se guarda por el índice único en rtn).
    setSelRtn('');
    if (cc) fetch('/api/sap',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'cliente_sap_info',empresa_id:sel.empresa_id||'honduras',cardCode:cc})})
      .then(r=>r.json()).then(d=>{ if(d?.rtn) setSelRtn(d.rtn); }).catch(()=>{});
    setLoadingFacturas(true);
    (async () => {
      // Facturas: por código SAP o, si no hay, por nombre del cliente.
      // Sin codigo SAP y sin nombre buscable NO se adivina: mejor vacio que
      // enseñar facturas de otro cliente.
      const puedeNombre = !cc && nombreBuscable(sel.empresa);
      let facs = [], cobs = [];
      if (cc || puedeNombre) {
        let fq = supabase.from('facturas_sap').select('doc_num,doc_date,doc_due_date,doc_total,total_usd,moneda,pagado,saldo_usd,vendedor').order('doc_date',{ascending:false}).limit(50);
        if (sel.empresa_id) fq = fq.eq('empresa_id', sel.empresa_id);
        fq = cc ? fq.eq('card_code', cc) : fq.ilike('card_name', `%${escLike(sel.empresa)}%`);
        ({ data: facs } = await fq);
        let cq = supabase.from('cobros_sap').select('doc_num,doc_date,doc_total,total_usd,moneda,tipo_pago').order('doc_date',{ascending:false}).limit(50);
        if (sel.empresa_id) cq = cq.eq('empresa_id', sel.empresa_id);
        cq = cc ? cq.eq('card_code', cc) : cq.ilike('card_name', `%${escLike(sel.empresa)}%`);
        ({ data: cobs } = await cq);
      }
      setFacturasCliente(facs || []);
      setCobrosCliente(cobs || []);
      const q = await cotizacionesDe({ clienteIds: [sel.id], sapCode: cc || null, empresaId: sel.empresa_id });
      setCotsCliente(q.lista || []);
      setLoadingFacturas(false);
    })().catch(e => {
      // SIN ESTO LA FICHA SE QUEDA EN "Cargando..." PARA SIEMPRE. Si cualquiera
      // de las dos consultas truena, la promesa se rechaza, setLoadingFacturas
      // nunca vuelve a false y el panel no dice ni que fallo ni que esta vacio.
      console.error('facturas del cliente:', e?.message || e);
      setFacturasCliente([]); setCobrosCliente([]); setCotsCliente([]); setLoadingFacturas(false);
      toast('No se pudieron traer las facturas de SAP: ' + (e?.message || 'sin respuesta'));
    });
  }, [sel?.id, sel?.sap_code, tab]);

  // Al abrir otra oportunidad, la ficha vuelve siempre a Resumen (si no, se abriria
  // en la pestana donde quedo la anterior, que suele estar vacia).
  useEffect(() => { setOppTab('resumen'); }, [sel?.id]);

  // Esc cierra la ficha (mismo comportamiento que el resto de modales de RADAR).
  useEffect(() => {
    if (!sel || tab !== 'pipeline') return;
    const h = e => { if (e.key === 'Escape') setSel(null); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [sel, tab]);

  const ETAPAS = [
    // Dots de etapa del DS v3 (mockup): Prospecto #94A3B8 · Contactado #0F766E · Cotización #C4933F · Negociación #0E7490
    {id:'prospecto',      label:'Prospecto',        color:'#94A3B8', prob:0.10},
    {id:'contactado',     label:'Contactado',       color:'#0F766E', prob:0.25},
    {id:'cotizacion',     label:'Cotización',       color:'#C4933F', prob:0.40},
    {id:'negociacion',    label:'Negociación',      color:'#0E7490', prob:0.60},
    {id:'cerrado',        label:'Cerrado',          color:'#0B7AB8', prob:0.75},
    {id:'facturado',      label:'Facturado',        color:'#8B5CF6', prob:0.85},
    {id:'pagado',         label:'Pagado',           color:'#06B6D4', prob:0.92},
    {id:'cerrado_ganado', label:'✅ Cerrado Ganado', color:'#10B981', prob:1.00},
    {id:'cerrado_perdido',label:'✗ Cerrado Perdido',color:'#EF4444', prob:0.00},
    {id:'prospecto_invalido',label:'⊘ Prospecto Inválido',color:'#94A3B8', prob:0.00},
    {id:'cliente_frio',   label:'❄️ Cliente Frío',  color:'#0EA5E9', prob:0.00},
  ];
  const EC = {activo:'#10B981',prospecto:'#F59E0B',inactivo:'#64748B',perdido:'#EF4444'};

  const cargarLineasSAP = async (docNum) => {
    setLoadingSapLines(true); setSapLines(null);
    try {
      // Seguridad: las credenciales SAP ya no pasan por el navegador — el
      // servidor las resuelve por empresa_id (api/sap.js, config server-side).
      const r = await fetch('/api/sap', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'quotation_lines', docNum, empresa_id: sel?.empresa_id || 'honduras' }),
      });
      const data = await r.json();
      if (data.ok) setSapLines(data);
      else setSapLines({ error: data.error || 'Error SAP' });
    } catch(e) { setSapLines({ error: e.message }); }
    finally { setLoadingSapLines(false); }
  };

  // Proforma: usa el PDF del api (base64) que SÍ lee precios/ISV de la cotización SAP.
  const generarProforma = async (docNum) => {
    setLoadingPDF(true); setSapPDF(null);
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'get_pdf', proforma:true, empresa_id: empresaActiva || sel?.empresa_id || 'honduras', docNum, emisor: ORG.coti?.[empresaActiva || sel?.empresa_id || 'honduras'] || null }) });
      const d = await r.json();
      if (d.ok && d.base64) {
        const bin = atob(d.base64); const bytes = new Uint8Array(bin.length);
        for (let i=0;i<bin.length;i++) bytes[i]=bin.charCodeAt(i);
        const url = URL.createObjectURL(new Blob([bytes],{type:'application/pdf'}));
        // Descarga directa (nombre real) + abrir en pestaña para previsualizar.
        const a = Object.assign(document.createElement('a'), { href:url, download:`Proforma-PRO-${docNum}.pdf` });
        document.body.appendChild(a); a.click(); a.remove();
        window.open(url,'_blank'); setTimeout(()=>URL.revokeObjectURL(url),60000);
        setSapPDF({ ok:true });
      } else setSapPDF({ ok:false, error: d.error || 'No se pudo generar la proforma' });
    } catch(e){ setSapPDF({ ok:false, error:e.message }); }
    setLoadingPDF(false);
  };

  // Cotización por el MISMO generador del API que la proforma (buildCotizacionPDF),
  // que sí trae precios, ISV y totales en lempiras. El camino viejo (HTML) no los traía.
  const generarCotizacion = async (docNum, descuentoPct = 0) => {
    setLoadingPDF(true); setSapPDF(null);
    try {
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'get_pdf', proforma:false, empresa_id: empresaActiva || sel?.empresa_id || 'honduras', docNum, descuentoPct: Number(descuentoPct)||0, emisor: ORG.coti?.[empresaActiva || sel?.empresa_id || 'honduras'] || null }) });
      const d = await r.json();
      if (d.ok && d.base64) {
        const bin = atob(d.base64); const bytes = new Uint8Array(bin.length);
        for (let i=0;i<bin.length;i++) bytes[i]=bin.charCodeAt(i);
        const url = URL.createObjectURL(new Blob([bytes],{type:'application/pdf'}));
        const a = Object.assign(document.createElement('a'), { href:url, download:`Cotizacion-${docNum}.pdf` });
        document.body.appendChild(a); a.click(); a.remove();
        window.open(url,'_blank'); setTimeout(()=>URL.revokeObjectURL(url),60000);
        setSapPDF({ ok:true });
      } else setSapPDF({ ok:false, error: d.error || 'No se pudo generar la cotización' });
    } catch(e){ setSapPDF({ ok:false, error:e.message }); }
    setLoadingPDF(false);
  };

  const descargarPDF = async (docNum, proforma = false) => {
    setLoadingPDF(true); setSapPDF(null);
    const tDoc = proforma ? 'FACTURA PROFORMA' : 'Cotización';
    const nPref = proforma ? 'PRO-' : '#';
    try {
      // Reusar líneas ya cargadas si son del mismo doc
      let lines = sapLines?.lines || [];
      let doc   = sapLines?.doc   || null;
      if (!lines.length) {
        {
          const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
            body: JSON.stringify({ action:'quotation_lines', docNum, empresa_id: sel?.empresa_id || 'honduras' }) });
          const d = await r.json();
          if (d.ok) { lines = d.lines||[]; doc = d.doc||null; }
        }
      }
      const opp = sel;
      const fmt  = n => Number(n||0).toLocaleString('es-HN',{minimumFractionDigits:2,maximumFractionDigits:2});
      const fech = d => d ? new Date(d+'T12:00:00').toLocaleDateString('es-HN',{day:'2-digit',month:'long',year:'numeric'}) : '—';
      const cur  = (doc?.DocCurrency==='USD'||doc?.DocCurrency==='US$') ? 'USD' : 'L';
      const sub  = lines.reduce((s,l)=>s+(l.LineTotal||0),0);
      const tax  = doc?.VatSum||0;
      const tot  = doc?.DocTotal||sub+tax;

      const rowsHTML = lines.map(l=>`
        <tr>
          <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#374151">${l.ItemCode||''}</td>
          <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#374151">${l.ItemDescription||l.Description||''}</td>
          <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:center">${l.Quantity||0}</td>
          <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right">${cur} ${fmt(l.UnitPrice)}</td>
          <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;color:#10B981">${fmt(l.DiscountPercent||0)}%</td>
          <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;font-weight:700">${cur} ${fmt(l.LineTotal)}</td>
        </tr>`).join('');

      const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
        <title>${tDoc} ${docNum} — ${opp?.cliente||''}</title>
        <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;padding:36px;max-width:820px;margin:0 auto}
        @media print{.no-print{display:none}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style>
      </head><body>
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;padding-bottom:18px;border-bottom:3px solid #0B7AB8">
          <div>
            <div style="font-size:24px;font-weight:900;color:#0B7AB8;letter-spacing:1px">${ORG.marcaUpper}</div>
            <div style="font-size:11px;color:#64748b;margin-top:3px">Soluciones en Manejo de Materiales · Honduras</div>
          </div>
          <div style="text-align:right">
            <div style="font-size:13px;font-weight:700;color:#64748b;text-transform:uppercase">${tDoc}</div>
            <div style="font-size:22px;font-weight:900;color:#0B7AB8">${nPref}${docNum}</div>
            ${proforma?'<div style="font-size:9px;font-weight:800;color:#D97706;margin-top:3px">Documento no fiscal · sin valor tributario</div>':''}
          </div>
        </div>
        <div style="display:flex;gap:24px;margin-bottom:24px">
          <div style="flex:1;background:#f8fafc;border-radius:8px;padding:14px">
            <div style="font-size:9px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:.08em;margin-bottom:6px">Cliente</div>
            <div style="font-size:15px;font-weight:700">${opp?.cliente||'—'}</div>
            ${opp?.vendedor?`<div style="font-size:11px;color:#64748b;margin-top:3px">Vendedor: ${opp.vendedor}</div>`:''}
          </div>
          <div style="background:#f8fafc;border-radius:8px;padding:14px;min-width:160px">
            <div style="font-size:9px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:.08em;margin-bottom:6px">Fechas</div>
            <div style="font-size:11px;color:#374151;margin-bottom:3px">Emisión: <strong>${fech(opp?.ultimo_seguimiento)}</strong></div>
            <div style="font-size:11px;color:#374151">Validez: <strong>${fech(opp?.fecha_cierre)}</strong></div>
          </div>
        </div>
        ${lines.length>0?`
        <table style="width:100%;border-collapse:collapse;margin-bottom:20px">
          <thead><tr style="background:#0B7AB8">
            <th style="padding:9px 10px;text-align:left;color:#fff;font-size:10px;text-transform:uppercase">Código</th>
            <th style="padding:9px 10px;text-align:left;color:#fff;font-size:10px;text-transform:uppercase">Descripción</th>
            <th style="padding:9px 10px;text-align:center;color:#fff;font-size:10px;text-transform:uppercase">Cant.</th>
            <th style="padding:9px 10px;text-align:right;color:#fff;font-size:10px;text-transform:uppercase">P. Unit.</th>
            <th style="padding:9px 10px;text-align:right;color:#fff;font-size:10px;text-transform:uppercase">Desc.</th>
            <th style="padding:9px 10px;text-align:right;color:#fff;font-size:10px;text-transform:uppercase">Total</th>
          </tr></thead>
          <tbody>${rowsHTML}</tbody>
        </table>
        <div style="display:flex;justify-content:flex-end;margin-bottom:24px">
          <div style="min-width:240px">
            <div style="display:flex;justify-content:space-between;padding:5px 10px;font-size:12px;color:#374151"><span>Subtotal</span><span>${cur} ${fmt(sub)}</span></div>
            ${tax?`<div style="display:flex;justify-content:space-between;padding:5px 10px;font-size:12px;color:#374151"><span>ISV</span><span>${cur} ${fmt(tax)}</span></div>`:''}
            <div style="display:flex;justify-content:space-between;padding:9px 10px;font-size:15px;font-weight:900;color:#0B7AB8;border-top:2px solid #0B7AB8;margin-top:3px"><span>TOTAL</span><span>${cur} ${fmt(tot)}</span></div>
          </div>
        </div>`:'<div style="padding:16px;background:#f8fafc;border-radius:8px;text-align:center;color:#64748b;margin-bottom:20px;font-size:12px">Sin líneas de detalle</div>'}
        ${doc?.Comments?`<div style="background:#f8fafc;border-radius:8px;padding:12px;margin-bottom:20px"><div style="font-size:9px;font-weight:800;color:#64748b;text-transform:uppercase;margin-bottom:5px">Comentarios</div><div style="font-size:11px;color:#374151;line-height:1.6;white-space:pre-wrap">${doc.Comments}</div></div>`:''}
        <div style="border-top:1px solid #e5e7eb;padding-top:12px;text-align:center;font-size:10px;color:#94a3b8">${ORG.nombre} · ${ORG.pais} · Generado desde RADAR ${new Date().toLocaleDateString('es-HN')}</div>
        <div class="no-print" style="text-align:center;margin-top:20px">
          <button onclick="window.print()" style="background:#0B7AB8;color:#fff;border:none;border-radius:8px;padding:11px 28px;font-size:13px;font-weight:700;cursor:pointer">🖨️ Imprimir / Guardar como PDF</button>
        </div>
      </body></html>`;

      const win = window.open('','_blank');
      if (win) { win.document.write(html); win.document.close(); win.focus(); setTimeout(()=>win.print(),600); }
      setSapPDF({ ok:true });
    } catch(e) { setSapPDF({ ok:false, error: e.message }); }
    finally { setLoadingPDF(false); }
  };

  const moveOpp = async (oppId, nuevaEtapa, sapNum, notaMov) => {
    const hoy = new Date().toISOString().split('T')[0];
    // Cambiar de etapa CUENTA como seguimiento: resetea el contador de urgencia.
    const upd = {etapa:nuevaEtapa, ultimo_seguimiento:hoy};
    if (sapNum) upd.sap_quotation_num = sapNum;
    // .select() confirma que el write realmente persistió (si RLS lo bloquea, data viene
    // vacío sin throw). Sin esto el cambio se veía en pantalla pero revertía al refrescar.
    const { data, error } = await supabase.from('oportunidades').update(upd).eq('id',oppId).select('id');
    if (error || !data || !data.length) {
      toast('No se pudo guardar el cambio de etapa: ' + (error?.message || 'no se actualizó ninguna fila (permiso o id inexistente)'));
      setDragOpp(null); return;
    }
    // Registrar el cambio de etapa (para contar días por etapa) y, si hay nota, el seguimiento.
    supabase.from('opp_etapa_historial').insert({opp_id:oppId, etapa:nuevaEtapa, vendedor:usuario.nombre, nota:(notaMov||'').trim()||null}).then(()=>{},()=>{});
    let insSeg = null;
    if (notaMov && notaMov.trim()) {
      const r = await supabase.from('opp_seguimientos').insert({opp_id:oppId, vendedor:usuario.nombre, nota:notaMov.trim()}).select().single();
      insSeg = r.data;
      // Mover etapa con comentario CUENTA como una llamada (para el scorecard).
      const o = opps.find(x=>x.id===oppId);
      supabase.from('actividades_programadas').insert({titulo:`Seguimiento — ${o?.titulo||o?.cliente||'oportunidad'}`, tipo:'llamada', fecha:hoy, vendedor:usuario.nombre, cliente_id:o?.cliente_id||null, opp_id:oppId, notas:notaMov.trim(), completada:true, origen:'auto', empresa_id:o?.empresa_id||null}).then(()=>{},()=>{});
    }
    setOpps(os=>os.map(o=>o.id===oppId?{...o,...upd}:o));
    if (sel?.id===oppId) {
      setSel(prev=>({...prev,...upd}));
      if (insSeg) setSegHist(h=>[insSeg,...h]);
      setEtapaHist(h=>[...h,{etapa:nuevaEtapa,created_at:new Date().toISOString()}]);
    }
    setDragOpp(null);
  };

  // Router del dropdown "Mover a etapa": cada destino va a su flujo (con sus bloqueos).
  const moverEtapaUI = (etapa) => {
    if (!sel || !etapa) return;
    if (etapa==='cerrado_ganado')      return intentarCerrarGanado(sel);
    if (etapa==='cerrado_perdido')     {
      // Un vendedor NO puede cerrar como Perdida una opp que no es suya: usa "No es mía".
      const norm = s => (s||'').replace(/\s+/g,' ').trim();
      const esMia = sel.vendedor && norm(sel.vendedor)===norm(usuario.nombre);
      if (usuario.rol==='ventas' && !esMia) {
        toast(`Esta oportunidad no es tuya${sel.vendedor?` (es de ${sel.vendedor})`:' (sin asignar)'}.\n\nSi no te corresponde, marcá "✕ No es mía" — NO la cierres como Perdida (eso la elimina para todos).`);
        return;
      }
      setModalPerdido(sel); setMotivoPerdido(''); setNotasPerdido(''); return;
    }
    if (etapa==='prospecto_invalido')  { setModalInvalido(sel); setNotaInvalido(''); return; }
    if (etapa==='cliente_frio')        { setModalCampania({opp:sel,frio:true}); setCadenciaCamp('mensual'); setNotaCamp(''); return; }
    if (['cotizacion','facturado','pagado'].includes(etapa)) return handleMoverEtapa(sel.id, etapa); // tienen su propio bloqueo
    // Etapas simples (prospecto, contactado, negociación, cerrado): exigir comentario.
    setModalMover({ opp: sel, etapa }); setNotaMover('');
  };

  const borrarSeg = async (s) => {
    if (!(await confirmar('¿Borrar este seguimiento?', { ok:'Borrar', peligro:true }))) return;
    await supabase.from('opp_seguimientos').delete().eq('id', s.id).then(()=>{},()=>{});
    setSegHist(h=>h.filter(x=>x.id!==s.id));
  };

  // "No es mía": el vendedor agrega su nombre a descartado_por; la opp se le oculta de
  // su vista normal pero NO se desasigna (sigue visible en el filtro "Sin asignar").
  const descartarOppMia = async (opp) => {
    const arr = Array.from(new Set([...(opp.descartado_por||[]), usuario.nombre]));
    const { data, error } = await supabase.from('oportunidades').update({descartado_por:arr}).eq('id',opp.id).select('id');
    if (error || !data || !data.length) { toast('No se pudo quitar de tu CRM: '+(error?.message||'sin permiso')); return; }
    setOpps(os=>os.map(o=>o.id===opp.id?{...o,descartado_por:arr}:o));
    setSel(null);
  };
  // "Es mía": el vendedor se asigna la oportunidad sin asignar.
  const reclamarOpp = async (opp) => {
    const { data, error } = await supabase.from('oportunidades').update({vendedor:usuario.nombre}).eq('id',opp.id).select('id');
    if (error || !data || !data.length) { toast('No se pudo asignar: '+(error?.message||'sin permiso')); return; }
    setOpps(os=>os.map(o=>o.id===opp.id?{...o,vendedor:usuario.nombre}:o));
    setSel(prev=>prev?.id===opp.id?{...prev,vendedor:usuario.nombre}:prev);
  };

  // ── Unir / Anexar oportunidades ──────────────────────────────────────────
  // Fusiona `source` dentro de `target`: el target se queda con la etapa más
  // avanzada y conserva cotización/factura/valor/notas de ambas; el source se borra.
  const rankEtapa = e => ETAPAS_PROB[e] ?? 0;
  const unirOpps = async (target, source) => {
    if (!target || !source || target.id === source.id) return;
    setSavingUnir(true);
    const hoy = new Date().toISOString().split('T')[0];
    const adv = rankEtapa(target.etapa) >= rankEtapa(source.etapa) ? target : source;
    const pick = k => target[k] || source[k] || null;
    const upd = {
      etapa: adv.etapa,
      titulo: target.titulo || source.titulo,
      cliente: pick('cliente'),
      cliente_id: pick('cliente_id'),
      vendedor: pick('vendedor'),
      valor: Math.max(target.valor || 0, source.valor || 0) || null,
      linea_negocio: pick('linea_negocio'),
      tipo: pick('tipo'),
      fecha_cierre: pick('fecha_cierre'),
      proxima_accion: pick('proxima_accion'),
      sap_quotation_num: pick('sap_quotation_num'),
      factura_sap_num: pick('factura_sap_num'),
      empresa_id: target.empresa_id || source.empresa_id,
      ultimo_seguimiento: hoy,
      notas: [target.notas, source.notas].filter(Boolean).join('\n──── (anexada) ────\n') || null,
    };
    // 1) Liberar del source los campos con UNIQUE (sap_quotation_num → opp_sap_qt_uniq, y
    //    factura por las dudas) ANTES de pasárselos al target. Si no, el target choca con el
    //    duplicado mientras el source aún existe → "duplicate key value" → no se guardaba.
    await supabase.from('oportunidades').update({ sap_quotation_num:null, factura_sap_num:null }).eq('id', source.id).then(()=>{},()=>{});

    // 2) Guardar el target Y confirmar que persistió (.select). Si falla, restaurar los
    //    campos del source para no perder nada y abortar SIN borrarlo.
    const { data: updData, error: updErr } = await supabase.from('oportunidades').update(upd).eq('id', target.id).select('id');
    if (updErr || !updData || !updData.length) {
      await supabase.from('oportunidades').update({ sap_quotation_num: source.sap_quotation_num||null, factura_sap_num: source.factura_sap_num||null }).eq('id', source.id).then(()=>{},()=>{});
      setSavingUnir(false);
      toast('No se pudo guardar la oportunidad unida (no se borró nada): ' + (updErr?.message || 'no se actualizó ninguna fila (permiso o id inexistente)'));
      return;
    }
    // 3) Recién ahora borrar la anexada (notifs primero por la FK).
    await supabase.from('notificaciones').delete().eq('opp_id', source.id).then(()=>{},()=>{});
    const { error } = await supabase.from('oportunidades').delete().eq('id', source.id);
    if (error) { setSavingUnir(false); toast('Se guardó la unión pero no se pudo borrar la anexada: '+error.message); return; }
    setOpps(os => os.map(o=>o.id===target.id?{...o,...upd}:o).filter(o=>o.id!==source.id));
    setSel(prev => prev?.id===source.id ? {...target,...upd} : (prev?.id===target.id ? {...prev,...upd} : prev));
    setSavingUnir(false); setModalUnir(null); setUnirSel(null); setUnirBusq('');
  };

  const abrirModalFactura = (opp, pendingEtapa) => {
    setModalFactura({ opp, pendingEtapa });
    setFacturaTab('digitos');
    setFacturas4dig('');
    setFacturaSearchMatch(null);
    setFacturaSearchErr('');
    setFacturaSelLista(null);
    setFacturasLista([]);
  };

  const cargarFacturasCliente = async (opp) => {
    setLoadingFacturasLista(true);
    const eid = opp.empresa_id;
    // Facturas del mismo cliente. No filtrar empresa si es 'all'/null (si no, las de
    // honduras/costarica no aparecen).
    // TODAS las facturas del cliente (pagadas y no pagadas) — antes solo traía sin pagar,
    // por eso las ventas ya cobradas no aparecían para vincular.
    let q = supabase.from('facturas_sap').select('doc_num,doc_date,doc_total,total_usd,moneda,pagado,card_name')
      .order('doc_date', { ascending: false }).limit(200);
    if (eid && eid !== 'all') q = q.eq('empresa_id', eid);
    // Nombre no buscable ("." y similares): no adivinar — lista vacia y que el
    // usuario busque la factura por numero.
    if (opp.cliente) {
      if (!nombreBuscable(opp.cliente)) { setFacturasLista([]); setLoadingFacturasLista(false); return; }
      q = q.ilike('card_name', `%${escLike(opp.cliente)}%`);
    }
    const { data } = await q;
    setFacturasLista(data || []);
    setLoadingFacturasLista(false);
  };

  // Cerrar Ganado SOLO si hay factura SAP ligada Y cobrada COMPLETA (no anticipo).
  const intentarCerrarGanado = async (opp) => {
    if (!opp?.factura_sap_num) { abrirModalFactura(opp || {}, 'cerrado_ganado'); return; }
    const { data: fac } = await supabase.from('facturas_sap')
      .select('pagado,doc_total,total_usd,doc_date,card_name')
      .eq('doc_num', opp.factura_sap_num).maybeSingle();
    if (fac?.pagado) {
      setModalGanado(opp);
      setFGanado({ valor: opp?.valor || '', notas: '' });
    } else {
      setModalPagadoSAP({ opp, fac, tipo: 'factura_pendiente', accion: 'ganado' });
    }
  };

  const handleMoverEtapa = async (oppId, nuevaEtapa) => {
    const opp = opps.find(o=>o.id===oppId);
    if (nuevaEtapa==='cotizacion') {
      setModalCotizSAP({oppId, pendingEtapa:'cotizacion'});
      setSapCotizInput('');
    } else if (nuevaEtapa==='facturado') {
      abrirModalFactura(opp||{id:oppId}, 'facturado');
    } else if (nuevaEtapa==='pagado') {
      const opp2 = opps.find(o=>o.id===oppId)||{id:oppId};
      if (opp2?.pago_anticipado_sap) {
        moveOpp(oppId, 'pagado');
      } else if (opp2?.factura_sap_num) {
        const { data: fac } = await supabase.from('facturas_sap')
          .select('pagado,doc_total,total_usd,doc_date,card_name')
          .eq('doc_num', opp2.factura_sap_num)
          .maybeSingle();
        if (fac?.pagado) {
          moveOpp(oppId, 'pagado');
        } else {
          setModalPagadoSAP({ opp: opp2, fac, tipo: 'factura_pendiente' });
        }
      } else {
        setModalPagadoSAP({ opp: opp2, fac: null, tipo: 'sin_factura' });
      }
    } else if (nuevaEtapa==='cerrado_ganado') {
      await intentarCerrarGanado(opp||{id:oppId});
    } else {
      moveOpp(oppId, nuevaEtapa);
    }
  };

  const confirmarCotizSAP = async () => {
    if (!modalCotizSAP) return;
    const num = sapCotizInput.trim();
    // Si ese número ya existe en OTRA opp (la que creó el sync de SAP), ofrecer unirlas
    // en vez de dejar la cotización duplicada (causa del "no se actualiza").
    if (num) {
      const dup = opps.find(o => o.id !== modalCotizSAP.oppId && o.sap_quotation_num &&
        (String(o.sap_quotation_num) === num || (num.length >= 3 && String(o.sap_quotation_num).endsWith(num))));
      if (dup) {
        const target = opps.find(o => o.id === modalCotizSAP.oppId);
        setModalCotizSAP(null); setSapCotizInput('');
        setUnirBusq(''); setUnirSel(dup); setModalUnir(target);
        return;
      }
    }
    setSavingCotiz(true);
    await moveOpp(modalCotizSAP.oppId, 'cotizacion', num || null);
    setSavingCotiz(false); setModalCotizSAP(null); setSapCotizInput('');
  };

  const convertirACliente = async () => {
    if (!fConvertir.nombre||!fConvertir.rtn||!fConvertir.telefono) return; setSavingConvertir(true);
    // Antiduplicado: si ya existe un cliente con ese RTN, reusarlo en vez de crear otro.
    const rtnN = normRtn(fConvertir.rtn);
    let data = rtnN ? clientes.find(c => normRtn(c.rtn) === rtnN) : null;
    if (!data) {
      const ins = await supabase.from('clientes').insert({
        empresa:   fConvertir.nombre,
        contacto:  fConvertir.contacto || fConvertir.nombre,
        telefono:  fConvertir.telefono,
        rtn:       fConvertir.rtn,
        email:     fConvertir.email     || null,
        ciudad:    fConvertir.ciudad    || null,
        industria: fConvertir.industria || null,
        vendedor:  modalConvertir.vendedor || '',
        estado:    'activo',
        pais:      'Honduras',
        ultima_actividad: new Date().toISOString(),
      }).select().single();
      data = ins.data;
    }
    if (data) {
      await supabase.from('oportunidades').update({cliente_id:data.id, cliente:data.empresa}).eq('id',modalConvertir.id);
      setClientes(cs=>(cs.some(c=>c.id===data.id)?cs:[...cs,data]).sort((a,b)=>String(a.empresa||'').localeCompare(String(b.empresa||''))));
      setOpps(os=>os.map(o=>o.id===modalConvertir.id?{...o,cliente_id:data.id,cliente:data.empresa}:o));
      if (sel?.id===modalConvertir.id) setSel(prev=>({...prev,cliente_id:data.id,cliente:data.empresa}));
    }
    setSavingConvertir(false); setModalConvertir(null);
    setFConvertir({nombre:'',telefono:'',rtn:''});
  };

  const MODALIDADES = [
    {k:'venta', l:'Venta',      campo:'precio_venta',      linea:'Venta de Equipo',          tipo:'Venta'},
    {k:'mes',   l:'Renta/mes',    campo:'precio_renta_mes',    linea:'Renta y Leasing Operativo', tipo:'Renta mensual'},
    {k:'sem',   l:'Renta/semana', campo:'precio_renta_semana', linea:'Renta y Leasing Operativo', tipo:'Renta semanal'},
    {k:'dia',   l:'Renta/día',    campo:'precio_renta_dia',    linea:'Renta y Leasing Operativo', tipo:'Renta por día'},
    {k:'hora',  l:'Renta/hora',   campo:'precio_renta_hora',   linea:'Renta por Hora',            tipo:'Renta por hora'},
  ];
  const aplicarEquipo = (eq, m) => {
    const precio = eq[m.campo] || 0;
    const nom = [eq.marca, eq.modelo].filter(Boolean).join(' ');
    setFOpp(x=>({ ...x, equipo_id: eq.id, valor: precio || x.valor, moneda:'USD',
      linea_negocio: m.linea, tipo: m.tipo,
      titulo: `${m.tipo} ${eq.codigo}${nom?` ${nom}`:''}${x.cliente?` — ${x.cliente}`:''}` }));
  };
  const equipoSelOpp = equiposPrecio.find(e=>e.id===fOpp.equipo_id);
  const equiposFiltOpp = equipoBusq.trim()
    ? equiposPrecio.filter(e=>`${e.codigo} ${e.marca} ${e.modelo}`.toLowerCase().includes(equipoBusq.trim().toLowerCase())).slice(0,8)
    : [];

  const crearOpp = async () => {
    if (!fOpp.titulo||!fOpp.cliente||!fOpp.linea_negocio) return; setSavingOpp(true);
    const moneda = fOpp.moneda || 'USD';
    const valorUSD = convertirAUSD(fOpp.valor, moneda);
    const tituloFinal = moneda !== 'USD' ? `${fOpp.titulo} [${moneda}→USD]` : fOpp.titulo;
    const {data} = await supabase.from('oportunidades').insert({
      titulo:tituloFinal, cliente:fOpp.cliente, cliente_id:fOpp.cliente_id||null,
      vendedor:fOpp.vendedor, valor:valorUSD, etapa:fOpp.etapa,
      tipo:fOpp.tipo, linea_negocio:fOpp.linea_negocio||null, equipo_id:fOpp.equipo_id||null,
      proxima_accion:fOpp.proxima_accion||null, fecha_cierre:fOpp.fecha_cierre||null,
      notas: moneda!=='USD' ? `Valor original: ${Number(fOpp.valor).toLocaleString()} ${moneda} (tasa ${moneda==='HNL'?tasaHNL.toFixed(4):tasaCRC.toFixed(2)})${fOpp.notas?' — '+fOpp.notas:''}` : fOpp.notas,
      ultimo_seguimiento:new Date().toISOString().split('T')[0],
      empresa_id: empresaActiva || 'honduras',
    }).select().single();
    if (data) {
      setOpps(os=>[...os,data]);
      if (fOpp.vendedor && fOpp.vendedor !== usuario.nombre) {
        notificar({ para: fOpp.vendedor, tipo: 'prospecto_asignado',
          titulo: 'Te asignaron un prospecto',
          mensaje: `Nuevo prospecto asignado por ${usuario.nombre}: "${tituloFinal}" — ${fOpp.cliente}`,
          modulo: 'crm', refId: data.id , orgId: usuario?.org_id || null }).then(()=>{});
      }
    }
    setSavingOpp(false); setModalOpp(false);
    setFOpp({titulo:'',cliente:'',cliente_id:'',vendedor:'',valor:'',moneda:'USD',etapa:'prospecto',tipo:'',linea_negocio:'',proxima_accion:'',fecha_cierre:'',notas:'',equipo_id:''}); setEquipoBusq('');
  };

  const editarOpp = async () => {
    if (!fEditOpp.id) return; setSavingEditOpp(true);
    const moneda = fEditOpp.moneda || 'USD';
    const valorUSD = convertirAUSD(fEditOpp.valor, moneda);
    const notaFinal = moneda !== 'USD'
      ? `Valor original: ${Number(fEditOpp.valor).toLocaleString()} ${moneda} (tasa ${moneda==='HNL'?tasaHNL.toFixed(4):tasaCRC.toFixed(2)})${fEditOpp.notas?' — '+fEditOpp.notas:''}`
      : fEditOpp.notas;
    const upd = {
      titulo: fEditOpp.titulo,
      valor: valorUSD,
      fecha_cierre: fEditOpp.fecha_cierre||null,
      linea_negocio: fEditOpp.linea_negocio||null,
      tipo: fEditOpp.tipo||null,
      proxima_accion: fEditOpp.proxima_accion||null,
      notas: notaFinal,
    };
    await supabase.from('oportunidades').update(upd).eq('id', fEditOpp.id);
    setOpps(os=>os.map(o=>o.id===fEditOpp.id?{...o,...upd}:o));
    setSel(prev=>prev?.id===fEditOpp.id?{...prev,...upd}:prev);
    setSavingEditOpp(false); setModalEditOpp(false);
  };

  const registrarNota = async () => {
    if (!nota.trim()||!sel) return; setSavingNota(true);
    await supabase.from('clientes').update({ultima_actividad:new Date().toISOString(),notas:nota}).eq('id',sel.id);
    setSel(c=>({...c,notas:nota,ultima_actividad:new Date().toISOString()}));
    setClientes(cs=>cs.map(c=>c.id===sel.id?{...c,notas:nota,ultima_actividad:new Date().toISOString()}:c));
    setNota(''); setSavingNota(false);
  };

  // Inscribir una oportunidad/cliente en una campaña de WhatsApp con cadencia.
  // Si frio=true, además saca la opp del pipeline (etapa terminal 'cliente_frio').
  const confirmarCampania = async () => {
    const m = modalCampania; if (!m?.opp) return; setSavingCamp(true);
    const opp = m.opp;
    const dias = cadenciaCamp==='semanal'?7:cadenciaCamp==='quincenal'?15:30;
    const prox = new Date(Date.now()+dias*864e5).toISOString().split('T')[0];
    const cli  = clientes.find(c=>c.id===opp.cliente_id);
    const row = {
      cliente_id: opp.cliente_id||null,
      opp_id:     opp.id||null,
      nombre:     opp.cliente||cli?.empresa||opp.titulo||'',
      telefono:   cli?.telefono||null,
      vendedor:   opp.vendedor||usuario.nombre,
      cadencia:   cadenciaCamp,
      proximo_envio: prox,
      activo:     true,
      notas:      notaCamp.trim()||null,
      empresa_id: opp.empresa_id||null,
    };
    // Evitar duplicar: si el cliente ya está inscrito, actualizar su inscripción.
    let existing = null;
    if (opp.cliente_id) {
      const { data } = await supabase.from('clientes_campania').select('id').eq('cliente_id',opp.cliente_id).eq('activo',true).maybeSingle();
      existing = data;
    }
    if (existing) await supabase.from('clientes_campania').update(row).eq('id',existing.id);
    else await supabase.from('clientes_campania').insert(row);
    if (m.frio) {
      const hoy = new Date().toISOString().split('T')[0];
      const upd = {etapa:'cliente_frio',fecha_cierre:hoy,ultimo_seguimiento:hoy};
      await supabase.from('oportunidades').update(upd).eq('id',opp.id);
      setOpps(os=>os.map(o=>o.id===opp.id?{...o,...upd}:o));
      setSel(prev=>prev?.id===opp.id?{...prev,...upd}:prev);
    }
    setSavingCamp(false); setModalCampania(null); setNotaCamp(''); setCadenciaCamp('mensual');
  };

  const filtradosClRaw = (clBusqResults !== null ? clBusqResults : clientes).filter(c=>{
    // Estos tres no son estados del cliente, son cortes de trabajo, por eso se
    // filtran aparte y no contra c.estado:
    //   libres       = quedo sin vendedor porque el suyo se fue
    //   sincontacto  = no se sabe por quien preguntar al llamar
    //   cert         = la base para llamar de certificacion de operador
    if (filtroEst==='libres') { if (c.vendedor || !c.vendedor_anterior) return false; }
    else if (filtroEst==='sincontacto') { if (String(c.contacto||'').trim()) return false; }
    else if (filtroEst==='rotos') { if (!esRoto(c)) return false; }

    else if (filtroEst!=='todos'&&c.estado!==filtroEst) return false;
    if (filtroSeg.length && !filtroSeg.includes(c.segmento)) return false;
    if (filtroKpi==='sin30'){ const d=c.ultima_actividad?dias(c.ultima_actividad):9999; if(d<30) return false; }
    if (filtroKpi==='saldo' && !(saldoDe(c)>0)) return false;
    if (filtroKpi==='sinvend' && c.vendedor) return false;
    if (filtroKpi==='rotos' && !esRoto(c)) return false;
    // Busca por nombre, contacto, vendedor y también por RTN / teléfono / código SAP
    // (el RTN se compara sin guiones ni espacios: "0501-9020-240330" == "05019020240330").
    if (clBusqResults === null && busq) {
      const b = busq.toLowerCase();
      const soloNum = s => String(s||'').replace(/[^0-9a-zA-Z]/g,'').toLowerCase();
      const bNum = soloNum(busq);
      return (c.empresa||'').toLowerCase().includes(b)
        || (c.contacto||'').toLowerCase().includes(b)
        || (c.vendedor||'').toLowerCase().includes(b)
        || (bNum.length>=4 && (soloNum(c.rtn).includes(bNum) || soloNum(c.telefono).includes(bNum) || soloNum(c.sap_code).includes(bNum)));
    }
    return true;
  });
  // Colapsar duplicados (misma empresa+país). Los dup vienen de SAP; mostramos UNO
  // (el más completo) — la ficha igual une opps/facturas/pagos por nombre. No borra datos.
  // CUANTAS FILAS SE PINTAN. Antes se pintaban TODAS: con el filtro en "Todos"
  // eso son 6,500 filas con sus botones metidas en el DOM de una sola vez, y es
  // la razon por la que el refresh se sentia eterno. Se pintan de a 150 y se
  // agregan mas cuando el usuario llega al final de la lista.
  const [techoCl, setTechoCl] = useState(150);
  const finListaCl = useRef(null);
  useEffect(()=>{ setTechoCl(150); }, [busq, filtroEst, filtroSeg, filtroKpi, empresaActiva]);
  useEffect(()=>{
    const nodo = finListaCl.current;
    if (!nodo) return;
    const obs = new IntersectionObserver(e=>{ if (e[0].isIntersecting) setTechoCl(t=>t+150); }, { rootMargin:'400px' });
    obs.observe(nodo);
    return ()=>obs.disconnect();
  });

  const filtradosCl = (()=>{
    const byKey = new Map();
    const score = c => (c.sap_code?2:0)+(c.telefono?1:0)+(c.vendedor?1:0)+(c.rtn?1:0)+(c.contacto?1:0);
    for (const c of filtradosClRaw) {
      const k = `${c.empresa_id||''}|${(c.empresa||'').toLowerCase().replace(/\s+/g,' ').trim()}`;
      const ex = byKey.get(k);
      if (!ex || score(c) > score(ex)) byKey.set(k, c);
    }
    return [...byKey.values()].sort((a,b)=>String(a.empresa||'').localeCompare(String(b.empresa||'')));
  })();

  const urg = d => d>5?C.red:d>2?C.orange:C.t4;

  // ── Lógica de filtros del pipeline ──────────────────────────────────────────
  // normV: normaliza nombre de vendedor (colapsa espacios raros/NBSP) para que el
  // filtro calce aunque el dato tenga espacios invisibles que SQL trim() no quita.
  const normV = s => (s||'').replace(/\s+/g,' ').trim();
  const vendedoresUnicos = [...new Set(opps.map(o=>normV(o.vendedor)).filter(Boolean))].sort();
  // Candidatos para "Unir / Anexar": sin búsqueda = mismo cliente; con búsqueda = todas.
  const unirCandidatos = !modalUnir ? [] : opps.filter(o => {
    if (o.id === modalUnir.id) return false;
    if (unirBusq.trim()) return (`${o.titulo||''} ${o.cliente||''}`).toLowerCase().includes(unirBusq.trim().toLowerCase());
    return (o.cliente_id && o.cliente_id === modalUnir.cliente_id) ||
           (o.cliente && modalUnir.cliente && o.cliente.toLowerCase() === modalUnir.cliente.toLowerCase());
  });
  const lineasUnicas = [...new Set(opps.map(o=>o.linea_negocio).filter(Boolean))].sort();

  const hoyStr = new Date().toISOString().split('T')[0];
  const inicioMes = new Date(); inicioMes.setDate(1); const inicioMesStr = inicioMes.toISOString().split('T')[0];
  const inicioTrimestre = new Date();
  inicioTrimestre.setMonth(Math.floor(inicioTrimestre.getMonth()/3)*3, 1);
  const inicioTriStr = inicioTrimestre.toISOString().split('T')[0];
  const inicioAño = new Date(new Date().getFullYear(),0,1).toISOString().split('T')[0];
  const en30d = new Date(); en30d.setDate(en30d.getDate()+30); const en30dStr = en30d.toISOString().split('T')[0];

  const meNorm = normV(usuario.nombre);
  const esVentasRol = usuario.rol === 'ventas';
  // Memoizado: con 1,700+ opps, refiltrar/reordenar en cada render (cada tecla,
  // cada hover) congelaba el pipeline. Solo recalcula cuando cambia un filtro real.
  const oppsFiltradas = useMemo(() => {
    const base = opps.filter(o => {
      const v = normV(o.vendedor);
      const sinV = !v;
      if (filtroVends.length > 0) {
        // Multi-select: incluir si el vendedor está seleccionado, o si pidieron "sin asignar".
        const wantSin = filtroVends.includes('__sin__');
        const sel = filtroVends.filter(x => x !== '__sin__');
        const match = (sinV && wantSin) || (v && sel.includes(v));
        if (!match) return false;
      } else {
        // Sin selección = todos. Vendedor (rol ventas) ve lo suyo + sin asignar no descartadas.
        if (esVentasRol && !VER_TODO_EMP.includes(o.empresa_id)) {
          const mias = v === meNorm;
          const descartadaPorMi = (o.descartado_por||[]).some(n=>normV(n)===meNorm);
          if (!mias && !(sinV && !descartadaPorMi)) return false;
        }
      }
      if (filtroLinea.length > 0 && !filtroLinea.includes(o.linea_negocio)) return false;
      if (busqOpp) {
        const b = busqOpp.toLowerCase();
        if (!(o.titulo||'').toLowerCase().includes(b) && !(o.cliente||'').toLowerCase().includes(b)) return false;
      }
      if (filtroPeriodo !== 'todo') {
        const ref = o.fecha_cierre || o.ultimo_seguimiento;
        if (filtroPeriodo === 'este_mes')   return ref && ref >= inicioMesStr;
        if (filtroPeriodo === 'trimestre')  return ref && ref >= inicioTriStr;
        if (filtroPeriodo === 'año')        return ref && ref >= inicioAño;
        if (filtroPeriodo === 'prox_30d')   return o.fecha_cierre && o.fecha_cierre >= hoyStr && o.fecha_cierre <= en30dStr && !['cerrado_ganado','cerrado_perdido'].includes(o.etapa);
        if (filtroPeriodo === 'vencidas')   return o.fecha_cierre && o.fecha_cierre < hoyStr && !['cerrado_ganado','cerrado_perdido'].includes(o.etapa);
        if (filtroPeriodo === 'estancadas') return dias(o.ultimo_seguimiento) >= 7 && !['cerrado_ganado','cerrado_perdido','prospecto_invalido','cliente_frio'].includes(o.etapa);
      }
      return true;
    });
    return sortOpps==='valor_desc' ? [...base].sort((a,b)=>(b.valor||0)-(a.valor||0))
      : sortOpps==='valor_asc' ? [...base].sort((a,b)=>(a.valor||0)-(b.valor||0))
      : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opps, filtroVends, filtroLinea, busqOpp, filtroPeriodo, sortOpps, esVentasRol, meNorm, hoyStr, VER_TODO_EMP.join(',')]);

  const hace60 = new Date(); hace60.setDate(hace60.getDate()-60); const hace60Str = hace60.toISOString().split('T')[0];

  const pipelineActivo  = oppsFiltradas.filter(o=>!['cerrado_ganado','cerrado_perdido','prospecto_invalido','cliente_frio'].includes(o.etapa));
  const pipelineTotal   = pipelineActivo.reduce((s,o)=>s+(o.valor||0),0);
  const pipelinePond    = pipelineActivo.reduce((s,o)=>s+(o.valor||0)*(ETAPAS_PROB[o.etapa]||0),0);
  const ganadasRec      = oppsFiltradas.filter(o=>o.etapa==='cerrado_ganado'&&(o.fecha_cierre||o.ultimo_seguimiento)>=hace60Str);
  const perdidasRec     = oppsFiltradas.filter(o=>o.etapa==='cerrado_perdido'&&(o.fecha_cierre||o.ultimo_seguimiento)>=hace60Str);
  const vencenHoy       = pipelineActivo.filter(o=>o.fecha_cierre===hoyStr);
  const vencenProx7     = pipelineActivo.filter(o=>o.fecha_cierre&&o.fecha_cierre>hoyStr&&o.fecha_cierre<=new Date(Date.now()+7*864e5).toISOString().split('T')[0]);
  const sinContacto5d   = pipelineActivo.filter(o=>dias(o.ultimo_seguimiento)>=5);

  // ── KPI ROW (estilo Stripe) — todo derivado de las opps ya cargadas, sin queries ──
  const fmtK = n => { const x=Math.abs(n||0); if(x>=1e6) return `$${(n/1e6).toFixed(1).replace(/\.0$/,'')}M`; if(x>=1e3) return `$${(n/1e3).toFixed(1).replace(/\.0$/,'')}K`; return `$${Math.round(n||0).toLocaleString('en-US')}`; };
  const kpiPipelineAbierto = pipelineTotal;                                   // suma valor de opps abiertas
  const ganadasMes  = oppsFiltradas.filter(o=>o.etapa==='cerrado_ganado'&&(o.fecha_cierre||o.ultimo_seguimiento)>=inicioMesStr);
  const kpiGanadoMes = ganadasMes.reduce((s,o)=>s+(o.valor||0),0);            // ganado del mes
  const perdidasMes = oppsFiltradas.filter(o=>o.etapa==='cerrado_perdido'&&(o.fecha_cierre||o.ultimo_seguimiento)>=inicioMesStr);
  const cerradasMes = ganadasMes.length + perdidasMes.length;
  const kpiTasaCierre = cerradasMes>0 ? Math.round(ganadasMes.length/cerradasMes*100) : 0; // won/(won+lost) del mes
  const kpiEstancadas = pipelineActivo.filter(o=>dias(o.ultimo_seguimiento)>60).length;     // sin contacto >60d
  // Sparklines decorativas (no hay serie histórica real): línea suave derivada del valor actual.
  const sparkSeed = (base,slope) => { const a=[]; for(let i=0;i<8;i++) a.push(base*(0.72+slope*i/7)+((i*7)%5)*base*0.015); return a; };

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflowX:'hidden',overflowY:tab==='pipeline'?'auto':'hidden',background:C.bg1}}>
      {/* Header */}
      <div style={{padding:'14px 24px 0',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexShrink:0}}>
        <div style={{display:'flex',gap:2,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2}}>
          {[['pipeline','Pipeline'],['clientes','Clientes'],...(conInactivos?[['inactivos','Sin atender']]:[])].map(([id,lbl])=>(
            <button key={id} onClick={()=>{setTab(id);setSel(null);}} style={{height:26,padding:'0 12px',background:tab===id?C.bg2:'transparent',color:tab===id?C.t1:C.t3,border:'none',borderRadius:R.sm,fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:tab===id?SH.sm:'none',transition:'background .15s ease, color .15s ease'}}>{lbl}</button>
          ))}
        </div>
        <div style={{position:'relative'}}>
          <button onClick={()=>setMenuAcciones(m=>!m)} className="radar-pill"
            style={{display:'inline-flex',alignItems:'center',gap:7,height:34,padding:'0 14px',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:12.5,fontWeight:600,color:C.t2,cursor:'pointer',boxShadow:SH.xs}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            Acciones <span style={{fontSize:9,opacity:.6}}>▾</span>
          </button>
          {menuAcciones&&(
            <div style={{position:'absolute',top:'112%',right:0,zIndex:1200,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:SH.lg,minWidth:215,overflow:'hidden',padding:'4px 0'}}>
              {[
                ['📍','Visita / Llamada',()=>setModalVisita(true)],
                ...(tab==='pipeline'?[['✨','Nuevo Prospecto',()=>{setFProspecto({empresa:'',vendedor:usuario.nombre});setModalProspecto(true);}]]:[]),
                ...(tab==='clientes'?[['➕','Nuevo Cliente',()=>setModalCliente(true)]]:[]),
                ['🧾','Cotizar en SAP',()=>abrirCotSAP(tab==='clientes'?sel:null)],
                ...(tab==='pipeline'?[['📊',exportando?'Exportando…':'Exportar Excel',()=>!exportando&&exportarOpps('xlsx')],['📄','Exportar CSV',()=>!exportando&&exportarOpps('csv')]]:[]),
                // Antes vivia en una franja naranja del ancho de la pantalla.
                // Es una tarea de mantenimiento ocasional, no un aviso permanente.
                ...(tab==='clientes'&&usuario.rol==='admin'&&clientes.some(esRoto)
                  ?[['🧹','Corregir nombres importados',limpiarComillas]]:[]),
              ].map(([ic,lbl,fn])=>(
                <button key={lbl} onClick={()=>{setMenuAcciones(false);fn();}}
                  style={{display:'flex',alignItems:'center',gap:9,width:'100%',textAlign:'left',padding:'9px 14px',background:'transparent',border:'none',fontSize:12.5,fontWeight:600,color:C.t2,cursor:'pointer'}}
                  onMouseEnter={e=>e.currentTarget.style.background=C.bg1} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <span style={{fontSize:13}}>{ic}</span>{lbl}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── KPI ROW (estilo Stripe) — solo pipeline, wired a datos reales ── */}
      {tab==='pipeline'&&(
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,padding:'16px 24px 4px',flexShrink:0}}>
          <KpiCard label="Pipeline abierto" tone="primary" icon={KI.funnel}
            value={fmtK(kpiPipelineAbierto)} delta={`${pipelineActivo.length} abiertas`} deltaTone="primary"
            sub={`pond. ${fmtK(pipelinePond)}`} spark={<Spark points={sparkSeed(Math.max(kpiPipelineAbierto,1),0.42)} color={C.blue}/>}/>
          <KpiCard label="Ganado (mes)" tone="green" icon={KI.check}
            value={fmtK(kpiGanadoMes)} delta={`${ganadasMes.length} cerradas`} deltaTone="green"
            sub="este mes" spark={<Spark points={sparkSeed(Math.max(kpiGanadoMes,1),0.55)} color={C.green}/>}/>
          <KpiCard label="Tasa de cierre" tone="info" icon={KI.trend}
            value={`${kpiTasaCierre}%`} delta={cerradasMes>0?`${ganadasMes.length}/${cerradasMes}`:'—'} deltaTone={kpiTasaCierre>=30?'green':'neutral'}
            sub="ganadas / cerradas (mes)" spark={<Spark points={sparkSeed(Math.max(kpiTasaCierre,1),0.28)} color={C.info}/>}/>
          <KpiCard label="Estancadas" tone="red" icon={KI.clock}
            value={`${kpiEstancadas}`} delta={kpiEstancadas>0?'+60d':'✓'} deltaTone={kpiEstancadas>0?'red':'green'}
            sub="sin contacto +60 días" spark={<Spark points={sparkSeed(Math.max(kpiEstancadas,1),0.2)} color={C.red}/>}/>
        </div>
      )}

      {/* FILTROS PIPELINE */}
      {tab==='pipeline'&&(
        <div style={{flexShrink:0}}>
          {/* Filtros — flotando sobre el gris, lenguaje del mockup */}
          <div style={{padding:'8px 24px 4px',display:'flex',alignItems:'center',gap:8,overflowX:'auto',scrollbarWidth:'none'}}>
            {/* Rango de tiempo — segmented control (lenguaje calmado del mockup) */}
            <div style={{display:'flex',background:C.bg1,borderRadius:R.md,padding:2,gap:2,border:`1px solid ${C.border}`,flexShrink:0}}>
              {[['todo','Todo'],['este_mes','Mes'],['trimestre','Trim'],['año','Año'],['prox_30d','30d']].map(([v,l])=>(
                <button key={v} onClick={()=>setFiltroPeriodo(v)}
                  style={{height:26,padding:'0 12px',borderRadius:R.sm,fontSize:12,fontWeight:600,cursor:'pointer',border:'none',
                    background:filtroPeriodo===v?C.bg2:'transparent',
                    color:filtroPeriodo===v?C.t1:C.t3,
                    boxShadow:filtroPeriodo===v?SH.sm:'none',transition:'all .15s',whiteSpace:'nowrap'}}>
                  {l}
                </button>
              ))}
            </div>

            {/* Pills sueltas de riesgo — Vencidas / Estancadas (mismo estado filtroPeriodo), íconos SVG del mockup */}
            {[
              {v:'vencidas', l:'Vencidas', ic:<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>},
              {v:'estancadas', l:'Estancadas', ic:<><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>},
            ].map(({v,l,ic})=>{
              const on = filtroPeriodo===v;
              return (
                <button key={v} className="radar-pill" onClick={()=>setFiltroPeriodo(on?'todo':v)}
                  style={{display:'inline-flex',alignItems:'center',gap:6,flexShrink:0,height:30,padding:'0 12px',borderRadius:R.md,fontSize:12,fontWeight:on?600:500,cursor:'pointer',
                    background:on?C.redTint:C.bg2, color:on?C.redText:C.t2,
                    border:`1px solid ${on?C.redBorder:C.border}`, boxShadow:on?'none':SH.xs, whiteSpace:'nowrap'}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{color:on?C.redText:C.t4}}>{ic}</svg>{l}
                </button>
              );
            })}

            <div style={{width:1,height:20,background:C.border,flexShrink:0}}/>

            {/* Vendedor — popup inline (antes era una fila entera) */}
            {usuario.rol!=='ventas' && (
              <div style={{position:'relative',flexShrink:0}}>
                <button ref={vendBtnRef} onClick={()=>setDropVend(d=>!d)} className="radar-pill"
                  style={{display:'inline-flex',alignItems:'center',gap:6,height:30,background:filtroVends.length>0?C.primaryTint:C.bg2,
                    color:filtroVends.length>0?C.blue:C.t2,border:`1px solid ${filtroVends.length>0?C.primaryBorder:C.border}`,
                    borderRadius:R.md,padding:'0 12px',fontSize:12,fontWeight:filtroVends.length>0?600:500,cursor:'pointer',outline:'none',whiteSpace:'nowrap',boxShadow:SH.xs}}>
                  👤 {filtroVends.length===0?'Vendedores'
                       :filtroVends.length===1?(filtroVends[0]==='__sin__'?'Sin asignar':filtroVends[0].split(' ')[0])
                       :`${filtroVends.length} seleccionados`}
                  <span style={{fontSize:8,opacity:.6}}>{dropVend?'▲':'▼'}</span>
                </button>
                {dropVend && (()=>{
                  const r = vendBtnRef.current?.getBoundingClientRect();
                  const toggle = (val)=>setFiltroVends(prev=>prev.includes(val)?prev.filter(x=>x!==val):[...prev,val]);
                  return <div ref={vendMenuRef} style={{position:'fixed',top:(r?.bottom||0)+6,left:r?.left||0,zIndex:9999,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'6px 0',minWidth:240,maxHeight:'60vh',overflowY:'auto',boxShadow:SH.lg}}>
                    <div onClick={()=>{setFiltroVends([]);}}
                      style={{padding:'8px 14px',fontSize:12,fontWeight:700,color:filtroVends.length===0?C.blue:C.t2,cursor:'pointer',display:'flex',alignItems:'center',gap:8}}>
                      <span style={{width:14,height:14,borderRadius:3,border:`2px solid ${filtroVends.length===0?C.blue:C.border2}`,background:filtroVends.length===0?C.blue:'transparent',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#fff',flexShrink:0}}>{filtroVends.length===0&&'✓'}</span>
                      Todos los vendedores
                    </div>
                    <div style={{height:1,background:C.border,margin:'2px 0'}}/>
                    {[['__sin__','⚠ Sin asignar'], ...vendedoresUnicos.map(v=>[v,v])].map(([val,lbl])=>{
                      const sel=filtroVends.includes(val);
                      return (
                        <div key={val} onClick={()=>toggle(val)}
                          style={{padding:'8px 14px',fontSize:12,fontWeight:600,color:sel?C.blue:C.t2,cursor:'pointer',display:'flex',alignItems:'center',gap:8,background:sel?C.primaryTint:'transparent'}}>
                          <span style={{width:14,height:14,borderRadius:3,border:`2px solid ${sel?C.blue:C.border2}`,background:sel?C.blue:'transparent',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#fff',flexShrink:0}}>{sel&&'✓'}</span>
                          {lbl}
                        </div>
                      );
                    })}
                  </div>;
                })()}
              </div>
            )}
            {filtroVends.length>0 && <button onClick={()=>setFiltroVends([])} style={{background:'none',border:'none',color:C.t4,fontSize:11,cursor:'pointer',whiteSpace:'nowrap'}}>limpiar</button>}

            {/* Líneas */}
            <div style={{position:'relative',flexShrink:0}}>
              <button ref={lineaBtnRef} onClick={()=>setDropLinea(d=>!d)} className="radar-pill"
                style={{display:'inline-flex',alignItems:'center',gap:5,height:30,background:filtroLinea.length>0?C.primaryTint:C.bg2,
                  color:filtroLinea.length>0?C.blue:C.t2,border:`1px solid ${filtroLinea.length>0?C.primaryBorder:C.border}`,
                  borderRadius:R.md,padding:'0 12px',fontSize:12,fontWeight:filtroLinea.length>0?600:500,cursor:'pointer',outline:'none',whiteSpace:'nowrap',boxShadow:SH.xs}}>
                💼 {filtroLinea.length===0?'Línea':filtroLinea.length===1?filtroLinea[0].split(' ')[0]:`${filtroLinea.length} líneas`}
                <span style={{fontSize:8,opacity:.6}}>{dropLinea?'▲':'▼'}</span>
              </button>
              {dropLinea&&(()=>{
                const r = lineaBtnRef.current?.getBoundingClientRect();
                return <div ref={lineaMenuRef} style={{position:'fixed',top:(r?.bottom||0)+6,left:r?.left||0,zIndex:9999,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'6px 0',minWidth:230,boxShadow:SH.lg}}>
                  <div onClick={()=>{setFiltroLinea([]);setDropLinea(false);}}
                    style={{padding:'8px 14px',fontSize:12,fontWeight:700,color:filtroLinea.length===0?C.blue:C.t2,cursor:'pointer',display:'flex',alignItems:'center',gap:8}}>
                    <span style={{width:14,height:14,borderRadius:3,border:`2px solid ${filtroLinea.length===0?C.blue:C.border2}`,background:filtroLinea.length===0?C.blue:'transparent',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#fff',flexShrink:0}}>{filtroLinea.length===0&&'✓'}</span>
                    Todas las líneas
                  </div>
                  <div style={{height:1,background:C.border,margin:'2px 0'}}/>
                  {lineasUnicas.map(l=>{
                    const sel=filtroLinea.includes(l);
                    return (
                      <div key={l} onClick={()=>setFiltroLinea(prev=>sel?prev.filter(x=>x!==l):[...prev,l])}
                        style={{padding:'8px 14px',fontSize:12,fontWeight:600,color:sel?C.blue:C.t2,cursor:'pointer',display:'flex',alignItems:'center',gap:8,background:sel?C.primaryTint:'transparent'}}>
                        <span style={{width:14,height:14,borderRadius:3,border:`2px solid ${sel?C.blue:C.border2}`,background:sel?C.blue:'transparent',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#fff',flexShrink:0}}>{sel&&'✓'}</span>
                        {l}
                      </div>
                    );
                  })}
                </div>;
              })()}
            </div>


            {/* Ordenar */}
            <select value={sortOpps} onChange={e=>setSortOpps(e.target.value)}
              style={{background:sortOpps!=='none'?C.primaryTint:C.bg2,color:sortOpps!=='none'?C.blue:C.t2,border:`1px solid ${sortOpps!=='none'?C.primaryBorder:C.border}`,
                borderRadius:R.md,height:30,padding:'0 10px',fontSize:12,fontWeight:sortOpps!=='none'?600:500,cursor:'pointer',outline:'none',flexShrink:0,boxShadow:SH.xs}}>
              <option value="none" style={{background:C.bg2}}>↕ Ordenar</option>
              <option value="valor_desc" style={{background:C.bg2}}>$ Mayor → Menor</option>
              <option value="valor_asc" style={{background:C.bg2}}>$ Menor → Mayor</option>
            </select>

            {/* Chips activos */}
            {filtroEtapaK&&(
              <button onClick={()=>setFiltroEtapaK('')}
                style={{display:'flex',alignItems:'center',gap:5,padding:'4px 10px',borderRadius:R.md,fontSize:11.5,fontWeight:600,cursor:'pointer',
                  background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,flexShrink:0}}>
                {ETAPAS.find(e=>e.id===filtroEtapaK)?.label} ✕
              </button>
            )}

            {/* Limpiar */}
            {(filtroVends.length>0||filtroPeriodo!=='todo'||filtroLinea.length>0||busqOpp||sortOpps!=='none'||filtroEtapaK)&&(
              <button onClick={()=>{setFiltroVends([]);setFiltroPeriodo('todo');setFiltroLinea([]);setBusqOpp('');setSortOpps('none');setDropLinea(false);setDropVend(false);setFiltroEtapaK('');}}
                style={{display:'flex',alignItems:'center',gap:4,padding:'4px 12px',borderRadius:R.md,fontSize:11.5,fontWeight:600,cursor:'pointer',
                  background:C.redTint,color:C.redText,border:`1px solid ${C.redBorder}`,flexShrink:0,marginLeft:'auto'}}>
                ✕ Limpiar
              </button>
            )}

            {/* Búsqueda — a la derecha, como el mockup */}
            <div style={{position:'relative',flexShrink:0,marginLeft:'auto'}}>
              <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:C.t4,pointerEvents:'none',display:'inline-flex'}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input value={busqOpp} onChange={e=>setBusqOpp(e.target.value)} placeholder="Filtrar tablero…"
                style={{background:C.bg2,border:`1px solid ${busqOpp?C.blue:C.border}`,borderRadius:R.md,height:30,padding:'0 12px 0 32px',
                  color:C.t1,fontSize:12,outline:'none',width:190,transition:'border-color .15s, box-shadow .15s',boxShadow:busqOpp?`0 0 0 3px ${C.ring}`:SH.xs}}/>
            </div>
            <Btn size="sm" onClick={()=>setModalOpp(true)}>+ Nueva Oportunidad</Btn>
          </div>
        </div>
      )}

      {/* PIPELINE KANBAN */}
      {tab==='pipeline'&&(
        <div style={{display:'flex'}}>
          <div style={{flex:1,overflowX:'auto',display:'flex',alignItems:'flex-start',padding:'12px 24px 24px',gap:16,background:C.bg0}}>
          {ETAPAS.filter(et=>!filtroEtapaK||et.id===filtroEtapaK).map(et=>{
            const isClosed = et.id==='cerrado_ganado'||et.id==='cerrado_perdido'||et.id==='prospecto_invalido'||et.id==='cliente_frio';
            const eOps = isClosed
              ? oppsFiltradas.filter(o=>o.etapa===et.id && dias(o.fecha_cierre||o.ultimo_seguimiento)<=60).sort((a,b)=>new Date(b.fecha_cierre||b.ultimo_seguimiento)-new Date(a.fecha_cierre||a.ultimo_seguimiento))
              : oppsFiltradas.filter(o=>o.etapa===et.id);
            const total = eOps.reduce((s,o)=>s+(o.valor||0),0);
            const isDrop = dragOpp && dragOpp.etapa!==et.id && !isClosed;
            return (
              <div key={et.id}
                onDragOver={e=>{if(!isClosed)e.preventDefault();}}
                onDrop={()=>!isClosed&&dragOpp&&handleMoverEtapa(dragOpp.id,et.id)}
                style={{flex:'0 0 256px',width:256,minWidth:256,maxWidth:256,display:'flex',flexDirection:'column',background:isDrop?C.primaryTint:'transparent',outline:isDrop?`2px dashed ${C.primaryBorder}`:'none',outlineOffset:-2,borderRadius:12,transition:'background .15s',opacity:isClosed?.85:1}}>
                {/* Header de columna — tarjeta blanca (receta 7.3) */}
                <div style={{padding:'2px 2px 0',flexShrink:0}}>
                  <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:'9px 12px',boxShadow:SH.sm,display:'flex',alignItems:'center',gap:8,minHeight:24}}>
                    <span style={{width:8,height:8,borderRadius:'50%',background:et.color,boxShadow:`0 0 0 3px ${et.color}24`,flexShrink:0}}/>
                    <span style={{fontSize:12,fontWeight:700,color:C.t1,letterSpacing:'-0.005em',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{et.label}</span>
                    <span className="radar-num" style={{background:C.bg3,color:C.t3,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,marginLeft:'auto',flexShrink:0}}>{eOps.length}</span>
                    {et.id==='prospecto'&&(
                      <button onClick={()=>{setFOpp(x=>({...x,etapa:'prospecto'}));setModalOpp(true);}}
                        title="Agregar prospecto" className="radar-plus"
                        style={{background:'transparent',color:C.t4,border:'none',borderRadius:R.sm,width:24,height:24,fontSize:15,lineHeight:'22px',fontWeight:700,cursor:'pointer',padding:0,flexShrink:0}}>+</button>
                    )}
                  </div>
                  <div className="radar-num" style={{fontSize:11.5,fontWeight:500,color:C.t3,padding:'8px 4px 0'}}><b style={{fontWeight:600,color:total>0?C.t2:C.t4}}>{fmtK(total||0)}</b>{et.id==='cerrado_ganado'?' cerrado':et.id==='cerrado_perdido'?' perdido':et.id==='prospecto_invalido'?' inválido':et.id==='cliente_frio'?' frío':' en pipeline'}</div>
                </div>
                <div style={{maxHeight:'70vh',overflowY:'auto',padding:'10px 2px 8px'}}>
                  {eOps.map(o=>{
                    const d=dias(o.ultimo_seguimiento);
                    const riskCol = !isClosed && d>5 ? C.red : !isClosed && d>2 ? C.orange : null;
                    const isSel = sel?.id===o.id;
                    // En la card, el numero SAP ya va en su badge: el titulo muestra solo lo descriptivo.
                    const tituloCard = (o.titulo||'').replace(/^\[SAP\]\s*Cotizaci\u00f3n\s*\d+\s*[\u2014\-]*\s*/i,'') || o.titulo || '';
                    const clienteCard = (o.cliente && o.cliente.trim().toLowerCase() !== tituloCard.trim().toLowerCase()) ? o.cliente : null;
                    return (
                      <div key={o.id} draggable={!isClosed} onDragStart={()=>!isClosed&&setDragOpp(o)} onDragEnd={()=>setDragOpp(null)}
                        onClick={()=>setSel(o)} className="radar-kcard"
                        style={{
                          background:C.bg2,
                          border:`1px solid ${isSel?C.blue:C.border}`,
                          borderLeft: riskCol ? `3px solid ${riskCol}` : `1px solid ${isSel?C.blue:C.border}`,
                          boxShadow: isSel ? `0 0 0 3px ${C.ring}` : SH.xs,
                          borderRadius:10,padding:riskCol?'10px 13px 9px 11px':'10px 13px 9px',cursor:isClosed?'pointer':'grab',marginBottom:8,userSelect:'none',
                          display:'flex',flexDirection:'column',gap:4
                        }}>
                        <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0,height:20}}>
                          <div title={o.titulo} style={{flex:1,minWidth:0,fontSize:11.5,fontWeight:600,letterSpacing:'-0.005em',color:C.t1,lineHeight:1.3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{tituloCard}</div>
                          {!isClosed&&(()=>{ const b = d>60?['Estancada',C.redTint,C.redText,C.redBorder,C.red]
                              : d>5?['Atención',C.orangeTint,C.orangeText,C.orangeBorder,C.orange]
                              : d>2?['Activo',C.primaryTint,C.blue,C.primaryBorder,C.blue]
                              : ['Al día',C.greenTint,C.greenText,C.greenBorder,C.green];
                            return <span style={{display:'inline-flex',alignItems:'center',gap:5,background:b[1],color:b[2],border:`1px solid ${b[3]}`,borderRadius:R.pill,padding:'0 8px',height:20,fontSize:10.5,fontWeight:600,whiteSpace:'nowrap',flexShrink:0}}><span style={{width:6,height:6,borderRadius:'50%',background:b[4],flexShrink:0}}/>{b[0]}</span>; })()}
                        </div>
                        <div title={clienteCard||''} style={{fontSize:10.5,color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',height:15,lineHeight:'15px'}}>{clienteCard||'\u00A0'}</div>
                        <div style={{display:'flex',alignItems:'center',gap:6,minWidth:0,height:20}}>
                          <span className="radar-num" style={{fontSize:13.5,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>{o.valor>0?`$${(o.valor).toLocaleString()}`:'\u2014'}</span>
                          <span style={{marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:5,flexShrink:0}}>
                            {!o.linea_negocio&&!isClosed&&<span title="Sin categoría" style={{display:'inline-flex',alignItems:'center',gap:3,background:C.orangeTint,color:C.orangeText,border:`1px solid ${C.orangeBorder}`,borderRadius:R.pill,padding:'1px 6px',fontSize:9.5,fontWeight:600,whiteSpace:'nowrap'}}>⚠ Sin cat.</span>}
                            {(o.sap_quotation_num||o.factura_sap_num)&&(
                              <span className="radar-num" style={{display:'inline-flex',alignItems:'center',gap:4,
                                background:o.etapa==='pagado'?C.greenTint:C.infoTint,color:o.etapa==='pagado'?C.greenText:C.infoText,
                                border:`1px solid ${o.etapa==='pagado'?C.greenBorder:C.infoBorder}`,borderRadius:R.pill,padding:'1px 7px',fontSize:10,fontWeight:600,whiteSpace:'nowrap'}}>
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
                                {o.etapa==='pagado'?'✓ Cobrada · F':'SAP '}{o.factura_sap_num||o.sap_quotation_num}
                              </span>
                            )}
                          </span>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:6,borderTop:`1px solid ${C.bg1}`,paddingTop:7,marginTop:3,minWidth:0,minHeight:27}}>
                          {o.vendedor
                            ? <><AvatarChip name={o.vendedor}/><span style={{fontSize:10,fontWeight:500,color:C.t2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{o.vendedor}</span></>
                            : <span style={{fontSize:10,fontWeight:600,color:C.redText,whiteSpace:'nowrap'}}>⚠ Sin asignar</span>}
                          {o.linea_negocio&&<LobTag l={o.linea_negocio} max={72}/>}
                          <span className="radar-num" title={`${d} días sin contacto`} style={{marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:3,fontSize:10,fontWeight:d>2?600:500,color:d>5?C.redText:d>2?C.orangeText:C.t4,flexShrink:0}}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            {d===0?'Hoy':`${d}d`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {eOps.length===0&&(loading
                    ? <div style={{display:'flex',flexDirection:'column',gap:8}}>
                        {[0,1].map(k=>(
                          <div key={k} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 13px 8px',boxShadow:SH.xs}}>
                            <Skel w="70%" h={11}/><Skel w="45%" h={9} style={{marginTop:7}}/><Skel w="35%" h={13} style={{marginTop:8}}/>
                          </div>
                        ))}
                      </div>
                    : <div style={{textAlign:'center',color:C.t4,fontSize:12,marginTop:20}}>Sin oportunidades</div>)}
                </div>
              </div>
            );
          })}
          </div>
          {/* Ficha de la oportunidad */}
          {sel&&tab==='pipeline'&&(
            // Ficha de oportunidad: modal centrado con pestanas (reemplaza el drawer
            // lateral de 320px que obligaba a scrollear ~450px de contenido).
            <div onClick={()=>setSel(null)} style={{position:'fixed',inset:0,background:'rgba(15,23,42,.6)',backdropFilter:'blur(2px)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
            <div className="radar-pop" onClick={e=>e.stopPropagation()} style={{background:C.bg2,borderRadius:R.lg,width:'100%',maxWidth:880,maxHeight:'88vh',boxShadow:SH.lg,display:'flex',flexDirection:'column',overflow:'hidden'}}>
              {/* ── CABECERA FIJA ───────────────────────────────────────── */}
              <div style={{padding:'20px 26px 0',flexShrink:0}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:20}}>
                  <div style={{flex:1,minWidth:0}}>
                    {(()=>{ const et=ETAPAS.find(e=>e.id===sel.etapa);
                      return (
                        <div style={{display:'flex',alignItems:'center',gap:9,marginBottom:7}}>
                          <span style={{width:7,height:7,borderRadius:'50%',background:et?.color||C.t4,flexShrink:0}}/>
                          <span style={{fontSize:11,fontWeight:700,color:et?.color||C.t3,letterSpacing:'.08em',textTransform:'uppercase'}}>{et?.label||sel.etapa}</span>
                          {sel.created_at && <span style={{fontSize:11,color:C.t4}}>· {dias(sel.created_at)}d en pipeline</span>}
                        </div>
                      ); })()}
                    <div style={{fontSize:23,fontWeight:800,letterSpacing:'-.025em',color:C.t1,lineHeight:1.2}}>{sel.titulo}</div>
                    <div style={{marginTop:6,fontSize:13,color:C.t3,display:'flex',alignItems:'center',gap:7,flexWrap:'wrap'}}>
                      {sel.cliente && <b style={{color:C.t2,fontWeight:600}}>{sel.cliente}</b>}
                      {sel.vendedor && <span>· {sel.vendedor}</span>}
                      {sel.linea_negocio && <span style={{fontSize:10.5,fontWeight:700,padding:'1px 8px',borderRadius:R.sm,background:C.infoTint,color:C.infoText,border:`1px solid ${C.infoBorder}`}}>{sel.linea_negocio}</span>}
                    </div>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:12,flexShrink:0}}>
                    <div style={{display:'flex',gap:5}}>
                      <button onClick={()=>{setModalUnir(sel);setUnirSel(null);setUnirBusq('');}} title="Unir / Anexar otra oportunidad"
                        style={{width:30,height:30,display:'grid',placeItems:'center',background:'none',border:'1px solid transparent',borderRadius:R.md,color:C.t4,fontSize:13,cursor:'pointer'}}>🔗</button>
                      <button onClick={()=>{setFEditOpp({id:sel.id,titulo:sel.titulo?.replace(/ \[(HNL|CRC)→USD\]/,''),valor:sel.valor||'',moneda:'USD',fecha_cierre:sel.fecha_cierre||'',linea_negocio:sel.linea_negocio||'',tipo:sel.tipo||'',proxima_accion:sel.proxima_accion||'',notas:''});setModalEditOpp(true);}} title="Editar"
                        style={{width:30,height:30,display:'grid',placeItems:'center',background:'none',border:'1px solid transparent',borderRadius:R.md,color:C.t4,fontSize:13,cursor:'pointer'}}>✏️</button>
                      <button onClick={()=>setSel(null)} title="Cerrar"
                        style={{width:30,height:30,display:'grid',placeItems:'center',background:'none',border:'1px solid transparent',borderRadius:R.md,color:C.t4,fontSize:20,cursor:'pointer',lineHeight:1}}>×</button>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div className="radar-num" style={{fontSize:28,fontWeight:800,letterSpacing:'-.035em',lineHeight:1.05,color:C.t1}}>{sel.valor>0?`$${Math.round(sel.valor).toLocaleString('en-US')}`:'Sin valor'}</div>
                      {sel.valor>0&&<div className="radar-num" style={{fontSize:12,color:C.t3,marginTop:4}} title={`Valor esperado = valor x probabilidad de cierre de la etapa (${Math.round((ETAPAS_PROB[sel.etapa]||0)*100)}%).`}>esperado <b style={{color:C.greenText,fontWeight:700}}>${Math.round((sel.valor)*(ETAPAS_PROB[sel.etapa]||0)).toLocaleString()}</b></div>}
                    </div>
                  </div>
                </div>

              {/* Alerta de asignacion y badges: fuera de las pestanas, siempre visibles */}
              {!sel.vendedor&&(
                <div style={{marginBottom:10,background:'#EF444420',border:'1px solid #EF444460',borderRadius:8,padding:'8px 12px'}}>
                  <div style={{fontSize:13,fontWeight:800,color:'#EF4444',marginBottom:usuario.rol==='ventas'?7:0}}>⚠ Sin asignar{usuario.rol!=='ventas'?' — asigná un vendedor':''}</div>
                  {usuario.rol==='ventas'&&(
                    <div style={{display:'flex',gap:6}}>
                      <button onClick={()=>reclamarOpp(sel)}
                        style={{flex:1,background:C.green,color:'#fff',border:'none',borderRadius:7,padding:'7px 10px',fontSize:12,fontWeight:800,cursor:'pointer'}}>✓ Es mía</button>
                      <button onClick={()=>descartarOppMia(sel)}
                        style={{flex:1,background:'transparent',color:'#EF4444',border:'1px solid #EF444460',borderRadius:7,padding:'7px 10px',fontSize:12,fontWeight:800,cursor:'pointer'}}>✕ No es mía</button>
                    </div>
                  )}
                </div>
              )}
              {/* Badges de moneda (el monto vive en la cabecera, no se repite aca) */}
              {(sel.titulo?.includes('[HNL→USD]') || sel.titulo?.includes('[SAP]')) && (
                <div style={{display:'flex',alignItems:'center',gap:6,marginTop:10,flexWrap:'wrap'}}>
                  {sel.titulo?.includes('[HNL→USD]') && <span style={{background:'#F59E0B20',color:C.orange,border:'1px solid #F59E0B40',borderRadius:6,padding:'2px 8px',fontSize:10,fontWeight:800}}>HNL → USD</span>}
                  {sel.titulo?.includes('[SAP]') && !sel.titulo?.includes('[HNL→USD]') && <span style={{background:'#0B7AB820',color:C.blue,border:'1px solid #0B7AB840',borderRadius:6,padding:'2px 8px',fontSize:10,fontWeight:800}}>USD</span>}
                </div>
              )}
              {sel.notas?.startsWith('Valor original SAP:') && (
                <div style={{fontSize:11,color:C.orange,marginBottom:4,fontWeight:600}}>{sel.notas.replace('Valor original SAP:','🔁 Original SAP:')}</div>
              )}
              {sel.factura_sap_num&&<div style={{marginBottom:8,background:'#10B98115',border:'1px solid #10B98140',borderRadius:8,padding:'7px 12px',fontSize:12,color:C.green,fontWeight:700}}>🧾 Factura SAP #{sel.factura_sap_num}</div>}
              {sel.etapa==='pagado'&&<div style={{marginBottom:8,background:C.greenTint,border:`1px solid ${C.greenBorder}`,borderRadius:8,padding:'7px 12px',fontSize:12,color:C.greenText,fontWeight:700}}>💵 Cobro registrado en SAP — pagada</div>}
              {sel.etapa==='facturado'&&!sel.factura_sap_num&&<div style={{marginBottom:8,background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:8,padding:'7px 12px',fontSize:11.5,color:C.orangeText,fontWeight:600}}>⚠ Facturada pero sin factura SAP vinculada — vinculala para que cuadre con Cobros</div>}
                {/* Pestanas */}
                <div style={{display:'flex',gap:22,marginTop:16,borderBottom:`1px solid ${C.border}`,overflowX:'auto'}}>
                  {[['resumen','Resumen',null],['seguimiento','Seguimiento',segHist.length||null],['whatsapp','WhatsApp',waChat?(waChat.mensajes_total||waMsgs.length||null):null],['sap','Cotización',null],['mas','Más',null]].map(([id,lbl,n])=>(
                    <button key={id} onClick={()=>setOppTab(id)}
                      style={{display:'inline-flex',alignItems:'center',gap:6,padding:'0 0 11px',border:'none',background:'none',fontSize:13,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap',marginBottom:-1,
                        color:oppTab===id?C.primary:C.t3,borderBottom:`2px solid ${oppTab===id?C.primary:'transparent'}`}}>
                      {lbl}{n?<span style={{fontSize:10,fontWeight:700,color:oppTab===id?C.primary:C.t4}}>{n}</span>:null}
                    </button>
                  ))}
                </div>
              </div>
              {/* ── CUERPO ──────────────────────────────────────────────── */}
              <div style={{flex:1,overflowY:'auto',padding:'20px 26px 24px'}}>
              {oppTab==='resumen' && (<>
              <div className="opp-two" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:26,alignItems:'start'}}>
                {/* ── COLUMNA IZQUIERDA ─────────────────────────────── */}
                <div style={{minWidth:0,paddingRight:26,borderRight:`1px solid ${C.border}`}}>
                  <div style={SEC_LAB}><i style={SEC_BAR}/>Datos</div>
                  {/* A QUIEN LLAMAR. La ficha traia la empresa y el telefono
                      pero no el nombre de la persona, asi que el vendedor
                      marcaba sin saber por quien preguntar. La oportunidad casi
                      nunca trae contacto propio (las cotizaciones de SAP no lo
                      mandan), asi que se cae al del cliente. Se lee en vivo y no
                      se copia, para que no quede viejo cuando alguien corrige
                      el dato en la ficha del cliente. */}
                  {(() => {
                    const cli = clientes.find(c => c.id === sel.cliente_id)
                      || clientes.find(c => String(c.empresa||'').trim().toUpperCase() === String(sel.cliente||'').trim().toUpperCase());
                    const quien = sel.contacto || cli?.contacto || null;
                    const tel = sel.telefono || cli?.telefono || null;
                    const wa = tel ? String(tel).replace(/[^0-9]/g,'') : '';
                    if (!quien && !tel) return (
                      <div style={{background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.md,
                        padding:'9px 11px',marginBottom:10,fontSize:12,color:C.t2}}>
                        No hay contacto ni telefono, ni en la oportunidad ni en el cliente. Hay que conseguirlo antes de llamar.
                      </div>
                    );
                    return (
                      <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,
                        padding:'10px 12px',marginBottom:10,display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                        <div style={{flex:1,minWidth:130}}>
                          <div style={{fontSize:10,fontWeight:800,letterSpacing:'.06em',textTransform:'uppercase',color:C.t4}}>
                            A quien llamar
                          </div>
                          <div style={{fontSize:14,fontWeight:700,color:C.t1,marginTop:2}}>
                            {quien || 'Sin nombre'}
                          </div>
                          {tel && <div className="radar-num" style={{fontSize:12.5,color:C.t3}}>{tel}</div>}
                          {!sel.contacto && quien && (
                            <div style={{fontSize:10.5,color:C.t4,marginTop:2}}>del cliente, no de esta oportunidad</div>
                          )}
                        </div>
                        {tel && (
                          <div style={{display:'flex',gap:6}}>
                            <a href={`tel:${String(tel).replace(/[^0-9+]/g,'')}`} onClick={e=>e.stopPropagation()}
                              style={{...BTN_PRIMARY,padding:'6px 12px',fontSize:12,textDecoration:'none',
                                background:C.bg3,color:C.t1}}>Llamar</a>
                            <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hola ${quien||''}, le contacto de ${ORG.nombre} sobre ${sel.titulo||'su cotizacion'}`)}`}
                              target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
                              style={{...BTN_PRIMARY,padding:'6px 12px',fontSize:12,textDecoration:'none',
                                background:'#25D366',color:'#fff'}}>WhatsApp</a>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{display:'flex',flexDirection:'column',gap:2}}>
                    {[
                      ['Cliente', sel.cliente, CI.contacts],
                      ['Tipo', sel.tipo, CI.clipboard],
                      ['Cierre estimado', fecha(sel.fecha_cierre), CI.calendar],
                      ['Último contacto', fecha(sel.ultimo_seguimiento), CI.phone],
                      ['Sin contacto', sel.ultimo_seguimiento?`${dias(sel.ultimo_seguimiento)} d`:null, CI.clock, dias(sel.ultimo_seguimiento)>=7?C.redText:C.greenText],
                      ['En pipeline', sel.created_at?`${dias(sel.created_at)} d`:null, CI.chart],
                      ['Próxima acción', sel.proxima_accion, CI.check2],
                    ].map(([l,v,ic,col],i)=>(
                      <div key={l} style={{display:'flex',alignItems:'center',gap:10,padding:'7px 9px',borderRadius:R.md,background:i%2===0?C.bg1:'transparent'}}>
                        <span style={{width:24,height:24,borderRadius:R.sm,background:C.primaryTint,color:C.primary,display:'grid',placeItems:'center',flexShrink:0}}><CIcon d={ic} size={13}/></span>
                        <span style={{fontSize:12,color:C.t2,fontWeight:600,flex:1,minWidth:0}}>{l}</span>
                        <span style={{fontSize:13,fontWeight:700,color:col||C.t1,textAlign:'right',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'58%'}}>{v||'—'}</span>
                      </div>
                    ))}
                  </div>

                  {/* Contacto */}
                  {(() => {
                    const telNota = (sel.notas||'').match(/(?:Tel|Teléfono|Telefono|Cel|Celular|WhatsApp|Wsp)[:\s]*(\+?[\d\s\-().]{7,})/i);
                    const limpiar = t => String(t||'').replace(/[^\d+]/g,'');
                    const telCli = limpiar(selClienteTel);
                    const telAlt = telNota ? limpiar(telNota[1]) : '';
                    const tel = telCli || telAlt;
                    if (!tel) return null;
                    const waNum = tel.startsWith('+') ? tel.replace('+','') : (tel.length === 8 ? `504${tel}` : tel);
                    return (
                      <div style={{marginTop:20,paddingTop:20,borderTop:`1px solid ${C.border}`}}>
                        <div style={SEC_LAB}><i style={SEC_BAR}/>Contacto</div>
                        <div style={{display:'flex',alignItems:'center',gap:10,padding:'7px 9px',borderRadius:R.md,background:C.bg1}}>
                          <span style={{width:24,height:24,borderRadius:R.sm,background:C.primaryTint,color:C.primary,display:'grid',placeItems:'center',flexShrink:0}}><CIcon d={CI.phone} size={13}/></span>
                          <span style={{fontSize:12,color:C.t2,fontWeight:600,flex:1}}>Teléfono</span>
                          <span className="radar-num" style={{fontSize:13,fontWeight:700,color:C.t1}}>{tel}</span>
                        </div>
                        {!telCli && telAlt && <div style={{fontSize:11,color:C.orangeText,marginTop:5}}>Número tomado de la nota, no de la ficha del cliente.</div>}
                        <div style={{display:'flex',gap:8,marginTop:11}}>
                          <a href={`tel:${tel}`} style={{flex:1,textAlign:'center',padding:'8px 0',borderRadius:R.md,border:`1px solid ${C.border}`,background:C.bg2,color:C.t2,fontSize:13,fontWeight:600,textDecoration:'none'}}>Llamar</a>
                          <a href={`https://wa.me/${waNum}?text=Hola,%20le%20contacto%20de%20${encodeURIComponent(ORG.nombre)}`} target="_blank" rel="noreferrer"
                            style={{flex:1,textAlign:'center',padding:'8px 0',borderRadius:R.md,border:`1px solid ${C.greenBorder}`,background:C.greenTint,color:C.greenText,fontSize:13,fontWeight:600,textDecoration:'none'}}>WhatsApp</a>
                        </div>
                        {!telCli && telAlt && sel.cliente_id && (
                          <button onClick={async()=>{
                            const { error } = await supabase.from('clientes').update({ telefono: telAlt, editado_manual_at:new Date().toISOString() }).eq('id', sel.cliente_id);
                            if (error) { toast('No se pudo guardar: '+error.message); return; }
                            setSelClienteTel(telAlt); toast('Teléfono guardado en la ficha del cliente.');
                          }} className="radar-btn" style={{width:'100%',marginTop:8,background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,borderRadius:R.md,padding:'7px 0',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                            Guardar este número en la ficha del cliente
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* ── COLUMNA DERECHA ───────────────────────────────── */}
                <div style={{minWidth:0}}>
                  <div style={SEC_LAB}><i style={SEC_BAR}/>Asignación</div>

                  {/* Alerta de datos pendientes */}
                  {!['cerrado_ganado','cerrado_perdido','prospecto_invalido','cliente_frio'].includes(sel.etapa) && (!sel.linea_negocio || !selClienteTel) && (
                    <div style={{marginBottom:14,background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.md,padding:'10px 12px'}}>
                      <div style={{fontSize:12,fontWeight:800,color:C.orangeText,marginBottom:6}}>Datos pendientes</div>
                      {!sel.linea_negocio && <div style={{fontSize:11.5,color:C.t2,marginBottom:!selClienteTel?8:0}}>Falta la <strong>línea de negocio</strong> — elegila abajo.</div>}
                      {!selClienteTel && (
                        <div style={{display:'flex',gap:6,alignItems:'center'}}>
                          <input value={telInput} onChange={e=>setTelInput(e.target.value)} placeholder="Teléfono del cliente (+504…)"
                            style={{flex:1,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'7px 10px',color:C.t1,fontSize:12.5,outline:'none'}}/>
                          <Btn size="sm" disabled={!telInput.trim()||savingTel} onClick={async()=>{
                            setSavingTel(true);
                            const tel = telInput.trim();
                            let cid = sel.cliente_id;
                            if (!cid && sel.cliente) {
                              let { data } = await supabase.from('clientes').select('id').ilike('empresa', sel.cliente).limit(1).maybeSingle();
                              if (!data) { const r = await supabase.from('clientes').select('id').ilike('empresa', `%${sel.cliente}%`).limit(1).maybeSingle(); data = r.data; }
                              if (data?.id) {
                                cid = data.id;
                                await supabase.from('oportunidades').update({cliente_id:cid}).eq('id',sel.id);
                                setOpps(os=>os.map(o=>o.id===sel.id?{...o,cliente_id:cid}:o));
                                setSel(s=>({...s,cliente_id:cid}));
                              }
                            }
                            if (cid) await supabase.from('clientes').update({telefono:tel, editado_manual_at:new Date().toISOString()}).eq('id',cid);
                            else toast('No encontré el cliente en RADAR para guardar el teléfono. Corré una sincronización de SAP y reintentá.');
                            if (cid) setSelClienteTel(tel);
                            setTelInput(''); setSavingTel(false);
                          }}>{savingTel?'…':'Guardar'}</Btn>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{display:'flex',flexDirection:'column',gap:13}}>
                    {/* Vendedor */}
                    <div>
                      <div style={FLD_LAB}>Vendedor</div>
                      {(usuario.rol==='admin'||usuario.rol==='gerente_ventas') ? (
                        <select value={sel.vendedor||''} onChange={async e=>{
                          const v=e.target.value;
                          await supabase.from('oportunidades').update({vendedor:v}).eq('id',sel.id);
                          setOpps(os=>os.map(o=>o.id===sel.id?{...o,vendedor:v}:o));
                          setSel(s=>({...s,vendedor:v}));
                        }} style={{...INP_ST, ...(sel.vendedor?{}:INP_WARN)}}>
                          <option value=''>— Sin asignar —</option>
                          {vendedoresList.map(v=><option key={v} value={v}>{v}</option>)}
                        </select>
                      ) : !sel.vendedor ? (
                        <button onClick={async()=>{
                          await supabase.from('oportunidades').update({vendedor:usuario.nombre}).eq('id',sel.id);
                          setOpps(os=>os.map(o=>o.id===sel.id?{...o,vendedor:usuario.nombre}:o));
                          setSel(s=>({...s,vendedor:usuario.nombre}));
                        }} style={{width:'100%',background:C.greenTint,color:C.greenText,border:`1px solid ${C.greenBorder}`,borderRadius:R.md,padding:'8px 10px',fontSize:13,fontWeight:700,cursor:'pointer',textAlign:'left'}}>
                          Asignarme esta oportunidad
                        </button>
                      ) : (
                        <div style={{fontSize:13.5,fontWeight:600,color:C.t1,padding:'8px 0'}}>{sel.vendedor}</div>
                      )}
                    </div>

                    {/* Línea de negocio */}
                    <div>
                      <div style={FLD_LAB}>Línea de negocio</div>
                      <select value={sel.linea_negocio||''} onChange={async e=>{
                        const v=e.target.value||null;
                        await supabase.from('oportunidades').update({linea_negocio:v}).eq('id',sel.id);
                        setOpps(os=>os.map(o=>o.id===sel.id?{...o,linea_negocio:v}:o));
                        setSel(s=>({...s,linea_negocio:v}));
                      }} style={{...INP_ST, ...(sel.linea_negocio?{}:INP_WARN)}}>
                        <option value=''>Sin categoría</option>
                        {ORG.lineasNegocio.map(l=><option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>

                    {/* Bloqueo interno */}
                    {!['cerrado_ganado','cerrado_perdido','prospecto_invalido','cliente_frio'].includes(sel.etapa) && (
                      <div>
                        <div style={FLD_LAB}>Bloqueo interno</div>
                        <select value={sel.bloqueo||''} onChange={async e=>{
                            const v = e.target.value || null;
                            const upd = { bloqueo: v, bloqueo_desde: v ? new Date().toISOString().split('T')[0] : null };
                            await supabase.from('oportunidades').update(upd).eq('id', sel.id);
                            setSel(x=>({...x,...upd}));
                            setOpps(os=>os.map(o=>o.id===sel.id?{...o,...upd}:o));
                          }}
                          style={{...INP_ST, ...(sel.bloqueo?{borderColor:C.orangeBorder,background:C.orangeTint,color:C.orangeText,fontWeight:700}:{})}}>
                          <option value="">Ninguno</option>
                          {[['PRECIO','Precio'],['CREDITO','Crédito'],['FINANCIAMIENTO','Financiamiento'],['DISPONIBILIDAD','Disponibilidad'],['TALLER','Taller'],['COMPRAS','Compras'],['GERENCIA','Gerencia'],['OTRO','Otro']].map(([v,l])=>
                            <option key={v} value={v}>{l}</option>)}
                        </select>
                        <div style={{fontSize:11,color:C.t3,marginTop:5}}>
                          {sel.bloqueo && sel.bloqueo_desde
                            ? `Frenada hace ${dias(sel.bloqueo_desde)} d. Sale en el reporte del lunes.`
                            : 'Marca por qué está frenada por decisión nuestra. Sale en el reporte del lunes.'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              </>)}
              {oppTab==='whatsapp' && (<>
              {/* Estado vacio: sin esto la pestana quedaba en blanco y parecia rota.
                  El chat es un espejo de WA Equipo: exige telefono del cliente en RADAR
                  Y una conversacion guardada del vendedor con ese numero. */}
              {!waChat && (
                <div style={{textAlign:'center',padding:'34px 20px',color:C.t3}}>
                  <div style={{fontSize:13.5,fontWeight:700,color:C.t2,marginBottom:5}}>Sin conversación de WhatsApp</div>
                  <div style={{fontSize:12.5,lineHeight:1.55,maxWidth:380,margin:'0 auto'}}>
                    {!selClienteTel
                      ? 'Este cliente no tiene teléfono cargado en RADAR. Agregalo en Resumen para poder cruzar el chat del vendedor.'
                      : 'No hay chat guardado del vendedor con este número. Los mensajes aparecen acá cuando el vendedor conversa desde WA Equipo.'}
                  </div>
                </div>
              )}
              {/* Chat de WhatsApp del vendedor con este cliente (espejo de WA Equipo, solo lectura) */}
              {waChat && (
                <div style={{marginTop:12,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
                  <div onClick={()=>setWaAbierto(v=>!v)} className="radar-hrow" style={{padding:'9px 12px',display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                    <span style={{width:7,height:7,borderRadius:'50%',background:'#25D366',flexShrink:0}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11.5,fontWeight:700,color:C.t1}}>WhatsApp de {(waChat.vendedor||'').split(' ')[0]} con el cliente</div>
                      <div style={{fontSize:10.5,color:C.t4,marginTop:1}}>{waChat.mensajes_total||waMsgs.length} mensajes{waChat.ultima_actividad?` · último ${fecha(waChat.ultima_actividad)}`:''}{waChat.categoria?` · ${waChat.categoria}`:''}</div>
                    </div>
                    <span style={{fontSize:10,color:C.t4,transform:waAbierto?'rotate(90deg)':'none',transition:'transform .15s'}}>▶</span>
                  </div>
                  {waAbierto && (
                    <div style={{borderTop:`1px solid ${C.border}`,padding:'10px 12px',background:C.bg1}}>
                      {waChat.resumen && <div style={{fontSize:11,color:C.t3,fontStyle:'italic',marginBottom:8,paddingBottom:8,borderBottom:`1px solid ${C.bg3}`}}>{waChat.resumen}</div>}
                      {waCargando && <div style={{fontSize:11,color:C.t4}}>Cargando conversación…</div>}
                      {!waCargando && waMsgs.length===0 && <div style={{fontSize:11,color:C.t4}}>Sin mensajes guardados.</div>}
                      <div style={{maxHeight:260,overflowY:'auto',display:'flex',flexDirection:'column',gap:6}}>
                        {/* WA Equipo marca los salientes como 'salida' (NEXA usa 'out') */}
                        {waMsgs.map((m,i)=>{ const mio = m.direccion==='salida' || m.direccion==='out'; return (
                          <div key={i} style={{alignSelf:mio?'flex-end':'flex-start',maxWidth:'85%',background:mio?'#DCF8C6':C.bg2,border:`1px solid ${mio?'#BBE8A0':C.border}`,borderRadius:9,padding:'6px 9px'}}>
                            <div style={{fontSize:11.5,color:C.t1,lineHeight:1.35,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{m.contenido || (m.tipo?`[${m.tipo}]`:'—')}</div>
                            <div style={{fontSize:9,color:C.t4,marginTop:2,textAlign:'right'}}>{m.ts?new Date(m.ts).toLocaleString('es-HN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}):''}</div>
                          </div>
                        );})}
                      </div>
                      <div style={{fontSize:9.5,color:C.t4,marginTop:8}}>Espejo del WhatsApp del vendedor (solo lectura). Para responder, usá su chat o el botón de WhatsApp de arriba.</div>
                    </div>
                  )}
                </div>
              )}
              </>)}
              {oppTab==='seguimiento' && (<>
              {sel.notas&&!sel.notas.startsWith('Valor original SAP:')&&(
                <div style={{marginTop:12,background:C.bg3,borderRadius:8,padding:12}}>
                  <div style={{fontSize:10,color:C.t4,fontWeight:700,textTransform:'uppercase',marginBottom:4}}>Última nota</div>
                  <div style={{fontSize:12,color:C.t2,lineHeight:1.5,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{sel.notas}</div>
                </div>
              )}
              </>)}
              {oppTab==='sap' && (<>
              {/* Crear la cotización en SAP para ESTE cliente. Antes solo se llegaba por
                  el menú global de acciones, que no venía precargado con el cliente. */}
              <div style={{marginBottom:16}}>
                <div style={SEC_LAB}><i style={SEC_BAR}/>Cotización en SAP</div>
                {(() => {
                  const numCot = cotSel || sel.sap_quotation_num || (String(sel.titulo||'').match(/\b(\d{4,6})\b/)||[])[1];
                  return (
                    <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',padding:'11px 13px',borderRadius:R.md,background:numCot?C.primaryTint:C.bg1,border:`1px solid ${numCot?C.primaryBorder:C.border}`}}>
                      <div style={{flex:1,minWidth:180}}>
                        <div style={{fontSize:13,fontWeight:700,color:C.t1}}>{numCot?`Cotización SAP ${numCot}`:'Sin cotización en SAP'}</div>
                        <div style={{fontSize:11.5,color:C.t3,marginTop:2}}>{numCot?'Editala, mirá las líneas o descargá los PDF abajo.':'Creála en SAP con el cliente y los artículos de esta oportunidad.'}</div>
                      </div>
                      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                        {numCot && (
                          <Btn size="sm" variant="secondary"
                            onClick={()=>editarCotSAP(numCot, sel.empresa_id && sel.empresa_id!=='all' ? sel.empresa_id : null)}>
                            ✏️ Editar
                          </Btn>
                        )}
                        <Btn size="sm" onClick={()=>abrirCotSAP(sel.cliente_id?{id:sel.cliente_id,empresa:sel.cliente}:(sel.cliente?{id:'',empresa:sel.cliente}:null))}>
                          {numCot?'Nueva cotización':'Cotizar en SAP'}
                        </Btn>
                      </div>
                    </div>
                  );
                })()}
                {/* TODAS LAS COTIZACIONES DEL CLIENTE.
                    La ficha solo dejaba trabajar con la cotizacion de ESTA
                    oportunidad: para ver una anterior -su PDF, sus lineas, una
                    proforma- habia que salir a buscarla a SAP a mano. Se listan
                    todas las del cliente y se elige sobre cual actuar; los
                    botones de abajo siguen a la elegida. */}
                {(() => {
                  const nom = String(sel.cliente||'').trim().toLowerCase();
                  const vistas = new Set();
                  const cots = (opps||[])
                    .filter(o => o.sap_quotation_num
                      && (o.cliente_id ? o.cliente_id===sel.cliente_id : (String(o.cliente||'').trim().toLowerCase()===nom && nom)))
                    .filter(o => { const n=String(o.sap_quotation_num); if(vistas.has(n)) return false; vistas.add(n); return true; })
                    .sort((a,b)=>Number(b.sap_quotation_num)-Number(a.sap_quotation_num));
                  if (cots.length < 2) return null;
                  const activa = cotSel || sel.sap_quotation_num;
                  return (
                    <div style={{marginTop:10}}>
                      <div style={{fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',marginBottom:5}}>
                        Cotizaciones de este cliente <span className="radar-num" style={{background:C.bg3,borderRadius:R.pill,padding:'1px 7px',marginLeft:4}}>{cots.length}</span>
                      </div>
                      <div style={{maxHeight:168,overflowY:'auto',border:`1px solid ${C.border}`,borderRadius:R.md}}>
                        {cots.map((o,i)=>{
                          const n = String(o.sap_quotation_num);
                          const esta = n === String(activa||'');
                          const et = ETAPAS.find(e=>e.id===o.etapa);
                          const f = (o.tipo||'').startsWith('SAP-') ? o.tipo.slice(4) : (o.fecha_cierre||'');
                          return (
                            <div key={n} onClick={()=>{ setCotSel(n); setSapLines(null); setSapPDF(null); }}
                              className="radar-hrow"
                              style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',cursor:'pointer',
                                borderBottom:i<cots.length-1?`1px solid ${C.bg3}`:'none',
                                background:esta?C.primaryTint:'transparent',
                                boxShadow:esta?`inset 3px 0 0 ${C.blue}`:'none'}}>
                              <span className="radar-num" style={{fontSize:11.5,fontWeight:800,color:esta?C.blue:C.t2,fontFamily:'monospace',minWidth:42}}>{n}</span>
                              <span style={{flex:1,minWidth:0,fontSize:11,color:C.t2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}
                                title={o.titulo}>{String(o.titulo||'').replace(/^\[SAP\]\s*Cotizaci[oó]n\s*\d+\s*—?\s*/i,'') || o.cliente}</span>
                              {f && <span style={{fontSize:9.5,color:C.t4,whiteSpace:'nowrap'}}>{f}</span>}
                              <span className="radar-num" style={{fontSize:11,fontWeight:700,color:C.t1,whiteSpace:'nowrap'}}>{fmt(o.valor||0)}</span>
                              {et && <span style={{fontSize:9,fontWeight:700,color:'#fff',background:et.color,borderRadius:R.pill,padding:'1px 7px',whiteSpace:'nowrap'}}>{et.label}</span>}
                            </div>
                          );
                        })}
                      </div>
                      <div style={{fontSize:9.5,color:C.t4,marginTop:4}}>Tocá una para verla, editarla o sacarle el PDF con los botones de abajo.</div>
                    </div>
                  );
                })()}
              </div>
              {/* Buscar en inventario sin salir de la oportunidad: ¿hay disponible y a qué
                  precio? Lee el cache de SAP (la reserva formal sigue siendo en el cotizador). */}
              <div style={{marginTop:12,background:C.bg3,borderRadius:8,padding:12}}>
                <div style={{fontSize:10,color:C.t4,fontWeight:700,textTransform:'uppercase',marginBottom:6}}>📦 Buscar en inventario</div>
                <div style={{display:'flex',gap:6}}>
                  <input value={invQ} onChange={e=>setInvQ(e.target.value)}
                    onKeyDown={e=>{ if(e.key==='Enter') buscarInventario(); }}
                    placeholder="Código, nombre o n° de parte…"
                    style={{flex:1,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:7,padding:'7px 10px',color:C.t1,fontSize:12,outline:'none'}}/>
                  <Btn size="sm" disabled={invBusy||invQ.trim().length<2} onClick={buscarInventario}>{invBusy?'…':'Buscar'}</Btn>
                </div>
                {invRes && (
                  <div style={{marginTop:8}}>
                    {invRes.length===0 && <div style={{fontSize:11,color:C.t4}}>Sin resultados en el inventario sincronizado.</div>}
                    {invRes.map((r,i)=>(
                      <div key={`${r.empresa_id}_${r.codigo}_${i}`} style={{padding:'6px 0',borderBottom:i<invRes.length-1?`1px solid ${C.border}`:'none'}}>
                        <div style={{display:'flex',justifyContent:'space-between',gap:6,alignItems:'baseline'}}>
                          <span className="radar-num" style={{fontSize:10.5,fontWeight:700,color:C.blue,fontFamily:'monospace'}}>{r.codigo}</span>
                          <span style={{fontSize:10,color:C.t4,whiteSpace:'nowrap'}}>{(ORG.empresas.find(e=>e.id===r.empresa_id)||{}).label||r.empresa_id}</span>
                        </div>
                        <div style={{fontSize:11,color:C.t2,margin:'1px 0 3px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.nombre}>{r.nombre}</div>
                        <div style={{display:'flex',gap:10,fontSize:10.5}}>
                          <span style={{color:C.t4}}>Disp. <b className="radar-num" style={{color:r.disponible>0?C.green:C.red}}>{r.disponible==null?'—':Number(r.disponible).toLocaleString('en-US')}</b></span>
                          <span style={{color:C.t4}}>Precio <b className="radar-num" style={{color:r.precio_usd?C.t1:C.t4}}>{r.precio_usd?`$${Number(r.precio_usd).toLocaleString('en-US',{minimumFractionDigits:2})}`:'—'}</b></span>
                        </div>
                      </div>
                    ))}
                    <div style={{fontSize:9.5,color:C.t4,marginTop:6}}>Del cache de SAP — el stock se revalida al cotizar.</div>
                  </div>
                )}
              </div>
              {/* Líneas SAP + PDF — opps de SAP (usa el campo, o si está vacío saca el número del título) */}
              {(() => { const qtNum = cotSel || sel.sap_quotation_num || ((sel.titulo||'').match(/Cotizaci[oó]n\s+(\d{2,})/i)||[])[1] || null; if (!qtNum) return null; return (
                <div style={{marginTop:14}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6,flexWrap:'wrap',gap:4}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase'}}>Líneas SAP · Cotiz. #{qtNum}</div>
                    <div style={{display:'flex',gap:4}}>
                      {!sapLines && <button onClick={()=>cargarLineasSAP(qtNum)}
                        disabled={loadingSapLines}
                        style={{background:`${C.blue}20`,color:C.blue,border:`1px solid ${C.blue}40`,borderRadius:6,padding:'3px 9px',fontSize:10,fontWeight:700,cursor:'pointer'}}>
                        {loadingSapLines?'⏳ Cargando...':'🔗 Ver líneas'}
                      </button>}
                      <button onClick={()=>generarCotizacion(qtNum)}
                        disabled={loadingPDF}
                        style={{background:sapPDF?.ok?'#10B98120':loadingPDF?`${C.t4}20`:`${C.orange}20`,
                               color:sapPDF?.ok?C.green:loadingPDF?C.t4:C.orange,
                               border:`1px solid ${sapPDF?.ok?C.green+'40':loadingPDF?C.border:C.orange+'40'}`,
                               borderRadius:6,padding:'3px 9px',fontSize:10,fontWeight:700,cursor:loadingPDF?'wait':'pointer'}}>
                        {loadingPDF?'⏳ Generando...':sapPDF?.ok?'✅ PDF':'📄 Cotización'}
                      </button>
                      <button onClick={()=>{ const p=window.prompt('¿Qué % de descuento aplicar a la cotización? (0–100)','10'); if(p!==null){ const n=Math.max(0,Math.min(100,parseFloat(p)||0)); generarCotizacion(qtNum, n); } }}
                        disabled={loadingPDF} title="Genera el PDF de la cotización con un descuento especial (solo en el PDF, no toca SAP)"
                        style={{background:`${C.pink}18`,color:C.pink,border:`1px solid ${C.pink}40`,
                               borderRadius:6,padding:'3px 9px',fontSize:10,fontWeight:700,cursor:loadingPDF?'wait':'pointer'}}>
                        🏷️ c/descuento
                      </button>
                      <button onClick={()=>generarProforma(qtNum)}
                        disabled={loadingPDF} title="Documento para el cliente (no toca inventario ni facturación)"
                        style={{background:`${C.indigo}18`,color:C.indigo,border:`1px solid ${C.indigo}40`,
                               borderRadius:6,padding:'3px 9px',fontSize:10,fontWeight:700,cursor:loadingPDF?'wait':'pointer'}}>
                        🧾 Proforma
                      </button>
                    </div>
                  </div>
                  {sapPDF?.ok===false && <div style={{fontSize:10,color:C.red,background:'#EF444410',borderRadius:6,padding:'5px 8px',marginBottom:6}}>⚠️ {sapPDF.error}</div>}
                  {sapLines?.error && <div style={{fontSize:11,color:C.red,marginBottom:4}}>{sapLines.error}</div>}
                  {sapLines?.doc?.Comments && <div style={{fontSize:11,color:C.t3,marginBottom:6,fontStyle:'italic'}}>💬 "{sapLines.doc.Comments}"</div>}
                  {sapLines?.lines?.length>0 && (
                    <div style={{background:C.bg3,borderRadius:8,overflow:'hidden'}}>
                      <div style={{display:'grid',gridTemplateColumns:'1fr auto auto',gap:'0 6px',padding:'5px 8px',borderBottom:`1px solid ${C.border}`,fontSize:9,fontWeight:800,color:C.t4,textTransform:'uppercase'}}>
                        <span>Artículo / Descripción</span><span style={{textAlign:'right'}}>Cant.</span><span style={{textAlign:'right'}}>Precio</span>
                      </div>
                      {sapLines.lines.map((l,i)=>(
                        <div key={i} style={{display:'grid',gridTemplateColumns:'1fr auto auto',gap:'0 6px',padding:'5px 8px',borderBottom:`1px solid ${C.border}`,background:i%2===0?'transparent':`${C.bg2}80`}}>
                          <span style={{fontSize:10.5,color:C.t2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={l.ItemDescription||l.ItemCode}>{l.ItemDescription||l.ItemCode||'—'}</span>
                          <span style={{fontSize:10.5,color:C.t4,textAlign:'right'}}>{l.Quantity}</span>
                          <span style={{fontSize:10.5,color:C.blue,fontWeight:700,textAlign:'right'}}>{l.UnitPrice>0?`$${(l.UnitPrice).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`:'—'}</span>
                        </div>
                      ))}
                      <div style={{padding:'5px 8px',display:'flex',justifyContent:'flex-end',gap:8,background:`${C.blue}10`}}>
                        <span style={{fontSize:10,color:C.t4,fontWeight:700}}>TOTAL:</span>
                        <span style={{fontSize:11,color:C.blue,fontWeight:900}}>${(sel.valor||0).toLocaleString('en-US',{minimumFractionDigits:2})}</span>
                      </div>
                    </div>
                  )}
                  {sapLines?.lines?.length===0 && !sapLines?.error && <div style={{fontSize:11,color:C.t4}}>Sin líneas registradas en SAP</div>}
                </div>
              ); })()}
              </>)}
              {oppTab==='mas' && (<>
              {sel?.etapa==='prospecto'&&!sel?.cliente_id&&(
                <div style={{marginTop:10}}>
                  <button onClick={()=>{setModalConvertir(sel);setFConvertir({nombre:sel.cliente||'',telefono:'',rtn:'',contacto:'',email:'',ciudad:'',industria:''});}}
                    style={{background:'#7C3AED20',color:'#7C3AED',border:'1px solid #7C3AED40',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer',width:'100%'}}>
                    👤 Convertir a Cliente
                  </button>
                </div>
              )}
              {sel?.etapa==='cerrado_perdido' && (
                <div style={{marginTop:14}}>
                  <button onClick={async()=>{
                    await supabase.from('oportunidades').update({etapa:'prospecto'}).eq('id',sel.id);
                    setOpps(os=>os.map(o=>o.id===sel.id?{...o,etapa:'prospecto'}:o));
                    setSel(prev=>({...prev,etapa:'prospecto'}));
                  }} style={{width:'100%',background:'#F59E0B20',color:C.orange,border:'1px solid #F59E0B40',borderRadius:8,padding:'7px 0',fontSize:12,fontWeight:800,cursor:'pointer'}}>
                    🔄 Reabrir oportunidad
                  </button>
                </div>
              )}
              </>)}
              {oppTab==='sap' && (<>
              {/* SAP Cobro Status — aparece cuando hay factura SAP vinculada */}
              {sel?.factura_sap_num && ['facturado','pagado'].includes(sel?.etapa) && (()=>{
                const st = sapPagoStatus[sel.factura_sap_num];
                const verificar = async () => {
                  const { data } = await supabase.from('facturas_sap').select('pagado,doc_total,total_usd,doc_date').eq('doc_num',sel.factura_sap_num).maybeSingle();
                  setSapPagoStatus(prev=>({...prev,[sel.factura_sap_num]:data||{pagado:false}}));
                };
                return (
                  <div style={{marginTop:12,background:st?.pagado?`${C.green}10`:`${C.orange}10`,border:`1px solid ${st?.pagado?C.green:C.orange}30`,borderRadius:10,padding:'10px 12px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:st?6:0}}>
                      <div style={{fontSize:11,fontWeight:700,color:st?.pagado?C.green:C.orange}}>
                        {st===undefined?'🧾 Factura SAP #'+sel.factura_sap_num:st.pagado?'✅ Cobrada en SAP':'⏳ Pendiente cobro en SAP'}
                      </div>
                      <button onClick={verificar} style={{background:'none',border:'none',fontSize:10,color:C.t4,cursor:'pointer',fontWeight:700,padding:'2px 6px',borderRadius:4,background:`${C.border}`,textDecoration:'underline'}}>Verificar</button>
                    </div>
                    {st&&<div style={{fontSize:10,color:C.t3}}>
                      {st.pagado?`Cobrado · $${(st.total_usd||0).toLocaleString('en-US',{minimumFractionDigits:2})}`:`Factura #${sel.factura_sap_num} · $${(st.total_usd||0).toLocaleString('en-US',{minimumFractionDigits:2})}`}
                    </div>}
                    {st?.pagado && sel.etapa==='facturado' && (
                      <button onClick={()=>moveOpp(sel.id,'pagado')} style={{marginTop:8,width:'100%',background:C.green,color:'#fff',border:'none',borderRadius:7,padding:'6px',fontSize:11,fontWeight:800,cursor:'pointer'}}>
                        ✅ Marcar como Pagado
                      </button>
                    )}
                  </div>
                );
              })()}
              {sel?.pago_anticipado_sap && sel?.etapa!=='pagado' && !['cerrado_ganado','cerrado_perdido'].includes(sel?.etapa) && (
                <div style={{marginTop:12,background:`${C.green}10`,border:`1px solid ${C.green}30`,borderRadius:10,padding:'10px 12px'}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.green,marginBottom:4}}>✅ Pago anticipado registrado en SAP</div>
                  <div style={{fontSize:10,color:C.t3,marginBottom:8}}>Susana registró un pago anticipado en la cotización SAP</div>
                  <button onClick={()=>moveOpp(sel.id,'pagado')} style={{width:'100%',background:C.green,color:'#fff',border:'none',borderRadius:7,padding:'6px',fontSize:11,fontWeight:800,cursor:'pointer'}}>
                    ✅ Marcar como Pagado
                  </button>
                </div>
              )}
              </>)}
              {oppTab==='seguimiento' && (<>
              {sel?.created_at && (() => {
                const totalDias = dias(sel.created_at);
                const hist = etapaHist || [];
                const tramos = hist.map((h,i)=>{
                  const ini = new Date(h.created_at);
                  const fin = i+1<hist.length ? new Date(hist[i+1].created_at) : new Date();
                  return { etapa:h.etapa, dias: Math.max(0, Math.round((fin-ini)/864e5)) };
                });
                const etActual = tramos.length ? tramos[tramos.length-1].dias : null;
                const etLabel = ETAPAS.find(e=>e.id===sel.etapa)?.label || sel.etapa;
                return (
                  <div style={{marginTop:14,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 12px'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                      <div style={{fontSize:12,color:C.t2}}>
                        🗓️ <strong style={{color:C.t1}}>{totalDias}d</strong> en pipeline
                        {etActual!=null && <span style={{color:C.t4}}> · {etActual}d en {etLabel}</span>}
                      </div>
                      {tramos.length>1 && <button onClick={()=>setDiasExpand(v=>!v)} style={{background:'none',border:'none',color:C.blue,fontSize:11,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>{diasExpand?'ocultar':'por etapa ▾'}</button>}
                    </div>
                    {diasExpand && tramos.length>0 && (
                      <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:4,borderTop:`1px solid ${C.border}`,paddingTop:8}}>
                        {tramos.map((t,i)=>{ const col=ETAPAS.find(e=>e.id===t.etapa)?.color||C.t4; return (
                          <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:11}}>
                            <span style={{color:col,fontWeight:700}}>{ETAPAS.find(e=>e.id===t.etapa)?.label||t.etapa}</span>
                            <span style={{color:C.t3}}>{t.dias}d</span>
                          </div>
                        );})}
                      </div>
                    )}
                  </div>
                );
              })()}
              </>)}
              {oppTab==='mas' && (<>
              <div style={{marginTop:12}}>
                <button onClick={()=>setConfirmEliminarOpp(sel)}
                  style={{width:'100%',background:'#EF444415',color:C.red,border:'1px solid #EF444430',borderRadius:8,padding:'6px 0',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                  🗑 Eliminar oportunidad
                </button>
              </div>
              </>)}
              {oppTab==='seguimiento' && (<>
              <div style={{marginTop:16}}>
                <Inp label="Registrar seguimiento" value={nota} onChange={setNota} placeholder="¿Qué pasó? ¿Próximo paso?" rows={3}/>
                <Btn full size="sm" onClick={async()=>{
                  if(!nota.trim()) return; setSavingNota(true);
                  const hoy=new Date().toISOString().split('T')[0];
                  const txt=nota.trim();
                  await supabase.from('oportunidades').update({ultimo_seguimiento:hoy,notas:txt}).eq('id',sel.id);
                  const {data:ins}=await supabase.from('opp_seguimientos').insert({opp_id:sel.id,vendedor:usuario.nombre,nota:txt}).select().single();
                  supabase.from('actividades_programadas').insert({titulo:`Seguimiento — ${sel.titulo||sel.cliente||'oportunidad'}`,tipo:'llamada',fecha:hoy,vendedor:usuario.nombre,cliente_id:sel.cliente_id||null,opp_id:sel.id,notas:txt,completada:true, origen:'auto',empresa_id:sel.empresa_id||null}).then(()=>{},()=>{});
                  setOpps(os=>os.map(o=>o.id===sel.id?{...o,ultimo_seguimiento:hoy,notas:txt}:o));
                  setSel(prev=>({...prev,ultimo_seguimiento:hoy,notas:txt}));
                  setSegHist(h=>[ins||{id:Math.random(),opp_id:sel.id,vendedor:usuario.nombre,nota:txt,created_at:new Date().toISOString()},...h]);
                  setNota(''); setSavingNota(false);
                }} disabled={!nota.trim()||savingNota}>{savingNota?'Guardando...':'📞 Marcar contactado hoy'}</Btn>
              </div>
              {segHist.length>0&&(
                <div style={{marginTop:16}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.06em'}}>Últimos seguimientos · {segHist.length}</div>
                    {segHist.length>2&&(
                      <button onClick={()=>setSegExpand(v=>!v)} style={{background:'none',border:'none',color:C.blue,fontSize:11,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:4,padding:0}}>
                        {segExpand?'Ver menos':'Ver todos'} <span style={{transition:'transform .15s',display:'inline-block',transform:segExpand?'rotate(90deg)':'none'}}>▸</span>
                      </button>
                    )}
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {(segExpand?segHist:segHist.slice(0,2)).map(s=>(
                      <div key={s.id} style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 11px',display:'flex',justifyContent:'space-between',gap:8,alignItems:'flex-start'}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,color:C.t1,lineHeight:1.45,whiteSpace:'pre-wrap'}}>{s.nota}</div>
                          <div style={{fontSize:10,color:C.t4,marginTop:4}}>{s.vendedor||'—'} · {fecha(s.created_at)}</div>
                        </div>
                        <button onClick={()=>borrarSeg(s)} title="Borrar seguimiento"
                          style={{background:'none',border:'none',color:C.t4,cursor:'pointer',fontSize:13,lineHeight:1,padding:2,flexShrink:0}}>✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>)}
              </div>
              {/* ── BARRA DE ACCION FIJA ────────────────────────────────── */}
              {!['cerrado_ganado','cerrado_perdido','prospecto_invalido','cliente_frio'].includes(sel?.etapa) && (
                <div style={{padding:'13px 26px',borderTop:`1px solid ${C.border}`,background:C.bg1,flexShrink:0}}>
              {!['cerrado_ganado','cerrado_perdido','prospecto_invalido','cliente_frio'].includes(sel?.etapa) && <div style={{marginTop:14}}>
                <div style={{fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',marginBottom:8}}>Mover a etapa</div>
                <select value="" onChange={e=>{ const v=e.target.value; e.target.value=''; moverEtapaUI(v); }}
                  style={{width:'100%',padding:'9px 11px',borderRadius:8,border:`1px solid ${C.border}`,background:C.bg2,color:C.t1,fontSize:13,cursor:'pointer'}}>
                  <option value="">Seleccionar etapa…</option>
                  {ETAPAS.filter(e=>e.id!==sel.etapa && (!['prospecto_invalido','cliente_frio'].includes(e.id) || ['prospecto','contactado'].includes(sel.etapa))).map(e=>(
                    <option key={e.id} value={e.id}>{e.label}</option>
                  ))}
                </select>
                <button onClick={()=>{setModalCampania({opp:sel,frio:false});setCadenciaCamp('mensual');setNotaCamp('');}}
                  style={{marginTop:8,width:'100%',background:'#22C55E15',color:'#16A34A',border:'1px solid #22C55E40',borderRadius:7,padding:'7px',fontSize:11,fontWeight:700,cursor:'pointer'}}>📅 Agregar a campaña (sin sacar del pipeline)</button>
              </div>}
                </div>
              )}
            </div>
            </div>
          )}
        </div>
      )}

      {/* CLIENTES */}

      {tab==='clientes'&&(
        <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
          {/* KPIs que FILTRAN (mockup aprobado): un clic y la tabla se acota */}
          <div style={{padding:'12px 16px 0'}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:8,marginBottom:10}}>
              {(() => {
                const nAct = clientes.filter(c=>c.estado==='activo').length;
                const nPro = clientes.filter(c=>c.estado==='prospecto').length;
                const nSin30 = clientes.filter(c=>{ const d=c.ultima_actividad?dias(c.ultima_actividad):9999; return d>=30 && c.estado==='activo'; }).length;
                const nSaldo = clientes.filter(c=>saldoDe(c)>0).length;
                const nSinV = clientes.filter(c=>!c.vendedor).length;
                const K = ({id,l,v,s2,color}) => (
                  <div onClick={()=>{setFiltroKpi(k=>k===id?'':id); if(id==='act'){setFiltroEst(filtroEst==='activo'?'todos':'activo');setFiltroKpi('');} if(id==='pro'){setFiltroEst(filtroEst==='prospecto'?'todos':'prospecto');setFiltroKpi('');}}}
                    style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:11,boxShadow:SH.xs,padding:'9px 13px',cursor:'pointer',
                      outline:(filtroKpi===id||(id==='act'&&filtroEst==='activo')||(id==='pro'&&filtroEst==='prospecto'))?`2px solid ${C.primary}`:'none',outlineOffset:1}}>
                    <div style={{fontSize:9,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4}}>{l}</div>
                    <div className="radar-num" style={{fontSize:19,fontWeight:800,letterSpacing:'-0.02em',color:color||C.t1}}>{v.toLocaleString('en-US')}</div>
                    <div style={{fontSize:9.5,color:C.t4}}>{s2}</div>
                  </div>
                );
                return (<>
                  <K id="act" l="Cartera activa" v={nAct} s2="compraron en 12 meses"/>
                  <K id="sin30" l="Sin contacto 30+ días" v={nSin30} s2="nadie los ha llamado" color={C.redText}/>
                  <K id="saldo" l="Con saldo vencido" v={nSaldo} s2="tienen facturas abiertas" color={C.orangeText}/>
                  <K id="pro" l="Prospectos" v={nPro} s2="aún no compran"/>
                  <K id="sinvend" l="Sin vendedor" v={nSinV} s2="cartera huérfana" color={C.orangeText}/>
                </>);
              })()}
            </div>
            {/* Los registros incompletos se atienden con un chip mas, junto a los
                demas filtros. La franja naranja que ocupaba el ancho de la
                pantalla pesaba mas que el problema que reportaba. */}
            <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:10}}>
              <input value={busq} onChange={e=>setBusq(e.target.value)} placeholder="🔍 Buscar por nombre, RTN, teléfono o código SAP…"
                style={{flex:1,minWidth:220,boxSizing:'border-box',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:'8px 12px',color:C.t1,fontSize:13,outline:'none',boxShadow:SH.xs}}/>
              {['A','B','C','D'].map(sg=>(
                <button key={sg} onClick={()=>setFiltroSeg(v=>v.includes(sg)?v.filter(x=>x!==sg):[...v,sg])}
                  style={{height:30,padding:'0 12px',borderRadius:999,fontSize:12,fontWeight:800,cursor:'pointer',
                    border:`1px solid ${filtroSeg.includes(sg)?C.primaryBorder:C.border}`,background:filtroSeg.includes(sg)?C.primaryTint:C.bg2,color:SEG_COLOR[sg]||C.t3}}>
                  {sg} · {clientes.filter(c=>c.segmento===sg).length}
                </button>
              ))}
              {/* 'libres' son los que quedaron sin vendedor porque el que los
                  tenia se fue. Antes no habia como verlos: seguian con el
                  nombre del que ya no trabaja aca, o sea con dueno, y no
                  aparecian en ninguna lista de pendientes. */}
              {(() => {
                const nLibres = clientes.filter(c=>!c.vendedor && c.vendedor_anterior).length;
                const nSinCont = clientes.filter(c=>!String(c.contacto||'').trim()).length;
                // Las bases de llamadas NO son un filtro sobre clientes: la mayoria
                // son leads de NEXA que todavia no son clientes. El chip
                // abre las bases de llamadas (ver BasesLlamadas).
                const ETIQ = {
                  todos: `Todos (${clientes.length})`,
                  libres: `Sin vendedor (${nLibres})`,
                  sincontacto: `Sin persona de contacto (${nSinCont})`,
                  cert: 'Listas de llamada',
                  inactivo: `Inactivos (${clientes.filter(c=>c.estado==='inactivo').length})`,
                };
                const nRotos = clientes.filter(esRoto).length;
                ETIQ.rotos = `Datos incompletos (${nRotos})`;
                return ['todos','libres','sincontacto','cert','rotos','inactivo']
                  .filter(e=>(e!=='libres'||nLibres>0) && (e!=='rotos'||nRotos>0)).map(e=>(
                  <button key={e} onClick={()=>{setFiltroEst(e);setFiltroKpi('');}}
                    style={{background:filtroEst===e?C.primaryTint:C.bg2,
                      color:filtroEst===e?C.blue:(['libres','sincontacto'].includes(e)?C.orangeText:C.t3),
                      border:`1px solid ${filtroEst===e?C.primaryBorder:C.border}`,
                      borderRadius:R.md,padding:'5px 11px',fontSize:11.5,fontWeight:600,cursor:'pointer'}}>
                    {ETIQ[e]}
                  </button>
                ));
              })()}
            </div>
          </div>

          {filtroEst==='cert' ? (
            <div style={{flex:1,overflowY:'auto',borderTop:`1px solid ${C.border}`,background:C.bg2}}>
              <BasesLlamadas usuario={usuario} />
            </div>
          ) : (
          <div style={{flex:1,display:'flex',overflow:'hidden'}}>
            <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden',borderTop:`1px solid ${C.border}`,background:C.bg2}}>
              <div style={{display:'grid',gridTemplateColumns:'2.2fr 1fr 52px 110px 100px 74px',gap:8,padding:'8px 16px',borderBottom:`1px solid ${C.border}`,background:C.bg2}}>
                {['Cliente','Vendedor','Seg.','Últ. contacto','Saldo','',].map((h,i)=>(
                  <span key={h+i} style={{fontSize:10,fontWeight:800,letterSpacing:'.06em',textTransform:'uppercase',color:C.t3,textAlign:i>=4?'right':'left'}}>{h}</span>
                ))}
              </div>
              <div style={{flex:1,overflowY:'auto'}}>
                {loading?<div style={{textAlign:'center',padding:40,color:C.t4}}>Cargando...</div>
                :filtradosCl.slice(0, techoCl).map(c=>{
                  const d = c.ultima_actividad ? dias(c.ultima_actividad) : null;
                  const saldo = saldoDe(c);
                  const venc = (salditos[c.sap_code]?.maxDias||0) > 0;
                  const waN = String(c.telefono||'').replace(/[^0-9]/g,'');
                  return (
                    <div key={c.id} onClick={()=>setSel(c)} className={sel?.id===c.id?undefined:'radar-hrow'}
                      style={{display:'grid',gridTemplateColumns:'2.2fr 1fr 52px 110px 100px 74px',gap:8,padding:'9px 16px',alignItems:'center',
                        borderBottom:`1px solid ${C.bg3}`,cursor:'pointer',background:sel?.id===c.id?C.primaryTint:'transparent',
                        boxShadow:sel?.id===c.id?`inset 3px 0 0 ${C.blue}`:'none'}}>
                      <span style={{minWidth:0}}>
                        <span style={{display:'block',fontSize:13,fontWeight:700,color:esRoto(c)?C.redText:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{String(c.empresa||'').trim()||'(sin nombre)'}</span>
                        <span className="radar-num" style={{display:'block',fontSize:10,color:C.t4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {[c.rtn, c.ciudad, c.sap_code?String(c.sap_code).replace(/^[a-z]+_/,''):null].filter(Boolean).join(' · ') || '—'}
                        </span>
                      </span>
                      <span style={{display:'flex',alignItems:'center',gap:6,minWidth:0}}>
                        {c.vendedor ? (<><AvatarChip name={c.vendedor}/><span style={{fontSize:11.5,fontWeight:600,color:C.t2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{c.vendedor.split(' ')[0]} {String(c.vendedor.split(' ')[1]||'').charAt(0)}.</span></>)
                          : (
                          <select value="" onClick={e=>e.stopPropagation()}
                            onChange={e=>{ if(e.target.value) asignarClienteRapido(c, e.target.value); }}
                            style={{fontSize:11,fontWeight:700,color:C.orangeText,background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:6,padding:'3px 5px',cursor:'pointer',outline:'none',maxWidth:'100%'}}>
                            <option value="">{c.vendedor_anterior ? '— tomar' : '— asignar'}</option>
                            {vendedoresList.map(v=><option key={v} value={v}>{v}</option>)}
                          </select>
                          )}
                        {/* No es lo mismo un cliente que nunca tuvo vendedor que
                            uno que quedo libre porque el suyo se fue. */}
                        {!c.vendedor && c.vendedor_anterior && (
                          <span title={`Era de ${c.vendedor_anterior}`}
                            style={{fontSize:9.5,color:C.t4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                            era de {c.vendedor_anterior.split(' ')[0]}
                          </span>
                        )}
                      </span>
                      <span>{c.segmento ? <span style={{fontSize:10.5,fontWeight:900,borderRadius:7,padding:'2px 8px',background:`${SEG_COLOR[c.segmento]||'#64748B'}18`,color:SEG_COLOR[c.segmento]||'#64748B'}}>{c.segmento}</span> : <span style={{color:C.t4,fontSize:11}}>—</span>}</span>
                      <span className="radar-num" style={{fontSize:11.5,fontWeight:700,display:'inline-flex',alignItems:'center',gap:6,color:d==null?C.t4:(d>30?C.redText:d>7?C.orangeText:C.greenText)}}>
                        <span style={{width:7,height:7,borderRadius:'50%',flexShrink:0,background:d==null?C.t4:(d>30?C.red:d>7?C.orange:C.green)}}/>
                        {d==null?'nunca':d===0?'hoy':`hace ${d} d`}
                      </span>
                      <span className="radar-num" style={{fontSize:12,fontWeight:700,textAlign:'right',color:saldo>0?(venc?C.redText:C.orangeText):C.t4}}>
                        {saldo>0?`$${Math.round(saldo).toLocaleString('en-US')}`:'—'}
                      </span>
                      <span style={{display:'flex',gap:5,justifyContent:'flex-end'}} onClick={e=>e.stopPropagation()}>
                        {waN.length>=8 && <a href={`https://wa.me/${waN.length===8?'504'+waN:waN}`} target="_blank" rel="noreferrer" title="WhatsApp"
                          style={{width:26,height:26,borderRadius:7,border:`1px solid ${C.border}`,background:C.bg2,display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:12,textDecoration:'none'}}>💬</a>}
                        {c.telefono && <a href={`tel:${String(c.telefono).replace(/[^0-9+]/g,'')}`} title="Llamar"
                          style={{width:26,height:26,borderRadius:7,border:`1px solid ${C.border}`,background:C.bg2,display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:12,textDecoration:'none'}}>📞</a>}
                      </span>
                    </div>
                  );
                })}
                {/* Al asomarse este div se pintan 150 mas. rootMargin 400px hace
                    que carguen antes de que el usuario llegue al fondo, asi que
                    no se ve el salto. */}
                {!loading && filtradosCl.length > techoCl && (
                  <div ref={finListaCl} style={{padding:'14px',textAlign:'center',fontSize:11.5,color:C.t4}}>
                    Cargando más… ({techoCl.toLocaleString('en-US')} de {filtradosCl.length.toLocaleString('en-US')})
                  </div>
                )}
              </div>
              <div style={{padding:'7px 16px',borderTop:`1px solid ${C.border}`,background:C.bg2,fontSize:11.5,color:C.t4}}>{filtradosCl.length.toLocaleString('en-US')} clientes</div>
            </div>

                    {sel&&tab==='clientes'?(
            <div style={{width:'clamp(360px,32vw,470px)',flexShrink:0,borderLeft:`1px solid ${C.border}`,overflowY:'auto',padding:20,background:C.bg2}}>
              {/* Header cliente */}
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16}}>
                <div style={{flex:1,minWidth:0}}>
                  <h2 style={{margin:'0 0 4px',fontSize:20,fontWeight:800,color:C.t1}}>{sel.empresa}</h2>
                  <div style={{fontSize:13,color:C.t3}}>{sel.contacto}{sel.cargo?` · ${sel.cargo}`:''}</div>
                  {sel.telefono&&(
                    <div style={{display:'flex',alignItems:'center',gap:8,marginTop:4}}>
                      <span style={{fontSize:13,color:C.t3}}>📞 {sel.telefono}</span>
                      <a href={`tel:${sel.telefono.replace(/[^0-9+]/g,'')}`}
                        style={{background:`${C.green}20`,color:C.green,border:`1px solid ${C.green}40`,borderRadius:6,padding:'2px 8px',fontSize:11,fontWeight:700,textDecoration:'none'}}>
                        Llamar
                      </a>
                      <a href={`https://wa.me/${sel.telefono.replace(/[^0-9]/g,'')}?text=Hola%20${encodeURIComponent(sel.contacto||sel.empresa)},%20le%20contacto%20de%20${encodeURIComponent(ORG.nombre)}`}
                        target="_blank" rel="noreferrer"
                        style={{background:'#25D36620',color:'#25D366',border:`1px solid #25D36640`,borderRadius:6,padding:'2px 8px',fontSize:11,fontWeight:700,textDecoration:'none'}}>
                        WhatsApp
                      </a>
                    </div>
                  )}
                  {sel.email&&<div style={{fontSize:13,color:C.t3,marginTop:2}}>✉️ {sel.email}</div>}
                </div>
                <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6,flexShrink:0,marginLeft:12}}>
                  <button onClick={()=>setSel(null)} title="Cerrar la ficha" aria-label="Cerrar la ficha"
                    style={{background:'none',border:'none',color:C.t4,fontSize:22,lineHeight:1,cursor:'pointer',padding:'0 2px',marginBottom:2}}>×</button>
                  <StageChip label={sel.estado||'—'} color={EC[sel.estado]||'#64748B'} fs={12}/>
                  <select value={sel.segmento||''} onChange={async e=>{
                    const v=e.target.value; await supabase.from('clientes').update({segmento:v||null}).eq('id',sel.id);
                    setSel(s=>({...s,segmento:v})); setClientes(cs=>cs.map(c=>c.id===sel.id?{...c,segmento:v}:c));
                  }} style={{background:sel.segmento?`${SEG_COLOR[sel.segmento]}20`:`${C.bg3}`,color:sel.segmento?SEG_COLOR[sel.segmento]:C.t4,border:`1px solid ${sel.segmento?SEG_COLOR[sel.segmento]+'40':C.border}`,borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:800,cursor:'pointer',outline:'none'}}>
                    <option value="">Segmento</option>
                    {ORG.segmentos.map(s=><option key={s.v} value={s.v} style={{background:C.bg2,color:C.t1}}>{s.v} — {s.l.split('(')[0].trim()}</option>)}
                  </select>
                  <select value={sel.iniciativa||''} onChange={async e=>{
                    const v=e.target.value; await supabase.from('clientes').update({iniciativa:v||null}).eq('id',sel.id);
                    setSel(s=>({...s,iniciativa:v})); setClientes(cs=>cs.map(c=>c.id===sel.id?{...c,iniciativa:v}:c));
                  }} style={{background:sel.iniciativa?`${C.indigo}20`:C.bg3,color:sel.iniciativa?C.indigo:C.t4,border:`1px solid ${sel.iniciativa?C.indigo+'40':C.border}`,borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:700,cursor:'pointer',outline:'none'}}>
                    <option value="">Iniciativa</option>
                    {ORG.iniciativas.map(i=><option key={i} value={i} style={{background:C.bg2,color:C.t1}}>{i}</option>)}
                  </select>
                  <Btn size="sm" variant="secondary" onClick={()=>{setFEditCl({...sel});setModalEditCl(true);}}>✏️ Editar</Btn>
                  {/* Cotizar desde la ficha del cliente. Estaba solo en el menu
                      Acciones de arriba, que no es donde uno lo busca teniendo
                      al cliente abierto enfrente. Va precargado con el cliente y
                      con la moneda de su pais. */}
                  <Btn size="sm" variant="secondary" onClick={()=>abrirCotSAP(sel)}>🧾 Cotizar</Btn>
                  <Btn size="sm" onClick={()=>{setVisitasPrefill(sel);setModalVisita(true);}}>📍 Nueva Visita</Btn>
                </div>
              </div>

              {/* Como viene este cliente. Va DESPUES del nombre: estos cuatro
                  numeros abrian la ficha y uno leia "$0 / $0 / 0x mes" sin saber
                  todavia de quien estaba hablando. */}
              {cliStats && (
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:16}}>
                  {[['Compras 12 meses',`$${Math.round(cliStats.compras12).toLocaleString('en-US')}`,C.t1],
                    ['Saldo abierto', saldoDe(sel)>0?`$${Math.round(saldoDe(sel)).toLocaleString('en-US')}`:'$0', saldoDe(sel)>0?C.orangeText:C.t1],
                    ['Ticket promedio',`$${Math.round(cliStats.ticket).toLocaleString('en-US')}`,C.t1],
                    ['Frecuencia',`${cliStats.freq}×/mes`,C.t1]].map(([l,v,cx])=>(
                    <div key={l} style={{border:`1px solid ${C.border}`,borderRadius:10,padding:'8px 12px'}}>
                      <div style={{fontSize:9,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4}}>{l}</div>
                      <div className="radar-num" style={{fontSize:16,fontWeight:800,color:cx,marginTop:1}}>{v}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Info grid */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:16}}>
                {[['Vendedor',sel.vendedor],['RTN',sel.rtn||selRtn],['País',sel.pais],['Industria',sel.industria],['Fuente',sel.fuente],['Forma pago',sel.forma_pago]].map(([l,v])=>(
                  <div key={l} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:12,boxShadow:SH.xs}}>
                    <div style={{fontSize:10.5,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em',marginBottom:3}}>{l}</div>
                    <div style={{fontSize:13,fontWeight:600,color:C.t1}}>{v||'—'}</div>
                  </div>
                ))}
              </div>

              {/* Oportunidades (todas: activas + cerradas) */}
              {(()=>{
                const nom=(sel.empresa||'').toLowerCase().trim();
                const oppsCli = opps.filter(o=> o.cliente_id===sel.id || ((o.cliente||'').toLowerCase().trim()===nom && nom));
                const abiertas = oppsCli.filter(o=>!['cerrado_ganado','cerrado_perdido','prospecto_invalido','cliente_frio','pagado'].includes(o.etapa));
                return oppsCli.length>0&&(
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',marginBottom:14,boxShadow:SH.sm}}>
                  <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.t1,letterSpacing:'-0.005em'}}>
                      Oportunidades <span className="radar-num" style={{background:C.bg3,color:C.t3,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,marginLeft:6}}>{oppsCli.length}</span>
                    </div>
                    <div className="radar-num" style={{fontSize:12,color:C.blue,fontWeight:700}}>
                      {fmt(abiertas.reduce((s,o)=>s+(o.valor||0),0))} activo
                    </div>
                  </div>
                  {oppsCli.map((o,i)=>{ const ECOL={prospecto:'#64748B',contactado:'#0D9488',cotizacion:'#F59E0B',negociacion:'#EC4899',cerrado:'#0B7AB8',facturado:'#8B5CF6',pagado:'#06B6D4',cerrado_ganado:'#10B981',cerrado_perdido:'#EF4444',prospecto_invalido:'#94A3B8',cliente_frio:'#0EA5E9'}; return (
                    <div key={o.id} onClick={()=>{setTab('pipeline');setSel(o);}} className="radar-hrow" style={{padding:'10px 16px',borderBottom:i<oppsCli.length-1?`1px solid ${C.bg3}`:'none',display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,cursor:'pointer'}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600,letterSpacing:'-0.005em',color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{o.titulo}</div>
                        <div style={{fontSize:11,color:C.t4,marginTop:2}}>{o.vendedor||'—'} · Cierre: {fecha(o.fecha_cierre)}</div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                        <span className="radar-num" style={{fontSize:12.5,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>{fmt(o.valor||0)}</span>
                        <StageChip label={o.etapa} color={ECOL[o.etapa]||'#64748B'} fs={10}/>
                      </div>
                    </div>
                  );})}
                </div>
              );})()}

              {/* Cuadre: facturado vs cobrado */}
              {(()=>{
                const usd0 = n => `$${(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
                const facturado = facturasCliente.reduce((s,f)=>s+(f.total_usd||0),0);
                const cobrado = cobrosCliente.reduce((s,c)=>s+(c.total_usd||0),0);
                const pend = facturasCliente.filter(f=>!f.pagado);
                const restante = pend.reduce((s,f)=>s+(f.saldo_usd!=null?f.saldo_usd:(f.total_usd||0)),0);
                const proxVence = pend.map(f=>f.doc_due_date).filter(Boolean).sort()[0];
                if (facturasCliente.length===0 && cobrosCliente.length===0) return null;
                const cuadra = restante <= 0.5;
                const col = cuadra ? C.greenText : C.redText;
                return (
                  <div style={{background:C.bg2,border:`1px solid ${cuadra?C.border:C.redBorder}`,borderRadius:12,padding:'12px 16px',marginBottom:14,boxShadow:SH.xs}}>
                    <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                      <div style={{flex:1,minWidth:90}}><div style={{fontSize:10.5,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>Facturado</div><div className="radar-num" style={{fontSize:16,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>{usd0(facturado)}</div></div>
                      <div style={{flex:1,minWidth:90}}><div style={{fontSize:10.5,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>Cobrado</div><div className="radar-num" style={{fontSize:16,fontWeight:700,letterSpacing:'-0.015em',color:C.greenText}}>{usd0(cobrado)}</div></div>
                      <div style={{flex:1,minWidth:90}}><div style={{fontSize:10.5,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>{cuadra?'Estado':'Restante'}</div><div className="radar-num" style={{fontSize:16,fontWeight:700,letterSpacing:'-0.015em',color:col}}>{cuadra?'✓ Al día':usd0(restante)}</div></div>
                    </div>
                    {!cuadra && <div style={{marginTop:8,fontSize:12,fontWeight:600,color:C.redText,background:C.redTint,border:`1px solid ${C.redBorder}`,borderRadius:8,padding:'7px 10px'}}>
                      ⚠️ Restante por pagar {usd0(restante)} · {pend.length} factura(s) pendiente(s){proxVence?` · vence ${fecha(proxVence)}`:''}
                    </div>}
                  </div>
                );
              })()}

              {/* Facturas SAP */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',marginBottom:14,boxShadow:SH.sm}}>
                <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.t1,letterSpacing:'-0.005em'}}>
                    Facturas SAP {facturasCliente.length>0&&<span className="radar-num" style={{background:C.bg3,color:C.t3,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,marginLeft:6}}>{facturasCliente.length}</span>}
                  </div>
                  {facturasCliente.length>0&&<div className="radar-num" style={{fontSize:12,color:C.t3,fontWeight:600}}>${facturasCliente.reduce((s,f)=>s+(f.total_usd||0),0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} total</div>}
                </div>
                {loadingFacturas?(
                  <div style={{padding:16,textAlign:'center',color:C.t4,fontSize:12}}>Cargando...</div>
                ):facturasCliente.length===0?(
                  <div style={{padding:16,textAlign:'center',color:C.t4,fontSize:12}}>Sin facturas en SAP para este cliente</div>
                ):(
                  facturasCliente.map((f,i)=>{ const abierta = facAbierta===f.doc_num; const det = facDet[f.doc_num]; return (
                    <div key={f.doc_num} style={{borderBottom:i<facturasCliente.length-1?`1px solid ${C.bg3}`:'none'}}>
                      <div onClick={()=>abrirFacturaDet(f)} className="radar-hrow" style={{padding:'10px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,cursor:'pointer'}}>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:C.t1,display:'flex',alignItems:'center',gap:6}}>
                            <span style={{color:C.t4,fontSize:10,transform:abierta?'rotate(90deg)':'none',transition:'transform .15s',display:'inline-block'}}>▶</span>
                            Factura #{f.doc_num}
                          </div>
                          <div style={{fontSize:11,color:C.t4,marginTop:1}}>Fecha: {fecha(f.doc_date)} · Vence: {fecha(f.doc_due_date)}</div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                          <span className="radar-num" style={{fontSize:13,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>${(f.total_usd||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                          <span style={{background:f.pagado?C.greenTint:C.orangeTint,color:f.pagado?C.greenText:C.orangeText,border:`1px solid ${f.pagado?C.greenBorder:C.orangeBorder}`,borderRadius:R.pill,padding:'2px 8px',fontSize:10,fontWeight:600}}>{f.pagado?'✓ Pagada':'⏳ Pendiente'}</span>
                        </div>
                      </div>
                      {abierta && (
                        <div style={{padding:'0 16px 12px',background:C.bg1}}>
                          {facCargando===f.doc_num && <div style={{fontSize:11.5,color:C.t3,padding:'8px 0'}}>Leyendo la factura en SAP…</div>}
                          {det?.error && <div style={{fontSize:11.5,color:C.redText,padding:'8px 0'}}>⚠ {det.error}</div>}
                          {det?.lines?.length>0 && (
                            <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden',marginTop:4}}>
                              <div style={{display:'grid',gridTemplateColumns:'1fr 52px 84px 92px',gap:6,padding:'6px 10px',background:C.bg3,fontSize:9.5,fontWeight:700,color:C.t4,textTransform:'uppercase'}}>
                                <span>Descripción</span><span style={{textAlign:'right'}}>Cant.</span><span style={{textAlign:'right'}}>Precio</span><span style={{textAlign:'right'}}>Total</span>
                              </div>
                              {det.lines.map((l,j)=>(
                                <div key={j} style={{display:'grid',gridTemplateColumns:'1fr 52px 84px 92px',gap:6,padding:'6px 10px',borderTop:`1px solid ${C.bg3}`,fontSize:11.5}}>
                                  <span style={{color:C.t1,overflow:'hidden',textOverflow:'ellipsis'}} title={l.ItemDescription||l.ItemCode||''}>{l.ItemDescription||l.ItemCode||'—'}</span>
                                  <span className="radar-num" style={{textAlign:'right',color:C.t2}}>{l.Quantity}</span>
                                  <span className="radar-num" style={{textAlign:'right',color:C.t2}}>{(l.unitPrice||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                                  <span className="radar-num" style={{textAlign:'right',fontWeight:700,color:C.t1}}>{(l.lineTotal||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                                </div>
                              ))}
                              <div style={{display:'flex',justifyContent:'space-between',padding:'7px 10px',borderTop:`1px solid ${C.border}`,background:C.primaryTint,fontSize:12}}>
                                <span style={{fontWeight:700,color:C.t2}}>Total {det.moneda||''}</span>
                                <span className="radar-num" style={{fontWeight:800,color:C.blue}}>{(det.docTotal||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                              </div>
                            </div>
                          )}
                          {det?.doc?.Comments && <div style={{fontSize:11,color:C.t3,fontStyle:'italic',marginTop:6}}>💬 {det.doc.Comments}</div>}
                          {det?.pdf?.base64 && (
                            <div style={{display:'flex',gap:7,marginTop:9}}>
                              <button onClick={e=>{e.stopPropagation();pdfFactura(f.doc_num,false);}} className="radar-btn" style={{fontSize:11.5,fontWeight:600,color:C.blue,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:R.md,padding:'6px 12px',cursor:'pointer'}}>Ver PDF</button>
                              <button onClick={e=>{e.stopPropagation();pdfFactura(f.doc_num,true);}} className="radar-btn" style={{fontSize:11.5,fontWeight:600,color:C.t2,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'6px 12px',cursor:'pointer'}}>Descargar</button>
                            </div>
                          )}
                          {det && !det.error && !det.pdf?.base64 && det.lines && <div style={{fontSize:10.5,color:C.t4,marginTop:6}}>El PDF no se pudo generar{det.pdf?.error?`: ${det.pdf.error}`:''}.</div>}
                        </div>
                      )}
                    </div>
                  );})
                )}
              </div>

              {/* Pagos recibidos (cobros SAP) */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',marginBottom:14,boxShadow:SH.sm}}>
                <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.t1,letterSpacing:'-0.005em'}}>
                    Pagos recibidos {cobrosCliente.length>0&&<span className="radar-num" style={{background:C.bg3,color:C.t3,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,marginLeft:6}}>{cobrosCliente.length}</span>}
                  </div>
                  {cobrosCliente.length>0&&<div className="radar-num" style={{fontSize:12,color:C.greenText,fontWeight:700}}>${cobrosCliente.reduce((s,c)=>s+(c.total_usd||0),0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</div>}
                </div>
                {loadingFacturas?(
                  <div style={{padding:16,textAlign:'center',color:C.t4,fontSize:12}}>Cargando...</div>
                ):cobrosCliente.length===0?(
                  <div style={{padding:16,textAlign:'center',color:C.t4,fontSize:12}}>Sin pagos registrados en SAP para este cliente</div>
                ):(
                  cobrosCliente.map((c,i)=>(
                    <div key={(c.doc_num||'')+i} className="radar-hrow" style={{padding:'10px 16px',borderBottom:i<cobrosCliente.length-1?`1px solid ${C.bg3}`:'none',display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
                      <div>
                        <div style={{fontSize:12,fontWeight:600,color:C.t1}}>Pago #{c.doc_num||'—'}</div>
                        <div style={{fontSize:11,color:C.t4,marginTop:1}}>{fecha(c.doc_date)}{c.tipo_pago?` · ${c.tipo_pago}`:''}</div>
                      </div>
                      <span className="radar-num" style={{fontSize:13,fontWeight:700,letterSpacing:'-0.01em',color:C.greenText}}>${(c.total_usd||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                    </div>
                  ))
                )}
              </div>

              {/* COTIZACIONES. Lo que el cliente PIDIO, que no es lo mismo que lo
                  que compro. Sale de cotizaciones_sap, no de SAP en vivo, asi
                  que alcanza todo el historico y abre al instante. */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',marginBottom:14,boxShadow:SH.sm}}>
                <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.t1,letterSpacing:'-0.005em'}}>
                    Cotizaciones {cotsCliente.length>0&&<span className="radar-num" style={{background:C.bg3,color:C.t3,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,marginLeft:6}}>{cotsCliente.length}</span>}
                  </div>
                  {cotsCliente.length>0&&<div className="radar-num" style={{fontSize:12,color:C.t3,fontWeight:600}}>${cotsCliente.reduce((a,c)=>a+(c.totalUsd||0),0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} total</div>}
                </div>
                {loadingFacturas?(
                  <div style={{padding:16,textAlign:'center',color:C.t4,fontSize:12}}>Cargando...</div>
                ):cotsCliente.length===0?(
                  <div style={{padding:16,textAlign:'center',color:C.t4,fontSize:12}}>Nunca se le ha cotizado nada en SAP</div>
                ):(
                  cotsCliente.map((c,i)=>{
                    const llave = `${c.empresa_id}:${c.docNum}`;
                    const abierta = cotAbiertaCl === llave;
                    const det = cotLineasCl[llave];
                    return (
                    <div key={llave+i} style={{borderBottom:i<cotsCliente.length-1?`1px solid ${C.bg3}`:'none'}}>
                      <div className="radar-hrow" onClick={()=>verLineasCliente(c)}
                        style={{padding:'10px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,cursor:'pointer'}}>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:600,color:C.t1}}>
                            <span style={{color:C.t4,fontSize:10,marginRight:4}}>{abierta?'\u25bc':'\u25b6'}</span>
                            Cotización #{c.docNum}
                            <span style={{marginLeft:6,fontSize:10,fontWeight:800,borderRadius:5,padding:'1px 6px',
                              background: c.estado==='abierta'?`${C.green}18`:C.bg3,
                              color: c.estado==='abierta'?C.greenText:C.t4}}>{c.estado}</span>
                          </div>
                          <div style={{fontSize:11,color:C.t4,marginTop:1}}>
                            {fecha(c.fecha)}{c.vence?` · vence ${fecha(c.vence)}`:''}
                            {c.empresa_id!==sel.empresa_id?` · ${nombreEmpresa(c.empresa_id)}`:''}
                          </div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                          <span className="radar-num" style={{fontSize:13,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,whiteSpace:'nowrap'}}>
                            ${(c.totalUsd||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                          </span>
                          <button onClick={e=>{e.stopPropagation(); bajarPdfCot(c);}} disabled={bajandoCot===c.docNum}
                            title="Bajar el PDF de SAP"
                            style={{border:`1px solid ${C.border}`,background:C.bg1,color:C.t3,borderRadius:6,
                              padding:'3px 8px',fontSize:10.5,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
                            {bajandoCot===c.docNum?'…':'PDF'}
                          </button>
                        </div>
                      </div>
                      {abierta && (
                        <div style={{padding:'0 16px 12px',background:C.bg1}}>
                          {!det?(<div style={{fontSize:11,color:C.t4,padding:'8px 0'}}>Cargando líneas...</div>)
                          :det.error?(<div style={{fontSize:11,color:C.redText,padding:'8px 0'}}>{det.error}</div>)
                          :det.lineas.length===0?(<div style={{fontSize:11,color:C.t4,padding:'8px 0'}}>Sin líneas registradas.</div>)
                          :det.lineas.map((l,j)=>(
                            <div key={j} style={{display:'flex',justifyContent:'space-between',gap:10,padding:'5px 0',borderBottom:j<det.lineas.length-1?`1px solid ${C.bg3}`:'none'}}>
                              <div style={{minWidth:0,fontSize:11,color:C.t2}}>
                                <b className="radar-num" style={{color:C.t1}}>{Number(l.cantidad||0)}×</b> {l.descripcion}
                                {l.item_code&&<span style={{color:C.t4}}> · {l.item_code}</span>}
                              </div>
                              <span className="radar-num" style={{fontSize:11,fontWeight:700,color:C.t2,whiteSpace:'nowrap'}}>
                                {c.moneda} {Number(l.total_linea||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })
                )}
              </div>

              {/* Historial de visitas y llamadas */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',marginBottom:14,boxShadow:SH.sm}}>
                <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.t1,letterSpacing:'-0.005em'}}>
                    Historial de contacto
                    {visitasCliente.length>0&&<span className="radar-num" style={{background:C.bg3,color:C.t3,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,marginLeft:8}}>{visitasCliente.length}</span>}
                  </div>
                </div>
                {loadingVisitas?(
                  <div style={{padding:20,textAlign:'center',color:C.t4,fontSize:12}}>Cargando...</div>
                ):visitasCliente.length===0?(
                  <div style={{padding:24,textAlign:'center',color:C.t4}}>
                    <div style={{fontSize:28,marginBottom:6}}>📭</div>
                    <div style={{fontSize:13}}>Sin visitas ni llamadas registradas</div>
                    <div style={{fontSize:11,marginTop:4}}>Usá el botón "Nueva Visita" para registrar el primer contacto</div>
                  </div>
                ):(
                  visitasCliente.map((v,i)=>{
                    const ECOLOR2 = {'Interesado':C.green,'Cotización enviada':C.orange,'Contrato activo':C.blue,'Sin interés':C.red,'No estaba':C.t4,'No contestó':C.t4,'Buzón de voz':C.t4};
                    const eColor  = ECOLOR2[v.estado_visita]||C.t4;
                    const fechaV  = new Date(v.created_at).toLocaleDateString('es-HN',{day:'2-digit',month:'short',year:'numeric'});
                    const horaV   = new Date(v.created_at).toLocaleTimeString('es-HN',{hour:'2-digit',minute:'2-digit'});
                    return (
                      <div key={v.id} style={{padding:'12px 16px',borderBottom:i<visitasCliente.length-1?`1px solid ${C.border}`:'none',display:'flex',gap:12}}>
                        {/* Icono tipo */}
                        <div style={{width:36,height:36,borderRadius:'50%',background:v.tipo==='visita'?`${C.blue}20`:`${C.green}20`,
                          display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0,marginTop:2}}>
                          {v.tipo==='visita'?'📍':'📞'}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:4,gap:8}}>
                            <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                              <span style={{fontSize:12,fontWeight:700,color:C.t1,textTransform:'capitalize'}}>{v.tipo}</span>
                              <StageChip label={v.estado_visita} color={eColor} fs={10}/>
                              {v.valor_estimado>0&&<span className="radar-num" style={{fontSize:11,fontWeight:700,color:C.blue}}>{fmt(v.valor_estimado)}</span>}
                            </div>
                            <div style={{fontSize:10,color:C.t4,flexShrink:0,textAlign:'right'}}>
                              <div>{fechaV}</div><div>{horaV}</div>
                            </div>
                          </div>
                          {v.equipo_interes&&<div style={{fontSize:11,color:C.t3,marginBottom:3}}>🔧 {v.equipo_interes}</div>}
                          <div style={{fontSize:12,color:C.t2,lineHeight:1.5,marginBottom:v.proxima_accion?4:0}}>{v.observaciones}</div>
                          {v.proxima_accion&&<div style={{fontSize:11,color:C.orange,fontWeight:600}}>→ {v.proxima_accion}</div>}
                          <div style={{display:'flex',gap:10,marginTop:4,alignItems:'center'}}>
                            <span style={{fontSize:10,color:C.t4}}>👤 {v.vendedor}</span>
                            {v.maps_url&&<a href={v.maps_url} target="_blank" rel="noreferrer"
                              style={{fontSize:10,color:C.blue,textDecoration:'none',fontWeight:600}}>📍 Ver mapa</a>}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Nota rápida */}
              {sel.notas&&<div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:14}}>
                <div style={{fontSize:10,color:C.t4,textTransform:'uppercase',fontWeight:700,marginBottom:6}}>Última nota manual</div>
                <div style={{fontSize:13,color:C.t2,lineHeight:1.5}}>{sel.notas}</div>
              </div>}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
                <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',marginBottom:8}}>Nota rápida</div>
                <Inp value={nota} onChange={setNota} placeholder="Agregar nota manual..." rows={2}/>
                <Btn size="sm" onClick={registrarNota} disabled={!nota.trim()||savingNota}>{savingNota?'Guardando...':'💬 Guardar nota'}</Btn>
              </div>
            </div>
          ):null}
          </div>
          )}
        </div>
      )}

      {/* ── SIN ATENDER (ex módulo Clientes Inactivos) — el componente trae su propio scroll ── */}
      {tab==='inactivos' && conInactivos && <ClientesInactivos usuario={usuario} empresaActiva={empresaActiva}/>}
      {/* ── ELEVADORAS (ex módulo standalone) — tipo documento: necesita scroll del contenedor ── */}

      {/* Modal editar cliente */}
      <Modal open={modalEditCl} onClose={()=>setModalEditCl(false)} title="Editar Cliente" width={560}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <Inp label="Empresa *" value={fEditCl.empresa||''} onChange={v=>setFEditCl(x=>({...x,empresa:v}))} required/>
          <Inp label="RTN" value={fEditCl.rtn||''} onChange={v=>setFEditCl(x=>({...x,rtn:v}))} placeholder="0000-0000-000000"/>
          <Inp label="Contacto principal" value={fEditCl.contacto||''} onChange={v=>setFEditCl(x=>({...x,contacto:v}))}/>
          <Inp label="Cargo" value={fEditCl.cargo||''} onChange={v=>setFEditCl(x=>({...x,cargo:v}))}/>
          <Inp label="Teléfono" value={fEditCl.telefono||''} onChange={v=>setFEditCl(x=>({...x,telefono:v}))} placeholder="+504 0000-0000"/>
          <Inp label="Email" type="email" value={fEditCl.email||''} onChange={v=>setFEditCl(x=>({...x,email:v}))}/>
          <Inp label="Ciudad" value={fEditCl.ciudad||''} onChange={v=>setFEditCl(x=>({...x,ciudad:v}))}/>
          <Inp label="País" value={fEditCl.pais||''} onChange={v=>setFEditCl(x=>({...x,pais:v}))}/>
          <Inp label="Industria" value={fEditCl.industria||''} onChange={v=>setFEditCl(x=>({...x,industria:v}))}/>
          <Inp label="Forma de pago" value={fEditCl.forma_pago||''} onChange={v=>setFEditCl(x=>({...x,forma_pago:v}))}/>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Vendedor asignado</div>
            <select value={fEditCl.vendedor||''} onChange={e=>setFEditCl(x=>({...x,vendedor:e.target.value}))}
              style={{width:'100%',background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',color:C.t1,fontSize:13,outline:'none',boxSizing:'border-box'}}>
              <option value="">— Sin asignar —</option>
              {vendedoresList.map(v=><option key={v} value={v} style={{background:C.bg2}}>{v}</option>)}
            </select>
          </div>
          <Sel label="Estado" value={fEditCl.estado||'activo'} onChange={v=>setFEditCl(x=>({...x,estado:v}))} options={[{v:'activo',l:'Activo'},{v:'prospecto',l:'Prospecto'},{v:'inactivo',l:'Inactivo'}]}/>
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:12}}>
          <Btn variant="secondary" onClick={()=>setModalEditCl(false)}>Cancelar</Btn>
          <Btn onClick={guardarEditCliente} disabled={!fEditCl.empresa||savingEditCl}>{savingEditCl?'Guardando...':'💾 Guardar cambios'}</Btn>
        </div>
      </Modal>

      <ModalVisita open={modalVisita} onClose={()=>{setModalVisita(false);setVisitasPrefill(null);if(sel&&tab==='clientes')cargarVisitas(sel);}} usuario={usuario} clientes={clientes} prefill={visitasPrefill} empresaActiva={empresaActiva}/>

      {/* Modal: cobro no registrado en SAP */}
      <Modal open={!!modalPagadoSAP} onClose={()=>setModalPagadoSAP(null)} title="🔒 Cobro no registrado en SAP" width={480}>
        {modalPagadoSAP&&(
          <div>
            {modalPagadoSAP.tipo==='factura_pendiente'&&(
              <>
                <div style={{background:`${C.orange}15`,border:`1px solid ${C.orange}30`,borderRadius:10,padding:'14px 16px',marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.orange,marginBottom:6}}>⏳ Factura SAP #{modalPagadoSAP.opp?.factura_sap_num} — Sin cobro registrado</div>
                  <div style={{fontSize:13,color:C.t2,lineHeight:1.6}}>
                    Esta factura todavía no aparece como cobrada en SAP B1. <strong>Susana</strong> debe ir al perfil del cliente en SAP y registrar el pago contra esta factura.
                  </div>
                </div>
                <div style={{fontSize:12,color:C.t3,lineHeight:1.7,marginBottom:16}}>
                  {modalPagadoSAP.accion==='ganado'
                    ? <>El cobro debe estar registrado <strong>completo</strong> en SAP (no anticipo) para poder <strong style={{color:'#10B981'}}>Cerrar Ganado</strong>. Una vez que Susana registre el pago total, el sistema lo detectará en el próximo sync.</>
                    : <>Una vez que Susana registre el cobro en SAP, el sistema lo detectará automáticamente en el próximo sync y moverá la oportunidad a <strong style={{color:'#06B6D4'}}>Pagado</strong>.</>}
                </div>
              </>
            )}
            {modalPagadoSAP.tipo==='sin_factura'&&(
              <>
                <div style={{background:`${C.blue}15`,border:`1px solid ${C.blue}30`,borderRadius:10,padding:'14px 16px',marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.blue,marginBottom:6}}>📄 Sin factura SAP vinculada</div>
                  <div style={{fontSize:13,color:C.t2,lineHeight:1.6}}>
                    Esta oportunidad no tiene una factura SAP vinculada. Si el cliente ya pagó, Susana debe <strong>registrar el cobro en SAP</strong> (IncomingPayments) a nombre del cliente.
                  </div>
                </div>
                <div style={{fontSize:12,color:C.t3,lineHeight:1.7,marginBottom:16}}>
                  El sistema detectará el pago automáticamente en el próximo sync y actualizará el estado a <strong style={{color:'#06B6D4'}}>Pagado</strong>.
                </div>
              </>
            )}
            <div style={{background:C.bg3,borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12,color:C.t4}}>
              <strong style={{color:C.t2}}>Flujo correcto:</strong> Susana registra cobro en SAP → Sync automático RADAR → Estado se actualiza a Pagado
            </div>
            {['admin','gerente_ventas'].includes(usuario?.rol)&&(
              <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14,marginTop:4}}>
                <div style={{fontSize:11,color:C.t4,marginBottom:8}}>Solo para administradores:</div>
                <Btn variant="secondary" size="sm" onClick={()=>{
                  if (modalPagadoSAP.accion==='ganado') { const o=modalPagadoSAP.opp; setModalPagadoSAP(null); setModalGanado(o); setFGanado({valor:o?.valor||'',notas:''}); }
                  else { moveOpp(modalPagadoSAP.opp.id,'pagado'); setModalPagadoSAP(null); }
                }}>
                  {modalPagadoSAP.accion==='ganado'?'Forzar Cerrar Ganado sin confirmación SAP':'Forzar Pagado sin confirmación SAP'}
                </Btn>
              </div>
            )}
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}>
              <Btn onClick={()=>setModalPagadoSAP(null)}>Entendido</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal eliminar oportunidad */}
      <Modal open={!!confirmEliminarOpp} onClose={()=>setConfirmEliminarOpp(null)} title="Eliminar Oportunidad" width={400}>
        <div style={{fontSize:14,color:C.t2,marginBottom:20,lineHeight:1.6}}>
          ¿Confirmás que querés <strong style={{color:C.red}}>eliminar permanentemente</strong> la oportunidad<br/>
          <strong style={{color:C.t1}}>"{confirmEliminarOpp?.titulo}"</strong>?<br/>
          Esta acción no se puede deshacer.
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" onClick={()=>setConfirmEliminarOpp(null)}>Cancelar</Btn>
          <Btn variant="danger" onClick={()=>eliminarOpp(confirmEliminarOpp)}>Sí, eliminar</Btn>
        </div>
      </Modal>

      {/* Modal cerrar como Perdido — motivo requerido */}
      <Modal open={!!modalPerdido} onClose={()=>setModalPerdido(null)} title="✗ Cerrar como Perdido" width={440}>
        {modalPerdido&&<>
          <p style={{fontSize:13,color:C.t3,marginTop:0,marginBottom:16}}>{modalPerdido.titulo}</p>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',marginBottom:6}}>Motivo de pérdida *</div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {[
                'Precio muy alto',
                'Falta de inventario / disponibilidad',
                'Se fue con la competencia',
                'Falta de seguimiento',
                'Presupuesto insuficiente del cliente',
                'Cliente postergó la decisión',
                'Necesidades no alineadas',
                'Proyecto cancelado',
                'Otro',
              ].map(m=>(
                <label key={m} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderRadius:8,border:`1px solid ${motivoPerdido===m?'#EF4444':'transparent'}`,background:motivoPerdido===m?'#EF444415':C.bg3,cursor:'pointer',transition:'all .12s'}}
                  onClick={()=>setMotivoPerdido(m)}>
                  <div style={{width:16,height:16,borderRadius:'50%',border:`2px solid ${motivoPerdido===m?'#EF4444':C.border}`,background:motivoPerdido===m?'#EF4444':'transparent',flexShrink:0,transition:'all .12s'}}/>
                  <span style={{fontSize:13,color:motivoPerdido===m?C.t1:C.t3,fontWeight:motivoPerdido===m?700:400}}>{m}</span>
                </label>
              ))}
            </div>
          </div>
          <Inp label="Notas adicionales (opcional)" value={notasPerdido} onChange={setNotasPerdido} rows={2} placeholder="Detalles, qué dijo el cliente..."/>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:12}}>
            <Btn variant="secondary" onClick={()=>setModalPerdido(null)}>Cancelar</Btn>
            <Btn variant="danger" disabled={!motivoPerdido||savingPerdido} onClick={async()=>{
              if(!modalPerdido||!motivoPerdido) return;
              setSavingPerdido(true);
              const hoy=new Date().toISOString().split('T')[0];
              const notaFinal=`[MOTIVO: ${motivoPerdido}]${notasPerdido?' — '+notasPerdido:''}`;
              await supabase.from('oportunidades').update({etapa:'cerrado_perdido',fecha_cierre:hoy,notas:notaFinal}).eq('id',modalPerdido.id);
              setOpps(os=>os.map(o=>o.id===modalPerdido.id?{...o,etapa:'cerrado_perdido',fecha_cierre:hoy,notas:notaFinal}:o));
              setSel(prev=>prev?.id===modalPerdido.id?{...prev,etapa:'cerrado_perdido',fecha_cierre:hoy,notas:notaFinal}:prev);
              setSavingPerdido(false); setModalPerdido(null);
            }}>{savingPerdido?'Guardando...':'✗ Confirmar pérdida'}</Btn>
          </div>
        </>}
      </Modal>

      {/* Modal Prospecto Inválido — solo desde prospecto/contactado, requiere comentario */}
      <Modal open={!!modalInvalido} onClose={()=>setModalInvalido(null)} title="⊘ Marcar como Prospecto Inválido" width={440}>
        {modalInvalido&&<>
          <p style={{fontSize:13,color:C.t3,marginTop:0,marginBottom:8}}>{modalInvalido.titulo}</p>
          <div style={{background:'#94A3B815',border:'1px solid #94A3B840',borderRadius:8,padding:'8px 12px',marginBottom:14,fontSize:12,color:C.t3,lineHeight:1.5}}>
            Se sale del pipeline activo (no cuenta como perdido ni como venta). Útil para datos errados, duplicados o contactos que nunca fueron una oportunidad real.
          </div>
          <Inp label="Motivo / comentario *" value={notaInvalido} onChange={setNotaInvalido} rows={3} placeholder="¿Por qué es inválido? (dato errado, duplicado, no aplica...)"/>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:12}}>
            <Btn variant="secondary" onClick={()=>setModalInvalido(null)}>Cancelar</Btn>
            <Btn disabled={!notaInvalido.trim()||savingInvalido} onClick={async()=>{
              if(!modalInvalido||!notaInvalido.trim()) return;
              setSavingInvalido(true);
              const hoy=new Date().toISOString().split('T')[0];
              const notaFinal=`[PROSPECTO INVÁLIDO] ${notaInvalido.trim()}`;
              const upd={etapa:'prospecto_invalido',fecha_cierre:hoy,ultimo_seguimiento:hoy,notas:notaFinal};
              await supabase.from('oportunidades').update(upd).eq('id',modalInvalido.id);
              setOpps(os=>os.map(o=>o.id===modalInvalido.id?{...o,...upd}:o));
              setSel(prev=>prev?.id===modalInvalido.id?{...prev,...upd}:prev);
              setSavingInvalido(false); setModalInvalido(null);
            }}>{savingInvalido?'Guardando...':'⊘ Confirmar inválido'}</Btn>
          </div>
        </>}
      </Modal>

      {/* Modal Campaña WhatsApp — inscribir en nurturing con cadencia */}
      <Modal open={!!modalCampania} onClose={()=>setModalCampania(null)} title={modalCampania?.frio?'❄️ Enfriar y agregar a campaña':'📅 Agregar a campaña de WhatsApp'} width={440}>
        {modalCampania&&<>
          <p style={{fontSize:13,color:C.t3,marginTop:0,marginBottom:10}}>{modalCampania.opp?.cliente||modalCampania.opp?.titulo}</p>
          {modalCampania.frio&&(
            <div style={{background:'#0EA5E915',border:'1px solid #0EA5E940',borderRadius:8,padding:'8px 12px',marginBottom:14,fontSize:12,color:C.t3,lineHeight:1.5}}>
              Se saca del pipeline activo (queda en <b>Cliente Frío</b>) y entra a la campaña. Deja de aparecer en seguimiento diario.
            </div>
          )}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',marginBottom:6}}>¿Cada cuánto recibe la campaña? *</div>
            <div style={{display:'flex',gap:8}}>
              {[['semanal','Semanal'],['quincenal','Quincenal'],['mensual','Mensual']].map(([v,l])=>(
                <button key={v} onClick={()=>setCadenciaCamp(v)}
                  style={{flex:1,padding:'8px 0',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',
                    border:`1px solid ${cadenciaCamp===v?'#0EA5E9':C.border}`,background:cadenciaCamp===v?'#0EA5E915':C.bg3,color:cadenciaCamp===v?'#0EA5E9':C.t3}}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <Inp label="Nota (opcional)" value={notaCamp} onChange={setNotaCamp} rows={2} placeholder="Contexto: qué le interesa, cuándo retomar..."/>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:12}}>
            <Btn variant="secondary" onClick={()=>setModalCampania(null)}>Cancelar</Btn>
            <Btn disabled={savingCamp} onClick={confirmarCampania}>{savingCamp?'Guardando...':(modalCampania.frio?'❄️ Enfriar e inscribir':'📅 Inscribir')}</Btn>
          </div>
        </>}
      </Modal>

      {/* Modal cambio de etapa simple — exige comentario (cuenta como seguimiento) */}
      <Modal open={!!modalMover} onClose={()=>setModalMover(null)} title="Cambiar de etapa" width={420}>
        {modalMover&&<>
          <p style={{fontSize:13,color:C.t3,marginTop:0,marginBottom:4}}>{modalMover.opp?.titulo}</p>
          <div style={{fontSize:13,color:C.t2,marginBottom:14}}>
            Mover a <strong style={{color:ETAPAS.find(e=>e.id===modalMover.etapa)?.color||C.t1}}>{ETAPAS.find(e=>e.id===modalMover.etapa)?.label||modalMover.etapa}</strong>
          </div>
          <Inp label="¿Qué pasó? (obligatorio) *" value={notaMover} onChange={setNotaMover} rows={3} placeholder="Resumí el avance: qué hablaste, próximo paso…"/>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:12}}>
            <Btn variant="secondary" onClick={()=>setModalMover(null)}>Cancelar</Btn>
            <Btn disabled={!notaMover.trim()||savingMover} onClick={async()=>{
              if(!notaMover.trim()||!modalMover) return; setSavingMover(true);
              await moveOpp(modalMover.opp.id, modalMover.etapa, null, notaMover.trim());
              setSavingMover(false); setModalMover(null); setNotaMover('');
            }}>{savingMover?'Guardando...':'Mover etapa'}</Btn>
          </div>
        </>}
      </Modal>

      {/* Modal nuevo cliente */}
      {/* Modal registrar cierre → Cerrado Ganado (requiere factura SAP) */}
      <Modal open={!!modalGanado} onClose={()=>setModalGanado(null)} title="✅ Cerrar Ganado" width={440}>
        <p style={{fontSize:13,color:C.t3,marginTop:0,marginBottom:16}}>{modalGanado?.titulo}</p>
        {modalGanado?.factura_sap_num&&(
          <div style={{background:'#10B98115',border:'1px solid #10B98140',borderRadius:8,padding:'8px 12px',marginBottom:12,fontSize:12,color:C.green,fontWeight:700}}>
            🧾 Factura SAP #{modalGanado.factura_sap_num} — El cobro se sincroniza automáticamente desde SAP
          </div>
        )}
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <Inp label="Valor final *" type="number" value={fGanado.valor} onChange={v=>setFGanado(x=>({...x,valor:v}))} placeholder="0.00"/>
          <Inp label="Notas del cierre" value={fGanado.notas} onChange={v=>setFGanado(x=>({...x,notas:v}))} placeholder="Condiciones, plazos, observaciones..." rows={2}/>
        </div>
        <div style={{marginTop:18,display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" size="sm" onClick={()=>setModalGanado(null)}>Cancelar</Btn>
          <Btn size="sm" disabled={!fGanado.valor||savingGanado} onClick={async()=>{
            if(!modalGanado||!fGanado.valor) return; setSavingGanado(true);
            const hoy=new Date().toISOString().split('T')[0];
            await supabase.from('oportunidades').update({etapa:'cerrado_ganado',fecha_cierre:hoy,valor:parseFloat(fGanado.valor),notas:fGanado.notas||null}).eq('id',modalGanado.id);
            setOpps(os=>os.map(o=>o.id===modalGanado.id?{...o,etapa:'cerrado_ganado',fecha_cierre:hoy,valor:parseFloat(fGanado.valor),notas:fGanado.notas||null}:o));
            setSel(prev=>prev?.id===modalGanado.id?{...prev,etapa:'cerrado_ganado',fecha_cierre:hoy,valor:parseFloat(fGanado.valor),notas:fGanado.notas||null}:prev);
            let cl=null;
            if(modalGanado.cliente_id){const{data}=await supabase.from('clientes').select('email,contacto').eq('id',modalGanado.cliente_id).maybeSingle();cl=data;}
            // Fallback por nombre SOLO si hay término real: con '' el ilike '%%' matcheaba
            // CUALQUIER cliente y el correo de felicitación se iba al equivocado (bug auditoría I).
            if(!cl?.email && String(modalGanado.cliente||'').trim().length>=3){const{data}=await supabase.from('clientes').select('email,contacto').ilike('empresa',`%${modalGanado.cliente.trim()}%`).limit(1).maybeSingle();cl=data;}
            if(cl?.email) fetch('/api/email',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({email:cl.email,cliente:cl.contacto||modalGanado.cliente,vendedor:modalGanado.vendedor,titulo:modalGanado.titulo})}).catch(e=>console.error('email cliente ganado:',e));
            fetch('/api/email',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({type:'servicio',cliente:modalGanado.cliente,vendedor:modalGanado.vendedor,titulo:modalGanado.titulo,valor:fGanado.valor,factura:modalGanado.factura_sap_num||'',notas:fGanado.notas,fecha:hoy})}).catch(e=>console.error('email servicio ganado:',e));
            setSavingGanado(false); setModalGanado(null); setSel(null);
          }}>{savingGanado?'Guardando...':'✅ Confirmar Cerrado Ganado'}</Btn>
        </div>
      </Modal>

      <Modal open={modalCliente} onClose={()=>{setModalCliente(false);setDupCliente(null);}} title="Nuevo Cliente" width={540}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          {/* EN QUE COMPANIA NACE. Cada una es una base de SAP distinta y hasta
              ahora no se podia elegir: el cliente caia siempre en la de la
              pestana activa, sin decirlo. */}
          <Sel label="Compañía *" value={empDe(fCl)} onChange={v=>setFCl(x=>({...x,empresa_id:v}))}
            options={ORG.empresas.map(e=>({v:e.id,l:e.label}))}/>
          <Inp label="Empresa *" value={fCl.empresa} onChange={v=>setFCl(x=>({...x,empresa:v}))} placeholder="Nombre de la empresa" required/>
          <Inp label="RTN" value={fCl.rtn} onChange={v=>setFCl(x=>({...x,rtn:v}))} placeholder="0000-0000-000000"/>
          <Inp label="Contacto principal" value={fCl.contacto} onChange={v=>setFCl(x=>({...x,contacto:v}))} placeholder="Nombre y apellido"/>
          <Inp label="Cargo" value={fCl.cargo} onChange={v=>setFCl(x=>({...x,cargo:v}))} placeholder="Gerente, Director..."/>
          <Inp label="Teléfono" value={fCl.telefono} onChange={v=>setFCl(x=>({...x,telefono:v}))} placeholder="+504 0000-0000"/>
          <Inp label="Email" type="email" value={fCl.email} onChange={v=>setFCl(x=>({...x,email:v}))} placeholder="correo@empresa.com"/>
          <Inp label="Ciudad" value={fCl.ciudad} onChange={v=>setFCl(x=>({...x,ciudad:v}))} placeholder="Tegucigalpa, San Pedro..."/>
          <Inp label="País" value={fCl.pais} onChange={v=>setFCl(x=>({...x,pais:v}))} placeholder="Honduras"/>
          <Inp label="Industria" value={fCl.industria} onChange={v=>setFCl(x=>({...x,industria:v}))} placeholder="Agroindustria, Logística..."/>
          {/* LINEA DE NEGOCIO, OBLIGATORIA. Es lo que decide en que lista de
              llamada entra el cliente: sin ella no aparece en ninguna y nadie lo
              vuelve a llamar. Era el campo que mas quedaba en blanco justamente
              porque el alta no lo pedia. */}
          <Sel label="Línea de negocio *" value={fCl.linea_negocio} onChange={v=>setFCl(x=>({...x,linea_negocio:v}))}
            options={[{v:'',l:'— Seleccionar —'},...ORG.lineasNegocio.map(l=>({v:l,l}))]}/>
          {/* Selector, no texto libre: escribirlo a mano generaba nombres que no
              calzaban con radar_usuarios y el cliente quedaba sin dueno real
              (no salia en su cartera, no contaba para su meta ni su comision). */}
          <Sel label="Vendedor asignado" value={fCl.vendedor} onChange={v=>setFCl(x=>({...x,vendedor:v}))}
            options={vendedoresList.map(v=>({v,l:v===usuario.nombre?`${v} (yo)`:v}))}/>
          <Sel label="Estado" value={fCl.estado} onChange={v=>setFCl(x=>({...x,estado:v}))} options={[{v:'activo',l:'Activo'},{v:'prospecto',l:'Prospecto'},{v:'inactivo',l:'Inactivo'}]}/>
          <Sel label="Segmento" value={fCl.segmento} onChange={v=>setFCl(x=>({...x,segmento:v}))} options={[{v:'',l:'Sin segmento'},...ORG.segmentos.map(s=>({v:s.v,l:s.l}))]}/>
          <Sel label="Iniciativa" value={fCl.iniciativa} onChange={v=>setFCl(x=>({...x,iniciativa:v}))} options={[{v:'',l:'Sin iniciativa'},...ORG.iniciativas.map(i=>({v:i,l:i}))]}/>
        </div>
        {/* Existe en OTRA compania: no es un duplicado, es el mismo cliente que
            factura por separado en otra base de SAP. Se avisa como dato, no como
            freno, porque asi se ve que la migracion va bien encaminada. */}
        {!dupCliente && (()=>{
          const nomN = String(fCl.empresa||'').trim().toUpperCase();
          if (nomN.length < 4) return null;
          const otra = clientes.find(c => String(c.empresa||'').trim().toUpperCase() === nomN && empDe(c) !== empDe(fCl));
          if (!otra) return null;
          return (
            <div style={{marginTop:10,padding:'10px 12px',background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:8,fontSize:12.5,color:C.blue}}>
              Este cliente ya existe en <b>{nombreEmpresa(empDe(otra))}</b>{otra.sap_code?` (${otra.sap_code})`:''}. Lo estás creando en <b>{nombreEmpresa(empDe(fCl))}</b>, que es una compañía distinta: se puede.
            </div>
          );
        })()}
        {dupCliente && (
          <div style={{marginTop:10,padding:'10px 12px',background:'#FEF3C7',border:'1px solid #F59E0B',borderRadius:8,fontSize:13,color:'#92400E',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <span>Ya existe este cliente{dupCliente.motivo==='RTN'?', con el mismo RTN':''} en {nombreEmpresa(empDe(dupCliente))}: <b>{dupCliente.empresa}</b>
              {dupCliente.sap_code?` (${dupCliente.sap_code} en SAP)`:''}. No se creó uno repetido.</span>
            {dupCliente.id && <Btn variant="secondary" onClick={()=>{const d=clientes.find(c=>c.id===dupCliente.id)||dupCliente;setSel(d);setTab('clientes');setModalCliente(false);setDupCliente(null);}}>Usar este cliente</Btn>}
          </div>
        )}
        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:4}}>
          <Btn variant="secondary" onClick={()=>{setModalCliente(false);setDupCliente(null);}}>Cancelar</Btn>
          <Btn onClick={crearCliente} disabled={!fCl.empresa||!fCl.linea_negocio||savingCl}>{savingCl?'Guardando...':'✅ Crear Cliente'}</Btn>
        </div>
      </Modal>

      {/* Modal nueva oportunidad */}
      <Modal open={modalOpp} onClose={()=>setModalOpp(false)} title="Nueva Oportunidad" width={520}>
        <Inp label="Título *" value={fOpp.titulo} onChange={v=>setFOpp(x=>({...x,titulo:v}))} placeholder="ej: Renta montacargas Toyota — Empresa ABC" required/>
        <ClienteBuscador
          clientes={clientes}
          value={fOpp.cliente}
          onSelect={c=>setFOpp(x=>({...x,cliente_id:c.id,cliente:c.empresa,vendedor:x.vendedor||c.vendedor||''}))}
          onClear={()=>setFOpp(x=>({...x,cliente_id:'',cliente:''}))}
          empresaActiva={empresaActiva}
        />
        {/* Equipo de la lista de precios (opcional) → jala el precio */}
        <div style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 12px',margin:'4px 0 10px'}}>
          <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',marginBottom:6}}>🏗️ Equipo de la lista de precios (opcional)</div>
          {equipoSelOpp ? (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                <div style={{fontSize:13,fontWeight:800,color:C.t1}}>{equipoSelOpp.codigo} · {[equipoSelOpp.marca,equipoSelOpp.modelo].filter(Boolean).join(' ')}</div>
                <button onClick={()=>{setFOpp(x=>({...x,equipo_id:''}));setEquipoBusq('');}} style={{background:'none',border:'none',color:C.t4,fontSize:16,cursor:'pointer'}}>×</button>
              </div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {MODALIDADES.map(m=>{const pr=equipoSelOpp[m.campo];return (
                  <button key={m.k} disabled={!pr} onClick={()=>aplicarEquipo(equipoSelOpp,m)}
                    style={{flex:'1 1 0',minWidth:90,background:fOpp.tipo===m.tipo?C.blue:(pr?C.bg2:'transparent'),color:fOpp.tipo===m.tipo?'#fff':(pr?C.t1:C.t4),border:`1px solid ${fOpp.tipo===m.tipo?C.blue:C.border}`,borderRadius:8,padding:'7px 6px',fontSize:11,fontWeight:700,cursor:pr?'pointer':'default'}}>
                    {m.l}<div style={{fontSize:12,fontWeight:900,marginTop:1}}>{pr?fmt(pr):'—'}</div>
                  </button>
                );})}
              </div>
            </div>
          ) : (
            <div style={{position:'relative'}}>
              <input value={equipoBusq} onChange={e=>setEquipoBusq(e.target.value)} placeholder="Buscar equipo por código / marca / modelo…"
                style={{width:'100%',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 10px',color:C.t1,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
              {equiposFiltOpp.length>0&&(
                <div style={{position:'absolute',top:'100%',left:0,right:0,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,marginTop:2,zIndex:10,maxHeight:200,overflowY:'auto',boxShadow:'0 8px 24px rgba(0,0,0,.15)'}}>
                  {equiposFiltOpp.map(e=>(
                    <div key={e.id} onClick={()=>{setFOpp(x=>({...x,equipo_id:e.id}));setEquipoBusq('');}}
                      style={{padding:'8px 11px',cursor:'pointer',borderBottom:`1px solid ${C.border}`,fontSize:13}}
                      onMouseEnter={ev=>ev.currentTarget.style.background=C.bg3} onMouseLeave={ev=>ev.currentTarget.style.background='transparent'}>
                      <span style={{fontWeight:800,color:C.t1}}>{e.codigo}</span> <span style={{color:C.t3}}>{[e.marca,e.modelo].filter(Boolean).join(' ')}</span>
                      <span style={{fontSize:10,color:C.t4,marginLeft:6}}>{e.precio_venta>0?`venta ${fmt(e.precio_venta)}`:''}{e.precio_renta_mes>0?` · mes ${fmt(e.precio_renta_mes)}`:''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Vendedor asignado</div>
            <select value={fOpp.vendedor} onChange={e=>setFOpp(x=>({...x,vendedor:e.target.value}))}
              style={{width:'100%',background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',color:fOpp.vendedor?C.t1:C.t4,fontSize:13,outline:'none',boxSizing:'border-box'}}>
              <option value="">— Sin asignar —</option>
              {vendedoresList.map(v=><option key={v} value={v} style={{background:C.bg2}}>{v}{v===usuario.nombre?' (yo)':''}</option>)}
            </select>
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Valor estimado</div>
            <div style={{display:'flex',gap:6}}>
              <select value={fOpp.moneda||'USD'} onChange={e=>setFOpp(x=>({...x,moneda:e.target.value}))}
                style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 8px',color:C.t1,fontSize:12,fontWeight:700,outline:'none',cursor:'pointer',flexShrink:0}}>
                <option value="USD">USD $</option>
                <option value="HNL">HNL L</option>
                {(empresaActiva==='costarica'||(!empresaActiva&&usuario.empresa_id==='costarica'))&&<option value="CRC">CRC ₡</option>}
              </select>
              <input type="number" value={fOpp.valor} onChange={e=>setFOpp(x=>({...x,valor:e.target.value}))} placeholder="0"
                style={{flex:1,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',color:C.t1,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
            </div>
            {fOpp.moneda!=='USD'&&fOpp.valor>0&&<div style={{fontSize:10,color:C.t4,marginTop:3}}>
              ≈ ${convertirAUSD(fOpp.valor,fOpp.moneda).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} USD (tasa {fOpp.moneda==='HNL'?`L${tasaHNL.toFixed(2)}`:tasaCRC.toFixed(0)})
            </div>}
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <Sel label="Etapa inicial" value={fOpp.etapa} onChange={v=>setFOpp(x=>({...x,etapa:v}))} options={[{v:'prospecto',l:'Prospecto'},{v:'contactado',l:'Contactado'},{v:'cotizacion',l:'Cotización'},{v:'negociacion',l:'Negociación'},{v:'cerrado',l:'Cerrado'},{v:'facturado',l:'Facturado'},{v:'pagado',l:'Pagado'}]}/>
          <Inp label="Fecha cierre estimada" type="date" value={fOpp.fecha_cierre} onChange={v=>setFOpp(x=>({...x,fecha_cierre:v}))}/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <Sel label="Línea de negocio *" value={fOpp.linea_negocio} onChange={v=>setFOpp(x=>({...x,linea_negocio:v}))} required options={[{v:'',l:'— Elegí una línea —'},...ORG.lineasNegocio.map(l=>({v:l,l}))]}/>
          <Inp label="Tipo" value={fOpp.tipo} onChange={v=>setFOpp(x=>({...x,tipo:v}))} placeholder="Renta, Venta..."/>
        </div>
        <Inp label="Próxima acción" value={fOpp.proxima_accion} onChange={v=>setFOpp(x=>({...x,proxima_accion:v}))} placeholder="ej: Visita de cierre el viernes"/>
        <Inp label="Notas iniciales" value={fOpp.notas} onChange={v=>setFOpp(x=>({...x,notas:v}))} rows={2} placeholder="contexto, detalles..."/>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" onClick={()=>setModalOpp(false)}>Cancelar</Btn>
          <Btn onClick={crearOpp} disabled={!fOpp.titulo||!fOpp.cliente||!fOpp.linea_negocio||savingOpp}>{savingOpp?'Guardando...':'✅ Crear Oportunidad'}</Btn>
        </div>
      </Modal>

      {/* Modal — Generar cotización en SAP */}
      <Modal open={modalCotSAP} onClose={()=>{if(!savingCotSAP)setModalCotSAP(false);}} title="🧾 Cotización / Orden de venta en SAP" width={620}>
        {resCotSAP?.ok ? (
          <div style={{textAlign:'center',padding:'10px 0'}}>
            <div style={{fontSize:40,marginBottom:6}}>✅</div>
            <div style={{fontSize:16,fontWeight:800,color:C.t1,marginBottom:4}}>{resCotSAP.esFactura?'Factura':resCotSAP.esOrden?'Orden de venta':'Cotización'} #{resCotSAP.docNum} {resCotSAP.editada?'actualizada':'creada'} en SAP{resCotSAP.empresa_id==='monhagro'?' (Monhagro)':''}</div>
            {resCotSAP.esFactura&&resCotSAP.ordenDocNum&&<div style={{fontSize:11.5,color:C.t4,marginBottom:2}}>desde la orden de venta #{resCotSAP.ordenDocNum}</div>}
            <div style={{fontSize:13,color:C.t3,marginBottom:2}}>Total: {resCotSAP.moneda||''} {(resCotSAP.docTotal||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
            {resCotSAP.moneda!=='USD'&&resCotSAP.docTotalUsd?<div style={{fontSize:11,color:C.t4,marginBottom:16}}>≈ USD {(resCotSAP.docTotalUsd||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</div>:<div style={{marginBottom:16}}/>}
            {resCotSAP.esOrden && !resCotSAP.esFactura ? (
              <div>
                <div style={{fontSize:12.5,color:C.t3,marginBottom:12}}>Paso 2: emitir la factura fiscal de esta orden en SAP.</div>
                <Btn onClick={facturarOrdenSAP} disabled={facturando}>{facturando?'Facturando en SAP...':'🧾 Facturar esta orden'}</Btn>
              </div>
            ) : resCotSAP.pdf?.base64 ? (
              <div style={{display:'flex',gap:8,justifyContent:'center',flexWrap:'wrap'}}>
                <Btn onClick={verPdfCotSAP}>👁️ Ver PDF</Btn>
                <Btn variant="secondary" onClick={descargarPdfCotSAP}>⬇️ Descargar</Btn>
              </div>
            ) : resCotSAP.editada ? (
              // Al editar no se pide el PDF de una: el cambio ya quedo grabado en
              // SAP y el PDF se baja solo si alguien lo va a mandar.
              <div>
                <div style={{fontSize:12.5,color:C.t3,marginBottom:10}}>Los cambios ya están en SAP.</div>
                <Btn onClick={reintentarPdfCotSAP} disabled={reintentandoPdf}>{reintentandoPdf?'Generando PDF...':'📄 Ver el PDF actualizado'}</Btn>
              </div>
            ) : (
              <div>
                <div style={{fontSize:12,color:C.orange,marginBottom:8}}>{resCotSAP.pdf?.error ? `El PDF no se generó: ${resCotSAP.pdf.error}` : 'El PDF no se pudo generar todavía.'}</div>
                <Btn onClick={reintentarPdfCotSAP} disabled={reintentandoPdf}>{reintentandoPdf?'Generando PDF...':'🔄 Reintentar PDF'}</Btn>
              </div>
            )}
            <div style={{marginTop:12}}>
              <Btn variant="secondary" onClick={()=>setModalCotSAP(false)}>Cerrar</Btn>
            </div>
          </div>
        ) : (<>
          {/* EDITAR UNA QUE YA EXISTE. Se busca por numero de cotizacion, que es
              lo que la gente tiene a mano; el DocEntry es un id interno de SAP
              que nadie conoce. */}
          {cotEditar ? (
            <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',
              background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:8,
              padding:'8px 11px',marginBottom:10}}>
              <div style={{flex:'1 1 240px',fontSize:12.5,color:C.primary,fontWeight:700}}>
                Editando la cotización #{cotEditar.docNum}
                {cotEditar.cardName ? <span style={{fontWeight:400,color:C.t3}}> · {cotEditar.cardName}</span> : null}
              </div>
              <button onClick={()=>{ setCotEditar(null); setNumCargar(''); setResCotSAP(null);
                  setFCotSAP(x=>({...x, lineas:[{itemCode:'',descripcion:'',cantidad:1,precio:''}], comentarios:'', cardManual:''})); }}
                style={{background:'none',border:'none',color:C.t3,fontSize:11.5,cursor:'pointer',textDecoration:'underline'}}>
                salir de edición
              </button>
            </div>
          ) : (
            <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:10}}>
              <span style={{fontSize:11.5,color:C.t3,flexShrink:0}}>¿Editar una ya hecha?</span>
              <input value={numCargar} onChange={e=>setNumCargar(e.target.value)}
                onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); cargarCotSAP(); } }}
                placeholder="N° de cotización"
                style={{width:150,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,
                  padding:'7px 10px',color:C.t1,fontSize:12.5,outline:'none'}}/>
              <Btn size="sm" variant="secondary" onClick={cargarCotSAP} disabled={cargandoCot||!numCargar.trim()}>
                {cargandoCot?'Buscando…':'Cargar'}
              </Btn>
            </div>
          )}
          {/* ELEGIR DESDE QUE EMPRESA SE EMITE NO ES COSA DE ADMIN.
              Las dos cosas estaban metidas en el mismo bloque de admin, asi que
              un vendedor no podia cambiar la emisora: la cotizacion salia desde
              la empresa que le tocara por defecto, y si el cliente tenia codigo
              de otra base, SAP la rebotaba sin que el pudiera hacer nada. El
              tipo de documento si sigue siendo de admin: una orden de venta
              compromete inventario, una cotizacion no. */}
          {!cotEditar && (
          <div style={{display:'grid',gridTemplateColumns:usuario.rol==='admin'?'1fr 1fr':'1fr',gap:12,marginBottom:10}}>
            {usuario.rol==='admin' && (
            <div>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Documento</div>
              <select value={fCotSAP.tipoDoc} onChange={e=>setFCotSAP(x=>({...x,tipoDoc:e.target.value}))}
                style={{width:'100%',background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',color:C.t1,fontSize:13,fontWeight:700,outline:'none',boxSizing:'border-box'}}>
                <option value="cotizacion">Cotización</option>
                <option value="orden">Orden de venta</option>
              </select>
            </div>
            )}
            <div>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Empresa emisora</div>
              <select value={fCotSAP.sapEmp} onChange={e=>setFCotSAP(x=>({...x,sapEmp:e.target.value,moneda:monedaDe(e.target.value||'honduras')}))}
                style={{width:'100%',background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',color:C.t1,fontSize:13,fontWeight:700,outline:'none',boxSizing:'border-box'}}>
                <option value="">Auto (según el cliente)</option>
                {ORG.empresas.map(e=><option key={e.id} value={e.id}>{e.label}</option>)}
              </select>
            </div>
          </div>
          )}
          <ClienteBuscador
            clientes={clientes}
            value={fCotSAP.cliente_nombre}
            label="Cliente (debe tener código SAP) *"
            onSelect={c=>setFCotSAP(x=>({...x,cliente_id:c.id,cliente_nombre:c.empresa}))}
            onClear={()=>setFCotSAP(x=>({...x,cliente_id:'',cliente_nombre:''}))}
            empresaActiva={empresaActiva}
          />
          {/* El campo manual aparece en DOS casos, no solo cuando la emisora es de
              otra base: tambien cuando el cliente elegido no tiene codigo SAP.
              Antes la validacion decia "usa el CardCode manual" con el campo
              oculto — te mandaba a algo que no podias ver. */}
          {(() => {
            const cliCot = fCotSAP.cliente_id ? clientes.find(c=>c.id===fCotSAP.cliente_id) : null;
            const sinCodigo = !!fCotSAP.cliente_id && !cliCot?.sap_code;
            const otraBase  = emisoraRequiereCardCode(fCotSAP.sapEmp);
            // TERCER CASO: el cliente SI tiene codigo, pero de OTRA empresa.
            // Cada empresa es una base de SAP distinta y no se conocen entre si.
            // Antes este caso no mostraba nada: ni el campo manual ni el boton de
            // crear, porque "ya tenia codigo". El vendedor quedaba sin salida y la
            // unica forma de resolverlo era entrar a SAP a mano.
            const empDelCodigo = cliCot?.sap_code && String(cliCot.sap_code).includes('_')
              ? String(cliCot.sap_code).split('_')[0] : null;
            const empEmisora = fCotSAP.sapEmp || cliCot?.empresa_id || empresaActiva || 'honduras';
            const codigoAjeno = !!empDelCodigo && empDelCodigo !== empEmisora;
            // CUARTO CASO: el codigo existe y es de esta base, pero en SAP ese BP
            // esta grabado como proveedor o como lead. SAP contesta "Chosen BP is
            // not a customer" y hasta aqui eso era un callejon sin salida: el
            // modal no mostraba nada porque el cliente "ya tenia codigo".
            const bpNoCliente = /not a customer/i.test(resCotSAP?.error || '');
            if (!sinCodigo && !otraBase && !codigoAjeno && !bpNoCliente) return null;
            return (
              <div style={{margin:'8px 0 4px'}}>
                <Inp label={(otraBase || codigoAjeno || bpNoCliente)
                    ? `CardCode SAP del cliente en ${nombreEmpresa(empEmisora)} (manual)`
                    : 'CardCode SAP del cliente (manual)'}
                  value={fCotSAP.cardManual} onChange={v=>setFCotSAP(x=>({...x,cardManual:v}))}
                  placeholder="Ej. C00025 (el código tal como existe en esa base)"/>
                <div style={{fontSize:10.5,color:C.t4,marginTop:2,lineHeight:1.5}}>
                  {bpNoCliente
                    ? <>En SAP de {nombreEmpresa(empEmisora)}, el código <b>{String(cliCot?.sap_code||'').split('_')[1]}</b> está
                       grabado como <b>proveedor</b>, no como cliente — por eso SAP no deja cotizarle.
                       Poné el código que tenga como cliente, o creálo con el botón.</>
                    : codigoAjeno
                    ? <>Este cliente tiene el código <b>{String(cliCot.sap_code).split('_')[1]}</b> de {nombreEmpresa(empDelCodigo)},
                       que no existe en la base de {nombreEmpresa(empEmisora)}. Si ya lo tienen allá, poné su código.
                       Si no, creálo con el botón — se busca primero por RTN, así que no se duplica.</>
                    : otraBase
                    ? <>Los clientes de RADAR vienen de la base HN — para {nombreEmpresa(fCotSAP.sapEmp)} poné el código tal como existe en SU base de SAP.</>
                    : <>Este cliente no existe en SAP todavía. Si ya existe allá con otro nombre, poné su CardCode. Si no existe, creálo con el botón — se busca por RTN antes de crear, así que no se duplica.</>}
                </div>

                {/* Crear el cliente en SAP desde acá. Solo admin: es maestro de
                    clientes del ERP, no un dato de CRM. */}
                {(sinCodigo || codigoAjeno || bpNoCliente) && usuario.rol==='admin' && (
                  <div style={{marginTop:8}}>
                    <Btn variant="secondary" onClick={crearClienteEnSAP} disabled={creandoCliSAP}>
                      {creandoCliSAP?'Creando en SAP…':'➕ Crear este cliente en SAP'}
                    </Btn>
                    {resCrearCliSAP && (
                      <div style={{marginTop:6,fontSize:12,lineHeight:1.55,
                        color:resCrearCliSAP.ok?C.greenText:C.redText}}>
                        {resCrearCliSAP.ok
                          ? (resCrearCliSAP.mensaje || `Creado en SAP como ${resCrearCliSAP.cardCode}. Ya podés generar la cotización.`)
                          : resCrearCliSAP.error}
                      </div>
                    )}
                    {!cliCot?.rtn && (
                      <div style={{marginTop:6,fontSize:11.5,color:C.orange,lineHeight:1.5}}>
                        Este cliente no tiene RTN. Es obligatorio para crearlo en SAP: es la llave real
                        para no duplicar. Editá el cliente y agregalo primero.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Moneda</div>
              {/* SAP SI DEJA CAMBIARLE LA MONEDA A UNA COTIZACION YA EMITIDA.
                  Se creia que no y el modal ofrecia rehacerla como nueva; eso es
                  lo que descuadro la 4244, que salio con los montos en dolares
                  pero marcada en lempiras. En un documento que ya existe el
                  cambio lo hace SAP (accion cambiar_moneda_cotizacion); en uno
                  nuevo se convierten los montos en pantalla. */}
              <select value={fCotSAP.moneda} disabled={cambiandoMoneda}
                onChange={e=>cotEditar ? cambiarMonedaEnSAP(e.target.value) : cambiarMonedaCot(e.target.value)}
                style={{width:'100%',background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',color:C.t1,fontSize:13,fontWeight:700,outline:'none',boxSizing:'border-box',cursor:cambiandoMoneda?'wait':'pointer'}}>
                <option value="USD">USD $</option>
                <option value="HNL">HNL L</option>
                <option value="CRC">CRC ₡</option>
              </select>
              {cambiandoMoneda && (
                <div style={{fontSize:10.5,color:C.t4,marginTop:4}}>Cambiando la moneda en SAP…</div>
              )}
              {cotEditar && !cambiandoMoneda && (
                <div style={{fontSize:10.5,color:C.t4,marginTop:4,lineHeight:1.45}}>
                  Cambiarla acá la cambia en SAP, en la {cotEditar.docNum}: convierte
                  los precios de cada línea con el tipo de cambio del documento.
                </div>
              )}
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>ISV / Impuesto</div>
              <select value={fCotSAP.isv} onChange={e=>setFCotSAP(x=>({...x,isv:e.target.value}))}
                style={{width:'100%',background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',color:C.t1,fontSize:13,fontWeight:700,outline:'none',boxSizing:'border-box'}}>
                <option value="con">Con ISV</option>
                <option value="exento">Exento (0%)</option>
              </select>
            </div>
            <Inp label="Válida hasta" type="date" value={fCotSAP.validez} onChange={v=>setFCotSAP(x=>({...x,validez:v}))}/>
            {/* LINEA DE NEGOCIO, ACA Y NO DESPUES.
                Cada cotizacion de SAP crea su propia oportunidad, asi que la
                ficha terminaba pidiendo la linea DESPUES de cotizar, una y otra
                vez. Se pregunta donde nace el documento: es un clic, y ya viene
                puesta la del cliente. */}
            <Sel label="Línea de negocio *" value={fCotSAP.linea_negocio}
              onChange={v=>setFCotSAP(x=>({...x,linea_negocio:v}))}
              options={[{v:'',l:'— Seleccionar —'},...ORG.lineasNegocio.map(l=>({v:l,l}))]}/>
          </div>
          <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',margin:'14px 0 6px'}}>Líneas</div>
          {/* Encabezados y filas comparten la MISMA grilla. Antes eran dos flex con
              wrap y se partian en puntos distintos, asi que cada etiqueta quedaba
              sobre la columna equivocada. */}
          <div className="cot-linea" style={{...GRID_COT, marginBottom:4, fontSize:9.5, fontWeight:700,
            letterSpacing:'.05em', textTransform:'uppercase', color:C.t4}}>
            <div style={{minWidth:0}}>Descripción</div>
            <div>Cant.</div>
            <div>Precio lista</div>
            <div>Con descuento</div>
            <div/>
          </div>
          {fCotSAP.lineas.map((l,i)=>(
            <div key={i} className="cot-linea" style={{...GRID_COT, marginBottom:8}}>
              <div style={{position:'relative',minWidth:0}}>
                <input value={l.descripcion}
                  onChange={e=>{const v=e.target.value; setFCotSAP(x=>{const ls=[...x.lineas];ls[i]={...ls[i],descripcion:v};return{...x,lineas:ls};}); buscarInvLinea(i, v);}}
                  onBlur={()=>setTimeout(()=>setInvSug(null), 200)}
                  placeholder="Buscar en inventario o escribir libre…"
                  style={{width:'100%',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 9px',color:C.t1,fontSize:12,outline:'none',boxSizing:'border-box'}}/>
                {invSug?.i===i && invSug.rows.length>0 && (
                  <div style={{position:'absolute',top:'100%',left:0,right:0,minWidth:'min(340px, 88vw)',zIndex:60,background:C.bg2,border:`1px solid ${C.border}`,
                    borderRadius:10,boxShadow:'0 8px 24px rgba(16,24,40,.16)',marginTop:3,overflow:'hidden',maxHeight:240,overflowY:'auto'}}>
                    {invSug.rows.map(r=>(
                      <div key={`${r.empresa_id}_${r.codigo}`} onMouseDown={()=>elegirInvLinea(i, r)}
                        className="radar-hrow" style={{padding:'7px 11px',cursor:'pointer',borderBottom:`1px solid ${C.bg3}`}}>
                        <div style={{display:'flex',justifyContent:'space-between',gap:8,alignItems:'baseline'}}>
                          <span className="radar-num" style={{fontSize:10.5,fontWeight:700,color:C.blue,fontFamily:'monospace'}}>{r.codigo}</span>
                          <span className="radar-num" style={{fontSize:10,fontWeight:700,whiteSpace:'nowrap',
                            color:r.es_servicio?C.t4:(r.disponible>0?C.greenText:C.redText)}}>
                            {r.es_servicio ? 'servicio'
                              : r.disponible==null ? '' : `disp. ${Number(r.disponible).toLocaleString('en-US')}`}
                          </span>
                        </div>
                        <div style={{fontSize:11.5,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.nombre}</div>
                        <div className="radar-num" style={{fontSize:10,color:C.t4}}>
                          {fCotSAP.moneda==='USD' && r.precio_usd
                            ? `$${Number(r.precio_usd).toLocaleString('en-US',{minimumFractionDigits:2})}`
                            : r.precio_local
                            ? `${r.moneda||'L'} ${Number(r.precio_local).toLocaleString('en-US',{minimumFractionDigits:2})}`
                            : 'sin precio de lista'}
                          {r.grupo?` · ${r.grupo}`:''}
                          {' · '}{nombreEmpresa(r.empresa_id)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <input type="number" value={l.cantidad} onChange={e=>setFCotSAP(x=>{const ls=[...x.lineas];ls[i]={...ls[i],cantidad:e.target.value};return{...x,lineas:ls};})}
                style={{...INP_COT}}/>
              <input type="number" value={l.precio} onChange={e=>setFCotSAP(x=>{const ls=[...x.lineas];ls[i]={...ls[i],precio:e.target.value};return{...x,lineas:ls};})}
                style={{...INP_COT}}/>
              {/* SE ESCRIBE EL PRECIO, NO EL PORCENTAJE. Es lo que el vendedor
                  negocia y lo que el cliente lee. El % se calcula solo y es lo
                  que viaja a SAP, que es como SAP guarda el descuento. */}
              <input type="number" value={l.precioDesc ?? ''}
                onChange={e=>setFCotSAP(x=>{const ls=[...x.lineas];ls[i]={...ls[i],precioDesc:e.target.value};return{...x,lineas:ls};})}
                placeholder="—"
                title="Precio unitario ya con el descuento aplicado"
                style={{...INP_COT, background: l.precioDesc?`${C.green}12`:C.bg1,
                  border:`1px solid ${l.precioDesc?C.greenBorder:C.border}`}}/>
              <button onClick={()=>setFCotSAP(x=>({...x,lineas:x.lineas.length>1?x.lineas.filter((_,j)=>j!==i):x.lineas}))}
                title="Quitar esta línea"
                style={{background:'none',border:'none',color:C.t4,fontSize:18,cursor:'pointer',padding:0}}>×</button>
              {/* El ItemCode es opcional y largo: va en su propio renglon debajo,
                  no peleando por ancho con la cantidad y el precio. */}
              <input value={l.itemCode}
                onChange={e=>setFCotSAP(x=>{const ls=[...x.lineas];ls[i]={...ls[i],itemCode:e.target.value};return{...x,lineas:ls};})}
                onBlur={e=>buscarPrecio(i, e.target.value)} placeholder="ItemCode (opcional)"
                style={{...INP_COT, gridColumn:'1 / -1', marginTop:-2}}/>
              {(()=>{ const d = precioSAP[i]; if (!d) return null;
                if (d.cargando) return <div style={{gridColumn:'1 / -1',fontSize:10.5,color:C.t4}}>Preguntándole el precio a SAP…</div>;
                if (!d.ok) return (
                  <div style={{gridColumn:'1 / -1',fontSize:10.5,color:C.orange,lineHeight:1.5}}>
                    No se pudo consultar el precio en SAP ({d.error}). Podés escribirlo a mano
                    y generar igual; si no coincide con la lista del cliente, SAP lo va a decir.
                  </div>
                );
                if (d.precio) {
                  const puesto = Number(l.precio) || 0;
                  const difiere = puesto > 0 && Math.abs(puesto - d.precio) > 0.009;
                  return (
                    <div style={{gridColumn:'1 / -1',fontSize:10.5,lineHeight:1.5,color:difiere?C.redText:C.greenText}}>
                      {difiere
                        ? <>SAP va a rechazar este precio. En <b>{d.listaNombre}</b>, que es la lista de este cliente,
                            el <b>{d.itemCode}</b> vale <b>{d.moneda||''} {d.precio.toLocaleString('en-US',{minimumFractionDigits:2})}</b>
                            {d.convertido && <> ({d.monedaLista} {d.precioLista.toLocaleString('en-US',{minimumFractionDigits:2})} de lista a la tasa {d.tasa.toLocaleString('en-US',{minimumFractionDigits:4})})</>}.{' '}
                            <button onClick={()=>setFCotSAP(x=>{const ls=[...x.lineas];ls[i]={...ls[i],precio:String(d.precio)};return{...x,lineas:ls};})}
                              style={{background:'none',border:'none',padding:0,color:C.primary,fontWeight:800,fontSize:10.5,cursor:'pointer',textDecoration:'underline'}}>
                              Poner {d.precio.toLocaleString('en-US',{minimumFractionDigits:2})}
                            </button></>
                        : <>Precio de <b>{d.listaNombre}</b>: {d.moneda||''} {d.precio.toLocaleString('en-US',{minimumFractionDigits:2})}
                            {d.convertido && <> · la lista lo tiene en <b>{d.monedaLista} {d.precioLista.toLocaleString('en-US',{minimumFractionDigits:2})}</b>, convertido a la tasa de SAP de hoy ({d.tasa.toLocaleString('en-US',{minimumFractionDigits:4})})</>}</>}
                    </div>
                  );
                }
                return (
                  <div style={{gridColumn:'1 / -1',fontSize:10.5,color:C.orange,lineHeight:1.5}}>
                    {d.itemCode} no tiene precio en {d.listaNombre}, que es la lista de este cliente.
                    {d.otras?.length>0 && <> Sí lo tiene en: {d.otras.map(o=>`lista ${o.lista} (${o.moneda||''} ${o.precio})`).join(', ')}.</>}
                    {' '}SAP va a rechazar la cotización hasta que se le ponga precio en su lista.
                  </div>
                ); })()}
              {(()=>{ const p=Number(l.precio)||0, d=l.precioDesc===''||l.precioDesc==null?null:Number(l.precioDesc);
                if (d==null || !p) return null;
                if (d > p) return <div style={{gridColumn:'1 / -1',fontSize:10.5,color:C.redText}}>El precio con descuento no puede ser mayor que el de lista.</div>;
                const pct = (1 - d/p) * 100, tot = d * (Number(l.cantidad)||0);
                return (
                  <div style={{gridColumn:'1 / -1',fontSize:10.5,color:C.t3,marginTop:-4}}>
                    Descuento {pct.toFixed(1)}% · total de la línea {fCotSAP.moneda==='USD'?'$':'L '}
                    {tot.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                  </div>
                ); })()}
            </div>
          ))}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:6}}>
            <button onClick={()=>setFCotSAP(x=>({...x,lineas:[...x.lineas,{descripcion:'',cantidad:'1',precio:'',precioDesc:'',itemCode:''}]}))}
              style={{background:'none',border:`1px dashed ${C.border}`,borderRadius:8,padding:'7px 12px',color:C.t3,fontSize:12,fontWeight:700,cursor:'pointer'}}>+ Agregar línea</button>
            {/* Cotizar una maquina de la flota sin salirse del modal ni buscar el
                precio en otro lado. Los precios viven en equipos y estan en USD. */}
            <button onClick={()=>setFlotaOpen(v=>!v)}
              style={{background:flotaOpen?C.primaryTint:'none',border:`1px dashed ${flotaOpen?C.primaryBorder:C.border}`,borderRadius:8,
                padding:'7px 12px',color:flotaOpen?C.blue:C.t3,fontSize:12,fontWeight:700,cursor:'pointer'}}>
              {flotaOpen?'× Cerrar flota':'Cotizar equipo de la flota'}</button>
          </div>
          {flotaOpen && (()=>{
            const q = flotaQ.trim().toLowerCase();
            const lista = equiposPrecio
              .filter(e=>e.estado!=='vendido')
              .filter(e=>!q || `${e.codigo||''} ${e.marca||''} ${e.modelo||''} ${e.tipo_equipo||''}`.toLowerCase().includes(q))
              .sort((a,b)=>(a.estado==='disponible'?0:1)-(b.estado==='disponible'?0:1) || String(a.codigo||'').localeCompare(String(b.codigo||'')));
            const sim = fCotSAP.moneda==='USD'?'$':fCotSAP.moneda==='CRC'?'₡':'L ';
            return (
              <div style={{border:`1px solid ${C.border}`,borderRadius:10,padding:10,marginBottom:10,background:C.bg1}}>
                <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
                  {PERIODOS_FLOTA.map(pp=>(
                    <button key={pp.k} onClick={()=>setFlotaPeriodo(pp.k)}
                      style={{border:`1px solid ${flotaPeriodo===pp.k?C.blue:C.border}`,background:flotaPeriodo===pp.k?C.primaryTint:C.bg2,
                        color:flotaPeriodo===pp.k?C.blue:C.t3,borderRadius:999,padding:'5px 12px',fontSize:11.5,fontWeight:700,cursor:'pointer'}}>{pp.l}</button>
                  ))}
                </div>
                <input value={flotaQ} onChange={e=>setFlotaQ(e.target.value)} autoFocus
                  placeholder="Buscar por código, marca, modelo o tipo…"
                  style={{width:'100%',boxSizing:'border-box',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 10px',color:C.t1,fontSize:12,outline:'none'}}/>
                {flotaPeriodo==='semana' && (
                  <div style={{fontSize:10.5,color:C.orangeText,background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:7,padding:'6px 9px',marginTop:7}}>
                    La flota no tiene tarifa semanal cargada. Se calcula como la mensual entre {SEMANAS_POR_MES}. Ajustá el precio si tu tarifa real es otra.
                  </div>
                )}
                <div style={{maxHeight:250,overflowY:'auto',marginTop:7}}>
                  {lista.length===0 && <div style={{fontSize:11.5,color:C.t4,padding:'10px 2px'}}>Ningún equipo coincide.</div>}
                  {lista.slice(0,120).map(eq=>{
                    const usd = precioFlotaUSD(eq, flotaPeriodo);
                    const loc = usd==null?null:desdeUSD(usd, fCotSAP.moneda);
                    return (
                      <div key={eq.id} onClick={()=>agregarEquipoFlota(eq)} className="radar-hrow"
                        title={usd==null?'Sin tarifa para este período — se agrega sin precio':'Agregar a la cotización'}
                        style={{display:'flex',justifyContent:'space-between',gap:10,alignItems:'center',padding:'7px 9px',
                          cursor:'pointer',borderBottom:`1px solid ${C.bg3}`,borderRadius:6}}>
                        <div style={{minWidth:0}}>
                          <div style={{display:'flex',gap:7,alignItems:'baseline',flexWrap:'wrap'}}>
                            <span className="radar-num" style={{fontSize:11,fontWeight:700,color:C.blue,fontFamily:'monospace'}}>{eq.codigo}</span>
                            <span style={{fontSize:12,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{[eq.marca,eq.modelo].filter(Boolean).join(' ')||'—'}</span>
                          </div>
                          <div style={{fontSize:10,color:C.t4,display:'flex',alignItems:'center',gap:5,flexWrap:'wrap'}}>
                            <span>{eq.tipo_equipo||'—'}</span>
                            <span style={{color:(STATUS[eq.estado]||STATUS.desconocido).text,background:(STATUS[eq.estado]||STATUS.desconocido).bg,
                              border:`1px solid ${(STATUS[eq.estado]||STATUS.desconocido).border}`,borderRadius:999,padding:'0 6px',fontWeight:700,fontSize:9.5}}>
                              {(STATUS[eq.estado]||STATUS.desconocido).label}</span>
                          </div>
                        </div>
                        <div className="radar-num" style={{fontSize:12,fontWeight:700,color:loc==null?C.t4:C.t1,whiteSpace:'nowrap'}}>
                          {loc==null?'sin tarifa':`${sim}${loc.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {fCotSAP.moneda!=='USD' && (
                  <div style={{fontSize:10,color:C.t4,marginTop:7}}>
                    Los precios de la flota están en dólares. Convertidos a {fCotSAP.moneda} con la tasa {fCotSAP.moneda==='HNL'?`L${tasaHNL.toFixed(4)}`:tasaCRC.toFixed(2)} por $1.
                  </div>
                )}
              </div>
            );
          })()}
          <div style={{fontSize:10,color:C.t4,marginBottom:10}}>El ItemCode es opcional. Si todas las líneas lo traen, se crea como documento de ítems; si no, como documento de servicio. Impuesto y bodega salen de la config de SAP.</div>
          <Inp label="Comentarios" value={fCotSAP.comentarios} onChange={v=>setFCotSAP(x=>({...x,comentarios:v}))} rows={2} placeholder="opcional"/>
          {resCotSAP?.error&&<div style={{fontSize:12,color:C.red,background:`${C.red}15`,borderRadius:8,padding:'8px 10px',marginBottom:10}}>
            ⚠️ {/not a customer/i.test(resCotSAP.error)
              ? 'Ese código existe en SAP pero está grabado como proveedor, no como cliente. SAP no deja cotizarle. Usá el campo de CardCode de arriba o creálo como cliente.'
              : /3052022|lista de precio/i.test(resCotSAP.error)
              // SAP manda el codigo del articulo adentro del mensaje. Se saca
              // para poder decirle al vendedor cual de sus lineas es.
              ? <>El precio no coincide con la lista de precios de este cliente
                  {(/C[oó]digo:?\s*([A-Za-z0-9._-]+)/.exec(resCotSAP.error) || [])[1]
                    ? <> (código <b>{(/C[oó]digo:?\s*([A-Za-z0-9._-]+)/.exec(resCotSAP.error) || [])[1]}</b>)</> : null}.
                  Borrá y volvé a escribir el ItemCode en esa línea: RADAR le pregunta el precio bueno a SAP y lo pone solo.</>
              : resCotSAP.error}
          </div>}
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <Btn variant="secondary" onClick={()=>{setModalCotSAP(false);setCotEditar(null);setNumCargar('');}} disabled={savingCotSAP}>Cancelar</Btn>
            {cotEditar
              ? <Btn onClick={guardarCotSAP} disabled={savingCotSAP}>{savingCotSAP?'Guardando en SAP...':'💾 Guardar cambios en SAP'}</Btn>
              : <Btn onClick={generarCotSAP} disabled={(!fCotSAP.cliente_id && !String(fCotSAP.cardManual||'').trim())||!fCotSAP.linea_negocio||savingCotSAP}>{savingCotSAP?'Generando en SAP...':'🧾 Generar en SAP'}</Btn>}
          </div>
        </>)}
      </Modal>

      {/* Modal — Adjuntar cotización SAP al mover a Cotización */}
      <Modal open={!!modalCotizSAP} onClose={()=>setModalCotizSAP(null)} title="Mover a Cotización" width={400}>
        <p style={{fontSize:13,color:C.t3,marginTop:0,marginBottom:16}}>
          ¿Tienes una cotización en SAP? Puedes vincularla ahora o hacerlo después.
        </p>
        <Inp
          label="Número de cotización SAP (o últimos 4 dígitos)"
          value={sapCotizInput}
          onChange={setSapCotizInput}
          placeholder="ej: 2818"
        />
        <p style={{fontSize:11,color:C.t4,margin:'4px 0 16px'}}>
          Déjalo vacío si aún no tienes cotización en SAP.
        </p>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" onClick={()=>setModalCotizSAP(null)}>Cancelar</Btn>
          <Btn onClick={confirmarCotizSAP} disabled={savingCotiz}>
            {savingCotiz?'Guardando...':'→ Cotización'}
          </Btn>
        </div>
      </Modal>

      {/* Modal — Unir / Anexar oportunidades */}
      <Modal open={!!modalUnir} onClose={()=>{setModalUnir(null);setUnirSel(null);setUnirBusq('');}} title="🔗 Unir / Anexar oportunidad" width={460}>
        {modalUnir && (<>
          <div style={{background:C.bg3,borderRadius:8,padding:'10px 12px',marginBottom:12}}>
            <div style={{fontSize:10,color:C.t4,fontWeight:700,textTransform:'uppercase',marginBottom:2}}>Oportunidad principal (se queda)</div>
            <div style={{fontSize:13,fontWeight:800,color:C.t1}}>{modalUnir.titulo}</div>
            <div style={{fontSize:11,color:C.t3}}>{modalUnir.cliente||'—'} · {modalUnir.etapa}{modalUnir.sap_quotation_num?` · Cotiz #${modalUnir.sap_quotation_num}`:''}</div>
          </div>
          {!unirSel ? (<>
            <div style={{fontSize:12,color:C.t3,marginBottom:8}}>Elegí la oportunidad o prospecto a anexar (se combina toda su info y se elimina):</div>
            <input value={unirBusq} onChange={e=>setUnirBusq(e.target.value)} placeholder="Buscar por título o cliente..."
              style={{width:'100%',background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 10px',color:C.t1,fontSize:13,outline:'none',marginBottom:8,boxSizing:'border-box'}}/>
            <div style={{maxHeight:240,overflowY:'auto',border:`1px solid ${C.border}`,borderRadius:8}}>
              {unirCandidatos.length===0
                ? <div style={{padding:16,textAlign:'center',color:C.t4,fontSize:12}}>{unirBusq?'Sin resultados':'No hay otras oportunidades de este cliente. Escribí arriba para buscar en todas.'}</div>
                : unirCandidatos.slice(0,40).map(o=>(
                  <div key={o.id} onClick={()=>setUnirSel(o)} style={{padding:'9px 12px',borderBottom:`1px solid ${C.border}`,cursor:'pointer'}}>
                    <div style={{fontSize:12.5,fontWeight:700,color:C.t1}}>{o.titulo}</div>
                    <div style={{fontSize:11,color:C.t3}}>{o.cliente||'—'} · {o.etapa}{o.valor>0?` · $${o.valor.toLocaleString()}`:''}{o.sap_quotation_num?` · Cotiz #${o.sap_quotation_num}`:''} · {o.vendedor||'sin asignar'}</div>
                  </div>
                ))}
            </div>
          </>) : (<>
            <div style={{background:'#7C3AED10',border:'1px solid #7C3AED40',borderRadius:8,padding:'10px 12px',marginBottom:12}}>
              <div style={{fontSize:10,color:'#7C3AED',fontWeight:700,textTransform:'uppercase',marginBottom:2}}>Se anexa y se elimina</div>
              <div style={{fontSize:13,fontWeight:800,color:C.t1}}>{unirSel.titulo}</div>
              <div style={{fontSize:11,color:C.t3}}>{unirSel.cliente||'—'} · {unirSel.etapa}{unirSel.sap_quotation_num?` · Cotiz #${unirSel.sap_quotation_num}`:''}</div>
            </div>
            <div style={{fontSize:12,color:C.t2,marginBottom:14,lineHeight:1.5}}>
              La principal quedará en etapa <b>{rankEtapa(modalUnir.etapa)>=rankEtapa(unirSel.etapa)?modalUnir.etapa:unirSel.etapa}</b>, conservando cotización, factura, valor y notas de ambas. <b style={{color:C.red}}>Esta acción no se puede deshacer.</b>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'space-between'}}>
              <Btn variant="secondary" onClick={()=>setUnirSel(null)}>← Elegir otra</Btn>
              <Btn onClick={()=>unirOpps(modalUnir, unirSel)} disabled={savingUnir}>{savingUnir?'Uniendo...':'✓ Sí, anexar'}</Btn>
            </div>
          </>)}
        </>)}
      </Modal>

      {/* Modal — Vincular factura SAP al mover a Facturado / Pagado / Cerrado Ganado */}
      <Modal open={!!modalFactura} onClose={()=>setModalFactura(null)} title="🧾 Vincular Factura SAP" width={480}>
        <p style={{fontSize:13,color:C.t3,marginTop:0,marginBottom:16}}>
          {modalFactura?.opp?.titulo || 'Oportunidad'} —{' '}
          {modalFactura?.pendingEtapa==='facturado'?'mover a Facturado':modalFactura?.pendingEtapa==='pagado'?'mover a Pagado':'cerrar como Ganado'}
        </p>
        {/* Tabs */}
        <div style={{display:'flex',gap:0,marginBottom:16,border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
          {[{k:'digitos',l:'🔢 Por dígitos'},{k:'lista',l:'📋 Lista del cliente'}].map(t=>(
            <button key={t.k} onClick={()=>{ setFacturaTab(t.k); if(t.k==='lista'&&facturasLista.length===0) cargarFacturasCliente(modalFactura.opp); }}
              style={{flex:1,padding:'9px 0',fontSize:12,fontWeight:700,cursor:'pointer',border:'none',outline:'none',
                background:facturaTab===t.k?C.blue:'transparent',color:facturaTab===t.k?'#fff':C.t3,transition:'background .15s'}}>
              {t.l}
            </button>
          ))}
        </div>

        {/* Tab: dígitos */}
        {facturaTab==='digitos'&&(
          <div>
            <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',marginBottom:6}}>Últimos 4–6 dígitos de la factura SAP</div>
            <div style={{display:'flex',gap:8}}>
              <input
                value={facturas4dig}
                onChange={e=>{setFacturas4dig(e.target.value.replace(/\D/g,'').slice(0,6));setFacturaSearchMatch(null);setFacturaSearchErr('');}}
                placeholder="ej: 2841"
                maxLength={6}
                style={{flex:1,background:C.bg1,border:`1px solid ${facturaSearchMatch?C.green:facturaSearchErr?C.red:C.border}`,borderRadius:8,padding:'9px 12px',color:C.t1,fontSize:16,fontWeight:700,outline:'none',letterSpacing:4}}
              />
              <button
                onClick={async()=>{
                  const dig=facturas4dig.trim();
                  if(!dig||dig.length<2){setFacturaSearchErr('Ingresá al menos 2 dígitos');return;}
                  setFacturaSearchErr('🔎 Buscando…');setFacturaSearchMatch(null);
                  const eid=modalFactura.opp.empresa_id;
                  // Paginar TODO (Supabase corta a 1000/req) y no filtrar empresa si es 'all'/null,
                  // si no, facturas en honduras/costarica no aparecen. Orden por id (PK, estable).
                  const PAGE=1000; let all=[]; let from=0;
                  for(;;){
                    let q=supabase.from('facturas_sap').select('doc_num,doc_date,doc_total,doc_currency,card_name,pagado,empresa_id').order('id',{ascending:true}).range(from,from+PAGE-1);
                    if(eid&&eid!=='all') q=q.eq('empresa_id',eid);
                    const {data,error}=await q;
                    if(error||!data||!data.length) break;
                    all=all.concat(data);
                    if(data.length<PAGE||from>30000) break;
                    from+=PAGE;
                  }
                  const match=all.find(f=>String(f.doc_num).endsWith(dig));
                  if(match){setFacturaSearchMatch(match);setFacturaSearchErr('');}
                  else{setFacturaSearchErr(`No se encontró factura SAP terminada en "${dig}". Si es una factura nueva quizá aún no se sincronizó desde SAP — sincronizá SAP (SAP B1) y reintentá.`);setFacturaSearchMatch(null);}
                }}
                style={{background:`${C.blue}20`,color:C.blue,border:`1px solid ${C.blue}40`,borderRadius:8,padding:'9px 16px',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
                🔍 Buscar
              </button>
            </div>
            {facturaSearchMatch&&(
              <div style={{marginTop:8,background:'#10B98115',border:'1px solid #10B98140',borderRadius:8,padding:'10px 12px'}}>
                <div style={{fontSize:12,color:C.green,fontWeight:800}}>✅ Factura #{facturaSearchMatch.doc_num}</div>
                <div style={{fontSize:11,color:C.t3,marginTop:3}}>
                  {facturaSearchMatch.card_name} · {facturaSearchMatch.doc_date} · {facturaSearchMatch.doc_currency}{' '}
                  {Number(facturaSearchMatch.doc_total||0).toLocaleString('en-US',{minimumFractionDigits:2})}
                  {facturaSearchMatch.pagado&&<span style={{color:C.green,marginLeft:6}}>· Cobrada</span>}
                </div>
              </div>
            )}
            {facturaSearchErr&&<div style={{marginTop:6,fontSize:11,color:C.red}}>{facturaSearchErr}</div>}
          </div>
        )}

        {/* Tab: lista */}
        {facturaTab==='lista'&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
              <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase'}}>Facturas del cliente sin cobrar</div>
              <button onClick={()=>cargarFacturasCliente(modalFactura.opp)} style={{background:'transparent',border:'none',color:C.blue,fontSize:11,cursor:'pointer',fontWeight:700}}>↻ Recargar</button>
            </div>
            {loadingFacturasLista?(
              <div style={{textAlign:'center',padding:'20px 0',color:C.t4,fontSize:13}}>Cargando facturas...</div>
            ):facturasLista.length===0?(
              <div style={{textAlign:'center',padding:'20px 0',color:C.t4,fontSize:13}}>No se encontraron facturas de este cliente en RADAR. Corré una sincronización de SAP y reintentá.</div>
            ):(
              <div style={{maxHeight:220,overflowY:'auto',display:'flex',flexDirection:'column',gap:6}}>
                {facturasLista.map(f=>(
                  <div key={f.doc_num} onClick={()=>setFacturaSelLista(f)}
                    style={{padding:'9px 12px',borderRadius:8,border:`2px solid ${facturaSelLista?.doc_num===f.doc_num?C.blue:C.border}`,
                      background:facturaSelLista?.doc_num===f.doc_num?`${C.blue}15`:C.bg2,cursor:'pointer',transition:'all .12s'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{fontSize:12,fontWeight:700,color:C.t1}}>#{f.doc_num}
                        <span style={{marginLeft:8,fontSize:10,fontWeight:700,padding:'1px 7px',borderRadius:10,background:f.pagado?`${C.green}20`:`${C.orange}20`,color:f.pagado?C.green:C.orange}}>{f.pagado?'✓ Pagada':'⏳ Pendiente'}</span>
                      </span>
                      <span style={{fontSize:12,fontWeight:700,color:C.t1}}>{f.moneda||'$'} {Number(f.doc_total||0).toLocaleString('en-US',{minimumFractionDigits:2})}</span>
                    </div>
                    <div style={{fontSize:11,color:C.t4,marginTop:2}}>{(f.doc_date||'').split('T')[0]}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{marginTop:20,display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" size="sm" onClick={()=>setModalFactura(null)}>Cancelar</Btn>
          <Btn size="sm"
            disabled={savingFactura||(facturaTab==='digitos'?!facturaSearchMatch:!facturaSelLista)}
            onClick={async()=>{
              if(!modalFactura) return;
              const factura=facturaTab==='digitos'?facturaSearchMatch:facturaSelLista;
              if(!factura) return;
              setSavingFactura(true);
              const {opp,pendingEtapa}=modalFactura;
              // CANDADO: una factura pertenece a UNA sola oportunidad.
              // Sin esta revision se podia pegar la misma factura a varias
              // oportunidades; asi fue como la 100007889 de 5 STAR termino en
              // tres, y el cobro quedo colgado de la que no era.
              const {data:yaUsada}=await supabase.from('oportunidades')
                .select('id,titulo,sap_quotation_num,etapa')
                .eq('factura_sap_num',String(factura.doc_num)).neq('id',opp.id).limit(1);
              if(yaUsada&&yaUsada.length){
                const q=yaUsada[0];
                setSavingFactura(false);
                alert(`La factura ${factura.doc_num} ya esta vinculada a otra oportunidad:\n\n`+
                  `${q.sap_quotation_num?'Cotizacion '+q.sap_quotation_num+' \u2014 ':''}${q.titulo||''} (${q.etapa})\n\n`+
                  `Una factura pertenece a una sola oportunidad. Si esta es la correcta, primero quitale la factura a la otra.`);
                return;
              }
              await supabase.from('oportunidades').update({factura_sap_num:String(factura.doc_num)}).eq('id',opp.id);
              setOpps(os=>os.map(o=>o.id===opp.id?{...o,factura_sap_num:String(factura.doc_num)}:o));
              setSel(prev=>prev?.id===opp.id?{...prev,factura_sap_num:String(factura.doc_num)}:prev);
              setSavingFactura(false);
              setModalFactura(null);
              if(pendingEtapa==='cerrado_ganado'){
                const oppActualizada={...opp,factura_sap_num:String(factura.doc_num)};
                setModalGanado(oppActualizada);
                setFGanado({valor:oppActualizada.valor||'',metodo_pago:'Transferencia',notas:'',factura_4dig:''});
              } else {
                moveOpp(opp.id, pendingEtapa);
              }
            }}>
            {savingFactura?'Guardando...':modalFactura?.pendingEtapa==='facturado'?'→ Mover a Facturado':modalFactura?.pendingEtapa==='pagado'?'→ Mover a Pagado':'→ Continuar a Cerrado Ganado'}
          </Btn>
        </div>
      </Modal>

      {/* Modal — Editar oportunidad */}
      <Modal open={modalEditOpp} onClose={()=>setModalEditOpp(false)} title="✏️ Editar Oportunidad" width={500}>
        <Inp label="Título *" value={fEditOpp.titulo||''} onChange={v=>setFEditOpp(x=>({...x,titulo:v}))} placeholder="Título de la oportunidad"/>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Valor</div>
          <div style={{display:'flex',gap:6}}>
            <select value={fEditOpp.moneda||'USD'} onChange={e=>setFEditOpp(x=>({...x,moneda:e.target.value}))}
              style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 8px',color:C.t1,fontSize:12,fontWeight:700,outline:'none',cursor:'pointer',flexShrink:0}}>
              <option value="USD">USD $</option>
              <option value="HNL">HNL L</option>
              <option value="CRC">CRC ₡</option>
            </select>
            <input type="number" value={fEditOpp.valor||''} onChange={e=>setFEditOpp(x=>({...x,valor:e.target.value}))} placeholder="0"
              style={{flex:1,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',color:C.t1,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
          </div>
          {(fEditOpp.moneda&&fEditOpp.moneda!=='USD'&&fEditOpp.valor>0)&&(
            <div style={{fontSize:11,color:C.green,marginTop:4,fontWeight:600}}>
              ≈ ${convertirAUSD(fEditOpp.valor,fEditOpp.moneda).toLocaleString('en-US',{minimumFractionDigits:2})} USD · tasa {fEditOpp.moneda==='HNL'?`L${tasaHNL.toFixed(4)}`:tasaCRC.toFixed(2)}
            </div>
          )}
          {fEditOpp.moneda==='USD'&&fEditOpp.valor>0&&(
            <div style={{fontSize:10,color:C.t4,marginTop:3}}>Valor actual en DB: ${Number(sel?.valor||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div>
          )}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <Inp label="Fecha cierre estimada" type="date" value={fEditOpp.fecha_cierre||''} onChange={v=>setFEditOpp(x=>({...x,fecha_cierre:v}))}/>
          <Inp label="Tipo" value={fEditOpp.tipo||''} onChange={v=>setFEditOpp(x=>({...x,tipo:v}))} placeholder="Renta, Venta..."/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <Sel label="Línea de negocio" value={fEditOpp.linea_negocio||''} onChange={v=>setFEditOpp(x=>({...x,linea_negocio:v}))} options={[{v:'',l:'Sin especificar'},...ORG.lineasNegocio.map(l=>({v:l,l}))]}/>
          <Inp label="Próxima acción" value={fEditOpp.proxima_accion||''} onChange={v=>setFEditOpp(x=>({...x,proxima_accion:v}))} placeholder="Próximo paso..."/>
        </div>
        <Inp label="Notas" value={fEditOpp.notas||''} onChange={v=>setFEditOpp(x=>({...x,notas:v}))} rows={2} placeholder="observaciones..."/>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:4}}>
          <Btn variant="secondary" onClick={()=>setModalEditOpp(false)}>Cancelar</Btn>
          <Btn onClick={editarOpp} disabled={!fEditOpp.titulo||savingEditOpp}>{savingEditOpp?'Guardando...':'💾 Guardar cambios'}</Btn>
        </div>
      </Modal>

      {/* Modal — Nuevo Prospecto rápido */}
      <Modal open={!!modalProspecto} onClose={()=>setModalProspecto(false)} title="✨ Nuevo Prospecto" width={380}>
        <p style={{fontSize:13,color:C.t3,marginTop:0,marginBottom:16}}>
          Registrá el nombre del prospecto. Podés convertirlo a cliente cuando esté listo.
        </p>
        <ClienteBuscador
          label="Empresa / Nombre *"
          clientes={clientes}
          value={fProspecto.empresa}
          onSelect={c=>setFProspecto(x=>({...x,empresa:c.empresa}))}
          onClear={()=>setFProspecto(x=>({...x,empresa:''}))}
          onChange={v=>setFProspecto(x=>({...x,empresa:v}))}
          empresaActiva={empresaActiva}
        />
        <Inp label="Título / Descripción" value={fProspecto.titulo||''} onChange={v=>setFProspecto(x=>({...x,titulo:v}))} placeholder="ej: Renta de montacargas, Interés en flota..."/>
        <Inp label="Teléfono" value={fProspecto.telefono||''} onChange={v=>setFProspecto(x=>({...x,telefono:v}))} placeholder="+504 0000-0000"/>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Vendedor asignado</div>
          <select value={fProspecto.vendedor} onChange={e=>setFProspecto(x=>({...x,vendedor:e.target.value}))}
            style={{width:'100%',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',color:C.t1,fontSize:13,outline:'none',boxSizing:'border-box'}}>
            <option value="">— Sin asignar —</option>
            {vendedoresList.map(v=><option key={v} value={v} style={{background:C.bg2}}>{v}{v===usuario.nombre?' (yo)':''}</option>)}
          </select>
        </div>
        {errorProspecto&&<div style={{background:'#EF444415',border:'1px solid #EF444440',borderRadius:8,padding:'8px 12px',fontSize:12,color:C.red,marginTop:8}}>{errorProspecto}</div>}
        <div style={{marginTop:18,display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" onClick={()=>{setModalProspecto(false);setErrorProspecto('');}}>Cancelar</Btn>
          <Btn onClick={crearProspecto} disabled={!fProspecto.empresa||savingProspecto}>
            {savingProspecto?'Guardando...':'✨ Agregar al Pipeline'}
          </Btn>
        </div>
      </Modal>

      {/* Modal — Convertir prospecto a cliente */}
      <Modal open={!!modalConvertir} onClose={()=>setModalConvertir(null)} title="Convertir a Cliente" width={460}>
        <p style={{fontSize:13,color:C.t3,marginTop:0,marginBottom:12}}>
          Completá los datos para registrar como cliente formal en el CRM.
        </p>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <div style={{gridColumn:'1/-1'}}>
            <Inp label="Empresa / Razón social *" value={fConvertir.nombre} onChange={v=>setFConvertir(x=>({...x,nombre:v}))} placeholder="Nombre completo o razón social"/>
          </div>
          <Inp label="RTN *" value={fConvertir.rtn} onChange={v=>setFConvertir(x=>({...x,rtn:v}))} placeholder="0000-0000-000000"/>
          <Inp label="Teléfono *" value={fConvertir.telefono} onChange={v=>setFConvertir(x=>({...x,telefono:v}))} placeholder="+504 0000-0000"/>
          <Inp label="Contacto principal" value={fConvertir.contacto||''} onChange={v=>setFConvertir(x=>({...x,contacto:v}))} placeholder="Nombre del contacto"/>
          <Inp label="Email" type="email" value={fConvertir.email||''} onChange={v=>setFConvertir(x=>({...x,email:v}))} placeholder="correo@empresa.com"/>
          <Inp label="Ciudad" value={fConvertir.ciudad||''} onChange={v=>setFConvertir(x=>({...x,ciudad:v}))} placeholder="San Pedro Sula"/>
          <Inp label="Industria" value={fConvertir.industria||''} onChange={v=>setFConvertir(x=>({...x,industria:v}))} placeholder="Logística, Manufactura..."/>
        </div>
        <div style={{marginTop:18,display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" onClick={()=>setModalConvertir(null)}>Cancelar</Btn>
          <Btn onClick={convertirACliente} disabled={!fConvertir.nombre||!fConvertir.rtn||!fConvertir.telefono||savingConvertir}>
            {savingConvertir?'Guardando...':'👤 Crear Cliente'}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}

// ─── SEGUIMIENTO ──────────────────────────────────────────────────────────────
function Seguimiento({ usuario, clientes = [], empresaActiva }) {
  const [opps, setOpps]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel]         = useState(null);
  const [nota, setNota]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [filtroV, setFiltroV] = useState([]);   // multi-seleccion de vendedores ([] = todos)
  const [filtroUrg, setFiltroUrg] = useState('');
  const [modalVisita, setModalVisita] = useState(false);

  const cargar = async (eid) => {
    let q = supabase.from('oportunidades').select('*').not('etapa','in','(cerrado_ganado,cerrado_perdido,prospecto_invalido,cliente_frio)').order('ultimo_seguimiento',{ascending:true});
    if (eid) q=q.eq('empresa_id',eid);
    if (usuario.rol==='ventas') q=q.or(`vendedor.eq.${usuario.nombre},vendedor.is.null,vendedor.eq.`);
    const {data}=await q;
    setOpps(data||[]); setLoading(false);
  };
  useEffect(()=>{cargar();},[empresaActiva]);
  // Limpiar la nota al cambiar de oportunidad: si no, el comentario de una se
  // arrastra y se guarda en la siguiente de la misma etapa.
  useEffect(()=>{ setNota(''); },[sel?.id]);

  const vendedores = [...new Set(opps.map(o=>o.vendedor).filter(Boolean))];
  const base = !filtroV.length ? opps : opps.filter(o => filtroV.includes(o.vendedor) || (filtroV.includes('sin_asignar') && !o.vendedor));
  const urgentes  = base.filter(o=>dias(o.ultimo_seguimiento)>5);
  const pendientes= base.filter(o=>{ const d=dias(o.ultimo_seguimiento); return d>=2&&d<=5; });
  const alDia     = base.filter(o=>dias(o.ultimo_seguimiento)<2);

  const EC2 = {prospecto:'#64748B',contactado:'#0D9488',cotizacion:'#F59E0B',negociacion:'#EC4899',cerrado:'#0B7AB8',facturado:'#8B5CF6',pagado:'#06B6D4',cerrado_ganado:'#10B981',cerrado_perdido:'#EF4444',prospecto_invalido:'#94A3B8',cliente_frio:'#0EA5E9'};

  const marcar = async () => {
    if (!nota.trim()||!sel) return; setSaving(true);
    const hoy=new Date().toISOString().split('T')[0];
    const txt=nota.trim();
    await supabase.from('oportunidades').update({ultimo_seguimiento:hoy,notas:txt}).eq('id',sel.id);
    await supabase.from('opp_seguimientos').insert({opp_id:sel.id,vendedor:usuario.nombre,nota:txt}).then(()=>{},()=>{});
    supabase.from('actividades_programadas').insert({titulo:`Seguimiento — ${sel.titulo||sel.cliente||'oportunidad'}`,tipo:'llamada',fecha:hoy,vendedor:usuario.nombre,cliente_id:sel.cliente_id||null,opp_id:sel.id,notas:txt,completada:true, origen:'auto',empresa_id:sel.empresa_id||null}).then(()=>{},()=>{});
    setOpps(os=>os.map(o=>o.id===sel.id?{...o,ultimo_seguimiento:hoy,notas:txt}:o));
    setSel(prev=>({...prev,ultimo_seguimiento:hoy,notas:txt}));
    setNota(''); setSaving(false);
  };

  const OppRow = ({o}) => {
    const d=dias(o.ultimo_seguimiento);
    const urgTone = d>5?TONES.red:d>2?TONES.orange:TONES.green;
    const isSel = sel?.id===o.id;
    return (
      <div onClick={()=>setSel(o)} className={isSel?undefined:'radar-hrow'}
        style={{display:'grid',gridTemplateColumns:'22px minmax(0,2fr) 1.1fr 110px 90px 150px',gap:10,alignItems:'center',
          padding:'10px 16px',borderBottom:`1px solid ${C.bg3}`,cursor:'pointer',background:isSel?C.primaryTint:'transparent'}}>
        <span style={{width:9,height:9,borderRadius:'50%',background:urgTone.solid}}/>
        <span style={{minWidth:0}}>
          <span style={{display:'block',fontSize:13,fontWeight:800,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
            {o.titulo}{o.valor>0 && <span className="radar-num" style={{fontWeight:800,color:C.t2}}> — ${(o.valor).toLocaleString('en-US')}</span>}
          </span>
          <span style={{display:'block',fontSize:11,color:C.t3,marginTop:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{o.cliente}</span>
        </span>
        <span style={{display:'flex',alignItems:'center',gap:6,minWidth:0}}>
          {o.vendedor ? (<><AvatarChip name={o.vendedor}/><span style={{fontSize:11.5,fontWeight:700,color:C.t2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{o.vendedor.split(' ')[0]}</span></>)
            : <span style={{fontSize:11,color:C.orangeText,fontWeight:800}}>sin asignar</span>}
        </span>
        <span><StageChip label={o.etapa} color={EC2[o.etapa]||'#64748B'} fs={10}/></span>
        <span className="radar-num" style={{fontSize:12.5,fontWeight:800,textAlign:'right',color:urgTone.text}}>{d===0?'hoy':`${d} días`}</span>
        <span style={{display:'flex',justifyContent:'flex-end'}}>
          <button onClick={(e)=>{e.stopPropagation();setSel(o);}}
            style={{height:30,padding:'0 12px',borderRadius:8,border:'none',background:C.primary,color:'#fff',fontSize:11,fontWeight:800,cursor:'pointer'}}>
            Registrar contacto
          </button>
        </span>
      </div>
    );
  };

  const Seccion = ({titulo,tone,items}) => items.length===0?null:(
    <div>
      <div style={{padding:'9px 16px',fontSize:10.5,fontWeight:800,color:tone.text,textTransform:'uppercase',letterSpacing:'.05em',background:tone.tint,borderBottom:`1px solid ${tone.border}`,display:'flex',alignItems:'center',gap:7}}>
        <span style={{width:8,height:8,borderRadius:'50%',background:tone.solid,flexShrink:0}}/>
        {titulo} · <span className="radar-num">{items.length}</span>
        <span className="radar-num" style={{marginLeft:'auto',fontWeight:700}}>${items.reduce((x,o)=>x+(Number(o.valor)||0),0).toLocaleString('en-US')} en juego</span>
      </div>
      {items.map(o=><OppRow key={o.id} o={o}/>)}
    </div>
  );

  return (
    <div style={{flex:1,overflowY:'auto',background:C.bg1,padding:'18px 24px 28px'}}>
      <div style={{maxWidth:1240,margin:'0 auto'}}>
        <div style={{display:'flex',alignItems:'flex-start',gap:12,marginBottom:12}}>
          <div>
            <div style={{fontSize:19,fontWeight:800,letterSpacing:'-0.015em',color:C.t1}}>Pendientes de contacto</div>
            <div style={{fontSize:12,color:C.t4,marginTop:2}}>oportunidades abiertas ordenadas por urgencia — llamá, registrá, y sale de la lista</div>
          </div>
          <div style={{marginLeft:'auto'}}>
            <Btn onClick={()=>setModalVisita(true)} style={{display:'inline-flex',alignItems:'center',gap:6}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              Visita / llamada</Btn>
          </div>
        </div>

        {/* KPI cards que FILTRAN */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:12}}>
          {[['urgente','Urgente · 5+ días',urgentes,TONES.red],['pendiente','Pendiente · 2–5 días',pendientes,TONES.orange],['aldia','Al día',alDia,TONES.green]].map(([k,l,items,t])=>(
            <div key={k} onClick={()=>setFiltroUrg(v=>v===k?'':k)}
              style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:11,boxShadow:SH.xs,padding:'9px 13px',cursor:'pointer',
                outline:filtroUrg===k?`2px solid ${t.solid}`:'none',outlineOffset:1}}>
              <div style={{fontSize:9,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4}}>{l}</div>
              <div className="radar-num" style={{fontSize:19,fontWeight:800,letterSpacing:'-0.02em',color:t.text}}>{items.length}</div>
              <div className="radar-num" style={{fontSize:9.5,color:C.t4}}>${items.reduce((x,o)=>x+(Number(o.valor)||0),0).toLocaleString('en-US')} en juego</div>
            </div>
          ))}
        </div>

        <div style={{marginBottom:12}}>
          <VendChips vendedores={vendedores} value={filtroV} onChange={setFiltroV}
            counts={v => v ? opps.filter(o=>o.vendedor===v).length : opps.length}/>
        </div>

        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:SH.xs,overflow:'hidden'}}>
          {loading?<div style={{textAlign:'center',padding:40,color:C.t4}}>Cargando...</div>:<>
            {(!filtroUrg||filtroUrg==='urgente')   && <Seccion titulo="Urgente — más de 5 días sin contacto" tone={TONES.red}    items={urgentes}/>}
            {(!filtroUrg||filtroUrg==='pendiente') && <Seccion titulo="Pendiente — 2 a 5 días"               tone={TONES.orange} items={pendientes}/>}
            {(!filtroUrg||filtroUrg==='aldia')     && <Seccion titulo="Al día — contactados recientemente"   tone={TONES.green}  items={alDia}/>}
            {base.length===0&&<div style={{textAlign:'center',color:C.t4,padding:40}}>Sin oportunidades</div>}
          </>}
        </div>
        <div style={{fontSize:10.5,color:C.t4,marginTop:8}}>"Registrar contacto" abre el detalle a la derecha — al guardar la nota, la fila baja a "Al día" y la lista se limpia sola.</div>
      </div>

      {/* Detalle como DRAWER (antes era un panel fijo que casi siempre estaba vacío) */}
      {sel?(
        <div onClick={()=>setSel(null)} style={{position:'fixed',inset:0,background:'rgba(11,18,32,.32)',zIndex:1100,display:'flex',justifyContent:'flex-end'}}>
        <div onClick={e=>e.stopPropagation()} style={{width:'min(560px,94vw)',height:'100%',background:C.bg2,boxShadow:'-8px 0 30px rgba(16,24,40,.18)',overflowY:'auto',padding:26}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
            <div>
              <h2 style={{margin:'0 0 4px',fontSize:20,fontWeight:800,color:C.t1}}>{sel.titulo}</h2>
              <div style={{fontSize:13,color:C.t3}}>{sel.cliente}{sel.contacto?` · ${sel.contacto}`:''}</div>
              {sel.vendedor&&<div style={{display:'inline-flex',alignItems:'center',gap:6,marginTop:5}}><AvatarChip name={sel.vendedor} size={18}/><span style={{fontSize:12,fontWeight:500,color:C.t2}}>{sel.vendedor}</span></div>}
            </div>
            <div style={{textAlign:'right'}}>
              {sel.valor>0&&<div className="radar-num" style={{fontSize:24,fontWeight:700,letterSpacing:'-0.02em',lineHeight:1.05,color:C.t1,marginBottom:6}}>${(sel.valor).toLocaleString()}</div>}
              <StageChip label={sel.etapa} color={EC2[sel.etapa]||'#64748B'} fs={11.5}/>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:20}}>
            {[['Último contacto',fecha(sel.ultimo_seguimiento)],['Días sin contacto',`${dias(sel.ultimo_seguimiento)}d`],['Cierre estimado',fecha(sel.fecha_cierre)],['Cotización SAP',sel.sap_quotation_num?`#${sel.sap_quotation_num}`:'—'],['Factura SAP',sel.factura_sap_num?`#${sel.factura_sap_num}`:'—'],['Etapa',sel.etapa]].map(([l,v])=>(
              <div key={l} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:12,boxShadow:SH.xs}}>
                <div style={{fontSize:10.5,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em',marginBottom:3}}>{l}</div>
                <div className="radar-num" style={{fontSize:13,fontWeight:600,color:C.t1}}>{v||'—'}</div>
              </div>
            ))}
          </div>

          {sel.notas&&<div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:16,boxShadow:SH.xs}}>
            <div style={{fontSize:10.5,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em',marginBottom:6}}>Última nota registrada</div>
            <div style={{fontSize:13,color:C.t2,lineHeight:1.6}}>{sel.notas}</div>
          </div>}

          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:16,marginBottom:14,boxShadow:SH.sm}}>
            <div style={{fontSize:10.5,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Registrar seguimiento de hoy</div>
            <Inp value={nota} onChange={setNota} placeholder="¿Qué pasó? ¿Cuál es el próximo paso?" rows={3}/>
            <div style={{display:'flex',gap:8}}>
              <Btn onClick={marcar} disabled={!nota.trim()||saving} style={{flex:1,display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6}}>
                {!saving&&<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                {saving?'Guardando...':'Marcar contactado hoy'}</Btn>
              <Btn variant="secondary" onClick={()=>setSel(null)} title="Cerrar" style={{display:'inline-flex',alignItems:'center',padding:'8px 12px'}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></Btn>
            </div>
          </div>

          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {['cerrado_perdido'].map(e=>(
              <button key={e} onClick={async()=>{
                await supabase.from('oportunidades').update({etapa:e,fecha_cierre:new Date().toISOString().split('T')[0]}).eq('id',sel.id);
                setOpps(os=>os.filter(o=>o.id!==sel.id));
                setSel(null);
              }} className="radar-btn" style={{display:'inline-flex',alignItems:'center',gap:6,background:C.redTint,color:C.redText,border:`1px solid ${C.redBorder}`,borderRadius:R.md,padding:'8px 16px',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                Marcar perdido
              </button>
            ))}
            {(sel.etapa==='prospecto'||sel.etapa==='contactado')&&(
              <button disabled={!nota.trim()||saving} title={!nota.trim()?'Escribí el motivo arriba primero':''} onClick={async()=>{
                if(!nota.trim()) return; setSaving(true);
                const hoy=new Date().toISOString().split('T')[0];
                await supabase.from('oportunidades').update({etapa:'prospecto_invalido',fecha_cierre:hoy,ultimo_seguimiento:hoy,notas:`[PROSPECTO INVÁLIDO] ${nota.trim()}`}).eq('id',sel.id);
                setOpps(os=>os.filter(o=>o.id!==sel.id));
                setSel(null); setNota(''); setSaving(false);
              }} className="radar-btn" style={{display:'inline-flex',alignItems:'center',gap:6,background:C.bg3,color:C.t3,border:`1px solid ${C.border2}`,borderRadius:R.md,padding:'8px 16px',fontSize:13,fontWeight:600,cursor:nota.trim()?'pointer':'not-allowed',opacity:nota.trim()?1:.5}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.9" y1="4.9" x2="19.1" y2="19.1"/></svg>
                Prospecto inválido
              </button>
            )}
          </div>
        </div>
        </div>
      ):null}
      <ModalVisita open={modalVisita} onClose={()=>setModalVisita(false)} usuario={usuario} clientes={clientes} empresaActiva={empresaActiva}/>
    </div>
  );
}

// ─── CAMPAÑAS (clientes fríos / nurturing WhatsApp) ─────────────────────────────
// Hub de Campañas: Marketing (Zoho-style) + Cadencia de clientes fríos, en pestañas.
function CampaniasHub({ usuario, empresaActiva }) {
  const [t, setT] = useState('marketing');
  return (
    <div style={{flex:1,overflowY:'auto',background:C.bg1,display:'flex',flexDirection:'column'}}>
      <div style={{display:'flex',gap:8,padding:'16px 24px 0',flexWrap:'wrap'}}>
        <Seg value={t} onChange={setT} items={[['marketing','Marketing / Campañas',CI.bullhorn],['contactos','Contactos',CI.contacts],['wa_result','Resultados WhatsApp',CI.phone],['cadencia','Cadencia clientes fríos',CI.calendar]]}/>
      </div>
      {t==='marketing' ? <Marketing usuario={usuario} empresaActiva={empresaActiva}/>
       : t==='contactos' ? <MktContactos usuario={usuario}/>
       : t==='wa_result' ? <WhatsAppResultados usuario={usuario}/>
       : <Campanias usuario={usuario} empresaActiva={empresaActiva}/>}
    </div>
  );
}

// Dashboard de resultados del WhatsApp multi-vendedor (sistema de Omar → RADAR).
function WhatsAppResultados({ usuario }) {
  const [rows, setRows] = useState(null);
  const [vend, setVend] = useState([]);   // multi ([] = todos)
  const [tab, setTab] = useState('calientes');   // calientes | todos
  useEffect(()=>{ (async()=>{
    const all = []; let from = 0;
    for(;;){ const { data, error } = await supabase.from('contactos_whatsapp').select('*').order('fecha_respuesta',{ascending:false,nullsFirst:false}).range(from,from+999);
      if(error||!data||!data.length) break; all.push(...data); if(data.length<1000) break; from+=1000; }
    setRows(all);
  })(); },[]);
  if(rows===null) return <div style={{padding:40,textAlign:'center',color:C.t4}}>Cargando…</div>;
  if(rows.length===0) return (
    <div style={{padding:24,maxWidth:900,margin:'0 auto'}}>
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'28px',textAlign:'center',color:C.t3,boxShadow:SH.sm}}>
        <div style={{width:52,height:52,borderRadius:R.lg,background:C.bg3,border:`1px solid ${C.border}`,color:C.t4,display:'inline-flex',alignItems:'center',justifyContent:'center',marginBottom:10}}><CIcon d={CI.phone} size={24}/></div>
        <div style={{fontSize:15,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,marginBottom:6}}>Todavía no hay resultados de WhatsApp</div>
        <div style={{fontSize:13}}>Cuando los vendedores corran <b>subir_a_radar.js</b>, acá vas a ver quién recibió, quién respondió (leads calientes) y quién no tiene WhatsApp — por vendedor.</div>
      </div>
    </div>
  );
  const vendedores = [...new Set(rows.map(r=>r.vendedor).filter(Boolean))].sort();
  const fil = !vend.length ? rows : rows.filter(r=>vend.includes(r.vendedor));
  const respondio = r => r.respondio || r.estado==='respondio';
  const sinWa = r => r.estado==='sin_whatsapp';
  const calientes = fil.filter(respondio);
  const kpis = [
    ['Contactados', fil.length, C.blue],
    ['Respondieron', calientes.length, C.green],
    ['Sin WhatsApp', fil.filter(sinWa).length, C.t4],
    ['Tasa respuesta', fil.length?`${(calientes.length/fil.length*100).toFixed(1)}%`:'—', C.indigo],
  ];
  // Resumen por vendedor
  const porVend = vendedores.map(v=>{ const rs=rows.filter(r=>r.vendedor===v); const rp=rs.filter(respondio).length;
    return { v, env:rs.length, resp:rp, sin:rs.filter(sinWa).length, tasa:rs.length?(rp/rs.length*100).toFixed(1):'0' }; });
  const waLink = t => `https://wa.me/${String(t||'').replace(/\D/g,'')}`;
  const lista = tab==='calientes' ? calientes : fil;
  return (
    <div style={{padding:24,maxWidth:1100,margin:'0 auto',width:'100%',boxSizing:'border-box'}}>
      <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:14}}>
        {kpis.map(([l,v,c])=>(
          <div key={l} className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',flex:1,minWidth:130,boxShadow:SH.sm}}>
            <div style={{fontSize:11,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.07em',marginBottom:6}}>{l}</div>
            <div className="radar-num" style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,color:C.t1}}>{typeof v==='number'?v.toLocaleString('es-HN'):v}</div>
          </div>
        ))}
      </div>

      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12,alignItems:'center'}}>
        <MultiSelect label="Vendedores" value={vend} onChange={setVend} options={vendedores}/>
        <Seg value={tab} onChange={setTab} items={[['calientes',`Leads calientes (${calientes.length})`,CI.zap],['todos',`Todos (${fil.length})`]]}/>
      </div>

      {/* Resumen por vendedor */}
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,overflow:'hidden',marginBottom:16,boxShadow:SH.sm}}>
        <div style={{display:'grid',gridTemplateColumns:'1.5fr 1fr 1fr 1fr 1fr',padding:'9px 14px',borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>
          {['Vendedor','Enviados','Respondieron','Sin WhatsApp','Tasa'].map(h=><span key={h}>{h}</span>)}
        </div>
        {porVend.map(p=>(
          <div key={p.v} className="radar-num" style={{display:'grid',gridTemplateColumns:'1.5fr 1fr 1fr 1fr 1fr',padding:'8px 14px',borderBottom:`1px solid ${C.bg3}`,fontSize:12.5,alignItems:'center'}}>
            <span style={{fontWeight:600,color:C.t1}}>{p.v}</span>
            <span style={{color:C.t2}}>{p.env}</span>
            <span style={{color:C.greenText,fontWeight:600}}>{p.resp}</span>
            <span style={{color:C.t4}}>{p.sin}</span>
            <span style={{color:C.blue,fontWeight:600}}>{p.tasa}%</span>
          </div>
        ))}
      </div>

      {/* Lista de contactos */}
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,overflow:'hidden',boxShadow:SH.sm}}>
        <div style={{display:'grid',gridTemplateColumns:'1.3fr 1fr 1fr 2fr 90px',padding:'9px 14px',borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>
          {['Nombre','Teléfono','Vendedor','Respuesta / Estado',''].map(h=><span key={h}>{h}</span>)}
        </div>
        {lista.length===0 ? <div style={{padding:20,textAlign:'center',color:C.t4,fontSize:13}}>{tab==='calientes'?'Nadie ha respondido todavía.':'Sin contactos.'}</div>
        : lista.slice(0,500).map(r=>(
          <div key={r.id} style={{display:'grid',gridTemplateColumns:'1.3fr 1fr 1fr 2fr 90px',padding:'8px 14px',borderBottom:`1px solid ${C.bg3}`,fontSize:12,alignItems:'center'}}>
            <span style={{color:C.t1,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.nombre||'—'}</span>
            <span className="radar-num" style={{color:C.t3}}>{r.telefono}</span>
            <span style={{color:C.t3}}>{r.vendedor||'—'}</span>
            <span style={{color:respondio(r)?C.t1:C.t4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {respondio(r) ? `💬 "${r.texto_respuesta||'(respondió)'}"` : sinWa(r) ? '☎️ Sin WhatsApp' : '✓ Enviado'}
            </span>
            <a href={waLink(r.telefono)} target="_blank" rel="noreferrer" style={{fontSize:11,fontWeight:600,color:C.greenText,textDecoration:'none',textAlign:'center'}}>Abrir WA</a>
          </div>
        ))}
      </div>
      {lista.length>500 && <div style={{fontSize:11,color:C.t4,textAlign:'center',marginTop:8}}>Mostrando 500 de {lista.length}.</div>}
    </div>
  );
}

// Lista de contactos de marketing (la base de Zoho importada) — ver, buscar, limpiar.
function MktContactos({ usuario }) {
  const [rows, setRows] = useState([]); const [q, setQ] = useState(''); const [page, setPage] = useState(0);
  const [stats, setStats] = useState(null); const [loading, setLoading] = useState(true);
  const [fuente, setFuente] = useState('zoho');   // zoho | clientes | wa
  const [sincro, setSincro] = useState(false);
  const post = (body) => fetch('/api/campanias',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(r=>r.json());
  const load = async (qv=q, pv=page, fv=fuente) => { setLoading(true); const d = await post({action:'contactos_list', q:qv, page:pv, fuente:fv}); setRows(d.rows||[]); setLoading(false); };
  useEffect(()=>{ load(q,page,fuente); /* eslint-disable-next-line */ }, [page, fuente]);
  useEffect(()=>{ const t=setTimeout(()=>{ setPage(0); load(q,0,fuente); },400); return ()=>clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  useEffect(()=>{ if(fuente!=='clientes') post({action:'contactos_stats',fuente:fuente==='wa'?'wa':'zoho'}).then(setStats); /* eslint-disable-next-line */ }, [fuente]);
  const sincronizarWA = async () => { setSincro(true); const d = await post({action:'sync_wa_contactos'}); setSincro(false);
    if(d.ok){ toast(`Sincronizado: ${d.nuevos} contactos nuevos · ${d.enriquecidos||0} con empresa auto-completada desde SAP (de ${d.total_wa} chats).`); setPage(0); load(q,0,'wa'); post({action:'contactos_stats',fuente:'wa'}).then(setStats); }
    else toast('Error: '+(d.error||'no se pudo')); };
  const del = async (r) => { if(!(await confirmar('¿Borrar este contacto de la lista?', { ok:'Borrar', peligro:true }))) return; const d=await post({action:'contacto_del',id:r.id,tabla:r.tabla}); if(d.ok) setRows(rs=>rs.filter(x=>x.id!==r.id)); else toast(d.error||'No se pudo'); };
  const optout = async (r) => { await post({action:'contacto_optout',id:r.id,tabla:r.tabla}); setRows(rs=>rs.map(x=>x.id===r.id?{...x,optout:true}:x)); };
  const [sel, setSel] = useState(null);   // contacto en edición ({} = nuevo)
  const [rubros, setRubros] = useState([]);
  const [guardando, setGuardando] = useState(false);
  useEffect(()=>{ post({action:'rubros_list'}).then(d=>setRubros(d.rubros||[])); /* eslint-disable-next-line */ }, []);
  const guardar = async () => {
    if(!sel.email && !sel.telefono){ toast('Poné al menos email o teléfono.'); return; }
    setGuardando(true);
    const d = await post({action:'contacto_save', id:sel.id, tabla:sel.tabla, nombre:sel.nombre, empresa:sel.empresa, email:sel.email, telefono:sel.telefono, rubro:sel.rubro, servicio:sel.servicio,
      optin_email:sel.optin_email, optin_wa:sel.optin_wa, origen: sel.origen || (fuente==='wa'?'whatsapp':undefined)});
    if(d.ok){
      if(sel.id) setRows(rs=>rs.map(x=>x.id===sel.id?{...x,...sel}:x));
      else if(d.row) setRows(rs=>[d.row,...rs]);
      if(sel.rubro && !rubros.includes(sel.rubro)) setRubros(r=>[...r,sel.rubro].sort());
      setSel(null);
    } else toast('Error: '+(d.error||'no se pudo guardar'));
    setGuardando(false);
  };
  return (
    <div style={{padding:24,maxWidth:1100,margin:'0 auto',width:'100%',boxSizing:'border-box'}}>
      <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
        <Seg value={fuente} onChange={v=>{setFuente(v);setPage(0);}} items={[['zoho','Lista Email',CI.send],['clientes','Clientes',CI.box],['wa','WhatsApp',CI.phone]]}/>
        {fuente==='wa' && <button className="radar-btn" onClick={sincronizarWA} disabled={sincro} style={{marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:R.md,border:`1px solid ${C.greenBorder}`,background:C.greenTint,color:C.greenText,fontSize:12.5,fontWeight:600,cursor:sincro?'wait':'pointer'}}><CIcon d={CI.refresh}/>{sincro?'Sincronizando…':'Traer del WhatsApp'}</button>}
      </div>
      {fuente!=='clientes' && stats && (
        <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:14}}>
          {[['Total contactos',stats.total],['Con teléfono',stats.conTel],['Opt-in Email',stats.optinEmail],['Opt-in WhatsApp',stats.optinWa]].map(([l,v])=>(
            <div key={l} className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',flex:1,minWidth:130,boxShadow:SH.sm}}>
              <div style={{fontSize:11,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.07em',marginBottom:6}}>{l}</div>
              <div className="radar-num" style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,color:C.t1}}>{Number(v).toLocaleString('es-HN')}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{display:'flex',gap:10,marginBottom:12}}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar por nombre, email o teléfono…" style={{flex:1,boxSizing:'border-box',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}/>
        <button onClick={()=>setSel({})} style={{padding:'9px 16px',borderRadius:R.md,border:'none',background:C.blue,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap',boxShadow:SH.primary}}>+ Agregar contacto</button>
      </div>
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,overflow:'hidden',boxShadow:SH.sm}}>
        <div style={{display:'grid',gridTemplateColumns:'1.3fr 1.7fr 1fr 0.8fr 0.9fr 0.7fr 80px',gap:0,padding:'9px 14px',borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>
          {['Nombre','Email','Teléfono','Rubro','Servicio','Opt-in',''].map(h=><span key={h}>{h}</span>)}
        </div>
        {loading ? <div style={{padding:24,textAlign:'center',color:C.t4}}>Cargando…</div>
        : rows.length===0 ? <div style={{padding:24,textAlign:'center',color:C.t4}}>Sin contactos.</div>
        : rows.map(r=>(
          <div key={`${r.tabla}-${r.id}`} onClick={()=>setSel(r)} style={{display:'grid',gridTemplateColumns:'1.3fr 1.7fr 1fr 0.8fr 0.9fr 0.7fr 80px',gap:0,padding:'8px 14px',borderBottom:`1px solid ${C.bg3}`,fontSize:12,alignItems:'center',opacity:r.optout?0.5:1,cursor:'pointer'}}>
            <span style={{color:r.nombre?C.t1:C.t4,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.nombre||'(sin nombre — editar)'}{r.tabla==='clientes'&&<span style={{fontSize:9,color:C.blue,marginLeft:4}}>SAP</span>}</span>
            <span style={{color:C.t3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.email||'—'}</span>
            <span className="radar-num" style={{color:C.t3}}>{r.telefono||'—'}</span>
            <span style={{color:r.rubro?C.t2:C.t4}}>{r.rubro||'+ rubro'}</span>
            <span style={{color:r.servicio?C.t2:C.t4}}>{r.servicio||'—'}</span>
            <span style={{fontSize:10}}>{r.optout?<span style={{color:C.redText}}>opt-out</span>:<>{r.optin_email&&<span style={{color:C.greenText}}>✉</span>} {r.optin_wa&&<span style={{color:C.greenText}}>📱</span>}</>}</span>
            <span style={{display:'flex',gap:6,justifyContent:'flex-end'}}>
              {!r.optout&&<button onClick={e=>{e.stopPropagation();optout(r);}} title="Marcar opt-out" style={{background:'none',border:`1px solid ${C.border}`,borderRadius:R.sm,padding:'2px 5px',color:C.t4,fontSize:9,cursor:'pointer'}}>opt-out</button>}
              {r.tabla!=='clientes'&&<button onClick={e=>{e.stopPropagation();del(r);}} title="Borrar" style={{background:'none',border:'none',color:C.red,fontSize:14,cursor:'pointer'}}>×</button>}
            </span>
          </div>
        ))}
      </div>
      <div style={{display:'flex',gap:10,alignItems:'center',justifyContent:'center',marginTop:12}}>
        <button onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'6px 14px',color:C.t2,fontWeight:600,cursor:page===0?'default':'pointer',opacity:page===0?.5:1}}>◀</button>
        <span className="radar-num" style={{fontSize:12,color:C.t3}}>Página {page+1}</span>
        <button onClick={()=>setPage(p=>p+1)} disabled={rows.length<50} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'6px 14px',color:C.t2,fontWeight:600,cursor:rows.length<50?'default':'pointer',opacity:rows.length<50?.5:1}}>▶</button>
      </div>

      {sel && (
        <div onClick={()=>setSel(null)} style={{position:'fixed',inset:0,background:'rgba(15,23,42,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{width:'min(440px,100%)',background:C.bg2,borderRadius:R.lg,padding:22,maxHeight:'90vh',overflowY:'auto',boxShadow:SH.lg}}>
            <div style={{fontSize:17,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,marginBottom:14}}>{sel.id?'Editar contacto':'+ Agregar contacto'}</div>
            {[['nombre','Nombre del contacto'],...(sel.tabla!=='clientes'?[['empresa','Empresa']]:[]),['email','Email'],['telefono','Teléfono']].map(([k,l])=>(
              <div key={k} style={{marginBottom:10}}>
                <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>{l}</div>
                <input value={sel[k]||''} onChange={e=>setSel(s=>({...s,[k]:e.target.value}))} style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 11px',color:C.t1,fontSize:14,outline:'none'}}/>
              </div>
            ))}
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>Rubro / giro</div>
              <input list="rubros-dl" value={sel.rubro||''} onChange={e=>setSel(s=>({...s,rubro:e.target.value}))} placeholder="ej. Textil, Agro, Construcción…" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 11px',color:C.t1,fontSize:14,outline:'none'}}/>
              <datalist id="rubros-dl">{rubros.map(r=><option key={r} value={r}/>)}</datalist>
            </div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>Servicio que compra</div>
              <input list="serv-dl" value={sel.servicio||''} onChange={e=>setSel(s=>({...s,servicio:e.target.value}))} placeholder="ej. Renta, Repuestos, Servicio técnico…" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 11px',color:C.t1,fontSize:14,outline:'none'}}/>
              <datalist id="serv-dl">{ORG.lineasNegocio.map(s=><option key={s} value={s}/>)}</datalist>
            </div>
            <div style={{display:'flex',gap:16,margin:'12px 0'}}>
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,color:C.t2,cursor:'pointer'}}><input type="checkbox" checked={!!sel.optin_email} onChange={e=>setSel(s=>({...s,optin_email:e.target.checked}))}/> Opt-in Email</label>
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,color:C.t2,cursor:'pointer'}}><input type="checkbox" checked={!!sel.optin_wa} onChange={e=>setSel(s=>({...s,optin_wa:e.target.checked}))}/> Opt-in WhatsApp</label>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:8}}>
              <button onClick={()=>setSel(null)} style={{padding:'9px 16px',borderRadius:R.md,border:`1px solid ${C.border}`,background:C.bg2,color:C.t2,fontSize:13,fontWeight:600,cursor:'pointer'}}>Cancelar</button>
              <button onClick={guardar} disabled={guardando} style={{padding:'9px 16px',borderRadius:R.md,border:'none',background:C.blue,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',boxShadow:SH.primary}}>{guardando?'Guardando…':'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Campanias({ usuario, empresaActiva }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroV, setFiltroV] = useState([]);   // multi ([] = todos)
  const [filtroC, setFiltroC] = useState([]);   // multi ([] = todas)

  const CAD = { semanal:{l:'Semanal',c:'#EF4444',d:7}, quincenal:{l:'Quincenal',c:'#F59E0B',d:15}, mensual:{l:'Mensual',c:'#0EA5E9',d:30} };

  const cargar = async (eid) => {
    setLoading(true);
    let q = supabase.from('clientes_campania').select('*').eq('activo',true).order('proximo_envio',{ascending:true});
    if (eid && eid!=='all') q = q.or(`empresa_id.eq.${eid},empresa_id.is.null`);
    if (usuario.rol==='ventas') q = q.eq('vendedor',usuario.nombre);
    const { data } = await q;
    setRows(data||[]); setLoading(false);
  };
  useEffect(()=>{ cargar(); },[empresaActiva]);

  const vendedores = [...new Set(rows.map(r=>r.vendedor).filter(Boolean))].sort();
  const vis = rows.filter(r=>(!filtroV.length||filtroV.includes(r.vendedor))&&(!filtroC.length||filtroC.includes(r.cadencia)));

  const cambiarCadencia = async (r, cad) => {
    const prox = new Date(Date.now()+(CAD[cad]?.d||30)*864e5).toISOString().split('T')[0];
    await supabase.from('clientes_campania').update({cadencia:cad,proximo_envio:prox}).eq('id',r.id);
    setRows(rs=>rs.map(x=>x.id===r.id?{...x,cadencia:cad,proximo_envio:prox}:x));
  };
  const quitar = async (r) => {
    if(!(await confirmar(`¿Sacar a ${r.nombre} de las campañas?`, { ok:'Sacar' }))) return;
    await supabase.from('clientes_campania').update({activo:false}).eq('id',r.id);
    setRows(rs=>rs.filter(x=>x.id!==r.id));
  };

  const porCad = c => rows.filter(r=>r.cadencia===c).length;

  return (
    <div style={{flex:1,overflowY:'auto',padding:24,background:C.bg1}}>
      <div style={{maxWidth:1000,margin:'0 auto'}}>
        <div style={{marginBottom:18}}>
          <h1 style={{margin:'0 0 4px',fontSize:18,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>Campañas de WhatsApp</h1>
          <p style={{margin:0,color:C.t3,fontSize:13}}>Clientes fríos en seguimiento periódico. Los templates mensuales se envían según la cadencia.</p>
        </div>

        {/* KPIs por cadencia */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12,marginBottom:18}}>
          <Stat label="Total en campaña" value={rows.length} color={C.blue}/>
          {Object.entries(CAD).map(([k,v])=>(
            <div key={k} className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',boxShadow:SH.sm}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>{v.l}</div>
              <div className="radar-num" style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',color:C.t1,lineHeight:1.05}}>{porCad(k)}</div>
            </div>
          ))}
        </div>

        {/* Filtros */}
        <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
          <MultiSelect label="Cadencia" value={filtroC} onChange={setFiltroC} width={190}
            options={Object.entries(CAD).map(([k,v])=>({value:k, label:v.l}))}/>
          {usuario.rol!=='ventas'&&(
            <MultiSelect label="Vendedores" value={filtroV} onChange={setFiltroV}/>
          )}
        </div>

        {/* Tabla */}
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,overflow:'hidden',boxShadow:SH.sm}}>
          {loading?<div style={{textAlign:'center',color:C.t4,padding:40}}>Cargando...</div>
          :vis.length===0?<div style={{textAlign:'center',color:C.t4,padding:40}}>Sin clientes en campaña. Usá "❄️ Enfriar" o "📅 Agregar a campaña" en una oportunidad.</div>
          :<table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
              {['Cliente','Vendedor','Cadencia','Próximo envío','Nota',''].map(h=>(
                <th key={h} style={{padding:'9px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {vis.map(r=>{
                const venc = r.proximo_envio && r.proximo_envio <= new Date().toISOString().split('T')[0];
                return (
                  <tr key={r.id} style={{borderBottom:`1px solid ${C.bg3}`}}>
                    <td style={{padding:'10px 14px'}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.t1}}>{r.nombre||'—'}</div>
                      {r.telefono&&<div className="radar-num" style={{fontSize:11,color:C.t4}}>{r.telefono}</div>}
                    </td>
                    <td style={{padding:'10px 14px',fontSize:12,color:C.t3}}>{r.vendedor||'—'}</td>
                    <td style={{padding:'10px 14px'}}>
                      <select value={r.cadencia} onChange={e=>cambiarCadencia(r,e.target.value)}
                        style={{background:C.bg3,color:C.t2,border:`1px solid ${C.border}`,borderRadius:R.pill,padding:'3px 10px',fontSize:11,fontWeight:600,cursor:'pointer',outline:'none'}}>
                        {Object.entries(CAD).map(([k,v])=><option key={k} value={k} style={{color:C.t1}}>{v.l}</option>)}
                      </select>
                    </td>
                    <td className="radar-num" style={{padding:'10px 14px',fontSize:12,fontWeight:600,color:venc?C.redText:C.t2}}>{r.proximo_envio?fecha(r.proximo_envio):'—'}{venc&&<span style={{marginLeft:6,fontSize:9.5,fontWeight:700,color:C.redText,background:C.redTint,border:`1px solid ${C.redBorder}`,borderRadius:R.sm,padding:'1px 6px'}}>vencido</span>}</td>
                    <td style={{padding:'10px 14px',fontSize:11,color:C.t4,maxWidth:240}}>{r.notas||'—'}</td>
                    <td style={{padding:'10px 14px',textAlign:'right'}}>
                      <button onClick={()=>quitar(r)} style={{background:C.redTint,border:`1px solid ${C.redBorder}`,borderRadius:R.sm,padding:'4px 9px',color:C.redText,fontSize:11,fontWeight:600,cursor:'pointer'}}>Sacar</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>}
        </div>
      </div>
    </div>
  );
}

// ─── MARKETING / CAMPAÑAS (estilo Zoho Campaigns) ─────────────────────────────
function Marketing({ usuario, empresaActiva }) {
  const [camps, setCamps]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [rubros, setRubros] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [preview, setPreview] = useState(null);
  const [prevLoading, setPrevLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lista, setLista] = useState(null);          // contactos del segmento (para elegir a mano)
  const [selKeys, setSelKeys] = useState(new Set());  // claves marcadas
  const [manualSel, setManualSel] = useState(false);  // ¿el usuario tocó la selección?
  const [buscaDest, setBuscaDest] = useState('');
  const NV0 = { nombre:'', canal:'whatsapp', filtros:{ fuente:'wa_equipo', rubro:'', ciudad:'', segmento:'', vendedor:'', recencia_dias:'' }, wa_template:'', cuerpo:'', asunto:'' };
  const [nv, setNv] = useState(NV0);
  const esGer = ['admin','gerente_ventas'].includes(usuario.rol);

  const cargar = async () => { setLoading(true);
    const { data } = await supabase.from('campanias_mkt').select('*').order('created_at',{ascending:false}).limit(100);
    setCamps(data||[]); setLoading(false);
  };
  useEffect(()=>{ cargar(); },[]);
  useEffect(()=>{ (async()=>{
    const { data:cl } = await supabase.from('clientes').select('rubro,ciudad').neq('estado','inactivo').limit(8000);
    setRubros([...new Set((cl||[]).map(c=>c.rubro).filter(Boolean))].sort());
    setCiudades([...new Set((cl||[]).map(c=>c.ciudad).filter(Boolean))].sort());
    const { data:us } = await supabase.from('radar_usuarios').select('nombre').eq('rol','ventas').eq('activo',true);
    setVendedores((us||[]).map(u=>u.nombre).sort());
  })(); },[]);

  const verPreview = async () => { setPrevLoading(true); setPreview(null); setLista(null); setSelKeys(new Set()); setManualSel(false); setBuscaDest('');
    try { const r = await fetch('/api/campanias',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'preview',canal:nv.canal,filtros:{...nv.filtros,seleccion:undefined,empresa_id:empresaActiva}})});
      const d = await r.json(); setPreview(d);
      if (d.ok && Array.isArray(d.lista)) { setLista(d.lista); setSelKeys(new Set(d.lista.map(x=>x.key))); }
    } catch(e){ setPreview({ok:false,error:e.message}); }
    setPrevLoading(false);
  };
  const toggleKey = k => { setManualSel(true); setSelKeys(s=>{ const n=new Set(s); n.has(k)?n.delete(k):n.add(k); return n; }); };
  const marcarTodos = v => { setManualSel(true); setSelKeys(v ? new Set((lista||[]).map(x=>x.key)) : new Set()); };
  const guardar = async () => {
    if(!nv.nombre){ toast('Ponle un nombre a la campaña.'); return; }
    if(manualSel && selKeys.size===0){ toast('No hay nadie seleccionado. Marcá al menos un contacto o quitá la selección manual.'); return; }
    setBusy(true);
    const aud = {...nv.filtros, empresa_id:empresaActiva};
    if(manualSel) aud.seleccion = [...selKeys]; // mandar SOLO a los marcados
    const row = { nombre:nv.nombre, canal:nv.canal, audiencia:aud, asunto:nv.asunto||null, cuerpo:nv.cuerpo||null, wa_template:nv.wa_template||null, estado:'borrador', creado_por:usuario.nombre };
    const { data } = await supabase.from('campanias_mkt').insert(row).select().single();
    if(data){ setCamps(c=>[data,...c]); setShowNew(false); setNv(NV0); setPreview(null); setLista(null); setSelKeys(new Set()); setManualSel(false); }
    setBusy(false);
  };
  const enviar = async (c) => {
    if(!(await confirmar(`¿Enviar "${c.nombre}" por ${c.canal.toUpperCase()} a la audiencia? Esto manda mensajes reales.`, { ok:'📤 Enviar campaña' }))) return;
    setBusy(true);
    try { const r = await fetch('/api/campanias',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'enviar',campania_id:c.id})});
      const d = await r.json();
      if(d.bloqueado) toast('Envíos DESACTIVADOS (freno envios_activos). Prendelos para enviar.');
      else if(d.ok) { toast(`Enviada: ${d.enviados} ok, ${d.fallidos} fallidos de ${d.total}.`); cargar(); }
      else toast('Error: '+(d.error||'no se pudo enviar'));
    } catch(e){ toast('Error: '+e.message); }
    setBusy(false);
  };

  const EST = { borrador:{l:'Borrador',c:C.t4}, programada:{l:'Programada',c:C.orange}, enviando:{l:'Enviando…',c:C.blue}, enviada:{l:'Enviada',c:C.green} };

  return (
    <div style={{flex:1,overflowY:'auto',padding:24,background:C.bg1}}>
      <div style={{maxWidth:1000,margin:'0 auto'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16,flexWrap:'wrap',gap:10}}>
          <div>
            <h1 style={{margin:'0 0 4px',fontSize:18,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>Marketing / Campañas</h1>
            <p style={{margin:0,color:C.t3,fontSize:13}}>Segmentá tu cartera y enviá campañas por WhatsApp o Email. Respeta el freno de envíos y el opt-out.</p>
          </div>
          {esGer && <button onClick={()=>{setShowNew(true);setPreview(null);}} style={{padding:'9px 16px',borderRadius:R.md,border:'none',background:C.blue,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',boxShadow:SH.primary}}>+ Nueva campaña</button>}
        </div>

        {loading ? <div style={{textAlign:'center',color:C.t4,padding:40}}>Cargando…</div>
        : camps.length===0 ? <div style={{textAlign:'center',color:C.t4,padding:40,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,boxShadow:SH.sm}}>Sin campañas. Creá la primera.</div>
        : (
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {camps.map(c=>{
              const est = EST[c.estado]||{l:c.estado,c:C.t4};
              const estTint = {borrador:{bg:C.bg3,tx:C.t3,bd:C.border},programada:{bg:C.orangeTint,tx:C.orangeText,bd:C.orangeBorder},enviando:{bg:C.primaryTint,tx:C.blue,bd:C.primaryBorder},enviada:{bg:C.greenTint,tx:C.greenText,bd:C.greenBorder}}[c.estado]||{bg:C.bg3,tx:C.t3,bd:C.border};
              return (
              <div key={c.id} className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'12px 16px',display:'flex',alignItems:'center',gap:14,flexWrap:'wrap',boxShadow:SH.sm}}>
                <span style={{width:28,height:28,borderRadius:R.sm,background:c.canal==='email'?C.infoTint:C.greenTint,border:`1px solid ${c.canal==='email'?C.infoBorder:C.greenBorder}`,color:c.canal==='email'?C.infoText:C.greenText,display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><CIcon d={c.canal==='email'?CI.send:CANAL_ICON.whatsapp} size={15}/></span>
                <div style={{flex:1,minWidth:180}}>
                  <div style={{fontSize:14,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>{c.nombre}</div>
                  <div className="radar-num" style={{fontSize:11,color:C.t4}}>{c.canal} · {fecha(c.created_at)}{c.estado==='enviada'?` · ${c.enviados} enviados / ${c.fallidos} fallidos`:''}</div>
                </div>
                <span style={{fontSize:10.5,fontWeight:600,color:estTint.tx,background:estTint.bg,border:`1px solid ${estTint.bd}`,borderRadius:R.pill,padding:'2px 8px'}}>{est.l}</span>
                {esGer && c.estado!=='enviada' && c.estado!=='enviando' && <button onClick={()=>enviar(c)} disabled={busy} style={{padding:'6px 14px',borderRadius:R.md,border:'none',background:C.green,color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:SH.sm}}>Enviar</button>}
              </div>);
            })}
          </div>
        )}
      </div>

      {/* Nueva campaña */}
      {showNew && (
        <div onClick={()=>setShowNew(false)} style={{position:'fixed',inset:0,background:'rgba(15,23,42,.5)',display:'flex',justifyContent:'center',alignItems:'flex-start',zIndex:1000,padding:'40px 20px',overflowY:'auto'}}>
          <div onClick={e=>e.stopPropagation()} style={{width:'min(560px,100%)',background:C.bg2,borderRadius:R.lg,padding:22,boxShadow:SH.lg}}>
            <div style={{fontSize:17,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,marginBottom:10}}>+ Nueva campaña</div>
            {/* Guía del flujo: nadie debería tener que adivinar cómo se manda una campaña */}
            <div style={{background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:11.5,color:C.t2,lineHeight:1.7}}>
              <b style={{color:C.primary}}>Cómo funciona:</b> ① Elegí el canal (WhatsApp o Email). ② Armá el segmento con los filtros
              (fuente, rubro, ciudad, o "no compran hace X días" para reactivar dormidos). ③ Tocá <b>Ver audiencia</b> para
              revisar exactamente a quién le va a llegar — podés destildar gente. ④ Para WhatsApp pegá el nombre de tu
              <b> plantilla aprobada por Meta</b> y el texto del cuerpo. ⑤ Guardá: la campaña queda en borrador y se envía
              desde la lista con el botón Enviar (hay un freno global — nada sale por accidente).
            </div>
            <Field label="Nombre"><input value={nv.nombre} onChange={e=>setNv(s=>({...s,nombre:e.target.value}))} style={inpS}/></Field>
            <Field label="Canal">
              <div style={{display:'flex',gap:8}}>
                {[['whatsapp','WhatsApp',CANAL_ICON.whatsapp],['email','Email',CI.send]].map(([v,l,ic])=>(
                  <button key={v} onClick={()=>setNv(s=>({...s,canal:v,filtros:{...s.filtros,fuente:v==='email'?'zoho':'wa_equipo'}}))} style={{flex:1,display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px',borderRadius:R.md,border:`1px solid ${nv.canal===v?C.primaryBorder:C.border}`,background:nv.canal===v?C.primaryTint:C.bg1,color:nv.canal===v?C.blue:C.t3,fontSize:13,fontWeight:nv.canal===v?600:500,cursor:'pointer'}}><CIcon d={ic}/>{l}</button>
                ))}
              </div>
            </Field>

            <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',margin:'14px 0 8px'}}>Audiencia (segmento)</div>
            <Field label="Fuente de contactos">
              <div style={{display:'flex',gap:6}}>
                {(nv.canal==='email'
                  ? [['zoho','Lista Zoho'],['clientes','Clientes SAP'],['ambas','Ambas']]
                  : [['wa_equipo','WhatsApp vendedores'],['clientes','Clientes SAP'],['ambas_wa','Ambos']]
                ).map(([v,l])=>(
                  <button key={v} onClick={()=>setNv(s=>({...s,filtros:{...s.filtros,fuente:v}}))} style={{flex:1,padding:'8px',borderRadius:R.md,border:`1px solid ${nv.filtros.fuente===v?C.primaryBorder:C.border}`,background:nv.filtros.fuente===v?C.primaryTint:C.bg1,color:nv.filtros.fuente===v?C.blue:C.t3,fontSize:11.5,fontWeight:nv.filtros.fuente===v?600:500,cursor:'pointer'}}>{l}</button>
                ))}
              </div>
            </Field>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:10}}>
              <SelF label="Rubro / giro" v={nv.filtros.rubro} set={x=>setNv(s=>({...s,filtros:{...s.filtros,rubro:x}}))} opts={rubros}/>
              <SelF label="Ciudad" v={nv.filtros.ciudad} set={x=>setNv(s=>({...s,filtros:{...s.filtros,ciudad:x}}))} opts={ciudades}/>
              <SelF label="Segmento" v={nv.filtros.segmento} set={x=>setNv(s=>({...s,filtros:{...s.filtros,segmento:x}}))} opts={['A','B','C','D']}/>
              <SelF label="Vendedor" v={nv.filtros.vendedor} set={x=>setNv(s=>({...s,filtros:{...s.filtros,vendedor:x}}))} opts={vendedores}/>
            </div>
            <Field label="No compran hace + de (días) — opcional"><input type="number" value={nv.filtros.recencia_dias} onChange={e=>setNv(s=>({...s,filtros:{...s.filtros,recencia_dias:e.target.value}}))} placeholder="ej. 60" style={inpS}/></Field>
            <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12.5,color:C.t2,margin:'2px 0 6px',cursor:'pointer'}}>
              <input type="checkbox" checked={!!nv.filtros.incluir_sin_optin} onChange={e=>setNv(s=>({...s,filtros:{...s.filtros,incluir_sin_optin:e.target.checked}}))}/>
              <span><b>Campaña de opt-in</b> — incluir a quienes aún NO aceptaron (para mandar la 1ª oferta con botón "Sí, quiero ofertas"). Si lo dejás desmarcado, solo va a quienes ya dieron opt-in.</span>
            </label>
            <button className="radar-btn" onClick={verPreview} disabled={prevLoading} style={{display:'inline-flex',alignItems:'center',gap:6,margin:'4px 0 6px',background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:R.md,padding:'7px 14px',color:C.blue,fontSize:12,fontWeight:600,cursor:'pointer'}}><CIcon d={<><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></>}/>{prevLoading?'Calculando…':'Ver audiencia'}</button>
            {preview && (preview.ok
              ? <div style={{fontSize:12,color:C.t2,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 12px',marginBottom:6}}><b>{preview.total}</b> {nv.canal==='whatsapp'?'móviles (con WhatsApp)':'contactos'} · {preview.conEmail} con email{nv.canal==='whatsapp'&&preview.fijosExcluidos>0?<div style={{color:C.orangeText,marginTop:4}}>☎️ Se excluyeron <b>{preview.fijosExcluidos}</b> teléfonos fijos (no tienen WhatsApp) de {preview.totalSegmento} del segmento.</div>:null}{preview.truncado?<div style={{color:C.orangeText,marginTop:4}}>Mostrando los primeros 1000 para elegir. Si no marcás nada, se manda a los {preview.total}.</div>:null}</div>
              : <div style={{fontSize:12,color:C.redText,marginBottom:6}}>{preview.error}</div>)}

            {/* Selección manual de destinatarios */}
            {lista && lista.length>0 && (
              <div style={{border:`1px solid ${C.border}`,borderRadius:R.md,marginBottom:8,overflow:'hidden'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',background:C.bg1,borderBottom:`1px solid ${C.border}`,flexWrap:'wrap'}}>
                  <b style={{fontSize:12,color:C.t2}}>Elegí a quién mandar</b>
                  <span style={{fontSize:11,fontWeight:600,color:manualSel?C.blue:C.t4,background:manualSel?C.primaryTint:'transparent',border:manualSel?`1px solid ${C.primaryBorder}`:'1px solid transparent',borderRadius:R.pill,padding:'1px 7px'}}>{selKeys.size} seleccionados</span>
                  <div style={{marginLeft:'auto',display:'flex',gap:6}}>
                    <button onClick={()=>marcarTodos(true)} style={{fontSize:11,fontWeight:600,color:C.blue,background:'transparent',border:`1px solid ${C.border}`,borderRadius:R.sm,padding:'3px 8px',cursor:'pointer'}}>Todos</button>
                    <button onClick={()=>marcarTodos(false)} style={{fontSize:11,fontWeight:600,color:C.t3,background:'transparent',border:`1px solid ${C.border}`,borderRadius:R.sm,padding:'3px 8px',cursor:'pointer'}}>Ninguno</button>
                  </div>
                </div>
                <input value={buscaDest} onChange={e=>setBuscaDest(e.target.value)} placeholder="Buscar por nombre, teléfono o email…" style={{...inpS,borderRadius:0,border:'none',borderBottom:`1px solid ${C.border}`}}/>
                <div style={{maxHeight:240,overflowY:'auto'}}>
                  {lista.filter(x=>{ const q=buscaDest.toLowerCase().trim(); return !q || (x.nombre||'').toLowerCase().includes(q) || (x.tel||'').includes(q) || (x.email||'').toLowerCase().includes(q); })
                    .slice(0,400).map(x=>(
                    <label key={x.key} style={{display:'flex',alignItems:'center',gap:9,padding:'7px 10px',borderBottom:`1px solid ${C.bg3}`,cursor:'pointer',fontSize:12.5}}>
                      <input type="checkbox" checked={selKeys.has(x.key)} onChange={()=>toggleKey(x.key)}/>
                      <span style={{flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:C.t1,fontWeight:600}}>{x.nombre||'(sin nombre)'}</span>
                      {nv.canal!=='email'&&<span title={x.movil?'móvil':'fijo (sin WhatsApp)'} style={{fontSize:11}}>{x.movil?'📱':'☎️'}</span>}
                      <span className="radar-num" style={{color:C.t4,fontSize:11}}>{nv.canal==='email'?(x.email||'sin email'):(x.tel||'sin tel')}</span>
                      <span style={{fontSize:10,fontWeight:600,color:x.fuente==='SAP'?C.greenText:C.blue}}>{x.fuente}</span>
                    </label>
                  ))}
                </div>
                <div style={{fontSize:11,color:C.t4,padding:'6px 10px',background:C.bg1,borderTop:`1px solid ${C.border}`}}>{manualSel?`Se mandará SOLO a los ${selKeys.size} marcados.`:`Si no desmarcás a nadie, se manda a todo el segmento (${preview?.total}).`}</div>
              </div>
            )}

            <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',margin:'14px 0 8px'}}>Contenido</div>
            {nv.canal==='whatsapp' ? <>
              <Field label="Plantilla WhatsApp aprobada (Meta)"><input value={nv.wa_template} onChange={e=>setNv(s=>({...s,wa_template:e.target.value}))} placeholder="nombre_de_plantilla" style={inpS}/></Field>
              <Field label="Texto del cuerpo ({{1}} de la plantilla)"><textarea value={nv.cuerpo} onChange={e=>setNv(s=>({...s,cuerpo:e.target.value}))} style={{...inpS,minHeight:70,resize:'vertical'}}/></Field>
              <div style={{fontSize:11,color:C.t4,marginTop:-4}}>Las campañas promocionales por WhatsApp requieren plantilla aprobada por Meta + opt-in del cliente.</div>
            </> : <>
              <Field label="Asunto"><input value={nv.asunto} onChange={e=>setNv(s=>({...s,asunto:e.target.value}))} style={inpS}/></Field>
              <Field label="Cuerpo (HTML o texto)"><textarea value={nv.cuerpo} onChange={e=>setNv(s=>({...s,cuerpo:e.target.value}))} style={{...inpS,minHeight:100,resize:'vertical'}}/></Field>
              <div style={{fontSize:11,color:C.t4,marginTop:-4}}>El email necesita RESEND_API_KEY + dominio verificado para enviar.</div>
            </>}

            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
              <button onClick={()=>setShowNew(false)} style={{padding:'9px 16px',borderRadius:R.md,border:`1px solid ${C.border}`,background:C.bg2,color:C.t2,fontSize:13,fontWeight:600,cursor:'pointer'}}>Cancelar</button>
              <button onClick={guardar} disabled={busy} style={{padding:'9px 16px',borderRadius:R.md,border:'none',background:C.blue,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',boxShadow:SH.primary}}>Guardar borrador</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const inpS = { width:'100%', boxSizing:'border-box', background:C.bg1, border:`1px solid ${C.border}`, borderRadius:R.md, padding:'9px 11px', color:C.t1, fontSize:14, outline:'none' };
const Field = ({label,children}) => <div style={{marginBottom:10}}><div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>{label}</div>{children}</div>;
const SelF = ({label,v,set,opts}) => <div><div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>{label}</div><select value={v} onChange={e=>set(e.target.value)} style={{...inpS,fontSize:13}}><option value="">Todos</option>{opts.map(o=><option key={o} value={o}>{o}</option>)}</select></div>;

// ─── TICKETS DE SOPORTE (reemplazo de Zoho) ───────────────────────────────────
const TK_ESTADO = {
  nuevo:      { l:'Nuevo',       c:C.red,    tint:C.redTint,     border:C.redBorder,     text:C.redText },
  en_proceso: { l:'En proceso',  c:C.blue,   tint:C.primaryTint, border:C.primaryBorder, text:C.blue },
  resuelto:   { l:'Resuelto',    c:C.green,  tint:C.greenTint,   border:C.greenBorder,   text:C.greenText },
  cerrado:    { l:'Cerrado',     c:C.t4,     tint:C.bg3,         border:C.border,        text:C.t3 },
};
const TK_TIPO = {
  problema:     { l:'Falla de equipo',       ic:CI.alert,     tone:'red' },
  mantenimiento:{ l:'Mantenimiento',         ic:CI.wrench,    tone:'orange' },
  repuestos:    { l:'Repuestos',             ic:CI.clipboard, tone:'neutral' },
  comercial:    { l:'Renta o compra',        ic:CI.cash,      tone:'green' },
  facturacion:  { l:'Facturación',           ic:CI.cash,      tone:'primary' },
  atencion:     { l:'Atención al cliente',   ic:CI.users,     tone:'info' },
  consulta:     { l:'Otra solicitud',        ic:CI.chat,      tone:'info' },
};
const TK_URG  = {
  alta:  { l:'Alta',  c:C.red,    tint:C.redTint,    border:C.redBorder,    text:C.redText },
  media: { l:'Media', c:C.orange, tint:C.orangeTint, border:C.orangeBorder, text:C.orangeText },
  baja:  { l:'Baja',  c:C.green,  tint:C.greenTint,  border:C.greenBorder,  text:C.greenText },
};
const TK_UBIC = ['San Pedro Sula','Choloma','Tegucigalpa','Choluteca','Comayagua'];
const eqLabel = e => [e.codigo, e.marca, e.modelo].filter(Boolean).join(' · ') || e.codigo || '';
// Comprime una imagen del input file a un dataURL JPEG (máx 1280px) para subirla liviana.
const comprimir = file => new Promise(res => { const fr=new FileReader(); fr.onload=()=>{ const img=new Image(); img.onload=()=>{ let max=1280,w=img.width,h=img.height; if(w>h&&w>max){h=Math.round(h*max/w);w=max;} else if(h>=w&&h>max){w=Math.round(w*max/h);h=max;} const c=document.createElement('canvas'); c.width=w;c.height=h; c.getContext('2d').drawImage(img,0,0,w,h); res(c.toDataURL('image/jpeg',0.7)); }; img.src=fr.result; }; fr.readAsDataURL(file); });

function Tickets({ usuario, equipos = [] }) {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [fEstado, setFEstado] = useState('abiertos');
  const [q, setQ]         = useState('');
  const [sel, setSel]     = useState(null);
  const [nota, setNota]   = useState('');
  const [vendedores, setVendedores] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const NV0 = { tipo:'problema', urgencia:'media', ubicacion:'', cliente:'', cliente_id:'', contacto_nombre:'', contacto_telefono:'+504 ', equipo:'', descripcion:'', asignado_a:'' };
  const [nv, setNv] = useState(NV0);
  const [busca, setBusca] = useState('');
  const [resCli, setResCli] = useState([]);
  const [fotosNv, setFotosNv] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const esVentas = usuario.rol === 'ventas';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const cargar = async () => {
    setLoading(true);
    let q1 = supabase.from('tickets').select('*').order('created_at',{ascending:false}).limit(500);
    if (esVentas) q1 = q1.or(`asignado_a.eq.${usuario.nombre},asignado_a.is.null`);
    const { data } = await q1;
    setRows(data||[]); setLoading(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ }, []);
  useEffect(()=>{ (async()=>{ const {data}=await supabase.from('radar_usuarios').select('nombre').eq('activo',true); setVendedores((data||[]).map(d=>d.nombre)); })(); }, []);

  const abiertos = rows.filter(r=>['nuevo','en_proceso'].includes(r.estado));
  const cont = e => rows.filter(r=>r.estado===e).length;
  const vis = rows.filter(r => {
    const okE = fEstado==='abiertos' ? ['nuevo','en_proceso'].includes(r.estado)
              : fEstado==='todos' ? true : r.estado===fEstado;
    const t = q.toLowerCase();
    const okQ = !t || [r.folio,r.cliente,r.equipo,r.descripcion,r.contacto_nombre].some(x=>(x||'').toLowerCase().includes(t));
    return okE && okQ;
  });

  const patch = async (id, upd) => {
    await supabase.from('tickets').update({...upd, updated_at:new Date().toISOString()}).eq('id',id);
    setRows(rs=>rs.map(r=>r.id===id?{...r,...upd}:r));
    setSel(s=>s&&s.id===id?{...s,...upd}:s);
  };
  const cambiarEstado = (t, estado) => patch(t.id, { estado, ...(estado==='resuelto'?{resuelto_at:new Date().toISOString()}:{}) });
  const agregarNota = async (t) => {
    if(!nota.trim()) return;
    const notas = [...(t.notas||[]), { fecha:new Date().toISOString(), texto:nota.trim(), autor:usuario.nombre }];
    await patch(t.id, { notas }); setNota('');
  };
  // Buscar cliente (autocompleta contacto/teléfono/ubicación)
  useEffect(()=>{ if(!showNew||busca.trim().length<2){ setResCli([]); return; }
    let vivo=true; const t=setTimeout(async()=>{
      const {data}=await supabase.from('clientes').select('id,empresa,contacto,telefono,ciudad').neq('estado','inactivo')
        .or(`empresa.ilike.%${busca}%,contacto.ilike.%${busca}%`).order('empresa').limit(8);
      if(vivo) setResCli(data||[]);
    },250); return ()=>{ vivo=false; clearTimeout(t); };
  }, [busca, showNew]);
  const elegirCliente = c => {
    const ubic = TK_UBIC.find(u=>(c.ciudad||'').toLowerCase().includes(u.toLowerCase())) || '';
    setNv(s=>({...s, cliente:c.empresa||'', cliente_id:String(c.id), contacto_nombre:c.contacto||s.contacto_nombre, contacto_telefono:c.telefono||s.contacto_telefono, ubicacion:ubic||s.ubicacion}));
    setBusca(''); setResCli([]);
  };
  // Equipos sugeridos: primero los del cliente elegido, luego el resto
  const eqDelCliente = nv.cliente_id ? equipos.filter(e=>String(e.asignado_a||'')===nv.cliente_id || (e.cliente_actual||'').toLowerCase()===(nv.cliente||'').toLowerCase()) : [];
  const eqSugeridos = [...eqDelCliente, ...equipos.filter(e=>!eqDelCliente.includes(e))].map(eqLabel).filter(Boolean);

  const addFotos = async (files) => {
    const arr = [...files].slice(0, 4 - fotosNv.length);
    for (const f of arr) { try { const d = await comprimir(f); setFotosNv(p=>p.length<4?[...p,d]:p); } catch {} }
  };
  const crearInterno = async () => {
    if(!nv.cliente||!nv.descripcion||!nv.ubicacion){ toast('Ubicación, cliente y descripción son obligatorios.'); return; }
    setGuardando(true);
    try {
      const r = await fetch('/api/ticket', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ ...nv, fotos:fotosNv, origen:'interno' }) });
      const d = await r.json();
      if(d&&d.ok&&d.ticket){ setRows(rs=>[d.ticket,...rs]); setShowNew(false); setNv(NV0); setFotosNv([]); setBusca(''); }
      else toast(d?.error||'No se pudo crear el ticket.');
    } catch(e){ toast('Error: '+e.message); }
    setGuardando(false);
  };

  // La org viaja en el link para que el formulario salga con la marca del
  // cliente y el ticket caiga en SU org (white-label).
  const orgQS = usuario?.org_id ? `org=${usuario.org_id}` : '';
  const linkPublico  = `${origin}/api/ticket${orgQS ? `?${orgQS}` : ''}`;
  const linkEncuesta = t => `${origin}/encuesta?t=${t.id}${orgQS ? `&${orgQS}` : ''}`;
  const waLink = (tel, texto) => `https://wa.me/${String(tel||'').replace(/[^0-9]/g,'')}?text=${encodeURIComponent(texto)}`;
  const copiar = (txt, msg) => { navigator.clipboard?.writeText(txt); toast(msg||'Copiado: '+txt); };

  // tone: objeto {text,tint,border,c} (tripleta DS) o un color plano (cae a chip neutro con dot).
  const Badge = ({tone,c,children}) => {
    const t = tone || (c ? { text:C.t3, tint:C.bg3, border:C.border, c } : { text:C.t3, tint:C.bg3, border:C.border, c:C.t4 });
    return (
      <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:10.5,fontWeight:600,color:t.text,background:t.tint,border:`1px solid ${t.border}`,borderRadius:R.pill,padding:'2px 8px',whiteSpace:'nowrap'}}>
        <span style={{width:6,height:6,borderRadius:'50%',background:t.c||t.text,flexShrink:0}}/>{children}
      </span>
    );
  };

  return (
    <div style={{flex:1,overflowY:'auto',padding:24,background:C.bg1}}>
      <div style={{maxWidth:1100,margin:'0 auto'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16,flexWrap:'wrap',gap:10}}>
          <div>
            <h1 style={{margin:'0 0 4px',fontSize:18,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,display:'flex',alignItems:'center',gap:9}}><KIcon d={CI.ticket} tone={TONES.primary}/>Tickets de soporte</h1>
            <p style={{margin:0,color:C.t3,fontSize:13}}>Solicitudes que entran por el formulario público de servicio. Al resolver, se les manda la encuesta.</p>
          </div>
          {!esVentas && <Btn onClick={()=>setShowNew(true)} style={{display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={CI.plus}/>Ticket interno</Btn>}
        </div>

        {/* Link público para clientes */}
        {!esVentas && (
          <div style={{background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:R.lg,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
            <span style={{fontSize:13,color:C.t2,display:'inline-flex',alignItems:'center',gap:7}}><CIcon d={CI.broadcast} size={15} style={{color:C.blue}}/><span><b>Link público para clientes</b> (mandalo por WhatsApp o ponelo en un QR):</span></span>
            <code className="radar-num" style={{fontSize:12,background:C.bg2,border:`1px solid ${C.border}`,padding:'4px 8px',borderRadius:R.sm,color:C.t1}}>{linkPublico}</code>
            <button onClick={()=>copiar(linkPublico,'Link del formulario copiado')} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:R.md,border:`1px solid ${C.border}`,background:C.bg2,color:C.t2,fontSize:12,fontWeight:600,cursor:'pointer'}}><CIcon d={CI.copy}/>Copiar link</button>
            <a href={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(linkPublico)}`} target="_blank" rel="noreferrer" style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12,color:C.blue,fontWeight:600,textDecoration:'none'}}><CIcon d={CI.qr}/>Ver QR</a>
            {/* Encuesta de servicio: el mismo patron pero para medir satisfaccion.
                Cada ticket resuelto ya manda la suya; este es el link generico. */}
            <span style={{flexBasis:'100%',height:0}}/>
            <span style={{fontSize:13,color:C.t2,display:'inline-flex',alignItems:'center',gap:7}}><CIcon d={CI.checkGoal||CI.send} size={15} style={{color:C.green}}/><span><b>Encuesta de servicio al cliente</b>:</span></span>
            <code className="radar-num" style={{fontSize:12,background:C.bg2,border:`1px solid ${C.border}`,padding:'4px 8px',borderRadius:R.sm,color:C.t1}}>{`${origin}/api/encuesta${orgQS?`?${orgQS}`:''}`}</code>
            <button onClick={()=>copiar(`${origin}/api/encuesta${orgQS?`?${orgQS}`:''}`,'Link de la encuesta copiado')} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:R.md,border:`1px solid ${C.border}`,background:C.bg2,color:C.t2,fontSize:12,fontWeight:600,cursor:'pointer'}}><CIcon d={CI.copy}/>Copiar link</button>
            <a href={`${origin}/api/encuesta${orgQS?`?${orgQS}`:''}`} target="_blank" rel="noreferrer" style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12,color:C.blue,fontWeight:600,textDecoration:'none'}}>Abrir ↗</a>
          </div>
        )}

        {/* KPIs */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12,marginBottom:16}}>
          {[['Abiertos',abiertos.length,CI.inbox,'primary'],['Nuevos',cont('nuevo'),CI.alert,'red'],['En proceso',cont('en_proceso'),CI.clock,'orange'],['Resueltos',cont('resuelto'),KI.check,'green']].map(([l,v,ic,tone])=>(
            <KpiCard key={l} label={l} value={v} icon={ic} tone={tone}/>
          ))}
        </div>

        {/* Filtros */}
        <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
          <div style={{display:'flex',gap:2,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2,flexWrap:'wrap'}}>
            {[['abiertos','Abiertos'],['nuevo','Nuevos'],['en_proceso','En proceso'],['resuelto','Resueltos'],['todos','Todos']].map(([k,l])=>(
              <button key={k} onClick={()=>setFEstado(k)} style={{height:28,padding:'0 14px',borderRadius:R.sm,border:'none',background:fEstado===k?C.bg2:'transparent',color:fEstado===k?C.t1:C.t3,fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:fEstado===k?SH.sm:'none'}}>{l}</button>
            ))}
          </div>
          <input className="radar-input" value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar folio, cliente, equipo…" style={{marginLeft:'auto',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 12px',color:C.t1,fontSize:13,minWidth:220,outline:'none'}}/>
        </div>

        {/* Lista */}
        {loading ? <div style={{textAlign:'center',color:C.t4,padding:40}}>Cargando…</div>
        : vis.length===0 ? <div style={{textAlign:'center',color:C.t4,padding:40,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,boxShadow:SH.sm}}>Sin tickets {fEstado!=='todos'?'en este filtro':''}.</div>
        : (
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {vis.map(t=>(
              <div key={t.id} onClick={()=>setSel(t)} className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderLeft:`4px solid ${TK_ESTADO[t.estado]?.c||C.t4}`,borderRadius:R.lg,padding:'12px 16px',cursor:'pointer',display:'flex',alignItems:'center',gap:14,flexWrap:'wrap',boxShadow:SH.sm}}>
                <KIcon d={TK_TIPO[t.tipo]?.ic||CI.ticket} tone={TONES[TK_TIPO[t.tipo]?.tone]||TONES.neutral}/>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                    <span style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>{t.cliente}</span>
                    <span className="radar-num" style={{fontSize:11,color:C.t4}}>{t.folio}</span>
                    {t.equipo && <span style={{fontSize:11,color:C.t3}}>· {t.equipo}</span>}
                  </div>
                  <div style={{fontSize:12,color:C.t3,marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:560}}>{t.descripcion}</div>
                </div>
                <Badge tone={TK_URG[t.urgencia]}>{TK_URG[t.urgencia]?.l||t.urgencia}</Badge>
                <Badge tone={TK_ESTADO[t.estado]}>{TK_ESTADO[t.estado]?.l||t.estado}</Badge>
                <span className="radar-num" style={{fontSize:11,color:C.t4,minWidth:70,textAlign:'right'}}>{fecha(t.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detalle */}
      {sel && (
        <div onClick={()=>setSel(null)} style={{position:'fixed',inset:0,background:'rgba(15,23,42,.5)',display:'flex',justifyContent:'flex-end',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{width:'min(520px,100%)',height:'100%',background:C.bg1,overflowY:'auto',boxShadow:'-8px 0 32px rgba(0,0,0,.2)'}}>
            <div style={{padding:'18px 22px',borderBottom:`1px solid ${C.border}`,background:C.bg2,position:'sticky',top:0,zIndex:2}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div className="radar-num" style={{fontSize:12,color:C.t4,fontWeight:600,display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={TK_TIPO[sel.tipo]?.ic||CI.ticket} style={{color:TONES[TK_TIPO[sel.tipo]?.tone]?.text||C.t4}}/>{sel.folio} · {TK_TIPO[sel.tipo]?.l}</div>
                <button onClick={()=>setSel(null)} style={{background:'none',border:'none',fontSize:22,color:C.t4,cursor:'pointer'}}>×</button>
              </div>
              <div style={{fontSize:18,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,marginTop:4}}>{sel.cliente}</div>
              <div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
                <Badge tone={TK_ESTADO[sel.estado]}>{TK_ESTADO[sel.estado]?.l}</Badge>
                <Badge tone={TK_URG[sel.urgencia]}>Urgencia {TK_URG[sel.urgencia]?.l}</Badge>
                {sel.ubicacion && <Badge tone={{text:C.lob.servicio.text,tint:C.lob.servicio.bg,border:C.lob.servicio.border,c:C.lob.servicio.text}}>{sel.ubicacion}</Badge>}
                <Badge c={C.t4}>{sel.origen==='publico'?'Cliente':'Interno'}</Badge>
              </div>
            </div>
            <div style={{padding:'18px 22px'}}>
              {/* Contacto */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:14,marginBottom:14,boxShadow:SH.sm}}>
                <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>Contacto</div>
                <div style={{fontSize:14,color:C.t1,fontWeight:600}}>{sel.contacto_nombre||'—'}</div>
                {sel.contacto_telefono && <div className="radar-num" style={{fontSize:13,color:C.t3,marginTop:2}}>{sel.contacto_telefono} {' '}
                  <a href={waLink(sel.contacto_telefono,`Hola ${sel.contacto_nombre||''}, te escribimos de ${ORG.nombre} por tu reporte ${sel.folio}.`)} target="_blank" rel="noreferrer" style={{color:C.greenText,fontWeight:600,textDecoration:'none'}}>· WhatsApp ↗</a></div>}
                {sel.equipo && <div style={{fontSize:13,color:C.t3,marginTop:6}}><b style={{color:C.t2}}>Equipo:</b> {sel.equipo}</div>}
              </div>
              {/* Descripción */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:14,marginBottom:14,boxShadow:SH.sm}}>
                <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>Lo que reportó</div>
                <div style={{fontSize:14,color:C.t1,lineHeight:1.5,whiteSpace:'pre-wrap'}}>{sel.descripcion}</div>
                {(sel.fotos||[]).length>0 && (
                  <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:10}}>
                    {(sel.fotos||[]).map((u,i)=>(
                      <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt="" style={{width:72,height:72,objectFit:'cover',borderRadius:R.md,border:`1px solid ${C.border}`}}/></a>
                    ))}
                  </div>
                )}
                <div className="radar-num" style={{fontSize:11,color:C.t4,marginTop:8}}>Recibido {fecha(sel.created_at)}</div>
              </div>
              {/* Estado + asignación */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:14,marginBottom:14,boxShadow:SH.sm}}>
                <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>Gestión</div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:10}}>
                  {Object.entries(TK_ESTADO).map(([k,v])=>(
                    <button key={k} onClick={()=>cambiarEstado(sel,k)} disabled={esVentas&&k==='cerrado'} style={{padding:'6px 12px',borderRadius:R.md,border:`1px solid ${sel.estado===k?v.border:C.border}`,background:sel.estado===k?v.tint:C.bg2,color:sel.estado===k?v.text:C.t3,fontSize:12,fontWeight:600,cursor:'pointer'}}>{v.l}</button>
                  ))}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:12,color:C.t3}}>Asignado a:</span>
                  <select className="radar-input" value={sel.asignado_a||''} onChange={e=>patch(sel.id,{asignado_a:e.target.value||null})} disabled={esVentas} style={{flex:1,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'7px 10px',color:C.t1,fontSize:13}}>
                    <option value="">— sin asignar —</option>
                    {vendedores.map(v=><option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              </div>
              {/* Encuesta (si resuelto/cerrado) */}
              {['resuelto','cerrado'].includes(sel.estado) && (
                <div style={{background:C.greenTint,border:`1px solid ${C.greenBorder}`,borderRadius:R.lg,padding:14,marginBottom:14}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.t1,marginBottom:6,display:'flex',alignItems:'center',gap:7}}><CIcon d={CI.smile} size={15} style={{color:C.greenText}}/>Encuesta de satisfacción</div>
                  <div style={{fontSize:12,color:C.t3,marginBottom:10}}>Mandale al cliente la encuesta. Su respuesta alimenta el bono de cuenta clave.</div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    <a href={waLink(sel.contacto_telefono,`Buen dia ${sel.contacto_nombre||''}, cerramos su solicitud ${sel.folio}. Nos ayuda mucho si califica la atencion (1 minuto): ${linkEncuesta(sel)}`)} target="_blank" rel="noreferrer" style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 14px',borderRadius:R.md,background:C.green,color:'#fff',fontSize:12,fontWeight:600,textDecoration:'none',boxShadow:SH.sm}}><CIcon d={CI.send}/>Enviar por WhatsApp</a>
                    <button onClick={()=>copiar(linkEncuesta(sel),'Link de encuesta copiado')} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 14px',borderRadius:R.md,border:`1px solid ${C.border}`,background:C.bg2,color:C.t2,fontSize:12,fontWeight:600,cursor:'pointer'}}><CIcon d={CI.copy}/>Copiar link</button>
                  </div>
                </div>
              )}
              {/* Notas */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:14,boxShadow:SH.sm}}>
                <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>Notas internas</div>
                {(sel.notas||[]).length===0 && <div style={{fontSize:12,color:C.t4,marginBottom:8}}>Sin notas todavía.</div>}
                {(sel.notas||[]).map((n,i)=>(
                  <div key={i} style={{borderLeft:`2px solid ${C.border2}`,paddingLeft:10,marginBottom:8}}>
                    <div style={{fontSize:13,color:C.t1}}>{n.texto}</div>
                    <div className="radar-num" style={{fontSize:10,color:C.t4}}>{n.autor} · {fecha(n.fecha)}</div>
                  </div>
                ))}
                <div style={{display:'flex',gap:8,marginTop:8}}>
                  <input className="radar-input" value={nota} onChange={e=>setNota(e.target.value)} onKeyDown={e=>e.key==='Enter'&&agregarNota(sel)} placeholder="Agregar nota…" style={{flex:1,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 10px',color:C.t1,fontSize:13,outline:'none'}}/>
                  <Btn onClick={()=>agregarNota(sel)}>Agregar</Btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Nuevo ticket interno */}
      {showNew && (
        <div onClick={()=>setShowNew(false)} style={{position:'fixed',inset:0,background:'rgba(15,23,42,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{width:'min(460px,100%)',background:C.bg2,borderRadius:R.lg,padding:22,maxHeight:'90vh',overflowY:'auto',boxShadow:SH.lg}}>
            <div style={{fontSize:17,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,marginBottom:4}}>+ Ticket interno</div>
            <div style={{fontSize:11,color:C.t4,marginBottom:14}}>Empresa: <b style={{color:C.t2}}>{ORG.nombre}</b></div>

            {/* Buscar cliente → autocompleta */}
            <div style={{marginBottom:10,position:'relative'}}>
              <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4,display:'flex',alignItems:'center',gap:6}}><CIcon d={CI.search} size={12}/>Buscar cliente (autocompleta contacto y teléfono)</div>
              <input className="radar-input" value={busca} onChange={e=>setBusca(e.target.value)} placeholder="Escribí el nombre del cliente…" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 11px',color:C.t1,fontSize:14,outline:'none'}}/>
              {resCli.length>0 && (
                <div style={{position:'absolute',top:'100%',left:0,right:0,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,marginTop:4,zIndex:5,maxHeight:200,overflowY:'auto',boxShadow:SH.lg}}>
                  {resCli.map(c=>(
                    <div key={c.id} onClick={()=>elegirCliente(c)} style={{padding:'8px 11px',cursor:'pointer',borderBottom:`1px solid ${C.bg3}`}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.t1}}>{c.empresa}</div>
                      <div style={{fontSize:11,color:C.t4}}>{[c.contacto,c.telefono,c.ciudad].filter(Boolean).join(' · ')||'sin contacto'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {[['cliente','Cliente / Razón social'],['contacto_nombre','Contacto'],['contacto_telefono','Teléfono']].map(([k,l])=>(
              <div key={k} style={{marginBottom:10}}>
                <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>{l}</div>
                <input className="radar-input" value={nv[k]} onChange={e=>setNv(s=>({...s,[k]:e.target.value}))} style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 11px',color:C.t1,fontSize:14,outline:'none'}}/>
              </div>
            ))}

            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>Ubicación</div>
              <select className="radar-input" value={nv.ubicacion} onChange={e=>setNv(s=>({...s,ubicacion:e.target.value}))} style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 11px',color:C.t1,fontSize:13}}>
                <option value="">Seleccioná…</option>{TK_UBIC.map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </div>

            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>Equipo (elegí o escribí uno)</div>
              <input className="radar-input" list="tk-eqlist" value={nv.equipo} onChange={e=>setNv(s=>({...s,equipo:e.target.value}))} placeholder="Equipo afectado…" autoComplete="off" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 11px',color:C.t1,fontSize:14,outline:'none'}}/>
              <datalist id="tk-eqlist">{eqSugeridos.slice(0,300).map((e,i)=><option key={i} value={e}/>)}</datalist>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:10}}>
              {[['tipo',TK_TIPO],['urgencia',TK_URG]].map(([f,opts])=>(
                <div key={f} style={{gridColumn:f==='tipo'?'1 / 3':'auto'}}>
                  <div style={{fontSize:11,color:C.t3,fontWeight:600,marginBottom:4,textTransform:'uppercase',letterSpacing:'.07em'}}>{f}</div>
                  <select className="radar-input" value={nv[f]} onChange={e=>setNv(s=>({...s,[f]:e.target.value}))} style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 11px',color:C.t1,fontSize:13}}>
                    {Object.entries(opts).map(([k,v])=><option key={k} value={k}>{v.l}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>Asignar a (opcional)</div>
              <select className="radar-input" value={nv.asignado_a} onChange={e=>setNv(s=>({...s,asignado_a:e.target.value}))} style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 11px',color:C.t1,fontSize:13}}>
                <option value="">— sin asignar —</option>{vendedores.map(v=><option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>Fotos (opcional)</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                {fotosNv.map((d,i)=>(
                  <div key={i} style={{position:'relative'}}>
                    <img src={d} alt="" style={{width:60,height:60,objectFit:'cover',borderRadius:R.md,border:`1px solid ${C.border}`}}/>
                    <button onClick={()=>setFotosNv(p=>p.filter((_,j)=>j!==i))} style={{position:'absolute',top:-6,right:-6,width:18,height:18,borderRadius:'50%',border:'none',background:C.red,color:'#fff',fontSize:11,cursor:'pointer',lineHeight:1}}>×</button>
                  </div>
                ))}
                {fotosNv.length<4 && (
                  <label style={{width:60,height:60,border:`1.5px dashed ${C.border2}`,borderRadius:R.md,display:'flex',alignItems:'center',justifyContent:'center',color:C.t4,fontSize:24,cursor:'pointer'}}>+
                    <input type="file" accept="image/*" multiple style={{display:'none'}} onChange={e=>{addFotos(e.target.files); e.target.value='';}}/>
                  </label>
                )}
              </div>
            </div>

            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>Descripción</div>
              <textarea className="radar-input" value={nv.descripcion} onChange={e=>setNv(s=>({...s,descripcion:e.target.value}))} style={{width:'100%',boxSizing:'border-box',minHeight:80,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 11px',color:C.t1,fontSize:14,outline:'none',resize:'vertical'}}/>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <Btn variant="secondary" onClick={()=>setShowNew(false)}>Cancelar</Btn>
              <Btn onClick={crearInterno} disabled={guardando}>{guardando?'Creando…':'Crear ticket'}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MANTENIMIENTO ────────────────────────────────────────────────────────────
function Mantenimiento({ equipos }) {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [filtroEq, setFiltroEq]   = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [f, setF] = useState({ equipo_id:'', tipo:'', descripcion:'', tecnico:'', mano_obra:'', costo_repuestos:'', taller:'', fecha:new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);

  const cargar = async () => {
    setLoading(true);
    let q=supabase.from('mantenimientos').select('*,equipos(codigo,modelo,tipo_equipo)').order('fecha',{ascending:false}).limit(200);
    if (filtroEq) q=q.eq('equipo_id',filtroEq);
    if (filtroTipo) q=q.eq('tipo',filtroTipo);
    const {data}=await q; setHistorial(data||[]); setLoading(false);
  };
  useEffect(()=>{cargar();},[filtroEq,filtroTipo]);

  const guardar = async () => {
    if (!f.equipo_id||!f.tipo) return; setSaving(true);
    const total=(parseFloat(f.mano_obra)||0)+(parseFloat(f.costo_repuestos)||0);
    const { error } = await supabase.from('mantenimientos').insert({equipo_id:f.equipo_id,tipo:f.tipo,descripcion:f.descripcion,tecnico:f.tecnico,mano_obra:parseFloat(f.mano_obra)||0,costo_repuestos:parseFloat(f.costo_repuestos)||0,total,taller:f.taller,fecha:f.fecha});
    if (error) { toast('No se pudo registrar el mantenimiento: '+error.message); setSaving(false); return; }
    await supabase.from('actividades').insert({tipo:'gasto',titulo:`Mantenimiento registrado`,descripcion:`${equipos.find(e=>e.id===f.equipo_id)?.codigo} — ${f.tipo}`,fecha_actividad:new Date().toISOString()});
    setSaving(false); setModalNuevo(false); setF({equipo_id:'',tipo:'',descripcion:'',tecnico:'',mano_obra:'',costo_repuestos:'',taller:'',fecha:new Date().toISOString().split('T')[0]}); cargar();
  };

  const enTaller = equipos.filter(e=>e.estado==='mantenimiento'||e.estado==='reparacion');
  // Corte de mes por string ISO (evita mezclar hora local con fechas UTC).
  const mesIso = new Date().toISOString().slice(0,8)+'01';
  const totalHistorial = historial.reduce((s,g)=>s+(g.total||0),0);
  const totalMes = historial.filter(g=>g.fecha&&String(g.fecha).slice(0,10)>=mesIso).reduce((s,g)=>s+(g.total||0),0);
  const totalMO  = historial.reduce((s,g)=>s+(g.mano_obra||0),0);
  const totalRep = historial.reduce((s,g)=>s+(g.costo_repuestos||0),0);
  const TIPOS_MANT = ['Mantenimiento preventivo','Reparación','Repuesto','Revisión','Overhaul','Otro'];

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* Toolbar — flota sobre gris, sin franja blanca */}
      <div style={{padding:'12px 16px 4px',display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <SearchSel label="" value={filtroEq} onChange={setFiltroEq} placeholder="Filtrar por equipo..."
          options={[{v:'',l:'Todos los equipos'},...equipos.map(e=>({v:e.id,l:`${e.codigo} — ${e.modelo}`}))]}/>
        <select className="radar-input" value={filtroTipo} onChange={e=>setFiltroTipo(e.target.value)}
          style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'0 12px',height:34,color:C.t1,fontSize:13,boxShadow:SH.xs,cursor:'pointer'}}>
          <option value="">Todos los tipos</option>
          {TIPOS_MANT.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
        <div style={{marginLeft:'auto'}}>
          <Btn onClick={()=>setModalNuevo(true)} style={{display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>}/>Registrar Gasto</Btn>
        </div>
      </div>

      <div style={{flex:1,overflowY:'auto',padding:'12px 20px 20px'}}>
        {/* KPIs estilo Stripe */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12,marginBottom:22}}>
          <KpiCard label="En taller ahora"    value={enTaller.length}  sub="mantenimiento + reparación" icon={KI.tool}   tone="orange"/>
          <KpiCard label="Gasto este mes"     value={fmt(totalMes)}    sub="registros del mes"          icon={KI.dollar} tone="red"/>
          <KpiCard label="Total mano de obra" value={fmt(totalMO)}     sub="acumulado en historial"     icon={KI.users}  tone="orange"/>
          <KpiCard label="Total repuestos"    value={fmt(totalRep)}    sub="acumulado en historial"     icon={KI.box}    tone="red"/>
        </div>

        {/* La grilla de "equipos en taller" se quito: duplicaba las columnas
            Mantenimiento y Por Reparar del Kanban de Flota. Aca vive la PLATA
            (gastos), el ESTADO de cada equipo se ve en Flota. El KPI de arriba
            deja el link para ir al Kanban. */}

        {/* Historial */}
        <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span>Historial de gastos · {historial.length} registros</span>
          {totalHistorial>0&&<span className="radar-num" style={{color:C.redText,fontSize:13,fontWeight:700}}>Total {fmt(totalHistorial)}</span>}
        </div>
        {loading?<div style={{textAlign:'center',color:C.t4,padding:40}}>Cargando...</div>
        :historial.length===0?<div style={{textAlign:'center',color:C.t4,padding:60,fontSize:14}}>Sin registros{filtroEq||filtroTipo?' con ese filtro':' aún'}</div>
        :historial.map(g=>(
          <div key={g.id} className="radar-hrow" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'12px 16px',marginBottom:8,boxShadow:SH.xs}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
              <div style={{display:'flex',gap:10,minWidth:0,flex:1}}>
                <EquipIcon e={g.equipos} size={34} icon={16}/>
                <div style={{minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3,flexWrap:'wrap'}}>
                    <span style={{fontSize:14,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>{g.equipos?.codigo||'—'}</span>
                    <span style={{background:C.orangeTint,color:C.orangeText,border:`1px solid ${C.orangeBorder}`,borderRadius:R.pill,padding:'2px 8px',fontSize:10.5,fontWeight:600}}>{g.tipo}</span>
                  </div>
                  {g.descripcion&&<div style={{fontSize:12,color:C.t3,marginBottom:2}}>{g.descripcion}</div>}
                  <div style={{fontSize:11,color:C.t4,display:'flex',gap:12,flexWrap:'wrap'}}>
                    <span>{fecha(g.fecha)}</span>
                    {g.tecnico&&<span style={{display:'inline-flex',alignItems:'center',gap:4}}><CIcon d={CI.user} size={11}/>{g.tecnico}</span>}
                    {g.taller&&<span style={{display:'inline-flex',alignItems:'center',gap:4}}><CIcon d={KI.tool} size={11}/>{g.taller}</span>}
                  </div>
                </div>
              </div>
              <div style={{textAlign:'right',flexShrink:0,marginLeft:12}}>
                <div className="radar-num" style={{fontSize:17,fontWeight:700,letterSpacing:'-0.02em',color:C.redText}}>{fmt(g.total||0)}</div>
                {(g.mano_obra||g.costo_repuestos)&&<div style={{fontSize:11,color:C.t4,marginTop:2}}>MO {fmt(g.mano_obra||0)} · Rep {fmt(g.costo_repuestos||0)}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={modalNuevo} onClose={()=>setModalNuevo(false)} title="Registrar Gasto / Mantenimiento" width={520}>
        <SearchSel label="Equipo *" value={f.equipo_id} onChange={v=>setF(x=>({...x,equipo_id:v}))} required placeholder="Buscar equipo..." options={equipos.map(e=>({v:e.id,l:`${e.codigo} — ${e.modelo} (${STATUS[e.estado]?.label||e.estado})`}))}/>
        <Sel label="Tipo *" value={f.tipo} onChange={v=>setF(x=>({...x,tipo:v}))} required options={TIPOS_MANT}/>
        <Inp label="Descripción" value={f.descripcion} onChange={v=>setF(x=>({...x,descripcion:v}))} placeholder="qué se hizo, qué piezas, qué falla..." rows={2}/>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <Inp label="Mano de obra ($)" type="number" value={f.mano_obra} onChange={v=>setF(x=>({...x,mano_obra:v}))} placeholder="0"/>
          <Inp label="Repuestos ($)" type="number" value={f.costo_repuestos} onChange={v=>setF(x=>({...x,costo_repuestos:v}))} placeholder="0"/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
          <Inp label="Técnico" value={f.tecnico} onChange={v=>setF(x=>({...x,tecnico:v}))} placeholder="nombre"/>
          <Inp label="Taller" value={f.taller} onChange={v=>setF(x=>({...x,taller:v}))} placeholder="Taller propio"/>
          <Inp label="Fecha" type="date" value={f.fecha} onChange={v=>setF(x=>({...x,fecha:v}))}/>
        </div>
        {(f.mano_obra||f.costo_repuestos)&&<div className="radar-num" style={{background:C.redTint,border:`1px solid ${C.redBorder}`,borderRadius:R.md,padding:'10px 14px',marginBottom:14,fontSize:16,fontWeight:700,letterSpacing:'-0.015em',color:C.redText}}>
          Total: {fmt((parseFloat(f.mano_obra)||0)+(parseFloat(f.costo_repuestos)||0))}
        </div>}
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" onClick={()=>setModalNuevo(false)}>Cancelar</Btn>
          <Btn onClick={guardar} disabled={!f.equipo_id||!f.tipo||saving}>{saving?'Guardando…':'Guardar Gasto'}</Btn>
        </div>
      </Modal>
    </div>
  );
}

// ─── SAP B1 ───────────────────────────────────────────────────────────────────
// Llave de radar_config donde vive la config SAP de cada empresa.
// MISMA convención que api/sap-inventario.js: honduras -> sap_config,
// el resto -> sap_config_<empresa_id>. Antes esto era un ternario
// 'costarica' vs default, así que una empresa nueva escribía encima de la
// config de Honduras y borraba esa conexión sin avisar.
const cfgKeySAP = (eid) => {
  const e = String(eid || 'honduras').toLowerCase();
  if (e === 'honduras' || e === '') return 'sap_config';
  if (e === 'costarica') return 'sap_config_cr';
  return `sap_config_${e}`;
};
const CFG_VACIA = { url:'', user:'', pass:'', company:'' };

function SAP() {
  const [empresaSAP, setEmpresaSAP] = useState('honduras');
  // Config por empresa, indexada por empresa_id: soporta N empresas, no dos.
  const [cfgs, setCfgs] = useState({});
  const [savedMap, setSavedMap] = useState({});
  const [editing, setEditing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncHistory, setSyncHistory] = useState([]);
  const [tasaHNL, setTasaHNL] = useState(null);
  const [tasaCRC, setTasaCRC] = useState(null);
  const [spMapDiag, setSpMapDiag] = useState(null);
  const [closingOpps, setClosingOpps] = useState(false);
  const [closeOppResult, setCloseOppResult] = useState(null);
  const [bases, setBases] = useState(null);        // lista de CompanyDB del servidor
  const [basesLoad, setBasesLoad] = useState(false);
  const [basesErr, setBasesErr] = useState(null);
  const activeCfg = cfgs[empresaSAP] || CFG_VACIA;
  const setActiveCfg = (fn) => setCfgs(prev => ({
    ...prev,
    [empresaSAP]: typeof fn === 'function' ? fn(prev[empresaSAP] || CFG_VACIA) : fn,
  }));

  const addLog = (msg, ok) => setLog(l=>[{ts:new Date().toLocaleTimeString('es-HN'),msg,ok},...l.slice(0,29)]);

  // Preguntarle al servidor SAP qué bases tiene. Sirve para conectar una
  // empresa nueva sin tener que entrar al cliente de SAP a buscar el nombre
  // exacto de la CompanyDB.
  const verBases = async () => {
    setBasesLoad(true); setBasesErr(null); setBases(null);
    try {
      const r = await fetch('/api/sap', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'list_companies' }),
      });
      const d = await r.json();
      if (d.ok && Array.isArray(d.empresas) && d.empresas.length) {
        setBases(d.empresas);
        addLog(`Bases del servidor SAP: ${d.empresas.length}`, true);
      } else {
        setBasesErr(d.error || 'El servidor no devolvió ninguna base.');
        addLog('No se pudo listar las bases de SAP', false);
      }
    } catch (e) {
      setBasesErr(e.message);
      addLog('No se pudo listar las bases de SAP', false);
    }
    setBasesLoad(false);
  };

  const cargarHistorial = async () => {
    const { data } = await supabase.from('sap_sync_log').select('*').order('synced_at',{ascending:false}).limit(10);
    setSyncHistory(data||[]);
  };

  // Cargar config guardada al montar
  useEffect(() => {
    (async () => {
      // like 'sap_config%' en vez de una lista fija: así aparece cualquier
      // empresa que se agregue después sin tocar este código.
      const [{ data: cfgRows }, { data: rows }] = await Promise.all([
        supabase.from('radar_config').select('key,value').like('key','sap_config%'),
        supabase.from('radar_config').select('key,value').in('key',['sap_last_sync','tasa_hnl_usd','tasa_crc_usd',`sap_sp_map_${empresaSAP}`]),
      ]);
      const byKey = Object.fromEntries((rows||[]).map(r=>[r.key,r.value]));
      const mapCfgs = {}, mapSaved = {};
      for (const r of (cfgRows||[])) {
        const eid = r.key === 'sap_config' ? 'honduras'
          : r.key === 'sap_config_cr' ? 'costarica'
          : r.key.replace(/^sap_config_/, '');
        if (!eid) continue;
        mapCfgs[eid]  = { ...CFG_VACIA, ...(r.value||{}) };
        mapSaved[eid] = !!(r.value?.url);
      }
      setCfgs(mapCfgs); setSavedMap(mapSaved);
      if (byKey.sap_last_sync?.ts) setLastSync(byKey.sap_last_sync.ts);
      if (byKey.tasa_hnl_usd?.rate) setTasaHNL(byKey.tasa_hnl_usd.rate);
      if (byKey.tasa_crc_usd?.rate) setTasaCRC(byKey.tasa_crc_usd.rate);
      if (byKey[`sap_sp_map_${empresaSAP}`]?.map) setSpMapDiag(byKey[`sap_sp_map_${empresaSAP}`].map);
      await cargarHistorial();
      setLoading(false);
    })();
  }, []);

  // Si hay config guardada HN, auto-conectar al montar
  useEffect(() => {
    if (!loading && savedMap.honduras && cfgs.honduras?.url) autoSync(cfgs.honduras, 'honduras');
  }, [loading, savedMap.honduras]); // eslint-disable-line

  const autoSync = async (c, eid) => {
    setSyncing(true); setStatus(null);
    const empresa_id = eid || empresaSAP;
    try {
      const test = await fetch('/api/sap',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'test',...c})});
      const td = await test.json();
      if (!td.ok) { setStatus('error'); addLog('Error conexión: '+(td.error||''), false); setSyncing(false); return; }
      setStatus('ok');
      const etiqueta = (ORG.empresas.find(e=>e.id===empresa_id)?.label || empresa_id).replace(/^[^\w]+\s*/,'');
      addLog(`Conexión exitosa SAP B1 (${etiqueta})`, true);
      for (const type of ['clientes','facturas','cobros','items','gastos','cotizaciones']) {
        const r = await fetch('/api/sap',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'sync',type,...c,empresa_id})});
        const d = await r.json();
        addLog(d.ok?`Sync ${type}: ${d.count||0} registros`:`Error sync ${type}: `+(d.error||''), d.ok);
      }
      const ts = new Date().toLocaleString('es-HN');
      setLastSync(ts);
      await supabase.from('radar_config').upsert({key:'sap_last_sync',value:{ts},updated_at:new Date().toISOString()},{onConflict:'org_id,key'});
      // Refrescar tasas recién guardadas por el backend
      const {data:tasasRows} = await supabase.from('radar_config').select('key,value').in('key',['tasa_hnl_usd','tasa_crc_usd']);
      (tasasRows||[]).forEach(r=>{ if(r.key==='tasa_hnl_usd'&&r.value?.rate) setTasaHNL(r.value.rate); if(r.key==='tasa_crc_usd'&&r.value?.rate) setTasaCRC(r.value.rate); });
      await cargarHistorial();
    } catch(e) { setStatus('error'); addLog('Error: '+e.message, false); }
    setSyncing(false);
  };

  const guardarYConectar = async () => {
    const c = activeCfg;
    if (!c.url||!c.user||!c.pass||!c.company) return;
    setSyncing(true);
    try {
      const test = await fetch('/api/sap',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'test',...c})});
      const td = await test.json();
      if (!td.ok) { setStatus('error'); addLog('Error conexión: '+(td.error||''), false); setSyncing(false); return; }
      const cfgKey = cfgKeySAP(empresaSAP);
      await supabase.from('radar_config').upsert({key:cfgKey,value:c,updated_at:new Date().toISOString()},{onConflict:'org_id,key'});
      setSavedMap(m=>({...m,[empresaSAP]:true}));
      setEditing(false); setStatus('ok');
      addLog('Configuración guardada. Iniciando sync…', true);
      setSyncing(false);
      await autoSync(c, empresaSAP);
    } catch(e) { setStatus('error'); addLog('Error: '+e.message, false); setSyncing(false); }
  };

  const SYNCS = [
    {id:'clientes',     icon:'👥',label:'Clientes',       result: log.find(l=>l.msg.startsWith('Sync clientes'))},
    {id:'facturas',     icon:'📄',label:'Facturas',        result: log.find(l=>l.msg.startsWith('Sync facturas'))},
    {id:'cobros',       icon:'💰',label:'Cobros / Pagos',   result: log.find(l=>l.msg.startsWith('Sync cobros'))},
    {id:'items',        icon:'📦',label:'Items / Activos', result: log.find(l=>l.msg.startsWith('Sync items'))},
    {id:'gastos',       icon:'🔧',label:'Gastos servicio', result: log.find(l=>l.msg.startsWith('Sync gastos'))},
    {id:'cotizaciones', icon:'📋',label:'Cotiz. SAP→CRM',  result: log.find(l=>l.msg.startsWith('Sync cotizaciones'))},
  ];

  if (loading) return (
    <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',background:C.bg1}}>
      <div style={{color:C.t4,fontSize:14}}>Cargando configuración SAP…</div>
    </div>
  );

  return (
    <div style={{flex:1,overflowY:'auto',padding:28,background:C.bg1}}>
      <div style={{maxWidth:820,margin:'0 auto'}}>
        <div style={{marginBottom:24}}>
          <h1 style={{margin:'0 0 4px',fontSize:22,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>🔗 Integración SAP Business One</h1>
          <p style={{margin:0,color:C.t4,fontSize:13}}>Sincronización automática diaria — clientes, facturas, activos y servicios</p>
        </div>

        {/* Selector de empresa */}
        <div style={{display:'flex',gap:8,marginBottom:20}}>
          {ORG.empresas.map(e=>(
            <button key={e.id} className="radar-pill" onClick={()=>{setEmpresaSAP(e.id);setEditing(false);setStatus(null);}}
              style={{padding:'0 18px',height:34,borderRadius:R.md,border:`1px solid ${empresaSAP===e.id?e.color:C.border}`,background:empresaSAP===e.id?`${e.color}14`:C.bg2,color:empresaSAP===e.id?e.color:C.t3,fontWeight:600,fontSize:13,cursor:'pointer'}}>
              {e.label}
            </button>
          ))}
        </div>

        {/* Estado cuando ya está configurado */}
        {savedMap[empresaSAP] && !editing ? (
          <div style={{background:C.bg2,border:`1px solid ${C.greenBorder}`,borderRadius:R.lg,padding:24,marginBottom:20,boxShadow:SH.sm}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
                  <span style={{width:10,height:10,borderRadius:'50%',background:status==='ok'?C.green:status==='error'?C.red:C.orange,display:'inline-block'}}/>
                  <span style={{fontSize:15,fontWeight:800,color:C.t1}}>
                    {status==='ok'?'SAP Conectado':status==='error'?'Error de conexión':'Verificando…'}
                  </span>
                </div>
                <div style={{fontSize:12,color:C.t4}}>{activeCfg.url}</div>
                {lastSync&&<div style={{fontSize:12,color:C.t3,marginTop:4}}>Última sync: {lastSync} · Sync automático diario activo</div>}
                {empresaSAP==='costarica'
                  ? (tasaCRC&&<div style={{fontSize:12,color:C.t4,marginTop:2}}>Tasa CRC/USD (venta): <strong style={{color:C.t2}}>₡{tasaCRC.toFixed(2)} x $1</strong> <span style={{color:C.t4}}>— BCCR</span></div>)
                  : (tasaHNL&&<div style={{display:'flex',alignItems:'center',gap:8,marginTop:2,flexWrap:'wrap'}}>
                      <span style={{fontSize:12,color:C.t4}}>Tasa HNL/USD (venta): <strong style={{color:C.t2}}>L{tasaHNL.toFixed(4)} x $1</strong></span>
                      <div style={{display:'flex',gap:4,alignItems:'center'}}>
                        <input className="radar-input" type="number" step="0.0001" defaultValue={tasaHNL} id="hnl-rate-input"
                          style={{width:80,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.sm,padding:'3px 6px',color:C.t1,fontSize:11}}/>
                        <button onClick={async()=>{
                          const input=document.getElementById('hnl-rate-input');
                          const r=parseFloat(input?.value);
                          if(!r||r<20||r>40){toast('Tasa inválida (debe ser entre 20 y 40)');return;}
                          const ts=new Date().toISOString();
                          await supabase.from('radar_config').upsert({key:'tasa_hnl_usd',value:{rate:r,ts},updated_at:ts},{onConflict:'org_id,key'});
                          setTasaHNL(r);
                        }} className="radar-pill" style={{background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,borderRadius:R.sm,padding:'3px 9px',fontSize:10,fontWeight:600,cursor:'pointer'}}>
                          Actualizar
                        </button>
                      </div>
                    </div>)
                }
              </div>
              <div style={{display:'flex',gap:8}}>
                <Btn onClick={()=>autoSync(activeCfg)} disabled={syncing} variant="primary">
                  {syncing?'Sincronizando…':'🔄 Sync Ahora'}
                </Btn>
                <Btn onClick={()=>setEditing(true)} variant="ghost">Cambiar config</Btn>
              </div>
            </div>

            {/* Cobros — cerrar opps cuyas facturas SAP ya están pagadas */}
            <div style={{marginTop:16,background:C.greenTint,border:`1px solid ${C.greenBorder}`,borderRadius:R.md,padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:C.t1}}>💰 Cobros SAP → Pipeline</div>
                <div style={{fontSize:11,color:C.t4,marginTop:2}}>Mueve a "Pagado" las oportunidades en "Facturado" cuya factura ya fue cobrada en SAP. El vendedor confirma el cierre final.</div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:12,flexShrink:0}}>
                {closeOppResult&&(
                  <span style={{fontSize:12,color:closeOppResult.error?C.red:C.green,fontWeight:700}}>
                    {closeOppResult.error?`Error: ${closeOppResult.error}`:closeOppResult.n===0?'Sin pendientes':` ${closeOppResult.n} cerrada(s)`}
                  </span>
                )}
                <Btn onClick={async()=>{
                  setClosingOpps(true); setCloseOppResult(null);
                  try {
                    const eid = empresaSAP;
                    const { data: pagadas } = await supabase.from('facturas_sap').select('doc_num').eq('empresa_id',eid).eq('pagado',true);
                    if (!pagadas || pagadas.length === 0) { setCloseOppResult({n:0}); setClosingOpps(false); return; }
                    const nums = pagadas.map(f=>String(f.doc_num));
                    // Solo mover facturado → pagado (no saltar a cerrado_ganado; el vendedor confirma ese paso)
                    const { data: opps } = await supabase.from('oportunidades').select('id').eq('empresa_id',eid).eq('etapa','facturado').in('factura_sap_num',nums);
                    if (!opps || opps.length === 0) { setCloseOppResult({n:0}); setClosingOpps(false); return; }
                    await supabase.from('oportunidades').update({etapa:'pagado'}).in('id',opps.map(o=>o.id));
                    setCloseOppResult({n:opps.length});
                  } catch(e) { setCloseOppResult({error:e.message}); }
                  setClosingOpps(false);
                }} disabled={closingOpps} variant="secondary" size="sm">
                  {closingOpps?'Procesando…':'Actualizar Pagados'}
                </Btn>
              </div>
            </div>

            {/* Cards de sync */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginTop:16}}>
              {SYNCS.map(s=>(
                <div key={s.id} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:14,textAlign:'center',boxShadow:SH.xs}}>
                  <div style={{fontSize:20,marginBottom:6}}>{s.icon}</div>
                  <div style={{fontSize:12,fontWeight:600,color:C.t1,marginBottom:4}}>{s.label}</div>
                  {s.result
                    ? <div className="radar-num" style={{fontSize:11,color:s.result.ok?C.greenText:C.redText,fontWeight:600}}>{s.result.ok?s.result.msg.split(': ')[1]+' reg.':'Error'}</div>
                    : <div style={{fontSize:11,color:C.t4}}>{syncing?'Syncing…':'—'}</div>
                  }
                </div>
              ))}
            </div>
            {/* Diagnóstico: nombres de vendedores en SAP */}
            {spMapDiag&&Object.keys(spMapDiag).length>0&&(
              <div style={{marginTop:16,background:C.bg3,borderRadius:10,padding:14}}>
                <div style={{fontSize:11,fontWeight:800,color:C.t4,textTransform:'uppercase',marginBottom:8}}>👤 Vendedores en SAP (último sync)</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {Object.entries(spMapDiag).filter(([code])=>parseInt(code)>=0).map(([code,name])=>(
                    <span key={code} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:6,padding:'3px 8px',fontSize:11,color:C.t2}}>
                      <span style={{color:C.t4,marginRight:4}}>#{code}</span>{name||'(sin nombre)'}
                    </span>
                  ))}
                </div>
                <div style={{fontSize:10,color:C.t4,marginTop:6}}>Estos son los nombres exactos que llegan de SAP. Deben coincidir con el nombre del vendedor en RADAR para asignarse automáticamente.</div>
              </div>
            )}
          </div>
        ) : (
          /* Formulario de configuración */
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:24,marginBottom:20}}>
            <div style={{fontSize:13,fontWeight:800,color:C.t1,marginBottom:16}}>⚙️ Configuración Service Layer</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
              <Inp label="URL Service Layer *" value={activeCfg.url} onChange={v=>setActiveCfg(x=>({...x,url:v}))} placeholder="https://tu-servidor-sap:50000/b1s/v1/"/>
              <Inp label="Company DB *" value={activeCfg.company} onChange={v=>setActiveCfg(x=>({...x,company:v}))} placeholder="SBO_TUEMPRESA"/>
              <Inp label="Usuario SAP *" value={activeCfg.user} onChange={v=>setActiveCfg(x=>({...x,user:v}))} placeholder="manager"/>
              <Inp label="Contraseña *" type="password" value={activeCfg.pass} onChange={v=>setActiveCfg(x=>({...x,pass:v}))} placeholder="••••••••"/>
            </div>

            {/* Descubrir la CompanyDB sin entrar al cliente de SAP */}
            <div style={{marginTop:14,background:C.bg3,borderRadius:10,padding:14}}>
              <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                <Btn onClick={verBases} variant="ghost" disabled={basesLoad}>
                  {basesLoad?'Consultando SAP…':'🔎 Ver bases del servidor'}
                </Btn>
                <span style={{fontSize:12,color:C.t4,lineHeight:1.5}}>
                  Le pregunta al servidor qué bases tiene. Tocá la que corresponda y se llena el Company DB.
                </span>
              </div>

              {bases&&(
                <div style={{marginTop:12}}>
                  <div style={{fontSize:11,fontWeight:800,color:C.t4,textTransform:'uppercase',marginBottom:8}}>
                    {bases.length} base(s) en el servidor
                  </div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                    {bases.map(b=>{
                      const sel = activeCfg.company===b.companyDB;
                      return (
                        <button key={b.companyDB} onClick={()=>setActiveCfg(x=>({...x,company:b.companyDB}))}
                          style={{background:sel?C.primaryTint:C.bg2,border:`1px solid ${sel?C.primary:C.border}`,
                            borderRadius:6,padding:'6px 10px',fontSize:12,cursor:'pointer',textAlign:'left',
                            color:sel?C.primary:C.t2,fontWeight:sel?700:500}}>
                          <span className="radar-num">{b.companyDB}</span>
                          {b.nombre&&<span style={{color:C.t4,marginLeft:6}}>{b.nombre}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {basesErr&&(
                <div style={{marginTop:10,fontSize:12,color:C.redText,lineHeight:1.6}}>
                  No se pudo listar: {basesErr}
                  <div style={{color:C.t4,marginTop:4}}>
                    Algunos servidores no exponen el listado. Si es el caso, escribí el Company DB a mano
                    y usá “Guardar y Conectar” — si el nombre está mal, la conexión falla y no se guarda nada.
                  </div>
                </div>
              )}
            </div>

            <div style={{display:'flex',gap:10,alignItems:'center',marginTop:12}}>
              <Btn onClick={guardarYConectar} disabled={!activeCfg.url||!activeCfg.user||!activeCfg.pass||!activeCfg.company||syncing}>
                {syncing?'Guardando…':'💾 Guardar y Conectar'}
              </Btn>
              {editing&&<Btn onClick={()=>setEditing(false)} variant="ghost">Cancelar</Btn>}
              {status==='error'&&<span style={{color:C.red,fontWeight:700,fontSize:13}}>❌ Error de conexión</span>}
            </div>
            <div style={{marginTop:10,background:C.bg3,borderRadius:8,padding:'10px 14px',fontSize:12,color:C.t4,lineHeight:1.6}}>
              Solo necesitas configurar esto una vez. RADAR guardará las credenciales y sincronizará automáticamente cada noche.
            </div>
          </div>
        )}

        {/* Log */}
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:24,marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:800,color:C.t1,marginBottom:14}}>📋 Log de actividad</div>
          {log.length===0
            ? <div style={{color:C.t4,fontSize:13,textAlign:'center',padding:'20px 0'}}>Sin actividad aún</div>
            : log.map((l,i)=>(
              <div key={i} style={{display:'flex',gap:10,padding:'6px 0',borderBottom:`1px solid ${C.border}`,fontSize:12}}>
                <span style={{color:C.t4,flexShrink:0}}>{l.ts}</span>
                <span style={{color:l.ok?C.green:C.red,fontWeight:600}}>{l.ok?'✓':'✗'}</span>
                <span style={{color:C.t2}}>{l.msg}</span>
              </div>
            ))
          }
        </div>

        {/* Historial de syncs persistentes */}
        {syncHistory.length>0&&(
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:24}}>
            <div style={{fontSize:13,fontWeight:800,color:C.t1,marginBottom:14}}>🗂️ Historial de sincronizaciones (SAP → RADAR)</div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${C.border}`}}>
                    {['Fecha','Trigger','Estado','Clientes','Items','Facturas','Servicios','Cotiz. nuevas','Cotiz. actualizadas'].map(h=>(
                      <th key={h} style={{padding:'6px 10px',textAlign:'left',color:C.t3,fontWeight:700,fontSize:11,textTransform:'uppercase',letterSpacing:'.06em',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {syncHistory.map((s,i)=>(
                    <tr key={s.id||i} style={{borderBottom:`1px solid ${C.border}`}}>
                      <td style={{padding:'6px 10px',color:C.t3,whiteSpace:'nowrap'}}>{s.synced_at?new Date(s.synced_at).toLocaleString('es-HN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}):'—'}</td>
                      <td style={{padding:'6px 10px',color:C.t3}}>{s.trigger||'manual'}</td>
                      <td style={{padding:'6px 10px'}}><span style={{color:s.status==='ok'?C.green:C.red,fontWeight:700}}>{s.status==='ok'?'✓ OK':'✗ Error'}</span></td>
                      <td style={{padding:'6px 10px',color:C.t2,textAlign:'center'}}>{s.clientes??'—'}</td>
                      <td style={{padding:'6px 10px',color:C.t2,textAlign:'center'}}>{s.items??'—'}</td>
                      <td style={{padding:'6px 10px',color:C.t2,textAlign:'center'}}>{s.facturas??'—'}</td>
                      <td style={{padding:'6px 10px',color:C.t2,textAlign:'center'}}>{s.servicecalls??'—'}</td>
                      <td style={{padding:'6px 10px',color:C.t2,textAlign:'center'}}>{s.cotizaciones_nuevas??'—'}</td>
                      <td style={{padding:'6px 10px',color:C.t2,textAlign:'center'}}>{s.cotizaciones_actualizadas??'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ELSA ─────────────────────────────────────────────────────────────────────
function AccionesPanel({ equipos, clientes, onClose }) {
  const [accion, setAccion] = useState(null);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  const log = async (tipo, titulo, desc) => {
    await supabase.from('actividades').insert({tipo,titulo,descripcion:desc,fecha_actividad:new Date().toISOString()});
  };

  const guardar = {
    renta: async () => {
      if (!f.equipo_id||!f.cliente_id||!f.valor) return; setSaving(true);
      await supabase.from('rentas').insert({equipo_id:f.equipo_id,cliente_id:f.cliente_id,estado:'activa',valor_mensual:parseFloat(f.valor),valor:parseFloat(f.valor),fecha_inicio:f.fecha||new Date().toISOString().split('T')[0],tipo_cobro:f.tipo_cobro||'mensual'});
      await supabase.from('equipos').update({estado:'en_renta',asignado_a:f.cliente_id}).eq('id',f.equipo_id);
      const eq=equipos.find(e=>e.id===f.equipo_id); const cl=clientes.find(c=>c.id===f.cliente_id);
      await log('renta','Renta registrada',`${eq?.codigo} → ${cl?.empresa} $${f.valor}/mes`);
      setSaving(false); setAccion(null); setF({});
    },
    gasto: async () => {
      if (!f.equipo_id||!f.tipo) return; setSaving(true);
      const total=(parseFloat(f.mano_obra)||0)+(parseFloat(f.costo_repuestos)||0);
      await supabase.from('mantenimientos').insert({equipo_id:f.equipo_id,tipo:f.tipo,descripcion:f.descripcion,mano_obra:parseFloat(f.mano_obra)||0,costo_repuestos:parseFloat(f.costo_repuestos)||0,total,tecnico:f.tecnico,taller:f.taller,fecha:new Date().toISOString().split('T')[0]});
      const eq=equipos.find(e=>e.id===f.equipo_id);
      await log('gasto',`Gasto en ${eq?.codigo}`,`${f.tipo} — ${fmt(total)}`);
      setSaving(false); setAccion(null); setF({});
    },
    estado: async () => {
      if (!f.equipo_id||!f.estado) return; setSaving(true);
      await supabase.from('equipos').update({estado:f.estado,razon_detencion:f.razon||null}).eq('id',f.equipo_id);
      const eq=equipos.find(e=>e.id===f.equipo_id);
      await log('estado','Estado actualizado',`${eq?.codigo} → ${STATUS[f.estado]?.label}`);
      setSaving(false); setAccion(null); setF({});
    },
    nota: async () => {
      if (!f.nota) return; setSaving(true);
      await log('nota',f.nota,f.detalle||'');
      setSaving(false); setAccion(null); setF({});
    },
  };

  const ACCIONES = [
    {id:'renta',  icon:'💼', label:'Nueva Renta',           color:C.blue},
    {id:'gasto',  icon:'🔧', label:'Registrar Gasto',       color:C.orange},
    {id:'estado', icon:'🏗️', label:'Cambiar Estado',        color:C.green},
    {id:'nota',   icon:'📝', label:'Nota Rápida',           color:C.indigo},
  ];

  return (
    <div style={{width:340,borderLeft:`1px solid ${C.border}`,background:C.bg2,display:'flex',flexDirection:'column',overflow:'hidden',flexShrink:0}}>
      <div style={{padding:'14px 18px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,fontWeight:800,color:C.t1}}>⚡ Acciones Rápidas</div>
        <button onClick={onClose} style={{background:'none',border:'none',color:C.t4,fontSize:20,cursor:'pointer',lineHeight:1}}>×</button>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:16}}>
        {!accion&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            {ACCIONES.map(a=>(
              <div key={a.id} onClick={()=>{setAccion(a.id);setF({});}}
                style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:10,padding:14,cursor:'pointer',textAlign:'center'}}
                onMouseEnter={e=>e.currentTarget.style.borderColor=a.color}
                onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                <div style={{fontSize:22,marginBottom:6}}>{a.icon}</div>
                <div style={{fontSize:12,fontWeight:700,color:C.t1}}>{a.label}</div>
              </div>
            ))}
          </div>
        )}
        {accion&&(
          <div>
            <button onClick={()=>setAccion(null)} style={{background:'none',border:'none',color:C.t4,cursor:'pointer',fontSize:12,marginBottom:14,padding:0}}>← Volver</button>
            <div style={{fontSize:15,fontWeight:800,color:C.t1,marginBottom:16}}>
              {ACCIONES.find(a=>a.id===accion)?.icon} {ACCIONES.find(a=>a.id===accion)?.label}
            </div>
            {accion==='renta'&&<>
              <SearchSel label="Equipo disponible *" value={f.equipo_id||''} onChange={v=>setF(x=>({...x,equipo_id:v}))} required placeholder="Buscar equipo..." options={equipos.filter(e=>e.estado==='disponible').map(e=>({v:e.id,l:`${e.codigo} — ${e.modelo}`}))}/>
              <SearchSel label="Cliente *" value={f.cliente_id||''} onChange={v=>setF(x=>({...x,cliente_id:v}))} required placeholder="Buscar cliente..." options={clientes.map(c=>({v:c.id,l:c.empresa}))}/>
              <Inp label="Valor mensual ($) *" type="number" value={f.valor||''} onChange={v=>setF(x=>({...x,valor:v}))} required/>
              <Inp label="Fecha inicio" type="date" value={f.fecha||new Date().toISOString().split('T')[0]} onChange={v=>setF(x=>({...x,fecha:v}))}/>
              <Sel label="Tipo cobro" value={f.tipo_cobro||'mensual'} onChange={v=>setF(x=>({...x,tipo_cobro:v}))} options={['mensual','quincenal','semanal','por hora','por proyecto']}/>
              <Btn full onClick={guardar.renta} disabled={!f.equipo_id||!f.cliente_id||!f.valor||saving} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6}}>{saving?'Guardando...':<><CIcon d={CI.check2}/>Registrar Renta</>}</Btn>
            </>}
            {accion==='gasto'&&<>
              <SearchSel label="Equipo *" value={f.equipo_id||''} onChange={v=>setF(x=>({...x,equipo_id:v}))} required placeholder="Buscar equipo..." options={equipos.map(e=>({v:e.id,l:`${e.codigo} — ${e.modelo}`}))}/>
              <Sel label="Tipo *" value={f.tipo||''} onChange={v=>setF(x=>({...x,tipo:v}))} required options={['Mantenimiento preventivo','Reparación','Repuesto','Revisión','Overhaul','Otro']}/>
              <Inp label="Descripción" value={f.descripcion||''} onChange={v=>setF(x=>({...x,descripcion:v}))} placeholder="qué se hizo..." rows={2}/>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:10}}>
                <Inp label="Mano obra ($)" type="number" value={f.mano_obra||''} onChange={v=>setF(x=>({...x,mano_obra:v}))} placeholder="0"/>
                <Inp label="Repuestos ($)" type="number" value={f.costo_repuestos||''} onChange={v=>setF(x=>({...x,costo_repuestos:v}))} placeholder="0"/>
              </div>
              <Inp label="Técnico" value={f.tecnico||''} onChange={v=>setF(x=>({...x,tecnico:v}))}/>
              {(f.mano_obra||f.costo_repuestos)&&<div style={{background:C.bg1,borderRadius:8,padding:'8px 12px',marginBottom:14,fontSize:14,fontWeight:800,color:C.red}}>Total: {fmt((parseFloat(f.mano_obra)||0)+(parseFloat(f.costo_repuestos)||0))}</div>}
              <Btn full onClick={guardar.gasto} disabled={!f.equipo_id||!f.tipo||saving} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6}}>{saving?'Guardando...':<><CIcon d={CI.save}/>Guardar Gasto</>}</Btn>
            </>}
            {accion==='estado'&&<>
              <SearchSel label="Equipo *" value={f.equipo_id||''} onChange={v=>setF(x=>({...x,equipo_id:v}))} required placeholder="Buscar equipo..." options={equipos.map(e=>({v:e.id,l:`${e.codigo} — ${e.modelo} (${STATUS[e.estado]?.label||e.estado})`}))}/>
              <Sel label="Nuevo Estado *" value={f.estado||''} onChange={v=>setF(x=>({...x,estado:v}))} required options={Object.entries(STATUS).map(([k,v])=>({v:k,l:v.label}))}/>
              <Inp label="Razón (opcional)" value={f.razon||''} onChange={v=>setF(x=>({...x,razon:v}))} placeholder="motivo del cambio..."/>
              <Btn full onClick={guardar.estado} disabled={!f.equipo_id||!f.estado||saving} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6}}>{saving?'Guardando...':<><CIcon d={CI.check2}/>Actualizar Estado</>}</Btn>
            </>}
            {accion==='nota'&&<>
              <Inp label="Nota *" value={f.nota||''} onChange={v=>setF(x=>({...x,nota:v}))} placeholder="¿Qué pasó?" rows={3}/>
              <Inp label="Detalle adicional" value={f.detalle||''} onChange={v=>setF(x=>({...x,detalle:v}))} placeholder="más contexto..." rows={2}/>
              <Btn full onClick={guardar.nota} disabled={!f.nota?.trim()||saving} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6}}>{saving?'Guardando...':<><CIcon d={CI.save}/>Guardar Nota</>}</Btn>
            </>}
          </div>
        )}
      </div>
    </div>
  );
}

function ELSA({ equipos, clientes }) {
  const [msgs, setMsgs] = useState([
    { role:'assistant', content:'¡Hola! Soy ELSA ⚡, tu asistente de inteligencia artificial para RADAR.\n\nPuedo ayudarte con:\n• 📊 Reportes diarios, semanales y mensuales de ingresos y costos\n• 💼 Registrar rentas, gastos y cambios de estado con solo decírmelo\n• 🔍 Consultar cualquier dato de la plataforma en tiempo real\n• 📝 Anotar actividades, contactos y recordatorios\n\n¿En qué te ayudo hoy?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [panel, setPanel] = useState(false);
  const endRef = useRef(null);
  const taRef = useRef(null);

  useEffect(()=>{ endRef.current?.scrollIntoView({behavior:'smooth'}); }, [msgs]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    if (taRef.current) taRef.current.style.height = 'auto';
    const newMsgs = [...msgs, {role:'user', content:msg}];
    setMsgs(newMsgs);
    setLoading(true);
    try {
      const apiMsgs = newMsgs.map(m => ({role:m.role, content:m.content}));
      const res = await fetch('/api/elsa', {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({messages: apiMsgs}),
      });
      const data = await res.json();
      if (data.error) {
        setMsgs(prev=>[...prev, {role:'assistant', content:`❌ Error: ${data.error}`}]);
      } else {
        setMsgs(prev=>[...prev, {role:'assistant', content: data.response || 'Sin respuesta.'}]);
      }
    } catch(e) {
      setMsgs(prev=>[...prev, {role:'assistant', content:'❌ Error de conexión. Verificá tu conexión a internet.'}]);
    }
    setLoading(false);
  };

  const SUGERENCIAS = [
    '📊 Dame el reporte del día de hoy',
    '💼 ¿Qué equipos están disponibles ahora mismo?',
    '🔧 ¿Cuánto se gastó en mantenimiento este mes?',
    '📈 ¿Cuál es el ingreso mensual de las rentas activas?',
    '📋 Mostrá las últimas 10 actividades del sistema',
    '🏗️ ¿Cuántas máquinas están en taller?',
  ];

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* Header */}
      <div style={{padding:'12px 20px',borderBottom:`1px solid ${C.border}`,background:C.bg2,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <div style={{width:36,height:36,borderRadius:10,background:'linear-gradient(135deg,#0F766E,#0D5F58)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>⚡</div>
          <div>
            <div style={{fontSize:15,fontWeight:800,color:C.t1}}>ELSA — Asistente IA</div>
            <div style={{fontSize:11,color:C.green,display:'flex',alignItems:'center',gap:5}}>
              <span style={{width:6,height:6,borderRadius:'50%',background:C.green,display:'inline-block'}}/>
              Activa · Powered by Claude
            </div>
          </div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <button onClick={()=>setMsgs([{role:'assistant',content:'Chat reiniciado. ¿En qué te ayudo?'}])} style={{background:'none',border:`1px solid ${C.border}`,borderRadius:7,padding:'5px 11px',color:C.t4,fontSize:11,cursor:'pointer',fontWeight:600}}>Limpiar chat</button>
          <Btn size="sm" variant="secondary" onClick={()=>setPanel(v=>!v)} style={{display:'inline-flex',alignItems:'center',gap:6}}>
            <CIcon d={panel?<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>:CI.zap}/>{panel?'Cerrar panel':'Acciones rápidas'}
          </Btn>
        </div>
      </div>

      {/* Body */}
      <div style={{flex:1,display:'flex',overflow:'hidden'}}>
        {/* Chat area */}
        <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
          {/* Messages */}
          <div style={{flex:1,overflowY:'auto',padding:'20px 24px'}}>
            {msgs.map((m,i)=>(
              <div key={i} style={{display:'flex',justifyContent:m.role==='user'?'flex-end':'flex-start',marginBottom:16,gap:10}}>
                {m.role==='assistant'&&(
                  <div style={{width:30,height:30,borderRadius:8,background:'linear-gradient(135deg,#0F766E,#0D5F58)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,flexShrink:0,marginTop:2}}>⚡</div>
                )}
                <div style={{maxWidth:'78%',background:m.role==='user'?'linear-gradient(135deg,#0F766E,#0D5F58)':C.bg2,border:m.role==='user'?'none':`1px solid ${C.border}`,borderRadius:m.role==='user'?'16px 16px 4px 16px':'16px 16px 16px 4px',padding:'12px 16px',fontSize:14,color:m.role==='user'?'#fff':C.t1,lineHeight:1.65,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>
                  {m.content}
                </div>
                {m.role==='user'&&(
                  <div style={{width:30,height:30,borderRadius:8,background:C.bg3,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,flexShrink:0,marginTop:2,color:C.t3,fontWeight:700}}>tú</div>
                )}
              </div>
            ))}
            {loading&&(
              <div style={{display:'flex',gap:10,marginBottom:16}}>
                <div style={{width:30,height:30,borderRadius:8,background:'linear-gradient(135deg,#0F766E,#0D5F58)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,flexShrink:0}}>⚡</div>
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:'16px 16px 16px 4px',padding:'12px 16px',fontSize:13,color:C.t3}}>
                  Consultando datos... ⏳
                </div>
              </div>
            )}
            {msgs.length===1&&!loading&&(
              <div style={{marginTop:8}}>
                <div style={{fontSize:11,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10,fontWeight:700}}>Preguntas frecuentes</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  {SUGERENCIAS.map((s,i)=>(
                    <button key={i} onClick={()=>send(s)}
                      style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 13px',color:C.t2,fontSize:12,textAlign:'left',cursor:'pointer',fontFamily:'inherit',lineHeight:1.4}}
                      onMouseEnter={e=>e.currentTarget.style.borderColor=C.blue}
                      onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div ref={endRef}/>
          </div>

          {/* Input */}
          <div style={{padding:'12px 20px',borderTop:`1px solid ${C.border}`,background:C.bg2,flexShrink:0}}>
            <div style={{display:'flex',gap:10,alignItems:'flex-end'}}>
              <textarea ref={taRef} value={input}
                onChange={e=>{setInput(e.target.value);e.target.style.height='auto';e.target.style.height=Math.min(e.target.scrollHeight,120)+'px';}}
                onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();send();}}}
                placeholder="Preguntale algo a ELSA... (Enter para enviar, Shift+Enter para nueva línea)"
                rows={1}
                style={{flex:1,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 14px',color:C.t1,fontSize:14,outline:'none',resize:'none',fontFamily:'inherit',lineHeight:1.5,maxHeight:120,overflowY:'auto'}}/>
              <button onClick={()=>send()} disabled={!input.trim()||loading}
                style={{height:42,width:42,borderRadius:10,border:'none',background:input.trim()&&!loading?'linear-gradient(135deg,#0F766E,#0D5F58)':C.bg3,color:input.trim()&&!loading?'#fff':C.t4,fontSize:18,cursor:input.trim()&&!loading?'pointer':'default',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                →
              </button>
            </div>
          </div>
        </div>

        {/* Acciones rápidas panel */}
        {panel&&<AccionesPanel equipos={equipos} clientes={clientes} onClose={()=>setPanel(false)}/>}
      </div>
    </div>
  );
}

// ─── INGRESOS ─────────────────────────────────────────────────────────────────
const TIPOS_INGRESO = ['Renta mensual','Servicio técnico','Venta de equipo','Operador','Transporte','Reparación a cliente','Otro'];

// ─── AGING / CUENTAS POR COBRAR — helpers compartidos ──────────────────────────
const HOY_STR = () => new Date().toISOString().split('T')[0];
const diasEntre = (desdeStr, hastaStr) => {
  if (!desdeStr || !hastaStr) return 0;
  const a = new Date(String(desdeStr).slice(0,10) + 'T00:00:00');
  const b = new Date(String(hastaStr).slice(0,10) + 'T00:00:00');
  return Math.round((b - a) / 86400000);
};
// días de atraso (positivo = vencida). Vencimiento = doc_due_date, fallback doc_date.
const diasAtrasoDe = (f, hoy = HOY_STR()) => diasEntre(f.doc_due_date || f.doc_date, hoy);
// Buckets de antigüedad — escala semántica DS v3 (0–30 ámbar suave → +60 rojo).
// Cada bucket lleva su tripleta (color pleno / texto / tinte / borde) para badges.
const AGING = [
  { key: 'corriente', label: 'Corriente', short: 'Al día', color: '#16A34A', text:'#15803D', tint:'#F0FDF4', bd:'#BBF7D0', test: d => d <= 0 },
  { key: 'b1',        label: '1–30 días', short: '1–30',   color: '#D97706', text:'#B45309', tint:'#FFFBEB', bd:'#FDE68A', test: d => d >= 1  && d <= 30 },
  { key: 'b2',        label: '31–60 días',short: '31–60',  color: '#EA580C', text:'#C2410C', tint:'#FFF7ED', bd:'#FED7AA', test: d => d >= 31 && d <= 60 },
  { key: 'b3',        label: '61–90 días',short: '61–90',  color: '#DC2626', text:'#B91C1C', tint:'#FEF2F2', bd:'#FECACA', test: d => d >= 61 && d <= 90 },
  { key: 'b4',        label: '90+ días',  short: '90+',    color: '#991B1B', text:'#991B1B', tint:'#FEF2F2', bd:'#FECACA', test: d => d >  90 },
];
const bucketDe = (d) => AGING.find(b => b.test(d)) || AGING[0];
// Cuentas INTERNAS (intercompañía HN↔CR) — fuera de cuentas por cobrar:
// no son deuda real de clientes y manchan los totales.
const INTERCO_NOMBRES = ['MONTACARGAS SA DE CV'];
const esInterco = f => INTERCO_NOMBRES.some(n => (f.card_name||'').toUpperCase().includes(n));
// Saldo en USD de una factura (saldo_usd ya descuenta abonos; fallback a total_usd)
// Saldo confiable: si SAP no sincronizo el saldo (saldo_usd NULL) NO asumimos que
// se debe la factura completa — eso inflaba la cartera con deuda FALSA (facturas ya
// pagadas en SAP cuyo saldo nunca bajo a RADAR). Saldo desconocido = 0 (no es deuda).
const saldoUSDde = (f) => (f.saldo_usd != null ? (f.saldo_usd || 0) : 0);
const SYM_MON = { HNL: 'L.', CRC: '₡', USD: '$' };
const fmtMonG = (n, mon) => `${SYM_MON[mon]||'$'}${(n||0).toLocaleString(mon==='USD'?'en-US':'es-HN',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtUSDG = n => `$${(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtUSD0 = n => `$${Math.round(n||0).toLocaleString('en-US')}`;
const rateDeFactura = f => (f.total_usd > 0 ? (f.doc_total||0) / f.total_usd : 1);

// Badge de antigüedad reutilizable — tripleta tinte/texto/borde + dot (receta 7.5)
function AgingBadge({ dias, mini }) {
  const b = bucketDe(dias);
  return (
    <span className="radar-num" style={{display:'inline-flex',alignItems:'center',gap:5,background:b.tint,color:b.text,border:`1px solid ${b.bd}`,borderRadius:R.pill,
      padding:mini?'1px 7px':'2px 8px',fontSize:mini?9.5:10.5,fontWeight:600,whiteSpace:'nowrap',lineHeight:1.5}}>
      <span style={{width:6,height:6,borderRadius:'50%',background:b.color,flexShrink:0}}/>
      {dias > 0 ? `${dias}d` : 'Al día'}
    </span>
  );
}

// ─── COBROS ───────────────────────────────────────────────────────────────────
function Cobros({ usuario, empresaActiva, embedded, initBucket }) {
  const [facturas,    setFacturas]    = useState([]);
  const [cobradosMap, setCobradosMap] = useState({}); // doc_num → monto ya cobrado
  const [loading,     setLoading]     = useState(true);
  // Bucket inicial cuando se llega desde una tarjeta de aging del Resumen
  useEffect(()=>{ if (initBucket) setAgingSel(initBucket); }, [initBucket]);
  const [filtro,      setFiltro]      = useState('pendiente'); // pendiente | cobrada | todas
  const [busq,        setBusq]        = useState('');
  const [modal,       setModal]       = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [fPago,       setFPago]       = useState({ fecha: new Date().toISOString().split('T')[0], monto: '', metodo: 'Transferencia', vendedor: '' });
  const [usuarios,    setUsuarios]    = useState([]);
  const [agingSel,    setAgingSel]    = useState('todos');   // bucket key | 'todos'
  const [carpeta,     setCarpeta]     = useState('activas'); // 'activas' (≤60d) | 'urgentes' (>60d)

  const esFacturacion = ['admin','gerente_ventas','finanzas'].includes(usuario.rol);

  const cargar = async () => {
    setLoading(true);
    let q = supabase.from('facturas_sap').select('*').order('doc_date', { ascending: false }).limit(500);
    if (empresaActiva) q = q.eq('empresa_id', empresaActiva);
    if (!esFacturacion) q = q.eq('vendedor', usuario.nombre);
    if (filtro === 'pendiente') q = q.eq('pagado', false);
    if (filtro === 'cobrada')   q = q.eq('pagado', true);
    const { data } = await q;
    const lista = (data || []).filter(f => !esInterco(f));
    setFacturas(lista);

    // Cargar pagos parciales registrados en ingresos para estas facturas
    if (lista.length > 0) {
      const nums = lista.map(f => String(f.doc_num));
      const { data: pagos } = await supabase.from('ingresos').select('factura_sap_num,monto').in('factura_sap_num', nums);
      const mapa = {};
      (pagos || []).forEach(p => {
        const k = p.factura_sap_num;
        mapa[k] = (mapa[k] || 0) + (p.monto || 0);
      });
      setCobradosMap(mapa);
    } else {
      setCobradosMap({});
    }
    setLoading(false);
  };

  useEffect(() => { cargar(); }, [filtro, empresaActiva]);
  useEffect(() => {
    supabase.from('radar_usuarios').select('nombre').eq('rol','ventas').eq('activo',true)
      .then(({ data }) => setUsuarios((data||[]).map(u => u.nombre)));
  }, []);

  // Abonado: lo mayor entre lo que reporta SAP (pagado_usd, incluye retenciones)
  // y lo registrado manualmente en RADAR (ingresos) — evita doble conteo.
  const abonadoDe = (f) => {
    const sap   = f.pagado_usd || 0;
    const local = cobradosMap[String(f.doc_num)] || 0;
    return Math.max(sap, local);
  };
  const saldoFactura = (f) => Math.max(0, (f.total_usd || 0) - abonadoDe(f));

  const abrirPago = (f) => {
    const saldoOrig = saldoFactura(f) * rateOf(f); // saldo en moneda original
    setModal(f);
    setFPago({ fecha: new Date().toISOString().split('T')[0], monto: saldoOrig > 0 ? String(saldoOrig.toFixed(2)) : '', metodo: 'Transferencia', vendedor: f.vendedor || '' });
  };

  const registrarPago = async () => {
    if (!fPago.monto || !modal) return;
    setSaving(true);
    const rate = rateOf(modal);
    const montoOrig = parseFloat(fPago.monto) || 0;        // lo que tecleó el usuario, en moneda original
    const montoPago = rate > 0 ? montoOrig / rate : montoOrig; // canónico en USD para ingresos/saldo
    const cobradoPrevio = cobradosMap[String(modal.doc_num)] || 0; // USD
    const totalCubierto = cobradoPrevio + montoPago;
    const pagoCompleto = totalCubierto >= (modal.total_usd || 0);
    await Promise.all([
      supabase.from('facturas_sap').update({
        editado_manual_at: new Date().toISOString(),   // S5: el sync no pisa esta gestion
        pagado: pagoCompleto,
        fecha_pago: pagoCompleto ? fPago.fecha : modal.fecha_pago,
        monto_pagado: totalCubierto,
        metodo_pago: fPago.metodo,
        vendedor: fPago.vendedor || modal.vendedor,
      }).eq('doc_num', modal.doc_num),
      supabase.from('ingresos').insert({
        vendedor: fPago.vendedor || modal.vendedor,
        monto: montoPago,
        fecha: fPago.fecha,
        tipo: 'Factura SAP',
        descripcion: `Cobro${pagoCompleto?'':' parcial'} factura ${modal.doc_num} — ${modal.card_name}`,
        factura_sap_num: String(modal.doc_num),
        empresa_id: modal.empresa_id,
      }),
    ]);
    setSaving(false);
    setModal(null);
    cargar();
  };

  const fmt  = n => `$${(n||0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
  const fmt2 = n => `$${(n||0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // Moneda original (L. / ₡ / $) + tasa efectiva de cada factura (unidades locales por USD)
  const SYM = { HNL: 'L.', CRC: '₡', USD: '$' };
  const fmtMon = (n, mon) => `${SYM[mon]||'$'}${(n||0).toLocaleString(mon==='USD'?'en-US':'es-HN',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const rateOf = f => (f.total_usd > 0 ? (f.doc_total||0) / f.total_usd : 1);

  // Urgente/emergencia = vencida hace MÁS de 60 días (carpeta aparte para no manchar la data)
  const esUrgente = (f) => !f.pagado && diasAtrasoDe(f) > 60;

  const filtradas = facturas.filter(f => {
    // Carpeta: separa lo urgente (60+) de lo activo (≤60). Las pagadas viven en "activas".
    if (carpeta === 'urgentes') { if (!esUrgente(f)) return false; }
    else                        { if (esUrgente(f))  return false; }
    // Chip de antigüedad (solo aplica a facturas no pagadas)
    if (agingSel !== 'todos' && !f.pagado) {
      if (bucketDe(diasAtrasoDe(f)).key !== agingSel) return false;
    }
    if (!busq) return true;
    const b = busq.toLowerCase();
    return (f.card_name||'').toLowerCase().includes(b) || String(f.doc_num||'').includes(b) || (f.vendedor||'').toLowerCase().includes(b);
  });

  const totalPendiente = facturas.filter(f=>!f.pagado).reduce((s,f)=>s+saldoFactura(f),0);
  const totalCobrado   = facturas.filter(f=>f.pagado).reduce((s,f)=>s+(cobradosMap[String(f.doc_num)]||f.monto_pagado||f.total_usd||0),0);

  // Totales dinámicos de la vista filtrada actual + de la carpeta de urgentes
  const urgentesList   = facturas.filter(esUrgente);
  const totalUrgente   = urgentesList.reduce((s,f)=>s+saldoFactura(f),0);
  const saldoFiltrado  = filtradas.filter(f=>!f.pagado).reduce((s,f)=>s+saldoFactura(f),0);
  const countAging = (key) => facturas.filter(f => !f.pagado && !esUrgente(f) && bucketDe(diasAtrasoDe(f)).key===key).length;

  // Badge de estado — tripleta tinte/texto/borde + dot (receta 7.5)
  const badgeEstado = (f) => {
    const t = f.pagado ? TONES.green : (abonadoDe(f) > 0.5 ? TONES.orange : TONES.red);
    const l = f.pagado ? 'Cobrada' : (abonadoDe(f) > 0.5 ? 'Pago parcial' : 'Pendiente');
    return (
      <span style={{display:'inline-flex',alignItems:'center',gap:5,background:t.tint,color:t.text,border:`1px solid ${t.border}`,borderRadius:R.pill,padding:'2px 8px',fontSize:10.5,fontWeight:600,whiteSpace:'nowrap'}}>
        <span style={{width:6,height:6,borderRadius:'50%',background:t.solid,flexShrink:0}}/>{l}
      </span>
    );
  };

  const contenido = (
    <>
        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20,flexWrap:'wrap',gap:12}}>
          {!embedded && <div>
            <div style={{fontSize:20,fontWeight:800,letterSpacing:'-0.015em',color:C.t1}}>Finanzas</div>
            <div style={{fontSize:12,color:C.t3,marginTop:2}}>Facturas SAP · Registro de pagos recibidos</div>
          </div>}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'stretch',marginLeft:embedded?'auto':0}}>
            <button className="radar-pill" onClick={cargar} disabled={loading}
              style={{alignSelf:'center',display:'inline-flex',alignItems:'center',gap:6,height:34,padding:'0 13px',borderRadius:R.md,border:`1px solid ${C.border}`,background:C.bg2,color:C.t2,fontSize:12.5,fontWeight:600,cursor:loading?'wait':'pointer',boxShadow:SH.xs}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{color:C.t4,opacity:loading?.55:1}}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
              Actualizar
            </button>
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 14px',boxShadow:SH.xs}}>
              <div style={{fontSize:10.5,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em'}}>Saldo pendiente (USD)</div>
              <div className="radar-num" style={{fontSize:18,fontWeight:700,letterSpacing:'-0.02em',color:C.redText,marginTop:2}}>{fmt(totalPendiente)}</div>
            </div>
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 14px',boxShadow:SH.xs}}>
              <div style={{fontSize:10.5,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em'}}>Cobrado (USD)</div>
              <div className="radar-num" style={{fontSize:18,fontWeight:700,letterSpacing:'-0.02em',color:C.greenText,marginTop:2}}>{fmt(totalCobrado)}</div>
            </div>
          </div>
        </div>

        {/* Filtros — segmented (receta 7.4, lenguaje CRM) + search con lupa flotando sobre gris */}
        <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
          <div style={{display:'inline-flex',gap:2,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2}}>
            {[['pendiente','Pendientes'],['cobrada','Cobradas'],['todas','Todas']].map(([v,l])=>(
              <button key={v} onClick={()=>setFiltro(v)}
                style={{height:26,padding:'0 12px',borderRadius:R.sm,border:'none',cursor:'pointer',fontSize:12,fontWeight:600,transition:'background .15s ease, color .15s ease',whiteSpace:'nowrap',
                  background:filtro===v?C.bg2:'transparent',color:filtro===v?C.t1:C.t3,boxShadow:filtro===v?SH.sm:'none'}}>
                {l}
              </button>
            ))}
          </div>
          <div style={{position:'relative',flex:1,minWidth:180}}>
            <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:C.t4,pointerEvents:'none',display:'inline-flex'}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </span>
            <input className="radar-input" value={busq} onChange={e=>setBusq(e.target.value)} placeholder="Buscar cliente, factura..."
              style={{width:'100%',height:30,boxSizing:'border-box',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'0 12px 0 32px',color:C.t1,fontSize:12.5,outline:'none',boxShadow:SH.xs}}/>
          </div>
        </div>

        {/* Carpetas (activas ≤60d vs urgente 60+) + antigüedad + total de la vista */}
        {embedded && (
          <div style={{marginBottom:16}}>
            <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap'}}>
              <button className="radar-pill" onClick={()=>{setCarpeta('activas');setAgingSel('todos');}}
                style={{display:'inline-flex',alignItems:'center',gap:6,height:30,padding:'0 12px',borderRadius:R.md,cursor:'pointer',fontSize:12,fontWeight:600,boxShadow:carpeta==='activas'?'none':SH.xs,whiteSpace:'nowrap',
                  border:`1px solid ${carpeta==='activas'?C.primaryBorder:C.border}`,
                  background:carpeta==='activas'?C.primaryTint:C.bg2,color:carpeta==='activas'?C.blue:C.t2}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/></svg>
                Por cobrar (≤60 días)
              </button>
              <button className="radar-pill" onClick={()=>{setCarpeta('urgentes');setAgingSel('todos');}}
                style={{display:'inline-flex',alignItems:'center',gap:6,height:30,padding:'0 12px',borderRadius:R.md,cursor:'pointer',fontSize:12,fontWeight:600,boxShadow:carpeta==='urgentes'?'none':SH.xs,whiteSpace:'nowrap',
                  border:`1px solid ${C.redBorder}`,
                  background:carpeta==='urgentes'?C.redTint:C.bg2,color:C.redText}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                Urgente · 60+ días <span className="radar-num">({urgentesList.length} · {fmt(totalUrgente)})</span>
              </button>
            </div>
            {carpeta==='activas' && (
              <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                <span style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em',marginRight:4}}>Antigüedad</span>
                {['todos','corriente','b1','b2'].map(v=>{
                  const b = AGING.find(x=>x.key===v);
                  const on = agingSel===v;
                  const st = v==='todos'
                    ? { bg:on?C.bg3:C.bg2, bd:on?C.border2:C.border, tx:on?C.t1:C.t3, dot:null }
                    : { bg:on?b.tint:C.bg2, bd:on?b.bd:C.border, tx:on?b.text:C.t3, dot:b.color };
                  return (
                    <button key={v} className="radar-btn" onClick={()=>setAgingSel(v)}
                      style={{display:'inline-flex',alignItems:'center',gap:5,padding:'4px 11px',borderRadius:R.pill,cursor:'pointer',
                        border:`1px solid ${st.bd}`,background:st.bg,color:st.tx,fontSize:11.5,fontWeight:600,boxShadow:SH.xs}}>
                      {st.dot&&<span style={{width:6,height:6,borderRadius:'50%',background:st.dot,flexShrink:0}}/>}
                      {v==='todos'?'Todas':b.short}{v!=='todos'?<span className="radar-num">&nbsp;({countAging(v)})</span>:null}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="radar-num" style={{marginTop:10,fontSize:12,color:C.t3}}>
              Mostrando <b style={{color:C.t1,fontWeight:600}}>{filtradas.length}</b> factura(s) · Saldo de esta vista:{' '}
              <b style={{color:carpeta==='urgentes'?C.redText:C.t1,fontWeight:600}}>{fmt(saldoFiltrado)}</b>
            </div>
          </div>
        )}

        {/* Tabla */}
        {loading ? <div style={{padding:20}}><SkelCards n={4}/><SkelRows n={6}/></div> : (
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflowX:'auto',boxShadow:SH.sm}}>
            <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',minWidth:760}}>
              <thead>
                <tr style={{borderBottom:`1px solid ${C.border}`}}>
                  {['Factura','Cliente','Fecha','Vence','Total','Abonado','Saldo','Estado',''].map((h,i)=>(
                    <th key={h||i} style={{padding:'9px 12px',textAlign:[4,5,6].includes(i)?'right':'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtradas.length===0&&<tr><td colSpan={9} style={{padding:'36px 20px',textAlign:'center',color:C.t4,fontSize:13}}>Sin facturas en esta vista — prueba otro filtro o sincroniza SAP.</td></tr>}
                {filtradas.map(f=>{
                  const abonado = f.pagado ? (abonadoDe(f)||f.monto_pagado||f.total_usd||0) : abonadoDe(f);
                  const saldo   = f.pagado ? 0 : saldoFactura(f);
                  const dias = diasAtrasoDe(f);
                  return (
                    <tr key={f.doc_num} style={{borderBottom:`1px solid ${C.bg3}`}}>
                      <td className="radar-num" style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:C.blue}}>{String(f.doc_num||'').replace(/^\w+_/,'')}</td>
                      <td style={{padding:'8px 12px',fontSize:12,color:C.t2,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.card_name}</td>
                      <td className="radar-num" style={{padding:'8px 12px',fontSize:11,color:C.t3,whiteSpace:'nowrap'}}>{f.doc_date}</td>
                      <td style={{padding:'8px 12px'}}>
                        <div className="radar-num" style={{fontSize:11,color:C.t3,marginBottom:3,whiteSpace:'nowrap'}}>{f.doc_due_date||'—'}</div>
                        {!f.pagado&&<AgingBadge dias={dias} mini/>}
                      </td>
                      <td className="radar-num" style={{padding:'8px 12px',textAlign:'right',whiteSpace:'nowrap'}}>
                        <div style={{fontSize:12,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>{fmtMon(f.doc_total, f.moneda)}</div>
                        {f.moneda!=='USD'&&<div style={{fontSize:10,color:C.t4}}>≈ {fmt2(f.total_usd)}</div>}
                      </td>
                      <td className="radar-num" style={{padding:'8px 12px',textAlign:'right',whiteSpace:'nowrap'}}>
                        {abonado>0.5?(<>
                          <div style={{fontSize:12,fontWeight:600,color:C.greenText}}>{fmtMon(abonado*rateOf(f), f.moneda)}</div>
                          {f.moneda!=='USD'&&<div style={{fontSize:10,color:C.t4}}>≈ {fmt2(abonado)}</div>}
                        </>):<span style={{fontSize:12,color:C.t4}}>—</span>}
                      </td>
                      <td className="radar-num" style={{padding:'8px 12px',textAlign:'right',whiteSpace:'nowrap'}}>
                        {saldo>0.5?(<>
                          <div style={{fontSize:12,fontWeight:700,letterSpacing:'-0.015em',color:C.redText}}>{fmtMon(saldo*rateOf(f), f.moneda)}</div>
                          {f.moneda!=='USD'&&<div style={{fontSize:10,color:C.t4}}>≈ {fmt2(saldo)}</div>}
                        </>):<span style={{fontSize:12,fontWeight:600,color:C.greenText}}>✓</span>}
                      </td>
                      <td style={{padding:'8px 12px'}}>{badgeEstado(f)}</td>
                      <td style={{padding:'8px 12px'}}>
                        {esFacturacion&&!f.pagado&&(
                          <button className="radar-btn" onClick={()=>abrirPago(f)}
                            style={{background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,borderRadius:R.sm,padding:'4px 10px',fontSize:11,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap',boxShadow:SH.xs}}>
                            {abonado>0?'Abonar saldo':'Registrar cobro'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {/* Modal registrar cobro */}
      <Modal open={!!modal} onClose={()=>setModal(null)} title="Registrar cobro" width={440}>
        {modal&&(()=>{
          const mon = modal.moneda || 'USD';
          const rate = rateOf(modal);
          const cobradoPrev = cobradosMap[String(modal.doc_num)] || 0;        // USD
          const saldoUSD = Math.max(0, (modal.total_usd||0) - cobradoPrev);
          const saldoOrig = saldoUSD * rate;                                  // moneda original
          const montoOrig = parseFloat(fPago.monto) || 0;
          const dual = (usd) => mon!=='USD' ? <div style={{fontSize:10,color:C.t4,marginTop:1}}>≈ {fmt2(usd)}</div> : null;
          return <>
            <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'12px 14px',marginBottom:16}}>
              <div style={{fontWeight:700,letterSpacing:'-0.005em',color:C.t1,fontSize:13}}>{modal.card_name}</div>
              <div className="radar-num" style={{color:C.t3,fontSize:11,marginTop:4}}>Factura #{modal.doc_num} · {mon}</div>
              <div style={{display:'flex',gap:16,marginTop:8}}>
                <div><div style={{fontSize:10.5,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em'}}>Total factura</div><div className="radar-num" style={{fontSize:13,fontWeight:700,color:C.t1}}>{fmtMon(modal.doc_total, mon)}</div>{dual(modal.total_usd)}</div>
                {cobradoPrev>0&&<div><div style={{fontSize:10.5,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em'}}>Ya cobrado</div><div className="radar-num" style={{fontSize:13,fontWeight:700,color:C.greenText}}>{fmtMon(cobradoPrev*rate, mon)}</div>{dual(cobradoPrev)}</div>}
                <div><div style={{fontSize:10.5,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em'}}>Saldo pendiente</div><div className="radar-num" style={{fontSize:13,fontWeight:700,color:saldoUSD>0?C.redText:C.greenText}}>{fmtMon(saldoOrig, mon)}</div>{dual(saldoUSD)}</div>
              </div>
            </div>
            <Inp label="Fecha de cobro" value={fPago.fecha} onChange={v=>setFPago(x=>({...x,fecha:v}))} type="date"/>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Monto a cobrar ({SYM[mon]||'$'} {mon})</div>
              <input type="number" className="radar-num" value={fPago.monto} onChange={e=>setFPago(x=>({...x,monto:e.target.value}))} placeholder="0.00" max={saldoOrig}
                style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:15,fontWeight:700,letterSpacing:'-0.015em',outline:'none'}}/>
              {montoOrig>0&&mon!=='USD'&&(
                <div className="radar-num" style={{fontSize:11,color:C.t4,marginTop:4}}>≈ {fmt2(rate>0?montoOrig/rate:montoOrig)} USD</div>
              )}
              {fPago.monto&&montoOrig<saldoOrig&&(
                <div className="radar-num" style={{fontSize:11,fontWeight:600,color:C.orangeText,marginTop:4}}>
                  Pago parcial — quedará saldo de {fmtMon(saldoOrig-montoOrig, mon)}
                </div>
              )}
              {fPago.monto&&montoOrig>=saldoOrig&&saldoOrig>0&&(
                <div style={{fontSize:11,fontWeight:600,color:C.greenText,marginTop:4}}>✓ Cubre el saldo completo</div>
              )}
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Método de pago</div>
              <select value={fPago.metodo} onChange={e=>setFPago(x=>({...x,metodo:e.target.value}))}
                className="radar-input" style={{width:'100%',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}>
                {['Transferencia','Cheque','Efectivo','Tarjeta','Depósito'].map(m=><option key={m}>{m}</option>)}
              </select>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Vendedor</div>
              <select value={fPago.vendedor} onChange={e=>setFPago(x=>({...x,vendedor:e.target.value}))}
                className="radar-input" style={{width:'100%',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}>
                <option value="">— Seleccionar —</option>
                {usuarios.map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div style={{display:'flex',gap:8}}>
              <Btn variant="secondary" onClick={()=>setModal(null)}>Cancelar</Btn>
              <Btn onClick={registrarPago} disabled={!fPago.monto||saving} full>{saving?'Guardando...':'Confirmar cobro'}</Btn>
            </div>
          </>;
        })()}
      </Modal>
    </>
  );

  if (embedded) return contenido;
  return (
    <div style={{flex:1,overflowY:'auto',background:C.bg1,padding:24}}>
      <div style={{maxWidth:960,margin:'0 auto'}}>{contenido}</div>
    </div>
  );
}

function FacturaCard({ f, fmtUSD, fmtFecha, editFecha, editVal, setEditFecha, setEditVal, guardarFechaEstimada, color }) {
  return (
    <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 10px',display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
      <span style={{fontSize:11,fontWeight:800,color:color||C.blue,minWidth:70}}>{f.doc_num}</span>
      <span style={{fontSize:11,color:C.t2,flex:1,minWidth:100,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.card_name}</span>
      <span style={{fontSize:11,fontWeight:700,color:C.t1,whiteSpace:'nowrap'}}>{fmtUSD(f.total_usd)}</span>
      {editFecha===f.doc_num ? (
        <div style={{display:'flex',gap:4,alignItems:'center'}}>
          <input type="date" value={editVal} onChange={e=>setEditVal(e.target.value)}
            style={{background:C.bg2,border:`1px solid ${C.blue}`,borderRadius:5,padding:'2px 6px',color:C.t1,fontSize:11,outline:'none'}}/>
          <button onClick={()=>guardarFechaEstimada(f.doc_num,editVal)} disabled={!editVal}
            style={{background:`${C.green}20`,border:`1px solid ${C.green}`,borderRadius:5,padding:'2px 6px',color:C.green,fontSize:10,cursor:'pointer'}}>✓</button>
          <button onClick={()=>setEditFecha(null)}
            style={{background:'none',border:`1px solid ${C.border}`,borderRadius:5,padding:'2px 6px',color:C.t4,fontSize:10,cursor:'pointer'}}>✕</button>
        </div>
      ) : (
        <button onClick={()=>{ setEditFecha(f.doc_num); setEditVal(f.fecha_estimada_pago||f.doc_date||''); }}
          style={{background:'none',border:`1px solid ${C.border}`,borderRadius:5,padding:'2px 8px',color:f.fecha_estimada_pago?C.green:C.t4,fontSize:10,cursor:'pointer',whiteSpace:'nowrap'}}>
          {f.fecha_estimada_pago ? `📅 ${fmtFecha(f.fecha_estimada_pago)}` : '+ Fecha est.'}
        </button>
      )}
    </div>
  );
}

function CobrosFacturacion({ usuario, empresaActiva, clientes = [], equipos = [], initialTab }) {
  const [tab, setTab]             = useState(() => ['resumen','porcobrar','cobrado','cliente','gestion','ingresos'].includes(initialTab) ? initialTab : 'resumen');
  // Tasa oficial del día (la que el cron baja del banco central y empuja a SAP)
  const [tcInfo, setTcInfo] = useState(null);
  useEffect(() => { (async () => {
    const esCR = empresaActiva === 'costarica';
    const { data } = await supabase.from('radar_config').select('value').eq('key', esCR ? 'tasa_crc_usd' : 'tasa_hnl_usd').maybeSingle();
    if (data?.value?.rate) setTcInfo({ rate: data.value.rate, ts: data.value.ts || null,
      fuente: esCR ? 'Banco Central de Costa Rica' : 'Banco Central de Honduras (BCH)',
      simbolo: esCR ? '₡' : 'L' });
    else setTcInfo(null);
  })(); }, [empresaActiva]);
  // Filtro de bucket al clickear una tarjeta de aging (antes: setBucketFiltro sin estado → CRASH)
  const [bucketFiltro, setBucketFiltro] = useState(null);
  const [facturas, setFacturas]   = useState([]);   // pendientes (pagado=false)
  const [clientesMap, setClientesMap] = useState({}); // card_code -> cliente (crédito/contacto)
  const [cobros, setCobros]       = useState([]);   // cobros_sap del período (pestaña Cobrado)
  const [notasCredito, setNotasCredito] = useState([]); // NC abiertas (crédito a favor)
  const [gestiones, setGestiones] = useState([]);   // gestiones_cobro
  const [kpiCobradoMes, setKpiCobradoMes] = useState(0);
  const [loading, setLoading]     = useState(true);
  const [loadingCobros, setLoadingCobros] = useState(false);
  const [periodo, setPeriodo]     = useState('mes');
  const [periodoMes, setPeriodoMes] = useState(new Date().toISOString().slice(0,7));
  const [modoCob, setModoCob]   = useState('mes');   // mes | semana | rango
  const [semanaCob, setSemanaCob] = useState(()=>{ const d=new Date(); const g=d.getDay(); d.setDate(d.getDate()+(g===0?-6:1-g)); return d.toISOString().slice(0,10); }); // lunes
  const [rangoIni, setRangoIni] = useState(new Date(Date.now()-7*86400000).toISOString().slice(0,10));
  const [rangoFin, setRangoFin] = useState(new Date().toISOString().slice(0,10));
  const [syncing, setSyncing]     = useState(false);
  const [syncMsg, setSyncMsg]     = useState('');
  const [copied, setCopied]       = useState(false);
  const [selCliente, setSelCliente] = useState(null); // card_code drill-down
  const [gestModal, setGestModal] = useState(null);   // {card_code, card_name, factura_doc_num?}
  const [gSaving, setGSaving]     = useState(false);
  const [gForm, setGForm]         = useState(null);

  const hoy = new Date();
  const hoyStr = HOY_STR();
  const pad = n => String(n).padStart(2,'0');
  const iso = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const fmtFecha = d => d ? new Date(String(d).slice(0,10)+'T12:00:00').toLocaleDateString('es-HN',{day:'2-digit',month:'short'}) : '—';
  const fmtFechaLarga = d => d ? new Date(String(d).slice(0,10)+'T12:00:00').toLocaleDateString('es-HN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
  const fmtMesLabel = mes => { const [y,m]=mes.split('-'); return new Date(parseInt(y),parseInt(m)-1,1).toLocaleDateString('es-HN',{month:'long',year:'numeric'}); };
  const soloHN = empresaActiva === 'honduras';
  const ccDe = sap_code => empresaActiva ? String(sap_code||'').replace(`${empresaActiva}_`,'') : String(sap_code||'').replace(/^[a-z]+_/,'');

  // ── Cargadores ──────────────────────────────────────────────────────────────
  const cargarBase = async () => {
    setLoading(true);
    let qf = supabase.from('facturas_sap').select('*').eq('pagado',false).order('doc_due_date',{ascending:true}).limit(2000);
    if (empresaActiva) qf = qf.eq('empresa_id',empresaActiva);
    let qc = supabase.from('clientes').select('sap_code,empresa,telefono,cellular,email,contacto,ciudad,vendedor,credit_limit,balance').neq('estado','inactivo');
    if (empresaActiva) qc = qc.eq('empresa_id',empresaActiva);
    let qg = supabase.from('gestiones_cobro').select('*').order('created_at',{ascending:false}).limit(500);
    if (empresaActiva) qg = qg.eq('empresa_id',empresaActiva);
    // Cobrado del mes (KPI): cobros_sap del mes en curso
    const mesIni = `${new Date().toISOString().slice(0,7)}-01`;
    let qk = supabase.from('cobros_sap').select('total_usd,doc_total,empresa_id').gte('doc_date',mesIni);
    if (empresaActiva) qk = qk.eq('empresa_id',empresaActiva);
    // Notas de crédito abiertas (crédito a favor del cliente, a netear contra su saldo)
    let qn = supabase.from('notas_credito_sap').select('*');
    if (empresaActiva) qn = qn.eq('empresa_id',empresaActiva);

    const [{data:fd},{data:cd},{data:gd},{data:kd},{data:nd}] = await Promise.all([qf,qc,qg,qk,qn]);
    setFacturas((fd||[]).filter(f => saldoUSDde(f) > 0.01 && !esInterco(f)));
    const map = {};
    (cd||[]).forEach(c => { const cc = ccDe(c.sap_code); if (cc) map[cc] = c; });
    setClientesMap(map);
    setGestiones(gd||[]);
    setNotasCredito((nd||[]).filter(n => (n.saldo_usd||0) > 0.01));
    setKpiCobradoMes((kd||[]).reduce((s,c)=>s+(c.total_usd||c.doc_total||0),0));
    setLoading(false);
  };

  // Rango según el modo elegido (mes / semana / rango de fechas exacto)
  const lunesISO = (base) => { const d=new Date(base+'T00:00:00'); const g=d.getDay(); d.setDate(d.getDate()+(g===0?-6:1-g)); return iso(d); };
  const getRango = () => {
    if (modoCob==='rango') return { desde: rangoIni, hasta: rangoFin };
    if (modoCob==='semana') { const d=new Date(semanaCob+'T00:00:00'); const fin=new Date(d); fin.setDate(fin.getDate()+6); return { desde:semanaCob, hasta:iso(fin) }; }
    const [y,m]=periodoMes.split('-'); const last=new Date(parseInt(y),parseInt(m),0);
    return { desde:`${periodoMes}-01`, hasta:iso(last) };
  };

  const cargarCobros = async () => {
    setLoadingCobros(true);
    const {desde,hasta} = getRango();
    let q = supabase.from('cobros_sap').select('*').gte('doc_date',desde).lte('doc_date',hasta).gt('doc_total',0).order('doc_date',{ascending:false});
    if (empresaActiva) q = q.eq('empresa_id',empresaActiva);
    const {data} = await q; setCobros(data||[]); setLoadingCobros(false);
  };

  useEffect(() => { cargarBase(); /* eslint-disable-next-line */ }, [empresaActiva]);
  useEffect(() => { if (tab==='cobrado') cargarCobros(); /* eslint-disable-next-line */ }, [tab,periodoMes,modoCob,semanaCob,rangoIni,rangoFin,empresaActiva]);

  // ── Sincronización SAP (facturas + clientes + cobros) ────────────────────────
  const syncSAP = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      // Seguridad: las credenciales SAP nunca pasan por el navegador — el
      // servidor las resuelve por empresa_id.
      const objetivos = empresaActiva ? [empresaActiva] : ['honduras','costarica'];
      const results = [];
      for (const eid of objetivos) {
        for (const type of ['facturas','clientes','cobros']) {
          setSyncMsg(`⏳ ${eid==='honduras'?'HN':'CR'} · ${type}...`);
          try {
            await fetch('/api/sap',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'sync',type,empresa_id:eid})});
          } catch {}
        }
        results.push(`${eid==='honduras'?'HN':'CR'} ✓`);
      }
      setSyncMsg(results.length ? `✅ Sincronizado ${results.join(' · ')}` : '⚠ Sin config SAP');
      await cargarBase();
      if (tab==='cobrado') cargarCobros();
    } catch(e) { setSyncMsg('❌ '+e.message); }
    setSyncing(false);
  };

  // ── Notas de crédito abiertas (crédito a favor) por cliente ──────────────────
  const creditoPorCliente = (() => {
    const g = {};
    notasCredito.forEach(n => {
      const cc = n.card_code || n.card_name || '—';
      if (!g[cc]) g[cc] = { saldo:0, items:[] };
      g[cc].saldo += (n.saldo_usd||0);
      g[cc].items.push(n);
    });
    return g;
  })();
  const creditoTotalNC = notasCredito.reduce((s,n)=>s+(n.saldo_usd||0),0);

  // ── Cálculos de cartera (AR) ─────────────────────────────────────────────────
  const totalPorCobrar = facturas.reduce((s,f)=>s+saldoUSDde(f),0);
  const vencidoUSD = facturas.filter(f=>diasAtrasoDe(f,hoyStr)>0).reduce((s,f)=>s+saldoUSDde(f),0);
  const pctVencido = totalPorCobrar>0 ? (vencidoUSD/totalPorCobrar*100) : 0;
  const dsoAprox = (() => {
    const tot = facturas.reduce((s,f)=>s+saldoUSDde(f),0);
    if (!tot) return 0;
    const w = facturas.reduce((s,f)=>s + Math.max(0,diasEntre(f.doc_date,hoyStr))*saldoUSDde(f), 0);
    return Math.round(w/tot);
  })();
  const agingData = AGING.map(b => {
    const items = facturas.filter(f => bucketDe(diasAtrasoDe(f,hoyStr)).key === b.key);
    return { ...b, monto: items.reduce((s,f)=>s+saldoUSDde(f),0), count: items.length };
  });
  const maxAging = Math.max(...agingData.map(b=>b.monto), 1);

  // Agrupar por cliente
  const porCliente = (() => {
    const g = {};
    facturas.forEach(f => {
      const cc = f.card_code || f.card_name || '—';
      if (!g[cc]) g[cc] = { card_code:f.card_code, card_name:f.card_name, saldo:0, count:0, vencido:0, peorDias:-9999, items:[] };
      const d = diasAtrasoDe(f,hoyStr);
      g[cc].saldo += saldoUSDde(f);
      g[cc].count += 1;
      if (d>0) g[cc].vencido += saldoUSDde(f);
      if (d > g[cc].peorDias) g[cc].peorDias = d;
      g[cc].items.push(f);
    });
    // Netear crédito de NC abiertas contra el saldo de cada cliente
    Object.values(g).forEach(c => {
      const cr = creditoPorCliente[c.card_code] || creditoPorCliente[c.card_name];
      c.creditoNC = cr ? cr.saldo : 0;
      c.ncItems = cr ? cr.items : [];
      c.saldoNeto = Math.max(0, c.saldo - c.creditoNC);
    });
    return Object.values(g).sort((a,b)=>b.saldoNeto-a.saldoNeto);
  })();
  const topDeudores = porCliente.slice(0,10);

  // Cartera por moneda
  const carteraMoneda = (() => {
    const g = {};
    facturas.forEach(f => {
      const m = f.moneda || 'USD';
      if (!g[m]) g[m] = { moneda:m, orig:0, usd:0, count:0 };
      const rate = rateDeFactura(f);
      g[m].orig += saldoUSDde(f) * (m==='USD'?1:rate);
      g[m].usd  += saldoUSDde(f);
      g[m].count += 1;
    });
    return Object.values(g).sort((a,b)=>b.usd-a.usd);
  })();

  // ── WhatsApp helpers ─────────────────────────────────────────────────────────
  const waDigits = (phone) => {
    let d = String(phone||'').replace(/\D/g,'');
    if (!d) return '';
    if (d.length <= 8) d = (empresaActiva==='costarica'?'506':'504') + d;
    return d;
  };
  const waLinkCliente = (grupo) => {
    const cli = clientesMap[grupo.card_code] || {};
    const tel = waDigits(cli.cellular || cli.telefono);
    const masVieja = [...grupo.items].sort((a,b)=>diasAtrasoDe(b,hoyStr)-diasAtrasoDe(a,hoyStr))[0];
    const msg = `Estimados ${grupo.card_name || cli.empresa || ''}, de parte de ${ORG.nombre} les saludamos. `+
      `Le recordamos amablemente que tiene un saldo pendiente de ${fmtUSDG(grupo.saldo)} `+
      `en ${grupo.count} factura(s).`+
      (masVieja ? ` La más antigua (#${String(masVieja.doc_num).replace(/^\w+_/,'')}) venció el ${fmtFechaLarga(masVieja.doc_due_date||masVieja.doc_date)}.` : '')+
      ` Agradecemos su pronta gestión de pago. ¡Gracias!`;
    if (!tel) return null;
    return `https://wa.me/${tel}?text=${encodeURIComponent(msg)}`;
  };

  // ── Gestiones de cobro ───────────────────────────────────────────────────────
  const abrirGestion = (card_code, card_name, factura_doc_num=null) => {
    setGForm({ tipo:'nota', promesa_fecha:'', monto_prometido:'', nota:'', proximo_paso:'', proximo_paso_fecha:'', canal:'whatsapp' });
    setGestModal({ card_code, card_name, factura_doc_num });
  };
  const guardarGestion = async () => {
    if (!gestModal || !gForm) return;
    setGSaving(true);
    const row = {
      empresa_id: empresaActiva || 'honduras',
      card_code: gestModal.card_code || null,
      card_name: gestModal.card_name || null,
      factura_doc_num: gestModal.factura_doc_num ? String(gestModal.factura_doc_num) : null,
      tipo: gForm.tipo,
      promesa_fecha: gForm.promesa_fecha || null,
      monto_prometido: gForm.monto_prometido ? parseFloat(gForm.monto_prometido) : null,
      nota: gForm.nota || null,
      proximo_paso: gForm.proximo_paso || null,
      proximo_paso_fecha: gForm.proximo_paso_fecha || null,
      canal: gForm.canal || null,
      usuario: usuario?.nombre || null,
    };
    const { data } = await supabase.from('gestiones_cobro').insert(row).select().single();
    if (data) setGestiones(g => [data, ...g]);
    setGSaving(false); setGestModal(null); setGForm(null);
  };
  const gestionesDe = (cc) => gestiones.filter(g => g.card_code === cc);

  // ── Reporte WhatsApp (copiar) ────────────────────────────────────────────────
  const generarResumen = () => {
    const lbl = empresaActiva ? (soloHN?'HONDURAS':'COSTA RICA') : 'MONTASA';
    const top = topDeudores.slice(0,8).map((d,i)=>` ${i+1}. ${d.card_name}: ${fmtUSD0(d.saldo)}${d.vencido>0?` (⚠ ${fmtUSD0(d.vencido)} venc.)`:''}`).join('\n');
    const ag = agingData.map(b=>` ${b.label}: ${fmtUSD0(b.monto)} (${b.count})`).join('\n');
    return `📊 *CARTERA POR COBRAR — ${lbl}*\n${fmtFechaLarga(hoyStr)}\n\n`+
      `💰 Total por cobrar: *${fmtUSDG(totalPorCobrar)}*\n`+
      `⚠️ Vencido: *${fmtUSDG(vencidoUSD)}* (${pctVencido.toFixed(0)}%)\n`+
      `📅 Antigüedad prom.: *${dsoAprox} días*\n`+
      `✅ Cobrado del mes: *${fmtUSDG(kpiCobradoMes)}*\n\n`+
      `*ANTIGÜEDAD*\n━━━━━━━━━━━━━━\n${ag}\n\n`+
      `*TOP DEUDORES*\n━━━━━━━━━━━━━━\n${top}\n\n_Generado desde RADAR · ${ORG.nombre}_`;
  };
  const copiarResumen = async () => {
    try { await navigator.clipboard.writeText(generarResumen()); setCopied(true); setTimeout(()=>setCopied(false),2500); } catch {}
  };

  // ── Cobrado (pestaña): totales por método ────────────────────────────────────
  const fmtHNL = n => `L.${(n||0).toLocaleString('es-HN',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const fmtCobro = n => soloHN ? fmtHNL(n) : fmtUSDG(n);
  const getTotalCobro = c => soloHN ? (c.doc_total||0) : (c.total_usd||0);
  const cTot = cobros.reduce((s,c)=>s+getTotalCobro(c),0);
  const cContado = cobros.filter(c=>c.es_anticipo).reduce((s,c)=>s+getTotalCobro(c),0);
  const cCredito = cobros.filter(c=>!c.es_anticipo).reduce((s,c)=>s+getTotalCobro(c),0);
  const cISV = cobros.reduce((s,c)=>s+(soloHN?(c.vat_sum||0):(c.vat_sum||0)/(c.doc_rate||1)),0);

  // ── UI helpers ───────────────────────────────────────────────────────────────
  // Semáforo de utilización de línea de crédito — solo plenos del DS (ramp de AGING)
  const utilColor = pct => pct>=100?C.red:pct>=80?'#EA580C':pct>=50?C.orange:C.green;
  // Ícono SVG (stroke 1.5) por tipo de gestión — cero emojis (DS v3).
  const GEST_IC = {
    promesa:      <><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>,
    llamada:      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/>,
    recordatorio: <><path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></>,
    nota:         <><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></>,
  };
  const GestIcon = ({tipo, size=15}) => {
    const tone = tipo==='promesa'?TONES.green:tipo==='llamada'?TONES.primary:tipo==='recordatorio'?TONES.orange:TONES.neutral;
    return (
      <span style={{width:size+13,height:size+13,borderRadius:8,background:tone.tint,border:`1px solid ${tone.border}`,color:tone.text,display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{GEST_IC[tipo]||GEST_IC.nota}</svg>
      </span>
    );
  };

  return (
    <div style={{flex:1,overflowY:'auto',background:C.bg1,padding:24}}>
      <div style={{maxWidth:1280,margin:'0 auto'}}>
        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20,flexWrap:'wrap',gap:12}}>
          <div>
            <div style={{fontSize:20,fontWeight:800,letterSpacing:'-0.015em',color:C.t1}}>Finanzas</div>
            <div style={{fontSize:12,color:C.t3,marginTop:2}}>Cuentas por cobrar · antigüedad · gestión de cobranza — datos de SAP</div>
          </div>
          <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4}}>
            <button className="radar-pill" onClick={syncSAP} disabled={syncing}
              style={{display:'inline-flex',alignItems:'center',gap:7,height:34,padding:'0 14px',borderRadius:R.md,border:`1px solid ${C.primaryBorder}`,background:C.primaryTint,color:C.blue,fontSize:12.5,fontWeight:600,cursor:syncing?'wait':'pointer',opacity:syncing?.7:1,boxShadow:SH.xs}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{opacity:syncing?.55:1}}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
              {syncing?'Sincronizando...':'Sincronizar SAP'}
            </button>
            {syncMsg&&<div style={{fontSize:11,color:syncMsg.startsWith('✅')?C.greenText:syncMsg.startsWith('❌')?C.redText:C.t4}}>{syncMsg}</div>}
          </div>
        </div>

        {/* Tab bar — segmented control (receta 7.4, lenguaje del CRM aprobado) */}
        <div style={{display:'inline-flex',gap:2,marginBottom:22,background:C.bg1,borderRadius:R.md,padding:2,border:`1px solid ${C.border}`,flexWrap:'wrap'}}>
          {[['resumen','Resumen'],['porcobrar','Por cobrar'],['cobrado','Cobrado'],['cliente','Por cliente'],['gestion','Gestión'],['ingresos','Ingresos']].map(([v,l])=>(
            <button key={v} onClick={()=>setTab(v)}
              style={{height:26,padding:'0 14px',borderRadius:R.sm,border:'none',cursor:'pointer',whiteSpace:'nowrap',
                background:tab===v?C.bg2:'transparent',color:tab===v?C.t1:C.t3,
                boxShadow:tab===v?SH.sm:'none',
                fontSize:12,fontWeight:600,transition:'background .15s ease, color .15s ease'}}>
              {l}
            </button>
          ))}
        </div>

        {/* ════════════ INGRESOS (ex módulo standalone — fusión mapa IA) ════════════ */}
        {tab==='ingresos' && <Ingresos clientes={clientes} equipos={equipos} empresaActiva={empresaActiva}/>}

        {tab!=='ingresos' && (loading ? <div style={{textAlign:'center',color:C.t4,padding:60}}>Cargando cartera...</div> : <>

        {/* ════════════ RESUMEN ════════════ */}
        {tab==='resumen'&&(
          <div style={{display:'flex',flexDirection:'column',gap:16}}>
            {/* KPIs — estilo Stripe (receta 7.1, shared KpiCard) */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))',gap:16}}>
              <KpiCard label="Por cobrar (USD)" value={fmtUSD0(totalPorCobrar)} icon={KI.dollar} tone="primary"
                sub={creditoTotalNC>0?`Neto NC: ${fmtUSD0(Math.max(0,totalPorCobrar-creditoTotalNC))} · ${facturas.length} facturas`:`${facturas.length} facturas abiertas`}/>
              <KpiCard label="Vencido" value={fmtUSD0(vencidoUSD)} icon={KI.clock} tone={pctVencido>25?'red':'orange'} sub={`${pctVencido.toFixed(0)}% de la cartera`}/>
              {creditoTotalNC>0
                ? <KpiCard label="Crédito a favor (NC)" value={fmtUSD0(creditoTotalNC)} icon={KI.box} tone="green" sub={`${notasCredito.length} nota(s) de crédito`}/>
                : <KpiCard label="Antigüedad prom." value={`${dsoAprox} d`} icon={KI.trend} tone={dsoAprox>45?'red':dsoAprox>30?'orange':'green'} sub="DSO aprox. de la cartera"/>}
              <KpiCard label="Cobrado del mes" value={fmtUSD0(kpiCobradoMes)} icon={KI.check} tone="green" sub={fmtMesLabel(new Date().toISOString().slice(0,7))}/>
            </div>

            {/* Aging — panel receta 7.8: head con borde + barra apilada 6px (lenguaje del dashboard) */}
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:SH.sm,overflow:'hidden'}}>
              <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
                <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>Antigüedad de la cartera <span style={{fontSize:11.5,fontWeight:500,color:C.t3}}>· aging por vencimiento</span></div>
                <button className="radar-pill" onClick={copiarResumen}
                  style={{display:'inline-flex',alignItems:'center',gap:6,height:30,padding:'0 12px',borderRadius:R.md,border:`1px solid ${copied?C.greenBorder:C.border}`,background:copied?C.greenTint:C.bg2,color:copied?C.greenText:C.t2,fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:copied?'none':SH.xs}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{copied?<polyline points="20 6 9 17 4 12"/>:<><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></>}</svg>
                  {copied?'Copiado':'Copiar resumen'}
                </button>
              </div>
              <div style={{padding:16}}>
                {/* barra apilada 6px + leyenda con dots (mismo lenguaje del dashboard) */}
                <div style={{display:'flex',height:6,borderRadius:999,overflow:'hidden',background:C.bg3,marginBottom:10}}>
                  {agingData.filter(b=>b.monto>0).map(b=>(
                    <div key={b.key} title={`${b.label}: ${fmtUSDG(b.monto)} (${(b.monto/Math.max(totalPorCobrar,1)*100).toFixed(0)}%)`}
                      style={{width:`${totalPorCobrar>0?(b.monto/totalPorCobrar*100):0}%`,background:b.color}}/>
                  ))}
                </div>
                <div style={{display:'flex',gap:14,flexWrap:'wrap',marginBottom:16}}>
                  {agingData.filter(b=>b.monto>0).map(b=>(
                    <span key={b.key} className="radar-num" style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:10.5,color:C.t3}}>
                      <span style={{width:6,height:6,borderRadius:'50%',background:b.color,flexShrink:0}}/>
                      {b.short} <b style={{fontWeight:600,color:C.t2}}>{(b.monto/Math.max(totalPorCobrar,1)*100).toFixed(0)}%</b>
                    </span>
                  ))}
                </div>
                {/* cards por bucket — escala semántica (tinte + borde izq. pleno) */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:10}}>
                  {agingData.map(b=>(
                    <button key={b.key} className="radar-kpi" onClick={()=>{ setBucketFiltro(b.key); setTab('porcobrar'); }}
                      style={{textAlign:'left',background:b.tint,border:`1px solid ${b.bd}`,borderLeft:`3px solid ${b.color}`,borderRadius:R.md,padding:'10px 12px',cursor:'pointer'}}>
                      <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em'}}>{b.label}</div>
                      <div className="radar-num" style={{fontSize:16,fontWeight:700,letterSpacing:'-0.02em',color:b.text,marginTop:3}}>{fmtUSD0(b.monto)}</div>
                      <div className="radar-num" style={{fontSize:10.5,color:C.t4,marginTop:2}}>{b.count} factura(s)</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))',gap:16}}>
              {/* Top deudores — panel receta 7.8 */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:SH.sm,overflow:'hidden'}}>
                <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                  <span style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>Top 10 deudores</span>
                  <button onClick={()=>setTab('cliente')} className="radar-plus" style={{background:'transparent',border:'none',color:C.blue,fontSize:12,fontWeight:600,cursor:'pointer',padding:'3px 8px',borderRadius:R.sm}}>Ver todos</button>
                </div>
                <div style={{padding:'4px 8px 8px'}}>
                  {topDeudores.length===0&&<div style={{color:C.t4,fontSize:13,padding:20,textAlign:'center'}}>Sin cartera pendiente</div>}
                  {topDeudores.map((d,i)=>{
                    const b = bucketDe(d.peorDias);
                    return (
                      <div key={(d.card_code||'')+i} className="radar-hrow" onClick={()=>{ setSelCliente(d.card_code); setTab('cliente'); }}
                        style={{display:'flex',alignItems:'center',gap:10,padding:'9px 8px',borderRadius:8,cursor:'pointer',borderBottom:i<topDeudores.length-1?`1px solid ${C.bg3}`:'none'}}>
                        <div className="radar-num" style={{fontSize:11,fontWeight:700,color:C.t4,width:18}}>{i+1}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12.5,fontWeight:600,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.card_name}</div>
                          <div style={{display:'flex',gap:6,alignItems:'center',marginTop:3}}>
                            <span className="radar-num" style={{fontSize:10.5,color:C.t4}}>{d.count} fact.</span>
                            {d.peorDias>0&&<span className="radar-num" style={{display:'inline-flex',alignItems:'center',gap:4,background:b.tint,color:b.text,border:`1px solid ${b.bd}`,borderRadius:R.pill,padding:'0 7px',fontSize:9.5,fontWeight:600,lineHeight:1.7,whiteSpace:'nowrap'}}><span style={{width:5,height:5,borderRadius:'50%',background:b.color,flexShrink:0}}/>{d.peorDias}d</span>}
                          </div>
                        </div>
                        <div style={{textAlign:'right'}}>
                          <div className="radar-num" style={{fontSize:13.5,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>{fmtUSD0(d.saldoNeto)}</div>
                          {d.creditoNC>0&&<div className="radar-num" style={{fontSize:10,color:C.greenText,fontWeight:600}} title={`Crédito NC: ${fmtUSDG(d.creditoNC)} · Saldo bruto: ${fmtUSDG(d.saldo)}`}>NC −{fmtUSD0(d.creditoNC)}</div>}
                          {d.vencido>0&&<div className="radar-num" style={{fontSize:10,color:C.redText,fontWeight:600}}>{fmtUSD0(d.vencido)} venc.</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Cartera por moneda — panel receta 7.8 */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:SH.sm,overflow:'hidden'}}>
                <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`}}>
                  <span style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>Cartera por moneda</span>
                  <span style={{fontSize:11.5,fontWeight:500,color:C.t3,marginLeft:6}}>· moneda original + equivalente USD</span>
                </div>
                <div style={{padding:'4px 8px 8px'}}>
                  {carteraMoneda.length===0&&<div style={{color:C.t4,fontSize:13,padding:20,textAlign:'center'}}>Sin datos</div>}
                  {carteraMoneda.map((m,i)=>(
                    <div key={m.moneda} className="radar-hrow" style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'11px 8px',borderRadius:8,borderBottom:i<carteraMoneda.length-1?`1px solid ${C.bg3}`:'none'}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>{m.moneda}</div>
                        <div className="radar-num" style={{fontSize:10.5,color:C.t4}}>{m.count} factura(s)</div>
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div className="radar-num" style={{fontSize:15,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>{fmtMonG(m.orig,m.moneda)}</div>
                        {m.moneda!=='USD'&&<div className="radar-num" style={{fontSize:11,color:C.t4}}>≈ {fmtUSDG(m.usd)}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Tipo de cambio del día: la tasa oficial que RADAR baja del banco
                    central cada mañana y empuja a SAP. Se enseña acá porque es la
                    tasa con la que se está valorando toda esta cartera. */}
                {tcInfo && (
                  <div style={{borderTop:`1px solid ${C.border}`,background:C.primaryTint,padding:'11px 16px',display:'flex',alignItems:'center',gap:10}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:10,fontWeight:800,letterSpacing:'.06em',textTransform:'uppercase',color:C.primary}}>Tipo de cambio del día</div>
                      <div style={{fontSize:10.5,color:C.t4,marginTop:2}}>
                        {tcInfo.fuente} · se actualiza solo cada mañana{tcInfo.ts ? ` · ${new Date(tcInfo.ts).toLocaleDateString('es-HN',{day:'numeric',month:'short'})}` : ''}
                      </div>
                    </div>
                    <div className="radar-num" style={{fontSize:19,fontWeight:800,letterSpacing:'-0.02em',color:C.t1}}>
                      {tcInfo.simbolo} {Number(tcInfo.rate).toFixed(2)}
                      <span style={{fontSize:10.5,fontWeight:600,color:C.t4,marginLeft:4}}>/ USD</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ════════════ POR COBRAR ════════════ */}
        {tab==='porcobrar'&&(
          <div>
            <Cobros usuario={usuario} empresaActiva={empresaActiva} embedded initBucket={bucketFiltro}/>
          </div>
        )}

        {/* ════════════ COBRADO ════════════ */}
        {tab==='cobrado'&&(
          <div>
            {/* Filtros — flotando sobre el gris, lenguaje del CRM (segmented + pills) */}
            <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
              <div style={{display:'inline-flex',gap:2,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2,flexShrink:0}}>
                {[['mes','Mes'],['semana','Semana'],['rango','Rango']].map(([v,l])=>(
                  <button key={v} onClick={()=>setModoCob(v)}
                    style={{height:26,padding:'0 12px',borderRadius:R.sm,border:'none',cursor:'pointer',whiteSpace:'nowrap',
                      background:modoCob===v?C.bg2:'transparent',color:modoCob===v?C.t1:C.t3,boxShadow:modoCob===v?SH.sm:'none',
                      fontSize:12,fontWeight:600,transition:'background .15s ease, color .15s ease'}}>{l}</button>
                ))}
              </div>
              <span style={{width:1,height:20,background:C.border,flexShrink:0}}/>
              {modoCob==='mes' && (
                <input type="month" className="radar-input radar-num" value={periodoMes} onChange={e=>setPeriodoMes(e.target.value)}
                  style={{height:30,boxSizing:'border-box',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'0 10px',color:C.t1,fontSize:12.5,outline:'none',boxShadow:SH.xs}}/>
              )}
              {modoCob==='semana' && (<>
                <span style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em'}}>Semana del</span>
                <input type="date" className="radar-input radar-num" value={semanaCob} onChange={e=>setSemanaCob(lunesISO(e.target.value))}
                  style={{height:30,boxSizing:'border-box',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'0 10px',color:C.t1,fontSize:12.5,outline:'none',boxShadow:SH.xs}}/>
                <span className="radar-num" style={{fontSize:11,color:C.t4}}>lun {fmtFecha(semanaCob)} → dom {fmtFecha(getRango().hasta)}</span>
              </>)}
              {modoCob==='rango' && (<>
                <span style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em'}}>Del</span>
                <input type="date" className="radar-input radar-num" value={rangoIni} onChange={e=>setRangoIni(e.target.value)}
                  style={{height:30,boxSizing:'border-box',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'0 10px',color:C.t1,fontSize:12.5,outline:'none',boxShadow:SH.xs}}/>
                <span style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em'}}>al</span>
                <input type="date" className="radar-input radar-num" value={rangoFin} onChange={e=>setRangoFin(e.target.value)}
                  style={{height:30,boxSizing:'border-box',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'0 10px',color:C.t1,fontSize:12.5,outline:'none',boxShadow:SH.xs}}/>
              </>)}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))',gap:16,marginBottom:16}}>
              <KpiCard label="Total cobrado" value={fmtCobro(cTot)} icon={KI.check} tone="green" sub={`${cobros.length} pagos`}/>
              <KpiCard label="Contado" value={fmtCobro(cContado)} icon={KI.dollar} tone="primary" sub={`${cobros.filter(c=>c.es_anticipo).length} pagos`}/>
              <KpiCard label="Recup. crédito" value={fmtCobro(cCredito)} icon={KI.trend} tone="info" sub={`${cobros.filter(c=>!c.es_anticipo).length} pagos`}/>
              <KpiCard label={soloHN?'ISV':'Impuesto'} value={fmtCobro(cISV)} icon={KI.box} tone="neutral" sub="del período"/>
            </div>
            {loadingCobros?<div style={{padding:20}}><SkelRows n={6}/></div>:(
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'auto',boxShadow:SH.sm}}>
                <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',minWidth:760}}>
                  <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                    {['#SAP','Cliente','Fecha','Vendedor',soloHN?'Total (HNL)':'Total (USD)','Método','Tipo'].map((h,i)=>(
                      <th key={i} style={{padding:'9px 12px',textAlign:i===4?'right':'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {cobros.length===0&&<tr><td colSpan={7} style={{padding:32,textAlign:'center',color:C.t4,fontSize:13}}>Sin cobros en el período seleccionado. Sincroniza SAP para cargar datos.</td></tr>}
                    {cobros.map(c=>{
                      const metodo = [c.cash_sum>0&&'Efectivo',c.credit_sum>0&&'Tarjeta',c.transfer_sum>0&&'Transfer.',c.check_sum>0&&'Cheque'].filter(Boolean).join(', ')||'—';
                      const tipoT = c.es_anticipo ? TONES.primary : TONES.info;
                      return (
                        <tr key={`${c.doc_num}-${c.empresa_id}`} style={{borderBottom:`1px solid ${C.bg3}`}}>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:C.blue}}>{c.doc_num}</td>
                          <td style={{padding:'8px 12px',fontSize:12,color:C.t2,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.card_name}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:11,color:C.t3,whiteSpace:'nowrap'}}>{fmtFecha(c.doc_date)}</td>
                          <td style={{padding:'8px 12px',fontSize:11,color:C.t3,maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.vendedor||'—'}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:C.t1,whiteSpace:'nowrap',textAlign:'right'}}>{fmtCobro(getTotalCobro(c))}</td>
                          <td style={{padding:'8px 12px',fontSize:11,color:C.t3}}>{metodo}</td>
                          <td style={{padding:'8px 12px'}}>
                            <span style={{background:tipoT.tint,color:tipoT.text,border:`1px solid ${tipoT.border}`,borderRadius:R.pill,padding:'2px 8px',fontSize:10.5,fontWeight:600,whiteSpace:'nowrap'}}>
                              {c.es_anticipo?'Contado':'Crédito'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ════════════ POR CLIENTE ════════════ */}
        {tab==='cliente'&&(() => {
          const sel = porCliente.find(d=>d.card_code===selCliente) || null;
          const cli = sel ? (clientesMap[sel.card_code]||{}) : {};
          return (
          <div style={{display:'grid',gridTemplateColumns:sel?'minmax(260px,340px) 1fr':'1fr',gap:16,alignItems:'start'}}>
            {/* lista de clientes */}
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',boxShadow:SH.sm}}>
              <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>Clientes con saldo <span className="radar-num" style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,color:C.t3,marginLeft:4}}>{porCliente.length}</span></div>
              <div style={{maxHeight:'66vh',overflowY:'auto'}}>
                {porCliente.length===0&&<div style={{color:C.t4,fontSize:13,padding:20,textAlign:'center'}}>Sin cartera pendiente</div>}
                {porCliente.map((d,i)=>{
                  const activo = d.card_code===selCliente;
                  return (
                    <div key={(d.card_code||'')+i} className="radar-hrow" onClick={()=>setSelCliente(d.card_code)}
                      style={{padding:'10px 14px',borderBottom:`1px solid ${C.bg3}`,cursor:'pointer',borderLeft:`3px solid ${activo?C.blue:'transparent'}`,background:activo?C.primaryTint:'transparent'}}>
                      <div style={{display:'flex',justifyContent:'space-between',gap:8}}>
                        <div style={{fontSize:12.5,fontWeight:600,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.card_name}</div>
                        <div className="radar-num" style={{fontSize:12.5,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,whiteSpace:'nowrap'}}>{fmtUSD0(d.saldoNeto)}</div>
                      </div>
                      <div style={{display:'flex',gap:6,alignItems:'center',marginTop:3}}>
                        <span className="radar-num" style={{fontSize:10.5,color:C.t4}}>{d.count} fact.</span>
                        <AgingBadge dias={d.peorDias} mini/>
                        {d.creditoNC>0&&<span style={{background:C.greenTint,color:C.greenText,border:`1px solid ${C.greenBorder}`,borderRadius:R.pill,padding:'0 7px',fontSize:9.5,fontWeight:600,lineHeight:1.7}} title={`Crédito NC: ${fmtUSDG(d.creditoNC)}`}>NC</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* detalle */}
            {sel&&(
              <div style={{display:'flex',flexDirection:'column',gap:14}}>
                {/* encabezado cliente */}
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:20,boxShadow:SH.sm}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,flexWrap:'wrap'}}>
                    <div>
                      <div style={{fontSize:18,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>{sel.card_name}</div>
                      <div style={{fontSize:11.5,color:C.t3,marginTop:3}}>
                        {[cli.contacto, cli.ciudad, cli.vendedor?`Vendedor: ${cli.vendedor}`:null].filter(Boolean).join(' · ')}
                      </div>
                      <div style={{display:'flex',gap:14,marginTop:6,flexWrap:'wrap'}}>
                        {(cli.cellular||cli.telefono)&&<span className="radar-num" style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:12,color:C.t3}}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{color:C.t4}}>{KI.phone}</svg>{cli.cellular||cli.telefono}</span>}
                        {cli.email&&<span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:12,color:C.t3}}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{color:C.t4}}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>{cli.email}</span>}
                      </div>
                    </div>
                    <div style={{display:'flex',gap:8}}>
                      {waLinkCliente(sel)
                        ? <a className="radar-pill" href={waLinkCliente(sel)} target="_blank" rel="noreferrer"
                            style={{display:'inline-flex',alignItems:'center',gap:6,height:32,padding:'0 13px',borderRadius:R.md,background:C.greenTint,border:`1px solid ${C.greenBorder}`,color:C.greenText,fontSize:12,fontWeight:600,textDecoration:'none',whiteSpace:'nowrap',boxShadow:SH.xs}}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>
                            Recordatorio</a>
                        : <span title="Sin teléfono" style={{display:'inline-flex',alignItems:'center',height:32,padding:'0 13px',borderRadius:R.md,background:C.bg3,color:C.t4,fontSize:12,fontWeight:600}}>Sin tel.</span>}
                      <button className="radar-pill" onClick={()=>abrirGestion(sel.card_code, sel.card_name)}
                        style={{display:'inline-flex',alignItems:'center',gap:6,height:32,padding:'0 13px',borderRadius:R.md,border:`1px solid ${C.primaryBorder}`,background:C.primaryTint,color:C.blue,fontSize:12,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap',boxShadow:SH.xs}}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        Gestión</button>
                    </div>
                  </div>
                  {/* saldo + crédito */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10,marginTop:16}}>
                    <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 12px'}}>
                      <div style={{fontSize:10.5,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em'}}>Saldo {sel.creditoNC>0?'neto':'total'}</div>
                      <div className="radar-num" style={{fontSize:18,fontWeight:700,letterSpacing:'-0.02em',color:C.t1}}>{fmtUSDG(sel.saldoNeto)}</div>
                      {sel.creditoNC>0&&<div className="radar-num" style={{fontSize:10,color:C.t4,marginTop:2}}>bruto {fmtUSD0(sel.saldo)}</div>}
                    </div>
                    <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 12px'}}>
                      <div style={{fontSize:10.5,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em'}}>Vencido</div>
                      <div className="radar-num" style={{fontSize:18,fontWeight:700,letterSpacing:'-0.02em',color:sel.vencido>0?C.redText:C.greenText}}>{fmtUSDG(sel.vencido)}</div>
                    </div>
                    {sel.creditoNC>0&&(
                      <div style={{background:C.greenTint,borderRadius:10,padding:'10px 12px',border:`1px solid ${C.greenBorder}`}}>
                        <div style={{fontSize:10.5,color:C.greenText,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em'}}>Crédito a favor (NC)</div>
                        <div className="radar-num" style={{fontSize:18,fontWeight:700,letterSpacing:'-0.02em',color:C.greenText}}>−{fmtUSDG(sel.creditoNC)}</div>
                        <div className="radar-num" style={{fontSize:10,color:C.t4,marginTop:2}}>{sel.ncItems.length} nota(s): {sel.ncItems.map(n=>String(n.doc_num)).join(', ')}</div>
                      </div>
                    )}
                    {cli.credit_limit>0&&(()=>{
                      const bal = cli.balance||0; const lim = cli.credit_limit||0; const pct = lim>0?(bal/lim*100):0;
                      const utilText = pct>=100?C.redText:pct>=80?'#C2410C':pct>=50?C.orangeText:C.greenText;
                      return (
                        <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 12px'}}>
                          <div style={{fontSize:10.5,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.06em'}}>Línea de crédito</div>
                          <div className="radar-num" style={{fontSize:13,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,marginTop:2}}>{fmtUSD0(bal)} <span style={{color:C.t4,fontWeight:500}}>/ {fmtUSD0(lim)}</span></div>
                          <div style={{height:6,background:C.bg3,borderRadius:999,marginTop:6,overflow:'hidden'}}>
                            <div style={{width:`${Math.min(100,pct)}%`,height:'100%',background:utilColor(pct),borderRadius:999}}/>
                          </div>
                          <div className="radar-num" style={{fontSize:10,color:utilText,fontWeight:600,marginTop:3}}>{pct.toFixed(0)}% usado</div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* facturas del cliente */}
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',boxShadow:SH.sm}}>
                  <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>Facturas abiertas <span className="radar-num" style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,color:C.t3,marginLeft:4}}>{sel.items.length}</span></div>
                  <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                      {['Factura','Emisión','Vence','Antigüedad','Saldo'].map(h=>(
                        <th key={h} style={{padding:'8px 14px',textAlign:h==='Saldo'?'right':'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {[...sel.items].sort((a,b)=>diasAtrasoDe(b,hoyStr)-diasAtrasoDe(a,hoyStr)).map(f=>{
                        const dias = diasAtrasoDe(f,hoyStr);
                        return (
                          <tr key={f.doc_num} style={{borderBottom:`1px solid ${C.bg3}`}}>
                            <td className="radar-num" style={{padding:'8px 14px',fontSize:12,fontWeight:700,color:C.blue}}>{String(f.doc_num||'').replace(/^\w+_/,'')}</td>
                            <td className="radar-num" style={{padding:'8px 14px',fontSize:11,color:C.t3}}>{fmtFecha(f.doc_date)}</td>
                            <td className="radar-num" style={{padding:'8px 14px',fontSize:11,color:C.t3}}>{fmtFecha(f.doc_due_date)}</td>
                            <td style={{padding:'8px 14px'}}><AgingBadge dias={dias} mini/></td>
                            <td className="radar-num" style={{padding:'8px 14px',fontSize:12,fontWeight:700,color:C.t1,textAlign:'right'}}>
                              {fmtMonG(saldoUSDde(f)*rateDeFactura(f), f.moneda)}
                              {f.moneda!=='USD'&&<div style={{fontSize:10,color:C.t4,fontWeight:400}}>≈ {fmtUSDG(saldoUSDde(f))}</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* notas de crédito abiertas (crédito a favor) */}
                {sel.creditoNC>0&&(
                  <div style={{background:C.bg2,border:`1px solid ${C.greenBorder}`,borderRadius:12,overflow:'hidden',boxShadow:SH.sm}}>
                    <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.greenText}}>Notas de crédito abiertas <span className="radar-num" style={{background:C.greenTint,border:`1px solid ${C.greenBorder}`,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,color:C.greenText,marginLeft:4}}>{sel.ncItems.length}</span> <span style={{fontSize:11.5,fontWeight:500,color:C.t3}}>· crédito a favor</span></div>
                    <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                        {['NC','Fecha','Total','Aplicado','Disponible'].map(h=>(
                          <th key={h} style={{padding:'8px 14px',textAlign:['Total','Aplicado','Disponible'].includes(h)?'right':'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {sel.ncItems.map(n=>(
                          <tr key={n.doc_num} style={{borderBottom:`1px solid ${C.bg3}`}}>
                            <td className="radar-num" style={{padding:'8px 14px',fontSize:12,fontWeight:700,color:C.greenText}}>{String(n.doc_num)}</td>
                            <td className="radar-num" style={{padding:'8px 14px',fontSize:11,color:C.t3}}>{fmtFecha(n.doc_date)}</td>
                            <td className="radar-num" style={{padding:'8px 14px',fontSize:11,color:C.t3,textAlign:'right'}}>{fmtMonG(n.doc_total,n.moneda)}</td>
                            <td className="radar-num" style={{padding:'8px 14px',fontSize:11,color:C.t4,textAlign:'right'}}>{fmtUSD0(n.aplicado_usd)}</td>
                            <td className="radar-num" style={{padding:'8px 14px',fontSize:12,fontWeight:700,color:C.greenText,textAlign:'right'}}>{fmtUSDG(n.saldo_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* historial gestiones — panel receta 7.8 */}
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:SH.sm,overflow:'hidden'}}>
                  <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>Historial de gestiones <span className="radar-num" style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,color:C.t3,marginLeft:4}}>{gestionesDe(sel.card_code).length}</span></div>
                  <div style={{padding:16}}>
                  {gestionesDe(sel.card_code).length===0
                    ? <div style={{fontSize:12,color:C.t4,padding:'4px 0'}}>Sin gestiones registradas. Usa el botón “Gestión”.</div>
                    : <div style={{display:'flex',flexDirection:'column',gap:8}}>
                        {gestionesDe(sel.card_code).map(g=>(
                          <div key={g.id} style={{display:'flex',gap:10,padding:'8px 10px',borderRadius:R.md,background:C.bg1,border:`1px solid ${C.border}`}}>
                            <GestIcon tipo={g.tipo}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:12,color:C.t2}}>{g.nota||g.proximo_paso||g.tipo}</div>
                              <div className="radar-num" style={{fontSize:10,color:C.t4,marginTop:2}}>
                                {fmtFechaLarga(g.created_at)} {g.usuario?`· ${g.usuario}`:''}
                                {g.promesa_fecha?` · promete ${fmtFecha(g.promesa_fecha)}${g.monto_prometido?` (${fmtUSD0(g.monto_prometido)})`:''}`:''}
                                {g.proximo_paso_fecha?` · próx. ${fmtFecha(g.proximo_paso_fecha)}`:''}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>}
                  </div>
                </div>
              </div>
            )}
            {!sel&&(
              <div style={{background:C.bg2,border:`1px dashed ${C.border2}`,borderRadius:12,padding:'56px 20px',textAlign:'center'}}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.t4} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom:10}}>{KI.users}</svg>
                <div style={{fontSize:13,fontWeight:600,color:C.t2}}>Selecciona un cliente</div>
                <div style={{fontSize:12,color:C.t4,marginTop:4}}>Elige uno de la lista para ver saldo, facturas abiertas y gestiones.</div>
              </div>
            )}
          </div>
          );
        })()}

        {/* ════════════ GESTIÓN ════════════ */}
        {tab==='gestion'&&(
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(340px,1fr))',gap:16,alignItems:'start'}}>
            {/* prioridad de cobro */}
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',boxShadow:SH.sm}}>
              <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>Prioridad de cobro <span style={{fontSize:11.5,fontWeight:500,color:C.t3}}>· más vencido primero</span></div>
              <div style={{maxHeight:'70vh',overflowY:'auto'}}>
                {porCliente.filter(d=>d.vencido>0).length===0&&(
                  <div style={{color:C.t4,fontSize:13,padding:'28px 20px',textAlign:'center'}}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom:8}}>{KI.check}</svg>
                    <div style={{fontSize:12.5,fontWeight:600,color:C.t2}}>Nada vencido por ahora</div>
                    <div style={{fontSize:11.5,color:C.t4,marginTop:2}}>Toda la cartera está al día.</div>
                  </div>
                )}
                {[...porCliente].filter(d=>d.vencido>0).sort((a,b)=>b.peorDias-a.peorDias).map((d,i)=>{
                  const b = bucketDe(d.peorDias);
                  const wa = waLinkCliente(d);
                  return (
                    <div key={(d.card_code||'')+i} style={{padding:'12px 14px',borderBottom:`1px solid ${C.bg3}`,borderLeft:`3px solid ${b.color}`}}>
                      <div style={{display:'flex',justifyContent:'space-between',gap:8,alignItems:'flex-start'}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.card_name}</div>
                          <div style={{display:'flex',gap:8,alignItems:'center',marginTop:3}}>
                            <AgingBadge dias={d.peorDias} mini/>
                            <span className="radar-num" style={{fontSize:11,color:C.t4}}>{d.count} fact.</span>
                          </div>
                        </div>
                        <div style={{textAlign:'right'}}>
                          <div className="radar-num" style={{fontSize:13.5,fontWeight:700,letterSpacing:'-0.015em',color:C.redText}}>{fmtUSD0(d.vencido)}</div>
                          <div className="radar-num" style={{fontSize:10,color:C.t4}}>de {fmtUSD0(d.saldo)}</div>
                        </div>
                      </div>
                      <div style={{display:'flex',gap:6,marginTop:8}}>
                        {wa
                          ? <a className="radar-btn" href={wa} target="_blank" rel="noreferrer" style={{flex:1,textAlign:'center',padding:'6px 0',borderRadius:R.sm,background:C.greenTint,border:`1px solid ${C.greenBorder}`,color:C.greenText,fontSize:11,fontWeight:600,textDecoration:'none'}}>WhatsApp</a>
                          : <span style={{flex:1,textAlign:'center',padding:'6px 0',borderRadius:R.sm,background:C.bg3,color:C.t4,fontSize:11,fontWeight:600}}>Sin tel.</span>}
                        <button className="radar-btn" onClick={()=>abrirGestion(d.card_code,d.card_name)} style={{flex:1,padding:'6px 0',borderRadius:R.sm,border:`1px solid ${C.primaryBorder}`,background:C.primaryTint,color:C.blue,fontSize:11,fontWeight:600,cursor:'pointer'}}>Gestión</button>
                        <button className="radar-btn" onClick={()=>{ setSelCliente(d.card_code); setTab('cliente'); }} style={{padding:'6px 10px',borderRadius:R.sm,border:`1px solid ${C.border}`,background:C.bg2,color:C.t2,fontSize:11,fontWeight:600,cursor:'pointer'}}>Ver</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* gestiones recientes + promesas */}
            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',boxShadow:SH.sm}}>
                <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>Promesas de pago activas <span className="radar-num" style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.pill,padding:'1px 8px',fontSize:11,fontWeight:600,color:C.t3,marginLeft:4}}>{gestiones.filter(g=>g.promesa_fecha).length}</span></div>
                <div style={{padding:'4px 8px'}}>
                  {gestiones.filter(g=>g.promesa_fecha).length===0&&<div style={{color:C.t4,fontSize:12,padding:14,textAlign:'center'}}>Sin promesas registradas</div>}
                  {gestiones.filter(g=>g.promesa_fecha).slice(0,12).map(g=>{
                    const vencida = g.promesa_fecha < hoyStr;
                    const pt = vencida ? TONES.red : TONES.green;
                    return (
                      <div key={g.id} className="radar-hrow" style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,padding:'9px 8px',borderRadius:8,borderBottom:`1px solid ${C.bg3}`}}>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:12.5,fontWeight:600,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{g.card_name||g.card_code}</div>
                          <div style={{fontSize:10.5,color:C.t4}}>{g.nota||'Promesa de pago'}</div>
                        </div>
                        <div style={{textAlign:'right',whiteSpace:'nowrap'}}>
                          <span className="radar-num" style={{display:'inline-flex',alignItems:'center',gap:5,background:pt.tint,color:pt.text,border:`1px solid ${pt.border}`,borderRadius:R.pill,padding:'2px 8px',fontSize:10.5,fontWeight:600}}>
                            <span style={{width:6,height:6,borderRadius:'50%',background:pt.solid,flexShrink:0}}/>{fmtFecha(g.promesa_fecha)}
                          </span>
                          {g.monto_prometido>0&&<div className="radar-num" style={{fontSize:10,color:C.t4,marginTop:2}}>{fmtUSD0(g.monto_prometido)}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',boxShadow:SH.sm}}>
                <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>Gestiones recientes</div>
                <div style={{padding:'4px 8px',maxHeight:'40vh',overflowY:'auto'}}>
                  {gestiones.length===0&&<div style={{color:C.t4,fontSize:12,padding:14,textAlign:'center'}}>Aún no hay gestiones registradas</div>}
                  {gestiones.slice(0,20).map(g=>(
                    <div key={g.id} className="radar-hrow" style={{display:'flex',gap:10,padding:'8px',borderRadius:8,borderBottom:`1px solid ${C.bg3}`}}>
                      <GestIcon tipo={g.tipo} size={14}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12.5,fontWeight:600,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{g.card_name||g.card_code}</div>
                        <div style={{fontSize:11,color:C.t3}}>{g.nota||g.proximo_paso||g.tipo}</div>
                        <div style={{fontSize:10.5,color:C.t4,marginTop:2}}>{fmtFechaLarga(g.created_at)} {g.usuario?`· ${g.usuario}`:''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        </>)}
      </div>

      {/* Modal registrar gestión */}
      <Modal open={!!gestModal} onClose={()=>{setGestModal(null);setGForm(null);}} title="Registrar gestión de cobro" width={460}>
        {gestModal&&gForm&&(
          <>
            <div style={{background:C.bg3,borderRadius:10,padding:'10px 14px',marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:800,color:C.t1}}>{gestModal.card_name}</div>
              {gestModal.factura_doc_num&&<div style={{fontSize:11,color:C.t4,marginTop:2}}>Factura #{String(gestModal.factura_doc_num).replace(/^\w+_/,'')}</div>}
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Tipo</div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {[['nota','Nota'],['promesa','Promesa'],['llamada','Llamada'],['recordatorio','Recordatorio']].map(([v,l])=>(
                  <button key={v} onClick={()=>setGForm(x=>({...x,tipo:v}))}
                    style={{padding:'6px 13px',borderRadius:R.md,border:`1px solid ${gForm.tipo===v?C.primaryBorder:C.border}`,background:gForm.tipo===v?C.primaryTint:C.bg2,color:gForm.tipo===v?C.blue:C.t2,fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:SH.xs}}>{l}</button>
                ))}
              </div>
            </div>
            {gForm.tipo==='promesa'&&(
              <div style={{display:'flex',gap:10,marginBottom:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Fecha prometida</div>
                  <input type="date" value={gForm.promesa_fecha} onChange={e=>setGForm(x=>({...x,promesa_fecha:e.target.value}))}
                    className="radar-input" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}/>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Monto (USD)</div>
                  <input type="number" value={gForm.monto_prometido} onChange={e=>setGForm(x=>({...x,monto_prometido:e.target.value}))} placeholder="0.00"
                    className="radar-input" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}/>
                </div>
              </div>
            )}
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Nota</div>
              <textarea value={gForm.nota} onChange={e=>setGForm(x=>({...x,nota:e.target.value}))} rows={3} placeholder="Detalle de la conversación / acuerdo..."
                className="radar-input" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none',resize:'vertical',fontFamily:'inherit'}}/>
            </div>
            <div style={{display:'flex',gap:10,marginBottom:12}}>
              <div style={{flex:1}}>
                <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Próximo paso</div>
                <input value={gForm.proximo_paso} onChange={e=>setGForm(x=>({...x,proximo_paso:e.target.value}))} placeholder="Ej: volver a llamar"
                  className="radar-input" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}/>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Fecha próx. paso</div>
                <input type="date" value={gForm.proximo_paso_fecha} onChange={e=>setGForm(x=>({...x,proximo_paso_fecha:e.target.value}))}
                  className="radar-input" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}/>
              </div>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Canal</div>
              <select value={gForm.canal} onChange={e=>setGForm(x=>({...x,canal:e.target.value}))}
                className="radar-input" style={{width:'100%',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}>
                {['whatsapp','llamada','email','visita'].map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{display:'flex',gap:8}}>
              <Btn variant="secondary" onClick={()=>{setGestModal(null);setGForm(null);}}>Cancelar</Btn>
              <Btn onClick={guardarGestion} disabled={gSaving} full>{gSaving?'Guardando...':'Guardar gestión'}</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

function Ingresos({ clientes, equipos, empresaActiva }) {
  const [tab, setTab]           = useState('resumen');
  const [rentas, setRentas]     = useState([]);
  const [ingresos, setIngresos] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [periodoMes, setPeriodo] = useState(new Date().toISOString().slice(0,7));
  const [modal, setModal]       = useState(false);
  const [saving, setSaving]     = useState(false);
  const [f, setF] = useState({tipo:'Renta mensual',cliente_id:'',equipo_id:'',monto:'',fecha:new Date().toISOString().split('T')[0],vendedor:'',descripcion:'',referencia:''});

  const cargar = async (eid) => {
    setLoading(true);
    const [mesI, mesF] = [periodoMes+'-01', periodoMes+'-31'];
    let qR = supabase.from('rentas').select('*,clientes(empresa),equipos(codigo,modelo,tipo_equipo)').eq('estado','activa');
    let qI = supabase.from('ingresos').select('*,clientes(empresa),equipos(codigo,modelo)').gte('fecha',mesI).lte('fecha',mesF).order('fecha',{ascending:false});
    if (eid) { qR=qR.eq('empresa_id',eid); qI=qI.eq('empresa_id',eid); }
    const [{data:r},{data:i}] = await Promise.all([qR, qI]);
    setRentas(r||[]); setIngresos(i||[]); setLoading(false);
  };
  useEffect(()=>{cargar();},[periodoMes, empresaActiva]);

  const guardar = async () => {
    if (!f.monto||!f.tipo) return; setSaving(true);
    const {data} = await supabase.from('ingresos').insert({
      tipo: f.tipo,
      cliente_id: f.cliente_id||null,
      equipo_id: f.equipo_id||null,
      monto: parseFloat(f.monto),
      fecha: f.fecha,
      vendedor: f.vendedor||null,
      descripcion: f.descripcion||null,
      referencia: f.referencia||null,
    }).select('*,clientes(empresa),equipos(codigo,modelo)').single();
    if (data) setIngresos(i=>[data,...i]);
    setSaving(false); setModal(false);
    setF({tipo:'Renta mensual',cliente_id:'',equipo_id:'',monto:'',fecha:new Date().toISOString().split('T')[0],vendedor:'',descripcion:'',referencia:''});
  };

  // Cálculos
  const mrr = rentas.reduce((s,r)=>s+(r.valor_mensual||0),0);
  const totalManual = ingresos.reduce((s,i)=>s+(i.monto||0),0);
  const totalMes = totalManual; // ingresos manuales del mes seleccionado

  // Analytics
  const porTipo = TIPOS_INGRESO.map(t=>({
    tipo:t,
    total:ingresos.filter(i=>i.tipo===t).reduce((s,i)=>s+(i.monto||0),0),
    count:ingresos.filter(i=>i.tipo===t).length,
  })).filter(x=>x.total>0).sort((a,b)=>b.total-a.total);

  const porVendedor = [...new Set(ingresos.map(i=>i.vendedor).filter(Boolean))].map(v=>({
    vendedor:v,
    total:ingresos.filter(i=>i.vendedor===v).reduce((s,i)=>s+(i.monto||0),0),
    count:ingresos.filter(i=>i.vendedor===v).length,
  })).sort((a,b)=>b.total-a.total);

  const porCliente = [...new Set(ingresos.map(i=>i.cliente_id).filter(Boolean))].map(cid=>{
    const items = ingresos.filter(i=>i.cliente_id===cid);
    return { empresa: items[0]?.clientes?.empresa||'—', total: items.reduce((s,i)=>s+(i.monto||0),0), count:items.length };
  }).sort((a,b)=>b.total-a.total).slice(0,8);

  const maxBar = Math.max(...porTipo.map(x=>x.total),1);

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* Toolbar — segmented + pill de mes, flotando sobre el gris (sin franja blanca) */}
      <div style={{padding:'12px 20px',display:'flex',gap:10,alignItems:'center',flexShrink:0,flexWrap:'wrap'}}>
        <div style={{display:'inline-flex',gap:2,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2}}>
          {[['resumen','Resumen'],['historial','Historial'],['analytics','Analytics']].map(([id,lbl])=>(
            <button key={id} onClick={()=>setTab(id)} style={{height:26,padding:'0 14px',background:tab===id?C.bg2:'transparent',color:tab===id?C.t1:C.t3,border:'none',borderRadius:R.sm,fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:tab===id?SH.sm:'none',transition:'background .15s ease, color .15s ease'}}>{lbl}</button>
          ))}
        </div>
        <input type="month" value={periodoMes} onChange={e=>setPeriodo(e.target.value)} className="radar-input radar-num"
          style={{height:34,boxSizing:'border-box',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'0 10px',color:C.t1,fontSize:12.5,outline:'none',boxShadow:SH.xs}}/>
        <div style={{marginLeft:'auto'}}><Btn size="sm" onClick={()=>setModal(true)}>+ Registrar ingreso</Btn></div>
      </div>

      <div style={{flex:1,overflowY:'auto',padding:'4px 20px 20px'}}>
        {loading?<><SkelCards n={4}/><SkelRows n={6}/></>:<>

        {/* KPIs siempre visibles */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:16,marginBottom:22}}>
          <KpiCard label="MRR (rentas activas)" value={fmt(mrr)} sub={`${rentas.length} contratos activos`} tone="green" icon={KI.dollar}/>
          <KpiCard label={`Ingresos ${periodoMes}`} value={fmt(totalMes)} sub={`${ingresos.length} registros`} tone="primary" icon={KI.trend}/>
          <KpiCard label="Ticket promedio" value={ingresos.length>0?fmt(Math.round(totalMes/ingresos.length)):'—'} sub="por registro este mes" tone="info" icon={KI.check}/>
          <KpiCard label="Top tipo" value={porTipo[0]?.tipo?.split(' ')[0]||'—'} sub={porTipo[0]?fmt(porTipo[0].total):'sin datos'} tone="orange" icon={KI.box}/>
        </div>

        {/* RESUMEN */}
        {tab==='resumen'&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
            {/* Rentas activas */}
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 20px',boxShadow:SH.sm}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10}}>Rentas activas — MRR <span className="radar-num">{fmt(mrr)}</span></div>
              {rentas.length===0?<div style={{color:C.t4,fontSize:13,textAlign:'center',padding:20}}>Sin rentas activas</div>
              :rentas.map(r=>(
                <div key={r.id} className="radar-hrow" style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 4px',borderRadius:8,borderBottom:`1px solid ${C.bg3}`}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{r.clientes?.empresa||'—'}</div>
                    <div style={{fontSize:11,color:C.t4}}>{r.equipos?.codigo} · {r.tipo_cobro||'mensual'}</div>
                  </div>
                  <div className="radar-num" style={{fontSize:15,fontWeight:700,letterSpacing:'-0.015em',color:C.greenText,flexShrink:0,marginLeft:12}}>{fmt(r.valor_mensual||0)}</div>
                </div>
              ))}
            </div>

            {/* Ingresos del mes */}
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 20px',boxShadow:SH.sm}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10}}>Registros del mes — <span className="radar-num">{fmt(totalMes)}</span></div>
              {ingresos.length===0?<div style={{color:C.t4,fontSize:13,textAlign:'center',padding:20}}>Sin registros este mes.<br/><span style={{fontSize:11}}>Usá "+ Registrar Ingreso" para agregar.</span></div>
              :ingresos.slice(0,10).map(i=>(
                <div key={i.id} className="radar-hrow" style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 4px',borderRadius:8,borderBottom:`1px solid ${C.bg3}`}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12.5,fontWeight:600,color:C.t1}}>{i.tipo}</div>
                    <div style={{fontSize:11,color:C.t4}}>{i.clientes?.empresa||''}{i.vendedor?` · ${i.vendedor}`:''} · {fecha(i.fecha)}</div>
                  </div>
                  <div className="radar-num" style={{fontSize:14,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,flexShrink:0,marginLeft:12}}>{fmt(i.monto||0)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* HISTORIAL */}
        {tab==='historial'&&(
          <div style={{display:'grid',gap:8}}>
            {ingresos.length===0?<div style={{textAlign:'center',color:C.t4,padding:60,fontSize:14}}>Sin ingresos registrados en {periodoMes}</div>
            :ingresos.map(i=>(
              <div key={i.id} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,boxShadow:SH.xs}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3}}>
                    <span style={{background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,borderRadius:R.pill,padding:'2px 8px',fontSize:10.5,fontWeight:600}}>{i.tipo}</span>
                    {i.vendedor&&<span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,color:C.t4}}><AvatarChip name={i.vendedor} size={16}/>{i.vendedor}</span>}
                  </div>
                  <div style={{fontSize:14,fontWeight:600,color:C.t1,marginBottom:2}}>{i.clientes?.empresa||i.descripcion||'—'}</div>
                  {i.equipos&&<div style={{fontSize:11,color:C.t3}}>{i.equipos.codigo} — {i.equipos.modelo}</div>}
                  <div style={{fontSize:11,color:C.t4,marginTop:2}}>{fecha(i.fecha)}{i.referencia?` · Ref: ${i.referencia}`:''}</div>
                </div>
                <div className="radar-num" style={{fontSize:20,fontWeight:700,letterSpacing:'-0.02em',color:C.greenText,flexShrink:0}}>{fmt(i.monto||0)}</div>
              </div>
            ))}
          </div>
        )}

        {/* ANALYTICS */}
        {tab==='analytics'&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
            {/* Por tipo */}
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 20px',boxShadow:SH.sm}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:14}}>Ingresos por tipo</div>
              {porTipo.length===0?<div style={{color:C.t4,fontSize:13,textAlign:'center',padding:20}}>Sin datos</div>
              :porTipo.map(x=>(
                <div key={x.tipo} style={{marginBottom:12}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                    <span style={{fontSize:12,fontWeight:600,color:C.t1}}>{x.tipo}</span>
                    <span className="radar-num" style={{fontSize:12,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>{fmt(x.total)}</span>
                  </div>
                  <div style={{height:8,background:C.bg3,borderRadius:R.pill,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${(x.total/maxBar*100).toFixed(1)}%`,background:C.blue,borderRadius:R.pill,transition:'width .4s'}}/>
                  </div>
                  <div className="radar-num" style={{fontSize:10.5,color:C.t4,marginTop:2}}>{x.count} {x.count===1?'registro':'registros'}</div>
                </div>
              ))}
            </div>

            {/* Por vendedor */}
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 20px',boxShadow:SH.sm}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:14}}>Ingresos por vendedor</div>
              {porVendedor.length===0?<div style={{color:C.t4,fontSize:13,textAlign:'center',padding:20}}>Sin datos con vendedor asignado</div>
              :porVendedor.map(v=>(
                <div key={v.vendedor} className="radar-hrow" style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 4px',borderRadius:8,borderBottom:`1px solid ${C.bg3}`}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <AvatarChip name={v.vendedor} size={28}/>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:C.t1}}>{v.vendedor}</div>
                      <div style={{fontSize:11,color:C.t4}}>{v.count} registros</div>
                    </div>
                  </div>
                  <div className="radar-num" style={{fontSize:15,fontWeight:700,letterSpacing:'-0.015em',color:C.greenText}}>{fmt(v.total)}</div>
                </div>
              ))}
            </div>

            {/* Top clientes */}
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 20px',boxShadow:SH.sm,gridColumn:'1/-1'}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:14}}>Top clientes por ingreso</div>
              {porCliente.length===0?<div style={{color:C.t4,fontSize:13,textAlign:'center',padding:20}}>Sin datos</div>
              :<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10}}>
                {porCliente.map((c,i)=>(
                  <div key={i} style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.t1,marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.empresa}</div>
                      <div style={{fontSize:11,color:C.t4}}>{c.count} registros</div>
                    </div>
                    <div className="radar-num" style={{fontSize:15,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,flexShrink:0}}>{fmt(c.total)}</div>
                  </div>
                ))}
              </div>}
            </div>
          </div>
        )}
        </>}
      </div>

      <Modal open={modal} onClose={()=>setModal(false)} title="Registrar Ingreso" width={520}>
        <Sel label="Tipo *" value={f.tipo} onChange={v=>setF(x=>({...x,tipo:v}))} required options={TIPOS_INGRESO}/>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <SearchSel label="Cliente" value={f.cliente_id} onChange={v=>setF(x=>({...x,cliente_id:v}))} placeholder="Buscar cliente..." options={[{v:'',l:'— Sin cliente'},...clientes.map(c=>({v:c.id,l:c.empresa}))]}/>
          <SearchSel label="Equipo" value={f.equipo_id} onChange={v=>setF(x=>({...x,equipo_id:v}))} placeholder="Buscar equipo..." options={[{v:'',l:'— Sin equipo'},...equipos.map(e=>({v:e.id,l:`${e.codigo} — ${e.modelo}`}))]}/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <Inp label="Monto ($) *" type="number" value={f.monto} onChange={v=>setF(x=>({...x,monto:v}))} placeholder="0.00" required/>
          <Inp label="Fecha" type="date" value={f.fecha} onChange={v=>setF(x=>({...x,fecha:v}))}/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <Inp label="Vendedor" value={f.vendedor} onChange={v=>setF(x=>({...x,vendedor:v}))} placeholder="nombre"/>
          <Inp label="Referencia / No. factura" value={f.referencia} onChange={v=>setF(x=>({...x,referencia:v}))} placeholder="FAC-001"/>
        </div>
        <Inp label="Descripción" value={f.descripcion} onChange={v=>setF(x=>({...x,descripcion:v}))} placeholder="detalle del ingreso..." rows={2}/>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" onClick={()=>setModal(false)}>Cancelar</Btn>
          <Btn onClick={guardar} disabled={!f.monto||!f.tipo||saving}>{saving?'Guardando...':'Registrar'}</Btn>
        </div>
      </Modal>
    </div>
  );
}

// ─── COTIZACIONES ─────────────────────────────────────────────────────────────
const OT_EST = [
  {id:'pendiente',  label:'Pendiente',  color:'#64748B'},
  {id:'en_proceso', label:'En proceso', color:'#F59E0B'},
  {id:'completada', label:'Completada', color:'#10B981'},
  {id:'cancelada',  label:'Cancelada',  color:'#EF4444'},
];
const OT_TIPOSTR = ['Mantenimiento preventivo','Mantenimiento correctivo','Instalación','Diagnóstico','Entrega de equipo','Retiro de equipo','Inspección','Capacitación','Otro'];

function Cotizaciones({ clientes, equipos, usuario, empresaActiva }) {
  const [cots, setCots]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filtro, setFiltro]     = useState('todas');
  const [sel, setSel]     = useState(null);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vendList, setVendList] = useState([]);
  const [f, setF] = useState({ equipo_id:'', cliente_id:'', vendedor:'', tipo_trabajo:'', tipo_ref:'ninguna', num_ref:'', descripcion:'', fecha:new Date().toISOString().split('T')[0] });

  const cargar = async (eid) => {
    setLoading(true);
    let q = supabase.from('cotizaciones').select('*,clientes(empresa),equipos(codigo,modelo,tipo_equipo)').order('created_at',{ascending:false});
    if (filtro!=='todas') q=q.eq('estado',filtro);
    if (eid) q=q.eq('empresa_id',eid);
    const {data}=await q; setCots(data||[]); setLoading(false);
  };
  useEffect(()=>{ cargar(); },[filtro, empresaActiva]);
  useEffect(()=>{
    supabase.from('radar_usuarios').select('nombre').in('rol',['ventas','gerente_ventas','admin','taller']).eq('activo',true)
      .then(({data})=>setVendList((data||[]).map(u=>u.nombre).sort()));
  },[]);

  const crearOT = async () => {
    if (!f.equipo_id||!f.tipo_trabajo) return; setSaving(true);
    const num = `OT-${Date.now().toString().slice(-6)}`;
    const {data} = await supabase.from('cotizaciones').insert({
      numero: num, equipo_id: f.equipo_id, cliente_id: f.cliente_id||null,
      vendedor: f.vendedor||null, estado:'pendiente', validez: f.fecha||null,
      notas: JSON.stringify({tipo_ref:f.tipo_ref, num_ref:f.num_ref, descripcion:f.descripcion}),
      subtotal:0, total:0, descuento:0,
      items:[{descripcion:f.tipo_trabajo, cantidad:'1', precio_unitario:'0', total:'0'}],
    }).select('*,clientes(empresa),equipos(codigo,modelo)').single();
    if (data) { setCots(o=>[data,...o]); setSel(data); }
    setSaving(false); setModal(false);
    setF({equipo_id:'',cliente_id:'',vendedor:'',tipo_trabajo:'',tipo_ref:'ninguna',num_ref:'',descripcion:'',fecha:new Date().toISOString().split('T')[0]});
  };

  const cambiarEstado = async (id, estado) => {
    await supabase.from('cotizaciones').update({estado}).eq('id',id);
    setCots(cs=>cs.map(c=>c.id===id?{...c,estado}:c));
    if (sel?.id===id) setSel(prev=>({...prev,estado}));
  };

  const parseNotas = (n) => { try { return JSON.parse(n||'{}'); } catch { return {descripcion:n}; } };
  const stateColor = id => OT_EST.find(e=>e.id===id)?.color||'#64748B';

  return (
    <div style={{flex:1,display:'flex',overflow:'hidden',background:C.bg1}}>
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{padding:'10px 16px',borderBottom:`1px solid ${C.border}`,background:C.bg2,flexShrink:0}}>
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
              {[['todas','Todas'],...OT_EST.map(e=>[e.id,e.label])].map(([v,l])=>(
                <button key={v} onClick={()=>{setFiltro(v);setSel(null);}}
                  style={{background:filtro===v?(stateColor(v)||C.blue):'transparent',color:filtro===v?'#fff':C.t4,border:`1px solid ${filtro===v?(stateColor(v)||C.blue):C.border}`,borderRadius:7,padding:'5px 12px',fontSize:11,fontWeight:700,cursor:'pointer'}}>{l}
                </button>
              ))}
            </div>
            <div style={{marginLeft:'auto'}}><Btn onClick={()=>setModal(true)}>+ Nueva Orden de Trabajo</Btn></div>
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:16}}>
          {loading?<div style={{textAlign:'center',color:C.t4,padding:40}}>Cargando...</div>
          :cots.length===0?<div style={{textAlign:'center',color:C.t4,padding:60,fontSize:14}}>Sin órdenes de trabajo</div>
          :<div style={{display:'grid',gap:8}}>
            {cots.map(o=>{
              const sc = stateColor(o.estado); const notas = parseNotas(o.notas);
              const isSelected = sel?.id===o.id;
              return (
                <div key={o.id} className="radar-card" onClick={()=>setSel(isSelected?null:o)}
                  style={{background:isSelected?C.primaryTint:C.bg2,border:`1px solid ${isSelected?C.primaryBorder:C.border}`,borderLeft:isSelected?`3px solid ${C.blue}`:`1px solid ${C.border}`,borderRadius:R.lg,padding:16,cursor:'pointer',boxShadow:SH.sm}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3}}>
                        <span className="radar-num" style={{fontSize:11,fontWeight:700,color:C.t4,fontFamily:'monospace'}}>{o.numero}</span>
                        <span style={{background:`${sc}14`,color:sc,border:`1px solid ${sc}33`,borderRadius:R.pill,padding:'2px 8px',fontSize:10.5,fontWeight:600}}>{OT_EST.find(e=>e.id===o.estado)?.label||o.estado}</span>
                      </div>
                      <div style={{fontSize:15,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,marginBottom:2}}>{o.items?.[0]?.descripcion||'—'}</div>
                      {o.equipos&&<div style={{fontSize:12,color:C.t3}}>🏗️ {o.equipos.codigo} — {o.equipos.modelo}</div>}
                      {o.clientes&&<div style={{fontSize:12,color:C.t3}}>🏢 {o.clientes.empresa}</div>}
                    </div>
                    <div style={{textAlign:'right',flexShrink:0,marginLeft:12}}>
                      {notas.tipo_ref&&notas.tipo_ref!=='ninguna'&&<div style={{fontSize:10,fontWeight:600,color:C.orangeText,background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.sm,padding:'2px 8px',marginBottom:4}}>
                        {notas.tipo_ref==='cotizacion_sap'?'SAP Cotiz.':notas.tipo_ref==='factura_sap'?'SAP Factura':'Opp.'} #{notas.num_ref}
                      </div>}
                      <div style={{fontSize:11,color:C.t4}}>{fecha(o.validez||o.created_at)}</div>
                      {o.vendedor&&<div style={{fontSize:11,color:C.t4,marginTop:2}}>👤 {o.vendedor}</div>}
                    </div>
                  </div>
                  {notas.descripcion&&<div style={{fontSize:11,color:C.t3,borderTop:`1px solid ${C.border}`,paddingTop:6,lineHeight:1.4}}>{notas.descripcion}</div>}
                </div>
              );
            })}
          </div>}
        </div>
      </div>

      {sel&&(
        <div style={{width:300,borderLeft:`1px solid ${C.border}`,background:C.bg2,overflowY:'auto',padding:20,flexShrink:0}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:14}}>
            <div style={{fontSize:14,fontWeight:800,color:C.t1}}>{sel.items?.[0]?.descripcion||'Orden'}</div>
            <button onClick={()=>setSel(null)} style={{background:'none',border:'none',color:C.t4,fontSize:20,cursor:'pointer'}}>×</button>
          </div>
          <div style={{fontSize:11,fontWeight:800,color:C.t4,fontFamily:'monospace',marginBottom:10}}>{sel.numero}</div>
          {(()=>{const n=parseNotas(sel.notas); return (<>
            {[['Equipo',sel.equipos?`${sel.equipos.codigo} — ${sel.equipos.modelo}`:null],['Cliente',sel.clientes?.empresa],['Fecha',fecha(sel.validez||sel.created_at)],['Técnico / Asignado',sel.vendedor]].map(([l,v])=>v?(
              <div key={l} style={{padding:'7px 0',borderBottom:`1px solid ${C.border}`}}>
                <div style={{fontSize:10,color:C.t4,fontWeight:700,textTransform:'uppercase',marginBottom:2}}>{l}</div>
                <div style={{fontSize:13,color:C.t1}}>{v}</div>
              </div>
            ):null)}
            {n.tipo_ref&&n.tipo_ref!=='ninguna'&&(
              <div style={{padding:'7px 0',borderBottom:`1px solid ${C.border}`}}>
                <div style={{fontSize:10,color:C.t4,fontWeight:700,textTransform:'uppercase',marginBottom:2}}>Referencia</div>
                <div style={{fontSize:13,color:C.orange,fontWeight:700}}>{n.tipo_ref==='cotizacion_sap'?'Cotización SAP':n.tipo_ref==='factura_sap'?'Factura SAP':'Oportunidad RADAR'} #{n.num_ref}</div>
              </div>
            )}
            {n.descripcion&&<div style={{marginTop:10,background:C.bg3,borderRadius:8,padding:12}}><div style={{fontSize:10,color:C.t4,fontWeight:700,textTransform:'uppercase',marginBottom:4}}>Descripción</div><div style={{fontSize:12,color:C.t2,lineHeight:1.5}}>{n.descripcion}</div></div>}
          </>);})()}
          <div style={{marginTop:14}}>
            <div style={{fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',marginBottom:6}}>Cambiar estado</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {OT_EST.filter(e=>e.id!==sel.estado).map(e=>(
                <button key={e.id} onClick={()=>cambiarEstado(sel.id,e.id)}
                  style={{background:`${e.color}20`,color:e.color,border:`1px solid ${e.color}40`,borderRadius:6,padding:'4px 10px',fontSize:11,fontWeight:700,cursor:'pointer'}}>{e.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      <Modal open={modal} onClose={()=>setModal(false)} title="Nueva Orden de Trabajo" width={520}>
        <SearchSel label="Equipo *" value={f.equipo_id} onChange={v=>setF(x=>({...x,equipo_id:v}))} required placeholder="Buscar equipo..." options={equipos.map(e=>({v:e.id,l:`${e.codigo} — ${e.modelo} (${e.estado})`}))}/>
        <SearchSel label="Cliente" value={f.cliente_id} onChange={v=>setF(x=>({...x,cliente_id:v}))} placeholder="Buscar cliente (opcional)..." options={[{v:'',l:'— Sin cliente —'},...clientes.map(c=>({v:c.id,l:c.empresa}))]}/>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <Sel label="Tipo de trabajo *" value={f.tipo_trabajo} onChange={v=>setF(x=>({...x,tipo_trabajo:v}))} options={[{v:'',l:'— Seleccionar —'},...OT_TIPOSTR.map(t=>({v:t,l:t}))]}/>
          <Inp label="Fecha" type="date" value={f.fecha} onChange={v=>setF(x=>({...x,fecha:v}))}/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <Sel label="Vincular a" value={f.tipo_ref} onChange={v=>setF(x=>({...x,tipo_ref:v,num_ref:''}))} options={[{v:'ninguna',l:'Sin referencia'},{v:'cotizacion_sap',l:'Cotización SAP'},{v:'factura_sap',l:'Factura SAP'},{v:'oportunidad',l:'Oportunidad RADAR'}]}/>
          {f.tipo_ref!=='ninguna'&&<Inp label="Número de referencia" value={f.num_ref} onChange={v=>setF(x=>({...x,num_ref:v}))} placeholder="ej: 2818"/>}
        </div>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Técnico / Asignado</div>
          <select value={f.vendedor} onChange={e=>setF(x=>({...x,vendedor:e.target.value}))}
            style={{width:'100%',background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 10px',color:f.vendedor?C.t1:C.t4,fontSize:13,outline:'none',boxSizing:'border-box'}}>
            <option value="">— Sin asignar —</option>
            {vendList.map(v=><option key={v} value={v} style={{background:C.bg2}}>{v}{v===usuario?.nombre?' (yo)':''}</option>)}
          </select>
        </div>
        <Inp label="Descripción / Instrucciones" value={f.descripcion} onChange={v=>setF(x=>({...x,descripcion:v}))} rows={2} placeholder="Detalle del trabajo a realizar..."/>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:4}}>
          <Btn variant="secondary" onClick={()=>setModal(false)}>Cancelar</Btn>
          <Btn onClick={crearOT} disabled={!f.equipo_id||!f.tipo_trabajo||saving}>{saving?'Guardando...':'🔧 Crear Orden'}</Btn>
        </div>
      </Modal>
    </div>
  );
}

// ─── WHATSAPP ─────────────────────────────────────────────────────────────────
function SupervisionVendedores() {
  const [vendedores, setVendedores] = useState([]);
  const [opps, setOpps]           = useState([]);
  const [ganadas, setGanadas]     = useState([]);
  const [waConvs, setWaConvs]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selV, setSelV]           = useState(null);

  const cargar = async () => {
    setLoading(true);
    const mesI = new Date(); mesI.setDate(1); const mesIso = mesI.toISOString().split('T')[0];
    const [rv, ro, rg, rw] = await Promise.all([
      supabase.from('radar_usuarios').select('id,nombre,whatsapp,rol').in('rol',['ventas','gerente_ventas']).eq('activo',true).order('nombre'),
      supabase.from('oportunidades').select('vendedor,valor,etapa,ultimo_seguimiento,titulo,cliente').not('etapa','in','(cerrado_ganado,cerrado_perdido,prospecto_invalido,cliente_frio)'),
      supabase.from('oportunidades').select('vendedor,valor').eq('etapa','cerrado_ganado').gte('fecha_cierre', mesIso),
      supabase.from('wa_mensajes').select('wa_from,role,created_at').neq('role','status').order('created_at',{ascending:false}),
    ]);
    setVendedores(rv.data||[]);
    setOpps(ro.data||[]);
    setGanadas(rg.data||[]);
    setWaConvs(rw.data||[]);
    setLoading(false);
  };

  useEffect(()=>{ cargar(); },[]);

  const hace3 = new Date(Date.now()-3*86400000).toISOString().split('T')[0];

  const statsVendedor = (nombre) => {
    const mis = opps.filter(o=>(o.vendedor||'').toLowerCase()===nombre.toLowerCase());
    const misGanadas = ganadas.filter(o=>(o.vendedor||'').toLowerCase()===nombre.toLowerCase());
    const urgentes = mis.filter(o=> o.ultimo_seguimiento && o.ultimo_seguimiento < hace3);
    const pipeline = mis.reduce((s,o)=>s+(o.valor||0),0);
    const ganado   = misGanadas.reduce((s,o)=>s+(o.valor||0),0);
    // WA convs únicas por este vendedor (futuro: filtrar por número del vendedor)
    const waUniq = new Set(waConvs.map(m=>m.wa_from)).size;
    return { total: mis.length, urgentes: urgentes.length, pipeline, ganado, ganadas: misGanadas.length, waConvs: waUniq };
  };

  const fmtUSD = v => v>=1000 ? `$${(v/1000).toFixed(1)}k` : `$${v}`;
  const oppsVendedor = (nombre) => opps.filter(o=>(o.vendedor||'').toLowerCase()===nombre.toLowerCase());

  if (loading) return <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:C.t4,fontSize:14}}>Cargando supervisión...</div>;

  return (
    <div style={{flex:1,display:'flex',overflow:'hidden'}}>
      {/* Lista vendedores */}
      <div style={{width:280,borderRight:`1px solid ${C.border}`,overflowY:'auto',background:C.bg2,flexShrink:0}}>
        <div style={{padding:'16px 16px 12px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{fontSize:14,fontWeight:800,color:C.t1}}>👥 Vendedores</div>
          <button onClick={cargar} style={{background:'none',border:'none',color:C.t4,cursor:'pointer',fontSize:16}}>↻</button>
        </div>
        {vendedores.map(v => {
          const s = statsVendedor(v.nombre);
          return (
            <div key={v.id} onClick={()=>setSelV(v)}
              style={{padding:'14px 16px',borderBottom:`1px solid ${C.border}`,cursor:'pointer',
                background: selV?.id===v.id ? `${C.blue}15` : 'transparent',
                borderLeft: selV?.id===v.id ? `3px solid ${C.blue}` : '3px solid transparent'}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                <div style={{width:36,height:36,borderRadius:'50%',background:'linear-gradient(135deg,#0F766E,#0D5F58)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,fontWeight:800,color:'#fff',flexShrink:0}}>
                  {v.nombre[0]}
                </div>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.t1}}>{v.nombre}</div>
                  <div style={{fontSize:11,color:C.t4}}>{v.rol}</div>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6}}>
                <div style={{background:C.bg3,borderRadius:8,padding:'6px 8px',textAlign:'center'}}>
                  <div style={{fontSize:16,fontWeight:900,color:C.blue}}>{s.total}</div>
                  <div style={{fontSize:9,color:C.t4}}>Pipeline</div>
                </div>
                <div style={{background:s.urgentes>0?`${C.red}15`:C.bg3,borderRadius:8,padding:'6px 8px',textAlign:'center'}}>
                  <div style={{fontSize:16,fontWeight:900,color:s.urgentes>0?C.red:C.t3}}>{s.urgentes}</div>
                  <div style={{fontSize:9,color:C.t4}}>Urgentes</div>
                </div>
                <div style={{background:C.bg3,borderRadius:8,padding:'6px 8px',textAlign:'center'}}>
                  <div style={{fontSize:13,fontWeight:900,color:C.green}}>{fmtUSD(s.ganado)}</div>
                  <div style={{fontSize:9,color:C.t4}}>Ganado/mes</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detalle vendedor */}
      <div style={{flex:1,overflowY:'auto',padding:28}}>
        {!selV ? (
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',flexDirection:'column',gap:8,color:C.t4}}>
            <div style={{fontSize:40}}>👆</div>
            <div style={{fontSize:14}}>Selecciona un vendedor para ver el detalle</div>
          </div>
        ) : (()=>{
          const s = statsVendedor(selV.nombre);
          const sus = oppsVendedor(selV.nombre);
          return (
            <div style={{maxWidth:700}}>
              {/* Header */}
              <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:24}}>
                <div style={{width:56,height:56,borderRadius:'50%',background:'linear-gradient(135deg,#0F766E,#0D5F58)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,fontWeight:800,color:'#fff'}}>
                  {selV.nombre[0]}
                </div>
                <div>
                  <div style={{fontSize:22,fontWeight:900,color:C.t1}}>{selV.nombre}</div>
                  <div style={{fontSize:13,color:C.t4}}>{selV.rol} {selV.whatsapp ? `· ${selV.whatsapp}` : '· Sin WhatsApp registrado'}</div>
                </div>
              </div>

              {/* KPIs */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:24}}>
                {[
                  {label:'Oportunidades activas', val:s.total, color:C.blue, icon:'📊'},
                  {label:'Sin seguimiento +3d',   val:s.urgentes, color:s.urgentes>0?C.red:C.green, icon:'⚠️'},
                  {label:'Ganadas este mes',       val:s.ganadas, color:C.green, icon:'🏆'},
                  {label:'Valor pipeline',         val:fmtUSD(s.pipeline), color:C.orange, icon:'💰'},
                ].map((k,i)=>(
                  <div key={i} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 14px'}}>
                    <div style={{fontSize:20,marginBottom:6}}>{k.icon}</div>
                    <div style={{fontSize:22,fontWeight:900,color:k.color}}>{k.val}</div>
                    <div style={{fontSize:11,color:C.t4,marginTop:2}}>{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Oportunidades */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,overflow:'hidden'}}>
                <div style={{padding:'14px 18px',borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:800,color:C.t1}}>
                  📋 Oportunidades activas ({sus.length})
                </div>
                {sus.length===0 && <div style={{padding:24,color:C.t4,fontSize:13,textAlign:'center'}}>Sin oportunidades activas</div>}
                {sus.map((o,i)=>{
                  const urgente = o.ultimo_seguimiento && o.ultimo_seguimiento < hace3;
                  return (
                    <div key={i} style={{padding:'12px 18px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:C.t1}}>{o.titulo}</div>
                        <div style={{fontSize:11,color:C.t4}}>{o.cliente} · {o.etapa}</div>
                        {urgente && <div style={{fontSize:10,color:C.red,marginTop:2}}>⚠️ Sin seguimiento +3 días</div>}
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontSize:13,fontWeight:700,color:C.green}}>{o.valor?fmtUSD(o.valor):'-'}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─── WA EQUIPO — chats de los vendedores (puente solo-lectura) ────────────────
const WA_CATS = {
  renta_leasing:    { label:'Renta y Leasing',     color:'#7C3AED' },
  renta_hora:       { label:'Renta por Hora',      color:'#8B5CF6' },
  mantenimiento:    { label:'Mant. Flotas',        color:'#0891B2' },
  venta_equipo:     { label:'Venta de Equipo',     color:'#0F766E' },
  repuestos:        { label:'Repuestos',           color:'#2563EB' },
  servicios:        { label:'Serv. Técnicos',      color:'#0D9488' },
  fletes:           { label:'Fletes',              color:'#A16207' },
  certificacion:    { label:'Cert. Operador',      color:'#BE185D' },
  revisiones_ihtt:  { label:'Revisiones IHTT',     color:'#9333EA' },
  reclamo:          { label:'Reclamo',             color:'#DC2626' },
  pago:             { label:'Pago',                color:'#059669' },
  seguimiento:      { label:'Seguimiento',         color:'#D97706' },
  otro:             { label:'Otro',                color:'#64748B' },
};
const WA_PRIVADO = 'Fabrizio Monterroso'; // bandeja oculta: solo su usuario + PIN

function WAEquipo({ usuario }) {
  const [chats,    setChats]    = useState([]);
  const [mensajes, setMensajes] = useState([]);
  const [vend,     setVend]     = useState('todos');
  const [sel,      setSel]      = useState(null);
  const [busq,     setBusq]     = useState('');
  const [catF,     setCatF]     = useState('todas');
  const [loading,  setLoading]  = useState(true);
  const [pinOk,    setPinOk]    = useState(false);
  const [pinModal, setPinModal] = useState(false);
  const [pinVal,   setPinVal]   = useState('');
  const [categorizando, setCategorizando] = useState(false);

  const esFabrizio = String(usuario.usuario || usuario.nombre || '').trim().toLowerCase().startsWith('fabrizio');
  const [qrPanel, setQrPanel] = useState(false);
  const [qrs, setQrs]         = useState([]);

  // Panel de vinculación (solo admin): lee QR + estado que publica el puente
  useEffect(() => {
    if (!qrPanel) return;
    let vivo = true;
    const leer = async () => {
      const { data } = await supabase.from('radar_config').select('key,value').like('key','wa_bridge_%');
      if (!vivo) return;
      const por = {};
      (data||[]).forEach(r => {
        const m = r.key.match(/^wa_bridge_(qr|estado)_(.+)$/);
        if (!m) return;
        por[m[2]] = por[m[2]] || { id: m[2] };
        if (m[1]==='qr')     { por[m[2]].qr = r.value?.qr; por[m[2]].qrTs = r.value?.ts; }
        if (m[1]==='estado') { por[m[2]].estado = r.value?.estado; }
      });
      setQrs(Object.values(por).sort((a,b)=>a.id.localeCompare(b.id)));
    };
    leer();
    const t = setInterval(leer, 5000);
    return () => { vivo = false; clearInterval(t); };
  }, [qrPanel]);

  const cargandoRef = useRef(false);
  // Lista blanca de números (radar_config 'wa_filtro' = {"numeros":["504...."]}).
  // Para cuando el dispositivo vinculado es un teléfono personal: el espejo trae
  // TODOS sus chats, y en una demo o pantalla compartida eso no se puede ver.
  // Con la lista puesta, solo aparecen los chats de esos números.
  const filtroNumsRef = useRef(null);
  const [, setFiltroN] = useState(0); // solo para repintar el botón del filtro
  // Editar la lista sin SQL: un prompt con los números separados por coma.
  // Vacío = quitar el filtro (se ve todo).
  const editarFiltro = async () => {
    const actual = (filtroNumsRef.current || []).join(', ');
    const entrada = window.prompt(
      'Números visibles (separados por coma). Solo los chats con estos números se muestran.\nDejalo vacío y aceptá para QUITAR el filtro y ver todo.',
      actual
    );
    if (entrada === null) return; // canceló
    const nums = entrada.split(',').map(n => n.replace(/\D/g,'').slice(-8)).filter(n => n.length === 8);
    try {
      if (nums.length) {
        await supabase.from('radar_config').upsert(
          { key:'wa_filtro', value:{ numeros:nums }, updated_at:new Date().toISOString() }, { onConflict:'org_id,key' });
      } else {
        await supabase.from('radar_config').delete().eq('key','wa_filtro');
      }
      filtroNumsRef.current = nums;
      toast(nums.length ? `Filtro activo: solo ${nums.length} número${nums.length>1?'s':''} visible${nums.length>1?'s':''}.` : 'Filtro quitado: se ven todos los chats.');
      await cargar();
    } catch (e) { toast('No se pudo guardar el filtro: ' + e.message); }
  };
  const cargar = async (silencioso = false) => {
    if (cargandoRef.current) return;           // no solapar refrescos
    cargandoRef.current = true;
    if (!silencioso) setLoading(true);          // el poll de fondo no parpadea la UI
    if (filtroNumsRef.current === null) {
      try {
        const { data: cfg } = await supabase.from('radar_config').select('value').eq('key','wa_filtro').maybeSingle();
        filtroNumsRef.current = (cfg?.value?.numeros || []).map(n => String(n).replace(/\D/g,'').slice(-8)).filter(n => n.length === 8);
      } catch { filtroNumsRef.current = []; }
    }
    const { data } = await supabase.from('wa_equipo_chats').select('*')
      .order('ultima_actividad', { ascending: false }).limit(500);
    const lista = filtroNumsRef.current;
    const visibles = (lista && lista.length)
      ? (data || []).filter(c => lista.includes(String(c.cliente_numero||'').replace(/\D/g,'').slice(-8)))
      : (data || []);
    setChats(visibles);
    setFiltroN((lista||[]).length);
    if (!silencioso) setLoading(false);
    cargandoRef.current = false;
  };
  useEffect(() => { cargar(); }, []);

  const selRef = useRef(null);
  useEffect(() => { selRef.current = sel; }, [sel]);
  useEffect(() => {
    const t = setInterval(() => { cargar(true); if (selRef.current) abrirChat(selRef.current); }, 8000);
    return () => clearInterval(t);
  }, []);

  const abrirChat = async (c) => {
    setSel(c);
    const { data } = await supabase.from('wa_equipo_mensajes').select('*')
      .eq('vendedor', c.vendedor).eq('chat_jid', c.chat_jid)
      .order('ts', { ascending: true }).limit(500);
    setMensajes(data || []);
  };

  const categorizarAhora = async () => {
    setCategorizando(true);
    try {
      const r = await fetch('/api/wa-categorizar', { method: 'POST' });
      await r.json().catch(()=>null);
      await cargar();
    } catch {}
    setCategorizando(false);
  };

  // PIN de la bandeja privada: vive en radar_config (key 'wa_pin_privado', value {"pin":"..."}),
  // solo legible con sesión iniciada — ya no viaja en el código público del bundle.
  const verificarPin = async () => {
    let esperado = '7274'; // fallback transitorio si la config aún no existe
    try {
      const { data } = await supabase.from('radar_config').select('value').eq('key','wa_pin_privado').maybeSingle();
      if (data?.value?.pin) esperado = String(data.value.pin);
    } catch {}
    if (pinVal === esperado) { setPinOk(true); setPinModal(false); }
    else setPinVal('');
  };

  // La bandeja privada no se lista para nadie; para Fabrizio, solo tras el PIN
  const visibles = chats.filter(c => c.vendedor !== WA_PRIVADO || (esFabrizio && pinOk));
  const vendedores = [...new Set(visibles.map(c => c.vendedor))].sort();
  const hayPrivada = esFabrizio && chats.some(c => c.vendedor === WA_PRIVADO);

  const hoyStr = new Date().toISOString().split('T')[0];
  const filtrados = visibles.filter(c => {
    if (vend !== 'todos' && c.vendedor !== vend) return false;
    if (catF !== 'todas' && (c.categoria || 'otro') !== catF) return false;
    if (!busq) return true;
    const b = busq.toLowerCase();
    return (c.cliente_nombre||'').toLowerCase().includes(b) || (c.cliente_numero||'').includes(b) || (c.resumen||'').toLowerCase().includes(b);
  });

  const kpis = {
    activosHoy: visibles.filter(c => (c.ultima_actividad||'').slice(0,10) === hoyStr).length,
    nuevosHoy:  visibles.filter(c => (c.primera_entrada||'').slice(0,10) === hoyStr).length,
    sinCat:     visibles.filter(c => !c.categoria).length,
    total:      visibles.length,
  };

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontSize:20,fontWeight:900,color:C.t1}}>📡 WhatsApp del Equipo</div>
          <div style={{fontSize:12,color:C.t4,marginTop:2}}>Chats de los vendedores con clientes · registro automático · solo lectura</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          {usuario.rol==='admin' && (
            <button onClick={editarFiltro}
              title="Con la lista puesta, solo se ven los chats de esos números (para dispositivos personales)"
              style={{padding:'8px 14px',borderRadius:9,border:`1px solid ${(filtroNumsRef.current||[]).length?C.orangeBorder:C.border}`,
                background:(filtroNumsRef.current||[]).length?C.orangeTint:C.bg2,
                color:(filtroNumsRef.current||[]).length?C.orangeText:C.t3,fontSize:12,fontWeight:700,cursor:'pointer'}}>
              {(filtroNumsRef.current||[]).length ? `🔒 Filtro activo (${filtroNumsRef.current.length})` : '🔒 Números visibles'}
            </button>
          )}
          {usuario.rol==='admin' && (
            <button onClick={()=>setQrPanel(v=>!v)}
              style={{padding:'8px 14px',borderRadius:9,border:`1px solid ${qrPanel?C.blue:C.border}`,background:qrPanel?`${C.blue}15`:C.bg2,color:qrPanel?C.blue:C.t3,fontSize:12,fontWeight:700,cursor:'pointer'}}>
              📲 Vincular dispositivos
            </button>
          )}
          <button onClick={categorizarAhora} disabled={categorizando}
            style={{padding:'8px 14px',borderRadius:9,border:'none',background:C.blue,color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer',opacity:categorizando?0.6:1}}>
            {categorizando?'⏳ Categorizando…':'🤖 Categorizar con IA'}
          </button>
          <button onClick={cargar} style={{padding:'8px 12px',borderRadius:9,border:`1px solid ${C.border}`,background:C.bg2,color:C.t3,fontSize:12,fontWeight:700,cursor:'pointer'}}>🔄</button>
        </div>
      </div>

      {/* Panel de vinculación: QRs en vivo que publica el puente */}
      {qrPanel && usuario.rol==='admin' && (
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:16,marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:800,color:C.t1,marginBottom:4}}>📲 Vincular WhatsApp de cada persona</div>
          <div style={{fontSize:11,color:C.t4,marginBottom:14}}>
            En su teléfono: WhatsApp → Configuración → <b>Dispositivos vinculados</b> → Vincular dispositivo → escanear SU QR.
            Los QR se renuevan solos cada pocos segundos.
          </div>
          {qrs.length===0 && <div style={{fontSize:12,color:C.t4,padding:10}}>Esperando al puente… (¿está corriendo en Railway?)</div>}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
            {qrs.map(q=>(
              <div key={q.id} style={{border:`1px solid ${C.border}`,borderRadius:12,padding:12,textAlign:'center'}}>
                <div style={{fontSize:12,fontWeight:800,color:C.t1,marginBottom:2,textTransform:'capitalize'}}>{q.id}</div>
                <div style={{fontSize:10,fontWeight:700,marginBottom:8,
                  color:q.estado==='conectado'?C.green:q.estado==='esperando_qr'?C.orange:C.t4}}>
                  {q.estado==='conectado'?'✅ Conectado':q.estado==='esperando_qr'?'⏳ Esperando escaneo':q.estado||'—'}
                </div>
                {q.estado==='esperando_qr' && q.qr
                  ? <img src={q.qr} alt={`QR ${q.id}`} style={{width:190,height:190,borderRadius:8,background:'#fff',padding:6}}/>
                  : q.estado==='conectado'
                    ? <div style={{fontSize:34,padding:'40px 0'}}>📡</div>
                    : <div style={{fontSize:11,color:C.t4,padding:'40px 0'}}>sin QR publicado</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPIs */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10,marginBottom:14}}>
        {[['Chats activos hoy',kpis.activosHoy,C.blue],['Clientes nuevos hoy',kpis.nuevosHoy,C.greenText],['Sin categorizar',kpis.sinCat,C.orangeText],['Conversaciones',kpis.total,C.t1]].map(([l,v,col])=>(
          <div key={l} className="radar-kpi" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',boxShadow:SH.sm}}>
            <div style={{fontSize:11,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.07em'}}>{l}</div>
            <div className="radar-num" style={{fontSize:24,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,marginTop:6,color:col}}>{v}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
        <button className="radar-pill" onClick={()=>setVend('todos')} style={{padding:'0 12px',height:32,borderRadius:R.md,border:`1px solid ${vend==='todos'?C.primaryBorder:C.border}`,background:vend==='todos'?C.primaryTint:C.bg2,color:vend==='todos'?C.blue:C.t3,fontSize:12,fontWeight:600,cursor:'pointer'}}>Todos</button>
        {vendedores.map(v=>(
          <button key={v} className="radar-pill" onClick={()=>setVend(v)} style={{padding:'0 12px',height:32,borderRadius:R.md,border:`1px solid ${vend===v?C.primaryBorder:C.border}`,background:vend===v?C.primaryTint:C.bg2,color:vend===v?C.blue:C.t3,fontSize:12,fontWeight:600,cursor:'pointer'}}>{v.split(' ')[0]}{v.includes('Sibila')?' (NEXA)':''}</button>
        ))}
        {hayPrivada && !pinOk && (
          <button className="radar-pill" onClick={()=>{setPinModal(true);setPinVal('');}} style={{padding:'0 12px',height:32,borderRadius:R.md,border:`1px dashed ${C.border2}`,background:C.bg2,color:C.t4,fontSize:12,fontWeight:600,cursor:'pointer'}}>🔒 Privada</button>
        )}
        <span style={{flex:1}}/>
        <select className="radar-input" value={catF} onChange={e=>setCatF(e.target.value)} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'6px 10px',color:C.t2,fontSize:12}}>
          <option value="todas">Todas las categorías</option>
          {Object.entries(WA_CATS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
        </select>
        <input className="radar-input" value={busq} onChange={e=>setBusq(e.target.value)} placeholder="Buscar cliente, número..."
          style={{minWidth:180,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'6px 12px',color:C.t1,fontSize:13}}/>
      </div>

      {/* Lista + conversación */}
      <div style={{display:'grid',gridTemplateColumns:sel?'minmax(280px,1fr) 1.4fr':'1fr',gap:14,alignItems:'start'}}>
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,overflow:'hidden',maxHeight:560,overflowY:'auto'}}>
          {loading && <div style={{padding:30,textAlign:'center',color:C.t4,fontSize:13}}>Cargando…</div>}
          {!loading && filtrados.length===0 && (
            <div style={{padding:30,textAlign:'center',color:C.t4,fontSize:13}}>
              Sin conversaciones todavía.<br/>Cuando los vendedores escaneen su QR, aquí caen sus chats.
            </div>
          )}
          {filtrados.map(c=>{
            const cat = WA_CATS[c.categoria] || null;
            const act = sel && sel.id===c.id;
            return (
              <div key={c.id} className={act?'':'radar-row'} onClick={()=>abrirChat(c)}
                style={{padding:'10px 14px',borderBottom:`1px solid ${C.bg3}`,cursor:'pointer',background:act?C.primaryTint:'transparent',borderLeft:act?`3px solid ${C.blue}`:'3px solid transparent'}}>
                <div style={{display:'flex',justifyContent:'space-between',gap:8,marginBottom:3}}>
                  <span style={{fontSize:13,fontWeight:600,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.cliente_nombre||c.cliente_numero||'—'}</span>
                  <span style={{fontSize:10,color:C.t4,flexShrink:0}}>{c.ultima_actividad?new Date(c.ultima_actividad).toLocaleString('es-HN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}):''}</span>
                </div>
                <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
                  <span style={{fontSize:10,color:C.t4}}>{(c.vendedor||'').split(' ')[0]}</span>
                  {cat && <span style={{background:`${cat.color}14`,color:cat.color,border:`1px solid ${cat.color}33`,borderRadius:R.sm,padding:'1px 7px',fontSize:9,fontWeight:600}}>{cat.label}</span>}
                  <span style={{fontSize:10,color:C.t4}}>· {c.mensajes_total} msj</span>
                </div>
                {c.resumen && <div style={{fontSize:11,color:C.t3,marginTop:4,lineHeight:1.4}}>{c.resumen}</div>}
              </div>
            );
          })}
        </div>

        {sel && (
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,display:'flex',flexDirection:'column',maxHeight:560}}>
            <div style={{padding:'12px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:C.t1}}>{sel.cliente_nombre||sel.cliente_numero}</div>
                <div style={{fontSize:11,color:C.t4}}>{sel.cliente_numero} · vendedor: {sel.vendedor}</div>
              </div>
              <button onClick={()=>setSel(null)} style={{background:'none',border:'none',color:C.t4,fontSize:16,cursor:'pointer'}}>✕</button>
            </div>
            <div style={{flex:1,overflowY:'auto',padding:14,display:'flex',flexDirection:'column',gap:8}}>
              {mensajes.map(m=>(
                <div key={m.id} style={{alignSelf:m.direccion==='salida'?'flex-end':'flex-start',maxWidth:'80%',
                  background:m.direccion==='salida'?`${C.green}15`:C.bg3,
                  border:`1px solid ${m.direccion==='salida'?`${C.green}30`:C.border}`,
                  borderRadius:m.direccion==='salida'?'12px 12px 4px 12px':'12px 12px 12px 4px',padding:'8px 12px'}}>
                  <div style={{fontSize:13,color:C.t1,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{m.contenido}</div>
                  <div style={{fontSize:9,color:C.t4,marginTop:3,textAlign:'right'}}>{new Date(m.ts).toLocaleString('es-HN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal PIN bandeja privada */}
      {pinModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setPinModal(false)}>
          <div onClick={e=>e.stopPropagation()} className="radar-pop" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:24,width:300,maxHeight:'90vh',overflowY:'auto',boxShadow:SH.lg}}>
            <div style={{fontSize:15,fontWeight:700,color:C.t1,marginBottom:12}}>🔒 Bandeja privada</div>
            <input className="radar-input" type="password" inputMode="numeric" autoFocus value={pinVal} onChange={e=>setPinVal(e.target.value)}
              onKeyDown={e=>{ if(e.key==='Enter') verificarPin(); }}
              placeholder="PIN" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'10px 12px',color:C.t1,fontSize:16,textAlign:'center',letterSpacing:6}}/>
            <button className="radar-btn" onClick={verificarPin}
              style={{marginTop:12,width:'100%',padding:10,borderRadius:R.md,border:'none',background:C.blue,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',boxShadow:SH.primary}}>Abrir</button>
          </div>
        </div>
      )}
    </div>
  );
}

function WhatsApp({ usuario }) {
  const [tab, setTab]             = useState('equipo');
  const [convs, setConvs]         = useState([]);
  const [selPhone, setSelPhone]   = useState(null);
  const [mensajes, setMensajes]   = useState([]);
  const [reply, setReply]         = useState('');
  const [sending, setSending]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [buscar, setBuscar]       = useState('');
  const msgEndRef = useRef(null);
  // El cierre del circuito NEXA: de la conversación del bot sale una oportunidad
  // asignada a un vendedor, y desde ahí la venta se trabaja en el CRM.
  const [vendedores, setVendedores] = useState([]);
  const [asignOpen, setAsignOpen] = useState(false);
  const [asignVend, setAsignVend] = useState('');
  const [asignNombre, setAsignNombre] = useState('');
  const [creandoOpp, setCreandoOpp] = useState(false);
  useEffect(() => {
    supabase.from('radar_usuarios').select('nombre').in('rol',['ventas','gerente_ventas','admin']).eq('activo',true)
      .then(({data}) => setVendedores((data||[]).map(u=>u.nombre)));
  }, []);
  const [oppDeChat, setOppDeChat] = useState(null); // opp ya creada para este numero
  useEffect(() => { (async () => {
    setOppDeChat(null);
    if (!selPhone) return;
    const num = String(selPhone).replace(/\D/g,'');
    if (num.length < 8) return;
    const { data } = await supabase.from('oportunidades')
      .select('id,titulo,vendedor,etapa').ilike('notas', `%${num}%`).limit(1);
    if (data && data[0]) setOppDeChat(data[0]);
  })(); }, [selPhone]); // eslint-disable-line
  const crearOppDesdeChat = async () => {
    if (!asignVend || !selPhone) return;
    setCreandoOpp(true);
    try {
      const nombre = asignNombre.trim() || `Lead WhatsApp ${selPhone}`;
      const { error } = await supabase.from('oportunidades').insert({
        titulo: `Lead WhatsApp — ${nombre}`,
        cliente: nombre,
        etapa: 'prospecto',
        vendedor: asignVend,
        notas: `Tel: +${String(selPhone).replace(/\D/g,'')}\nOrigen: bot de WhatsApp (NEXA)`,
        ultimo_seguimiento: new Date().toISOString().split('T')[0],
        empresa_id: ORG.empresas[0]?.id || 'honduras',
      });
      if (error) throw error;
      toast(`Oportunidad creada y asignada a ${asignVend}. Ya está en el CRM.`);
      setOppDeChat({ titulo: nombre, vendedor: asignVend, etapa: 'prospecto' });
      setAsignOpen(false); setAsignVend(''); setAsignNombre('');
    } catch (e) { toast('No se pudo crear: ' + e.message); }
    setCreandoOpp(false);
  };

  const cargarConvs = async () => {
    setLoading(true);
    // role='status' son ACUSES DE ENTREGA de Meta (sent/delivered/read), no mensajes.
    // Se guardan para diagnostico (api/whatsapp.js), pero mostrarlos como conversacion
    // hacia parecer que el bot le escribia "delivered" a la gente e inflaba el conteo.
    const { data } = await supabase
      .from('wa_mensajes')
      .select('*')
      .neq('role', 'status')
      .order('created_at', { ascending: false });
    if (!data) { setLoading(false); return; }
    // Agrupar por wa_from — última conversación primero
    const map = {};
    data.forEach(m => {
      if (!map[m.wa_from]) map[m.wa_from] = { phone: m.wa_from, ultimo: m.contenido, fecha: m.created_at, total: 0, sinLeer: 0 };
      map[m.wa_from].total++;
    });
    setConvs(Object.values(map));
    setLoading(false);
  };

  const cargarMensajes = async (phone) => {
    setLoadingMsg(true);
    const { data } = await supabase
      .from('wa_mensajes')
      .select('*')
      .eq('wa_from', phone)
      .neq('role', 'status')
      .order('created_at', { ascending: true });
    setMensajes(data || []);
    setLoadingMsg(false);
    setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const seleccionarConv = (phone) => {
    setSelPhone(phone);
    cargarMensajes(phone);
    setReply('');
  };

  const enviarRespuesta = async () => {
    if (!reply.trim() || !selPhone || sending) return;
    setSending(true);
    try {
      await fetch('/api/whatsapp-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: selPhone, message: reply.trim() }),
      });
      setReply('');
      await cargarMensajes(selPhone);
      await cargarConvs();
    } catch {}
    setSending(false);
  };

  useEffect(() => { cargarConvs(); }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      cargarConvs();
      if (selPhone) cargarMensajes(selPhone);
    }, 20000);
    return () => clearInterval(interval);
  }, [selPhone]);

  const fmtFecha = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const hoy = new Date();
    if (d.toDateString() === hoy.toDateString()) return d.toLocaleTimeString('es-HN', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('es-HN', { day: '2-digit', month: 'short' });
  };

  const convsFiltradas = convs.filter(c => c.phone.includes(buscar));

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* ── Tabs ── */}
      <div style={{display:'flex',alignItems:'center',gap:10,borderBottom:`1px solid ${C.border}`,background:C.bg2,flexShrink:0,padding:'10px 16px'}}>
        <Seg value={tab} onChange={setTab} items={[['equipo','Equipo',CI.broadcast],['inbox','Bot / NEXA',CI.inbox]]}/>
      </div>
      {tab==='equipo' && <div style={{flex:1,overflowY:'auto',padding:18}}><WAEquipo usuario={usuario}/></div>}

      {tab==='inbox' && <div style={{flex:1,display:'flex',overflow:'hidden'}}>
      {/* ── Panel izquierdo — Lista de conversaciones ── */}
      <div style={{width:300,borderRight:`1px solid ${C.border}`,display:'flex',flexDirection:'column',background:C.bg2,flexShrink:0}}>
        {/* Header */}
        <div style={{padding:'16px 16px 12px',borderBottom:`1px solid ${C.border}`}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
            <div style={{fontSize:15,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>WhatsApp</div>
            <button className="radar-btn" onClick={cargarConvs} title="Refrescar" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,color:C.t3,cursor:'pointer',width:30,height:30,display:'inline-flex',alignItems:'center',justifyContent:'center'}}><CIcon d={CI.refresh} size={15}/></button>
          </div>
          <div style={{position:'relative'}}>
            <span style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:C.t4,pointerEvents:'none',display:'inline-flex'}}><CIcon d={CI.search} size={14}/></span>
            <input className="radar-input"
              value={buscar} onChange={e=>setBuscar(e.target.value)}
              placeholder="Buscar número..."
              style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 12px 8px 32px',fontSize:12.5,color:C.t1,outline:'none'}}
            />
          </div>
        </div>
        {/* Lista */}
        <div style={{flex:1,overflowY:'auto'}}>
          {loading && <div style={{padding:24,textAlign:'center',color:C.t4,fontSize:12}}>Cargando...</div>}
          {!loading && convsFiltradas.length === 0 && (
            <div style={{padding:32,textAlign:'center',color:C.t4,fontSize:12}}>
              <div style={{width:52,height:52,borderRadius:R.lg,background:C.bg3,border:`1px solid ${C.border}`,color:C.t4,display:'inline-flex',alignItems:'center',justifyContent:'center',marginBottom:10}}><CIcon d={CI.inbox} size={24}/></div>
              <div>Sin conversaciones aún</div>
              <div style={{marginTop:8,fontSize:11}}>Los mensajes aparecerán aquí cuando el bot reciba WhatsApps</div>
            </div>
          )}
          {convsFiltradas.map(c => (
            <div key={c.phone} onClick={() => seleccionarConv(c.phone)}
              className={selPhone===c.phone?'':'radar-row'}
              style={{padding:'12px 16px',borderBottom:`1px solid ${C.bg3}`,cursor:'pointer',
                ...(selPhone===c.phone ? {background:C.primaryTint} : {}),
                borderLeft: selPhone===c.phone ? `3px solid ${C.blue}` : '3px solid transparent',
                transition:'background 0.12s ease'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:4}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:34,height:34,borderRadius:'50%',background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,flexShrink:0}}>
                    {c.phone.slice(-2)}
                  </div>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:C.t1}}>{c.phone}</div>
                    <div style={{fontSize:11,color:C.t4}}>{c.total} mensajes</div>
                  </div>
                </div>
                <div style={{fontSize:10,color:C.t4,flexShrink:0}}>{fmtFecha(c.fecha)}</div>
              </div>
              <div style={{fontSize:11,color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',paddingLeft:42}}>
                {c.ultimo?.slice(0,60)}{c.ultimo?.length>60?'...':''}
              </div>
            </div>
          ))}
        </div>
        {/* Stats footer */}
        <div style={{padding:'10px 16px',borderTop:`1px solid ${C.border}`,display:'flex',gap:16}}>
          <div style={{fontSize:11,color:C.t4}}><span style={{fontWeight:700,color:C.t1}}>{convs.length}</span> conversaciones</div>
        </div>
      </div>

      {/* ── Panel derecho — Thread ── */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {!selPhone ? (
          <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:12,color:C.t4}}>
            <div style={{width:64,height:64,borderRadius:R.lg,background:C.bg3,border:`1px solid ${C.border}`,color:C.t4,display:'inline-flex',alignItems:'center',justifyContent:'center'}}><CIcon d={CI.phone} size={28}/></div>
            <div style={{fontSize:14,fontWeight:600,color:C.t2}}>Selecciona una conversación</div>
            <div style={{fontSize:12}}>Los mensajes del bot y los clientes aparecen aquí</div>
          </div>
        ) : (
          <>
            {/* Header del chat */}
            <div style={{padding:'14px 20px',borderBottom:`1px solid ${C.border}`,background:C.bg2,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <div style={{width:38,height:38,borderRadius:'50%',background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,fontWeight:700}}>
                  {selPhone.slice(-2)}
                </div>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:C.t1}}>{selPhone}</div>
                  <div style={{fontSize:11,color:C.greenText,fontWeight:600}}>● Bot activo</div>
                </div>
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                {oppDeChat ? (
                  <button onClick={()=>setAsignOpen(v=>!v)} title="Ya tiene oportunidad — clic para crear otra"
                    style={{height:32,padding:'0 14px',borderRadius:R.md,border:`1px solid ${C.greenBorder}`,background:C.greenTint,color:C.greenText,
                      fontSize:12,fontWeight:800,cursor:'pointer'}}>
                    ✓ Asignada a {String(oppDeChat.vendedor||'').split(' ')[0]}
                  </button>
                ) : (
                  <button onClick={()=>setAsignOpen(v=>!v)}
                    style={{height:32,padding:'0 14px',borderRadius:R.md,border:'none',background:C.primary,color:'#fff',
                      fontSize:12,fontWeight:700,cursor:'pointer',boxShadow:SH.primary}}>
                    Asignar a vendedor
                  </button>
                )}
                <button className="radar-btn" onClick={()=>cargarMensajes(selPhone)} title="Refrescar" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,color:C.t3,cursor:'pointer',width:30,height:30,display:'inline-flex',alignItems:'center',justifyContent:'center'}}><CIcon d={CI.refresh} size={15}/></button>
              </div>
            </div>

            {/* De chat del bot a oportunidad del CRM: se elige vendedor, se le pone
                nombre al lead, y la venta sigue su vida en el pipeline. */}
            {asignOpen && (
              <div style={{padding:'12px 20px',borderBottom:`1px solid ${C.border}`,background:C.primaryTint,display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
                <div style={{flex:1,minWidth:170}}>
                  <div style={{fontSize:10,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4,marginBottom:4}}>Nombre del lead / empresa</div>
                  <input value={asignNombre} onChange={e=>setAsignNombre(e.target.value)} placeholder={`Lead WhatsApp ${selPhone}`}
                    style={{width:'100%',height:32,padding:'0 10px',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,color:C.t1,fontSize:12.5,outline:'none'}}/>
                </div>
                <div style={{minWidth:170}}>
                  <div style={{fontSize:10,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4,marginBottom:4}}>Vendedor</div>
                  <select value={asignVend} onChange={e=>setAsignVend(e.target.value)}
                    style={{width:'100%',height:32,padding:'0 8px',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,color:C.t1,fontSize:12.5,cursor:'pointer',outline:'none'}}>
                    <option value=''>— Elegir —</option>
                    {vendedores.map(v=><option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <Btn size="sm" disabled={!asignVend||creandoOpp} onClick={crearOppDesdeChat}>
                  {creandoOpp ? 'Creando…' : 'Crear oportunidad'}
                </Btn>
              </div>
            )}

            {/* Mensajes */}
            <div style={{flex:1,overflowY:'auto',padding:'20px 24px',display:'flex',flexDirection:'column',gap:10}}>
              {loadingMsg && <div style={{textAlign:'center',color:C.t4,fontSize:12}}>Cargando mensajes...</div>}
              {mensajes.map((m,i) => (
                <div key={m.id||i} style={{display:'flex',justifyContent: m.role==='user'?'flex-start':'flex-end'}}>
                  <div style={{
                    maxWidth:'70%',
                    background: m.role==='user' ? C.bg2 : C.greenTint,
                    border: `1px solid ${m.role==='user' ? C.border : C.greenBorder}`,
                    borderRadius: m.role==='user' ? '4px 14px 14px 14px' : '14px 4px 14px 14px',
                    padding:'10px 14px',
                    boxShadow:SH.xs,
                  }}>
                    <div style={{fontSize:10,fontWeight:700,color: m.role==='user'?C.t4:C.greenText,marginBottom:4,display:'inline-flex',alignItems:'center',gap:4}}>
                      <CIcon d={m.role==='user'?CI.user:CI.zap} size={11}/>{m.role==='user' ? 'Cliente' : 'Bot RADAR'}
                    </div>
                    <div style={{fontSize:13,color:C.t1,lineHeight:1.5,whiteSpace:'pre-wrap'}}>{m.contenido}</div>
                    <div style={{fontSize:10,color:C.t4,marginTop:4,textAlign:'right'}}>{fmtFecha(m.created_at)}</div>
                  </div>
                </div>
              ))}
              <div ref={msgEndRef}/>
            </div>

            {/* Input respuesta manual */}
            <div style={{padding:'12px 20px',borderTop:`1px solid ${C.border}`,background:C.bg2}}>
              <div style={{fontSize:10,color:C.t4,marginBottom:6,display:'inline-flex',alignItems:'center',gap:5}}><CIcon d={CI.zap} size={11}/>El bot responde automáticamente — esto es para respuesta manual del agente</div>
              <div style={{display:'flex',gap:10}}>
                <input
                  value={reply} onChange={e=>setReply(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&enviarRespuesta()}
                  placeholder="Escribe una respuesta manual..."
                  style={{flex:1,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'10px 14px',fontSize:13,color:C.t1,outline:'none'}}
                />
                <button onClick={enviarRespuesta} disabled={sending||!reply.trim()}
                  style={{background:C.blue,color:'#fff',border:'none',borderRadius:R.md,padding:'10px 18px',fontSize:13,fontWeight:600,cursor:sending?'wait':'pointer',opacity:sending||!reply.trim()?0.5:1,boxShadow:sending||!reply.trim()?'none':SH.primary}}>
                  {sending?'..':'Enviar'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      </div>}
    </div>
  );
}


// ─── CLIENTES INACTIVOS / SIN ASIGNAR ─────────────────────────────────────────
function ClientesInactivos({ usuario, empresaActiva }) {
  const [tab, setTab]             = useState('inactivos'); // inactivos | sin_asignar
  const [empresa, setEmpresa]     = useState(empresaActiva || 'all');
  // Umbral configurable: cada org decide cuantos meses sin comprar hacen
  // "inactivo" a un cliente. Se guarda en radar_config y se manda al API.
  const [mesesInact, setMesesInact] = useState(6);
  const [mesesListo, setMesesListo] = useState(false);
  const [filtroVend, setFiltroVend] = useState([]);   // multi ([] = todos)
  const [loading, setLoading]     = useState(false);
  const [data, setData]           = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [asignando, setAsignando] = useState(null); // cardCode
  const [expandido, setExpandido] = useState(null); // cardCode
  const [cotLines, setCotLines]   = useState({}); // cardCode → {lines, doc}
  const [loadingCot, setLoadingCot] = useState(null);
  const [sapCfg, setSapCfg]       = useState(null);

  const fmt = n => `$${Number(n||0).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})}`;
  const fmtF = d => d ? new Date(String(d).slice(0,10)+'T12:00:00').toLocaleDateString('es-HN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
  // Formatea monto con etiqueta de moneda real (USD muestra $, HNL/CRC su símbolo)
  const fmtMon = (n, moneda) => {
    const v = (n||0).toLocaleString('en-US');
    if (moneda === 'USD') return `$${v}`;
    if (moneda === 'CRC') return `₡${v}`;
    if (moneda === 'HNL') return `L ${v}`;
    return `$${v}`;
  };
  const diasInactivo = d => { if (!d) return 999; return Math.floor((Date.now()-new Date(d+'T12:00:00').getTime())/86400000); };
  // Identidad única: el mismo cardCode puede existir en HN y CR
  const keyOf = c => `${c.empresa_id||''}_${c.cardCode}`;
  const flagOf = () => '';

  useEffect(() => {
    supabase.from('radar_config').select('value').eq('key','inactivos_meses').maybeSingle()
      .then(({data}) => { if (data?.value?.meses) setMesesInact(Number(data.value.meses)); setMesesListo(true); });
    supabase.from('radar_config').select('key,value').in('key',['sap_config','sap_config_cr']).then(({data:rows})=>{
      const m = Object.fromEntries((rows||[]).map(r=>[r.key,r.value]));
      setSapCfg(m);
    });
    supabase.from('radar_usuarios').select('nombre,rol').in('rol',['ventas','gerente_ventas','admin']).eq('activo',true).then(({data:u})=>setVendedores(u||[]));
  },[]);

  useEffect(() => { if (mesesListo) cargar(); }, [tab, empresa, mesesInact, mesesListo]);
  // La pestaña "Sin asignar" necesita sapCfg (carga async): re-cargar al llegar
  useEffect(() => { if (tab==='sin_asignar' && sapCfg) cargar(); }, [sapCfg]);

  const getSapCfgForEmpresa = (eid) => {
    if (eid === 'costarica') return sapCfg?.sap_config_cr;
    return sapCfg?.sap_config;
  };

  const cargar = async () => {
    setLoading(true); setData([]);
    try {
      if (tab === 'inactivos') {
        const eid = empresa === 'all' ? undefined : empresa;
        const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ action:'clientes_6meses', empresa_id: eid || 'all', meses: mesesInact }) });
        const d = await r.json();
        setData(d.clientes || []);
      } else {
        // Sin asignar — necesita credenciales SAP
        const empresas = empresa === 'all' ? ['honduras','costarica'] : [empresa];
        const resultados = [];
        for (const eid of empresas) {
          const cfg = getSapCfgForEmpresa(eid);
          if (!cfg?.url) continue;
          const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
            body: JSON.stringify({ action:'clientes_inactivos', empresa_id: eid, ...cfg }) });
          const d = await r.json();
          if (d.ok) resultados.push(...(d.clientes||[]).map(c=>({...c, empresa_id:eid})));
        }
        setData(resultados);
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const verCotizacion = async (cliente) => {
    const k = keyOf(cliente);
    const docNum = cliente.ultimaCotizacion?.docNum;
    if (!docNum) return;
    if (cotLines[k]) { setExpandido(expandido===k?null:k); return; }
    setLoadingCot(k); setExpandido(k);
    try {
      // empresa_id: el servidor resuelve las credenciales SAP (no viajan al navegador)
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'quotation_lines', docNum, empresa_id: cliente.empresa_id }) });
      const d = await r.json();
      if (d.ok) setCotLines(prev=>({...prev,[k]:d}));
    } catch(e) {}
    setLoadingCot(null);
  };

  const asignar = async (cl, vendedor) => {
    setAsignando(null);
    // Aviso al vendedor: le cayo un cliente (campanita).
    if (vendedor && vendedor !== usuario.nombre) {
      notificar({ para: vendedor, tipo: 'cliente_asignado',
        titulo: 'Te asignaron un cliente',
        mensaje: `${usuario.nombre} te asignó el cliente "${cl.nombre}"`,
        modulo: 'crm' , orgId: usuario?.org_id || null });
    }
    const prevVend = cl.vendedor;
    const revert = () => setData(prev=>prev.map(c=>keyOf(c)===keyOf(cl)?{...c,vendedor:prevVend}:c));
    // Optimista
    setData(prev=>prev.map(c=>keyOf(c)===keyOf(cl)?{...c,vendedor}:c));
    if (!cl.empresa_id) { revert(); toast('Cliente sin empresa identificada; no se pudo guardar.'); return; }
    try {
      // Server-side: escribe el vendedor en SAP y guarda en RADAR sin duplicar
      const r = await fetch('/api/sap', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'claim_cliente', empresa_id: cl.empresa_id, cardCode: cl.cardCode,
          vendedor, nombre: cl.nombre, ciudad: cl.ciudad, telefono: cl.telefono, email: cl.email }) });
      const d = await r.json();
      if (!d.ok) { revert(); toast('No se pudo guardar: ' + (d.error || 'error desconocido')); return; }
      if (!d.sapUpdated) toast('Tomado en RADAR ✅\nNota: no se actualizó en SAP — ' + (d.sapMsg || 'sin detalle') + '.');
    } catch(e) { revert(); toast('Error de red al asignar.'); }
  };

  const empresaLabel = {all:'Todo',honduras:'HN',costarica:'CR'};
  const lista = (tab==='inactivos' && filtroVend.length)
    ? data.filter(c => filtroVend.includes(c.vendedor) || (filtroVend.includes('__none__') && !c.vendedor))
    : data;

  return (
    <div style={{flex:1,overflowY:'auto',background:C.bg1}}>
      {/* Header */}
      <div style={{background:C.bg2,borderBottom:`1px solid ${C.border}`,padding:'16px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12}}>
        <div>
          <div style={{fontSize:18,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>Clientes Sin Atender</div>
          <div style={{fontSize:12,color:C.t3,marginTop:2}}>Sin asignar en SAP · Sin comprar en {mesesInact}+ meses · <span className="radar-num">{lista.length}</span> clientes</div>
        </div>
        <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
          {tab==='inactivos' && (
            <div style={{display:'inline-flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:10.5,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4}}>Sin comprar hace</span>
              <div style={{display:'inline-flex',gap:2,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2}}>
                {[3,6,9,12,18,24].map(m=>(
                  <button key={m} onClick={async ()=>{ setMesesInact(m);
                    await supabase.from('radar_config').upsert({key:'inactivos_meses',value:{meses:m},updated_at:new Date().toISOString()},{onConflict:'org_id,key'});
                  }}
                    style={{height:28,padding:'0 11px',border:'none',borderRadius:7,cursor:'pointer',fontSize:12,fontWeight:700,
                      background:mesesInact===m?C.bg2:'transparent',color:mesesInact===m?C.t1:C.t3,boxShadow:mesesInact===m?SH.sm:'none'}}>
                    {m}m
                  </button>
                ))}
              </div>
            </div>
          )}
          {tab==='inactivos' && (
            <VendChips vendedores={vendedores.map(v=>v.nombre)} value={filtroVend}
              onChange={setFiltroVend} extra={[['__none__','Sin asignar']]}
              counts={v => !v ? data.length : v==='__none__' ? data.filter(c=>!c.vendedor).length : data.filter(c=>c.vendedor===v).length}/>
          )}
          {ORG.empresas.length > 1 && (
            <div style={{display:'flex',gap:2,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2}}>
              {['all','honduras','costarica'].map(e=>(
                <button key={e} onClick={()=>setEmpresa(e)}
                  style={{height:28,padding:'0 12px',borderRadius:R.sm,fontSize:12,fontWeight:600,cursor:'pointer',border:'none',background:empresa===e?C.bg2:'transparent',color:empresa===e?C.t1:C.t3,boxShadow:empresa===e?SH.sm:'none'}}>
                  {empresaLabel[e]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{background:C.bg2,borderBottom:`1px solid ${C.border}`,padding:'0 24px',display:'flex',gap:0}}>
        {[['inactivos',`Clientes inactivos`],['sin_asignar','Sin asignar en SAP']].map(([id,lbl])=>(
          <button key={id} onClick={()=>setTab(id)}
            style={{padding:'10px 18px',border:'none',background:'transparent',cursor:'pointer',fontSize:12,fontWeight:tab===id?700:400,color:tab===id?C.blue:C.t4,borderBottom:`2px solid ${tab===id?C.blue:'transparent'}`,marginBottom:-1}}>
            {lbl}
          </button>
        ))}
      </div>

      <div style={{padding:20,maxWidth:1100,margin:'0 auto'}}>
        {loading ? (
          <div style={{textAlign:'center',padding:60,color:C.t4,fontSize:13}}>Cargando clientes...</div>
        ) : lista.length === 0 ? (
          <div style={{textAlign:'center',padding:60,color:C.t4}}>
            
            <div style={{fontSize:14,fontWeight:700}}>Sin clientes en esta categoría</div>
          </div>
        ) : tab==='inactivos' ? (
          (() => {
            const maxDias = Math.max(1, ...lista.map(c=>diasInactivo(c.ultimaCompra)));
            const valorDormido = lista.reduce((x,c)=>x+(Number(c.compradoAnual)||0),0);
            const sinAsig = lista.filter(c=>!c.vendedor).length;
            const dolar = n => `$${Math.round(n||0).toLocaleString('en-US')}`;
            return (
              <>
                {/* KPIs del modulo (mockup): cuantos duermen y cuanto valen */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:12}}>
                  {[['Clientes dormidos', lista.length, `sin comprar en ${mesesInact}+ meses`, C.t1],
                    ['Valor histórico dormido', dolar(valorDormido), 'lo que compraban por año', C.orangeText],
                    ['Sin vendedor', sinAsig, 'nadie es responsable', sinAsig>0?C.redText:C.t1]].map(([l,v,s2,cx])=>(
                    <div key={l} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:11,boxShadow:SH.xs,padding:'9px 13px'}}>
                      <div style={{fontSize:9,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4}}>{l}</div>
                      <div className="radar-num" style={{fontSize:19,fontWeight:800,letterSpacing:'-0.02em',color:cx}}>{typeof v==='number'?v.toLocaleString('en-US'):v}</div>
                      <div style={{fontSize:9.5,color:C.t4}}>{s2}</div>
                    </div>
                  ))}
                </div>
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:SH.xs,overflow:'hidden'}}>
                  <div style={{display:'grid',gridTemplateColumns:'2fr 1.1fr 1.1fr 1fr 1fr 220px',gap:10,alignItems:'center',padding:'9px 16px',borderBottom:`1px solid ${C.border}`}}>
                    {['Cliente','Última compra','Tiempo dormido','Compraba por año','Vendedor',''].map((h,i)=>(
                      <span key={h+i} style={{fontSize:10,fontWeight:800,letterSpacing:'.06em',textTransform:'uppercase',color:C.t3,textAlign:i===3?'right':'left'}}>{h}</span>
                    ))}
                  </div>
                  {lista.map(cl => {
                    const k = keyOf(cl);
                    const d = diasInactivo(cl.ultimaCompra);
                    const exp = expandido === k;
                    const lines = cotLines[k];
                    const tieneCot = cl.ultimaCotizacion?.docNum;
                    const dc = d>365?C.redText:d>270?C.orangeText:C.orangeText;
                    const db = d>365?C.red:C.orange;
                    const waN = String(cl.telefono||'').replace(/[^0-9]/g,'');
                    return (
                      <div key={k}>
                        <div className="radar-hrow" style={{display:'grid',gridTemplateColumns:'2fr 1.1fr 1.1fr 1fr 1fr 220px',gap:10,alignItems:'center',padding:'10px 16px',borderBottom:`1px solid ${C.bg3}`}}>
                          <span style={{minWidth:0}}>
                            <span style={{display:'block',fontSize:13,fontWeight:800,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{cl.nombre}</span>
                            <span className="radar-num" style={{fontSize:10.5,color:C.t4}}>{cl.cardCode}{cl.ciudad?` · ${cl.ciudad}`:''}
                              {tieneCot && <button onClick={()=>verCotizacion(cl)} style={{background:'none',border:'none',color:C.blue,fontSize:10.5,fontWeight:700,cursor:'pointer',padding:0,marginLeft:6}}>
                                {loadingCot===k?'cargando…':(exp&&lines?'ocultar cotización ▲':`últ. cotización #${cl.ultimaCotizacion.docNum} ▼`)}</button>}
                            </span>
                          </span>
                          <span className="radar-num" style={{fontSize:12,color:C.t2,fontWeight:600}}>{fmtF(cl.ultimaCompra)}</span>
                          <span>
                            <span className="radar-num" style={{fontSize:12.5,fontWeight:800,color:dc}}>{d} días</span>
                            <span style={{display:'block',height:6,background:C.bg3,borderRadius:4,overflow:'hidden',marginTop:4,maxWidth:120}}>
                              <span style={{display:'block',height:'100%',borderRadius:4,width:`${Math.min(100,(d/maxDias)*100)}%`,background:db}}/>
                            </span>
                          </span>
                          <span className="radar-num" style={{fontSize:12.5,fontWeight:800,textAlign:'right',color:cl.compradoAnual>0?C.t1:C.t4}}>
                            {cl.compradoAnual>0?dolar(cl.compradoAnual):'—'}
                          </span>
                          <span>
                            {cl.vendedor
                              ? <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11.5,fontWeight:700,color:C.t2}}><AvatarChip name={cl.vendedor}/>{cl.vendedor.split(' ')[0]}</span>
                              : <span style={{fontSize:11,color:C.orangeText,fontWeight:800}}>sin asignar</span>}
                          </span>
                          <span style={{display:'flex',gap:5,justifyContent:'flex-end',position:'relative'}}>
                            {cl.vendedor !== usuario?.nombre && (
                              <button onClick={()=>asignar(cl, usuario.nombre)}
                                style={{height:30,padding:'0 11px',borderRadius:8,border:`1px solid ${C.greenBorder}`,background:C.greenTint,color:C.greenText,fontSize:11,fontWeight:800,cursor:'pointer'}}>✓ Tomar</button>
                            )}
                            {['admin','gerente_ventas'].includes(usuario?.rol) && (
                              <button onClick={()=>setAsignando(asignando===k?null:k)}
                                style={{height:30,padding:'0 9px',borderRadius:8,border:`1px solid ${C.orangeBorder}`,background:C.orangeTint,color:C.orangeText,fontSize:11,fontWeight:800,cursor:'pointer'}}>Asignar ▾</button>
                            )}
                            {waN.length>=8 && <a href={`https://wa.me/${waN.length===8?'504'+waN:waN}`} target="_blank" rel="noreferrer" title="WhatsApp"
                              style={{width:30,height:30,borderRadius:8,border:`1px solid ${C.border}`,background:C.bg2,display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:13,textDecoration:'none'}}>💬</a>}
                            {asignando===k && (
                              <div style={{position:'absolute',top:34,right:0,zIndex:40,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:6,boxShadow:SH.md,minWidth:170}}>
                                {vendedores.map(v=>(
                                  <div key={v.nombre} onClick={()=>asignar(cl,v.nombre)}
                                    style={{padding:'6px 9px',fontSize:12,cursor:'pointer',borderRadius:R.sm,color:C.t2}}
                                    onMouseEnter={e=>e.currentTarget.style.background=C.bg3}
                                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>{v.nombre}</div>
                                ))}
                                <div onClick={()=>setAsignando(null)} style={{padding:'5px 9px',fontSize:11,cursor:'pointer',color:C.t4}}>Cancelar</div>
                              </div>
                            )}
                          </span>
                        </div>
                        {exp && lines?.lines?.length > 0 && (
                          <div style={{borderBottom:`1px solid ${C.border}`,padding:'12px 16px',background:C.bg1}}>
                            <div style={{fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Líneas de cotización #{cl.ultimaCotizacion?.docNum}</div>
                            {lines.lines.map((l,i)=>(
                              <div key={i} style={{display:'flex',gap:10,fontSize:11.5,padding:'4px 0',borderBottom:`1px solid ${C.bg3}`}}>
                                <span style={{flex:1,color:C.t2}}>{l.ItemDescription||l.Dscription||l.ItemCode||'—'}</span>
                                <span className="radar-num" style={{color:C.t3}}>× {l.Quantity}</span>
                                <span className="radar-num" style={{color:C.blue,fontWeight:700}}>{fmtMon(l.lineTotal ?? l.LineTotal, lines.moneda)}</span>
                              </div>
                            ))}
                            <div className="radar-num" style={{textAlign:'right',marginTop:8,fontSize:12,fontWeight:700,color:C.blue}}>
                              Total: {fmtMon(lines.docTotal ?? lines.doc?.DocTotal, lines.moneda)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{fontSize:10.5,color:C.t4,marginTop:8}}>"Compraba por año" = la suma de sus facturas de los 12 meses antes de dormirse. Al asignar, al vendedor le llega la notificación.</div>
              </>
            );
          })()
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {lista.map(cl => {
              const k = keyOf(cl);
              const dias = tab==='inactivos' ? diasInactivo(cl.ultimaCompra) : null;
              const exp = expandido === k;
              const lines = cotLines[k];
              const tieneCot = cl.ultimaCotizacion?.docNum;
              const urgTone = dias>365?TONES.red:dias>270?TONES.orange:TONES.orange;
              const urgColor = urgTone.text;
              const flag = empresa==='all' ? flagOf(cl) : '';
              return (
                <div key={k} className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,overflow:'hidden',boxShadow:SH.sm}}>
                  <div style={{padding:'12px 16px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                    {/* Info principal */}
                    <div style={{flex:'1 1 200px',minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{flag&&`${flag} `}{cl.nombre}</div>
                      <div className="radar-num" style={{fontSize:11,color:C.t4,marginTop:2}}>{cl.cardCode}{cl.ciudad?` · ${cl.ciudad}`:''}</div>
                    </div>
                    {/* Contacto */}
                    <div style={{flex:'0 0 160px',fontSize:11,color:C.t3}}>
                      {cl.telefono&&<div className="radar-num">{cl.telefono}</div>}
                      {cl.email&&<div style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:160}}>✉ {cl.email}</div>}
                    </div>
                    {/* Última compra */}
                    {tab==='inactivos' && (
                      <div style={{flex:'0 0 140px',textAlign:'center'}}>
                        <div style={{fontSize:10,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em'}}>Última compra</div>
                        <div className="radar-num" style={{fontSize:12,fontWeight:600,color:urgColor}}>{fmtF(cl.ultimaCompra)}</div>
                        <div className="radar-num" style={{fontSize:10,fontWeight:700,color:urgColor}}>{dias}d sin comprar</div>
                      </div>
                    )}
                    {/* Última cotización */}
                    {tieneCot && (
                      <div style={{flex:'0 0 160px',textAlign:'center'}}>
                        <div style={{fontSize:10,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em'}}>Últ. cotización SAP</div>
                        <div className="radar-num" style={{fontSize:12,color:C.t2,fontWeight:600}}>#{cl.ultimaCotizacion.docNum}</div>
                        <div className="radar-num" style={{fontSize:11,color:C.blue}}>{fmtF(cl.ultimaCotizacion.fecha)} · {fmtMon(cl.ultimaCotizacion.total, cl.ultimaCotizacion.moneda)}</div>
                        <button onClick={()=>verCotizacion(cl)}
                          style={{marginTop:4,background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,borderRadius:R.sm,padding:'3px 9px',fontSize:10.5,fontWeight:600,cursor:'pointer'}}>
                          {loadingCot===k?'Cargando...':(exp&&lines?'▲ Ocultar':'▼ Ver líneas')}
                        </button>
                      </div>
                    )}
                    {/* Vendedor + Asignar */}
                    <div style={{flex:'0 0 160px',textAlign:'right'}}>
                      {cl.vendedor ? (
                        <span style={{background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,borderRadius:R.pill,padding:'3px 10px',fontSize:11,fontWeight:600}}>👤 {cl.vendedor}</span>
                      ) : (
                        <span style={{background:C.redTint,color:C.redText,border:`1px solid ${C.redBorder}`,borderRadius:R.pill,padding:'3px 10px',fontSize:11,fontWeight:600}}>Sin asignar</span>
                      )}
                      {asignando === k ? (
                        <div style={{marginTop:6,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:6,boxShadow:SH.md,zIndex:10}}>
                          {vendedores.map(v=>(
                            <div key={v.nombre} onClick={()=>asignar(cl,v.nombre)}
                              style={{padding:'5px 9px',fontSize:12,cursor:'pointer',borderRadius:R.sm,color:C.t2}}
                              onMouseEnter={e=>e.currentTarget.style.background=C.bg3}
                              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                              {v.nombre}
                            </div>
                          ))}
                          <div onClick={()=>setAsignando(null)} style={{padding:'5px 9px',fontSize:11,cursor:'pointer',color:C.t4,marginTop:4}}>Cancelar</div>
                        </div>
                      ) : (
                        <div style={{marginTop:6,display:'flex',flexDirection:'column',gap:4}}>
                          {cl.vendedor !== usuario?.nombre && (
                            <button onClick={()=>asignar(cl, usuario.nombre)}
                              style={{display:'block',width:'100%',background:C.greenTint,color:C.greenText,border:`1px solid ${C.greenBorder}`,borderRadius:R.md,padding:'6px 9px',fontSize:11,fontWeight:600,cursor:'pointer'}}>
                              Tomar este cliente
                            </button>
                          )}
                          {['admin','gerente_ventas'].includes(usuario?.rol) && (
                            <button onClick={()=>setAsignando(k)}
                              style={{display:'block',width:'100%',background:'transparent',color:C.t3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'5px 9px',fontSize:10.5,fontWeight:600,cursor:'pointer'}}>
                              Asignar a otro →
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Líneas cotización expandida */}
                  {exp && lines?.lines?.length > 0 && (
                    <div style={{borderTop:`1px solid ${C.border}`,padding:'12px 16px',background:C.bg1}}>
                      <div style={{fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Líneas de cotización #{cl.ultimaCotizacion?.docNum}</div>
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                        <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                          {['Código','Descripción','Cant.','Precio','Total'].map(h=><th key={h} style={{padding:'6px 8px',textAlign:'left',color:C.t3,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {lines.lines.map((l,i)=>(
                            <tr key={i} style={{borderBottom:`1px solid ${C.bg3}`}}>
                              <td className="radar-num" style={{padding:'6px 8px',color:C.t4}}>{l.ItemCode||'—'}</td>
                              <td style={{padding:'6px 8px',color:C.t2}}>{l.ItemDescription||l.Dscription||'—'}</td>
                              <td className="radar-num" style={{padding:'6px 8px',color:C.t3}}>{l.Quantity}</td>
                              <td className="radar-num" style={{padding:'6px 8px',color:C.t3}}>{fmtMon(l.unitPrice ?? l.UnitPrice ?? l.Price, lines.moneda)}</td>
                              <td className="radar-num" style={{padding:'6px 8px',color:C.blue,fontWeight:600}}>{fmtMon(l.lineTotal ?? l.LineTotal, lines.moneda)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="radar-num" style={{textAlign:'right',marginTop:8,fontSize:12,fontWeight:700,color:C.blue}}>
                        Total: {fmtMon(lines.docTotal ?? lines.doc?.DocTotal, lines.moneda)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── USUARIOS ─────────────────────────────────────────────────────────────────
function Usuarios({ usuarioActual }) {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading]  = useState(true);
  const [modal, setModal]      = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving]    = useState(false);
  const [f, setF] = useState({nombre:'',usuario:'',clave:'',rol:'ventas',activo:true,modulos_extra:[],sap_slp_code:{}});
  const [spMaps, setSpMaps] = useState({honduras:{}, costarica:{}});

  // Se DERIVA de NAV_META, no se escribe a mano.
  //
  // Antes era una lista fija y cada modulo nuevo habia que acordarse de
  // agregarlo aca: por eso Flota Cliente no se le podia asignar a Kevin, y por
  // eso tampoco aparecian Inventario, Tickets, Supervision, Metas ni Campanas,
  // que llevaban meses sin poder asignarse a nadie.
  //
  // Derivandolo, cualquier modulo que se agregue al catalogo queda asignable
  // solo. Se sacan Dashboard (lo tiene todo el mundo, no es contratable) y los
  // ids legacy que hoy son pestanas dentro de un hub.
  const TODOS_MODULOS = Object.entries(NAV_META)
    .filter(([id]) => id !== 'dashboard' && !HUB_DE[id])
    .map(([id, meta]) => ({ id, label: meta.label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const cargar = async () => {
    setLoading(true);
    const {data}=await supabase.from('radar_usuarios').select('*').order('nombre');
    setUsuarios(data||[]); setLoading(false);
  };
  useEffect(()=>{cargar();},[]);

  // Mapa de vendedores SAP (código → nombre) por empresa, EN VIVO desde SAP.
  useEffect(()=>{
    const load = async eid => {
      try {
        const r = await fetch('/api/sap',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'list_salespersons',empresa_id:eid})});
        const d = await r.json();
        if (d.ok && Array.isArray(d.salespersons)) return Object.fromEntries(d.salespersons.map(s=>[s.code,s.name]));
      } catch(_){}
      return {};
    };
    (async()=>{
      const [honduras,costarica] = await Promise.all([load('honduras'),load('costarica')]);
      setSpMaps({honduras,costarica});
    })();
  },[]);

  const abrir = (u=null) => {
    setEditUser(u);
    setF(u ? {nombre:u.nombre||'',usuario:u.usuario||'',clave:'',rol:u.rol||'ventas',activo:u.activo!==false,modulos_extra:u.modulos_extra||[],sap_slp_code:u.sap_slp_code||{}} : {nombre:'',usuario:'',clave:'',rol:'ventas',activo:true,modulos_extra:[],sap_slp_code:{}});
    setModal(true);
  };

  const guardar = async () => {
    if (!f.nombre||!f.usuario) return; setSaving(true);
    // S3: la contrasena vive SOLO en Supabase Auth. La columna radar_usuarios.clave
    // no se escribe mas (quedaba en texto plano y ademas el "cambio" de contrasena
    // no tocaba Auth: el login seguia con la vieja — la funcion estaba rota).
    // Se descartan los extras que el rol YA incluye: guardarlos no daba ningun acceso
    // nuevo y ensuciaba el permiso (ej. marcar Rentas en un vendedor que ya la tiene).
    const extrasLimpios = (f.modulos_extra||[]).filter(id=>!rolIncluye(f.rol, id));
    const payload = {nombre:f.nombre,usuario:f.usuario.toLowerCase().trim(),rol:f.rol,activo:f.activo,modulos_extra:extrasLimpios,sap_slp_code:f.sap_slp_code||{}};
    if (editUser) {
      if (f.clave) {
        const { data:{ session } } = await supabase.auth.getSession();
        const r = await fetch('/api/usuarios-admin', {
          method:'POST',
          headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${session?.access_token||''}` },
          body: JSON.stringify({ action:'set_password', usuario: payload.usuario, clave: f.clave }),
        }).then(x=>x.json()).catch(e=>({ok:false,error:e.message}));
        if (!r.ok) { toast(`Error: no se pudo cambiar la contraseña — ${r.error||'error'}`); setSaving(false); return; }
      }
      await supabase.from('radar_usuarios').update(payload).eq('id',editUser.id);
      setUsuarios(us=>us.map(u=>u.id===editUser.id?{...u,...payload}:u));
    } else {
      if (!f.clave) { setSaving(false); return; }
      // OJO CON EL ORDEN. Antes esto llamaba a supabase.auth.signUp() primero, y
      // eso CAMBIA la sesion del navegador al usuario recien creado: la linea
      // siguiente ya no corria como admin sino como el nuevo, RLS le bloqueaba
      // el insert y nadie miraba el error. Resultado: el usuario quedaba en Auth
      // pero sin ficha, y al entrar le decia "contraseña incorrecta" aunque la
      // contraseña estuviera bien. De paso, al admin lo dejaba con la sesion del
      // otro sin avisarle.
      //
      // Ahora se inserta la ficha PRIMERO, con la sesion de admin todavia viva,
      // y el alta en Auth la hace el servidor (usuarios-admin crea el usuario si
      // no existe). La sesion del navegador no se toca.
      const { data, error } = await supabase.from('radar_usuarios').insert(payload).select().single();
      if (error) {
        toast(`No se pudo crear el usuario: ${error.message}`);
        setSaving(false); return;
      }
      const { data:{ session } } = await supabase.auth.getSession();
      const r = await fetch('/api/usuarios-admin', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${session?.access_token||''}` },
        body: JSON.stringify({ action:'set_password', usuario: payload.usuario, clave: f.clave }),
      }).then(x=>x.json()).catch(e=>({ok:false,error:e.message}));
      if (!r.ok) {
        // La ficha quedo creada pero sin contrasena: se borra para no dejar a
        // medias un usuario que no va a poder entrar.
        await supabase.from('radar_usuarios').delete().eq('id', data.id);
        toast(`No se pudo crear la contraseña: ${r.error||'error'}`);
        setSaving(false); return;
      }
      setUsuarios(us=>[...us,data]);
      toast(`Usuario ${payload.usuario} creado`);
    }
    setSaving(false); setModal(false);
  };

  const toggleActivo = async (u) => {
    if (u.id===usuarioActual?.id) return;
    const desactivando = u.activo;

    // Desactivar solo lo sacaba del login. Sus clientes y oportunidades seguian
    // con su nombre, o sea con dueno, asi que no aparecian en ninguna lista de
    // "por atender" y quedaban invisibles. Paso con Ricardo: 69 clientes
    // sueltos que nadie sabia que estaban sueltos.
    if (desactivando) {
      const ok = window.confirm(
        `Desactivar a ${u.nombre}?\n\n` +
        'Sus clientes y sus oportunidades abiertas van a quedar SIN VENDEDOR, ' +
        'anotando que eran de el, para que alguien los pueda tomar.'
      );
      if (!ok) return;
    }

    await supabase.from('radar_usuarios').update({activo:!u.activo}).eq('id',u.id);
    setUsuarios(us=>us.map(x=>x.id===u.id?{...x,activo:!u.activo}:x));

    if (desactivando) {
      const { data, error } = await supabase.rpc('liberar_cartera',
        { p_vendedor: u.nombre, p_quien: usuarioActual?.nombre || null });
      if (error) { toast(`El usuario quedo desactivado, pero su cartera NO se libero: ${error.message}`); return; }
      const c = data?.clientes || 0, o = data?.oportunidades || 0;
      toast(c || o
        ? `${u.nombre} desactivado. Quedaron libres ${c} cliente${c===1?'':'s'} y ${o} oportunidad${o===1?'':'es'}.`
        : `${u.nombre} desactivado. No tenia cartera asignada.`);
    }
  };

  const ROL_COLOR = {admin:C.indigo,gerente_ventas:'#7C3AED',ventas:C.blue,taller:C.orange,finanzas:C.green};
  const ROL_LABEL = {admin:'Admin',gerente_ventas:'Gte. Ventas',ventas:'Ventas',taller:'Taller',finanzas:'Finanzas'};

  return (
    <div style={{flex:1,overflowY:'auto',padding:28,background:C.bg1}}>
      <div style={{maxWidth:760,margin:'0 auto'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:22}}>
          <div>
            <h1 style={{margin:'0 0 4px',fontSize:22,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,display:'flex',alignItems:'center',gap:9}}><KIcon d={CI.users} tone={TONES.primary}/>Panel de Usuarios</h1>
            <p style={{margin:0,color:C.t4,fontSize:13}}>Gestioná accesos y roles de RADAR</p>
          </div>
          <Btn onClick={()=>abrir()} style={{display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={CI.plus}/>Nuevo Usuario</Btn>
        </div>

        {/* Stats */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12,marginBottom:22}}>
          <KpiCard label="Total usuarios" value={usuarios.length} icon={CI.users} tone="primary"/>
          <KpiCard label="Activos" value={usuarios.filter(u=>u.activo!==false).length} icon={KI.check} tone="green"/>
          {['admin','ventas','taller','finanzas'].map(r=>(
            <div key={r} className="radar-kpi" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',boxShadow:SH.sm,display:'flex',flexDirection:'column',gap:10}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{ROL_LABEL[r]}</div>
              <div className="radar-num" style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',color:C.t1,lineHeight:1.05}}>{usuarios.filter(u=>u.rol===r).length}</div>
            </div>
          ))}
        </div>

        {/* Tabla */}
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,overflow:'hidden'}}>
          <div style={{padding:'12px 20px',borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em'}}>
            Usuarios del sistema
          </div>
          {loading?<div style={{textAlign:'center',color:C.t4,padding:40}}>Cargando...</div>
          :usuarios.length===0?<div style={{textAlign:'center',color:C.t4,padding:40}}>Sin usuarios registrados</div>
          :<table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr style={{borderBottom:`1px solid ${C.border}`}}>
                {['Nombre','Usuario','Rol','Estado','Acciones'].map(h=>(
                  <th key={h} style={{padding:'8px 16px',textAlign:'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usuarios.map(u=>{
                const activo=u.activo!==false;
                const rc=ROL_COLOR[u.rol]||C.t4;
                const esYo=u.id===usuarioActual?.id;
                return (
                  <tr key={u.id} style={{borderBottom:`1px solid ${C.bg3}`,opacity:activo?1:.55}}>
                    <td style={{padding:'12px 16px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div style={{width:32,height:32,borderRadius:'50%',background:`${rc}20`,color:rc,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800}}>{(u.nombre||'?')[0]}</div>
                        <div>
                          <div style={{fontSize:14,fontWeight:700,color:C.t1}}>{u.nombre}{esYo&&<span style={{fontSize:10,color:C.t4,marginLeft:6}}>(tú)</span>}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{padding:'12px 16px',fontSize:13,color:C.t3,fontFamily:'monospace'}}>{u.usuario}</td>
                    <td style={{padding:'12px 16px'}}>
                      <span style={{background:`${rc}14`,color:rc,border:`1px solid ${rc}33`,borderRadius:R.pill,padding:'2px 9px',fontSize:10.5,fontWeight:600}}>{ROL_LABEL[u.rol]||u.rol}</span>
                    </td>
                    <td style={{padding:'12px 16px'}}>
                      <span style={{background:activo?C.greenTint:C.bg3,color:activo?C.greenText:C.t3,border:`1px solid ${activo?C.greenBorder:C.border}`,borderRadius:R.pill,padding:'2px 9px',fontSize:10.5,fontWeight:600}}>{activo?'Activo':'Inactivo'}</span>
                    </td>
                    <td style={{padding:'12px 16px'}}>
                      <div style={{display:'flex',gap:6}}>
                        <Btn size="sm" variant="secondary" onClick={()=>abrir(u)}>Editar</Btn>
                        {!esYo&&<button onClick={()=>toggleActivo(u)} style={{background:'none',border:`1px solid ${C.border}`,borderRadius:6,padding:'4px 10px',color:activo?C.red:C.green,fontSize:11,fontWeight:700,cursor:'pointer'}}>{activo?'Desactivar':'Activar'}</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>}
        </div>
      </div>

      <Modal open={modal} onClose={()=>setModal(false)} title={editUser?'Editar Usuario':'Nuevo Usuario'} width={460}>
        <Inp label="Nombre completo *" value={f.nombre} onChange={v=>setF(x=>({...x,nombre:v}))} placeholder="Juan Pérez" required/>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:12}}>
          <Inp label="Usuario (login) *" value={f.usuario} onChange={v=>setF(x=>({...x,usuario:v}))} placeholder="juanperez" required/>
          <Inp label={editUser?'Nueva contraseña (dejar vacío para no cambiar)':'Contraseña *'} type="password" value={f.clave} onChange={v=>setF(x=>({...x,clave:v}))} placeholder="••••••••" required={!editUser}/>
        </div>
        <Sel label="Rol *" value={f.rol} onChange={v=>setF(x=>({...x,rol:v}))} required options={[{v:'admin',l:'Admin — acceso total'},{v:'gerente_ventas',l:'Gerente Ventas — todo ventas + reportes'},{v:'ventas',l:'Ventas — CRM, rentas, cotizaciones'},{v:'taller',l:'Taller — flota y mantenimiento'},{v:'finanzas',l:'Finanzas — rentas y reportes'}]}/>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>Código de vendedor en SAP <span style={{textTransform:'none',fontWeight:400}}>(para "Tomar cliente")</span></div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            {[['honduras','Honduras'],['costarica','Costa Rica']].map(([eid,lbl])=>{
              const opts = Object.entries(spMaps[eid]||{}).sort((a,b)=>Number(a[0])-Number(b[0]));
              const val = f.sap_slp_code?.[eid] ?? '';
              const setVal = v => setF(x=>({...x,sap_slp_code:{...(x.sap_slp_code||{}), [eid]: v===''?undefined:Number(v)}}));
              return (
                <div key={eid}>
                  <div style={{fontSize:11,color:C.t3,marginBottom:4}}>{lbl}</div>
                  {opts.length>0 ? (
                    <select className="radar-input" value={val} onChange={e=>setVal(e.target.value)}
                      style={{width:'100%',padding:'8px 10px',borderRadius:R.md,border:`1px solid ${C.border}`,background:C.bg1,color:C.t1,fontSize:13}}>
                      <option value="">— Sin asignar —</option>
                      {opts.map(([code,name])=><option key={code} value={code}>{code} · {name}</option>)}
                    </select>
                  ) : (
                    <input className="radar-input" type="number" value={val} onChange={e=>setVal(e.target.value)} placeholder="Código SAP"
                      style={{width:'100%',padding:'8px 10px',borderRadius:R.md,border:`1px solid ${C.border}`,background:C.bg1,color:C.t1,fontSize:13}}/>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{fontSize:10,color:C.t4,marginTop:6}}>Si la lista está vacía, corré una sincronización de SAP primero (o escribí el código a mano).</div>
        </div>
        {/* Lo que el ROL ya trae no se ofrece como "adicional" (era confuso: se podian
            marcar modulos que el rol ya incluia y el check no hacia nada). */}
        {(()=>{
          const incluidos = TODOS_MODULOS.filter(m=>rolIncluye(f.rol, m.id));
          const opcionales = TODOS_MODULOS.filter(m=>!rolIncluye(f.rol, m.id));
          const extrasSel = (f.modulos_extra||[]).filter(id=>!rolIncluye(f.rol, id));
          // Dos controles compactos del mismo alto, uno al lado del otro: el de la
          // izquierda es solo-lectura (se despliega), el de la derecha es el checklist.
          const ctrl = { display:'inline-flex',alignItems:'center',gap:6,height:32,padding:'0 12px',borderRadius:R.md,fontSize:12,fontWeight:600,whiteSpace:'nowrap',boxShadow:SH.xs,cursor:'pointer',listStyle:'none' };
          return (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:6}}>Permisos</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:8,alignItems:'flex-start'}}>
                {incluidos.length>0 && (
                  <details style={{position:'relative'}}>
                    <summary style={{...ctrl,background:C.greenTint,border:`1px solid ${C.greenBorder}`,color:C.greenText}}>
                      <CIcon d={CI.check2} size={12}/>Incluidos por el rol: {incluidos.length}
                      <span style={{opacity:.55,fontSize:9}}>▾</span>
                    </summary>
                    <div style={{position:'absolute',top:36,left:0,zIndex:1200,minWidth:210,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,boxShadow:SH.lg,padding:8}}>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'3px 10px'}}>
                        {incluidos.map(m=>(
                          <span key={m.id} style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11.5,color:C.t2,whiteSpace:'nowrap'}}>
                            <span style={{color:C.greenText,display:'inline-flex'}}><CIcon d={CI.check2} size={11}/></span>{m.label}
                          </span>
                        ))}
                      </div>
                      <div style={{fontSize:10.5,color:C.t3,marginTop:7,paddingTop:6,borderTop:`1px solid ${C.border}`}}>Vienen con el rol — no hace falta agregarlos.</div>
                    </div>
                  </details>
                )}
                {opcionales.length===0
                  ? <span style={{...ctrl,background:C.bg1,border:`1px solid ${C.border}`,color:C.t3,cursor:'default'}}>Acceso total</span>
                  : <MultiSelect label="Accesos adicionales" width={230}
                      value={extrasSel}
                      onChange={vals=>setF(x=>({...x,modulos_extra:vals}))}
                      options={opcionales.map(m=>({value:m.id,label:m.label}))}/>}
              </div>
            </div>
          );
        })()}
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
          <input type="checkbox" checked={f.activo} onChange={e=>setF(x=>({...x,activo:e.target.checked}))} id="activo_cb" style={{width:16,height:16,cursor:'pointer'}}/>
          <label htmlFor="activo_cb" style={{fontSize:13,color:C.t2,cursor:'pointer'}}>Usuario activo (puede iniciar sesión)</label>
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="secondary" onClick={()=>setModal(false)}>Cancelar</Btn>
          <Btn onClick={guardar} disabled={!f.nombre||!f.usuario||(!editUser&&!f.clave)||saving} style={{display:'inline-flex',alignItems:'center',gap:6}}>{saving?'Guardando...':<><CIcon d={CI.save}/>Guardar</>}</Btn>
        </div>
      </Modal>
    </div>
  );
}

// ─── FORECAST VENTAS ──────────────────────────────────────────────────────────
function ForecastCompromisos({ usuario, vendedorSel, vendedoresSel, tipo, fechaActual, fechaAnterior, labelPeriodo, labelAnterior }) {
  const CATS = ORG.lineasNegocio; // mismas categorías que las metas (taxonomía única)

  const [filas, setFilas]       = useState([]);
  const [anteriores, setAnt]    = useState([]);
  const [opps, setOpps]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [saving, setSaving]     = useState(false);
  const [nueva, setNueva]       = useState({ cliente:'', categoria:'', monto_estimado:'', oportunidad_id:'', notas:'' });
  const [offset, setOffset]     = useState(0);     // 0=esta semana, +1=próxima, -1=pasada (navegador)
  const [editId, setEditId]     = useState(null);  // id del compromiso en edición
  const [oppQ, setOppQ]         = useState('');     // búsqueda de oportunidad en el modal
  const [cerrar, setCerrar]     = useState(null);   // {fila} para capturar el monto real al cerrar

  // Navegación por semana/día: corre la fecha base según el offset.
  const addDays = (isoStr, n) => { const d = new Date(isoStr + 'T00:00:00'); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; };
  const step  = tipo === 'semanal' ? 7 : 1;
  const semIni = addDays(fechaActual, offset * step);
  const semAnt = addDays(semIni, -step);
  const fmtRango = iso => { const d=new Date(iso+'T00:00:00'); const f=addDays(iso, step-1); const e=new Date(f+'T00:00:00');
    const o={day:'numeric',month:'short'}; return tipo==='semanal' ? `${d.toLocaleDateString('es-HN',o)} – ${e.toLocaleDateString('es-HN',o)}` : d.toLocaleDateString('es-HN',{weekday:'long',day:'numeric',month:'short'}); };
  const etiqueta = offset===0 ? labelPeriodo : offset===1 ? (tipo==='semanal'?'próxima semana':'mañana') : offset===-1 ? labelAnterior : fmtRango(semIni);

  const cargar = async () => {
    setLoading(true);
    const vends = (vendedoresSel && vendedoresSel.length > 0) ? vendedoresSel : [vendedorSel];
    const multi = vends.length > 1;
    const [ra, rb, ro, rAct, rTodas] = await Promise.all([
      multi
        ? supabase.from('forecast_semanal').select('*').eq('semana_inicio', semIni).in('vendedor', vends).eq('tipo', tipo).order('created_at')
        : supabase.from('forecast_semanal').select('*').eq('semana_inicio', semIni).eq('vendedor', vends[0]).eq('tipo', tipo).order('created_at'),
      multi
        ? supabase.from('forecast_semanal').select('*').eq('semana_inicio', semAnt).in('vendedor', vends).eq('tipo', tipo).eq('estado','pendiente').order('created_at')
        : supabase.from('forecast_semanal').select('*').eq('semana_inicio', semAnt).eq('vendedor', vends[0]).eq('tipo', tipo).eq('estado','pendiente').order('created_at'),
      multi
        ? supabase.from('oportunidades').select('id,titulo,cliente,valor,vendedor').not('etapa','in','(cerrado_ganado,cerrado_perdido,prospecto_invalido,cliente_frio)').in('vendedor', vends).order('titulo').limit(500)
        : supabase.from('oportunidades').select('id,titulo,cliente,valor,vendedor').not('etapa','in','(cerrado_ganado,cerrado_perdido,prospecto_invalido,cliente_frio)').eq('vendedor', vends[0]).order('titulo').limit(500),
      // LAS OPORTUNIDADES SUELTAS TAMBIEN SE PUEDEN COMPROMETER.
      // La consulta de arriba trae solo las del vendedor elegido, asi que las
      // 422 oportunidades abiertas -3.66 millones de dolares- que quedaron a
      // nombre de gente que ya no esta en RADAR no las podia vincular nadie.
      // AEROSOLES tiene dos, de Ricardo Ruiz, y por eso buscar "aero" decia
      // "sin coincidencias" aunque existieran.
      supabase.from('radar_usuarios').select('nombre').eq('activo', true),
      supabase.from('oportunidades').select('id,titulo,cliente,valor,vendedor')
        .not('etapa','in','(cerrado_ganado,cerrado_perdido,prospecto_invalido,cliente_frio)')
        .order('titulo').limit(1000),
    ]);
    setFilas(ra.data||[]);
    setAnt(rb.data||[]);
    const activos = new Set((rAct.data || []).map(u => u.nombre));
    const huerfanas = (rTodas.data || []).filter(o => !o.vendedor || !activos.has(o.vendedor));
    // Primero las propias, despues las sueltas, sin repetir.
    const vistas = new Set();
    const juntas = [...(ro.data || []), ...huerfanas].filter(o => {
      if (vistas.has(o.id)) return false; vistas.add(o.id); return true;
    });
    setOpps(juntas);
    setLoading(false);
  };

  const multiVend = vendedoresSel && vendedoresSel.length > 1;

  useEffect(()=>{ cargar(); },[vendedorSel, vendedoresSel?.join(','), fechaActual, offset]);

  const cambiarEstado = async (id, estado) => {
    await supabase.from('forecast_semanal').update({ estado }).eq('id', id);
    cargar();
  };
  // Cerrar un compromiso registrando el monto REAL (puede ser parcial, no el 100%).
  const confirmarCierre = async () => {
    if (!cerrar) return;
    const real = parseFloat(cerrar.monto)||0;
    await supabase.from('forecast_semanal').update({ estado:'cerrado', monto_real: real }).eq('id', cerrar.fila.id);
    setCerrar(null); cargar();
  };

  const abrirEdicion = (f) => {
    setEditId(f.id);
    setNueva({ cliente:f.cliente||'', categoria:f.categoria||'', monto_estimado:f.monto_estimado??'', oportunidad_id:f.oportunidad_id||'', notas:f.notas||'' });
    setOppQ(f.oportunidad_titulo||''); setModal(true);
  };
  const cerrarModal = () => { setModal(false); setEditId(null); setOppQ(''); setNueva({ cliente:'', categoria:'', monto_estimado:'', oportunidad_id:'', notas:'' }); };

  // Eliminar una promesa/compromiso: la quita del forecast (y de su arrastre,
  // porque el arrastre es la MISMA fila reprogramada). Para corregir cargas
  // erroneas que inflan la proyeccion (ej. un monto tecleado de mas).
  const eliminar = async (f) => {
    if (!window.confirm(`Eliminar la promesa de "${f.cliente}" por ${fmt(f.monto_estimado||0)}?\n\nSe quita del forecast y del arrastre. Esta accion no se puede deshacer.`)) return;
    const { error } = await supabase.from('forecast_semanal').delete().eq('id', f.id);
    if (error) { alert('No se pudo eliminar: ' + error.message); return; }
    cargar();
  };

  const agregarFila = async () => {
    if (!nueva.cliente || !nueva.categoria) return;
    setSaving(true);
    const opp = opps.find(o=>o.id===nueva.oportunidad_id);
    const datos = {
      cliente: nueva.cliente,
      categoria: nueva.categoria,
      monto_estimado: parseFloat(nueva.monto_estimado)||0,
      oportunidad_id: nueva.oportunidad_id||null,
      oportunidad_titulo: opp?.titulo||nueva._oppTitulo||null,
      notas: nueva.notas||null,
    };
    if (editId) {
      await supabase.from('forecast_semanal').update(datos).eq('id', editId);
    } else {
      await supabase.from('forecast_semanal').insert({ ...datos, semana_inicio: semIni, vendedor: vendedorSel, tipo, estado:'pendiente' });
    }
    setSaving(false); cerrarModal(); cargar();
  };

  const postergadas = offset===0 ? anteriores.map(a=>({...a, _postergada:true})) : [];
  const todas = [...postergadas, ...filas];
  const montoCierre = f => (f.monto_real!=null ? f.monto_real : f.monto_estimado)||0;
  // KPIs sobre 'todas' (incluye las postergadas que también se muestran en la tabla)
  const totalSemana = todas.reduce((s,f)=>s+(f.monto_estimado||0),0);
  const cerradas    = todas.filter(f=>f.estado==='cerrado').reduce((s,f)=>s+montoCierre(f),0);
  const oppsFiltradas = oppQ.trim() ? opps.filter(o=>`${o.titulo} ${o.cliente}`.toLowerCase().includes(oppQ.toLowerCase())).slice(0,8) : opps.slice(0,8);

  // EL CAMPO DE CLIENTE ERA TEXTO LIBRE. Se escribia el nombre a mano, asi que
  // dos compromisos del mismo cliente podian quedar con dos escrituras
  // distintas y no habia forma de cruzarlos con el CRM. Busca en el servidor,
  // como el buscador de clientes del CRM: son 6,600 y no caben en memoria.
  const [cliSug, setCliSug] = useState(null);
  const cliTimer = useRef(null);
  const buscarCliente = (q) => {
    clearTimeout(cliTimer.current);
    if (!q || q.trim().length < 3) { setCliSug(null); return; }
    cliTimer.current = setTimeout(async () => {
      const pat = `%${q.trim().replace(/[%_]/g,'')}%`;
      const { data } = await supabase.from('clientes')
        .select('id,empresa,contacto,vendedor,empresa_id').neq('estado','inactivo')
        .or(`empresa.ilike.${pat},contacto.ilike.${pat}`)
        .order('empresa').limit(8);
      setCliSug(data || []);
    }, 250);
  };

  const rowEstado = (f) => {
    if (f.estado==='cerrado')    return { bg:`${C.green}15`, badge:'✅ Cerrado',    bc:C.green };
    if (f.estado==='postergado') return { bg:`${C.red}10`,   badge:'⏭ Postergado', bc:C.red };
    if (f._postergada)           return { bg:`${C.orange}15`,badge:'⚠️ Sem. ant.',  bc:C.orange };
    return { bg:'transparent', badge:'🕐 Pendiente', bc:C.t4 };
  };

  if (loading) return <div style={{textAlign:'center',padding:40,color:C.t4}}>Cargando...</div>;

  return (
    <div>
      {/* Navegador de semana/día — permite llenar la PRÓXIMA (p.ej. el sábado) o revisar la PASADA */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:14,marginBottom:16}}>
        <button onClick={()=>setOffset(o=>o-1)} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,padding:'7px 14px',color:C.t2,fontSize:14,fontWeight:800,cursor:'pointer'}}>◀</button>
        <div style={{textAlign:'center',minWidth:200}}>
          <div style={{fontSize:14,fontWeight:800,color:offset===0?C.blue:C.t1,textTransform:'capitalize'}}>{etiqueta}</div>
          <div style={{fontSize:11,color:C.t4}}>{fmtRango(semIni)}{offset!==0&&<> · <span onClick={()=>setOffset(0)} style={{color:C.blue,cursor:'pointer',fontWeight:700}}>volver a {labelPeriodo}</span></>}</div>
        </div>
        <button onClick={()=>setOffset(o=>o+1)} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,padding:'7px 14px',color:C.t2,fontSize:14,fontWeight:800,cursor:'pointer'}}>▶</button>
      </div>

      {/* KPIs semana */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
        {[
          {label:'Compromisos semana', val:todas.length,        color:C.blue,  icon:'📋'},
          {label:'Monto comprometido', val:fmt(totalSemana),    color:C.orange, icon:'💰'},
          {label:'Ya cerrado',         val:fmt(cerradas),       color:C.green,  icon:'✅'},
        ].map((k,i)=>(
          <div key={i} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px 18px'}}>
            <div style={{fontSize:18,marginBottom:4}}>{k.icon}</div>
            <div style={{fontSize:22,fontWeight:900,color:k.color}}>{k.val}</div>
            <div style={{fontSize:11,color:C.t4}}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Header tabla */}
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,overflow:'hidden'}}>
        <div style={{padding:'12px 18px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontSize:14,fontWeight:800,color:C.t1}}>📅 Compromisos — {labelPeriodo}</div>
            {anteriores.length>0&&<div style={{fontSize:11,color:C.orange,marginTop:2}}>⚠️ {anteriores.length} compromiso(s) de {labelAnterior} sin cerrar</div>}
          </div>
          <button onClick={()=>setModal(true)}
            style={{background:C.blue,color:'#fff',border:'none',borderRadius:8,padding:'8px 16px',fontSize:13,fontWeight:700,cursor:'pointer'}}>
            + Agregar
          </button>
        </div>

        {/* Header columnas */}
        <div style={{display:'grid',gridTemplateColumns:multiVend?'1.5fr 2fr 2fr 100px 2fr 120px 100px':'2fr 2fr 100px 2fr 120px 100px',gap:0,padding:'8px 18px',background:C.bg3,borderBottom:`1px solid ${C.border}`}}>
          {(multiVend?['Vendedor','Cliente','Categoría','Monto','Oportunidad CRM','Estado','Acción']:['Cliente','Categoría','Monto','Oportunidad CRM','Estado','Acción']).map(h=>(
            <div key={h} style={{fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase'}}>{h}</div>
          ))}
        </div>

        {todas.length===0&&<div style={{padding:32,textAlign:'center',color:C.t4,fontSize:13}}>Sin compromisos esta semana — agrega el primero</div>}

        {todas.map((f,i)=>{
          const e = rowEstado(f);
          return (
            <div key={f.id} style={{display:'grid',gridTemplateColumns:multiVend?'1.5fr 2fr 2fr 100px 2fr 120px 100px':'2fr 2fr 100px 2fr 120px 100px',gap:0,
              padding:'11px 18px',borderBottom:`1px solid ${C.border}`,background:e.bg,alignItems:'center'}}>
              {multiVend&&<div style={{fontSize:11,fontWeight:700,color:C.blue}}>{f.vendedor}</div>}
              <div style={{fontSize:13,fontWeight:600,color:C.t1}}>{f.cliente}</div>
              <div style={{fontSize:12,color:C.t3}}>{f.categoria}</div>
              <div style={{fontSize:13,fontWeight:700,color:C.green}}>{fmt(montoCierre(f))}
                {f.estado==='cerrado'&&f.monto_real!=null&&f.monto_real!==f.monto_estimado&&<div style={{fontSize:9,color:C.t4,fontWeight:600}}>est. {fmt(f.monto_estimado||0)}</div>}
              </div>
              <div style={{fontSize:11,color:C.t4}}>{f.oportunidad_titulo||'—'}</div>
              <div style={{fontSize:11,fontWeight:700,color:e.bc}}>{e.badge}</div>
              <div style={{display:'flex',gap:4}}>
                {f.estado!=='cerrado'&&<button title="Cerrar (registrar monto real)" onClick={()=>setCerrar({fila:f, monto:String(f.monto_estimado||'')})}
                  style={{background:`${C.green}20`,color:C.green,border:'none',borderRadius:6,padding:'4px 8px',fontSize:10,fontWeight:700,cursor:'pointer'}}>✅</button>}
                <button title="Editar" onClick={()=>abrirEdicion(f)}
                  style={{background:`${C.blue}15`,color:C.blue,border:'none',borderRadius:6,padding:'4px 8px',fontSize:10,fontWeight:700,cursor:'pointer'}}>✏️</button>
                {f.estado==='pendiente'&&<button title="Postergar" onClick={()=>cambiarEstado(f.id,'postergado')}
                  style={{background:`${C.red}15`,color:C.red,border:'none',borderRadius:6,padding:'4px 8px',fontSize:10,fontWeight:700,cursor:'pointer'}}>⏭</button>}
                <button title="Eliminar" onClick={()=>eliminar(f)}
                  style={{background:`${C.red}15`,color:C.red,border:'none',borderRadius:6,padding:'4px 8px',fontSize:10,fontWeight:700,cursor:'pointer'}}>🗑</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal agregar */}
      {modal&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:16,padding:28,width:500,maxWidth:'95vw',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{fontSize:16,fontWeight:800,color:C.t1,marginBottom:20}}>{editId?'✏️ Editar compromiso':'+ Nuevo compromiso de venta'}</div>
            <div style={{display:'grid',gap:14}}>
              <div>
                <div style={{fontSize:11,color:C.t4,marginBottom:5}}>CLIENTE *</div>
                <div style={{position:'relative'}}>
                  <input value={nueva.cliente}
                    onChange={e=>{ const v=e.target.value; setNueva(x=>({...x,cliente:v})); buscarCliente(v); }}
                    onBlur={()=>setTimeout(()=>setCliSug(null), 150)}
                    placeholder="Buscar cliente por nombre o contacto…"
                    className="radar-input" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}/>
                  {cliSug && cliSug.length > 0 && (
                    <div style={{position:'absolute',top:'100%',left:0,right:0,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,marginTop:4,zIndex:6,maxHeight:220,overflowY:'auto',boxShadow:'0 8px 24px rgba(0,0,0,.18)'}}>
                      {cliSug.map(c=>(
                        <div key={c.id} onMouseDown={()=>{ setNueva(x=>({...x,cliente:c.empresa})); setCliSug(null); }}
                          style={{padding:'8px 12px',cursor:'pointer',borderBottom:`1px solid ${C.border}30`}}>
                          <div style={{fontSize:12,fontWeight:700,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.empresa}</div>
                          <div style={{fontSize:11,color:C.t4}}>
                            {c.contacto || 'sin contacto'}{c.vendedor?` · ${c.vendedor.split(' ')[0]}`:' · libre'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {cliSug && cliSug.length === 0 && nueva.cliente.trim().length >= 3 && (
                    <div style={{fontSize:10.5,color:C.t4,marginTop:3}}>Ningún cliente con ese nombre. Podés escribirlo igual.</div>
                  )}
                </div>
              </div>
              <div>
                <div style={{fontSize:11,color:C.t4,marginBottom:5}}>CATEGORÍA *</div>
                <select value={nueva.categoria} onChange={e=>setNueva(x=>({...x,categoria:e.target.value}))}
                  className="radar-input" style={{width:'100%',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}>
                  <option value="">— Seleccionar —</option>
                  {CATS.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div style={{fontSize:11,color:C.t4,marginBottom:5}}>MONTO ESTIMADO (USD)</div>
                <input type="number" value={nueva.monto_estimado} onChange={e=>setNueva(x=>({...x,monto_estimado:e.target.value}))}
                  placeholder="0"
                  className="radar-input" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:C.t4,marginBottom:5}}>OPORTUNIDAD DEL CRM (opcional)</div>
                {nueva.oportunidad_id ? (
                  <div style={{display:'flex',alignItems:'center',gap:8,background:`${C.blue}12`,border:`1px solid ${C.blue}40`,borderRadius:8,padding:'9px 12px'}}>
                    <span style={{flex:1,fontSize:12,color:C.t1,fontWeight:700}}>{nueva._oppTitulo || opps.find(o=>o.id===nueva.oportunidad_id)?.titulo || oppQ || 'Oportunidad vinculada'}</span>
                    <button onClick={()=>{ setNueva(x=>({...x,oportunidad_id:'',_oppTitulo:''})); setOppQ(''); }} style={{background:'none',border:'none',color:C.red,fontSize:16,cursor:'pointer',lineHeight:1}}>×</button>
                  </div>
                ) : (
                  <div style={{position:'relative'}}>
                    <input value={oppQ} onChange={e=>setOppQ(e.target.value)} placeholder="🔎 Buscar oportunidad por cliente o título…"
                      className="radar-input" style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}/>
                    {oppQ.trim() && (
                      <div style={{position:'absolute',top:'100%',left:0,right:0,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,marginTop:4,zIndex:5,maxHeight:220,overflowY:'auto',boxShadow:'0 8px 24px rgba(0,0,0,.18)'}}>
                        {oppsFiltradas.length===0 && <div style={{padding:'10px 12px',fontSize:12,color:C.t4}}>Sin coincidencias.</div>}
                        {oppsFiltradas.map(o=>(
                          <div key={o.id} onClick={()=>{ setNueva(x=>({...x,oportunidad_id:o.id,_oppTitulo:o.titulo, cliente:x.cliente||o.cliente})); setOppQ(''); }}
                            style={{padding:'8px 12px',cursor:'pointer',borderBottom:`1px solid ${C.border}30`}}>
                            <div style={{fontSize:12,fontWeight:700,color:C.t1}}>{o.titulo}</div>
                            <div style={{fontSize:11,color:C.t4}}>
                              {o.cliente}{o.valor?` · $${o.valor.toLocaleString()}`:''}
                              {/* De quien es, si no es de quien esta comprometiendo. */}
                              {o.vendedor && o.vendedor !== vendedorSel && <span style={{color:C.orangeText}}> · era de {String(o.vendedor).split(' ')[0]}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <div style={{fontSize:11,color:C.t4,marginBottom:5}}>NOTAS</div>
                <textarea value={nueva.notas} onChange={e=>setNueva(x=>({...x,notas:e.target.value}))}
                  placeholder="Detalles adicionales..."
                  style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none',resize:'none',height:70}}/>
              </div>
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:20}}>
              <button onClick={cerrarModal} style={{background:'none',border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 18px',color:C.t4,fontSize:13,cursor:'pointer'}}>Cancelar</button>
              <button onClick={agregarFila} disabled={saving||!nueva.cliente||!nueva.categoria}
                style={{background:C.blue,color:'#fff',border:'none',borderRadius:8,padding:'9px 18px',fontSize:13,fontWeight:700,cursor:'pointer',opacity:saving||!nueva.cliente||!nueva.categoria?.5:1}}>
                {saving?'Guardando...':editId?'Guardar cambios':'Agregar compromiso'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cerrar compromiso: capturar el monto REAL (puede ser parcial) */}
      {cerrar&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1001}}>
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:16,padding:24,width:380,maxWidth:'95vw',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{fontSize:15,fontWeight:800,color:C.t1,marginBottom:6}}>✅ Cerrar compromiso</div>
            <div style={{fontSize:12,color:C.t4,marginBottom:14}}>{cerrar.fila.cliente} — estimado {fmt(cerrar.fila.monto_estimado||0)}. ¿Cuánto se cerró realmente?</div>
            <div style={{fontSize:11,color:C.t4,marginBottom:5}}>MONTO REAL (USD)</div>
            <input type="number" autoFocus value={cerrar.monto} onChange={e=>setCerrar(c=>({...c,monto:e.target.value}))}
              style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px',color:C.t1,fontSize:15,fontWeight:700,outline:'none'}}/>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:18}}>
              <button onClick={()=>setCerrar(null)} style={{background:'none',border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 16px',color:C.t4,fontSize:13,cursor:'pointer'}}>Cancelar</button>
              <button onClick={confirmarCierre} style={{background:C.green,color:'#fff',border:'none',borderRadius:8,padding:'9px 18px',fontSize:13,fontWeight:700,cursor:'pointer'}}>Cerrar compromiso</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── METAS & REVISIÓN — meta por categoría + conciliación contra SAP ──────────
// ── Bono automático de la Coordinadora Comercial (Fase A) ─────────────────────
// KPIs medibles hoy: alertas (25), giras (15), actividades (15), datos (10) —
// renormalizados a 100 mientras NEXA (leads 20+15) espera la Fase B.
// El puntaje sale SOLO de la operación registrada; nada se ingresa a mano.
function BonoAsistente({ usuario }) {
  const pad2 = n => String(n).padStart(2,'0');
  const hoyD = new Date();
  const mes = `${hoyD.getFullYear()}-${pad2(hoyD.getMonth()+1)}`;
  const mesIni = `${mes}-01`;
  const hoyISO = hoyD.toISOString().slice(0,10);
  const [loading, setLoading] = useState(true);
  const [alertas, setAlertas] = useState([]);
  const [giras, setGiras]     = useState([]);
  const [acts, setActs]       = useState([]);
  const [leads, setLeads]     = useState([]);
  const [asistente, setAsistente] = useState('Claudia');
  const [detalle, setDetalle] = useState(null); // clave del KPI expandido
  const esAdminB = usuario.rol === 'admin';

  const cargar = async () => {
    setLoading(true);
    const d3  = new Date(Date.now()-3*86400000).toISOString().slice(0,10);
    const d30 = new Date(Date.now()-30*86400000).toISOString().slice(0,10);
    const [{data:usrs},{data:al},{data:gi},{data:ld},{data:oppCot},{data:oppEst},{data:facV}] = await Promise.all([
      supabase.from('radar_usuarios').select('nombre').ilike('nombre','claudia%').limit(1),
      supabase.from('alertas_operacion').select('*').gte('creada_at', mesIni+'T00:00:00').order('creada_at',{ascending:false}).limit(1000),
      supabase.from('giras').select('id,vendedor,fecha,km_total,costo,n_paradas,estado,revisada_por,revisada_at').eq('estado','finalizada').gte('fecha',mesIni).order('fecha',{ascending:false}).limit(500),
      supabase.from('sibila_leads').select('id,nombre,telefono,necesidad,vendedor,estado,created_at').gte('created_at',mesIni+'T00:00:00').limit(1000),
      supabase.from('oportunidades').select('id,titulo,cliente,vendedor,ultimo_seguimiento').eq('etapa','cotizacion').lt('ultimo_seguimiento',d3).limit(200),
      supabase.from('oportunidades').select('id,titulo,cliente,vendedor,ultimo_seguimiento').not('etapa','in','(cerrado_ganado,cerrado_perdido,pagado,prospecto_invalido,cliente_frio)').lt('ultimo_seguimiento',d30).limit(200),
      supabase.from('facturas_sap').select('doc_num,empresa_id,card_name,saldo_usd,total_usd,dias_atraso').eq('pagado',false).gt('dias_atraso',0).order('saldo_usd',{ascending:false}).limit(200),
    ]);
    const nomA = usrs?.[0]?.nombre || 'Claudia';
    setAsistente(nomA);
    const { data: ac } = await supabase.from('actividades_programadas')
      .select('id,titulo,tipo,fecha,completada,resultado_real,resultado_at')
      .eq('vendedor', nomA).gte('fecha', mesIni).lte('fecha', hoyISO).limit(1000);
    setActs(ac||[]); setGiras(gi||[]); setLeads(ld||[]);
    // ── Generar/refrescar la cola de alertas (dedup por tipo+ref) y auto-resolver
    //    las que ya no aplican: cerrar la causa ES resolver la alerta. ──
    const candidatas = [
      ...(ld||[]).filter(l=>!l.vendedor && !['perdido','no_respondio','convertido'].includes(l.estado))
        .map(l=>({ tipo:'lead_sin_asignar', ref:`lead:${l.id}`, titulo:`Lead sin asignar: ${l.nombre||l.telefono||l.id}`, detalle:l.necesidad||null })),
      ...(oppCot||[]).map(o=>({ tipo:'cotiz_sin_seguimiento', ref:`opp:${o.id}`, titulo:`Cotización sin seguimiento +3 días: ${(o.titulo||o.cliente||'').slice(0,60)}`, detalle:o.vendedor||null })),
      ...(facV||[]).slice(0,100).map(f=>({ tipo:'factura_vencida', ref:`fac:${f.doc_num}|${f.empresa_id}`, titulo:`Factura vencida #${f.doc_num}: ${(f.card_name||'').slice(0,50)}`, detalle:`${f.dias_atraso} días · $${Math.round(f.saldo_usd||f.total_usd||0).toLocaleString('en-US')}` })),
      ...(oppEst||[]).map(o=>({ tipo:'cuenta_estancada', ref:`opp30:${o.id}`, titulo:`Cuenta estancada +30 días: ${(o.cliente||o.titulo||'').slice(0,60)}`, detalle:o.vendedor||null })),
    ];
    if (candidatas.length) await supabase.from('alertas_operacion').upsert(candidatas, { onConflict:'org_id,tipo,ref', ignoreDuplicates:true }).then(()=>{},()=>{});
    const vivasRef = new Set(candidatas.map(c=>c.ref));
    const abiertas = (al||[]).filter(a=>a.estado!=='resuelta');
    const yaNoAplican = abiertas.filter(a=>!vivasRef.has(a.ref));
    if (yaNoAplican.length) {
      const ahora = new Date().toISOString();
      await supabase.from('alertas_operacion').update({ estado:'resuelta', resuelta_at:ahora, resuelta_por:'auto (causa corregida)' }).in('id', yaNoAplican.map(a=>a.id)).then(()=>{},()=>{});
    }
    const { data: al2 } = await supabase.from('alertas_operacion').select('*').gte('creada_at', mesIni+'T00:00:00').order('creada_at',{ascending:false}).limit(1000);
    setAlertas(al2||[]);
    setLoading(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const marcarGestion = async (a) => {
    const motivo = window.prompt('¿Por qué queda en gestión? (depende de un tercero)');
    if (!motivo || !motivo.trim()) return;
    await supabase.from('alertas_operacion').update({ estado:'en_gestion', motivo_gestion:motivo.trim() }).eq('id',a.id);
    setAlertas(as=>as.map(x=>x.id===a.id?{...x,estado:'en_gestion',motivo_gestion:motivo.trim()}:x));
  };
  const resolverAlerta = async (a) => {
    const ahora = new Date().toISOString();
    await supabase.from('alertas_operacion').update({ estado:'resuelta', resuelta_at:ahora, resuelta_por:usuario.nombre }).eq('id',a.id);
    setAlertas(as=>as.map(x=>x.id===a.id?{...x,estado:'resuelta',resuelta_at:ahora,resuelta_por:usuario.nombre}:x));
  };
  const aprobarGira = async (g) => {
    const ahora = new Date().toISOString();
    const { error } = await supabase.from('giras').update({ revisada_por:usuario.nombre, revisada_at:ahora }).eq('id',g.id);
    if (error) { toast('No se pudo: '+error.message); return; }
    setGiras(gs=>gs.map(x=>x.id===g.id?{...x,revisada_por:usuario.nombre,revisada_at:ahora}:x));
    toast('Gira auditada y aprobada.');
  };

  // ── Cálculo de KPIs (tope 100 c/u; nada compensa a otro) ──
  const H48 = 48*3600000;
  const kAl = (()=>{ // resueltas a tiempo (<=48h) / universo (vencidas o resueltas); en_gestion pausa
    const uni = alertas.filter(a=>a.estado!=='en_gestion' && (a.estado==='resuelta' || (Date.now()-new Date(a.creada_at).getTime())>H48));
    const ok = uni.filter(a=>a.estado==='resuelta' && a.resuelta_at && (new Date(a.resuelta_at)-new Date(a.creada_at))<=H48);
    return { pct: uni.length? Math.min(100,Math.round(ok.length/uni.length*100)) : 100, ok:ok.length, tot:uni.length,
      sub:`${ok.length}/${uni.length} resueltas en 48h`, regs: alertas.filter(a=>a.estado!=='resuelta') };
  })();
  const kGi = (()=>{
    const rev = giras.filter(g=>g.revisada_at);
    return { pct: giras.length? Math.min(100,Math.round(rev.length/giras.length*100)) : 100, ok:rev.length, tot:giras.length,
      sub:`${rev.length}/${giras.length} giras auditadas`, regs: giras.filter(g=>!g.revisada_at) };
  })();
  const kAc = (()=>{
    const cum = acts.filter(a=>a.completada && (a.resultado_real||'').trim());
    return { pct: acts.length? Math.min(100,Math.round(cum.length/acts.length*100)) : 100, ok:cum.length, tot:acts.length,
      sub:`${cum.length}/${acts.length} a tiempo con resultado`, regs: acts.filter(a=>!(a.completada&&(a.resultado_real||'').trim())) };
  })();
  const kDa = (()=>{
    const comp = leads.filter(l=>l.nombre && l.telefono && l.necesidad && l.vendedor);
    return { pct: leads.length? Math.min(100,Math.round(comp.length/leads.length*100)) : 100, ok:comp.length, tot:leads.length,
      sub:`${comp.length}/${leads.length} leads con datos completos`, regs: leads.filter(l=>!(l.nombre&&l.telefono&&l.necesidad&&l.vendedor)) };
  })();
  const KPIS = [
    { clave:'alertas', nombre:'Cierre de alertas de RADAR', meta:95, peso:25, k:kAl, det:'Rojas resueltas en 48h: sin asignar, cotiz. sin seguimiento, facturas vencidas, estancadas' },
    { clave:'giras',   nombre:'Auditoría de giras / gasolina', meta:95, peso:15, k:kGi, det:'Giras finalizadas revisadas (km vs visitas) antes de pagar' },
    { clave:'acts',    nombre:'Ejecución de actividades', meta:95, peso:15, k:kAc, det:'Actividades asignadas completadas a tiempo, con resultado' },
    { clave:'datos',   nombre:'Higiene de datos', meta:95, peso:10, k:kDa, det:'Leads gestionados con campos críticos completos' },
  ];
  const pesoMedible = KPIS.reduce((s,x)=>s+x.peso,0); // 65 (NEXA 35 en Fase B)
  const puntaje = pesoMedible>0 ? KPIS.reduce((s,x)=>s+x.k.pct*(x.peso/pesoMedible),0) : 0;
  const escala = p => p>=100?1 : p>=95?0.9 : p>=90?0.7 : p>=80?0.4 : 0;
  const BASE = 3000;
  const bonoProy = Math.round(BASE*escala(puntaje));
  const tono = (pct,meta) => pct>=meta?{c:C.greenText,b:C.green,t:C.greenTint,bd:C.greenBorder} : pct>=meta-10?{c:C.orangeText,b:C.orange,t:C.orangeTint,bd:C.orangeBorder} : {c:C.redText,b:C.red,t:C.redTint,bd:C.redBorder};
  const TRAMOS = [['≥ 100%','100%',p=>p>=100],['95–99.99%','90%',p=>p>=95&&p<100],['90–94.99%','70%',p=>p>=90&&p<95],['80–89.99%','40%',p=>p>=80&&p<90],['< 80%','0%',p=>p<80]];

  if (loading) return <div style={{padding:20}}><SkelCards n={4}/><SkelRows n={6}/></div>;
  return (
    <div>
      <div style={{fontSize:12,color:C.t3,marginBottom:14}}>{asistente} · Coordinadora Comercial · {mes} · Los KPIs de NEXA (35%) se activan en la Fase B — el puntaje se calcula sobre los {pesoMedible}% medibles, renormalizados.</div>
      {/* HERO */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:12,marginBottom:18}}>
        <div style={{background:'linear-gradient(160deg,#0B1512,#14201D 70%)',border:'1px solid #233530',borderRadius:R.xl||14,padding:'15px 17px',color:'#fff'}}>
          <div style={{fontSize:10.5,fontWeight:600,color:'#7E938D',textTransform:'uppercase',letterSpacing:'.05em'}}>Puntaje del mes (proyectado)</div>
          <div className="radar-num" style={{fontSize:28,fontWeight:800,marginTop:6}}>{puntaje.toFixed(1)}%</div>
          <div style={{fontSize:11,color:'#5EEAD4',marginTop:4,fontWeight:700}}>Tramo {TRAMOS.find(t=>t[2](puntaje))?.[0]} → paga {TRAMOS.find(t=>t[2](puntaje))?.[1]}</div>
        </div>
        <div className="radar-kpi" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:'15px 17px',boxShadow:SH.sm}}>
          <div style={{fontSize:10.5,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.05em'}}>Bono proyectado</div>
          <div className="radar-num" style={{fontSize:28,fontWeight:800,marginTop:6,color:C.blue}}>L {bonoProy.toLocaleString('es-HN')}</div>
          <div style={{fontSize:11.5,color:C.t3,marginTop:4}}>de L {BASE.toLocaleString('es-HN')} · {Math.round(escala(puntaje)*100)}% al ritmo actual</div>
        </div>
        <div className="radar-kpi" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:'15px 17px',boxShadow:SH.sm}}>
          <div style={{fontSize:10.5,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.05em'}}>Si cierra en verde (≥95%)</div>
          <div className="radar-num" style={{fontSize:22,fontWeight:800,marginTop:6,color:C.greenText}}>L {Math.round(BASE*0.9).toLocaleString('es-HN')}</div>
          <div style={{fontSize:11.5,color:C.t3,marginTop:4}}>y con 100%: L {BASE.toLocaleString('es-HN')}</div>
        </div>
        <div className="radar-kpi" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:'15px 17px',boxShadow:SH.sm}}>
          <div style={{fontSize:10.5,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.05em'}}>Pendientes ahora</div>
          <div className="radar-num" style={{fontSize:22,fontWeight:800,marginTop:6,color:kAl.regs.length?C.orangeText:C.greenText}}>{kAl.regs.length} alertas · {kGi.regs.length} giras</div>
          <div style={{fontSize:11.5,color:C.t3,marginTop:4}}>por cerrar / auditar hoy</div>
        </div>
      </div>

      {/* KPIs */}
      {KPIS.map(x=>{ const t=tono(x.k.pct,x.meta); const open=detalle===x.clave; return (
        <div key={x.clave}>
          <div onClick={()=>setDetalle(open?null:x.clave)} className="radar-kpi" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px 14px',marginBottom:open?0:9,boxShadow:SH.sm,display:'grid',gridTemplateColumns:'18px 1fr 150px 110px 80px',gap:13,alignItems:'center',cursor:'pointer',borderBottomLeftRadius:open?0:12,borderBottomRightRadius:open?0:12}}>
            <span style={{width:12,height:12,borderRadius:'50%',background:t.b}}/>
            <div><div style={{fontSize:13,fontWeight:700,color:C.t1}}>{x.nombre}</div><div style={{fontSize:10.5,color:C.t3,marginTop:1}}>{x.det}</div></div>
            <div><div style={{height:7,background:C.bg3,borderRadius:R.pill,overflow:'hidden'}}><div style={{height:'100%',width:`${Math.min(100,x.k.pct)}%`,background:t.b,borderRadius:R.pill}}/></div><div style={{fontSize:10,color:C.t4,marginTop:3}}>{x.k.sub}</div></div>
            <div style={{textAlign:'right'}}><span className="radar-num" style={{fontSize:17,fontWeight:800,color:t.c}}>{x.k.pct}%</span><div style={{fontSize:9.5,color:C.t4}}>meta {x.meta}%</div></div>
            <div style={{textAlign:'right'}}><div style={{fontSize:9.5,fontWeight:700,color:C.t3,textTransform:'uppercase'}}>Peso {x.peso}%</div><div className="radar-num" style={{fontSize:13.5,fontWeight:800,color:C.blue}}>{(x.k.pct*(x.peso/pesoMedible)).toFixed(1)}</div></div>
          </div>
          {open && (
            <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderTop:'none',borderRadius:'0 0 12px 12px',padding:'10px 14px',marginBottom:9,maxHeight:260,overflowY:'auto'}}>
              {x.k.regs.length===0 && <div style={{fontSize:12,color:C.greenText,fontWeight:600}}>Sin pendientes — todo el KPI está en verde.</div>}
              {x.clave==='alertas' && x.k.regs.map(a=>(
                <div key={a.id} style={{display:'flex',alignItems:'center',gap:9,padding:'7px 0',borderBottom:`1px solid ${C.bg3}`,fontSize:12}}>
                  <span style={{width:8,height:8,borderRadius:'50%',background:a.estado==='en_gestion'?C.orange:C.red,flexShrink:0}}/>
                  <span style={{flex:1,minWidth:0,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={a.detalle||''}>{a.titulo}{a.estado==='en_gestion'&&<span style={{color:C.orangeText,fontSize:10.5}}> · en gestión: {a.motivo_gestion}</span>}</span>
                  {a.estado!=='en_gestion'&&<button onClick={e=>{e.stopPropagation();marcarGestion(a);}} style={{fontSize:10.5,fontWeight:600,color:C.orangeText,background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.pill,padding:'3px 9px',cursor:'pointer'}}>En gestión</button>}
                  <button onClick={e=>{e.stopPropagation();resolverAlerta(a);}} style={{fontSize:10.5,fontWeight:600,color:C.greenText,background:C.greenTint,border:`1px solid ${C.greenBorder}`,borderRadius:R.pill,padding:'3px 9px',cursor:'pointer'}}>Resuelta</button>
                </div>
              ))}
              {x.clave==='giras' && x.k.regs.map(g=>(
                <div key={g.id} style={{display:'flex',alignItems:'center',gap:9,padding:'7px 0',borderBottom:`1px solid ${C.bg3}`,fontSize:12}}>
                  <span style={{flex:1,color:C.t1}}>{(g.vendedor||'').split(' ')[0]} · {g.fecha} · {g.n_paradas||0} parada(s) · {Math.round(g.km_total||0)} km · L {Math.round(g.costo||0).toLocaleString('es-HN')}</span>
                  <button onClick={e=>{e.stopPropagation();aprobarGira(g);}} style={{fontSize:10.5,fontWeight:700,color:'#fff',background:C.blue,border:'none',borderRadius:R.pill,padding:'4px 12px',cursor:'pointer',boxShadow:SH.primary}}>Auditar y aprobar</button>
                </div>
              ))}
              {x.clave==='acts' && x.k.regs.map(a=>(
                <div key={a.id} style={{padding:'6px 0',borderBottom:`1px solid ${C.bg3}`,fontSize:12,color:C.t1}}>{a.fecha} · {a.titulo||a.tipo}{!a.completada?<span style={{color:C.redText,fontSize:10.5}}> · sin completar</span>:<span style={{color:C.orangeText,fontSize:10.5}}> · sin resultado</span>}</div>
              ))}
              {x.clave==='datos' && x.k.regs.map(l=>(
                <div key={l.id} style={{padding:'6px 0',borderBottom:`1px solid ${C.bg3}`,fontSize:12,color:C.t1}}>{l.nombre||l.telefono||l.id}<span style={{color:C.t4,fontSize:10.5}}> · falta: {[!l.nombre&&'nombre',!l.telefono&&'teléfono',!l.necesidad&&'necesidad',!l.vendedor&&'vendedor'].filter(Boolean).join(', ')}</span></div>
              ))}
            </div>
          )}
        </div>
      );})}
      {/* NEXA fase B */}
      {[['Respuesta a leads NEXA (< 15 min)',20],['Asignación de leads NEXA (< 30 min)',15]].map(([n,p])=>(
        <div key={n} style={{background:C.bg2,border:`1px dashed ${C.border2}`,borderRadius:12,padding:'11px 14px',marginBottom:9,display:'flex',alignItems:'center',gap:12,opacity:.65}}>
          <span style={{width:12,height:12,borderRadius:'50%',background:C.border2}}/>
          <span style={{flex:1,fontSize:13,fontWeight:600,color:C.t3}}>{n}</span>
          <span style={{fontSize:10,fontWeight:700,color:C.t3,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.pill,padding:'2px 9px'}}>Fase B — se activa al instrumentar NEXA</span>
          <span style={{fontSize:9.5,fontWeight:700,color:C.t4,textTransform:'uppercase'}}>Peso {p}%</span>
        </div>
      ))}

      {/* Escala */}
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:16,marginTop:14,boxShadow:SH.sm,maxWidth:520}}>
        <div style={{fontSize:13.5,fontWeight:700,color:C.t1,marginBottom:10}}>Escala de pago</div>
        {TRAMOS.map(([r,p,fn])=>{ const on=fn(puntaje); return (
          <div key={r} style={{display:'flex',alignItems:'center',gap:10,fontSize:12,padding:'6px 10px',borderRadius:R.md,border:`1px solid ${on?C.primaryBorder:C.border}`,background:on?C.primaryTint:'transparent',marginBottom:6}}>
            <span className="radar-num" style={{fontWeight:700,color:C.t2,width:100}}>{r}</span>
            {on&&<span style={{fontSize:10.5,fontWeight:700,color:C.blue}}>ESTÁS AQUÍ</span>}
            <span className="radar-num" style={{marginLeft:'auto',fontWeight:800,color:on?C.blue:C.t2}}>{p}</span>
          </div>
        );})}
        <div style={{fontSize:10.5,color:C.t4,marginTop:8}}>Ningún KPI supera 100% ni compensa a otro. El cierre de mes congela el resultado.{esAdminB?'':' Consultá a gerencia para exclusiones.'}</div>
      </div>
    </div>
  );
}

// ─── PROYECCIÓN SEMANAL (Metas v2, aprobado Dirección) ────────────────────────
// Vista gerencial: resumen total del equipo -> tabla por vendedor -> individual.
// BOOKINGS (RADAR opp ganada) / FACTURACIÓN (SAP) / COBROS (SAP) separados.
// El vendedor NO marca "cobrado": se lee de cobros_sap. Carryover: la promesa
// original nunca se pierde (semana_promesa_original + semanas_arrastrada).
function ProyeccionSemanal({ usuario, empresaActiva, vendedores, metaDe, esKam, cobros, facturasPag, vendCobro }) {
  const lunesDe = (d) => { const x=new Date(d); x.setUTCDate(x.getUTCDate()-((x.getUTCDay()+6)%7)); return x.toISOString().slice(0,10); };
  const addSem  = (l,n=1)=>{ const x=new Date(l+'T12:00:00Z'); x.setUTCDate(x.getUTCDate()+7*n); return x.toISOString().slice(0,10); };
  const hoyISO  = new Date().toISOString().slice(0,10);
  const lunAct  = lunesDe(hoyISO);           // semana que corre
  const lunProx = addSem(lunAct);            // semana a proyectar
  const mesIni  = hoyISO.slice(0,7)+'-01';
  const usd = n => '$'+Math.round(n||0).toLocaleString('en-US');

  const [fc, setFc]       = useState([]);       // forecast de esta y próxima semana
  const [pres, setPres]   = useState([]);       // presentación por vendedor/semana
  const [oppGan, setOppGan] = useState([]);     // opps ganadas del mes (bookings)
  const [loading, setLoading] = useState(true);
  const [sel, setSel]     = useState(null);     // vendedor en drill-down
  const [modal, setModal] = useState(null);     // {vendedor, tipo} presentar/editar
  const esAdmin = usuario.rol==='admin' || usuario.rol==='gerente_ventas';

  const cargar = async () => {
    setLoading(true);
    const [{data:f},{data:p},{data:g}] = await Promise.all([
      supabase.from('forecast_semanal').select('*').in('semana_inicio',[lunAct,lunProx]).limit(3000),
      supabase.from('forecast_presentacion').select('*').in('semana',[lunAct,lunProx]).limit(500),
      supabase.from('oportunidades').select('vendedor,valor,fecha_cierre,etapa').in('etapa',['cerrado_ganado','pagado']).gte('fecha_cierre',mesIni).limit(3000),
    ]);
    setFc(f||[]); setPres(p||[]); setOppGan(g||[]); setLoading(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ }, [empresaActiva]);

  // ── agregación por vendedor ──
  const cobradoMes = {}; cobros.forEach(c=>{ const v=vendCobro(c); if(v) cobradoMes[v]=(cobradoMes[v]||0)+(c.total_usd||c.doc_total||0); });
  const factMes = {}; facturasPag.forEach(f=>{ const v=f.vendedor; if(v){ const t=Number(f.total_usd||0); factMes[v]=(factMes[v]||0)+t; } });
  const bookMes = {}; oppGan.forEach(o=>{ if(o.vendedor) bookMes[o.vendedor]=(bookMes[o.vendedor]||0)+Number(o.valor||0); });

  const presDe = (v,sem)=> (pres||[]).find(x=>x.vendedor===v && x.semana===sem);
  const esCobro = (x)=> x.tipo==='cobros';
  const tipoNorm = (x)=> esCobro(x) ? 'cobros' : 'ventas'; // legacy 'semanal'/'diario'/null = ventas
  const sumFc = (v,sem,tipo)=> (fc||[]).filter(x=>x.vendedor===v && x.semana_inicio===sem && tipoNorm(x)===tipo);
  const totFc = (arr)=> arr.reduce((s,x)=>s+Number(x.monto_estimado||0),0);
  const arrFc = (arr)=> arr.filter(x=>Number(x.semanas_arrastrada||0)>0).reduce((s,x)=>s+Number(x.monto_estimado||0),0);

  const filas = vendedores.map(v=>{
    const vP = sumFc(v,lunProx,'ventas'), cP = sumFc(v,lunProx,'cobros');
    const vTot = totFc(vP), cTot = totFc(cP);
    const vArr = arrFc(vP), cArr = arrFc(cP);
    // cumplimiento de la semana que corre (ventas cerradas vs prometido, cobros SAP vs prometido)
    const vAct = sumFc(v,lunAct,'ventas'), cAct = sumFc(v,lunAct,'cobros');
    const vProm = totFc(vAct), cProm = totFc(cAct);
    const vCerr = vAct.filter(x=>String(x.estado).toUpperCase()==='CUMPLIDA').reduce((s,x)=>s+Number(x.monto_real||x.monto_estimado||0),0);
    const cCobr = cAct.reduce((s,x)=>s+Number(x.monto_cobrado||0),0);
    const p = presDe(v,lunProx);
    return {
      v, kam:esKam(v),
      bookings: bookMes[v]||0, facturacion: factMes[v]||0, cobros: cobradoMes[v]||0, meta: metaDe(v),
      vProx: vTot, cProx: cTot, vArr, cArr,
      cumplV: vProm>0?Math.round(100*vCerr/vProm):null, cumplC: cProm>0?Math.round(100*cCobr/cProm):null,
      pres: p?.estado || 'NO_PRESENTADA', sinProy: !!p?.sin_proyeccion,
    };
  });

  const T = filas.reduce((a,f)=>({
    bookings:a.bookings+f.bookings, facturacion:a.facturacion+f.facturacion, cobros:a.cobros+f.cobros,
    meta:a.meta+f.meta, vProx:a.vProx+f.vProx, cProx:a.cProx+f.cProx, vArr:a.vArr+f.vArr, cArr:a.cArr+f.cArr,
  }), {bookings:0,facturacion:0,cobros:0,meta:0,vProx:0,cProx:0,vArr:0,cArr:0});
  const presentaron = filas.filter(f=>f.pres==='ENVIADA'||f.sinProy).length;
  const noPresent   = filas.filter(f=>f.pres==='NO_PRESENTADA'&&!f.sinProy);
  const diasMes = new Date(new Date().getFullYear(), new Date().getMonth()+1, 0).getDate();
  const frac = Math.max(0.03, new Date().getDate()/diasMes);
  const proyCierre = T.cobros/frac;

  const exportarExcel = () => {
    const resumen = [
      {Metrica:'Ventas cerradas del mes (RADAR)', Valor:Math.round(T.bookings)},
      {Metrica:'Facturacion del mes (SAP)', Valor:Math.round(T.facturacion)},
      {Metrica:'Cobros del mes (SAP)', Valor:Math.round(T.cobros)},
      {Metrica:'Metas aprobadas', Valor:Math.round(T.meta)},
      {Metrica:'Proyeccion cierre de mes', Valor:Math.round(proyCierre)},
      {Metrica:'Ventas proyectadas prox. semana', Valor:Math.round(T.vProx)},
      {Metrica:'Cobros proyectados prox. semana', Valor:Math.round(T.cProx)},
      {Metrica:'Ventas arrastradas', Valor:Math.round(T.vArr)},
      {Metrica:'Cobros arrastrados', Valor:Math.round(T.cArr)},
      {Metrica:'Vendedores que presentaron', Valor:presentaron},
      {Metrica:'Vendedores que NO presentaron', Valor:noPresent.length},
    ];
    const porVend = filas.map(f=>({
      Vendedor:f.v, 'Ventas cerradas':Math.round(f.bookings), Facturacion:Math.round(f.facturacion), Cobros:Math.round(f.cobros),
      'Ventas prox':Math.round(f.vProx), 'Cobros prox':Math.round(f.cProx),
      'Arrastre ventas':Math.round(f.vArr), 'Arrastre cobros':Math.round(f.cArr),
      'Cumpl ventas %':f.cumplV??'', 'Cumpl cobros %':f.cumplC??'', Presentacion:f.pres,
    }));
    const detV = (fc||[]).filter(x=>tipoNorm(x)==='ventas').map(x=>({
      Vendedor:x.vendedor, Cliente:x.cliente, Oportunidad:x.oportunidad_titulo||'', Linea:x.linea_negocio||x.categoria||'',
      Monto:Math.round(x.monto_estimado||0), 'Semana original':x.semana_promesa_original||x.semana_inicio,
      'Semana actual':x.semana_inicio, 'Semanas arrastrada':x.semanas_arrastrada||0, Estado:x.estado,
    }));
    const detC = (fc||[]).filter(x=>tipoNorm(x)==='cobros').map(x=>({
      Vendedor:x.vendedor, Cliente:x.cliente, Factura:x.factura_doc||'', Saldo:Math.round(x.saldo_documento||0),
      Prometido:Math.round(x.monto_estimado||0), Cobrado:Math.round(x.monto_cobrado||0),
      Pendiente:Math.round((x.monto_estimado||0)-(x.monto_cobrado||0)),
      'Semana original':x.semana_promesa_original||x.semana_inicio, 'Semanas arrastrada':x.semanas_arrastrada||0, Estado:x.estado,
    }));
    const noP = noPresent.map(f=>({Vendedor:f.v, Empresa:empresaActiva||'TODAS', Estado:'NO PRESENTO'}));
    const bytes = buildXlsxMulti([
      {name:'Resumen', cols:['Metrica','Valor'], rows:resumen},
      {name:'Por vendedor', cols:['Vendedor','Ventas cerradas','Facturacion','Cobros','Ventas prox','Cobros prox','Arrastre ventas','Arrastre cobros','Cumpl ventas %','Cumpl cobros %','Presentacion'], rows:porVend},
      {name:'Detalle ventas', cols:['Vendedor','Cliente','Oportunidad','Linea','Monto','Semana original','Semana actual','Semanas arrastrada','Estado'], rows:detV},
      {name:'Detalle cobros', cols:['Vendedor','Cliente','Factura','Saldo','Prometido','Cobrado','Pendiente','Semana original','Semanas arrastrada','Estado'], rows:detC},
      {name:'No presentados', cols:['Vendedor','Empresa','Estado'], rows:noP},
    ]);
    const a = Object.assign(document.createElement('a'),{href:URL.createObjectURL(new Blob([bytes],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})),download:`Proyeccion-${lunProx}.xlsx`});
    document.body.appendChild(a); a.click(); a.remove();
  };

  const presentar = async (vend, estado, sinProy, motivo) => {
    await supabase.from('forecast_presentacion').upsert({
      vendedor:vend, semana:lunProx, empresa_id:empresaActiva||null, estado,
      sin_proyeccion:!!sinProy, motivo:motivo||null,
      presentado_at:estado==='ENVIADA'?new Date().toISOString():null, presentado_por:usuario.nombre,
    }, { onConflict:'org_id,vendedor,semana' });
    setModal(null); cargar();
  };

  if (loading) return <div style={{padding:20}}><SkelCards n={4}/><SkelRows n={6}/></div>;

  const Chip = ({estado,sinProy}) => {
    const m = sinProy ? ['Sin proy. (con razón)',C.t3,C.bg3]
      : estado==='ENVIADA' ? ['Presentada',C.greenText,C.greenTint]
      : estado==='BORRADOR' ? ['Borrador',C.orange,'#FEF3C7'] : ['NO presentó',C.redText,C.redTint];
    return <span style={{fontSize:10.5,fontWeight:700,color:m[1],background:m[2],padding:'2px 8px',borderRadius:20,whiteSpace:'nowrap'}}>{m[0]}</span>;
  };
  const Cel = ({children,b,c,r})=><td style={{padding:'8px 10px',fontSize:12,color:c||C.t1,fontWeight:b?800:500,textAlign:r?'right':'left',whiteSpace:'nowrap'}}>{children}</td>;

  return (
    <div>
      {/* ── REPORTE SEMANAL (cierre semana anterior + apertura) ── */}
      <ReporteSemanal/>
      {/* ── RESUMEN TOTAL DEL EQUIPO ── */}
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:'16px 18px',marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:'wrap',gap:8}}>
          <div style={{fontSize:15,fontWeight:900,color:C.t1}}>Resumen del equipo · semana {lunProx}</div>
          <button onClick={exportarExcel} style={{display:'inline-flex',alignItems:'center',gap:6,height:34,padding:'0 14px',borderRadius:R.md,border:'none',background:C.green,color:'#fff',fontSize:12.5,fontWeight:700,cursor:'pointer'}}><CIcon d={CI.download}/>Exportar Excel</button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10}}>
          {[['Ventas cerradas mes (RADAR)',usd(T.bookings),C.blue],['Facturación mes (SAP)',usd(T.facturacion),C.indigo],['Cobros mes (SAP)',usd(T.cobros),C.green],['Metas aprobadas',usd(T.meta),C.t2],['Proyección cierre',usd(proyCierre),C.orange],['Ventas próx. semana',usd(T.vProx),C.blue],['Cobros próx. semana',usd(T.cProx),C.green],['Arrastre ventas',usd(T.vArr),C.redText],['Arrastre cobros',usd(T.cArr),C.redText],['Presentaron',`${presentaron}/${filas.length}`,C.greenText],['NO presentaron',String(noPresent.length),noPresent.length?C.redText:C.greenText]].map(([l,v,c],i)=>(
            <div key={i} style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 12px'}}>
              <div style={{fontSize:10.5,color:C.t3,fontWeight:600,marginBottom:3}}>{l}</div>
              <div style={{fontSize:16,fontWeight:900,color:c}} className="radar-num">{v}</div>
            </div>
          ))}
        </div>
        {noPresent.length>0 && <div style={{marginTop:10,fontSize:12,color:C.redText}}>⚠ No presentaron: {noPresent.map(f=>f.v.split(' ')[0]).join(', ')}</div>}
      </div>

      {/* ── TABLA POR VENDEDOR ── */}
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,overflow:'hidden'}}>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',minWidth:900}}>
            <thead><tr style={{background:C.bg1,borderBottom:`2px solid ${C.border}`}}>
              {['Vendedor','Vtas cerradas','Facturación SAP','Cobros SAP','V. próx','C. próx','Arr. V','Arr. C','Cumpl V','Cumpl C','Estado'].map((h,i)=>(
                <th key={i} style={{padding:'9px 10px',fontSize:10.5,fontWeight:700,color:C.t3,textAlign:i===0?'left':'right',textTransform:'uppercase',letterSpacing:'.04em',whiteSpace:'nowrap'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filas.map(f=>(
                <tr key={f.v} onClick={()=>setSel(sel===f.v?null:f.v)} className="radar-hrow" style={{borderBottom:`1px solid ${C.bg3}`,cursor:'pointer'}}>
                  <Cel b>{f.v.split(' ')[0]}{f.kam&&<span style={{fontSize:9,color:C.indigo,marginLeft:4}}>KAM</span>}</Cel>
                  <Cel r c={C.blue}>{usd(f.bookings)}</Cel>
                  <Cel r c={C.indigo}>{usd(f.facturacion)}</Cel>
                  <Cel r c={C.green}>{usd(f.cobros)}</Cel>
                  <Cel r>{usd(f.vProx)}</Cel>
                  <Cel r>{usd(f.cProx)}</Cel>
                  <Cel r c={f.vArr?C.redText:C.t4}>{f.vArr?usd(f.vArr):'—'}</Cel>
                  <Cel r c={f.cArr?C.redText:C.t4}>{f.cArr?usd(f.cArr):'—'}</Cel>
                  <Cel r c={f.cumplV==null?C.t4:f.cumplV>=80?C.greenText:C.redText}>{f.cumplV==null?'—':f.cumplV+'%'}</Cel>
                  <Cel r c={f.cumplC==null?C.t4:f.cumplC>=80?C.greenText:C.redText}>{f.cumplC==null?'—':f.cumplC+'%'}</Cel>
                  <td style={{padding:'8px 10px',textAlign:'right'}}><Chip estado={f.pres} sinProy={f.sinProy}/></td>
                </tr>
              ))}
              <tr style={{background:C.bg1,borderTop:`2px solid ${C.border}`,fontWeight:900}}>
                <Cel b>TOTAL</Cel><Cel r b c={C.blue}>{usd(T.bookings)}</Cel><Cel r b c={C.indigo}>{usd(T.facturacion)}</Cel>
                <Cel r b c={C.green}>{usd(T.cobros)}</Cel><Cel r b>{usd(T.vProx)}</Cel><Cel r b>{usd(T.cProx)}</Cel>
                <Cel r b c={C.redText}>{usd(T.vArr)}</Cel><Cel r b c={C.redText}>{usd(T.cArr)}</Cel><Cel>—</Cel><Cel>—</Cel><Cel r b>{presentaron}/{filas.length}</Cel>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── DRILL-DOWN INDIVIDUAL ── */}
      {sel && (()=>{
        const f = filas.find(x=>x.v===sel); if(!f) return null;
        const vAct = sumFc(sel,lunAct,'ventas'), cAct = sumFc(sel,lunAct,'cobros');
        const vPro = sumFc(sel,lunProx,'ventas'), cPro = sumFc(sel,lunProx,'cobros');
        // Color del chip de estado (misma semántica en toda la vista).
        const estCol = e => ({ CUMPLIDA:C.greenText, PARCIAL:C.orange, ARRASTRADA:C.redText, PERDIDA:C.redText, CANCELADA:C.t4 }[String(e||'').toUpperCase()] || C.t3);
        const estBg  = e => ({ CUMPLIDA:C.greenTint, PARCIAL:'#FEF3C7', ARRASTRADA:C.redTint, PERDIDA:C.redTint }[String(e||'').toUpperCase()] || C.bg3);
        // Cada línea es una fila de 2 niveles: cliente + monto arriba, detalle +
        // estado abajo. Nombres largos se truncan con puntos suspensivos.
        // Editar el monto o eliminar una promesa/proyeccion, directo desde el
        // detalle del vendedor. Corrige cargas erroneas que inflan la proyeccion
        // (ej. un monto tecleado de mas) sin dejar como quitarlas.
        const editarLinea = async (x) => {
          const val = window.prompt(`Nuevo monto para "${x.cliente||'—'}" (actual ${usd(x.monto_estimado)}):`, String(Math.round(x.monto_estimado||0)));
          if (val==null) return;
          const monto = parseFloat(String(val).replace(/[^0-9.]/g,''))||0;
          const { error } = await supabase.from('forecast_semanal').update({ monto_estimado: monto }).eq('id', x.id);
          if (error) { alert('No se pudo editar: '+error.message); return; }
          cargar();
        };
        const eliminarLinea = async (x) => {
          if (!window.confirm(`Eliminar la promesa de "${x.cliente||'—'}" por ${usd(x.monto_estimado)}?\n\nSe quita del forecast y del arrastre. No se puede deshacer.`)) return;
          const { error } = await supabase.from('forecast_semanal').delete().eq('id', x.id);
          if (error) { alert('No se pudo eliminar: '+error.message); return; }
          cargar();
        };
        const LineaFc = ({x,tipo,color})=>{
          const detalle = tipo==='cobros'
            ? `Cobrado ${usd(x.monto_cobrado||0)} · pendiente ${usd((x.monto_estimado||0)-(x.monto_cobrado||0))}${x.factura_doc?` · Fact. ${x.factura_doc}`:''}`
            : (x.oportunidad_titulo || x.categoria || 'Sin detalle');
          const arr = Number(x.semanas_arrastrada)>0 ? ` · arrastre ${x.semanas_arrastrada} sem` : '';
          return (
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,padding:'8px 0',borderBottom:`1px solid ${C.bg3}`}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{fontSize:12.5,fontWeight:700,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{x.cliente||'—'}</div>
                <div style={{fontSize:10.5,color:C.t4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',marginTop:1}}>{detalle}{arr}</div>
              </div>
              <div style={{textAlign:'right',flexShrink:0,display:'flex',alignItems:'center',gap:8}}>
                <div>
                  <div className="radar-num" style={{fontSize:13,fontWeight:800,color}}>{usd(x.monto_estimado)}</div>
                  <span style={{display:'inline-block',fontSize:9,fontWeight:800,color:estCol(x.estado),background:estBg(x.estado),padding:'1px 6px',borderRadius:20,marginTop:2,letterSpacing:'.02em'}}>{String(x.estado||'').toUpperCase()}</span>
                </div>
                <button title="Editar monto" onClick={()=>editarLinea(x)}
                  style={{background:`${C.blue}15`,color:C.blue,border:'none',borderRadius:6,padding:'4px 7px',fontSize:12,cursor:'pointer',lineHeight:1}}>✏️</button>
                <button title="Eliminar" onClick={()=>eliminarLinea(x)}
                  style={{background:`${C.red}15`,color:C.red,border:'none',borderRadius:6,padding:'4px 7px',fontSize:12,cursor:'pointer',lineHeight:1}}>🗑</button>
              </div>
            </div>
          );
        };
        const Bloc = ({t,items,tipo,color})=>(
          <div style={{flex:1,minWidth:260}}>
            <div style={{fontSize:12,fontWeight:800,color,marginBottom:4}}>{t}</div>
            {items.length?items.map((x,i)=><LineaFc key={i} x={x} tipo={tipo} color={color}/>):<div style={{fontSize:11.5,color:C.t4,padding:'6px 0'}}>Sin líneas.</div>}
          </div>
        );
        return (
          <div style={{marginTop:14,background:C.bg2,border:`1px solid ${C.primaryBorder}`,borderRadius:14,padding:'16px 18px'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:'wrap',gap:8}}>
              <div style={{fontSize:15,fontWeight:900,color:C.t1}}>{sel}</div>
              <button onClick={()=>setModal({vendedor:sel})} style={{height:32,padding:'0 14px',borderRadius:R.md,border:'none',background:C.primary,color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>Presentar proyección</button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))',gap:10,marginBottom:14}}>
              {[['Ventas cerradas',usd(f.bookings),C.blue],['Facturación',usd(f.facturacion),C.indigo],['Cobros',usd(f.cobros),C.green],['Meta',usd(f.meta),C.t2]].map(([l,v,c],i)=>(
                <div key={i} style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:10,padding:'8px 10px'}}><div style={{fontSize:10,color:C.t3}}>{l}</div><div style={{fontSize:14,fontWeight:800,color:c}} className="radar-num">{v}</div></div>
              ))}
            </div>
            <div style={{fontSize:12,fontWeight:800,color:C.t2,margin:'4px 0 8px'}}>Compromisos de esta semana ({lunAct})</div>
            <div style={{display:'flex',gap:16,flexWrap:'wrap',marginBottom:14}}><Bloc t="VENTAS" items={vAct} tipo="ventas" color={C.blue}/><Bloc t="COBROS" items={cAct} tipo="cobros" color={C.green}/></div>
            <div style={{fontSize:12,fontWeight:800,color:C.t2,margin:'4px 0 8px'}}>Proyección próxima semana ({lunProx})</div>
            <div style={{display:'flex',gap:16,flexWrap:'wrap'}}><Bloc t={`VENTAS — total ${usd(f.vProx)} (nuevas + arrastre ${usd(f.vArr)})`} items={vPro} tipo="ventas" color={C.blue}/><Bloc t={`COBROS — total ${usd(f.cProx)} (nuevos + arrastre ${usd(f.cArr)})`} items={cPro} tipo="cobros" color={C.green}/></div>
          </div>
        );
      })()}

      {/* ── MODAL PRESENTAR ── */}
      <Modal open={!!modal} onClose={()=>setModal(null)} title="Presentar proyección" width={440}>
        {modal && <PresentarProy vendedor={modal.vendedor} onPresentar={presentar}
          lineasV={sumFc(modal.vendedor,lunProx,'ventas')} lineasC={sumFc(modal.vendedor,lunProx,'cobros')}
          estadoActual={(presDe(modal.vendedor,lunProx)||{}).estado} semana={lunProx} />}
      </Modal>
    </div>
  );
}

// Modal de presentación: ENVIADA, BORRADOR, o SIN PROYECCIÓN (razón obligatoria).
function PresentarProy({ vendedor, onPresentar, lineasV=[], lineasC=[], estadoActual=null, semana=null }) {
  const [sinProy, setSinProy] = useState(false);
  const [motivo, setMotivo]   = useState('');
  const usd = n => `$${Math.round(Number(n)||0).toLocaleString('en-US')}`;
  const totV = lineasV.reduce((s,x)=>s+Number(x.monto_estimado||0),0);
  const totC = lineasC.reduce((s,x)=>s+Number(x.monto_estimado||0),0);
  const nLineas = lineasV.length + lineasC.length;
  return (
    <div>
      <p style={{fontSize:13,color:C.t3,marginTop:0}}>{vendedor} — proyección de la semana {semana||'próxima'}.
        {estadoActual==='ENVIADA' && <span style={{color:C.greenText,fontWeight:700}}> Ya presentada (podés re-presentar para actualizar).</span>}</p>

      {/* Resumen de lo que se va a presentar */}
      <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'12px 14px',marginBottom:12}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
          <span style={{fontSize:12,color:C.t3}}>Ventas proyectadas ({lineasV.length})</span>
          <span className="radar-num" style={{fontSize:13,fontWeight:800,color:C.blue}}>{usd(totV)}</span>
        </div>
        <div style={{display:'flex',justifyContent:'space-between'}}>
          <span style={{fontSize:12,color:C.t3}}>Cobros proyectados ({lineasC.length})</span>
          <span className="radar-num" style={{fontSize:13,fontWeight:800,color:C.green}}>{usd(totC)}</span>
        </div>
        {!nLineas && <div style={{fontSize:11.5,color:C.t4,marginTop:8,lineHeight:1.4}}>No hay líneas cargadas para esta semana. Cargalas en la pestaña <b>Compromisos</b>, o marcá "Sin proyección" con la razón.</div>}
      </div>

      <label style={{display:'flex',alignItems:'center',gap:9,padding:'10px 12px',border:`1px solid ${sinProy?C.orange:C.border}`,borderRadius:R.md,background:sinProy?'#FEF3C7':C.bg1,cursor:'pointer',marginBottom:10}}>
        <input type="checkbox" checked={sinProy} onChange={e=>setSinProy(e.target.checked)}/>
        <span style={{fontSize:13,color:C.t1}}>Sin proyección esta semana (blanco NO significa cero)</span>
      </label>
      {sinProy && <textarea placeholder="Razón obligatoria…" value={motivo} onChange={e=>setMotivo(e.target.value)} rows={2} style={{width:'100%',boxSizing:'border-box',border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 10px',fontSize:13,marginBottom:10,resize:'vertical'}}/>}
      <div style={{display:'flex',gap:8}}>
        <Btn variant="ghost" onClick={()=>onPresentar(vendedor,'BORRADOR',false,null)} full>Guardar borrador</Btn>
        <Btn onClick={()=>onPresentar(vendedor,'ENVIADA', sinProy, sinProy?motivo:null)} disabled={(sinProy&&!motivo.trim())||(!sinProy&&!nLineas)} full>
          {sinProy?'Presentar sin proyección':'Presentar'}
        </Btn>
      </div>
    </div>
  );
}

function MetasRevision({ usuario, empresaActiva }) {
  const pad = n => String(n).padStart(2,'0');
  const hoy = new Date();
  const [tab, setTab]   = useState('resumen');
  const [fcVista, setFcVista] = useState('semana'); // compromisos: semana | hoy (dentro del detalle)
  // Sub-vista dentro de la pestaña Bonos: constructor de planes o bono asistente.
  // Claudia (no admin) solo ve el asistente, así que arranca ahí.
  const [subBonos, setSubBonos] = useState(usuario.rol==='admin' ? 'planes' : 'asist');
  // Maestro-detalle de la pestaña de metas: vendedor seleccionado y líneas de
  // negocio "abiertas" a mano (chips) que aún no tienen monto guardado.
  const [metaSel, setMetaSel] = useState(null);
  const [catsAbiertas, setCatsAbiertas] = useState({});
  const [simAdd, setSimAdd] = useState(0); // simulador de comisión: venta adicional (USD)
  const [mes, setMes]   = useState(`${hoy.getFullYear()}-${pad(hoy.getMonth()+1)}`);
  const [vendedores, setVendedores] = useState([]);
  const [vendSel, setVendSel] = useState(null);
  const [metas, setMetas]   = useState({});   // {vendedor: metaGlobalMensual}
  const [metasCat, setMetasCat] = useState({}); // {vendedor: {categoria: monto}} reparto
  const [kam, setKam]       = useState(['Audry Garcia','Carlos Hernandez']); // KAM sin meta
  const [metaDef, setMetaDef] = useState(60000); // meta mensual default
  const [cobros, setCobros] = useState([]);
  const [opps, setOpps]     = useState([]);
  const [fcSem, setFcSem]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [comCfg, setComCfg]   = useState({ meta:60000, meta_minima:0.75, rates:{} });
  const [facturasPag, setFacturasPag] = useState([]); // facturas pagadas del mes
  const [catOverride, setCatOverride] = useState({}); // "doc|emp" → categoría manual
  const [oppCatMap, setOppCatMap]     = useState({}); // factura_sap_num → línea de negocio
  const [facVendMap, setFacVendMap]   = useState({}); // "doc|emp" → vendedor asignado a mano
  const [oppVendMap, setOppVendMap]   = useState({}); // factura_sap_num → vendedor de la opp
  const [cliVendMap, setCliVendMap]   = useState({}); // "emp|card_code" → dueño del cliente
  const [bonos, setBonos]       = useState({});   // {vendedor: monto bono del mes}
  const [kamCuentas, setKamCuentas] = useState({}); // {vendedor: substring cliente clave}
  const [ktaSnap, setKtaSnap]   = useState(null);  // salud de la cuenta clave del KAM
  const [kamBono, setKamBono]   = useState(null);  // bono KAM calculado (API /report?kam=) — mes en curso
  const [verConcil, setVerConcil] = useState(false); // drill-down de conciliación CRM↔SAP
  const [kamPortfolios, setKamPortfolios] = useState({}); // radar_config kam_portfolios {vendedor:[cuentas]}
  const [editCart, setEditCart] = useState(false);   // editando cartera del KAM
  const [cartDraft, setCartDraft] = useState([]);    // borrador de cuentas
  const [nuevaCta, setNuevaCta]   = useState('');
  const [comVista, setComVista]   = useState('vend'); // comisiones: 'vend' (un vendedor) | 'todos' (consolidado)
  const [comExp, setComExp]       = useState(false);  // menú Exportar abierto
  // Fase 2 — constructor de bonos configurables (bonos_config)
  const [bonosCfg, setBonosCfg]   = useState([]);     // lista de planes de bono
  const [bonoSel, setBonoSel]     = useState(null);   // borrador del plan en edición
  const [bonoSaving, setBonoSaving] = useState(false);
  const esGerencia = ['admin','gerente_ventas'].includes(usuario.rol);
  const FUENTES_BONO = [
    ['cumplimiento_meta','Cumplimiento de meta'],['pipeline_vs_meta','Pipeline vs meta (3x)'],
    ['visitas_llamadas','Visitas + llamadas'],['salud_crm','Salud del CRM'],['cobrado_sap','Cobrado (SAP)'],
    ['retencion','Retención de cartera'],['crecimiento','Crecimiento de cartera'],
    ['satisfaccion','Satisfacción (encuesta)'],['actividad','Actividad (contactos)'],['manual','Manual (gerencia)'],
  ];
  const FUENTE_LBL = Object.fromEntries(FUENTES_BONO);
  const fmt2 = n => `$${(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  // Rango del mes seleccionado
  const [yy,mm] = mes.split('-').map(Number);
  const mesIni = `${mes}-01`;
  const mesFin = `${mes}-${pad(new Date(yy,mm,0).getDate())}`;
  // Lunes de esta semana y de la pasada
  const lunesDe = (base) => { const d=new Date(base); const g=d.getDay(); const diff=g===0?-6:1-g; d.setDate(d.getDate()+diff); return d.toISOString().split('T')[0]; };
  const lunes = lunesDe(hoy);
  const lunesAnt = lunesDe(new Date(Date.now()-7*86400000));
  const hoyISO = hoy.toISOString().slice(0,10);
  const ayerISO = new Date(Date.now()-86400000).toISOString().slice(0,10);

  const cargar = async () => {
    setLoading(true);
    const [{data:vs},{data:mv},{data:mc},{data:cfgRows},{data:cb},{data:op},{data:fc},{data:comRows},{data:fpag},{data:fcat},{data:oppFac},{data:cliVendRows},{data:bonoRows},{data:ktaCfg},{data:kamPort}] = await Promise.all([
      supabase.from('radar_usuarios').select('nombre').eq('rol','ventas').eq('activo',true).order('nombre'),
      supabase.from('metas_vendedor').select('vendedor,meta_dolares').eq('mes',mes),
      supabase.from('metas_categoria').select('vendedor,categoria,meta_usd').eq('mes',mes),
      supabase.from('radar_config').select('value').eq('key','meta_config'),
      (() => { let q=supabase.from('cobros_sap').select('total_usd,doc_total,vendedor,doc_date,card_name,card_code,empresa_id').gte('doc_date',mesIni).lte('doc_date',mesFin).gt('doc_total',0); if(empresaActiva&&empresaActiva!=='all') q=q.eq('empresa_id',empresaActiva); return q; })(),
      (() => { let q=supabase.from('oportunidades').select('vendedor,valor,linea_negocio,fecha_cierre,etapa,factura_sap_num,titulo,cliente').in('etapa',['pagado','cerrado_ganado']).gte('fecha_cierre',mesIni).lte('fecha_cierre',mesFin); if(empresaActiva&&empresaActiva!=='all') q=q.eq('empresa_id',empresaActiva); return q; })(),
      supabase.from('forecast_semanal').select('*').in('semana_inicio',[lunes,lunesAnt]),
      supabase.from('radar_config').select('value').eq('key','comision_config'),
      (() => { let q=supabase.from('facturas_sap').select('doc_num,empresa_id,card_name,card_code,doc_date,doc_total,total_usd,vat_sum,moneda,vendedor,pagado').gte('doc_date',mesIni).lte('doc_date',mesFin); if(empresaActiva&&empresaActiva!=='all') q=q.eq('empresa_id',empresaActiva); return q; })(),
      supabase.from('factura_categoria').select('doc_num,empresa_id,categoria,vendedor'),
      supabase.from('oportunidades').select('factura_sap_num,linea_negocio,vendedor').not('factura_sap_num','is',null).limit(5000),
      supabase.from('clientes').select('sap_code,vendedor').not('vendedor','is',null),
      supabase.from('bono_kam').select('vendedor,monto').eq('mes',mes),
      supabase.from('radar_config').select('value').eq('key','kam_cuentas'),
      supabase.from('radar_config').select('value').eq('key','kam_portfolios'),
    ]);
    // Un vendedor (rol ventas) solo se ve a sí mismo; gerencia ve a todos.
    let nombres = (vs||[]).map(v=>v.nombre);
    if (!esGerencia) nombres = nombres.filter(n=>n===usuario.nombre);
    setVendedores(nombres);
    if (nombres.length && (!vendSel || !nombres.includes(vendSel))) setVendSel(nombres[0]);
    const mm2 = {};
    (mv||[]).forEach(r=>{ mm2[r.vendedor]=r.meta_dolares; });
    setMetas(mm2);
    const mc2 = {};
    (mc||[]).forEach(r=>{ mc2[r.vendedor]=mc2[r.vendedor]||{}; mc2[r.vendedor][r.categoria]=r.meta_usd; });
    setMetasCat(mc2);
    const cfg = cfgRows?.[0]?.value || {};
    if (Array.isArray(cfg.kam)) setKam(cfg.kam);
    if (cfg.default != null) setMetaDef(Number(cfg.default)||60000);
    setCobros(cb||[]); setOpps(op||[]); setFcSem(fc||[]);
    // Comisiones
    const comV = comRows?.[0]?.value; if (comV) setComCfg({ meta:Number(comV.meta)||60000, meta_minima:Number(comV.meta_minima)||0.75, rates:comV.rates||{} });
    setFacturasPag(fpag||[]);
    const co={}, fv={}; (fcat||[]).forEach(r=>{ const k=`${r.doc_num}|${r.empresa_id}`; if(r.categoria) co[k]=r.categoria; if(r.vendedor) fv[k]=r.vendedor; }); setCatOverride(co); setFacVendMap(fv);
    const om={}, ov={}; (oppFac||[]).forEach(r=>{ const k=String(r.factura_sap_num); if(r.linea_negocio&&!om[k]) om[k]=r.linea_negocio; if(r.vendedor&&!ov[k]) ov[k]=r.vendedor; }); setOppCatMap(om); setOppVendMap(ov);
    // Dueño del cliente: sap_code = "empresa_id_CardCode" → clave "empresa_id|CardCode"
    const cvm={}; (cliVendRows||[]).forEach(r=>{ const sc=String(r.sap_code||''); const i=sc.indexOf('_'); if(i>0&&r.vendedor) cvm[`${sc.slice(0,i)}|${sc.slice(i+1)}`]=r.vendedor; }); setCliVendMap(cvm);
    const bo={}; (bonoRows||[]).forEach(r=>{ bo[r.vendedor]=r.monto; }); setBonos(bo);
    setKamCuentas(ktaCfg?.[0]?.value || {});
    setKamPortfolios(kamPort?.[0]?.value || {});
    setLoading(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ }, [mes, empresaActiva]);

  // Editar/eliminar una promesa (compromiso de forecast) directo desde el detalle
  // del vendedor. Corrige cargas erroneas que inflan la proyeccion sin dejar como
  // borrarlas (ej. un monto tecleado de mas). RLS permite delete/update a autenticados.
  const usd0 = n => '$'+Math.round(Number(n)||0).toLocaleString('en-US');
  const editarPromesa = async (f) => {
    const actual = f.monto_real!=null ? f.monto_real : f.monto_estimado;
    const val = window.prompt(`Nuevo monto para "${f.cliente}" (actual ${usd0(actual)}):`, String(Math.round(actual||0)));
    if (val==null) return;
    const monto = parseFloat(String(val).replace(/[^0-9.]/g,''))||0;
    const { error } = await supabase.from('forecast_semanal').update({ monto_estimado: monto }).eq('id', f.id);
    if (error) { alert('No se pudo editar: '+error.message); return; }
    cargar();
  };
  const eliminarPromesa = async (f) => {
    if (!window.confirm(`Eliminar la promesa de "${f.cliente}" por ${usd0(f.monto_real!=null?f.monto_real:f.monto_estimado)}?\n\nSe quita del forecast y del arrastre. No se puede deshacer.`)) return;
    const { error } = await supabase.from('forecast_semanal').delete().eq('id', f.id);
    if (error) { alert('No se pudo eliminar: '+error.message); return; }
    cargar();
  };
  // Cargar planes de bono (Fase 2) — solo gerencia usa la pestaña de configuración
  const cargarBonos = async () => {
    const { data } = await supabase.from('bonos_config').select('*').order('created_at',{ascending:true});
    setBonosCfg(data||[]);
  };
  useEffect(()=>{ if(usuario.rol==='admin') cargarBonos(); /* eslint-disable-next-line */ }, []);
  // Handlers CRUD del constructor de bonos
  const bonoVacio = () => ({ nombre:'Nuevo bono', aplica_a:'vendedores', aplica_valor:null, periodo:'mensual',
    base:3000, tope:5000, activo:true,
    dimensiones:[{clave:'d1',nombre:'Dimensión 1',fuente:'cumplimiento_meta',objetivo:'',peso:100}],
    acelerador:{ activo:true, max:2000, sobre:100, techo:120, gate:{crm:80,visitas:80} } });
  const abrirBono = (b) => setBonoSel(JSON.parse(JSON.stringify(b || bonoVacio())));
  const sumaPesos = (b) => (b?.dimensiones||[]).reduce((s,d)=>s+(Number(d.peso)||0),0);
  const guardarBonoCfg = async () => {
    if (!bonoSel) return;
    if (sumaPesos(bonoSel) !== 100) { toast('Los pesos de las dimensiones deben sumar 100%.'); return; }
    setBonoSaving(true);
    const row = { nombre:bonoSel.nombre, aplica_a:bonoSel.aplica_a, aplica_valor:bonoSel.aplica_valor||null,
      periodo:bonoSel.periodo, base:Number(bonoSel.base)||0, tope:Number(bonoSel.tope)||0,
      dimensiones:bonoSel.dimensiones, acelerador:bonoSel.acelerador, activo:bonoSel.activo, updated_at:new Date().toISOString() };
    let res;
    if (bonoSel.id) res = await supabase.from('bonos_config').update(row).eq('id',bonoSel.id).select().single();
    else res = await supabase.from('bonos_config').insert(row).select().single();
    setBonoSaving(false);
    if (res.error) { toast('No se pudo guardar: '+res.error.message); return; }
    await cargarBonos(); setBonoSel(res.data); toast('Bono guardado.');
  };
  const toggleBonoActivo = async (b) => {
    await supabase.from('bonos_config').update({ activo:!b.activo, updated_at:new Date().toISOString() }).eq('id',b.id);
    cargarBonos(); if (bonoSel?.id===b.id) setBonoSel(s=>({...s,activo:!s.activo}));
  };
  const clonarBono = async (b) => {
    const c = JSON.parse(JSON.stringify(b)); delete c.id; delete c.created_at; delete c.updated_at;
    c.nombre = `${b.nombre} (copia)`; c.activo = false;
    const { data, error } = await supabase.from('bonos_config').insert(c).select().single();
    if (error) { toast('No se pudo clonar: '+error.message); return; }
    await cargarBonos(); setBonoSel(data); toast('Bono clonado como borrador.');
  };
  const borrarBonoCfg = async (b) => {
    if (!(await confirmar(`¿Borrar el bono "${b.nombre}"? No afecta bonos ya pagados.`, { ok:'Borrar', peligro:true }))) return;
    await supabase.from('bonos_config').delete().eq('id',b.id);
    await cargarBonos(); if (bonoSel?.id===b.id) setBonoSel(null);
  };

  const esKam   = (v) => kam.includes(v);
  const metaDe  = (v) => esKam(v) ? 0 : (metas[v] != null ? Number(metas[v]) : metaDef);

  const guardarMeta = async (vendedor, valor) => {
    setSaving(true);
    await supabase.from('metas_vendedor').upsert(
      { vendedor, mes, meta_dolares: Number(valor)||0 }, { onConflict:'vendedor,mes' }
    );
    setMetas(m=>({...m, [vendedor]:Number(valor)||0}));
    setSaving(false);
  };
  const guardarMetaCat = async (vendedor, categoria, valor) => {
    setSaving(true);
    await supabase.from('metas_categoria').upsert(
      { vendedor, mes, categoria, meta_usd: Number(valor)||0, empresa_id: empresaActiva&&empresaActiva!=='all'?empresaActiva:null, updated_at:new Date().toISOString() },
      { onConflict:'vendedor,mes,categoria' }
    );
    setMetasCat(m=>({...m, [vendedor]:{...(m[vendedor]||{}), [categoria]:Number(valor)||0}}));
    setSaving(false);
  };
  // Vendedor efectivo de una factura: override venta → dueño del cliente → SAP → opp
  const vendEfectivo = (f) => facVendMap[`${f.doc_num}|${f.empresa_id}`] || cliVendMap[`${f.empresa_id}|${f.card_code}`] || f.vendedor || oppVendMap[String(f.doc_num)] || null;
  // Dueño de un cobro: dueño del cliente → vendedor SAP
  const vendCobro = (c) => cliVendMap[`${c.empresa_id}|${c.card_code}`] || c.vendedor || null;
  const guardarVendFactura = async (doc_num, empresa_id, vendedor) => {
    await supabase.from('factura_categoria').upsert(
      { doc_num, empresa_id, vendedor: vendedor||null, updated_at:new Date().toISOString() }, { onConflict:'doc_num,empresa_id' }
    );
    setFacVendMap(m=>({...m, [`${doc_num}|${empresa_id}`]: vendedor||null }));
  };
  const guardarBono = async (vendedor, monto) => {
    await supabase.from('bono_kam').upsert(
      { vendedor, mes, monto:Number(monto)||0, updated_at:new Date().toISOString() }, { onConflict:'vendedor,mes' }
    );
    setBonos(b=>({...b, [vendedor]:Number(monto)||0}));
  };
  const guardarCatFactura = async (doc_num, empresa_id, categoria) => {
    await supabase.from('factura_categoria').upsert(
      { doc_num, empresa_id, categoria, updated_at:new Date().toISOString() }, { onConflict:'doc_num,empresa_id' }
    );
    setCatOverride(m=>({...m, [`${doc_num}|${empresa_id}`]:categoria}));
  };
  const toggleKam = async (vendedor) => {
    const next = esKam(vendedor) ? kam.filter(x=>x!==vendedor) : [...kam, vendedor];
    setKam(next);
    await supabase.from('radar_config').upsert(
      { key:'meta_config', value:{ default:metaDef, kam:next }, updated_at:new Date().toISOString() }, { onConflict:'org_id,key' }
    );
  };
  // Guarda la cartera (cuentas clave) del KAM en radar_config y recalcula el bono.
  const guardarCartera = async () => {
    const limpio = cartDraft.map(s=>String(s).trim()).filter(Boolean);
    const next = { ...kamPortfolios, [vendSel]: limpio };
    await supabase.from('radar_config').upsert(
      { key:'kam_portfolios', value:next, updated_at:new Date().toISOString() }, { onConflict:'org_id,key' }
    );
    setKamPortfolios(next); setEditCart(false);
    // Recalcular bono con la nueva cartera
    setKamBono(null);
    fetch(`/api/report?kam=${encodeURIComponent(vendSel)}`).then(r=>r.json()).then(d=>{ if(d&&d.ok) setKamBono(d); }).catch(()=>{});
  };

  // ── Cálculos del vendedor seleccionado ──
  const vendKam   = esKam(vendSel);
  const metaTotal = metaDe(vendSel);
  const cobrosV = cobros.filter(c=>vendCobro(c)===vendSel);
  const realSAP = cobrosV.reduce((s,c)=>s+(c.total_usd||c.doc_total||0),0);   // VERDAD: dinero ingresado (por dueño del cliente)
  const oppsV = opps.filter(o=>o.vendedor===vendSel);
  const crmTotal = oppsV.reduce((s,o)=>s+(o.valor||0),0);
  const pct = metaTotal>0 ? Math.round(realSAP/metaTotal*100) : null;
  const falta = Math.max(0, metaTotal-realSAP);
  const metaSemanal = metaTotal>0 ? metaTotal/4.33 : 0;
  const metaCatSel = metasCat[vendSel] || {};   // reparto de meta por categoría
  // CRM por categoría (desglose)
  const crmPorCat = {};
  oppsV.forEach(o=>{ const k=o.linea_negocio||'Sin categoría'; crmPorCat[k]=(crmPorCat[k]||0)+(o.valor||0); });
  // Forecast del vendedor
  const fcV = fcSem.filter(f=>f.vendedor===vendSel);
  const fcEsta = fcV.filter(f=>f.semana_inicio===lunes);
  const fcArrastre = fcV.filter(f=>f.semana_inicio===lunesAnt && f.estado!=='cerrado');
  // Conciliación: descuadre CRM vs SAP
  const descuadre = realSAP - crmTotal;

  // ── COMISIONES ──
  const comMeta    = comCfg.meta || 60000;
  const comMetaMin = comCfg.meta_minima || 0.75;
  const comRates   = comCfg.rates || {};
  const cumplPct   = comMeta>0 ? realSAP/comMeta : 0;           // cumplimiento monetario (cobros, incl. anticipos)
  // KAM no llevan factor de meta: comisionan al 100% lo que vendan.
  const comFactor  = vendKam ? 1 : (cumplPct>=comMetaMin ? 1 : Math.max(0, 1-(comMetaMin-cumplPct)));
  const bonoKam    = vendKam ? (Number(bonos[vendSel])||0) : 0;
  const catDeFactura = (f) => catOverride[`${f.doc_num}|${f.empresa_id}`] || oppCatMap[String(f.doc_num)] || null;
  const subtotalUsd  = (f) => { const r=(f.total_usd>0&&f.doc_total>0)?(f.doc_total/f.total_usd):1; const vatUsd=(f.vat_sum||0)/(r||1); return Math.max(0,(f.total_usd||0)-vatUsd); };
  // Ventas totales del mes = suma de subtotales SIN ISV de todo lo pagado (base del 1% de Administración)
  const ventasTotalesMes = facturasPag.filter(f=>f.pagado).reduce((s,f)=>s+subtotalUsd(f),0);
  const vendEsAdmin = vendSel==='Administrador';
  const comFilas = facturasPag.filter(f=>f.pagado && vendEfectivo(f)===vendSel).map(f=>{
    const cat=catDeFactura(f); const sub=subtotalUsd(f); const pct=vendEsAdmin?0:(cat?(Number(comRates[cat])||0):0);
    return { f, cat, sub, pct, com: sub*pct };
  });
  // Administración: su comisión es el 1% de las ventas totales del mes, no de sus facturas
  const comBase  = vendEsAdmin ? ventasTotalesMes*0.01 : comFilas.reduce((s,x)=>s+x.com,0);
  const comTotV  = comFilas.reduce((s,x)=>s+(Number(x.f.total_usd)||0),0);   // ventas del mes (con ISV)
  const comSubV  = comFilas.reduce((s,x)=>s+x.sub,0);                        // ventas del mes (sin ISV)
  const comFinal = vendEsAdmin ? comBase : comBase*comFactor;   // USD puro — el bono KAM (L) se muestra aparte, NUNCA se suma aquí
  const comSinCat= vendEsAdmin ? 0 : comFilas.filter(x=>!x.cat).length;
  // Ventas del mes sin vendedor efectivo (bandeja de asignación)
  const ventasSinAsignar = facturasPag.filter(f=>!vendEfectivo(f)).sort((a,b)=>(b.total_usd||0)-(a.total_usd||0));

  // ── Comisión de CUALQUIER vendedor (vista consolidada + export) — misma regla que arriba.
  //    Administración: 1% sobre las VENTAS TOTALES del mes (todas las facturas pagadas). ──
  const r2c = n => Math.round((Number(n)||0)*100)/100;
  const ADMIN_PCT = 0.01;
  const calcComDe = (v) => {
    const esAdmin = v==='Administrador';
    const filas = facturasPag.filter(f=>f.pagado && vendEfectivo(f)===v).map(f=>{
      const cat=catDeFactura(f); const sub=subtotalUsd(f);
      const p = esAdmin ? 0 : (cat?(Number(comRates[cat])||0):0);
      return { f, cat, sub, pct:p, com: sub*p };
    });
    // Administración: su comisión NO sale de sus facturas — es el 1% de TODO lo vendido en el mes
    const base  = esAdmin ? ventasTotalesMes*ADMIN_PCT : filas.reduce((s,x)=>s+x.com,0);
    const real  = cobros.filter(c=>vendCobro(c)===v).reduce((s,c)=>s+(c.total_usd||c.doc_total||0),0);
    const cumpl = comMeta>0 ? real/comMeta : 0;
    const factor= (esAdmin || esKam(v)) ? 1 : (cumpl>=comMetaMin ? 1 : Math.max(0, 1-(comMetaMin-cumpl)));
    const bono  = esKam(v) ? (Number(bonos[v])||0) : 0;
    // final SIEMPRE en USD (solo comisión × factor). El bono KAM es en LEMPIRAS y se
    // muestra aparte — sumarlo aquí mezclaba monedas e inflaba el pago ~25× (bug auditoría G).
    return { v, filas, base, real, cumpl, factor, bono, final: base*factor, kam: esKam(v), admin: esAdmin,
             sub: filas.reduce((s,x)=>s+x.sub,0), tot: filas.reduce((s,x)=>s+(Number(x.f.total_usd)||0),0) };
  };
  // Detalle de TODAS las facturas pagadas del mes (con su vendedor efectivo)
  const comDetalleMes = () => facturasPag.filter(f=>f.pagado).map(f=>{
    const v=vendEfectivo(f); const cat=catDeFactura(f); const sub=subtotalUsd(f);
    const p = v && v!=='Administrador' && cat ? (Number(comRates[cat])||0) : 0;
    return { f, v, cat, sub, pct:p, com: sub*p };
  }).sort((a,b)=>(a.v||'zzz').localeCompare(b.v||'zzz') || (b.sub-a.sub));

  // Exporta las comisiones del MES SELECCIONADO (detalle + resumen por vendedor)
  const exportarComisiones = (formato) => {
    try {
      const det = comDetalleMes();
      if (!det.length) { toast(`No hay facturas pagadas en ${mes}.`); return; }
      const cols = ['Factura','Empresa','Fecha','Cliente','Vendedor','Categoría','Total USD','ISV USD','Subtotal sin ISV','% Comisión','Comisión base USD'];
      const rows = det.map(x=>({ 'Factura':x.f.doc_num, 'Empresa':x.f.empresa_id, 'Fecha':x.f.doc_date, 'Cliente':x.f.card_name,
        'Vendedor':x.v||'SIN ASIGNAR', 'Categoría':x.cat||'sin categoría', 'Total USD':r2c(x.f.total_usd),
        'ISV USD':r2c((x.f.total_usd||0)-x.sub), 'Subtotal sin ISV':r2c(x.sub),
        '% Comisión':x.pct?+(x.pct*100).toFixed(2):0, 'Comisión base USD':r2c(x.com) }));
      const lista = vendedores.map(calcComDe).filter(c=>c.filas.length||c.final>0);
      const adm = calcComDe('Administrador');
      const sa = det.filter(x=>!x.v);
      rows.push({});
      rows.push({ 'Factura':'══ RESUMEN POR VENDEDOR ══', 'Cliente':'# Facturas', 'Total USD':'Total ventas', 'Subtotal sin ISV':'Subtotal', '% Comisión':'Factor %', 'Comisión base USD':'A PAGAR USD' });
      let totPagar = 0;
      lista.forEach(c=>{
        totPagar += c.final;
        rows.push({ 'Factura':c.v + (c.kam?' (KAM)':''), 'Cliente':c.filas.length, 'Total USD':r2c(c.tot), 'Subtotal sin ISV':r2c(c.sub),
          '% Comisión':Math.round(c.factor*100), 'Comisión base USD':r2c(c.final) });
      });
      if (adm.final>0 || adm.filas.length) { totPagar += adm.final;
        rows.push({ 'Factura':'Administración (1% de ventas del mes)', 'Cliente':adm.filas.length, 'Total USD':r2c(adm.tot), 'Subtotal sin ISV':r2c(adm.sub), 'Comisión base USD':r2c(adm.final) }); }
      if (sa.length) rows.push({ 'Factura':'⚠ SIN ASIGNAR', 'Cliente':sa.length, 'Total USD':r2c(sa.reduce((s,x)=>s+(Number(x.f.total_usd)||0),0)), 'Subtotal sin ISV':r2c(sa.reduce((s,x)=>s+x.sub,0)), 'Comisión base USD':0 });
      rows.push({ 'Factura':'TOTAL A PAGAR', 'Comisión base USD':r2c(totPagar) });
      const bajar = (contenido, mime, nombre) => {
        const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([contenido],{type:mime})), download: nombre });
        document.body.appendChild(a); a.click(); a.remove();
      };
      if (formato==='xlsx') {
        bajar(buildXlsxComisiones({ mes, lista, adm, sinAsig: sa }), 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', `comisiones_${mes}.xlsx`);
      } else {
        const esc = v => { const s=String(v??''); return /[",\n;]/.test(s) ? '"'+s.replace(/"/g,'""')+'"' : s; };
        const csv = [cols.join(','), ...rows.map(r=>cols.map(c=>esc(r[c])).join(','))].join('\n');
        bajar('﻿'+csv, 'text/csv;charset=utf-8;', `comisiones_${mes}.csv`);
      }
      setComExp(false);
      toast(`Comisiones de ${mes} exportadas ✅`);
    } catch(e){ toast('Error al exportar: '+(e?.message||e)); }
  };

  // Snapshot de la cuenta clave del KAM (facturación mes actual vs anterior + saldo)
  useEffect(() => {
    const match = vendKam ? kamCuentas[vendSel] : null;
    if (!(tab==='comisiones' && match)) { setKtaSnap(null); return; }
    let vivo = true;
    (async () => {
      const prevMes = (() => { const d=new Date(yy, mm-2, 1); return `${d.getFullYear()}-${pad(d.getMonth()+1)}`; })();
      const pIni=`${prevMes}-01`, pFin=`${prevMes}-${pad(new Date(Number(prevMes.split('-')[0]),Number(prevMes.split('-')[1]),0).getDate())}`;
      const [{data:cur},{data:prev},{data:pend}] = await Promise.all([
        supabase.from('facturas_sap').select('total_usd').ilike('card_name',`%${match}%`).gte('doc_date',mesIni).lte('doc_date',mesFin),
        supabase.from('facturas_sap').select('total_usd').ilike('card_name',`%${match}%`).gte('doc_date',pIni).lte('doc_date',pFin),
        supabase.from('facturas_sap').select('saldo_usd').ilike('card_name',`%${match}%`).eq('pagado',false),
      ]);
      if(!vivo) return;
      setKtaSnap({
        cliente: match,
        mesAct: (cur||[]).reduce((s,r)=>s+(r.total_usd||0),0),
        mesAnt: (prev||[]).reduce((s,r)=>s+(r.total_usd||0),0),
        pendiente: (pend||[]).reduce((s,r)=>s+(r.saldo_usd||0),0),
      });
    })();
    return ()=>{ vivo=false; };
  }, [tab, vendSel, mes]); // eslint-disable-line

  // Bono KAM calculado en el backend (retención/crecimiento/satisfacción/actividad).
  // Mismo cálculo que el WhatsApp → una sola fuente de verdad. Solo mes en curso.
  useEffect(() => {
    if (!(tab==='comisiones' && vendKam && vendSel)) { setKamBono(null); return; }
    let vivo = true; setKamBono(null);
    fetch(`/api/report?kam=${encodeURIComponent(vendSel)}`)
      .then(r=>r.json()).then(d=>{ if(vivo && d && d.ok) setKamBono(d); }).catch(()=>{});
    return ()=>{ vivo=false; };
  }, [tab, vendSel]); // eslint-disable-line

  // ── PRIMERA OLA 360: Pace / Proyección + Leaderboard (todo con datos ya cargados) ──
  const diasMes  = new Date(yy, mm, 0).getDate();
  const esMesActual = mes === `${hoy.getFullYear()}-${pad(hoy.getMonth()+1)}`;
  const diaHoy   = esMesActual ? hoy.getDate() : diasMes;
  const fracMes  = Math.max(0.03, diaHoy / diasMes);          // fracción del mes transcurrida
  const diasRest = Math.max(0, diasMes - diaHoy);
  // Cobrado real por vendedor (dueño del cobro) — misma fuente que la conciliación
  const cobradoPorVend = {};
  cobros.forEach(c=>{ const v=vendCobro(c); if(v) cobradoPorVend[v]=(cobradoPorVend[v]||0)+(c.total_usd||c.doc_total||0); });
  // Stats por vendedor para Panel + Equipo (KAM van con su propia lógica, sin meta)
  const statsVend = vendedores.map(v=>{
    const cobrado = cobradoPorVend[v] || 0;
    const meta    = metaDe(v);
    const proy    = cobrado / fracMes;                        // run-rate a fin de mes
    const pctMeta = meta>0 ? cobrado/meta : null;
    const pctProy = meta>0 ? proy/meta   : null;
    const faltaDia= (meta>0 && diasRest>0) ? Math.max(0, meta-cobrado)/diasRest : 0;
    return { v, kam:esKam(v), cobrado, meta, proy, pctMeta, pctProy, faltaDia, com: calcComDe(v).final };
  }).sort((a,b)=>((b.pctMeta??-1)-(a.pctMeta??-1)) || (b.cobrado-a.cobrado));
  const equipoCobrado = statsVend.reduce((s,x)=>s+x.cobrado,0);
  const equipoMeta    = statsVend.reduce((s,x)=>s+x.meta,0);
  const equipoProy    = equipoCobrado / fracMes;
  const equipoCom     = statsVend.reduce((s,x)=>s+x.com,0);
  const equipoPctProy = equipoMeta>0 ? equipoProy/equipoMeta : null;
  // Alertas accionables derivadas (los que no van a llegar)
  const alertasPace = statsVend
    .filter(x=>!x.kam && x.meta>0 && x.pctProy!=null && x.pctProy<0.95)
    .map(x=>({ v:x.v, pctProy:x.pctProy, faltaDia:x.faltaDia,
      sev: x.pctProy<0.8 ? 'r' : 'w' }));
  const paceTone = p => p==null ? C.t3 : p>=1 ? C.greenText : p>=0.9 ? C.orangeText : C.redText;
  const paceBg   = p => p==null ? C.blue  : p>=1 ? C.green    : p>=0.9 ? C.orange    : C.red;

  return (
    <div style={{flex:1,overflowY:'auto',height:'100%',padding:'18px 22px',background:C.bg1}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,letterSpacing:'-0.015em',color:C.t1}}>Metas & Revisión</div>
          <div style={{fontSize:12,color:C.t3,marginTop:2}}>Meta global mensual · cumplimiento real (cobros SAP) · KAM sin meta · conciliación</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <input type="month" value={mes} onChange={e=>setMes(e.target.value)}
            style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'6px 10px',color:C.t1,fontSize:13,outline:'none'}}/>
          <button className="radar-btn" onClick={cargar} title="Refrescar" style={{width:34,height:34,display:'inline-flex',alignItems:'center',justifyContent:'center',borderRadius:R.md,border:`1px solid ${C.border}`,background:C.bg2,color:C.t3,cursor:'pointer',boxShadow:SH.xs}}><CIcon d={CI.refresh} size={15}/></button>
        </div>
      </div>

      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
        {/* Una sola pestaña "Bonos" (constructor + asistente adentro). "Meta mensual"
            en vez de "Mi desglose" y "Semanal" en vez de "Forecast semana". */}
        <Seg value={tab==='proyeccion'?'proyeccion':['comisiones'].includes(tab)?'comisiones':(tab==='bonos'||tab==='bono_asist')?'bonos':'resumen'} onChange={setTab}
          items={[['resumen','Metas',CI.target],['proyeccion','Proyección',CI.chart],['comisiones','Comisiones',CI.cash],...((usuario.rol==='admin'||/^claudia/i.test(usuario.nombre||''))?[['bonos','Bonos',CI.checkGoal]]:[])]}/>
        {/* Las vistas viejas (definir metas, semanal, hoy, sin asignar...) viven
            DENTRO del detalle; si el usuario esta en una, puede volver: */}
        {!['resumen','proyeccion','comisiones','bonos','bono_asist'].includes(tab) && (
          <button onClick={()=>setTab('resumen')}
            style={{height:32,padding:'0 14px',borderRadius:R.md,border:`1px solid ${C.primaryBorder}`,background:C.primaryTint,color:C.primary,fontSize:12,fontWeight:700,cursor:'pointer'}}>
            ← Volver a Metas
          </button>
        )}
      </div>

      {loading ? <div style={{padding:20}}><SkelCards n={4}/><SkelRows n={6}/></div> : tab==='proyeccion' ? (
        <ProyeccionSemanal usuario={usuario} empresaActiva={empresaActiva} vendedores={vendedores} metaDe={metaDe} esKam={esKam} cobros={cobros} facturasPag={facturasPag} vendCobro={vendCobro} />
      ) : tab==='resumen' ? (
        (() => {
          // ── Metas consolidado (mockup aprobado): rail del equipo + detalle ──
          const diasMes = new Date(yy, mm, 0).getDate();
          const esMesActual = mes === hoyISO.slice(0,7);
          const diasTrans = esMesActual ? Math.max(1, Number(hoyISO.slice(8,10))) : diasMes;
          const cobradoDe = (v) => cobros.filter(c=>vendCobro(c)===v).reduce((s,c)=>s+(Number(c.total_usd)||0),0);
          const proyDe = (cob) => esMesActual ? Math.round(cob/diasTrans*diasMes) : Math.round(cob);
          const filasV = vendedores.map(v => {
            const meta = metaDe(v); const cob = cobradoDe(v);
            return { v, meta, cob, pct: meta>0 ? Math.round(cob/meta*100) : null, proy: proyDe(cob) };
          });
          const eqMeta = filasV.reduce((s,f)=>s+f.meta,0);
          const eqCob  = filasV.reduce((s,f)=>s+f.cob,0);
          const eqPct  = eqMeta>0 ? Math.round(eqCob/eqMeta*100) : 0;
          const dolar  = (n) => `$${Math.round(n||0).toLocaleString('en-US')}`;
          const tonoPct = (p) => p==null ? {bg:C.bg3,tx:C.t4} : p>=70 ? {bg:C.greenTint,tx:C.greenText} : p>=30 ? {bg:C.orangeTint,tx:C.orangeText} : {bg:C.redTint,tx:C.redText};
          const sel = filasV.find(f=>f.v===vendSel) || filasV[0];
          const facturadoSel = opps.filter(o=>o.vendedor===sel?.v).reduce((s,o)=>s+(Number(o.valor)||0),0);
          const porCobrarSel = Math.max(0, facturadoSel - (sel?.cob||0));
          const misFc = fcSem.filter(f=>f.vendedor===sel?.v && f.semana_inicio===lunes && (fcVista==='hoy' ? f.tipo==='hoy' : f.tipo!=='hoy'));
          const fcCerrados = misFc.filter(f=>f.estado==='cerrado');
          const fcMonto = misFc.reduce((s,f)=>s+(Number(f.monto_estimado)||0),0);
          const cerradoCat = {};
          opps.filter(o=>o.vendedor===sel?.v).forEach(o=>{ const c=o.linea_negocio||'Sin categoría'; cerradoCat[c]=(cerradoCat[c]||0)+(Number(o.valor)||0); });
          const catsSel = [...new Set([...Object.keys(cerradoCat), ...Object.keys(metasCat[sel?.v]||{})])]
            .map(c=>({ c, cerrado:cerradoCat[c]||0, meta:Number((metasCat[sel?.v]||{})[c])||0 }))
            .sort((a,b)=>b.cerrado-a.cerrado).slice(0,6);
          const maxCat = Math.max(1, ...catsSel.map(x=>Math.max(x.cerrado,x.meta)));
          const CAT_COLS = ['#0F766E','#1D4ED8','#D97706','#DB2777','#7C3AED','#65748B'];
          const Rail = ({f, esEquipo, activo, onClick}) => {
            const escala = Math.max(f.meta, f.proy, f.cob, 1) * 1.06;
            const t = tonoPct(f.pct);
            return (
              <div onClick={onClick} style={{borderRadius:12,padding:'12px 14px',cursor:'pointer',boxShadow:SH.xs,
                background:esEquipo?'linear-gradient(135deg,#0F766E,#0d5f59)':C.bg2,
                border:esEquipo?'none':`1px solid ${C.border}`,
                outline:activo?`2px solid ${C.primary}`:'none',outlineOffset:1,color:esEquipo?'#fff':C.t1}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline'}}>
                  <span style={{fontSize:13,fontWeight:800}}>{f.nombre}</span>
                  <span className="radar-num" style={{fontSize:12,fontWeight:800,borderRadius:999,padding:'2px 9px',
                    background:esEquipo?'rgba(255,255,255,.18)':t.bg,color:esEquipo?'#fff':t.tx}}>{f.pct==null?'KAM':`${f.pct}%`}</span>
                </div>
                <div style={{height:7,borderRadius:4,background:esEquipo?'rgba(255,255,255,.22)':C.bg3,marginTop:9,position:'relative'}}>
                  <div style={{height:'100%',borderRadius:4,width:`${Math.min(100,(f.cob/escala)*100)}%`,background:esEquipo?'#fff':C.primary}}/>
                  {f.meta>0 && <div style={{position:'absolute',top:-3,bottom:-3,width:2,background:esEquipo?'rgba(255,255,255,.8)':C.t1,opacity:.55,left:`${Math.min(100,(f.meta/escala)*100)}%`}}/>}
                </div>
                <div className="radar-num" style={{display:'flex',justifyContent:'space-between',fontSize:10.5,marginTop:6,color:esEquipo?'rgba(255,255,255,.75)':C.t4}}>
                  <span>{dolar(f.cob)} de {f.meta>0?dolar(f.meta):'—'}</span>
                  <span>proy. {dolar(f.proy)}</span>
                </div>
              </div>
            );
          };
          return (
            <div style={{display:'grid',gridTemplateColumns:'minmax(250px,300px) 1fr',gap:16,alignItems:'start'}}>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {esGerencia && <Rail esEquipo activo={false} onClick={()=>setTab('equipo')}
                  f={{nombre:'Todo el equipo', pct:eqPct, cob:eqCob, meta:eqMeta, proy:proyDe(eqCob)}}/>}
                {filasV.map(f => <Rail key={f.v} f={{...f, nombre:f.v}} activo={f.v===sel?.v} onClick={()=>setVendSel(f.v)}/>)}
              </div>

              {sel && (
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:SH.xs,padding:'18px 20px'}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:14,flexWrap:'wrap'}}>
                  <div>
                    <div style={{fontSize:17,fontWeight:800,color:C.t1}}>{sel.v}</div>
                    <div style={{fontSize:11,color:C.t4,marginTop:2}}>
                      cumplimiento real según cobros de SAP
                      {esGerencia && <button onClick={()=>setTab('metas')}
                        style={{marginLeft:8,border:`1px solid ${C.border}`,background:C.bg1,borderRadius:6,fontSize:10,fontWeight:700,padding:'3px 9px',cursor:'pointer',color:C.t3}}>✎ Editar metas</button>}
                    </div>
                  </div>
                  <div style={{marginLeft:'auto',textAlign:'right'}}>
                    <div className="radar-num" style={{fontSize:30,fontWeight:800,letterSpacing:'-0.03em',color:C.t1}}>{dolar(sel.cob)}</div>
                    <div style={{fontSize:11,color:C.t4}}>cobrado real (SAP){sel.meta>0?` · ${sel.pct}% de ${dolar(sel.meta)}`:' · KAM (sin meta)'}</div>
                  </div>
                </div>

                {sel.meta>0 && (() => { const esc = Math.max(sel.meta, sel.proy, sel.cob, 1)*1.06; return (<>
                  <div style={{height:14,borderRadius:8,background:C.bg3,position:'relative',margin:'14px 0 6px'}}>
                    <div style={{height:'100%',borderRadius:8,width:`${Math.min(100,(sel.cob/esc)*100)}%`,background:'linear-gradient(90deg,#0F766E,#14b8a6)'}}/>
                    <div style={{position:'absolute',top:-4,bottom:-4,width:2.5,background:C.t1,left:`${Math.min(100,(sel.meta/esc)*100)}%`}}/>
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:10.5,color:C.t4,marginBottom:12}}>
                    <span>día {diasTrans} de {diasMes}</span>
                    <span>proyección a fin de mes: <b className="radar-num" style={{color:sel.proy>=sel.meta?C.greenText:C.orangeText}}>{dolar(sel.proy)} · {Math.round(sel.proy/sel.meta*100)}%</b></span>
                    <span>meta {dolar(sel.meta)}</span>
                  </div>
                </>); })()}

                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10,margin:'12px 0'}}>
                  {[['Ventas cerradas (RADAR)', dolar(facturadoSel), C.t1],
                    ['Cobrado (SAP)', dolar(sel.cob), C.greenText],
                    ['Por cobrar', dolar(porCobrarSel), C.orangeText],
                    ['Comisión proy.', sel.v===vendSel ? fmt2(comFinal) : '—', C.t1]].map(([l,v,c])=>(
                    <div key={l} style={{border:`1px solid ${C.border}`,borderRadius:11,padding:'10px 13px'}}>
                      <div style={{fontSize:9.5,fontWeight:800,letterSpacing:'.06em',textTransform:'uppercase',color:C.t4}}>{l}</div>
                      <div className="radar-num" style={{fontSize:18,fontWeight:800,marginTop:2,color:c}}>{v}</div>
                    </div>
                  ))}
                </div>

                {/* Compromisos: lo que era "Semanal" y "Hoy" vive aca */}
                <div style={{border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',marginTop:4}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,padding:'11px 14px',background:C.bg1,borderBottom:`1px solid ${C.border}`,flexWrap:'wrap'}}>
                    <span style={{fontSize:11,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t3}}>Compromisos</span>
                    <span className="radar-num" style={{fontSize:11,color:C.t4}}>{fcCerrados.length} de {misFc.length} cerrados · {dolar(fcMonto)} comprometido</span>
                    <div style={{display:'inline-flex',background:C.bg3,borderRadius:8,padding:2,marginLeft:'auto'}}>
                      {[['semana','Esta semana'],['hoy','Hoy']].map(([v,l])=>(
                        <button key={v} onClick={()=>setFcVista(v)}
                          style={{height:26,padding:'0 13px',border:'none',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',
                            background:fcVista===v?C.bg2:'transparent',color:fcVista===v?C.t1:C.t4,boxShadow:fcVista===v?SH.xs:'none'}}>{l}</button>
                      ))}
                    </div>
                    <button onClick={()=>setTab(fcVista==='hoy'?'hoy':'forecast')}
                      style={{background:C.primary,color:'#fff',border:'none',borderRadius:7,height:28,padding:'0 13px',fontSize:11.5,fontWeight:700,cursor:'pointer'}}>+ Agregar</button>
                  </div>
                  {misFc.length===0 && <div style={{padding:'16px 14px',fontSize:12.5,color:C.t4}}>Sin compromisos {fcVista==='hoy'?'para hoy':'esta semana'} — agregá el primero.</div>}
                  {misFc.map((f,i)=>(
                    <div key={f.id||i} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderBottom:i<misFc.length-1?`1px solid ${C.bg3}`:'none',fontSize:12.5}}>
                      <span style={{fontSize:10,fontWeight:800,borderRadius:999,padding:'2px 9px',
                        background:f.estado==='cerrado'?C.greenTint:C.orangeTint,color:f.estado==='cerrado'?C.greenText:C.orangeText}}>{f.estado==='cerrado'?'cerrado':'pendiente'}</span>
                      <span style={{color:C.t1,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>{f.cliente}{f.categoria?` — ${f.categoria}`:''}</span>
                      <span className="radar-num" style={{fontWeight:800,color:C.t1,whiteSpace:'nowrap'}}>{dolar(f.monto_real!=null?f.monto_real:f.monto_estimado)}</span>
                      <button title="Editar monto" onClick={()=>editarPromesa(f)}
                        style={{background:`${C.blue}15`,color:C.blue,border:'none',borderRadius:6,padding:'3px 7px',fontSize:11,fontWeight:700,cursor:'pointer'}}>✏️</button>
                      <button title="Eliminar promesa" onClick={()=>eliminarPromesa(f)}
                        style={{background:`${C.red}15`,color:C.red,border:'none',borderRadius:6,padding:'3px 7px',fontSize:11,fontWeight:700,cursor:'pointer'}}>🗑</button>
                    </div>
                  ))}
                </div>

                {/* Por linea de negocio */}
                {catsSel.length>0 && (
                  <div style={{border:`1px solid ${C.border}`,borderRadius:12,padding:'13px 15px',marginTop:14}}>
                    <div style={{fontSize:11,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t3,marginBottom:10}}>Por línea de negocio — cerrado vs meta repartida</div>
                    {catsSel.map((x,i)=>(
                      <div key={x.c} style={{display:'flex',alignItems:'center',gap:10,padding:'5px 0'}}>
                        <span style={{width:170,fontSize:12,fontWeight:700,color:C.t2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{x.c}</span>
                        <div style={{flex:1,height:8,background:C.bg3,borderRadius:5,overflow:'hidden'}}>
                          <div style={{height:'100%',borderRadius:5,width:`${Math.min(100,(x.cerrado/maxCat)*100)}%`,background:CAT_COLS[i%CAT_COLS.length]}}/>
                        </div>
                        <span className="radar-num" style={{width:130,textAlign:'right',fontSize:11.5,color:C.t3}}>{dolar(x.cerrado)}{x.meta>0?` / ${dolar(x.meta)}`:''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}
            </div>
          );
        })()
      ) : tab==='panel' ? (
        // ════ PANEL: pace/proyección del equipo + alertas accionables ════
        (() => {
          const SB = 1.3; // tope de escala del bullet (130% de meta)
          const posOf = p => Math.min(100, ((p||0)/SB)*100);
          const kpis = [
            ['Cobrado del mes', fmt(Math.round(equipoCobrado)), C.t1, `${statsVend.length} vendedores · día ${diaHoy}/${diasMes}`],
            ['Meta del mes', fmt(Math.round(equipoMeta)), C.t2, 'suma de metas del equipo'],
            ['Proyección a fin de mes', fmt(Math.round(equipoProy)), paceTone(equipoPctProy), equipoPctProy!=null?`${Math.round(equipoPctProy*100)}% de meta · faltan ${diasRest} días`:'sin meta'],
            ['Comisión proyectada', fmt(Math.round(equipoCom)), C.green, 'equipo completo · mes en curso'],
          ];
          return (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:12,marginBottom:18}}>
              {kpis.map(([l,v,col,sub])=>(
                <div key={l} className="radar-kpi" style={{background:C.bg2,border:`1px solid ${col===paceTone(equipoPctProy)&&equipoPctProy!=null&&equipoPctProy<1?C.orangeBorder:C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',boxShadow:SH.sm}}>
                  <div style={{fontSize:11,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.07em'}}>{l}</div>
                  <div className="radar-num" style={{fontSize:25,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,marginTop:8,color:col===C.t2?C.t1:col}}>{v}</div>
                  <div style={{fontSize:11,color:C.t4,marginTop:6}}>{sub}</div>
                </div>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1.5fr 1fr',gap:14,alignItems:'start'}}>
              {/* Pace bullet grid */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:16,boxShadow:SH.sm}}>
                <div style={{display:'flex',alignItems:'baseline',gap:10,marginBottom:6}}>
                  <div style={{fontSize:14,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>¿Vamos a llegar?</div>
                  <div style={{fontSize:11.5,color:C.t3}}>real · proyección · meta, por vendedor</div>
                </div>
                {statsVend.map((s,i)=>(
                  <div key={s.v} style={{display:'grid',gridTemplateColumns:'150px 1fr 128px',alignItems:'center',gap:12,padding:'11px 0',borderTop:i?`1px solid ${C.border}`:'none'}}>
                    <div style={{display:'flex',alignItems:'center',gap:9,minWidth:0}}>
                      <div style={{width:28,height:28,borderRadius:'50%',background:C.primaryTint,color:C.blue,display:'grid',placeItems:'center',fontSize:10,fontWeight:700,flexShrink:0}}>{s.v.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}</div>
                      <div style={{minWidth:0}}><div style={{fontSize:12.5,fontWeight:600,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{s.v.split(' ')[0]} {s.v.split(' ')[1]?.[0]||''}{s.kam&&<span style={{fontSize:8.5,fontWeight:700,color:'#B45309',background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:R.pill,padding:'1px 5px',marginLeft:5}}>KAM</span>}</div></div>
                    </div>
                    {s.kam ? (
                      <div style={{fontSize:11.5,color:C.t3}}>Sin meta — se mide por bono cuenta clave</div>
                    ) : (
                      <div style={{position:'relative',height:24}}>
                        <div style={{position:'absolute',left:0,right:0,top:7,height:11,background:C.bg3,borderRadius:R.pill}}/>
                        <div style={{position:'absolute',left:0,top:7,height:11,width:`${posOf(s.pctMeta)}%`,background:C.blue,borderRadius:R.pill}}/>
                        <div title="proyección" style={{position:'absolute',top:3,left:`calc(${posOf(s.pctProy)}% - 1.5px)`,height:18,width:3,background:paceBg(s.pctProy),borderRadius:2}}/>
                        <div title="meta" style={{position:'absolute',top:1,left:`calc(${posOf(1)}% - 1px)`,height:22,width:2,background:C.t1,borderRadius:2}}/>
                      </div>
                    )}
                    <div style={{textAlign:'right'}}>
                      <div className="radar-num" style={{fontSize:13,fontWeight:700,color:C.t1}}>{fmt(Math.round(s.cobrado))}</div>
                      {!s.kam && s.pctProy!=null && <div className="radar-num" style={{fontSize:11,fontWeight:700,color:paceTone(s.pctProy),marginTop:1}}>proy. {Math.round(s.pctProy*100)}% {s.pctProy>=1?'✓':s.pctProy<0.8?'✕':''}</div>}
                    </div>
                  </div>
                ))}
                {!statsVend.length && <div style={{padding:20,textAlign:'center',color:C.t4,fontSize:13}}>Sin vendedores con datos este mes.</div>}
                <div style={{display:'flex',gap:16,fontSize:11,color:C.t3,marginTop:12,paddingTop:10,borderTop:`1px solid ${C.border}`}}>
                  <span><span style={{display:'inline-block',width:14,height:8,borderRadius:4,background:C.blue,marginRight:6,verticalAlign:'middle'}}/>Real cobrado</span>
                  <span><span style={{display:'inline-block',width:3,height:12,background:C.t2,marginRight:6,verticalAlign:'middle'}}/>Proyección</span>
                  <span><span style={{display:'inline-block',width:3,height:12,background:C.t1,marginRight:6,verticalAlign:'middle'}}/>Meta</span>
                </div>
              </div>
              {/* Alertas accionables */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:16,boxShadow:SH.sm}}>
                <div style={{fontSize:14,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,marginBottom:12,display:'flex',alignItems:'center',gap:8}}>Requiere acción {alertasPace.length>0&&<span style={{fontSize:10,fontWeight:700,background:C.red,color:'#fff',borderRadius:R.pill,padding:'1px 7px'}}>{alertasPace.length}</span>}</div>
                {alertasPace.length===0 && <div style={{padding:'20px 4px',textAlign:'center',color:C.t4,fontSize:12.5,display:'flex',flexDirection:'column',alignItems:'center',gap:8}}><span style={{color:C.green,display:'inline-flex'}}><CIcon d={CI.checkGoal} size={22}/></span>Todo el equipo proyecta ≥95% de meta.</div>}
                {alertasPace.map(a=>(
                  <div key={a.v} style={{display:'flex',alignItems:'flex-start',gap:11,padding:'11px 12px',borderRadius:R.md,border:`1px solid ${a.sev==='r'?C.redBorder:C.orangeBorder}`,background:a.sev==='r'?C.redTint:C.orangeTint,marginBottom:9}}>
                    <span style={{width:28,height:28,borderRadius:8,flexShrink:0,display:'grid',placeItems:'center',background:C.bg2,color:a.sev==='r'?C.red:C.orange}}><CIcon d={CI.alert} size={15}/></span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12.5,fontWeight:600,color:C.t1}}>{a.v.split(' ')[0]} proyecta {Math.round(a.pctProy*100)}% de meta</div>
                      <div style={{fontSize:11,color:C.t3,marginTop:2}}>{diasRest>0?`Necesita cerrar ${fmt(Math.round(a.faltaDia))}/día los ${diasRest} días restantes.`:'Mes cerrado — no alcanzó la meta.'}</div>
                    </div>
                    <button className="radar-btn" onClick={()=>{ setVendSel(a.v); setTab('revision'); }} style={{fontSize:11.5,fontWeight:600,color:C.blue,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:R.md,padding:'6px 11px',cursor:'pointer',whiteSpace:'nowrap'}}>Ver</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          );
        })()
      ) : tab==='equipo' ? (
        // ════ EQUIPO: leaderboard del equipo ════
        <div>
          <div style={{display:'flex',alignItems:'baseline',gap:10,marginBottom:12}}>
            <div style={{fontSize:15,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>Leaderboard del equipo</div>
            <div style={{fontSize:11.5,color:C.t3}}>{mes} · ordenado por % de meta</div>
          </div>
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'auto',boxShadow:SH.sm}}>
            <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',minWidth:760}}>
              <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                {['#','Vendedor','% Meta','Cobrado','Progreso','Proyección','Comisión'].map((h,i)=>(
                  <th key={h} style={{padding:'10px 12px',textAlign:i>=2?'right':'left',fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {statsVend.filter(s=>!s.kam).map((s,i)=>{
                  const medal = i===0?{bg:'#FEF3C7',c:'#B45309'}:i===1?{bg:'#F1F5F9',c:'#64748B'}:i===2?{bg:'#FDE7D3',c:'#C2410C'}:{bg:C.bg3,c:C.t2};
                  const pc = s.pctMeta==null?C.t4:s.pctMeta>=1?C.greenText:s.pctMeta>=0.85?C.orangeText:C.redText;
                  const pb = s.pctMeta==null?C.t4:s.pctMeta>=1?C.green:s.pctMeta>=0.85?C.orange:C.red;
                  return (
                    <tr key={s.v} className="radar-hrow" style={{borderBottom:`1px solid ${C.bg3}`,cursor:'pointer'}} onClick={()=>{ setVendSel(s.v); setTab('revision'); }}>
                      <td style={{padding:'10px 12px'}}><span style={{display:'inline-grid',placeItems:'center',width:24,height:24,borderRadius:7,fontSize:12,fontWeight:800,background:medal.bg,color:medal.c}}>{i+1}</span></td>
                      <td style={{padding:'10px 12px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:9}}>
                          <div style={{width:28,height:28,borderRadius:'50%',background:C.primaryTint,color:C.blue,display:'grid',placeItems:'center',fontSize:10,fontWeight:700}}>{s.v.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}</div>
                          <div><div style={{fontSize:13,fontWeight:600,color:C.t1}}>{s.v}</div></div>
                        </div>
                      </td>
                      <td className="radar-num" style={{padding:'10px 12px',textAlign:'right'}}><span style={{fontSize:11.5,fontWeight:700,color:pc,background:s.pctMeta>=1?C.greenTint:s.pctMeta>=0.85?C.orangeTint:C.redTint,borderRadius:R.pill,padding:'2px 9px'}}>{s.pctMeta!=null?`${Math.round(s.pctMeta*100)}%`:'—'}</span></td>
                      <td className="radar-num" style={{padding:'10px 12px',textAlign:'right',fontSize:13,fontWeight:700,color:C.t1}}>{fmt(Math.round(s.cobrado))}</td>
                      <td style={{padding:'10px 12px',textAlign:'right'}}><span style={{display:'inline-block',width:90,height:7,background:C.bg3,borderRadius:R.pill,overflow:'hidden',verticalAlign:'middle'}}><span style={{display:'block',height:'100%',width:`${Math.min(100,(s.pctMeta||0)*100)}%`,background:pb,borderRadius:R.pill}}/></span></td>
                      <td className="radar-num" style={{padding:'10px 12px',textAlign:'right',fontSize:12.5,fontWeight:600,color:paceTone(s.pctProy)}}>{s.pctProy!=null?`${Math.round(s.pctProy*100)}%`:'—'}</td>
                      <td className="radar-num" style={{padding:'10px 12px',textAlign:'right',fontSize:13,fontWeight:700,color:C.green}}>{fmt(Math.round(s.com))}</td>
                    </tr>
                  );
                })}
                {statsVend.filter(s=>!s.kam).length===0 && <tr><td colSpan={7} style={{padding:26,textAlign:'center',color:C.t4,fontSize:13}}>Sin vendedores con meta este mes.</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{fontSize:11,color:C.t4,marginTop:10,display:'flex',alignItems:'center',gap:6}}><CIcon d={CI.bulb} size={13}/>El <b>movimiento vs semana pasada</b> (▲/▼) se activa cuando corramos el snapshot semanal. Tocá una fila para ver el detalle del vendedor.</div>
        </div>
      ) : (tab==='bonos'||tab==='bono_asist') && (usuario.rol!=='admin' || subBonos==='asist') ? (
        // ════ BONOS · Asistente — se calcula solo de la operación ════
        <div>
          {usuario.rol==='admin' && (
            <div style={{display:'inline-flex',gap:2,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2,marginBottom:14}}>
              {[['planes','Planes de bono'],['asist','Bono Asistente']].map(([v,l])=>(
                <button key={v} onClick={()=>setSubBonos(v)} style={{height:26,padding:'0 12px',border:'none',borderRadius:R.sm,cursor:'pointer',fontSize:12,fontWeight:600,background:subBonos===v?C.bg2:'transparent',color:subBonos===v?C.t1:C.t3,boxShadow:subBonos===v?SH.sm:'none'}}>{l}</button>
              ))}
            </div>
          )}
          <BonoAsistente usuario={usuario}/>
        </div>
      ) : (tab==='bonos'||tab==='bono_asist') ? (
        <>
        <div style={{display:'inline-flex',gap:2,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2,marginBottom:14}}>
          {[['planes','Planes de bono'],['asist','Bono Asistente']].map(([v,l])=>(
            <button key={v} onClick={()=>setSubBonos(v)} style={{height:26,padding:'0 12px',border:'none',borderRadius:R.sm,cursor:'pointer',fontSize:12,fontWeight:600,background:subBonos===v?C.bg2:'transparent',color:subBonos===v?C.t1:C.t3,boxShadow:subBonos===v?SH.sm:'none'}}>{l}</button>
          ))}
        </div>
        {
        // ════ CONSTRUCTOR DE BONOS (Fase 2) — gerencia arma/edita bonos sin código ════
        (() => {
          const setB = (patch) => setBonoSel(s => ({ ...s, ...patch }));
          const setDim = (i, field, val) => setBonoSel(s => ({ ...s, dimensiones: s.dimensiones.map((d,j)=>j===i?{...d,[field]:val}:d) }));
          const addDim = () => setBonoSel(s => ({ ...s, dimensiones:[...s.dimensiones, {clave:'d'+(s.dimensiones.length+1), nombre:'Nueva dimensión', fuente:'cumplimiento_meta', objetivo:'', peso:0}] }));
          const delDim = (i) => setBonoSel(s => ({ ...s, dimensiones: s.dimensiones.filter((_,j)=>j!==i) }));
          const setAcc = (patch) => setBonoSel(s => ({ ...s, acelerador:{...(s.acelerador||{}), ...patch} }));
          const suma = bonoSel ? sumaPesos(bonoSel) : 0;
          const inp = { height:36, border:`1px solid ${C.border}`, borderRadius:R.md, padding:'0 11px', fontSize:13, color:C.t1, background:C.bg1, outline:'none', width:'100%', boxSizing:'border-box' };
          const lbl = { fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:5 };
          const APLICA = [['vendedores','Todos los vendedores'],['kam','KAM (cuentas clave)'],['area','Área específica'],['vendedor','Vendedor específico']];
          return (
          <div style={{display:'grid',gridTemplateColumns:'300px 1fr',gap:18,alignItems:'start'}}>
            {/* Lista de planes */}
            <div>
              {bonosCfg.length===0 && <div style={{fontSize:12.5,color:C.t4,padding:'14px 4px',lineHeight:1.5}}>Aún no hay bonos. Creá el primero o corré el SQL de siembra para traer los actuales.</div>}
              {bonosCfg.map(b=>{
                const on = bonoSel?.id===b.id;
                return (
                  <div key={b.id} onClick={()=>abrirBono(b)} style={{border:`1px solid ${on?C.primaryBorder:C.border}`,background:on?C.primaryTint:C.bg2,borderRadius:R.lg,padding:'12px 13px',marginBottom:10,cursor:'pointer',boxShadow:on?`0 0 0 3px ${C.ring}`:SH.xs}}>
                    <div style={{fontSize:13.5,fontWeight:700,color:C.t1}}>{b.nombre}</div>
                    <div style={{fontSize:11.5,color:C.t3,marginTop:3}} className="radar-num">Base L {Number(b.base).toLocaleString('es-HN')} · tope L {Number(b.tope).toLocaleString('es-HN')}</div>
                    <div style={{display:'flex',gap:6,marginTop:9,flexWrap:'wrap'}}>
                      <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:R.pill,color:b.activo?C.greenText:C.t3,background:b.activo?C.greenTint:C.bg3,border:`1px solid ${b.activo?C.greenBorder:C.border}`}}>{b.activo?'● Activo':'○ Inactivo'}</span>
                      <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:R.pill,color:C.blue,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`}}>{({vendedores:'Vendedores',kam:'KAM',area:'Área',vendedor:'Vendedor'})[b.aplica_a]||b.aplica_a}</span>
                      <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:R.pill,color:C.t3,background:C.bg3,border:`1px solid ${C.border}`,textTransform:'capitalize'}}>{b.periodo}</span>
                    </div>
                  </div>
                );
              })}
              <button className="radar-btn" onClick={()=>abrirBono(null)} style={{width:'100%',border:`1.5px dashed ${C.border2}`,background:'transparent',borderRadius:R.lg,padding:12,color:C.t3,fontWeight:600,fontSize:12.5,display:'flex',alignItems:'center',justifyContent:'center',gap:7,cursor:'pointer'}}><CIcon d={CI.plus} size={15}/>Nuevo bono</button>
            </div>

            {/* Editor */}
            {!bonoSel ? (
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.xl,padding:'48px 24px',textAlign:'center',color:C.t4,fontSize:13.5,boxShadow:SH.sm}}>Elegí un bono de la izquierda para editarlo, o creá uno nuevo.</div>
            ) : (
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.xl,padding:18,boxShadow:SH.sm}}>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
                  <div style={{fontSize:15,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,flex:1}}>{bonoSel.id?'Editar bono':'Nuevo bono'}</div>
                  <span style={{fontSize:13,fontWeight:600,color:C.t2,display:'inline-flex',alignItems:'center',gap:9}}>Activo
                    <span onClick={()=>setB({activo:!bonoSel.activo})} style={{width:38,height:22,borderRadius:R.pill,background:bonoSel.activo?C.blue:C.border2,position:'relative',cursor:'pointer',transition:'background .15s'}}><span style={{position:'absolute',top:2,left:bonoSel.activo?18:2,width:18,height:18,borderRadius:'50%',background:'#fff',boxShadow:SH.sm,transition:'left .15s'}}/></span>
                  </span>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:14}}>
                  <div><label style={lbl}>Nombre</label><input style={inp} value={bonoSel.nombre} onChange={e=>setB({nombre:e.target.value})}/></div>
                  <div><label style={lbl}>Aplica a</label><select style={inp} value={bonoSel.aplica_a} onChange={e=>setB({aplica_a:e.target.value})}>{APLICA.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></div>
                  <div><label style={lbl}>Período</label><select style={inp} value={bonoSel.periodo} onChange={e=>setB({periodo:e.target.value})}><option value="mensual">Mensual</option><option value="trimestral">Trimestral</option><option value="anual">Anual</option></select></div>
                </div>
                {(bonoSel.aplica_a==='area'||bonoSel.aplica_a==='vendedor') && (
                  <div style={{marginBottom:14}}><label style={lbl}>{bonoSel.aplica_a==='area'?'Nombre del área':'Nombre del vendedor'}</label><input style={inp} value={bonoSel.aplica_valor||''} onChange={e=>setB({aplica_valor:e.target.value})} placeholder={bonoSel.aplica_a==='area'?'Servicio, Repuestos…':'Ricardo Ruiz…'}/></div>
                )}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
                  <div><label style={lbl}>Monto base (L)</label><input className="radar-num" style={inp} value={bonoSel.base} onChange={e=>setB({base:e.target.value.replace(/[^0-9.]/g,'')})}/></div>
                  <div><label style={lbl}>Tope total con acelerador (L)</label><input className="radar-num" style={inp} value={bonoSel.tope} onChange={e=>setB({tope:e.target.value.replace(/[^0-9.]/g,'')})}/></div>
                </div>

                {/* Dimensiones */}
                <div style={{display:'flex',alignItems:'baseline',gap:10,marginBottom:6}}><div style={{fontSize:13.5,fontWeight:700,color:C.t1}}>Dimensiones</div><div style={{fontSize:11.5,color:C.t3}}>cada una pesa un % del base · deben sumar 100%</div></div>
                <div style={{display:'grid',gridTemplateColumns:'1.3fr 1.4fr 1fr 82px 30px',gap:9,fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.04em',padding:'0 0 6px'}}><span>Dimensión</span><span>Fuente de datos</span><span>Objetivo</span><span style={{textAlign:'right'}}>Peso %</span><span/></div>
                {bonoSel.dimensiones.map((d,i)=>(
                  <div key={i} style={{display:'grid',gridTemplateColumns:'1.3fr 1.4fr 1fr 82px 30px',gap:9,alignItems:'center',padding:'6px 0',borderTop:`1px solid ${C.border}`}}>
                    <input style={{...inp,height:34,fontSize:12.5}} value={d.nombre} onChange={e=>setDim(i,'nombre',e.target.value)}/>
                    <select style={{...inp,height:34,fontSize:12.5}} value={d.fuente} onChange={e=>setDim(i,'fuente',e.target.value)}>{FUENTES_BONO.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select>
                    <input style={{...inp,height:34,fontSize:12.5}} value={d.objetivo||''} onChange={e=>setDim(i,'objetivo',e.target.value)} placeholder="—"/>
                    <input className="radar-num" style={{...inp,height:34,fontSize:12.5,textAlign:'right'}} value={d.peso} onChange={e=>setDim(i,'peso',Number(e.target.value.replace(/[^0-9]/g,''))||0)}/>
                    <button onClick={()=>delDim(i)} style={{width:28,height:28,borderRadius:R.md,border:`1px solid ${C.border}`,background:C.bg1,color:C.t4,cursor:'pointer'}}>×</button>
                  </div>
                ))}
                <button onClick={addDim} style={{marginTop:10,background:'none',border:'none',color:C.blue,fontWeight:600,fontSize:12.5,display:'inline-flex',alignItems:'center',gap:6,cursor:'pointer',padding:'4px 2px'}}><CIcon d={CI.plus} size={14}/>Agregar dimensión</button>
                <div style={{display:'flex',alignItems:'center',gap:9,marginTop:12,fontSize:12.5,fontWeight:600}}>
                  <span style={{color:C.t3}}>Suma de pesos</span>
                  <span style={{flex:1,height:8,background:C.bg3,borderRadius:R.pill,overflow:'hidden'}}><span style={{display:'block',height:'100%',width:`${Math.min(100,suma)}%`,background:suma===100?C.green:C.red,borderRadius:R.pill,transition:'width .2s'}}/></span>
                  <span style={{color:suma===100?C.greenText:C.redText}} className="radar-num">{suma}%{suma===100?' ✓':' (debe dar 100%)'}</span>
                </div>

                {/* Acelerador */}
                <div style={{fontSize:13.5,fontWeight:700,color:C.t1,margin:'20px 0 6px'}}>Acelerador</div>
                <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:14}}>
                  <span style={{fontSize:13,fontWeight:600,color:C.t2,display:'inline-flex',alignItems:'center',gap:9}}>
                    <span onClick={()=>setAcc({activo:!bonoSel.acelerador?.activo})} style={{width:38,height:22,borderRadius:R.pill,background:bonoSel.acelerador?.activo?C.blue:C.border2,position:'relative',cursor:'pointer'}}><span style={{position:'absolute',top:2,left:bonoSel.acelerador?.activo?18:2,width:18,height:18,borderRadius:'50%',background:'#fff',boxShadow:SH.sm,transition:'left .15s'}}/></span>
                    Premiar sobre-cumplimiento
                  </span>
                  {bonoSel.acelerador?.activo && (
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginTop:12}}>
                      <div><label style={lbl}>Monto máx (L)</label><input className="radar-num" style={inp} value={bonoSel.acelerador?.max??2000} onChange={e=>setAcc({max:Number(e.target.value.replace(/[^0-9]/g,''))||0})}/></div>
                      <div><label style={lbl}>Se activa sobre (%)</label><input className="radar-num" style={inp} value={bonoSel.acelerador?.sobre??100} onChange={e=>setAcc({sobre:Number(e.target.value.replace(/[^0-9]/g,''))||0})}/></div>
                      <div><label style={lbl}>Techo de cálculo (%)</label><input className="radar-num" style={inp} value={bonoSel.acelerador?.techo??120} onChange={e=>setAcc({techo:Number(e.target.value.replace(/[^0-9]/g,''))||0})}/></div>
                    </div>
                  )}
                </div>

                <div style={{display:'flex',gap:9,marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                  {bonoSel.id && <button onClick={()=>clonarBono(bonoSel)} className="radar-btn" style={{height:38,padding:'0 16px',borderRadius:R.md,fontSize:13,fontWeight:700,border:`1px solid ${C.border}`,background:C.bg1,color:C.t2,cursor:'pointer'}}>Clonar</button>}
                  {bonoSel.id && <button onClick={()=>borrarBonoCfg(bonoSel)} className="radar-btn" style={{height:38,padding:'0 16px',borderRadius:R.md,fontSize:13,fontWeight:700,border:`1px solid ${C.redBorder}`,background:C.redTint,color:C.redText,cursor:'pointer'}}>Borrar</button>}
                  <button onClick={guardarBonoCfg} disabled={bonoSaving} className="radar-btn" style={{marginLeft:'auto',height:38,padding:'0 18px',borderRadius:R.md,fontSize:13,fontWeight:700,border:'none',background:C.blue,color:'#fff',cursor:'pointer',boxShadow:SH.primary,display:'inline-flex',alignItems:'center',gap:7}}><CIcon d={CI.checkGoal} size={15}/>{bonoSaving?'Guardando…':'Guardar bono'}</button>
                </div>
                <div style={{fontSize:11,color:C.t4,marginTop:12,display:'flex',gap:6,alignItems:'flex-start'}}><CIcon d={CI.bulb} size={13}/>Por ahora el cálculo de pago sigue usando la fórmula actual; el siguiente paso conecta el motor a esta configuración (base, pesos, tope y acelerador). Así no cambia ningún pago sin que lo revisemos.</div>
              </div>
            )}
          </div>
          );
        })()
        }
        </>
      ) : tab==='metas' ? (
        // ════ METAS — maestro-detalle: el equipo a la izquierda, el mes del elegido a la derecha ════
        (() => {
          const LOB_PAL = ['#0B7AB8','#0F766E','#0E7490','#DB2777','#7C3AED','#C4933F','#16A34A','#B45309','#64748B'];
          const lobColor = (c) => LOB_PAL[Math.max(0, ORG.lineasNegocio.indexOf(c)) % LOB_PAL.length];
          const proyDe = (v) => ORG.lineasNegocio.reduce((s,c)=>s+(Number((metasCat[v]||{})[c])||0),0);
          const noKam  = vendedores.filter(v=>!esKam(v));
          const metaEq = noKam.reduce((s,v)=>s+metaDe(v),0);
          const proyEq = noKam.reduce((s,v)=>s+proyDe(v),0);
          const pctEq  = metaEq>0 ? Math.round(proyEq/metaEq*100) : 0;
          const sinRep = noKam.filter(v=>proyDe(v)<=0).length;
          const vSel = (metaSel && vendedores.includes(metaSel)) ? metaSel : (usuario.rol==='ventas' ? vendedores.find(x=>x===usuario.nombre) : null) || vendedores[0];
          if (!vSel) return <div style={{fontSize:13,color:C.t4}}>Sin vendedores para este mes.</div>;

          const k = esKam(vSel);
          const meta = metaDe(vSel);
          const cat = metasCat[vSel]||{};
          const proy = proyDe(vSel);
          const pctP = meta>0 ? Math.round(proy/meta*100) : 0;
          const conMonto = ORG.lineasNegocio.filter(c=>(Number(cat[c])||0)>0 || catsAbiertas[`${vSel}|${c}`]).sort((a,b)=>(Number(cat[b])||0)-(Number(cat[a])||0));
          const sinMonto = ORG.lineasNegocio.filter(c=>!conMonto.includes(c));
          const maxCat = Math.max(1, ...conMonto.map(c=>Number(cat[c])||0));
          // Geometría de la barra: la meta es una MARCA sobre la escala, y pasarse
          // se dibuja como franja de logro — no como error.
          const escala = Math.max(meta, proy) * 1.06 || 1;
          const wFill  = Math.min(proy, meta) / escala * 100;
          const wExtra = proy > meta ? (proy - meta) / escala * 100 : 0;
          const xTick  = meta / escala * 100;
          const seg = conMonto.filter(c=>(Number(cat[c])||0)>0);

          return (
            <div style={{display:'flex',gap:14,alignItems:'flex-start',flexWrap:'wrap'}}>
              {/* ── rail: el equipo de un vistazo ── */}
              <div style={{flex:'1 1 250px',maxWidth:330,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:SH.xs,overflow:'hidden'}}>
                <div style={{padding:'13px 15px',borderBottom:`1px solid ${C.border}`,background:C.primaryTint}}>
                  <div style={{fontSize:10,fontWeight:800,letterSpacing:'.08em',textTransform:'uppercase',color:C.primary}}>Equipo · {mes}</div>
                  <div className="radar-num" style={{fontSize:18,fontWeight:800,color:C.t1,marginTop:2}}>{fmt(proyEq)} <span style={{fontSize:12,color:C.t3,fontWeight:700}}>de {fmt(metaEq)}</span></div>
                  <div style={{position:'relative',height:7,background:C.bg3,borderRadius:5,marginTop:8}}>
                    <div style={{position:'absolute',inset:'0 auto 0 0',width:`${Math.min(100,pctEq)}%`,background:C.primary,borderRadius:5}}/>
                  </div>
                  <div style={{fontSize:11,color:C.t3,marginTop:5}}>{pctEq}% proyectado{sinRep>0?` · ${sinRep} sin repartir`:''}</div>
                </div>
                {vendedores.map(v=>{
                  const kv=esKam(v), pv=proyDe(v), mv=metaDe(v);
                  const pct = mv>0?Math.round(pv/mv*100):0;
                  const sel = v===vSel;
                  return (
                    <div key={v} onClick={()=>setMetaSel(v)}
                      style={{display:'flex',alignItems:'center',gap:10,padding:'11px 13px',borderBottom:`1px solid ${C.bg3}`,cursor:'pointer',
                        background:sel?C.primaryTint:'transparent',boxShadow:sel?`inset 3px 0 0 ${C.primary}`:'none'}}>
                      <AvatarChip name={v} size={30}/>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{v}</div>
                        <div className="radar-num" style={{fontSize:10.5,color:C.t4}}>{kv?'cuenta clave':`meta ${fmt(mv)}`}</div>
                      </div>
                      <span className="radar-num" style={{marginLeft:'auto',fontSize:11.5,fontWeight:800,borderRadius:999,padding:'2px 8px',
                        background:kv?'#FDF6E9':pct>=100?C.greenTint:pv>0?C.orangeTint:C.bg3,
                        color:kv?'#C4933F':pct>=100?C.greenText:pv>0?C.orangeText:C.t4}}>
                        {kv?'KAM':pv>0?`${pct}%`:'sin repartir'}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* ── panel: el mes del vendedor elegido ── */}
              <div style={{flex:'999 1 420px',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:SH.xs,padding:'18px 20px'}}>
                <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                  <AvatarChip name={vSel} size={38}/>
                  <div>
                    <div style={{fontSize:16,fontWeight:800,color:C.t1}}>{vSel}</div>
                    <div style={{fontSize:11.5,color:C.t4}}>{k?'se mide por bono de cuenta clave':`semana ≈ ${fmt(Math.round(meta/4.33))}`}</div>
                  </div>
                  <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                    {esGerencia && (
                      <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:C.t3,cursor:'pointer'}}>
                        <input type="checkbox" checked={k} onChange={()=>toggleKam(vSel)}/> KAM (sin meta)
                      </label>
                    )}
                    {!k && (
                      <div style={{display:'flex',alignItems:'center',gap:7}}>
                        <span style={{fontSize:10,fontWeight:800,letterSpacing:'.07em',textTransform:'uppercase',color:C.t4}}>Meta mensual</span>
                        {esGerencia ? (
                          <div style={{position:'relative'}}>
                            <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:13,fontWeight:700,color:C.t4,pointerEvents:'none'}}>$</span>
                            <input key={`${vSel}-${mes}-${metaDef}`} type="text" inputMode="numeric"
                              defaultValue={Number(metas[vSel]!=null?metas[vSel]:metaDef).toLocaleString('en-US')}
                              onBlur={e=>{ const n=Number(String(e.target.value).replace(/[^0-9.]/g,''))||0; if(n!==(Number(metas[vSel]!=null?metas[vSel]:metaDef)||0)) guardarMeta(vSel,n); else e.target.value=n.toLocaleString('en-US'); }}
                              style={{width:118,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 10px 8px 22px',color:C.t1,fontSize:14,fontWeight:800,outline:'none',textAlign:'right'}}/>
                          </div>
                        ) : (
                          <span className="radar-num" style={{fontSize:16,fontWeight:800,color:C.t1}}>{fmt(meta)}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {k ? (
                  <div style={{marginTop:14,fontSize:12.5,color:C.t4,fontStyle:'italic'}}>
                    Su tablero vive en Comisiones → bono cuenta clave (retención · crecimiento · satisfacción · actividad).
                  </div>
                ) : (
                  <>
                    {/* Lo proyectado, en grande y con su %: no siempre proyectan el
                        100%, y pasarse es un logro — nunca un regaño en rojo. */}
                    <div style={{display:'flex',alignItems:'baseline',gap:12,marginTop:16,flexWrap:'wrap'}}>
                      <span className="radar-num" style={{fontSize:32,fontWeight:800,letterSpacing:'-0.03em',color:proy>0?C.t1:C.t4}}>{fmt(proy)}</span>
                      <span style={{fontSize:13,fontWeight:800,borderRadius:999,padding:'4px 12px',
                        background:pctP>=100?C.greenTint:proy>0?C.orangeTint:C.bg3,
                        color:pctP>=100?C.greenText:proy>0?C.orangeText:C.t4,
                        border:`1px solid ${pctP>=100?C.greenBorder:proy>0?C.orangeBorder:C.border}`}}>
                        {proy>0?`${pctP}% de la meta`:'sin proyección'}
                      </span>
                      <span style={{fontSize:12,color:C.t4,marginLeft:'auto'}}>
                        {proy>0?`repartido en ${seg.length} línea${seg.length===1?'':'s'}`:'este vendedor todavía no reparte su mes'}
                        {proy>0&&proy<meta?` · faltan ${fmt(meta-proy)}`:''}
                      </span>
                    </div>
                    <div style={{position:'relative',height:12,background:C.bg3,borderRadius:7,margin:'12px 0 4px',overflow:'visible'}}>
                      <div style={{position:'absolute',top:0,bottom:0,left:0,width:`${wFill}%`,background:C.green,borderRadius:'7px 0 0 7px'}}/>
                      {wExtra>0 && <div style={{position:'absolute',top:0,bottom:0,left:`${wFill}%`,width:`${wExtra}%`,
                        background:'repeating-linear-gradient(45deg,#22C55E,#22C55E 5px,#86EFAC 5px,#86EFAC 10px)',borderRadius:'0 7px 7px 0'}}/>}
                      <div style={{position:'absolute',top:-4,bottom:-4,left:`${xTick}%`,width:2.5,background:C.t1,borderRadius:2}}/>
                      <div className="radar-num" style={{position:'absolute',top:-18,left:`${xTick}%`,transform:'translateX(-50%)',fontSize:9.5,fontWeight:800,color:C.t3,whiteSpace:'nowrap'}}>meta {fmt(meta)}</div>
                    </div>

                    {/* La repartición como barra apilada por línea de negocio */}
                    {seg.length>0 && (
                      <>
                        <div style={{display:'flex',height:32,borderRadius:9,overflow:'hidden',margin:'18px 0 6px',boxShadow:SH.xs}}>
                          {seg.map(c=>{
                            const w = (Number(cat[c])||0)/proy*100;
                            return (
                              <div key={c} title={`${c}: ${fmt(Number(cat[c])||0)}`}
                                style={{width:`${w}%`,background:lobColor(c),display:'flex',alignItems:'center',justifyContent:'center',
                                  color:'#fff',fontSize:10.5,fontWeight:800,whiteSpace:'nowrap',overflow:'hidden'}}>
                                {w>=12?`${c.split(' ')[0]} ${Math.round(w)}%`:''}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{display:'flex',gap:12,flexWrap:'wrap',fontSize:10.5,color:C.t3,marginBottom:12}}>
                          {seg.map(c=>(
                            <span key={c}><i style={{display:'inline-block',width:8,height:8,borderRadius:3,background:lobColor(c),marginRight:4,verticalAlign:-1}}/>{c}</span>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Solo las líneas con monto (o abiertas con chip), por peso.
                        Se acabó el muro de 9 inputs vacíos por vendedor. */}
                    {conMonto.map(c=>{
                      const m = Number(cat[c])||0;
                      return (
                        <div key={c} style={{display:'grid',gridTemplateColumns:'12px 1fr 120px 100px',gap:10,alignItems:'center',padding:'9px 4px',borderBottom:`1px solid ${C.bg3}`}}>
                          <span style={{width:10,height:10,borderRadius:3,background:lobColor(c)}}/>
                          <span style={{fontSize:13,fontWeight:600,color:C.t1}}>{c}</span>
                          <div style={{position:'relative',height:7,background:C.bg3,borderRadius:5}}>
                            <div style={{position:'absolute',inset:'0 auto 0 0',width:`${Math.max(2,m/maxCat*100)}%`,background:lobColor(c),borderRadius:5}}/>
                          </div>
                          {/* Texto formateado, no type=number: number no deja mostrar
                              comas y los montos se veian desordenados (39000). */}
                          <div style={{position:'relative',justifySelf:'end'}}>
                            <span style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',fontSize:12,fontWeight:700,color:C.t4,pointerEvents:'none'}}>$</span>
                            <input type="text" inputMode="numeric" key={`${vSel}-${c}-${cat[c]||''}`}
                              defaultValue={cat[c]?Number(cat[c]).toLocaleString('en-US'):''} placeholder="0"
                              onBlur={e=>{ const n=Number(String(e.target.value).replace(/[^0-9.]/g,''))||0; if(n!==(Number(cat[c])||0)) guardarMetaCat(vSel,c,n); else e.target.value = n?n.toLocaleString('en-US'):''; }}
                              style={{width:104,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'7px 9px 7px 20px',color:C.t1,fontSize:13,fontWeight:700,outline:'none',textAlign:'right'}}/>
                          </div>
                        </div>
                      );
                    })}

                    {sinMonto.length>0 && (
                      <div style={{display:'flex',gap:7,flexWrap:'wrap',marginTop:13,alignItems:'center'}}>
                        <span style={{fontSize:11,color:C.t4,fontWeight:700}}>Agregar línea:</span>
                        {sinMonto.map(c=>(
                          <button key={c} onClick={()=>setCatsAbiertas(a=>({...a,[`${vSel}|${c}`]:true}))}
                            style={{border:`1px dashed ${C.border}`,background:C.bg2,color:C.t3,borderRadius:999,padding:'5px 12px',fontSize:11.5,fontWeight:700,cursor:'pointer'}}>
                            + {c}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {saving && <div style={{fontSize:11,color:C.t4,marginTop:8}}>Guardando…</div>}
              </div>
            </div>
          );
        })()
      ) : tab==='forecast' ? (
        // ════ FORECAST SEMANA — compromisos del vendedor ════
        <div>
          {esGerencia && (
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14}}>
              {vendedores.map(v=>(
                <button key={v} onClick={()=>setVendSel(v)}
                  style={{padding:'6px 14px',borderRadius:R.md,border:`1px solid ${vendSel===v?C.primaryBorder:C.border}`,background:vendSel===v?C.primaryTint:C.bg2,color:vendSel===v?C.blue:C.t2,fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:SH.xs}}>{v.split(' ')[0]}</button>
              ))}
            </div>
          )}
          {vendSel
            ? <ForecastCompromisos usuario={usuario} vendedorSel={vendSel} tipo="semanal"
                fechaActual={lunes} fechaAnterior={lunesAnt} labelPeriodo="esta semana" labelAnterior="semana pasada"/>
            : <div style={{fontSize:12,color:C.t4}}>Seleccioná un vendedor.</div>}
        </div>
      ) : tab==='hoy' ? (
        // ════ HOY — qué entra hoy (forecast diario) ════
        <div>
          {esGerencia && (
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14}}>
              {vendedores.map(v=>(
                <button key={v} onClick={()=>setVendSel(v)}
                  style={{padding:'6px 14px',borderRadius:R.md,border:`1px solid ${vendSel===v?C.primaryBorder:C.border}`,background:vendSel===v?C.primaryTint:C.bg2,color:vendSel===v?C.blue:C.t2,fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:SH.xs}}>{v.split(' ')[0]}</button>
              ))}
            </div>
          )}
          <div style={{fontSize:12,color:C.t4,marginBottom:10}}>Lo que entra <b>hoy</b> ({hoyISO}). Marcá ✅ cuando se concrete; lo no cumplido de ayer se arrastra.</div>
          {vendSel
            ? <ForecastCompromisos usuario={usuario} vendedorSel={vendSel} tipo="diario"
                fechaActual={hoyISO} fechaAnterior={ayerISO} labelPeriodo="hoy" labelAnterior="ayer"/>
            : <div style={{fontSize:12,color:C.t4}}>Seleccioná un vendedor.</div>}
        </div>
      ) : tab==='sinasig' ? (
        // ════ VENTAS SIN ASIGNAR — bandeja de asignación ════
        <div>
          <div style={{fontSize:12,color:C.t4,marginBottom:12}}>
            Facturas del mes <b>sin vendedor</b> ({mes}). Asigná cada una a su vendedor.
            <b> "Administración"</b> = venta de la casa. Administración cobra <b>1% de las ventas totales del mes</b>.
          </div>
          {ventasSinAsignar.length===0 ? (
            <div style={{background:C.greenTint,border:`1px solid ${C.greenBorder}`,borderRadius:R.lg,padding:18,fontSize:13,color:C.greenText,fontWeight:600}}>✓ Todas las facturas del mes tienen vendedor asignado.</div>
          ) : (
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,overflow:'auto',boxShadow:SH.xs}}>
              <div style={{padding:'11px 14px',borderBottom:`1px solid ${C.border}`,fontSize:12.5,fontWeight:700,letterSpacing:'-0.005em',color:C.orangeText}}>⚠️ {ventasSinAsignar.length} venta(s) sin asignar · {fmt(Math.round(ventasSinAsignar.reduce((s,f)=>s+(f.total_usd||0),0)))}</div>
              <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',minWidth:620}}>
                <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                  {['Factura','Cliente','Fecha','Total','Subtotal (sin ISV)','Asignar a'].map(h=>(
                    <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {ventasSinAsignar.map(f=>(
                    <tr key={`${f.doc_num}-${f.empresa_id}`} style={{borderBottom:`1px solid ${C.bg3}`}}>
                      <td style={{padding:'8px 12px',fontSize:12,fontWeight:800,color:C.blue}}>{f.doc_num}</td>
                      <td style={{padding:'8px 12px',fontSize:12,color:C.t2,maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.card_name}</td>
                      <td style={{padding:'8px 12px',fontSize:11,color:C.t3}}>{f.doc_date}</td>
                      <td style={{padding:'8px 12px',fontSize:12,color:C.t3}}>{fmt2(f.total_usd)}</td>
                      <td style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:C.t1}}>{fmt2(subtotalUsd(f))}</td>
                      <td style={{padding:'8px 12px'}}>
                        <select value="" onChange={e=>guardarVendFactura(f.doc_num,f.empresa_id,e.target.value)}
                          style={{background:C.bg3,border:`1px solid ${C.orange}`,borderRadius:7,padding:'4px 8px',color:C.t2,fontSize:11,outline:'none'}}>
                          <option value="">— elegir —</option>
                          {vendedores.map(v=><option key={v} value={v}>{v}</option>)}
                          <option value="Administrador">Administración (venta de la casa)</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : tab==='comisiones' ? (
        // ════ COMISIONES ════
        <div>
          {esGerencia && (
            <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:12}}>
              <Seg value={comVista} onChange={setComVista} items={[['vend','Por vendedor',CI.user],['todos','Todos',CI.users]]}/>
              <div style={{position:'relative'}}>
                <button className="radar-btn" onClick={()=>setComExp(m=>!m)}
                  style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 14px',border:`1px solid ${C.border}`,borderRadius:R.md,background:C.bg2,color:C.t2,fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:SH.xs}}><CIcon d={CI.download}/>Exportar {mes}</button>
                {comExp && (
                  <div className="radar-pop" style={{position:'absolute',top:'110%',left:0,zIndex:30,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,overflow:'hidden',boxShadow:SH.lg,minWidth:170}}>
                    <button onClick={()=>exportarComisiones('xlsx')} style={{display:'block',width:'100%',textAlign:'left',padding:'10px 14px',background:'transparent',border:'none',borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:600,color:C.t2,cursor:'pointer'}}>Excel (.xlsx)</button>
                    <button onClick={()=>exportarComisiones('csv')} style={{display:'block',width:'100%',textAlign:'left',padding:'10px 14px',background:'transparent',border:'none',fontSize:13,fontWeight:600,color:C.t2,cursor:'pointer'}}>CSV (.csv)</button>
                  </div>
                )}
              </div>
              {ventasSinAsignar.length>0 && (
                <button className="radar-btn" onClick={()=>setTab('sinasig')}
                  style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 14px',border:`1px solid ${C.orangeBorder}`,borderRadius:R.md,background:C.orangeTint,color:C.orangeText,fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:SH.xs}}><CIcon d={CI.flag}/>{ventasSinAsignar.length} venta(s) sin asignar</button>
              )}
              <div style={{fontSize:11,color:C.t4}}>Cambiá el mes (arriba) para ver o exportar meses anteriores.</div>
            </div>
          )}
          {esGerencia && comVista==='todos' ? (() => {
            // ── Vista CONSOLIDADA: todos los vendedores + detalle editable ──
            const lista = vendedores.map(calcComDe);
            const adm   = calcComDe('Administrador');
            const det   = comDetalleMes();
            const totPagar = lista.reduce((s,c)=>s+c.final,0) + adm.final;
            const totSub   = det.reduce((s,x)=>s+x.sub,0);
            const sinAsig  = det.filter(x=>!x.v);
            const selV = (x) => (
              <select value={x.v||''} onChange={e=>guardarVendFactura(x.f.doc_num,x.f.empresa_id,e.target.value)}
                style={{background:C.bg3,border:`1px solid ${x.v?C.border:C.orange}`,borderRadius:7,padding:'4px 8px',color:x.v?C.t2:C.orange,fontSize:11,outline:'none',maxWidth:150}}>
                <option value="">— sin asignar —</option>
                {vendedores.map(v=><option key={v} value={v}>{v}</option>)}
                <option value="Administrador">Administración (venta de la casa)</option>
              </select>
            );
            return (
              <div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10,marginBottom:14}}>
                  {[['Total a pagar (USD)', fmt(Math.round(totPagar)), C.green, 'solo comisiones — bonos KAM aparte (en L)'],
                    ['Subtotal pagado (sin ISV)', fmt(Math.round(totSub)), C.t2, `${det.length} facturas pagadas`],
                    ['Con comisión', String(lista.filter(c=>c.final>0).length), C.blue, `de ${vendedores.length} vendedores`],
                    ['Sin asignar', String(sinAsig.length), sinAsig.length?C.orange:C.green, sinAsig.length?`${fmt(Math.round(sinAsig.reduce((s,x)=>s+x.sub,0)))} en juego`:'todo asignado ✓'],
                  ].map(([l,v,col,sub])=>(
                    <div key={l} className="radar-kpi" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',boxShadow:SH.sm}}>
                      <div style={{fontSize:11,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.07em'}}>{l}</div>
                      <div className="radar-num" style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,marginTop:8,color:col===C.t2?C.t1:col}}>{v}</div>
                      <div style={{fontSize:11,color:C.t4,marginTop:6}}>{sub}</div>
                    </div>
                  ))}
                </div>
                {/* Cuadro resumen por vendedor */}
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'auto',marginBottom:14}}>
                  <div style={{padding:'10px 14px',borderBottom:`1px solid ${C.border}`,fontSize:12,fontWeight:700,color:C.t1,display:'flex',alignItems:'center',gap:7}}><span style={{color:C.t3,display:'inline-flex'}}><CIcon d={CI.cash} size={14}/></span>Resumen por vendedor · {mes}</div>
                  <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',minWidth:760}}>
                    <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                      {['Vendedor','Facturas','Subtotal (sin ISV)','Comisión base','Cumplim.','Factor','Bono KAM','A pagar'].map(h=>(
                        <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {lista.map(c=>(
                        <tr key={c.v} style={{borderBottom:`1px solid ${C.bg3}`,cursor:'pointer'}} onClick={()=>{ setVendSel(c.v); setComVista('vend'); }} title="Ver el detalle de este vendedor">
                          <td style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:C.blue}}>{c.v}{esKam(c.v)?' 🔑':''}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,color:C.t3}}>{c.filas.length}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:C.t1}}>{fmt2(c.sub)}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,color:C.t2}}>{fmt2(c.base)}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,color:esKam(c.v)?C.t4:(c.cumpl>=comMetaMin?C.greenText:C.orangeText)}}>{esKam(c.v)?'—':`${Math.round(c.cumpl*100)}%`}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:c.factor>=1?C.greenText:C.orangeText}}>{Math.round(c.factor*100)}%</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,color:c.bono?C.indigo:C.t4}}>{c.bono?`L.${Math.round(c.bono).toLocaleString('es-HN')}`:'—'}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:13,fontWeight:700,color:c.final>0?C.greenText:C.t4}}>{fmt2(c.final)}</td>
                        </tr>
                      ))}
                      {(adm.final>0 || adm.filas.length>0) && (
                        <tr style={{borderBottom:`1px solid ${C.bg3}`}}>
                          <td style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:C.t3}}>Administración</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,color:C.t3}}>{adm.filas.length}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,color:C.t3}}>{fmt2(adm.sub)}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,color:C.t2}}>{fmt2(adm.base)}</td>
                          <td colSpan={3} style={{padding:'8px 12px',fontSize:11,color:C.t4}}>1% de las ventas totales del mes sin ISV ({fmt2(det.reduce((s,x)=>s+x.sub,0))})</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:13,fontWeight:700,color:adm.final>0?C.greenText:C.t4}}>{fmt2(adm.final)}</td>
                        </tr>
                      )}
                      <tr style={{background:C.primaryTint}}>
                        <td style={{padding:'10px 12px',fontSize:12,fontWeight:800,color:C.t1}}>TOTAL</td>
                        <td className="radar-num" style={{padding:'10px 12px',fontSize:12,fontWeight:700,color:C.t2}}>{det.length}</td>
                        <td className="radar-num" style={{padding:'10px 12px',fontSize:12,fontWeight:800,color:C.t1}}>{fmt2(totSub)}</td>
                        <td className="radar-num" style={{padding:'10px 12px',fontSize:12,fontWeight:700,color:C.t2}}>{fmt2(lista.reduce((s,c)=>s+c.base,0))}</td>
                        <td colSpan={3}/>
                        <td className="radar-num" style={{padding:'10px 12px',fontSize:14,fontWeight:800,color:C.greenText}}>{fmt2(totPagar)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {/* Detalle completo del mes — todo editable */}
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'auto'}}>
                  <div style={{padding:'10px 14px',borderBottom:`1px solid ${C.border}`,fontSize:12,fontWeight:700,color:C.t1,display:'flex',alignItems:'center',gap:7}}><span style={{color:C.t3,display:'inline-flex'}}><CIcon d={CI.clipboard} size={14}/></span>Detalle · {det.length} facturas pagadas — asigná vendedor y categoría acá mismo</div>
                  <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',minWidth:900}}>
                    <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                      {['Factura','Empresa','Cliente','Fecha','Vendedor','Categoría','Subtotal (sin ISV)','%','Comisión'].map(h=>(
                        <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {det.length===0 && <tr><td colSpan={9} style={{padding:26,textAlign:'center',color:C.t4,fontSize:13}}>Sin facturas pagadas en {mes}.</td></tr>}
                      {det.map(x=>(
                        <tr key={`${x.f.doc_num}-${x.f.empresa_id}`} style={{borderBottom:`1px solid ${C.bg3}`,background:x.v?'transparent':C.orangeTint}}>
                          <td style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:C.blue}}>{x.f.doc_num}</td>
                          <td style={{padding:'8px 12px',fontSize:11,color:C.t4,textTransform:'uppercase'}}>{x.f.empresa_id}</td>
                          <td style={{padding:'8px 12px',fontSize:12,color:C.t2,maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{x.f.card_name}</td>
                          <td style={{padding:'8px 12px',fontSize:11,color:C.t3,whiteSpace:'nowrap'}}>{x.f.doc_date}</td>
                          <td style={{padding:'8px 12px'}}>{selV(x)}</td>
                          <td style={{padding:'8px 12px'}}>
                            <select value={x.cat||''} onChange={e=>guardarCatFactura(x.f.doc_num,x.f.empresa_id,e.target.value)}
                              style={{background:C.bg3,border:`1px solid ${x.cat?C.border:C.orange}`,borderRadius:7,padding:'4px 8px',color:x.cat?C.t2:C.orange,fontSize:11,outline:'none',maxWidth:160}}>
                              <option value="">— sin categoría —</option>
                              {ORG.lineasNegocio.map(c=><option key={c} value={c}>{c}</option>)}
                            </select>
                          </td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:C.t1}}>{fmt2(x.sub)}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,color:C.t3}}>{x.pct?`${(x.pct*100).toFixed(1)}%`:'—'}</td>
                          <td className="radar-num" style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:x.com>0?C.greenText:C.t4}}>{fmt2(x.com)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{fontSize:11,color:C.t4,marginTop:8}}>Base: facturas <b>pagadas</b> (pagos aplicados por Susana en SAP) del mes {mes}. Comisión sobre el <b>subtotal sin ISV</b> (Administración: <b>1% de las ventas totales del mes</b>). La columna "A pagar" ya incluye el factor de cumplimiento y el bono KAM.</div>
              </div>
            );
          })() : (
          <>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14}}>
            {vendedores.map(v=>(
              <button key={v} className="radar-btn" onClick={()=>setVendSel(v)}
                style={{padding:'6px 14px',borderRadius:R.md,border:`1px solid ${vendSel===v?C.primaryBorder:C.border}`,background:vendSel===v?C.primaryTint:C.bg2,color:vendSel===v?C.blue:C.t2,fontSize:12,fontWeight:vendSel===v?600:500,cursor:'pointer',boxShadow:SH.xs}}>{v.split(' ')[0]}{esKam(v)?' ·KAM':''}</button>
            ))}
            {esGerencia && (
              <button className="radar-btn" onClick={()=>setVendSel('Administrador')}
                style={{padding:'6px 14px',borderRadius:R.md,border:`1px solid ${vendEsAdmin?C.primaryBorder:C.border}`,background:vendEsAdmin?C.primaryTint:C.bg2,color:vendEsAdmin?C.blue:C.t2,fontSize:12,fontWeight:vendEsAdmin?600:500,cursor:'pointer',boxShadow:SH.xs}}>🏛 Administración</button>
            )}
          </div>

          {/* KAM: bono cuenta clave calculado (retención + crecimiento + satisfacción + actividad) */}
          {esKam(vendSel) && (() => {
            const pL = n => `L.${Math.round(Number(n)||0).toLocaleString('es-HN')}`;
            if (!kamBono) return <div style={{fontSize:12,color:C.t4,marginBottom:14}}>Calculando bono de {vendSel.split(' ')[0]}…</div>;
            const k = kamBono;
            const comps = [
              { lbl:'Retención cartera', peso:'35%', gana:k.retL, tope:1050, pct:k.retPct, ok:k.retOk,
                det:`${fmt(Math.round(k.cobMes))} vs ${fmt(Math.round(k.cobPrev))} mes ant.` },
              { lbl:'Crecimiento', peso:'30%', gana:k.crecL, tope:900, pct:k.crecPct, ok:k.crecScore>=1,
                det:`${k.growthPct>=0?'+':''}${Math.round(k.growthPct*100)}% MoM · meta +3%` },
              { lbl:'Satisfacción', peso:'20%', gana:k.satL, tope:600, pct:k.satPct, ok:!k.sinEnc && k.satScore>=0.8,
                det:k.sinEnc?'Pendiente — aún sin encuestas':`${Number(k.satProm).toFixed(1)}/5` },
              { lbl:'Actividad', peso:'15%', gana:k.actL, tope:450, pct:k.actPct, ok:k.actScore>=1,
                det:`${k.actTotal}/${k.metaAct} contactos · ${k.externos} ext. (mín ${k.minExternos})` },
            ];
            return (
            <div style={{display:'grid',gridTemplateColumns:'1.5fr 1fr',gap:12,marginBottom:14}}>
              {/* Bono breakdown */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:16,boxShadow:SH.sm}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:4}}>
                  <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1,display:'flex',alignItems:'center',gap:7}}><span style={{color:C.blue,display:'inline-flex'}}><CIcon d={CI.checkGoal} size={15}/></span>Bono cuenta clave <span style={{fontSize:10,color:C.t4,fontWeight:600}}>· mes en curso</span></div>
                </div>
                <div style={{display:'flex',alignItems:'baseline',gap:8,marginBottom:12}}>
                  <div className="radar-num" style={{fontSize:28,fontWeight:700,letterSpacing:'-0.025em',color:k.bonoPct>=70?C.greenText:C.orangeText}}>{pL(k.total)}</div>
                  <div className="radar-num" style={{fontSize:12,color:C.t4}}>/ {pL(5000)} ({k.bonoPct}%)</div>
                </div>
                {comps.map(c=>(
                  <div key={c.lbl} style={{marginBottom:10}}>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:3}}>
                      <span style={{color:C.t2,fontWeight:600}}>{c.lbl} <span style={{color:C.t4,fontWeight:500}}>{c.peso}</span></span>
                      <span className="radar-num" style={{color:c.ok?C.greenText:C.t3,fontWeight:600}}>{pL(c.gana)} / {pL(c.tope)}</span>
                    </div>
                    <div style={{height:8,background:C.bg3,borderRadius:R.pill,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${Math.min(100,c.pct)}%`,background:c.ok?C.green:C.orange,borderRadius:R.pill}}/>
                    </div>
                    <div style={{fontSize:10,color:C.t4,marginTop:2}}>{c.det}</div>
                  </div>
                ))}
                <div style={{borderTop:`1px solid ${C.border}`,paddingTop:8,marginTop:4,fontSize:11,display:'flex',alignItems:'center',gap:5,
                  color:k.accelGate?C.greenText:C.t4}}>
                  <CIcon d={CI.zap} size={12}/>Acelerador: {k.accelL>0?`+${pL(k.accelL)}`:'L.0'} <span style={{color:C.t4}}>— {k.accelGate?'la cartera está creciendo':'requiere crecer +10% sin que baje'}</span>
                </div>
              </div>
              {/* Cartera health + cuentas */}
              <div style={{background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:R.lg,padding:14}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1,display:'flex',alignItems:'center',gap:7}}><span style={{color:C.blue,display:'inline-flex'}}><CIcon d={CI.box} size={15}/></span>Su cartera ({(k.cartera||[]).length})</div>
                  {esGerencia && !editCart && <button className="radar-btn" onClick={()=>{ setCartDraft(k.cartera||[]); setEditCart(true); }} style={{display:'inline-flex',alignItems:'center',gap:5,background:C.bg2,border:`1px solid ${C.primaryBorder}`,borderRadius:R.sm,padding:'3px 10px',color:C.blue,fontSize:11,fontWeight:600,cursor:'pointer'}}><CIcon d={<><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></>} size={11}/>Editar</button>}
                </div>
                <div style={{display:'flex',gap:16,flexWrap:'wrap',fontSize:13,marginBottom:10}}>
                  <div><span style={{color:C.t4,fontSize:11}}>Cobrado este mes</span><div className="radar-num" style={{fontWeight:700,color:C.t1}}>{fmt(Math.round(k.cobMes))}</div></div>
                  <div><span style={{color:C.t4,fontSize:11}}>Mes anterior</span><div className="radar-num" style={{fontWeight:700,color:C.t3}}>{fmt(Math.round(k.cobPrev))}</div></div>
                  <div><span style={{color:C.t4,fontSize:11}}>Tendencia</span><div className="radar-num" style={{fontWeight:700,color:k.retOk?C.greenText:C.redText}}>{k.growthPct>=0?'▲':'▼'} {Math.round(k.growthPct*100)}%</div></div>
                </div>
                {!editCart ? (
                  <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                    {(k.cartera||[]).map(ct=>(
                      <span key={ct} style={{fontSize:10,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:6,padding:'2px 7px',color:C.t3}}>{ct}</span>
                    ))}
                  </div>
                ) : (
                  <div>
                    <div style={{fontSize:10,color:C.t4,marginBottom:6}}>Agregá/quitá cuentas. Se comparan por nombre (sin importar mayúsculas/acentos).</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:8}}>
                      {cartDraft.map((ct,i)=>(
                        <span key={i} style={{fontSize:11,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:6,padding:'3px 8px',color:C.t2,display:'flex',alignItems:'center',gap:6}}>{ct}
                          <button onClick={()=>setCartDraft(d=>d.filter((_,j)=>j!==i))} style={{background:'none',border:'none',color:C.red,fontSize:13,cursor:'pointer',lineHeight:1,padding:0}}>×</button>
                        </span>
                      ))}
                    </div>
                    <div style={{display:'flex',gap:6,marginBottom:8}}>
                      <input value={nuevaCta} onChange={e=>setNuevaCta(e.target.value)} onKeyDown={e=>{ if(e.key==='Enter'&&nuevaCta.trim()){ setCartDraft(d=>[...d,nuevaCta.trim()]); setNuevaCta(''); } }} placeholder="Nueva cuenta…" style={{flex:1,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:7,padding:'6px 10px',color:C.t1,fontSize:12,outline:'none'}}/>
                      <button className="radar-btn" onClick={()=>{ if(nuevaCta.trim()){ setCartDraft(d=>[...d,nuevaCta.trim()]); setNuevaCta(''); } }} style={{background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,borderRadius:R.md,padding:'6px 12px',fontSize:12,fontWeight:600,cursor:'pointer'}}>+ Agregar</button>
                    </div>
                    <div style={{display:'flex',gap:6,justifyContent:'flex-end'}}>
                      <button onClick={()=>{ setEditCart(false); setNuevaCta(''); }} style={{background:'none',border:`1px solid ${C.border}`,borderRadius:7,padding:'6px 12px',color:C.t4,fontSize:12,cursor:'pointer'}}>Cancelar</button>
                      <button onClick={guardarCartera} style={{background:C.indigo,color:'#fff',border:'none',borderRadius:7,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer'}}>Guardar cartera</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })()}
          <>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10,marginBottom:14}}>
              {(vendEsAdmin ? [
                ['Ventas de la casa', fmt(Math.round(comTotV)), C.t1, `${comFilas.length} facturas · ${fmt(Math.round(comSubV))} sin ISV`],
                ['Ventas totales del mes', fmt(Math.round(ventasTotalesMes)), C.t2, 'sin ISV — base del 1%'],
                ['Comisión Administración', fmt2(comBase), C.green, '1% de las ventas totales del mes'],
              ] : esKam(vendSel) ? [
                ['Ventas pagadas', fmt(Math.round(comTotV)), C.t1, `${comFilas.length} facturas · ${fmt(Math.round(comSubV))} sin ISV`],
                ['Comisión ventas (USD)', fmt(Math.round(comBase)), C.t2, `${comFilas.length} facturas (sin meta)`],
                ['Bono cuenta clave (L)', kamBono?`L.${Math.round(kamBono.total).toLocaleString('es-HN')}`:'…', C.indigo, kamBono?`${kamBono.bonoPct}% de L.5,000 · mes en curso`:'calculando'],
              ] : [
                ['Ventas pagadas', fmt(Math.round(comTotV)), C.t1, `${comFilas.length} facturas · ${fmt(Math.round(comSubV))} sin ISV`],
                ['Comisión base', fmt(Math.round(comBase)), C.t2, `${comFilas.length} facturas pagadas`],
                ['Cumplimiento meta', `${Math.round(cumplPct*100)}%`, cumplPct>=comMetaMin?C.green:C.orange, `de $${comMeta.toLocaleString('en-US')}`],
                ['Factor', `${Math.round(comFactor*100)}%`, comFactor>=1?C.green:C.orange, `mín. ${Math.round(comMetaMin*100)}% → 100%`],
                ['Comisión a pagar', fmt(Math.round(comFinal)), C.green, comFactor<1?`base × ${Math.round(comFactor*100)}%`:'100%'],
              ]).map(([l,v,col,sub])=>(
                <div key={l} className="radar-kpi" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',boxShadow:SH.sm}}>
                  <div style={{fontSize:11,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.07em'}}>{l}</div>
                  <div className="radar-num" style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,marginTop:8,color:col===C.t2?C.t1:col}}>{v}</div>
                  <div style={{fontSize:11,color:C.t4,marginTop:6}}>{sub}</div>
                </div>
              ))}
            </div>
            {!vendEsAdmin && !esKam(vendSel) && (() => {
              const efRate  = comSubV>0 ? comBase/comSubV : 0;               // % efectivo sobre subtotal
              const simMax  = Math.max(20000, Math.round(comMeta*0.6/1000)*1000);
              const add     = Math.min(simAdd, simMax);
              const newCumpl= comMeta>0 ? (realSAP+add)/comMeta : 0;
              const newFactor = newCumpl>=comMetaMin ? 1 : Math.max(0,1-(comMetaMin-newCumpl));
              const newFinal  = (comBase + add*efRate) * newFactor;
              const extra     = Math.max(0, newFinal - comFinal);
              const term = (k,v,strong) => (
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 12px',textAlign:'center',minWidth:88}}>
                  <div style={{fontSize:9.5,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.03em'}}>{k}</div>
                  <div className="radar-num" style={{fontSize:15,fontWeight:750,marginTop:2,color:strong||C.t1}}>{v}</div>
                </div>
              );
              return (
              <div style={{display:'grid',gridTemplateColumns:'1.15fr 1fr',gap:12,marginBottom:14}}>
                {/* Fórmula transparente */}
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:16,boxShadow:SH.sm}}>
                  <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1,marginBottom:12,display:'flex',alignItems:'center',gap:7}}><span style={{color:C.blue,display:'inline-flex'}}><CIcon d={CI.checkGoal} size={14}/></span>Cómo se calcula tu comisión</div>
                  <div style={{display:'flex',alignItems:'center',gap:9,flexWrap:'wrap',padding:'12px',background:C.bg1,border:`1px dashed ${C.border2}`,borderRadius:R.md}}>
                    {term('Subtotal pagado', fmt(Math.round(comSubV)))}
                    <span style={{fontSize:17,fontWeight:700,color:C.t4}}>×</span>
                    {term('% efectivo', `${(efRate*100).toFixed(1)}%`)}
                    <span style={{fontSize:17,fontWeight:700,color:C.t4}}>×</span>
                    {term('Factor', comFactor.toFixed(2))}
                    <span style={{fontSize:17,fontWeight:700,color:C.t4}}>=</span>
                    <div style={{marginLeft:'auto',textAlign:'right'}}>
                      <div style={{fontSize:9.5,fontWeight:600,color:C.t3,textTransform:'uppercase'}}>A pagar</div>
                      <div className="radar-num" style={{fontSize:20,fontWeight:800,color:C.blue}}>{fmt(Math.round(comFinal))}</div>
                    </div>
                  </div>
                  <div style={{fontSize:10.5,color:C.t4,marginTop:10,display:'flex',gap:6,alignItems:'flex-start'}}><CIcon d={CI.help} size={13}/><span>Comisión sobre subtotal <b>sin ISV</b>. El factor sube de {Math.round(comMetaMin*100)}% (cumplimiento mínimo) a 100% al llegar a meta.</span></div>
                </div>
                {/* Simulador */}
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:16,boxShadow:SH.sm}}>
                  <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1,marginBottom:4,display:'flex',alignItems:'center',gap:7}}><span style={{color:C.blue,display:'inline-flex'}}><CIcon d={CI.zap} size={14}/></span>Simulador <span style={{fontSize:10,color:C.t4,fontWeight:600}}>· ¿cuánto más si cerrás más?</span></div>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:11.5,fontWeight:600,color:C.t2,margin:'12px 0 6px'}}><span>Cerrar adicional este mes</span><span className="radar-num" style={{color:C.blue,fontWeight:800}}>{fmt(add)}</span></div>
                  <input type="range" min={0} max={simMax} step={500} value={add} onChange={e=>setSimAdd(Number(e.target.value))} style={{width:'100%',accentColor:C.blue,cursor:'pointer'}}/>
                  <div style={{display:'grid',gridTemplateColumns:'1fr auto 1fr',alignItems:'center',gap:10,marginTop:14,padding:12,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:R.md}}>
                    <div style={{textAlign:'center'}}><div style={{fontSize:9.5,fontWeight:600,color:C.t3,textTransform:'uppercase'}}>Hoy</div><div className="radar-num" style={{fontSize:18,fontWeight:800,color:C.t1,marginTop:2}}>{fmt(Math.round(comFinal))}</div></div>
                    <span style={{color:C.blue,display:'inline-flex'}}><CIcon d={CI.send} size={18}/></span>
                    <div style={{textAlign:'center'}}><div style={{fontSize:9.5,fontWeight:600,color:C.t3,textTransform:'uppercase'}}>Estimado</div><div className="radar-num" style={{fontSize:18,fontWeight:800,color:C.blue,marginTop:2}}>{fmt(Math.round(newFinal))}</div></div>
                  </div>
                  <div style={{fontSize:11,color:C.t3,marginTop:10,display:'flex',gap:6,alignItems:'flex-start'}}><CIcon d={CI.bulb} size={13}/><span>{add>0?<>Cerrando {fmt(add)} más llegás al <b>{Math.round(newCumpl*100)}% de meta</b> → factor {newFactor.toFixed(2)} y ganás <b style={{color:C.greenText}}>{fmt(Math.round(extra))} extra</b>.</>:'Mové el slider para simular tu comisión con ventas adicionales.'}</span></div>
                </div>
              </div>
              );
            })()}
            {comSinCat>0 && <div style={{fontSize:11,color:C.orangeText,marginBottom:10}}>⚠️ {comSinCat} factura(s) sin categoría — no comisionan hasta asignarles una. {esGerencia?'Asignala en la columna Categoría.':'Pedí a gerencia que las categorice.'}</div>}
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,overflow:'auto'}}>
              <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',minWidth:680}}>
                <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                  {['Factura','Cliente',...(esGerencia?['Vendedor']:[]),'Subtotal (sin ISV)','Categoría','%','Comisión'].map(h=>(
                    <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {comFilas.length===0 && <tr><td colSpan={esGerencia?7:6} style={{padding:26,textAlign:'center',color:C.t4,fontSize:13}}>Sin facturas pagadas este mes para {vendSel?.split(' ')[0]}.</td></tr>}
                  {comFilas.map(({f,cat,sub,pct,com})=>(
                    <tr key={`${f.doc_num}-${f.empresa_id}`} style={{borderBottom:`1px solid ${C.bg3}`}}>
                      <td style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:C.blue}}>{f.doc_num}</td>
                      <td style={{padding:'8px 12px',fontSize:12,color:C.t2,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.card_name}</td>
                      {esGerencia && (
                        <td style={{padding:'8px 12px'}}>
                          <select value={vendSel} onChange={e=>guardarVendFactura(f.doc_num,f.empresa_id,e.target.value)} title="Reasignar esta venta"
                            style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:7,padding:'4px 8px',color:C.t2,fontSize:11,outline:'none',maxWidth:140}}>
                            {vendedores.map(v=><option key={v} value={v}>{v}</option>)}
                            <option value="Administrador">Administración (venta de la casa)</option>
                          </select>
                        </td>
                      )}
                      <td style={{padding:'8px 12px',fontSize:12,color:C.t1,fontWeight:700}}>{fmt2(sub)}</td>
                      <td style={{padding:'8px 12px'}}>
                        {esGerencia ? (
                          <select value={cat||''} onChange={e=>guardarCatFactura(f.doc_num,f.empresa_id,e.target.value)}
                            style={{background:C.bg3,border:`1px solid ${cat?C.border:C.orange}`,borderRadius:7,padding:'4px 8px',color:cat?C.t2:C.orange,fontSize:11,outline:'none',maxWidth:180}}>
                            <option value="">— sin categoría —</option>
                            {ORG.lineasNegocio.map(c=><option key={c} value={c}>{c}</option>)}
                          </select>
                        ) : <span style={{fontSize:11,color:cat?C.t3:C.orange}}>{cat||'sin categoría'}</span>}
                      </td>
                      <td className="radar-num" style={{padding:'8px 12px',fontSize:12,color:C.t3}}>{pct?`${(pct*100).toFixed(1)}%`:'—'}</td>
                      <td className="radar-num" style={{padding:'8px 12px',fontSize:12,fontWeight:700,color:com>0?C.greenText:C.t4}}>{fmt2(com)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{fontSize:11,color:C.t4,marginTop:8}}>{vendEsAdmin
              ? 'Ventas de la casa (sin vendedor). La comisión de Administración es el 1% del subtotal sin ISV de TODAS las ventas pagadas del mes.'
              : <>Comisión sobre subtotal (sin ISV) de facturas pagadas.{!esKam(vendSel)&&' El factor aplica el cumplimiento monetario del mes (incluye anticipos).'}</>}</div>
          </>
          </>
          )}
        </div>
      ) : (
        // ════ REVISIÓN por vendedor ════
        <div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:16}}>
            {vendedores.map(v=>(
              <button key={v} className="radar-btn" onClick={()=>setVendSel(v)}
                style={{padding:'6px 14px',borderRadius:R.md,border:`1px solid ${vendSel===v?C.primaryBorder:C.border}`,background:vendSel===v?C.primaryTint:C.bg2,color:vendSel===v?C.blue:C.t2,fontSize:12,fontWeight:vendSel===v?600:500,cursor:'pointer',boxShadow:SH.xs}}>
                {v.split(' ')[0]}{esKam(v)?' ·KAM':''}
              </button>
            ))}
          </div>

          {vendKam && <div style={{background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:R.md,padding:'10px 14px',marginBottom:14,fontSize:12,color:C.t2,display:'flex',alignItems:'center',gap:7}}><span style={{color:C.blue,display:'inline-flex'}}><CIcon d={CI.user} size={14}/></span><span><b>{vendSel}</b> es Key Account Manager — no se mide por meta. Se muestra su actividad e ingresos atribuidos.</span></div>}

          {/* KPIs principales */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:10,marginBottom:16}}>
            {(vendKam ? [
              ['Tipo', 'KAM', C.indigo],
              ['Ingresado real (SAP)', fmt(Math.round(realSAP)), C.green],
              ['Cerrado en CRM', fmt(Math.round(crmTotal)), C.t2],
            ] : [
              ['Meta del mes', fmt(metaTotal), C.t2],
              ['Facturado (CRM ganado)', fmt(Math.round(crmTotal)), C.indigo],
              ['Cobrado (SAP)', fmt(Math.round(realSAP)), C.green],
              ['Por cobrar', fmt(Math.round(Math.max(0,crmTotal-realSAP))), crmTotal-realSAP>50?C.orange:C.green],
              ['Cumplimiento', pct!=null?`${pct}%`:'sin meta', pct==null?C.t4:pct>=80?C.green:pct>=50?C.orange:C.red],
              ['Falta para meta', fmt(Math.round(falta)), falta>0?C.red:C.green],
              ['Meta semanal', fmt(Math.round(metaSemanal)), C.t2],
            ]).map(([l,v,col])=>(
              <div key={l} className="radar-kpi" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',boxShadow:SH.sm}}>
                <div style={{fontSize:11,color:C.t3,textTransform:'uppercase',fontWeight:600,letterSpacing:'.07em'}}>{l}</div>
                <div className="radar-num" style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,marginTop:8,color:col===C.t2?C.t1:col}}>{v}</div>
              </div>
            ))}
          </div>

          {/* Barra de cumplimiento (goalBar — verde >=85, teal 70-84, ámbar <70) */}
          {metaTotal>0 && (
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:16,marginBottom:16,boxShadow:SH.sm}}>
              <div style={{position:'relative',height:8,background:C.bg3,borderRadius:R.pill,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${Math.min(pct,100)}%`,background:pct>=85?C.green:pct>=70?C.blue:C.orange,borderRadius:R.pill,transition:'width .4s cubic-bezier(.4,0,.2,1)'}}/>
              </div>
              <div className="radar-num" style={{fontSize:11.5,color:C.t3,marginTop:8}}>{fmt(Math.round(realSAP))} de {fmt(metaTotal)} · ingresado realmente según SAP</div>
            </div>
          )}

          {/* Conciliación CRM vs SAP */}
          <div style={{background:C.bg2,border:`1px solid ${Math.abs(descuadre)>50?C.orange:C.border}`,borderRadius:12,padding:14,marginBottom:16}}>
            <div onClick={()=>setVerConcil(v=>!v)} style={{display:'flex',alignItems:'center',justifyContent:'space-between',cursor:'pointer'}}>
              <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1,marginBottom:8,display:'flex',alignItems:'center',gap:7}}><span style={{color:C.t3,display:'inline-flex'}}><CIcon d={CI.search} size={14}/></span>Conciliación CRM ↔ SAP</div>
              <div style={{fontSize:12,color:C.blue,fontWeight:700}}>{verConcil?'Ocultar detalle ▲':'Ver detalle ▼'}</div>
            </div>
            <div style={{display:'flex',gap:24,flexWrap:'wrap',fontSize:13}}>
              <div><span style={{color:C.t4}}>CRM dice cerrado/pagado: </span><b style={{color:C.t1}}>{fmt(Math.round(crmTotal))}</b> <span style={{color:C.t4}}>({oppsV.length} opps)</span></div>
              <div><span style={{color:C.t4}}>SAP ingresó: </span><b style={{color:C.green}}>{fmt(Math.round(realSAP))}</b> <span style={{color:C.t4}}>({cobrosV.length} pagos)</span></div>
              <div><span style={{color:C.t4}}>Diferencia: </span><b style={{color:Math.abs(descuadre)>50?C.orange:C.green}}>{descuadre>=0?'+':''}{fmt(Math.round(descuadre))}</b></div>
            </div>
            {Math.abs(descuadre)>50 && !verConcil && <div style={{fontSize:11,color:C.orange,marginTop:8}}>⚠️ Hay descuadre. Tocá <b>Ver detalle</b> para ver cada pago de SAP y qué falta registrar en CRM.</div>}
            {verConcil && (() => {
              const norm = s => (s||'').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g,'').replace(/[^a-z0-9]+/g,' ').trim();
              const oppNames = oppsV.map(o=>norm(o.cliente)).filter(Boolean);
              const enCRM = nm => { const n=norm(nm); return !!n && oppNames.some(o=>o.includes(n)||n.includes(o)); };
              const pagos = [...cobrosV].sort((a,b)=>(b.total_usd||b.doc_total||0)-(a.total_usd||a.doc_total||0));
              return (
                <div style={{marginTop:12,borderTop:`1px solid ${C.border}`,paddingTop:12}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.t1,marginBottom:6,display:'flex',alignItems:'center',gap:6}}><span style={{color:C.t3,display:'inline-flex'}}><CIcon d={CI.cash} size={13}/></span>Pagos en SAP este mes ({pagos.length})</div>
                  <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
                    {pagos.length===0 && <div style={{padding:12,fontSize:12,color:C.t4}}>Sin pagos en SAP este mes.</div>}
                    {pagos.map((c,i)=>{ const ok=enCRM(c.card_name); return (
                      <div key={i} className="radar-hrow" style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderBottom:`1px solid ${C.bg3}`,fontSize:12}}>
                        <span style={{flex:1,color:C.t1,fontWeight:600}}>{c.card_name||'—'}</span>
                        <span style={{color:C.t4,minWidth:80}}>{fecha(c.doc_date)}</span>
                        <span style={{color:C.green,fontWeight:700,minWidth:90,textAlign:'right'}}>{fmt(Math.round(c.total_usd||c.doc_total||0))}</span>
                        <span style={{minWidth:120,textAlign:'right'}}>{ok
                          ? <span style={{color:C.green,fontWeight:700,fontSize:11}}>✓ en CRM</span>
                          : <span style={{color:C.orange,fontWeight:700,fontSize:11}}>⚠ falta en CRM</span>}</span>
                      </div>
                    ); })}
                  </div>
                  <div style={{fontSize:11,color:C.t4,marginTop:8}}>Los marcados <b style={{color:C.orange}}>⚠ falta en CRM</b> tienen pago en SAP pero ninguna oportunidad de {vendSel?.split(' ')[0]} que coincida — registralos/movélos a ganado en el CRM.</div>
                </div>
              );
            })()}
          </div>

          {/* Por categoría — meta repartida vs cerrado (CRM) */}
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:14,marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:800,color:C.t1,marginBottom:10}}>Por categoría — {vendKam?'cerrado (CRM)':'meta repartida vs cerrado'} · {mes}</div>
            {(() => {
              const cats = ORG.lineasNegocio.filter(c=>(Number(metaCatSel[c])||0)>0 || (crmPorCat[c]||0)>0);
              if (!cats.length) return <div style={{fontSize:12,color:C.t4}}>Sin meta repartida ni cierres este mes. Repartí la meta en "Definir metas".</div>;
              return cats.map(cat=>{
                const meta = Number(metaCatSel[cat])||0, real = crmPorCat[cat]||0;
                const p = meta>0?Math.round(real/meta*100):null;
                return (
                  <div key={cat} style={{marginBottom:8}}>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:3}}>
                      <span style={{color:C.t2}}>{cat}</span>
                      <span style={{color:C.t3}}>{fmt(Math.round(real))}{meta>0?` / ${fmt(meta)} (${p}%)`:''}</span>
                    </div>
                    <div style={{height:8,background:C.bg3,borderRadius:R.pill,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${meta>0?Math.min(p,100):0}%`,background:p==null?C.blue:p>=85?C.green:p>=70?C.blue:C.orange,borderRadius:R.pill}}/>
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* Forecast semanal */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:14}}>
              <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1,marginBottom:8,display:'flex',alignItems:'center',gap:7}}><span style={{color:C.t3,display:'inline-flex'}}><CIcon d={CI.calendar} size={14}/></span>Proyectado esta semana</div>
              {fcEsta.length===0 ? <div style={{fontSize:12,color:C.t4}}>Sin forecast cargado esta semana.</div> :
                fcEsta.map(f=>(
                  <div key={f.id} style={{display:'flex',justifyContent:'space-between',fontSize:12,padding:'6px 0',borderBottom:`1px solid ${C.bg3}`}}>
                    <span style={{color:C.t2}}>{(f.cliente||'').slice(0,22)} <span style={{color:C.t4}}>· {f.categoria}</span></span>
                    <span style={{color:f.estado==='cerrado'?C.green:C.t3,fontWeight:700}}>{fmt(f.monto_estimado)} {f.estado==='cerrado'?'✓':''}</span>
                  </div>
                ))}
            </div>
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:14}}>
              <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.orangeText,marginBottom:8,display:'flex',alignItems:'center',gap:7}}><span style={{display:'inline-flex'}}><CIcon d={CI.clock} size={14}/></span>No cumplido semana pasada</div>
              {fcArrastre.length===0 ? <div style={{fontSize:12,color:C.t4}}>Nada pendiente de la semana pasada.</div> :
                fcArrastre.map(f=>(
                  <div key={f.id} style={{display:'flex',justifyContent:'space-between',fontSize:12,padding:'6px 0',borderBottom:`1px solid ${C.bg3}`}}>
                    <span style={{color:C.t2}}>{(f.cliente||'').slice(0,22)} <span style={{color:C.t4}}>· {f.categoria}</span></span>
                    <span style={{color:C.red,fontWeight:700}}>{fmt(f.monto_estimado)}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── REPORTES ─────────────────────────────────────────────────────────────────
function Reportes({ usuario, clientes, equipos, empresaActiva }) {
  const [tab]               = useState('mensual');  // solo 'mensual' (se quito el selector "Metas equipo")
  const [periodo, setPeriodo] = useState(new Date().toISOString().slice(0,7));
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState({ rentas:[], ingresos:[], gastos:[], metas:[] });
  const [sendingWA, setSendingWA] = useState(null);
  const [waResult, setWaResult]   = useState(null);

  const enviarReporteWA = async (tipo) => {
    setSendingWA(tipo); setWaResult(null);
    try {
      // S2: la clave compartida ya no viaja al navegador. El endpoint acepta el
      // JWT de un admin como credencial (esAdmin en el servidor).
      const { data:{ session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/report?tipo=${tipo}`, { headers: { Authorization: `Bearer ${session?.access_token||''}` } });
      const data = await r.json();
      if (data.error) {
        setWaResult({ tipo, ok:false, msg: data.error, detail: data });
      } else {
        const enviados = data.enviados || [];
        const sinNum   = enviados.filter(e=>e.status==='sin_numero').map(e=>e.vendedor);
        const enviados2= enviados.filter(e=>e.status!=='sin_numero');
        const ok       = enviados2.some(e=>e.sid||e.status==='queued'||e.status==='sent');
        let msg = ok ? `Enviado a ${enviados2.length} contacto(s)` : 'Sin envíos exitosos';
        if (sinNum.length) msg += ` · Sin número: ${sinNum.join(', ')}`;
        setWaResult({ tipo, ok, msg, detail: data });
      }
    } catch(e) { setWaResult({ tipo, ok:false, msg: e.message }); }
    finally { setSendingWA(null); }
  };

  const cargar = async (eid) => {
    setLoading(true);
    const [mesI, mesF] = [periodo+'-01', periodo+'-31'];
    let qR = supabase.from('rentas').select('*,clientes(empresa),equipos(codigo,modelo,tipo_equipo)').eq('estado','activa');
    let qI = supabase.from('ingresos').select('*,clientes(empresa),equipos(codigo,modelo)').gte('fecha',mesI).lte('fecha',mesF).order('fecha',{ascending:false});
    let qG = supabase.from('mantenimientos').select('*,equipos(codigo,modelo)').gte('fecha',mesI).lte('fecha',mesF).order('fecha',{ascending:false});
    if (eid) { qR=qR.eq('empresa_id',eid); qI=qI.eq('empresa_id',eid); qG=qG.eq('empresa_id',eid); }
    const [{ data:r },{ data:i },{ data:g },{ data:m }] = await Promise.all([
      qR, qI, qG,
      supabase.from('metas_diarias').select('*').gte('fecha',mesI).lte('fecha',mesF).order('fecha',{ascending:false}),
    ]);
    setD({ rentas:r||[], ingresos:i||[], gastos:g||[], metas:m||[] });
    setLoading(false);
  };
  useEffect(()=>{ cargar(); },[periodo, empresaActiva]);

  const mrr         = d.rentas.reduce((s,r)=>s+(r.valor_mensual||0),0);
  const totalIng    = d.ingresos.reduce((s,i)=>s+(i.monto||0),0);
  const totalGastos = d.gastos.reduce((s,g)=>s+(g.total||0),0);
  const utilidad    = mrr + totalIng - totalGastos;

  const porVendedorIng = [...new Set(d.ingresos.map(i=>i.vendedor).filter(Boolean))].map(v=>({
    v, total: d.ingresos.filter(i=>i.vendedor===v).reduce((s,i)=>s+(i.monto||0),0),
    n: d.ingresos.filter(i=>i.vendedor===v).length,
  })).sort((a,b)=>b.total-a.total);

  const porClienteIng = Object.values(d.ingresos.reduce((acc,i)=>{
    const key = i.cliente_id||'sin_cliente';
    if (!acc[key]) acc[key]={ empresa: i.clientes?.empresa||'Sin cliente', total:0, n:0 };
    acc[key].total+=i.monto||0; acc[key].n++;
    return acc;
  },{})).sort((a,b)=>b.total-a.total).slice(0,8);

  // Metas equipo — agrupar por vendedor
  const metasPorVendedor = Object.values(d.metas.reduce((acc,m)=>{
    const k = m.usuario_nombre||'—';
    if (!acc[k]) acc[k]={ nombre:k, dias:0, metaTotal:0, metaSemanal:0 };
    acc[k].dias++; acc[k].metaTotal+=m.meta_ingreso||0;
    if (m.meta_semanal) acc[k].metaSemanal=m.meta_semanal;
    return acc;
  },{})).map(v=>({
    ...v,
    realTotal: d.ingresos.filter(i=>i.vendedor===v.nombre).reduce((s,i)=>s+(i.monto||0),0),
    pct: v.metaTotal>0 ? Math.round(d.ingresos.filter(i=>i.vendedor===v.nombre).reduce((s,i)=>s+(i.monto||0),0)/v.metaTotal*100) : null,
  })).sort((a,b)=>b.realTotal-a.realTotal);

  const exportCSV = (rows, filename, cols) => {
    const csv = [cols.map(c=>c.h).join(','), ...rows.map(r=>cols.map(c=>`"${r[c.k]||''}"`).join(','))].join('\n');
    const a = Object.assign(document.createElement('a'),{href:URL.createObjectURL(new Blob([csv],{type:'text/csv'})),download:filename});
    a.click();
  };

  const exportPDF = () => window.print();

  const pctC = p => p===null?C.t4:p>=100?C.green:p>=70?C.orange:C.red;

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* Print styles */}
      <style>{`@media print{body{background:#fff!important}.no-print{display:none!important}.print-area{color:#000!important;background:#fff!important}}`}</style>

      {/* Toolbar */}
      <div className="no-print" style={{padding:'12px 16px 4px',display:'flex',gap:8,alignItems:'center',flexShrink:0,flexWrap:'wrap'}}>
        {/* Reporte mensual (se quito "Metas equipo": duplicaba Metas & Revision y no se usaba) */}
        <input className="radar-input" type="month" value={periodo} onChange={e=>setPeriodo(e.target.value)}
          style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'0 12px',height:34,color:C.t1,fontSize:13,boxShadow:SH.xs,outline:'none'}}/>
        <div style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {/* Disparar reportes WhatsApp manualmente */}
          <div style={{display:'flex',gap:6,alignItems:'center',borderRight:`1px solid ${C.border}`,paddingRight:10,marginRight:4}}>
            <span style={{fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.06em'}}>WhatsApp ahora:</span>
            <button className="radar-btn" onClick={()=>enviarReporteWA('manana')} disabled={!!sendingWA}
              style={{display:'inline-flex',alignItems:'center',gap:5,height:30,background:sendingWA==='manana'?C.bg3:C.greenTint,color:sendingWA==='manana'?C.t4:C.greenText,
                border:`1px solid ${sendingWA==='manana'?C.border:C.greenBorder}`,
                borderRadius:R.md,padding:'0 11px',fontSize:11.5,fontWeight:600,cursor:sendingWA?'wait':'pointer'}}>
              <CIcon d={sendingWA==='manana'?CI.clock:CI.sun}/>{sendingWA==='manana'?'Enviando...':'Mañana'}
            </button>
            <button className="radar-btn" onClick={()=>enviarReporteWA('tarde')} disabled={!!sendingWA}
              style={{display:'inline-flex',alignItems:'center',gap:5,height:30,background:sendingWA==='tarde'?C.bg3:C.primaryTint,color:sendingWA==='tarde'?C.t4:C.blue,
                border:`1px solid ${sendingWA==='tarde'?C.border:C.primaryBorder}`,
                borderRadius:R.md,padding:'0 11px',fontSize:11.5,fontWeight:600,cursor:sendingWA?'wait':'pointer'}}>
              <CIcon d={sendingWA==='tarde'?CI.clock:CI.sunset}/>{sendingWA==='tarde'?'Enviando...':'Tarde'}
            </button>
            {waResult&&(
              <button onClick={()=>toast('DIAGNÓSTICO WhatsApp:\n\n'+JSON.stringify(waResult.detail||waResult,null,2))}
                style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:10.5,fontWeight:600,height:30,color:waResult.ok?C.greenText:C.redText,background:waResult.ok?C.greenTint:C.redTint,border:`1px solid ${waResult.ok?C.greenBorder:C.redBorder}`,
                  borderRadius:R.md,padding:'0 10px',cursor:'pointer',maxWidth:240,overflow:'hidden',whiteSpace:'nowrap'}}
                title="Click para ver diagnóstico completo">
                <CIcon d={waResult.ok?CI.check2:CI.alert}/><span style={{overflow:'hidden',textOverflow:'ellipsis'}}>{waResult.msg}</span>
              </button>
            )}
          </div>
          <Btn variant="secondary" size="sm" onClick={exportPDF} style={{display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={CI.printer}/>PDF</Btn>
          <Btn variant="secondary" size="sm" onClick={()=>exportCSV(d.ingresos,`ingresos_${periodo}.csv`,[
            {h:'Fecha',k:'fecha'},{h:'Tipo',k:'tipo'},{h:'Monto',k:'monto'},{h:'Vendedor',k:'vendedor'},{h:'Referencia',k:'referencia'},{h:'Descripcion',k:'descripcion'}
          ])} style={{display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={CI.download}/>CSV Ingresos</Btn>
          <Btn variant="secondary" size="sm" onClick={()=>exportCSV(d.gastos,`gastos_${periodo}.csv`,[
            {h:'Fecha',k:'fecha'},{h:'Tipo',k:'tipo'},{h:'Total',k:'total'},{h:'MO',k:'mano_obra'},{h:'Repuestos',k:'costo_repuestos'},{h:'Tecnico',k:'tecnico'}
          ])} style={{display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={CI.download}/>CSV Gastos</Btn>
        </div>
      </div>

      <div className="print-area" style={{flex:1,overflowY:'auto',padding:20}}>
        {loading?<div style={{textAlign:'center',color:C.t4,padding:40}}>Cargando...</div>:<>

        {tab==='mensual'&&(<>
          {/* Header para print */}
          <div style={{display:'none'}} className="print-only">
            <h1 style={{margin:'0 0 4px'}}>Reporte Mensual — {periodo}</h1>
            <p style={{margin:'0 0 20px',color:'#666'}}>{ORG.nombre}</p>
          </div>

          {/* KPIs */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:16,marginBottom:22}}>
            <KpiCard label="MRR rentas" value={fmt(mrr)} sub={`${d.rentas.length} contratos`} tone="green" icon={KI.dollar}/>
            <KpiCard label="Ingresos registrados" value={fmt(totalIng)} sub={`${d.ingresos.length} registros`} tone="primary" icon={KI.trend}/>
            <KpiCard label="Gastos taller" value={fmt(totalGastos)} sub={`${d.gastos.length} registros`} tone="red" icon={KI.tool}/>
            <KpiCard label="Utilidad estimada" value={fmt(utilidad)} sub="MRR + ingresos − gastos" tone={utilidad>=0?'green':'red'} icon={KI.check}/>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
            {/* Top clientes */}
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:20,boxShadow:SH.sm}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:14}}>Top clientes por ingreso</div>
              {porClienteIng.length===0?<div style={{color:C.t4,fontSize:13,textAlign:'center',padding:16}}>Sin registros</div>
              :porClienteIng.map((c,i)=>(
                <div key={i} className="radar-hrow" style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 4px',borderRadius:8,borderBottom:`1px solid ${C.bg3}`}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span className="radar-num" style={{fontSize:11,color:C.t4,fontWeight:700,width:18}}>{i+1}</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:C.t1}}>{c.empresa}</div>
                      <div style={{fontSize:10.5,color:C.t4}}>{c.n} registros</div>
                    </div>
                  </div>
                  <div className="radar-num" style={{fontSize:15,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>{fmt(c.total)}</div>
                </div>
              ))}
            </div>

            {/* Top vendedores */}
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:20,boxShadow:SH.sm}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:14}}>Ingresos por vendedor</div>
              {porVendedorIng.length===0?<div style={{color:C.t4,fontSize:13,textAlign:'center',padding:16}}>Sin registros con vendedor</div>
              :porVendedorIng.map((v,i)=>(
                <div key={i} className="radar-hrow" style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 4px',borderRadius:8,borderBottom:`1px solid ${C.bg3}`}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <AvatarChip name={v.v} size={28}/>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:C.t1}}>{v.v}</div>
                      <div style={{fontSize:10.5,color:C.t4}}>{v.n} registros</div>
                    </div>
                  </div>
                  <div className="radar-num" style={{fontSize:15,fontWeight:700,letterSpacing:'-0.015em',color:C.greenText}}>{fmt(v.total)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Rentas activas */}
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:20,marginBottom:16,overflowX:'auto',boxShadow:SH.sm}}>
            <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:14}}>Contratos activos — MRR <span className="radar-num">{fmt(mrr)}</span></div>
            <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',fontSize:13,minWidth:560}}>
              <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                {['Cliente','Equipo','Tipo cobro','Valor/mes','Inicio'].map(h=>(
                  <th key={h} style={{padding:'8px 10px',textAlign:h==='Valor/mes'?'right':'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{d.rentas.map(r=>(
                <tr key={r.id} style={{borderBottom:`1px solid ${C.bg3}`}}>
                  <td style={{padding:'9px 10px',fontWeight:600,color:C.t1}}>{r.clientes?.empresa||'—'}</td>
                  <td className="radar-num" style={{padding:'9px 10px',color:C.t3}}>{r.equipos?.codigo}</td>
                  <td style={{padding:'9px 10px',color:C.t3}}>{r.tipo_cobro||'mensual'}</td>
                  <td className="radar-num" style={{padding:'9px 10px',fontWeight:700,color:C.greenText,textAlign:'right'}}>{fmt(r.valor_mensual||0)}</td>
                  <td className="radar-num" style={{padding:'9px 10px',color:C.t4}}>{fecha(r.fecha_inicio)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>

          {/* Gastos del mes */}
          {d.gastos.length>0&&(
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:20,overflowX:'auto',boxShadow:SH.sm}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:14}}>Gastos de taller — <span className="radar-num">{fmt(totalGastos)}</span></div>
              <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',fontSize:13,minWidth:640}}>
                <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                  {['Equipo','Tipo','Descripción','M.O.','Repuestos','Total','Fecha'].map(h=>(
                    <th key={h} style={{padding:'8px 10px',textAlign:['M.O.','Repuestos','Total'].includes(h)?'right':'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{d.gastos.map(g=>(
                  <tr key={g.id} style={{borderBottom:`1px solid ${C.bg3}`}}>
                    <td style={{padding:'8px 10px',fontWeight:600,color:C.t1}}>{g.equipos?.codigo||'—'}</td>
                    <td style={{padding:'8px 10px',color:C.t3}}>{g.tipo}</td>
                    <td style={{padding:'8px 10px',color:C.t3,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{g.descripcion||'—'}</td>
                    <td className="radar-num" style={{padding:'8px 10px',color:C.t2,textAlign:'right'}}>{fmt(g.mano_obra||0)}</td>
                    <td className="radar-num" style={{padding:'8px 10px',color:C.t2,textAlign:'right'}}>{fmt(g.costo_repuestos||0)}</td>
                    <td className="radar-num" style={{padding:'8px 10px',fontWeight:700,color:C.redText,textAlign:'right'}}>{fmt(g.total||0)}</td>
                    <td className="radar-num" style={{padding:'8px 10px',color:C.t4}}>{fecha(g.fecha)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </>)}

        {false&&(<>
          <div style={{display:'grid',gap:12,marginBottom:22}}>
            {metasPorVendedor.length===0
              ?<div style={{textAlign:'center',color:C.t4,padding:60,fontSize:14}}>Sin check-ins registrados en {periodo}</div>
              :metasPorVendedor.map(v=>(
              <div key={v.nombre} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:20,boxShadow:SH.sm}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:14}}>
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <AvatarChip name={v.nombre} size={40}/>
                    <div>
                      <div style={{fontSize:16,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>{v.nombre}</div>
                      <div style={{fontSize:12,color:C.t4}}>{v.dias} días con check-in en {periodo}</div>
                    </div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    {v.pct!==null&&<div className="radar-num" style={{fontSize:24,fontWeight:700,letterSpacing:'-0.02em',color:pctC(v.pct)}}>{v.pct}%</div>}
                    <div style={{fontSize:11,color:C.t4}}>cumplimiento</div>
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
                  {[
                    ['Meta acumulada',fmt(v.metaTotal),C.t1],
                    ['Avance esperado',v.metaSemanal>0?fmt(Math.round(v.metaSemanal/5*v.dias)):fmt(v.metaTotal),C.orangeText],
                    ['Ingreso real',fmt(v.realTotal),pctC(v.pct)],
                  ].map(([l,val,c])=>(
                    <div key={l} style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'10px 14px',textAlign:'center'}}>
                      <div style={{fontSize:10.5,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>{l}</div>
                      <div className="radar-num" style={{fontSize:17,fontWeight:700,letterSpacing:'-0.015em',color:c}}>{val}</div>
                    </div>
                  ))}
                </div>
                {/* Barra progreso (goalBar semántico) */}
                {v.metaTotal>0&&(
                  <div style={{marginTop:12}}>
                    <div style={{height:8,background:C.bg3,borderRadius:R.pill,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${Math.min(v.pct||0,100)}%`,background:(v.pct||0)>=85?C.green:(v.pct||0)>=70?C.blue:C.orange,borderRadius:R.pill,transition:'width .4s cubic-bezier(.4,0,.2,1)'}}/>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Tabla detalle días */}
          {d.metas.length>0&&(
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:20,overflowX:'auto',boxShadow:SH.sm}}>
              <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:14}}>Detalle por día</div>
              <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse',fontSize:13,minWidth:560}}>
                <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
                  {['Fecha','Vendedor','Meta día','Meta semana','Prioridad'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:['Meta día','Meta semana'].includes(h)?'right':'left',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{d.metas.map(m=>(
                  <tr key={m.id} style={{borderBottom:`1px solid ${C.bg3}`}}>
                    <td className="radar-num" style={{padding:'9px 12px',color:C.t3}}>{fecha(m.fecha)}</td>
                    <td style={{padding:'9px 12px',fontWeight:600,color:C.t1}}>{m.usuario_nombre||'—'}</td>
                    <td className="radar-num" style={{padding:'9px 12px',fontWeight:700,color:C.blue,textAlign:'right'}}>{fmt(m.meta_ingreso||0)}</td>
                    <td className="radar-num" style={{padding:'9px 12px',color:C.t3,textAlign:'right'}}>{m.meta_semanal?fmt(m.meta_semanal):'—'}</td>
                    <td style={{padding:'9px 12px',color:C.t4,maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.notas||'—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </>)}
        </>}
      </div>
    </div>
  );
}

// ─── BIENVENIDA DEL DÍA ───────────────────────────────────────────────────────
// Pantalla ligera al iniciar sesión: saludo + frase motivadora + dato del día.
// El "dato del día" cambia según el departamento del usuario y rota cada día.
const BIENV_FRASES = [
  { t:'El éxito es la suma de pequeños esfuerzos repetidos cada día.', a:'Robert Collier' },
  { t:'No cuentes los días; haz que los días cuenten.', a:'Muhammad Ali' },
  { t:'La suerte aparece cuando la preparación se topa con la oportunidad.', a:'Séneca' },
  { t:'La disciplina es el puente entre las metas y los logros.', a:'Jim Rohn' },
  { t:'Empieza donde estás, usa lo que tienes, haz lo que puedas.', a:'Arthur Ashe' },
  { t:'El mejor momento para plantar un árbol fue hace 20 años; el segundo mejor es hoy.', a:'Proverbio' },
  { t:'La calidad no es un acto, es un hábito.', a:'Aristóteles' },
  { t:'Hazlo con miedo, pero hazlo.', a:'' },
];
const BIENV_DATOS = {
  ventas: [
    'Regla 70/30: en una buena reunión de ventas el cliente habla el 70% del tiempo. Tu trabajo es preguntar y escuchar.',
    'El 80% de las ventas se cierran entre el 5º y el 12º contacto, pero la mayoría se rinde en el 2º. La constancia vende.',
    'Vendé el resultado, no el producto: el cliente no quiere un montacargas, quiere mover su carga sin parar la operación.',
    'El silencio cierra ventas: después de decir el precio, callate. El primero que habla suele ceder.',
    'Una objeción es interés disfrazado. El que no piensa comprar solo dice "lo pienso".',
    'Anclaje de precio: mostrá primero la opción premium y las demás se verán accesibles en comparación.',
    'Dar seguimiento el mismo día puede multiplicar tu tasa de cierre. La velocidad de respuesta es ventaja competitiva.',
  ],
  finanzas: [
    'Una venta no es venta hasta que se cobra. El efectivo es el oxígeno del negocio.',
    'La contabilidad de partida doble la describió Luca Pacioli en 1494 y sigue siendo la base de toda empresa moderna.',
    'El DSO (días promedio de cobro): mientras más bajo, más sano el flujo. Medilo cada mes.',
    'A los 90 días de atraso, una cuenta por cobrar pierde gran parte de su valor recuperable. Cobrar a tiempo es rentabilidad.',
    'Una factura clara —fecha, monto y forma de pago visibles— se paga hasta 2x más rápido.',
    'Vigilá el efectivo, no solo la utilidad: empresas rentables quiebran por quedarse sin caja.',
  ],
  taller: [
    'El mantenimiento preventivo cuesta 1; el correctivo cuesta hasta 5. Adelantarse siempre paga.',
    'Cerca del 70% de las fallas hidráulicas vienen de aceite contaminado. Filtros limpios = equipo vivo.',
    'Un equipo bien calibrado puede consumir hasta 15% menos combustible. El ajuste fino ahorra dinero.',
    'Registrar cada falla crea un historial que predice la próxima. Los datos del taller son oro.',
    'Revisar la presión de llantas cada semana reduce desgaste y accidentes. Lo simple, hecho siempre, rinde.',
    'El primer montacargas moderno nació en los años 1920; hoy mueven gran parte del comercio mundial.',
  ],
  gerencia: [
    'Principio de Pareto: el 20% de tus clientes genera el 80% del ingreso. ¿Sabés quiénes son?',
    'El reconocimiento cuesta $0 y sube la productividad del equipo. Felicitá hoy a alguien por su trabajo.',
    '"La cultura se desayuna a la estrategia." — atribuido a Peter Drucker.',
    'Lo que no se mide, se adivina. Definí 3 indicadores y revisalos cada semana.',
    'Delegar no es soltar el control, es multiplicar tu tiempo: lo que otro hace al 80%, delegalo.',
    'Decisiones lentas matan oportunidades; decisiones sin datos matan empresas. Buscá el equilibrio.',
  ],
  general: [
    'Tu cerebro rinde mejor en bloques de ~90 minutos con pausas. Trabajá enfocado, luego respirá.',
    'Escribir tus 3 prioridades del día antes de abrir el correo multiplica tu enfoque.',
    'Caminar 5 minutos cada hora y tomar agua mejora memoria y ánimo. El cuerpo sostiene la mente.',
    'Decir "no" a lo urgente sin importancia te deja tiempo para lo importante sin urgencia.',
  ],
};
const BIENV_DEPTO = {
  ventas:        { key:'ventas',   label:'Ventas',    emoji:'💼' },
  gerente_ventas:{ key:'gerencia', label:'Gerencia',  emoji:'📈' },
  admin:         { key:'gerencia', label:'Dirección', emoji:'🧭' },
  finanzas:      { key:'finanzas', label:'Finanzas',  emoji:'💰' },
  taller:        { key:'taller',   label:'Taller',    emoji:'🔧' },
};

function Bienvenida({ usuario, onClose }) {
  const cerrar = () => {
    try { localStorage.setItem(`bienvenida_${usuario.id}_${new Date().toISOString().split('T')[0]}`,'1'); } catch(e){}
    onClose();
  };
  const ahora = new Date();
  const h = ahora.getHours();
  const saludo = h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches';
  const emojiHora = h < 12 ? '☀️' : h < 19 ? '🌤️' : '🌙';
  const fechaLarga = ahora.toLocaleDateString('es-HN',{weekday:'long',day:'numeric',month:'long'});
  const nombre = (usuario.nombre||'').split(' ')[0];

  const depto = BIENV_DEPTO[usuario.rol] || { key:'general', label:'Equipo', emoji:'⭐' };
  const dia = Math.floor(Date.now()/86400000);
  const frase = BIENV_FRASES[dia % BIENV_FRASES.length];
  const datos = BIENV_DATOS[depto.key] || BIENV_DATOS.general;
  const dato  = datos[dia % datos.length];

  return (
    <div onClick={cerrar} style={{position:'fixed',inset:0,background:'rgba(8,12,17,.96)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',padding:20,overflowY:'auto'}}>
      <div onClick={e=>e.stopPropagation()} style={{width:'100%',maxWidth:460,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'34px 30px',boxShadow:SH.lg,position:'relative'}}>
        {/* Acento superior */}
        <div style={{position:'absolute',top:0,left:24,right:24,height:3,borderRadius:3,background:'linear-gradient(90deg,#0F766E,#0D9488)'}}/>

        {/* Saludo */}
        <div style={{textAlign:'center',marginBottom:24}}>
          <div style={{fontSize:34,marginBottom:8}}>{emojiHora}</div>
          <div style={{fontSize:22,fontWeight:900,color:C.t1,marginBottom:4}}>{saludo}, {nombre}</div>
          <div style={{fontSize:12,color:C.t4,textTransform:'capitalize'}}>{fechaLarga}</div>
          <div style={{display:'inline-block',marginTop:10,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.pill,padding:'3px 12px',fontSize:11,fontWeight:600,color:C.t3}}>
            {depto.emoji} {depto.label}
          </div>
        </div>

        {/* Frase del día */}
        <div style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:14,padding:'18px 20px',marginBottom:14}}>
          <div style={{fontSize:18,lineHeight:1.45,color:C.t1,fontWeight:600,fontStyle:'italic'}}>“{frase.t}”</div>
          {frase.a && <div style={{fontSize:12,color:C.t4,marginTop:8,textAlign:'right'}}>— {frase.a}</div>}
        </div>

        {/* Dato del día (según depto) */}
        <div style={{background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:R.lg,padding:'16px 18px',marginBottom:24}}>
          <div style={{fontSize:10,fontWeight:800,color:C.blue,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8,display:'flex',alignItems:'center',gap:6}}><CIcon d={CI.bulb} size={13}/>Dato del día</div>
          <div style={{fontSize:13.5,lineHeight:1.5,color:C.t2}}>{dato}</div>
        </div>

        <button onClick={cerrar}
          style={{width:'100%',padding:14,borderRadius:12,border:'none',background:'linear-gradient(135deg,#0F766E,#0D5F58)',color:'#fff',fontSize:15,fontWeight:800,cursor:'pointer'}}>
          Vamos 🚀
        </button>
      </div>
    </div>
  );
}

// ─── TALLER ───────────────────────────────────────────────────────────────────
const TECNICOS = ['Tornero','Soldador','Mecánico A','Mecánico B1','Mecánico B2','Auxiliar'];
// Estados de OT con tripleta DS v3 (pleno/texto/tinte/borde) — dot-pill como el CRM.
const OT_TRI = {
  abierta:     { label:'Abierta',     color:C.orange,   text:C.orangeText, bg:C.orangeTint, border:C.orangeBorder },
  en_progreso: { label:'En Progreso', color:C.info,     text:C.infoText,   bg:C.infoTint,   border:C.infoBorder },
  completada:  { label:'Completada',  color:C.green,    text:C.greenText,  bg:C.greenTint,  border:C.greenBorder },
  cerrada:     { label:'Cerrada',     color:'#65748B',  text:'#65748B',    bg:C.bg3,        border:C.border },
};
const OT_ESTADOS = { abierta:C.orange, en_progreso:C.info, completada:C.green, cerrada:'#65748B' };
const OT_ESTADOS_LABEL = { abierta:'Abierta', en_progreso:'En Progreso', completada:'Completada', cerrada:'Cerrada' };
const OTStatusChip = ({ estado }) => {
  const s = OT_TRI[estado] || { label:estado, color:C.t4, text:C.t3, bg:C.bg3, border:C.border };
  return <span style={{display:'inline-flex',alignItems:'center',gap:6,background:s.bg,color:s.text,border:`1px solid ${s.border}`,borderRadius:R.pill,padding:'2px 9px',fontSize:10.5,fontWeight:600,whiteSpace:'nowrap'}}><span style={{width:6,height:6,borderRadius:'50%',background:s.color,flexShrink:0}}/>{s.label}</span>;
};

function Taller({ equipos }) {
  const [tab, setTab]           = useState('dashboard');
  const [ots, setOts]           = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modalNueva, setModalNueva] = useState(false);
  const [modalDetalle, setModalDetalle] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [f, setF] = useState({
    numero:'', equipo_id:'', cliente_nombre:'', falla_reportada:'', diagnostico:'',
    trabajo_realizado:'', repuestos_usados:'', tiempo_reparacion:'', tecnico:'',
    lugar:'montasa', estado:'abierta', sugerencias:'', observaciones:'',
    costo_repuestos:'', costo_mano_obra:'', fecha_entrada: new Date().toISOString().split('T')[0], fecha_cierre:'',
  });

  const cargar = async () => {
    setLoading(true);
    let q = supabase.from('ordenes_trabajo').select('*,equipos(codigo,modelo,tipo_equipo)').order('created_at',{ascending:false}).limit(200);
    if (filtroEstado) q = q.eq('estado', filtroEstado);
    const { data } = await q; setOts(data||[]); setLoading(false);
  };
  useEffect(()=>{ cargar(); },[filtroEstado]);

  const guardar = async () => {
    if (!f.falla_reportada||!f.tecnico) return; setSaving(true);
    const num = `OT-${Date.now().toString().slice(-6)}`;
    const { error } = await supabase.from('ordenes_trabajo').insert({
      ...f,
      numero: f.numero||num,
      costo_repuestos: parseFloat(f.costo_repuestos)||0,
      costo_mano_obra: parseFloat(f.costo_mano_obra)||0,
      tiempo_reparacion: parseFloat(f.tiempo_reparacion)||null,
    });
    if (error) { toast('No se pudo crear la OT: '+error.message); setSaving(false); return; }
    setSaving(false); setModalNueva(false);
    setF({ numero:'', equipo_id:'', cliente_nombre:'', falla_reportada:'', diagnostico:'', trabajo_realizado:'', repuestos_usados:'', tiempo_reparacion:'', tecnico:'', lugar:'montasa', estado:'abierta', sugerencias:'', observaciones:'', costo_repuestos:'', costo_mano_obra:'', fecha_entrada:new Date().toISOString().split('T')[0], fecha_cierre:'' });
    cargar();
  };

  const cambiarEstado = async (id, nuevoEstado) => {
    const patch = { estado: nuevoEstado };
    if (nuevoEstado==='cerrada'||nuevoEstado==='completada') patch.fecha_cierre = new Date().toISOString().split('T')[0];
    const { error } = await supabase.from('ordenes_trabajo').update(patch).eq('id',id);
    if (error) { toast('No se pudo cambiar el estado: '+error.message); return; }
    setModalDetalle(prev => prev ? {...prev,...patch} : null);
    cargar();
  };

  const otsAbiertas   = ots.filter(o=>o.estado==='abierta');
  const otsEnProg     = ots.filter(o=>o.estado==='en_progreso');
  const otsCompletadas= ots.filter(o=>o.estado==='completada');
  const otsCerradas   = ots.filter(o=>o.estado==='cerrada');
  const enTallerAhora = equipos.filter(e=>e.estado==='mantenimiento'||e.estado==='reparacion');

  // Carga de técnicos: contar OTs abiertas o en progreso por técnico
  const cargaTecnico = TECNICOS.map(t=>({
    nombre:t,
    abiertas: ots.filter(o=>o.tecnico===t&&(o.estado==='abierta'||o.estado==='en_progreso')).length,
    completadas: ots.filter(o=>o.tecnico===t&&o.estado==='completada').length,
  }));

  const inp = k => v => setF(p=>({...p,[k]:v}));

  const TABS = [
    { id:'dashboard', label:'Dashboard' },
    { id:'ordenes',   label:`Órdenes (${ots.filter(o=>o.estado!=='cerrada').length})` },
    { id:'tecnicos',  label:'Técnicos' },
  ];

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* Header — flota sobre gris, tabs segmented (receta 7.4) */}
      <div style={{padding:'12px 16px 4px',display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
        <Seg items={TABS.map(t=>[t.id,t.label])} value={tab} onChange={setTab}/>
        {tab==='ordenes'&&(
          <select className="radar-input" value={filtroEstado} onChange={e=>setFiltroEstado(e.target.value)}
            style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'0 12px',height:34,color:C.t1,fontSize:12.5,boxShadow:SH.xs,cursor:'pointer'}}>
            <option value="">Todos los estados</option>
            {Object.entries(OT_ESTADOS_LABEL).map(([v,l])=><option key={v} value={v}>{l}</option>)}
          </select>
        )}
        <div style={{marginLeft:'auto'}}>
          <Btn onClick={()=>setModalNueva(true)} style={{display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>}/>Nueva OT</Btn>
        </div>
      </div>

      <div style={{flex:1,overflowY:'auto',padding:'12px 20px 20px'}}>

        {/* ── DASHBOARD ── */}
        {tab==='dashboard'&&(
          <>
            {/* KPI tiles estilo Stripe */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12,marginBottom:24}}>
              <KpiCard label="OTs Abiertas"     value={otsAbiertas.length}    sub="pendiente de acción" icon={KI.tool}   tone="orange" onClick={()=>setTab('ordenes')}/>
              <KpiCard label="En Progreso"      value={otsEnProg.length}      sub="trabajando ahora"    icon={KI.clock}  tone="info"   onClick={()=>setTab('ordenes')}/>
              <KpiCard label="Completadas"      value={otsCompletadas.length} sub="pendiente de cierre" icon={KI.check}  tone="green"  onClick={()=>setTab('ordenes')}/>
              <KpiCard label="Equipos en Taller" value={enTallerAhora.length} sub="mant + reparación"   icon={KI.box}    tone="red"    onClick={()=>setTab('ordenes')}/>
            </div>

            {/* Equipos en taller — cards de estado */}
            {enTallerAhora.length>0&&(
              <div style={{marginBottom:24}}>
                <div style={{fontSize:11,fontWeight:600,color:C.orangeText,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10,display:'flex',alignItems:'center',gap:7}}><CIcon d={KI.tool} size={13}/>Equipos en taller ahora</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12}}>
                  {enTallerAhora.map(e=>{
                    const otEq = ots.find(o=>o.equipo_id===e.id&&(o.estado==='abierta'||o.estado==='en_progreso'));
                    const s=STATUS[e.estado]||STATUS.desconocido;
                    return (
                      <div key={e.id} className="radar-kcard" style={{background:C.bg2,border:`1px solid ${C.border}`,borderLeft:`3px solid ${s.color}`,borderRadius:R.md,padding:'12px 14px',boxShadow:SH.xs}}>
                        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                          <EquipIcon e={e} size={32} icon={16}/>
                          <div style={{minWidth:0}}>
                            <div style={{fontSize:14,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>{e.codigo}</div>
                            <div style={{fontSize:11,color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{[e.marca,e.modelo].filter(Boolean).join(' ')}</div>
                          </div>
                        </div>
                        {otEq?<div style={{fontSize:11,color:C.orangeText,display:'inline-flex',alignItems:'center',gap:5}}><span style={{width:6,height:6,borderRadius:'50%',background:C.orange}}/>OT {otEq.numero} · {otEq.tecnico}</div>
                          :<div style={{fontSize:11,color:C.t4}}>Sin OT activa</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* OTs abiertas recientes */}
            <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10}}>Órdenes abiertas y en progreso</div>
            {[...otsAbiertas,...otsEnProg].slice(0,10).map(o=>(
              <OTCard key={o.id} ot={o} onClick={()=>setModalDetalle(o)} onEstado={cambiarEstado}/>
            ))}
            {otsAbiertas.length===0&&otsEnProg.length===0&&(
              <div style={{textAlign:'center',color:C.t4,padding:'48px 20px',fontSize:14,display:'flex',flexDirection:'column',alignItems:'center',gap:10}}>
                <span style={{width:52,height:52,borderRadius:R.lg,background:C.greenTint,border:`1px solid ${C.greenBorder}`,color:C.greenText,display:'inline-flex',alignItems:'center',justifyContent:'center'}}><CIcon d={KI.check} size={24}/></span>
                No hay órdenes abiertas
              </div>
            )}
          </>
        )}

        {/* ── ÓRDENES ── */}
        {tab==='ordenes'&&(
          <>
            {loading?<div style={{textAlign:'center',color:C.t4,padding:40}}>Cargando...</div>
            :ots.length===0?<div style={{textAlign:'center',color:C.t4,padding:60,fontSize:14}}>Sin órdenes{filtroEstado?' con ese estado':''} aún</div>
            :ots.map(o=>(
              <OTCard key={o.id} ot={o} onClick={()=>setModalDetalle(o)} onEstado={cambiarEstado}/>
            ))}
          </>
        )}

        {/* ── TÉCNICOS ── */}
        {tab==='tecnicos'&&(
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
            {cargaTecnico.map(t=>(
              <div key={t.nombre} className="radar-kcard" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'16px 18px',boxShadow:SH.xs}}>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
                  <AvatarChip name={t.nombre} size={30}/>
                  <div style={{fontSize:14,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}>{t.nombre}</div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 10px',textAlign:'center'}}>
                    <div className="radar-num" style={{fontSize:20,fontWeight:700,letterSpacing:'-0.02em',color:t.abiertas>0?C.orangeText:C.t4}}>{t.abiertas}</div>
                    <div style={{fontSize:10,color:C.t4,marginTop:2,textTransform:'uppercase',letterSpacing:'.06em',fontWeight:600}}>Activas</div>
                  </div>
                  <div style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'8px 10px',textAlign:'center'}}>
                    <div className="radar-num" style={{fontSize:20,fontWeight:700,letterSpacing:'-0.02em',color:C.greenText}}>{t.completadas}</div>
                    <div style={{fontSize:10,color:C.t4,marginTop:2,textTransform:'uppercase',letterSpacing:'.06em',fontWeight:600}}>Completadas</div>
                  </div>
                </div>
                {t.abiertas>0&&(
                  <div style={{marginTop:10}}>
                    {ots.filter(o=>o.tecnico===t.nombre&&(o.estado==='abierta'||o.estado==='en_progreso')).map(o=>(
                      <div key={o.id} className="radar-hrow" onClick={()=>setModalDetalle(o)} style={{fontSize:11,color:C.t3,padding:'5px 4px',borderTop:`1px solid ${C.bg3}`,cursor:'pointer',marginTop:4,display:'flex',alignItems:'center',gap:6}}>
                        <span style={{width:6,height:6,borderRadius:'50%',background:OT_ESTADOS[o.estado],flexShrink:0}}/><span style={{color:OT_TRI[o.estado]?.text||C.t2,fontWeight:700}}>{o.numero}</span> — {o.equipos?.codigo||'Sin equipo'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal nueva OT */}
      <Modal open={modalNueva} onClose={()=>setModalNueva(false)} title="Nueva Orden de Trabajo" width={720}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:'0 16px'}}>
          <Inp label="Número OT" value={f.numero} onChange={inp('numero')} placeholder="OT-000001 (auto si vacío)"/>
          <Inp label="Fecha entrada" value={f.fecha_entrada} onChange={inp('fecha_entrada')} type="date"/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:'0 16px'}}>
          <SearchSel label="Equipo" value={f.equipo_id} onChange={inp('equipo_id')}
            options={[{v:'',l:'Sin equipo asignado'},...equipos.map(e=>({v:e.id,l:`${e.codigo} — ${e.modelo}`}))]}/>
          <Inp label="Cliente / Empresa" value={f.cliente_nombre} onChange={inp('cliente_nombre')} placeholder="Nombre del cliente"/>
        </div>
        <Inp label="Falla reportada *" value={f.falla_reportada} onChange={inp('falla_reportada')} placeholder="¿Qué reporta el cliente o qué se observó?" rows={2} required/>
        <Inp label="Diagnóstico técnico" value={f.diagnostico} onChange={inp('diagnostico')} placeholder="¿Cuál es el diagnóstico?" rows={2}/>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:'0 16px'}}>
          <Sel label="Técnico asignado *" value={f.tecnico} onChange={inp('tecnico')} required
            options={TECNICOS}/>
          <Sel label="Lugar de reparación" value={f.lugar} onChange={inp('lugar')}
            options={[{v:'montasa',l:'Taller propio'},{v:'cliente',l:'En sitio del cliente'}]}/>
        </div>
        <Inp label="Repuestos a usar" value={f.repuestos_usados} onChange={inp('repuestos_usados')} placeholder="Lista los repuestos necesarios" rows={2}/>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0 16px'}}>
          <Inp label="Tiempo estimado (horas)" value={f.tiempo_reparacion} onChange={inp('tiempo_reparacion')} type="number" placeholder="0"/>
          <Inp label="Costo repuestos ($)" value={f.costo_repuestos} onChange={inp('costo_repuestos')} type="number" placeholder="0"/>
          <Inp label="Mano de obra ($)" value={f.costo_mano_obra} onChange={inp('costo_mano_obra')} type="number" placeholder="0"/>
        </div>
        <Inp label="Trabajo realizado" value={f.trabajo_realizado} onChange={inp('trabajo_realizado')} placeholder="Descripción del trabajo efectuado" rows={2}/>
        <Inp label="Sugerencias futuras" value={f.sugerencias} onChange={inp('sugerencias')} placeholder="Mantenimientos o reparaciones futuras sugeridas" rows={2}/>
        <Inp label="Observaciones generales" value={f.observaciones} onChange={inp('observaciones')} rows={2}/>
        <Sel label="Estado" value={f.estado} onChange={inp('estado')}
          options={Object.entries(OT_ESTADOS_LABEL).map(([v,l])=>({v,l}))}/>
        <Btn onClick={guardar} disabled={!f.falla_reportada||!f.tecnico||saving} full>
          {saving?'Guardando...':'Crear Orden de Trabajo'}
        </Btn>
      </Modal>

      {/* Modal detalle OT */}
      {modalDetalle&&(
        <Modal open={!!modalDetalle} onClose={()=>setModalDetalle(null)} title={`${modalDetalle.numero||'OT'} — Detalle`} width={680}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
            {[
              {l:'Equipo', v:modalDetalle.equipos?.codigo||'Sin equipo'},
              {l:'Cliente', v:modalDetalle.cliente_nombre||'—'},
              {l:'Técnico', v:modalDetalle.tecnico||'—'},
              {l:'Lugar', v:modalDetalle.lugar==='cliente'?'En sitio del cliente':'Taller propio'},
              {l:'Fecha entrada', v:fecha(modalDetalle.fecha_entrada)},
              {l:'Fecha cierre', v:fecha(modalDetalle.fecha_cierre)},
            ].map(({l,v})=>(
              <div key={l} style={{background:C.bg3,borderRadius:8,padding:'10px 12px'}}>
                <div style={{fontSize:10,color:C.t4,marginBottom:3,textTransform:'uppercase',letterSpacing:'.06em'}}>{l}</div>
                <div style={{fontSize:13,fontWeight:700,color:C.t1}}>{v}</div>
              </div>
            ))}
          </div>
          {[
            {l:'Falla reportada', v:modalDetalle.falla_reportada},
            {l:'Diagnóstico', v:modalDetalle.diagnostico},
            {l:'Trabajo realizado', v:modalDetalle.trabajo_realizado},
            {l:'Repuestos usados', v:modalDetalle.repuestos_usados},
            {l:'Sugerencias futuras', v:modalDetalle.sugerencias},
            {l:'Observaciones', v:modalDetalle.observaciones},
          ].filter(x=>x.v).map(({l,v})=>(
            <div key={l} style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>{l}</div>
              <div style={{fontSize:13,color:C.t2,background:C.bg3,borderRadius:8,padding:'10px 12px',lineHeight:1.5}}>{v}</div>
            </div>
          ))}
          {(modalDetalle.costo_repuestos>0||modalDetalle.costo_mano_obra>0)&&(
            <div style={{display:'flex',gap:8,marginBottom:16}}>
              <div style={{flex:1,background:C.bg3,borderRadius:8,padding:'10px 12px',textAlign:'center'}}>
                <div style={{fontSize:10,color:C.t4,marginBottom:2}}>Repuestos</div>
                <div style={{fontSize:16,fontWeight:900,color:C.orange}}>{fmt(modalDetalle.costo_repuestos)}</div>
              </div>
              <div style={{flex:1,background:C.bg3,borderRadius:8,padding:'10px 12px',textAlign:'center'}}>
                <div style={{fontSize:10,color:C.t4,marginBottom:2}}>Mano de obra</div>
                <div style={{fontSize:16,fontWeight:900,color:C.blue}}>{fmt(modalDetalle.costo_mano_obra)}</div>
              </div>
              <div style={{flex:1,background:C.bg3,borderRadius:8,padding:'10px 12px',textAlign:'center'}}>
                <div style={{fontSize:10,color:C.t4,marginBottom:2}}>Total</div>
                <div style={{fontSize:16,fontWeight:900,color:C.green}}>{fmt((modalDetalle.costo_repuestos||0)+(modalDetalle.costo_mano_obra||0))}</div>
              </div>
            </div>
          )}
          {/* Cambio de estado */}
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:16}}>
            <div style={{fontSize:11,fontWeight:600,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10,display:'flex',alignItems:'center',gap:8}}>Estado actual<OTStatusChip estado={modalDetalle.estado}/></div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {Object.entries(OT_ESTADOS_LABEL).filter(([v])=>v!==modalDetalle.estado).map(([v,l])=>(
                <Btn key={v} variant={v==='cerrada'?'secondary':v==='completada'?'primary':'warn'}
                  onClick={()=>cambiarEstado(modalDetalle.id,v)}>
                  {l}
                </Btn>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function OTCard({ ot, onClick, onEstado }) {
  const s = OT_TRI[ot.estado] || { color:C.t4 };
  const total = (ot.costo_repuestos||0)+(ot.costo_mano_obra||0);
  return (
    <div className="radar-hrow" onClick={onClick} style={{background:C.bg2,border:`1px solid ${C.border}`,borderLeft:`3px solid ${s.color}`,borderRadius:R.md,padding:'12px 16px',marginBottom:8,cursor:'pointer',boxShadow:SH.xs}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
        <div style={{display:'flex',gap:10,minWidth:0,flex:1}}>
          {ot.equipos?.codigo&&<EquipIcon e={ot.equipos} size={34} icon={16}/>}
          <div style={{minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
              <span style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>{ot.numero||'OT'}</span>
              <OTStatusChip estado={ot.estado}/>
              {ot.equipos?.codigo&&<span style={{fontSize:11,color:C.t3}}>{ot.equipos.codigo}</span>}
              {ot.cliente_nombre&&<span style={{fontSize:11,color:C.t3}}>· {ot.cliente_nombre}</span>}
            </div>
            <div style={{fontSize:12,color:C.t2,marginBottom:3}}>{ot.falla_reportada}</div>
            <div style={{fontSize:11,color:C.t4,display:'flex',gap:12,flexWrap:'wrap'}}>
              <span style={{display:'inline-flex',alignItems:'center',gap:4}}><CIcon d={CI.user} size={11}/>{ot.tecnico||'Sin asignar'}</span>
              <span style={{display:'inline-flex',alignItems:'center',gap:4}}><CIcon d={CI.calendar} size={11}/>{fecha(ot.fecha_entrada)}</span>
              {ot.lugar==='cliente'&&<span style={{display:'inline-flex',alignItems:'center',gap:4}}><CIcon d={CI.pin} size={11}/>En sitio</span>}
            </div>
          </div>
        </div>
        {total>0&&<div className="radar-num" style={{fontSize:15,fontWeight:700,letterSpacing:'-0.015em',color:C.orangeText,flexShrink:0}}>{fmt(total)}</div>}
      </div>
    </div>
  );
}

// ─── VIÁTICOS ─────────────────────────────────────────────────────────────────
// Tripleta DS v3 por estado (dot-pill como el resto de la app).
const VIATICO_TRI = {
  pendiente: { label:'Pendiente', color:C.orange, text:C.orangeText, bg:C.orangeTint, border:C.orangeBorder },
  aprobado:  { label:'Aprobado',  color:C.green,  text:C.greenText,  bg:C.greenTint,  border:C.greenBorder },
  rechazado: { label:'Rechazado', color:C.red,    text:C.redText,    bg:C.redTint,    border:C.redBorder },
  liquidado: { label:'Liquidado', color:C.blue,   text:C.blue,       bg:C.primaryTint,border:C.primaryBorder },
};
const VIATICO_LABEL   = { pendiente:'Pendiente', aprobado:'Aprobado', rechazado:'Rechazado', liquidado:'Liquidado' };
const ViaticoChip = ({ estado }) => {
  const s = VIATICO_TRI[estado] || { label:estado, color:C.t4, text:C.t3, bg:C.bg3, border:C.border };
  return <span style={{display:'inline-flex',alignItems:'center',gap:6,background:s.bg,color:s.text,border:`1px solid ${s.border}`,borderRadius:R.pill,padding:'2px 9px',fontSize:10.5,fontWeight:600,whiteSpace:'nowrap'}}><span style={{width:6,height:6,borderRadius:'50%',background:s.color,flexShrink:0}}/>{s.label}</span>;
};

function Viaticos({ usuario }) {
  const [lista, setLista]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modalNueva, setModalNueva] = useState(false);
  const [modalDetalle, setModalDetalle] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [f, setF] = useState({
    numero:'', solicitante: usuario?.nombre||'', destino:'', motivo:'',
    fecha_salida:'', fecha_regreso:'',
    hotel:'', combustible:'', alimentacion:'', operador:'', otros:'', otros_descripcion:'',
    observaciones:'',
  });

  const cargar = async () => {
    setLoading(true);
    let q = supabase.from('viaticos').select('*').order('created_at',{ascending:false}).limit(200);
    if (filtroEstado) q = q.eq('estado',filtroEstado);
    if (usuario?.rol!=='admin' && usuario?.rol!=='gerente_ventas') q = q.eq('solicitante',usuario?.nombre);
    const { data } = await q; setLista(data||[]); setLoading(false);
  };
  useEffect(()=>{ cargar(); },[filtroEstado]);

  const calcTotal = (x) => (parseFloat(x.hotel)||0)+(parseFloat(x.combustible)||0)+(parseFloat(x.alimentacion)||0)+(parseFloat(x.operador)||0)+(parseFloat(x.otros)||0);

  const guardar = async () => {
    if (!f.destino||!f.motivo||!f.fecha_salida) return; setSaving(true);
    const num = `VT-${Date.now().toString().slice(-6)}`;
    const total = calcTotal(f);
    const { error } = await supabase.from('viaticos').insert({
      ...f,
      numero: f.numero||num,
      hotel: parseFloat(f.hotel)||0,
      combustible: parseFloat(f.combustible)||0,
      alimentacion: parseFloat(f.alimentacion)||0,
      operador: parseFloat(f.operador)||0,
      otros: parseFloat(f.otros)||0,
      total_estimado: total,
      estado: 'pendiente',
    });
    if (error) { toast('No se pudo guardar la solicitud: '+error.message); setSaving(false); return; }
    setSaving(false); setModalNueva(false);
    setF({ numero:'', solicitante:usuario?.nombre||'', destino:'', motivo:'', fecha_salida:'', fecha_regreso:'', hotel:'', combustible:'', alimentacion:'', operador:'', otros:'', otros_descripcion:'', observaciones:'' });
    cargar();
  };

  const cambiarEstado = async (id, nuevoEstado) => {
    const { error } = await supabase.from('viaticos').update({ estado: nuevoEstado, aprobado_por: usuario?.nombre }).eq('id',id);
    if (error) { toast('No se pudo actualizar: '+error.message); return; }
    setModalDetalle(prev => prev ? {...prev, estado:nuevoEstado} : null);
    cargar();
  };

  const inp = k => v => setF(p=>({...p,[k]:v}));
  const totalPreview = calcTotal(f);

  const pendientes = lista.filter(v=>v.estado==='pendiente');
  const aprobados  = lista.filter(v=>v.estado==='aprobado');

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* Header — flota sobre gris, sin franja blanca */}
      <div style={{padding:'12px 16px 4px',display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <div style={{fontSize:14,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,display:'inline-flex',alignItems:'center',gap:8}}><span style={{width:28,height:28,borderRadius:8,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,color:C.blue,display:'inline-flex',alignItems:'center',justifyContent:'center'}}><Icon id="viaticos" size={15}/></span>Solicitudes de Viáticos</div>
        {(usuario?.rol==='admin'||usuario?.rol==='gerente_ventas')&&(
          <select className="radar-input" value={filtroEstado} onChange={e=>setFiltroEstado(e.target.value)}
            style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'0 12px',height:34,color:C.t1,fontSize:12.5,boxShadow:SH.xs,cursor:'pointer'}}>
            <option value="">Todos</option>
            {Object.entries(VIATICO_LABEL).map(([v,l])=><option key={v} value={v}>{l}</option>)}
          </select>
        )}
        <div style={{marginLeft:'auto'}}>
          <Btn onClick={()=>setModalNueva(true)} style={{display:'inline-flex',alignItems:'center',gap:6}}><CIcon d={<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>}/>Nueva Solicitud</Btn>
        </div>
      </div>

      <div style={{flex:1,overflowY:'auto',padding:'12px 20px 20px'}}>
        {/* KPIs solo admin — estilo Stripe */}
        {usuario?.rol==='admin'&&(
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12,marginBottom:20}}>
            <KpiCard label="Pendientes"   value={lista.filter(x=>x.estado==='pendiente').length} sub="por revisar" icon={KI.clock}  tone="orange"/>
            <KpiCard label="Aprobados"    value={lista.filter(x=>x.estado==='aprobado').length}  sub="autorizados" icon={KI.check}  tone="green"/>
            <KpiCard label="Rechazados"   value={lista.filter(x=>x.estado==='rechazado').length} sub="no aprobados" icon={KI.funnel} tone="red"/>
            <KpiCard label="Total aprob." value={fmt(lista.filter(x=>x.estado==='aprobado').reduce((s,x)=>s+(x.total_estimado||0),0))} sub="monto autorizado" icon={KI.dollar} tone="primary"/>
          </div>
        )}

        {/* Lista */}
        {loading?<div style={{textAlign:'center',color:C.t4,padding:40}}>Cargando...</div>
        :lista.length===0?<div style={{textAlign:'center',color:C.t4,padding:60,fontSize:14}}>Sin solicitudes aún</div>
        :lista.map(v=>{
          const s = VIATICO_TRI[v.estado] || { color:C.t4 };
          return (
            <div key={v.id} className="radar-hrow" onClick={()=>setModalDetalle(v)}
              style={{background:C.bg2,border:`1px solid ${C.border}`,borderLeft:`3px solid ${s.color}`,borderRadius:R.md,padding:'14px 16px',marginBottom:8,cursor:'pointer',boxShadow:SH.xs}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:5,flexWrap:'wrap'}}>
                    <span style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>{v.numero}</span>
                    <ViaticoChip estado={v.estado}/>
                    <span style={{fontSize:11,color:C.t3,display:'inline-flex',alignItems:'center',gap:4}}><CIcon d={CI.user} size={11}/>{v.solicitante}</span>
                  </div>
                  <div style={{fontSize:13,color:C.t2,marginBottom:3,display:'flex',alignItems:'center',gap:5}}><CIcon d={CI.pin} size={12} style={{color:C.t4,flexShrink:0}}/>{v.destino} — {v.motivo}</div>
                  <div style={{fontSize:11,color:C.t4,display:'inline-flex',alignItems:'center',gap:5}}>
                    <CIcon d={CI.calendar} size={11}/>{fecha(v.fecha_salida)}{v.fecha_regreso?` → ${fecha(v.fecha_regreso)}`:''}
                  </div>
                </div>
                <div style={{textAlign:'right',flexShrink:0}}>
                  <div className="radar-num" style={{fontSize:18,fontWeight:700,letterSpacing:'-0.02em',color:C.greenText}}>{fmt(v.total_estimado||0)}</div>
                  <div className="radar-num" style={{fontSize:10,color:C.t4,marginTop:2}}>{fecha(v.created_at)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal nueva solicitud */}
      <Modal open={modalNueva} onClose={()=>setModalNueva(false)} title="Nueva Solicitud de Viáticos" width={680}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:'0 16px'}}>
          <Inp label="Solicitante" value={f.solicitante} onChange={inp('solicitante')} required/>
          <Inp label="Destino *" value={f.destino} onChange={inp('destino')} placeholder="Ciudad / cliente / lugar" required/>
        </div>
        <Inp label="Motivo del viaje *" value={f.motivo} onChange={inp('motivo')} placeholder="¿Para qué es el viaje? (entrega de equipo, visita cliente, etc.)" required/>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:'0 16px'}}>
          <Inp label="Fecha de salida *" value={f.fecha_salida} onChange={inp('fecha_salida')} type="date" required/>
          <Inp label="Fecha de regreso" value={f.fecha_regreso} onChange={inp('fecha_regreso')} type="date"/>
        </div>

        <div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10,marginTop:4}}>Gastos estimados</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:'0 16px'}}>
          <Inp label="Hotel ($)" value={f.hotel} onChange={inp('hotel')} type="number" placeholder="0"/>
          <Inp label="Combustible ($)" value={f.combustible} onChange={inp('combustible')} type="number" placeholder="0"/>
          <Inp label="Alimentación ($)" value={f.alimentacion} onChange={inp('alimentacion')} type="number" placeholder="0"/>
          <Inp label="Operador ($)" value={f.operador} onChange={inp('operador')} type="number" placeholder="0"/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(240px,100%),1fr))',gap:'0 16px'}}>
          <Inp label="Otros ($)" value={f.otros} onChange={inp('otros')} type="number" placeholder="0"/>
          <Inp label="Descripción otros" value={f.otros_descripcion} onChange={inp('otros_descripcion')} placeholder="¿Qué es?"/>
        </div>

        {totalPreview>0&&(
          <div style={{background:C.bg3,borderRadius:R.md,padding:'12px 16px',marginBottom:14,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:12,color:C.t3,fontWeight:600}}>Total estimado</span>
            <span className="radar-num" style={{fontSize:22,fontWeight:700,letterSpacing:'-0.025em',color:C.greenText}}>{fmt(totalPreview)}</span>
          </div>
        )}

        <Inp label="Observaciones" value={f.observaciones} onChange={inp('observaciones')} rows={2}/>
        <Btn onClick={guardar} disabled={!f.destino||!f.motivo||!f.fecha_salida||saving} full>
          {saving?'Guardando...':'Enviar Solicitud'}
        </Btn>
      </Modal>

      {/* Modal detalle viático */}
      {modalDetalle&&(
        <Modal open={!!modalDetalle} onClose={()=>setModalDetalle(null)} title={`${modalDetalle.numero} — Detalle`} width={580}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
            {[
              {l:'Solicitante', v:modalDetalle.solicitante},
              {l:'Destino', v:modalDetalle.destino},
              {l:'Motivo', v:modalDetalle.motivo},
              {l:'Salida', v:fecha(modalDetalle.fecha_salida)},
              {l:'Regreso', v:fecha(modalDetalle.fecha_regreso)},
              {l:'Aprobado por', v:modalDetalle.aprobado_por||'—'},
            ].map(({l,v})=>(
              <div key={l} style={{background:C.bg3,borderRadius:8,padding:'8px 12px'}}>
                <div style={{fontSize:10,color:C.t4,marginBottom:2,textTransform:'uppercase',letterSpacing:'.06em'}}>{l}</div>
                <div style={{fontSize:13,fontWeight:700,color:C.t1}}>{v||'—'}</div>
              </div>
            ))}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8,marginBottom:14}}>
            {[
              {l:'Hotel',        v:modalDetalle.hotel},
              {l:'Combustible',  v:modalDetalle.combustible},
              {l:'Alimentación', v:modalDetalle.alimentacion},
              {l:'Operador',     v:modalDetalle.operador},
              {l:'Otros',        v:modalDetalle.otros},
            ].map(({l,v})=>(
              <div key={l} style={{background:C.bg3,borderRadius:8,padding:'8px 10px',textAlign:'center'}}>
                <div style={{fontSize:10,color:C.t4,marginBottom:2}}>{l}</div>
                <div className="radar-num" style={{fontSize:13,fontWeight:700,letterSpacing:'-0.01em',color:v>0?C.orangeText:C.t4}}>{fmt(v||0)}</div>
              </div>
            ))}
          </div>
          <div style={{background:C.greenTint,border:`1px solid ${C.greenBorder}`,borderRadius:R.md,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:12,color:C.t3,fontWeight:600}}>Total estimado</span>
            <span className="radar-num" style={{fontSize:22,fontWeight:700,letterSpacing:'-0.025em',color:C.greenText}}>{fmt(modalDetalle.total_estimado||0)}</span>
          </div>
          {modalDetalle.observaciones&&(
            <div style={{marginBottom:14}}>
              <div style={{fontSize:10,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Observaciones</div>
              <div style={{fontSize:13,color:C.t2,background:C.bg3,borderRadius:8,padding:'10px 12px'}}>{modalDetalle.observaciones}</div>
            </div>
          )}
          {/* Acciones admin */}
          {(usuario?.rol==='admin'||usuario?.rol==='gerente_ventas')&&modalDetalle.estado==='pendiente'&&(
            <div style={{display:'flex',gap:8,borderTop:`1px solid ${C.border}`,paddingTop:14}}>
              <Btn variant="primary" onClick={()=>cambiarEstado(modalDetalle.id,'aprobado')} full style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6}}><CIcon d={KI.check}/>Aprobar</Btn>
              <Btn variant="danger"  onClick={()=>cambiarEstado(modalDetalle.id,'rechazado')} full style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6}}><CIcon d={<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>}/>Rechazar</Btn>
            </div>
          )}
          {(usuario?.rol==='admin'||usuario?.rol==='gerente_ventas')&&modalDetalle.estado==='aprobado'&&(
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14}}>
              <Btn variant="secondary" onClick={()=>cambiarEstado(modalDetalle.id,'liquidado')} full style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6}}><CIcon d={CI.cash}/>Marcar como Liquidado</Btn>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ─── PRESENTACIÓN SEMANAL ─────────────────────────────────────────────────────
function PresentacionSemanal({ lunasISO, domingoISO, semLabel, empresaActiva, onClose }) {
  const [slide, setSlide] = useState(0);
  const [data, setData] = useState(null);

  const ETAPA_INFO_P = [
    {id:'prospecto',   label:'Prospecto',   color:'#64748B', prob:0.10},
    {id:'contactado',  label:'Contactado',  color:'#0D9488', prob:0.25},
    {id:'cotizacion',  label:'Cotización',  color:'#F59E0B', prob:0.40},
    {id:'negociacion', label:'Negociación', color:'#EC4899', prob:0.60},
    {id:'cerrado',     label:'Cerrado',     color:'#0B7AB8', prob:0.75},
    {id:'facturado',   label:'Facturado',   color:'#8B5CF6', prob:0.85},
    {id:'pagado',      label:'Pagado',      color:'#06B6D4', prob:0.92},
  ];

  useEffect(() => {
    const desde = lunasISO + 'T00:00:00';
    const hasta = domingoISO + 'T23:59:59';
    const fetchAll = async () => {
      let qC = supabase.from('clientes').select('id,empresa,vendedor,ciudad,industria').gte('created_at', desde).lte('created_at', hasta);
      let qA = supabase.from('visitas').select('vendedor,tipo,created_at').gte('created_at', desde).lte('created_at', hasta);
      let qG = supabase.from('oportunidades').select('id,titulo,cliente,vendedor,valor,fecha_cierre,linea_negocio').eq('etapa','cerrado_ganado').gte('fecha_cierre', lunasISO).lte('fecha_cierre', domingoISO);
      let qN = supabase.from('oportunidades').select('id,titulo,cliente,etapa,valor,vendedor').gte('created_at', desde).lte('created_at', hasta).not('etapa','eq','cerrado_perdido');
      let qP = supabase.from('oportunidades').select('etapa,valor').not('etapa','in','(cerrado_ganado,cerrado_perdido,prospecto_invalido,cliente_frio)');
      if (empresaActiva) {
        qC = qC.eq('empresa_id', empresaActiva);
        qG = qG.eq('empresa_id', empresaActiva);
        qN = qN.eq('empresa_id', empresaActiva);
        qP = qP.eq('empresa_id', empresaActiva);
      }
      const [{data:c},{data:a},{data:g},{data:n},{data:p}] = await Promise.all([qC,qA,qG,qN,qP]);
      setData({ clientes:c||[], actividades:a||[], ganadas:g||[], nuevosOpps:n||[], pipeline:p||[] });
    };
    fetchAll();
  }, [lunasISO, domingoISO, empresaActiva]);

  useEffect(() => {
    const handler = e => {
      if (e.key==='ArrowRight'||e.key===' ') { e.preventDefault(); setSlide(s=>Math.min(s+1,7)); }
      if (e.key==='ArrowLeft') { e.preventDefault(); setSlide(s=>Math.max(s-1,0)); }
      if (e.key==='Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const cl = data?.clientes||[], ac = data?.actividades||[], ga = data?.ganadas||[], no = data?.nuevosOpps||[], pi = data?.pipeline||[];
  const totalVis = ac.filter(x=>x.tipo==='visita').length;
  const totalLla = ac.filter(x=>x.tipo==='llamada').length;
  const totalVentas = ga.reduce((s,o)=>s+(o.valor||0),0);
  const totalPipe = pi.reduce((s,o)=>s+(o.valor||0),0);
  const pipePondP = pi.reduce((s,o)=>s+(o.valor||0)*(ETAPAS_PROB[o.etapa]||0),0);

  const pipeEtapas = ETAPA_INFO_P.map(e=>({...e,
    count:pi.filter(o=>o.etapa===e.id).length,
    bruto:pi.filter(o=>o.etapa===e.id).reduce((s,o)=>s+(o.valor||0),0),
    pond:pi.filter(o=>o.etapa===e.id).reduce((s,o)=>s+(o.valor||0)*e.prob,0),
  })).filter(e=>e.count>0);

  const nuevosPorEtapa = ETAPA_INFO_P.map(e=>({...e,
    count:no.filter(o=>o.etapa===e.id).length,
    bruto:no.filter(o=>o.etapa===e.id).reduce((s,o)=>s+(o.valor||0),0),
  })).filter(e=>e.count>0);

  const vendMap = {};
  ga.forEach(o=>{ if(!vendMap[o.vendedor]) vendMap[o.vendedor]={nombre:o.vendedor,deals:0,valor:0}; vendMap[o.vendedor].deals++; vendMap[o.vendedor].valor+=(o.valor||0); });
  const topVend = Object.values(vendMap).sort((a,b)=>b.valor-a.valor);
  const maxPipe = Math.max(...pipeEtapas.map(e=>e.bruto),1);
  const maxNuevos = Math.max(...nuevosPorEtapa.map(e=>e.count),1);

  const SLIDE_COUNT = 8;
  const loading = !data;

  const slides = [
    // 0 — Portada
    ()=>(
      <div style={{textAlign:'center'}}>
        <div style={{fontSize:12,color:'#475569',textTransform:'uppercase',letterSpacing:'.25em',marginBottom:20}}>{ORG.nombre}</div>
        <div style={{fontSize:80,fontWeight:900,color:'#FFFFFF',lineHeight:1,marginBottom:16}}>Reporte<br/>Semanal</div>
        <div style={{fontSize:26,fontWeight:700,color:C.blue,marginBottom:40}}>{semLabel}</div>
        <div style={{fontSize:15,color:'#475569'}}>Presiona <span style={{background:'rgba(255,255,255,.08)',borderRadius:5,padding:'2px 8px',fontFamily:'monospace'}}>→</span> para comenzar</div>
      </div>
    ),
    // 1 — Resumen
    ()=>(
      <div style={{width:'100%',maxWidth:940}}>
        <div style={{fontSize:12,color:'#475569',textTransform:'uppercase',letterSpacing:'.2em',marginBottom:36,textAlign:'center'}}>Resumen ejecutivo</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:20}}>
          {[
            ['👥','Clientes Nuevos',cl.length,'ingresados esta semana',C.green],
            ['📋','Actividades',totalVis+totalLla,`${totalVis} visitas · ${totalLla} llamadas`,C.blue],
            ['✅','Ventas Cerradas',ga.length,`$${Math.round(totalVentas).toLocaleString('en-US')}`,C.green],
            ['🎯','Nuevos Prospectos',no.length,'ingresados al pipeline',C.orange],
          ].map(([ico,lbl,val,sub,col])=>(
            <div key={lbl} style={{background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.08)',borderRadius:20,padding:'28px 24px',textAlign:'center'}}>
              <div style={{fontSize:34,marginBottom:14}}>{ico}</div>
              <div style={{fontSize:10,color:'#475569',textTransform:'uppercase',letterSpacing:'.12em',marginBottom:8}}>{lbl}</div>
              <div style={{fontSize:60,fontWeight:900,color:col,lineHeight:1,marginBottom:8}}>{val}</div>
              <div style={{fontSize:13,color:'#94A3B8'}}>{sub}</div>
            </div>
          ))}
        </div>
      </div>
    ),
    // 2 — Clientes nuevos
    ()=>(
      <div style={{width:'100%',maxWidth:820}}>
        <div style={{fontSize:12,color:'#475569',textTransform:'uppercase',letterSpacing:'.2em',marginBottom:8,textAlign:'center'}}>👥 Clientes Nuevos Ingresados</div>
        <div style={{fontSize:80,fontWeight:900,color:C.green,textAlign:'center',lineHeight:1,marginBottom:32}}>{cl.length}</div>
        {cl.length===0
          ? <div style={{textAlign:'center',color:'#475569',fontSize:18}}>Sin clientes nuevos esta semana</div>
          : <div style={{display:'grid',gridTemplateColumns:`repeat(${Math.min(cl.length<=3?cl.length:3,3)},1fr)`,gap:14}}>
              {cl.map(x=>(
                <div key={x.id} style={{background:'rgba(16,185,129,.06)',border:'1px solid rgba(16,185,129,.2)',borderRadius:16,padding:'18px 20px'}}>
                  <div style={{fontSize:17,fontWeight:800,color:'#FFFFFF',marginBottom:6}}>{x.empresa}</div>
                  <div style={{fontSize:12,color:'#94A3B8'}}>Vendedor: <span style={{color:'#FFFFFF'}}>{x.vendedor||'—'}</span></div>
                  {x.industria&&<div style={{fontSize:11,color:'#475569',marginTop:4}}>{x.industria}</div>}
                </div>
              ))}
            </div>
        }
      </div>
    ),
    // 3 — Actividades
    ()=>(
      <div style={{width:'100%',maxWidth:700,textAlign:'center'}}>
        <div style={{fontSize:12,color:'#475569',textTransform:'uppercase',letterSpacing:'.2em',marginBottom:36}}>📋 Actividades de la Semana</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24,marginBottom:36}}>
          <div style={{background:'rgba(11,122,184,.1)',border:'1px solid rgba(11,122,184,.25)',borderRadius:24,padding:'36px 24px'}}>
            <div style={{fontSize:10,color:'#475569',textTransform:'uppercase',letterSpacing:'.12em',marginBottom:16}}>Visitas presenciales</div>
            <div style={{fontSize:90,fontWeight:900,color:C.blue,lineHeight:1}}>{totalVis}</div>
          </div>
          <div style={{background:'rgba(16,185,129,.1)',border:'1px solid rgba(16,185,129,.25)',borderRadius:24,padding:'36px 24px'}}>
            <div style={{fontSize:10,color:'#475569',textTransform:'uppercase',letterSpacing:'.12em',marginBottom:16}}>Llamadas / contactos</div>
            <div style={{fontSize:90,fontWeight:900,color:C.green,lineHeight:1}}>{totalLla}</div>
          </div>
        </div>
        <div style={{fontSize:18,color:'#94A3B8'}}>{totalVis+totalLla} interacciones con clientes esta semana</div>
      </div>
    ),
    // 4 — Ventas cerradas
    ()=>(
      <div style={{width:'100%',maxWidth:900}}>
        <div style={{fontSize:12,color:'#475569',textTransform:'uppercase',letterSpacing:'.2em',marginBottom:8,textAlign:'center'}}>✅ Ventas Cerradas</div>
        <div style={{fontSize:64,fontWeight:900,color:C.green,textAlign:'center',lineHeight:1,marginBottom:6}}>${Math.round(totalVentas).toLocaleString('en-US')}</div>
        <div style={{fontSize:16,color:'#94A3B8',textAlign:'center',marginBottom:28}}>{ga.length} {ga.length===1?'venta cerrada':'ventas cerradas'}</div>
        {ga.length===0
          ? <div style={{textAlign:'center',color:'#475569',fontSize:18}}>Sin ventas cerradas esta semana</div>
          : <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {ga.map((o,i)=>(
                <div key={o.id} style={{display:'flex',alignItems:'center',background:'rgba(16,185,129,.05)',border:'1px solid rgba(16,185,129,.15)',borderRadius:14,padding:'14px 20px',gap:16}}>
                  <div style={{fontSize:22,fontWeight:900,color:C.green,minWidth:30,textAlign:'center'}}>{i+1}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:16,fontWeight:800,color:'#FFFFFF'}}>{o.titulo}</div>
                    <div style={{fontSize:12,color:'#94A3B8'}}>{o.cliente}{o.vendedor?` · ${o.vendedor}`:''}</div>
                  </div>
                  <div style={{fontSize:20,fontWeight:900,color:C.green}}>${(o.valor||0).toLocaleString('en-US')}</div>
                </div>
              ))}
            </div>
        }
      </div>
    ),
    // 5 — Nuevos prospectos por etapa
    ()=>(
      <div style={{width:'100%',maxWidth:800}}>
        <div style={{fontSize:12,color:'#475569',textTransform:'uppercase',letterSpacing:'.2em',marginBottom:8,textAlign:'center'}}>🎯 Nuevos Prospectos por Etapa</div>
        <div style={{fontSize:80,fontWeight:900,color:C.orange,textAlign:'center',lineHeight:1,marginBottom:32}}>{no.length}</div>
        {nuevosPorEtapa.length===0
          ? <div style={{textAlign:'center',color:'#475569',fontSize:18}}>Sin nuevos prospectos esta semana</div>
          : <div style={{display:'flex',flexDirection:'column',gap:14}}>
              {nuevosPorEtapa.map(e=>(
                <div key={e.id} style={{display:'flex',alignItems:'center',gap:14}}>
                  <div style={{width:110,fontSize:13,fontWeight:700,color:e.color,textAlign:'right',flexShrink:0}}>{e.label}</div>
                  <div style={{flex:1,height:40,background:'rgba(255,255,255,.05)',borderRadius:8,overflow:'hidden'}}>
                    <div style={{width:`${Math.max(e.count/maxNuevos*100,4)}%`,height:'100%',background:e.color,borderRadius:8,display:'flex',alignItems:'center',paddingLeft:14,boxSizing:'border-box'}}>
                      <span style={{fontSize:16,fontWeight:900,color:'#fff'}}>{e.count}</span>
                    </div>
                  </div>
                  <div style={{width:72,fontSize:12,color:'#64748B',textAlign:'right',flexShrink:0}}>${Math.round(e.bruto/1000)}k</div>
                </div>
              ))}
            </div>
        }
      </div>
    ),
    // 6 — Pipeline actual
    ()=>(
      <div style={{width:'100%',maxWidth:900}}>
        <div style={{fontSize:12,color:'#475569',textTransform:'uppercase',letterSpacing:'.2em',marginBottom:16,textAlign:'center'}}>🔻 Pipeline Actual por Etapa</div>
        <div style={{display:'flex',justifyContent:'center',gap:40,marginBottom:28}}>
          {[['Total pipeline',`$${Math.round(totalPipe/1000)}k`,'#FFFFFF'],['Ponderado',`$${Math.round(pipePondP/1000)}k`,C.green],['Oportunidades',pi.length,C.blue]].map(([l,v,col])=>(
            <div key={l} style={{textAlign:'center'}}>
              <div style={{fontSize:10,color:'#475569',textTransform:'uppercase',letterSpacing:'.1em',marginBottom:6}}>{l}</div>
              <div style={{fontSize:40,fontWeight:900,color:col}}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {pipeEtapas.map(e=>(
            <div key={e.id} style={{display:'flex',alignItems:'center',gap:14}}>
              <div style={{width:110,fontSize:13,fontWeight:700,color:e.color,textAlign:'right',flexShrink:0}}>{e.label}</div>
              <div style={{flex:1,height:34,background:'rgba(255,255,255,.05)',borderRadius:8,overflow:'hidden'}}>
                <div style={{width:`${Math.max(e.bruto/maxPipe*100,2)}%`,height:'100%',background:e.color,borderRadius:8}}/>
              </div>
              <div style={{width:110,fontSize:12,textAlign:'right',flexShrink:0}}>
                <span style={{fontWeight:700,color:e.color}}>{e.count}</span>
                <span style={{color:'#475569'}}> · </span>
                <span style={{color:'#94A3B8'}}>${Math.round(e.bruto/1000)}k</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
    // 7 — Ranking vendedores
    ()=>(
      <div style={{width:'100%',maxWidth:700}}>
        <div style={{fontSize:12,color:'#475569',textTransform:'uppercase',letterSpacing:'.2em',marginBottom:36,textAlign:'center'}}>🏆 Ventas Cerradas por Vendedor</div>
        {topVend.length===0
          ? <div style={{textAlign:'center',color:'#475569',fontSize:18}}>Sin ventas registradas esta semana</div>
          : <div style={{display:'flex',flexDirection:'column',gap:16}}>
              {topVend.map((v,i)=>(
                <div key={v.nombre} style={{display:'flex',alignItems:'center',background:'rgba(255,255,255,.04)',border:`1px solid ${i===0?C.orange+'50':'rgba(255,255,255,.08)'}`,borderRadius:18,padding:'20px 28px',gap:20}}>
                  <div style={{fontSize:36}}>{['🥇','🥈','🥉'][i]||'👤'}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:22,fontWeight:900,color:'#FFFFFF'}}>{v.nombre}</div>
                    <div style={{fontSize:13,color:'#64748B'}}>{v.deals} venta{v.deals!==1?'s':''} cerrada{v.deals!==1?'s':''}</div>
                  </div>
                  <div style={{fontSize:30,fontWeight:900,color:i===0?C.orange:'#FFFFFF'}}>${Math.round(v.valor).toLocaleString('en-US')}</div>
                </div>
              ))}
            </div>
        }
      </div>
    ),
  ];

  return (
    <div style={{position:'fixed',inset:0,background:'#060A12',zIndex:2000,display:'flex',flexDirection:'column',fontFamily:"'Inter','DM Sans',system-ui,sans-serif"}}>
      {/* Header */}
      <div style={{height:52,background:'rgba(255,255,255,.03)',borderBottom:'1px solid rgba(255,255,255,.07)',display:'flex',alignItems:'center',padding:'0 32px',justifyContent:'space-between',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div style={{width:28,height:28,borderRadius:7,background:'linear-gradient(135deg,#0F766E,#0D5F58)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>📡</div>
          <span style={{fontSize:13,fontWeight:700,color:'#475569'}}>RADAR</span>
          <span style={{color:'#1E2633',margin:'0 6px'}}>·</span>
          <span style={{fontSize:13,color:'#374151'}}>Comité Comercial</span>
          <span style={{color:'#1E2633',margin:'0 6px'}}>·</span>
          <span style={{fontSize:13,color:'#374151'}}>{semLabel}</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:14}}>
          <span style={{fontSize:13,color:'#374151'}}>{slide+1} / {SLIDE_COUNT}</span>
          <button onClick={onClose} style={{background:'none',border:'1px solid rgba(255,255,255,.1)',borderRadius:7,padding:'4px 14px',color:'#475569',fontSize:12,cursor:'pointer',fontWeight:600}}>✕ Salir</button>
        </div>
      </div>

      {/* Body */}
      <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',padding:48,overflowY:'auto'}}>
        {loading
          ? <div style={{color:'#374151',fontSize:16}}>Cargando datos...</div>
          : slides[slide]?.()
        }
      </div>

      {/* Footer */}
      <div style={{height:54,background:'rgba(255,255,255,.03)',borderTop:'1px solid rgba(255,255,255,.07)',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 32px',flexShrink:0}}>
        <div style={{display:'flex',gap:6}}>
          {Array.from({length:SLIDE_COUNT},(_,i)=>(
            <button key={i} onClick={()=>setSlide(i)}
              style={{width:i===slide?28:8,height:8,borderRadius:4,background:i===slide?C.blue:'rgba(255,255,255,.12)',border:'none',cursor:'pointer',padding:0,transition:'all .2s'}}/>
          ))}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <button onClick={()=>setSlide(s=>Math.max(s-1,0))} disabled={slide===0}
            style={{background:'none',border:'1px solid rgba(255,255,255,.1)',borderRadius:8,padding:'6px 18px',color:slide===0?'#1E2633':'#94A3B8',fontSize:16,cursor:slide===0?'not-allowed':'pointer',fontWeight:700}}>←</button>
          <span style={{fontSize:11,color:'#1E2633'}}>← → para navegar · ESC para salir</span>
          <button onClick={()=>setSlide(s=>Math.min(s+1,SLIDE_COUNT-1))} disabled={slide===SLIDE_COUNT-1}
            style={{background:slide===SLIDE_COUNT-1?'none':`linear-gradient(135deg,${C.blue},#10B981)`,border:'none',borderRadius:8,padding:'6px 18px',color:slide===SLIDE_COUNT-1?'#1E2633':'#FFFFFF',fontSize:16,cursor:slide===SLIDE_COUNT-1?'not-allowed':'pointer',fontWeight:700}}>→</button>
        </div>
      </div>
    </div>
  );
}

// ─── COMITÉ COMERCIAL ─────────────────────────────────────────────────────────
// ─── REVISIÓN COMERCIAL (interactiva) dentro de Comité ────────────────────────
function RevisionComite({ usuario }) {
  const [periodo, setPeriodo] = useState('mes');        // semana | quincena | mes
  const [off, setOff]         = useState(0);            // 0 = actual, -1 anterior…
  const [refFecha, setRefFecha] = useState('');         // fecha específica (opcional)
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [exp, setExp]         = useState({});           // vendedor expandido
  const [verOpps, setVerOpps] = useState({});           // ver lista completa de opps por vendedor
  const [oFil, setOFil]       = useState({ etapa:'', q:'' }); // filtro de la tabla de oportunidades
  const [reunion, setReunion] = useState('cierre');     // apertura | cierre | mes (tipo de reunión)
  const ffec = f => f ? new Date(f+'T00:00:00').toLocaleDateString('es-HN',{day:'numeric',month:'short'}) : '—';
  const pad = n => String(n).padStart(2,'0');
  const isoD = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const SEMC = s => s==='🟢'?C.green:s==='🔴'?C.red:C.orange;

  const rango = () => {
    const base = refFecha ? new Date(refFecha+'T12:00:00') : new Date();
    if (periodo==='mes') { const d=new Date(base.getFullYear(),base.getMonth()+off,1); const fin=new Date(d.getFullYear(),d.getMonth()+1,0);
      return { desde:isoD(d), hasta:isoD(fin), label:d.toLocaleDateString('es-HN',{month:'long',year:'numeric'}) }; }
    if (periodo==='semana') { const d=new Date(base); const g=d.getDay()||7; d.setHours(0,0,0,0); d.setDate(d.getDate()-g+1+off*7); const fin=new Date(d); fin.setDate(d.getDate()+6);
      return { desde:isoD(d), hasta:isoD(fin), label:`${d.toLocaleDateString('es-HN',{day:'numeric',month:'short'})} – ${fin.toLocaleDateString('es-HN',{day:'numeric',month:'short'})}` }; }
    // quincena (1-15 / 16-fin), navegable por mitades
    let idx = base.getFullYear()*24 + base.getMonth()*2 + (base.getDate()<=15?0:1) + off;
    const yy=Math.floor(idx/24), mm=Math.floor((idx%24)/2), hh=idx%2;
    const d = hh===0?new Date(yy,mm,1):new Date(yy,mm,16); const fin = hh===0?new Date(yy,mm,15):new Date(yy,mm+1,0);
    return { desde:isoD(d), hasta:isoD(fin), label:`${hh===0?'1ª':'2ª'} quincena · ${d.toLocaleDateString('es-HN',{month:'short',year:'numeric'})}` };
  };
  const R = rango();

  useEffect(()=>{ let vivo=true; setLoading(true);
    fetch(`/api/report?revision=1&json=1&desde=${R.desde}&hasta=${R.hasta}`)
      .then(r=>r.json()).then(d=>{ if(vivo){ setData(d); setLoading(false); } }).catch(()=>{ if(vivo) setLoading(false); });
    return ()=>{ vivo=false; };
  }, [periodo, off, refFecha]); // eslint-disable-line

  const Chip = ({on,onClick,children}) => <button className="radar-btn" onClick={onClick} style={{padding:'7px 16px',borderRadius:R.md,border:`1px solid ${on?C.primaryBorder:C.border}`,background:on?C.primaryTint:C.bg2,color:on?C.blue:C.t3,fontSize:13,fontWeight:600,cursor:'pointer',boxShadow:SH.xs}}>{children}</button>;
  const alertasDe = v => {
    const a=[];
    if(v.vencidas.length) a.push([`${v.vencidas.length} cierre(s) vencido/hoy`,'¿qué falta para cerrarlas o mover la fecha?']);
    if(v.cotizSinSeg.length) a.push([`${v.cotizSinSeg.length} cotización(es) sin seguimiento +3 días`,'¿ya les diste seguimiento? ¿qué dijeron?']);
    if(v.factVenc.length) a.push([`${v.factVenc.length} factura(s) vencida(s)`,'¿cuándo pagan? ¿promesa de pago?']);
    if(v.estancadas.length) a.push([`${v.estancadas.length} cuenta(s) estancada(s) +30 días`,'¿las trabajás o las liberamos?']);
    if(v.visSinGps>0) a.push([`${v.visSinGps} visita(s) SIN GPS`,'sin GPS no se paga combustible — que marquen llegada']);
    return a;
  };

  return (
    <div style={{padding:'4px 0'}}>
      {/* Selector de REUNIÓN */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
        {[['apertura','Apertura semanal','semana',CI.calendar],['cierre','Cierre semanal','semana',CI.flag],['mes','Apertura de mes','mes',CI.chart]].map(([v,l,per,ic])=>(
          <button key={v} className="radar-btn" onClick={()=>{ setReunion(v); setPeriodo(per); setOff(0); }}
            style={{display:'inline-flex',alignItems:'center',gap:7,padding:'9px 18px',borderRadius:R.md,border:`1px solid ${reunion===v?C.blue:C.border}`,background:reunion===v?C.blue:C.bg2,color:reunion===v?'#fff':C.t2,fontSize:13,fontWeight:600,cursor:'pointer',boxShadow:reunion===v?SH.primary:SH.xs}}><CIcon d={ic}/>{l}</button>
        ))}
      </div>

      {/* Controles de periodo/fecha */}
      <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center',marginBottom:16}}>
        {[['semana','Semanal'],['quincena','Quincenal'],['mes','Mensual']].map(([v,l])=>(
          <Chip key={v} on={periodo===v} onClick={()=>{setPeriodo(v);setOff(0);}}>{l}</Chip>
        ))}
        <div style={{display:'flex',alignItems:'center',gap:8,marginLeft:8}}>
          <button onClick={()=>setOff(o=>o-1)} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',color:C.t2,fontWeight:800,cursor:'pointer'}}>◀</button>
          <div style={{minWidth:170,textAlign:'center',fontSize:13,fontWeight:800,color:C.t1,textTransform:'capitalize'}}>{R.label}{off!==0&&<div style={{fontSize:10,color:C.blue,cursor:'pointer'}} onClick={()=>setOff(0)}>volver al actual</div>}</div>
          <button onClick={()=>setOff(o=>o+1)} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',color:C.t2,fontWeight:800,cursor:'pointer'}}>▶</button>
        </div>
        <input type="date" value={refFecha} onChange={e=>{setRefFecha(e.target.value);setOff(0);}} title="Saltar a una fecha específica"
          style={{marginLeft:'auto',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 10px',color:C.t1,fontSize:13}}/>
        {refFecha&&<button onClick={()=>setRefFecha('')} style={{background:'none',border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 10px',color:C.t4,fontSize:12,cursor:'pointer'}}>hoy</button>}
      </div>

      {/* Leyenda */}
      {data?.leyenda&&(
        <div style={{background:C.bg3,borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:11,color:C.t3,lineHeight:1.7}}>
          <div style={{display:'flex',gap:16,flexWrap:'wrap'}}><span>🟢 {data.leyenda.verde}</span><span>🟡 {data.leyenda.amarillo}</span><span>🔴 {data.leyenda.rojo}</span></div>
          <div style={{marginTop:4}}><b>Crudo</b> = valor total de las oportunidades · <b>Ponderado</b> = valor × probabilidad de cierre de cada etapa (lo que de verdad se espera ganar). <b>Contactos</b> = seguimientos + llamadas + visitas registradas.</div>
        </div>
      )}

      {/* Resumen de equipo según la reunión */}
      {data?.vds && (() => {
        const V = data.vds;
        const sM = (v,arr,cond)=> v[arr].reduce((a,f)=>a+((!cond||cond(f))?(f.monto_estimado||0):0),0);
        const promSem = V.reduce((s,v)=>s+sM(v,'fcEsta'),0);
        const cumpSem = V.reduce((s,v)=>s+sM(v,'fcEsta',f=>f.estado==='cerrado'),0);
        const proxSem = V.reduce((s,v)=>s+sM(v,'fcProx'),0);
        const arrastre= V.reduce((s,v)=>s+sM(v,'fcAntPend'),0);
        const planVis = V.reduce((s,v)=>s+v.planVisitas.length,0);
        const visGps  = V.reduce((s,v)=>s+v.visGps,0);
        const contactos=V.reduce((s,v)=>s+v.contactosSem,0);
        const pipePond= V.reduce((s,v)=>s+v.pipePond,0);
        const metaTot = V.reduce((s,v)=>s+(v.meta||0),0);
        const cobTot  = V.reduce((s,v)=>s+v.cobMes,0);
        const Card=({l,val,col,sub})=> <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px 16px',flex:1,minWidth:150}}><div style={{fontSize:10,color:C.t4,textTransform:'uppercase',fontWeight:700}}>{l}</div><div style={{fontSize:22,fontWeight:900,color:col||C.t1}}>{val}</div>{sub&&<div style={{fontSize:11,color:C.t4,marginTop:2}}>{sub}</div>}</div>;
        let cards=[], preg='';
        if (reunion==='apertura') {
          cards=[['Prometido esta semana',fmt(Math.round(promSem)),C.indigo],['Próxima semana',fmt(Math.round(proxSem)),C.t2],['Visitas agendadas',planVis,C.blue],['Arrastre sin cerrar',fmt(Math.round(arrastre)),arrastre>0?C.orange:C.green]];
          preg='Apertura: ¿cada vendedor confirmó qué cierra esta semana y sus visitas agendadas? ¿qué hacemos con lo que se arrastra?';
        } else if (reunion==='cierre') {
          const pct = promSem>0?Math.round(cumpSem/promSem*100):0;
          cards=[['Prometido',fmt(Math.round(promSem)),C.t2],['Cumplido',fmt(Math.round(cumpSem)),C.green,`${pct}%`],['Se arrastra',fmt(Math.round(arrastre)),arrastre>0?C.orange:C.green],['Visitas c/GPS',visGps,C.indigo,`${contactos} contactos`]];
          preg='Cierre: ¿por qué no cerró lo prometido? ¿qué se arrastra y con qué fecha nueva? ¿quién quedó corto en actividad?';
        } else {
          const cubre = pipePond>=metaTot;
          cards=[['Meta mensual equipo',fmt(Math.round(metaTot)),C.t2],['Pipeline ponderado',fmt(Math.round(pipePond)),cubre?C.green:C.orange,cubre?'cubre la meta':`falta ${fmt(Math.round(metaTot-pipePond))}`],['Cobrado del mes',fmt(Math.round(cobTot)),C.green],['Ganadas / Perdidas',`${data.ganadasMes.length} / ${data.perdidasMes.length}`,C.t2]];
          preg='Apertura de mes: ¿el pipeline ponderado cubre la meta? ¿quién está corto y cuál es su plan de acción para llegar?';
        }
        return <div style={{marginBottom:16}}>
          <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:8}}>{cards.map(([l,val,col,sub],i)=><Card key={i} l={l} val={val} col={col} sub={sub}/>)}</div>
          <div style={{background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:R.md,padding:'9px 14px',fontSize:12.5,color:C.t2,fontWeight:600,display:'flex',alignItems:'center',gap:8}}><span style={{color:C.blue,display:'inline-flex',flexShrink:0}}><CIcon d={CI.chat} size={14}/></span><span>{preg}</span></div>
        </div>;
      })()}

      {loading ? <div style={{padding:20}}><SkelCards n={4}/><SkelRows n={5}/></div>
      : !data?.vds?.length ? <div style={{textAlign:'center',color:C.t4,padding:40}}>Sin datos para este periodo.</div>
      : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {data.vds.map(v=>{
            const ab = !!exp[v.n]; const al = alertasDe(v);
            return (
            <div key={v.n} style={{background:C.bg2,border:`1px solid ${C.border}`,borderLeft:`4px solid ${SEMC(v.sem)}`,borderRadius:12,overflow:'hidden'}}>
              {/* Cabecera clickeable */}
              <div onClick={()=>setExp(e=>({...e,[v.n]:!e[v.n]}))} style={{display:'flex',alignItems:'center',gap:14,padding:'12px 16px',cursor:'pointer',flexWrap:'wrap'}}>
                <span style={{width:12,height:12,borderRadius:'50%',background:SEMC(v.sem),flexShrink:0}}/>
                <span style={{fontSize:14,fontWeight:800,color:C.t1,minWidth:90}}>{v.n.split(' ')[0]}{v.esKam?' ·KAM':''}</span>
                <span style={{fontSize:12,color:C.t3}}>Avance <b style={{color:SEMC(v.sem)}}>{v.avancePct}%</b></span>
                <span style={{fontSize:12,color:C.t3}}>Cobrado <b style={{color:C.green}}>{fmt(Math.round(v.cobMes))}</b></span>
                <span style={{fontSize:12,color:C.t3}}>Pipeline pond. <b style={{color:C.indigo}}>{fmt(Math.round(v.pipePond))}</b></span>
                <span style={{fontSize:12,color:C.t3}}>Contactos <b style={{color:C.t1}}>{v.contactosSem}</b> · Visitas GPS <b style={{color:v.visGps>0?C.green:C.t4}}>{v.visGps}</b></span>
                {al.length>0&&<span style={{marginLeft:'auto',fontSize:11,fontWeight:800,color:C.red}}>⚠ {al.length} alerta(s)</span>}
                <span style={{fontSize:12,color:C.t4,marginLeft:al.length?0:'auto'}}>{ab?'▲':'▼'}</span>
              </div>
              {/* Detalle desplegable — una sola columna, secciones claras */}
              {ab&&(()=>{ const Sec=({t,c})=> <div style={{fontSize:11,fontWeight:800,color:c||C.t4,textTransform:'uppercase',letterSpacing:'.04em',margin:'16px 0 8px'}}>{t}</div>;
                const vOpps = !!verOpps[v.n];
                return (
                <div style={{padding:'0 18px 18px',borderTop:`1px solid ${C.border}`}}>
                  {/* Alertas */}
                  <Sec t="Alertas / Acción" c={C.redText}/>
                  {al.length===0&&<div style={{fontSize:12,color:C.t4}}>Sin alertas. ✅</div>}
                  {al.map(([t,q],i)=>(
                    <div key={i} style={{marginBottom:8,paddingLeft:4}}>
                      <div style={{fontSize:13,color:C.t1,fontWeight:600}}>• {t}</div>
                      <div style={{fontSize:12,color:C.t4,marginLeft:10}}>→ {q}</div>
                    </div>
                  ))}

                  {/* Actividad */}
                  <Sec t="Actividad" c={C.indigo}/>
                  <div style={{fontSize:13,color:C.t2,marginBottom:8}}>{v.contactosSem} contactos · {v.llamSem} llamadas · {v.visGps} visitas con GPS{v.visSinGps>0&&<span style={{color:C.orange}}> · {v.visSinGps} sin GPS (no paga combustible)</span>}</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                    <div>
                      <div style={{fontSize:11,color:C.t4,fontWeight:700,marginBottom:5}}>Visitas del período ({v.visitasLista.length})</div>
                      {v.visitasLista.length===0&&<div style={{fontSize:12,color:C.t4}}>Sin visitas registradas.</div>}
                      {v.visitasLista.slice(0,8).map((x,i)=>(<div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:12,padding:'3px 0',borderBottom:`1px solid ${C.border}30`}}>
                        <span style={{color:C.t2,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{x.titulo}</span>
                        <span style={{color:x.gps?C.green:C.orange,fontWeight:700,fontSize:11}}>{ffec(x.fecha)} {x.gps?'✓GPS':'✗GPS'}</span></div>))}
                    </div>
                    <div>
                      <div style={{fontSize:11,color:C.t4,fontWeight:700,marginBottom:5}}>Plan de visitas (próximas)</div>
                      {v.planVisitas.length===0&&<div style={{fontSize:12,color:C.t4}}>Sin visitas agendadas.</div>}
                      {v.planVisitas.slice(0,8).map((x,i)=>(<div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:12,padding:'3px 0',borderBottom:`1px solid ${C.border}30`}}>
                        <span style={{color:C.t2,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{x.titulo}</span>
                        <span style={{color:C.blue,fontWeight:700,fontSize:11}}>{ffec(x.fecha)}</span></div>))}
                    </div>
                  </div>

                  {/* Compromisos / proyección semanal (qué prometió) */}
                  <Sec t="Compromisos / Proyección semanal"/>
                  {(() => {
                    const fcRow = (f,i,col) => <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:12,padding:'3px 0',borderBottom:`1px solid ${C.border}25`}}>
                      <span style={{color:C.t2,maxWidth:230,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.cliente||f.oportunidad_titulo||'—'}{f.categoria?<span style={{color:C.t4}}> · {f.categoria}</span>:null}</span>
                      <span style={{color:col,fontWeight:700}}>{fmt(Math.round(f.monto_estimado||0))}{f.estado==='cerrado'?' ✓':''}</span></div>;
                    return <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                      <div>
                        <div style={{fontSize:11,color:C.t4,fontWeight:700,marginBottom:5}}>Prometido esta semana ({v.fcEsta.length}) · {fmt(Math.round(v.fcEsta.reduce((s,f)=>s+(f.monto_estimado||0),0)))}</div>
                        {v.fcEsta.length===0&&<div style={{fontSize:12,color:C.t4}}>Sin compromisos cargados.</div>}
                        {v.fcEsta.map((f,i)=>fcRow(f,i,f.estado==='cerrado'?C.green:C.t1))}
                      </div>
                      <div>
                        <div style={{fontSize:11,color:C.t4,fontWeight:700,marginBottom:5}}>Próxima semana ({v.fcProx.length}) · {fmt(Math.round(v.fcProx.reduce((s,f)=>s+(f.monto_estimado||0),0)))}</div>
                        {v.fcProx.length===0&&<div style={{fontSize:12,color:C.t4}}>Sin plan cargado.</div>}
                        {v.fcProx.map((f,i)=>fcRow(f,i,C.blue))}
                      </div>
                    </div>;
                  })()}
                  {v.fcAntPend.length>0&&<div style={{marginTop:10}}>
                    <div style={{fontSize:11,color:C.orange,fontWeight:700,marginBottom:5}}>⚠ Prometido la semana pasada y NO cerrado ({v.fcAntPend.length})</div>
                    {v.fcAntPend.map((f,i)=>(<div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:12,padding:'3px 0'}}>
                      <span style={{color:C.t2}}>{f.cliente||f.oportunidad_titulo||'—'}{f.categoria?<span style={{color:C.t4}}> · {f.categoria}</span>:null}</span>
                      <span style={{color:C.orange,fontWeight:700}}>{fmt(Math.round(f.monto_estimado||0))}</span></div>))}
                  </div>}
                  <div style={{fontSize:13,color:C.t2,marginTop:10}}>Ganadas del período: <b style={{color:C.green}}>{data.ganadasMes.filter(o=>o.vendedor===v.n).length}</b> · perdidas: <b style={{color:C.red}}>{data.perdidasMes.filter(o=>o.vendedor===v.n).length}</b></div>

                  {/* Pipeline + oportunidades */}
                  <Sec t="Pipeline por etapa" c={C.indigo}/>
                  {v.porEtapa.length===0&&<div style={{fontSize:12,color:C.t4}}>Sin pipeline abierto.</div>}
                  {v.porEtapa.map(e=>(
                    <div key={e.e} style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'4px 0',borderBottom:`1px solid ${C.border}30`}}>
                      <span style={{color:C.t2,textTransform:'capitalize'}}>{e.e} <span style={{color:C.t4}}>({e.n})</span></span>
                      <span style={{color:C.t3,fontSize:12}}>crudo <b style={{color:C.t1}}>{fmt(Math.round(e.crudo))}</b> · pond. <b style={{color:C.indigo}}>{fmt(Math.round(e.pond))}</b></span>
                    </div>
                  ))}
                  {v.oppsLista.length>0&&(
                    <button onClick={()=>setVerOpps(s=>({...s,[v.n]:!s[v.n]}))} style={{marginTop:10,background:`${C.blue}12`,border:`1px solid ${C.blue}40`,borderRadius:8,padding:'7px 14px',color:C.blue,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                      {vOpps?'Ocultar oportunidades ▲':`Ver todas las oportunidades (${v.oppsLista.length}) ▼`}
                    </button>
                  )}
                  {vOpps&&(()=>{
                    const etapas=[...new Set(v.oppsLista.map(o=>o.etapa))];
                    const filt=v.oppsLista.filter(o=>(!oFil.etapa||o.etapa===oFil.etapa)&&(!oFil.q||`${o.titulo||''} ${o.cliente||''}`.toLowerCase().includes(oFil.q.toLowerCase())));
                    return (<div style={{marginTop:10}}>
                      <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap',alignItems:'center'}}>
                        <select value={oFil.etapa} onChange={e=>setOFil(s=>({...s,etapa:e.target.value}))} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 10px',color:C.t1,fontSize:12}}>
                          <option value="">Todas las etapas</option>{etapas.map(et=><option key={et} value={et} style={{textTransform:'capitalize'}}>{et}</option>)}
                        </select>
                        <input value={oFil.q} onChange={e=>setOFil(s=>({...s,q:e.target.value}))} placeholder="Buscar cliente / cotización…" style={{flex:1,minWidth:160,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 10px',color:C.t1,fontSize:12,outline:'none'}}/>
                        <span style={{fontSize:11,color:C.t4}}>{filt.length} de {v.oppsLista.length}</span>
                      </div>
                      <div style={{border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}>
                        <div style={{display:'grid',gridTemplateColumns:'2.4fr 1fr 1fr 1fr',gap:0,padding:'7px 12px',background:C.bg3,fontSize:10,fontWeight:800,color:C.t4,textTransform:'uppercase'}}>
                          <span>Oportunidad</span><span>Etapa</span><span style={{textAlign:'right'}}>Crudo</span><span style={{textAlign:'right'}}>Ponderado</span>
                        </div>
                        {filt.length===0&&<div style={{padding:12,fontSize:12,color:C.t4}}>Sin coincidencias.</div>}
                        {filt.map((o,i)=>(
                          <div key={i} style={{display:'grid',gridTemplateColumns:'2.4fr 1fr 1fr 1fr',gap:0,padding:'7px 12px',borderBottom:`1px solid ${C.border}25`,fontSize:12,alignItems:'center'}}>
                            <span style={{color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{o.titulo||o.cliente||'—'}</span>
                            <span style={{color:C.t3,textTransform:'capitalize',fontSize:11}}>{o.etapa}</span>
                            <span style={{textAlign:'right',color:C.t2}}>{fmt(Math.round(o.valor))}</span>
                            <span style={{textAlign:'right',color:C.indigo,fontWeight:700}}>{fmt(o.pond)}</span>
                          </div>
                        ))}
                      </div>
                    </div>);
                  })()}
                </div>
                );
              })()}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── COMITÉ CONSOLIDADO (una sola pantalla: metas + cobros proy + pipeline + NEXA) ──
// Reemplaza la duplicación de Metas/Proyección/Comité. Reusa el JSON de revision
// (data.vds por vendedor) + leads de NEXA. Filtros: semana / mes / rango.
function ComiteConsolidado({ usuario, empresaActiva }) {
  const [periodo, setPeriodo] = useState('sem');       // sem | mes | rango
  const [rango, setRango]     = useState({ desde:'', hasta:'' });
  const [data, setData]       = useState(null);
  const [leads, setLeads]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel]         = useState(null);

  const pad = n => String(n).padStart(2,'0');
  const isoD = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const RG = (() => {
    const b = new Date();
    if (periodo==='mes') { const d=new Date(b.getFullYear(),b.getMonth(),1), f=new Date(b.getFullYear(),b.getMonth()+1,0);
      return { desde:isoD(d), hasta:isoD(f), label:d.toLocaleDateString('es-HN',{month:'long',year:'numeric'}) }; }
    if (periodo==='rango' && rango.desde && rango.hasta) return { desde:rango.desde, hasta:rango.hasta, label:`${rango.desde} → ${rango.hasta}` };
    const d=new Date(b); const g=d.getDay()||7; d.setHours(0,0,0,0); d.setDate(d.getDate()-g+1); const f=new Date(d); f.setDate(d.getDate()+6);
    return { desde:isoD(d), hasta:isoD(f), label:`${d.toLocaleDateString('es-HN',{day:'numeric',month:'short'})} – ${f.toLocaleDateString('es-HN',{day:'numeric',month:'short'})}` };
  })();

  useEffect(()=>{ let vivo=true; setLoading(true);
    Promise.all([
      fetch(`/api/report?revision=1&json=1&desde=${RG.desde}&hasta=${RG.hasta}`).then(r=>r.json()).catch(()=>null),
      supabase.from('sibila_leads').select('vendedor,estado,created_at').gte('created_at',RG.desde+'T00:00:00').lte('created_at',RG.hasta+'T23:59:59').limit(5000)
        .then(r=>r.data||[]).catch(()=>[]),
    ]).then(([d,l])=>{ if(!vivo) return; setData(d); setLeads(l||[]); setLoading(false); });
    return ()=>{ vivo=false; };
  }, [periodo, rango.desde, rango.hasta]); // eslint-disable-line

  const usd = n => `$${Math.round(Number(n)||0).toLocaleString('en-US')}`;
  const first = n => String(n||'').split(' ')[0];
  const ini = n => String(n||'').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();
  const sumFc = (arr, tipo) => (arr||[]).filter(f => tipo ? (tipo==='cobros' ? f.tipo==='cobros' : f.tipo!=='cobros') : true).reduce((s,f)=>s+Number(f.monto_estimado||0),0);
  const semColor = s => s==='🟢'?C.greenText : s==='🔴'?C.redText : C.orangeText;
  const semLabel = s => s==='🟢'?'Al día' : s==='🔴'?'Atrasado' : 'En riesgo';
  const cumplC = p => p>=50?C.greenText : p>=25?C.orangeText : C.redText;

  // leads por vendedor
  const leadsBy = {};
  leads.forEach(l => { const v = l.vendedor || '—'; if(!leadsBy[v]) leadsBy[v]={asig:0,cont:0,conv:0,sin:0};
    leadsBy[v].asig++; const e=String(l.estado||'').toLowerCase();
    if (e && e!=='nuevo' && e!=='sin_contactar') leadsBy[v].cont++;
    if (e==='convertido' || e==='ganado' || e==='cliente') leadsBy[v].conv++;
    if (!e || e==='nuevo' || e==='sin_contactar') leadsBy[v].sin++;
  });
  const vds = (data?.vds || []).map(v => {
    const cobrosProy = sumFc(v.fcEsta,'cobros'), arrastre = sumFc(v.fcAntPend);
    const lb = leadsBy[v.n] || {asig:0,cont:0,conv:0,sin:0};
    const p = v.meta ? Math.round((v.cobMes||0)/v.meta*100) : 0;
    return { ...v, cobrosProy, arrastre, lb, p };
  });
  const activos = vds.filter(v => (v.meta||v.cobMes||v.cobrosProy||v.arrastre||v.pipePond||v.lb.asig||v.contactosSem));
  const selV = activos.find(v=>v.n===sel) || activos.find(v=>!v.esKam) || activos[0];

  const T = activos.reduce((a,v)=>({ meta:a.meta+(v.meta||0), cob:a.cob+(v.cobMes||0), proy:a.proy+v.cobrosProy, arr:a.arr+v.arrastre, pipe:a.pipe+(v.pipePond||0) }), {meta:0,cob:0,proy:0,arr:0,pipe:0});
  const porCobrar = activos.reduce((s,v)=>s+(v.factPorVencer||[]).reduce((x,f)=>x+(f.saldo_usd||f.total_usd||0),0)+((v.factVenc||[]).reduce((x,f)=>x+(f.saldo_usd||f.total_usd||0),0)),0);
  const totLeads = leads.length, totCont = leads.filter(l=>{const e=String(l.estado||'').toLowerCase();return e&&e!=='nuevo'&&e!=='sin_contactar';}).length;

  const Th = ({children,l}) => <th style={{fontSize:10,fontWeight:800,letterSpacing:'.04em',textTransform:'uppercase',color:C.t4,textAlign:l?'left':'right',padding:'10px 12px',borderBottom:`2px solid ${C.border}`,whiteSpace:'nowrap',background:C.bg1}}>{children}</th>;
  const Td = ({children,l,b,c}) => <td className={l?'':'radar-num'} style={{padding:'11px 12px',fontSize:13,fontWeight:b?800:500,color:c||C.t1,textAlign:l?'left':'right',whiteSpace:'nowrap',borderBottom:`1px solid ${C.bg3}`}}>{children}</td>;
  const Kpi = ({l,v,c,sub,rail}) => <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:'13px 15px',boxShadow:SH.sm,position:'relative',overflow:'hidden'}}><span style={{position:'absolute',left:0,top:0,bottom:0,width:3,background:rail}}/><div style={{fontSize:10,fontWeight:700,letterSpacing:'.04em',textTransform:'uppercase',color:C.t4,marginBottom:6,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{l}</div><div className="radar-num" style={{fontSize:22,fontWeight:900,letterSpacing:'-.02em',color:c||C.t1,lineHeight:1}}>{v}</div>{sub&&<div style={{fontSize:11,color:C.t3,marginTop:4}}>{sub}</div>}</div>;
  const av = (n,col,sz=26) => <span style={{width:sz,height:sz,borderRadius:sz>30?11:8,background:col,color:'#fff',display:'grid',placeItems:'center',fontSize:sz>30?14:11,fontWeight:800,flexShrink:0}}>{ini(n)}</span>;
  const colV = n => { const h=[...String(n)].reduce((a,c)=>a+c.charCodeAt(0),0); return [C.primary,C.info,C.orange,C.indigo,'#6D28D9','#334155'][h%6]; };
  const chip = (col,txt) => <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,fontWeight:800,color:col,background:col+'1f',border:`1px solid ${col}44`,padding:'3px 9px',borderRadius:999}}><span style={{width:7,height:7,borderRadius:'50%',background:col}}/>{txt}</span>;

  // Export a Excel del comité (lo que pedia Omar): resumen + por vendedor +
  // detalle de promesas + NEXA. Usa la misma data que ya muestra la pantalla.
  const exportarExcelComite = () => {
    const resumen = [
      {Metrica:'Periodo', Valor:RG.label},
      {Metrica:'Meta del periodo', Valor:Math.round(T.meta)},
      {Metrica:'Cobrado (SAP)', Valor:Math.round(T.cob)},
      {Metrica:'Cumplimiento %', Valor:T.meta?Math.round(T.cob/T.meta*100):0},
      {Metrica:'Por cobrar (cartera abierta)', Valor:Math.round(porCobrar)},
      {Metrica:'Cobros proyectados semana', Valor:Math.round(T.proy)},
      {Metrica:'Arrastre (no cayo)', Valor:Math.round(T.arr)},
      {Metrica:'Pipeline ponderado', Valor:Math.round(T.pipe)},
      {Metrica:'Leads NEXA', Valor:totLeads},
      {Metrica:'Leads contactados', Valor:totCont},
    ];
    const porVend = activos.map(v=>({
      Vendedor:v.n, Meta:Math.round(v.meta||0), Cobrado:Math.round(v.cobMes||0),
      'Cumpl %':v.meta?v.p:'', 'Cobros proy':Math.round(v.cobrosProy||0), Arrastre:Math.round(v.arrastre||0),
      'Pipeline pond':Math.round(v.pipePond||0), 'Leads asig':v.lb.asig, 'Leads cont':v.lb.cont, 'Leads conv':v.lb.conv,
      Visitas:v.visSem||0, Llamadas:v.llamSem||0, Estado:semLabel(v.sem),
    }));
    const detalle = [];
    activos.forEach(v=>{ (v.fcEsta||[]).forEach(f=>detalle.push({
      Vendedor:v.n, Tipo:f.tipo==='cobros'?'Cobro':'Venta', Cliente:f.cliente||f.oportunidad_titulo||'-',
      Monto:Math.round(f.monto_estimado||0), Estado:f.estado||'pendiente',
      Factura:f.factura_doc||'', 'Fecha prometida':f.fecha_prometida||'' })); });
    const nexa = Object.entries(leadsBy).filter(([n])=>n!=='—').map(([n,x])=>({
      Vendedor:n, Asignados:x.asig, Contactados:x.cont, Convertidos:x.conv, 'Sin tocar':x.sin,
      '% respuesta':x.asig?Math.round(x.cont/x.asig*100):0 }));
    const bytes = buildXlsxMulti([
      {name:'Resumen', cols:['Metrica','Valor'], rows:resumen},
      {name:'Por vendedor', cols:['Vendedor','Meta','Cobrado','Cumpl %','Cobros proy','Arrastre','Pipeline pond','Leads asig','Leads cont','Leads conv','Visitas','Llamadas','Estado'], rows:porVend},
      {name:'Detalle promesas', cols:['Vendedor','Tipo','Cliente','Monto','Estado','Factura','Fecha prometida'], rows:detalle},
      {name:'NEXA por vendedor', cols:['Vendedor','Asignados','Contactados','Convertidos','Sin tocar','% respuesta'], rows:nexa},
    ]);
    const a = Object.assign(document.createElement('a'),{href:URL.createObjectURL(new Blob([bytes],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})),download:`Comite-${RG.desde}_a_${RG.hasta}.xlsx`});
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    // El hub (MetasHub) envuelve los modulos en overflow:hidden asumiendo que cada uno
    // trae su propio scroll. Este NO lo tenia: el detalle del vendedor y la tabla
    // quedaban recortados y no se podia bajar. flex:1 + minHeight:0 + overflowY:auto.
    <div style={{flex:1,minHeight:0,minWidth:0,overflowY:'auto',padding:'14px 16px 20px',display:'flex',flexDirection:'column',gap:16}}>
      {/* Filtros de fecha */}
      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
        <span style={{fontSize:12,fontWeight:700,color:C.t3}}>Filtrar por:</span>
        <select value={periodo} onChange={e=>setPeriodo(e.target.value)}
          style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:9,padding:'8px 12px',color:C.t1,fontSize:12.5,fontWeight:600,cursor:'pointer',outline:'none'}}>
          <option value="sem">Esta semana</option>
          <option value="mes">Este mes</option>
          <option value="rango">Rango de fechas</option>
        </select>
        {periodo==='rango' ? (
          <div style={{display:'inline-flex',alignItems:'center',gap:6}}>
            <input type="date" value={rango.desde} onChange={e=>setRango(r=>({...r,desde:e.target.value}))} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:9,padding:'7px 10px',color:C.t1,fontSize:12.5}}/>
            <span style={{color:C.t4}}>→</span>
            <input type="date" value={rango.hasta} onChange={e=>setRango(r=>({...r,hasta:e.target.value}))} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:9,padding:'7px 10px',color:C.t1,fontSize:12.5}}/>
          </div>
        ) : <div style={{fontSize:13,fontWeight:800,color:C.t1,textTransform:'capitalize'}}>{RG.label}</div>}
        <button onClick={exportarExcelComite} title="Exportar a Excel" style={{marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:6,height:36,border:`1px solid ${C.greenBorder}`,borderRadius:10,background:C.greenTint,color:C.greenText,cursor:'pointer',padding:'0 13px',fontSize:12.5,fontWeight:700,boxShadow:SH.xs}}>⬇ Excel</button>
        <button onClick={()=>setPeriodo(p=>p)} title="Actualizar" style={{width:36,height:36,border:`1px solid ${C.border}`,borderRadius:10,background:C.bg2,color:C.t3,cursor:'pointer',boxShadow:SH.xs}}>↻</button>
      </div>

      {loading ? <div style={{padding:20}}><SkelCards n={6}/><SkelRows n={6}/></div> : (
      <>
        {/* KPIs */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(158px,1fr))',gap:11}}>
          <Kpi l="Meta del período" v={usd(T.meta)} rail={C.primary} sub={`${activos.filter(v=>v.meta).length} vendedores con meta`}/>
          <Kpi l="Cobrado (SAP)" v={usd(T.cob)} c={C.greenText} rail={C.green} sub={T.meta?`${Math.round(T.cob/T.meta*100)}% de la meta`:''}/>
          <Kpi l="Por cobrar" v={usd(porCobrar)} c={C.orangeText} rail={C.orange} sub="cartera abierta"/>
          <Kpi l="Cobros proy. semana" v={usd(T.proy)} c={C.infoText} rail={C.info} sub="lo que va a caer"/>
          <Kpi l="Arrastre (no cayó)" v={usd(T.arr)} c={T.arr?C.redText:C.t3} rail={C.red} sub="pasa a la otra semana"/>
          <Kpi l="Leads NEXA" v={totLeads} c="#6D28D9" rail="#6D28D9" sub={`${totCont} contactados`}/>
        </div>

        {/* Maestra + detalle */}
        <div style={{display:'grid',gridTemplateColumns:'minmax(0,1.55fr) minmax(0,1fr)',gap:16,alignItems:'start'}} className="comite-grid">
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:SH.sm,overflow:'hidden'}}>
            <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
              <div style={{fontSize:14,fontWeight:800}}>Equipo · {RG.label}</div>
              <div style={{fontSize:11.5,color:C.t3}}>clic en un vendedor para su detalle →</div>
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{borderCollapse:'collapse',width:'100%',minWidth:760}}>
                <thead><tr><Th l>Vendedor</Th><Th>Meta</Th><Th>Cobrado</Th><Th>Cumpl.</Th><Th>Cobros proy.</Th><Th>Arrastre</Th><Th>Pipeline</Th><Th>Leads</Th><Th>Actividad</Th><Th l>Estado</Th></tr></thead>
                <tbody>
                  {activos.map(v=>(
                    <tr key={v.n} onClick={()=>setSel(v.n)} className="radar-hrow" style={{cursor:'pointer',background:(selV&&selV.n===v.n)?C.primaryTint:'transparent'}}>
                      <Td l><div style={{display:'flex',alignItems:'center',gap:9}}>{av(v.n,colV(v.n))}<span style={{fontWeight:700}}>{first(v.n)}{v.esKam&&<span style={{fontSize:9,fontWeight:800,color:'#6D28D9',background:'#6D28D91f',border:'1px solid #6D28D944',padding:'1px 5px',borderRadius:5,marginLeft:5}}>KAM</span>}</span></div></Td>
                      <Td>{v.meta?usd(v.meta):'—'}</Td>
                      <Td b c={C.greenText}>{usd(v.cobMes)}</Td>
                      <Td><span style={{color:cumplC(v.p),fontWeight:800}}>{v.meta?v.p+'%':'—'}</span></Td>
                      <Td b c={C.infoText}>{v.cobrosProy?usd(v.cobrosProy):'—'}</Td>
                      <Td c={v.arrastre?C.redText:C.t4}>{v.arrastre?usd(v.arrastre):'—'}</Td>
                      <Td>{v.pipePond?usd(v.pipePond):'—'}</Td>
                      <Td>{v.lb.asig?`${v.lb.cont}/${v.lb.asig}`:'—'}</Td>
                      <Td c={C.t3}>{v.visSem||0}v · {v.llamSem||0}ll</Td>
                      <Td l>{chip(semColor(v.sem),semLabel(v.sem))}</Td>
                    </tr>
                  ))}
                  <tr style={{background:C.bg1,borderTop:`2px solid ${C.border}`,fontWeight:900}}>
                    <Td l b>TOTAL</Td><Td b>{usd(T.meta)}</Td><Td b c={C.greenText}>{usd(T.cob)}</Td>
                    <Td b>{T.meta?Math.round(T.cob/T.meta*100)+'%':'—'}</Td><Td b c={C.infoText}>{usd(T.proy)}</Td>
                    <Td b c={C.redText}>{usd(T.arr)}</Td><Td b>{usd(T.pipe)}</Td><Td b>{totLeads}</Td><Td>—</Td><Td>—</Td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Detalle */}
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:SH.sm,padding:'15px 16px'}}>
            {selV ? (()=>{ const v=selV;
              const cob = (v.fcEsta||[]).filter(f=>f.tipo==='cobros');
              const vts = (v.fcEsta||[]).filter(f=>f.tipo!=='cobros');
              const estCob = e => { e=String(e||'').toUpperCase(); return e==='CUMPLIDA'||e==='CERRADO'?['Cobrado',C.greenText]:e==='ARRASTRADA'?['Arrastra',C.redText]:['Va a caer',C.infoText]; };
              const totCob = cob.reduce((s,f)=>s+Number(f.monto_estimado||0),0);
              const Line = ({t1,t2,rt,badge})=> <div style={{display:'flex',justifyContent:'space-between',gap:10,padding:'9px 11px',borderRadius:9,border:`1px solid ${C.border}`,marginBottom:6,background:C.bg2,fontSize:12.5}}><div style={{minWidth:0}}><div style={{fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t1}</div>{t2&&<div style={{fontSize:10.5,color:C.t3,marginTop:1}}>{t2}</div>}</div><div style={{textAlign:'right',whiteSpace:'nowrap'}}>{rt&&<div className="radar-num" style={{fontWeight:700}}>{rt}</div>}{badge}</div></div>;
              const Sect = ({children,amt})=> <div style={{fontSize:11,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t3,margin:'16px 0 8px',display:'flex',justifyContent:'space-between'}}>{children}{amt!=null&&<span className="radar-num" style={{color:C.greenText}}>{usd(amt)}</span>}</div>;
              const badge = (txt,col)=> <span style={{fontSize:9.5,fontWeight:800,color:col,background:col+'1f',border:`1px solid ${col}44`,padding:'2px 7px',borderRadius:6}}>{txt}</span>;
              return <div>
                <div style={{display:'flex',alignItems:'center',gap:11,marginBottom:2}}>
                  {av(v.n,colV(v.n),38)}
                  <div style={{flex:1,minWidth:0}}><div style={{fontSize:16,fontWeight:900}}>{v.n}{v.esKam&&<span style={{fontSize:9,fontWeight:800,color:'#6D28D9',background:'#6D28D91f',border:'1px solid #6D28D944',padding:'1px 5px',borderRadius:5,marginLeft:6}}>KAM</span>}</div>
                    <div style={{fontSize:11.5,color:C.t3}}>{v.meta?`Meta ${usd(v.meta)} · cobrado ${usd(v.cobMes)} (${v.p}%)`:'Cuenta clave — sin meta de cobro'}</div></div>
                  {chip(semColor(v.sem),semLabel(v.sem))}
                </div>
                <Sect amt={totCob}><span>Cobros proyectados esta semana</span></Sect>
                {cob.length? cob.map((f,i)=>{const[l,c]=estCob(f.estado);return <Line key={i} t1={f.cliente||'—'} t2={`${f.factura_doc?`Factura ${f.factura_doc}`:'—'}${f.fecha_prometida?` · ${f.fecha_prometida}`:''}`} rt={usd(f.monto_estimado)} badge={badge(l,c)}/>;}) : <div style={{fontSize:11.5,color:C.t3}}>Sin cobros proyectados cargados.</div>}
                <Sect><span>Ventas que espera cerrar</span></Sect>
                {vts.length? vts.map((f,i)=><Line key={i} t1={f.cliente||f.oportunidad_titulo||'—'} t2={f.oportunidad_titulo||''} rt={usd(f.monto_estimado)} badge={badge('pendiente',C.orangeText)}/>) : <div style={{fontSize:11.5,color:C.t3}}>Sin ventas comprometidas.</div>}
                <Sect><span>Leads NEXA asignados</span></Sect>
                <Line t1={`${v.lb.asig} leads asignados`} t2={`${v.lb.cont} contactados · ${v.lb.conv} convertidos · ${v.lb.sin} sin tocar`} badge={badge(`${v.lb.asig?Math.round(v.lb.cont/v.lb.asig*100):0}% respuesta`,'#6D28D9')}/>
                <Sect><span>Pipeline ponderado</span><span className="radar-num" style={{color:C.t2}}>{usd(v.pipePond)}</span></Sect>
                {(v.porEtapa||[]).length? v.porEtapa.map((e,i)=><Line key={i} t1={String(e.e||'').charAt(0).toUpperCase()+String(e.e||'').slice(1)} t2={`${e.n} ops`} rt={usd(e.pond)}/>) : <div style={{fontSize:11.5,color:C.t3}}>Sin pipeline.</div>}
              </div>;
            })() : <div style={{color:C.t4,padding:20}}>Sin vendedores en el período.</div>}
          </div>
        </div>

        {/* NEXA por vendedor */}
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:SH.sm,overflow:'hidden'}}>
          <div style={{padding:'13px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',gap:10,alignItems:'center'}}><div style={{fontSize:14,fontWeight:800}}>NEXA · leads por vendedor</div><div style={{fontSize:11.5,color:C.t3}}>asignación y respuesta del período</div></div>
          <div style={{overflowX:'auto'}}>
            <table style={{borderCollapse:'collapse',width:'100%',minWidth:560}}>
              <thead><tr><Th l>Vendedor</Th><Th>Asignados</Th><Th>Contactados</Th><Th>Convertidos</Th><Th>Sin tocar</Th><Th>% respuesta</Th></tr></thead>
              <tbody>
                {Object.entries(leadsBy).filter(([n])=>n!=='—').sort((a,b)=>b[1].asig-a[1].asig).map(([n,x])=>(
                  <tr key={n}><Td l><div style={{display:'flex',alignItems:'center',gap:9}}>{av(n,colV(n),22)}<span style={{fontWeight:700}}>{first(n)}</span></div></Td>
                    <Td b>{x.asig}</Td><Td>{x.cont}</Td><Td b c={C.primary}>{x.conv}</Td>
                    <Td c={x.sin?C.redText:C.t4}>{x.sin||'—'}</Td>
                    <Td c={C.t3}>{x.asig?Math.round(x.cont/x.asig*100)+'%':'—'}</Td></tr>
                ))}
                {!Object.keys(leadsBy).filter(n=>n!=='—').length && <tr><Td l c={C.t4}>Sin leads asignados en el período.</Td><Td/><Td/><Td/><Td/><Td/></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Guía */}
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:SH.sm,padding:'15px 16px'}}>
          <div style={{fontSize:14,fontWeight:800,marginBottom:10}}>¿Quién ingresa qué?</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(215px,1fr))',gap:12}}>
            {[['Gerencia',C.primary,'define',['Meta mensual de cada vendedor','Reparto de meta por línea','Aprueba prioridades del comité']],
              ['Vendedor (lunes)',C.info,'ingresa',['Cobros que van a caer esta semana','Ventas que espera cerrar','Lo que no cae se arrastra solo']],
              ['SAP (auto)',C.greenText,'alimenta',['Cobrado real (pagos)','Facturación y por cobrar','Contado vs crédito']],
              ['NEXA + CRM (auto)','#6D28D9','alimenta',['Leads asignados y respuesta','Pipeline y etapa de cada opp','Actividad (visitas / llamadas)']]].map(([t,col,tag,items])=>(
              <div key={t} style={{border:`1px solid ${C.border}`,borderRadius:12,padding:'13px 14px',background:C.bg1}}>
                <div style={{fontWeight:800,fontSize:13,marginBottom:6,color:C.t1}}>{t}</div>
                <span style={{fontSize:9.5,fontWeight:800,textTransform:'uppercase',letterSpacing:'.04em',color:col,background:col+'1f',border:`1px solid ${col}44`,padding:'2px 7px',borderRadius:6}}>{tag}</span>
                <ul style={{margin:'8px 0 0',paddingLeft:16,fontSize:12,color:C.t2,lineHeight:1.55}}>{items.map((it,i)=><li key={i}>{it}</li>)}</ul>
              </div>
            ))}
          </div>
          <div style={{fontSize:11.5,color:C.t3,marginTop:10,lineHeight:1.5}}>Regla del comité: el vendedor <b>solo ingresa lo que promete</b> (cobros y ventas de la semana). Todo lo <b>real</b> entra solo de SAP/NEXA/CRM — así se compara promesa vs realidad sin duplicar.</div>
        </div>
      </>
      )}
    </div>
  );
}

function ComiteComercial({ usuario, empresaActiva, onNavigate }) {
  const [opps, setOpps]       = useState([]);
  const [visitas, setVisitas] = useState([]);
  const [metas, setMetas]     = useState([]);
  const [ganadas, setGanadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [presenting, setPresenting] = useState(false);
  const [vista, setVista] = useState('revision');   // revision | resumen

  const hoy = new Date();
  const dow = hoy.getDay()||7;
  const lunes = new Date(hoy); lunes.setDate(hoy.getDate()-dow+1); lunes.setHours(0,0,0,0);
  const viernes = new Date(lunes); viernes.setDate(lunes.getDate()+4);
  const pad = n=>String(n).padStart(2,'0');
  const lunesISO = `${lunes.getFullYear()}-${pad(lunes.getMonth()+1)}-${pad(lunes.getDate())}`;
  const mes = `${hoy.getFullYear()}-${pad(hoy.getMonth()+1)}`;
  const semNum = (() => { const d=new Date(hoy); d.setDate(d.getDate()+3-(d.getDay()||7)+1); const w=new Date(d.getFullYear(),0,4); return 1+Math.round(((d-w)/86400000-3+(w.getDay()||7)-1)/7); })();
  const fmtDate = d => d.toLocaleDateString('es-HN',{day:'numeric',month:'short'});

  // Last week range
  const lunesLW = new Date(lunes); lunesLW.setDate(lunes.getDate()-7);
  const domingoLW = new Date(lunes); domingoLW.setDate(lunes.getDate()-1);
  const lunesLWISO = `${lunesLW.getFullYear()}-${pad(lunesLW.getMonth()+1)}-${pad(lunesLW.getDate())}`;
  const domingoLWISO = `${domingoLW.getFullYear()}-${pad(domingoLW.getMonth()+1)}-${pad(domingoLW.getDate())}`;
  const semLWLabel = `${fmtDate(lunesLW)} — ${fmtDate(domingoLW)}`;

  useEffect(()=>{
    let qO = supabase.from('oportunidades').select('*').not('etapa','in','(cerrado_ganado,cerrado_perdido,prospecto_invalido,cliente_frio)');
    let qG = supabase.from('oportunidades').select('vendedor,valor').eq('etapa','cerrado_ganado').gte('fecha_cierre',mes+'-01');
    if (empresaActiva) { qO=qO.eq('empresa_id',empresaActiva); qG=qG.eq('empresa_id',empresaActiva); }
    Promise.all([
      qO,
      supabase.from('visitas').select('vendedor,tipo,created_at').gte('created_at',lunesISO+'T00:00:00'),
      supabase.from('metas_vendedor').select('*').eq('mes',mes),
      qG,
    ]).then(([{data:o},{data:v},{data:m},{data:g}])=>{
      setOpps(o||[]); setVisitas(v||[]); setMetas(m||[]); setGanadas(g||[]); setLoading(false);
    });
  },[empresaActiva]);

  if(loading) return <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:C.t4,fontSize:14}}>Cargando comité...</div>;

  const ETAPA_INFO = [
    {id:'prospecto',   label:'Prospecto',   color:'#64748B', prob:0.10},
    {id:'contactado',  label:'Contactado',  color:'#0D9488', prob:0.25},
    {id:'cotizacion',  label:'Cotización',  color:'#F59E0B', prob:0.40},
    {id:'negociacion', label:'Negociación', color:'#EC4899', prob:0.60},
    {id:'cerrado',     label:'Cerrado',     color:'#0B7AB8', prob:0.75},
    {id:'facturado',   label:'Facturado',   color:'#8B5CF6', prob:0.85},
    {id:'pagado',      label:'Pagado',      color:'#06B6D4', prob:0.92},
  ];

  const pipePond = opps.reduce((s,o)=>s+(o.valor||0)*(ETAPAS_PROB[o.etapa]||0),0);
  const metaTotal = metas.reduce((s,m)=>s+(m.meta_dolares||0),0);
  const realMes = ganadas.reduce((s,o)=>s+(o.valor||0),0);
  // Cobertura clásica: pipeline ponderado ÷ meta MENSUAL (regla sana: ≥3x).
  // Antes dividía la meta entre 3 (la "reducía") e inflaba el ratio 3× → verde falso.
  const cobertura = metaTotal>0 ? (pipePond/metaTotal).toFixed(1) : '—';
  const pctMeta = metaTotal>0 ? Math.round(realMes/metaTotal*100) : 0;
  const cobColor = parseFloat(cobertura)>=3?C.green:parseFloat(cobertura)>=1.5?C.orange:C.red;

  const byEtapa = ETAPA_INFO.map(e=>({
    ...e,
    count: opps.filter(o=>o.etapa===e.id).length,
    bruto: opps.filter(o=>o.etapa===e.id).reduce((s,o)=>s+(o.valor||0),0),
    pond:  opps.filter(o=>o.etapa===e.id).reduce((s,o)=>s+(o.valor||0)*e.prob,0),
  }));
  const maxBruto = Math.max(...byEtapa.map(e=>e.bruto),1);

  const byLinea = ORG.lineasNegocio.map(l=>({
    label:l, pond: opps.filter(o=>o.linea_negocio===l).reduce((s,o)=>s+(o.valor||0)*(ETAPAS_PROB[o.etapa]||0),0),
    count: opps.filter(o=>o.linea_negocio===l).length,
  })).filter(l=>l.pond>0).sort((a,b)=>b.pond-a.pond);
  const maxLinea = Math.max(...byLinea.map(l=>l.pond),1);

  const topCierres = [...opps].filter(o=>['negociacion','cerrado','facturado','pagado'].includes(o.etapa)).sort((a,b)=>(b.valor||0)-(a.valor||0)).slice(0,3);

  const actEquipo = metas.map(m=>{
    const vis = visitas.filter(v=>v.vendedor===m.vendedor&&v.tipo==='visita').length;
    const lla = visitas.filter(v=>v.vendedor===m.vendedor&&v.tipo==='llamada').length;
    const nOpps = opps.filter(o=>o.vendedor===m.vendedor&&new Date(o.created_at)>=lunes).length;
    const real = ganadas.filter(o=>o.vendedor===m.vendedor).reduce((s,o)=>s+(o.valor||0),0);
    const pct = m.meta_dolares>0?Math.round(real/m.meta_dolares*100):0;
    return {nombre:m.vendedor,vis,lla,nOpps,pct,meta:m.meta_dolares};
  }).sort((a,b)=>b.pct-a.pct);

  const KPI_BAR = (val,max,color) => (
    <div style={{display:'flex',alignItems:'center',gap:6}}>
      <div style={{flex:1,height:6,background:C.bg3,borderRadius:R.pill,overflow:'hidden'}}>
        <div style={{width:`${Math.min(val/max*100,100)}%`,height:'100%',background:color,borderRadius:R.pill,transition:'width .3s'}}/>
      </div>
      <span className="radar-num" style={{fontSize:10,color:C.t3,fontWeight:700,minWidth:20,textAlign:'right'}}>{val}</span>
    </div>
  );

  const STAT = (label,value,sub,color=C.t1) => (
    <div className="radar-kpi" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',flex:1,boxShadow:SH.sm}}>
      <div style={{fontSize:11,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8,fontWeight:600}}>{label}</div>
      <div className="radar-num" style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',color,lineHeight:1.05}}>{value}</div>
      {sub&&<div style={{fontSize:11,color:C.t4,marginTop:6}}>{sub}</div>}
    </div>
  );

  return (
    <div style={{flex:1,overflowY:'auto',background:C.bg1}}>
      {presenting && <PresentacionSemanal lunasISO={lunesLWISO} domingoISO={domingoLWISO} semLabel={semLWLabel} empresaActiva={empresaActiva} onClose={()=>setPresenting(false)}/>}
      {/* Header */}
      <div style={{background:C.bg2,borderBottom:`1px solid ${C.border}`,padding:'20px 28px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <div style={{fontSize:19,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,display:'flex',alignItems:'center',gap:9}}><span style={{width:28,height:28,borderRadius:R.sm,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,color:C.blue,display:'inline-flex',alignItems:'center',justifyContent:'center'}}><CIcon d={CI.calendar} size={16}/></span>Comité Comercial</div>
          <div style={{fontSize:13,color:C.t3,marginTop:3}}>Semana {semNum} · {fmtDate(lunes)} — {fmtDate(viernes)}</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <button className="radar-btn" onClick={()=>setPresenting(true)}
            style={{background:C.blue,border:'none',borderRadius:R.md,padding:'8px 18px',color:'#FFFFFF',fontSize:13,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:7,boxShadow:`${SH.primary}, inset 0 1px 0 rgba(255,255,255,.12)`}}>
            <CIcon d={CI.play} size={14}/> Presentar Semana Pasada
          </button>
          <div style={{fontSize:12,color:C.t4}}>{hoy.toLocaleDateString('es-HN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</div>
        </div>
      </div>

      <div style={{padding:'14px 28px 0',display:'flex',gap:10,maxWidth:1200,margin:'0 auto'}}>
        <Seg value={vista} onChange={setVista} items={[['revision','Revisión comercial',CI.clipboard],['resumen','Resumen pipeline',CI.chart]]}/>
      </div>
      {vista==='revision' && <div style={{padding:'10px 28px 28px',maxWidth:1200,margin:'0 auto'}}><RevisionComite usuario={usuario}/></div>}
      {vista==='resumen' && (
      <div style={{padding:24,maxWidth:1200,margin:'0 auto',display:'flex',flexDirection:'column',gap:24}}>

        {/* KPI Cards */}
        <div style={{display:'flex',gap:14}}>
          {STAT('Pipeline Ponderado',`$${Math.round(pipePond/1000)}k`,'Valor ajustado por probabilidad',C.blue)}
          {STAT('Cobertura',`${cobertura}x`,`Pipeline ponderado / Meta del mes (mín. 3x)`,cobColor)}
          {STAT(`Meta ${hoy.toLocaleString('es',{month:'long'})}`,`${pctMeta}%`,`${fmt(realMes)} de ${fmt(metaTotal)}`,pctMeta>=100?C.green:pctMeta>=70?C.orange:C.red)}
          {STAT('Oportunidades',opps.length,`${byEtapa.filter(e=>e.count>0).length} etapas activas`,C.t1)}
        </div>

        {/* Funnel + Líneas */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>

          {/* Funnel */}
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:20}}>
            <div style={{fontSize:13,fontWeight:800,color:C.t1,marginBottom:16,textTransform:'uppercase',letterSpacing:'.06em'}}>🔻 Funnel de Pipeline</div>
            {byEtapa.map(e=>{
              const w = e.bruto>0?Math.round(e.bruto/maxBruto*100):0;
              return (
                <div key={e.id} style={{marginBottom:12,cursor:'pointer',borderRadius:8,padding:'4px 6px',margin:'0 -6px 8px',transition:'background .15s'}}
                  onClick={()=>onNavigate&&onNavigate('crm',{etapa:e.id})}
                  onMouseEnter={ev=>ev.currentTarget.style.background=`${e.color}15`}
                  onMouseLeave={ev=>ev.currentTarget.style.background='transparent'}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{width:8,height:8,borderRadius:'50%',background:e.color,display:'inline-block'}}/>
                      <span style={{fontSize:12,fontWeight:700,color:e.color}}>{e.label}</span>
                      <span style={{fontSize:11,color:C.t4}}>({e.count})</span>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <span style={{fontSize:11,color:C.t2,fontWeight:700}}>${Math.round(e.bruto/1000)}k</span>
                      <span style={{fontSize:10,color:C.t4}}> · pond. </span>
                      <span style={{fontSize:11,color:e.color,fontWeight:700}}>${Math.round(e.pond/1000)}k</span>
                    </div>
                  </div>
                  <div style={{height:10,background:C.bg3,borderRadius:5,overflow:'hidden'}}>
                    <div style={{width:`${w}%`,height:'100%',background:`linear-gradient(90deg,${e.color}99,${e.color})`,borderRadius:5,transition:'width .4s'}}/>
                  </div>
                </div>
              );
            })}
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginTop:4,display:'flex',justifyContent:'space-between'}}>
              <span style={{fontSize:11,color:C.t3,fontWeight:600,letterSpacing:'.06em'}}>TOTAL PIPELINE</span>
              <div>
                <span className="radar-num" style={{fontSize:13,color:C.t2,fontWeight:700}}>{fmt(opps.reduce((s,o)=>s+(o.valor||0),0))}</span>
                <span style={{fontSize:11,color:C.t4}}> · pond. </span>
                <span className="radar-num" style={{fontSize:13,color:C.greenText,fontWeight:700}}>{fmt(pipePond)}</span>
              </div>
            </div>
          </div>

          {/* Por línea */}
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:20}}>
            <div style={{fontSize:13,fontWeight:800,color:C.t1,marginBottom:16,textTransform:'uppercase',letterSpacing:'.06em'}}>📦 Pipeline por Línea</div>
            {byLinea.length===0?(
              <div style={{textAlign:'center',color:C.t4,fontSize:13,padding:20}}>Sin datos — asigná líneas de negocio a las oportunidades</div>
            ):byLinea.map((l,i)=>{
              const COLORES=['#0B7AB8','#10B981','#F59E0B','#EC4899','#0D9488'];
              const col = COLORES[i%COLORES.length];
              const w = Math.round(l.pond/maxLinea*100);
              return (
                <div key={l.label} style={{marginBottom:14,cursor:'pointer',borderRadius:8,padding:'4px 6px',margin:'0 -6px 10px',transition:'background .15s'}}
                  onClick={()=>onNavigate&&onNavigate('crm',{linea:l.label})}
                  onMouseEnter={ev=>ev.currentTarget.style.background=`${col}15`}
                  onMouseLeave={ev=>ev.currentTarget.style.background='transparent'}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                    <span style={{fontSize:12,color:C.t2,fontWeight:600,maxWidth:180,lineHeight:1.3}}>{l.label}</span>
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <span style={{fontSize:12,fontWeight:800,color:col}}>{fmt(l.pond)}</span>
                      <span style={{fontSize:10,color:C.t4,marginLeft:4}}>{l.count} opp</span>
                    </div>
                  </div>
                  <div style={{height:8,background:C.bg3,borderRadius:4,overflow:'hidden'}}>
                    <div style={{width:`${w}%`,height:'100%',background:col,borderRadius:4,transition:'width .4s'}}/>
                  </div>
                </div>
              );
            })}
            {/* Sin categoría */}
            {(()=>{ const sinCat=opps.filter(o=>!o.linea_negocio&&!['cerrado_ganado','cerrado_perdido'].includes(o.etapa)); if(!sinCat.length) return null;
              return (
                <div style={{marginTop:16,paddingTop:14,borderTop:`1px solid ${C.border}`}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                    <span style={{fontSize:11,fontWeight:600,color:C.orangeText}}>⚠ Sin categoría asignada</span>
                    <span style={{fontSize:11,color:C.t4}}>{sinCat.length} oportunidades · {fmt(sinCat.reduce((s,o)=>s+(o.valor||0),0))}</span>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:5,maxHeight:200,overflowY:'auto'}}>
                    {sinCat.map(o=>(
                      <div key={o.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.sm,padding:'6px 10px'}}>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:C.t1,lineHeight:1.2}}>{o.titulo}</div>
                          <div style={{fontSize:10,color:C.t4}}>{o.vendedor||'Sin asignar'} · {o.etapa}</div>
                        </div>
                        <span className="radar-num" style={{fontSize:11,fontWeight:700,color:C.t1,flexShrink:0,marginLeft:8}}>{fmt(o.valor||0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Top 3 en cierre */}
        {topCierres.length>0&&(
          <div>
            <div style={{fontSize:13,fontWeight:800,color:C.t1,marginBottom:12,textTransform:'uppercase',letterSpacing:'.06em'}}>🔥 Top Oportunidades en Cierre</div>
            <div style={{display:'grid',gridTemplateColumns:`repeat(${topCierres.length},1fr)`,gap:14}}>
              {topCierres.map((o,i)=>{
                const et = ETAPA_INFO.find(e=>e.id===o.etapa)||ETAPA_INFO[4];
                const pond = Math.round((o.valor||0)*et.prob);
                return (
                  <div key={o.id} style={{background:C.bg2,border:`2px solid ${et.color}40`,borderRadius:14,padding:18,position:'relative',overflow:'hidden'}}>
                    <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${et.color},${et.color}66)`}}/>
                    {i===0&&<div style={{position:'absolute',top:10,right:12,fontSize:18}}>🥇</div>}
                    {i===1&&<div style={{position:'absolute',top:10,right:12,fontSize:18}}>🥈</div>}
                    {i===2&&<div style={{position:'absolute',top:10,right:12,fontSize:18}}>🥉</div>}
                    <div style={{fontSize:14,fontWeight:700,color:C.t1,marginBottom:4,paddingRight:28,lineHeight:1.3}}>{o.titulo}</div>
                    <div style={{fontSize:12,color:C.t3,marginBottom:10}}>{o.cliente}</div>
                    <div className="radar-num" style={{fontSize:24,fontWeight:700,letterSpacing:'-0.02em',color:C.t1,marginBottom:2}}>{fmt(o.valor)}</div>
                    <div className="radar-num" style={{fontSize:11,color:C.t4,marginBottom:10}}>Pond. <span style={{color:et.color,fontWeight:700}}>{fmt(pond)}</span></div>
                    {[['Etapa',<span style={{color:et.color,fontWeight:700}}>{et.label}</span>],['Vendedor',o.vendedor],o.proxima_accion&&['→ Próxima',o.proxima_accion]].filter(Boolean).map(([l,v])=>(
                      <div key={l} style={{display:'flex',justifyContent:'space-between',fontSize:11,padding:'4px 0',borderTop:`1px solid ${C.border}`}}>
                        <span style={{color:C.t4}}>{l}</span><span style={{color:C.t2,fontWeight:600,textAlign:'right',maxWidth:'60%'}}>{v}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actividad del equipo */}
        {actEquipo.length>0&&(
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,overflow:'hidden'}}>
            <div style={{padding:'14px 20px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontSize:13,fontWeight:800,color:C.t1,textTransform:'uppercase',letterSpacing:'.06em'}}>🏃 Actividad del Equipo — Semana {semNum}</div>
              <div style={{fontSize:11,color:C.t4}}>Metas: 6 vis · 100 lla · 5 opps</div>
            </div>
            <div style={{overflowX:'auto'}}>
              <table className="radar-tbl" style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr>
                    {['Vendedor','Visitas /6','Llamadas /100','Opps nuevas /5','% Meta mes'].map(h=>(
                      <th key={h} style={{padding:'10px 16px',fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',textAlign:'left',borderBottom:`1px solid ${C.border}`,background:C.bg2}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {actEquipo.map((v,i)=>{
                    const pctColor = v.pct>=100?C.greenText:v.pct>=70?C.orangeText:v.pct>=40?C.blue:C.redText;
                    return (
                      <tr key={v.nombre} style={{borderBottom:`1px solid ${C.bg3}`}}>
                        <td style={{padding:'12px 16px'}}>
                          <div style={{fontSize:13,fontWeight:600,color:C.t1}}>{String(v.nombre||'—').split(' ')[0]}</div>
                          <div style={{fontSize:10,color:C.t4}}>{String(v.nombre||'').split(' ').slice(1).join(' ')}</div>
                        </td>
                        <td style={{padding:'12px 16px',minWidth:120}}>{KPI_BAR(v.vis,6,v.vis>=6?C.green:C.blue)}</td>
                        <td style={{padding:'12px 16px',minWidth:140}}>{KPI_BAR(v.lla,100,v.lla>=100?C.green:C.orange)}</td>
                        <td style={{padding:'12px 16px',minWidth:130}}>{KPI_BAR(v.nOpps,5,v.nOpps>=5?C.green:C.indigo)}</td>
                        <td style={{padding:'12px 16px',minWidth:140}}>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <div style={{flex:1,height:8,background:C.bg3,borderRadius:R.pill,overflow:'hidden'}}>
                              <div style={{width:`${Math.min(v.pct,100)}%`,height:'100%',background:v.pct>=100?C.green:v.pct>=70?C.orange:v.pct>=40?C.blue:C.red,borderRadius:R.pill}}/>
                            </div>
                            <span className="radar-num" style={{fontSize:12,fontWeight:700,color:pctColor,minWidth:36}}>{v.pct}%</span>
                          </div>
                          <div className="radar-num" style={{fontSize:10,color:C.t4,marginTop:3}}>{fmt(v.meta)} meta</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {actEquipo.length===0&&metas.length===0&&(
          <div style={{textAlign:'center',padding:40,color:C.t4}}>
            <div style={{fontSize:40,marginBottom:10}}>📊</div>
            <div style={{fontSize:14,marginBottom:4}}>Sin metas configuradas</div>
            <div style={{fontSize:12}}>Entrá a Forecast → configurá metas mensuales por vendedor</div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// ─── INVENTARIO SAP ───────────────────────────────────────────────────────────
// Cache del inventario a nivel de MODULO, no de componente. El estado de React
// muere al salir de la pantalla, asi que volver a Inventario rehacia la carga
// entera desde cero — con ~16 mil items eso son varios segundos de "Cargando…"
// cada vez. Aca sobrevive mientras la pestana este abierta.
const INV_CACHE = new Map();          // empresa -> { porEmp, frescura, cuando }
const INV_VIGENCIA = 5 * 60000;       // pasados 5 min se refresca solo, sin tapar la vista

function InventarioSAP() {
  const [empresa, setEmpresa] = useState('ambas');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [busq, setBusq]       = useState('');
  const [filtroGrupo, setFiltroGrupo]   = useState([]);   // multi ([] = todos)
  const [filtroBodega, setFiltroBodega] = useState([]);   // multi ([] = todas)
  // Linea: el prefijo del codigo (R01, L01, EPP...). SAP solo manda "familias",
  // pero la codificacion de Monhaco lleva la categoria real adentro del codigo.
  const [filtroLinea, setFiltroLinea]   = useState([]);   // multi ([] = todas)
  const [tope, setTope] = useState(500);
  const [soloConStock, setSoloConStock] = useState(false);
  const [soloQuiebre, setSoloQuiebre]   = useState(false);
  const [soloSinCosto, setSoloSinCosto] = useState(false);
  const [soloMargenNeg, setSoloMargenNeg] = useState(false);
  const [expandido, setExpandido]       = useState({});
  // Vista: productos (SKU, cantidades) o equipos serializados (cada uno es unico).
  // No se administra un repuesto y un montacargas con la misma logica.
  const [vista, setVista] = useState('productos');
  const [equipos, setEquipos] = useState(null);
  const [eqEstado, setEqEstado] = useState('todos');
  const [busqEq, setBusqEq] = useState('');
  // Ficha del producto (drawer) + su disponibilidad en las otras empresas.
  const [fichaSel, setFichaSel] = useState(null);
  const [fichaTab, setFichaTab] = useState('resumen');
  const [otras, setOtras] = useState(null);
  // Filtros por columna (capa 2 del mockup): rango min/max sobre columnas numericas.
  // popCol guarda que popover esta abierto y DONDE (coordenadas fijas, porque la
  // tabla tiene overflow y un popover adentro quedaria recortado).
  const [colFiltros, setColFiltros] = useState({});
  const [popCol, setPopCol] = useState(null);
  // Vistas guardadas: el set completo de filtros con nombre, en localStorage.
  const [vistas, setVistas] = useState(() => {
    try { return JSON.parse(localStorage.getItem('radar_inv_vistas') || '[]'); } catch { return []; }
  });
  const [vistaActiva, setVistaActiva] = useState('');

  // Carga perezosa de la flota: solo al entrar a la vista de equipos.
  useEffect(() => {
    if (vista !== 'equipos' || equipos !== null) return;
    (async () => {
      const { data } = await supabase.from('equipos')
        .select('id,codigo,marca,modelo,serie,tipo_equipo,estado,horometro,costo_adquisicion,precio_venta,precio_renta_mes,cliente_actual,sucursal,ubicacion,empresa_id')
        .limit(1000);
      setEquipos(data || []);
    })();
  }, [vista]); // eslint-disable-line

  // Abrir la ficha consulta EN QUE OTRAS EMPRESAS existe el mismo codigo:
  // es la base del "disponible en otra empresa" del mockup.
  const abrirFicha = async (item) => {
    setFichaSel(item); setFichaTab('resumen'); setOtras('cargando');
    try {
      const { data } = await supabase.from('inventario_sap')
        .select('empresa_id,stock,comprometido,disponible,costo_usd,precio_usd,synced_at')
        .eq('codigo', item.codigo).neq('empresa_id', item.empresa_id);
      setOtras(data || []);
    } catch { setOtras([]); }
  };

  // Fase 2: sync de movimiento (ventas 6 meses + OC abiertas). Igual que el
  // refresco de inventario: una empresa por pedido, con progreso a la vista.
  const [movBusy, setMovBusy] = useState(false);
  const [movProg, setMovProg] = useState('');
  const calcularMovimiento = async () => {
    setMovBusy(true); setError(null); setMovProg('');
    const lista = empresa === 'ambas' ? ORG.empresas.map(e=>e.id) : [empresa];
    const fallos = [];
    for (const ei of lista) {
      const etiqueta = EMP_LABEL[ei] || ei;
      setMovProg(`${etiqueta} (${lista.indexOf(ei)+1} de ${lista.length})…`);
      try {
        const r = await fetch('/api/sap-inventario', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ empresa_id: ei, movimiento: true }),
        });
        const txt = await r.text();
        if (!r.ok) { fallos.push(`${etiqueta}: HTTP ${r.status}${r.status===504?' (se pasó del tiempo límite)':''}`); continue; }
        let d; try { d = JSON.parse(txt); } catch { fallos.push(`${etiqueta}: respuesta no válida`); continue; }
        if (!d.ok) { fallos.push(`${etiqueta}: ${d.error || 'error desconocido'}`); continue; }
        Object.entries(d.resultado || {}).forEach(([k,v]) => { if (v.error) fallos.push(`${EMP_LABEL[k]||k}: ${v.error}`); });
      } catch (e) { fallos.push(`${etiqueta}: ${e.message}`); }
    }
    setMovProg('');
    if (fallos.length) setError(fallos.join(' · '));
    await cargarCache(true);
    setMovBusy(false);
  };

  const [refrescando, setRefrescando] = useState(false);
  const [frescura, setFrescura] = useState(null);

  // Lee del CACHE en la base: instantaneo. Un cron lo refresca cada 3 horas, y el
  // boton de abajo lo fuerza. El stock con el que se COMPROMETE una venta se
  // revalida en vivo contra SAP en el cotizador, no se toma de aca.
  const cargarCache = async (forzar = false) => {
    // 1) Lo que ya se trajo se pinta AL INSTANTE. Volver al modulo deja de costar
    //    una espera en blanco.
    const guardado = INV_CACHE.get(empresa);
    if (guardado) { setData(guardado.porEmp); setFrescura(guardado.frescura); }
    if (guardado && !forzar && Date.now() - guardado.cuando < INV_VIGENCIA) return;

    // 2) Si ya hay algo en pantalla, el refresco va en segundo plano: nada de
    //    tapar la tabla con "Cargando…" para terminar mostrando lo mismo.
    if (!guardado) setLoading(true);
    setError(null);
    try {
      // Supabase corta cada request a 1,000 filas (mismo bug que ya mordio al CRM):
      // un .limit(20000) devuelve 1,000 items arbitrarios de 15,876 y el dashboard
      // muestra puro item muerto sin stock. Hay que paginar por rango.
      //
      // Antes las paginas se pedian EN SERIE: con 16 mil items eran dieciseis
      // viajes esperando uno al otro, y esa era la lentitud. Ahora se pregunta
      // primero cuantas filas hay y se piden todas las paginas a la vez.
      const conFiltro = (q) => empresa !== 'ambas' ? q.eq('empresa_id', empresa) : q;

      const { count, error: errC } = await conFiltro(
        supabase.from('inventario_sap').select('codigo', { count: 'exact', head: true })
      );
      if (errC) throw new Error(errC.message);

      let rows = [];
      if (count == null) {
        // Sin conteo no se puede repartir: se cae al recorrido en serie de antes.
        for (let desde = 0; ; desde += 1000) {
          const { data: pagina, error: err } = await conFiltro(
            supabase.from('inventario_sap').select('*').order('codigo').range(desde, desde + 999));
          if (err) throw new Error(err.message);
          rows = rows.concat(pagina || []);
          if (!pagina || pagina.length < 1000) break;
          if (rows.length >= 80000) break;
        }
      } else {
        const total = Math.min(count, 80000); // tope de seguridad
        const paginas = Math.ceil(total / 1000);
        const trozos = await Promise.all(
          Array.from({ length: paginas }, (_, i) => conFiltro(
            supabase.from('inventario_sap').select('*').order('codigo').range(i * 1000, i * 1000 + 999)))
        );
        const roto = trozos.find(t => t.error);
        if (roto) throw new Error(roto.error.message);
        rows = trozos.flatMap(t => t.data || []);
      }
      // Se reagrupa por empresa para no cambiar la forma que ya consume la pantalla.
      const porEmp = {};
      (rows || []).forEach(r => {
        const ei = r.empresa_id;
        if (!porEmp[ei]) porEmp[ei] = { items:[], total:0, stock_por_bodega:false, bodegas_disponibles:[] };
        porEmp[ei].items.push(r);
        if (Array.isArray(r.bodegas) && r.bodegas.length) {
          porEmp[ei].stock_por_bodega = true;
          r.bodegas.forEach(b => {
            if (!porEmp[ei].bodegas_disponibles.some(x => String(x.codigo)===String(b.codigo)))
              porEmp[ei].bodegas_disponibles.push({ codigo:b.codigo, nombre:b.nombre });
          });
        }
      });
      Object.values(porEmp).forEach(v => { v.total = v.items.length; });
      const sellos = (rows || []).map(r => r.synced_at).filter(Boolean).sort();
      const frescuraNueva = sellos.length ? sellos[sellos.length-1] : null;

      INV_CACHE.set(empresa, { porEmp, frescura: frescuraNueva, cuando: Date.now() });
      setData(porEmp);
      setFrescura(frescuraNueva);
    } catch (e) {
      // Si ya habia algo pintado del cache, se deja: es viejo pero sirve mucho
      // mas que vaciar la pantalla porque un refresco de fondo fallo.
      setError(e.message);
    }
    setLoading(false);
  };

  const [progreso, setProgreso] = useState('');

  // Fuerza el refresco: consulta SAP y reescribe el cache.
  //
  // UNA EMPRESA POR PEDIDO. Pedir las 4 de una sola vez recorre el catalogo completo
  // de cada base con las colecciones de bodegas y precios anidadas, y eso excede el
  // limite de tiempo de la funcion: Vercel la corta y el navegador se queda sin
  // respuesta que leer ("no carga nada"). Secuencial y con progreso a la vista.
  const refrescarDesdeSAP = async () => {
    setRefrescando(true); setError(null); setProgreso('');
    const lista = empresa === 'ambas' ? ORG.empresas.map(e=>e.id) : [empresa];
    const fallos = [];
    for (const ei of lista) {
      const etiqueta = EMP_LABEL[ei] || ei;
      setProgreso(`${etiqueta} (${lista.indexOf(ei)+1} de ${lista.length})…`);
      try {
        const r = await fetch('/api/sap-inventario', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ empresa_id: ei, guardar: true }),
        });
        // Si la funcion muere por tiempo, la respuesta NO es JSON. Sin esto el
        // r.json() explota con un error de parseo que no dice nada util.
        const txt = await r.text();
        if (!r.ok) { fallos.push(`${etiqueta}: HTTP ${r.status}${r.status===504?' (se pasó del tiempo límite)':''}`); continue; }
        let d; try { d = JSON.parse(txt); }
        catch { fallos.push(`${etiqueta}: respuesta no válida (${txt.slice(0,80)})`); continue; }
        if (!d.ok) { fallos.push(`${etiqueta}: ${d.error || 'error desconocido'}`); continue; }
        Object.entries(d.resultado || {}).forEach(([k,v]) => { if (v.error) fallos.push(`${EMP_LABEL[k]||k}: ${v.error}`); });
      } catch (e) { fallos.push(`${etiqueta}: ${e.message}`); }
    }
    setProgreso('');
    if (fallos.length) setError(fallos.join(' · '));
    await cargarCache(true);   // acaba de cambiar SAP: el cache local ya no vale
    setRefrescando(false);
  };

  // Abre con el inventario puesto. Sin botones.
  useEffect(() => { cargarCache(); }, [empresa]); // eslint-disable-line

  const haceCuanto = (iso) => {
    if (!iso) return null;
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 1)  return 'hace segundos';
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min/60);
    if (h < 24) return `hace ${h} h`;
    return `hace ${Math.floor(h/24)} d`;
  };

  // Empresas del tenant, no una lista fija.
  const EMP_LABEL = Object.fromEntries(ORG.empresas.map(e => [e.id, e.label]));
  const empSel = empresa === 'ambas' ? ORG.empresas.map(e=>e.id) : [empresa];

  const allItems = (() => {
    if (!data) return [];
    return empSel.flatMap(ei => data[ei]?.items || []);
  })();

  // ¿Alguna empresa devolvió desglose por bodega? Si no, se dice, en vez de mostrar
  // columnas vacías como si el dato no existiera.
  const hayBodegas = empSel.some(ei => data?.[ei]?.stock_por_bodega);
  const bodegas = (() => {
    const m = {};
    empSel.forEach(ei => (data?.[ei]?.bodegas_disponibles || []).forEach(b => { m[b.codigo] = b.nombre; }));
    return Object.entries(m).sort((a,b) => String(a[0]).localeCompare(String(b[0])));
  })();

  const grupoMap = {};
  allItems.forEach(i => { grupoMap[i.grupo_code] = i.grupo; });
  const grupos = Object.entries(grupoMap).sort((a,b) => (a[1]||'').localeCompare(b[1]||''));
  // Lineas presentes, ordenadas por cuantos items tiene cada una: lo que mas
  // pesa primero, en vez de un orden alfabetico que no dice nada.
  const lineas = (() => {
    const m = {};
    allItems.forEach(i => { const l = lineaDe(i.codigo); if (l) m[l] = (m[l]||0) + 1; });
    return Object.entries(m).sort((a,b) => b[1] - a[1]);
  })();

  // Vista por bodega: recorta cada ítem a esa bodega. "Qué tengo en esta sucursal".
  let base = allItems;
  if (filtroBodega.length) {
    base = base.map(i => {
      const solo = (i.bodegas || []).filter(b => filtroBodega.includes(String(b.codigo)));
      if (!solo.length) return null;
      const stock = solo.reduce((s,b)=>s+b.stock,0);
      const comprometido = solo.reduce((s,b)=>s+b.comprometido,0);
      return { ...i, bodegas:solo, stock, comprometido, disponible:stock-comprometido,
               valor_total_usd: i.costo_usd == null ? null : Math.round(stock * i.costo_usd * 100)/100 };
    }).filter(Boolean);
  }
  if (filtroGrupo.length)  base = base.filter(i => filtroGrupo.includes(i.grupo_code));
  if (filtroLinea.length)  base = base.filter(i => filtroLinea.includes(lineaDe(i.codigo)));
  if (busq.length >= 2) {
    const b = busq.toLowerCase();
    // Sin guards: SAP tiene items con nombre o codigo en null (articulos dados de
    // baja a medias) y la busqueda reventaba con "Cannot read properties of null".
    base = base.filter(i =>
      String(i.codigo||'').toLowerCase().includes(b) ||
      String(i.nombre||'').toLowerCase().includes(b) ||
      String(i.num_parte||'').toLowerCase().includes(b));
  }

  // Los contadores de los chips se calculan ANTES de aplicar los chips, para que
  // cada chip diga cuánto agarraría aunque otro esté activo.
  const nConStock  = base.filter(i => i.stock > 0).length;
  const nQuiebre   = base.filter(i => i.disponible != null && i.disponible <= 0 && i.stock > 0).length;
  const nSinCosto  = base.filter(i => i.costo_usd == null && i.stock > 0).length;
  const nMargenNeg = base.filter(i => i.margen_pct != null && i.margen_pct < 0).length;

  let filtered = base;
  if (soloConStock)  filtered = filtered.filter(i => i.stock > 0);
  // Quiebre: hay existencia pero nada vendible, porque ya está comprometida.
  if (soloQuiebre)   filtered = filtered.filter(i => i.disponible != null && i.disponible <= 0 && i.stock > 0);
  if (soloSinCosto)  filtered = filtered.filter(i => i.costo_usd == null && i.stock > 0);
  if (soloMargenNeg) filtered = filtered.filter(i => i.margen_pct != null && i.margen_pct < 0);

  // Filtros por columna (rango min/max). Un item sin dato (null) no pasa un
  // filtro numerico: si filtras por costo, "sin costo" no es una respuesta.
  Object.entries(colFiltros).forEach(([campo, f]) => {
    if (!f || (f.min === '' && f.max === '')) return;
    filtered = filtered.filter(i => {
      const v = i[campo];
      if (v == null) return false;
      if (f.min !== '' && Number(v) < Number(f.min)) return false;
      if (f.max !== '' && Number(v) > Number(f.max)) return false;
      return true;
    });
  });
  const nColFiltros = Object.values(colFiltros).filter(f => f && (f.min !== '' || f.max !== '')).length;
  // Al cambiar de filtro se vuelve al tope inicial: si no, quedaba "mostrando
  // 5,000" sobre un resultado de 12 items.
  const claveFiltros = `${busq}|${filtroGrupo}|${filtroLinea}|${filtroBodega}|${soloConStock}|${soloQuiebre}|${soloSinCosto}|${soloMargenNeg}|${nColFiltros}|${empresa}`;
  useEffect(() => { setTope(500); }, [claveFiltros]);

  // ── Vistas guardadas: nombre + snapshot completo de los filtros ──
  const persistirVistas = (vs) => {
    setVistas(vs);
    try { localStorage.setItem('radar_inv_vistas', JSON.stringify(vs)); } catch {}
  };
  const guardarVista = () => {
    const nombre = window.prompt('Nombre de la vista (ej. "Llantas con stock HN"):');
    if (!nombre || !nombre.trim()) return;
    const v = { nombre: nombre.trim(), empresa, busq, filtroGrupo, filtroBodega,
      soloConStock, soloQuiebre, soloSinCosto, soloMargenNeg, colFiltros };
    persistirVistas([...vistas.filter(x => x.nombre !== v.nombre), v]);
    setVistaActiva(v.nombre);
  };
  const aplicarVista = (v) => {
    // Retrocompat: vistas viejas guardaron string; las nuevas, array.
    const arrF = x => Array.isArray(x) ? x : (x ? [x] : []);
    setEmpresa(v.empresa ?? 'ambas'); setBusq(v.busq ?? '');
    setFiltroGrupo(arrF(v.filtroGrupo)); setFiltroBodega(arrF(v.filtroBodega));
    setSoloConStock(!!v.soloConStock); setSoloQuiebre(!!v.soloQuiebre);
    setSoloSinCosto(!!v.soloSinCosto); setSoloMargenNeg(!!v.soloMargenNeg);
    setColFiltros(v.colFiltros || {});
    setVistaActiva(v.nombre);
  };
  const limpiarFiltros = () => {
    setBusq(''); setFiltroGrupo([]); setFiltroBodega([]);
    setSoloConStock(false); setSoloQuiebre(false); setSoloSinCosto(false); setSoloMargenNeg(false); setFiltroLinea([]);
    setColFiltros({}); setVistaActiva('');
  };
  const hayFiltros = busq || filtroGrupo.length || filtroLinea.length || filtroBodega.length || soloConStock || soloQuiebre || soloSinCosto || soloMargenNeg || nColFiltros > 0;

  const totalStock   = filtered.reduce((s,i) => s + i.stock, 0);
  const totalComprom = filtered.reduce((s,i) => s + (i.comprometido||0), 0);
  // Los sin costo NO suman $0 al capital: se cuentan aparte como dato faltante.
  const valorTotal   = filtered.reduce((s,i) => s + (i.valor_total_usd||0), 0);
  const conStock     = filtered.filter(i => i.stock > 0).length;
  const enQuiebre    = filtered.filter(i => i.disponible != null && i.disponible <= 0 && i.stock > 0).length;
  const sinCostoConStock = filtered.filter(i => i.costo_usd == null && i.stock > 0).length;
  const sinGrupo     = allItems.filter(i => !i.grupo_code).length;

  // Distribución por bodega: dónde está el capital inmovilizado.
  const porBodega = (() => {
    const m = {};
    filtered.forEach(i => (i.bodegas || []).forEach(b => {
      const k = String(b.codigo);
      if (!m[k]) m[k] = { codigo:b.codigo, nombre:b.nombre, unidades:0, valor:0, items:0 };
      m[k].unidades += b.stock;
      m[k].valor    += b.stock * (i.costo_usd || 0);
      if (b.stock > 0) m[k].items++;
    }));
    return Object.values(m).sort((a,b) => b.valor - a.valor);
  })();
  const maxValBodega = Math.max(1, ...porBodega.map(b => b.valor));

  const topValor = [...filtered].sort((a,b) => (b.valor_total_usd||0) - (a.valor_total_usd||0)).slice(0,6);

  const exportCSV = () => {
    const headers = ['Código','Nombre','N° parte','Grupo','Empresa','Bodega','Stock','Comprometido','Disponible','Costo USD','Precio USD','Margen %','Valor USD'];
    const rows = [];
    filtered.forEach(i => {
      const emp = EMP_LABEL[i.empresa_id] || i.empresa_id;
      // Una fila POR BODEGA: es el formato con el que se puede trabajar en Excel.
      if (i.bodegas && i.bodegas.length) {
        i.bodegas.forEach(b => rows.push([i.codigo,i.nombre,i.num_parte||'',i.grupo,emp,
          `${b.codigo} - ${b.nombre}`,b.stock,b.comprometido,b.disponible,
          i.costo_usd,i.precio_usd??'',i.margen_pct??'',Math.round(b.stock*i.costo_usd*100)/100]));
      } else {
        rows.push([i.codigo,i.nombre,i.num_parte||'',i.grupo,emp,'',i.stock,
          i.comprometido??'',i.disponible??'',i.costo_usd,i.precio_usd??'',i.margen_pct??'',i.valor_total_usd]);
      }
    });
    const csv = [headers,...rows].map(r =>
      r.map(c => `"${String(c||'').replace(/"/g,'""')}"`).join(',')
    ).join('\n');
    const blob = new Blob(['\ufeff'+csv], { type:'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `inventario_sap_${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const COLS = '24px 116px 1fr 96px 120px 68px 80px 76px 76px 76px 58px 92px';
  // Tercer elemento = campo filtrable por rango (popover al hacer click en el header).
  const HEAD = [['','left'],['Código','left'],['Nombre','left'],['N° parte','left'],['Grupo','left'],
    ['Stock','right','stock'],['Comprom.','right','comprometido'],['Disp.','right','disponible'],
    ['Costo','right','costo_usd'],['Precio','right','precio_usd'],['Margen','right','margen_pct'],
    ['Valor USD','right','valor_total_usd']];
  const money = (n) => n == null ? '—' : `$${Number(n).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const num = (n) => n == null ? '—' : Number(n).toLocaleString('en-US');

  return (
    <div style={{flex:1,overflowY:'auto',padding:'24px 24px 28px',background:C.bg1}}>
      <div style={{maxWidth:1240,margin:'0 auto'}}>

        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20,flexWrap:'wrap',gap:12}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <span style={{width:36,height:36,borderRadius:R.md,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,color:C.blue,display:'inline-flex',alignItems:'center',justifyContent:'center'}}><Icon id="inventario" size={19}/></span>
            <div>
              <h1 style={{margin:'0 0 2px',fontSize:19,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>Inventario SAP</h1>
              {/* Salud de la integración: SAP es la fuente, y acá se dice cómo
                  está la foto — frescura, cuántos con costo, cuántos sin grupo. */}
              <div style={{fontSize:12,color:C.t4,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                {frescura ? (
                  <>
                    <span style={{width:6,height:6,borderRadius:'50%',background:C.green,display:'inline-block'}}/>
                    <span>Actualizado {haceCuanto(frescura)} desde SAP</span>
                    <span style={{color:C.border}}>|</span>
                    <span className="radar-num">{allItems.length.toLocaleString('en-US')} artículos</span>
                    {nSinCosto > 0 && (<>
                      <span style={{color:C.border}}>|</span>
                      <button onClick={()=>setSoloSinCosto(true)}
                        style={{background:'none',border:'none',padding:0,cursor:'pointer',color:C.orangeText,fontWeight:700,fontSize:12}}>
                        {nSinCosto.toLocaleString('en-US')} con stock sin costo
                      </button>
                    </>)}
                    {sinGrupo > 0 && (<>
                      <span style={{color:C.border}}>|</span>
                      <span className="radar-num">{sinGrupo.toLocaleString('en-US')} sin grupo</span>
                    </>)}
                  </>
                ) : 'Ítems desde SAP B1 — sin servicios'}
              </div>
            </div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            {/* Pills de empresa (receta 7.4) */}
            <div style={{display:'inline-flex',gap:2,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2}}>
              {[['ambas','Todas'], ...ORG.empresas.map(x=>[x.id,x.label])].map(([e,l]) => (
                <button key={e} onClick={()=>{setEmpresa(e);setFiltroBodega([]);}}
                  style={{height:26,padding:'0 12px',border:'none',borderRadius:R.sm,cursor:'pointer',fontSize:12,fontWeight:600,
                    background:empresa===e?C.bg2:'transparent',color:empresa===e?C.t1:C.t3,boxShadow:empresa===e?SH.sm:'none',whiteSpace:'nowrap'}}>
                  {l}
                </button>
              ))}
            </div>
            <Btn onClick={refrescarDesdeSAP} disabled={refrescando||loading} variant="secondary" style={{display:'inline-flex',alignItems:'center',gap:6}}>
              <CIcon d={CI.refresh}/>{refrescando ? (progreso || 'Consultando SAP…') : 'Refrescar desde SAP'}
            </Btn>
          </div>
        </div>

        {/* Productos vs equipos serializados */}
        <div style={{display:'inline-flex',gap:2,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:3,marginBottom:14}}>
          {[['productos','Productos y repuestos'],['analisis','Análisis'],
            // Los serializados son del negocio de renta: solo si la org tiene Flota.
            ...(orgTiene('flota') ? [['equipos',`Equipos serializados${equipos?` · ${equipos.length}`:''}`]] : [])].map(([v,l])=>(
            <button key={v} onClick={()=>setVista(v)}
              style={{height:30,padding:'0 15px',border:'none',borderRadius:7,cursor:'pointer',fontSize:12.5,fontWeight:700,
                background:vista===v?C.primary:'transparent',color:vista===v?'#fff':C.t3}}>{l}</button>
          ))}
        </div>

        {vista==='equipos' ? (
          (() => {
            const EQ_META = {
              disponible:   ['Disponible',    '#16A34A', C.greenTint,  C.greenText],
              en_renta:     ['En renta',      '#1D4ED8', '#EFF6FF',    '#1D4ED8'],
              mantenimiento:['Mantenimiento', '#D97706', C.orangeTint, C.orangeText],
              reparacion:   ['En reparación', '#DC2626', C.redTint,    C.redText],
              detenido:     ['Detenido',      '#DB2777', '#FDF2F8',    '#DB2777'],
              inactivo:     ['Inactivo',      '#65748B', C.bg3,        C.t3],
              vendido:      ['Vendido',       '#56657F', C.bg3,        C.t4],
            };
            const meta = (e) => EQ_META[e] || ['Sin estado', C.t4, C.bg3, C.t4];
            const baseEq = (equipos||[]).filter(e => empresa==='ambas' || (e.empresa_id||'honduras')===empresa);
            const nPor = (est) => baseEq.filter(e => est==='todos' ? e.estado!=='vendido' : e.estado===est).length;
            let listaEq = baseEq.filter(e => eqEstado==='todos' ? e.estado!=='vendido' : e.estado===eqEstado);
            if (busqEq.length>=2) {
              const b = busqEq.toLowerCase();
              listaEq = listaEq.filter(e => ['codigo','modelo','serie','marca','cliente_actual'].some(k => String(e[k]||'').toLowerCase().includes(b)));
            }
            const EMOJI = { MONTACARGAS:'🏗️', GRUA:'🏗️', PLATAFORMA:'🛗', ELEVADOR:'🛗' };
            return (
              <>
                <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:14}}>
                  <div style={{position:'relative',flex:1,minWidth:200}}>
                    <span style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:C.t4,display:'inline-flex'}}><CIcon d={CI.search} size={14}/></span>
                    <input className="radar-input" value={busqEq} onChange={e=>setBusqEq(e.target.value)}
                      placeholder="Buscar código, modelo, serie, cliente…"
                      style={{width:'100%',height:34,padding:'0 12px 0 32px',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,color:C.t1,fontSize:13,boxShadow:SH.xs}}/>
                  </div>
                  {[['todos','Todos'],['disponible','Disponibles'],['en_renta','En renta'],['mantenimiento','Taller'],['reparacion','Reparación'],['vendido','Vendidos']].map(([v,l])=>(
                    <button key={v} onClick={()=>setEqEstado(v)}
                      style={{height:32,padding:'0 12px',borderRadius:999,fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',
                        background:eqEstado===v?C.primaryTint:C.bg2,border:`1px solid ${eqEstado===v?C.primaryBorder:C.border}`,color:eqEstado===v?C.primary:C.t3}}>
                      {l}<span className="radar-num" style={{marginLeft:4,opacity:.7}}>{nPor(v)}</span>
                    </button>
                  ))}
                </div>
                {equipos===null && <div style={{padding:40,textAlign:'center',color:C.t4,fontSize:13}}>Cargando flota…</div>}
                {equipos!==null && listaEq.length===0 && <div style={{padding:40,textAlign:'center',color:C.t4,fontSize:13}}>Sin equipos con estos filtros</div>}
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:12}}>
                  {listaEq.slice(0,120).map(e => {
                    const [lbl,banda,tint,tx] = meta(e.estado);
                    return (
                      <div key={e.id} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:13,boxShadow:SH.xs,overflow:'hidden',display:'flex',flexDirection:'column'}}>
                        <div style={{height:4,background:banda}}/>
                        <div style={{display:'flex',gap:10,alignItems:'flex-start',padding:'11px 13px 9px'}}>
                          <div style={{width:42,height:42,borderRadius:10,background:C.bg3,display:'flex',alignItems:'center',justifyContent:'center',fontSize:19,flexShrink:0}}>
                            {EMOJI[String(e.tipo_equipo||'').toUpperCase()]||'⚙️'}
                          </div>
                          <div style={{minWidth:0,flex:1}}>
                            <div style={{fontSize:13,fontWeight:800,color:C.t1,lineHeight:1.25}}>{[e.marca,e.modelo].filter(Boolean).join(' ')||e.codigo}</div>
                            <div className="radar-num" style={{fontSize:10,color:C.t4,marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                              {e.serie?`SERIE ${e.serie}`:''}{e.serie&&e.codigo?' · ':''}{e.codigo||''}
                            </div>
                          </div>
                          <span style={{fontSize:10,fontWeight:800,borderRadius:999,padding:'2px 8px',background:tint,color:tx,whiteSpace:'nowrap'}}>{lbl}</span>
                        </div>
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'7px 9px',borderTop:`1px solid ${C.bg3}`,padding:'8px 13px'}}>
                          <div><div style={{fontSize:9,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4}}>Horómetro</div>
                            <div className="radar-num" style={{fontSize:12,fontWeight:700,color:C.t1}}>{e.horometro?`${e.horometro} h`:'—'}</div></div>
                          <div><div style={{fontSize:9,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4}}>Costo</div>
                            <div className="radar-num" style={{fontSize:12,fontWeight:700,color:C.t1}}>{e.costo_adquisicion?`$${Math.round(e.costo_adquisicion).toLocaleString('en-US')}`:'—'}</div></div>
                          <div><div style={{fontSize:9,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4}}>{e.estado==='en_renta'?'Renta/mes':'Precio venta'}</div>
                            <div className="radar-num" style={{fontSize:12,fontWeight:700,color:C.greenText}}>
                              {e.estado==='en_renta'
                                ? (e.precio_renta_mes?`L ${Math.round(e.precio_renta_mes).toLocaleString('en-US')}`:'—')
                                : (e.precio_venta?`$${Math.round(e.precio_venta).toLocaleString('en-US')}`:'—')}</div></div>
                        </div>
                        <div style={{padding:'7px 13px',borderTop:`1px solid ${C.bg3}`,background:C.bg1,fontSize:11,color:C.t3,marginTop:'auto',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {e.estado==='en_renta' && e.cliente_actual ? <>Rentado a <b style={{color:C.t1}}>{e.cliente_actual}</b></>
                            : e.estado==='disponible' ? 'Listo para venta o renta'
                            : (e.ubicacion || e.sucursal || '—')}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {listaEq.length>120 && <div style={{padding:'12px 0',textAlign:'center',fontSize:12,color:C.t4}}>Mostrando 120 de {listaEq.length} — refiná la búsqueda</div>}
              </>
            );
          })()
        ) : vista==='analisis' ? (
          (() => {
            // ── Fase 2: analisis del inventario a partir del movimiento cacheado ──
            const hayMov = allItems.some(i => i.mov_synced_at);
            const movFrescura = hayMov ? allItems.map(i=>i.mov_synced_at).filter(Boolean).sort().slice(-1)[0] : null;
            if (!hayMov) return (
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,boxShadow:SH.xs,padding:'48px 24px',textAlign:'center'}}>
                <div style={{fontSize:34,marginBottom:10}}>📈</div>
                <div style={{fontSize:15,fontWeight:800,color:C.t1,marginBottom:6}}>Falta leer el historial de SAP</div>
                <div style={{fontSize:12.5,color:C.t3,maxWidth:460,margin:'0 auto 16px',lineHeight:1.5}}>
                  El análisis necesita las ventas de los últimos 6 meses y las órdenes de compra abiertas.
                  Se leen una vez y quedan guardadas; después un proceso automático las refresca cada día.
                </div>
                <Btn onClick={calcularMovimiento} disabled={movBusy}>
                  {movBusy ? (movProg || 'Leyendo historial…') : 'Leer historial de SAP'}
                </Btn>
              </div>
            );

            const hoyMs = Date.now();
            const en = allItems.map(i => {
              const vm = i.ventas_meses || {};
              const tot6 = Object.values(vm).reduce((s,v)=>s+Math.max(0,Number(v)||0),0);
              const ventaMes = tot6/6;
              const cob = (ventaMes>0 && i.disponible!=null) ? Math.round(i.disponible/(ventaMes/30)) : null;
              const diasUlt = i.ultima_venta ? Math.floor((hoyMs - new Date(i.ultima_venta).getTime())/86400000) : null;
              const llegada = (i.oc_pendiente||[]).filter(o=>o.fecha).sort((a,b)=>a.fecha<b.fecha?-1:1)[0] || null;
              return { ...i, tot6, ventaMes, cob, diasUlt, llegada };
            });
            const conStockE = en.filter(i => i.stock > 0);

            const sinMov   = conStockE.filter(i => i.tot6 <= 0);
            const capSinMov = sinMov.reduce((s,i)=>s+(i.valor_total_usd||0),0);
            // Riesgo: se vende y lo disponible alcanza para 15 dias o menos.
            const riesgo   = en.filter(i => i.ventaMes>0 && i.cob!=null && i.cob<=15)
              .sort((a,b)=>b.ventaMes-a.ventaMes);
            const sobre    = conStockE.filter(i => i.cob!=null && i.cob>180);
            const capSobre = sobre.reduce((s,i)=>s+(i.valor_total_usd||0),0);
            const sanos    = conStockE.filter(i => i.tot6>0 && i.cob!=null && i.cob<=180).length;
            const dolar    = (v) => `$${Math.round(v).toLocaleString('en-US')}`;

            // Antiguedad POR ULTIMA VENTA (no fecha de compra: SAP no la expone aca).
            // Ponderada por valor: lo que importa es cuanta plata esta vieja.
            const RANGOS = [
              ['Vendido hace 0–30 días','#16A34A',0,30],
              ['31–90 días','#84CC16',31,90],
              ['91–180 días','#D97706',91,180],
              ['+180 días o nunca','#DC2626',181,Infinity],
            ];
            const antig = RANGOS.map(([lbl,col,d1,d2]) => {
              const grupo = conStockE.filter(i => {
                const d = i.diasUlt == null ? Infinity : i.diasUlt;
                return d >= d1 && d <= d2;
              });
              return { lbl, col, items:grupo.length, valor:grupo.reduce((s,i)=>s+(i.valor_total_usd||0),0) };
            });
            const antigTotal = Math.max(1, antig.reduce((s,r)=>s+r.valor,0));

            // Rotacion por grupo: unidades vendidas en 6m contra stock actual.
            const rotG = {};
            en.forEach(i => {
              const g = i.grupo || 'Sin grupo';
              if (!rotG[g]) rotG[g] = { vendido:0, stock:0, valor:0 };
              rotG[g].vendido += i.tot6; rotG[g].stock += i.stock; rotG[g].valor += (i.valor_total_usd||0);
            });
            const rotRows = Object.entries(rotG).filter(([,v])=>v.vendido>0||v.stock>0)
              .sort((a,b)=>b[1].vendido-a[1].vendido).slice(0,8);
            const maxVend = Math.max(1, ...rotRows.map(([,v])=>v.vendido));

            // ABC por valor: A = el 80% del capital, B el siguiente 15%, C el resto.
            const conValor = conStockE.filter(i=>(i.valor_total_usd||0)>0)
              .sort((a,b)=>(b.valor_total_usd||0)-(a.valor_total_usd||0));
            const valTotal = Math.max(1, conValor.reduce((s,i)=>s+i.valor_total_usd,0));
            let acum = 0;
            const abc = { A:{n:0,v:0}, B:{n:0,v:0}, C:{n:0,v:0} };
            conValor.forEach(i => {
              acum += i.valor_total_usd;
              const clase = acum <= valTotal*0.8 ? 'A' : acum <= valTotal*0.95 ? 'B' : 'C';
              abc[clase].n++; abc[clase].v += i.valor_total_usd;
            });

            // Proximas llegadas: todas las lineas de OC abiertas, ordenadas por fecha.
            const hoyISO = new Date(hoyMs).toISOString().slice(0,10);
            const llegadas = en.flatMap(i => (i.oc_pendiente||[]).map(o=>({...o, codigo:i.codigo, nombre:i.nombre, empresa_id:i.empresa_id})))
              .sort((a,b)=> String(a.fecha||'9999') < String(b.fecha||'9999') ? -1 : 1);
            const atrasadas = llegadas.filter(l => l.fecha && l.fecha < hoyISO);

            const Cabeza = ({t, extra}) => (
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:10,gap:8}}>
                <div style={{fontSize:12,fontWeight:800,letterSpacing:'.04em',textTransform:'uppercase',color:C.t3}}>{t}</div>
                {extra}
              </div>
            );
            const Tarjeta = ({children, style}) => (
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,boxShadow:SH.xs,padding:'16px 18px',...style}}>{children}</div>
            );

            return (
              <>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:'wrap',gap:8}}>
                  <div style={{fontSize:11.5,color:C.t4}}>
                    Ventas de los últimos 6 meses (netas de devoluciones) y OC abiertas · movimiento actualizado {haceCuanto(movFrescura)}
                  </div>
                  <Btn onClick={calcularMovimiento} disabled={movBusy} variant="secondary" size="sm">
                    {movBusy ? (movProg || 'Actualizando…') : 'Actualizar movimiento'}
                  </Btn>
                </div>

                {/* KPIs */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))',gap:12,marginBottom:16}}>
                  <KpiCard label="Capital sin movimiento" value={dolar(capSinMov)} tone="red"
                    sub={`${sinMov.length.toLocaleString('en-US')} artículos con stock y 0 ventas en 6 meses`}/>
                  <KpiCard label="Riesgo de quiebre" value={riesgo.length.toLocaleString('en-US')} tone="orange"
                    sub="se venden y quedan ≤ 15 días de cobertura"/>
                  <KpiCard label="Sobrestock" value={dolar(capSobre)} tone="orange"
                    sub={`${sobre.length.toLocaleString('en-US')} artículos con +180 días de cobertura`}/>
                  <KpiCard label="Rotación sana" value={sanos.toLocaleString('en-US')} tone="green"
                    sub="con venta activa y cobertura ≤ 180 días"/>
                </div>

                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(340px,1fr))',gap:14,marginBottom:16}}>
                  {/* Antiguedad del capital */}
                  <Tarjeta>
                    <Cabeza t="Antigüedad del capital" extra={<span style={{fontSize:10.5,color:C.t4}}>por última venta, ponderado por valor</span>}/>
                    <div style={{display:'flex',height:14,borderRadius:7,overflow:'hidden',marginBottom:12}}>
                      {antig.map(r => r.valor>0 && (
                        <div key={r.lbl} style={{width:`${(r.valor/antigTotal)*100}%`,background:r.col,minWidth:r.valor>0?3:0}}/>
                      ))}
                    </div>
                    {antig.map(r => (
                      <div key={r.lbl} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:`1px solid ${C.bg3}`}}>
                        <span style={{width:9,height:9,borderRadius:3,background:r.col,flexShrink:0}}/>
                        <span style={{fontSize:12,color:C.t2,flex:1}}>{r.lbl}</span>
                        <span className="radar-num" style={{fontSize:11,color:C.t4}}>{r.items.toLocaleString('en-US')} ítems</span>
                        <span className="radar-num" style={{fontSize:12.5,fontWeight:800,color:C.t1,minWidth:76,textAlign:'right'}}>{dolar(r.valor)}</span>
                        <span className="radar-num" style={{fontSize:10.5,color:C.t4,minWidth:34,textAlign:'right'}}>{Math.round((r.valor/antigTotal)*100)}%</span>
                      </div>
                    ))}
                    <div style={{fontSize:10,color:C.t4,marginTop:8}}>Solo artículos con stock. "Nunca" = sin venta registrada en la ventana de 6 meses.</div>
                  </Tarjeta>

                  {/* Riesgo de quiebre */}
                  <Tarjeta>
                    <Cabeza t="Riesgo de quiebre" extra={<span style={{fontSize:10.5,color:C.t4}}>{riesgo.length} artículos</span>}/>
                    {riesgo.length===0 && <div style={{fontSize:12.5,color:C.t4,padding:'14px 0'}}>Nada en riesgo: todo lo que se vende tiene más de 15 días de cobertura.</div>}
                    {riesgo.slice(0,8).map(i => (
                      <div key={`${i.empresa_id}_${i.codigo}`} style={{padding:'8px 0',borderBottom:`1px solid ${C.bg3}`}}>
                        <div style={{display:'flex',justifyContent:'space-between',gap:8,alignItems:'baseline'}}>
                          <span style={{fontSize:12.5,fontWeight:700,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}} title={i.nombre}>{i.nombre}</span>
                          <span className="radar-num" style={{fontSize:11.5,fontWeight:800,color:i.cob<=5?C.redText:C.orangeText,whiteSpace:'nowrap'}}>
                            {i.cob<=0 ? 'sin disponible' : `${i.cob} días`}
                          </span>
                        </div>
                        <div style={{display:'flex',gap:12,fontSize:10.5,color:C.t4,marginTop:2,flexWrap:'wrap'}}>
                          <span className="radar-num">{i.codigo}</span>
                          <span>Disp. <b className="radar-num" style={{color:C.t2}}>{num(i.disponible)}</b></span>
                          <span>Vende <b className="radar-num" style={{color:C.t2}}>{Math.round(i.ventaMes*10)/10}</b>/mes</span>
                          {i.llegada
                            ? <span style={{color:C.greenText,fontWeight:700}}>Llegan {num(i.llegada.cant)} el {i.llegada.fecha}</span>
                            : <span style={{color:C.redText,fontWeight:700}}>Sin OC en camino</span>}
                        </div>
                      </div>
                    ))}
                  </Tarjeta>
                </div>

                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(340px,1fr))',gap:14,marginBottom:16}}>
                  {/* Capital sin movimiento */}
                  <Tarjeta>
                    <Cabeza t="Capital sin movimiento" extra={<span className="radar-num" style={{fontSize:11,fontWeight:800,color:C.redText}}>{dolar(capSinMov)}</span>}/>
                    {sinMov.length===0 && <div style={{fontSize:12.5,color:C.t4,padding:'14px 0'}}>Todo el stock tuvo al menos una venta en 6 meses.</div>}
                    {[...sinMov].sort((a,b)=>(b.valor_total_usd||0)-(a.valor_total_usd||0)).slice(0,8).map(i => (
                      <div key={`${i.empresa_id}_${i.codigo}`} style={{display:'flex',justifyContent:'space-between',gap:8,padding:'7px 0',borderBottom:`1px solid ${C.bg3}`,alignItems:'baseline'}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12.5,fontWeight:700,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={i.nombre}>{i.nombre}</div>
                          <div className="radar-num" style={{fontSize:10.5,color:C.t4}}>{i.codigo} · stock {num(i.stock)}{i.diasUlt!=null?` · última venta hace ${i.diasUlt} d`:' · sin venta en 6+ meses'}</div>
                        </div>
                        <span className="radar-num" style={{fontSize:12.5,fontWeight:800,color:C.t1,whiteSpace:'nowrap'}}>{i.valor_total_usd==null?'sin costo':dolar(i.valor_total_usd)}</span>
                      </div>
                    ))}
                    {sinMov.length>0 && <div style={{fontSize:10,color:C.t4,marginTop:8}}>Candidatos a promoción o traslado (Fase 3).</div>}
                  </Tarjeta>

                  {/* Rotacion por grupo */}
                  <Tarjeta>
                    <Cabeza t="Rotación por grupo" extra={<span style={{fontSize:10.5,color:C.t4}}>unidades vendidas en 6 meses</span>}/>
                    {rotRows.map(([g,v]) => (
                      <div key={g} style={{padding:'6px 0'}}>
                        <div style={{display:'flex',justifyContent:'space-between',gap:8,marginBottom:3}}>
                          <span style={{fontSize:11.5,fontWeight:700,color:C.t2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{g}</span>
                          <span className="radar-num" style={{fontSize:10.5,color:C.t4,whiteSpace:'nowrap'}}>
                            {Math.round(v.vendido).toLocaleString('en-US')} vend · stock {Math.round(v.stock).toLocaleString('en-US')}
                            {v.stock>0 && v.vendido>0 ? ` · ${(Math.round((v.vendido*2/v.stock)*10)/10)}x/año` : ''}
                          </span>
                        </div>
                        <div style={{height:7,background:C.bg3,borderRadius:4,overflow:'hidden'}}>
                          <div style={{width:`${(v.vendido/maxVend)*100}%`,height:'100%',background:C.primary,borderRadius:4}}/>
                        </div>
                      </div>
                    ))}
                  </Tarjeta>
                </div>

                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(340px,1fr))',gap:14}}>
                  {/* ABC */}
                  <Tarjeta>
                    <Cabeza t="Clasificación ABC del capital"/>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                      {[['A','80% del valor',C.greenText,C.greenTint,C.greenBorder],
                        ['B','siguiente 15%',C.orangeText,C.orangeTint,C.orangeBorder],
                        ['C','el 5% restante',C.t3,C.bg3,C.border]].map(([k,d,tx,bg,bd]) => (
                        <div key={k} style={{background:bg,border:`1px solid ${bd}`,borderRadius:10,padding:'12px 10px',textAlign:'center'}}>
                          <div style={{fontSize:19,fontWeight:900,color:tx}}>{k}</div>
                          <div className="radar-num" style={{fontSize:14,fontWeight:800,color:C.t1,marginTop:4}}>{abc[k].n.toLocaleString('en-US')}</div>
                          <div style={{fontSize:9.5,color:C.t4}}>ítems</div>
                          <div className="radar-num" style={{fontSize:11.5,fontWeight:700,color:tx,marginTop:4}}>{dolar(abc[k].v)}</div>
                          <div style={{fontSize:9,color:C.t4,marginTop:2}}>{d}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{fontSize:10,color:C.t4,marginTop:10}}>Los A son pocos ítems con casi todo el capital: ahí va el control fino (conteos, mínimos, negociación con proveedor).</div>
                  </Tarjeta>

                  {/* Proximas llegadas */}
                  <Tarjeta>
                    <Cabeza t="Próximas llegadas" extra={<span style={{fontSize:10.5,color:C.t4}}>{llegadas.length} líneas de OC abiertas</span>}/>
                    {llegadas.length===0 && <div style={{fontSize:12.5,color:C.t4,padding:'14px 0'}}>No hay órdenes de compra abiertas.</div>}
                    {atrasadas.length>0 && (
                      <div style={{background:C.redTint,border:`1px solid ${C.redBorder}`,borderRadius:8,padding:'7px 11px',fontSize:11.5,color:C.redText,fontWeight:700,marginBottom:8}}>
                        {atrasadas.length} línea{atrasadas.length>1?'s':''} con fecha ya vencida — reclamale al proveedor
                      </div>
                    )}
                    {llegadas.slice(0,8).map((l,ix) => (
                      <div key={ix} style={{display:'flex',justifyContent:'space-between',gap:8,padding:'7px 0',borderBottom:`1px solid ${C.bg3}`,alignItems:'baseline'}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:700,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={l.nombre}>{l.nombre}</div>
                          <div className="radar-num" style={{fontSize:10.5,color:C.t4}}>{l.codigo}{l.doc?` · OC ${l.doc}`:''}</div>
                        </div>
                        <div style={{textAlign:'right',whiteSpace:'nowrap'}}>
                          <div className="radar-num" style={{fontSize:12,fontWeight:800,color:l.fecha && l.fecha<hoyISO?C.redText:C.t1}}>{l.fecha||'sin fecha'}</div>
                          <div className="radar-num" style={{fontSize:10.5,color:C.t4}}>{num(l.cant)} und</div>
                        </div>
                      </div>
                    ))}
                  </Tarjeta>
                </div>
              </>
            );
          })()
        ) : (<>

        {/* Cache vacio: la primera vez hay que llenarlo */}
        {(!data || Object.keys(data).length===0) && !loading && !refrescando && !error && (
          <div style={{textAlign:'center',padding:'72px 20px',color:C.t4,display:'flex',flexDirection:'column',alignItems:'center',gap:12}}>
            <span style={{width:64,height:64,borderRadius:R.lg,background:C.bg3,border:`1px solid ${C.border}`,color:C.t4,display:'inline-flex',alignItems:'center',justifyContent:'center'}}><Icon id="inventario" size={30}/></span>
            <div style={{fontSize:16,fontWeight:700,color:C.t2}}>Todavía no hay inventario guardado</div>
            <div style={{fontSize:13,maxWidth:420,lineHeight:1.6}}>
              Pulsá "Refrescar desde SAP" una vez. Tarda 20–40 segundos, y después esta pantalla
              abre al instante — el inventario se refresca solo cada 3 horas.
            </div>
          </div>
        )}

        {error && (
          <div style={{background:C.redTint,border:`1px solid ${C.redBorder}`,borderRadius:R.md,padding:'14px 18px',color:C.redText,fontSize:13,marginBottom:20,display:'flex',alignItems:'center',gap:8}}>
            <CIcon d={<><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>} size={15}/>{error}
          </div>
        )}

        {/* refrescando sin datos también muestra esto: si no, el cuerpo quedaba en
            blanco durante el primer refresh y parecía muerto */}
        {(loading || (refrescando && (!data || Object.keys(data).length===0))) && (
          <div style={{textAlign:'center',padding:'72px 20px',color:C.t4,display:'flex',flexDirection:'column',alignItems:'center',gap:10}}>
            <span style={{width:52,height:52,borderRadius:R.lg,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,color:C.blue,display:'inline-flex',alignItems:'center',justifyContent:'center'}}><CIcon d={CI.refresh} size={24}/></span>
            <div style={{fontSize:15,fontWeight:600,color:C.t2}}>{refrescando ? (progreso ? `Consultando SAP — ${progreso}` : 'Consultando SAP B1…') : 'Cargando…'}</div>
            <div style={{fontSize:12}}>{refrescando ? 'Alrededor de 1 minuto por empresa — recorre el catálogo completo' : ''}</div>
          </div>
        )}

        {/* OJO: hay que contar las llaves. data arranca como {} y un objeto vacio es
            truthy, asi que con solo "data &&" se dibujaban los KPIs en cero y la
            advertencia de bodegas ENCIMA del estado vacio, diciendo que SAP no
            expuso las bodegas cuando todavia no se le habia preguntado nada. */}
        {data && Object.keys(data).length > 0 && !loading && (<>
          {/* KPIs */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(178px,1fr))',gap:12,marginBottom:14}}>
            <KpiCard label="Capital inmovilizado" value={`$${Math.round(valorTotal).toLocaleString('en-US')}`}
              sub={sinCostoConStock>0?`sin contar ${sinCostoConStock} ítems sin costo`:'stock x costo, en USD'}
              icon={KI.dollar} tone="primary"/>
            <KpiCard label="Ítems con stock"      value={conStock.toLocaleString('en-US')} sub={`de ${filtered.length.toLocaleString('en-US')} filtrados`} icon={KI.check} tone="green"/>
            <KpiCard label="Unidades"             value={Math.round(totalStock).toLocaleString('en-US')} sub="suma de existencias" icon={KI.box} tone="info"/>
            {hayBodegas && <KpiCard label="Comprometido" value={Math.round(totalComprom).toLocaleString('en-US')}
              sub="ya apartado en documentos" icon={KI.funnel} tone={totalComprom>0?'orange':'neutral'}/>}
            {hayBodegas && <KpiCard label="Sin disponible" value={enQuiebre.toLocaleString('en-US')}
              sub={enQuiebre>0?'hay stock pero todo comprometido':'nada bloqueado'} icon={KI.tool} tone={enQuiebre>0?'red':'green'}/>}
          </div>

          {/* Aviso si esta instancia no expone el desglose */}
          {!hayBodegas && (
            <div style={{background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.md,
              padding:'10px 14px',fontSize:12,color:C.orangeText,marginBottom:14,lineHeight:1.55}}>
              Esta instancia de SAP no expuso el stock por bodega, así que el stock es el total de la empresa
              y no se puede saber cuánto está comprometido. Ojo al prometer existencias.
            </div>
          )}

          {/* Dashboard: donde esta el capital + que vale mas */}
          {hayBodegas && porBodega.length > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'1.4fr 1fr',gap:12,marginBottom:14}}>
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'16px 18px',boxShadow:SH.xs}}>
                <div style={{fontSize:13,fontWeight:700,color:C.t1,marginBottom:2}}>Dónde está el inventario</div>
                <div style={{fontSize:11.5,color:C.t4,marginBottom:12}}>Valor por bodega — tocá una para filtrar</div>
                <div style={{display:'flex',flexDirection:'column',gap:7,maxHeight:230,overflowY:'auto'}}>
                  {porBodega.map(b => {
                    const sel = filtroBodega.includes(String(b.codigo));
                    return (
                      <button key={b.codigo} onClick={()=>setFiltroBodega(sel?'':String(b.codigo))}
                        style={{display:'grid',gridTemplateColumns:'150px 1fr 96px',gap:10,alignItems:'center',
                          background:sel?C.primaryTint:'transparent',border:`1px solid ${sel?C.primaryBorder:'transparent'}`,
                          borderRadius:R.sm,padding:'5px 7px',cursor:'pointer',textAlign:'left'}}>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:sel?C.primary:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={b.nombre}>{b.nombre}</div>
                          <div className="radar-num" style={{fontSize:10,color:C.t4}}>{b.codigo} · {b.items} ítems</div>
                        </div>
                        <div style={{height:18,background:C.bg3,borderRadius:4,overflow:'hidden'}}>
                          <div style={{width:`${Math.max(2,(b.valor/maxValBodega)*100)}%`,height:'100%',
                            background:sel?C.primary:`color-mix(in srgb, ${C.blue} 62%, #FFF)`}}/>
                        </div>
                        <div className="radar-num" style={{fontSize:12,fontWeight:700,color:C.t1,textAlign:'right'}}>
                          ${Math.round(b.valor).toLocaleString('en-US')}
                          <div style={{fontSize:10,fontWeight:400,color:C.t4}}>{Math.round(b.unidades).toLocaleString('en-US')} u</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'16px 18px',boxShadow:SH.xs}}>
                <div style={{fontSize:13,fontWeight:700,color:C.t1,marginBottom:2}}>Lo que más vale</div>
                <div style={{fontSize:11.5,color:C.t4,marginBottom:12}}>Top ítems por capital retenido</div>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {topValor.map((i,n) => (
                    <div key={`${i.empresa_id}_${i.codigo}`} style={{display:'flex',alignItems:'center',gap:9}}>
                      <span className="radar-num" style={{width:16,fontSize:11,color:C.t4,fontWeight:700}}>{n+1}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={i.nombre}>{i.nombre}</div>
                        <div className="radar-num" style={{fontSize:10,color:C.t4}}>{i.codigo} · {num(i.stock)} u</div>
                      </div>
                      <div className="radar-num" style={{fontSize:12,fontWeight:700,color:C.t1}}>${Math.round(i.valor_total_usd).toLocaleString('en-US')}</div>
                    </div>
                  ))}
                  {topValor.length===0 && <div style={{fontSize:12,color:C.t4}}>Sin ítems con valor</div>}
                </div>
              </div>
            </div>
          )}

          {/* Vistas guardadas: un clic restaura el set completo de filtros */}
          {(vistas.length > 0 || hayFiltros) && (
            <div style={{display:'flex',gap:8,marginBottom:10,alignItems:'center',flexWrap:'wrap'}}>
              <span style={{fontSize:10.5,fontWeight:800,letterSpacing:'.06em',textTransform:'uppercase',color:C.t4}}>Vistas</span>
              {vistas.map(v => (
                <span key={v.nombre} style={{display:'inline-flex',alignItems:'center',gap:6,height:28,padding:'0 4px 0 12px',borderRadius:999,
                  fontSize:12,fontWeight:700,background:vistaActiva===v.nombre?C.primaryTint:C.bg2,
                  border:`1px solid ${vistaActiva===v.nombre?C.primaryBorder:C.border}`,color:vistaActiva===v.nombre?C.primary:C.t3}}>
                  <button onClick={()=>aplicarVista(v)} style={{background:'none',border:'none',cursor:'pointer',padding:0,font:'inherit',color:'inherit'}}>{v.nombre}</button>
                  <button onClick={()=>{persistirVistas(vistas.filter(x=>x.nombre!==v.nombre)); if(vistaActiva===v.nombre) setVistaActiva('');}}
                    title="Borrar vista"
                    style={{background:'none',border:'none',cursor:'pointer',color:C.t4,fontSize:13,lineHeight:1,padding:'0 6px'}}>×</button>
                </span>
              ))}
              {hayFiltros && (
                <button onClick={guardarVista}
                  style={{height:28,padding:'0 12px',borderRadius:999,fontSize:12,fontWeight:700,cursor:'pointer',
                    background:'transparent',border:`1px dashed ${C.border}`,color:C.t3}}>+ Guardar vista actual</button>
              )}
              {hayFiltros && (
                <button onClick={limpiarFiltros}
                  style={{height:28,padding:'0 10px',borderRadius:999,fontSize:12,fontWeight:700,cursor:'pointer',
                    background:'none',border:'none',color:C.t4,textDecoration:'underline'}}>Limpiar todo</button>
              )}
            </div>
          )}

          {/* Filtros — flotan sobre gris */}
          <div style={{display:'flex',gap:10,marginBottom:14,alignItems:'center',flexWrap:'wrap'}}>
            <div style={{position:'relative',flex:1,minWidth:200}}>
              <span style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:C.t4,pointerEvents:'none',display:'inline-flex'}}><CIcon d={CI.search} size={14}/></span>
              <input className="radar-input" value={busq} onChange={e=>setBusq(e.target.value)}
                placeholder="Buscar código, nombre o n° de parte…"
                style={{width:'100%',height:34,padding:'0 12px 0 32px',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,color:C.t1,fontSize:13,boxShadow:SH.xs}} />
            </div>
            {hayBodegas && bodegas.length > 0 && (
              <MultiSelect label="Bodegas" value={filtroBodega} onChange={setFiltroBodega} width={220}
                options={bodegas.map(([code,name]) => ({ value:String(code), label:`${code} — ${name}` }))}/>
            )}
            {lineas.length > 1 && (
              <MultiSelect label="Líneas" value={filtroLinea} onChange={setFiltroLinea} width={200}
                options={lineas.map(([code,n]) => ({ value:code, label:`${code} · ${n.toLocaleString()} ítems` }))}/>
            )}
            <MultiSelect label="Grupos" value={filtroGrupo} onChange={setFiltroGrupo} width={220}
              options={grupos.map(([code,name]) => ({ value:code, label:name }))}/>
            {/* Chips de filtro con contador (capa 1 del mockup): un clic, combinables.
                El contador se calcula antes de aplicar los chips. */}
            {[
              ['Con stock', nConStock, soloConStock, setSoloConStock, 'teal', true],
              ['Sin disponible', nQuiebre, soloQuiebre, setSoloQuiebre, 'red', hayBodegas],
              ['Sin costo', nSinCosto, soloSinCosto, setSoloSinCosto, 'orange', true],
              ['Margen negativo', nMargenNeg, soloMargenNeg, setSoloMargenNeg, 'red', true],
            ].filter(([,,,,,ver])=>ver).map(([lbl,n,on,set,tono])=>{
              const c = tono==='red' ? {bg:C.redTint,bd:C.redBorder,tx:C.redText}
                : tono==='orange' ? {bg:C.orangeTint,bd:C.orangeBorder,tx:C.orangeText}
                : {bg:C.primaryTint,bd:C.primaryBorder,tx:C.primary};
              return (
                <button key={lbl} onClick={()=>set(v=>!v)}
                  style={{display:'inline-flex',alignItems:'center',gap:5,height:32,padding:'0 12px',borderRadius:999,
                    fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',
                    background:on?c.bg:C.bg2,border:`1px solid ${on?c.bd:C.border}`,color:on?c.tx:C.t3}}>
                  {lbl}<span className="radar-num" style={{fontWeight:800,opacity:.75}}>{n.toLocaleString('en-US')}</span>
                </button>
              );
            })}
            <button className="radar-pill" onClick={exportCSV}
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'0 14px',height:34,background:C.greenTint,color:C.greenText,border:`1px solid ${C.greenBorder}`,borderRadius:R.md,fontSize:12.5,fontWeight:600,cursor:'pointer'}}>
              <CIcon d={CI.download}/>Exportar CSV
            </button>
          </div>

          {/* Errores por empresa */}
          {Object.entries(data).filter(([,v])=>v.error).map(([ei,v]) => (
            <div key={ei} style={{background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.md,
              padding:'10px 14px',fontSize:12,color:C.orangeText,marginBottom:10,display:'flex',alignItems:'center',gap:8}}>
              <CIcon d={KI.tool} size={14}/>{EMP_LABEL[ei]||ei}: {v.error}
            </div>
          ))}

          {/* Tabla hairline (receta 7.7) */}
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,overflowX:'auto',boxShadow:SH.xs}}>
            <div style={{display:'grid',gridTemplateColumns:COLS,padding:'9px 16px',minWidth:1080,gap:6,
              background:C.bg2,borderBottom:`1px solid ${C.border}`,position:'sticky',top:0,zIndex:1}}>
              {HEAD.map(([h,al,campo],n) => {
                const activo = campo && colFiltros[campo] && (colFiltros[campo].min !== '' || colFiltros[campo].max !== '');
                if (!campo) return <div key={h+n} style={{fontSize:11,fontWeight:700,color:C.t3,textTransform:'uppercase',letterSpacing:'.06em',textAlign:al}}>{h}</div>;
                return (
                  <button key={h+n}
                    onClick={(e)=>{
                      const r = e.currentTarget.getBoundingClientRect();
                      // Fijo en pantalla: dentro de la tabla el overflow lo recortaria.
                      setPopCol(popCol?.campo===campo ? null : {campo, label:h, x:Math.min(r.left, window.innerWidth-230), y:r.bottom+4});
                    }}
                    style={{fontSize:11,fontWeight:700,color:activo?C.primary:C.t3,textTransform:'uppercase',letterSpacing:'.06em',
                      textAlign:al,background:'none',border:'none',cursor:'pointer',padding:0,display:'inline-flex',
                      alignItems:'center',gap:3,justifyContent:al==='right'?'flex-end':'flex-start'}}>
                    {h}<span style={{fontSize:8,opacity:activo?1:.55}}>{activo?'●':'▾'}</span>
                  </button>
                );
              })}
            </div>

            <div style={{maxHeight:560,overflowY:'auto'}}>
              {filtered.length === 0 && (
                <div style={{padding:40,textAlign:'center',color:C.t4,fontSize:13}}>Sin resultados con los filtros actuales</div>
              )}
              {filtered.slice(0,tope).map((item) => {
                const k = `${item.empresa_id}_${item.codigo}`;
                const abierto = !!expandido[k];
                const nBod = (item.bodegas||[]).length;
                // Rojo cuando hay existencia pero nada vendible.
                const bloqueado = item.disponible != null && item.disponible <= 0 && item.stock > 0;
                return (
                  <div key={k}>
                    <div className="radar-hrow"
                      onClick={()=>abrirFicha(item)}
                      style={{display:'grid',gridTemplateColumns:COLS,padding:'9px 16px',minWidth:1080,gap:6,
                        borderBottom:`1px solid ${C.bg3}`,alignItems:'center',cursor:'pointer',
                        background:abierto?C.bg1:'transparent'}}>
                      {/* La flecha expande bodegas inline; el resto de la fila abre la ficha */}
                      <div onClick={e=>{e.stopPropagation(); nBod>0 && setExpandido(x=>({...x,[k]:!x[k]}));}}
                        style={{color:C.t4,fontSize:10,transform:abierto?'rotate(90deg)':'none',transition:'transform .15s',
                          cursor:nBod>0?'pointer':'default',padding:'4px 2px',margin:'-4px -2px'}}>
                        {nBod>0 ? '▶' : ''}
                      </div>
                      <div className="radar-num" style={{fontSize:12,fontWeight:600,color:C.blue,fontFamily:'monospace',wordBreak:'break-all'}}>{item.codigo}</div>
                      <div style={{fontSize:12,color:C.t1,paddingRight:8,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={item.nombre}>
                        {item.nombre}
                        {nBod>1 && <span style={{marginLeft:6,fontSize:10,color:C.t4}}>({nBod} bodegas)</span>}
                      </div>
                      <div className="radar-num" style={{fontSize:10.5,color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={item.num_parte||''}>{item.num_parte || '—'}</div>
                      <div style={{fontSize:10.5,color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={item.grupo}>{item.grupo}</div>
                      <div className="radar-num" style={{fontSize:13,fontWeight:600,color:item.stock>0?C.t1:C.t4,textAlign:'right'}}>{num(item.stock)}</div>
                      <div className="radar-num" style={{fontSize:12,color:item.comprometido>0?C.orangeText:C.t4,textAlign:'right'}}>{item.comprometido ? num(item.comprometido) : '—'}</div>
                      <div className="radar-num" style={{fontSize:13,fontWeight:700,textAlign:'right',
                        color:item.disponible==null?C.t4:(bloqueado?C.redText:(item.disponible>0?C.greenText:C.t4))}}>
                        {num(item.disponible)}
                      </div>
                      {/* "sin costo" explícito, nunca $0: cero real y dato faltante
                          son cosas distintas (principio del prompt de inventario) */}
                      <div className="radar-num" style={{fontSize:11.5,textAlign:'right',
                        color:item.costo_usd==null?C.t4:C.t3,fontStyle:item.costo_usd==null?'italic':'normal'}}>
                        {item.costo_usd==null?'sin costo':money(item.costo_usd)}
                      </div>
                      <div className="radar-num" style={{fontSize:11.5,color:item.precio_usd?C.t2:C.t4,textAlign:'right'}}>{money(item.precio_usd)}</div>
                      <div className="radar-num" style={{fontSize:11.5,textAlign:'right',fontWeight:600,
                        color:item.margen_pct==null?C.t4:(item.margen_pct<0?C.redText:(item.margen_pct<15?C.orangeText:C.greenText))}}>
                        {item.margen_pct==null?'—':`${item.margen_pct}%`}
                      </div>
                      <div className="radar-num" style={{fontSize:12,fontWeight:item.valor_total_usd>0?700:400,color:item.valor_total_usd>0?C.t1:C.t4,textAlign:'right'}}>
                        {item.valor_total_usd > 0 ? `$${Math.round(item.valor_total_usd).toLocaleString('en-US')}` : '—'}
                      </div>
                    </div>

                    {/* Desglose: en QUE bodega esta y cuanto se puede vender de cada una */}
                    {abierto && nBod>0 && (
                      <div style={{background:C.bg1,borderBottom:`1px solid ${C.bg3}`,padding:'8px 16px 10px 156px',minWidth:1080}}>
                        <div style={{display:'grid',gridTemplateColumns:'80px 1fr 78px 78px 78px 78px',gap:6,padding:'0 0 4px'}}>
                          {['Bodega','Nombre','Stock','Comprom.','Disp.','Pedido'].map((h,n)=>(
                            <div key={h} style={{fontSize:9.5,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.06em',textAlign:n>=2?'right':'left'}}>{h}</div>
                          ))}
                        </div>
                        {item.bodegas.map(b => (
                          <div key={b.codigo} style={{display:'grid',gridTemplateColumns:'80px 1fr 78px 78px 78px 78px',gap:6,padding:'3px 0'}}>
                            <div className="radar-num" style={{fontSize:11,fontWeight:600,color:C.blue}}>{b.codigo}</div>
                            <div style={{fontSize:11,color:C.t2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{b.nombre}</div>
                            <div className="radar-num" style={{fontSize:11,color:C.t1,textAlign:'right'}}>{num(b.stock)}</div>
                            <div className="radar-num" style={{fontSize:11,color:b.comprometido>0?C.orangeText:C.t4,textAlign:'right'}}>{b.comprometido?num(b.comprometido):'—'}</div>
                            <div className="radar-num" style={{fontSize:11,fontWeight:700,color:b.disponible>0?C.greenText:C.redText,textAlign:'right'}}>{num(b.disponible)}</div>
                            <div className="radar-num" style={{fontSize:11,color:b.pedido>0?C.t2:C.t4,textAlign:'right'}}>{b.pedido?num(b.pedido):'—'}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {filtered.length > tope && (
              <div style={{padding:'14px 16px',borderTop:`1px solid ${C.bg3}`,textAlign:'center'}}>
                <div style={{fontSize:12,color:C.t3,marginBottom:9}}>
                  Mostrando <b style={{color:C.t1}}>{tope.toLocaleString()}</b> de {filtered.length.toLocaleString()} ítems
                </div>
                <div style={{display:'flex',gap:8,justifyContent:'center',flexWrap:'wrap'}}>
                  <button onClick={()=>setTope(t=>t+2000)}
                    style={{padding:'8px 16px',borderRadius:8,border:`1px solid ${C.primary}`,background:C.primary,
                            color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                    Mostrar 2,000 más
                  </button>
                  <button onClick={()=>setTope(filtered.length)}
                    style={{padding:'8px 16px',borderRadius:8,border:`1px solid ${C.border}`,background:C.bg2,
                            color:C.t2,fontSize:12,fontWeight:600,cursor:'pointer'}}>
                    Mostrar todos ({filtered.length.toLocaleString()})
                  </button>
                </div>
              </div>
            )}
          </div>
        </>)}
        </>)}

        {/* ── Popover de filtro por columna ── */}
        {popCol && (
          <>
            <div onClick={()=>setPopCol(null)} style={{position:'fixed',inset:0,zIndex:1050}}/>
            <div style={{position:'fixed',left:popCol.x,top:popCol.y,zIndex:1051,width:216,background:C.bg2,
              border:`1px solid ${C.border}`,borderRadius:10,boxShadow:'0 8px 24px rgba(16,24,40,.16)',padding:'12px 14px'}}>
              <div style={{fontSize:11,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:C.t3,marginBottom:8}}>
                Filtrar {popCol.label}
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                {['min','max'].map(lado => (
                  <input key={lado} className="radar-input" type="number" placeholder={lado==='min'?'Desde':'Hasta'}
                    value={colFiltros[popCol.campo]?.[lado] ?? ''}
                    onChange={e=>setColFiltros(f=>({...f,[popCol.campo]:{min:'',max:'',...f[popCol.campo],[lado]:e.target.value}}))}
                    style={{width:'50%',height:32,padding:'0 9px',background:C.bg1,border:`1px solid ${C.border}`,
                      borderRadius:8,color:C.t1,fontSize:12.5}}/>
                ))}
              </div>
              <div style={{display:'flex',justifyContent:'space-between',marginTop:10}}>
                <button onClick={()=>{setColFiltros(f=>{const n={...f}; delete n[popCol.campo]; return n;}); setPopCol(null);}}
                  style={{background:'none',border:'none',cursor:'pointer',fontSize:12,color:C.t4,padding:0,textDecoration:'underline'}}>Limpiar</button>
                <button onClick={()=>setPopCol(null)}
                  style={{height:28,padding:'0 14px',borderRadius:7,border:'none',cursor:'pointer',fontSize:12,fontWeight:700,
                    background:C.primary,color:'#fff'}}>Listo</button>
              </div>
            </div>
          </>
        )}

        {/* ── Ficha del producto (drawer) ── */}
        {fichaSel && (
          <div onClick={()=>setFichaSel(null)}
            style={{position:'fixed',inset:0,background:'rgba(11,18,32,.32)',zIndex:1100,display:'flex',justifyContent:'flex-end'}}>
            <div onClick={e=>e.stopPropagation()}
              style={{width:'min(480px,94vw)',height:'100%',background:C.bg2,boxShadow:'-8px 0 30px rgba(16,24,40,.18)',display:'flex',flexDirection:'column'}}>
              <div style={{padding:'16px 18px 12px',borderBottom:`1px solid ${C.border}`,background:C.primaryTint}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                  <div style={{minWidth:0,flex:1}}>
                    <div className="radar-num" style={{fontSize:11,color:C.blue,fontWeight:700,fontFamily:'monospace'}}>
                      {fichaSel.codigo}{fichaSel.num_parte?` · nº parte ${fichaSel.num_parte}`:''}
                    </div>
                    <div style={{fontSize:15,fontWeight:800,color:C.t1,lineHeight:1.3,marginTop:2}}>{fichaSel.nombre}</div>
                    <div style={{fontSize:11,color:C.t4,marginTop:3}}>{fichaSel.grupo} · {EMP_LABEL[fichaSel.empresa_id]||fichaSel.empresa_id}</div>
                  </div>
                  <button onClick={()=>setFichaSel(null)} style={{background:'none',border:'none',fontSize:20,color:C.t3,cursor:'pointer',lineHeight:1}}>×</button>
                </div>
                <div style={{display:'flex',gap:2,marginTop:12}}>
                  {[['resumen','Resumen'],['ubicaciones',`Ubicaciones${(fichaSel.bodegas||[]).length?` · ${(fichaSel.bodegas||[]).length}`:''}`],['otras','Otras empresas']].map(([v,l])=>(
                    <button key={v} onClick={()=>setFichaTab(v)}
                      style={{padding:'6px 11px',border:'none',borderRadius:'7px 7px 0 0',fontSize:11.5,fontWeight:750,cursor:'pointer',
                        background:'transparent',color:fichaTab===v?C.primary:C.t4,boxShadow:fichaTab===v?`inset 0 -2px 0 ${C.primary}`:'none'}}>{l}</button>
                  ))}
                </div>
              </div>
              <div style={{flex:1,overflowY:'auto',padding:'14px 18px'}}>
                {fichaTab==='resumen' && (() => {
                  const vm = fichaSel.ventas_meses || {};
                  const meses = Object.keys(vm).sort().slice(-6);
                  const tot6 = meses.reduce((a,m)=>a+Math.max(0,Number(vm[m])||0),0);
                  const ventaMes = tot6/6;
                  const cob = (ventaMes>0 && fichaSel.disponible!=null) ? Math.round(fichaSel.disponible/(ventaMes/30)) : null;
                  const maxV = Math.max(1, ...meses.map(m=>Number(vm[m])||0));
                  const llegada = (fichaSel.oc_pendiente||[]).filter(o=>o.fecha).sort((a,b)=>a.fecha<b.fecha?-1:1)[0];
                  const quiebre = fichaSel.disponible!=null && fichaSel.disponible<=0 && fichaSel.stock>0;
                  const bods = fichaSel.bodegas||[];
                  const maxB = Math.max(1, ...bods.map(b=>b.stock));
                  const pedidoTot = bods.reduce((x,b)=>x+(b.pedido||0),0);
                  const M = ({l,v,c,sub}) => (
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:9.5,fontWeight:800,letterSpacing:'.06em',textTransform:'uppercase',color:C.t4}}>{l}</div>
                      <div className="radar-num" style={{fontSize:17,fontWeight:800,color:c||C.t1,marginTop:2,letterSpacing:'-0.01em'}}>{v}</div>
                      {sub && <div style={{fontSize:10,color:C.t4,marginTop:1}}>{sub}</div>}
                    </div>
                  );
                  return (
                    <>
                      {/* Lo vendible HOY, grande y con veredicto */}
                      <div style={{background:quiebre?C.redTint:(fichaSel.disponible>0?C.greenTint:C.bg3),
                        border:`1px solid ${quiebre?C.redBorder:(fichaSel.disponible>0?C.greenBorder:C.border)}`,
                        borderRadius:12,padding:'14px 16px',display:'flex',alignItems:'center',gap:14,marginBottom:14}}>
                        <div>
                          <div className="radar-num" style={{fontSize:34,fontWeight:800,letterSpacing:'-0.03em',lineHeight:1,
                            color:quiebre?C.redText:(fichaSel.disponible>0?C.greenText:C.t3)}}>
                            {fichaSel.disponible==null?'—':num(fichaSel.disponible)}
                          </div>
                          <div style={{fontSize:10.5,fontWeight:700,color:C.t3,marginTop:3}}>disponible para vender hoy</div>
                        </div>
                        <div style={{marginLeft:'auto',textAlign:'right',fontSize:11.5,color:C.t3,lineHeight:1.7}}>
                          <div>Stock físico <b className="radar-num" style={{color:C.t1}}>{num(fichaSel.stock)}</b></div>
                          <div>Comprometido <b className="radar-num" style={{color:fichaSel.comprometido>0?C.orangeText:C.t4}}>{fichaSel.comprometido?num(fichaSel.comprometido):'—'}</b></div>
                          {pedidoTot>0 && <div>Pedido <b className="radar-num" style={{color:C.t2}}>{num(pedidoTot)}</b></div>}
                        </div>
                      </div>
                      {llegada && (
                        <div style={{background:C.blueTint||'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:10,padding:'9px 13px',
                          fontSize:12,color:'#1D4ED8',fontWeight:700,marginBottom:14}}>
                          En camino: {num(llegada.cant)} unidades — llegan el {llegada.fecha}{llegada.doc?` (OC ${llegada.doc})`:''}
                        </div>
                      )}

                      {/* Numeros de negocio con el margen VISUAL */}
                      <div style={{border:`1px solid ${C.border}`,borderRadius:12,padding:'13px 15px',marginBottom:14}}>
                        <div style={{display:'flex',gap:12,marginBottom:11}}>
                          <M l="Costo" v={fichaSel.costo_usd==null?'sin costo':money(fichaSel.costo_usd)} c={fichaSel.costo_usd==null?C.t4:C.t1}/>
                          <M l="Precio lista" v={money(fichaSel.precio_usd)}/>
                          <M l="Margen" v={fichaSel.margen_pct==null?'—':`${fichaSel.margen_pct}%`}
                            c={fichaSel.margen_pct==null?C.t4:(fichaSel.margen_pct<0?C.redText:fichaSel.margen_pct<15?C.orangeText:C.greenText)}/>
                          <M l="Valor total" v={fichaSel.valor_total_usd==null?'—':`$${Math.round(fichaSel.valor_total_usd).toLocaleString('en-US')}`}/>
                        </div>
                        {fichaSel.margen_pct!=null && fichaSel.margen_pct>=0 && (
                          <div style={{height:8,borderRadius:5,overflow:'hidden',display:'flex',background:C.bg3}}>
                            <div style={{width:`${100-fichaSel.margen_pct}%`,background:'#94A3B8'}} title="costo"/>
                            <div style={{width:`${fichaSel.margen_pct}%`,background:C.green}} title="margen"/>
                          </div>
                        )}
                        {fichaSel.margen_pct!=null && fichaSel.margen_pct>=0 && (
                          <div style={{display:'flex',justifyContent:'space-between',fontSize:9.5,color:C.t4,marginTop:4}}>
                            <span>costo</span><span>margen {fichaSel.margen_pct}%</span>
                          </div>
                        )}
                      </div>

                      {/* Como se mueve: barras de ventas de 6 meses + cobertura */}
                      <div style={{border:`1px solid ${C.border}`,borderRadius:12,padding:'13px 15px',marginBottom:14}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:10}}>
                          <span style={{fontSize:10,fontWeight:800,letterSpacing:'.06em',textTransform:'uppercase',color:C.t4}}>Cómo se mueve</span>
                          {tot6>0
                            ? <span className="radar-num" style={{fontSize:11,color:C.t3}}>vende <b style={{color:C.t1}}>{Math.round(ventaMes*10)/10}</b>/mes{cob!=null && <> · cobertura <b style={{color:cob<=15?C.redText:cob>180?C.orangeText:C.greenText}}>{cob} días</b></>}</span>
                            : <span style={{fontSize:11,color:C.redText,fontWeight:700}}>sin ventas en 6+ meses</span>}
                        </div>
                        {meses.length>0 ? (
                          <div style={{display:'flex',gap:6,alignItems:'flex-end',height:52}}>
                            {meses.map(m=>(
                              <div key={m} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:3,height:'100%',justifyContent:'flex-end'}}>
                                <div className="radar-num" style={{fontSize:9,color:C.t4}}>{Number(vm[m])||0}</div>
                                <div style={{width:'100%',borderRadius:'4px 4px 0 0',background:C.primary,opacity:.85,
                                  height:`${Math.max(6,(Number(vm[m])||0)/maxV*34)}px`}}/>
                                <div style={{fontSize:8.5,color:C.t4}}>{m.slice(5)}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{fontSize:11.5,color:C.t4}}>Sin movimiento registrado en la ventana de 6 meses — capital dormido.</div>
                        )}
                      </div>

                      {/* Donde esta: top bodegas con barra */}
                      {bods.length>0 && (
                        <div style={{border:`1px solid ${C.border}`,borderRadius:12,padding:'13px 15px'}}>
                          <div style={{fontSize:10,fontWeight:800,letterSpacing:'.06em',textTransform:'uppercase',color:C.t4,marginBottom:9}}>Dónde está</div>
                          {bods.slice(0,4).map(b=>(
                            <div key={b.codigo} style={{display:'flex',alignItems:'center',gap:10,padding:'4px 0'}}>
                              <span style={{fontSize:11.5,fontWeight:700,color:C.t2,width:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={b.nombre}>{b.nombre}</span>
                              <div style={{flex:1,height:7,background:C.bg3,borderRadius:4,overflow:'hidden'}}>
                                <div style={{width:`${(b.stock/maxB)*100}%`,height:'100%',background:C.primary,borderRadius:4,opacity:.8}}/>
                              </div>
                              <span className="radar-num" style={{fontSize:11.5,fontWeight:700,color:b.disponible>0?C.greenText:C.redText,width:52,textAlign:'right'}}>{num(b.disponible)} disp</span>
                            </div>
                          ))}
                          {bods.length>4 && <button onClick={()=>setFichaTab('ubicaciones')} style={{background:'none',border:'none',padding:0,marginTop:6,fontSize:11,color:C.primary,fontWeight:700,cursor:'pointer'}}>Ver las {bods.length} ubicaciones</button>}
                        </div>
                      )}
                    </>
                  );
                })()}
                {fichaTab==='ubicaciones' && (
                  (fichaSel.bodegas||[]).length===0
                    ? <div style={{fontSize:12.5,color:C.t4}}>Esta instancia no expone el desglose por bodega.</div>
                    : (fichaSel.bodegas||[]).map(b=>(
                      <div key={b.codigo} style={{border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 12px',marginBottom:8}}>
                        <div style={{display:'flex',justifyContent:'space-between',gap:8}}>
                          <span style={{fontSize:12.5,fontWeight:750,color:C.t1}}>{b.nombre}</span>
                          <span className="radar-num" style={{fontSize:10.5,color:C.t4}}>{b.codigo}</span>
                        </div>
                        <div style={{display:'flex',gap:14,marginTop:6,fontSize:11.5,color:C.t3,flexWrap:'wrap'}}>
                          <span>Stock <b className="radar-num" style={{color:C.t1}}>{num(b.stock)}</b></span>
                          <span>Comprometido <b className="radar-num" style={{color:b.comprometido>0?C.orangeText:C.t4}}>{num(b.comprometido)}</b></span>
                          <span>Disponible <b className="radar-num" style={{color:b.disponible>0?C.greenText:C.redText}}>{num(b.disponible)}</b></span>
                          {b.pedido>0 && <span>Pedido <b className="radar-num" style={{color:C.t2}}>{num(b.pedido)}</b></span>}
                        </div>
                      </div>
                    ))
                )}
                {fichaTab==='otras' && (
                  otras==='cargando' ? <div style={{fontSize:12.5,color:C.t4}}>Consultando las otras empresas…</div>
                  : !otras || otras.length===0 ? <div style={{fontSize:12.5,color:C.t4}}>Este código no existe en las otras empresas del grupo.</div>
                  : otras.map(o=>{
                      // El caso estrella: acá no hay, allá sí.
                      const destacado = (fichaSel.disponible!=null && fichaSel.disponible<=0) && o.disponible>0;
                      return (
                        <div key={o.empresa_id} style={{border:`1px solid ${destacado?C.greenBorder:C.border}`,borderRadius:10,padding:'10px 12px',marginBottom:8,
                          background:destacado?C.greenTint:'transparent'}}>
                          <div style={{fontSize:12.5,fontWeight:750,color:C.t1}}>{EMP_LABEL[o.empresa_id]||o.empresa_id}</div>
                          <div style={{display:'flex',gap:14,marginTop:6,fontSize:11.5,color:C.t3,flexWrap:'wrap'}}>
                            <span>Disponible <b className="radar-num" style={{color:o.disponible>0?C.greenText:C.t4}}>{o.disponible==null?'—':num(o.disponible)}</b></span>
                            <span>Stock <b className="radar-num" style={{color:C.t1}}>{num(o.stock)}</b></span>
                            <span>Costo <b className="radar-num" style={{color:o.costo_usd==null?C.t4:C.t1}}>{o.costo_usd==null?'sin costo':money(o.costo_usd)}</b></span>
                          </div>
                          {destacado && <div style={{marginTop:7,fontSize:11,fontWeight:750,color:C.greenText}}>Acá no hay disponible — esta empresa sí tiene. Candidato a transferencia.</div>}
                        </div>
                      );
                    })
                )}
              </div>
              <div style={{padding:'10px 18px',borderTop:`1px solid ${C.border}`,fontSize:10.5,color:C.t4}}>
                Sincronizado desde SAP{fichaSel.synced_at?` · ${haceCuanto(fichaSel.synced_at)}`:''} — solo lectura
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── VISITAS (planificación semanal + check-in) ─────────────────────────────────
// Parseo LOCAL de fechas: 'YYYY-MM-DD' con new Date() se interpreta como UTC y en
// Honduras (UTC-6) se corre al dia anterior. Forzamos medianoche local.
const _pfLocal = (d) => (typeof d==='string' && /^\d{4}-\d{2}-\d{2}/.test(d)) ? new Date(d.slice(0,10)+'T00:00:00') : new Date(d);
const lunesDe = (d) => { const x=_pfLocal(d); const dow=x.getDay(); x.setDate(x.getDate()-(dow===0?6:dow-1)); x.setHours(0,0,0,0); return x; };
const ymd = (d) => { const x=_pfLocal(d); return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`; };
const DIAS_SEM = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];

// ── Giras: distancia entre 2 puntos GPS (Haversine, km en línea recta) ────────
const havKm = (a, b) => {
  const R = 6371, rad = x => x * Math.PI / 180;
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat/2)**2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng/2)**2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

// Mapa de una gira: salida (S) → paradas numeradas → llegada final (F), con la
// ruta punteada. Leaflet + OpenStreetMap (gratis, sin API key).
function GiraMapa({ gira, onClose }) {
  const mapRef = useRef(null);
  const [pts, setPts] = useState(null);
  useEffect(() => { (async () => {
    const { data } = await supabase.from('actividades_programadas')
      .select('titulo,latitud,longitud,llegada_at,clientes(empresa)')
      .eq('gira_id', gira.id).not('latitud','is',null).order('llegada_at');
    const arr = [];
    if (gira.inicio_lat != null) arr.push({ lat:gira.inicio_lat, lng:gira.inicio_lng, tipo:'inicio', lbl:'Salida', ts:gira.inicio_at });
    (data||[]).forEach((v,i) => arr.push({ lat:v.latitud, lng:v.longitud, tipo:'parada', n:i+1,
      lbl:v.clientes?.empresa || (v.titulo||'').replace(/^Visita:\s*/,'') || `Parada ${i+1}`, ts:v.llegada_at }));
    if (gira.fin_lat != null) arr.push({ lat:gira.fin_lat, lng:gira.fin_lng, tipo:'fin', lbl:'Llegada final', ts:gira.fin_at });
    setPts(arr);
  })(); }, [gira]);
  useEffect(() => {
    if (!pts || pts.length === 0 || !mapRef.current) return;
    const map = L.map(mapRef.current, { attributionControl:false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom:19 }).addTo(map);
    const latlngs = pts.map(p => [p.lat, p.lng]);
    pts.forEach(p => {
      const bg = p.tipo==='inicio' ? '#10B981' : p.tipo==='fin' ? '#0B3D91' : '#0F766E';
      const txt = p.tipo==='inicio' ? 'S' : p.tipo==='fin' ? 'F' : p.n;
      const icon = L.divIcon({ className:'', iconSize:[26,26], iconAnchor:[13,13],
        html:`<div style="background:${bg};color:#fff;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${txt}</div>` });
      L.marker([p.lat, p.lng], { icon }).addTo(map)
        .bindPopup(`<b>${p.lbl}</b><br>${p.ts ? new Date(p.ts).toLocaleTimeString('es-HN',{hour:'2-digit',minute:'2-digit'}) : ''}`);
    });
    if (latlngs.length > 1) L.polyline(latlngs, { color:'#0F766E', weight:3, dashArray:'7 7', opacity:.8 }).addTo(map);
    map.fitBounds(L.latLngBounds(latlngs).pad(0.25));
    return () => map.remove();
  }, [pts]);
  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:14}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg2,borderRadius:16,padding:16,width:680,maxWidth:'96vw'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <div>
            <div style={{fontSize:15,fontWeight:800,color:C.t1}}>Gira de {(gira.vendedor||'').split(' ')[0]} — {new Date(gira.fecha+'T00:00:00').toLocaleDateString('es-HN',{weekday:'long',day:'numeric',month:'long'})}</div>
            <div style={{fontSize:11.5,color:C.t4,marginTop:2}}>
              {gira.n_paradas||0} parada(s){gira.km_total!=null?` · ${Number(gira.km_total).toFixed(1)} km · L.${Number(gira.costo||0).toLocaleString('es-HN',{maximumFractionDigits:0})}`:' · en curso'}
            </div>
          </div>
          <button onClick={onClose} style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',fontSize:13,fontWeight:700,color:C.t2,cursor:'pointer'}}>Cerrar</button>
        </div>
        {pts===null ? <div style={{height:420,display:'flex',alignItems:'center',justifyContent:'center',color:C.t4}}>Cargando ruta…</div>
         : pts.length===0 ? <div style={{height:120,display:'flex',alignItems:'center',justifyContent:'center',color:C.t4,fontSize:13}}>Esta gira no tiene puntos GPS registrados.</div>
         : <div ref={mapRef} style={{height:420,borderRadius:12,overflow:'hidden'}}/>}
      </div>
    </div>
  );
}

// ── Visitas · DS v3: kit visual (íconos stroke 1.5 estilo feather, pills fantasma, dot-pills) ──
const VIS_I = {
  calendar: <><rect x="3" y="4" width="18" height="17" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
  grid:     <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></>,
  truck:    <><rect x="1" y="5" width="14" height="11" rx="1"/><path d="M15 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2"/><circle cx="18.5" cy="18.5" r="2"/></>,
  pin:      <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>,
  check:    <polyline points="20 6 9 17 4 12"/>,
  x:        <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
  clock:    <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
  printer:  <><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></>,
  mail:     <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
  chevL:    <polyline points="15 18 9 12 15 6"/>,
  chevR:    <polyline points="9 18 15 12 9 6"/>,
  map:      <><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></>,
  sliders:  <><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></>,
  coin:     <><circle cx="12" cy="12" r="9"/><path d="M12 7v10M9.5 9.7h3.6a1.8 1.8 0 010 3.6h-2.2a1.8 1.8 0 000 3.6H15"/></>,
  note:     <><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></>,
  flag:     <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></>,
  user:     <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
  target:   <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>,
  dots:     <><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></>,
  undo:     <><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></>,
  trash:    <><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></>,
  checkc:   <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>,
  arrowR:   <><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></>,
  stop:     <rect x="6" y="6" width="12" height="12" rx="2"/>,
};
const VIco = ({ d, size=14, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flexShrink:0,display:'block',...style}}>{d}</svg>
);
// Pill fantasma de toolbar (receta 7.4 PILL, h30) con ícono SVG y tono tintado opcional.
const VPill = ({ icon, children, onClick, title, disabled, tone, style={} }) => (
  <button className="radar-pill" onClick={onClick} title={title} disabled={disabled}
    style={{display:'inline-flex',alignItems:'center',gap:6,height:30,padding:'0 12px',borderRadius:R.md,fontSize:12,fontWeight:tone?600:500,cursor:disabled?'not-allowed':'pointer',whiteSpace:'nowrap',flexShrink:0,
      background:tone?tone.tint:C.bg2,color:tone?tone.text:C.t2,border:`1px solid ${tone?tone.border:C.border}`,boxShadow:tone?'none':SH.xs,opacity:disabled?.6:1,...style}}>
    {icon&&<VIco d={icon} style={{color:tone?tone.text:C.t4}}/>}{children}
  </button>
);
// Badge dot-pill de 20px (receta 7.5) sobre tripleta TONES.
const VDot = ({ tone, children, title, num }) => (
  <span title={title} className={num?'radar-num':undefined} style={{display:'inline-flex',alignItems:'center',gap:5,height:20,boxSizing:'border-box',padding:'0 8px',borderRadius:R.pill,fontSize:10.5,fontWeight:600,background:tone.tint,color:tone.text,border:`1px solid ${tone.border}`,whiteSpace:'nowrap'}}>
    <span style={{width:6,height:6,borderRadius:'50%',background:tone.solid,flexShrink:0}}/>{children}
  </span>
);
// Identidad de tipo de visita — chips estilo tag LOB (receta 7.5).
const TIPO_VIS_TONO = { comercial:C.lob.venta, entrega:C.lob.renta, combinada:C.lob.servicio, otra:{ text:C.t3, bg:C.bg3, border:C.border } };
const VTipoChip = ({ tipo, label }) => {
  const t = TIPO_VIS_TONO[tipo] || TIPO_VIS_TONO.otra;
  return <span style={{display:'inline-flex',alignItems:'center',height:20,boxSizing:'border-box',padding:'0 7px',borderRadius:R.sm,fontSize:10,fontWeight:600,background:t.bg,color:t.text,border:`1px solid ${t.border}`,whiteSpace:'nowrap'}}>{label}</span>;
};
// ── Visitas v4 (mockup aprobado radar-diseno-visitas): day-strip + agenda de UN
//    día en filas tipo Linear + rail derecho. CSS del módulo inyectado una vez:
//    hover de filas, acciones reveladas (ancho SIEMPRE reservado), tooltips y
//    reglas responsive/anti-solape del blueprint. ──────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('radar-vis-fx')) {
  const st = document.createElement('style'); st.id = 'radar-vis-fx';
  st.textContent = `
    .vis-daychip{transition:box-shadow .18s cubic-bezier(.4,0,.2,1),transform .18s cubic-bezier(.4,0,.2,1),border-color .18s ease,background .18s ease;}
    .vis-daychip:hover{box-shadow:${SH.md};transform:translateY(-1px);}
    .vis-arow{transition:transform .15s ease,box-shadow .15s ease,border-color .15s ease;}
    .vis-arow:hover{transform:translateY(-1px);box-shadow:${SH.md};border-color:${C.border2};}
    .vis-acts{opacity:0;transform:translateX(4px);transition:opacity .15s ease,transform .15s ease;}
    .vis-arow:hover .vis-acts,.vis-arow:focus-within .vis-acts,.vis-acts.shown{opacity:1;transform:translateX(0);}
    @media (hover:none){.vis-acts{opacity:1;transform:none;}}
    .vis-act{transition:background .15s ease,color .15s ease,border-color .15s ease,transform .15s ease;}
    .vis-act:hover{transform:translateY(-1px);}
    .vis-act.ok:hover{background:${C.greenTint};color:${C.greenText};border-color:${C.greenBorder};}
    .vis-act.no:hover{background:${C.redTint};color:${C.redText};border-color:${C.redBorder};}
    .vis-act.mo:hover{background:${C.bg3};color:${C.t2};border-color:${C.border};}
    .vis-act.here:hover{background:${C.primaryHover};}
    .vis-act::after{content:attr(data-tip);position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%) translateY(2px);background:${C.t1};color:#fff;font-size:10.5px;font-weight:500;padding:4px 8px;border-radius:6px;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .15s ease,transform .15s ease;box-shadow:${SH.md};z-index:6;}
    .vis-act:hover::after{opacity:1;transform:translateX(-50%) translateY(0);}
    .vis-act:last-child::after{left:auto;right:0;transform:translateX(0) translateY(2px);}
    .vis-act:last-child:hover::after{transform:translateX(0) translateY(0);}
    .vis-rlink{transition:background .15s ease,transform .15s ease;}
    .vis-rlink:hover{background:#FEF3C7;transform:translateY(-1px);}
    .vis-qlink{transition:color .15s ease;}
    .vis-qlink:hover{color:${C.blue};}
    .vis-qlink:hover svg{color:${C.blue} !important;}
    .vis-mi{transition:background .13s ease;}
    .vis-mi:hover{background:${C.bg3};}
    .vis-weekarrow{transition:background .15s ease,color .15s ease,border-color .15s ease;}
    .vis-weekarrow:hover{background:${C.bg1};color:${C.t1};border-color:${C.border2};}
    @media (max-width:1280px){.vis-sname{display:none;}.vis-seller{width:36px !important;}.vis-dcount{display:none;}}
    @media (max-width:1100px){.vis-cols{grid-template-columns:1fr !important;}.vis-rail{flex-direction:row !important;flex-wrap:wrap;}.vis-rail>div{flex:1 1 260px;}}
    @media (max-width:860px){.vis-statcol{display:none;}.vis-vtypecol{display:none;}.vis-acts{width:auto !important;min-width:64px;}.vis-daystrip{grid-template-columns:repeat(3,1fr) !important;}}
  `;
  document.head.appendChild(st);
}
const nomCorto = n => { const p=(n||'').trim().split(/\s+/); return p.length>1 ? `${p[0][0]}. ${p[1]}` : (p[0]||'—'); };
// weekDonut — SVG r28 stroke 11 (dasharray sobre C=175.9), valores reales y total en texto al centro.
const VisDonut = ({ segs, total }) => {
  const CIR = 2*Math.PI*28; let acc = 0;
  return (
    <svg width="84" height="84" viewBox="0 0 84 84" role="img" aria-label={`${total} visitas de la semana`} style={{flexShrink:0}}>
      <g transform="rotate(-90 42 42)" fill="none" strokeWidth="11">
        <circle cx="42" cy="42" r="28" stroke={C.bg3}/>
        {total>0 && segs.filter(s=>s.n>0).map((s,i)=>{
          const frac = s.n/total, len = Math.max(.5, frac*CIR - (frac<1?2:0)), off = -(acc*CIR); acc += frac;
          return <circle key={i} cx="42" cy="42" r="28" stroke={s.color} strokeDasharray={`${len} ${CIR-len}`} strokeDashoffset={off}/>;
        })}
      </g>
      <text x="42" y="41.5" textAnchor="middle" style={{fontSize:17,fontWeight:700,letterSpacing:'-0.02em',fill:C.t1,fontVariantNumeric:'tabular-nums'}}>{total}</text>
      <text x="42" y="53" textAnchor="middle" style={{fontSize:8.5,fontWeight:600,letterSpacing:'.06em',textTransform:'uppercase',fill:C.t3}}>visitas</text>
    </svg>
  );
};
// dayBars — plan vs hechas por día (barras 8px rx2, baseline 64) con valores en texto.
const VisDayBars = ({ dias }) => {
  const maxV = Math.max(1, ...dias.map(d=>Math.max(d.plan,d.hechas)));
  const H = v => Math.round(v/maxV*36);
  return (
    <svg viewBox="0 0 260 84" width="260" height="84" preserveAspectRatio="xMinYMid meet" role="img" style={{display:'block',maxWidth:'100%'}}
      aria-label={`Visitas plan contra hechas por día: ${dias.map(d=>`${d.lbl} ${d.plan} de plan, ${d.hechas} hechas`).join('; ')}`}>
      {dias.map((d,i)=>{
        const gx = i*43.33, hp = H(d.plan), hh = H(d.hechas);
        return (
          <g key={i}>
            <text x={gx+21.7} y={64-Math.max(hp,hh)-6} textAnchor="middle" style={{fontSize:9.5,fontWeight:600,fill:C.t3,fontVariantNumeric:'tabular-nums'}}>{d.plan}/{d.hechas}</text>
            {hp>0 && <rect x={gx+12} y={64-hp} width="8" height={hp} rx="2" fill={d.esHoy?C.primaryBorder:C.bg3}/>}
            {hh>0 && <rect x={gx+23} y={64-hh} width="8" height={hh} rx="2" fill={C.blue}/>}
            <text x={gx+21.7} y="78" textAnchor="middle" style={{fontSize:9,fontWeight:600,letterSpacing:'.06em',fill:d.esHoy?C.blue:C.t4}}>{d.letra}</text>
          </g>
        );
      })}
    </svg>
  );
};

function Visitas({ usuario, clientes, empresaActiva }) {
  const esVentas = usuario.rol==='ventas';
  const [tab, setTab]       = useState('semana');     // semana | mes
  const [visitas, setVisitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekOff, setWeekOff] = useState(0);          // 0 = esta semana, 1 = próxima
  const [monthOff, setMonthOff] = useState(0);
  const [fVend, setFVend]   = useState(esVentas ? usuario.nombre : 'todos');
  const [vendedores, setVendedores] = useState([]);
  const [dropVend, setDropVend] = useState(false);   // popup pill "Vendedores" (solo UI)
  const vendBtnRef = useRef(null);
  const [selDiaRaw, setSelDia] = useState(null);      // día elegido en el day-strip (solo UI)
  const [rowMenu, setRowMenu]  = useState(null);      // menú "⋯" de una fila: {v,x,y}
  const [vDetalle, setVDetalle] = useState(null);     // ficha de la visita (tap en móvil): nombre completo + info + seguimiento
  const [modal, setModal]   = useState(false);
  const nv = { cliente_id:'', empresa:'', fecha:'', hora:'', notas:'', vendedor: usuario.nombre||'',
    tipo_visita:'comercial', proposito:'', resultado_esperado:'', contacto_visita:'', equipo_interes:'', etapa_visita:'', facturas_entrega:'', km:'' };
  // Requerimiento SGI: tipos de visita, resultado post-visita, aprobación del jefe y km.
  const TIPOS_VIS = [
    ['comercial','Comercial'], ['entrega','Entrega docs'], ['combinada','Combinada'], ['otra','Otra gestión'],
  ];
  const TIPO_VIS_LBL = Object.fromEntries(TIPOS_VIS);
  const ETAPAS_VIS = ['Prospección inicial','Presentación de equipo','Entrega/seguimiento de cotización','Demostración','Negociación','Cierre'];
  const [modalRes, setModalRes] = useState(null);   // visita a la que se le reporta resultado
  const [fRes, setFRes] = useState({ resultado_real:'', compromisos:'' });
  const [tarifas, setTarifas] = useState(null);      // tarifas por km desde radar_config
  const esJefe = ['admin','gerente_ventas'].includes(usuario.rol);
  useEffect(()=>{ supabase.from('radar_config').select('value').eq('key','tarifas_km').maybeSingle()
    .then(({data})=>setTarifas(data?.value||null)); },[]);

  // ── GIRAS: kilometraje GPS por checkpoints (salida → visitas → llegada final) ──
  const [gira, setGira]         = useState(null);   // gira activa del usuario
  const [giraPts, setGiraPts]   = useState([]);     // puntos GPS de la gira activa (incluye salida)
  const [giraCfg, setGiraCfg]   = useState({ precio_km:3.5, factor_ruta:1.3 });
  const [giras, setGiras]       = useState([]);     // historial (tab Giras)
  const [giraMapa, setGiraMapa] = useState(null);   // gira a mostrar en el mapa
  const [modalLlegada, setModalLlegada] = useState(false);
  const [cfgKm, setCfgKm]       = useState(null);   // modal config tarifa (jefe)
  const [giraBusy, setGiraBusy] = useState(false);
  // km de la gira: suma de tramos en línea recta × factor de ruta (~carretera real)
  const kmGira = pts => { let s=0; for (let i=1;i<pts.length;i++) s+=havKm(pts[i-1],pts[i]); return s*(Number(giraCfg.factor_ruta)||1.3); };
  const gpsActual = () => new Promise((res,rej)=>{
    if (!navigator.geolocation) return rej(new Error('Este dispositivo no tiene GPS disponible.'));
    navigator.geolocation.getCurrentPosition(
      p=>res({ lat:+p.coords.latitude.toFixed(6), lng:+p.coords.longitude.toFixed(6) }),
      ()=>rej(new Error('No se pudo obtener tu ubicación — revisá que el GPS esté activo.')),
      { timeout:10000, enableHighAccuracy:true });
  });
  useEffect(()=>{ // config + gira activa al entrar
    supabase.from('radar_config').select('value').eq('key','gira_config').maybeSingle()
      .then(({data})=>{ if (data?.value) setGiraCfg(c=>({...c,...data.value})); });
    supabase.from('giras').select('*').eq('vendedor',usuario.nombre).eq('estado','activa')
      .order('inicio_at',{ascending:false}).limit(1)
      .then(async ({data})=>{
        const g = data?.[0]; if (!g) return;
        setGira(g);
        const { data:vs } = await supabase.from('actividades_programadas')
          .select('latitud,longitud,llegada_at').eq('gira_id',g.id).not('latitud','is',null).order('llegada_at');
        setGiraPts([{lat:g.inicio_lat,lng:g.inicio_lng}, ...(vs||[]).map(x=>({lat:x.latitud,lng:x.longitud}))]);
      });
  },[]); // eslint-disable-line
  const iniciarGira = async () => {
    setGiraBusy(true);
    try {
      const p = await gpsActual();
      const { data, error } = await supabase.from('giras')
        .insert({ vendedor:usuario.nombre, inicio_lat:p.lat, inicio_lng:p.lng, fecha:ymd(new Date()) }).select().single();
      if (error) throw new Error(error.message);
      setGira(data); setGiraPts([p]);
      toast('Gira iniciada — marcá "Llegué acá" en cada visita');
    } catch(e){ toast('No se pudo iniciar la gira: '+e.message); }
    setGiraBusy(false);
  };
  const finalizarGira = async () => {
    setGiraBusy(true);
    let p = null; try { p = await gpsActual(); } catch {}
    const pts = p ? [...giraPts, p] : giraPts;
    const km = Math.round(kmGira(pts)*10)/10;
    const costo = Math.round(km*(Number(giraCfg.precio_km)||0)*100)/100;
    const upd = { estado:'finalizada', fin_at:new Date().toISOString(), fin_lat:p?.lat??null, fin_lng:p?.lng??null,
      km_total:km, costo, n_paradas:Math.max(0,giraPts.length-1) };
    const { error } = await supabase.from('giras').update(upd).eq('id',gira.id);
    setGiraBusy(false);
    if (error) { toast('No se pudo finalizar: '+error.message); return; }
    toast(`Gira finalizada: ${Math.max(0,giraPts.length-1)} parada(s) · ${km} km · L.${costo.toLocaleString('es-HN',{maximumFractionDigits:0})}${p?'':' (sin GPS final: no se contó el regreso)'}`);
    setGira(null); setGiraPts([]);
  };
  const cancelarGira = async () => {
    if (!(await confirmar('¿Cancelar la gira activa? No se registrará kilometraje.', { ok:'Cancelar gira', peligro:true }))) return;
    await supabase.from('giras').update({ estado:'cancelada', fin_at:new Date().toISOString() }).eq('id',gira.id);
    setGira(null); setGiraPts([]);
  };
  const cargarGiras = async () => {
    let q = supabase.from('giras').select('*').order('inicio_at',{ascending:false}).limit(200);
    if (esVentas) q = q.eq('vendedor', usuario.nombre);
    else if (fVend!=='todos') q = q.eq('vendedor', fVend);
    const { data } = await q; setGiras(data||[]);
  };
  useEffect(()=>{ if (tab==='giras') cargarGiras(); },[tab,fVend]); // eslint-disable-line
  const guardarCfgKm = async () => {
    const v = { precio_km:+cfgKm.precio_km||0, factor_ruta:+cfgKm.factor_ruta||1.3 };
    const { error } = await supabase.from('radar_config').upsert({ key:'gira_config', value:v }, { onConflict:'org_id,key' });
    if (error) { toast('No se pudo guardar: '+error.message); return; }
    setGiraCfg(v); setCfgKm(null); toast(`Tarifa guardada: L.${v.precio_km}/km`);
  };
  const [f, setF]           = useState(nv);
  const [saving, setSaving] = useState(false);
  const [clBusq, setClBusq] = useState('');

  const cargar = async () => {
    setLoading(true);
    let q = supabase.from('actividades_programadas').select('*,clientes(empresa,telefono,ciudad)').eq('tipo','visita').order('fecha',{ascending:true}).order('hora',{ascending:true,nullsFirst:true}).limit(2000);
    if (esVentas) q = q.eq('vendedor', usuario.nombre);
    if (empresaActiva && empresaActiva!=='all') q = q.eq('empresa_id', empresaActiva);
    const { data } = await q;
    setVisitas(data||[]); setLoading(false);
  };
  useEffect(()=>{ cargar(); },[empresaActiva]); // eslint-disable-line
  useEffect(()=>{ supabase.from('radar_usuarios').select('nombre,rol').then(({data})=>setVendedores((data||[]).filter(u=>['ventas','gerente_ventas'].includes(u.rol)).map(u=>u.nombre).sort())); },[]);

  const visF = visitas.filter(v => fVend==='todos' || v.vendedor===fVend);

  // ── Semana ──
  const baseLunes = lunesDe(new Date()); baseLunes.setDate(baseLunes.getDate()+weekOff*7);
  const diasSemana = DIAS_SEM.map((lbl,i)=>{ const d=new Date(baseLunes); d.setDate(d.getDate()+i); return { lbl, fecha: ymd(d), num: d.getDate(), esHoy: ymd(d)===ymd(new Date()) }; });
  const finSemana = new Date(baseLunes); finSemana.setDate(finSemana.getDate()+5);
  const mesCorto = d => d.toLocaleDateString('es-HN',{month:'short'}).replace('.','');
  const lblSemana = baseLunes.getMonth()===finSemana.getMonth()
    ? `${baseLunes.getDate()} – ${finSemana.getDate()} ${mesCorto(finSemana)} ${finSemana.getFullYear()}`
    : `${baseLunes.getDate()} ${mesCorto(baseLunes)} – ${finSemana.getDate()} ${mesCorto(finSemana)} ${finSemana.getFullYear()}`;
  const visSemana = visF.filter(v => diasSemana.some(d=>d.fecha===v.fecha));
  // Reembolso de km de la semana (depósito SEMANAL, según SGI): solo km autorizados por el jefe.
  const reembolsoSem = (()=>{ const por={}; visSemana.forEach(v=>{
    if (v.km_autorizado && Number(v.km)>0) {
      const k=(v.vendedor||'—'); const t=Number(tarifas?.[v.tipo_visita==='combinada'?'combinada':(v.tipo_visita||'comercial')]||0);
      por[k]=por[k]||{km:0,monto:0}; por[k].km+=Number(v.km); por[k].monto+=Number(v.km)*t;
    } }); return por; })();
  const reembolsoTotal = Object.values(reembolsoSem).reduce((s,r)=>s+r.monto,0);

  // ── Día seleccionado del day-strip: hoy si cae en la semana visible; si no, el lunes ──
  const selDia = (selDiaRaw && diasSemana.some(d=>d.fecha===selDiaRaw)) ? selDiaRaw
    : (diasSemana.find(d=>d.esHoy)?.fecha || diasSemana[0].fecha);
  const itemsDia  = visSemana.filter(v=>v.fecha===selDia);
  const ordHora   = (a,b)=>((a.hora||'99')+'').localeCompare((b.hora||'99')+'');
  const grpManana = itemsDia.filter(v=>v.hora&&v.hora<'13').sort(ordHora);
  const grpTarde  = itemsDia.filter(v=>!v.hora||v.hora>='13').sort(ordHora);
  const dPend = itemsDia.filter(v=>!v.completada&&(v.aprobacion||'pendiente')==='pendiente').length;
  const dDone = itemsDia.filter(v=>v.completada).length;
  const pendSem = visSemana.filter(v=>(v.aprobacion||'pendiente')==='pendiente').length;
  // Rail: resumen semanal REAL — visitadas / aprobadas / por aprobar (+ rechazadas si hay)
  const semDone = visSemana.filter(v=>v.completada).length;
  const semApr  = visSemana.filter(v=>!v.completada&&v.aprobacion==='aprobada').length;
  const semRej  = visSemana.filter(v=>!v.completada&&v.aprobacion==='rechazada').length;
  const semPend = visSemana.length - semDone - semApr - semRej;
  const diasBars = diasSemana.map(d=>{ const it=visF.filter(v=>v.fecha===d.fecha);
    return { letra:d.lbl==='Miércoles'?'X':d.lbl[0], lbl:d.lbl, plan:it.length, hechas:it.filter(v=>v.completada).length, esHoy:d.esHoy }; });
  // Tendencia 8 semanas — derivada de las visitas ya cargadas (sin datos inventados)
  const trend8 = Array.from({length:8},(_,i)=>{ const l=new Date(baseLunes); l.setDate(l.getDate()-(7-i)*7);
    const ini=ymd(l); const f2=new Date(l); f2.setDate(f2.getDate()+5);
    return visF.filter(v=>v.fecha>=ini&&v.fecha<=ymd(f2)).length; });
  const trendDelta = trend8[7]-trend8[6];
  // Cobertura por vendedor: hecho vs plan de la semana visible
  const covRows = Object.entries(visSemana.reduce((m,v)=>{ const k=v.vendedor||'Sin asignar';
    m[k]=m[k]||{plan:0,hecho:0}; m[k].plan++; if(v.completada)m[k].hecho++; return m; },{}))
    .sort((a,b)=>b[1].plan-a[1].plan);

  const marcarLlegada = async (v) => {
    if (!(await confirmar(`¿Marcar como visitada la visita a ${nomCliente(v)}? Registra tu llegada (con GPS si está activo).`, { ok:'Sí, marcar llegada' }))) return;
    const apply = async (lat,lng) => {
      const upd = { completada:true, llegada_at:new Date().toISOString(), latitud:lat, longitud:lng };
      // Gira activa: engancha la visita y auto-llena los km del tramo (GPS, no manual)
      if (gira && lat!=null && v.vendedor===usuario.nombre) {
        upd.gira_id = gira.id;
        if (!(Number(v.km)>0) && giraPts.length) {
          const ult = giraPts[giraPts.length-1];
          if (ult.lat!=null) upd.km = Math.round(havKm(ult,{lat,lng})*(Number(giraCfg.factor_ruta)||1.3)*10)/10;
        }
      }
      const { data, error } = await supabase.from('actividades_programadas').update(upd).eq('id',v.id).select('id');
      if (error || !data?.length) { toast('No se pudo marcar: '+(error?.message||'sin permiso')); return; }
      setVisitas(vs=>vs.map(x=>x.id===v.id?{...x,...upd}:x));
      if (upd.gira_id) setGiraPts(ps=>[...ps,{lat,lng}]);
      setModalLlegada(false);
      if (gira && lat==null) toast('Se marcó la visita pero SIN GPS — no suma km a la gira.');
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        p=>apply(+p.coords.latitude.toFixed(6), +p.coords.longitude.toFixed(6)),
        ()=>apply(null,null), { timeout:8000, enableHighAccuracy:true });
    } else apply(null,null);
  };
  const desmarcar = async (v) => {
    const upd={completada:false,llegada_at:null,latitud:null,longitud:null};
    const { error } = await supabase.from('actividades_programadas').update(upd).eq('id',v.id);
    if (error) { toast('No se pudo desmarcar: '+error.message); return; }
    setVisitas(vs=>vs.map(x=>x.id===v.id?{...x,...upd}:x));
  };
  const borrar = async (v) => { if(!(await confirmar('¿Borrar esta visita planificada?', { ok:'Borrar', peligro:true })))return; const {error}=await supabase.from('actividades_programadas').delete().eq('id',v.id); if(error){toast('No se pudo borrar: '+error.message);return;} setVisitas(vs=>vs.filter(x=>x.id!==v.id)); };

  const abrirNueva = (fecha) => { setF({...nv, fecha: fecha||'', vendedor: esVentas?usuario.nombre:(fVend!=='todos'?fVend:usuario.nombre)}); setClBusq(''); setModal(true); };
  const crear = async () => {
    if (!f.empresa || !f.fecha || !f.vendedor) return;
    // SGI: toda visita lleva propósito; la comercial exige contacto decisor y equipo/necesidad;
    // la de entrega exige el detalle de facturas.
    if (!f.proposito.trim()) { toast('Indicá el propósito específico de la visita (requerimiento SGI).'); return; }
    if ((f.tipo_visita==='comercial'||f.tipo_visita==='combinada') && !f.contacto_visita.trim()) { toast('Indicá el contacto (nombre y cargo) con quien te vas a reunir.'); return; }
    if ((f.tipo_visita==='entrega'||f.tipo_visita==='combinada') && !f.facturas_entrega.trim()) { toast('Detallá las facturas/documentos a entregar.'); return; }
    setSaving(true);
    const eid = (empresaActiva&&empresaActiva!=='all')?empresaActiva:(usuario.empresa_id&&usuario.empresa_id!=='all'?usuario.empresa_id:'honduras');
    const ins = { titulo:`Visita: ${f.empresa}`, tipo:'visita', fecha:f.fecha, hora:f.hora||null, vendedor:f.vendedor, cliente_id:f.cliente_id||null, notas:f.notas||null, completada:false, empresa_id:eid,
      tipo_visita:f.tipo_visita, proposito:f.proposito.trim(), resultado_esperado:f.resultado_esperado.trim()||null,
      contacto_visita:f.contacto_visita.trim()||null, equipo_interes:f.equipo_interes.trim()||null, etapa_visita:f.etapa_visita||null,
      facturas_entrega:f.facturas_entrega.trim()||null, km:parseFloat(f.km)||null, aprobacion:'pendiente' };
    const { data, error } = await supabase.from('actividades_programadas').insert(ins).select('*,clientes(empresa,telefono,ciudad)').single();
    setSaving(false);
    if (error) { toast('Error: '+error.message); return; }
    setVisitas(vs=>[...vs,data]); setModal(false); setF(nv);
  };

  // ── SGI: reporte de resultado (24h), aprobación del jefe y autorización de km ──
  const abrirResultado = (v) => { setFRes({ resultado_real:v.resultado_real||'', compromisos:v.compromisos||'' }); setModalRes(v); };
  const guardarResultado = async () => {
    if (!fRes.resultado_real.trim()) { toast('Contá qué se logró en la visita.'); return; }
    const upd = { resultado_real:fRes.resultado_real.trim(), compromisos:fRes.compromisos.trim()||null, resultado_at:new Date().toISOString() };
    const { error } = await supabase.from('actividades_programadas').update(upd).eq('id',modalRes.id);
    if (error) { toast('No se pudo guardar el resultado: '+error.message); return; }
    setVisitas(vs=>vs.map(x=>x.id===modalRes.id?{...x,...upd}:x));
    setModalRes(null); toast('Resultado registrado.');
  };
  const aprobarVisita = async (v, estado) => {
    const upd = { aprobacion:estado, aprobada_por:usuario.nombre, aprobada_at:new Date().toISOString() };
    const { error } = await supabase.from('actividades_programadas').update(upd).eq('id',v.id);
    if (error) { toast('No se pudo: '+error.message); return; }
    setVisitas(vs=>vs.map(x=>x.id===v.id?{...x,...upd}:x));
  };
  const aprobarSemana = async () => {
    const pend = visSemana.filter(v=>(v.aprobacion||'pendiente')==='pendiente');
    if (!pend.length) { toast('No hay visitas pendientes de aprobar en esta semana.'); return; }
    if (!(await confirmar(`¿Aprobar el plan de la semana? (${pend.length} visita${pend.length>1?'s':''} pendiente${pend.length>1?'s':''})`, { ok:'Aprobar plan' }))) return;
    const upd = { aprobacion:'aprobada', aprobada_por:usuario.nombre, aprobada_at:new Date().toISOString() };
    const { error } = await supabase.from('actividades_programadas').update(upd).in('id', pend.map(v=>v.id));
    if (error) { toast('No se pudo aprobar: '+error.message); return; }
    setVisitas(vs=>vs.map(x=>pend.some(p=>p.id===x.id)?{...x,...upd}:x));
    toast(`Plan aprobado: ${pend.length} visitas.`);
  };
  const autorizarKm = async (v) => {
    const upd = { km_autorizado: !v.km_autorizado };
    const { error } = await supabase.from('actividades_programadas').update(upd).eq('id',v.id);
    if (error) { toast('No se pudo: '+error.message); return; }
    setVisitas(vs=>vs.map(x=>x.id===v.id?{...x,...upd}:x));
  };

  // ── SGI: plan imprimible / correo ──
  const tarifaDe = t => Number(tarifas?.[t==='combinada'?'combinada':t]||0);
  const htmlPlan = () => {
    const semTit = `Plan de visitas · ${lblSemana}`;
    const porV = {};
    visSemana.forEach(v=>{ const k=(v.vendedor||'Sin asignar'); (porV[k]=porV[k]||[]).push(v); });
    const filas = Object.keys(porV).sort().map(vend=>{
      const rows = porV[vend].sort((a,b)=>(a.fecha+(a.hora||'')).localeCompare(b.fecha+(b.hora||''))).map(v=>{
        const est = v.resultado_at?'Con resultado':v.completada?'Visitada':'Planificada';
        const apr = v.aprobacion==='aprobada'?'Aprobada':v.aprobacion==='rechazada'?'Rechazada':'Pendiente';
        return `<tr>
          <td>${new Date(v.fecha+'T00:00:00').toLocaleDateString('es-HN',{weekday:'short',day:'numeric'})}${v.hora?' '+v.hora.slice(0,5):''}</td>
          <td><b>${nomCliente(v)}</b>${v.clientes?.ciudad?'<br><span class="mut">'+v.clientes.ciudad+'</span>':''}</td>
          <td>${TIPO_VIS_LBL[v.tipo_visita]||v.tipo_visita||'—'}</td>
          <td>${v.proposito||v.notas||'—'}${v.resultado_esperado?'<br><span class="mut">Espera: '+v.resultado_esperado+'</span>':''}</td>
          <td>${v.contacto_visita||v.facturas_entrega||'—'}</td>
          <td style="text-align:right">${v.km?Number(v.km).toFixed(0):'—'}</td>
          <td>${apr}</td><td>${est}</td>
        </tr>`;
      }).join('');
      const kmTot = porV[vend].reduce((s,v)=>s+(Number(v.km)||0),0);
      const reemb = porV[vend].filter(v=>v.km_autorizado&&Number(v.km)>0)
        .reduce((s,v)=>s+Number(v.km)*Number(tarifas?.[v.tipo_visita==='combinada'?'combinada':(v.tipo_visita||'comercial')]||0),0);
      return `<h2>${vend} <span class="mut">· ${porV[vend].length} visitas · ${kmTot.toFixed(0)} km${reemb>0?` · reembolso autorizado L.${reemb.toLocaleString('es-HN',{maximumFractionDigits:0})}`:''}</span></h2>
        <table><thead><tr><th>Día</th><th>Cliente</th><th>Tipo</th><th>Propósito</th><th>Contacto / Docs</th><th>Km</th><th>Aprobación</th><th>Estado</th></tr></thead><tbody>${rows}</tbody></table>`;
    }).join('');
    return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><title>${semTit}</title><style>
      body{font-family:Arial,Helvetica,sans-serif;color:#0F172A;margin:28px;font-size:12px}
      h1{font-size:19px;margin:0 0 2px} .sub{color:#56657F;font-size:12px;margin-bottom:18px}
      h2{font-size:14px;margin:20px 0 6px;border-bottom:2px solid #0F766E;padding-bottom:3px}
      table{width:100%;border-collapse:collapse;margin-bottom:8px}
      th{background:#EDF1F7;text-align:left;font-size:10px;text-transform:uppercase;color:#56657F}
      th,td{border:1px solid #D5DBE6;padding:6px 8px;vertical-align:top}
      .mut{color:#8593A9;font-size:10.5px}
      @media print { body{margin:8mm} }
    </style></head><body>
      <h1>${ORG.marcaUpper} — Plan de visitas</h1>
      <div class="sub">${lblSemana} · Generado ${new Date().toLocaleDateString('es-HN',{day:'numeric',month:'long',year:'numeric'})} · Requerimiento SGI de gastos de viaje</div>
      ${filas || '<p>Sin visitas planificadas esta semana.</p>'}
      <p class="mut" style="margin-top:24px">Solo se reembolsan visitas con ciclo completo: planificada en RADAR → aprobada por el jefe inmediato → ejecutada → con reporte de resultado (y acuse de recibido cuando aplique).</p>
      <table style="margin-top:30px;border:none"><tr>
        <td style="border:none;text-align:center;padding-top:30px">______________________________<br>Gerencia Comercial Ventas</td>
        <td style="border:none;text-align:center;padding-top:30px">______________________________<br>Coordinador SGI</td>
      </tr></table>
    </body></html>`;
  };
  const imprimirPlan = () => {
    const w = window.open('', '_blank');
    if (!w) { toast('El navegador bloqueó la ventana. Permití pop-ups para imprimir.'); return; }
    w.document.write(htmlPlan()); w.document.close();
    setTimeout(()=>w.print(), 400);
  };
  const [mailPlan, setMailPlan] = useState(null);   // null | {to}
  const [enviandoPlan, setEnviandoPlan] = useState(false);
  const enviarPlan = async () => {
    const to = (mailPlan?.to||'').trim();
    if (!/.+@.+\..+/.test(to)) { toast('Escribí un correo válido.'); return; }
    setEnviandoPlan(true);
    try {
      const r = await fetch('/api/email',{method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ type:'plan_visitas', to, subject:`Plan de visitas · ${lblSemana}`, html: htmlPlan() })});
      const d = await r.json().catch(()=>({}));
      if (r.ok && d.ok!==false) { toast('Plan enviado a '+to); setMailPlan(null); }
      else toast('No se pudo enviar: '+(d.error||('HTTP '+r.status)));
    } catch(e){ toast('No se pudo enviar: '+e.message); }
    setEnviandoPlan(false);
  };

  const nomCliente = v => v.clientes?.empresa || (v.titulo||'').replace(/^Visita:\s*/,'') || '—';

  // ── Fila de agenda (mockup .arow): columnas FIJAS hora 44 · vendedor 104 ·
  //    estado 112 · acciones 144 (ancho SIEMPRE reservado); solo cliente/propósito
  //    flexa con ellipsis. Acciones = icon buttons 28px revelados al hover. ──
  const actSt = (extra={}) => ({width:28,height:28,borderRadius:R.md,border:'1px solid transparent',background:'transparent',display:'grid',placeItems:'center',color:C.t3,cursor:'pointer',position:'relative',padding:0,flexShrink:0,...extra});
  const filaVisita = (v) => {
    const pend = !v.completada && (v.aprobacion||'pendiente')==='pendiente';
    const rech = !v.completada && v.aprobacion==='rechazada';
    const why  = [v.contacto_visita, v.proposito||v.notas, v.clientes?.ciudad].filter(Boolean).join(' · ');
    return (
      <div key={v.id} className="vis-arow" tabIndex={0} onClick={()=>setVDetalle(v)}
        style={{background:v.completada?`linear-gradient(0deg, rgba(15,118,110,.025), rgba(15,118,110,.025)), ${C.bg2}`:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,minHeight:56,boxSizing:'border-box',padding:'9px 12px 9px 14px',boxShadow:SH.xs,display:'flex',alignItems:'center',gap:12,minWidth:0,cursor:'pointer'}}>
        <span className="radar-num" style={{width:44,flexShrink:0,fontSize:12,fontWeight:v.hora?600:500,color:v.hora?C.t2:C.t4}}>{v.hora?v.hora.slice(0,5):'—'}</span>
        <span onClick={()=>setVDetalle(v)} title="Ver detalle y seguimiento" style={{flex:1,minWidth:0,lineHeight:1.35,cursor:'pointer'}}>
          <span style={{display:'block',fontSize:13,fontWeight:600,letterSpacing:'-0.005em',color:v.completada?C.t2:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{nomCliente(v)}</span>
          <span title={why} style={{display:'block',fontSize:11.5,color:C.t3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',marginTop:1}}>{why||' '}</span>
        </span>
        <span className="vis-vtypecol" style={{flexShrink:0}}>{v.tipo_visita?<VTipoChip tipo={v.tipo_visita} label={TIPO_VIS_LBL[v.tipo_visita]||v.tipo_visita}/>:null}</span>
        <span className="vis-seller" style={{display:'inline-flex',alignItems:'center',gap:7,width:104,flexShrink:0,minWidth:0,overflow:'hidden'}}>
          {v.vendedor?<><AvatarChip name={v.vendedor}/><span className="vis-sname" style={{fontSize:11,fontWeight:500,color:C.t2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{nomCorto(v.vendedor)}</span></>:null}
        </span>
        <span className="vis-statcol" style={{width:112,flexShrink:0,display:'flex',justifyContent:'flex-start',overflow:'hidden'}}>
          {v.completada
            ? <span className="radar-num" title={v.resultado_at?'Visitada · resultado registrado':'Visitada'} style={{display:'inline-flex',alignItems:'center',gap:5,height:20,boxSizing:'border-box',padding:'0 8px',borderRadius:R.pill,fontSize:10.5,fontWeight:600,background:C.primaryTint,color:C.blue,border:`1px solid ${C.primaryBorder}`,whiteSpace:'nowrap'}}><VIco d={VIS_I.check} size={11}/>Visitada{v.llegada_at?` · ${new Date(v.llegada_at).toLocaleTimeString('es-HN',{hour:'2-digit',minute:'2-digit'})}`:''}</span>
            : rech ? <VDot tone={TONES.red}>Rechazada</VDot>
            : pend ? <VDot tone={TONES.orange}>Por aprobar</VDot>
            : <VDot tone={TONES.green}>Aprobada</VDot>}
        </span>
        <span className={'vis-acts'+((v.completada&&!v.resultado_at)?' shown':'')} onClick={e=>e.stopPropagation()} style={{display:'inline-flex',alignItems:'center',gap:4,flexShrink:0,width:144,justifyContent:'flex-end'}}>
          {esJefe&&pend&&<button className="vis-act ok" data-tip="Aprobar visita" onClick={()=>aprobarVisita(v,'aprobada')} style={actSt()}><VIco d={VIS_I.check} size={15}/></button>}
          {esJefe&&pend&&<button className="vis-act no" data-tip="Rechazar visita" onClick={()=>aprobarVisita(v,'rechazada')} style={actSt()}><VIco d={VIS_I.x} size={14}/></button>}
          {!v.completada&&!rech&&<button className="vis-act here" data-tip="Estoy aquí — marcar llegada" onClick={()=>marcarLlegada(v)} style={actSt({background:C.blue,color:'#fff',boxShadow:`${SH.primary}, inset 0 1px 0 rgba(255,255,255,.14)`})}><VIco d={VIS_I.pin} size={14}/></button>}
          {v.completada&&!v.resultado_at&&<button className="vis-rlink" title="Reportar el resultado de la visita — requisito 24h (SGI)" onClick={()=>abrirResultado(v)} style={{display:'inline-flex',alignItems:'center',height:22,boxSizing:'border-box',padding:'0 8px',borderRadius:R.pill,background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,color:C.orangeText,fontSize:10.5,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap',flexShrink:0}}>Resultado (24h)</button>}
          <button className="vis-act mo" data-tip="Más opciones" onClick={e=>{const r=e.currentTarget.getBoundingClientRect();setRowMenu({v,x:r.right,y:r.bottom});}} style={actSt()}><VIco d={VIS_I.dots} size={14}/></button>
        </span>
      </div>
    );
  };
  const qlinkSt = {display:'inline-flex',alignItems:'center',gap:6,background:'none',border:'none',fontSize:12,fontWeight:600,color:C.t3,cursor:'pointer',padding:'4px 2px',borderRadius:R.sm};

  // ── Mes ──
  const baseMes = new Date(); baseMes.setMonth(baseMes.getMonth()+monthOff, 1);
  const mesIni = ymd(baseMes); const finMes = new Date(baseMes.getFullYear(),baseMes.getMonth()+1,0); const mesFin = ymd(finMes);
  const visMes = visF.filter(v=>v.fecha>=mesIni && v.fecha<=mesFin);
  const mesPlan = visMes.length, mesHechas = visMes.filter(v=>v.completada).length;
  const porSemMes = {};
  visMes.forEach(v=>{ const k=ymd(lunesDe(v.fecha)); (porSemMes[k]=porSemMes[k]||[]).push(v); });

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* Toolbar (mockup): segmented + weeknav + Vendedores + spacer + Aprobar plan + Planificar */}
      <div style={{padding:'10px 20px 0',display:'flex',alignItems:'center',gap:8,flexShrink:0,flexWrap:'wrap'}}>
        <div style={{display:'flex',gap:2,background:C.bg3,borderRadius:R.md,padding:2,border:`1px solid ${C.border}`,flexShrink:0}} role="tablist" aria-label="Vista">
          {[['semana','Semana'],['mes','Mes'],['giras','Giras']].map(([id,l])=>(
            <button key={id} onClick={()=>setTab(id)} style={{height:26,padding:'0 12px',border:'none',borderRadius:R.sm,cursor:'pointer',fontSize:12,fontWeight:600,background:tab===id?C.bg2:'transparent',color:tab===id?C.t1:C.t3,boxShadow:tab===id?SH.sm:'none',transition:'background .15s ease, color .15s ease'}}>{l}</button>
          ))}
        </div>
        {tab==='semana'&&(<>
          <span style={{width:1,height:20,background:C.border,flexShrink:0}}/>
          <span style={{display:'inline-flex',alignItems:'center',gap:2,flexShrink:0}} aria-label="Navegar semanas">
            <button className="vis-weekarrow" onClick={()=>setWeekOff(w=>w-1)} aria-label="Semana anterior" style={{width:26,height:26,borderRadius:R.sm,border:`1px solid ${C.border}`,background:C.bg2,display:'grid',placeItems:'center',color:C.t3,cursor:'pointer',padding:0}}><VIco d={VIS_I.chevL} size={13}/></button>
            <span className="radar-num" style={{fontSize:12.5,fontWeight:600,color:C.t2,padding:'0 10px',whiteSpace:'nowrap'}}>{lblSemana}</span>
            <button className="vis-weekarrow" onClick={()=>setWeekOff(w=>w+1)} aria-label="Semana siguiente" style={{width:26,height:26,borderRadius:R.sm,border:`1px solid ${C.border}`,background:C.bg2,display:'grid',placeItems:'center',color:C.t3,cursor:'pointer',padding:0}}><VIco d={VIS_I.chevR} size={13}/></button>
          </span>
        </>)}
        {!esVentas&&(
          <div style={{position:'relative',flexShrink:0}}>
            <button ref={vendBtnRef} onClick={()=>setDropVend(d=>!d)} className="radar-pill"
              style={{display:'inline-flex',alignItems:'center',gap:6,height:30,padding:'0 12px',borderRadius:R.md,fontSize:12,fontWeight:fVend!=='todos'?600:500,cursor:'pointer',whiteSpace:'nowrap',outline:'none',
                background:fVend!=='todos'?C.primaryTint:C.bg2,color:fVend!=='todos'?C.blue:C.t2,border:`1px solid ${fVend!=='todos'?C.primaryBorder:C.border}`,boxShadow:SH.xs}}>
              <VIco d={VIS_I.user} style={{color:fVend!=='todos'?C.blue:C.t4}}/>
              {fVend==='todos'?'Vendedores':fVend.split(' ')[0]}
              <span style={{fontSize:8,opacity:.6}}>{dropVend?'▲':'▼'}</span>
            </button>
            {dropVend&&(()=>{
              const r = vendBtnRef.current?.getBoundingClientRect();
              return (<>
                <div onClick={()=>setDropVend(false)} style={{position:'fixed',inset:0,zIndex:9998}}/>
                <div style={{position:'fixed',top:(r?.bottom||0)+6,left:r?.left||0,zIndex:9999,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'6px 0',minWidth:230,maxHeight:'60vh',overflowY:'auto',boxShadow:SH.lg}}>
                  {[['todos','Todos los vendedores'],...vendedores.map(v=>[v,v])].map(([val,lbl])=>{
                    const on = fVend===val;
                    return (
                      <div key={val} onClick={()=>{setFVend(val);setDropVend(false);}}
                        style={{padding:'9px 14px',fontSize:12,fontWeight:600,color:on?C.blue:C.t2,cursor:'pointer',display:'flex',alignItems:'center',gap:8,background:on?C.primaryTint:'transparent'}}>
                        <span style={{width:14,height:14,borderRadius:'50%',border:`2px solid ${on?C.blue:C.border2}`,background:on?C.blue:'transparent',display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{on&&<VIco d={VIS_I.check} size={8} style={{color:'#fff'}}/>}</span>
                        {lbl}
                      </div>
                    );
                  })}
                </div>
              </>);
            })()}
          </div>
        )}
        <div style={{marginLeft:'auto'}}/>
        {tab==='semana'&&esJefe&&pendSem>0&&(
          <button className="radar-pill" onClick={aprobarSemana} title="Aprobar todas las visitas pendientes de la semana"
            style={{display:'inline-flex',alignItems:'center',gap:6,height:30,padding:'0 12px',borderRadius:R.md,fontSize:12.5,fontWeight:600,background:C.greenTint,border:`1px solid ${C.greenBorder}`,color:C.greenText,cursor:'pointer',boxShadow:SH.xs,whiteSpace:'nowrap',flexShrink:0}}>
            <VIco d={VIS_I.checkc} size={14} style={{color:C.greenText}}/>Aprobar plan
            <span className="radar-num" style={{fontSize:10.5,fontWeight:700,background:C.bg2,border:`1px solid ${C.greenBorder}`,color:C.greenText,padding:'0 6px',borderRadius:R.pill,lineHeight:'16px'}}>{pendSem}</span>
          </button>
        )}
        <Btn size="sm" onClick={()=>abrirNueva(tab==='semana'?selDia:mesIni)}>+ Planificar visita</Btn>
      </div>

      {/* GIRA: callout superior solo en Mes/Giras — en Semana vive en el rail (mockup) */}
      {tab!=='semana'&&(
      <div style={{margin:'10px 20px 0',flexShrink:0}}>
        {gira ? (
          <div style={{background:C.greenTint,border:`1px solid ${C.greenBorder}`,borderRadius:12,padding:'7px 12px',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <span style={{display:'inline-flex',alignItems:'center',gap:7,fontSize:12.5,fontWeight:700,color:C.greenText}}><VIco d={VIS_I.truck} size={15}/>Gira en curso</span>
            <span className="radar-num" style={{fontSize:11.5,color:C.t3}}>
              salida {new Date(gira.inicio_at).toLocaleTimeString('es-HN',{hour:'2-digit',minute:'2-digit'})} · {Math.max(0,giraPts.length-1)} parada(s) · ~{(Math.round(kmGira(giraPts)*10)/10).toFixed(1)} km
            </span>
            <span style={{flex:1}}/>
            <button className="radar-btn" onClick={()=>setModalLlegada(true)} disabled={giraBusy} style={{display:'inline-flex',alignItems:'center',gap:6,height:32,padding:'0 13px',background:C.blue,color:'#fff',border:'none',borderRadius:R.md,fontSize:12,fontWeight:600,cursor:giraBusy?'not-allowed':'pointer',boxShadow:`${SH.primary}, inset 0 1px 0 rgba(255,255,255,.12)`,opacity:giraBusy?.6:1}}><VIco d={VIS_I.pin}/>Llegué acá</button>
            <VPill icon={VIS_I.map} onClick={()=>setGiraMapa(gira)} style={{height:32}}>Ver ruta</VPill>
            <VPill icon={VIS_I.flag} onClick={finalizarGira} disabled={giraBusy} tone={TONES.primary} style={{height:32}}>{giraBusy?'Un momento…':'Finalizar gira'}</VPill>
            <button onClick={cancelarGira} title="Cancelar gira (no registra km)" style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:32,height:32,background:'transparent',border:'none',borderRadius:R.md,color:C.t4,cursor:'pointer',padding:0}}><VIco d={VIS_I.x}/></button>
          </div>
        ) : (
          <div style={{background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,borderRadius:12,padding:'7px 12px',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:28,height:28,borderRadius:8,background:C.bg2,border:`1px solid ${C.primaryBorder}`,color:C.blue,flexShrink:0}}><VIco d={VIS_I.truck} size={15}/></span>
            <span style={{fontSize:11.5,color:C.t2,flex:1,minWidth:180}}>¿Salís a visitar? Iniciá la gira al salir de la oficina (o de tu casa) para registrar el kilometraje por GPS.</span>
            <button className="radar-btn" onClick={iniciarGira} disabled={giraBusy} style={{display:'inline-flex',alignItems:'center',gap:6,height:32,padding:'0 14px',background:C.blue,color:'#fff',border:'none',borderRadius:R.md,fontSize:12,fontWeight:600,cursor:giraBusy?'not-allowed':'pointer',boxShadow:`${SH.primary}, inset 0 1px 0 rgba(255,255,255,.12)`,opacity:giraBusy?.6:1,flexShrink:0}}><VIco d={VIS_I.pin}/>{giraBusy?'Obteniendo GPS…':'Iniciar gira'}</button>
          </div>
        )}
      </div>
      )}

      {tab==='semana' ? (
        <div style={{flex:1,minHeight:0,overflowY:'auto',overflowX:'hidden'}}>
          {/* DAY STRIP — navegador de la semana (mockup .daystrip): 6 chips, seleccionado = pill teal */}
          <div className="vis-daystrip" role="tablist" aria-label="Días de la semana" style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:12,padding:'16px 20px 0'}}>
            {diasSemana.map(d=>{
              const n = visF.filter(v=>v.fecha===d.fecha).length, sel = d.fecha===selDia;
              return (
                <button key={d.fecha} className="vis-daychip" role="tab" aria-selected={sel} onClick={()=>setSelDia(d.fecha)}
                  style={{background:sel?C.blue:C.bg2,border:`1px solid ${sel?C.blue:C.border}`,borderRadius:12,padding:'10px 14px 11px',boxShadow:sel?`${SH.primary}, inset 0 1px 0 rgba(255,255,255,.14)`:SH.xs,cursor:'pointer',display:'flex',alignItems:'center',gap:12,minWidth:0,textAlign:'left',overflow:'hidden'}}>
                  <span className="radar-num" style={{fontSize:22,fontWeight:700,letterSpacing:'-0.02em',lineHeight:1,color:sel?'#FFFFFF':C.t1}}>{d.num}</span>
                  <span style={{minWidth:0,display:'flex',flexDirection:'column',gap:3,alignItems:'flex-start'}}>
                    <span style={{fontSize:11,fontWeight:600,letterSpacing:'.07em',textTransform:'uppercase',color:sel?'rgba(255,255,255,.85)':C.t3,display:'flex',alignItems:'center',gap:5,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'100%'}}>
                      {d.esHoy&&<span style={{width:6,height:6,borderRadius:'50%',background:sel?'#FFFFFF':C.blue,boxShadow:sel?'0 0 0 3px rgba(255,255,255,.22)':'0 0 0 3px rgba(15,118,110,.14)',flexShrink:0}}/>}
                      {d.lbl.slice(0,3)}{d.esHoy?' · hoy':''}
                    </span>
                    <span className="radar-num vis-dcount" style={{fontSize:10.5,fontWeight:600,color:sel?'#FFFFFF':C.t3,background:sel?'rgba(255,255,255,.16)':C.bg1,border:`1px solid ${sel?'rgba(255,255,255,.22)':C.border}`,padding:'0 7px',borderRadius:R.pill,lineHeight:'17px',whiteSpace:'nowrap'}}>{n} visita{n===1?'':'s'}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* AGENDA DEL DÍA (2/3) + RAIL (1/3) — mockup .cols */}
          <div className="vis-cols" style={{display:'grid',gridTemplateColumns:'minmax(0,2fr) minmax(300px,1fr)',gap:20,padding:'20px 20px 24px',alignItems:'start'}}>

            {/* ===== Agenda del día ===== */}
            <div style={{minWidth:0}}>
              <div style={{display:'flex',alignItems:'baseline',gap:12,flexWrap:'wrap',padding:'2px 2px 12px'}}>
                <span style={{fontSize:15,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,textTransform:'capitalize'}}>{new Date(selDia+'T00:00:00').toLocaleDateString('es-HN',{weekday:'long',day:'numeric',month:'long'})}</span>
                <span className="radar-num" style={{fontSize:11.5,color:C.t3}}>
                  {itemsDia.length} visita{itemsDia.length===1?'':'s'}{dPend>0?` · ${dPend} por aprobar`:''}{dDone>0?` · ${dDone} visitada${dDone===1?'':'s'}`:''}
                </span>
              </div>
              {loading ? (
                <div style={{display:'flex',flexDirection:'column',gap:8}}>{[0,1,2,3].map(i=><Skel key={i} h={56} r={10}/>)}</div>
              ) : itemsDia.length===0 ? (
                <div style={{background:C.bg2,border:`1px dashed ${C.border2}`,borderRadius:10,padding:'30px 16px',textAlign:'center'}}>
                  <div style={{fontSize:12.5,color:C.t4}}>Sin visitas para este día.</div>
                  <button className="vis-qlink" onClick={()=>abrirNueva(selDia)} style={{...qlinkSt,marginTop:8,color:C.blue}}>+ Planificar una visita</button>
                </div>
              ) : (<>
                {grpManana.length>0&&(<>
                  <div style={{fontSize:10.5,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',color:C.t3,padding:'0 2px 8px',display:'flex',alignItems:'center',gap:8}}>Mañana<span style={{flex:1,height:1,background:C.border}}/></div>
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>{grpManana.map(filaVisita)}</div>
                </>)}
                {grpTarde.length>0&&(<>
                  <div style={{fontSize:10.5,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',color:C.t3,padding:grpManana.length>0?'20px 2px 8px':'0 2px 8px',display:'flex',alignItems:'center',gap:8}}>Tarde<span style={{flex:1,height:1,background:C.border}}/></div>
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>{grpTarde.map(filaVisita)}</div>
                </>)}
              </>)}
              {/* Footer del plan — links silenciosos (mockup .plan-foot) */}
              <div style={{display:'flex',alignItems:'center',gap:20,flexWrap:'wrap',padding:'16px 4px 0',borderTop:`1px solid ${C.border}`,marginTop:20}}>
                <span style={{fontSize:11.5,color:C.t4,fontWeight:500}}>Plan de la semana</span>
                <button className="vis-qlink" title="Imprimir / guardar PDF del plan de la semana" onClick={imprimirPlan} style={qlinkSt}><VIco d={VIS_I.printer} size={13} style={{color:C.t4}}/>Imprimir</button>
                <button className="vis-qlink" title="Enviar el plan por correo" onClick={()=>setMailPlan({to:'fabrizio.monterroso@montasa.com'})} style={qlinkSt}><VIco d={VIS_I.mail} size={13} style={{color:C.t4}}/>Enviar por correo</button>
                <button className="vis-qlink" onClick={()=>setWeekOff(1)} style={{...qlinkSt,marginLeft:'auto'}}>Planificar próxima semana<VIco d={VIS_I.arrowR} size={13} style={{color:C.t4}}/></button>
              </div>
            </div>

            {/* ===== Rail derecho ===== */}
            <aside className="vis-rail" style={{display:'flex',flexDirection:'column',gap:16,minWidth:0}}>

              {/* Resumen semanal: weekDonut + dayBars (datos reales de la semana) */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 16px',boxShadow:SH.sm}}>
                <div style={{fontSize:11,fontWeight:600,letterSpacing:'.07em',textTransform:'uppercase',color:C.t3,display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginBottom:12}}>Resumen semanal<span className="radar-num" style={{fontSize:10.5,fontWeight:600,letterSpacing:0,textTransform:'none',color:C.t4}}>{lblSemana}</span></div>
                <div style={{display:'flex',alignItems:'center',gap:16}}>
                  <VisDonut total={visSemana.length} segs={[{n:semDone,color:C.blue},{n:semApr,color:C.green},{n:semPend,color:C.orange},{n:semRej,color:C.red}]}/>
                  <div style={{display:'flex',flexDirection:'column',gap:8,flex:1,minWidth:0}}>
                    {[['Visitadas',semDone,C.blue],['Aprobadas',semApr,C.green],['Por aprobar',semPend,C.orange],...(semRej>0?[['Rechazadas',semRej,C.red]]:[])].map(([l,n,c])=>(
                      <div key={l} style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:C.t2,minWidth:0}}>
                        <span style={{width:7,height:7,borderRadius:'50%',background:c,flexShrink:0}}/>
                        <span style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{l}</span>
                        <b className="radar-num" style={{marginLeft:'auto',fontWeight:600,color:C.t1,paddingLeft:8}}>{n}</b>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${C.bg1}`}}>
                  <div style={{fontSize:10.5,fontWeight:600,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4,marginBottom:8}}>Por día · plan / hechas</div>
                  <VisDayBars dias={diasBars}/>
                  <div style={{display:'flex',gap:14,marginTop:10,flexWrap:'wrap'}}>
                    {[[C.bg3,'Planificadas'],[C.blue,'Completadas'],[C.primaryBorder,'Hoy (plan)']].map(([c,l])=>(
                      <span key={l} style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:10.5,fontWeight:500,color:C.t3,whiteSpace:'nowrap'}}><span style={{width:7,height:7,borderRadius:2,background:c,border:c===C.bg3?`1px solid ${C.border}`:'none',flexShrink:0}}/>{l}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Gira — callout primaryTint (en curso) o invitación a iniciar; handlers existentes */}
              <div style={{background:gira?C.primaryTint:C.bg2,border:`1px solid ${gira?C.primaryBorder:C.border}`,borderRadius:R.lg,padding:'14px 16px 16px',boxShadow:SH.sm}}>
                {gira?(()=>{
                  const ms = Date.now()-new Date(gira.inicio_at).getTime();
                  const hG = Math.floor(ms/3600000), mG = Math.max(0,Math.floor((ms%3600000)/60000));
                  const dur = hG>0?`${hG} h ${mG} m`:`${mG} m`;
                  const kmG = (Math.round(kmGira(giraPts)*10)/10).toFixed(1);
                  const btnGhost = {flex:1,height:28,borderRadius:R.md,background:C.bg2,color:C.blue,border:`1px solid ${C.primaryBorder}`,fontSize:11.5,fontWeight:600,display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,cursor:'pointer',whiteSpace:'nowrap',minWidth:0};
                  return (<>
                    <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                      <span style={{width:32,height:32,borderRadius:R.md,background:C.bg2,border:`1px solid ${C.primaryBorder}`,display:'grid',placeItems:'center',color:C.blue,flexShrink:0,boxShadow:SH.xs}}><VIco d={VIS_I.truck} size={16}/></span>
                      <span style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.primaryActive}}>Gira en curso</div>
                        <div className="radar-num" style={{fontSize:12,color:'#134E48',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{nomCorto(gira.vendedor)} · inició {new Date(gira.inicio_at).toLocaleTimeString('es-HN',{hour:'2-digit',minute:'2-digit'})}</div>
                      </span>
                      <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:10,fontWeight:700,letterSpacing:'.06em',textTransform:'uppercase',color:C.blue,whiteSpace:'nowrap',flexShrink:0,paddingTop:2}}><span style={{width:7,height:7,borderRadius:'50%',background:C.blue,boxShadow:'0 0 0 3px rgba(15,118,110,.18)'}}/>En vivo</span>
                    </div>
                    <div style={{display:'flex',gap:14,margin:'12px 0',padding:'10px 12px',background:'rgba(255,255,255,.72)',border:`1px solid ${C.primaryBorder}`,borderRadius:R.md}}>
                      {[[`${kmG} km`,'Recorrido'],[String(Math.max(0,giraPts.length-1)),'Paradas'],[dur,'Duración']].map(([gv,gl])=>(
                        <span key={gl} style={{minWidth:0}}>
                          <div className="radar-num" style={{fontSize:16,fontWeight:700,letterSpacing:'-0.015em',color:C.primaryActive,whiteSpace:'nowrap'}}>{gv}</div>
                          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.05em',textTransform:'uppercase',color:C.t3,marginTop:1}}>{gl}</div>
                        </span>
                      ))}
                    </div>
                    <button className="radar-btn" onClick={()=>setModalLlegada(true)} disabled={giraBusy} style={{height:30,width:'100%',borderRadius:R.md,background:C.blue,color:'#fff',fontSize:12.5,fontWeight:600,display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7,border:'none',boxShadow:`${SH.primary}, inset 0 1px 0 rgba(255,255,255,.12)`,cursor:giraBusy?'not-allowed':'pointer',opacity:giraBusy?.6:1}}><VIco d={VIS_I.pin} size={14}/>Llegué acá</button>
                    <div style={{display:'flex',gap:8,marginTop:8}}>
                      <button className="radar-pill" onClick={()=>setGiraMapa(gira)} style={btnGhost}><VIco d={VIS_I.map} size={12}/>Ver ruta</button>
                      <button className="radar-pill" onClick={finalizarGira} disabled={giraBusy} style={{...btnGhost,cursor:giraBusy?'not-allowed':'pointer',opacity:giraBusy?.6:1}}><VIco d={VIS_I.stop} size={12}/>{giraBusy?'Un momento…':'Finalizar gira'}</button>
                      <button onClick={cancelarGira} title="Cancelar gira (no registra km)" style={{width:28,height:28,display:'grid',placeItems:'center',background:'transparent',border:'none',borderRadius:R.md,color:C.t4,cursor:'pointer',padding:0,flexShrink:0}}><VIco d={VIS_I.x} size={13}/></button>
                    </div>
                  </>);
                })():(<>
                  <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                    <span style={{width:32,height:32,borderRadius:R.md,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,display:'grid',placeItems:'center',color:C.blue,flexShrink:0}}><VIco d={VIS_I.truck} size={16}/></span>
                    <span style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>Sin gira activa</div>
                      <div style={{fontSize:11.5,color:C.t3,marginTop:2,lineHeight:1.4}}>¿Salís a visitar? Iniciá la gira al salir de la oficina (o de tu casa) para registrar el kilometraje por GPS.</div>
                    </span>
                  </div>
                  <button className="radar-btn" onClick={iniciarGira} disabled={giraBusy} style={{height:30,width:'100%',marginTop:12,borderRadius:R.md,background:C.blue,color:'#fff',fontSize:12.5,fontWeight:600,display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7,border:'none',boxShadow:`${SH.primary}, inset 0 1px 0 rgba(255,255,255,.12)`,cursor:giraBusy?'not-allowed':'pointer',opacity:giraBusy?.6:1}}><VIco d={VIS_I.pin} size={14}/>{giraBusy?'Obteniendo GPS…':'Iniciar gira'}</button>
                </>)}
              </div>

              {/* Cobertura por vendedor: trendSpark 8 sem + bullet hecho/plan (datos reales) */}
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 16px',boxShadow:SH.sm}}>
                <div style={{fontSize:11,fontWeight:600,letterSpacing:'.07em',textTransform:'uppercase',color:C.t3,display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginBottom:12}}>Cobertura por vendedor<span style={{fontSize:10.5,fontWeight:600,letterSpacing:0,textTransform:'none',color:C.t4}}>hecho / plan</span></div>
                <div style={{paddingBottom:12,marginBottom:12,borderBottom:`1px solid ${C.bg1}`}}>
                  <div style={{display:'flex',alignItems:'baseline',gap:8,marginBottom:8}}>
                    <span style={{fontSize:10.5,fontWeight:600,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>Visitas por semana · 8 sem</span>
                    <span className="radar-num" style={{marginLeft:'auto',fontSize:10.5,fontWeight:600,color:trendDelta>0?C.greenText:trendDelta<0?C.redText:C.t4,whiteSpace:'nowrap'}}>{trendDelta>0?`+${trendDelta}`:trendDelta<0?String(trendDelta):'±0'}</span>
                    <span className="radar-num" style={{fontSize:15,fontWeight:700,letterSpacing:'-0.015em',color:C.t1}}>{trend8[7]}</span>
                  </div>
                  {(()=>{ const mx=Math.max(...trend8), mn=Math.min(...trend8), rg=(mx-mn)||1;
                    const pts=trend8.map((tv,i)=>[4+i*32, 30-((tv-mn)/rg)*24]);
                    return (
                      <svg viewBox="0 0 232 36" preserveAspectRatio="none" style={{display:'block',width:'100%',height:36}} role="img" aria-label={`Visitas por semana (8 semanas): ${trend8.join(', ')}`}>
                        <polyline points={pts.map(p=>p.join(',')).join(' ')} fill="none" stroke={C.blue} strokeOpacity=".45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"/>
                        <path d={`M${pts[7][0]} ${pts[7][1]} h0.01`} stroke={C.blue} strokeWidth="5" strokeLinecap="round" vectorEffect="non-scaling-stroke"/>
                      </svg>
                    ); })()}
                </div>
                {covRows.length===0
                  ? <div style={{fontSize:12,color:C.t4,textAlign:'center',padding:'8px 0'}}>Sin visitas esta semana.</div>
                  : <div style={{display:'flex',flexDirection:'column',gap:12}}>
                      {covRows.map(([nom,r])=>{ const pct=Math.min(100,Math.round(r.hecho/r.plan*100));
                        return (
                          <div key={nom} style={{display:'flex',alignItems:'center',gap:10,minWidth:0}}>
                            <AvatarChip name={nom} size={24}/>
                            <span style={{flex:1,minWidth:0}}>
                              <span style={{display:'flex',alignItems:'baseline',gap:8}}>
                                <span style={{fontSize:12,fontWeight:600,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{nom}</span>
                                <span className="radar-num" style={{marginLeft:'auto',fontSize:11,fontWeight:600,color:C.t2,whiteSpace:'nowrap'}}>{r.hecho} <span style={{color:C.t4,fontWeight:500}}>/ {r.plan}</span></span>
                              </span>
                              <span style={{display:'block',height:5,borderRadius:R.pill,background:C.bg3,marginTop:8,position:'relative'}}>
                                <span style={{position:'absolute',top:0,bottom:0,left:0,width:`${pct}%`,borderRadius:R.pill,background:pct<50?C.orange:C.blue}}/>
                              </span>
                            </span>
                          </div>
                        ); })}
                    </div>}
                {/* Depósito semanal de km — solo lo autorizado por el jefe (SGI) */}
                {reembolsoTotal>0&&(
                  <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${C.bg1}`}}>
                    <div style={{fontSize:10.5,fontWeight:600,letterSpacing:'.05em',textTransform:'uppercase',color:C.t4,marginBottom:6}}>Depósito semanal de km</div>
                    {Object.entries(reembolsoSem).map(([nom,r])=>(
                      <div key={nom} className="radar-num" style={{display:'flex',alignItems:'baseline',gap:8,fontSize:11.5,color:C.t2,padding:'2px 0',minWidth:0}}>
                        <span style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{nomCorto(nom)}</span>
                        <span style={{marginLeft:'auto',whiteSpace:'nowrap'}}>L.{r.monto.toLocaleString('es-HN',{maximumFractionDigits:0})} <span style={{color:C.t4}}>({r.km.toFixed(0)} km)</span></span>
                      </div>
                    ))}
                    <div className="radar-num" style={{display:'flex',fontSize:12,fontWeight:700,color:C.greenText,paddingTop:4}}><span>Total autorizado</span><span style={{marginLeft:'auto'}}>L.{reembolsoTotal.toLocaleString('es-HN',{maximumFractionDigits:0})}</span></div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      ) : tab==='mes' ? (
        <div style={{flex:1,overflowY:'auto',padding:20}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <button className="radar-pill" onClick={()=>setMonthOff(m=>m-1)} title="Mes anterior" style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:30,height:30,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,color:C.t2,cursor:'pointer',boxShadow:SH.xs,padding:0,flexShrink:0}}><VIco d={VIS_I.chevL}/></button>
            <div style={{fontSize:15,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,minWidth:160,textAlign:'center',textTransform:'capitalize'}}>{baseMes.toLocaleDateString('es-HN',{month:'long',year:'numeric'})}</div>
            <button className="radar-pill" onClick={()=>setMonthOff(m=>m+1)} title="Mes siguiente" style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:30,height:30,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,color:C.t2,cursor:'pointer',boxShadow:SH.xs,padding:0,flexShrink:0}}><VIco d={VIS_I.chevR}/></button>
            <div style={{marginLeft:'auto',display:'flex',gap:10,flexWrap:'wrap'}}>
              <div className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'12px 16px',boxShadow:SH.sm}}><div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em'}}>Planificadas</div><div className="radar-num" style={{fontSize:24,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,color:C.t1,marginTop:4}}>{mesPlan}</div></div>
              <div className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'12px 16px',boxShadow:SH.sm}}><div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em'}}>Realizadas</div><div className="radar-num" style={{fontSize:24,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,color:C.greenText,marginTop:4}}>{mesHechas}</div></div>
              <div className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'12px 16px',boxShadow:SH.sm}}><div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em'}}>Cumplimiento</div><div className="radar-num" style={{fontSize:24,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,color:C.blue,marginTop:4}}>{mesPlan?Math.round(mesHechas/mesPlan*100):0}%</div></div>
            </div>
          </div>
          {Object.keys(porSemMes).sort().length===0&&<div style={{textAlign:'center',color:C.t4,marginTop:40}}>Sin visitas este mes</div>}
          {Object.keys(porSemMes).sort().map(k=>{
            const sem=porSemMes[k]; const l=new Date(k+'T00:00:00'); const fin=new Date(k+'T00:00:00'); fin.setDate(fin.getDate()+5);
            return (
              <div key={k} style={{marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:700,color:C.t2,marginBottom:6}}>Semana {l.getDate()} – {fin.getDate()} {fin.toLocaleDateString('es-HN',{month:'short'})} · <span className="radar-num">{sem.filter(v=>v.completada).length}/{sem.length}</span></div>
                <div style={{display:'grid',gap:6}}>
                  {sem.sort((a,b)=>a.fecha.localeCompare(b.fecha)).map(v=>(
                    <div key={v.id} className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 14px',display:'flex',alignItems:'center',gap:12,boxShadow:SH.xs,minHeight:22}}>
                      <span className="radar-num" style={{fontSize:11,color:C.t4,width:90,flexShrink:0}}>{new Date(v.fecha+'T00:00:00').toLocaleDateString('es-HN',{weekday:'short',day:'numeric'})}{v.hora?` ${v.hora.slice(0,5)}`:''}</span>
                      <span title={nomCliente(v)} style={{flex:1,minWidth:0,fontSize:13,fontWeight:600,letterSpacing:'-0.005em',color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{nomCliente(v)}</span>
                      {v.tipo_visita&&<VTipoChip tipo={v.tipo_visita} label={TIPO_VIS_LBL[v.tipo_visita]||v.tipo_visita}/>}
                      {!esVentas&&v.vendedor&&<span style={{display:'inline-flex',alignItems:'center',gap:6,flexShrink:0}}><AvatarChip name={v.vendedor}/><span style={{fontSize:11,fontWeight:500,color:C.t2}}>{(v.vendedor||'').split(' ')[0]}</span></span>}
                      <VDot tone={v.completada?TONES.green:TONES.orange}>{v.completada?'Visitada':'Pendiente'}</VDot>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Tab GIRAS: historial con km, costo y mapa ── */
        <div style={{flex:1,overflowY:'auto',padding:20}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,flexWrap:'wrap'}}>
            <div style={{display:'inline-flex',alignItems:'center',gap:8,fontSize:15,fontWeight:700,letterSpacing:'-0.01em',color:C.t1}}><VIco d={VIS_I.truck} size={16} style={{color:C.t3}}/>Historial de giras</div>
            <span className="radar-num" style={{fontSize:11.5,color:C.t4}}>L.{Number(giraCfg.precio_km||0)}/km · factor de ruta ×{Number(giraCfg.factor_ruta||1.3)}</span>
            {esJefe&&<VPill icon={VIS_I.sliders} onClick={()=>setCfgKm({precio_km:giraCfg.precio_km,factor_ruta:giraCfg.factor_ruta})}>Tarifa</VPill>}
            <div style={{marginLeft:'auto',display:'flex',gap:10,flexWrap:'wrap'}}>
              {(()=>{ const mesAct=ymd(new Date()).slice(0,7);
                const fin=giras.filter(g=>g.estado==='finalizada'&&String(g.fecha||'').startsWith(mesAct));
                const km=fin.reduce((s,g)=>s+Number(g.km_total||0),0), cs=fin.reduce((s,g)=>s+Number(g.costo||0),0);
                return (<>
                  <div className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'12px 16px',boxShadow:SH.sm}}><div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em'}}>Km este mes</div><div className="radar-num" style={{fontSize:24,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,color:C.blue,marginTop:4}}>{km.toFixed(0)}</div></div>
                  <div className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'12px 16px',boxShadow:SH.sm}}><div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em'}}>Depreciación</div><div className="radar-num" style={{fontSize:24,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,color:C.greenText,marginTop:4}}>L.{cs.toLocaleString('es-HN',{maximumFractionDigits:0})}</div></div>
                </>); })()}
            </div>
          </div>
          {giras.length===0&&<div style={{textAlign:'center',color:C.t4,marginTop:40}}>Sin giras registradas todavía.<br/>Cuando salgás a visitar, tocá "Iniciar gira" en el aviso de arriba.</div>}
          <div style={{display:'grid',gap:8}}>
            {giras.map(g=>{
              const gt=g.estado==='finalizada'?TONES.green:g.estado==='activa'?TONES.primary:TONES.neutral;
              return (
              <div key={g.id} className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px 14px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',boxShadow:SH.xs}}>
                <span className="radar-num" style={{fontSize:12,color:C.t4,minWidth:105}}>{new Date((g.fecha||'')+'T00:00:00').toLocaleDateString('es-HN',{weekday:'short',day:'numeric',month:'short'})}</span>
                {!esVentas&&<span style={{display:'inline-flex',alignItems:'center',gap:6,minWidth:95}}><AvatarChip name={g.vendedor}/><span style={{fontSize:12,fontWeight:500,color:C.t2}}>{(g.vendedor||'').split(' ')[0]}</span></span>}
                <VDot tone={gt}>{g.estado==='finalizada'?'Finalizada':g.estado==='activa'?'En curso':'Cancelada'}</VDot>
                <span className="radar-num" style={{fontSize:12,color:C.t3}}>{g.n_paradas||0} parada(s)</span>
                {g.km_total!=null&&<span className="radar-num" style={{fontSize:12.5,fontWeight:700,letterSpacing:'-0.015em',color:C.blue}}>{Number(g.km_total).toFixed(1)} km</span>}
                {g.costo!=null&&g.estado==='finalizada'&&<span className="radar-num" style={{fontSize:12.5,fontWeight:700,letterSpacing:'-0.015em',color:C.greenText}}>L.{Number(g.costo).toLocaleString('es-HN',{maximumFractionDigits:0})}</span>}
                <span className="radar-num" style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:11,color:C.t4}}><VIco d={VIS_I.clock} size={11}/>{new Date(g.inicio_at).toLocaleTimeString('es-HN',{hour:'2-digit',minute:'2-digit'})}{g.fin_at?` → ${new Date(g.fin_at).toLocaleTimeString('es-HN',{hour:'2-digit',minute:'2-digit'})}`:''}</span>
                <VPill icon={VIS_I.map} tone={TONES.primary} onClick={()=>setGiraMapa(g)} style={{marginLeft:'auto'}}>Ver mapa</VPill>
              </div>
            );})}
          </div>
        </div>
      )}

      {/* Menú "⋯" de la fila de agenda — todas las acciones secundarias, handlers existentes */}
      {rowMenu&&(()=>{ const v=rowMenu.v;
        const items=[
          ...((!v.completada&&v.aprobacion==='rechazada')?[['pin','Marcar llegada (GPS)',()=>marcarLlegada(v),false]]:[]),
          ...(v.completada?[['undo','Deshacer llegada',()=>desmarcar(v),false]]:[]),
          ...(v.completada?[['note',v.resultado_at?'Editar resultado':'Reportar resultado',()=>abrirResultado(v),false]]:[]),
          ...(v.latitud?[['map','Ver ubicación en mapa',()=>window.open(`https://maps.google.com/?q=${v.latitud},${v.longitud}`,'_blank'),false]]:[]),
          ...((esJefe&&v.resultado_at&&v.km>0)?[['coin',v.km_autorizado?'Quitar autorización de km':`Autorizar km (${Number(v.km).toFixed(0)})`,()=>autorizarKm(v),false]]:[]),
          ['trash','Borrar visita',()=>borrar(v),true],
        ];
        return (<>
          <div onClick={()=>setRowMenu(null)} style={{position:'fixed',inset:0,zIndex:9998}}/>
          <div className="radar-pop" style={{position:'fixed',top:rowMenu.y+6,left:Math.max(8,Math.min(rowMenu.x-210,(window.innerWidth||1200)-218)),zIndex:9999,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'6px 0',width:210,boxShadow:SH.lg}}>
            {items.map(([ic,lbl,fn,danger],i)=>(
              <button key={i} className="vis-mi" onClick={()=>{setRowMenu(null);fn();}} style={{display:'flex',alignItems:'center',gap:9,width:'100%',padding:'8px 14px',background:'transparent',border:'none',fontSize:12,fontWeight:500,color:danger?C.redText:C.t2,cursor:'pointer',textAlign:'left',boxSizing:'border-box'}}>
                <VIco d={VIS_I[ic]} size={13} style={{color:danger?C.redText:C.t4}}/>{lbl}
              </button>
            ))}
          </div>
        </>);
      })()}

      {/* GIRA: ¿a qué visita llegaste? (anexa la planificada del calendario) */}
      {modalLlegada&&(
        <div onClick={()=>setModalLlegada(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bg2,borderRadius:16,padding:22,width:400,maxWidth:'92vw',maxHeight:'85vh',overflowY:'auto'}}>
            <div style={{fontSize:16,fontWeight:800,color:C.t1,marginBottom:4}}>¿A qué visita llegaste?</div>
            <div style={{fontSize:11.5,color:C.t4,marginBottom:12}}>Se marca la llegada con tu GPS y se suma el tramo a la gira.</div>
            {(()=>{ const hoyStr=ymd(new Date());
              const pend=visitas.filter(v=>v.fecha===hoyStr&&!v.completada&&v.vendedor===usuario.nombre);
              return (<>
                {pend.length===0&&<div style={{fontSize:12.5,color:C.t4,marginBottom:10}}>No tenés visitas planificadas pendientes para hoy.</div>}
                <div style={{display:'grid',gap:8}}>
                  {pend.map(v=>(
                    <button key={v.id} onClick={()=>marcarLlegada(v)} style={{textAlign:'left',background:C.bg3,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 13px',cursor:'pointer'}}>
                      <div style={{fontSize:13.5,fontWeight:800,color:C.t1}}>{nomCliente(v)}</div>
                      <div style={{fontSize:11,color:C.t4,marginTop:2}}>{v.hora?`${v.hora.slice(0,5)} · `:''}{TIPO_VIS_LBL[v.tipo_visita]||''}{v.clientes?.ciudad?` · ${v.clientes.ciudad}`:''}</div>
                    </button>
                  ))}
                </div>
                <button onClick={()=>{setModalLlegada(false);abrirNueva(hoyStr);}} style={{width:'100%',marginTop:10,background:C.primaryTint,border:`1px dashed ${C.primaryBorder}`,color:C.blue,borderRadius:R.md,padding:'9px',fontSize:12.5,fontWeight:600,cursor:'pointer'}}>+ Visita no planificada (creala y luego marcá la llegada)</button>
              </>); })()}
            <button onClick={()=>setModalLlegada(false)} style={{width:'100%',marginTop:10,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:9,padding:'8px',fontSize:12.5,fontWeight:700,color:C.t2,cursor:'pointer'}}>Cerrar</button>
          </div>
        </div>
      )}

      {/* GIRA: configurar tarifa por km (jefe) */}
      {cfgKm&&(
        <div onClick={()=>setCfgKm(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bg2,borderRadius:16,padding:22,width:340,maxWidth:'92vw'}}>
            <div style={{fontSize:16,fontWeight:800,color:C.t1,marginBottom:12}}>Tarifa de kilometraje</div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <label style={{fontSize:11.5,color:C.t4,fontWeight:700}}>Precio por km (L.) — depreciación/combustible
                <input type="number" step="0.1" value={cfgKm.precio_km} onChange={e=>setCfgKm(x=>({...x,precio_km:e.target.value}))}
                  style={{width:'100%',boxSizing:'border-box',marginTop:4,padding:'9px 11px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:14,background:C.bg1,color:C.t1}}/>
              </label>
              <label style={{fontSize:11.5,color:C.t4,fontWeight:700}}>Factor de ruta (línea recta → carretera, estándar 1.3)
                <input type="number" step="0.05" value={cfgKm.factor_ruta} onChange={e=>setCfgKm(x=>({...x,factor_ruta:e.target.value}))}
                  style={{width:'100%',boxSizing:'border-box',marginTop:4,padding:'9px 11px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:14,background:C.bg1,color:C.t1}}/>
              </label>
            </div>
            <div style={{display:'flex',gap:8,marginTop:14}}>
              <button onClick={()=>setCfgKm(null)} style={{flex:1,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:9,padding:'9px',fontSize:13,fontWeight:700,color:C.t2,cursor:'pointer'}}>Cancelar</button>
              <button onClick={guardarCfgKm} style={{flex:1,background:C.blue,border:'none',borderRadius:9,padding:'9px',fontSize:13,fontWeight:700,color:'#fff',cursor:'pointer'}}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* GIRA: mapa de la ruta */}
      {giraMapa&&<GiraMapa gira={giraMapa} onClose={()=>setGiraMapa(null)}/>}

      {/* SGI: reporte de resultado post-visita */}
      {/* Ficha de la visita (tap en la fila) — nombre completo + info + seguimiento. Clave en móvil. */}
      {vDetalle&&(()=>{ const v=vDetalle;
        const estado = v.completada ? 'Visitada' : v.aprobacion==='rechazada' ? 'Rechazada' : (v.aprobacion||'pendiente')==='pendiente' ? 'Por aprobar' : 'Aprobada';
        const estTone = v.completada ? TONES.primary : v.aprobacion==='rechazada' ? TONES.red : (v.aprobacion||'pendiente')==='pendiente' ? TONES.orange : TONES.green;
        const fechaLbl = (()=>{ try { return new Date(v.fecha+'T00:00:00').toLocaleDateString('es-HN',{weekday:'long',day:'numeric',month:'long'}); } catch { return v.fecha; } })();
        const campos = [
          ['Propósito', v.proposito || v.notas],
          ['Resultado esperado', v.resultado_esperado],
          ['Contacto', v.contacto_visita],
          ['Equipo de interés', v.equipo_interes],
          ['Docs / facturas', v.facturas_entrega],
          ['Kilómetros', v.km ? `${v.km} km` : null],
        ].filter(([,val])=>val);
        return (
        <div onClick={()=>setVDetalle(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16,boxSizing:'border-box'}}>
          <div onClick={e=>e.stopPropagation()} className="radar-pop" style={{background:C.bg2,borderRadius:16,padding:20,width:460,maxWidth:'92vw',maxHeight:'88vh',overflowY:'auto',boxShadow:SH.lg}}>
            <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:12}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:17,fontWeight:800,color:C.t1,letterSpacing:'-0.01em',lineHeight:1.25}}>{nomCliente(v)}</div>
                <div style={{fontSize:12,color:C.t4,marginTop:3,textTransform:'capitalize'}}>{fechaLbl}{v.hora?` · ${v.hora.slice(0,5)}`:''}{v.clientes?.ciudad?` · ${v.clientes.ciudad}`:''}</div>
              </div>
              <button onClick={()=>setVDetalle(null)} style={{background:C.bg3,border:'none',borderRadius:'50%',width:30,height:30,fontSize:18,lineHeight:1,color:C.t3,cursor:'pointer',flexShrink:0}}>×</button>
            </div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14}}>
              {v.tipo_visita&&<VTipoChip tipo={v.tipo_visita} label={TIPO_VIS_LBL[v.tipo_visita]||v.tipo_visita}/>}
              <VDot tone={estTone}>{estado}</VDot>
            </div>
            {campos.length>0&&(
              <div style={{display:'flex',flexDirection:'column',gap:11,marginBottom:16}}>
                {campos.map(([k,val])=>(
                  <div key={k}>
                    <div style={{fontSize:10.5,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.04em',marginBottom:2}}>{k}</div>
                    <div style={{fontSize:13.5,color:C.t1,lineHeight:1.4,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{val}</div>
                  </div>
                ))}
              </div>
            )}
            {/* Seguimiento */}
            <div style={{background:v.resultado_at?C.primaryTint:C.bg3,border:`1px solid ${v.resultado_at?C.primaryBorder:C.border}`,borderRadius:12,padding:14,marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:800,color:C.t1,textTransform:'uppercase',letterSpacing:'.04em',marginBottom:8,display:'flex',alignItems:'center',gap:7}}><VIco d={VIS_I.check} size={14}/>Seguimiento</div>
              {v.resultado_at ? (
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  <div>
                    <div style={{fontSize:10.5,fontWeight:700,color:C.t4,textTransform:'uppercase',marginBottom:2}}>Qué se logró</div>
                    <div style={{fontSize:13.5,color:C.t1,lineHeight:1.45,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{v.resultado_real||'—'}</div>
                  </div>
                  {v.compromisos&&<div>
                    <div style={{fontSize:10.5,fontWeight:700,color:C.t4,textTransform:'uppercase',marginBottom:2}}>Compromisos / próxima acción</div>
                    <div style={{fontSize:13.5,color:C.t1,lineHeight:1.45,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{v.compromisos}</div>
                  </div>}
                  <div style={{fontSize:10.5,color:C.t4}}>Registrado {new Date(v.resultado_at).toLocaleDateString('es-HN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</div>
                </div>
              ) : (
                <div style={{fontSize:13,color:C.t3}}>Aún sin seguimiento registrado.</div>
              )}
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {!v.completada&&v.aprobacion!=='rechazada'&&<button onClick={()=>{ setVDetalle(null); marcarLlegada(v); }} style={{flex:'1 1 45%',background:C.bg3,border:`1px solid ${C.border}`,borderRadius:10,padding:'11px',fontSize:13.5,fontWeight:700,color:C.t2,cursor:'pointer',display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7}}><VIco d={VIS_I.pin} size={15}/>Marcar llegada</button>}
              {v.completada&&<button onClick={async()=>{ if(!(await confirmar('¿Marcar esta visita como NO visitada? Se quitará la llegada registrada.',{ok:'Sí, deshacer',peligro:true})))return; setVDetalle(null); desmarcar(v); }} style={{flex:'1 1 45%',background:C.redTint,border:`1px solid ${C.redBorder}`,borderRadius:10,padding:'11px',fontSize:13.5,fontWeight:700,color:C.redText,cursor:'pointer',display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7}}><VIco d={VIS_I.x} size={15}/>Deshacer visitada</button>}
              <button onClick={()=>{ setVDetalle(null); abrirResultado(v); }} style={{flex:'1 1 45%',background:C.blue,border:'none',borderRadius:10,padding:'11px',fontSize:13.5,fontWeight:700,color:'#fff',cursor:'pointer',boxShadow:SH.primary}}>{v.resultado_at?'Editar seguimiento':'Escribir seguimiento'}</button>
            </div>
          </div>
        </div>
        );
      })()}

      {modalRes&&(
        <div onClick={()=>setModalRes(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bg2,borderRadius:16,padding:22,width:420,maxWidth:'92vw',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{fontSize:16,fontWeight:800,color:C.t1,marginBottom:4}}>Resultado de la visita</div>
            <div style={{fontSize:12,color:C.t4,marginBottom:12}}>{nomCliente(modalRes)} · {modalRes.fecha}{modalRes.proposito?` · ${modalRes.proposito}`:''}</div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <textarea autoFocus placeholder="¿Qué se logró? (resultado real) *" value={fRes.resultado_real} onChange={e=>setFRes(x=>({...x,resultado_real:e.target.value}))} rows={3} style={{padding:'9px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1,resize:'vertical'}}/>
              <textarea placeholder="Compromisos adquiridos + próxima acción y fecha" value={fRes.compromisos} onChange={e=>setFRes(x=>({...x,compromisos:e.target.value}))} rows={2} style={{padding:'9px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1,resize:'vertical'}}/>
              <div style={{fontSize:11,color:C.t4}}>El reporte dentro de 24h es requisito para el reembolso de kilometraje (SGI).</div>
            </div>
            <div style={{display:'flex',gap:8,marginTop:14}}>
              <button onClick={()=>setModalRes(null)} style={{flex:1,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:9,padding:'9px',fontSize:13,fontWeight:700,color:C.t2,cursor:'pointer'}}>Cancelar</button>
              <button onClick={guardarResultado} style={{flex:1,background:C.indigo,border:'none',borderRadius:9,padding:'9px',fontSize:13,fontWeight:700,color:'#fff',cursor:'pointer'}}>Guardar resultado</button>
            </div>
          </div>
        </div>
      )}

      {/* SGI: enviar plan por correo */}
      {mailPlan&&(
        <div onClick={()=>setMailPlan(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bg2,borderRadius:16,padding:22,width:380,maxWidth:'92vw'}}>
            <div style={{fontSize:16,fontWeight:800,color:C.t1,marginBottom:4}}>Enviar plan de visitas</div>
            <div style={{fontSize:12,color:C.t4,marginBottom:12}}>{lblSemana} · {visSemana.length} visitas</div>
            <input type="email" value={mailPlan.to} onChange={e=>setMailPlan({to:e.target.value})} placeholder="correo@montasa.com"
              onKeyDown={e=>e.key==='Enter'&&enviarPlan()}
              style={{width:'100%',boxSizing:'border-box',padding:'10px 12px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1,outline:'none'}}/>
            <div style={{display:'flex',gap:8,marginTop:14}}>
              <button onClick={()=>setMailPlan(null)} style={{flex:1,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:9,padding:'9px',fontSize:13,fontWeight:700,color:C.t2,cursor:'pointer'}}>Cancelar</button>
              <button onClick={enviarPlan} disabled={enviandoPlan} style={{flex:1,background:C.blue,border:'none',borderRadius:9,padding:'9px',fontSize:13,fontWeight:700,color:'#fff',cursor:'pointer'}}>{enviandoPlan?'Enviando…':'Enviar'}</button>
            </div>
          </div>
        </div>
      )}

      {modal&&(
        <div onClick={()=>setModal(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bg2,borderRadius:16,padding:22,width:400,maxWidth:'92vw',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{fontSize:16,fontWeight:800,color:C.t1,marginBottom:14}}>Planificar visita</div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <ClienteBuscador clientes={clientes} value={clBusq} empresaActiva={empresaActiva}
                onChange={setClBusq} onSelect={c=>{setF(x=>({...x,cliente_id:c.id,empresa:c.empresa}));setClBusq(c.empresa);}} onClear={()=>{setF(x=>({...x,cliente_id:'',empresa:''}));setClBusq('');}}
                onCreateNew={()=>{setF(x=>({...x,cliente_id:'',empresa:clBusq}));}} label="Cliente / lugar *"/>
              <div style={{display:'flex',gap:8}}>
                <label style={{flex:1,fontSize:11,color:C.t4,fontWeight:700}}>Día *<input type="date" value={f.fecha} onChange={e=>setF(x=>({...x,fecha:e.target.value}))} style={{width:'100%',marginTop:3,padding:'8px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1}}/></label>
                <label style={{width:110,fontSize:11,color:C.t4,fontWeight:700}}>Hora<input type="time" value={f.hora} onChange={e=>setF(x=>({...x,hora:e.target.value}))} style={{width:'100%',marginTop:3,padding:'8px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1}}/></label>
              </div>
              {!esVentas&&<select value={f.vendedor} onChange={e=>setF(x=>({...x,vendedor:e.target.value}))} style={{padding:'8px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1}}><option value="">Vendedor…</option>{vendedores.map(v=><option key={v} value={v}>{v}</option>)}</select>}

              {/* ── Requerimiento SGI ── */}
              <div>
                <div style={{fontSize:11,color:C.t4,fontWeight:700,marginBottom:4}}>Tipo de visita *</div>
                <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                  {TIPOS_VIS.map(([v,l])=>(
                    <button key={v} onClick={()=>setF(x=>({...x,tipo_visita:v}))} style={{flex:'1 0 45%',padding:'7px 6px',borderRadius:R.md,border:`1px solid ${f.tipo_visita===v?C.primaryBorder:C.border}`,background:f.tipo_visita===v?C.primaryTint:C.bg1,color:f.tipo_visita===v?C.blue:C.t3,fontSize:11.5,fontWeight:600,cursor:'pointer'}}>{l}</button>
                  ))}
                </div>
              </div>
              <textarea placeholder="Propósito específico de la visita *" value={f.proposito} onChange={e=>setF(x=>({...x,proposito:e.target.value}))} rows={2} style={{padding:'9px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1,resize:'vertical'}}/>
              <input placeholder="Resultado esperado (ej. presentar cotización, obtener OC)" value={f.resultado_esperado} onChange={e=>setF(x=>({...x,resultado_esperado:e.target.value}))} style={{padding:'9px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1}}/>
              {(f.tipo_visita==='comercial'||f.tipo_visita==='combinada')&&<>
                <input placeholder="Contacto: nombre, cargo y teléfono (decisor) *" value={f.contacto_visita} onChange={e=>setF(x=>({...x,contacto_visita:e.target.value}))} style={{padding:'9px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1}}/>
                <input placeholder="Equipo/necesidad: tipo, compra o renta, cantidad" value={f.equipo_interes} onChange={e=>setF(x=>({...x,equipo_interes:e.target.value}))} style={{padding:'9px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1}}/>
                <select value={f.etapa_visita} onChange={e=>setF(x=>({...x,etapa_visita:e.target.value}))} style={{padding:'8px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:f.etapa_visita?C.t1:C.t4}}>
                  <option value="">Etapa de la oportunidad…</option>
                  {ETAPAS_VIS.map(e2=><option key={e2} value={e2}>{e2}</option>)}
                </select>
              </>}
              {(f.tipo_visita==='entrega'||f.tipo_visita==='combinada')&&(
                <textarea placeholder="Facturas/documentos a entregar (números por cliente) *" value={f.facturas_entrega} onChange={e=>setF(x=>({...x,facturas_entrega:e.target.value}))} rows={2} style={{padding:'9px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1,resize:'vertical'}}/>
              )}
              {f.tipo_visita==='otra'&&(
                <div style={{fontSize:11,color:C.orangeText,background:C.orangeTint,border:`1px solid ${C.orangeBorder}`,borderRadius:R.md,padding:'8px 10px'}}>Las "otras gestiones" requieren autorización previa del jefe inmediato — quedará <b>pendiente de aprobación</b>.</div>
              )}
              <input type="number" min="0" placeholder="Kilometraje estimado (km, ida y vuelta)" value={f.km} onChange={e=>setF(x=>({...x,km:e.target.value}))} style={{padding:'9px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1}}/>
              <textarea placeholder="Notas adicionales" value={f.notas} onChange={e=>setF(x=>({...x,notas:e.target.value}))} rows={2} style={{padding:'9px',border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,background:C.bg1,color:C.t1,resize:'vertical'}}/>
            </div>
            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button onClick={()=>setModal(false)} style={{flex:1,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:9,padding:'9px',fontSize:13,fontWeight:700,color:C.t2,cursor:'pointer'}}>Cancelar</button>
              <button disabled={saving||!f.empresa||!f.fecha} onClick={crear} style={{flex:1,background:C.blue,border:'none',borderRadius:9,padding:'9px',fontSize:13,fontWeight:700,color:'#fff',cursor:'pointer',opacity:(saving||!f.empresa||!f.fecha)?.6:1}}>{saving?'Guardando…':'Agregar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SIBILA (leads de redes sociales) ──────────────────────────────────────────
// Íconos SVG de canal (stroke 1.5) — WhatsApp verde / Instagram morado / Messenger azul (DS v3, sin emoji).
const CANAL_ICON = {
  whatsapp:  <path d="M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 21l2.1-5.4A8.5 8.5 0 1 1 21 11.5z"/>,
  instagram: <><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><line x1="17.5" y1="6.5" x2="17.5" y2="6.5"/></>,
  messenger: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>,
};
const CANALES_SIB = {
  whatsapp:  { icon:CANAL_ICON.whatsapp,  label:'WhatsApp',  color:C.green, text:C.greenText, tint:C.greenTint, border:C.greenBorder },
  instagram: { icon:CANAL_ICON.instagram, label:'Instagram', color:'#6D28D9', text:'#6D28D9', tint:'#F5F3FF', border:'#DDD6FE' },
  messenger: { icon:CANAL_ICON.messenger, label:'Messenger', color:C.info,  text:C.infoText,  tint:C.infoTint,  border:C.infoBorder },
};
const REDES_SIB = [
  {v:'facebook_ad',        l:'Anuncio Facebook'},
  {v:'instagram_ad',       l:'Anuncio Instagram'},
  {v:'instagram_organico', l:'Instagram orgánico'},
  {v:'messenger',          l:'Messenger'},
  {v:'whatsapp_organico',  l:'WhatsApp orgánico'},
  {v:'referido',           l:'Referido'},
  {v:'otro',               l:'Otro'},
];
const REDES_LBL = Object.fromEntries(REDES_SIB.map(r=>[r.v,r.l]));
const ESTADOS_SIB = [
  {id:'nuevo',        l:'Nuevo',          c:C.blue,   text:C.blue,       tint:C.primaryTint, border:C.primaryBorder},
  {id:'contactado',   l:'Contactado',     c:C.info,   text:C.infoText,   tint:C.infoTint,    border:C.infoBorder},
  {id:'prospecto',    l:'Prospecto real', c:C.orange, text:C.orangeText, tint:C.orangeTint,  border:C.orangeBorder},
  {id:'convertido',   l:'Convertido',     c:C.green,  text:C.greenText,  tint:C.greenTint,   border:C.greenBorder},
  {id:'no_respondio', l:'No respondió',   c:C.t4,     text:C.t3,         tint:C.bg3,         border:C.border},
  {id:'perdido',      l:'Perdido',        c:C.red,    text:C.redText,    tint:C.redTint,     border:C.redBorder},
  {id:'invalido',     l:'Inválido',       c:C.t4,     text:C.t3,         tint:C.bg3,         border:C.border},
];
const ESTADO_SIB = Object.fromEntries(ESTADOS_SIB.map(e=>[e.id,e]));
// D1 (Sprint 1): razones de invalidez — catalogo cerrado, razon OBLIGATORIA.
// La conversion oficial se mide sobre leads validos (totales - invalidos).
const RAZONES_INVALIDO = [
  ['DUPLICADO','Duplicado'], ['NO_APLICA','No aplica'], ['SIN_NECESIDAD','Sin necesidad'],
  ['DATOS_FALSOS','Datos falsos'], ['FUERA_DE_COBERTURA','Fuera de cobertura'],
  ['PROVEEDOR_SPAM','Proveedor / spam'], ['OTRO','Otro (detallar)'],
];

function Sibila({ usuario, empresaActiva }) {
  const [tab, setTab]         = useState('bandeja');
  // D1: modal de lead invalido — razon obligatoria antes de marcar.
  // (estas declaraciones vivian por error en Seguimiento: NEXA tronaba con
  // ReferenceError al abrir — el bug que dejaba a Claudia fuera)
  const [modalInvalido, setModalInvalido] = useState(null); // {leadId}
  const [razonInv, setRazonInv]           = useState('');
  const [razonInvDet, setRazonInvDet]     = useState('');
  const [leads, setLeads]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel]         = useState(null);
  const [msgs, setMsgs]       = useState([]);
  const [fEstado, setFEstado] = useState([]);   // multi ([] = todos)
  const [fCanal, setFCanal]   = useState([]);
  const [fRed, setFRed]       = useState([]);
  const [busq, setBusq]       = useState('');
  const [modalNuevo, setModalNuevo] = useState(false);
  const nuevoVacio = {nombre:'',canal:'whatsapp',telefono:'',red_origen:'whatsapp_organico',necesidad:'',mensaje_inicial:''};
  const [fNuevo, setFNuevo]   = useState(nuevoVacio);
  const [saving, setSaving]   = useState(false);
  const [vendedores, setVendedores] = useState([]);
  const [nuevoMsg, setNuevoMsg] = useState('');
  const [savingMsg, setSavingMsg] = useState(false);
  const [subiendo, setSubiendo] = useState(null);   // nombre del archivo que va en camino
  const fileRef = useRef(null);
  const [modalPlantilla, setModalPlantilla] = useState(false);
  const [plantillas, setPlantillas] = useState(null);      // null = no se han pedido
  const [plantillaSel, setPlantillaSel] = useState(null);
  const [paramsPl, setParamsPl] = useState([]);
  const [enviandoPl, setEnviandoPl] = useState(false);
  const [rebote, setRebote] = useState(false);     // Meta rechazo por la ventana de 24 h
  // Móvil: maestro-detalle (lista O chat, nunca los dos aplastados) — auditoría D
  const [angosto, setAngosto] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const fn = e => setAngosto(e.matches);
    mq.addEventListener ? mq.addEventListener('change', fn) : mq.addListener(fn);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', fn) : mq.removeListener(fn); };
  }, []);

  const cargar = async (sil=false) => {
    if (!sil) setLoading(true);
    let q = supabase.from('sibila_leads').select('*').order('ultimo_mensaje',{ascending:false}).limit(2000);
    if (empresaActiva && empresaActiva!=='all') q = q.eq('empresa_id', empresaActiva);
    const { data } = await q;
    if (data) setLeads(data);
    if (!sil) setLoading(false);
  };
  useEffect(()=>{ cargar(); },[empresaActiva]); // eslint-disable-line
  // Bandeja EN VIVO: refresco silencioso cada 7s (leads nuevos + conversación abierta),
  // como una bandeja multiagente de verdad — Claudia responde sin recargar nada.
  const selRefS = useRef(null); useEffect(()=>{ selRefS.current = sel; },[sel]);
  const chatRefS = useRef(null);
  useEffect(()=>{
    const t = setInterval(async ()=>{
      cargar(true);
      const s = selRefS.current;
      if (s?.id) {
        const { data } = await supabase.from('sibila_mensajes').select('*').eq('lead_id',s.id).order('created_at',{ascending:true});
        if (data) setMsgs(prev => data.length !== prev.length ? data : prev);
      }
    }, 7000);
    return ()=>clearInterval(t);
  },[empresaActiva]); // eslint-disable-line
  useEffect(()=>{ if (chatRefS.current) chatRefS.current.scrollTop = chatRefS.current.scrollHeight; },[msgs]);
  // Solo vendedores ACTIVOS de ventas; Carlos y Elia son de otras áreas (misma
  // exclusión que usan los reportes).
  useEffect(()=>{ const EXCLUIR=['Carlos','Elia'];
    supabase.from('radar_usuarios').select('nombre,rol').eq('activo',true).then(({data})=>setVendedores(
      (data||[]).filter(u=>['ventas','gerente_ventas'].includes(u.rol))
        .map(u=>u.nombre)
        .filter(n=>!EXCLUIR.includes(String(n||'').trim().split(' ')[0])))); },[]);

  const abrir = async (lead) => {
    setSel(lead);
    setRebote(false);   // el rechazo era de la conversacion anterior
    const { data } = await supabase.from('sibila_mensajes').select('*').eq('lead_id',lead.id).order('created_at',{ascending:true});
    setMsgs(data||[]);
    // Marcar como LEÍDO: apaga el punto verde de la lista al abrir la conversación.
    const ahora = new Date().toISOString();
    supabase.from('sibila_leads').update({ leido_at: ahora }).eq('id', lead.id).then(()=>{},()=>{});
    setLeads(ls=>ls.map(l=>l.id===lead.id?{...l,leido_at:ahora}:l));
  };
  const actualizar = async (id, upd) => {
    const { error } = await supabase.from('sibila_leads').update(upd).eq('id',id);
    if (error) { toast('No se pudo guardar: '+error.message); return; }
    setLeads(ls=>ls.map(l=>l.id===id?{...l,...upd}:l));
    setSel(s=>s?.id===id?{...s,...upd}:s);
  };
  // Asignar el lead a un vendedor: guarda el vendedor, crea/enlaza la oportunidad en el CRM
  // CON la conversación de NEXA en las notas (los vendedores NO entran a NEXA, así la ven),
  // y le dispara una NOTIFICACIÓN al vendedor. Un clic en la notif lo lleva a esa opp.
  const asignarVendedor = async (vendedor) => {
    if (!sel) return;
    const prev = sel.vendedor;
    await actualizar(sel.id, { vendedor: vendedor || null });
    if (!vendedor || vendedor === prev) return;
    const transcript = (msgs||[]).map(m=>`${m.direccion==='in'?'Cliente':'Nosotros'}: ${m.texto||''}`).join('\n').slice(0,4000);
    const origen = `Lead NEXA (${sel.red_origen||sel.canal||'redes'})${sel.telefono?` · ${sel.telefono}`:''}`;
    const notas = `${origen}\n\n--- Conversacion NEXA ---\n${transcript||'(sin mensajes registrados)'}`;
    // Cliente con el TELEFONO del lead: así el vendedor puede escribirle por WhatsApp
    // sin tener que agregar el número a mano. Reusa un cliente existente si ya tiene ese tel.
    let clienteId = null;
    if (sel.telefono) {
      const telDig = String(sel.telefono).replace(/[^0-9]/g,'');
      if (telDig.length >= 8) {
        const { data: ex } = await supabase.from('clientes').select('id,telefono').ilike('telefono',`%${telDig.slice(-8)}%`).limit(1).maybeSingle();
        if (ex) clienteId = ex.id;
      }
      if (!clienteId) {
        const { data: nc } = await supabase.from('clientes').insert({
          empresa: sel.nombre || sel.telefono, contacto: sel.nombre || null, telefono: sel.telefono,
          vendedor, estado:'prospecto', empresa_id: sel.empresa_id || 'honduras',
        }).select('id').single();
        clienteId = nc?.id || null;
      }
    }
    let oppId = sel.oportunidad_id;
    try {
      if (oppId) {
        await supabase.from('oportunidades').update({ vendedor, notas, ...(clienteId?{cliente_id:clienteId}:{}) }).eq('id', oppId);
      } else {
        const { data: opp, error } = await supabase.from('oportunidades').insert({
          titulo: sel.nombre || origen, cliente: sel.nombre || sel.telefono || 'Lead NEXA', cliente_id: clienteId,
          vendedor, etapa:'prospecto', valor: sel.valor || 0, notas,
          // D2 (Sprint 1): la necesidad elegida en NEXA ya no se pierde al convertir,
          // y el origen queda estructurado (antes solo sobrevivia como texto en notas).
          linea_negocio: sel.necesidad || null,
          origen: 'nexa',
          ultimo_seguimiento: new Date().toISOString().split('T')[0],
          empresa_id: sel.empresa_id || 'honduras',
        }).select().single();
        if (error) throw new Error(error.message);
        oppId = opp?.id;
        // Enlazar el lead con la opp (si la columna existe). No es crítico: si falla,
        // no mostramos error — la opp y la notificación ya quedaron creadas.
        if (oppId) {
          // D2: convertir tambien actualiza el estado — la tasa de conversion del
          // modulo dejaba de contar leads convertidos si nadie movia el select a mano.
          const { error: linkErr } = await supabase.from('sibila_leads').update({ oportunidad_id: oppId, estado: 'convertido' }).eq('id', sel.id);
          if (!linkErr) { setLeads(ls=>ls.map(l=>l.id===sel.id?{...l,oportunidad_id:oppId,estado:'convertido'}:l)); setSel(s=>s?.id===sel.id?{...s,oportunidad_id:oppId,estado:'convertido'}:s); }
        }
      }
      // 'leida' y 'opp_id' no son parametros de notificar -opp_id ni siquiera
      // es columna de la tabla- y venian arrastrados de la version vieja.
      await notificar({ para: vendedor, tipo:'prospecto_asignado',
        titulo: 'Nuevo prospecto de NEXA',
        mensaje: `Nuevo prospecto de NEXA: ${sel.nombre||sel.telefono||'lead'} — abrí para ver la conversación y responder`,
        modulo: 'crm', refId: oppId || null, orgId: usuario?.org_id || null });
      toast(`Asignado a ${String(vendedor).split(' ')[0]} — se le notificó y quedó en su CRM.`);
    } catch(e){ toast('Se asignó, pero no se pudo crear la oportunidad/notificación: '+(e?.message||e)); }
  };
  const crearLead = async () => {
    if (!fNuevo.nombre) return; setSaving(true);
    const ins = { ...fNuevo, contacto_id: fNuevo.telefono || ('manual_'+Date.now()),
      empresa_id:(empresaActiva&&empresaActiva!=='all')?empresaActiva:'honduras',
      estado:'nuevo', ultimo_mensaje_dir:'in', mensajes_count: fNuevo.mensaje_inicial?1:0 };
    const { data, error } = await supabase.from('sibila_leads').insert(ins).select().single();
    setSaving(false);
    if (error) { toast(error.message); return; }
    if (fNuevo.mensaje_inicial) await supabase.from('sibila_mensajes').insert({lead_id:data.id,direccion:'in',texto:fNuevo.mensaje_inicial});
    setLeads(ls=>[data,...ls]); setModalNuevo(false); setFNuevo(nuevoVacio);
  };
  // REABRIR LA CONVERSACION. Pasadas 24 horas del ultimo mensaje DEL CLIENTE,
  // WhatsApp rechaza cualquier texto libre: solo entra una plantilla aprobada por
  // Meta. No es un limite de NEXA, es la regla de la Cloud API. Las plantillas se
  // piden a Meta en el momento, asi que si aprueban una nueva aparece sola.
  const abrirPlantillas = async () => {
    setModalPlantilla(true); setPlantillaSel(null); setParamsPl([]);
    if (plantillas) return;
    const r = await fetch('/api/sibila', { method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ action:'plantillas' }) });
    const d = await r.json().catch(()=>({}));
    if (!d.ok) { setPlantillas([]); toast('No se pudieron leer las plantillas: '+(d.error||'error')); return; }
    setPlantillas(d.plantillas || []);
  };

  const enviarPlantilla = async () => {
    if (!plantillaSel || !sel) return;
    if (paramsPl.some(p => !String(p||'').trim())) return;
    setEnviandoPl(true);
    try {
      const r = await fetch('/api/sibila', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'reabrir', lead_id:sel.id, plantilla:plantillaSel.nombre,
          idioma:plantillaSel.idioma, params:paramsPl, usuario: usuario?.nombre||null }) });
      const d = await r.json().catch(()=>({}));
      if (!d.ok) { toast('No se pudo enviar: '+(d.error||'error')); return; }
      const upd = { ultimo_mensaje:new Date().toISOString(), ultimo_mensaje_dir:'out',
        ultimo_mensaje_texto: d.texto, mensajes_count:(sel.mensajes_count||0)+1 };
      setMsgs(m=>[...m, d.mensaje || { id:'tmp_'+Date.now(), lead_id:sel.id, direccion:'out',
        texto:d.texto, enviado_por:usuario?.nombre||null, created_at:new Date().toISOString() }]);
      setSel(s=>({...s,...upd})); setLeads(ls=>ls.map(l=>l.id===sel.id?{...l,...upd}:l));
      setModalPlantilla(false);
      toast('Plantilla enviada. Cuando el cliente conteste se reabre la ventana de 24 horas.');
    } catch(e){ toast('No se pudo enviar: '+(e?.message||e)); }
    finally { setEnviandoPl(false); }
  };

  // Mandarle un archivo al lead (ficha tecnica, foto del equipo, cotizacion).
  // Lo que este escrito en la caja de texto se manda como pie del archivo, para
  // que no salga un adjunto pelado sin contexto.
  const enviarArchivo = async (file) => {
    if (!file || !sel) return;
    // 25 MB es el techo practico: el archivo viaja en base64 dentro del JSON, que
    // lo infla un tercio. Fichas tecnicas, fotos y cotizaciones caben de sobra.
    const MAX_MB = 25;
    if (file.size > MAX_MB * 1048576) { toast(`El archivo pesa ${(file.size/1048576).toFixed(1)} MB y el maximo son ${MAX_MB} MB.`); return; }
    setSubiendo(file.name);
    try {
      const b64 = await new Promise((ok, mal) => {
        const fr = new FileReader();
        fr.onload = () => ok(String(fr.result).split(',').pop());
        fr.onerror = () => mal(new Error('No se pudo leer el archivo'));
        fr.readAsDataURL(file);
      });
      const caption = nuevoMsg.trim();
      const r = await fetch('/api/sibila', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'responder_archivo', lead_id:sel.id, archivo:b64,
          nombre:file.name, mime:file.type, caption, usuario: usuario?.nombre||null }) });
      const d = await r.json().catch(()=>({}));
      if (!d.ok) { toast('No se pudo enviar: '+(d.error||'error')); return; }
      const upd = { ultimo_mensaje:new Date().toISOString(), ultimo_mensaje_dir:'out',
        ultimo_mensaje_texto: caption || `[${d.tipo === 'document' ? 'archivo' : d.tipo}]`,
        mensajes_count:(sel.mensajes_count||0)+1 };
      setMsgs(m=>[...m, d.mensaje || { id:'tmp_'+Date.now(), lead_id:sel.id, direccion:'out',
        texto: caption || `[${d.tipo}] ${file.name}`, media_url:d.url, media_tipo:d.tipo,
        enviado_por: usuario?.nombre||null, created_at:new Date().toISOString() }]);
      setSel(s=>({...s,...upd})); setLeads(ls=>ls.map(l=>l.id===sel.id?{...l,...upd}:l));
      setNuevoMsg('');
      toast(d.como_documento
        ? 'Archivo enviado como documento (WhatsApp no acepto ese formato en su tipo original).'
        : 'Archivo enviado al lead.');
    } catch(e) { toast('No se pudo enviar: '+(e?.message||e)); }
    finally { setSubiendo(null); if (fileRef.current) fileRef.current.value=''; }
  };

  const registrarMsg = async (dir) => {
    if (!nuevoMsg.trim()||!sel) return; setSavingMsg(true);
    const texto = nuevoMsg.trim();
    const conectado = ['whatsapp','messenger','instagram'].includes(sel.canal) && sel.contacto_id && !String(sel.contacto_id).startsWith('manual_');
    // Salida por canal conectado: el API ENVÍA de verdad (WhatsApp/Messenger/IG) y registra.
    if (dir==='out' && conectado) {
      try {
        const r = await fetch('/api/sibila',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'responder',lead_id:sel.id,texto,usuario:usuario?.nombre||null})});
        const d = await r.json().catch(()=>({}));
        if (!d.ok) {
          // Meta devuelve esto de varias formas segun el canal y la version.
          // Si es la ventana de 24 h, se ofrece la plantilla en vez de dejar
          // al vendedor con un error que no le dice que hacer.
          const e = String(d.error||'');
          if (/24\s*hour|24\s*horas|131047|re-?engage|outside.*window|fuera.*ventana/i.test(e)) {
            setRebote(true);
            toast('Pasaron más de 24 horas: hay que reabrir con una plantilla.');
          } else {
            toast('No se pudo enviar: '+(e||'error'));
          }
          setSavingMsg(false); return;
        }
        setRebote(false);
        // ultimo_mensaje_texto lo escribe un trigger en la base; aca se refleja
        // igual para que la bandeja no siga mostrando el mensaje anterior hasta
        // la proxima recarga.
        const upd = { ultimo_mensaje:new Date().toISOString(), ultimo_mensaje_dir:'out', ultimo_mensaje_texto:texto, mensajes_count:(sel.mensajes_count||0)+1 };
        setMsgs(m=>[...m,{id:'tmp_'+Date.now(),lead_id:sel.id,direccion:'out',texto,enviado_por:usuario?.nombre||null,created_at:new Date().toISOString()}]);
        setSel(s=>({...s,...upd})); setLeads(ls=>ls.map(l=>l.id===sel.id?{...l,...upd}:l));
        setNuevoMsg(''); setSavingMsg(false); toast('Mensaje enviado al lead ✅');
        return;
      } catch(e){ toast('No se pudo enviar: '+e.message); setSavingMsg(false); return; }
    }
    // Registro manual (lead sin canal conectado o mensaje entrante anotado a mano)
    const { data, error } = await supabase.from('sibila_mensajes').insert({lead_id:sel.id,direccion:dir,texto}).select().single();
    if (error) { toast('No se pudo guardar: '+error.message); setSavingMsg(false); return; }
    const upd = { ultimo_mensaje:new Date().toISOString(), ultimo_mensaje_dir:dir, ultimo_mensaje_texto:texto, mensajes_count:(sel.mensajes_count||0)+1 };
    await supabase.from('sibila_leads').update(upd).eq('id',sel.id);
    setMsgs(m=>[...m,data]); setSel(s=>({...s,...upd})); setLeads(ls=>ls.map(l=>l.id===sel.id?{...l,...upd}:l));
    setNuevoMsg(''); setSavingMsg(false);
  };

  const leadsF = leads.filter(l=>{
    if (fEstado.length && !fEstado.includes(l.estado)) return false;
    if (fCanal.length && !fCanal.includes(l.canal)) return false;
    if (fRed.length && !fRed.includes(l.red_origen)) return false;
    if (busq){ const b=busq.toLowerCase(); if(!(l.nombre||'').toLowerCase().includes(b)&&!(l.telefono||'').toLowerCase().includes(b)&&!(l.necesidad||'').toLowerCase().includes(b)) return false; }
    return true;
  });

  // ── Analíticas ──
  const byKey = (arr,key,lblMap)=>{ const m={}; arr.forEach(l=>{const k=l[key]||'(s/d)';m[k]=(m[k]||0)+1;}); return Object.entries(m).sort((a,b)=>b[1]-a[1]).map(([k,n])=>({k:lblMap?.[k]||k,n})); };
  const total       = leads.length;
  // CDS v1 (Sprint 1): convertido = tiene oportunidad creada (no un select manual);
  // la conversion se mide sobre leads VALIDOS (totales - invalidos).
  const convertidos = leads.filter(l=>l.oportunidad_id || l.estado==='convertido');
  const invalidosN  = leads.filter(l=>l.estado==='invalido').length;
  const validosN    = Math.max(0, total - invalidosN);
  const fantasma    = leads.filter(l=>l.estado==='no_respondio').length;
  const ingresos    = convertidos.reduce((s,l)=>s+(l.valor||0),0);
  const convRate    = validosN? Math.round(convertidos.length/validosN*100):0;
  const fantRate    = total? Math.round(fantasma/total*100):0;
  const porRed      = byKey(leads,'red_origen',REDES_LBL);
  const porCanal    = byKey(leads,'canal',Object.fromEntries(Object.entries(CANALES_SIB).map(([k,v])=>[k,v.label])));
  const porNecesidad= byKey(leads.filter(l=>l.necesidad),'necesidad');
  const maxRed = Math.max(1,...porRed.map(x=>x.n));
  const maxCanal = Math.max(1,...porCanal.map(x=>x.n));
  const maxNec = Math.max(1,...porNecesidad.map(x=>x.n));
  // Serie temporal: leads por día (últimos 30 días), apilados por canal.
  const CANAL_COLOR = { whatsapp:C.green, instagram:C.pink, messenger:C.blue };
  const dias30 = [...Array(30)].map((_,i)=>{ const d=new Date(); d.setDate(d.getDate()-(29-i)); return d.toISOString().slice(0,10); });
  const serieDias = dias30.map(f=>{
    const del = leads.filter(l=>(l.created_at||'').slice(0,10)===f);
    return { f, total: del.length,
      seg: Object.keys(CANAL_COLOR).map(c=>({ c, n: del.filter(l=>l.canal===c).length })),
      otros: del.filter(l=>!CANAL_COLOR[l.canal]).length };
  });
  const maxDia = Math.max(1,...serieDias.map(x=>x.total));
  const leads7  = leads.filter(l=>(l.created_at||'')>=dias30[23]).length;
  const leads30 = leads.filter(l=>(l.created_at||'')>=dias30[0]).length;

  const Bar = ({label,n,max,color}) => (
    <div style={{marginBottom:9}}>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:C.t2,marginBottom:3}}><span>{label}</span><span className="radar-num" style={{fontWeight:700,color:C.t1}}>{n}</span></div>
      <div style={{height:8,background:C.bg3,borderRadius:R.pill,overflow:'hidden'}}><div style={{height:'100%',width:`${Math.round(n/max*100)}%`,background:color||C.blue,borderRadius:R.pill,transition:'width .3s'}}/></div>
    </div>
  );
  const Card = ({titulo,children}) => (
    <div className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:18,boxShadow:SH.sm}}>
      <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1,marginBottom:14}}>{titulo}</div>{children}
    </div>
  );
  const Kpi = ({label,val,color}) => (
    <div className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:'14px 16px 13px',flex:1,minWidth:150,boxShadow:SH.sm}}>
      <div style={{fontSize:11,color:C.t3,fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>{label}</div>
      <div className="radar-num" style={{fontSize:26,fontWeight:700,letterSpacing:'-0.025em',lineHeight:1.05,color:color||C.t1}}>{val}</div>
    </div>
  );

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* Header */}
      <div style={{padding:'12px 20px',borderBottom:`1px solid ${C.border}`,background:C.bg2,display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:14}}>
          <div style={{fontSize:17,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,display:'flex',alignItems:'center',gap:8}}><span style={{width:26,height:26,borderRadius:R.sm,background:C.primaryTint,border:`1px solid ${C.primaryBorder}`,color:C.blue,display:'inline-flex',alignItems:'center',justifyContent:'center'}}><CIcon d={CI.inbox} size={15}/></span>NEXA <span style={{fontSize:12,fontWeight:500,color:C.t4}}>· Leads de redes</span></div>
          <Seg value={tab} onChange={setTab} items={[['bandeja','Bandeja',CI.inbox],['analiticas','Analíticas',CI.chart]]}/>
        </div>
        <button onClick={()=>setModalNuevo(true)} className="radar-btn" style={{display:'inline-flex',alignItems:'center',gap:6,background:C.blue,color:'#fff',border:'none',borderRadius:R.md,padding:'8px 16px',fontSize:13,fontWeight:600,cursor:'pointer',boxShadow:`${SH.primary}, inset 0 1px 0 rgba(255,255,255,.12)`}}><CIcon d={<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>}/>Lead manual</button>
      </div>

      {tab==='analiticas' ? (
        <div style={{flex:1,overflowY:'auto',padding:20}}>
          <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:16}}>
            <Kpi label="Leads totales" val={total}/>
            <Kpi label="Últimos 7 días" val={leads7} color={C.blue}/>
            <Kpi label="Últimos 30 días" val={leads30} color={C.indigo}/>
            <Kpi label="Válidos" val={validosN} color={C.blue}/>
            <Kpi label="Inválidos" val={invalidosN} color={C.t4}/>
            <Kpi label="Convertidos" val={convertidos.length} color={C.green}/>
            <Kpi label="Conversión (sobre válidos)" val={`${convRate}%`} color={C.green}/>
            <Kpi label="No respondió" val={`${fantRate}%`} color={C.t4}/>
            <Kpi label="Ingresos convertidos" val={fmt(ingresos)} color={C.green}/>
          </div>

          {/* Ingreso de leads por día, apilado por canal */}
          <div className="radar-card" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:18,marginBottom:14,boxShadow:SH.sm}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8,marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:700,letterSpacing:'-0.005em',color:C.t1}}>Ingreso de leads — últimos 30 días</div>
              <div style={{display:'flex',gap:12,fontSize:11,color:C.t3}}>
                {Object.entries(CANAL_COLOR).map(([c,col])=>(
                  <span key={c} style={{display:'flex',alignItems:'center',gap:5}}><span style={{width:10,height:10,borderRadius:3,background:col,display:'inline-block'}}/>{CANALES_SIB[c]?.label||c}</span>
                ))}
                <span style={{display:'flex',alignItems:'center',gap:5}}><span style={{width:10,height:10,borderRadius:3,background:C.t4,display:'inline-block'}}/>Otros</span>
              </div>
            </div>
            <div style={{display:'flex',alignItems:'flex-end',gap:3,height:150}}>
              {serieDias.map(d=>{
                const dd = new Date(d.f+'T12:00:00');
                return (
                  <div key={d.f} title={`${dd.toLocaleDateString('es-HN',{weekday:'short',day:'numeric',month:'short'})}: ${d.total} lead${d.total!==1?'s':''}`}
                    style={{flex:1,display:'flex',flexDirection:'column',justifyContent:'flex-end',height:'100%',cursor:'default'}}>
                    {d.total>0&&<div style={{fontSize:9,fontWeight:800,color:C.t3,textAlign:'center',marginBottom:2}}>{d.total}</div>}
                    <div style={{display:'flex',flexDirection:'column-reverse',borderRadius:'4px 4px 0 0',overflow:'hidden'}}>
                      {d.seg.map(s=>s.n>0&&<div key={s.c} style={{height:Math.max(3,Math.round(s.n/maxDia*118)),background:CANAL_COLOR[s.c]}}/>)}
                      {d.otros>0&&<div style={{height:Math.max(3,Math.round(d.otros/maxDia*118)),background:C.t4}}/>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{display:'flex',gap:3,marginTop:5}}>
              {serieDias.map((d,i)=>{
                const dd=new Date(d.f+'T12:00:00');
                return <div key={d.f} style={{flex:1,textAlign:'center',fontSize:8,color:i%5===0||i===29?C.t4:'transparent'}}>{dd.getDate()}/{dd.getMonth()+1}</div>;
              })}
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:14}}>
            <Card titulo="Leads por red de origen">{porRed.length?porRed.map(r=><Bar key={r.k} label={r.k} n={r.n} max={maxRed} color={C.pink}/>):<div style={{color:C.t4,fontSize:12}}>Sin datos aún</div>}</Card>
            <Card titulo="Leads por canal">{porCanal.length?porCanal.map(r=><Bar key={r.k} label={r.k} n={r.n} max={maxCanal} color={C.blue}/>):<div style={{color:C.t4,fontSize:12}}>Sin datos aún</div>}</Card>
            <Card titulo="Qué necesitan">{porNecesidad.length?porNecesidad.map(r=><Bar key={r.k} label={r.k} n={r.n} max={maxNec} color={C.indigo}/>):<div style={{color:C.t4,fontSize:12}}>Sin datos aún</div>}</Card>
            <Card titulo="Por estado">{ESTADOS_SIB.map(e=>{const n=leads.filter(l=>l.estado===e.id).length;return <Bar key={e.id} label={e.l} n={n} max={Math.max(1,total)} color={e.c}/>;})}</Card>
          </div>
        </div>
      ) : (
        <div style={{flex:1,display:'flex',overflow:'hidden'}}>
          {/* Conversaciones — columna estilo WhatsApp Web */}
          <div style={{width:angosto?'100%':340,minWidth:angosto?0:280,display:(angosto&&sel)?'none':'flex',flexDirection:'column',overflow:'hidden',borderRight:angosto?'none':`1px solid ${C.border}`,background:C.bg2,flexShrink:0}}>
            <div style={{padding:'10px 12px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <div style={{position:'relative',marginBottom:7}}>
                <span style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:C.t4,pointerEvents:'none',display:'inline-flex'}}><CIcon d={CI.search} size={14}/></span>
                <input className="radar-input" value={busq} onChange={e=>setBusq(e.target.value)} placeholder="Buscar nombre / tel / necesidad"
                  style={{width:'100%',boxSizing:'border-box',padding:'8px 12px 8px 32px',border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:13,background:C.bg1,color:C.t1,outline:'none'}}/>
              </div>
              <div style={{display:'flex',gap:5}}>
                <MultiSelect label="Estado" value={fEstado} onChange={setFEstado} width={180}
                  options={ESTADOS_SIB.map(e=>({value:e.id, label:e.l}))}/>
                <MultiSelect label="Canal" value={fCanal} onChange={setFCanal} width={180}
                  options={Object.entries(CANALES_SIB).map(([k,v])=>({value:k, label:v.label}))}/>
                <MultiSelect label="Red" value={fRed} onChange={setFRed} width={180}
                  options={REDES_SIB.map(r=>({value:r.v, label:r.l}))}/>
              </div>
            </div>
            <div style={{flex:1,overflowY:'auto'}}>
              {loading ? <div style={{textAlign:'center',color:C.t4,marginTop:30}}>Cargando…</div>
                : leadsF.length===0 ? <div style={{textAlign:'center',color:C.t4,marginTop:30,fontSize:13,padding:'0 20px'}}>Sin leads. Los mensajes al 9826-8710 y a las redes caerán aquí solos.</div>
                : leadsF.map(l=>{
                  const cn = CANALES_SIB[l.canal]||{icon:CANAL_ICON.whatsapp,color:C.t4,tint:C.bg3,text:C.t3,border:C.border}; const es = ESTADO_SIB[l.estado]||{l:l.estado,text:C.t3,tint:C.bg3,border:C.border};
                  // No leído = el último mensaje es del cliente y es posterior a la última vez que se abrió el chat.
                  const noLeido = l.ultimo_mensaje_dir==='in' && (!l.leido_at || (l.ultimo_mensaje && l.ultimo_mensaje > l.leido_at));
                  const activo = sel?.id===l.id;
                  return (
                    <div key={l.id} onClick={()=>abrir(l)} className={activo?'':'radar-row'} style={{display:'flex',gap:10,alignItems:'center',padding:'11px 14px',cursor:'pointer',transition:'background .12s ease',
                      ...(activo?{background:C.primaryTint}:{}),borderLeft:`3px solid ${activo?C.blue:'transparent'}`,borderBottom:`1px solid ${C.bg3}`}}>
                      <div style={{width:42,height:42,borderRadius:'50%',background:cn.tint,border:`1px solid ${cn.border}`,color:cn.text,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><CIcon d={cn.icon} size={19}/></div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',justifyContent:'space-between',gap:6}}>
                          <span style={{fontSize:13.5,fontWeight:noLeido?700:600,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{l.nombre||l.telefono||'(sin nombre)'}</span>
                          <span className="radar-num" style={{fontSize:10,color:noLeido?C.greenText:C.t4,fontWeight:noLeido?700:400,flexShrink:0}}>{l.ultimo_mensaje?new Date(l.ultimo_mensaje).toLocaleDateString('es-HN',{day:'numeric',month:'short'}):''}</span>
                        </div>
                        <div style={{display:'flex',justifyContent:'space-between',gap:6,marginTop:3,alignItems:'center'}}>
                          <span style={{fontSize:11.5,color:C.t4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{l.ultimo_mensaje_dir==='out'?'↩ ':''}{l.ultimo_mensaje_texto||l.mensaje_inicial||REDES_LBL[l.red_origen]||'—'}</span>
                          <span style={{fontSize:9,fontWeight:600,color:es.text,background:es.tint,border:`1px solid ${es.border}`,borderRadius:R.sm,padding:'1px 6px',flexShrink:0,whiteSpace:'nowrap'}}>{es.l}</span>
                        </div>
                      </div>
                      {noLeido&&<span style={{width:9,height:9,borderRadius:'50%',background:C.green,flexShrink:0}}/>}
                    </div>
                  );
                })}
            </div>
          </div>

          {/* CHAT GRANDE — el WhatsApp de trabajo */}
          {!sel ? (angosto ? null : (
            <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:C.bg1,color:C.t4}}>
              <div style={{width:64,height:64,borderRadius:R.lg,background:C.bg3,border:`1px solid ${C.border}`,color:C.t4,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:14}}><CIcon d={CI.phone} size={28}/></div>
              <div style={{fontSize:15,fontWeight:700,color:C.t3}}>Seleccioná una conversación</div>
              <div style={{fontSize:12.5,marginTop:4}}>Los mensajes de WhatsApp, Facebook e Instagram llegan aquí en vivo.</div>
            </div>
          )) : (()=>{ const conectado = ['whatsapp','messenger','instagram'].includes(sel.canal) && sel.contacto_id && !String(sel.contacto_id).startsWith('manual_');
                     const cn = CANALES_SIB[sel.canal]||{icon:CANAL_ICON.whatsapp,color:C.t4,tint:C.bg3,text:C.t3,border:C.border}; return (
            <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
              {/* Header del chat: identidad + gestión del lead en una barra */}
              <div style={{padding:'9px 16px',background:C.bg2,borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',flexShrink:0}}>
                {angosto && (
                  <button onClick={()=>setSel(null)} title="Volver a la bandeja"
                    style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'6px 11px',fontSize:15,fontWeight:600,color:C.t2,cursor:'pointer',flexShrink:0}}>←</button>
                )}
                <div style={{width:38,height:38,borderRadius:'50%',background:cn.tint,border:`1px solid ${cn.border}`,color:cn.text,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><CIcon d={cn.icon} size={17}/></div>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:14.5,fontWeight:700,letterSpacing:'-0.01em',color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{sel.nombre||sel.telefono||'(sin nombre)'}</div>
                  <div style={{fontSize:11,color:C.t4}}>{CANALES_SIB[sel.canal]?.label||sel.canal} · {REDES_LBL[sel.red_origen]||sel.red_origen||'—'}{sel.telefono?` · ${sel.telefono}`:''} {conectado&&<span style={{color:C.greenText,fontWeight:600}}>· ● en vivo</span>}</div>
                </div>
                <div style={{marginLeft:'auto',display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
                  {(()=>{ const et=ESTADO_SIB[sel.estado]||{}; return (
                  <select value={sel.estado||'nuevo'} onChange={e=>{
                      const v = e.target.value;
                      // D1: invalido exige razon — abre el modal en vez de guardar directo
                      if (v === 'invalido') { setRazonInv(''); setRazonInvDet(''); setModalInvalido({leadId: sel.id}); return; }
                      actualizar(sel.id,{estado:v});
                    }}
                    style={{padding:'6px 9px',border:`1px solid ${et.border||C.border}`,borderRadius:R.md,fontSize:12,fontWeight:600,background:et.tint||C.bg1,color:et.text||C.t2,outline:'none'}}>
                    {ESTADOS_SIB.map(e=><option key={e.id} value={e.id}>{e.l}</option>)}
                  </select>
                  );})()}
                  <select value={sel.necesidad||''} onChange={e=>actualizar(sel.id,{necesidad:e.target.value||null})} style={{padding:'6px 9px',border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:12,background:C.bg1,color:sel.necesidad?C.t1:C.t4,maxWidth:150,outline:'none'}}>
                    <option value="">Necesidad…</option>{ORG.lineasNegocio.map(l=><option key={l} value={l}>{l}</option>)}
                  </select>
                  <select value={sel.vendedor||''} onChange={e=>asignarVendedor(e.target.value||null)} style={{padding:'6px 9px',border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:12,background:C.bg1,color:sel.vendedor?C.t1:C.t4,maxWidth:140,outline:'none'}}>
                    <option value="">Asignar a…</option>{vendedores.map(v=><option key={v} value={v}>{v}</option>)}
                  </select>
                  <input type="number" placeholder="$ est." defaultValue={sel.valor||''} onBlur={e=>actualizar(sel.id,{valor:e.target.value?parseFloat(e.target.value):null})} className="radar-num" style={{width:80,padding:'6px 9px',border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:12,background:C.bg1,color:C.t1,outline:'none'}}/>
                  <button onClick={()=>setSel(null)} title="Cerrar" style={{background:'none',border:'none',color:C.t4,fontSize:20,cursor:'pointer',padding:'0 4px'}}>×</button>
                </div>
              </div>
              {sel.anuncio && <div style={{background:C.lob.servicio.bg,borderBottom:`1px solid ${C.lob.servicio.border}`,padding:'6px 16px',fontSize:11,color:C.lob.servicio.text,fontWeight:600,flexShrink:0}}>📣 Vino del anuncio: {sel.anuncio}</div>}

              {/* Mensajes a pantalla completa */}
              <div ref={chatRefS} style={{flex:1,overflowY:'auto',padding:'20px max(24px,6%)'}}>
                {msgs.length===0 ? <div style={{color:C.t4,fontSize:13,textAlign:'center',marginTop:40}}>Sin mensajes todavía</div>
                  : msgs.map((m,mi)=>(
                    <Fragment key={m.id}>
                    {/* Separador de dia: solo cuando cambia respecto del mensaje anterior */}
                    {(mi===0 || diaISO(m.created_at) !== diaISO(msgs[mi-1].created_at)) && <SepDia ts={m.created_at}/>}
                    <div style={{display:'flex',justifyContent:m.direccion==='out'?'flex-end':'flex-start',marginBottom:8}}>
                      <div style={{maxWidth:'68%',padding:'8px 13px',borderRadius:m.direccion==='out'?'14px 14px 4px 14px':'14px 14px 14px 4px',fontSize:13.5,lineHeight:1.45,
                        background:m.direccion==='out'?'#D9FDD3':'#FFFFFF',color:'#111B21',boxShadow:'0 1px 1.5px rgba(0,0,0,.1)',whiteSpace:'pre-wrap',wordBreak:'break-word'}}>
                        {m.media_url && (
                          (m.media_tipo==='image'||m.media_tipo==='sticker')
                            ? <img src={m.media_url} alt="" onClick={()=>window.open(m.media_url,'_blank')} style={{maxWidth:'100%',maxHeight:280,borderRadius:8,display:'block',cursor:'pointer',marginBottom:m.texto&&!/^\[\w+\]$/.test(m.texto)?6:0}}/>
                          : m.media_tipo==='audio'
                            ? <audio controls src={m.media_url} style={{maxWidth:260,display:'block',marginBottom:6}}/>
                          : m.media_tipo==='video'
                            ? <video controls src={m.media_url} style={{maxWidth:'100%',maxHeight:280,borderRadius:8,display:'block',marginBottom:6}}/>
                          : <a href={m.media_url} target="_blank" rel="noreferrer" style={{display:'block',color:'#027EB5',fontWeight:700,marginBottom:6}}>📎 Descargar archivo</a>
                        )}
                        {m.texto && !(m.media_url && /^\[\w+\]$/.test(m.texto)) && m.texto}
                        <div title={m.created_at?new Date(m.created_at).toLocaleString('es-HN',{weekday:'long',day:'numeric',month:'long',year:'numeric',hour:'2-digit',minute:'2-digit'}):''}
                          style={{fontSize:9.5,color:'#667781',textAlign:'right',marginTop:3}}>
                          {m.direccion==='out' && m.enviado_por && <span style={{fontWeight:700,marginRight:6}}>{m.enviado_por}</span>}
                          {m.created_at?new Date(m.created_at).toLocaleTimeString('es-HN',{hour:'2-digit',minute:'2-digit'}):''}
                        </div>
                      </div>
                    </div>
                    </Fragment>
                  ))}
              </div>

              {/* Composer */}
              <div style={{padding:'10px 16px',background:C.bg2,borderTop:`1px solid ${C.border}`,flexShrink:0}}>
                {/* El aviso NO se adivina desde el reloj de esta pantalla. Antes se
                    calculaba aca y se bloqueaba la caja de texto, y bastaba con que
                    faltara un mensaje en la lista para dejar a alguien sin poder
                    escribirle a un cliente con el que si podia hablar. Ahora solo
                    aparece cuando Meta rechaza de verdad, con el motivo que dio. */}
                {rebote && (
                  <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',
                    background:C.lob.servicio.bg,border:`1px solid ${C.lob.servicio.border}`,
                    borderRadius:R.md,padding:'9px 12px',marginBottom:9}}>
                    <div style={{flex:'1 1 260px',fontSize:11.5,color:C.lob.servicio.text}}>
                      <b>WhatsApp no dejó pasar el mensaje.</b> Pasaron más de 24 horas desde
                      que el cliente escribió, así que solo entra una plantilla aprobada.
                      Tu texto sigue escrito abajo.
                    </div>
                    <Btn size="sm" onClick={abrirPlantillas}>Reabrir conversación</Btn>
                  </div>
                )}
                <div style={{display:'flex',gap:8,alignItems:'center'}}>
                  <input ref={fileRef} type="file" style={{display:'none'}}
                    onChange={e=>{ const f=e.target.files?.[0]; if(f) enviarArchivo(f); }}/>
                  {conectado && (
                    <button className="radar-btn" disabled={!!subiendo||savingMsg} onClick={()=>fileRef.current?.click()}
                      title="Adjuntar archivo (foto, PDF, ficha tecnica)"
                      style={{background:C.bg1,border:`1px solid ${C.border}`,borderRadius:'50%',width:44,height:44,flexShrink:0,cursor:subiendo?'default':'pointer',color:C.t2,fontSize:17,display:'inline-flex',alignItems:'center',justifyContent:'center',opacity:subiendo?.5:1}}>
                      {subiendo ? '...' : '📎'}
                    </button>
                  )}
                  <input value={nuevoMsg} onChange={e=>setNuevoMsg(e.target.value)} autoFocus
                    onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){ e.preventDefault(); registrarMsg('out'); } }}
                    placeholder={conectado?'Escribí tu respuesta y presioná Enter…':'Anotar nota (lead manual, no envía)…'}
                    style={{flex:1,padding:'12px 16px',border:`1px solid ${C.border}`,borderRadius:R.pill,fontSize:14,background:C.bg1,color:C.t1,outline:'none'}}/>
                  <button className="radar-btn" disabled={savingMsg||!nuevoMsg.trim()} onClick={()=>registrarMsg('out')} title="Enviar"
                    style={{background:C.blue,border:'none',borderRadius:'50%',width:44,height:44,cursor:savingMsg||!nuevoMsg.trim()?'default':'pointer',color:'#fff',flexShrink:0,display:'inline-flex',alignItems:'center',justifyContent:'center',opacity:savingMsg||!nuevoMsg.trim()?.5:1,boxShadow:savingMsg||!nuevoMsg.trim()?'none':SH.primary}}><CIcon d={CI.send} size={18} style={{marginLeft:-1}}/></button>
                </div>
                {subiendo && <div style={{marginTop:6,fontSize:11.5,color:C.t3}}>Enviando {subiendo}...</div>}
                {conectado && !subiendo && <div style={{marginTop:6,fontSize:10.5,color:C.t4}}>El texto que escribas se manda como pie del archivo adjunto.</div>}
                {!conectado&&<button disabled={savingMsg||!nuevoMsg.trim()} onClick={()=>registrarMsg('in')} style={{marginTop:5,background:'none',border:'none',color:C.t4,fontSize:10.5,cursor:'pointer',textDecoration:'underline'}}>anotar como mensaje ENTRANTE del cliente</button>}
              </div>
            </div>
          ); })()}
        </div>
      )}

      {/* REABRIR CONVERSACION — plantillas aprobadas por Meta */}
      <Modal open={modalPlantilla} onClose={()=>setModalPlantilla(false)}
        title="Reabrir conversación" width={560}>
        <div style={{display:'grid',gap:12}}>
          <div style={{fontSize:12.5,color:C.t3}}>
            Pasadas 24 horas desde el último mensaje del cliente, WhatsApp solo acepta
            plantillas aprobadas por Meta. Estas son las que tiene aprobadas el número de NEXA.
            Cuando el cliente conteste, se reabre la ventana y podés escribirle normal.
          </div>

          {plantillas === null ? (
            <div style={{fontSize:12.5,color:C.t4}}>Cargando plantillas…</div>
          ) : !plantillas.length ? (
            <div style={{fontSize:12.5,color:C.t3,background:C.bg1,border:`1px solid ${C.border}`,
              borderRadius:R.md,padding:12}}>
              No hay plantillas aprobadas para este número. Se crean en el Administrador de
              WhatsApp de Meta (Herramientas de cuenta → Plantillas de mensajes) y Meta las
              revisa; suele tardar unas horas. Una vez aprobadas aparecen acá solas.
            </div>
          ) : (
            <div style={{display:'grid',gap:7,maxHeight:260,overflowY:'auto'}}>
              {plantillas.map(p=>(
                <label key={p.nombre+p.idioma} style={{display:'block',padding:'9px 11px',cursor:'pointer',
                  border:`1px solid ${plantillaSel?.nombre===p.nombre?C.primaryBorder:C.border}`,
                  borderRadius:R.md, background: plantillaSel?.nombre===p.nombre?C.primaryTint:C.bg1}}>
                  <div style={{display:'flex',alignItems:'center',gap:9}}>
                    <input type="radio" name="plantilla" checked={plantillaSel?.nombre===p.nombre}
                      onChange={()=>{ setPlantillaSel(p); setParamsPl(Array(p.huecos).fill('')); }}
                      style={{accentColor:C.primary}}/>
                    <span style={{fontSize:12.5,fontWeight:700,color:C.t1}}>{p.nombre}</span>
                    <span style={{fontSize:10,color:C.t4}}>{p.idioma} · {p.categoria}</span>
                  </div>
                  <div style={{fontSize:11.5,color:C.t3,marginTop:5,marginLeft:24,whiteSpace:'pre-wrap'}}>{p.texto}</div>
                </label>
              ))}
            </div>
          )}

          {/* Los huecos de la plantilla. Meta los numera {{1}}, {{2}}… y no deja
              mandarla con alguno vacío. */}
          {plantillaSel && plantillaSel.huecos > 0 && (
            <div style={{display:'grid',gap:7}}>
              <div style={{fontSize:11,fontWeight:700,color:C.t3}}>
                Esta plantilla tiene {plantillaSel.huecos} dato{plantillaSel.huecos===1?'':'s'} que completar:
              </div>
              {Array.from({length:plantillaSel.huecos}).map((_,i)=>(
                <input key={i} value={paramsPl[i]||''} placeholder={`Dato {{${i+1}}}`}
                  onChange={e=>setParamsPl(v=>{ const n=[...v]; n[i]=e.target.value; return n; })}
                  style={{...INP_ST,cursor:'text'}}/>
              ))}
            </div>
          )}

          {plantillaSel && (
            <div style={{background:'#D9FDD3',borderRadius:10,padding:'9px 12px',fontSize:13,
              color:'#111B21',whiteSpace:'pre-wrap'}}>
              {(()=>{ let t = plantillaSel.texto;
                paramsPl.forEach((v,i)=>{ t = t.split(`{{${i+1}}}`).join(v || `{{${i+1}}}`); });
                return t; })()}
            </div>
          )}

          <div style={{display:'flex',gap:8}}>
            <Btn variant="secondary" style={{flex:1}} onClick={()=>setModalPlantilla(false)}>Cancelar</Btn>
            <Btn style={{flex:1}} onClick={enviarPlantilla}
              disabled={enviandoPl || !plantillaSel || paramsPl.some(p=>!String(p||'').trim())}>
              {enviandoPl ? 'Enviando…' : 'Enviar plantilla'}
            </Btn>
          </div>
        </div>
      </Modal>

      {/* D1 (Sprint 1): Modal LEAD INVALIDO — razon obligatoria del catalogo */}
      {modalInvalido && (
        <div onClick={()=>setModalInvalido(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} className="radar-pop" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:22,width:380,maxWidth:'92vw',boxShadow:SH.lg}}>
            <div style={{fontSize:16,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,marginBottom:4}}>Marcar lead como inválido</div>
            <div style={{fontSize:12,color:C.t3,marginBottom:14}}>La razón es obligatoria: alimenta la métrica de calidad de leads y la revisión de campañas.</div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {RAZONES_INVALIDO.map(([v,l])=>(
                <label key={v} style={{display:'flex',alignItems:'center',gap:9,padding:'8px 11px',border:`1px solid ${razonInv===v?C.primaryBorder:C.border}`,borderRadius:R.md,background:razonInv===v?C.primaryTint:C.bg1,cursor:'pointer',fontSize:13,color:C.t1}}>
                  <input type="radio" name="razoninv" checked={razonInv===v} onChange={()=>setRazonInv(v)} style={{accentColor:C.primary}}/>
                  {l}
                </label>
              ))}
              {razonInv==='OTRO' && (
                <textarea placeholder="Detalle (obligatorio para Otro)…" value={razonInvDet} onChange={e=>setRazonInvDet(e.target.value)} rows={2}
                  style={{padding:'9px 11px',border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:13,background:C.bg1,color:C.t1,resize:'vertical',outline:'none'}}/>
              )}
            </div>
            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button onClick={()=>setModalInvalido(null)} style={{flex:1,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px',fontSize:13,fontWeight:600,color:C.t2,cursor:'pointer'}}>Cancelar</button>
              <button
                disabled={!razonInv || (razonInv==='OTRO' && !razonInvDet.trim())}
                onClick={async ()=>{
                  await actualizar(modalInvalido.leadId, {
                    estado:'invalido', razon_invalido: razonInv,
                    razon_invalido_detalle: razonInvDet.trim() || null,
                    invalidado_por: usuario?.nombre || null, invalidado_at: new Date().toISOString(),
                  });
                  setModalInvalido(null);
                  toast('Lead marcado como inválido.');
                }}
                style={{flex:1,background:C.red,border:'none',borderRadius:R.md,padding:'9px',fontSize:13,fontWeight:600,color:'#fff',cursor:'pointer',opacity:(!razonInv||(razonInv==='OTRO'&&!razonInvDet.trim()))?.5:1}}>
                Confirmar inválido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal lead manual */}
      {modalNuevo && (
        <div onClick={()=>setModalNuevo(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} className="radar-pop" style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:22,width:380,maxWidth:'92vw',maxHeight:'90vh',overflowY:'auto',boxShadow:SH.lg}}>
            <div style={{fontSize:16,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,marginBottom:14}}>Nuevo lead manual</div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <input placeholder="Nombre *" value={fNuevo.nombre} onChange={e=>setFNuevo(f=>({...f,nombre:e.target.value}))} style={{padding:'9px 11px',border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:13,background:C.bg1,color:C.t1,outline:'none'}}/>
              <input placeholder="Teléfono (504…)" value={fNuevo.telefono} onChange={e=>setFNuevo(f=>({...f,telefono:e.target.value}))} style={{padding:'9px 11px',border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:13,background:C.bg1,color:C.t1,outline:'none'}}/>
              <div style={{display:'flex',gap:8}}>
                <select value={fNuevo.canal} onChange={e=>setFNuevo(f=>({...f,canal:e.target.value}))} style={{flex:1,padding:'9px 11px',border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:13,background:C.bg1,color:C.t1,outline:'none'}}>{Object.entries(CANALES_SIB).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}</select>
                <select value={fNuevo.red_origen} onChange={e=>setFNuevo(f=>({...f,red_origen:e.target.value}))} style={{flex:1,padding:'9px 11px',border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:13,background:C.bg1,color:C.t1,outline:'none'}}>{REDES_SIB.map(r=><option key={r.v} value={r.v}>{r.l}</option>)}</select>
              </div>
              <select value={fNuevo.necesidad} onChange={e=>setFNuevo(f=>({...f,necesidad:e.target.value}))} style={{padding:'9px 11px',border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:13,background:C.bg1,color:C.t1,outline:'none'}}><option value="">Necesidad / línea (opcional)</option>{ORG.lineasNegocio.map(l=><option key={l} value={l}>{l}</option>)}</select>
              <textarea placeholder="Primer mensaje (opcional)" value={fNuevo.mensaje_inicial} onChange={e=>setFNuevo(f=>({...f,mensaje_inicial:e.target.value}))} rows={2} style={{padding:'9px 11px',border:`1px solid ${C.border}`,borderRadius:R.md,fontSize:13,background:C.bg1,color:C.t1,resize:'vertical',outline:'none'}}/>
            </div>
            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button onClick={()=>setModalNuevo(false)} style={{flex:1,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'9px',fontSize:13,fontWeight:600,color:C.t2,cursor:'pointer'}}>Cancelar</button>
              <button disabled={saving||!fNuevo.nombre} onClick={crearLead} style={{flex:1,background:C.blue,border:'none',borderRadius:R.md,padding:'9px',fontSize:13,fontWeight:600,color:'#fff',cursor:'pointer',opacity:(saving||!fNuevo.nombre)?.6:1,boxShadow:SH.primary}}>{saving?'Guardando…':'Crear lead'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
// ─── ASISTENTE IA (widget flotante, en toda la app) ─────────────────────────────
function AsistenteWidget({ usuario }) {
  const [open, setOpen]   = useState(false);
  const [msgs, setMsgs]   = useState([{ role:'assistant', content:`¡Hola ${(usuario?.nombre||'').split(' ')[0]}! ⚡ Soy ELSA. Preguntame lo que sea: cómo vas en tu meta, a quién darle seguimiento hoy, cómo cotizar en SAP, o cualquier duda de la app.` }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  useEffect(()=>{ if(scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, open, loading]);

  const enviar = async () => {
    const t = input.trim(); if (!t || loading) return;
    const next = [...msgs, { role:'user', content:t }];
    setMsgs(next); setInput(''); setLoading(true);
    try {
      // Mismo cerebro que el módulo ELSA (/api/elsa): herramientas + datos en vivo con
      // crédito de Anthropic, o fallback a Groq si no hay.
      const r = await fetch('/api/elsa', { method:'POST', headers:{'content-type':'application/json'},
        body: JSON.stringify({ messages: next.map(m=>({ role:m.role, content:m.content })) }) });
      const d = await r.json();
      setMsgs(m=>[...m, { role:'assistant', content: d.response || d.reply || ('⚠️ '+(d.error||'No pude responder')) }]);
    } catch (e) { setMsgs(m=>[...m, { role:'assistant', content:'⚠️ Error de conexión' }]); }
    setLoading(false);
  };
  const sugerencias = ['¿Cómo voy este mes?', '¿A quién le doy seguimiento hoy?', '¿Cómo cotizo en SAP?'];

  return (<>
    <button onClick={()=>setOpen(o=>!o)} title="ELSA — Asistente IA"
      style={{position:'fixed',bottom:20,right:20,zIndex:1200,width:56,height:56,borderRadius:'50%',border:'none',cursor:'pointer',background:'linear-gradient(135deg,#0F766E,#0D9488)',color:'#fff',fontSize:24,boxShadow:'0 6px 20px rgba(15,118,110,.40)'}}>{open?'✕':'⚡'}</button>
    {open&&(
      <div className="radar-pop" style={{position:'fixed',bottom:86,right:20,zIndex:1200,width:380,maxWidth:'92vw',height:520,maxHeight:'76vh',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,boxShadow:SH.lg,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{padding:'12px 16px',background:'linear-gradient(135deg,#0F766E,#0D9488)',color:'#fff'}}>
          <div style={{fontSize:15,fontWeight:800}}>⚡ ELSA</div>
          <div style={{fontSize:11,opacity:.85}}>Tu asistente · datos en vivo</div>
        </div>
        <div ref={scrollRef} style={{flex:1,overflowY:'auto',padding:14,display:'flex',flexDirection:'column',gap:10,background:C.bg1}}>
          {msgs.map((m,i)=>(
            <div key={i} style={{alignSelf:m.role==='user'?'flex-end':'flex-start',maxWidth:'85%',background:m.role==='user'?C.blue:C.bg2,color:m.role==='user'?'#fff':C.t1,border:m.role==='user'?'none':`1px solid ${C.border}`,borderRadius:12,padding:'9px 12px',fontSize:13,lineHeight:1.45,whiteSpace:'pre-wrap'}}>{m.content}</div>
          ))}
          {loading&&<div style={{alignSelf:'flex-start',color:C.t4,fontSize:13}}>escribiendo…</div>}
          {msgs.length<=1&&!loading&&(
            <div style={{display:'flex',flexWrap:'wrap',gap:6,marginTop:4}}>
              {sugerencias.map(s=><button key={s} onClick={()=>{setInput(s);}} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:'5px 10px',fontSize:11,color:C.t3,cursor:'pointer'}}>{s}</button>)}
            </div>
          )}
        </div>
        <div style={{padding:10,borderTop:`1px solid ${C.border}`,display:'flex',gap:8}}>
          <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')enviar();}} placeholder="Escribí tu pregunta…"
            style={{flex:1,background:C.bg1,border:`1px solid ${C.border}`,borderRadius:10,padding:'9px 12px',color:C.t1,fontSize:13,outline:'none'}}/>
          <button onClick={enviar} disabled={loading||!input.trim()} style={{background:C.blue,color:'#fff',border:'none',borderRadius:10,padding:'0 14px',fontSize:14,fontWeight:800,cursor:'pointer',opacity:(loading||!input.trim())?.6:1}}>➤</button>
        </div>
      </div>
    )}
  </>);
}

// ─── CONSOLA DEL PROVEEDOR (super-admin · admin.radar.app / ruta /admin) ──────
// Dashboard interno: métricas + crear/gestionar organizaciones.
function ConsoleDashboard() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ nombre:'', pais:'Honduras', plan:'core', admin_usuario:'', admin_nombre:'', admin_pass:'' });
  const [saving, setSaving] = useState(false);
  const [creado, setCreado] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/onboarding', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ action:'listar_orgs' }) });
      const d = await r.json();
      setOrgs(d.orgs || []);
    } catch (_) { setOrgs([]); }
    setLoading(false);
  };
  useEffect(()=>{ cargar(); }, []);

  const crear = async () => {
    if (!f.nombre.trim() || !f.admin_usuario.trim() || !f.admin_nombre.trim() || f.admin_pass.length < 6) {
      toast('Completá nombre de la org, usuario, nombre del admin y contraseña (mín. 6).'); return;
    }
    setSaving(true); setCreado(null);
    try {
      const r = await fetch('/api/onboarding', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'crear_org', ...f, admin_usuario: f.admin_usuario.trim().toLowerCase() }) });
      const d = await r.json();
      if (d.ok) {
        toast('✓ Organización creada: '+d.org.nombre);
        setCreado({ org:d.org, admin:d.admin, pass:f.admin_pass });
        setF({ nombre:'', pais:'Honduras', plan:'core', admin_usuario:'', admin_nombre:'', admin_pass:'' });
        cargar();
      } else { toast(d.error || 'No se pudo crear la organización.'); }
    } catch (e) { toast('Error: '+(e?.message||e)); }
    setSaving(false);
  };

  const inp = (k, ph, type='text') => (
    <input value={f[k]} onChange={e=>setF(x=>({...x,[k]:e.target.value}))} placeholder={ph} type={type}
      style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px',color:C.t1,fontSize:14,outline:'none'}}/>
  );

  return (
    <div style={{flex:1,overflowY:'auto',padding:28,background:C.bg1}}>
      <div style={{maxWidth:960,margin:'0 auto'}}>
        <h2 style={{fontSize:22,fontWeight:900,color:C.t1,margin:'0 0 4px'}}>🏢 Organizaciones</h2>
        <div style={{fontSize:13,color:C.t4,marginBottom:18}}>Creá una organización nueva (cliente o demo) con su propio espacio aislado y su usuario administrador.</div>

        {/* Métricas globales del negocio */}
        {(() => {
          const clientes = orgs.filter(o => o.id !== MONTASA_ORG);
          const activas = orgs.filter(o => o.activo !== false);
          const porPlan = orgs.reduce((a,o)=>{ a[o.plan||'core']=(a[o.plan||'core']||0)+1; return a; }, {});
          const M = [
            ['Organizaciones', String(orgs.length), C.blue, `${activas.length} activas`],
            ['Clientes', String(clientes.length), C.green, 'sin contar el proveedor'],
            ['Enterprise', String(porPlan.enterprise||0), C.indigo, `Pro ${porPlan.pro||0} · Core ${porPlan.core||0}`],
            ['MRR estimado', '—', C.t3, 'conectar facturación'],
          ];
          return (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12,marginBottom:22}}>
              {M.map(([l,v,c,s])=>(
                <div key={l} style={{background:C.bg2,border:`1px solid ${C.border}`,borderTop:`2px solid ${c}`,borderRadius:12,padding:'14px 16px',boxShadow:SH.sm}}>
                  <div style={{fontSize:10,color:C.t4,fontWeight:800,textTransform:'uppercase',letterSpacing:'.06em'}}>{l}</div>
                  <div className="radar-num" style={{fontSize:26,fontWeight:900,color:c,lineHeight:1.1,marginTop:3}}>{v}</div>
                  <div style={{fontSize:11,color:C.t4,marginTop:2}}>{s}</div>
                </div>
              ))}
            </div>
          );
        })()}

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,alignItems:'start'}}>
          {/* Form */}
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:20,boxShadow:SH.sm}}>
            <div style={{fontSize:14,fontWeight:800,color:C.t1,marginBottom:14}}>Nueva organización</div>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div><div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5}}>Nombre de la empresa *</div>{inp('nombre','ACME Industrial S.A.')}</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div><div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5}}>País</div>{inp('pais','Honduras')}</div>
                <div><div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5}}>Plan</div>
                  <select value={f.plan} onChange={e=>setF(x=>({...x,plan:e.target.value}))} style={{width:'100%',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px',color:C.t1,fontSize:14,outline:'none'}}>
                    <option value="core">Core</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option>
                  </select></div>
              </div>
              <div style={{borderTop:`1px solid ${C.border}`,margin:'4px 0',paddingTop:12,fontSize:12,fontWeight:800,color:C.t3}}>Usuario administrador</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div><div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5}}>Usuario *</div>{inp('admin_usuario','acme.admin')}</div>
                <div><div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5}}>Nombre *</div>{inp('admin_nombre','Juan Pérez')}</div>
              </div>
              <div><div style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5}}>Contraseña * (mín. 6)</div>{inp('admin_pass','••••••••','password')}</div>
              <button onClick={crear} disabled={saving} style={{marginTop:6,background:saving?C.bg3:'linear-gradient(135deg,#0F766E,#0D5F58)',color:saving?C.t4:'#fff',border:'none',borderRadius:9,padding:'12px',fontSize:14,fontWeight:800,cursor:saving?'default':'pointer'}}>{saving?'Creando…':'Crear organización'}</button>
            </div>
            {creado && (
              <div style={{marginTop:14,background:`${C.green}12`,border:`1px solid ${C.green}40`,borderRadius:10,padding:'12px 14px',fontSize:12.5,color:C.t2}}>
                <div style={{fontWeight:800,color:C.green,marginBottom:6}}>✓ {creado.org.nombre} creada</div>
                Entra en el mismo login con:<br/>Usuario: <b>{creado.admin.usuario}</b><br/>Contraseña: <b>{creado.pass}</b>
                <div style={{fontSize:10,color:C.t4,marginTop:6}}>Anotá estos datos: la contraseña no se vuelve a mostrar.</div>
              </div>
            )}
          </div>

          {/* Lista */}
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,padding:20,boxShadow:SH.sm}}>
            <div style={{fontSize:14,fontWeight:800,color:C.t1,marginBottom:14}}>Organizaciones ({orgs.length})</div>
            {loading ? <SkelRows n={4}/> : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {orgs.map(o=>(
                  <div key={o.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',border:`1px solid ${C.border}`,borderRadius:10}}>
                    <div style={{width:32,height:32,borderRadius:8,background:`${C.blue}18`,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:900,color:C.blue,fontSize:13}}>{(o.nombre||'?')[0]}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13.5,fontWeight:800,color:C.t1}}>{o.nombre}{o.id===MONTASA_ORG&&<span style={{marginLeft:6,fontSize:9,background:`${C.indigo}18`,color:C.indigo,borderRadius:5,padding:'1px 6px'}}>PROVEEDOR</span>}</div>
                      <div style={{fontSize:11,color:C.t4}}>{o.slug} · plan {o.plan} · {o.activo?'activa':'inactiva'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Consola del proveedor: login de super-admin + shell. Se renderiza en la ruta /admin
// (separada de la app de los clientes). Solo entran usuarios con app_metadata.super_admin.
function ProviderConsole() {
  const [estado, setEstado] = useState('cargando'); // cargando | login | ok
  const [email, setEmail] = useState('');
  const chequear = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.app_metadata?.super_admin === true) { setEmail(session.user.email || ''); setEstado('ok'); }
    else setEstado('login');
  };
  useEffect(() => { chequear(); }, []);

  if (estado === 'cargando') return <div style={{height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:C.t4,background:C.bg0}}>Cargando…</div>;
  if (estado === 'login') return <SuperAdminLogin onOk={chequear} />;

  const salir = async () => { await supabase.auth.signOut(); setEstado('login'); };
  return (
    <div style={{height:'100vh',maxHeight:'100dvh',display:'flex',flexDirection:'column',background:C.bg0,fontFamily:"'Inter',system-ui,sans-serif",color:C.t1,overflow:'hidden'}}>
      <div style={{height:54,borderBottom:`1px solid ${C.border}`,background:'linear-gradient(90deg,#1A1A2E,#2A2A47)',display:'flex',alignItems:'center',padding:'0 20px',gap:12,flexShrink:0}}>
        <span style={{fontWeight:900,letterSpacing:'.14em',color:'#fff'}}>RADAR</span>
        <span style={{fontSize:11,color:'#A5B4FC',fontWeight:700,background:'rgba(255,255,255,.1)',borderRadius:6,padding:'3px 9px'}}>CONSOLA DEL PROVEEDOR</span>
        <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:12}}>
          <span style={{fontSize:12,color:'#C7CBF7'}}>{email}</span>
          <button onClick={salir} style={{background:'rgba(255,255,255,.12)',border:'none',borderRadius:7,padding:'6px 12px',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>Salir</button>
        </div>
      </div>
      <ConsoleDashboard />
    </div>
  );
}
function SuperAdminLogin({ onOk }) {
  const [u,setU]=useState(''); const [p,setP]=useState(''); const [err,setErr]=useState(''); const [ld,setLd]=useState(false);
  const go = async () => {
    if(!u||!p||ld) return; setLd(true); setErr('');
    const { error } = await supabase.auth.signInWithPassword({ email:`${u.toLowerCase().trim()}@radar.montasa`, password:p });
    if (error) { setErr('Credenciales incorrectas'); setLd(false); return; }
    const { data:{ session } } = await supabase.auth.getSession();
    if (session?.user?.app_metadata?.super_admin === true) { onOk(); }
    else { await supabase.auth.signOut(); setErr('Esta cuenta no es super-administrador de RADAR.'); setLd(false); }
  };
  return (
    <div style={{height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'linear-gradient(135deg,#1A1A2E,#2A2A47)',fontFamily:"'Inter',system-ui,sans-serif",padding:16}}>
      <div style={{width:340,padding:'40px 36px',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,boxShadow:SH.lg}}>
        <div style={{textAlign:'center',marginBottom:28}}>
          <div style={{width:52,height:52,borderRadius:R.lg,background:'linear-gradient(135deg,#0F766E,#0D5F58)',boxShadow:'inset 0 1px 0 rgba(255,255,255,.18)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,margin:'0 auto 12px'}}>👑</div>
          <div style={{fontSize:24,fontWeight:900,letterSpacing:'.12em',color:C.blue}}>RADAR</div>
          <div style={{fontSize:10.5,color:C.t4,marginTop:4,letterSpacing:'.08em'}}>CONSOLA DEL PROVEEDOR</div>
        </div>
        {['Usuario','Contraseña'].map((lbl,i)=>(
          <div key={lbl} style={{marginBottom:i===0?12:18}}>
            <div style={{fontSize:11,color:C.t3,textTransform:'uppercase',letterSpacing:'.07em',fontWeight:600,marginBottom:5}}>{lbl}</div>
            <input className="radar-input" type={i===1?'password':'text'} value={i===0?u:p} onChange={e=>i===0?setU(e.target.value):setP(e.target.value)} onKeyDown={e=>e.key==='Enter'&&go()}
              autoCapitalize="none" autoCorrect="off"
              style={{width:'100%',boxSizing:'border-box',background:C.bg1,border:`1px solid ${C.border}`,borderRadius:R.md,padding:'11px 12px',color:C.t1,fontSize:14}}/>
          </div>
        ))}
        {err&&<div style={{background:C.redTint,border:`1px solid ${C.redBorder}`,borderRadius:R.md,padding:'8px 12px',marginBottom:14,fontSize:12,color:C.redText,textAlign:'center'}}>{err}</div>}
        <button className="radar-btn" onClick={go} disabled={!u||!p||ld} style={{width:'100%',padding:12,borderRadius:R.md,border:'none',background:u&&p?'linear-gradient(135deg,#0F766E,#0D5F58)':C.bg3,color:u&&p?'#fff':C.t4,fontSize:14,fontWeight:600,letterSpacing:'.02em',boxShadow:u&&p?SH.primary:'none',cursor:u&&p?'pointer':'default'}}>{ld?'Entrando…':'ENTRAR →'}</button>
      </div>
    </div>
  );
}

export function AppRoot() {
  const isAdmin = typeof window !== 'undefined' && window.location.pathname.replace(/\/+$/,'').endsWith('/admin');
  return isAdmin ? <ProviderConsole /> : <App />;
}

// ─── AGENDA HUB ───────────────────────────────────────────────────────────────
// Fusiona Seguimiento + Actividades + Visitas en UN módulo con tabs (las tres
// vistas leen/escriben la misma tabla actividades_programadas). Wrapper puro:
// cero cambios de lógica en los componentes internos. Los ids legacy
// ('seguimiento'/'actividades'/'visitas') siguen funcionando — el switch de
// módulos los redirige aquí pasando initialTab.
const AGENDA_TABS = [
  ['pendientes', 'Pendientes'],
  ['agenda',     'Agenda'],
  ['visitas',    'Visitas'],
];
function AgendaHub({ usuario, clientes, empresaActiva, initialTab }) {
  const [tab, setTab] = useState(() => {
    if (initialTab && AGENDA_TABS.some(([id]) => id === initialTab)) return initialTab;
    try { const t = localStorage.getItem('radar_agenda_tab'); return AGENDA_TABS.some(([id]) => id === t) ? t : 'pendientes'; }
    catch { return 'pendientes'; }
  });
  useEffect(() => { try { localStorage.setItem('radar_agenda_tab', tab); } catch {} }, [tab]);
  // Anuncia la sub-vista al topbar (breadcrumb Agenda / Pendientes) — mismo patrón que CRM
  useEffect(() => {
    const lbl = (AGENDA_TABS.find(([id]) => id === tab) || [])[1] || '';
    window.dispatchEvent(new CustomEvent('radar-submod', { detail: lbl }));
    return () => window.dispatchEvent(new CustomEvent('radar-submod', { detail: '' }));
  }, [tab]);
  return (
    <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {/* Tab bar — flota sobre el gris, mismo estilo que el toggle Pipeline/Clientes del CRM */}
      <div style={{padding:'14px 24px 0',display:'flex',alignItems:'center',flexShrink:0}}>
        <div style={{display:'flex',gap:2,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2}}>
          {AGENDA_TABS.map(([id,lbl]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{height:26,padding:'0 12px',background:tab===id?C.bg2:'transparent',color:tab===id?C.t1:C.t3,border:'none',borderRadius:R.sm,fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:tab===id?SH.sm:'none',transition:'background .15s ease, color .15s ease'}}>{lbl}</button>
          ))}
        </div>
      </div>
      {/* Contenido: Seguimiento y Visitas son "tipo app" (scroll interno → overflow hidden);
          Actividades es "tipo documento" (sin scroll propio → este contenedor scrollea) */}
      <div key={tab} style={{flex:1,minHeight:0,minWidth:0,display:'flex',flexDirection:'column',overflowY:tab==='agenda'?'auto':'hidden',overflowX:'hidden'}}>
        {tab==='pendientes' && <Seguimiento usuario={usuario} clientes={clientes} empresaActiva={empresaActiva}/>}
        {tab==='agenda'     && <Actividades usuario={usuario} clientes={clientes} empresaActiva={empresaActiva}/>}
        {tab==='visitas'    && <Visitas usuario={usuario} clientes={clientes} empresaActiva={empresaActiva}/>}
      </div>
    </div>
  );
}

// ── Hubs del mapa IA (mismo patrón AgendaHub: wrapper con tabs, cero cambios de
//    lógica en los módulos envueltos, breadcrumb via radar-submod, tab persistida) ──
function HubTabs({ tabs, tab, setTab }) {
  return (
    <div style={{padding:'14px 24px 0',display:'flex',alignItems:'center',flexShrink:0}}>
      <div style={{display:'flex',gap:2,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:R.md,padding:2}}>
        {tabs.map(([id,lbl]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{height:26,padding:'0 12px',background:tab===id?C.bg2:'transparent',color:tab===id?C.t1:C.t3,border:'none',borderRadius:R.sm,fontSize:12,fontWeight:600,cursor:'pointer',boxShadow:tab===id?SH.sm:'none',transition:'background .15s ease, color .15s ease'}}>{lbl}</button>
        ))}
      </div>
    </div>
  );
}
// Estado de tab compartido por los hubs: initialTab (redirects legacy) > localStorage > default.
// submodLabels: qué anunciar al breadcrumb por tab ('' = nada, cuando repetiría el nombre del hub).
function useHubTab(tabs, storageKey, initialTab, fallback, submodLabels) {
  const valid = id => tabs.some(([t]) => t === id);
  const [tab, setTab] = useState(() => {
    if (initialTab && valid(initialTab)) return initialTab;
    try { const t = localStorage.getItem(storageKey); return valid(t) ? t : fallback; }
    catch { return fallback; }
  });
  useEffect(() => { try { localStorage.setItem(storageKey, tab); } catch {} }, [tab, storageKey]);
  useEffect(() => {
    const lbl = submodLabels ? (submodLabels[tab] ?? '') : ((tabs.find(([id]) => id === tab) || [])[1] || '');
    window.dispatchEvent(new CustomEvent('radar-submod', { detail: lbl }));
    return () => window.dispatchEvent(new CustomEvent('radar-submod', { detail: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  return [valid(tab) ? tab : fallback, setTab];
}

// MENSAJES: Bandeja WhatsApp (incluye WA Equipo como tab interna) · NEXA · Campañas
function MensajesHub({ usuario, empresaActiva, puedeSibila, initialTab }) {
  const tabs = [['whatsapp','WhatsApp'], ...(puedeSibila?[['nexa','NEXA']]:[]), ['campanias','Campañas']];
  const [tab, setTab] = useHubTab(tabs, 'radar_mensajes_tab', initialTab, 'whatsapp');
  return (
    <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      <HubTabs tabs={tabs} tab={tab} setTab={setTab}/>
      {/* Los tres módulos son "tipo app" (scroll interno) → overflow hidden */}
      <div key={tab} style={{flex:1,minHeight:0,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {tab==='whatsapp'  && <WhatsApp usuario={usuario}/>}
        {tab==='nexa'      && puedeSibila && <Sibila usuario={usuario} empresaActiva={empresaActiva}/>}
        {tab==='campanias' && <CampaniasHub usuario={usuario} empresaActiva={empresaActiva}/>}
      </div>
    </div>
  );
}

// FLOTA: Equipos · Mantenimiento (tab solo para roles que ya lo tenían)
function FlotaHub({ usuario, clientes, equipos, onEquiposChange, empresaActiva, initialTab }) {
  const conMant = ['admin','taller'].includes(usuario?.rol) || (usuario?.modulos_extra||[]).includes('mantenimiento');
  // 'Gastos de taller': el ESTADO de los equipos vive en el Kanban de Equipos; esta
  // pestana es el registro de PLATA (mano de obra, repuestos, historial).
  const tabs = [['equipos','Equipos'], ...(conMant?[['mantenimiento','Gastos de taller']]:[])];
  const [tab, setTab] = useHubTab(tabs, 'radar_flota_tab', initialTab, 'equipos', {equipos:conMant?'Equipos':'',mantenimiento:'Gastos de taller'});
  return (
    <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {tabs.length>1 && <HubTabs tabs={tabs} tab={tab} setTab={setTab}/>}
      <div key={tab} style={{flex:1,minHeight:0,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {tab==='equipos'       && <Flota clientes={clientes} equipos={equipos} onEquiposChange={onEquiposChange} empresaActiva={empresaActiva}/>}
        {tab==='mantenimiento' && <Mantenimiento equipos={equipos}/>}
      </div>
    </div>
  );
}

// TALLER: Taller · Órdenes de Trabajo (ex Cotizaciones; tab solo si el rol lo tenía)
function TallerHub({ usuario, clientes, equipos, empresaActiva, initialTab }) {
  const conOT = usuario?.rol==='admin' || (usuario?.modulos_extra||[]).includes('cotizaciones');
  const tabs = [['taller','Taller'], ...(conOT?[['ot','Órdenes de Trabajo']]:[])];
  const [tab, setTab] = useHubTab(tabs, 'radar_taller_tab', initialTab, 'taller', {taller:'',ot:'Órdenes de Trabajo'});
  return (
    <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {tabs.length>1 && <HubTabs tabs={tabs} tab={tab} setTab={setTab}/>}
      <div key={tab} style={{flex:1,minHeight:0,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {tab==='taller' && <Taller equipos={equipos}/>}
        {tab==='ot'     && <Cotizaciones clientes={clientes} equipos={equipos} usuario={usuario} empresaActiva={empresaActiva}/>}
      </div>
    </div>
  );
}

// METAS: Metas & Revisión (módulo intacto, con sus tabs internas) · Comité (ex 'semana')
function MetasHub({ usuario, empresaActiva, onNavigate, initialTab }) {
  const conComite = usuario?.rol==='admin' || (usuario?.modulos_extra||[]).includes('semana');
  const tabs = [['metas','Metas & Revisión'], ...(conComite?[['comite','Comité']]:[])];
  const [tab, setTab] = useHubTab(tabs, 'radar_metas_tab', initialTab, 'metas', {metas:'',comite:'Comité'});
  // MetasRevision recibía empresa fija del usuario si la tiene (mismo cálculo que el switch viejo)
  const eaMetas = (usuario?.empresa_id && usuario.empresa_id!=='all') ? usuario.empresa_id : empresaActiva;
  return (
    <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bg1}}>
      {tabs.length>1 && <HubTabs tabs={tabs} tab={tab} setTab={setTab}/>}
      {/* Ambos módulos traen su propio overflowY:auto → contenedor hidden */}
      <div key={tab} style={{flex:1,minHeight:0,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {tab==='metas'  && <MetasRevision usuario={usuario} empresaActiva={eaMetas}/>}
        {tab==='comite' && conComite && <ComiteConsolidado usuario={usuario} empresaActiva={empresaActiva}/>}
      </div>
    </div>
  );
}

// ─── PLAN DE ACCION — OCTUBRE ────────────────────────────────────────────────
// UNA sola lista, no tres. Las listas de llantas, mantenimientos e IHTT se
// cruzan en 154 clientes: tenerlas separadas obliga a llamar dos veces al
// mismo. Aca cada cliente es una fila con sus frentes como etiqueta, todo
// ordenado por lo que compro, de mayor a menor.
//
// El orden no es un capricho: 49 clientes concentran el 95% del dinero de los
// ultimos 24 meses. Llamar en orden alfabetico es tirar el mes.
//
// Y tomar es obligatorio: no se puede registrar una gestion sin quedarse el
// cliente. Llamar sin tomarlo es como no haber llamado, porque despues nadie
// sabe quien lo tiene.
// Se usan lunesDe() e ymd() de nivel de modulo, que ya traen el ajuste de
// zona horaria de Honduras (UTC-6, que al pasar por UTC se corre un dia).
const PLAN_PERIODO = '2026-10';
const PLAN_FRENTES = ['LLANTAS', 'MANTENIMIENTOS', 'IHTT', 'VENTA DE EQUIPO',
                      'RENTA', 'FLETES', 'MANO DE OBRA'];
// La etiqueta de la fila se cortaba a 4 letras, y 'VENTA DE EQUIPO' quedaba
// como 'VENT'. Las abreviaturas van escritas, no recortadas.
const PLAN_FRENTE_CORTO = { 'LLANTAS':'LLAN', 'MANTENIMIENTOS':'MANT', 'IHTT':'IHTT',
  'VENTA DE EQUIPO':'EQUIPO', 'RENTA':'RENTA', 'FLETES':'FLETE', 'MANO DE OBRA':'M.OBRA' };
const PLAN_RESULTADOS = [
  { v:'contesto',      l:'Contestó — hablamos',        tono:'green'  },
  { v:'cotizar',       l:'Quiere cotización',          tono:'green'  },
  { v:'cotizado',      l:'Le mandé la cotización',     tono:'green'  },
  { v:'compro',        l:'Compró',                     tono:'green'  },
  { v:'volver_llamar', l:'Volver a llamar',            tono:'orange' },
  { v:'no_contesta',   l:'No contesta',                tono:'gray'   },
  { v:'numero_malo',   l:'El número está malo',        tono:'red'    },
  { v:'no_interesa',   l:'No le interesa',             tono:'red'    },
];
const PRIO_COLOR = { A:C.green, B:C.blue, C:C.orange, D:C.t4 };

function PlanOctubre({ usuario }) {
  const [tab, setTab]       = useState(() => { try { return localStorage.getItem('radar_plan_tab') || 'lista'; } catch { return 'lista'; } });
  useEffect(() => { try { localStorage.setItem('radar_plan_tab', tab); } catch {} }, [tab]);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('radar-submod', { detail: tab==='lista'?'Mi lista':'Tablero' }));
    return () => window.dispatchEvent(new CustomEvent('radar-submod', { detail: '' }));
  }, [tab]);

  const [filas, setFilas]   = useState(null);
  const [metas, setMetas]   = useState([]);
  const [visMeta, setVisMeta] = useState([]);
  const [gest, setGest]     = useState([]);
  const [equipo, setEquipo] = useState([]);
  const [cargando, setCargando] = useState(true);

  // Filtros. Por defecto: lo mio y lo libre, sin gestionar, ordenado por plata.
  const [fFrente, setFFrente]   = useState('');
  const [fPrio, setFPrio]       = useState('');
  const [fDuenio, setFDuenio]   = useState('pendientes');
  const [fVend, setFVend]       = useState('');   // ver lo que agarro un vendedor
  const [fEstado, setFEstado]   = useState('sin_gestionar');
  const [busq, setBusq]         = useState('');
  const [fTipo, setFTipo]       = useState('');
  const [abierto, setAbierto]   = useState('');   // cliente con la ficha abierta
  const [ficha, setFicha]       = useState({});   // lo traido, por cliente
  const [cotAbierta, setCotAbierta] = useState(null);
  const [cotLineas, setCotLineas]   = useState({});
  const [bajando, setBajando]       = useState(null);
  const [verMas, setVerMas]     = useState(60);

  const [modal, setModal]   = useState(null);   // cliente al que se le registra la gestion
  const [f, setF]           = useState({});
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    const [l, m, v, g, u] = await Promise.all([
      supabase.from('v_plan_octubre').select('*').limit(1000),
      supabase.from('plan_metas').select('*').eq('periodo', PLAN_PERIODO),
      supabase.from('plan_visitas_meta').select('*').eq('periodo', PLAN_PERIODO),
      supabase.from('plan_gestiones').select('vendedor,frente,tipo,resultado,created_at,cliente_id,monto_posible')
        .eq('periodo', PLAN_PERIODO).order('created_at', { ascending:false }).limit(4000),
      supabase.from('radar_usuarios').select('nombre').eq('activo', true).in('rol', ['ventas','gerente_ventas','admin']),
    ]);
    // usd_facturado es el nombre nuevo; usd_24m el viejo. Mientras no se corra
    // el 07, la vista devuelve el viejo y la lista tiene que seguir andando.
    const normalizar = (r) => ({ ...r, usd_facturado: r.usd_facturado ?? r.usd_24m ?? 0 });
    if (l.error) toast('No se pudo traer la lista del plan: ' + l.error.message);
    setFilas((l.data || []).map(normalizar).sort((a,b)=>(b.usd_facturado||0)-(a.usd_facturado||0)));
    setMetas(m.data || []); setVisMeta(v.data || []);
    setGest(g.data || []);  setEquipo((u.data || []).map(x => x.nombre).sort());
    setCargando(false);
  };
  useEffect(() => { cargar(); }, []);

  // UN CLIENTE DE ALGUIEN QUE YA NO TRABAJA AQUI ESTA LIBRE.
  // 391 de los 704 de la lista estaban a nombre de gente que renuncio -225 de
  // Claudia Guardado sola-, y el filtro de siempre es "mios y libres": no eran
  // de nadie presente y tampoco figuraban como libres, asi que no los veia
  // NADIE. Desaparecian de la lista aunque alguien ya les hubiera hablado.
  // vendedor_activo lo manda la vista; si todavia no se corrio el SQL llega
  // undefined y el !== false deja el comportamiento de antes.
  const esHuerfano = (x) => !!x.vendedor && x.vendedor_activo === false;
  const esLibre    = (x) => !x.vendedor || esHuerfano(x);

  // ── La lista filtrada ─────────────────────────────────────────────────────
  const vista = useMemo(() => {
    let r = filas || [];
    if (fFrente) r = r.filter(x => (x.frentes || []).includes(fFrente));
    if (fPrio)   r = r.filter(x => x.prioridad === fPrio);
    if (fVend) {
      // Preguntar por un vendedor manda sobre "mios y libres": si no, pedir
      // los de Kevin desde la cuenta de Audry devolvia una lista vacia.
      r = r.filter(x => x.vendedor === fVend);
    } else if (fDuenio === 'huerfanos') {
      r = r.filter(x => esHuerfano(x));
    } else {
      if (fDuenio === 'mios')       r = r.filter(x => x.vendedor === usuario?.nombre);
      if (fDuenio === 'libres')     r = r.filter(x => esLibre(x));
      if (fDuenio === 'pendientes') r = r.filter(x => esLibre(x) || x.vendedor === usuario?.nombre);
    }
    if (fEstado === 'sin_gestionar') r = r.filter(x => !x.gestiones);
    if (fEstado === 'gestionados')   r = r.filter(x => x.gestiones > 0);
    if (fTipo === 'contrato') r = r.filter(x => x.es_contrato);
    if (fTipo === 'renovar')  r = r.filter(x => x.es_contrato && x.dias_sin_renta > 120);
    if (busq.trim()) {
      const b = busq.trim().toLowerCase();
      r = r.filter(x => (x.empresa||'').toLowerCase().includes(b)
                     || (x.contacto||'').toLowerCase().includes(b)
                     || (x.sap_code||'').toLowerCase().includes(b));
    }
    return r;   // la vista ya viene ordenada de mayor a menor
  }, [filas, fFrente, fPrio, fDuenio, fVend, fEstado, fTipo, busq, usuario]);

  // Cuantos clientes lleva agarrados cada quien. Va en el propio selector: ver
  // quien va sacando de la lista no deberia costar cuatro clics.
  const porVendedor = useMemo(() => {
    const m = new Map();
    (filas || []).forEach(x => { if (x.vendedor) m.set(x.vendedor, (m.get(x.vendedor) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [filas]);

  // ── Los numeros del tablero ───────────────────────────────────────────────
  const hoy = ymd(new Date());
  const lunes = ymd(lunesDe(new Date()));
  const resumen = useMemo(() => {
    const dia = (g) => String(g.created_at || '').slice(0,10);
    const esLlamada = (g) => g.tipo !== 'visita';
    const por = {};
    const toca = (v) => (por[v] = por[v] || { hoy:0, sem:0, mes:0, vis_sem:0, vis_mes:0, cotiz:0, compro:0, monto:0 });
    gest.forEach(g => {
      const p = toca(g.vendedor); const d = dia(g);
      if (esLlamada(g)) {
        p.mes += 1;
        if (d >= lunes) p.sem += 1;
        if (d === hoy)  p.hoy += 1;
      } else {
        p.vis_mes += 1;
        if (d >= lunes) p.vis_sem += 1;
      }
      if (['cotizar','cotizado'].includes(g.resultado)) p.cotiz += 1;
      if (g.resultado === 'compro') { p.compro += 1; p.monto += Number(g.monto_posible || 0); }
    });
    return por;
  }, [gest, hoy, lunes]);

  const metaDe = (v) => (metas.filter(m => m.vendedor === v)
    .reduce((a,m) => ({ llamadas:a.llamadas+m.meta_llamadas, cierres:a.cierres+m.meta_cierres, ingreso:a.ingreso+Number(m.meta_ingreso) }),
            { llamadas:0, cierres:0, ingreso:0 }));

  // LA FICHA. Llamar sin saber que compro, cuanto, cuando y que se le cotizo
  // es llamar a ciegas con un numero en la mano. Se trae al abrir y no antes:
  // son 305 clientes y en una sesion se abren diez.
  const abrirFicha = async (c) => {
    const k = c.cliente_id;
    if (abierto === k) { setAbierto(''); return; }
    setAbierto(k);
    if (ficha[k]) return;
    setFicha(d => ({ ...d, [k]: { cargando: true } }));

    // Se pide para TODOS los registros del cliente, no solo el principal: el
    // mismo cliente tiene su historia partida entre Monhaco y Montasa.
    const ids = (c.cliente_ids && c.cliente_ids.length) ? c.cliente_ids : [c.cliente_id];
    try {
      const [cat, fact, act] = await Promise.all([
        supabase.from('v_cliente_categorias').select('categoria,lineas,facturas,usd,ultima')
          .in('cliente_id', ids).order('usd', { ascending: false }),
        supabase.from('facturas_sap').select('doc_num,doc_date,total_usd,moneda,pagado,empresa_id')
          .in('cliente_id', ids).order('doc_date', { ascending: false }).limit(6),
        supabase.from('actividades').select('tipo,titulo,descripcion,fecha_actividad')
          .in('cliente_id', ids).order('fecha_actividad', { ascending: false }).limit(4),
      ]);
      setFicha(d => ({ ...d, [k]: {
        categorias: cat?.data || [], facturas: fact?.data || [], actividades: act?.data || [],
        cots: { cargando: !!c.sap_code },
      }}));
    } catch (e) {
      setFicha(d => ({ ...d, [k]: { categorias: [], facturas: [], actividades: [],
        cots: { lista: [], error: 'No se pudo cargar la ficha.' } } }));
      return;
    }

    // Las cotizaciones salen de la base. Se piden aparte igual, para que el
    // resto de la ficha no quede esperandolas. Van TODOS los registros del
    // grupo: el mismo cliente puede estar dos veces y cotizado en las dos.
    try {
      const d = await cotizacionesDe({
        clienteIds: c.cliente_ids && c.cliente_ids.length ? c.cliente_ids : [c.cliente_id],
        sapCode: c.sap_code, empresaId: c.empresa_id || 'honduras',
      });
      setFicha(x => ({ ...x, [k]: { ...x[k], cots: d } }));
    } catch {
      setFicha(x => ({ ...x, [k]: { ...x[k], cots: { lista: [], error: 'No se pudieron leer las cotizaciones.' } } }));
    }
  };

  const verLineasPlan = async (cot) => {
    const llave = `${cot.empresa_id}:${cot.docNum}`;
    if (cotAbierta === llave) { setCotAbierta(null); return; }
    setCotAbierta(llave);
    if (cotLineas[llave]) return;
    setCotLineas(l => ({ ...l, [llave]: { cargando: true } }));
    try {
      const r = await fetch('/api/sap', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'quotation_lines', docNum: cot.docNum, empresa_id: cot.empresa_id }) });
      const d = await r.json();
      setCotLineas(l => ({ ...l, [llave]: { lineas: d.lines || [], moneda: d.moneda, error: d.ok ? null : d.error } }));
    } catch { setCotLineas(l => ({ ...l, [llave]: { lineas: [], error: 'No se pudieron traer las lineas.' } })); }
  };

  const bajarPdfPlan = async (cot) => {
    setBajando(cot.docNum);
    try {
      const r = await fetch('/api/sap', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_pdf', proforma: false, empresa_id: cot.empresa_id, docNum: cot.docNum,
                               emisor: ORG.coti?.[cot.empresa_id] || null }) });
      const d = await r.json();
      if (d.ok && d.base64) {
        const bin = atob(d.base64); const by = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) by[i] = bin.charCodeAt(i);
        const url = URL.createObjectURL(new Blob([by], { type: 'application/pdf' }));
        const a = Object.assign(document.createElement('a'), { href: url, download: `Cotizacion-${cot.docNum}.pdf` });
        document.body.appendChild(a); a.click(); a.remove();
        window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else toast('No se pudo bajar el PDF: ' + (d.error || 'SAP no respondio'));
    } catch { toast('No se pudo bajar el PDF.'); }
    setBajando(null);
  };

  // ── Registrar la gestion ──────────────────────────────────────────────────
  const abrir = (c) => {
    setModal(c);
    setF({ resultado:'', dijo:'', proxima:'', proxima_fecha:'', monto:'',
           frente:(c.frentes||[])[0] || '', tipo:'llamada',
           cellular:c.cellular || '', contacto:c.contacto || '', email:c.email || '' });
  };

  const guardar = async () => {
    if (!modal || !f.resultado || guardando) return;
    setGuardando(true);
    const ahora = new Date().toISOString();

    // TOMAR ES OBLIGATORIO. Si el cliente no tiene dueno, se lo queda quien
    // registra la gestion. Sin esto la lista vuelve a quedar sin dueno en una
    // semana y nadie sabe quien hablo con quien.
    const cambios = {};
    // Un cliente de alguien que renuncio tambien se toma: si no, se registra la
    // gestion, el cliente sigue a nombre del que se fue y al refrescar vuelve a
    // desaparecer de la lista del que acaba de llamarlo.
    if (!modal.vendedor || esHuerfano(modal)) {
      cambios.vendedor = usuario.nombre;
      cambios.vendedor_anterior = modal.vendedor || modal.vendedor_anterior || null;
      cambios.vendedor_manual_at = ahora;
      cambios.vendedor_manual_por = usuario.nombre;
    }
    // Lo que el vendedor corrigio en la llamada. Solo lo que cambio.
    if (f.cellular.trim() && f.cellular.trim() !== (modal.cellular || '')) cambios.cellular = f.cellular.trim();
    if (f.contacto.trim() && f.contacto.trim() !== (modal.contacto || '')) cambios.contacto = f.contacto.trim();
    if (f.email.trim()    && f.email.trim()    !== (modal.email || ''))    cambios.email    = f.email.trim();
    if (Object.keys(cambios).length) {
      cambios.editado_manual_at = ahora;
      const { error } = await supabase.from('clientes').update(cambios).eq('id', modal.cliente_id);
      if (error) { setGuardando(false); toast('No se pudo guardar el cliente: ' + error.message); return; }
    }

    const { error } = await supabase.from('plan_gestiones').insert({
      periodo: PLAN_PERIODO, cliente_id: modal.cliente_id, vendedor: usuario.nombre,
      frente: f.frente || null, tipo: f.tipo, resultado: f.resultado,
      dijo: f.dijo.trim() || null, proxima: f.proxima.trim() || null,
      proxima_fecha: f.proxima_fecha || null,
      monto_posible: f.monto ? Number(f.monto) : null,
    });
    if (error) { setGuardando(false); toast('No se pudo registrar: ' + error.message); return; }

    // Que quede tambien en el historial del cliente, que es donde lo va a
    // buscar el que abra su ficha dentro de tres meses.
    supabase.from('actividades').insert([{
      cliente_id: modal.cliente_id, tipo: f.tipo === 'visita' ? 'visita' : 'llamada',
      titulo: `Plan octubre — ${PLAN_RESULTADOS.find(r=>r.v===f.resultado)?.l || f.resultado}`,
      descripcion: [f.dijo.trim(), f.proxima.trim() && `Sigue: ${f.proxima.trim()}`].filter(Boolean).join(' · ') || null,
      fecha_actividad: ahora, completada: true,
      requiere_seguimiento: !!f.proxima_fecha, fecha_seguimiento: f.proxima_fecha || null,
    }]).then(()=>{},()=>{});

    setGuardando(false); setModal(null);
    toast(`${modal.empresa.slice(0,32)} — registrado${cambios.vendedor ? ' y tomado' : ''}.`);
    cargar();
  };

  const Chip = ({ on, onClick, children }) => (
    <button onClick={onClick} className="radar-btn" style={{ padding:'5px 12px', borderRadius:R.pill,
      border:`1px solid ${on?C.primaryBorder:C.border}`, background:on?C.primaryTint:C.bg2,
      color:on?C.blue:C.t3, fontSize:12, fontWeight:700, cursor:'pointer' }}>{children}</button>
  );

  if (cargando) return <div style={{ padding:40, color:C.t4, fontSize:13 }}>Cargando el plan...</div>;

  const tot = (filas||[]).length;
  const hechos = (filas||[]).filter(x => x.gestiones > 0).length;

  return (
    <div style={{ flex:1, overflowY:'auto', background:C.bg1 }}>
      <div style={{ padding:'18px 24px 0' }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:12, flexWrap:'wrap', marginBottom:4 }}>
          <h2 style={{ margin:0, fontSize:19, fontWeight:800, color:C.t1 }}>Plan de acción — Octubre</h2>
          <span style={{ fontSize:12.5, color:C.t3 }}>
            {hechos} de {tot} clientes gestionados · {Math.round(100*hechos/Math.max(tot,1))}%
          </span>
        </div>
        <div style={{ fontSize:12, color:C.t4, marginBottom:12 }}>
          Una sola lista. Ordenada por lo que cada cliente facturó en los últimos 36 meses.
        </div>
        <div style={{ display:'inline-flex', gap:2, background:C.bg3, border:`1px solid ${C.border}`,
          borderRadius:R.md, padding:2, marginBottom:14 }}>
          {[['lista','Mi lista'],['tablero','Tablero']].map(([id,l]) => (
            <button key={id} onClick={()=>setTab(id)} style={{ height:28, padding:'0 16px', border:'none',
              borderRadius:R.sm, background:tab===id?C.bg2:'transparent', color:tab===id?C.t1:C.t3,
              fontSize:12.5, fontWeight:700, cursor:'pointer' }}>{l}</button>
          ))}
        </div>
      </div>

      {tab === 'lista' ? (
        <div style={{ padding:'0 24px 28px' }}>
          {/* FILTROS. Las categorías son filtro, no pestañas: el mismo cliente
              puede estar en dos frentes y con pestañas aparecería dos veces. */}
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
            <input className="radar-input" value={busq} onChange={e=>setBusq(e.target.value)}
              placeholder="Buscar cliente, contacto o código SAP"
              style={{ flex:'1 1 240px', minWidth:200, background:C.bg2, border:`1px solid ${C.border}`,
                borderRadius:R.md, padding:'7px 12px', fontSize:13, color:C.t1 }}/>
            <Chip on={!fFrente} onClick={()=>setFFrente('')}>Todos los frentes</Chip>
            {PLAN_FRENTES.map(fr => <Chip key={fr} on={fFrente===fr} onClick={()=>setFFrente(fFrente===fr?'':fr)}>{fr}</Chip>)}
          </div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
            <Chip on={!fPrio} onClick={()=>setFPrio('')}>Toda prioridad</Chip>
            {['A','B','C','D'].map(p => (
              <Chip key={p} on={fPrio===p} onClick={()=>setFPrio(fPrio===p?'':p)}>
                <span style={{ color:PRIO_COLOR[p] }}>●</span> {p}
              </Chip>
            ))}
            <span style={{ width:10 }}/>
            <Chip on={fDuenio==='pendientes'} onClick={()=>setFDuenio('pendientes')}>Míos y libres</Chip>
            <Chip on={fDuenio==='mios'}       onClick={()=>setFDuenio('mios')}>Solo míos</Chip>
            <Chip on={fDuenio==='libres'}     onClick={()=>setFDuenio('libres')}>Solo libres</Chip>
            <Chip on={fDuenio==='todos'}      onClick={()=>setFDuenio('todos')}>Todos</Chip>
            <Chip on={fDuenio==='huerfanos'}  onClick={()=>setFDuenio('huerfanos')}>De los que ya no están</Chip>
            <select value={fVend} onChange={e=>setFVend(e.target.value)}
              title="Ver lo que agarro un vendedor"
              style={{ background:fVend?C.primary:C.bg2, color:fVend?'#fff':C.t2,
                border:`1px solid ${fVend?C.primary:C.border}`, borderRadius:999,
                padding:'5px 10px', fontSize:12, fontWeight:700, cursor:'pointer', outline:'none' }}>
              <option value="">Quien lo agarro…</option>
              {porVendedor.map(([v, n]) => <option key={v} value={v}>{v} ({n})</option>)}
              {porVendedor.length === 0 && <option disabled>nadie ha tomado clientes</option>}
            </select>
            <span style={{ width:10 }}/>
            <Chip on={fEstado==='sin_gestionar'} onClick={()=>setFEstado('sin_gestionar')}>Sin tocar</Chip>
            <Chip on={fEstado==='gestionados'}   onClick={()=>setFEstado('gestionados')}>Ya gestionados</Chip>
            <Chip on={fEstado==='todos'}         onClick={()=>setFEstado('todos')}>Todo</Chip>
            <span style={{ width:10 }}/>
            <Chip on={fTipo==='contrato'} onClick={()=>setFTipo(fTipo==='contrato'?'':'contrato')}>De contrato</Chip>
            <Chip on={fTipo==='renovar'}  onClick={()=>setFTipo(fTipo==='renovar'?'':'renovar')}>Toca renovar</Chip>
          </div>

          <div style={{ fontSize:11.5, color:C.t4, marginBottom:8 }}>
            {vista.length} cliente{vista.length===1?'':'s'}
            {vista.length > verMas && ` · mostrando los ${verMas} de mayor compra`}
          </div>

          <div style={{ display:'grid', gap:5 }}>
            {vista.slice(0, verMas).map(c => {
              const huerfano = esHuerfano(c);
              const libre = !c.vendedor || huerfano;
              const mio = c.vendedor === usuario?.nombre;
              const tel = c.cellular || c.telefono;
              const res = PLAN_RESULTADOS.find(r => r.v === c.ultimo_resultado);
              const abiertaEsta = abierto === c.cliente_id;
              const fx = ficha[c.cliente_id] || {};
              return (
                <Fragment key={c.cliente_id}>
                <div style={{ background:C.bg2, border:`1px solid ${c.gestiones?C.border:C.border2}`,
                  borderLeft:`3px solid ${PRIO_COLOR[c.prioridad]}`,
                  borderRadius: abiertaEsta ? `${R.md}px ${R.md}px 0 0` : R.md, padding:'9px 12px',
                  display:'flex', gap:10, alignItems:'center', flexWrap:'wrap', opacity:c.gestiones?0.72:1 }}>

                  <div onClick={()=>abrirFicha(c)} title="Ver qué compró, qué se le cotizó y qué se le dijo"
                    style={{ flex:'1 1 230px', minWidth:0, cursor:'pointer' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.t1, overflow:'hidden',
                      textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      <span style={{ color:C.t4, marginRight:5, display:'inline-block',
                        transform: abiertaEsta ? 'rotate(90deg)' : 'none', transition:'transform .15s' }}>▸</span>
                      {c.empresa}
                      {c.registros_duplicados > 1 && (
                        <span title={`Este cliente tiene ${c.registros_duplicados} registros en la base; los números están sumados`}
                          style={{ marginLeft:6, fontSize:9, fontWeight:800, padding:'1px 5px', borderRadius:4,
                            background:C.bg3, color:C.t4 }}>{c.registros_duplicados} reg</span>
                      )}
                    </div>
                    <div style={{ fontSize:11, color:C.t4, marginTop:1, overflow:'hidden',
                      textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {[c.contacto, c.sap_code, c.ciudad].filter(Boolean).join(' · ') || 'sin contacto'}
                    </div>
                  </div>

                  <div style={{ minWidth:116, display:'flex', gap:4, flexWrap:'wrap' }}>
                    {(c.frentes||[]).map(fr => (
                      <span key={fr} style={{ fontSize:9.5, fontWeight:800, padding:'2px 6px', borderRadius:4,
                        background:C.bg3, color:C.t3, letterSpacing:'.03em' }}>{PLAN_FRENTE_CORTO[fr] || fr.slice(0,4)}</span>
                    ))}
                  </div>

                  <div style={{ minWidth:124, textAlign:'right' }}>
                    <div className="radar-num" style={{ fontSize:13, fontWeight:800,
                      color: (c.usd_facturado||0) > 0 ? C.t1 : C.t4 }}>
                      {(c.usd_facturado||0) > 0
                        ? `$${Number(c.usd_facturado).toLocaleString()}`
                        : 'nunca compró'}
                    </div>
                    {c.es_contrato ? (
                      <div style={{ fontSize:10, fontWeight:700,
                        color: c.dias_sin_renta > 120 ? C.redText : C.blue }}>
                        contrato · {c.dias_sin_renta > 120 ? 'toca renovar' : `facturó hace ${c.dias_sin_renta}d`}
                      </div>
                    ) : (
                      <div style={{ fontSize:10, color:C.t4 }}>
                        {c.ultima_compra ? `hace ${c.dias_sin_comprar} días` : '—'}
                      </div>
                    )}
                  </div>

                  <div style={{ minWidth:128, fontSize:11.5 }}>
                    {libre
                      ? <>
                          <span style={{ color:C.orangeText, fontWeight:700 }}>libre — tomalo</span>
                          {/* No es lo mismo un cliente que nunca tuvo vendedor que
                              uno que quedo suelto porque el suyo renuncio. */}
                          {huerfano && (
                            <div style={{ fontSize:10, color:C.t4 }}>era de {String(c.vendedor).split(' ')[0]}</div>
                          )}
                        </>
                      : <span style={{ color: mio ? C.blue : C.t3, fontWeight: mio ? 700 : 500 }}>
                          {mio ? 'tuyo' : c.vendedor}
                        </span>}
                    {(c.faltan||[]).length > 0 && (
                      <div style={{ fontSize:10, color:C.orangeText }} title="Datos que se pueden pedir en la llamada">
                        falta {c.faltan.join(', ')}
                      </div>
                    )}
                  </div>

                  <div style={{ minWidth:150, fontSize:11 }}>
                    {res ? (
                      <>
                        <div style={{ fontWeight:700, color: res.tono==='green'?C.greenText : res.tono==='red'?C.redText : C.t3 }}>
                          {res.l}
                        </div>
                        {c.ultimo_dijo && <div style={{ color:C.t4, overflow:'hidden', textOverflow:'ellipsis',
                          whiteSpace:'nowrap' }} title={c.ultimo_dijo}>"{c.ultimo_dijo}"</div>}
                      </>
                    ) : <span style={{ color:C.t4 }}>sin gestionar</span>}
                  </div>

                  <div style={{ display:'flex', gap:6, marginLeft:'auto', alignItems:'center' }}>
                    {/* EL NUMERO A LA VISTA. Un icono obliga a abrir la ficha
                        para saber a donde se esta llamando; el numero escrito
                        se marca de una, y en el telefono es un toque. */}
                    {tel ? (
                      <a href={`tel:${String(tel).replace(/[^0-9+]/g,'')}`} title={`Llamar a ${tel}`}
                        className="radar-num"
                        style={{ display:'inline-flex', alignItems:'center', gap:6, height:30, padding:'0 11px',
                          borderRadius:8, background:C.bg1, border:`1px solid ${C.border}`, color:C.t1,
                          fontSize:12.5, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap' }}>
                        <CIcon d={CI.phone} size={13}/>{tel}
                      </a>
                    ) : (
                      <span style={{ fontSize:11.5, color:C.orangeText, fontWeight:700, whiteSpace:'nowrap' }}>
                        sin teléfono
                      </span>
                    )}
                    <button onClick={()=>abrir(c)} className="radar-btn"
                      style={{ ...BTN_PRIMARY, padding:'0 12px', height:30, fontSize:11.5,
                        background: libre ? C.primary : C.bg3, color: libre ? '#fff' : C.t1 }}>
                      {libre ? 'Tomar y registrar' : 'Registrar'}
                    </button>
                  </div>
                </div>

                {abiertaEsta && (
                  <div style={{ background:C.bg1, border:`1px solid ${C.border}`, borderTop:'none',
                    borderRadius:`0 0 ${R.md}px ${R.md}px`, padding:'12px 14px 14px', marginBottom:3 }}>
                    {fx.cargando ? <div style={{ fontSize:12, color:C.t4 }}>Cargando...</div> : (<>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(215px,1fr))', gap:16 }}>

                        <div>
                          <div style={{ fontSize:10, fontWeight:800, letterSpacing:'.06em',
                            textTransform:'uppercase', color:C.t4, marginBottom:7 }}>Qué compra</div>
                          {(fx.categorias||[]).length ? (
                            <div style={{ display:'grid', gap:5 }}>
                              {fx.categorias.slice(0,6).map((x,i)=>(
                                <div key={i} style={{ display:'flex', justifyContent:'space-between', gap:8, fontSize:11.5 }}>
                                  <span style={{ color:C.t2 }}>{x.categoria}</span>
                                  <span className="radar-num" style={{ color:C.t3, whiteSpace:'nowrap' }}>
                                    ${Math.round(x.usd||0).toLocaleString()} · {String(x.ultima||'').slice(0,7)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : <div style={{ fontSize:11.5, color:C.t4 }}>Sin compras registradas.</div>}
                        </div>

                        <div>
                          <div style={{ fontSize:10, fontWeight:800, letterSpacing:'.06em',
                            textTransform:'uppercase', color:C.t4, marginBottom:7 }}>Últimas facturas</div>
                          {(fx.facturas||[]).length ? (
                            <div style={{ display:'grid', gap:5 }}>
                              {fx.facturas.map((x,i)=>(
                                <div key={i} style={{ display:'flex', justifyContent:'space-between', gap:8, fontSize:11.5 }}>
                                  <span style={{ color:C.t3 }}>
                                    #{x.doc_num} · {String(x.doc_date||'').slice(0,10)}
                                  </span>
                                  <span className="radar-num" style={{ color: x.pagado===false?C.orangeText:C.t2, fontWeight:600 }}>
                                    ${Math.round(x.total_usd||0).toLocaleString()}{x.pagado===false?' ·debe':''}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : <div style={{ fontSize:11.5, color:C.t4 }}>Sin facturas.</div>}
                        </div>

                        <div>
                          <div style={{ fontSize:10, fontWeight:800, letterSpacing:'.06em',
                            textTransform:'uppercase', color:C.t4, marginBottom:7 }}>Datos del cliente</div>
                          <div style={{ display:'grid', gap:3, fontSize:11.5, color:C.t2 }}>
                            <div>{c.contacto || <span style={{color:C.orangeText}}>sin contacto</span>}</div>
                            <div className="radar-num">{c.cellular || c.telefono || <span style={{color:C.orangeText}}>sin teléfono</span>}</div>
                            <div style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{c.email || <span style={{color:C.t4}}>sin correo</span>}</div>
                            <div style={{ color:C.t4 }}>{[c.ciudad, c.sap_code].filter(Boolean).join(' · ')}</div>
                            {c.rtn_relleno && <div style={{ color:C.t4, fontSize:10.5 }}>RTN en relleno (viene así de SAP)</div>}
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize:10, fontWeight:800, letterSpacing:'.06em',
                            textTransform:'uppercase', color:C.t4, marginBottom:7 }}>Último contacto</div>
                          {(fx.actividades||[]).length ? (
                            <div style={{ display:'grid', gap:6 }}>
                              {fx.actividades.map((a,i)=>(
                                <div key={i} style={{ fontSize:11.5 }}>
                                  <div style={{ color:C.t3 }}>{String(a.fecha_actividad||'').slice(0,10)} · {a.tipo}</div>
                                  {a.descripcion && <div style={{ color:C.t2 }}>{a.descripcion.slice(0,110)}</div>}
                                </div>
                              ))}
                            </div>
                          ) : <div style={{ fontSize:11.5, color:C.t4 }}>Nadie le ha hablado.</div>}
                        </div>
                      </div>

                      {/* COTIZACIONES, en vivo contra SAP */}
                      <div style={{ marginTop:14, borderTop:`1px solid ${C.border}`, paddingTop:11 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:7 }}>
                          <div style={{ fontSize:10, fontWeight:800, letterSpacing:'.06em',
                            textTransform:'uppercase', color:C.t4 }}>Cotizaciones</div>
                          {fx.cots?.cargando && <span style={{ fontSize:10.5, color:C.t4 }}>consultando SAP...</span>}
                        </div>
                        {!c.sap_code ? (
                          <div style={{ fontSize:11.5, color:C.t4 }}>Sin código de SAP: no hay cotizaciones que buscar.</div>
                        ) : fx.cots?.error ? (
                          <div style={{ fontSize:11.5, color:C.orangeText }}>{fx.cots.error}</div>
                        ) : (fx.cots?.lista||[]).length === 0 && !fx.cots?.cargando ? (
                          <div style={{ fontSize:11.5, color:C.t4 }}>Sin cotizaciones en 24 meses.</div>
                        ) : (
                          <div style={{ display:'grid', gap:4 }}>
                            {(fx.cots?.lista||[]).map(q => {
                              const llave = `${q.empresa_id}:${q.docNum}`;
                              const ln = cotLineas[llave] || {};
                              return (
                                <div key={llave} style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:R.md }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 10px', flexWrap:'wrap' }}>
                                    <span className="radar-num" style={{ fontSize:11.5, fontWeight:700, color:C.t1, minWidth:56 }}>#{q.docNum}</span>
                                    <span style={{ fontSize:11.5, color:C.t3, minWidth:78 }}>{q.fecha}</span>
                                    <span className="radar-num" style={{ fontSize:11.5, fontWeight:700, color:C.t1 }}>
                                      {q.moneda==='USD'?'$':'L'}{Math.round(q.total).toLocaleString()}
                                    </span>
                                    <span style={{ fontSize:9.5, fontWeight:700, padding:'2px 6px', borderRadius:999,
                                      background:q.estado==='abierta'?C.greenTint:C.bg3,
                                      color:q.estado==='abierta'?C.greenText:C.t4 }}>{q.estado}</span>
                                    <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
                                      <button onClick={()=>verLineasPlan(q)} style={{ fontFamily:'inherit', fontSize:11,
                                        fontWeight:700, padding:'3px 9px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                                        background:C.bg1, color:C.t2, cursor:'pointer' }}>
                                        {cotAbierta===llave?'Ocultar':'Qué pidió'}
                                      </button>
                                      <button onClick={()=>bajarPdfPlan(q)} disabled={bajando===q.docNum}
                                        style={{ fontFamily:'inherit', fontSize:11, fontWeight:700, padding:'3px 9px',
                                          borderRadius:R.sm, border:`1px solid ${C.primaryBorder}`, background:C.primaryTint,
                                          color:C.blue, cursor: bajando===q.docNum?'wait':'pointer' }}>
                                        {bajando===q.docNum?'...':'PDF'}
                                      </button>
                                    </div>
                                  </div>
                                  {cotAbierta===llave && (
                                    <div style={{ borderTop:`1px solid ${C.border}`, padding:'7px 10px', background:C.bg1 }}>
                                      {ln.cargando ? <div style={{fontSize:11.5,color:C.t4}}>Trayendo las líneas...</div>
                                       : ln.error ? <div style={{fontSize:11.5,color:C.redText}}>{ln.error}</div>
                                       : (ln.lineas||[]).map((l,i)=>(
                                        <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 50px 88px',
                                          gap:8, fontSize:11.5, alignItems:'baseline' }}>
                                          <span style={{ color:C.t2 }}>{l.ItemDescription || l.ItemCode}</span>
                                          <span className="radar-num" style={{ color:C.t4, textAlign:'right' }}>{l.Quantity}</span>
                                          <span className="radar-num" style={{ color:C.t1, textAlign:'right', fontWeight:600 }}>
                                            {ln.moneda==='USD'?'$':'L'}{Number(l.lineTotal||0).toLocaleString(undefined,{maximumFractionDigits:0})}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>)}
                  </div>
                )}
                </Fragment>
              );
            })}
          </div>

          {vista.length > verMas && (
            <button onClick={()=>setVerMas(v => v + 60)} className="radar-btn"
              style={{ marginTop:12, ...BTN_PRIMARY, background:C.bg3, color:C.t1 }}>
              Ver {Math.min(60, vista.length - verMas)} más
            </button>
          )}
          {!vista.length && (
            <div style={{ textAlign:'center', padding:'40px 20px', color:C.t3, fontSize:13 }}>
              Nada con esos filtros. Probá con "Todo" o quitá la prioridad.
            </div>
          )}
        </div>
      ) : (
        <PlanTablero usuario={usuario} metas={metas} visMeta={visMeta} resumen={resumen}
          metaDe={metaDe} equipo={equipo} filas={filas||[]} gest={gest}/>
      )}

      {/* REGISTRAR LA GESTION. Tomar va adentro: no se puede registrar sin
          quedarse el cliente. */}
      <Modal open={!!modal} onClose={()=>!guardando && setModal(null)}
        title={modal ? modal.empresa.slice(0, 46) : ''} width={520}>
        {modal && (
          <div style={{ display:'grid', gap:11 }}>
            {!modal.vendedor && (
              <div style={{ background:C.primaryTint, border:`1px solid ${C.primaryBorder}`, borderRadius:R.md,
                padding:'8px 11px', fontSize:12, color:C.blue }}>
                Este cliente no tiene dueño. Al guardar <b>queda tuyo</b>.
              </div>
            )}
            <div style={{ fontSize:11.5, color:C.t3 }}>
              {modal.es_contrato
                ? <>Cuenta de <b style={{color:C.blue}}>contrato</b>: facturó <b className="radar-num" style={{color:C.t1}}>${Number(modal.usd_renta).toLocaleString()}</b> de renta en 18 meses,
                    última hace {modal.dias_sin_renta} días.
                    {modal.dias_sin_renta > 120 && <b style={{color:C.redText}}> Toca renovar.</b>}</>
                : modal.usd_facturado > 0
                ? <>Facturó <b className="radar-num" style={{color:C.t1}}>${Number(modal.usd_facturado).toLocaleString()}</b> en 36 meses · última hace {modal.dias_sin_comprar} días</>
                : <>Nunca le hemos facturado en 24 meses.</>}
              {modal.frentes_txt && <> · {modal.frentes_txt}</>}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <Sel label="Qué pasó *" value={f.resultado} onChange={v=>setF(x=>({...x,resultado:v}))}
                required options={PLAN_RESULTADOS.map(r=>({ v:r.v, l:r.l }))}/>
              <Sel label="Frente" value={f.frente} onChange={v=>setF(x=>({...x,frente:v}))}
                options={PLAN_FRENTES.map(fr=>({ v:fr, l:fr }))}/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <Sel label="Cómo fue" value={f.tipo} onChange={v=>setF(x=>({...x,tipo:v}))}
                options={[{v:'llamada',l:'Llamada'},{v:'visita',l:'Visita'},{v:'whatsapp',l:'WhatsApp'},{v:'correo',l:'Correo'}]}/>
              <Inp label="Monto posible (USD)" type="number" value={f.monto}
                onChange={v=>setF(x=>({...x,monto:v}))} placeholder="opcional"/>
            </div>

            <Inp label="Qué dijo el cliente" value={f.dijo} onChange={v=>setF(x=>({...x,dijo:v}))} rows={2}
              placeholder="textual, lo que dijo: necesita precio de 4 llantas, pide visita, cambió de proveedor..."/>

            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:10 }}>
              <Inp label="Qué sigue" value={f.proxima} onChange={v=>setF(x=>({...x,proxima:v}))}
                placeholder="mandar cotización, visitarlo, volver a llamar"/>
              <Inp label="Cuándo" type="date" value={f.proxima_fecha} onChange={v=>setF(x=>({...x,proxima_fecha:v}))}/>
            </div>

            {/* ARREGLAR EL DATO. Solo lo que se consigue hablando. El RTN no va
                aca: son 496 clientes con relleno y viene asi desde SAP, no es
                algo que se resuelva por telefono. */}
            {(modal.faltan||[]).length > 0 && (
              <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:10 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.t4, textTransform:'uppercase',
                  letterSpacing:'.07em', marginBottom:7 }}>
                  Ya que lo tenés al teléfono — falta {modal.faltan.join(', ')}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:10 }}>
                  {modal.faltan.includes('celular') || modal.faltan.includes('telefono')
                    ? <Inp label="Celular" value={f.cellular} onChange={v=>setF(x=>({...x,cellular:v}))} placeholder="9999-9999"/> : null}
                  {modal.faltan.includes('contacto')
                    ? <Inp label="Con quién hablaste" value={f.contacto} onChange={v=>setF(x=>({...x,contacto:v}))}/> : null}
                  {modal.faltan.includes('correo')
                    ? <Inp label="Correo" value={f.email} onChange={v=>setF(x=>({...x,email:v}))} placeholder="para mandarle la cotización"/> : null}
                </div>
              </div>
            )}

            {modal.rtn_relleno && (
              <div style={{ fontSize:11, color:C.t4 }}>
                Nota: su RTN en SAP está en relleno. No hace falta pedirlo en la llamada — va aparte.
              </div>
            )}

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn variant="secondary" onClick={()=>setModal(null)} disabled={guardando}>Cancelar</Btn>
              <Btn onClick={guardar} disabled={!f.resultado || guardando}>
                {guardando ? 'Guardando...' : (!modal.vendedor ? 'Tomar y guardar' : 'Guardar')}
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── EL TABLERO DEL PLAN ─────────────────────────────────────────────────────
// Lo que se mira el lunes a las 8:30 y lo que se manda por WhatsApp todos los
// dias: quien tomo que, cuantos clientes se tocaron hoy, esta semana y en el
// mes, y quien va atrasado en visitas.
function PlanTablero({ usuario, metas, visMeta, resumen, metaDe, equipo, filas, gest }) {
  const gente = useMemo(() => {
    const s = new Set([...metas.map(m=>m.vendedor), ...visMeta.map(v=>v.persona), ...Object.keys(resumen)]);
    return [...s].filter(Boolean).sort();
  }, [metas, visMeta, resumen]);

  const totLlam = Object.values(resumen).reduce((a,p)=>a+p.mes, 0);
  const totMetaLlam = metas.reduce((a,m)=>a+m.meta_llamadas, 0);
  const totCierres = Object.values(resumen).reduce((a,p)=>a+p.compro, 0);
  const totMetaCierres = metas.reduce((a,m)=>a+m.meta_cierres, 0);
  const tocados = filas.filter(x=>x.gestiones>0).length;

  const semaforo = (real, meta) => {
    if (!meta) return { c:C.t4, t:'sin meta' };
    const p = real / meta;
    if (p >= 0.9) return { c:C.green,  t:`${Math.round(p*100)}%` };
    if (p >= 0.6) return { c:C.orange, t:`${Math.round(p*100)}%` };
    return { c:C.red, t:`${Math.round(p*100)}%` };
  };

  const Caja = ({ t, v, s, color }) => (
    <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:R.lg, padding:'13px 16px', minWidth:150 }}>
      <div style={{ fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'.07em' }}>{t}</div>
      <div className="radar-num" style={{ fontSize:25, fontWeight:900, color:color||C.t1, marginTop:3 }}>{v}</div>
      {s && <div style={{ fontSize:11, color:C.t4 }}>{s}</div>}
    </div>
  );

  // Por frente, para ver cual se esta quedando atras.
  const porFrente = useMemo(() => {
    const m = {};
    PLAN_FRENTES.forEach(fr => { m[fr] = { meta:0, real:0, clientes:0, tocados:0 }; });
    metas.forEach(x => { if (m[x.frente]) m[x.frente].meta += x.meta_llamadas; });
    gest.forEach(g => { if (g.frente && m[g.frente]) m[g.frente].real += 1; });
    filas.forEach(c => (c.frentes||[]).forEach(fr => {
      if (!m[fr]) return; m[fr].clientes += 1; if (c.gestiones) m[fr].tocados += 1;
    }));
    return m;
  }, [metas, gest, filas]);

  return (
    <div style={{ padding:'0 24px 28px' }}>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:18 }}>
        <Caja t="Clientes tocados" v={`${tocados}/${filas.length}`}
          s={`${Math.round(100*tocados/Math.max(filas.length,1))}% de la lista`}
          color={tocados/Math.max(filas.length,1) >= 0.5 ? C.green : C.orange}/>
        <Caja t="Gestiones del mes" v={totLlam} s={`meta ${totMetaLlam}`}
          color={semaforo(totLlam, totMetaLlam).c}/>
        <Caja t="Hoy" v={Object.values(resumen).reduce((a,p)=>a+p.hoy,0)} s="llamadas de todo el equipo"/>
        <Caja t="Esta semana" v={Object.values(resumen).reduce((a,p)=>a+p.sem,0)} s="llamadas"/>
        <Caja t="Cierres" v={totCierres} s={`meta ${totMetaCierres}`} color={semaforo(totCierres, totMetaCierres).c}/>
      </div>

      {/* POR PERSONA */}
      <div style={{ fontSize:11, fontWeight:800, color:C.t4, textTransform:'uppercase',
        letterSpacing:'.07em', marginBottom:8 }}>Por persona</div>
      <div style={{ overflowX:'auto', marginBottom:22 }}>
        <table style={{ width:'100%', minWidth:720, borderCollapse:'collapse', fontSize:12.5 }}>
          <thead>
            <tr style={{ fontSize:10, textTransform:'uppercase', color:C.t4, letterSpacing:'.05em' }}>
              {['Persona','Hoy','Semana','Mes','Meta mes','%','Visitas sem.','Mín.','Cotiz.','Cerró'].map((h,i)=>(
                <th key={h} style={{ textAlign:i?'right':'left', padding:'6px 10px', borderBottom:`1px solid ${C.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gente.map(p => {
              const r = resumen[p] || { hoy:0, sem:0, mes:0, vis_sem:0, cotiz:0, compro:0 };
              const mm = metaDe(p);
              const sf = semaforo(r.mes, mm.llamadas);
              const minVis = (visMeta.find(v=>v.persona===p)||{}).min_semana || 0;
              const visOk = !minVis || r.vis_sem >= minVis;
              const yo = p === usuario?.nombre;
              return (
                <tr key={p} style={{ background: yo ? C.primaryTint : 'transparent' }}>
                  <td style={{ padding:'7px 10px', fontWeight: yo?800:600, color:C.t1,
                    borderBottom:`1px solid ${C.border}` }}>{p}{yo && ' (vos)'}</td>
                  <td style={{ textAlign:'right', padding:'7px 10px', borderBottom:`1px solid ${C.border}` }} className="radar-num">{r.hoy}</td>
                  <td style={{ textAlign:'right', padding:'7px 10px', borderBottom:`1px solid ${C.border}` }} className="radar-num">{r.sem}</td>
                  <td style={{ textAlign:'right', padding:'7px 10px', fontWeight:700, borderBottom:`1px solid ${C.border}` }} className="radar-num">{r.mes}</td>
                  <td style={{ textAlign:'right', padding:'7px 10px', color:C.t4, borderBottom:`1px solid ${C.border}` }} className="radar-num">{mm.llamadas||'—'}</td>
                  <td style={{ textAlign:'right', padding:'7px 10px', fontWeight:800, color:sf.c, borderBottom:`1px solid ${C.border}` }}>{sf.t}</td>
                  <td style={{ textAlign:'right', padding:'7px 10px', fontWeight:700,
                    color: visOk ? C.greenText : C.redText, borderBottom:`1px solid ${C.border}` }} className="radar-num">{r.vis_sem}</td>
                  <td style={{ textAlign:'right', padding:'7px 10px', color:C.t4, borderBottom:`1px solid ${C.border}` }} className="radar-num">{minVis||'—'}</td>
                  <td style={{ textAlign:'right', padding:'7px 10px', borderBottom:`1px solid ${C.border}` }} className="radar-num">{r.cotiz}</td>
                  <td style={{ textAlign:'right', padding:'7px 10px', fontWeight:700, color:C.greenText, borderBottom:`1px solid ${C.border}` }} className="radar-num">{r.compro}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* POR FRENTE */}
      <div style={{ fontSize:11, fontWeight:800, color:C.t4, textTransform:'uppercase',
        letterSpacing:'.07em', marginBottom:8 }}>Por frente</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))', gap:10, marginBottom:22 }}>
        {PLAN_FRENTES.map(fr => {
          const d = porFrente[fr]; const sf = semaforo(d.real, d.meta);
          return (
            <div key={fr} style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:R.lg, padding:'12px 15px' }}>
              <div style={{ fontSize:11.5, fontWeight:800, color:C.t2, marginBottom:6 }}>{fr}</div>
              <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
                <span className="radar-num" style={{ fontSize:22, fontWeight:900, color:sf.c }}>{d.real}</span>
                <span style={{ fontSize:12, color:C.t4 }}>{d.meta ? `de ${d.meta} llamadas` : 'llamadas · sin meta este mes'}</span>
              </div>
              <div style={{ fontSize:11, color:C.t4, marginTop:3 }}>
                {d.tocados} de {d.clientes} clientes tocados
              </div>
              <div style={{ height:5, background:C.bg3, borderRadius:3, marginTop:8, overflow:'hidden' }}>
                <div style={{ width:`${Math.min(100, 100*d.real/Math.max(d.meta,1))}%`, height:'100%', background:sf.c }}/>
              </div>
            </div>
          );
        })}
      </div>

      {/* QUIEN TOMO QUE — lo ultimo que paso, en orden */}
      <div style={{ fontSize:11, fontWeight:800, color:C.t4, textTransform:'uppercase',
        letterSpacing:'.07em', marginBottom:8 }}>Lo último que pasó</div>
      <div style={{ display:'grid', gap:4 }}>
        {gest.slice(0, 25).map((g,i) => {
          const c = filas.find(x => x.cliente_id === g.cliente_id);
          const res = PLAN_RESULTADOS.find(r => r.v === g.resultado);
          return (
            <div key={i} style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:R.md,
              padding:'7px 11px', display:'flex', gap:10, alignItems:'baseline', fontSize:12 }}>
              <span style={{ color:C.t4, fontSize:11, minWidth:88 }}>
                {new Date(g.created_at).toLocaleString('es-HN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
              </span>
              <span style={{ fontWeight:700, color:C.t2, minWidth:110 }}>{String(g.vendedor).split(' ')[0]}</span>
              <span style={{ flex:1, minWidth:0, color:C.t1, overflow:'hidden', textOverflow:'ellipsis',
                whiteSpace:'nowrap' }}>{c ? c.empresa : 'cliente'}</span>
              <span style={{ fontWeight:700, fontSize:11,
                color: res?.tono==='green'?C.greenText : res?.tono==='red'?C.redText : C.t3 }}>
                {res?.l || g.resultado}
              </span>
            </div>
          );
        })}
        {!gest.length && <div style={{ fontSize:12.5, color:C.t4, padding:'14px 0' }}>
          Todavía nadie ha registrado una gestión este mes.
        </div>}
      </div>
    </div>
  );
}

export default function App() {
  const [usuario, setUsuario]     = useState(null);
  // Recordar el módulo activo: al refrescar la página volvés donde estabas.
  const [mod, setMod] = useState(() => {
    try { const m = localStorage.getItem('radar_mod'); return (m && NAV_META[m]) ? m : 'dashboard'; }
    catch { return 'dashboard'; }
  });
  useEffect(() => { try { localStorage.setItem('radar_mod', mod); } catch {} }, [mod]);
  // Sidebar: grupos colapsables + ocultar toda la barra (página a pantalla completa)
  const [navGrupoAbierto, setNavGrupoAbierto] = useState(null); // id del grupo desplegado
  // Breadcrumb del topbar: los modulos anuncian su sub-vista (ej. CRM -> Pipeline)
  const [subMod, setSubMod] = useState('');
  useEffect(() => {
    const h = e => setSubMod(e.detail || '');
    window.addEventListener('radar-submod', h);
    return () => window.removeEventListener('radar-submod', h);
  }, []);
  useEffect(() => { setSubMod(''); }, [mod]);
  const [sidebarOculto, setSidebarOculto] = useState(() => {
    try { return localStorage.getItem('radar_sidebar_oculto') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('radar_sidebar_oculto', sidebarOculto ? '1' : '0'); } catch {} }, [sidebarOculto]);
  const [crmInit, setCrmInit]     = useState({});
  const [sibilaAcceso, setSibilaAcceso] = useState(null);   // lista de nombres con acceso a Sibila
  useEffect(()=>{ supabase.from('radar_config').select('value').eq('key','sibila_acceso').maybeSingle()
    .then(({data})=>{ if (Array.isArray(data?.value?.usuarios)) setSibilaAcceso(data.value.usuarios); }); },[]);
  const [equipos, setEquipos]     = useState([]);
  const [clientes, setClientes]   = useState([]);
  const [checkIn, setCheckIn]     = useState(false);
  const [alertas, setAlertas]     = useState([]);
  const [notifs, setNotifs]       = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [empresaVista, setEmpresaVista] = useState('all');

  const [sessionChecked, setSessionChecked] = useState(false);
  // Al volver a una pestana que estuvo dormida (o tras suspender la laptop), el token
  // de Supabase ya vencio: el navegador congela los timers en pestanas de fondo, asi que
  // el auto-refresh del SDK no corre. Las consultas volvian 401 y la pantalla quedaba
  // vacia hasta darle refresh 2-3 veces. Aca, al recuperar el foco, se revalida la
  // sesion y se remonta el modulo (via key) para que vuelva a pedir sus datos.
  const [revalTick, setRevalTick] = useState(0);
  useEffect(() => {
    let ocultoDesde = null;
    const onVis = async () => {
      if (document.visibilityState === 'hidden') { ocultoDesde = Date.now(); return; }
      const dormido = ocultoDesde ? Date.now() - ocultoDesde : 0;
      ocultoDesde = null;
      if (dormido < 60000) return;                 // ausencias cortas: no molestar

      // ANTES: cualquier ausencia de mas de un minuto remontaba el modulo
      // entero. Volver despues de cinco minutos te sacaba de la pestana donde
      // estabas -de Clientes a Pipeline-, perdia el scroll, los filtros, y
      // recargaba todo. Y casi siempre para nada: el token de Supabase dura una
      // hora, asi que a los cinco minutos seguia perfectamente valido.
      //
      // AHORA se pregunta primero. getUser() SI sale a la red y valida el token
      // de verdad; getSession() solo lee el que esta guardado, que es por lo que
      // antes no se notaba que estaba vencido.
      let vivo = false;
      try {
        const { data, error } = await supabase.auth.getUser();
        vivo = !error && !!data?.user;
      } catch (_) { vivo = false; }
      if (vivo) return;                            // todo bien: no tocar la pantalla

      // El token murio -esto pasa de verdad tras suspender la laptop-: se
      // renueva y recien ahi se recarga el modulo.
      try {
        const { data } = await supabase.auth.refreshSession();
        if (!data?.session) { setUsuario(null); return; }
      } catch (_) { setUsuario(null); return; }
      setRevalTick(t => t + 1);
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onVis);
    };
  }, []);
  useEffect(()=>{
    supabase.auth.getSession().then(async ({data:{session}})=>{
      if (session) {
        const username = session.user.email.replace('@radar.montasa','');
        const {data} = await supabase.from('radar_usuarios').select('*').eq('usuario',username).eq('activo',true).single();
        if (data) { await cargarOrgConfig(data.org_id); setUsuario(data); }
      }
      setSessionChecked(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' && !session) {
        setUsuario(null);
      }
      if (event === 'TOKEN_REFRESHED' && session) {
        const username = session.user.email.replace('@radar.montasa','');
        const {data} = await supabase.from('radar_usuarios').select('*').eq('usuario',username).eq('activo',true).single();
        if (data) { await cargarOrgConfig(data.org_id); setUsuario(data); }
      }
    });
    return () => subscription.unsubscribe();
  },[]);

  useEffect(()=>{
    if (!usuario) return;
    const eid = (usuario.empresa_id && usuario.empresa_id !== 'all') ? usuario.empresa_id : null;
    const fe = q => eid ? q.eq('empresa_id', eid) : q;
    Promise.all([
      fe(supabase.from('equipos').select('*').order('codigo')),
      // Supabase corta cada consulta en 1,000 filas. Sin paginar, de 5,132
      // clientes solo llegaban los primeros 1,000 en orden alfabetico: cualquier
      // cliente de la D en adelante no existia para el navegador (Proconsumo
      // esta en la posicion 1,527). Ademas se trae sap_code, que es lo que
      // distingue al cliente real de SAP de los duplicados de la importacion
      // vieja.
      (async () => {
        // Supabase corta cada consulta en 1,000 filas, asi que hay que paginar.
        // ANTES se pedia una pagina, se esperaba la respuesta, se pedia la
        // siguiente: con 6,688 clientes son SIETE viajes en fila india y el
        // usuario espera la SUMA de los siete. En una conexion normal eso son
        // tres o cuatro segundos de puro ir y venir, antes de contar lo que
        // tarda en bajar el dato.
        //
        // AHORA la primera pagina viene con el total, y con ese numero se piden
        // todas las demas DE UNA VEZ: se espera el viaje mas lento, no la suma.
        // Es el mismo patron que ya usaba el CRM para las oportunidades.
        const COLS = 'id,empresa,contacto,telefono,email,estado,vendedor,pais,fuente,forma_pago,cargo,industria,ciudad,linea_negocio,ultima_actividad,valor_total_rentas,notas,sap_code';
        const PAGE = 1000;
        const base = (opt) => fe(supabase.from('clientes').select(COLS, opt)).order('empresa');
        const primera = await base({ count: 'exact' }).range(0, PAGE - 1);
        if (primera.error) { console.error('carga de clientes', primera.error); return { data: [] }; }
        const filas = [...(primera.data || [])];
        const total = Math.min(primera.count ?? filas.length, 20000);
        if (total > PAGE) {
          const desdes = [];
          for (let d = PAGE; d < total; d += PAGE) desdes.push(d);
          const paginas = await Promise.all(desdes.map(d => base().range(d, d + PAGE - 1)));
          for (const pg of paginas) {
            if (pg.error) { console.error('carga de clientes', pg.error); continue; }
            if (pg.data) filas.push(...pg.data);
          }
        }
        return { data: filas };
      })(),
    ]).then(([{data:eq},{data:cl}])=>{ setEquipos(eq||[]); setClientes(cl||[]); });

    // Bienvenida del día (saludo + frase + dato según departamento) — 1 vez al día
    {
      const today = new Date().toISOString().split('T')[0];
      if (!localStorage.getItem(`bienvenida_${usuario.id}_${today}`)) setCheckIn(true);
    }

    // Cargar alertas activas
    cargarAlertas(usuario.id);
    // Cargar notificaciones no leídas
    cargarNotifs(usuario.nombre);

  },[usuario]);

  const cargarNotifs = async (nombre) => {
    // Se traen las NO leidas y tambien las leidas de los ultimos 7 dias. Antes
    // solo las no leidas: al marcarlas se borraban de la pantalla y no habia
    // forma de volver a ellas, ni siquiera para saber que habian existido.
    const hace7 = new Date(Date.now() - 7*86400000).toISOString();
    const {data} = await supabase.from('notificaciones').select('*')
      .eq('destinatario',nombre)
      .or(`leida.eq.false,created_at.gte.${hace7}`)
      .order('leida',{ascending:true}).order('created_at',{ascending:false}).limit(30);
    setNotifs(data||[]);
  };

  // Polling cada 60s para notificaciones en tiempo real (sin Supabase Realtime)
  useEffect(() => {
    if (!usuario) return;
    const id = setInterval(() => cargarNotifs(usuario.nombre), 60000);
    return () => clearInterval(id);
  }, [usuario]);

  // Cmd+K / Ctrl+K abre búsqueda global
  useEffect(() => {
    const handler = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Marcar SOLO la que se toco: las demas siguen ahi hasta que alguien las mire.
  const marcarUnaLeida = async (id) => {
    await supabase.from('notificaciones').update({ leida:true, fecha_leida:new Date().toISOString() }).eq('id', id);
    setNotifs(ns => ns.map(n => n.id === id ? {...n, leida:true} : n));
  };

  const marcarLeidasNotifs = async () => {
    if (!notifs.length) return;
    const ids = notifs.filter(n=>!n.leida).map(n=>n.id);
    if (!ids.length) return;
    await supabase.from('notificaciones').update({leida:true, fecha_leida:new Date().toISOString()}).in('id',ids);
    setNotifs(ns => ns.map(n => ({...n, leida:true})));
  };

  const cargarAlertas = async (uid) => {
    const today = new Date(); today.setDate(today.getDate()-1);
    const {data} = await supabase.from('actividades').select('*').eq('tipo','alerta').gte('created_at',today.toISOString()).order('created_at',{ascending:false}).limit(20);
    setAlertas(data||[]);
  };

  const login  = async u => { await cargarOrgConfig(u?.org_id); setUsuario(u); };
  const logout = async () => { await supabase.auth.signOut(); setUsuario(null); };

  if (!sessionChecked) return null;
  if (!usuario) return <Login onLogin={login}/>;

  // Conteos para badges en nav
  const urgSeg = alertas.filter(a=>a.titulo?.includes('Seguimiento')).length;

  // Badge counts — el conteo de "Sin atender" (ex clientes_inactivos) ahora vive
  // como tab del CRM, así que su badge se muestra sobre 'crm'.
  const NAV_BADGES = { agenda: urgSeg };

  // Merge modulos_extra into nav groups (mapa IA: grupos nuevos)
  const MOD_GROUP = {
    plan:'g_comercial', crm:'g_comercial', agenda:'g_comercial', mensajes:'g_comercial', tickets:'g_comercial',
    cobros:'g_finanzas', metas:'g_analisis',
    flota:'g_operaciones', flota_cliente:'g_operaciones', taller:'g_operaciones', rentas:'g_operaciones', viaticos:'g_operaciones',
    inventario:'g_inventario',
    reportes:'g_analisis', forecast:'g_analisis',
    usuarios:'g_admin', sap:'g_admin',
  };
  // Los modulos_extra guardados con ids viejos se traducen a su hub (las tabs
  // internas del hub siguen gateadas: NEXA por puedeSibila, Comité, OT, etc.)
  // Mapa unico de ids viejos -> hub (definido a nivel modulo como HUB_DE). El modulo
  // Forecast se elimino: sus permisos y deep-links caen en Metas.
  const LEGACY_HUB = HUB_DE;
  const extrasUsuario = [...new Set((usuario.modulos_extra || []).map(m => LEGACY_HUB[m] || m))];
  const baseGroups = NAV_GROUPS_ROL[usuario.rol] || NAV_GROUPS_ROL.ventas;
  const navGroups = baseGroups.map(g => {
    // El Dashboard es de la plataforma, no un módulo contratable: siempre va.
    if (!g.items) return (g.id === 'dashboard' || orgTiene(g.id)) ? g : null;
    const add = extrasUsuario.filter(m => MOD_GROUP[m]===g.id && !g.items.includes(m));
    // Módulos contratados por la org: lo que no tiene, no aparece — ni para admin.
    const items = [...g.items, ...add].filter(orgTiene);
    return items.length ? {...g, items} : null;
  }).filter(Boolean);
  const existingIds = navGroups.flatMap(g => g.items||[g.id]);
  const standaloneExtras = extrasUsuario.filter(m => !MOD_GROUP[m] && !existingIds.includes(m) && orgTiene(m));
  // SIBILA: acceso restringido por nombre (editable en radar_config 'sibila_acceso'
  // = {"usuarios":["Fabrizio","Claudia","Cristian"]}). Nadie más lo ve en el nav.
  const puedeSibila = (() => {
    const n = (usuario.nombre||'').trim().toLowerCase();
    const lista = (Array.isArray(sibilaAcceso) && sibilaAcceso.length ? sibilaAcceso : ['fabrizio','claudia','cristian'])
      .map(x=>String(x).trim().toLowerCase()).filter(Boolean);
    return lista.some(x => n === x || n.startsWith(x+' ') || n.split(' ')[0] === x);
  })();
  // NEXA ya no es item del nav: vive como tab de Mensajes, gateada por puedeSibila dentro del hub.
  const allNavGroups = [...navGroups, ...standaloneExtras.map(m=>({id:m,label:NAV_META[m]?.label||m,icon:NAV_META[m]?.icon||'•',single:true}))]
    .filter(g => g.items ? g.items.length > 0 : true);

  // ── S1 (Track P0): el router revalida el rol en cada render ────────────────
  // El modulo activo venia de localStorage validado solo contra NAV_META (todos
  // los modulos que existen), no contra los del rol: cualquier usuario podia
  // setear radar_mod='usuarios' en la consola, refrescar, y llegar al panel de
  // administracion. Ahora el set de modulos permitidos se deriva del nav real
  // del usuario (rol + modulos_extra + modulos de la org) y todo id fuera de
  // ese set — incluidos los legacy, traducidos a su hub — cae a dashboard.
  const modsPermitidos = (() => {
    const ids = new Set(['dashboard']);
    allNavGroups.forEach(g => (g.items || [g.id]).forEach(i => ids.add(i)));
    Object.entries(LEGACY_HUB).forEach(([viejo, hub]) => { if (ids.has(hub)) ids.add(viejo); });
    return ids;
  })();
  // OJO: aqui NO puede haber hooks — estamos despues del early-return de Login
  // (un useEffect aqui rompia React #310 al iniciar sesion: mas hooks que antes).
  // modSeguro es un calculo puro; el router renderiza con el y eso basta como
  // seguridad. El estado `mod` se corrige solo con el proximo click del nav.
  const modSeguro = modsPermitidos.has(mod) ? mod : 'dashboard';

  return (
    <div style={{fontFamily:"'Inter','DM Sans',system-ui,sans-serif",background:C.bg0,height:'100vh',maxHeight:'100dvh',display:'flex',flexDirection:'row',color:C.t1,overflow:'hidden'}}>
      <Toasts/>
      <Confirmes/>
      {checkIn && <Bienvenida usuario={usuario} onClose={()=>setCheckIn(false)}/>}
      {searchOpen && <GlobalSearch clientes={clientes} equipos={equipos} onClose={()=>setSearchOpen(false)} onNavigate={id=>{ setMod(id); setSearchOpen(false); }}/>}

      {/* ─── SIDEBAR oscuro (colapsable + ocultable) ─── */}
      {!sidebarOculto && (
      <aside style={{width:212,flexShrink:0,background:C.sbBg,display:'flex',flexDirection:'column',borderRight:`1px solid ${C.sbBorder}`}}>
        <div style={{padding:'16px 12px 14px 16px',display:'flex',alignItems:'center',gap:10,borderBottom:`1px solid ${C.sbBorder}`}}>
          <div style={{width:32,height:32,borderRadius:9,background:C.blue,boxShadow:'inset 0 1px 0 rgba(255,255,255,.18), 0 1px 2px rgba(0,0,0,.30)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>📡</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:14,fontWeight:900,letterSpacing:'.1em',color:C.sbTextActive}}>RADAR</div>
          </div>
          <button onClick={()=>setSidebarOculto(true)} title="Ocultar menú"
            style={{border:'none',background:'transparent',color:C.sbMuted,cursor:'pointer',fontSize:18,lineHeight:1,padding:4,borderRadius:6,flexShrink:0}}
            onMouseEnter={e=>{e.currentTarget.style.background=C.sbHover;e.currentTarget.style.color=C.sbTextActive;}}
            onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color=C.sbMuted;}}>«</button>
        </div>
        <nav style={{flex:1,overflowY:'auto',padding:'10px 8px',display:'flex',flexDirection:'column',gap:1}}>
          {(() => {
            const NavRow = (id, indent=false) => {
              const item = NAV_META[id]; if (!item) return null;
              const act = mod===id; const badge = NAV_BADGES[id];
              return (
                <button key={id} onClick={()=>setMod(id)} title={item.label}
                  style={{display:'flex',alignItems:'center',gap:10,width:'100%',padding:indent?'7px 10px 7px 30px':'8px 10px',border:'none',cursor:'pointer',borderRadius:8,textAlign:'left',
                    background:act?C.sbActive:'transparent',color:act?C.sbTextActive:C.sbText,fontSize:12.5,fontWeight:act?600:500,
                    boxShadow:act?'inset 0 1px 0 rgba(255,255,255,.12), 0 1px 2px rgba(0,0,0,.25)':'none',transition:'background .15s, color .15s'}}
                  onMouseEnter={e=>{ if(!act){ e.currentTarget.style.background=C.sbHover; e.currentTarget.style.color=C.sbTextActive; } }}
                  onMouseLeave={e=>{ if(!act){ e.currentTarget.style.background='transparent'; e.currentTarget.style.color=C.sbText; } }}>
                  {!indent && <Icon id={id} size={16}/>}<span style={{flex:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{item.label}</span>
                  {badge>0 && <span style={{background:C.red,color:'#fff',borderRadius:9,minWidth:16,height:16,padding:'0 4px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:900}}>{badge>9?'9+':badge}</span>}
                </button>
              );
            };
            // El grupo del módulo activo arranca abierto; el usuario puede abrir/cerrar cualquiera.
            const grupoDeMod = allNavGroups.find(g=>!g.single && g.items?.includes(mod))?.id;
            const abierto = navGrupoAbierto === null ? grupoDeMod : navGrupoAbierto;
            return allNavGroups.map(group => {
              if (group.single) return NavRow(group.id);
              const open = abierto === group.id;
              const tieneActivo = group.items.includes(mod);
              const badgeGrupo = group.items.reduce((s,id)=>s+(NAV_BADGES[id]||0),0);
              return (
                <div key={group.id} style={{marginTop:4}}>
                  <button onClick={()=>setNavGrupoAbierto(open ? '' : group.id)} title={group.label}
                    style={{display:'flex',alignItems:'center',gap:7,width:'100%',padding:'9px 10px 5px',border:'none',cursor:'pointer',borderRadius:6,textAlign:'left',
                      background:'transparent',color:tieneActivo?C.sbText:C.sbMuted,fontSize:11,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',transition:'color .15s'}}
                    onMouseEnter={e=>{e.currentTarget.style.color=C.sbText;}}
                    onMouseLeave={e=>{e.currentTarget.style.color=tieneActivo?C.sbText:C.sbMuted;}}>
                    <span style={{fontSize:8,transition:'transform .15s',display:'inline-block',transform:open?'rotate(90deg)':'none',flexShrink:0}}>▶</span>
                    <span style={{flex:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{group.label}</span>
                    {!open && badgeGrupo>0 && <span style={{background:C.red,color:'#fff',borderRadius:9,minWidth:16,height:16,padding:'0 4px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:900,letterSpacing:0}}>{badgeGrupo>9?'9+':badgeGrupo}</span>}
                  </button>
                  {open && <div style={{marginTop:1}}>{group.items.map(id=>NavRow(id,true))}</div>}
                </div>
              );
            });
          })()}
        </nav>
      </aside>
      )}

      {/* ─── MAIN (barra superior + contenido) ─── */}
      <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{height:56,borderBottom:`1px solid ${C.border}`,background:C.bg2,display:'flex',alignItems:'center',padding:'0 16px',gap:10,flexShrink:0,position:'relative',zIndex:100,boxShadow:SH.xs}}>
          {sidebarOculto && (
            <button onClick={()=>setSidebarOculto(false)} title="Mostrar menú"
              style={{border:`1px solid ${C.border}`,background:C.bg2,color:C.t2,cursor:'pointer',fontSize:16,lineHeight:1,padding:'6px 9px',borderRadius:R.md,flexShrink:0}}>☰</button>
          )}
          <div style={{fontSize:17,fontWeight:700,letterSpacing:'-0.015em',color:C.t1,whiteSpace:'nowrap'}}>{subMod ? <><span style={{color:C.t4,fontWeight:600}}>{NAV_META[mod]?.label} / </span>{subMod}</> : (NAV_META[mod]?.label || '')}</div>
          <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
          {/* Selector empresa (solo admin, y solo si la org tiene MAS de una empresa) */}
          {(usuario.rol==='admin') && ORG.empresas.length > 1 && (
            <div style={{display:'flex',gap:2,background:C.bg1,borderRadius:8,padding:3,border:`1px solid ${C.border}`}}>
              {[['all','Todo'],['honduras','HN'],['costarica','CR']].map(([val,lbl])=>(
                <button key={val} onClick={()=>setEmpresaVista(val)}
                  style={{padding:'4px 12px',border:'none',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:600,
                    background:empresaVista===val?C.bg2:'transparent',color:empresaVista===val?C.t1:C.t3,boxShadow:empresaVista===val?SH.sm:'none',transition:'all .15s',whiteSpace:'nowrap'}}>
                  {lbl}
                </button>
              ))}
            </div>
          )}
          {/* 🔍 Búsqueda global */}
          <button onClick={()=>setSearchOpen(true)}
            style={{background:'none',border:`1px solid ${C.border}`,borderRadius:7,padding:'5px 10px',color:C.t4,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',gap:6,lineHeight:1}}>
            <span>🔍</span>
            <span style={{fontSize:11,color:C.t4}}>Buscar</span>
            <span style={{fontSize:10,color:C.t4,background:C.bg1,borderRadius:4,padding:'1px 5px',fontFamily:'monospace'}}>⌘K</span>
          </button>
          {/* 🔔 Notificaciones */}
          <div style={{position:'relative'}}>
            {/* Abrir la campanita ya NO marca todo como leido a los 3 segundos.
                Con eso, dos avisos de clientes nuevos desaparecieron para siempre
                sin que nadie pudiera entrar a verlos: un aviso que se borra solo
                antes de que lo atiendan es peor que no tenerlo. Se marca al
                tocarlo, o con el boton de "marcar todas". */}
            <button onClick={()=>setShowNotifs(v=>!v)}
              style={{background:'none',border:`1px solid ${notifs.some(n=>!n.leida)?C.orange:C.border}`,borderRadius:8,padding:'7px 10px',color:notifs.some(n=>!n.leida)?C.orange:C.t3,cursor:'pointer',position:'relative',lineHeight:0,display:'inline-flex',alignItems:'center'}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              {(()=>{ const nl=notifs.filter(n=>!n.leida).length; return nl>0&&<span style={{position:'absolute',top:-5,right:-5,background:C.red,color:'#fff',borderRadius:'50%',width:16,height:16,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:900,lineHeight:1}}>{nl>9?'9+':nl}</span>; })()}
            </button>
            {showNotifs&&(
              <div style={{position:'fixed',top:60,right:14,width:320,maxWidth:'92vw',maxHeight:'70vh',overflowY:'auto',background:C.bg2,border:`1px solid ${C.border}`,borderRadius:R.lg,boxShadow:SH.lg,zIndex:3000}}>
                <div style={{padding:'12px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontSize:13,fontWeight:800,color:C.t1}}>Notificaciones</span>
                  {notifs.some(n=>!n.leida)&&<button onClick={marcarLeidasNotifs} style={{background:'none',border:'none',color:C.t4,fontSize:11,cursor:'pointer'}}>Marcar todas leídas</button>}
                </div>
                <div style={{maxHeight:360,overflowY:'auto'}}>
                  {notifs.length===0
                    ? <div style={{padding:24,textAlign:'center',color:C.t4,fontSize:13}}>Sin notificaciones nuevas</div>
                    : notifs.map(n=>(
                      /* Ir a donde apunta el aviso. Antes solo miraba n.opp_id,
                         una columna que la tabla NO tiene, asi que el clic nunca
                         llevaba a ningun lado. Ahora usa link_modulo/link_id, que
                         es lo que se guarda al crear la notificacion. */
                      <div key={n.id} onClick={()=>{
                        const destino = n.link_modulo || (n.tipo==='prospecto_asignado' ? 'crm' : null);
                        if (destino === 'crm' && n.link_id) setCrmInit(n.tipo==='cliente_nuevo_web' ? {clienteId:n.link_id} : {oppId:n.link_id});
                        else if (destino === 'crm') setCrmInit({});
                        if (destino) setMod(destino);
                        setShowNotifs(false); marcarUnaLeida(n.id);
                      }}
                        className="radar-hrow" style={{padding:'12px 16px',borderBottom:`1px solid ${C.border}`,
                          background:n.leida?'transparent':C.bg3, opacity:n.leida?0.6:1,
                          borderLeft:n.leida?'3px solid transparent':`3px solid ${C.orange}`,
                          cursor:n.link_modulo?'pointer':'default'}}>
                        {n.titulo&&<div style={{fontSize:12,fontWeight:700,color:C.t1,marginBottom:2}}>{n.titulo}</div>}
                        <div style={{fontSize:12,color:C.t2,lineHeight:1.4,marginBottom:4}}>{n.mensaje}</div>
                        <div style={{fontSize:10,color:C.t4,display:'flex',justifyContent:'space-between',alignItems:'center'}}><span>{n.created_at?new Date(n.created_at).toLocaleString('es-HN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}):'—'}{n.leida?' · leída':''}</span>{n.link_modulo&&<span style={{color:C.blue,fontWeight:700}}>Abrir →</span>}</div>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:6,background:C.bg3,borderRadius:8,padding:'5px 10px'}}>
            <div style={{width:22,height:22,borderRadius:'50%',background:'linear-gradient(135deg,#0F766E,#0D5F58)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800}}>{(usuario.nombre||'?')[0]}</div>
            <span style={{fontSize:13,fontWeight:600}}>{(usuario.nombre||'').split(' ')[0]}</span>
            <span style={{fontSize:9,color:C.t4,background:C.bg1,borderRadius:4,padding:'2px 5px',textTransform:'uppercase'}}>{usuario.rol}</span>
          </div>
          <button onClick={logout} style={{background:'none',border:`1px solid ${C.border}`,borderRadius:7,padding:'5px 11px',color:C.t4,fontSize:12,cursor:'pointer',fontWeight:600}}>Salir</button>
        </div>
        </div>
      {/* CONTENT — overflowY:auto (no 'hidden'): los módulos "tipo documento" (sin scroll
          propio) se cortaban sin poder bajar. Ahora la página scrollea; los módulos "tipo
          app" con scroll interno (Cobros, NEXA, CRM…) no se afectan porque nunca exceden
          la altura. overflowX:hidden evita scroll horizontal a nivel de página. */}
      <div key={`${mod}:${revalTick}`} className="modFadeIn" style={{flex:1,display:'flex',overflowY:'auto',overflowX:'hidden'}}>
        {/* empresaActiva: admin usa el selector del nav; otros usuarios usan su empresa_id fijo.
            'all' (o vacío) = sin filtro — si se pasa 'all' literal, los módulos hacen
            .eq('empresa_id','all') y no ven NADA (bug: flota en 0 para vendedores multiempresa). */}
        {(()=>{ const ea = usuario.rol==='admin'
          ? (empresaVista!=='all'?empresaVista:null)
          : ((usuario.empresa_id && usuario.empresa_id!=='all') ? usuario.empresa_id : null); return (<>
          {modSeguro==='plan'          && <PlanOctubre usuario={usuario}/>}
          {modSeguro==='dashboard'     && <Dashboard usuario={usuario} equipos={equipos} onNavigate={setMod}/>}
          {/* FLOTA hub (Equipos · Mantenimiento) — ids legacy redirigen con su tab */}
          {(modSeguro==='flota'||modSeguro==='mantenimiento') &&
            <FlotaHub usuario={usuario} clientes={clientes} equipos={equipos} onEquiposChange={setEquipos} empresaActiva={ea}
              initialTab={{flota:'equipos',mantenimiento:'mantenimiento'}[mod]}/>}
          {modSeguro==='flota_cliente' && <FlotaCliente usuario={usuario} clientes={clientes} empresaActiva={ea}/>}
          {modSeguro==='rentas'        && <Rentas clientes={clientes} equipos={equipos} empresaActiva={ea}/>}
          {/* TALLER hub (Taller · Órdenes de Trabajo, ex Cotizaciones) */}
          {(modSeguro==='taller'||modSeguro==='cotizaciones') &&
            <TallerHub usuario={usuario} clientes={clientes} equipos={equipos} empresaActiva={ea}
              initialTab={{taller:'taller',cotizaciones:'ot'}[mod]}/>}
          {/* COBROS (+ tab Ingresos) — ids legacy ingresos/facturacion redirigen */}
          {(modSeguro==='cobros'||modSeguro==='facturacion'||modSeguro==='ingresos') &&
            <CobrosFacturacion usuario={usuario} empresaActiva={ea} clientes={clientes} equipos={equipos}
              initialTab={modSeguro==='ingresos'?'ingresos':undefined}/>}
          {/* METAS hub (Metas & Revisión intacto · Comité, ex 'semana') */}
          {(modSeguro==='metas'||modSeguro==='semana') &&
            <MetasHub usuario={usuario} empresaActiva={ea} onNavigate={(m,f)=>{setCrmInit(f||{});setMod(m);}}
              initialTab={modSeguro==='semana'?'comite':undefined}/>}
          {/* MENSAJES hub (WhatsApp · NEXA · Campañas) — ids legacy redirigen con su tab */}
          {(modSeguro==='mensajes'||modSeguro==='whatsapp'||modSeguro==='sibila'||modSeguro==='campanias') &&
            <MensajesHub usuario={usuario} empresaActiva={ea} puedeSibila={puedeSibila}
              initialTab={{whatsapp:'whatsapp',sibila:'nexa',campanias:'campanias'}[mod]}/>}
          {modSeguro==='agenda'        && <AgendaHub usuario={usuario} clientes={clientes} empresaActiva={ea}/>}
          {/* ids legacy → AgendaHub con su tab inicial (radar_mod guardado y deep-links de notificaciones siguen funcionando) */}
          {(modSeguro==='seguimiento'||modSeguro==='actividades'||modSeguro==='visitas') &&
            <AgendaHub usuario={usuario} clientes={clientes} empresaActiva={ea}
              initialTab={{seguimiento:'pendientes',actividades:'agenda',visitas:'visitas'}[mod]}/>}
          {modSeguro==='reportes'      && <Reportes usuario={usuario} clientes={clientes} equipos={equipos} empresaActiva={ea}/>}
          {/* CRM (+ tabs Sin atender · Elevadoras) — ids legacy redirigen con su tab */}
          {(modSeguro==='crm'||modSeguro==='clientes_inactivos'||modSeguro==='elevadoras') &&
            <CRM usuario={usuario} empresaActiva={ea} initFiltro={crmInit}
              initTab={{clientes_inactivos:'inactivos'}[mod]}/>}
          {modSeguro==='tickets'       && <Tickets usuario={usuario} equipos={equipos}/>}
          {modSeguro==='viaticos'      && <Viaticos usuario={usuario}/>}
          {modSeguro==='elsa'          && <ELSA equipos={equipos} clientes={clientes}/>}
          {modSeguro==='usuarios'      && <Usuarios usuarioActual={usuario}/>}
          {modSeguro==='sap'           && <SAP/>}
          {modSeguro==='inventario'    && <InventarioSAP/>}
          {modSeguro==='supervision'   && <SupervisionVendedores/>}
        </>); })()}
      </div>

      {/* STATUS BAR */}
      <div style={{height:26,borderTop:`1px solid ${C.border}`,background:C.bg2,display:'flex',alignItems:'center',padding:'0 14px',gap:16,flexShrink:0}}>
        <span style={{fontSize:10,color:C.t4,letterSpacing:'.05em'}}>RADAR 2.0 · {ORG.nombre}</span>
        <div style={{display:'flex',gap:10}}>
          {/* Contadores de flota: solo si la org tiene ese módulo */}
          {orgTiene('flota') && Object.entries(STATUS).map(([k,v])=>{ const n=equipos.filter(e=>e.estado===k).length; if(!n)return null;
            return <div key={k} style={{display:'flex',alignItems:'center',gap:4,fontSize:10}}>
              <div style={{width:6,height:6,borderRadius:'50%',background:v.color}}/><span style={{color:C.t4}}>{n} {v.label}</span>
            </div>;
          })}
        </div>
        <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:4}}>
          <div style={{width:6,height:6,borderRadius:'50%',background:'#10B981'}}/><span style={{fontSize:10,color:C.t4}}>Online</span>
        </div>
      </div>

      </div>{/* /main */}
      {!(mod==='sibila' || subMod==='NEXA') && <AsistenteWidget usuario={usuario}/>}{/* en NEXA tapa el botón de enviar del chat */}
    </div>
  );
}
